import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Compass, Home, Map, Camera, Radio, ShoppingBag, Bike, User, MessageCircle, Heart, Share2, Send, Bookmark, MoreHorizontal, ChevronLeft, ChevronRight, X, Plus, Search, Bell, Shield, AlertTriangle, Navigation, Clock, Users, Star, CheckCircle, Phone, Globe, Wrench, MapPin, Calendar, Zap, Award, TrendingUp, Lock, Flag, ChevronDown, Play, Volume2, VolumeX, Tag, Image, Info, Edit2, Trash2, Music2, Download, Utensils, RefreshCw , Eye, EyeOff } from "lucide-react";
import { supabase } from "./supabase";
import { Capacitor, registerPlugin } from "@capacitor/core";
import QRCode from "qrcode"; // QR de emergencia para el casco (ficha médica)
import ImageCropper from "./ImageCropper";

// ¿Corriendo dentro de la app nativa (Capacitor) o en el navegador web?
const esNativo = () => { try { return Capacitor.isNativePlatform(); } catch (e) { return false; } };
// El hash con el que llegó la página, capturado ANTES de que supabase-js lo consuma:
// si viene del link de confirmación de email (type=signup), avisamos que ya está adentro
const HASH_LLEGADA = typeof window !== "undefined" ? String(window.location.hash || "") : "";
let avisoConfirmacionMostrado = false;

// En la app nativa el código va bundleado (origin localhost), así que las llamadas a las
// funciones serverless tienen que ser absolutas a olbos.ai. En web, relativas.
const API_BASE = esNativo() ? "https://olbos.ai" : "";

// Para armar links compartibles (vivo, evento, ruta, invitaciones): en la app nativa el
// origin es "https://localhost" (rompe los links de WhatsApp), así que usamos olbos.ai.
const SITE_URL = esNativo() ? "https://olbos.ai" : (typeof window !== "undefined" ? window.location.origin : "https://olbos.ai");

// ─── GEO con BACKGROUND ──────────────────────────────────────────────────────
// El track recorder tiene que seguir grabando con la pantalla apagada / la app en
// background. Usamos @transistorsoft/capacitor-background-geolocation, que persiste
// las posiciones en una base nativa (SQLite) — sobrevive a que Android congele/mate
// el WebView (clave en Samsung). En la web, navigator.geolocation normal.
// `startGeoWatch` unifica los dos: onPunto recibe {lat,lng,v,acc,t} y devuelve { stop }.
// El plugin nativo (transistorsoft) es un SERVICIO ÚNICO. Para que el recorder y el
// compartir-en-vivo puedan correr a la vez sin pisarse, multiplexamos: una sola
// suscripción reparte a todos los consumidores, y el servicio se frena cuando se va el último.
let _bgConsumidores = [];
let _bgSub = null;
let _bgStarted = false;
let _bgLastTs = 0;            // ts del último punto ENTREGADO a los consumidores
let _bgBackfillEnCurso = false;
let _bgResumeListener = null;

// ── PUBLICACIÓN NATIVA DE LA POSICIÓN (09-oct-2026) ──
// Con la pantalla bloqueada Android congela el WebView: los upserts de posición dejaban de
// salir y los demás veían el punto clavado hasta que el rider desbloqueaba (Matías, camino
// a Carlos Paz). El plugin de GPS tiene su propio cliente HTTP nativo: cada ~10 fixes manda
// un POST al RPC bg_publicar con un token de esta sesión, y la fila bg_sesiones dice a dónde
// publicar (rodada del check-in / mapa en vivo, link "seguime en vivo"). Mientras se graba,
// el servidor además guarda los puntos (bg_puntos) para rellenar la traza al desbloquear.
const BG_RPC_KEY = "sb_publishable_MEGFzGTTPQu9E38MPcONAQ_b6uuTG27";
const _bgToken = (() => {
  const nuevo = () => "bg_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
  try { let t = sessionStorage.getItem("olbos_bg_token"); if (!t) { t = nuevo(); sessionStorage.setItem("olbos_bg_token", t); } return t; } catch (e) { return nuevo(); }
})();
// pasivo = el mapa está abierto SIN grabar (navegando o rodando libre): el servidor guarda
// igual los puntos en bg_puntos, así lo andado con la pantalla bloqueada se recupera al
// volver y suma como km de navegación (pedido de Carlos, 30-oct: "que los km del navegador
// se computen los graben o no"). Antes solo se guardaban grabando y esos km se perdían.
let _bgSesion = { evento_id: null, share_token: null, traza_id: null, pasivo: false };
const bgSesionActualizar = (campos) => {
  if (!esNativo()) return;
  _bgSesion = { ..._bgSesion, ...campos };
  try {
    supabase.auth.getUser().then(({ data }) => {
      const uid = data && data.user && data.user.id;
      if (!uid) return;
      let plat = "nativo"; try { plat = window.Capacitor.getPlatform(); } catch (e) { /* */ }
      const fila = { token: _bgToken, user_id: uid, ..._bgSesion, plataforma: plat, updated_at: new Date().toISOString() };
      supabase.from("bg_sesiones").upsert(fila, { onConflict: "token" }).then(({ error }) => {
        // Sin la columna "pasivo" en la base (SQL sin correr) la sesión igual tiene que andar
        if (error && /pasivo/i.test(error.message || "")) {
          const { pasivo, ...sinPasivo } = fila;
          supabase.from("bg_sesiones").upsert(sinPasivo, { onConflict: "token" }).then(() => {}, () => {});
        }
      }, () => {});
    }, () => {});
  } catch (e) { /* nunca rompe el GPS */ }
};

const _bgAPunto = (location) => {
  const c = (location && location.coords) || {};
  return {
    lat: c.latitude, lng: c.longitude,
    v: (c.speed != null && c.speed >= 0) ? c.speed : 0,
    acc: c.accuracy,
    head: (c.heading != null && c.heading >= 0) ? c.heading : null,
    alt: (c.altitude != null && isFinite(c.altitude) && c.altitude > -500) ? c.altitude : null, // m sobre el nivel del mar
    t: location && location.timestamp ? new Date(location.timestamp).getTime() : undefined,
  };
};

const _bgEntregar = (punto) => {
  if (punto.t) _bgLastTs = Math.max(_bgLastTs, punto.t);
  _bgConsumidores.forEach(cs => { try { cs.onPunto(punto); } catch (e) {} });
};

// BACKFILL: el plugin graba SIEMPRE en su base nativa (SQLite), incluso con el WebView
// congelado (pantalla apagada / Waze en primer plano). Pero los callbacks JS NO llegan
// mientras está congelado → la traza quedaba con huecos (líneas rectas) y el "en vivo"
// no se actualizaba. Acá leemos de la base nativa los puntos que el JS se perdió y los
// entregamos en orden a todos los consumidores. Se dispara al detectar un hueco y al
// volver la app al frente.
const _bgBackfill = async (BG) => {
  if (_bgBackfillEnCurso || _bgConsumidores.length === 0) return;
  _bgBackfillEnCurso = true;
  // Primero lo que el plugin ya subió al servidor (autoSync borra de su base local lo
  // sincronizado): bg_puntos guarda los fixes mientras se graba. Después, lo que quedó
  // sin subir (sin señal) en la base nativa. Los dos filtrados por "más nuevo que lo entregado".
  try {
    const desde = new Date(Math.max(_bgLastTs, Date.now() - 12 * 3600 * 1000)).toISOString();
    const { data } = await supabase.from("bg_puntos").select("lat,lng,v,acc,head,alt,t").eq("token", _bgToken).gt("t", desde).order("t").limit(5000);
    const extra = (data || [])
      .map(r => ({ lat: r.lat, lng: r.lng, v: r.v != null ? r.v : 0, acc: r.acc, head: r.head, alt: r.alt, t: new Date(r.t).getTime() }))
      .filter(p => p.lat != null && p.t > _bgLastTs)
      .sort((a, b) => a.t - b.t);
    extra.forEach(p => _bgEntregar(p));
  } catch (e) {}
  try {
    const locs = await BG.getLocations();
    const perdidos = (locs || [])
      .map(_bgAPunto)
      .filter(p => p.lat != null && p.t && p.t > _bgLastTs)
      .sort((a, b) => a.t - b.t);
    perdidos.forEach(p => _bgEntregar(p));
    // Limpiar el backlog ya entregado para que el próximo backfill sea liviano
    try { await BG.destroyLocations(); } catch (e) {}
  } catch (e) {}
  _bgBackfillEnCurso = false;
};

// Posición actual UNA vez, por el canal correcto: en nativo usa el plugin de
// background-geolocation (evita el cartel "localhost quiere usar tu ubicación"
// del WebView); en web, el navigator de siempre. Devuelve {lat, lng, acc, v}.
const obtenerPosicion = async (opts = {}) => {
  if (esNativo()) {
    try {
      const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
      const loc = await BG.getCurrentPosition({ samples: 1, timeout: 15, persist: false });
      const r = { lat: loc.coords.latitude, lng: loc.coords.longitude, acc: loc.coords.accuracy, v: loc.coords.speed || 0 };
      guardarPosReciente(r);
      return r;
    } catch (e) { /* cae al navegador como red de seguridad */ }
  }
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("Sin geolocalización")); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { const r = { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy, v: p.coords.speed || 0 }; guardarPosReciente(r); resolve(r); },
      reject,
      { enableHighAccuracy: true, timeout: 12000, ...opts }
    );
  });
};
// Última posición conocida (para "Salidas ahora cerca tuyo" en Social/Eventos sin pedir
// permiso de ubicación apenas se abre la pantalla). La guardan obtenerPosicion y el mapa.
const guardarPosReciente = (p) => {
  try { if (p && isFinite(p.lat) && isFinite(p.lng)) localStorage.setItem("olbos_pos_reciente", JSON.stringify({ lat: p.lat, lng: p.lng, t: Date.now() })); } catch (e) { /* */ }
};
const posReciente = (maxHoras = 24) => {
  try {
    const p = JSON.parse(localStorage.getItem("olbos_pos_reciente") || "null");
    return p && isFinite(p.lat) && isFinite(p.lng) && Date.now() - p.t < maxHoras * 3600000 ? { lat: p.lat, lng: p.lng } : null;
  } catch (e) { return null; }
};

// Android mata el servicio de ubicación de las apps "optimizadas" cuando la pantalla
// lleva un rato apagada, y la grabación se corta en silencio. Una sola vez, al empezar
// a grabar, se pide la exención (el ajuste del sistema se abre con el plugin).
const pedirIgnorarBateria = async () => {
  if (!esNativo()) return;
  try {
    const plat = window.Capacitor && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : "";
    if (plat !== "android") return;
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    const ds = BG.deviceSettings;
    if (!ds || !ds.isIgnoringBatteryOptimizations) return;
    const ya = await ds.isIgnoringBatteryOptimizations();
    // Queda registrado en cada grabación: así se ve desde la base si el teléfono tiene a
    // Olbos restringida (prueba de Carlos 10-oct: 1 Hz con pantalla prendida y un punto
    // cada 3-6 min con la pantalla apagada, con el ajuste sin confirmar).
    registrarUso("bateria_estado", { sin_restriccion: !!ya });
    if (ya) return;
    // Antes se preguntaba UNA sola vez por teléfono: si el rider cerraba el ajuste sin
    // cambiarlo, no volvía a enterarse nunca. Ahora se pregunta una vez por sesión
    // mientras siga restringida.
    if (window._olbosBateriaPedida) return;
    window._olbosBateriaPedida = true;
    if (window.confirm("Para que el recorrido se siga grabando con la pantalla apagada, Android tiene que dejar a Olbos sin restricciones de batería.\n\n¿Abrimos ese ajuste ahora? Elegí «Sin restricciones» (o «No optimizar»).")) {
      // showIgnoreBatteryOptimizations solo ARMA el pedido; lo que abre el ajuste es show().
      // Hasta el 03-nov se llamaba solo a la primera y el ajuste nunca se abría.
      await abrirAjusteBateria();
    }
  } catch (e) { /* sin el ajuste, se graba igual mientras Android lo permita */ }
};

// ── PLUGINS NATIVOS LOCALES (10-oct-2026, Originarios) ──
// OlbosIntercom: servicio en primer plano tipo micrófono (Android) / sesión de audio de
// llamada (iOS) para que el intercomunicador siga con la pantalla apagada.
// OlbosLlamada (solo Android): si entra una llamada con el mapa abierto, al atenderla y al
// cortarla se trae Olbos de vuelta al frente. iOS no permite traer una app al frente: ahí
// esas llamadas rechazan y se ignoran.
// Código nativo: android/app/src/main/java/ai/olbos/app/ y ios/App/App/AppDelegate.swift.
const OlbosIntercom = registerPlugin("OlbosIntercom");
const OlbosLlamada = registerPlugin("OlbosLlamada");
// OlbosCar (solo Android): la navegación en la pantalla de Android Auto (docs/ANDROID_AUTO.md)
const OlbosCar = registerPlugin("OlbosCar");
const _plataformaNativa =() => { try { return esNativo() ? Capacitor.getPlatform() : "web"; } catch (e) { return "web"; } };
// Varios CanalDeVoz pueden estar montados a la vez (detalle del evento + mapa de la rodada):
// el servicio se apaga recién cuando se va el último que lo pidió.
let _intercomFondoUsos = [];
const intercomFondo = {
  disponible: () => ["android", "ios"].includes(_plataformaNativa()),
  prender: (clave) => {
    if (!_intercomFondoUsos.includes(clave)) _intercomFondoUsos.push(clave);
    if (!intercomFondo.disponible()) return;
    try {
      OlbosIntercom.start().then(() => {}, (e) => {
        try { reportarError("intercom_fondo " + String((e && e.message) || e).slice(0, 200), null, "intercomFondo.prender"); } catch (e2) { /* */ }
      });
    } catch (e) { /* sin plugin: el intercom sigue como antes */ }
  },
  apagar: (clave) => {
    _intercomFondoUsos = _intercomFondoUsos.filter(c => c !== clave);
    if (_intercomFondoUsos.length || !intercomFondo.disponible()) return;
    try { OlbosIntercom.stop().then(() => {}, () => {}); } catch (e) { /* */ }
  },
};
const volverDeLlamada = {
  activar: () => {
    if (_plataformaNativa() !== "android") return;
    try { OlbosLlamada.activar().then(() => {}, () => {}); } catch (e) { /* */ }
  },
  desactivar: () => {
    if (_plataformaNativa() !== "android") return;
    try { OlbosLlamada.desactivar().then(() => {}, () => {}); } catch (e) { /* */ }
  },
};
// Traer el mapa al frente desde segundo plano: Android 10+ solo lo deja con el permiso
// "Mostrar sobre otras apps". Se pide UNA vez, al empezar a grabar.
const pedirPermisoVolverDeLlamada = async () => {
  if (_plataformaNativa() !== "android") return;
  try {
    if (localStorage.getItem("olbos_superposicion_pedida")) return;
    const r = await OlbosLlamada.permisoSuperposicion();
    localStorage.setItem("olbos_superposicion_pedida", "1");
    if (r && r.otorgado) return;
    if (window.confirm("Para volver al mapa solo después de una llamada, Olbos necesita poder mostrarse sobre otras apps.\n\nSi te llaman mientras manejás, al atender y al cortar el camino vuelve a la pantalla sin que toques nada.\n\n¿Abrimos ese ajuste? Buscá Olbos y activá «Mostrar sobre otras apps».")) {
      await OlbosLlamada.abrirAjusteSuperposicion();
    }
  } catch (e) { /* sin el permiso queda el aviso "Volvé al mapa" para tocar */ }
};

// ─── CHEQUEO DEL TELÉFONO ANTES DE LA RODADA (03-nov-2026) ───────────────────
// Emilia sale con grupos todos los fines de semana y nunca logró que todos quedaran en el
// mapa: cada teléfono pide los permisos distinto y nadie sabe qué tocar. Acá se mira el
// teléfono (permiso, "todo el tiempo", ubicación exacta, GPS prendido, batería) y, según la
// marca, se dice exactamente dónde se toca. Lo usan ChequeoRodada y la entrada al mapa.
const abrirAjusteBateria = async () => {
  try {
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    const req = await BG.deviceSettings.showIgnoreBatteryOptimizations();
    await BG.deviceSettings.show(req);
    return true;
  } catch (e) { return false; }
};
// Xiaomi, Huawei, OPPO y cía. tienen además su propio "administrador de energía"
const abrirAhorroDeLaMarca = async () => {
  try {
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    const req = await BG.deviceSettings.showPowerManager();
    await BG.deviceSettings.show(req);
    return true;
  } catch (e) { return false; }
};
const marcaTelefono = (s) => {
  const t = String(s || "").toLowerCase();
  // OJO: no buscar "apple": el navegador de Android dice "AppleWebKit" y daba iPhone
  if (/iphone|ipad|ipod/.test(t)) return "apple";
  if (/samsung|sm-[a-z]\d/.test(t)) return "samsung";
  if (/xiaomi|redmi|poco|\bmi \d|m2\d{3}/.test(t)) return "xiaomi";
  if (/motorola|\bmoto\b|xt\d{4}/.test(t)) return "motorola";
  if (/huawei|honor/.test(t)) return "huawei";
  if (/realme|rmx\d{4}/.test(t)) return "realme";
  if (/oppo|cph\d{4}/.test(t)) return "oppo";
  if (/\bvivo\b/.test(t)) return "vivo";
  if (/pixel|google/.test(t)) return "google";
  return null;
};
const NOMBRE_MARCA = { apple: "iPhone", samsung: "Samsung", xiaomi: "Xiaomi", motorola: "Motorola", huawei: "Huawei", realme: "realme", oppo: "OPPO", vivo: "vivo", google: "Pixel" };
const MARCAS_CON_AHORRO_PROPIO = ["xiaomi", "huawei", "oppo", "realme", "vivo", "samsung"];

// Devuelve cómo está el teléfono. Sin `conFix` no pide una posición (es lo rápido, para
// decidir si hace falta mostrar el chequeo); con `pedir` dispara el cartel del permiso.
const leerEstadoTelefono = async ({ conFix = true, pedir = false } = {}) => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const plat = _plataformaNativa();
  const so = plat !== "web" ? plat : (/iphone|ipad|ipod/i.test(ua) ? "ios" : /android/i.test(ua) ? "android" : "otro");
  const e = { plat, so, marca: so === "ios" ? "apple" : marcaTelefono(ua), modelo: null, enAppAjena: esNavegadorInApp(), ubicacion: "desconocida", gps: null, precisa: null, bateria: null, ahorro: null, acc: null };
  if (esNativo()) {
    try {
      const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
      try { const di = await BG.getDeviceInfo(); if (di) { e.modelo = di.model || null; e.marca = marcaTelefono(`${di.manufacturer || ""} ${di.model || ""}`) || e.marca; } } catch (x) { /* */ }
      const st = await BG.getProviderState();
      if (st) {
        e.ubicacion = st.status === BG.AUTHORIZATION_STATUS_ALWAYS ? "siempre"
          : st.status === BG.AUTHORIZATION_STATUS_WHEN_IN_USE ? "en_uso"
          : st.status === BG.AUTHORIZATION_STATUS_NOT_DETERMINED ? "sin_preguntar" : "denegada";
        if (st.enabled === false) e.gps = false; else if (st.enabled === true) e.gps = true;
        if (st.accuracyAuthorization != null) e.precisa = st.accuracyAuthorization === BG.ACCURACY_AUTHORIZATION_FULL;
      }
      if (plat === "android") { try { e.bateria = !!(await BG.deviceSettings.isIgnoringBatteryOptimizations()); } catch (x) { /* */ } }
      try { e.ahorro = !!(await BG.isPowerSaveMode()); } catch (x) { /* */ }
    } catch (x) { /* sin plugin: queda "desconocida" y no se frena a nadie */ }
  } else {
    if (typeof navigator === "undefined" || !navigator.geolocation) e.gps = false;
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const p = await navigator.permissions.query({ name: "geolocation" });
        e.ubicacion = p.state === "granted" ? "en_uso" : p.state === "denied" ? "denegada" : "sin_preguntar";
      }
    } catch (x) { /* Safari viejo: no dice nada hasta pedir */ }
  }
  const conPermiso = e.ubicacion === "siempre" || e.ubicacion === "en_uso";
  // La precisión de verdad se ve con un fix: la ubicación "aproximada" da 1 a 3 km de margen
  if ((conFix && conPermiso) || pedir) {
    try {
      const p = await Promise.race([
        obtenerPosicion({ timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 12000)),
      ]);
      e.acc = p && p.acc != null ? Math.round(p.acc) : null;
      if (e.precisa == null && e.acc != null) e.precisa = e.acc <= 1200;
      if (!conPermiso) e.ubicacion = esNativo() ? e.ubicacion : "en_uso";
      if (e.gps == null) e.gps = true;
      if (esNativo() && !conPermiso) {
        try { const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation"); const st = await BG.getProviderState(); if (st) e.ubicacion = st.status === BG.AUTHORIZATION_STATUS_ALWAYS ? "siempre" : st.status === BG.AUTHORIZATION_STATUS_WHEN_IN_USE ? "en_uso" : e.ubicacion; } catch (x) { /* */ }
      }
    } catch (err) {
      if (!esNativo() && err && err.code === 1) e.ubicacion = "denegada";
      if (!esNativo() && err && err.code === 2) e.gps = false;
    }
  }
  return e;
};

// Lo que se revisa, con su estado: ok true (bien), false (falta) o null (no se sabe todavía).
// `aviso` = no frena la entrada, solo avisa (ahorro de batería, web con la pantalla bloqueada).
const checksTelefono = (e) => {
  const nat = esNativo();
  const conPermiso = e.ubicacion === "siempre" || e.ubicacion === "en_uso";
  const l = [];
  l.push({ id: "permiso", titulo: "Permiso de ubicación", ok: conPermiso ? true : e.ubicacion === "desconocida" ? null : false,
    detalle: e.ubicacion === "denegada" ? "Olbos no tiene permiso: así nadie del grupo te ve." : "Sin este permiso el grupo no te ve en el mapa." });
  if (nat) l.push({ id: "fondo", titulo: e.so === "ios" ? "Ubicación «Siempre»" : "Ubicación «Todo el tiempo»", ok: e.ubicacion === "siempre" ? true : conPermiso ? false : null,
    detalle: "Así como está, cuando bloqueás la pantalla quedás clavado en el mapa y el grupo te pierde." });
  if (e.gps === false) l.push({ id: "gps", titulo: "Ubicación del teléfono prendida", ok: false, detalle: "La ubicación del teléfono está apagada." });
  l.push({ id: "precisa", titulo: "Ubicación exacta", ok: e.precisa === false ? false : e.precisa === true ? true : null,
    detalle: "Está en modo aproximado: te dibuja a 2 o 3 km de donde estás." });
  if (nat && e.plat === "android") l.push({ id: "bateria", titulo: "Batería sin restricciones", ok: e.bateria === true ? true : e.bateria === false ? false : null,
    detalle: "Android le corta el GPS a las apps «optimizadas» a los pocos minutos de pantalla apagada." });
  if (e.ahorro) l.push({ id: "ahorro", titulo: "Ahorro de batería apagado", ok: false, aviso: true,
    detalle: "Con el ahorro prendido el GPS manda tu posición muy de vez en cuando." });
  if (!nat) l.push({ id: "web", titulo: "Con la pantalla bloqueada", ok: false, aviso: true,
    detalle: "Desde el navegador, al bloquear la pantalla dejás de mandar tu posición." });
  return l;
};
const pendientesTelefono = (e) => checksTelefono(e).filter(c => c.ok === false && !c.aviso);

// Los pasos exactos, en el idioma de cada teléfono
const pasosArreglo = (id, e) => {
  const nat = esNativo(), ios = e.so === "ios", m = e.marca;
  if (id === "permiso" || id === "fondo") {
    if (nat && ios) return ["Tocá «Abrir Ajustes».", "Entrá a «Ubicación».", "Elegí «Siempre» y dejá prendida «Ubicación exacta».", "Volvé a Olbos: se revisa solo."];
    if (nat) {
      const donde = m === "xiaomi" ? "«Permisos de la app» → «Ubicación»" : "«Permisos» → «Ubicación»";
      return [`Tocá «${id === "permiso" ? "Dar permiso" : "Abrir ajustes"}».`, `Si se abre la pantalla de ajustes, entrá a ${donde}.`, "Elegí «Permitir todo el tiempo» y activá «Usar ubicación precisa».", "Volvé a Olbos: se revisa solo."];
    }
    if (e.enAppAjena) return [
      ios ? "Estás en el navegador de WhatsApp o Instagram. Tocá el ícono de compartir o los tres puntos y elegí «Abrir en Safari»." : "Estás en el navegador de WhatsApp o Instagram. Tocá los tres puntos de arriba y elegí «Abrir en Chrome».",
      "Ahí aceptá la ubicación cuando te la pida.",
    ];
    if (e.ubicacion === "denegada") return ios
      ? ["Abrí «Ajustes» del iPhone → «Privacidad y seguridad» → «Localización».", "Prendé «Localización», entrá a «Sitios web de Safari» y elegí «Al usar la app».", "Prendé «Ubicación exacta», volvé acá y tocá «Revisar de nuevo»."]
      : ["Tocá el ícono que está a la izquierda de olbos.ai, arriba de todo.", "Entrá a «Permisos» → «Ubicación» y elegí «Permitir».", "Volvé acá y tocá «Revisar de nuevo»."];
    return ["Tocá «Dar permiso».", ios ? "En el cartel elegí «Permitir»." : "En el cartel elegí «Precisa» y «Mientras la app está en uso» (o «Permitir»)."];
  }
  if (id === "gps") return ios
    ? ["Abrí «Ajustes» → «Privacidad y seguridad» → «Localización» y prendela.", "Volvé acá y tocá «Revisar de nuevo»."]
    : ["Deslizá la barra de arriba hacia abajo y prendé «Ubicación» (el ícono del pin).", "Volvé acá y tocá «Revisar de nuevo»."];
  if (id === "precisa") {
    if (ios) return nat ? ["Tocá «Abrir Ajustes» → «Ubicación».", "Prendé «Ubicación exacta»."] : ["Abrí «Ajustes» → «Privacidad y seguridad» → «Localización» → «Sitios web de Safari».", "Prendé «Ubicación exacta» y volvé acá."];
    return nat ? ["Tocá «Arreglar» y en el cartel elegí «Precisa».", "Si no aparece: Ajustes → Aplicaciones → Olbos → Permisos → Ubicación → activá «Usar ubicación precisa»."]
      : ["Abrí Ajustes del teléfono → Aplicaciones → Chrome → Permisos → Ubicación.", "Activá «Usar ubicación precisa» y volvé acá."];
  }
  if (id === "bateria") {
    const extra = {
      xiaomi: "En Xiaomi también: Ajustes → Apps → Administrar apps → Olbos → prendé «Inicio automático».",
      samsung: "En Samsung también: Ajustes → Batería → «Límites de uso en segundo plano» → sacá a Olbos de «Apps en suspensión».",
      huawei: "En Huawei también: Ajustes → Batería → «Inicio de apps» → Olbos → «Administrar manualmente», con todo prendido.",
      oppo: "En OPPO también: Ajustes → Batería → Olbos → «Permitir actividad en segundo plano».",
      realme: "En realme también: Ajustes → Batería → Olbos → «Permitir actividad en segundo plano».",
      vivo: "En vivo también: Ajustes → Batería → «Consumo alto en segundo plano» → prendé Olbos.",
    }[m];
    return ["Tocá «Arreglar».", "Elegí «Sin restricciones» (en algunos teléfonos dice «No optimizar» o «Permitir»).", ...(extra ? [extra] : [])];
  }
  if (id === "ahorro") return ios
    ? ["Abrí «Ajustes» → «Batería» y apagá «Modo de bajo consumo»."]
    : ["Deslizá la barra de arriba hacia abajo y apagá «Ahorro de batería» (o «Ahorro de energía»)."];
  if (id === "web") return ["Para que te sigan viendo con la pantalla bloqueada, bajá la app de Olbos (es gratis).", "Si seguís desde el navegador, dejá la pantalla prendida: mientras el mapa está abierto la mantenemos encendida."];
  return [];
};

// El botón de cada problema. Devuelve después de abrir el cartel o el ajuste.
const arreglarTelefono = async (id, e) => {
  try {
    if (!esNativo()) {
      if (id === "permiso" || id === "precisa") { try { await obtenerPosicion({ timeout: 12000, enableHighAccuracy: true, maximumAge: 0 }); } catch (x) { /* el chequeo lo vuelve a mirar */ } }
      return;
    }
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    if (id === "permiso" || id === "fondo") {
      let st = null;
      try { st = await BG.requestPermission(); } catch (x) { st = x; } // rechaza con el estado si no quedó en "siempre"
      const ok = id === "fondo" ? st === BG.AUTHORIZATION_STATUS_ALWAYS : (st === BG.AUTHORIZATION_STATUS_ALWAYS || st === BG.AUTHORIZATION_STATUS_WHEN_IN_USE);
      if (!ok && e.plat === "ios") { try { window.location.href = "app-settings:"; } catch (x) { /* */ } }
      return;
    }
    if (id === "precisa") {
      if (e.plat === "ios") { try { window.location.href = "app-settings:"; } catch (x) { /* */ } return; }
      try { await BG.requestTemporaryFullAccuracy("Rodada"); } catch (x) { try { await BG.requestPermission(); } catch (y) { /* */ } }
      return;
    }
    if (id === "bateria") { await abrirAjusteBateria(); return; }
  } catch (x) { /* el chequeo vuelve a mirar y muestra los pasos a mano */ }
};

const startGeoWatch = async (onPunto, onError) => {
  if (esNativo()) {
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    const consumidor = { onPunto, onError };
    _bgConsumidores.push(consumidor);
    if (!_bgSub) {
      _bgSub = BG.onLocation(
        async (location) => {
          const punto = _bgAPunto(location);
          // ¿Hueco grande desde el último punto entregado? El WebView estuvo congelado:
          // recuperar de la base nativa lo perdido ANTES de entregar el punto nuevo.
          if (punto.t && _bgLastTs && punto.t - _bgLastTs > 60000) await _bgBackfill(BG);
          if (punto.t && punto.t <= _bgLastTs) return; // ya entregado por un backfill
          _bgEntregar(punto);
        },
        (err) => { _bgConsumidores.forEach(cs => { try { cs.onError && cs.onError(err); } catch (e) {} }); }
      );
      try {
        BG.onHttp((r) => {
          if (r && r.status >= 200 && r.status < 300) return;
          try { reportarError("bg_http " + (r && r.status) + " " + String(r && r.responseText).slice(0, 300), null, "BG.onHttp"); } catch (e) {}
        });
      } catch (e) {}
    }
    // Al volver la app al frente (o des-congelarse el WebView), recuperar lo perdido ya.
    if (!_bgResumeListener) {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        _bgResumeListener = await CapApp.addListener("appStateChange", (s) => {
          if (s.isActive && _bgStarted) _bgBackfill(BG);
        });
      } catch (e) {}
    }
    if (!_bgStarted) {
      await BG.ready({
        desiredAccuracy: BG.DESIRED_ACCURACY_NAVIGATION, // máxima precisión (modo navegación vehicular)
        distanceFilter: 8,
        disableElasticity: true,                 // filtro de distancia FIJO (no se achica a baja velocidad → menos zigzag)
        disableStopDetection: true,              // no frenar al detenerse (semáforo, parada)
        pausesLocationUpdatesAutomatically: false,
        stopOnTerminate: true,                   // la app cerrada = GPS apagado (Matías, 08-oct: 5 h de GPS de fondo)
        startOnBoot: false,
        foregroundService: true,
        backgroundPermissionRationale: {
          title: "Permitir ubicación en segundo plano",
          message: "Olbos necesita tu ubicación para grabar el recorrido y compartir tu posición en vivo con la pantalla apagada.",
          positiveAction: "Permitir todo el tiempo",
        },
        notification: { title: "Olbos · ubicación activa", text: "Grabando / compartiendo tu ubicación en segundo plano." },
        maxDaysToPersist: 2,
        // Publicación nativa: el plugin postea los fixes al RPC bg_publicar aunque el
        // WebView esté congelado (ver bgSesionActualizar). De a 10 fixes (~80 m) por POST.
        url: "https://api.olbos.ai/rest/v1/rpc/bg_publicar",
        method: "POST",
        headers: { apikey: BG_RPC_KEY, Authorization: "Bearer " + BG_RPC_KEY, "Content-Type": "application/json" },
        params: { p_token: _bgToken },
        httpRootProperty: "p_locs",
        locationTemplate: '{"lat":"<%= latitude %>","lng":"<%= longitude %>","v":"<%= speed %>","acc":"<%= accuracy %>","head":"<%= heading %>","alt":"<%= altitude %>","t":"<%= timestamp %>"}',
        autoSync: true,
        batchSync: true,
        autoSyncThreshold: 10,
        maxBatchSize: 50,
        maxRecordsToPersist: 5000,
      });
      // Sesión nueva: el backlog nativo de sesiones anteriores no pertenece a esta grabación.
      _bgLastTs = Date.now();
      try { await BG.destroyLocations(); } catch (e) {}
      await BG.start();
      _bgStarted = true;
      try {
        const st = await BG.getProviderState();
        const siempre = !!st && st.status === BG.AUTHORIZATION_STATUS_ALWAYS;
        window.dispatchEvent(new CustomEvent("olbos-gps-fondo", { detail: { ok: siempre, status: st && st.status, enabled: st && st.enabled } }));
      } catch (e) {}
      // Al arrancar, el plugin entra en modo ESTACIONARIO y NO emite ubicaciones continuas
      // hasta que su detección de movimiento decide que arrancaste (quedaba varios minutos
      // "quieto" al empezar a grabar). Forzamos el estado "moving" ya mismo.
      try { await BG.changePace(true); } catch (e) {}
      // Y pedimos un fix inmediato para pintar el primer punto sin esperar al primer onLocation.
      try {
        const loc = await BG.getCurrentPosition({ samples: 1, timeout: 20, persist: true });
        const punto = _bgAPunto(loc);
        if (punto.lat != null) _bgEntregar(punto);
      } catch (e) {}
    }
    return { stop: async () => {
      _bgConsumidores = _bgConsumidores.filter(cs => cs !== consumidor);
      if (_bgConsumidores.length === 0) {
        try { await BG.stop(); } catch (e) {}
        _bgStarted = false;
        if (_bgSub) { try { _bgSub.remove(); } catch (e) {} _bgSub = null; }
      }
    }};
  }
  const wid = navigator.geolocation.watchPosition(
    (p) => onPunto({ lat: p.coords.latitude, lng: p.coords.longitude, v: p.coords.speed || 0, acc: p.coords.accuracy, head: p.coords.heading, alt: (p.coords.altitude != null && isFinite(p.coords.altitude)) ? p.coords.altitude : null, t: p.timestamp }),
    (err) => onError && onError(err),
    { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 }
  );
  return { stop: () => { try { navigator.geolocation.clearWatch(wid); } catch (e) {} } };
};

// ─── Recarga ante chunk viejo (tras un deploy) ───────────────────────────────
// Los chunks lazy (import()) cambian de hash en cada build. Un index cacheado pide
// un .chunk.js que ya no existe → "Loading chunk N failed" / ChunkLoadError. Recargamos
// UNA vez (flag en sessionStorage) para tomar la versión nueva, sin loops.
const esErrorDeChunk = (e) => /loading chunk|chunkloaderror|importing a module script failed|failed to fetch dynamically imported/i.test((e && (e.message || e)) || "");
const recargarSiChunkViejo = (e) => {
  if (!esErrorDeChunk(e)) return false;
  try {
    if (typeof window !== "undefined" && !sessionStorage.getItem("olbos_chunk_reload")) {
      sessionStorage.setItem("olbos_chunk_reload", "1");
      window.location.reload();
      return true;
    }
  } catch (err) {}
  return false;
};
if (typeof window !== "undefined") {
  window.addEventListener("error", (ev) => recargarSiChunkViejo(ev && (ev.error || ev.message)));
  window.addEventListener("unhandledrejection", (ev) => recargarSiChunkViejo(ev && ev.reason));
  // Si la app corrió 10s sin recargar, liberamos el flag (próximo deploy podrá recargar).
  setTimeout(() => { try { sessionStorage.removeItem("olbos_chunk_reload"); } catch (e) {} }, 10000);
}

// Login con Google: en web es el OAuth redirect normal; en la app nativa Google bloquea
// el OAuth en el webview, así que lo abrimos en el navegador del sistema y volvemos a la
// app por deep link (ai.olbos.app://auth-callback), donde se completa la sesión.
const loginGoogle = async () => {
  if (!esNativo()) {
    return supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: "https://olbos.ai" } });
  }
  const { Browser } = await import("@capacitor/browser");
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: "ai.olbos.app://auth-callback", skipBrowserRedirect: true },
  });
  if (error) return { error };
  if (data?.url) await Browser.open({ url: data.url });
  return { error: null };
};

// Login con Apple — SOLO iOS nativo (requisito de App Store por ofrecer login de Google).
// El plugin nativo devuelve el identityToken; usamos un nonce: el HASHEADO va a Apple y el
// CRUDO a Supabase (signInWithIdToken lo re-hashea y compara con el claim del token).
const _sha256Hex = async (str) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("");
};
const loginApple = async () => {
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  const rawNonce = [...crypto.getRandomValues(new Uint8Array(16))].map(b => b.toString(16).padStart(2, "0")).join("");
  const hashedNonce = await _sha256Hex(rawNonce);
  const result = await SignInWithApple.authorize({
    clientId: "ai.olbos.app",        // bundle id (flujo nativo iOS)
    redirectURI: "https://olbos.ai",  // requerido por la firma del plugin; no se usa en nativo
    scopes: "name email",
    nonce: hashedNonce,
  });
  const resp = (result && result.response) || {};
  if (!resp.identityToken) return { error: { message: "No se recibió el token de Apple" } };
  const { error } = await supabase.auth.signInWithIdToken({
    provider: "apple",
    token: resp.identityToken,
    nonce: rawNonce,
  });
  if (error) return { error };
  // Apple solo manda nombre/email en el PRIMER login → lo guardamos en user_metadata (la app lee
  // user_metadata.nombre, igual que el signup por email). Fire-and-forget: el builder de Supabase
  // NO tiene .catch → usamos .then(ok, err) para no cortar la ejecución.
  const nombreApple = [resp.givenName, resp.familyName].filter(Boolean).join(" ").trim();
  if (nombreApple) {
    supabase.auth.updateUser({ data: { nombre: nombreApple } }).then(() => {}, () => {});
  }
  return { error: null };
};

// ─── @USUARIO ÚNICO ──────────────────────────────────────────────────────────
// Los @ no se pueden repetir (pedido de Carlos 04-sep-2026, tras un "charly" clon
// de @Charly): sin espacios, 3-24 caracteres, letras/números/._- y libre en
// perfiles ignorando mayúsculas. La base además tiene índice único + trigger.
const normalizarUsername = (u) => String(u || "").trim().replace(/\s+/g, "");
const validarUsernameDisponible = async (u, miId = null) => {
  const n = normalizarUsername(u);
  if (n.length < 3) return "El @usuario tiene que tener al menos 3 caracteres.";
  if (n.length > 24) return "El @usuario puede tener hasta 24 caracteres.";
  if (!/^[A-Za-z0-9._-]+$/.test(n)) return "El @usuario solo puede tener letras, números, punto, guion o guion bajo (sin espacios ni tildes).";
  try {
    let q = supabase.from("perfiles").select("id").ilike("username", n).limit(1);
    if (miId) q = q.neq("id", miId);
    const { data } = await q;
    if (data && data.length) return `El @${n} ya está en uso — probá otro (por ejemplo @${n}${Math.floor(10 + Math.random() * 90)}).`;
  } catch (e) { /* sin red: que lo frene la base */ }
  return null;
};

// ─── BADGES ──────────────────────────────────────────────────────────────────
const FOUNDER_EMAIL = "cpmoyanob@gmail.com";

const getBadge = (email, username, numero_registro) => {
  if (!email && !username) return null;
  if (email === FOUNDER_EMAIL || ["loscar", "charly"].includes(String(username || "").toLowerCase()))
    return { tipo: "founder", label: "Founder" };
  // Matías (outreach, stands, pruebas de cada versión): el que encabeza la rodada
  if (String(username || "").toLowerCase() === "matiaguilar_26")
    return { tipo: "capitan", label: "Capitán de Olbos" };
  // Original Rider: primeros 300 por número de registro en Supabase
  if (numero_registro && numero_registro <= 300)
    return { tipo: "original", label: "Original Rider" };
  return null;
};

// El @usuario REAL para firmar posts y notificaciones: el de `perfiles`, nunca el
// de user_metadata de Auth (que es el nombre del registro original o el de Google
// y no cambia cuando el rider edita su @ — Carlos publicaba como @Loscar siendo
// @charly desde hacía semanas, 02-sep-2026).
const usernameReal = async (user) => {
  if (!user) return "";
  try {
    const { data } = await supabase.from("perfiles").select("username").eq("id", user.id).maybeSingle();
    if (data?.username) return data.username;
  } catch (e) { /* cae al respaldo */ }
  return user.user_metadata?.nombre || (user.email || "").split("@")[0];
};

// Ícono de MOTO en línea (lucide no tiene: su Bike es una bicicleta y se notaba).
// Mismo lenguaje que lucide: viewBox 24, trazo 2, puntas redondeadas. Perfil
// deportivo: dos ruedas, cuadro en diagonal y columna de dirección con manubrio.
const IconoMoto = ({ size = 16, color = "#111111", strokeWidth = 2, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
    <circle cx="5.5" cy="17" r="3.2" />
    <circle cx="18.5" cy="17" r="3.2" />
    <path d="M5.5 17 L9 11.5 H13.7 L18.5 17" />
    <path d="M13.7 11.5 L15.2 8.4" />
    <path d="M13.8 8.4 H17" />
    <path d="M9 11.5 L7.2 9.6 H4.8" />
  </svg>
);

// Chip estilo etiqueta cosida (manual de marca): negro cálido, mayúsculas
// espaciadas, ícono de línea. Founder lleva el dorado heritage; nada de emojis
// ni colores fuera de paleta (el navy+dorado chillón anterior era pre-rebrand).
const BadgeTag = ({ badge }) => {
  if (!badge) return null;
  const Ico = badge.tipo === "founder" ? Zap : badge.tipo === "capitan" ? Compass : Flag;
  const dorado = badge.tipo === "founder";
  const capitan = badge.tipo === "capitan";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 5, background: C.accent, color: dorado ? "#D9B368" : capitan ? "#F9F5F2" : "#bdb6ab", border: `1px solid ${dorado ? "#8A6D3B88" : capitan ? "rgba(249,245,242,.55)" : "rgba(249,245,242,.25)"}`, letterSpacing: 1.1, textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0 }}>
      <Ico size={9} strokeWidth={2.5} /> {badge.label}
    </span>
  );
};

// ─── MAPA DE TALLERES ─────────────────────────────────────────────────────────
const MapaTalleres = ({ talleres, userPos, filtroCerca, distancia, onSelect }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    const L = window.L;
    if (!L) return;

    const centerLat = userPos?.lat || -34.6037;
    const centerLng = userPos?.lng || -58.3816;
    const map = L.map(mapRef.current, { zoomControl: false, attributionControl: false }).setView([centerLat, centerLng], 12);
    mapInstanceRef.current = map;
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", { maxZoom: 19 }).addTo(map);

    // marcador del usuario
    if (userPos) {
      const myIcon = L.divIcon({
        html: `<div style="width:36px;height:36px;border-radius:50%;background:#111;border:3px solid #111111;box-shadow:0 0 0 5px rgba(255,69,0,.2);display:flex;align-items:center;justify-content:center;color:#fff;font-size:10px;font-weight:800;">Vos</div>`,
        iconSize: [36, 36], iconAnchor: [18, 18], className: ""
      });
      L.marker([userPos.lat, userPos.lng], { icon: myIcon }).addTo(map);
    }

    // talleres
    const filtered = filtroCerca && userPos
      ? talleres.filter(t => distancia(userPos.lat, userPos.lng, t.lat, t.lng) <= 10)
      : talleres;

    filtered.forEach(t => {
      const icon = L.divIcon({
        html: `<div style="width:38px;height:38px;border-radius:50%;background:#111111;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-size:16px;">🔧</div>`,
        iconSize: [38, 38], iconAnchor: [19, 19], className: ""
      });
      L.marker([t.lat, t.lng], { icon }).addTo(map)
        .bindPopup(`<b>${t.nombre}</b><br>${t.dir}<br><span style="color:#1A9E5C;font-weight:700;">⭐ ${t.rating}</span>`)
        .on("click", () => onSelect(t));
    });

    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [userPos, filtroCerca]);

  return <div ref={mapRef} style={{ height: 260, width: "100%", borderRadius: 14, overflow: "hidden", marginBottom: 14, border: `1px solid ${C.border}` }} />;
};

// ─── HELPER: NAVEGACIÓN GPS ──────────────────────────────────────────────────
// En la app nativa el link https de Waze abría primero el navegador y desde ahí
// había que tocar "Abrir Waze": un paso de más arriba de la moto. El esquema
// waze:// abre la app directo. En la web queda el https, que resuelve solo.
const WAZE_BASE = esNativo() ? "waze://?" : "https://waze.com/ul?";

const BotonesNavegacion = ({ direccion, ciudad }) => {
  const query = encodeURIComponent(`${direccion}, ${ciudad || ""}, Argentina`);
  const waze = `${WAZE_BASE}q=${query}&navigate=yes`;
  const maps = `https://maps.google.com/?q=${query}`;
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {/* El navegador propio primero (pedido de Matías: en todos lados donde estén
          Waze/Maps). Sin coords acá: abre el buscador del mapa con la dirección */}
      <button onClick={() => navegarConOlbosBusqueda(`${direccion}, ${ciudad || ""}`)}
        style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.accent, border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer" }}>
        <span style={{ fontSize: 14 }}>🏍️</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Olbos</span>
      </button>
      <a href={waze} target="_blank" rel="noreferrer"
        style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "#00D4FF10", border: "1px solid #00D4FF33", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
        <span style={{ fontSize: 16 }}>🚙</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#00AACC" }}>Waze</span>
      </a>
      <a href={maps} target="_blank" rel="noreferrer"
        style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "#11111110", border: "1px solid #11111133", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
        <span style={{ fontSize: 16 }}>📍</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Maps</span>
      </a>
    </div>
  );
};

// El saludo de dos dedos SIN el ® (pedido de Carlos 30-ago: la manito limpia).
// Versión anterior con ®: v1782417837/gztuneprapvdbf39rt9l.jpg
const LOGO_URL = "https://res.cloudinary.com/dhwhk48wb/image/upload/v1788141318/oyvqzu1tgcjbk1gmh6ap.png";

// ─── OPTIMIZACIÓN DE ENTREGA CLOUDINARY (velocidad estilo Instagram) ──────────
// Reescribe la URL de Cloudinary para servir una versión LIVIANA en vez del
// original de varios MB: f_auto (WebP/AVIF según navegador), q_auto (calidad
// automática) y un tope de ancho. Aplica retroactivo a TODO lo ya subido, sin
// re-subir nada — las transformaciones se resuelven al momento de entregar.
// Bonus clave: f_auto convierte los HEIC de iPhone (que el navegador NO sabe
// mostrar → cuadrado negro) a un formato válido, así que también arregla las
// publicaciones "en negro".
// Cloudinary factura por TRANSFORMACIÓN derivada: cada ancho distinto crea una nueva
// (y f_auto la multiplica por formato). Por eso los anchos se redondean a tres fijos:
// una misma foto genera 3 derivadas como máximo en vez de 11.
const ANCHOS_CLD = [48, 400, 720, 1080];
const snapAncho = (w) => ANCHOS_CLD.find(a => a >= (w || 1080)) || 1080;

const cldOpt = (url, w = 1080) => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("res.cloudinary.com/") || !url.includes("/upload/")) return url;
  if (/\/upload\/[^/]*[qf]_auto/.test(url)) return url; // ya optimizada
  return url.replace("/upload/", `/upload/f_auto,q_auto:eco,w_${snapAncho(w)},c_limit/`);
};
// Thumbnail chico para grillas (galería, previews) — más liviano todavía.
const cldThumb = (url, w = 400) => cldOpt(url, w);
// Poster (primer frame) liviano de un video de Cloudinary, para mostrar algo al
// toque en vez de un rectángulo negro mientras bufferea.
const cldPoster = (url, w = 800) => {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com/")) return undefined;
  return url
    .replace("/upload/", `/upload/so_0,f_auto,q_auto:eco,w_${snapAncho(w)},c_limit/`)
    .replace(/\.(mp4|mov|webm|m4v|avi)(\?.*)?$/i, ".jpg");
};
// Placeholder borroso (LQIP) ultra liviano: miniatura de ~48px que se muestra al
// INSTANTE y se desenfoca por CSS mientras carga la foto real (efecto blur-up de
// Instagram). Pesa <2 KB y se ve apenas abre el feed. Sirve también para el
// primer frame de un video.
const cldBlur = (url, w = 48) => {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com/") || !url.includes("/upload/")) return undefined;
  const esVideo = /\/video\/upload\//.test(url);
  const base = url.replace("/upload/", `/upload/f_auto,q_auto:low,w_48,c_limit${esVideo ? ",so_0" : ""}/`);
  return esVideo ? base.replace(/\.(mp4|mov|webm|m4v|avi)(\?.*)?$/i, ".jpg") : base;
};

// Achica la foto ANTES de subirla: el celular saca archivos de 3-8 MB y eso se paga dos
// veces (storage al guardarlo y bandwidth cada vez que alguien lo mira). A 1600px de lado
// mayor una foto queda en ~300 KB sin diferencia visible en pantalla.
const achicarImagen = async (file, maxLado = 1600) => {
  try {
    if (!file || !(file.type || "").startsWith("image/")) return file;   // videos y otros: sin tocar
    if (file.size < 400 * 1024) return file;                              // ya es liviana
    const img = await new Promise((res, rej) => {
      const i = new Image();
      const u = URL.createObjectURL(file);
      i.onload = () => { URL.revokeObjectURL(u); res(i); };
      i.onerror = () => { URL.revokeObjectURL(u); rej(new Error("no carga")); };
      i.src = u;
    });
    const esc = Math.min(1, maxLado / Math.max(img.width, img.height));
    if (esc >= 1) return file;
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * esc);
    c.height = Math.round(img.height * esc);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise(r => c.toBlob(r, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], String(file.name || "foto").replace(/.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }   // ante cualquier problema (HEIC, etc.) sube el original
};

// ─── COLA DE FOTOS SIN SEÑAL ─────────────────────────────────────────────────
// En la ruta se saca la foto justo donde no hay datos. Antes eso terminaba en un
// "no se pudo subir" y la foto se perdía. Ahora: se captura la posición y la hora
// EN EL MOMENTO, se achica y se guarda en el teléfono (IndexedDB); cuando vuelve la
// señal se sube sola y queda anclada al lugar correcto del recorrido, aunque el
// rider haya cerrado la app o terminado la grabación.
const COLA_DB = "olbos_cola", COLA_STORE = "media";
const colaAbrir = () => new Promise((res, rej) => {
  const r = indexedDB.open(COLA_DB, 1);
  r.onupgradeneeded = () => {
    const db = r.result;
    if (!db.objectStoreNames.contains(COLA_STORE)) db.createObjectStore(COLA_STORE, { keyPath: "id", autoIncrement: true });
  };
  r.onsuccess = () => res(r.result);
  r.onerror = () => rej(r.error);
});
const avisarCola = () => { try { window.dispatchEvent(new CustomEvent("olbos-cola-fotos")); } catch (e) { /* */ } };
const colaGuardar = async (item) => {
  const db = await colaAbrir();
  return new Promise((res, rej) => {
    const tx = db.transaction(COLA_STORE, "readwrite");
    tx.objectStore(COLA_STORE).add(item);
    tx.oncomplete = () => { avisarCola(); res(true); };
    tx.onerror = () => rej(tx.error);
  });
};
const colaListar = async () => {
  try {
    const db = await colaAbrir();
    return await new Promise((res) => {
      const tx = db.transaction(COLA_STORE, "readonly");
      const q = tx.objectStore(COLA_STORE).getAll();
      q.onsuccess = () => res(q.result || []);
      q.onerror = () => res([]);
    });
  } catch (e) { return []; }
};
const colaBorrar = async (id) => {
  try {
    const db = await colaAbrir();
    return await new Promise((res) => {
      const tx = db.transaction(COLA_STORE, "readwrite");
      tx.objectStore(COLA_STORE).delete(id);
      tx.oncomplete = () => { avisarCola(); res(true); };
      tx.onerror = () => res(false);
    });
  } catch (e) { return false; }
};
// Vacía la cola: sube a Cloudinary y engancha cada archivo a su traza. Se llama al
// abrir la app y cuando vuelve la conexión. Si algo falla, el ítem QUEDA en la cola.
let colaCorriendo = false;
const subirColaFotos = async () => {
  if (colaCorriendo) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  colaCorriendo = true;
  try {
    const items = await colaListar();
    for (const it of items) {
      try {
        const fd = new FormData();
        fd.append("file", it.blob);
        fd.append("upload_preset", "olbos_uploads");
        const endpoint = it.isVideo
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const r = await fetch(endpoint, { method: "POST", body: fd });
        const data = await r.json();
        if (!data.secure_url) continue;             // se reintenta la próxima vez
        const nuevo = { url: data.secure_url, type: it.isVideo ? "video" : "image", lat: it.lat, lng: it.lng, ts: it.ts };
        if (it.trazaId) {
          // Releer las fotos de la traza para no pisar lo que se subió mientras tanto
          const { data: fila } = await supabase.from("trazas").select("fotos").eq("id", it.trazaId).maybeSingle();
          const previas = Array.isArray(fila?.fotos) ? fila.fotos : [];
          if (!previas.some(f => f && f.url === nuevo.url)) {
            await supabase.from("trazas").update({ fotos: [...previas, nuevo] }).eq("id", it.trazaId);
          }
        }
        await colaBorrar(it.id);
      } catch (e) { /* sin señal otra vez: queda para el próximo intento */ }
    }
  } finally { colaCorriendo = false; }
};

// ─── PLACA DE GANADOR DE SORTEO (imagen 1080x1350 lista para stories/feed) ────
// Canvas con la estética de marca (negro cálido + crema). Devuelve un Blob png.
const generarPlacaSorteo = async ({ premioNombre, premioFoto, ganadorNombre, ganadorUsername, contexto, porQuien }) => {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const CREMA = "#F9F5F2", MUTED = C.muted;
  ctx.fillStyle = "#111111"; ctx.fillRect(0, 0, W, H);
  ctx.textAlign = "center";
  const wrap = (texto, maxAncho, fuente) => {
    ctx.font = fuente;
    const palabras = String(texto || "").split(/\s+/);
    const lineas = []; let linea = "";
    for (const p of palabras) {
      const prueba = linea ? linea + " " + p : p;
      if (ctx.measureText(prueba).width > maxAncho && linea) { lineas.push(linea); linea = p; }
      else linea = prueba;
    }
    if (linea) lineas.push(linea);
    return lineas.slice(0, 2);
  };
  // wordmark
  ctx.fillStyle = CREMA; ctx.font = "800 52px Archivo, sans-serif";
  ctx.fillText("OLBOS®", W / 2, 110);
  ctx.fillStyle = MUTED; ctx.font = "700 30px Archivo, sans-serif";
  const tituloSep = "S O R T E O   ·   G A N A D O R";
  ctx.fillText(tituloSep, W / 2, 180);
  // foto del premio (si carga; Cloudinary manda CORS así que no ensucia el canvas)
  let fotoOk = false;
  if (premioFoto) {
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image(); i.crossOrigin = "anonymous";
        i.onload = () => res(i); i.onerror = rej;
        i.src = cldOpt(premioFoto, 800);
      });
      const S = 430, x = (W - S) / 2, y = 240, r = 36;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + r, y); ctx.arcTo(x + S, y, x + S, y + S, r); ctx.arcTo(x + S, y + S, x, y + S, r);
      ctx.arcTo(x, y + S, x, y, r); ctx.arcTo(x, y, x + S, y, r); ctx.closePath(); ctx.clip();
      const esc = Math.max(S / img.width, S / img.height);
      ctx.drawImage(img, x + (S - img.width * esc) / 2, y + (S - img.height * esc) / 2, img.width * esc, img.height * esc);
      ctx.restore();
      fotoOk = true;
    } catch (e) { /* sin foto, la placa sale igual */ }
  }
  let yCur = fotoOk ? 770 : 480;
  if (!fotoOk) { ctx.font = "160px serif"; ctx.fillText("🏆", W / 2, yCur); yCur += 130; }
  // premio
  ctx.fillStyle = CREMA;
  const lPremio = wrap(premioNombre, 900, "800 68px Archivo, sans-serif");
  lPremio.forEach(l => { ctx.fillText(l, W / 2, yCur); yCur += 78; });
  yCur += 30;
  // ganador
  ctx.fillStyle = CREMA; ctx.font = "800 84px Archivo, sans-serif";
  const lGanador = wrap(ganadorNombre || ("@" + (ganadorUsername || "ganador")), 940, "800 84px Archivo, sans-serif");
  lGanador.forEach(l => { ctx.fillText(l, W / 2, yCur); yCur += 94; });
  if (ganadorNombre && ganadorUsername) {
    ctx.fillStyle = MUTED; ctx.font = "500 44px Archivo, sans-serif";
    ctx.fillText("@" + ganadorUsername, W / 2, yCur); yCur += 66;
  }
  // contexto (evento o patrocinador)
  if (contexto) {
    ctx.fillStyle = "rgba(249,245,242,.75)";
    const lCtx = wrap(contexto, 900, "600 40px Archivo, sans-serif");
    lCtx.forEach(l => { ctx.fillText(l, W / 2, yCur + 20); yCur += 52; });
  }
  if (porQuien) {
    ctx.fillStyle = MUTED; ctx.font = "500 34px Archivo, sans-serif";
    ctx.fillText("por @" + porQuien, W / 2, yCur + 40); yCur += 40;
  }
  // pie de marca
  ctx.fillStyle = MUTED; ctx.font = "500 32px Archivo, sans-serif";
  ctx.fillText("El camino es el destino · olbos.ai", W / 2, H - 70);
  return await new Promise(res => canvas.toBlob(res, "image/png"));
};

const compartirPlacaSorteo = async (datos) => {
  try {
    const blob = await generarPlacaSorteo(datos);
    if (!blob) throw new Error("sin blob");
    const file = new File([blob], "sorteo-olbos.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Ganador del sorteo" });
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sorteo-olbos.png";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (e) {
    if (e?.name !== "AbortError") alert("No se pudo generar la placa. Probá de nuevo.");
  }
};

// ─── THEME ───────────────────────────────────────────────────────────────────
// Paleta Olbos (manual de marca Bo Project, jun 2026): monocromática negro cálido +
// crema "White Rider". accent = negro #111 (reemplaza el naranja). Semánticos sobrios.
// MODO OSCURO (fase 1, oculto tras selector solo-admin): mismos tokens en negro
// cálido. El tema se resuelve UNA vez al cargar y cambiarlo recarga la página —
// simple y sin estados intermedios. En claro, todo resuelve a los valores de siempre.
const TEMAS = {
  claro: {
    bg: "#F9F5F2", surface: "#F1ECE6", card: "#FFFFFF", card2: "#F6F1EB",
    border: "#E7E1DA", accent: "#111111", accentLo: "#1111110C",
    gold: "#8A6D3B", goldLo: "#8A6D3B0C",
    text: "#111111", muted: "#8A857F", faint: "#CFC8BF",
    green: "#2F7D4F", yellow: "#B07D1A", red: "#B3372C",
    blue: "#3B5566", blueLo: "#3B55660F",
    yellowLo: "#B07D1A0F",
    greenLo: "#2F7D4F0F", redLo: "#B3372C0F",
  },
  oscuro: {
    // Negro cálido "cuero de noche": nunca negro puro, siempre con temperatura.
    // accent oscuro es un negro ELEVADO (no crema): así los cientos de botones con
    // texto blanco hardcodeado siguen siendo legibles sin tocarlos uno por uno.
    bg: "#131110", surface: "#1D1A16", card: "#25211B", card2: "#211D18",
    border: "#3A342C", accent: "#302B24", accentLo: "#F9F5F20E",
    gold: "#C2A26B", goldLo: "#C2A26B14",
    text: "#F3EEE7", muted: "#A69F94", faint: "#57503F",
    green: "#5CA57C", yellow: "#D9A94A", red: "#D96A5E",
    blue: "#7C97A8", blueLo: "#7C97A81A",
    yellowLo: "#D9A94A14",
    greenLo: "#5CA57C14", redLo: "#D96A5E14",
  },
};
const temaPreferido = () => { try { return localStorage.getItem("olbos_tema") || "claro"; } catch (e) { return "claro"; } };
const resolverTema = (pref) => {
  if (pref === "oscuro") return "oscuro";
  if (pref === "sistema") {
    try { if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "oscuro"; } catch (e) { /* */ }
  }
  return "claro";
};
const TEMA_ACTIVO = resolverTema(temaPreferido());
const C = TEMAS[TEMA_ACTIVO];
const cambiarTema = (pref) => {
  // Cambiar recarga la app entera. Con un recorrido grabando o en pausa, la recarga
  // tira el grabador y lo que no llegó a guardarse (06-oct-2026: Carlos pausó en una
  // parada, fue a cambiar a oscuro y el recorrido quedó en el limbo).
  if (window.olbosRecEstado === "grabando" || window.olbosRecEstado === "pausada") {
    alert("Tenés un recorrido grabando o en pausa. Finalizalo antes de cambiar la apariencia, así no se pierde nada.\n\nPara ver el mapa oscuro mientras rodás, tocá 🌙 en el mapa.");
    return;
  }
  try { localStorage.setItem("olbos_tema", pref); } catch (e) { /* */ }
  window.location.reload();
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,600&display=swap');
  /* Modo oscuro fase 2 (04-sep-2026): los controles nativos (date/time/select/textarea)
     tomaban el blanco del navegador con texto crema encima. color-scheme hace que el
     navegador pinte pickers y scrollbars según el tema, y los inputs sin fondo explícito
     heredan card/text. Los inline styles siguen mandando donde existan. */
  :root{ color-scheme: ${TEMA_ACTIVO === "oscuro" ? "dark" : "light"}; }
  input, textarea, select { background-color: ${C.card}; color: ${C.text}; }
  input::placeholder, textarea::placeholder { color: ${C.muted}; opacity: 1; }
  input[type="date"], input[type="time"], input[type="datetime-local"] { color-scheme: ${TEMA_ACTIVO === "oscuro" ? "dark" : "light"}; }
  /* Safe areas (iOS notch / Dynamic Island / home indicator). En iOS los env() dan el
     inset real (requiere viewport-fit=cover); en web/Android viejo dan 0 → todo queda igual.
     En Android, Capacitor inyecta --safe-area-inset-* (SystemBars) por el bug de env() en
     WebViews < 140, así que lo aprovechamos con fallback a env() y a 0. */
  :root{
    --sat: var(--safe-area-inset-top, env(safe-area-inset-top, 0px));
    --sab: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));
    --sal: var(--safe-area-inset-left, env(safe-area-inset-left, 0px));
    --sar: var(--safe-area-inset-right, env(safe-area-inset-right, 0px));
    /* Padding-top de los headers: mantiene los 44px de hoy en web/Android y, en iOS,
       usa el inset del status bar + 12px si es mayor (Dynamic Island ~59px). */
    --header-pt: max(44px, calc(var(--sat) + 12px));
  }
  *{box-sizing:border-box;margin:0;padding:0}
  /* overflow-x hidden: ningún elemento puede empujar la página más allá del viewport
     (en iOS quedaba "corrida" y había que panear a la izquierda) */
  html,body{overflow-x:hidden;max-width:100vw}
  body{background:${C.bg};color:${C.text};font-family:'Archivo',sans-serif;-webkit-font-smoothing:antialiased}
  input,textarea,select{font-family:'Archivo',sans-serif;outline:none}
  button{cursor:pointer;border:none;background:none;font-family:'Archivo',sans-serif}
  ::-webkit-scrollbar{width:3px}
  ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
  @keyframes bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }
  @keyframes ping { 0%{transform:scale(1);opacity:.8} 100%{transform:scale(2);opacity:0} }
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes sosPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
  @keyframes ripple{0%{opacity:.6;transform:scale(1)}100%{opacity:0;transform:scale(2.8)}}
  @keyframes ping{0%{opacity:.8;transform:scale(1)}100%{opacity:0;transform:scale(2.2)}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .sin-scrollbar::-webkit-scrollbar{display:none}
  /* ── Escritorio (≥1024 px): la app deja de ser una columna de 430 px ──
     Pedido de Carlos (02-sep y 17-sep-2026): organizar viajes, gestionar eventos,
     espacios comerciales y campañas de mail tiene que ser cómodo desde la compu.
     Barra lateral + columna central de 600 px + modales centrados de 640 px.
     Los selectores por atributo agarran los estilos inline por su valor: es la
     forma de convertir de una vez las ~95 hojas con maxWidth: 430 sin tocarlas. */
  @media (min-width: 1024px) {
    .olbos-escritorio [style*="max-width: 430px"] { max-width: 640px !important; }
    .olbos-escritorio [style*="max-width: 480px"] { max-width: 680px !important; }
    .olbos-escritorio [style*="position: fixed"][style*="align-items: flex-end"] { align-items: center !important; padding: 24px 0; }
    .olbos-escritorio [style*="border-radius: 20px 20px 0"],
    .olbos-escritorio [style*="border-radius: 18px 18px 0"],
    .olbos-escritorio [style*="border-radius: 16px 16px 0"],
    .olbos-escritorio [style*="border-radius: 24px 24px 0"] { border-radius: 20px !important; }
    .olbos-escritorio [style*="max-height: 90vh"], .olbos-escritorio [style*="height: 90vh"] { max-height: 92vh !important; height: auto !important; }
    .olbos-escritorio .nav-escritorio-item:hover { background: ${C.surface}; }
    .olbos-escritorio ::-webkit-scrollbar { width: 8px; }
    .olbos-escritorio ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }
  }
  @keyframes tabDesdeDerecha{from{transform:translateX(9%);opacity:.5}to{transform:none;opacity:1}}
  @keyframes tabDesdeIzquierda{from{transform:translateX(-9%);opacity:.5}to{transform:none;opacity:1}}
  .tab-entra-der{animation:tabDesdeDerecha .24s ease-out}
  .tab-entra-izq{animation:tabDesdeIzquierda .24s ease-out}
  img{-webkit-user-drag:none;user-drag:none;-webkit-touch-callout:none;user-select:none}
  @keyframes heartPop{0%{transform:scale(1)}50%{transform:scale(1.4)}100%{transform:scale(1)}}
  /* corazón grande del doble toque sobre la foto (estilo Instagram) */
  @keyframes likeBurst{0%{opacity:0;transform:scale(.3)}15%{opacity:1;transform:scale(1.2)}30%{transform:scale(.95)}45%{transform:scale(1.05)}70%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(1.15)}}
  .like-burst{animation:likeBurst .9s ease-out forwards}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  /* Mapa de noche (Carlos, 06-oct): el mapa vectorial de Google dibuja calles y nombres
     en un canvas WebGL; invertirlo y rotar el tono lo deja oscuro como Waze de noche
     (parques verdes, agua azul). Los marcadores (moto, riders) son DOM aparte: no se tocan. */
  .olbos-mapa-oscuro canvas{filter:invert(1) hue-rotate(180deg) brightness(.8) contrast(.95) saturate(.7)}
  .fade-up{animation:fadeUp .25s ease}
`;

// ─── UTILS ───────────────────────────────────────────────────────────────────
const OTag = ({ children, color }) => (
  <span style={{
    fontSize: 10, fontWeight: 500, letterSpacing: 0.3,
    padding: "3px 8px", borderRadius: 4,
    color: color || "#444",
    background: color ? color + "12" : "transparent",
    border: `1px solid ${color ? color + "30" : "#DADADA"}`,
    whiteSpace: "nowrap", display: "inline-block",
  }}>{children}</span>
);

const Pill = ({ children, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500,
    border: `1px solid ${active ? C.text : C.border}`,
    background: "transparent",
    color: active ? C.text : C.muted, fontWeight: active ? 700 : 400, transition: "all .2s", whiteSpace: "nowrap", fontFamily: "'Inter'",
  }}>{children}</button>
);

const Stars = ({ n, size = 13 }) => (
  <span style={{ color: C.gold, fontSize: size, letterSpacing: 1 }}>
    {"★".repeat(Math.round(n))}{"☆".repeat(5 - Math.round(n))}
  </span>
);

const Divider = () => <div style={{ height: 1, background: C.border, margin: "16px 0" }} />;

const SectionHead = ({ title, sub, action, onAction }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14 }}>
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: -0.3 }}>{title}</h2>
      {sub && <p style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{sub}</p>}
    </div>
    {action && <button onClick={onAction} style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>{action}</button>}
  </div>
);

const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} style={{
    background: C.card, borderRadius: 14, border: `1px solid ${C.border}`,
    overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", ...style, cursor: onClick ? "pointer" : "default",
  }}>{children}</div>
);

const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>}
    <input {...props} style={{
      width: "100%", padding: "10px 14px", borderRadius: 8,
      background: C.surface, border: `1px solid ${C.border}`,
      color: C.text, fontSize: 14, outline: "none",
      transition: "border-color .2s", ...props.style,
    }} />
  </div>
);

const Select = ({ label, options, value, onChange }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>}
    <select value={value} onChange={onChange} style={{
      width: "100%", padding: "10px 14px", borderRadius: 8,
      background: C.surface, border: `1px solid ${C.border}`,
      color: C.text, fontSize: 14, outline: "none",
    }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Btn = ({ children, variant = "primary", onClick, full, small, style = {}, disabled = false }) => {
  const styles = {
    primary:     { background: C.muted, color: "#fff", border: "1px solid #8a857f" },
    outline:     { background: "transparent", border: `1.5px solid ${C.border}`, color: C.muted },
    outlineDark: { background: "transparent", border: `1.5px solid ${C.text}`, color: C.text },
    ghost:       { background: C.surface, color: C.text, border: `1px solid ${C.border}` },
    accent:      { background: C.accent, color: "#fff", border: `1px solid ${C.accent}` },
    danger:      { background: C.red, color: "#fff", border: `1px solid ${C.red}` },
    gold:        { background: "#1A1A1A", color: "#C8922A", border: `1.5px solid #C8922A44` },
    green:       { background: C.green, color: "#fff", border: `1px solid ${C.green}` },
  };
  return (
    // Radio generoso, peso medio y letterSpacing negativo: menos "bloque" y más iOS
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? "8px 16px" : "14px 20px",
      borderRadius: small ? 10 : 14, fontWeight: 600,
      fontSize: small ? 12.5 : 15, letterSpacing: "-0.01em",
      width: full ? "100%" : "auto", transition: "all .15s",
      cursor: disabled ? "default" : "pointer",
      ...styles[variant] || styles.primary, ...style,
    }}>{children}</button>
  );
};

const BadgeRank = ({ rank }) => {
  const map = { 1: ["🥇", C.gold], 2: ["🥈", "#A8A8A8"], 3: ["🥉", "#CD7F32"] };
  const [icon, color] = map[rank] || ["", C.muted];
  return icon ? <span style={{ fontSize: 16 }}>{icon}</span> : null;
};

// ─── DATA ─────────────────────────────────────────────────────────────────────
const MOTOS_USER = [
  { id: 1, marca: "BMW", modelo: "R1250GS", año: 2022, color: "Blanca", cc: 1254 },
  { id: 2, marca: "KTM", modelo: "790 Adventure", año: 2021, color: "Naranja", cc: 799 },
];

const EVENTS = [];

const EVENTS_PASADOS = [];
// Columnas de la LISTA de eventos: todo menos lo pesado (descripcion, puntos, polyline,
// medias), que se trae al abrir la ficha (DetalleEventoModal recarga la fila completa) y en
// el mapa en vivo (GrupoRodadaOverlay). Con 150 eventos y select("*") la agenda eran
// cientos de KB por apertura; con la celda saturada en el predio, 10-20 s de "Cargando".
const COLS_EVENTO_LISTA = "id, creator_id, titulo, fecha, hora, desde, hasta, km, dificultad, tipo_camino, max_riders, precio, foto_url, lat_inicio, lng_inicio, created_at, tipo, fecha_fin, lugar, precio_entrada, lat, lng, equipo, apoyo_ruta, ida_vuelta, telefono, grupo_id, inscripcion_config, estado, tiene_sorteo, checkin_manual, checkin_abierto, track_oficial_id, voz_config, checkin_modo, pago_link, pago_info, sorteo_elegibilidad, destacado, inscripcion_cerrada, evento_padre_id, vistas, visibilidad_tipo, inscripcion, negocio_id, cobra_negocio_id, publicado_por";

const ROUTES = [];

// ─── Ocultar contenido de bloqueados/silenciados ─────────────────────────────
// Cache module-level de los user_ids cuyo contenido NO se muestra al usuario
// actual (bloqueados + silenciados). El feed y los comentarios filtran con esto.
// RPC get_ocultos → { bloqueados: [ids], silenciados: [ids] }.
let _ocultosCache = null;
const getOcultos = async () => {
  if (_ocultosCache) return _ocultosCache;
  try {
    const { data: raw } = await supabase.rpc("get_ocultos");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    _ocultosCache = {
      bloqueados: Array.isArray(d?.bloqueados) ? d.bloqueados : [],
      silenciados: Array.isArray(d?.silenciados) ? d.silenciados : [],
    };
  } catch (e) { _ocultosCache = { bloqueados: [], silenciados: [] }; }
  return _ocultosCache;
};
const idsOcultos = async () => { const o = await getOcultos(); return [...o.bloqueados, ...o.silenciados]; };
const invalidarOcultos = () => { _ocultosCache = null; };

// Renderiza un texto con menciones @usuario (color naranja) y #[tipo:nombre](id) (chips azules clickeables)
// Fecha de HOY en el huso del usuario, formato YYYY-MM-DD. OJO: toISOString()
// da el día UTC — a las 21:00 de Argentina ya es "mañana" en UTC, y eso hacía
// desaparecer los eventos de hoy de la agenda a la noche y reiniciaba el
// contador de "riders hoy" del panel a las 21:00.
const hoyLocalISO = (d = new Date()) => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().split("T")[0];
};

const renderTextoConMenciones = (texto, onMentionUser) => {
  if (!texto) return null;
  // regex que captura: @usuario o #[tipo:nombre](id)
  const regex = /(@\w+)|(#\[(ruta|moto|evento|negocio):([^\]]+)\]\(([^)]+)\))/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  while ((m = regex.exec(texto)) !== null) {
    if (m.index > lastIdx) parts.push({ type: "text", value: texto.slice(lastIdx, m.index) });
    if (m[1]) {
      parts.push({ type: "user", value: m[1] });
    } else {
      parts.push({ type: "entity", tipo: m[3], nombre: m[4], id: m[5] });
    }
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < texto.length) parts.push({ type: "text", value: texto.slice(lastIdx) });

  const iconos = { ruta: "🛣️", moto: "🏍️", evento: "📅", negocio: "📍" };

  return parts.map((p, i) => {
    if (p.type === "text") return <React.Fragment key={i}>{p.value}</React.Fragment>;
    if (p.type === "user") {
      return <span key={i} style={{ color: C.text, fontWeight: 700, cursor: "pointer" }}
        onClick={() => onMentionUser?.(p.value.slice(1))}>{p.value}</span>;
    }
    // entity chip — azul como hashtag
    return (
      <span key={i} style={{ color: "#1A73E8", fontWeight: 600, cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("olbos-navegar", { detail: { tipo: p.tipo, id: p.id, nombre: p.nombre } }));
        }}>
        {iconos[p.tipo]} {p.nombre}
      </span>
    );
  });
};


const FEED = [];

const TALLERES = [];

// Placeholder "sin foto" para modelos sin imagen real o con hotlink roto: ícono de moto
// blanca sobre gris claro, embebido como SVG (data URI) → nunca se rompe, no depende de nada.
const MOTO_FALLBACK = "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20240%20180%22%3E%3Crect%20width%3D%22240%22%20height%3D%22180%22%20fill%3D%22%23dcdce1%22%2F%3E%3Cpath%20transform%3D%22translate(84%2C54)%20scale(3)%22%20fill%3D%22%23ffffff%22%20d%3D%22M19.44%209.03L15.41%205H11v2h3.59l2%202H5c-2.8%200-5%202.2-5%205s2.2%205%205%205c2.46%200%204.45-1.69%204.9-4h1.65l2.77-2.77c-.21.54-.32%201.14-.32%201.77%200%202.8%202.2%205%205%205s5-2.2%205-5c0-2.79-2.21-5-5.23-4.97zM7.82%2015C7.4%2016.15%206.28%2017%205%2017c-1.63%200-3-1.37-3-3s1.37-3%203-3c1.28%200%202.4.85%202.82%202H5v2h2.82zM19%2017c-1.63%200-3-1.37-3-3s1.37-3%203-3%203%201.37%203%203-1.37%203-3%203z%22%2F%3E%3C%2Fsvg%3E";
// Fotos de stock genéricas que quedaron como placeholders → tratarlas como "sin foto".
const MOTO_GENERICAS = ["1547549082", "1609630875171", "1568772585407", "1558618666"];
const imgMoto = (url) => (!url || (url.includes("images.unsplash.com") && MOTO_GENERICAS.some(g => url.includes(g)))) ? MOTO_FALLBACK : url;

const MOTOS_DB = [

  // ═══════════════════════════════════════════════════════
  // BMW
  // ═══════════════════════════════════════════════════════
  { id:"bmw1", marca:"BMW", modelo:"R 1300 GS", año:2024, cc:1300, tipo:"Adventure", peso:237, pot:145, par:149, consumo:"5.0", precio_ar:"USD 31.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732556/catalogo/bmw1.jpg", credito:"Foto: Cjp24 · CC BY-SA 4.0", pros:["Motor boxer más potente","ASA opcional","Conectividad total"], contras:["Precio altísimo","Mantenimiento complejo"], desc:"La GS renació. El 1300 es más ligera, más potente y más tecnológica.", rating:4.8 },
  { id:"bmw2", marca:"BMW", modelo:"R 1300 GS Adventure", año:2024, cc:1300, tipo:"Adventure", peso:268, pot:145, par:149, consumo:"5.2", precio_ar:"USD 35.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732566/catalogo/bmw2.jpg", pros:["Depósito 30L","Suspensión semi-activa","La adventure más completa"], contras:["La más pesada del segmento","Precio muy alto"], desc:"La GS para el viaje largo. Depósito de 30L y suspensión semi-activa.", rating:4.8 },
  { id:"bmw3", marca:"BMW", modelo:"S 1000 RR", año:2024, cc:999, tipo:"Sport", peso:197, pot:210, par:113, consumo:"6.2", precio_ar:"USD 29.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732566/catalogo/bmw3.jpg", credito:"Foto: Stefan Krause, Germany · CC BY-SA 3.0", pros:["210cv","Electrónica WorldSBK","Diseño icónico"], contras:["Solo para expertos","Mantenimiento carísimo"], desc:"La superbike alemana. 210cv, electrónica de derivada directa del WorldSBK.", rating:4.7 },
  { id:"bmw4", marca:"BMW", modelo:"S 1000 XR", año:2024, cc:999, tipo:"Sport Touring", peso:220, pot:170, par:113, consumo:"5.8", precio_ar:"USD 26.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732566/catalogo/bmw4.jpg", credito:"Foto: Andrew Napier · CC BY 2.0", pros:["170cv en formato tourer","Posición erguida","Electrónica completa"], contras:["Precio alto","Pesada en maniobras"], desc:"La sport tourer de BMW. El inline-4 de la S1000RR en formato de viaje largo.", rating:4.6 },
  { id:"bmw5", marca:"BMW", modelo:"M 1000 RR", año:2024, cc:999, tipo:"Sport", peso:192, pot:212, par:113, consumo:"6.5", precio_ar:"USD 45.000", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/2021_BMW_M_1000_RR.jpg/800px-2021_BMW_M_1000_RR.jpg", pros:["La superbike más extrema de BMW","Alas de fibra de carbono","Edición limitada"], contras:["Precio estratosférico","Solo para circuito"], desc:"La M de las motos. BMW M Performance en su máxima expresión.", rating:4.9 },
  { id:"bmw6", marca:"BMW", modelo:"R 12 nineT", año:2024, cc:1170, tipo:"Custom", peso:222, pot:109, par:116, consumo:"4.9", precio_ar:"USD 21.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733867/catalogo/bmw6.jpg", credito:"Foto: Wikisympathisant · CC BY-SA 4.0", pros:["Estética atemporal","Boxer clásico","Alta base para customizar"], contras:["Sin asistencias modernas","Precio elevado"], desc:"La nineT renovada. El boxer 1170 en su versión más honesta.", rating:4.4 },
  { id:"bmw7", marca:"BMW", modelo:"F 900 R", año:2024, cc:895, tipo:"Naked", peso:209, pot:105, par:93, consumo:"5.0", precio_ar:"USD 14.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732567/catalogo/bmw7.jpg", credito:"Foto: Wiki LIC · CC BY-SA 4.0", pros:["105cv accesibles","Precio razonable para BMW","Electrónica completa"], contras:["Sin pantalla TFT grande"], desc:"La naked de media cilindrada de BMW. 105cv, buen precio.", rating:4.3 },
  { id:"bmw8", marca:"BMW", modelo:"F 900 XR", año:2024, cc:895, tipo:"Adventure", peso:219, pot:105, par:93, consumo:"5.1", precio_ar:"USD 16.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732608/catalogo/bmw8.png", credito:"Foto: MotorNL · CC BY 3.0", pros:["Versátil en ciudad y ruta","Gran pantalla TFT","Precio razonable"], contras:["Sin capacidad off-road real"], desc:"La adventure media de BMW. Cómoda, versátil y bien equipada.", rating:4.4 },
  { id:"bmw9", marca:"BMW", modelo:"G 310 GS", año:2024, cc:313, tipo:"Adventure", peso:170, pot:34, par:28, consumo:"3.5", precio_ar:"USD 7.200", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732567/catalogo/bmw9.jpg", credito:"Foto: Wikisympathisant · CC BY-SA 4.0", pros:["Precio accesible","Peso liviano","Ideal para principiantes"], contras:["Motor modesto"], desc:"La entrada al mundo BMW. 313cc, 170kg, ideal para el primer adventure.", rating:4.1 },

  // ═══════════════════════════════════════════════════════
  // HONDA
  // ═══════════════════════════════════════════════════════
  { id:"hon1", marca:"Honda", modelo:"Africa Twin 1100 Adventure Sports DCT", año:2024, cc:1084, tipo:"Adventure", peso:226, pot:102, par:105, consumo:"5.4", precio_ar:"USD 19.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733867/catalogo/hon1.jpg", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["DCT único en el segmento","Versátil en todo terreno","Confiabilidad Honda"], contras:["Motor menos refinado que europeas","Pantalla pequeña"], desc:"La doble cilindro más vendida del mundo. DCT único en el segmento.", rating:4.6 },
  { id:"hon2", marca:"Honda", modelo:"CBR 1000 RR-R Fireblade SP", año:2024, cc:1000, tipo:"Sport", peso:200, pot:217, par:113, consumo:"6.8", precio_ar:"USD 34.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732567/catalogo/hon2.jpg", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["217cv motor MotoGP derivado","Electrónica Öhlins","La más potente de Honda"], contras:["Solo para expertos","Precio muy alto"], desc:"La Fireblade extrema. 217cv con tecnología del RC213V.", rating:4.9 },
  { id:"hon3", marca:"Honda", modelo:"CB 1000 Hornet", año:2024, cc:1000, tipo:"Naked", peso:201, pot:151, par:104, consumo:"5.8", precio_ar:"USD 16.500", img:"https://powersports.honda.com/motorcycle/standard/cb750-hornet/2025/-/media/products/family/cb750-hornet/trim-hero/gallery/cb750-hornet/2025/matte-black-metallic/2025-cb750-hornet-matte_black_metallic-gallery-01.png", pros:["151cv accesibles","Inline-4 suave","Electrónica completa"], contras:["Competencia fuerte"], desc:"El Hornet grande. Inline-4 de 1000cc en formato naked equilibrado.", rating:4.5 },
  { id:"hon4", marca:"Honda", modelo:"CB 750 Hornet", año:2024, cc:755, tipo:"Naked", peso:190, pot:92, par:75, consumo:"4.8", precio_ar:"USD 11.200", img:"https://powersports.honda.com/motorcycle/standard/cb500f/2026/-/media/products/family/cb500f/trim-hero/gallery/cb500-hornet/2026/matte-black-metallic/2026-cb500-hornet-matte_black_metallic-gallery-01.png", pros:["Precio excelente","Twin 270° carismático","Maniobrabilidad urbana"], contras:["Sin TFT","Frenos mejorables"], desc:"El Hornet volvió. Twin 270°, 92cv, 190kg.", rating:4.4 },
  { id:"hon5", marca:"Honda", modelo:"CB 500 F", año:2024, cc:471, tipo:"Naked", peso:189, pot:48, par:44, consumo:"4.1", precio_ar:"USD 7.800", img:"https://powersports.honda.com/motorcycle/standard/cb300r/2026/-/media/products/family/cb300r/trim-hero/gallery/cb300r/2026/matte-black-metallic/2026-cb300r-matte_black_metallic-gallery-01.png", pros:["Precio accesible","Confiabilidad Honda","Ideal A2"], contras:["Sin electrónica avanzada"], desc:"La naked confiable de Honda. 471cc, 48cv.", rating:4.2 },
  { id:"hon6", marca:"Honda", modelo:"CB 300 R", año:2024, cc:286, tipo:"Naked", peso:143, pot:31, par:28, consumo:"3.6", precio_ar:"USD 6.200", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732570/catalogo/hon6.jpg", credito:"Foto: Thesupermat · CC BY-SA 4.0", pros:["Muy liviana","Diseño premium","Ideal para ciudad"], contras:["No sirve en ruta larga"], desc:"La naked pequeña con diseño premium. 143kg, neo-sports café.", rating:4.1 },
  { id:"hon7", marca:"Honda", modelo:"NT 1100", año:2024, cc:1084, tipo:"Sport Touring", peso:238, pot:102, par:104, consumo:"5.0", precio_ar:"USD 16.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732570/catalogo/hon7.jpg", credito:"Foto: CheminduCret · CC BY-SA 4.0", pros:["DCT disponible","Motor Africa Twin","Precio razonable"], contras:["Diseño conservador"], desc:"El sport tourer accesible de Honda con motor del Africa Twin.", rating:4.3 },
  { id:"hon8", marca:"Honda", modelo:"GL 1800 Gold Wing Tour", año:2024, cc:1833, tipo:"Touring", peso:380, pot:128, par:170, consumo:"6.5", precio_ar:"USD 38.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732570/catalogo/hon8.jpg", credito:"Foto: Tarrakaner · CC BY-SA 4.0", pros:["El touring más lujoso","6 cilindros planos","Audio premium DCT"], contras:["Precio altísimo","Peso intimidante"], desc:"El Rey del touring. 6 cilindros en línea plana, lujo absoluto.", rating:4.8 },
  { id:"hon9", marca:"Honda", modelo:"XRE 300", año:2024, cc:286, tipo:"Trail", peso:153, pot:27, par:27, consumo:"3.4", precio_ar:"USD 5.900", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732570/catalogo/hon9.jpg", pros:["Trail urbana versátil","Bajo mantenimiento","Precio accesible"], contras:["No para off-road exigente"], desc:"La trail urbana de Honda. Versátil y económica.", rating:4.0 },
  { id:"hon10", marca:"Honda", modelo:"Forza 750", año:2024, cc:745, tipo:"Scooter", peso:214, pot:59, par:69, consumo:"4.1", precio_ar:"USD 12.500", img:"https://powersports.honda.com/motorcycle/standard/cb1000-hornet-sp/2025/-/media/products/family/cb1000-hornet-sp/trim-hero/gallery/cb1000-hornet-sp/2025/matte-black-metallic/2025-cb1000-hornet-sp-matte_black_metallic-gallery-01.png", pros:["Maletero enorme","DCT de serie","Comodidad touring"], contras:["Pesado en ciudad"], desc:"El maxi-scooter premium de Honda con DCT.", rating:4.3 },

  // ═══════════════════════════════════════════════════════
  // YAMAHA
  // ═══════════════════════════════════════════════════════
  { id:"yam1", marca:"Yamaha", modelo:"MT-09", año:2024, cc:890, tipo:"Naked", peso:193, pot:119, par:93, consumo:"5.1", precio_ar:"USD 12.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732571/catalogo/yam1.jpg", credito:"Foto: Markscheider · CC BY 3.0", pros:["Triple 3 cilindros único","Carácter brutal","Precio razonable"], contras:["Puede intimidar","Consumo alto en Sport"], desc:"El dark side de Yamaha. El triple 890cc es el motor más carismático del segmento.", rating:4.6 },
  { id:"yam2", marca:"Yamaha", modelo:"MT-09 SP", año:2024, cc:890, tipo:"Naked", peso:193, pot:119, par:93, consumo:"5.1", precio_ar:"USD 15.200", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732571/catalogo/yam2.jpg", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["Suspensión Öhlins","Quickshifter","Versión tope de gama"], contras:["Precio premium"], desc:"La MT-09 con suspensión Öhlins y quickshifter de serie.", rating:4.7 },
  { id:"yam3", marca:"Yamaha", modelo:"MT-10 SP", año:2024, cc:998, tipo:"Naked", peso:210, pot:166, par:112, consumo:"6.0", precio_ar:"USD 21.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732609/catalogo/yam3.jpg", credito:"Foto: Kenneth Allen · CC BY-SA 2.0", pros:["R1 motor en naked","Electrónica completa","166cv controlables"], contras:["Precio alto"], desc:"El hyper-naked con motor del R1. 166cv, Öhlins, quickshifter.", rating:4.7 },
  { id:"yam4", marca:"Yamaha", modelo:"R1", año:2024, cc:998, tipo:"Sport", peso:200, pot:200, par:113, consumo:"6.5", precio_ar:"USD 23.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733868/catalogo/yam4.jpg", credito:"Foto: Jakub 'Flyz1' Maciejewski · CC BY-SA 3.0", pros:["200cv electrónicamente gestionados","IMU 6 ejes","El inline-4 más refinado"], contras:["Cara","Exige mucho del piloto"], desc:"La superbike japonesa por excelencia. 200cv, electrónica MotoGP.", rating:4.8 },
  { id:"yam5", marca:"Yamaha", modelo:"Ténéré 700", año:2024, cc:689, tipo:"Adventure", peso:204, pot:73, par:68, consumo:"4.5", precio_ar:"USD 13.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732571/catalogo/yam5.jpg", credito:"Foto: Wikisympathisant · CC BY-SA 4.0", pros:["Ligereza excepcional","Mecánica simple","Precio accesible"], contras:["Sin asistencias avanzadas"], desc:"La adventure honesta. 700cc, 204kg, mecánica simple.", rating:4.7 },
  { id:"yam6", marca:"Yamaha", modelo:"Ténéré 700 World Raid", año:2024, cc:689, tipo:"Adventure", peso:220, pot:73, par:68, consumo:"4.7", precio_ar:"USD 17.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732573/catalogo/yam6.jpg", credito:"Foto: MotorideSA · CC BY-SA 4.0", pros:["Depósito 23L","Suspensión off-road","Electrónica mejorada"], contras:["Más cara y pesada"], desc:"La Ténéré radical. Más ripio, más distancia.", rating:4.6 },
  { id:"yam7", marca:"Yamaha", modelo:"Tracer 9 GT+", año:2024, cc:890, tipo:"Sport Touring", peso:220, pot:119, par:93, consumo:"5.3", precio_ar:"USD 19.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732573/catalogo/yam7.jpg", credito:"Foto: AVMOTO · CC BY-SA 4.0", pros:["Triple 890 en tourer","Radar ACC","Suspensión semi-activa"], contras:["Precio alto"], desc:"El sport tourer del triple. Radar, cruise control, semi-activa.", rating:4.7 },
  { id:"yam8", marca:"Yamaha", modelo:"XSR 900", año:2024, cc:890, tipo:"Clásica", peso:193, pot:119, par:93, consumo:"5.1", precio_ar:"USD 14.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732573/catalogo/yam8.png", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["Estética retro-moderna","Motor MT-09","Personalizable"], contras:["Precio similar a MT-09"], desc:"El triple de la MT-09 vestido de retro.", rating:4.6 },
  { id:"yam9", marca:"Yamaha", modelo:"FZ 25", año:2024, cc:249, tipo:"Naked", peso:153, pot:27, par:20, consumo:"3.5", precio_ar:"USD 4.800", img:"https://motos0km.com.ar/models/yamaha-fz-25-card-mobile-003399.jpg", pros:["Precio muy accesible","Liviana","Ideal para ciudad"], contras:["Motor limitado","Sin ABS"], desc:"La naked accesible de Yamaha para el mercado latinoamericano.", rating:4.0 },

  // ═══════════════════════════════════════════════════════
  // KTM
  // ═══════════════════════════════════════════════════════
  { id:"ktm1", marca:"KTM", modelo:"1290 Super Duke R EVO", año:2024, cc:1301, tipo:"Naked", peso:179, pot:180, par:140, consumo:"6.5", precio_ar:"USD 29.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732573/catalogo/ktm1.jpg", credito:"Foto: Lutzeputz · CC BY-SA 3.0", pros:["180cv en naked","Suspensión semi-activa WP","La hyper-naked definitiva"], contras:["Precio extremo","Solo para expertos"], desc:"La beast de KTM. 180cv, 179kg. El hyper-naked más extremo del mercado.", rating:4.9 },
  { id:"ktm2", marca:"KTM", modelo:"1290 Super Adventure S", año:2024, cc:1301, tipo:"Adventure", peso:228, pot:160, par:138, consumo:"5.8", precio_ar:"USD 27.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732574/catalogo/ktm2.jpg", credito:"Foto: San Andreas · CC BY-SA 3.0", pros:["160cv — la adventure más potente","Radar ACC","WP semi-activa"], contras:["Precio premium","Consumo alto"], desc:"La aventura al extremo. 160cv, radar ACC, suspensión semi-activa.", rating:4.7 },
  { id:"ktm3", marca:"KTM", modelo:"1290 Super Adventure R", año:2024, cc:1301, tipo:"Adventure", peso:223, pot:160, par:138, consumo:"5.9", precio_ar:"USD 28.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732574/catalogo/ktm3.jpg", credito:"Foto: KTM AG · CC BY-SA 4.0", pros:["Off-road extremo","WP Xplor largo recorrido","160cv para el barro"], contras:["Precio máximo"], desc:"La 1290 para off-road duro. Suspensión de largo recorrido.", rating:4.8 },
  { id:"ktm4", marca:"KTM", modelo:"990 Duke", año:2024, cc:947, tipo:"Naked", peso:175, pot:123, par:100, consumo:"5.4", precio_ar:"USD 16.000", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_ktm-naked-bikes-990-duke-right-side-view-studio_%23SALL_%23AEPI_%23V1.png", pros:["175kg — la más liviana del segmento","Twin LC8 brutal","Agilidad extrema"], contras:["Solo para pilotos expertos"], desc:"La naked más liviana y extrema de KTM. 175kg con 123cv.", rating:4.7 },
  { id:"ktm5", marca:"KTM", modelo:"890 Duke R", año:2024, cc:889, tipo:"Naked", peso:166, pot:121, par:99, consumo:"5.2", precio_ar:"USD 15.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732574/catalogo/ktm5.jpg", credito:"Foto: Matti Blume · CC BY-SA 4.0", pros:["WP Apex ajustable","166kg absurdos","Quickshifter bidireccional"], contras:["Exigente en ciudad"], desc:"La R de la Duke 890. Suspensión WP, 166kg.", rating:4.7 },
  { id:"ktm6", marca:"KTM", modelo:"890 Adventure R", año:2024, cc:889, tipo:"Adventure", peso:189, pot:105, par:100, consumo:"5.0", precio_ar:"USD 18.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732574/catalogo/ktm6.jpg", pros:["Mejor off-road media cilindrada","WP largo recorrido","Peso contenido"], contras:["Precio elevado"], desc:"La adventure media de KTM para off-road serio.", rating:4.7 },
  { id:"ktm12", marca:"KTM", modelo:"790 Adventure R", año:2024, cc:799, tipo:"Adventure", peso:189, pot:95, par:88, consumo:"4.9", precio_ar:"USD 15.500", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/8/87/KTM_790_Adventure.jpg/960px-KTM_790_Adventure.jpg", pros:["Twin LC8 carismático","Suspensión WP de largo recorrido","Capacidad off-road real"], contras:["Mantenimiento complejo","Servicio especializado"], desc:"La hermana liviana de la 890. Mismo ADN aventurero, motor 790 twin LC8, más accesible.", rating:4.6 },
  { id:"ktm7", marca:"KTM", modelo:"790 Duke", año:2024, cc:799, tipo:"Naked", peso:169, pot:105, par:87, consumo:"4.9", precio_ar:"USD 13.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732575/catalogo/ktm7.jpg", credito:"Foto: 28pg · CC BY-SA 4.0", pros:["169kg — increíblemente liviana","Twin LC8 105cv","La más divertida del segmento"], contras:["Sin TFT grande"], desc:"La naked que cambió el juego. 169kg, 105cv, la más ágil.", rating:4.6 },
  { id:"ktm8", marca:"KTM", modelo:"390 Duke", año:2024, cc:399, tipo:"Naked", peso:149, pot:45, par:39, consumo:"3.8", precio_ar:"USD 8.500", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_KTM-2026-naked-bikes-390-duke-black-right-side-view_%23SALL_%23AEPI_%23V1.png", pros:["Precio/prestaciones líder","TFT con Bluetooth","Suspensión WP"], contras:["Fallas eléctricas reportadas"], desc:"La naked más vendida de media cilindrada. 399cc, TFT.", rating:4.3 },
  { id:"ktm9", marca:"KTM", modelo:"390 Adventure", año:2024, cc:399, tipo:"Adventure", peso:163, pot:45, par:37, consumo:"3.6", precio_ar:"USD 9.200", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732610/catalogo/ktm9.png", credito:"Foto: KTM AG · CC BY-SA 4.0", pros:["Adventure liviana","Maniobabilidad increíble","Precio accesible"], contras:["Motor limitado en ruta larga"], desc:"La entrada al mundo adventure. 163kg, maniobabilidad increíble.", rating:4.2 },
  { id:"ktm10", marca:"KTM", modelo:"RC 390", año:2024, cc:399, tipo:"Sport", peso:147, pot:45, par:37, consumo:"4.0", precio_ar:"USD 9.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732575/catalogo/ktm10.jpg", credito:"Foto: Dennis Bratland · CC BY-SA 4.0", pros:["La entry sport más liviana","TFT y electrónica completa","Ready to race"], contras:["Incómoda en ciudad"], desc:"La pequeña supersport de KTM. 147kg, lista para pista.", rating:4.3 },
  { id:"ktm11", marca:"KTM", modelo:"250 Duke", año:2024, cc:249, tipo:"Naked", peso:147, pot:30, par:24, consumo:"3.0", precio_ar:"USD 5.800", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_KTM-2026-naked-bikes-250-duke-black-right-side-view_%23SALL_%23AEPI_%23V1.png", pros:["Precio accesible","Muy liviana","TFT de serie"], contras:["Motor modesto"], desc:"La entrada al mundo KTM. 249cc, TFT de serie.", rating:4.0 },

  // ═══════════════════════════════════════════════════════
  // TRIUMPH
  // ═══════════════════════════════════════════════════════
  { id:"tri1", marca:"Triumph", modelo:"Speed Triple 1200 RS", año:2024, cc:1160, tipo:"Naked", peso:198, pot:180, par:125, consumo:"6.5", precio_ar:"USD 26.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732575/catalogo/tri1.jpg", credito:"Foto: MotorideSA · CC BY-SA 4.0", pros:["180cv en naked","Chasis Öhlins completo","El triple más potente"], contras:["Solo para expertos","Precio alto"], desc:"La naked extrema de Triumph. 180cv, suspensión Öhlins.", rating:4.6 },
  { id:"tri2", marca:"Triumph", modelo:"Tiger 1200 GT Explorer", año:2024, cc:1160, tipo:"Adventure", peso:260, pot:150, par:130, consumo:"5.8", precio_ar:"USD 28.000", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Triumph_Tiger_1200_GT_Explorer_2022.jpg/800px-Triumph_Tiger_1200_GT_Explorer_2022.jpg", pros:["Triple 1200 único","Suspensión Showa semi-activa","Radar ACC"], contras:["Precio alto","Peso considerable"], desc:"La Tiger grande. Triple 1200 en versión touring extrema.", rating:4.7 },
  { id:"tri3", marca:"Triumph", modelo:"Tiger 900 Rally Pro", año:2024, cc:888, tipo:"Adventure", peso:201, pot:95, par:87, consumo:"5.2", precio_ar:"USD 21.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733868/catalogo/tri3.jpg", credito:"Foto: Vauxford · CC BY-SA 4.0", pros:["Triple 900 carismático","Suspensión Showa ajustable","Electrónica completa"], contras:["Precio premium"], desc:"La aventurera británica con alma de triple.", rating:4.7 },
  { id:"tri4", marca:"Triumph", modelo:"Street Triple 765 RS", año:2024, cc:765, tipo:"Naked", peso:168, pot:130, par:80, consumo:"5.5", precio_ar:"USD 16.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733869/catalogo/tri4.jpg", credito:"Foto: Ohgud.kibn7ewyu2 · CC BY-SA 4.0", pros:["Triple 765 incomparable","168kg extraordinario","Electrónica de superbike"], contras:["Precio elevado"], desc:"La naked británica definitiva. El triple 765 suena y gira como nada.", rating:4.8 },
  { id:"tri5", marca:"Triumph", modelo:"Bonneville T120", año:2024, cc:1200, tipo:"Clásica", peso:228, pot:80, par:105, consumo:"4.8", precio_ar:"USD 17.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733870/catalogo/tri5.jpg", credito:"Foto: George E. Koronaios · CC BY-SA 4.0", pros:["Autenticidad británica","Twin 1200 torquero","Calidad excepcional"], contras:["Sin asistencias modernas","Pesada"], desc:"La Bonnie original. 60 años de historia en cada curva.", rating:4.5 },
  { id:"tri6", marca:"Triumph", modelo:"Rocket 3 R", año:2024, cc:2458, tipo:"Custom", peso:291, pot:167, par:221, consumo:"7.8", precio_ar:"USD 31.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732578/catalogo/tri6.jpg", credito:"Foto: Mr.choppers · CC BY-SA 3.0", pros:["221Nm — el triple más grande del mundo","2458cc exclusividad absoluta","Parece custom pero gira increíble"], contras:["Peso impresionante","Precio altísimo"], desc:"El motor más grande en producción: 2458cc, triple, 221Nm.", rating:4.8 },

  // ═══════════════════════════════════════════════════════
  // KAWASAKI
  // ═══════════════════════════════════════════════════════
  { id:"kaw1", marca:"Kawasaki", modelo:"ZX-10R", año:2024, cc:998, tipo:"Sport", peso:207, pot:203, par:114, consumo:"6.8", precio_ar:"USD 24.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732611/catalogo/kaw1.png", pros:["203cv — superbike más potente de Japón","Electrónica WorldSBK","Récord en Nürburgring"], contras:["Solo para expertos","Cara"], desc:"La Ninja extrema. 203cv derivados directamente del WorldSBK.", rating:4.8 },
  { id:"kaw2", marca:"Kawasaki", modelo:"H2 SX SE", año:2024, cc:998, tipo:"Sport Touring", peso:260, pot:200, par:137, consumo:"7.0", precio_ar:"USD 35.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732578/catalogo/kaw2.jpg", credito:"Foto: INigel · CC BY-SA 4.0", pros:["200cv con compresor en tourer","Radar ACC","El único del mundo"], contras:["Precio extremo","Consumo alto"], desc:"El sport tourer supercharged. El único del mundo con compresor centrífugo.", rating:4.9 },
  { id:"kaw3", marca:"Kawasaki", modelo:"Z 1000", año:2024, cc:1043, tipo:"Naked", peso:221, pot:142, par:111, consumo:"5.8", precio_ar:"USD 16.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733872/catalogo/kaw3.jpg", credito:"Foto: Transformer18 from Switzerland · CC BY 2.0", pros:["142cv brutales","Diseño agresivo icónico","Inline-4 refinado"], contras:["Peso elevado"], desc:"La naked de 1000cc de Kawasaki. 142cv, diseño intimidante.", rating:4.4 },
  { id:"kaw4", marca:"Kawasaki", modelo:"Z 900", año:2024, cc:948, tipo:"Naked", peso:193, pot:125, par:98, consumo:"5.4", precio_ar:"USD 12.900", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732578/catalogo/kaw4.jpg", credito:"Foto: T.doi.z900 · CC BY-SA 4.0", pros:["125cv accesibles","Precio competitivo","Carácter equilibrado"], contras:["No brilla en nada específico"], desc:"La naked pragmática de Kawasaki. 125cv, precio razonable.", rating:4.3 },
  { id:"kaw5", marca:"Kawasaki", modelo:"Versys 1000 SE", año:2024, cc:1043, tipo:"Touring", peso:255, pot:120, par:102, consumo:"5.5", precio_ar:"USD 20.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732580/catalogo/kaw5.jpg", credito:"Foto: Tokumeigakarinoaoshima · CC BY-SA 4.0", pros:["Suspensión semi-activa Showa","Comodidad extrema","Gran depósito"], contras:["Peso elevado"], desc:"La tourer equilibrada. 1000cc, suspensión semi-activa.", rating:4.4 },
  { id:"kaw6", marca:"Kawasaki", modelo:"Ninja 650", año:2024, cc:649, tipo:"Sport", peso:193, pot:68, par:65, consumo:"4.5", precio_ar:"USD 9.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732612/catalogo/kaw6.jpg", credito:"Foto: A7N8X · CC BY-SA 4.0", pros:["Precio accesible","Twin agradable","Versátil"], contras:["No emociona"], desc:"La Ninja accesible. 650cc, precio justo, versatilidad total.", rating:4.2 },
  { id:"kaw7", marca:"Kawasaki", modelo:"KLX 300", año:2024, cc:292, tipo:"Enduro", peso:128, pot:28, par:25, consumo:"2.9", precio_ar:"USD 5.200", img:"https://motos0km.com.ar/models/kawasaki-klx-300-card-mobile-00b300.jpg", pros:["Peso de pluma","Mecánica simple","Matriculable"], contras:["No sirve en autopista"], desc:"La dual sport accesible de Kawasaki.", rating:4.2 },

  // ═══════════════════════════════════════════════════════
  // DUCATI
  // ═══════════════════════════════════════════════════════
  { id:"duc1", marca:"Ducati", modelo:"Panigale V4 S", año:2024, cc:1103, tipo:"Sport", peso:195, pot:215, par:124, consumo:"7.0", precio_ar:"USD 42.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732580/catalogo/duc1.jpg", credito:"Foto: Matti Blume · CC BY-SA 4.0", pros:["215cv V4","La superbike italiana definitiva","Alas aerodinámicas"], contras:["Precio extremo","Solo para circuito"], desc:"La superbike más bella del mundo. 215cv, V4 Desmosedici.", rating:4.9 },
  { id:"duc2", marca:"Ducati", modelo:"Streetfighter V4 S", año:2024, cc:1103, tipo:"Naked", peso:193, pot:208, par:123, consumo:"6.8", precio_ar:"USD 39.000", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Ducati_Streetfighter_V4_S_2020.jpg/800px-Ducati_Streetfighter_V4_S_2020.jpg", pros:["208cv en naked","Alas más agresivas del mercado","V4 y electrónica completa"], contras:["Precio altísimo","Solo para expertos"], desc:"El hyper-naked definitivo. La Panigale sin carenado.", rating:4.9 },
  { id:"duc3", marca:"Ducati", modelo:"Multistrada V4 S", año:2024, cc:1158, tipo:"Adventure", peso:243, pot:170, par:125, consumo:"6.0", precio_ar:"USD 35.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732580/catalogo/duc3.jpg", credito:"Foto: AVMOTO · CC BY-SA 4.0", pros:["170cv V4","Radar ACC","Öhlins semi-activa"], contras:["La más cara del segmento"], desc:"La superdeportiva disfrazada de adventure.", rating:4.8 },
  { id:"duc4", marca:"Ducati", modelo:"DesertX", año:2024, cc:937, tipo:"Adventure", peso:223, pot:110, par:92, consumo:"5.5", precio_ar:"USD 23.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732583/catalogo/duc4.jpg", credito:"Foto: Artem Lepesin · CC BY-SA 4.0", pros:["L-Twin en adventure","Diseño radical","Largo recorrido"], contras:["Precio alto"], desc:"Ducati en el desierto. El 937 en formato adventure radical.", rating:4.5 },
  { id:"duc5", marca:"Ducati", modelo:"Monster 937", año:2024, cc:937, tipo:"Naked", peso:188, pot:111, par:93, consumo:"5.6", precio_ar:"USD 19.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733873/catalogo/duc5.jpg", pros:["188kg muy liviana","L-Twin italiano","Diseño atemporal"], contras:["Precio elevado"], desc:"El Monster nunca pasa de moda. 937cc, 111cv, 188kg.", rating:4.5 },
  { id:"duc6", marca:"Ducati", modelo:"Scrambler Icon", año:2024, cc:803, tipo:"Clásica", peso:186, pot:73, par:65, consumo:"4.7", precio_ar:"USD 14.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732583/catalogo/duc6.jpg", credito:"Foto: MotorideSA · CC BY-SA 4.0", pros:["Estética icónica","Accesible en Ducati","Ideal para customizar"], contras:["Sin asistencias avanzadas"], desc:"La Scrambler de Ducati. Estilo y actitud.", rating:4.3 },

  // ═══════════════════════════════════════════════════════
  // SUZUKI
  // ═══════════════════════════════════════════════════════
  { id:"suz1", marca:"Suzuki", modelo:"Hayabusa", año:2024, cc:1340, tipo:"Sport", peso:264, pot:190, par:150, consumo:"7.2", precio_ar:"USD 25.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733873/catalogo/suz1.jpg", credito:"Foto: AlbertoSossella · CC BY-SA 4.0", pros:["El hyperbike más famoso","1340cc suave y brutal","Electrónica moderna"], contras:["Peso alto","Ya no es la más rápida"], desc:"La leyenda viva. El Hayabusa sigue siendo icónico 25 años después.", rating:4.7 },
  { id:"suz2", marca:"Suzuki", modelo:"GSX-R 1000R", año:2024, cc:999, tipo:"Sport", peso:203, pot:202, par:117, consumo:"6.8", precio_ar:"USD 22.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732583/catalogo/suz2.jpg", credito:"Foto: Thesupermat · CC BY-SA 4.0", pros:["202cv VVT","Motor MotoGP derivado","Referente del segmento"], contras:["Electrónica algo anticuada"], desc:"La superbike de Suzuki. VVT, 202cv, herencia del MotoGP.", rating:4.5 },
  { id:"suz3", marca:"Suzuki", modelo:"GSX-S1000", año:2024, cc:999, tipo:"Naked", peso:214, pot:152, par:106, consumo:"5.8", precio_ar:"USD 14.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733874/catalogo/suz3.jpg", credito:"Foto: Cjp24 · CC BY-SA 4.0", pros:["Motor GSX-R en naked","Precio excelente","Electrónica completa"], contras:["Diseño divisivo"], desc:"El motor de la GSX-R en formato naked. Rendimiento de superbike.", rating:4.4 },
  { id:"suz4", marca:"Suzuki", modelo:"V-Strom 1050 DE", año:2024, cc:1037, tipo:"Adventure", peso:229, pot:107, par:100, consumo:"5.3", precio_ar:"USD 17.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733875/catalogo/suz4.jpg", credito:"Foto: Maaj01 · CC BY-SA 4.0", pros:["La más equilibrada del segmento","Precio justo","Motor confiable"], contras:["Sin electrónica sofisticada"], desc:"La adventure pragmática. Confiabilidad japonesa, precio justo.", rating:4.4 },
  { id:"suz5", marca:"Suzuki", modelo:"GSX-8S", año:2024, cc:776, tipo:"Naked", peso:202, pot:83, par:78, consumo:"4.9", precio_ar:"USD 10.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732583/catalogo/suz5.png", credito:"Foto: Wikimedia Commons · CC BY 3.0", pros:["Precio competitivo","Twin 270° agradable"], contras:["No tan carismática"], desc:"La naked accesible de Suzuki. 776cc twin, buen precio.", rating:4.1 },

  // ═══════════════════════════════════════════════════════
  // HARLEY-DAVIDSON
  // ═══════════════════════════════════════════════════════
  { id:"hd1", marca:"Harley-Davidson", modelo:"Road Glide", año:2024, cc:1923, tipo:"Touring", peso:380, pot:96, par:166, consumo:"6.8", precio_ar:"USD 36.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732584/catalogo/hd1.jpg", credito:"Foto: User:Lightburst · CC BY-SA 4.0", pros:["El touring americano definitivo","Milwaukee-Eight 121","Audio Boom GTS"], contras:["Peso monumental","Precio altísimo"], desc:"El rey del asfalto americano. 380kg, audio premium.", rating:4.6 },
  { id:"hd2", marca:"Harley-Davidson", modelo:"Sportster S", año:2024, cc:1252, tipo:"Custom", peso:228, pot:121, par:127, consumo:"6.1", precio_ar:"USD 24.000", img:"https://harley-davidson.com.ar/web/wp-content/uploads/2025/02/2026-sportster-s-m04b-motorcycle-nav.jpg", pros:["Revolution Max moderno","La Harley más dinámica","Electrónica completa"], contras:["Precio alto"], desc:"La Harley moderna. Revolution Max 1250.", rating:4.4 },
  { id:"hd3", marca:"Harley-Davidson", modelo:"Street Bob 114", año:2024, cc:1868, tipo:"Custom", peso:289, pot:93, par:155, consumo:"6.5", precio_ar:"USD 25.500", img:"https://harley-davidson.com.ar/web/wp-content/uploads/2025/02/2026-street-bob-m44lc-motorcycle-nav.jpg", pros:["Estilo bobber puro","Milwaukee-Eight 114","Comunidad H-D"], contras:["Sin asistencias modernas"], desc:"El bobber de Milwaukee. Minimalismo y torque.", rating:4.5 },
  { id:"hd4", marca:"Harley-Davidson", modelo:"Fat Boy 114", año:2024, cc:1868, tipo:"Custom", peso:317, pot:93, par:155, consumo:"6.6", precio_ar:"USD 27.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732584/catalogo/hd4.jpg", credito:"Foto: Terragio67 · CC BY-SA 4.0", pros:["El custom más reconocible","Llantas de disco icónicas","Torque brutal"], contras:["No para curvas","Peso extremo"], desc:"El Fat Boy. Las llantas de disco más famosas de la historia.", rating:4.6 },
  { id:"hd5", marca:"Harley-Davidson", modelo:"Pan America 1250 Special", año:2024, cc:1252, tipo:"Adventure", peso:245, pot:150, par:128, consumo:"6.0", precio_ar:"USD 28.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732584/catalogo/hd5.jpg", credito:"Foto: Olinone · CC BY-SA 4.0", pros:["Adventure real de Harley","Suspensión adaptativa","Revolution Max en off-road"], contras:["Precio alto"], desc:"Harley en el adventure. Revolution Max 1250 con suspensión adaptativa.", rating:4.5 },

  // ═══════════════════════════════════════════════════════
  // ROYAL ENFIELD
  // ═══════════════════════════════════════════════════════
  { id:"re1", marca:"Royal Enfield", modelo:"Himalayan 450", año:2024, cc:452, tipo:"Adventure", peso:196, pot:40, par:40, consumo:"4.2", precio_ar:"USD 7.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732584/catalogo/re1.jpg", credito:"Foto: Rouv1505 · CC BY-SA 4.0", pros:["Motor 450 nuevo","Precio accesible","Capaz en off-road ligero"], contras:["Potencia limitada"], desc:"La Himalayan creció. 450cc, motor nuevo, más capaz.", rating:4.3 },
  { id:"re2", marca:"Royal Enfield", modelo:"Continental GT 650", año:2024, cc:648, tipo:"Clásica", peso:198, pot:47, par:52, consumo:"4.5", precio_ar:"USD 9.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732585/catalogo/re2.jpg", credito:"Foto: Cjp24 · CC BY-SA 4.0", pros:["Twin 650 suave","Estética café racer","Precio razonable"], contras:["Potencia modesta"], desc:"El café racer de Royal Enfield. El twin 650 en versión deportiva.", rating:4.2 },
  { id:"re3", marca:"Royal Enfield", modelo:"Meteor 350", año:2024, cc:349, tipo:"Clásica", peso:191, pot:21, par:27, consumo:"3.5", precio_ar:"USD 6.200", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732585/catalogo/re3.jpg", credito:"Foto: Jean-Paul Corlin · CC BY-SA 4.0", pros:["Estética cruiser clásica","Motor suave","Precio accesible"], contras:["Poca potencia"], desc:"La cruiser clásica accesible. Estilo y confort.", rating:4.1 },
  { id:"re4", marca:"Royal Enfield", modelo:"Hunter 350", año:2024, cc:349, tipo:"Naked", peso:181, pot:20, par:27, consumo:"3.4", precio_ar:"USD 5.800", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732585/catalogo/re4.png", credito:"Foto: Pintu dasaundhi · CC BY-SA 4.0", pros:["La más liviana de RE","Diseño moderno","Precio muy accesible"], contras:["Motor muy limitado"], desc:"La naked de Royal Enfield. Ágil y estilosa.", rating:4.0 },

  // ═══════════════════════════════════════════════════════
  // CF MOTO
  // ═══════════════════════════════════════════════════════
  // Los 19 de la línea oficial argentina, con specs y fotos del concesionario
  // oficial (cfmotomartinez.com.ar). Las seis que estaban cargadas tenían datos y
  // nombres de otros mercados: acá no se venden como "800MT Sport/Touring" ni
  // "700CL-X Heritage" sino Explorer, MT-X y CL-X Sport/Adventure.
  { id:"cfm1", marca:"CF Moto", modelo:"800NK", año:2024, cc:799, tipo:"Naked", peso:186, pot:100, par:77, consumo:"5.0", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2025/12/800NK-Blanca.webp", pros:["100 cv del bicilíndrico 800","TFT de 7 pulgadas","ABS en curva"], contras:["Red de service todavía en armado"], desc:"La naked grande de CF Moto. 100 cv, ABS en curva y TFT de 7\".", rating:4.2 },
  { id:"cfm2", marca:"CF Moto", modelo:"675NK", año:2024, cc:675, tipo:"Naked", pot:95, par:70, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2026/05/CFMoto-675NK-azul-blanco.png", pros:["Tricilíndrico de 95 cv","Poco común en el segmento","Equipamiento completo"], contras:["No publican el peso"], desc:"La naked de tres cilindros de CF Moto. 675cc y 95 cv.", rating:4.2 },
  { id:"cfm3", marca:"CF Moto", modelo:"650NK", año:2024, cc:649, tipo:"Naked", peso:206, pot:60.3, par:56, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/650NK-01-400x400.png", pros:["Bicilíndrico de 650 accesible","Buena para el día a día","Apta para principiantes"], contras:["206 kg"], desc:"La naked de 650 de CF Moto, de las más vendidas de la marca.", rating:4.0 },
  { id:"cfm4", marca:"CF Moto", modelo:"450NK", año:2024, cc:450, tipo:"Naked", pot:49.6, par:39, consumo:"3.8", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2025/12/CFMOTO-450NK-01.jpg", pros:["Bicilíndrico de 49,6 cv","Embrague antirrebote","TFT de serie"], contras:["No publican el peso"], desc:"La naked de 450 de CF Moto. Bicilíndrica y con embrague antirrebote.", rating:4.1 },
  { id:"cfm5", marca:"CF Moto", modelo:"300NK", año:2024, cc:292, tipo:"Naked", peso:151, pot:27.5, par:25, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/300NK-01-400x400.png", pros:["151 kg, muy manejable","Modos Sport y Eco","Buena primera moto de marca"], contras:["27,5 cv"], desc:"La naked chica de CF Moto. 151 kg y modos de potencia.", rating:4.0 },
  { id:"cfm6", marca:"CF Moto", modelo:"800MT-X", año:2024, cc:799, tipo:"Adventure", peso:196, pot:95, par:87, consumo:"5.1", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2026/05/CFMoto-800MT-X-azul.png", pros:["87 Nm, la de más torque de la marca","196 kg para una adventure de 800","Equipamiento completo"], contras:["Red de service en armado"], desc:"La adventure de 800 más liviana de CF Moto. 95 cv y 87 Nm.", rating:4.3 },
  { id:"cfm7", marca:"CF Moto", modelo:"800MT Explorer", año:2024, cc:799, tipo:"Adventure", peso:231, par:77, pot:"", consumo:"5.2", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2025/12/CFMOTO-800MT-Explorer-2.webp", pros:["La más equipada para viajar","Bicilíndrico de 800","Protecciones de serie"], contras:["231 kg"], desc:"La 800MT preparada para viaje largo, con equipamiento completo.", rating:4.2 },
  { id:"cfm8", marca:"CF Moto", modelo:"700MT ADV", año:2024, cc:693, tipo:"Adventure", pot:"", par:"", peso:"", consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2026/07/cfmotomartinez-700mt-adv-black-hero.png", pros:["Llantas de rayos","Alforjas y defensas de serie","Preparada para tierra"], contras:["Ficha técnica incompleta en el sitio"], desc:"La 700MT en versión aventurera, con llantas de rayos y alforjas.", rating:4.1 },
  { id:"cfm9", marca:"CF Moto", modelo:"700MT", año:2024, cc:693, tipo:"Adventure", peso:218, par:68, pot:74, consumo:"4.9", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/700MT-01-400x400.png", pros:["74 cv del bicilíndrico 700","Buena para ruta y ripio","Precio competitivo"], contras:["218 kg"], desc:"La adventure de 700 de CF Moto. 74 cv y 68 Nm.", rating:4.1 },
  { id:"cfm10", marca:"CF Moto", modelo:"650MT", año:2024, cc:649, tipo:"Adventure", pot:61, par:56, peso:"", consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2025/12/CFMOTO-650MT-00.webp", pros:["Adventure de 650 accesible","Postura cómoda","Apta para A2"], contras:["No publican el peso"], desc:"La adventure de 650 de CF Moto, pensada para ruta.", rating:4.0 },
  { id:"cfm11", marca:"CF Moto", modelo:"450MT", año:2024, cc:450, tipo:"Adventure", peso:175, par:44, pot:43.5, consumo:"3.7", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/09/CFMOTO-450MT-02.webp", pros:["175 kg, de las más livianas del segmento","Rueda de 21 adelante","Bicilíndrica"], contras:["43,5 cv"], desc:"La adventure media de CF Moto. 175 kg y rueda de 21 adelante.", rating:4.3 },
  { id:"cfm12", marca:"CF Moto", modelo:"650GT", año:2024, cc:649, tipo:"Sport Touring", peso:226, pot:60, par:56, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/650GT-01-400x400.png", pros:["Protección aerodinámica de serie","Cómoda para ruta","Precio competitivo"], contras:["226 kg"], desc:"La sport touring de CF Moto. Carenado completo y postura cómoda.", rating:4.0 },
  { id:"cfm13", marca:"CF Moto", modelo:"700CL-X Sport", año:2024, cc:693, tipo:"Clásica", peso:205, pot:75, par:68, consumo:"4.6", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/700CLX-SPORT-01-400x400.png", pros:["75 cv del twin 700","Estética café racer","Suspensiones de buen nivel"], contras:["205 kg"], desc:"La CL-X en versión sport, con aire de café racer.", rating:4.2 },
  { id:"cfm14", marca:"CF Moto", modelo:"700CL-X Adventure", año:2024, cc:"", tipo:"Scrambler", peso:204, pot:"", par:"", consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2025/12/CFMOTO-700CLX-ADV-00.png", pros:["Estética scrambler","Neumáticos mixtos","Misma base que la CL-X Sport"], contras:["Ficha técnica incompleta en el sitio"], desc:"La CL-X en versión scrambler, con neumáticos mixtos.", rating:4.1 },
  { id:"cfm15", marca:"CF Moto", modelo:"450CL-C", año:2024, cc:449, tipo:"Custom", peso:181, pot:40, par:42, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2026/05/CFMoto-450CLC-2.png", pros:["Custom liviana, 181 kg","Bicilíndrica de 450","Asiento bajo"], contras:["40 cv"], desc:"La custom chica de CF Moto. 449cc bicilíndrica y 181 kg.", rating:4.0 },
  { id:"cfm16", marca:"CF Moto", modelo:"300CL-X", año:2024, cc:292, tipo:"Clásica", peso:155, par:25, pot:"", consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/300CLX-01-400x400.png", pros:["155 kg, muy manejable","Estética neo-retro","Buena primera moto"], contras:["No publican la potencia"], desc:"La neo-retro chica de CF Moto. 292cc y 155 kg.", rating:4.0 },
  { id:"cfm17", marca:"CF Moto", modelo:"675SR-R", año:2024, cc:675, tipo:"Sport", pot:95, par:70, peso:"", consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2026/05/CFMoto-675SRR-gris.png", pros:["Tricilíndrico de 95 cv","Deportiva pura","Poco común en el segmento"], contras:["No publican el peso"], desc:"La deportiva de tres cilindros de CF Moto. 675cc y 95 cv.", rating:4.3 },
  { id:"cfm18", marca:"CF Moto", modelo:"450SR", año:2024, cc:450, tipo:"Sport", peso:168, pot:50, par:39, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/03/450SR-01-400x400.png", pros:["50 cv y 168 kg","Bicilíndrica","Carenado completo"], contras:["Postura deportiva, incómoda en ciudad"], desc:"La deportiva de 450 de CF Moto. 50 cv en 168 kg.", rating:4.2 },
  { id:"cfm19", marca:"CF Moto", modelo:"300SR", año:2024, cc:300, tipo:"Sport", peso:165, pot:29, par:25.3, consumo:"", img:"https://cfmotomartinez.com.ar/wp-content/uploads/2024/07/300-SR-Pista-1295x600.webp", pros:["Deportiva chica con carenado completo","165 kg","Buena para arrancar en pista"], contras:["29 cv"], desc:"La deportiva de entrada de CF Moto. 300cc con carenado completo.", rating:4.0 },

  // ═══════════════════════════════════════════════════════
  // ZONTES
  // ═══════════════════════════════════════════════════════
  // Los 14 que trae Beta Motor, con specs y fotos de zontes.com.ar. Las cuatro que
  // ya estaban traían cilindrada y potencia algo distintas a las oficiales y el
  // nombre de dos de ellas no era el que usa la marca acá (GK350, V350).
  { id:"zon1", marca:"Zontes", modelo:"703 RR", año:2024, cc:699, tipo:"Sport", pot:95, par:72, peso:195, consumo:"5.0", precio_ar:"USD 12.000", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-703RR.png", pros:["Twin 700 de 95 cv","Diseño supersport","TFT y quickshifter"], contras:["Marca joven en el segmento premium"], desc:"La supersport de Zontes. Bicilíndrica de 699cc y 95 cv.", rating:4.1 },
  { id:"zon2", marca:"Zontes", modelo:"GK350 Neo Retro", año:2024, cc:348, tipo:"Clásica", pot:39, par:32, peso:168, consumo:"3.8", precio_ar:"USD 6.500", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-argentina-350GK-Negra-1.png", pros:["Diseño neo-retro logrado","TFT de serie","Precio accesible"], contras:["39 cv para 350cc"], desc:"La neo-retro de Zontes, con motor de 348cc y 39 cv.", rating:4.0 },
  { id:"zon3", marca:"Zontes", modelo:"368G Adventure", año:2024, cc:368, tipo:"Adventure", pot:39, par:35, peso:203, consumo:"3.9", precio_ar:"USD 7.500", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733876/catalogo/zon3.jpg", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["Equipamiento de adventure grande","TFT de serie","Precio accesible"], contras:["203 kg para 368cc"], desc:"La adventure media de Zontes. Estética premium con mecánica accesible.", rating:3.9 },
  { id:"zon4", marca:"Zontes", modelo:"V350", año:2024, cc:348, tipo:"Custom", pot:39, par:32, peso:174, consumo:"3.6", precio_ar:"USD 7.000", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-350V.png", pros:["Estética power cruiser","TFT y ABS de serie","174 kg, liviana para una custom"], contras:["39 cv para su porte"], desc:"El power cruiser de Zontes. Rueda trasera de 180 y ABS independiente.", rating:3.9 },
  { id:"zon5", marca:"Zontes", modelo:"703F Adventure", año:2024, cc:699, tipo:"Adventure", pot:95, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788733876/catalogo/zon5.jpg", credito:"Foto: Chanokchon · CC BY-SA 4.0", pros:["95 cv del bicilíndrico 700","La adventure más grande de la marca","Equipamiento completo"], contras:["Zontes no publica el peso"], desc:"La adventure de 700cc de Zontes, con el mismo twin de la 703 RR.", rating:4.1 },
  { id:"zon6", marca:"Zontes", modelo:"M368", año:2024, cc:368, tipo:"Scooter", pot:39, peso:168, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-368M.png", pros:["39 cv, potente para un scooter","Motor de 368cc","Equipamiento completo"], contras:["168 kg"], desc:"El maxiscooter de Zontes. 368cc y 39 cv.", rating:4.0 },
  { id:"zon7", marca:"Zontes", modelo:"E368", año:2024, cc:368, tipo:"Scooter", pot:39, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/07/zontes-motos-argentina-368E-ETC-hero.png", pros:["Mismo motor de 368cc","Formato urbano","TFT de serie"], contras:["Zontes no publica el peso"], desc:"La variante urbana del maxiscooter 368 de Zontes.", rating:3.9 },
  { id:"zon8", marca:"Zontes", modelo:"D368", año:2024, cc:368, tipo:"Scooter", pot:39, peso:168, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-368D-.png", pros:["39 cv en formato scooter","Buena para ruta","Equipamiento completo"], contras:["168 kg"], desc:"El tercer scooter de la familia 368, de corte más deportivo.", rating:3.9 },
  { id:"zon9", marca:"Zontes", modelo:"Z200", año:2024, cc:198, tipo:"Naked", pot:21.5, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-200Z.jpg", pros:["21,5 cv en 200cc","Buena para arrancar","Equipamiento por encima del segmento"], contras:["Zontes no publica el peso"], desc:"La naked chica de Zontes. 198cc con equipamiento de moto grande.", rating:3.9 },
  { id:"zon10", marca:"Zontes", modelo:"G1-200", año:2024, cc:198, tipo:"Clásica", pot:21.5, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-200G.png", pros:["Estética retro cuidada","Liviana y fácil","Buen equipamiento para 200cc"], contras:["21,5 cv"], desc:"La retro de 200cc de Zontes, para ciudad y salidas cortas.", rating:3.9 },
  { id:"zon11", marca:"Zontes", modelo:"U3-200", año:2024, cc:198, tipo:"Scooter", pot:21.5, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-200U.png", pros:["Scooter con motor de 198cc","Ágil en ciudad","Buen equipamiento"], contras:["Zontes no publica el peso"], desc:"El scooter de 200cc de Zontes, pensado para ciudad.", rating:3.8 },
  { id:"zon12", marca:"Zontes", modelo:"T2 350", año:2024, cc:312, tipo:"Trail", pot:35, consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/08/Zontes-motos-argentina-350T2-hero.png", pros:["Versión trail con llantas de rayos","35 cv","Sirve para camino de tierra"], contras:["Zontes no publica el peso"], desc:"La trail de Zontes, con llantas de rayos y 312cc.", rating:4.0 },
  { id:"zon13", marca:"Zontes", modelo:"R350", año:2024, cc:"", tipo:"Naked", pot:"", consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/04/Zontes-motos-argentina-350R.png", pros:["Horquilla invertida","Tanque de 15 litros","Óptica full LED"], contras:["La ficha técnica del sitio oficial está rota"], desc:"La naked deportiva de 350 de Zontes.", rating:3.9 },
  { id:"zon14", marca:"Zontes", modelo:"X350", año:2024, cc:"", tipo:"Sport Touring", pot:"", consumo:"", img:"https://zontes.com.ar/wp-content/uploads/2026/08/zontes-moto-argentina-x350-champagne-hero.png", pros:["Tanque de 19 litros","Horquilla invertida","Óptica full LED"], contras:["La ficha técnica del sitio oficial está rota"], desc:"La sport touring de 350 de Zontes. 19 litros de tanque.", rating:3.9 },

  // ═══════════════════════════════════════════════════════
  // VOGE
  // ═══════════════════════════════════════════════════════
  { id:"vog1", marca:"Voge", modelo:"500 R", año:2024, cc:494, tipo:"Naked", peso:187, pot:47, par:43, consumo:"4.2", precio_ar:"USD 8.200", img:"", pros:["Diseño premium","TFT completa","Precio vs Honda CB500"], contras:["Marca nueva en AR"], desc:"La naked que compite con Honda CB500. Mejor equipada a menor precio.", rating:4.1 },
  { id:"vog2", marca:"Voge", modelo:"800 DS Rally", año:2024, cc:799, tipo:"Adventure", peso:220, pot:94, par:79, consumo:"5.0", precio_ar:"USD 14.500", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Voge_800DS_Rally.jpg/960px-Voge_800DS_Rally.jpg", pros:["Adventure con estética rally","Twin 800 capable","TFT y conectividad"], contras:["Poco historial en AR"], desc:"La adventure rally de Voge. 800cc twin, estética Dakar.", rating:4.1 },
  { id:"vog3", marca:"Voge", modelo:"525 R", año:2024, cc:494, tipo:"Sport", peso:185, pot:47, par:43, consumo:"4.1", precio_ar:"USD 8.800", img:"", pros:["Diseño deportivo premium","TFT","Precio accesible"], contras:["Motor modesto"], desc:"La deportiva de Voge con carenado completo.", rating:4.0 },
  { id:"vog4", marca:"Voge", modelo:"300 RR", año:2024, cc:292, tipo:"Sport", peso:162, pot:29, par:26, consumo:"3.4", precio_ar:"USD 5.800", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/c/cc/Moscow%2C_Voge_300RR_motorcycle%2C_Sept_2025_01.jpg/960px-Moscow%2C_Voge_300RR_motorcycle%2C_Sept_2025_01.jpg", pros:["Entry sport equipada","TFT de serie","Precio imbatible"], contras:["Motor muy limitado"], desc:"La entry sport de Voge. 300cc, TFT, diseño agresivo.", rating:3.9 },

  // ═══════════════════════════════════════════════════════
  // KOVE
  // ═══════════════════════════════════════════════════════
  { id:"kov1", marca:"Kove", modelo:"800 X Adventure", año:2024, cc:799, tipo:"Adventure", peso:199, pot:94, par:79, consumo:"4.9", precio_ar:"USD 13.500", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/Kove_800X_GT.jpg/960px-Kove_800X_GT.jpg", pros:["Motor KTM LC8 derivado","199kg muy liviana","TFT y asistencias completas"], contras:["Marca nueva en AR"], desc:"La adventure con motor KTM-derived. 799cc, 199kg.", rating:4.3 },
  { id:"kov2", marca:"Kove", modelo:"450 Rally", año:2024, cc:449, tipo:"Enduro", peso:128, pot:52, par:43, consumo:"4.5", precio_ar:"USD 9.500", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Kove_450_Rally_Dakar.jpg/960px-Kove_450_Rally_Dakar.jpg", pros:["Rally homologada","Participó en el Dakar","Precio accesible para lo que es"], contras:["No para uso urbano"], desc:"La rally de Kove. Participó en el Dakar.", rating:4.4 },
  { id:"kov3", marca:"Kove", modelo:"450 RR Pro", año:2024, cc:443, tipo:"Sport", peso:160, pot:70, par:39, consumo:"5.5", precio_ar:"USD 14.000", img:"", pros:["4 cilindros en 443cc","70cv a 13.000rpm","Única en su segmento"], contras:["Solo para pistas"], desc:"La supersport de 4 cilindros en 443cc. Única en el mundo.", rating:4.5 },

  // ═══════════════════════════════════════════════════════
  // BAJAJ / MOTOMEL / TVS
  // ═══════════════════════════════════════════════════════
  // Los 10 que trae Bajaj al país, con specs y fotos de bajaj.com.ar. Acá la línea
  // se vende como Rouser, no Pulsar: la NS200 estaba cargada con el nombre de otros
  // mercados. Bajaj no publica peso ni torque en la ficha web.
  { id:"baj1", marca:"Bajaj", modelo:"Dominar 400", año:2024, cc:400, tipo:"Sport Touring", pot:40, peso:182, par:35, consumo:"3.8", precio_ar:"USD 6.500", img:"https://bajaj.com.ar/wp-content/uploads/2026/06/D400_roja.png", pros:["40 cv, la más potente de la marca","Preparada para ruta","Precio accesible"], contras:["182 kg"], desc:"El sport tourer de Bajaj. 400cc y 40 cv para salir a ruta.", rating:4.0 },
  { id:"baj2", marca:"Bajaj", modelo:"Rouser NS200", año:2024, cc:199, tipo:"Naked", pot:23.5, peso:148, par:18, consumo:"3.0", precio_ar:"USD 4.200", img:"https://bajaj.com.ar/wp-content/uploads/2026/06/NS200-Negro-perfil.png", pros:["Liviana y ágil","Precio de entrada","Base muy usada y conocida"], contras:["Sin ABS en la versión base"], desc:"La naked de 200cc de Bajaj, de las más vendidas del segmento.", rating:3.9 },
  { id:"baj3", marca:"Bajaj", modelo:"Rouser NS400z", año:2024, cc:373, tipo:"Naked", pot:40, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/08/NS400_3-negra-1.png", pros:["40 cv, la naked más grande de Bajaj","Modos de manejo","Equipamiento completo"], contras:["Bajaj no publica el peso"], desc:"La naked grande de Bajaj. 373cc y 40 cv con modos de manejo.", rating:4.1 },
  { id:"baj4", marca:"Bajaj", modelo:"Rouser N250", año:2024, cc:250, tipo:"Naked", pot:24.5, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/07/Rouser-N250-UG-Negro-Lateral-1.png", pros:["Monoshock con Nitrox","Tanque de 14 litros","Motor refrigerado por aceite"], contras:["Bajaj no publica el peso"], desc:"La naked de 250 de Bajaj, con suspensión Nitrox y 14 litros de tanque.", rating:4.0 },
  { id:"baj5", marca:"Bajaj", modelo:"Rouser NS160", año:2024, cc:160, tipo:"Naked", pot:17, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2026/06/NS-160-negra.png", pros:["ABS delantero","Horquilla invertida","Tablero con Bluetooth y GPS"], contras:["Bajaj no publica el peso"], desc:"La 160 de Bajaj, con horquilla invertida y tablero conectado.", rating:3.9 },
  { id:"baj6", marca:"Bajaj", modelo:"Rouser P150", año:2024, cc:150, tipo:"Naked", pot:14.5, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/07/P150_SD_5-copy-4.png", pros:["Pensada para uso diario","Buen consumo","Precio accesible"], contras:["14,5 cv"], desc:"La 150 de uso diario de Bajaj, entre la Boxer y la NS160.", rating:3.8 },
  { id:"baj7", marca:"Bajaj", modelo:"Rouser LS125", año:2024, cc:125, tipo:"Naked", pot:12, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/07/125-LS-AMARILLA_33.png", pros:["Muy económica de mantener","Liviana","Ideal para arrancar"], contras:["125cc: no sale a ruta"], desc:"La 125 de Bajaj, primera moto o para moverse en ciudad.", rating:3.7 },
  { id:"baj8", marca:"Bajaj", modelo:"Dominar 250", año:2024, cc:250, tipo:"Sport Touring", pot:27, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/07/dominar-sparkling-black.png", pros:["27 cv en 250cc","Postura cómoda para ruta","Hermana menor de la 400"], contras:["Bajaj no publica el peso"], desc:"La Dominar chica. Mismo concepto de ruta en 250cc.", rating:4.0 },
  { id:"baj9", marca:"Bajaj", modelo:"Boxer 150", año:2024, cc:150, tipo:"Naked", pot:12, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2025/07/8-0A0A7077-1.png", pros:["Pensada para trabajo duro","Repuestos baratos","Muy simple de mantener"], contras:["Equipamiento mínimo"], desc:"La Boxer de 150, una moto de trabajo conocida por aguantar.", rating:3.9 },
  { id:"baj10", marca:"Bajaj", modelo:"Boxer CT100", año:2024, cc:102, tipo:"Naked", pot:7.5, consumo:"", img:"https://bajaj.com.ar/wp-content/uploads/2026/01/Boxer-CT100-Rojo-perfil-1.png", pros:["De las más económicas del mercado","Consumo muy bajo","Repuestos en todos lados"], contras:["102cc: solo para ciudad"], desc:"La más accesible de Bajaj. Moto de trabajo de 100cc.", rating:3.7 },
  { id:"mml1", marca:"Motomel", modelo:"Skua 250", año:2024, cc:249, tipo:"Trail", peso:145, pot:21, par:19, consumo:"3.0", precio_ar:"$ 5.800.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732585/catalogo/mml1.jpg", credito:"Foto: Bruno Rene Vargas · CC BY-SA 4.0", pros:["Nacional","Repuestos accesibles","Ideal para campo"], contras:["Motor muy básico"], desc:"La trail argentina. Nacional, repuestos en todo el país.", rating:3.8 },
  { id:"tvs1", marca:"TVS", modelo:"Apache RR 310", año:2024, cc:312, tipo:"Sport", peso:174, pot:34, par:28, consumo:"3.5", precio_ar:"USD 6.800", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/TVS_Apache_RR310_2018.jpg/800px-TVS_Apache_RR310_2018.jpg", pros:["Desarrollada con BMW","Quickshifter de serie","Precio muy accesible"], contras:["Motor limitado"], desc:"La deportiva india con ADN BMW.", rating:4.1 },
  { id:"tvs2", marca:"TVS", modelo:"Ronin", año:2024, cc:225, tipo:"Naked", peso:155, pot:20, par:19, consumo:"3.0", precio_ar:"USD 4.500", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/TVS_Ronin_2022.jpg/800px-TVS_Ronin_2022.jpg", pros:["Diseño neo-retro atractivo","Precio accesible","Versátil"], contras:["Motor muy limitado"], desc:"La naked neo-retro de TVS. Diseño atractivo.", rating:3.9 },
  { id:"ben1", marca:"Benelli", modelo:"TRK 702 X", año:2024, cc:693, tipo:"Adventure", peso:213, pot:80, par:67, consumo:"4.8", precio_ar:"USD 11.000", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1788732613/catalogo/ben1.jpg", credito:"Foto: MotorideSA · CC BY-SA 4.0", pros:["Precio muy competitivo","Twin 700 accesible","Equipamiento completo"], contras:["Service limitado en AR"], desc:"La adventure de 700cc más accesible del mercado.", rating:4.1 },

  // ═══════════════════════════════════════════════════════
  // CORVEN
  // Specs y fotos del sitio oficial (corvenmotos.com.ar). Corven no publica
  // torque ni precio, y en varias fichas tampoco peso o consumo: esos campos
  // van vacíos a propósito — la ficha de la app ahora omite lo que no hay.
  // ═══════════════════════════════════════════════════════
  { id:"cor1", marca:"Corven", modelo:"Energy 110 AD", año:2024, cc:107, tipo:"Cub", peso:97, pot:6.7, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/Energy-110-AD-Roja.jpg", pros:["Freno a disco delantero","Repuestos en todo el país","Muy bajo consumo"], contras:["Motor justo para dos personas"], desc:"La cub de trabajo diario, en versión con freno a disco.", rating:4.0 },
  { id:"cor2", marca:"Corven", modelo:"Energy 110 RT", año:2024, cc:107, tipo:"Cub", peso:97, pot:6.7, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/energy110-rt-roja.jpg", pros:["La más económica de la línea","Simple de mantener","Repuestos en todo el país"], contras:["Freno a tambor adelante"], desc:"La versión de entrada de la Energy, con llantas de rayos.", rating:3.9 },
  { id:"cor3", marca:"Corven", modelo:"Energy Tuning", año:2024, cc:107, tipo:"Cub", peso:92, pot:6.6, consumo:"3.1", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/07/Energy-Tuning-Silverstone.png", pros:["La más liviana de la línea","Estética deportiva","32 km por litro"], contras:["Tanque de apenas 4 litros"], desc:"La Energy con vestimenta deportiva. 92 kg en orden de marcha.", rating:4.0 },
  { id:"cor4", marca:"Corven", modelo:"Mirage 110 RT", año:2024, cc:110, tipo:"Cub", peso:99, pot:7.5, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/mirage-110-rt-blanca.jpg", pros:["Embrague semiautomático","Fácil para arrancar de cero","Muy económica"], contras:["Frenos a tambor en ambas ruedas"], desc:"Cub de 110 con embrague semiautomático: no hay que manejar el clutch.", rating:3.8 },
  { id:"cor5", marca:"Corven", modelo:"Mirage 110 AD", año:2024, cc:110, tipo:"Cub", peso:99, pot:7.5, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/mirage-110-ad-blanca.jpg", pros:["Freno a disco delantero","Embrague semiautomático","Muy económica"], contras:["Prestaciones justas en ruta"], desc:"La Mirage con freno a disco adelante.", rating:3.9 },
  { id:"cor6", marca:"Corven", modelo:"Triax 150 ONE", año:2024, cc:150, tipo:"Trail", pot:"", peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2026/05/Triax-150-One-Blanca.png", pros:["Puerta de entrada al on/off","5 velocidades","Repuestos accesibles"], contras:["Corven no publica peso ni potencia"], desc:"La Triax más accesible. Monocilíndrica de 150 refrigerada por aire.", rating:3.8 },
  { id:"cor7", marca:"Corven", modelo:"Triax 150 MAX", año:2024, cc:150, tipo:"Trail", pot:"", peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2026/06/Triax-150-Max-Negra.jpg", pros:["Monoamortiguador regulable","5 velocidades","Repuestos accesibles"], contras:["Corven no publica peso ni potencia"], desc:"La Triax 150 con suspensión trasera regulable.", rating:3.9 },
  { id:"cor8", marca:"Corven", modelo:"Triax 200 R3", año:2024, cc:200, tipo:"Trail", peso:150, pot:14, consumo:"5.0", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/02/fotos-galeria-productos-Triax-200-R33-400x516.png", pros:["Tanque de 12,5 litros","Buena para ripio","Servicio en todo el país"], contras:["150 kg para 14 cv"], desc:"El escalón intermedio de la Triax. Autonomía de unos 250 km.", rating:3.9 },
  { id:"cor9", marca:"Corven", modelo:"Triax 250 R3", año:2024, cc:250, tipo:"Trail", peso:131, pot:16.7, consumo:"3.3", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/07/IMG_9041-scaled.png", pros:["131 kg, liviana para su cilindrada","Rueda de 21 pulgadas adelante","Autonomía de unos 370 km"], contras:["Motor a carburador"], desc:"La trail nacional de 250. Rueda de 21 adelante y 12,5 litros de tanque.", rating:4.1 },
  { id:"cor10", marca:"Corven", modelo:"Triax 250 Adventure", año:2024, cc:250, tipo:"Adventure", pot:19, peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/08/Triax-250-adv-base-blanca.png", pros:["Tanque de 16 litros","6 velocidades","La más equipada de la línea"], contras:["Corven no publica el peso"], desc:"La Triax preparada para viajar: 16 litros de tanque y caja de 6.", rating:4.0 },
  { id:"cor11", marca:"Corven", modelo:"Expert 80", año:2024, cc:80, tipo:"Scooter", pot:"", peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2026/06/Expert80_1920x960_Corven.jpg", pros:["Automático, sin cambios","Ideal para ciudad","Muy económico"], contras:["80cc: solo para uso urbano"], desc:"El scooter chico de Corven. Variador automático por correa.", rating:3.7 },
  { id:"cor12", marca:"Corven", modelo:"Expert 150 Milano", año:2024, cc:150, tipo:"Scooter", pot:9.5, peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/05/Corven_04.10_Mural-114-400x516.jpg", pros:["CVT automática","Freno a disco delantero","Anda 90-95 km/h"], contras:["Corven no publica el peso"], desc:"El scooter grande de Corven, con estética italiana y 150cc.", rating:4.0 },
  { id:"cor13", marca:"Corven", modelo:"Hunter 150 AD", año:2024, cc:150, tipo:"Naked", peso:125, pot:11, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/hunter-150-ad-roja.jpg", pros:["Tanque de 12 litros","Freno a disco delantero","Clásica de trabajo"], contras:["Motor a carburador"], desc:"La street de 150 de Corven, en versión con freno a disco.", rating:3.9 },
  { id:"cor14", marca:"Corven", modelo:"Hunter 150 R2", año:2024, cc:150, tipo:"Scooter", peso:105, pot:11.5, consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/07/Hunter-150r2-azul.png", pros:["105 kg, muy liviano","Automático CVT","Ágil en ciudad"], contras:["Tanque de solo 5,5 litros"], desc:"Scooter de 150 automático, el más liviano de la línea.", rating:3.9 },
  { id:"cor15", marca:"Corven", modelo:"Hunter 150 RT", año:2024, cc:150, tipo:"Naked", pot:11.5, peso:"", consumo:"", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/12/hunter-150-rt-roja.jpg", pros:["Tanque de 12 litros","5 marchas","La más económica de la Hunter"], contras:["Frenos a tambor"], desc:"La versión de entrada de la Hunter 150.", rating:3.7 },
  { id:"cor16", marca:"Corven", modelo:"DX 70", año:2024, cc:72, tipo:"Cub", peso:76, pot:4.5, consumo:"1.9", img:"https://corvenmotos.com.ar/wp-content/uploads/2025/02/Dx-70-007-Azul-min-400x516.png", pros:["53 km por litro","76 kg, la más liviana","Embrague automático"], contras:["70cc: no sale a ruta"], desc:"La chiquita de Corven. 53 km por litro y embrague automático.", rating:3.8 },

  // ═══════════════════════════════════════════════════════
  // MOTO MORINI
  // Specs y fotos del sitio oficial argentino (motomorini.com.ar), que sí publica
  // torque. No publica consumo, ni la cilindrada del Calibro.
  // ═══════════════════════════════════════════════════════
  { id:"mor1", marca:"Moto Morini", modelo:"Alltrhike 450", año:2024, cc:450, tipo:"Adventure", peso:190, pot:44, par:44, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/Allthrike-right-profile-2.png-1554x1080.webp", pros:["190 kg, liviana para el segmento","Suspensión Kayaba regulable","ABS off-road desconectable"], contras:["Marca con red chica en el país"], desc:"La adventure media de Morini. Horquilla invertida Kayaba y 18 litros de tanque.", rating:4.2 },
  { id:"mor2", marca:"Moto Morini", modelo:"X-Cape 700", año:2024, cc:693, tipo:"Adventure", peso:213, pot:70, par:68, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/06/MotoMorini-xcape-700.png", pros:["70 cv del bicilíndrico 700","Rueda de 19 adelante","Equipamiento completo de serie"], contras:["213 kg en seco"], desc:"La X-Cape con el motor 700. Bicilíndrica en paralelo, Euro 5+.", rating:4.3 },
  { id:"mor3", marca:"Moto Morini", modelo:"X-Cape 650", año:2024, cc:649, tipo:"Adventure", peso:213, pot:60, par:54, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/03/miniatura-menu_xcape650-1-767x533@2x.png", pros:["TFT de 7 pulgadas con navegación","Horquilla regulable de 50 mm","Asiento a 820 mm"], contras:["213 kg en seco"], desc:"La adventure de 650 de Morini. TFT de 7\" y rueda de 19 adelante.", rating:4.2 },
  { id:"mor4", marca:"Moto Morini", modelo:"X-Cape 1200", año:2024, cc:1200, tipo:"Adventure", peso:283, pot:125, par:105, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/X-Cape-1200-right-profile_white1.png-1554x1080.webp", pros:["125 cv y 105 Nm","Tanque de 24,5 litros","Frenos Brembo con doble disco"], contras:["283 kg en orden de marcha"], desc:"La adventure grande de Morini. 24,5 litros de tanque para viajar lejos.", rating:4.3 },
  { id:"mor5", marca:"Moto Morini", modelo:"Rumble 350", año:2024, cc:350, tipo:"Custom", peso:175, pot:32.6, par:31, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2026/07/RUMBLE-350-thumb.png", pros:["Motor V2 a 60 grados","Asiento bajo, a 735 mm","Menos de 175 kg"], contras:["32 cv para 350cc"], desc:"La custom chica de Morini, con motor V2 de 350cc.", rating:4.0 },
  { id:"mor6", marca:"Moto Morini", modelo:"Calibro Custom", año:2024, cc:"", tipo:"Custom", peso:200, pot:69, par:68, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/calibro_ok-1-copia-corretta-767x533@2x.png", pros:["Asiento a 725 mm, muy bajo","69 cv, buena para ruta","Doble cuna de acero"], contras:["Morini no publica la cilindrada"], desc:"La custom de Morini. Asiento bajo y 68 Nm para andar tranquilo.", rating:4.1 },
  { id:"mor7", marca:"Moto Morini", modelo:"Calibro Bagger", año:2024, cc:"", tipo:"Custom", peso:200, pot:69, par:68, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/calibro_ok_bagger-1554x1080.png", pros:["Alforjas rígidas de serie","Asiento a 725 mm","Preparada para viajar"], contras:["Morini no publica la cilindrada"], desc:"La Calibro en versión bagger, con alforjas rígidas y pantalla.", rating:4.1 },
  { id:"mor8", marca:"Moto Morini", modelo:"3½ Sport", año:2024, cc:350, tipo:"Naked", peso:165, pot:32.6, par:31, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2026/07/sport-350-thumb.png", pros:["Menos de 165 kg","Motor V2 a 60 grados","Guiño a la 3½ original"], contras:["32 cv para 350cc"], desc:"La naked de 350 que recupera el nombre de la 3½ de los 70.", rating:4.1 },
  { id:"mor9", marca:"Moto Morini", modelo:"Seiemmezzo STR", año:2024, cc:649, tipo:"Naked", peso:200, pot:61, par:54, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/SEIEMMEZZO-STR.png", pros:["61 cv del bicilíndrico 650","Postura erguida, cómoda","Estética retro cuidada"], contras:["200 kg para una naked media"], desc:"La naked de 650 de Morini, con aire retro y postura erguida.", rating:4.1 },
  { id:"mor10", marca:"Moto Morini", modelo:"Seiemmezzo SCR", año:2024, cc:649, tipo:"Scrambler", peso:200, pot:61, par:54, consumo:"", img:"https://motomorini.com.ar/2025/wp-content/uploads/2025/04/scr-navygreen-1-1280x890-2.png", pros:["Estética scrambler lograda","Mismo motor 650 de 61 cv","Sirve para camino de tierra"], contras:["200 kg para hacer off-road"], desc:"La versión scrambler de la Seiemmezzo, con neumáticos mixtos.", rating:4.1 },

  // ═══════════════════════════════════════════════════════
  // KAYO — off-road puro (la importa Corven). Solo las motos: los tres Terrain
  // del catálogo oficial son cuatriciclos y no van acá. Kayo publica cilindrada,
  // potencia y torque en la ficha; el peso queda en el PDF que no expone.
  // ═══════════════════════════════════════════════════════
  { id:"kay1", marca:"Kayo", modelo:"KT 250 Pro 2T", año:2024, cc:250, tipo:"Enduro", pot:29, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2026/03/KAYO_KT250_Pro_2T.png", pros:["Motor 2 tiempos de 250","Enduro de competencia","Muy liviana"], contras:["No es para calle"], desc:"La enduro 2 tiempos de Kayo, pensada para competir.", rating:4.1 },
  { id:"kay2", marca:"Kayo", modelo:"TXR K6R", año:2024, cc:250, tipo:"Enduro", pot:38, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2026/03/Kayo-TXR-K6-R-Lateral-2.png", pros:["38 cv, la más potente de la línea","Suspensiones de competencia","Chasis liviano"], contras:["No es para calle"], desc:"La tope de gama off-road de Kayo. 38 cv en 250cc.", rating:4.2 },
  { id:"kay3", marca:"Kayo", modelo:"TXR 250 K4", año:2024, cc:249, tipo:"Enduro", pot:20, par:23.7, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2025/06/7-1.jpg", pros:["Entrada al off-road de 250","Caja de 6 velocidades","Repuestos vía Corven"], contras:["20 cv, la más suave de las 250"], desc:"La 250 de acceso de Kayo. Entrega suave para el que arranca en el off-road.", rating:4.0 },
  { id:"kay4", marca:"Kayo", modelo:"TXR 250 K6", año:2024, cc:249, tipo:"Enduro", pot:30, par:23.7, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2025/06/8-1.jpg", pros:["30 cv a 9.500 rpm","Caja de 6 velocidades","Suspensión para terreno roto"], contras:["No es para calle"], desc:"La 250 de 30 cv de Kayo. Caja de 6 y suspensión de largo recorrido.", rating:4.2 },
  { id:"kay5", marca:"Kayo", modelo:"TXR 60", año:2024, cc:60, tipo:"Enduro", pot:2, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2025/06/Diseno-Corven-Kayo-TXR60.png", pros:["Para chicos que arrancan","Tamaño y peso mínimos","Simple de mantener"], contras:["Solo para uso infantil"], desc:"La minicross de Kayo para los que empiezan de muy chicos.", rating:4.0 },
  { id:"kay6", marca:"Kayo", modelo:"TXR 110", año:2024, cc:110, tipo:"Enduro", pot:7, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2025/06/Diseno-Corven-Kayo-TXR110.png", pros:["El escalón después de la 60","Medidas para preadolescentes","Repuestos vía Corven"], contras:["Solo para uso infantil"], desc:"La dirt bike de 110 de Kayo, el paso siguiente a la TXR 60.", rating:4.0 },
  { id:"kay7", marca:"Kayo", modelo:"TXR 140", año:2024, cc:140, tipo:"Enduro", pot:11, peso:"", consumo:"", img:"https://kayo.com.ar/wp-content/uploads/2025/07/TXR140_1200x750.jpg", pros:["11 cv, ya sirve para adultos livianos","Buena para aprender en tierra","Repuestos vía Corven"], contras:["No es patentable"], desc:"La dirt bike de 140 de Kayo. El escalón antes de las 250.", rating:4.1 },

  // ═══════════════════════════════════════════════════════
  // SUNRA — eléctricas
  // Las eléctricas no tienen cilindrada ni consumo en L/100: en vez de cc van
  // motor_w, vmax, autonomia, bateria y carga, y la ficha técnica los muestra
  // solo si están (el resto del catálogo no los tiene y no cambia nada).
  // Datos de las fichas oficiales de cada modelo; la autonomía que publican las
  // marcas es "en condiciones ideales", así que va como dato de fábrica, no como
  // promesa: la de verdad la va a decir la comunidad.
  // ═══════════════════════════════════════════════════════
  { id:"sun1", marca:"Sunra", modelo:"Robot S", año:2024, tipo:"Eléctrica", motor_w:3000, vmax:90, autonomia:"100", bateria:"2 × 72V 20Ah", carga:"4 a 6 h", img:"", pros:["Dos baterías extraíbles, cargás adentro","90 km/h, va por avenida","Arranque por huella y control desde la app"], contras:["Autonomía de fábrica, en ideales","Sin red de carga rápida"], desc:"La eléctrica de calle de Sunra. Motor de 3.000W, dos baterías de litio que salen del chasis y se cargan en cualquier enchufe.", rating:4.2 },
  { id:"sun2", marca:"Sunra", modelo:"Anger S", año:2024, tipo:"Eléctrica", motor_w:3000, vmax:90, autonomia:"100", bateria:"2 × 72V 20Ah", carga:"8 h", img:"", pros:["Mismo motor de 3.000W que la Robot S","Reversa para estacionar","Huella, alarma y audio Bluetooth"], contras:["Carga más lenta que la Robot S","Autonomía de fábrica, en ideales"], desc:"La hermana con más equipamiento de la Robot S: reversa, alarma y arranque por huella.", rating:4.1 },
  { id:"sun3", marca:"Sunra", modelo:"Miku Super", año:2024, tipo:"Eléctrica", motor_w:3000, vmax:90, autonomia:"100", bateria:"2 × 72V 20Ah", carga:"4 a 6 h", img:"", pros:["Homologada para dos","Frenos a disco con CBS","Carga 150 kg"], contras:["Ruedas de 12″, poco cómoda en pozos","Autonomía de fábrica, en ideales"], desc:"El scooter grande de Sunra: 3.000W, dos plazas de verdad y frenada combinada.", rating:4.1 },
  { id:"sun4", marca:"Sunra", modelo:"Hawk", año:2024, tipo:"Eléctrica", motor_w:1800, vmax:50, autonomia:"60", bateria:"72V 20Ah", carga:"7 h", img:"", pros:["Formato de moto, no de scooter","Batería extraíble","Simple y barata de mantener"], contras:["50 km/h: es para ciudad","Carga de 7 horas"], desc:"Con cara de moto y 1.800W. Para moverse en ciudad sin pensar en la nafta.", rating:3.9 },
  { id:"sun5", marca:"Sunra", modelo:"Miku Max", año:2024, tipo:"Eléctrica", motor_w:800, vmax:40, autonomia:"45", bateria:"60V 20Ah", carga:"8 h", img:"", pros:["Liviana y fácil para arrancar","Control crucero y puerto USB","Batería extraíble de 60V"], contras:["40 km/h, solo tránsito lento","Una sola persona"], desc:"El scooter chico de Sunra, con motor FOC de 800W. Para trayectos cortos de ciudad.", rating:3.8 },
  { id:"sun6", marca:"Sunra", modelo:"Grace", año:2024, tipo:"Eléctrica", motor_w:800, vmax:45, autonomia:"50", bateria:"60V 20Ah", carga:"8 h", img:"", pros:["De las más livianas para maniobrar","Baúl con puerto USB","Batería extraíble"], contras:["Freno trasero a tambor","45 km/h, solo ciudad"], desc:"Scooter urbano de 800W, pensado para el uso diario corto.", rating:3.8 },

  // ═══════════════════════════════════════════════════════
  // SUPER SOCO — eléctricas
  // ═══════════════════════════════════════════════════════
  { id:"sso1", marca:"Super Soco", modelo:"TC Max", año:2024, tipo:"Eléctrica", motor_w:5100, vmax:95, par:150, peso:72, autonomia:"110", bateria:"Litio extraíble", carga:"3,5 h", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/c/cc/Super_Soco_TC_Max_-_gris.jpg/960px-Super_Soco_TC_Max_-_gris.jpg", credito:"Foto: Herodotptlomeu · CC BY-SA 4.0", pros:["150 Nm desde la primera vuelta de motor","Ruedas de 17″ y suspensión invertida","Hasta 160 km con la segunda batería"], contras:["La más cara de las eléctricas de calle","Sin carga rápida"], desc:"La naked eléctrica de Super Soco. Motor central de 5.100W de pico, 150 Nm y ciclo parte de moto de verdad: 17″, invertida y monoamortiguador.", rating:4.3 },
  { id:"sso2", marca:"Super Soco", modelo:"TS Street Hunter", año:2024, tipo:"Eléctrica", motor_w:2500, vmax:75, peso:104, autonomia:"70", bateria:"Litio 32Ah extraíble", carga:"3,5 h", img:"", pros:["Motor Bosch de 2.500W","Ruedas de 17″ y frenos con CBS","Se le suma una segunda batería"], contras:["75 km/h de techo","Con una sola batería no sirve para ruta"], desc:"La naked chica de Super Soco, con motor Bosch. Hasta unos 140 km si le ponés la segunda batería.", rating:4.0 },
  { id:"sso3", marca:"Super Soco", modelo:"TC Wanderer", año:2024, tipo:"Eléctrica", motor_w:2500, vmax:75, autonomia:"70", bateria:"Litio extraíble", carga:"3,5 h", img:"", pros:["Estética retro con rueda de rayos","Mismo motor de 2.500W de la TS","Ampliable a dos baterías"], contras:["75 km/h de techo","Poca autonomía con una batería"], desc:"La versión clásica del chasis de la TS: rueda de rayos, faro redondo y asiento cosido.", rating:4.0 },
  { id:"sso4", marca:"Super Soco", modelo:"CPx", año:2024, tipo:"Eléctrica", motor_w:3680, vmax:90, par:150, peso:107, autonomia:"70", bateria:"60V 45Ah", carga:"3,5 h", img:"https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/Super_Soco_CPX.jpg/960px-Super_Soco_CPX.jpg", credito:"Foto: Herodotptlomeu · CC BY-SA 4.0", pros:["Hasta 140 km con la segunda batería","Tres modos y reversa","Baúl central con USB"], contras:["107 kg, pesa para un scooter","Ruedas de 16″"], desc:"El scooter eléctrico de Super Soco: 90 km/h, batería extraíble y lugar bajo el asiento para una segunda.", rating:4.1 },
  { id:"sso5", marca:"Super Soco", modelo:"CUx", año:2024, tipo:"Eléctrica", motor_w:1600, vmax:45, peso:74, autonomia:"60 a 70", bateria:"Litio extraíble", carga:"7 h", img:"", pros:["74 kg, se maneja con el cuerpo","Frenos con CBS y luces LED","Tablero multifunción"], contras:["45 km/h, solo ciudad","Carga de 7 horas"], desc:"El scooter urbano chico de Super Soco. Liviano, simple y con batería que sale para cargar.", rating:3.9 },


  // ═══════════════════════════════════════════════════════
  // AMPLIACIÓN 13-oct-2026: 188 motos y 10 marcas nuevas (Zanella, Gilera, Keller,
  // Guerrero, Mondial, Hero, Husqvarna, GasGas, Aprilia, Moto Guzzi) más las gamas
  // chicas de Honda, Yamaha, Kawasaki, Suzuki, Benelli, Royal Enfield y Motomel.
  // Specs y fotos de los sitios oficiales argentinos; sin precios y sin inventar
  // datos (lo que la marca no publica va ""). Las fotos de más de 500 KB se
  // subieron a Cloudinary (catalogo/<id>); el resto hotlinkea el sitio oficial.
  // ═══════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════
  // HONDA — gama chica, media y adventure (agregado 09-oct-2026)
  // Fuente: motos.honda.com.ar (Honda Motor de Argentina). Specs de las
  // fichas técnicas PDF oficiales; fotos de estudio del sitio (webp 1200 px).
  // Honda Argentina NO publica potencia, torque ni consumo → van "".
  // Pesos = en orden de marcha según ficha.
  // ═══════════════════════════════════════════════════════
  { id:"hon11", marca:"Honda", modelo:"Wave 110S", año:2024, cc:109, tipo:"Cub", peso:102, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/68911efed7ef9.webp", pros:["Freno a disco adelante con CBS","Embrague semiautomático, 4 velocidades","Fabricada en Argentina, repuestos en todos lados"], contras:["Carburador","Tanque chico de 3,7 litros"], desc:"La cub que mueve al país. 109cc, 102 kg y un disco adelante para el uso diario.", rating:4 },
  { id:"hon12", marca:"Honda", modelo:"Biz 110", año:2025, cc:109, tipo:"Cub", peso:99, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6aa0845779d8e.webp", pros:["Inyección electrónica PGM-FI","Disco adelante con CBS","Solo 99 kg, la más liviana de la gama"], contras:["Rueda trasera de 14","Freno trasero a tambor"], desc:"La cub con inyección de Honda, pensada para la ciudad. 109cc y 99 kg.", rating:4 },
  { id:"hon13", marca:"Honda", modelo:"CB125F Twister", año:2024, cc:125, tipo:"Naked", peso:117, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/689122b4e49fd.webp", pros:["Motor 125 OHC probado y económico","Disco perforado de 240 mm adelante","Tanque de 10,1 litros, buena autonomía"], contras:["Carburador","Freno trasero a tambor y solo 4 marchas"], desc:"La 125 de entrada para arrancar en moto. Simple, liviana y fabricada en Argentina.", rating:3.8 },
  { id:"hon14", marca:"Honda", modelo:"XR150L", año:2024, cc:149, tipo:"Trail", peso:131, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/689121b3c4eed.webp", pros:["Disco de 240 mm con ABS adelante","Arranque eléctrico y a patada","Tanque de 12 litros, rueda 19/17"], contras:["Carburador","Freno trasero a tambor"], desc:"La trail chica de Honda para el camino de tierra y el pueblo. 149cc y 131 kg.", rating:3.9 },
  { id:"hon15", marca:"Honda", modelo:"XR190L", año:2024, cc:184, tipo:"Trail", peso:135, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6891219a8694e.webp", pros:["Inyección electrónica","Disco adelante (ABS) y disco atrás","Arranque eléctrico y a patada, tanque de 12 litros"], contras:["Suspensión de recorrido justo para off-road exigente","Honda no publica potencia"], desc:"La XR150L con más motor e inyección. Doble propósito de uso diario, fabricada en Argentina.", rating:4 },
  { id:"hon16", marca:"Honda", modelo:"XR300L Tornado", año:2025, cc:294, tipo:"Trail", peso:153, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/689120851b869.webp", pros:["Motor 294cc, 4 válvulas con inyección PGM-FI","Suspensión Showa de largo recorrido (221/227 mm)","ABS, 6 velocidades y rueda de 21 adelante"], contras:["Asiento alto (890 mm)","Honda no publica potencia"], desc:"La Tornado de siempre, ahora con 294cc, inyección y 6 marchas. La trail para el interior.", rating:4.4 },
  { id:"hon17", marca:"Honda", modelo:"CB190R 2.0", año:2025, cc:184, tipo:"Naked", peso:141, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6984e758a4bd1.webp", pros:["Horquilla invertida Showa","Frenos a disco Nissin con ABS en ambas ruedas","Inyección PGM-FI y embrague anti-rebote"], contras:["Solo 5 marchas","Motor refrigerado por aire"], desc:"La naked chica de Honda renovada. 184cc, 141 kg y equipamiento de moto más grande.", rating:4.2 },
  { id:"hon18", marca:"Honda", modelo:"CB300F Twister", año:2024, cc:294, tipo:"Naked", peso:151, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/689122a35cb01.webp", pros:["Control de tracción HSTC","ABS, horquilla invertida Showa y 6 marchas","Motor 294cc 4 válvulas con inyección"], contras:["Monocilíndrica refrigerada por aire","Honda no publica potencia"], desc:"La Twister grande. 294cc, 6 marchas y control de tracción, fabricada en Argentina.", rating:4.3 },
  { id:"hon19", marca:"Honda", modelo:"PCX160", año:2024, cc:157, tipo:"Scooter", peso:133, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6a2825095e933.webp", pros:["Motor 4 válvulas refrigerado por líquido","ABS adelante y suspensión Showa","Tanque de 8 litros, cubiertas tubeless"], contras:["Ruedas chicas (14/13)","Sin ABS atrás"], desc:"El scooter urbano de Honda. 157cc refrigerado por líquido, ABS y fabricado en Brasil.", rating:4.3 },
  { id:"hon20", marca:"Honda", modelo:"Elite 125", año:2024, cc:124, tipo:"Scooter", peso:104, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6891215b2cfe3.webp", pros:["Inyección PGM-FI","Disco adelante con CBS","Solo 104 kg, fácil para la ciudad"], contras:["Ruedas de 12/10","Tanque de 5,3 litros"], desc:"El scooter de entrada de Honda. 125cc con inyección, liviano y simple.", rating:3.9 },
  { id:"hon21", marca:"Honda", modelo:"CRF250R", año:2025, cc:249, tipo:"Enduro", peso:107, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6a95a9e9696f7.webp", pros:["Motor DOHC refrigerado por líquido con inyección","Launch control HRC y control de tracción HSTC","Suspensión Showa de 310 mm, 107 kg"], contras:["Solo para competición, no se patenta","Tanque de 6,3 litros"], desc:"La motocross de 250 de Honda. Pura pista: 107 kg y electrónica de carrera.", rating:4.3 },
  { id:"hon22", marca:"Honda", modelo:"CRF250RX", año:2024, cc:249, tipo:"Enduro", peso:110, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/689121feedb11.webp", pros:["Tanque de 8 litros y rueda trasera de 18 para enduro","Motor DOHC con inyección y launch control","Suspensión Showa de largo recorrido"], contras:["No es homologada para calle","Mantenimiento de moto de competición"], desc:"La CRF250R preparada para enduro y cross country: más tanque y rueda de 18 atrás.", rating:4.3 },
  { id:"hon23", marca:"Honda", modelo:"CRF450R", año:2025, cc:450, tipo:"Enduro", peso:113, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6a997651d1be7.webp", pros:["Motor Unicam 449,7cc con inyección","Launch control HRC, HSTC y selector de mapas","113 kg con suspensión Showa de 310 mm"], contras:["Solo competición, no se patenta","Exigente para pilotos nuevos"], desc:"La motocross grande de Honda. 450cc y 113 kg, para la pista.", rating:4.4 },
  { id:"hon24", marca:"Honda", modelo:"CRF450RX", año:2024, cc:450, tipo:"Enduro", peso:116, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/68912219be4c3.webp", pros:["Tanque de 8 litros y rueda de 18 atrás","Motor Unicam con inyección y control de tracción","Suspensión Showa de largo recorrido"], contras:["No homologada para calle","Mantenimiento de moto de competición"], desc:"La CRF450R versión enduro: más autonomía y rueda de 18 para el campo.", rating:4.4 },
  { id:"hon25", marca:"Honda", modelo:"CRF1100L Africa Twin", año:2025, cc:1084, tipo:"Adventure", peso:231, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6a282ce79dd4b.webp", pros:["Bicilíndrica 1084cc con opción de caja DCT","Suspensión Showa invertida con 204/220 mm de recorrido","Ruedas 21/18 y doble disco de 310 mm con ABS"], contras:["231 kg (242 con DCT)","Asiento alto"], desc:"La Africa Twin estándar, sin el paquete Adventure Sports. Más liviana y rayada para el ripio.", rating:4.5 },
  // --- EXTRAS Honda (no estaban en la lista, pero están en la gama vigente del sitio) ---
  { id:"hon26", marca:"Honda", modelo:"Navi", año:2024, cc:109, tipo:"Scooter", peso:104, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/68911fe545d09.webp", pros:["Transmisión automática V-Matic","Solo 104 kg y asiento bajo (762 mm)","Arranque eléctrico y a patada"], contras:["Frenos a tambor en ambas ruedas","Tanque de 3,5 litros y carburador"], desc:"La mini-moto automática de Honda. 109cc, divertida y fácil para la ciudad.", rating:3.8 },
  { id:"hon27", marca:"Honda", modelo:"GLH 150", año:2024, cc:149, tipo:"Naked", peso:129, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6891212f9d7f3.webp", pros:["Inyección electrónica","Disco adelante con CBS","Tanque de 12,7 litros, ruedas de 18 tubeless"], contras:["Freno trasero a tambor","Equipamiento básico"], desc:"La 150 de trabajo de Honda. Inyección, 129 kg y buena autonomía para el día a día.", rating:3.9 },
  { id:"hon28", marca:"Honda", modelo:"CB350 H'ness", año:2024, cc:348, tipo:"Clásica", peso:181, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/697b972752d1d.webp", pros:["Estética clásica con ruedas 19/18","ABS en ambas ruedas, disco de 310 mm","Inyección PGM-FI y embrague anti-rebote"], contras:["Solo 5 marchas","Motor refrigerado por aire"], desc:"La clásica de Honda. 348cc monocilíndrico, 181 kg y cromados, hecha en India.", rating:4.2 },
  { id:"hon29", marca:"Honda", modelo:"NX 190", año:2025, cc:184, tipo:"Adventure", peso:147, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6984ead1ea55f.webp", pros:["Horquilla invertida Showa y ABS en ambas ruedas","Inyección PGM-FI, tanque de 12 litros","Postura adventure con 147 kg"], contras:["Ruedas de 17 para calle, no es una trail","Solo 5 marchas"], desc:"La hermana adventure de la CB190R 2.0. Misma mecánica, manubrio alto y look viajero.", rating:4.1 },
  { id:"hon30", marca:"Honda", modelo:"NX500", año:2025, cc:471, tipo:"Adventure", peso:196, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6aa48610adfc3.webp", pros:["Bicilíndrica DOHC 471cc refrigerada por líquido","Disponible con E-Clutch (embrague electrónico)","Doble disco de 296 mm con ABS, rueda de 19 adelante"], contras:["Recorridos de suspensión justos (133/135 mm)","Honda no publica potencia"], desc:"La adventure mediana de Honda, sucesora de la CB500X. 471cc y opción de E-Clutch.", rating:4.4 },
  { id:"hon31", marca:"Honda", modelo:"CB500 Hornet", año:2025, cc:471, tipo:"Naked", peso:188, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6aa46206499d0.webp", pros:["Bicilíndrica 471cc DOHC, 8 válvulas","Horquilla invertida Showa y doble disco con ABS","Disponible con E-Clutch"], contras:["Honda no publica potencia","Precio por encima de las 300"], desc:"La naked media de Honda, reemplaza a la CB500F. Con opción de embrague electrónico E-Clutch.", rating:4.4 },
  { id:"hon32", marca:"Honda", modelo:"NC750X", año:2024, cc:745, tipo:"Adventure", peso:215, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/6891206caa74e.webp", pros:["Bicilíndrica 745cc pensada para el uso diario","Control de tracción HSTC y ABS","Doble disco de 296 mm, cubiertas 17/17"], contras:["215 kg","Suspensión de 120 mm, orientada a calle"], desc:"La crossover práctica de Honda. 745cc tranquilos, cómoda y económica para todos los días.", rating:4.3 },
  { id:"hon33", marca:"Honda", modelo:"XL750 Transalp", año:2026, cc:755, tipo:"Adventure", peso:210, pot:"", par:"", consumo:"", img:"https://motos.honda.com.ar/assets/uploads/models_versions/69eb890bb2012.webp", pros:["Bicilíndrica 755cc, 8 válvulas","Ruedas 21/18 y suspensión Showa de 200/190 mm","Doble disco de 310 mm con ABS, 210 kg"], contras:["Tanque de 16,9 litros","Honda no publica potencia"], desc:"La Transalp volvió. 755cc, rueda de 21 y 210 kg: la adventure media de Honda para la ruta y el ripio.", rating:4.5 },

  // ═══════════════════════════════════════════════════════
  // YAMAHA — gama chica, media, scooters y off-road (agregado 09-oct-2026)
  // Fuente: yamaha-motor.com.ar (Yamaha Motor Argentina). Specs de la ficha
  // técnica de cada modelo; fotos de estudio del sitio (jpg 1600 px).
  // Yamaha Argentina NO publica potencia, torque ni consumo → van "".
  // Pesos = en orden de marcha según ficha. Nombres tal cual el sitio.
  // ═══════════════════════════════════════════════════════
  { id:"yam10", marca:"Yamaha", modelo:"Crypton Finn", año:2026, cc:115, tipo:"Cub", peso:"", pot:"", par:"", consumo:"", img:"https://landings.yamaha-motor.com.ar/imagenes/crypton-finn-2026/hero-crypton-v2.jpg", pros:["Inyección electrónica","Frenos UBS (combinados)","Faros LED y tablero digital"], contras:["Yamaha todavía no publica la ficha técnica completa","Recién lanzada, poca información oficial"], desc:"La nueva Crypton 2026 de Yamaha. 115cc con inyección, frenos UBS y LED.", rating:3.9 },
  { id:"yam11", marca:"Yamaha", modelo:"XTZ125", año:2024, cc:124, tipo:"Trail", peso:118, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/fd6f9d4aca9c65775e005625379d5e3b_full_size.jpg", pros:["Rueda de 21 adelante y 260 mm de despeje","Solo 118 kg","Arranque eléctrico y a pedal"], contras:["Carburador","Freno trasero a tambor"], desc:"La trail chica de Yamaha. 124cc, liviana y con rueda de 21 para la tierra.", rating:3.9 },
  { id:"yam12", marca:"Yamaha", modelo:"XTZ 250 ABS", año:2024, cc:249, tipo:"Trail", peso:152, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/84aa18f3109f32115c3ac65e499b5c16_full_size.jpg", pros:["ABS adelante y frenos a disco en ambas ruedas","Suspensión de largo recorrido (220/205 mm)","Inyección electrónica y tanque de 14 litros"], contras:["Solo 5 marchas","Refrigerada por aire"], desc:"La doble propósito de Yamaha para el interior. 249cc con inyección y ABS.", rating:4.3 },
  { id:"yam13", marca:"Yamaha", modelo:"MT-03 ABS", año:2024, cc:321, tipo:"Naked", peso:166, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/bb4c3bf5371db42ae4af0635330c795c_full_size.jpg", pros:["Bicilíndrica DOHC refrigerada por líquido","ABS de serie","166 kg, ágil para ciudad"], contras:["Disco delantero simple de 298 mm","Yamaha no publica potencia"], desc:"La MT de entrada. Bicilíndrica 321cc, liviana y con ABS.", rating:4.3 },
  { id:"yam14", marca:"Yamaha", modelo:"MT-07 ABS", año:2025, cc:689, tipo:"Naked", peso:183, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/9a726bebc440ac6596e531b736b2f43f_full_size.jpg", pros:["Bicilíndrica CP2 de 689cc","Doble disco de 282 mm con ABS","183 kg, cubierta trasera 180"], contras:["Yamaha no publica potencia","Suspensión básica (130 mm)"], desc:"La naked media más vendida de Yamaha, renovada. 689cc, 183 kg y carácter.", rating:4.5 },
  { id:"yam15", marca:"Yamaha", modelo:"R7", año:2025, cc:689, tipo:"Sport", peso:189, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/570a1eba3026eeed59554bf8407e4a0f_full_size.jpg", pros:["Motor CP2 de 689cc en carrocería supersport","Doble disco de 298 mm","Cubiertas 120/70 y 180/55 ZR17"], contras:["Postura deportiva exigente","Yamaha no publica potencia"], desc:"La deportiva media de Yamaha, nueva línea. El bicilíndrico de la MT-07 con carenado.", rating:4.4 },
  { id:"yam16", marca:"Yamaha", modelo:"FZ V4.0 ABS", año:2025, cc:149, tipo:"Naked", peso:137, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/12f32a801b38e6d20862c3580052c42b_full_size.jpg", pros:["ABS y frenos a disco en ambas ruedas","Inyección electrónica","Cubierta trasera radial 140/60-R17"], contras:["Refrigerada por aire","Yamaha no publica potencia"], desc:"La FZ 150 de cuarta generación. Naked de 149cc con ABS para la ciudad.", rating:4.1 },
  { id:"yam17", marca:"Yamaha", modelo:"FZ X", año:2024, cc:149, tipo:"Scrambler", peso:140, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/4444d679af24792600fa26e51cafa8a8_full_size.jpg", pros:["Estilo neo-retro sobre la base de la FZ","Frenos a disco en ambas ruedas","Inyección electrónica"], contras:["Refrigerada por aire","Yamaha no publica potencia"], desc:"La FZ con look retro-scrambler. 149cc, 140 kg y faro redondo.", rating:4 },
  { id:"yam18", marca:"Yamaha", modelo:"Ray ZR 125 Fi", año:2024, cc:125, tipo:"Scooter", peso:99, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906063/catalogo/yam18.jpg", pros:["Solo 99 kg","Inyección electrónica","Disco adelante de 190 mm"], contras:["Freno trasero a tambor","Ruedas de 12/10"], desc:"El scooter liviano de Yamaha. 125cc con inyección y 99 kg.", rating:3.9 },
  { id:"yam19", marca:"Yamaha", modelo:"Fascino", año:2024, cc:125, tipo:"Scooter", peso:99, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/ff51cff6b269df746054f9e236a1f537_full_size.jpg", pros:["Diseño retro","Inyección electrónica y 99 kg","Disco adelante de 190 mm"], contras:["Freno trasero a tambor","Ruedas de 12/10"], desc:"El scooter 125 con estética retro de Yamaha. Misma base que la Ray ZR.", rating:3.9 },
  { id:"yam20", marca:"Yamaha", modelo:"NMAX Connected", año:2025, cc:155, tipo:"Scooter", peso:131, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/53094d9263334060b29d0a6abe6e191b_full_size.jpg", pros:["Motor 155cc refrigerado por líquido","Conectividad con el celular (Y-Connect)","Frenos a disco de 230 mm adelante y atrás"], contras:["Ruedas de 13","Recorrido trasero corto (85 mm)"], desc:"El scooter premium de 155cc de Yamaha, con conectividad y motor refrigerado por líquido.", rating:4.3 },
  { id:"yam21", marca:"Yamaha", modelo:"XMAX 300 Connected", año:2025, cc:292, tipo:"Scooter", peso:183, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/a09c4027fc8d4e00879cd635cfbf676b_full_size.jpg", pros:["Motor 292cc refrigerado por líquido","Conectividad con el celular","Discos de 267/245 mm, ruedas de 15/14"], contras:["183 kg","Precio de maxi-scooter"], desc:"El maxi-scooter de Yamaha. 292cc, cómodo para ruta y con conectividad.", rating:4.4 },
  { id:"yam22", marca:"Yamaha", modelo:"TT-R230", año:2024, cc:223, tipo:"Enduro", peso:115, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/17f8eccabf44723b290c890f95d0db9f_full_size.jpg", pros:["Arranque eléctrico","Suspensión de 240/220 mm, rueda de 21","Solo 115 kg, fácil para empezar en el off-road"], contras:["Carburador","Freno trasero a tambor y no se patenta"], desc:"La trail de campo recreativa de Yamaha. 223cc, dócil y liviana.", rating:3.9 },
  { id:"yam23", marca:"Yamaha", modelo:"YZ125", año:2025, cc:125, tipo:"Enduro", peso:96, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/cd7270e149d0131091406d929db40125_full_size.jpg", pros:["Motor 2 tiempos refrigerado por líquido","Solo 96 kg","Suspensión de 300/315 mm"], contras:["Arranque a pedal","Solo competición, no se patenta"], desc:"La motocross 2T de 125 de Yamaha. 96 kg para la pista.", rating:4.2 },
  { id:"yam24", marca:"Yamaha", modelo:"YZ250", año:2025, cc:249, tipo:"Enduro", peso:103, pot:"", par:"", consumo:"", img:"https://www.yamaha-motor.com.ar/images/productos/c06ce38cb6c376eb54c72a572a6d8a2b_full_size.jpg", pros:["Motor 2 tiempos de 249cc refrigerado por líquido","103 kg","Suspensión de 300/315 mm"], contras:["Arranque a pedal","Solo competición, no se patenta"], desc:"La motocross 2T grande de Yamaha. Potencia de dos tiempos en 103 kg.", rating:4.2 },
  { id:"yam25", marca:"Yamaha", modelo:"YZ250F", año:2025, cc:250, tipo:"Enduro", peso:105, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906063/catalogo/yam25.jpg", pros:["Motor DOHC 4T con inyección y arranque eléctrico","Chasis de aluminio, 105 kg","Suspensión de 310/295 mm"], contras:["Solo competición, no se patenta","Mantenimiento de moto de carrera"], desc:"La motocross 4T de 250 de Yamaha. Inyección, arranque eléctrico y chasis de aluminio.", rating:4.4 },
  { id:"yam26", marca:"Yamaha", modelo:"YZ450F", año:2025, cc:450, tipo:"Enduro", peso:110, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906066/catalogo/yam26.jpg", pros:["Motor DOHC 450 con inyección y arranque eléctrico","110 kg","Suspensión de 310/295 mm"], contras:["Solo competición, no se patenta","Exigente para pilotos nuevos"], desc:"La motocross grande de Yamaha. 450cc, 110 kg, para la pista.", rating:4.4 },

  // ═══════════════════════════════════════════════════════
  // ZANELLA — línea completa 2025/2026 (agregado 09-oct-2026)
  // Specs de las fichas técnicas PDF oficiales y fotos de zanellaglobal.com/ar (sitio oficial, zanella.com.ar redirige ahí).
  // Zanella publica consumo en las fichas (se carga tal cual); no publica torque salvo en la RTZ 500. Peso = "peso vacío" de la ficha (Andina 300 y RTZ 500: en orden de marcha).
  // ═══════════════════════════════════════════════════════
  { id:"zan1", marca:"Zanella", modelo:"ZB 110 Full", año:2025, cc:107, tipo:"Cub", peso:91, pot:6.5, par:"", consumo:"1.8", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906067/catalogo/zan1.png", pros:["Disco adelante con CBS y llantas de aleación","Iluminación full LED y tablero digital","Puertos USB A y C con porta celular"], contras:["Freno trasero a tambor","Tanque de 3,2 litros"], desc:"La cub más vendida de Zanella en su versión completa. Motor 107cc de 4 marchas semiautomáticas, pensada para el día a día en ciudad.", rating:3.9 },
  { id:"zan2", marca:"Zanella", modelo:"ZB 125 Sport", año:2025, cc:120, tipo:"Cub", peso:93, pot:7.5, par:"", consumo:"1.8", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/colores2-960x634-1.png", pros:["Motor 120cc de 7,5 hp, algo más de fuerza que la ZB 110","Disco adelante con CBS y llantas de aleación","Full LED, tablero digital y USB A/C"], contras:["Freno trasero a tambor","Tanque de 3,2 litros"], desc:"La ZB con motor 120cc. Misma receta urbana de la 110 pero con un poco más de potencia y equipamiento completo.", rating:3.9 },
  { id:"zan3", marca:"Zanella", modelo:"Due 110 ST", año:2025, cc:107, tipo:"Cub", peso:86, pot:6.5, par:"", consumo:"1.7", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906068/catalogo/zan3.png", pros:["Muy liviana: 86 kg en vacío","Ruedas de 17 adelante y atrás","Frenos con sistema CBS y asiento memory foam"], contras:["Frenos a tambor en ambas ruedas","Llantas con rayos"], desc:"La versión de entrada de la Due, la cub 110 más simple de Zanella. Motor 107cc y tanque de 4 litros.", rating:3.7 },
  { id:"zan4", marca:"Zanella", modelo:"Due 125 Sport", año:2025, cc:120, tipo:"Cub", peso:86, pot:7.5, par:"", consumo:"1.8", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906070/catalogo/zan4.png", pros:["Disco adelante con CBS y llantas de aleación","Solo 86 kg con motor 120cc","Puertos USB A y C, plásticos con protección UV"], contras:["Freno trasero a tambor","No tiene iluminación LED"], desc:"La Due con motor 120cc y freno a disco. Cub urbana liviana con ruedas de 17 y 4 litros de tanque.", rating:3.8 },
  { id:"zan5", marca:"Zanella", modelo:"Hot 90 Shot", año:2025, cc:86, tipo:"Cub", peso:74, pot:5.9, par:"", consumo:"1.8", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/hot90_lateralgrande.png", pros:["Mini moto de 74 kg con ruedas de 10 pulgadas","Caja automática de 4 velocidades, sin embrague","Manubrio rebatible y escape scrambler cromado"], contras:["Frenos a tambor adelante y atrás","Tanque de 2,5 litros"], desc:"La mini moto estilo vintage de Zanella, a lo Dax. Motor 86cc automático y asiento slammer: para la ciudad y el campo cerca.", rating:3.7 },
  { id:"zan6", marca:"Zanella", modelo:"ZR 150 OHC", año:2025, cc:149, tipo:"Trail", peso:110, pot:11.5, par:"", consumo:"2.7", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906070/catalogo/zan6.png", pros:["Motor OHC de 149cc con 5 velocidades","300 mm de despeje y rueda de 19 adelante","Full LED, tablero digital y tanque de 12 litros"], contras:["Freno trasero a tambor","Sin ABS"], desc:"La on-off chica de Zanella con el motor OHC nuevo. Rueda de 19 y mono amortiguador, para ciudad y caminos de tierra.", rating:4 },
  { id:"zan7", marca:"Zanella", modelo:"ZR 200 OHC", año:2025, cc:198, tipo:"Trail", peso:110, pot:14.5, par:"", consumo:"2.7", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906071/catalogo/zan7.png", pros:["Motor OHC de 198cc y 14,5 hp","Frenos a disco adelante y atrás","300 mm de despeje, full LED y tablero digital"], contras:["Sin ABS","Caja de 5 velocidades"], desc:"La ZR intermedia: mismo chasis que la 150 con motor 198cc y doble disco. Buena opción para el interior.", rating:4.1 },
  { id:"zan8", marca:"Zanella", modelo:"ZR 250 OHC", año:2025, cc:250, tipo:"Trail", peso:115, pot:19, par:"", consumo:"2.5", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/07/modelo1-silver-green-zr250-ohc-2.png", pros:["Motor OHC balanceado de 249,9cc con caja de 6 velocidades","Rueda de 21 adelante y 18 atrás, discos en ambas","Cubrecárter de serie, full LED y asiento memory foam"], contras:["Sin ABS","Refrigeración por aire"], desc:"La ZR más grande, lanzada en 2025. 19 hp, 6 marchas y ruedas de 21/18: la Zanella para rutas de ripio y tierra.", rating:4.2 },
  { id:"zan9", marca:"Zanella", modelo:"ZT 150", año:2025, cc:150, tipo:"Trail", peso:120, pot:11.5, par:"", consumo:"2.7", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/07/zt150_lateral01-1.png", pros:["Ruedas de 21 adelante y 18 atrás","290 mm de despeje y mono amortiguador","Tanque de 12 litros"], contras:["Freno trasero a tambor","Sin ABS"], desc:"La trail de Zanella con estética XR y ruedas grandes. Motor OHC de 149,9cc y 5 velocidades.", rating:3.9 },
  { id:"zan10", marca:"Zanella", modelo:"RX 150 Full", año:2025, cc:149, tipo:"Naked", peso:109, pot:12.7, par:"", consumo:"2.7", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/RX-150-FULL_NARDO-GRAY.png", pros:["Motor 149cc de 12,7 hp con 5 velocidades","Disco adelante con CBS y llantas de aleación","Óptica full LED, tablero digital y tanque de 12 litros"], contras:["Freno trasero a tambor","Sin ABS"], desc:"La street 150 clásica de Zanella en su versión completa. Ruedas de 18 y parrilla trasera, para ciudad y ruta corta.", rating:4 },
  { id:"zan11", marca:"Zanella", modelo:"Sapucai 150", año:2025, cc:149, tipo:"Clásica", peso:96, pot:12, par:"", consumo:"2.0", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/sapucai_lateralgrande.png", pros:["Liviana: 96 kg con motor 149cc","Escape Grand Classic cromado y asiento slammer","Parrilla trasera y puerto USB"], contras:["Frenos a tambor en ambas ruedas","Tanque de 8,4 litros"], desc:"La clásica económica de Zanella: estética retro, motor 149cc de 5 velocidades y ruedas de 18 con rayos.", rating:3.8 },
  { id:"zan12", marca:"Zanella", modelo:"Patagonian Eagle 150", año:2025, cc:149, tipo:"Custom", peso:129, pot:13, par:"", consumo:"2.0", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/patagonianeagle150_lateral.png", pros:["Llantas de aleación y disco adelante","Rueda trasera de 16 y escape cromado estilo vintage","Asiento dividido tipo roadster y puerto USB"], contras:["Freno trasero a tambor","Sin ABS"], desc:"La custom de Zanella con motor 149cc de 13 hp. Tanque de 11,5 litros y postura relajada para ruta tranquila.", rating:4 },
  { id:"zan13", marca:"Zanella", modelo:"Ceccato R150", año:2025, cc:149, tipo:"Clásica", peso:96, pot:12, par:"", consumo:"2.0", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906072/catalogo/zan13.png", pros:["Estética café racer: velocímetro central, manubrio y óptica a tono","Disco adelante de doble pistón","Solo 96 kg y puerto USB"], contras:["Freno trasero a tambor","Tanque de 8,4 litros"], desc:"La café racer chica de la línea Ceccato. Misma base que la Sapucai con motor 149cc y ruedas de 18 con rayos.", rating:3.9 },
  { id:"zan14", marca:"Zanella", modelo:"Ceccato X 250", año:2025, cc:250, tipo:"Clásica", peso:108, pot:20, par:"", consumo:"", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/CECCATO-X-250-LATERAL-PLATEADA.png", pros:["Doble disco adelante y disco atrás","Horquilla invertida y monoshock","Caja de 6 velocidades, tablero digital y USB"], contras:["Sin ABS","Refrigeración por aire"], desc:"La café racer 250 de Zanella. Motor de 20 hp, 108 kg y neumáticos 120/70-17 adelante y atrás.", rating:4.1 },
  { id:"zan15", marca:"Zanella", modelo:"Styler RS", año:2025, cc:123, tipo:"Scooter", peso:109, pot:8.4, par:"", consumo:"2.8", img:"https://zanellaglobal.com/ar/wp-content/uploads/2025/08/STYLER-RS_main.png", pros:["Disco adelante con CBS y llantas de aleación","Iluminación LED y escape con protector","Puerto USB y porta objetos"], contras:["Ruedas de 10 pulgadas","Tanque de 4 litros"], desc:"El scooter de 125 de Zanella. Motor 123cc automático y 109 kg, para moverse en ciudad.", rating:3.8 },
  { id:"zan16", marca:"Zanella", modelo:"Andina 300", año:2026, cc:292, tipo:"Adventure", peso:152, pot:28, par:"", consumo:"", img:"https://zanellaglobal.com/ar/wp-content/uploads/2026/01/azul-andina-300.png", pros:["Motor 4 válvulas refrigerado por líquido con 6 velocidades","ABS y discos de 298/218 mm","Suspensión invertida, tanque de 18 litros y llantas multirayos"], contras:["Monocilíndrica de 28 hp para una adventure de 152 kg","No publican consumo"], desc:"La adventure chica de la línea Ceccato, novedad 2026. Rueda de 19 adelante, 180 mm de despeje y tanque grande para viajar.", rating:4.2 },
  { id:"zan17", marca:"Zanella", modelo:"Ceccato Roadster 500", año:2026, cc:486, tipo:"Naked", peso:180, pot:44, par:"", consumo:"4.3", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906073/catalogo/zan17.png", pros:["Bicilíndrica de 486cc refrigerada por líquido","ABS Bosch de doble canal con frenos J.Juan de 320/240 mm","Suspensión invertida KYB ajustable y Pirelli tubeless"], contras:["Tanque de 13,5 litros","No publican torque"], desc:"La naked de 500 de la línea Ceccato: 44 hp, 6 velocidades y 160 km/h de máxima. El salto de Zanella a la media cilindrada.", rating:4.3 },
  { id:"zan18", marca:"Zanella", modelo:"RTZ 500", año:2026, cc:486, tipo:"Adventure", peso:209, pot:47, par:43, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906074/catalogo/zan18.png", pros:["Bicilíndrica DOHC de 486cc, 47 hp y 43 Nm","ABS Bosch doble canal desconectable atrás, frenos J.Juan","Suspensión KYB regulable, rueda de 19 y tanque de 16 litros"], contras:["209 kg en orden de marcha","No publican consumo"], desc:"La adventure grande de Zanella (línea Motori), lanzada a fines de 2025. Cubrecárter de serie, 200 mm de despeje y Pirelli tubeless.", rating:4.4 },
  // ═══════════════════════════════════════════════════════
  // GILERA — línea completa 2025/2026 (agregado 09-oct-2026)
  // Specs y fotos de gilera.com.ar (sitio oficial de Gilera Argentina). Gilera no publica torque ni consumo. Peso = "peso en seco" de la ficha.
  // ═══════════════════════════════════════════════════════
  { id:"gil1", marca:"Gilera", modelo:"Smash Full II", año:2026, cc:107, tipo:"Cub", peso:87, pot:7.2, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2026/09/GILERA_SMASH_FULL_180_ROJA.png", pros:["Frenos a disco adelante y atrás con CBS","Llantas de aleación e iluminación LED","Puertos USB A y C, baúl bajo asiento"], contras:["Tanque de 3,5 litros","Tablero analógico-digital"], desc:"La cub más vendida del país en su versión completa. Motor 107cc de 7,2 hp, 4 marchas semiautomáticas y 87 kg.", rating:3.9 },
  { id:"gil2", marca:"Gilera", modelo:"Smash VS II", año:2026, cc:107, tipo:"Cub", peso:87, pot:7.2, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2026/07/GILERA_SMASH_VS_180_ROJA.png", pros:["La Smash de entrada, con el mismo motor 107cc","Puertos USB A y C","Baúl bajo asiento y parrilla trasera"], contras:["Frenos a tambor adelante y atrás","Llantas con rayos y tablero analógico"], desc:"La versión base de la Smash. Para quien quiere la mecánica probada de Gilera al mínimo costo.", rating:3.6 },
  { id:"gil3", marca:"Gilera", modelo:"Smash R Full II", año:2026, cc:107, tipo:"Cub", peso:95, pot:7.2, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906076/catalogo/gil3.png", pros:["Discos adelante y atrás con CBS","Llantas de aleación, LED y tablero digital","Puertos USB A y C"], contras:["Tanque de 3,5 litros","Velocidad máxima de 80 km/h"], desc:"La Smash con carrocería deportiva, más alta y con cubierta trasera más ancha. Mismo motor 107cc semiautomático.", rating:3.9 },
  { id:"gil4", marca:"Gilera", modelo:"Smash R Tuning II", año:2026, cc:107, tipo:"Cub", peso:95, pot:7.2, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906078/catalogo/gil4.png", pros:["Ruedas anchas 110/90-13 estilo tuning","Discos adelante y atrás con CBS","Llantas de aleación, LED y tablero digital"], contras:["Tanque de 3,5 litros","Velocidad máxima de 80 km/h"], desc:"La Smash R con ruedas de 13 pulgadas anchas, para quien busca estética. Motor 107cc y equipamiento completo.", rating:3.8 },
  { id:"gil5", marca:"Gilera", modelo:"Smash X125 CBS", año:2025, cc:119, tipo:"Cub", peso:100, pot:7.5, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2024/12/GILERA_SMASH_X125_180_ROJA.png", pros:["Motor 119cc de 7,5 hp, el más grande de la familia Smash","Discos adelante y atrás con CBS","Ruedas de 17 en ambos ejes y tanque de 4,5 litros"], contras:["Faro delantero halógeno","100 kg, la Smash más pesada"], desc:"La Smash de 125 con ruedas de 17. Un poco más de motor y de tanque para quien hace más kilómetros.", rating:3.8 },
  { id:"gil6", marca:"Gilera", modelo:"VC 150 II", año:2026, cc:149, tipo:"Naked", peso:110, pot:12, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906080/catalogo/gil6.png", pros:["Motor nuevo con eje balanceador","Discos adelante y atrás con CBS","Tanque de 14 litros, LED y puertos USB A y C"], contras:["Sin ABS","Giros halógenos"], desc:"La street 150 de Gilera renovada en 2026. Motor 149cc de 12 hp y 5 marchas, ruedas de 18 con rayos.", rating:4 },
  { id:"gil7", marca:"Gilera", modelo:"Sahel 150 II", año:2026, cc:149, tipo:"Trail", peso:123, pot:12, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906082/catalogo/gil7.png", pros:["Rueda de 19 adelante y 200 mm de despeje","Discos adelante y atrás","Mono amortiguador, LED y tablero digital"], contras:["Sin ABS ni CBS","Motor justo para ruta"], desc:"La on-off chica de Gilera. Motor 149cc de 12 hp, 5 marchas y tanque de 12 litros para caminos de tierra.", rating:4 },
  { id:"gil8", marca:"Gilera", modelo:"SMX 200", año:2025, cc:197, tipo:"Trail", peso:120, pot:13.7, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2024/12/GILERA_SMX200_END_180_ROJA.png", pros:["300 mm de despeje y ruedas de 19/18","Discos adelante y atrás","Tanque de 13,5 litros y 120 kg"], contras:["Sin ABS","Caja de 5 marchas"], desc:"La enduro de calle de Gilera. Motor 197cc de 13,7 hp, liviana y con mucho despeje para el off road.", rating:4 },
  { id:"gil9", marca:"Gilera", modelo:"SMX 200 ADV", año:2025, cc:197, tipo:"Adventure", peso:135, pot:15.4, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2024/12/GILERA_SMX200_ADV_180_ROJA.png", pros:["Parabrisas y cubrepuños de serie","Caja de 6 marchas y 15,4 hp","Discos adelante y atrás, rueda de 19"], contras:["Sin ABS","Refrigeración por aire"], desc:"La versión adventure de la SMX 200: carenado, parabrisas y 6 marchas para hacer ruta y entrar al ripio.", rating:4.1 },
  { id:"gil10", marca:"Gilera", modelo:"SMX 250 ADV", año:2025, cc:249, tipo:"Adventure", peso:152, pot:24.1, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2024/12/GILERA_SMX250_ADV_180_ROJA.png", pros:["Motor 4 válvulas de 24,1 hp con radiador de aceite","Horquilla invertida y ruedas de 21/18","Parabrisas, cubrepuños y soporte trasero de serie"], contras:["Sin ABS","Tanque de 11,5 litros"], desc:"La adventure de 250 de Gilera con carburador. 300 mm de despeje, 6 marchas y equipamiento de viaje.", rating:4.2 },
  { id:"gil11", marca:"Gilera", modelo:"SMX 250 ADV 4V FI", año:2026, cc:249, tipo:"Adventure", peso:152, pot:24.1, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906084/catalogo/gil11.png", pros:["Inyección electrónica y ABS desconectable","Motor 4 válvulas de 24,1 hp con 6 velocidades","Horquilla invertida, ruedas de 21/18 y 300 mm de despeje"], contras:["Tanque de 11,5 litros","Refrigeración por aire y aceite"], desc:"La SMX 250 con inyección y ABS, lanzada en 2026. La Gilera más preparada para ruta larga y ripio.", rating:4.3 },
  { id:"gil12", marca:"Gilera", modelo:"AC4 250 4V FI", año:2026, cc:249, tipo:"Naked", peso:139, pot:24.1, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2026/09/GILERA_AC4_180_AZUL.png", pros:["Inyección electrónica y motor 4 válvulas de 24,1 hp","Horquilla invertida y llantas de aleación de 17","Discos con CBS, full LED y tanque de 15,5 litros"], contras:["Sin ABS","Refrigeración por aire y aceite"], desc:"La naked de 250 de Gilera, nueva en 2026. Mismo motor de la SMX 250 FI en chasis de calle con 6 velocidades.", rating:4.2 },
  { id:"gil13", marca:"Gilera", modelo:"Naviglio", año:2026, cc:278, tipo:"Scooter", peso:188, pot:26.4, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2026/07/GILERA_NAVIGLIO_180_ROJO.png", pros:["Motor 278cc inyección refrigerado por líquido, 26,4 hp","ABS, control de tracción y sensor de presión de neumáticos","Tablero TFT táctil de 5 pulgadas, keyless, dash cam y puños calefaccionados"], contras:["188 kg en seco","Tanque de 12,5 litros"], desc:"El maxi scooter de Gilera, novedad 2026. Ruedas de 15/14, baúl para un casco integral y equipamiento premium.", rating:4.4 },
  { id:"gil14", marca:"Gilera", modelo:"Piccola", año:2026, cc:150, tipo:"Scooter", peso:101, pot:10.1, par:"", consumo:"", img:"https://gilera.com.ar/wp-content/uploads/2024/12/piccola_verde_26.png", pros:["Scooter de 150 liviano, 101 kg","Disco adelante con CBS y full LED","Tablero LCD de 3 pulgadas, USB y baulera bajo asiento"], contras:["Freno trasero a tambor","Rueda trasera de 10 pulgadas"], desc:"El scooter urbano de Gilera con estilo retro. Motor 149,6cc de 10,1 hp, CVT y tanque de 6,2 litros.", rating:3.9 },

  // ═══════════════════════════════════════════════════════
  // MOTOMEL — línea 2026 (agregado 09-oct-2026)
  // Specs y fotos de motomel.com.ar (fichas técnicas de cada producto). Solo DLX, Max, Skua V6 y Strato Euro publican consumo.
  // mml1 = Skua 250 ya existía. Quedaron afuera por el tope de 14: Shark 150, Strato Alpino 150, Skua 125 Xtreme, Blitz Tunning/Full Aleación/Black Edition, S2 150 Start, Motocargo, STRATOe.
  // ═══════════════════════════════════════════════════════
  { id:"mml2", marca:"Motomel", modelo:"Sirius 190 CBS", año:2024, cc:199, tipo:"Naked", peso:128, pot:15, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906086/catalogo/mml2.png", pros:["Freno a disco en ambas ruedas con CBS","Motor CBB OHC con balanceador","Horquilla invertida y llantas de aleación"], contras:["Sin ABS","No publican el par motor"], desc:"La naked chica de Motomel: 199cc con balanceador, tanque de 16 litros y tablero digital. Para ciudad y alguna ruta corta.", rating:4 },
  { id:"mml3", marca:"Motomel", modelo:"Blitz 110 CBS", año:2024, cc:110, tipo:"Cub", peso:99, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906088/catalogo/mml3.png", pros:["Freno delantero a disco con CBS","Embrague automático y 4 marchas","Baúl bajo asiento porta casco"], contras:["Freno trasero a tambor","Tanque de 3,5 litros"], desc:"La cub más vendida de Motomel. Motor OHC de 110cc, embrague automático y bajo costo de mantenimiento.", rating:3.8 },
  { id:"mml4", marca:"Motomel", modelo:"Blitz 110 CBS Automática", año:2026, cc:110, tipo:"Cub", peso:99, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906089/catalogo/mml4.png", pros:["Transmisión automática, sin cambios ni embrague","Freno delantero a disco con CBS","Amortiguadores regulables"], contras:["Freno trasero a tambor"], desc:"La Blitz sin cambios: girás el puño y salís. Misma base de 110cc con la practicidad de un scooter.", rating:3.9 },
  { id:"mml5", marca:"Motomel", modelo:"Blitz One 110", año:2026, cc:110, tipo:"Cub", peso:99, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906091/catalogo/mml5.png", pros:["Iluminación principal LED","Freno a disco delantero con CBS","Cargador USB y tablero con indicador de marchas"], contras:["Freno trasero a tambor"], desc:"La Blitz renovada de 2026: LED, USB y pintura con protección UV sobre la mecánica de siempre.", rating:3.9 },
  { id:"mml6", marca:"Motomel", modelo:"Blitz Plus CBS", año:2024, cc:107, tipo:"Cub", peso:99, pot:7.1, par:8.3, consumo:"", img:"https://motomel.com.ar/wp-content/uploads/2024/11/BlitzPlus-slider-negro.png", pros:["Luces Full LED con DRL","Alarma con arranque a distancia","Llantas de aleación y cargador USB"], contras:["Freno trasero a tambor"], desc:"La Blitz mejor equipada: Full LED, alarma, USB y llantas de aleación sobre el mismo motor de 107cc.", rating:4 },
  { id:"mml7", marca:"Motomel", modelo:"DLX Deluxe CBS", año:2024, cc:108, tipo:"Cub", peso:90, pot:8, par:"", consumo:"2.1", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906093/catalogo/mml7.png", pros:["Consumo declarado de 2,1 L/100 km","Freno delantero a disco con CBS","Parrilla porta equipaje y asiento amplio"], contras:["Freno trasero a tambor","Llantas de rayos"], desc:"Cub de trabajo con rodado 17 en ambas ruedas, 90 kg y motor OHC de bajo consumo. Pensada para el día a día.", rating:3.8 },
  { id:"mml8", marca:"Motomel", modelo:"Max 110", año:2024, cc:108, tipo:"Cub", peso:80, pot:7.1, par:"", consumo:"2.1", img:"https://motomel.com.ar/wp-content/uploads/2023/01/Max110-slider-negro.png", pros:["Solo 80 kg y formato compacto","Motor OHC con consumo de 2,1 L/100 km","Freno a disco delantero y llantas de aleación"], contras:["Rodado 10, no es para ruta","Tanque de 3,8 litros"], desc:"Mini moto urbana con caja de 4 velocidades y embrague automático. Chiquita, liviana y apta para dos.", rating:3.7 },
  { id:"mml9", marca:"Motomel", modelo:"S2 150 Full", año:2026, cc:150, tipo:"Naked", peso:115, pot:13.4, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906095/catalogo/mml9.png", pros:["Frenos a disco adelante y atrás con CBS","Iluminación Full LED y tablero digital","Llantas de aleación y cargador USB A+C"], contras:["Sin ABS","Carburador"], desc:"La calle 150 tipo CG de Motomel en su versión más completa. Mecánica probada con equipamiento renovado en 2026.", rating:4.1 },
  { id:"mml10", marca:"Motomel", modelo:"Victory 150", año:2024, cc:149, tipo:"Naked", peso:105, pot:12, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906096/catalogo/mml10.png", pros:["Caja de 5 velocidades","Cargador USB de serie","Tanque de 10 litros"], contras:["Freno trasero a tambor","Llantas de rayos"], desc:"Calle 150 de entrada con estilo propio y 105 kg. Para quien quiere pasar de la cub a una moto con cambios.", rating:3.8 },
  { id:"mml11", marca:"Motomel", modelo:"Skua 150", año:2026, cc:150, tipo:"Trail", peso:122.5, pot:11.4, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906097/catalogo/mml11.png", pros:["Rueda de 21 adelante y 18 atrás","Tablero 100% digital y Full LED","Monoamortiguador trasero y parrilla portaequipaje"], contras:["Freno trasero a tambor","Potencia justa para ruta"], desc:"La Skua 150 modelo 2026: la on-off clásica de Motomel con tablero digital, LED, USB y asiento rediseñado.", rating:4 },
  { id:"mml12", marca:"Motomel", modelo:"Skua 150 V6", año:2024, cc:150, tipo:"Trail", peso:122.5, pot:11.4, par:"", consumo:"2.1", img:"https://motomel.com.ar/wp-content/uploads/2023/01/Skua150-V6-slider-negro.png", pros:["Motor OHV probado con 2,1 L/100 km","Suspensión de gran recorrido","Rueda de 21 adelante"], contras:["Freno trasero a tambor","Sin LED ni tablero digital"], desc:"La versión anterior de la Skua 150, todavía en venta. Misma mecánica, equipamiento más básico.", rating:3.9 },
  { id:"mml13", marca:"Motomel", modelo:"Skua 250 Adventure", año:2024, cc:250, tipo:"Adventure", peso:146, pot:17, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906099/catalogo/mml13.png", pros:["Caja de 6 velocidades","Horquilla invertida y disco en ambas ruedas","Faro LED con DRL y tanque de 15 litros"], contras:["Sin ABS","Ruedas de 17 en ambos ejes"], desc:"La Skua con carrocería adventure, parabrisas y ruedas de 17. Motor SOHC con balanceador y 6 marchas para ruta.", rating:4.1 },
  { id:"mml14", marca:"Motomel", modelo:"Strato Euro 150 CBS LED", año:2024, cc:150, tipo:"Scooter", peso:93, pot:9.5, par:"", consumo:"2.9", img:"https://motomel.com.ar/wp-content/uploads/2019/06/StratoEuro150-slider-negro.png", pros:["Full LED con DRL","Consumo declarado de 2,9 L/100 km","Freno a disco con CBS y llantas de aleación"], contras:["Rodado 10","Tanque de 5 litros"], desc:"Scooter de estilo europeo con variador automático y pintura perlada bicapa. Cómodo para dos en la ciudad.", rating:3.9 },
  { id:"mml15", marca:"Motomel", modelo:"XMM 250", año:2024, cc:223, tipo:"Enduro", peso:127, pot:16, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906101/catalogo/mml15.png", pros:["Rueda de 21 adelante y 18 atrás","Horquilla invertida y disco en ambas ruedas","Motor SOHC con balanceador"], contras:["Tanque chico de 7 litros","Sin ABS"], desc:"La enduro de Motomel: 223cc reales, 127 kg, suspensión invertida de largo recorrido y cubiertas semi taco.", rating:4 },
  // ═══════════════════════════════════════════════════════
  // KELLER — línea 2026 (agregado 09-oct-2026)
  // Specs y fotos de motoskeller.com.ar (sitio oficial; kellermotos.com.ar no existe). Keller no publica consumo ni par.
  // Las fotos de las Crono Classic son de frente (las laterales pesan 20 MB). La Miracle 200 solo tiene fotos pesadas (7,8 MB).
  // No figuran en el sitio: KN 110 (hoy es la Crono Classic), KR 260, Stratus 250, K65, Classic 150, Big Brother 250.
  // ═══════════════════════════════════════════════════════
  { id:"kel1", marca:"Keller", modelo:"Crono Classic Eco 110", año:2024, cc:107, tipo:"Cub", peso:89, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906105/catalogo/kel1.png", pros:["Solo 89 kg en seco","Embrague semiautomático de 4 marchas","Repuestos baratos y en todos lados"], contras:["Frenos a tambor adelante y atrás"], desc:"La 110 de entrada de Keller, fabricada en Córdoba. La más vendida del país en su versión base.", rating:3.6 },
  { id:"kel2", marca:"Keller", modelo:"Crono Classic Full 110", año:2024, cc:107, tipo:"Cub", peso:89, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906107/catalogo/kel2.png", pros:["Freno delantero a disco de 220 mm","Llantas de aleación","Tablero con nivel de combustible y marcha"], contras:["Freno trasero a tambor"], desc:"La Crono Classic con disco adelante y llantas de aleación. Misma mecánica de 107cc SOHC.", rating:3.8 },
  { id:"kel3", marca:"Keller", modelo:"Crono Classic Plus 110", año:2024, cc:107, tipo:"Cub", peso:89, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906110/catalogo/kel3.png", pros:["Alarma con corta corriente y mando a distancia","Llantas de aleación","Motor SOHC de 107cc"], contras:["Frenos a tambor adelante y atrás"], desc:"Versión intermedia de la Crono: suma alarma y llantas de aleación pero conserva los tambores.", rating:3.7 },
  { id:"kel4", marca:"Keller", modelo:"Crono Tunning 125", año:2024, cc:120, tipo:"Cub", peso:99, pot:8.7, par:"", consumo:"", img:"https://motoskeller.com.ar/wp-content/uploads/2024/05/Tunning_125_1-scaled.jpg", pros:["Motor de 120cc SOHC, 8,7 cv","Freno delantero a disco de 220 mm","Llantas de aleación de 13 pulgadas"], contras:["Freno trasero a tambor","Tanque de 4 litros"], desc:"La cub deportiva de Keller con ruedas anchas de 13 y escape visto. Un poco más de motor que la Crono.", rating:3.8 },
  { id:"kel5", marca:"Keller", modelo:"Stratus 150 Eco", año:2024, cc:149, tipo:"Naked", peso:115, pot:11, par:"", consumo:"", img:"https://motoskeller.com.ar/wp-content/uploads/2024/04/Stratus_Eco-scaled.jpg", pros:["Tanque de 12 litros","Caja de 5 velocidades","Bastidor reforzado"], contras:["Frenos a tambor adelante y atrás","Motor OHV básico"], desc:"Calle 150 estilo CG en su versión más económica. Mecánica simple y autonomía para el trabajo diario.", rating:3.6 },
  { id:"kel6", marca:"Keller", modelo:"Stratus 150 Full", año:2024, cc:149, tipo:"Naked", peso:115, pot:11, par:"", consumo:"", img:"https://motoskeller.com.ar/wp-content/uploads/2024/04/Stratus_Full-scaled.jpg", pros:["Freno delantero a disco de 240 mm","Llantas de aleación","Tanque de 12 litros"], contras:["Freno trasero a tambor","Motor OHV básico"], desc:"La Stratus con disco adelante y llantas de aleación. Misma base de 149cc y 5 marchas.", rating:3.8 },
  { id:"kel7", marca:"Keller", modelo:"Miracle 150 EVO", año:2024, cc:149, tipo:"Trail", peso:122, pot:11.5, par:"", consumo:"", img:"https://motoskeller.com.ar/wp-content/uploads/2024/05/Miracle-150-scaled.jpg", pros:["Frenos a disco adelante y atrás","Rueda de 21 adelante y 18 atrás","Horquilla reforzada de 31 mm y monoshock"], contras:["Motor OHV de 11,5 cv","Tablero analógico"], desc:"La on-off 150 de Keller con rueda de 21, discos en ambas ruedas y 310 mm de despeje.", rating:4 },
  { id:"kel8", marca:"Keller", modelo:"Miracle 200 EVO", año:2024, cc:196, tipo:"Trail", peso:122, pot:15, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791572741/catalogo/kel8.png", pros:["Motor de 196cc y 15 cv","Frenos a disco de 260 y 220 mm","Monoshock trasero y 310 mm de despeje"], contras:["Rueda delantera de 19, no de 21","Tablero analógico"], desc:"La Miracle con motor de 200: misma ciclística, más empuje para ruta. Ruedas de 19 y 17.", rating:4 },
  { id:"kel9", marca:"Keller", modelo:"Quasar 250", año:2024, cc:250, tipo:"Adventure", peso:140, pot:19, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906113/catalogo/kel9.png", pros:["Motor SOHC de 250cc con balanceador, 19 cv","Horquilla de 41 mm y rueda de 21","Tablero digital y top case de 44 l opcional"], contras:["Sin ABS","Llantas de acero estampado"], desc:"La trail grande de Keller con carenado adventure y parabrisas. 140 kg, 14 litros y 280 mm de despeje.", rating:4.2 },
  { id:"kel10", marca:"Keller", modelo:"Sygnus 150", año:2025, cc:150, tipo:"Scooter", peso:110, pot:9.4, par:"", consumo:"", img:"https://motoskeller.com.ar/wp-content/uploads/2025/08/FOTO-11-1-scaled.png", pros:["Variador CVT, sin cambios","Freno delantero a disco y llantas de aleación de 13","Alarma con corta corriente y mando a distancia"], contras:["Freno trasero a tambor","Tanque de 6 litros"], desc:"El scooter nuevo de Keller (2025): 149,5cc SOHC con forzador, ruedas de 13 y alarma de serie.", rating:3.9 },
  // ═══════════════════════════════════════════════════════
  // GUERRERO — línea 2026 (agregado 09-oct-2026)
  // Specs y fotos de tuguerrero.com.ar (sitio oficial, Wix; guerreromotos.com.ar no existe). Las fotos van por el CDN de Wix (static.wixstatic.com) redimensionadas a 1200 px.
  // Guerrero casi no publica potencia (solo la Day 70, en kW) y no publica par ni consumo.
  // No figuran en el sitio: G90, GR1 125 Gladiator, GC 150/200/250 Urban (hoy solo Urban 150), GXR 250 Tundra (hoy GXR 300 Rally), GXL 200, GMX 150/200/250, G3R 150, scooters Andiamo. Motocarga 200 se dejó afuera por utilitario.
  // ═══════════════════════════════════════════════════════
  { id:"gue1", marca:"Guerrero", modelo:"Trip 110 Full", año:2024, cc:108, tipo:"Cub", peso:100, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906114/catalogo/gue1.png", pros:["Frenos a disco adelante y atrás con sistema combinado","Caja de 4 marchas","Arranque eléctrico y a patada"], contras:["No publican la potencia"], desc:"La cub de Guerrero mejor equipada: 108cc y discos en ambas ruedas. Pensada para el uso diario.", rating:3.8 },
  { id:"gue2", marca:"Guerrero", modelo:"Trip 110 Econo", año:2024, cc:108, tipo:"Cub", peso:100, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906115/catalogo/gue2.png", pros:["Bajo costo de mantenimiento","Caja de 4 marchas","Arranque eléctrico y a patada"], contras:["Frenos a tambor adelante y atrás","No publican la potencia"], desc:"La Trip más económica: mismo motor de 108cc con frenos a tambor.", rating:3.6 },
  { id:"gue3", marca:"Guerrero", modelo:"Trip 110 Tuning", año:2024, cc:108, tipo:"Cub", peso:100, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906116/catalogo/gue3.png", pros:["Frenos a disco adelante y atrás con sistema combinado","Estética deportiva con llantas de aleación","Caja de 4 marchas"], contras:["No publican la potencia"], desc:"La Trip con look deportivo y frenos a disco en ambas ruedas.", rating:3.8 },
  { id:"gue4", marca:"Guerrero", modelo:"Trip 110 Automatic", año:2024, cc:108, tipo:"Cub", peso:100, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906117/catalogo/gue4.png", pros:["Transmisión automática, sin cambios","Arranque eléctrico y a patada","Bajo costo de mantenimiento"], contras:["Frenos a tambor adelante y atrás","No publican la potencia"], desc:"La Trip sin cambios: 108cc automática para quien quiere la simpleza de un scooter en formato cub.", rating:3.7 },
  { id:"gue5", marca:"Guerrero", modelo:"Trip Plus 125", año:2024, cc:120, tipo:"Cub", peso:90, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906118/catalogo/gue5.png", pros:["Motor de 120cc, un paso más que la 110","Frenos a disco adelante y atrás con sistema combinado","Solo 90 kg en seco"], contras:["No publican la potencia","Tanque de 4 litros"], desc:"La cub grande de Guerrero: 120cc, 4 marchas y discos en ambas ruedas en un chasis liviano.", rating:3.8 },
  { id:"gue6", marca:"Guerrero", modelo:"Urban 150", año:2024, cc:149, tipo:"Naked", peso:114, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906119/catalogo/gue6.png", pros:["Frenos a disco adelante y atrás con sistema combinado","Tanque de 14 litros","Caja de 5 marchas"], contras:["No publican la potencia"], desc:"La calle 150 de Guerrero tipo CG, con discos en ambas ruedas y buen tanque para el día a día.", rating:3.8 },
  { id:"gue7", marca:"Guerrero", modelo:"Day 70", año:2024, cc:70, tipo:"Cub", peso:74, pot:5.4, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906120/catalogo/gue7.png", pros:["Solo 74 kg en seco","Formato Dax, fácil de llevar","Caja de 4 marchas con arranque eléctrico"], contras:["Frenos a tambor adelante y atrás","Tanque de 2,5 litros"], desc:"La mini estilo Dax de Guerrero: 4 kW (5,4 cv), 74 kg y rodado chico. Para la ciudad y el barrio.", rating:3.6 },
  { id:"gue8", marca:"Guerrero", modelo:"Econo Clasic 110", año:2024, cc:110, tipo:"Cub", peso:89, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906121/catalogo/gue8.png", pros:["Solo 89 kg en seco","Amortiguadores regulables","Estilo clásico tipo Super Cub"], contras:["Frenos a tambor adelante y atrás","No publican la potencia"], desc:"Cub de línea clásica con cubre piernas, 110cc y 4 marchas. La más básica de la gama.", rating:3.6 },
  { id:"gue9", marca:"Guerrero", modelo:"GXL 150", año:2024, cc:149, tipo:"Trail", peso:120, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906122/catalogo/gue9.png", pros:["Caja de 5 marchas con embrague manual","Freno delantero a disco con sistema combinado","Tanque de 10 litros"], contras:["Freno trasero a tambor","No publican la potencia"], desc:"La on-off 150 de Guerrero: 120 kg, 5 marchas y cubiertas mixtas para tierra y asfalto.", rating:3.8 },
  { id:"gue10", marca:"Guerrero", modelo:"GXR 300 Rally", año:2024, cc:292, tipo:"Adventure", peso:150, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906123/catalogo/gue10.png", pros:["Motor de 292cc con 6 marchas","Frenos a disco adelante y atrás con sistema combinado","Carenado rally con parabrisas"], contras:["No publican la potencia","Tanque de 10,5 litros"], desc:"La trail grande de Guerrero con estética rally. 292cc, 6 velocidades y 150 kg en seco.", rating:4.1 },
  // ═══════════════════════════════════════════════════════
  // MONDIAL — línea 2026 (agregado 09-oct-2026)
  // Specs de las fichas técnicas PDF de mondial.com.ar (sitio oficial, grupo Corven; mondialmotos.com.ar no existe). Fotos del mismo sitio. Mondial no publica par ni consumo.
  // La LD 110 Max Automática no tiene ficha PDF: solo cilindrada y equipamiento de la página. Quedó afuera por el tope de 10: RD 150 RT (la RD 150 con tambores).
  // No figuran en el sitio: RD 200, HD 254, TD 200, FD 150/200, W 250 Super Dax.
  // ═══════════════════════════════════════════════════════
  { id:"mon1", marca:"Mondial", modelo:"LD 110 Max AD", año:2026, cc:110, tipo:"Cub", peso:92, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906124/catalogo/mon1.png", pros:["Freno delantero a disco con CBS","Llantas de aleación","Puerto USB A-C y baúl bajo asiento"], contras:["Freno trasero a tambor","Tanque de 3 litros"], desc:"La cub de Mondial en versión AD: suspensiones reforzadas, guardabarros alto y disco adelante. 110cc y 4 marchas.", rating:3.9 },
  { id:"mon2", marca:"Mondial", modelo:"LD 110 Max RT", año:2026, cc:110, tipo:"Cub", peso:92, pot:7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906125/catalogo/mon2.png", pros:["Puerto USB A-C de serie","Embrague semiautomático de 4 marchas","Solo 92 kg"], contras:["Frenos a tambor adelante y atrás","Tanque de 3 litros"], desc:"La LD 110 Max más económica, con llantas de rayos y tambores. Misma mecánica de 7 cv.", rating:3.7 },
  { id:"mon3", marca:"Mondial", modelo:"LD 110 Max Automática", año:2026, cc:110, tipo:"Cub", peso:"", pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906126/catalogo/mon3.png", pros:["Transmisión automática, sin cambios","Iluminación Full LED","Conexión USB y frenos con CBS"], contras:["No publican peso ni potencia"], desc:"La LD 110 Max sin cambios, nueva en 2026. LED, USB y CBS para moverse por la ciudad sin pensar en el embrague.", rating:3.9 },
  { id:"mon4", marca:"Mondial", modelo:"RD 150 AD", año:2024, cc:150, tipo:"Naked", peso:110, pot:11.5, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906127/catalogo/mon4.png", pros:["Freno delantero a disco con CBS","Llantas de aleación","Tanque de 12 litros y 5 marchas"], contras:["Freno trasero a tambor","Carburador"], desc:"La calle 150 de Mondial con llantas de aleación y disco adelante. 110 kg, liviana y económica.", rating:3.9 },
  { id:"mon5", marca:"Mondial", modelo:"RD 150 Classic", año:2024, cc:149, tipo:"Clásica", peso:105, pot:11.6, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906128/catalogo/mon5.png", pros:["Estética retro con tablero de dos relojes","Freno delantero a disco con CBS","Puerto USB y parrilla trasera"], contras:["Freno trasero a tambor","Tanque de 9 litros"], desc:"La RD 150 con vestido clásico: asiento plano, relojes blancos y llantas de rayos. 149cc y 105 kg.", rating:3.9 },
  { id:"mon6", marca:"Mondial", modelo:"W150 Café Racer", año:2024, cc:149, tipo:"Clásica", peso:105, pot:11.5, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906129/catalogo/mon6.png", pros:["Estilo café racer con asiento bajo","Tablero con cuenta RPM analógico y display digital","Freno delantero a disco"], contras:["Freno trasero a tambor","Posición de manejo exigente para el día a día"], desc:"La café racer chica de Mondial: 149cc, 5 marchas, 105 kg y mucha estética. Para la ciudad con estilo.", rating:4 },
  { id:"mon7", marca:"Mondial", modelo:"HD 250", año:2024, cc:252, tipo:"Custom", peso:145, pot:17.4, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906130/catalogo/mon7.png", pros:["Motor bicilíndrico de 252cc","Frenos a disco adelante y atrás con CBS","Tanque de 14 litros y llantas de aleación"], contras:["Sin ABS","Doble carburador, más mantenimiento"], desc:"La custom chica de Mondial con motor bicilíndrico refrigerado por aceite. Estética vintage, 145 kg y 5 marchas.", rating:4.1 },
  { id:"mon8", marca:"Mondial", modelo:"TD 150 L", año:2026, cc:150, tipo:"Trail", peso:150, pot:11.5, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906130/catalogo/mon8.png", pros:["Rueda de 21 adelante y 18 atrás","Puerto USB A-C e indicador de combustible","Freno delantero a disco"], contras:["Freno trasero a tambor","Publican 150 kg, pesada para una 150"], desc:"La on-off 150 de Mondial renovada, con rodado 21/18, USB y tanque de 11,5 litros.", rating:3.9 },
  { id:"mon9", marca:"Mondial", modelo:"TD 250", año:2024, cc:250, tipo:"Trail", peso:150, pot:15, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906131/catalogo/mon9.png", pros:["Motor de 250cc y 15 cv","Rueda de 21 adelante y 18 atrás","Tanque de 12 litros"], contras:["Freno trasero a tambor","Sin ABS"], desc:"La trail más potente de Mondial: 250cc, 5 marchas y suspensión de largo recorrido para salir del asfalto.", rating:4 },
  { id:"mon10", marca:"Mondial", modelo:"Dax 70", año:2024, cc:70, tipo:"Cub", peso:77.5, pot:4.7, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906132/catalogo/mon10.png", pros:["Solo 77,5 kg","Embrague automático de 4 marchas","Parrilla trasera y asiento antideslizante"], contras:["Frenos a tambor adelante y atrás","4,7 cv, solo para ciudad"], desc:"La Dax clásica fabricada por Mondial: 70cc, embrague automático, cromados y formato de siempre.", rating:3.7 },

  // ═══════════════════════════════════════════════════════
  // KAWASAKI — huecos de gama (agregado 09-oct-2026)
  // Specs y fotos de kawasaki.ar (sitio oficial de Kawasaki Argentina / Grupo Corven;
  // kawasaki.com.ar no resuelve y kawasakiargentina.com.ar está secuestrado por spam).
  // Kawasaki no publica consumo. Ninja 400 / Z 400 ya no figuran: las reemplazaron la 500.
  // ═══════════════════════════════════════════════════════
  { id:"kaw8", marca:"Kawasaki", modelo:"Ninja 500", año:2025, cc:451, tipo:"Sport", peso:173, pot:51, par:43, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2026/08/web-Kawasaki-Ninja-500se-1080x800px-1-1024x759.jpg", pros:["Bicilíndrico de 451cc con 51cv","ABS de serie y embrague antirrebote","173 kg, fácil para arrancar en deportivas"], contras:["Carenado y posición más de paseo que de pista"], desc:"La deportiva de entrada de Kawasaki, reemplazo de la Ninja 400. Bicilíndrico de 451cc, 51cv y 173 kg en orden de marcha.", rating:4.3 },
  { id:"kaw9", marca:"Kawasaki", modelo:"Z 500", año:2025, cc:451, tipo:"Naked", peso:"", pot:51, par:43, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2025/09/24ER500E_44SBK1SRF3CG_A-1536x1153.jpg", pros:["Mismo bicilíndrico de 451cc de la Ninja 500","ABS y embrague asistido con antirrebote","Disco semiflotante de 310 mm adelante"], contras:["Kawasaki Argentina no publica el peso"], desc:"La naked chica de la familia Z, que reemplazó a la Z 400. 451cc, 51cv y 43 Nm.", rating:4.2 },
  { id:"kaw10", marca:"Kawasaki", modelo:"Versys 300", año:2024, cc:296, tipo:"Adventure", peso:175, pot:40, par:25.7, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2026/08/web-Kawasaki-Versys300-1080x800px-1.jpg", pros:["Bicilíndrico de 296cc que gira alto (40cv a 11.500 rpm)","Tanque de 17 litros","Rueda de 19 adelante y se ensambla en Venado Tuerto"], contras:["Motor justo para ruta con viento o carga"], desc:"La trail chica de Kawasaki, pensada para viajar liviano. 296cc, 40cv y 175 kg en orden de marcha.", rating:4.1 },
  { id:"kaw11", marca:"Kawasaki", modelo:"Versys 650", año:2024, cc:649, tipo:"Adventure", peso:219, pot:67, par:61, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2026/08/web-Kawasaki-Versys650-1080x800px-1.jpg", pros:["Bicilíndrico de 649cc con 67cv","Tanque de 21 litros para tramos largos","Posición cómoda y parabrisas regulable"], contras:["219 kg, pesada para tierra"], desc:"La ruteras media de Kawasaki, con el mismo motor de la Ninja 650. 649cc, 67cv y tanque de 21 litros.", rating:4.3 },
  { id:"kaw12", marca:"Kawasaki", modelo:"Z 650", año:2024, cc:649, tipo:"Naked", peso:191, pot:67, par:66, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2026/08/web-Kawasaki-z650-1080x800px-1.jpg", pros:["67cv y 66 Nm en 191 kg","Bicilíndrico fácil de usar en ciudad","Se ensambla en Argentina (Corven)"], contras:["Tanque de 15 litros"], desc:"La naked media de Kawasaki. 649cc, 67cv y 191 kg en orden de marcha.", rating:4.2 },
  { id:"kaw13", marca:"Kawasaki", modelo:"Z 900 RS", año:2024, cc:948, tipo:"Clásica", peso:212, pot:125, par:98.6, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2026/03/z-900rs-05.jpg", pros:["Tetracilíndrico de 948cc con 125cv","Estética retro inspirada en la Z1 de los 70","Base mecánica de la Z 900"], contras:["Precio alto para una naked","212 kg"], desc:"La retro de Kawasaki sobre la base de la Z 900. Cuatro cilindros, 948cc y 125cv con look de Z1.", rating:4.5 },
  { id:"kaw14", marca:"Kawasaki", modelo:"Vulcan S", año:2024, cc:649, tipo:"Custom", peso:229, pot:61, par:62.4, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2025/10/22MY_Vulcan-S_-GY1_ACT-4.001-1024x717.jpg", pros:["Bicilíndrico de 649cc con 61cv","Ergonomía ajustable (Ergo-Fit)","Asiento bajo, apta para piloto chico"], contras:["Tanque de 14 litros","229 kg"], desc:"La custom media de Kawasaki, con el motor de la Ninja 650 y asiento bajo. 649cc y 61cv.", rating:4.1 },
  { id:"kaw15", marca:"Kawasaki", modelo:"KLR 650", año:2024, cc:652, tipo:"Adventure", peso:210, pot:40, par:53, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906133/catalogo/kaw15.jpg", pros:["Tanque de 23 litros, autonomía de viaje","Monocilíndrico de 652cc con 53 Nm","Mecánica simple y robusta"], contras:["Solo 40cv","210 kg para una mono"], desc:"La trail de viaje clásica de Kawasaki. Monocilíndrico de 652cc, 23 litros de tanque y 210 kg.", rating:4.2 },
  { id:"kaw16", marca:"Kawasaki", modelo:"Ninja ZX-6R", año:2024, cc:636, tipo:"Sport", peso:"", pot:124, par:69, consumo:"", img:"https://kawasaki.ar/wp-content/uploads/2025/09/2020_Ninja-ZX-6R_GN1_ACT-5.001-1024x717.jpg", pros:["Tetracilíndrico de 636cc con 124cv a 13.000 rpm","Pinzas radiales monobloque y discos de 310 mm","Supersport de pista homologada"], contras:["Kawasaki Argentina no publica el peso","Incómoda para uso diario"], desc:"La supersport media de Kawasaki. Cuatro cilindros de 636cc, 124cv y frenos radiales.", rating:4.4 },
  { id:"kaw17", marca:"Kawasaki", modelo:"Ninja ZX-4RR KRT", año:2024, cc:399, tipo:"Sport", peso:"", pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906134/catalogo/kaw17.png", pros:["Tetracilíndrico de 399cc, único en su categoría","Doble disco de 290 mm con ABS","Colores KRT de competición"], contras:["Kawasaki Argentina no publica potencia ni peso"], desc:"La deportiva chica de cuatro cilindros. 399cc con doble disco adelante y ABS.", rating:4.3 },
  { id:"kaw18", marca:"Kawasaki", modelo:"KLE 500", año:2026, cc:451, tipo:"Adventure", peso:194, pot:46, par:43, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906135/catalogo/kaw18.jpg", pros:["Bicilíndrico de 451cc con 46cv","Rueda de 21 adelante y llantas rayadas","194 kg en orden de marcha"], contras:["Tanque de 15,9 litros","Modelo nuevo, repuestos y red recién arrancan"], desc:"La trail media nueva de Kawasaki, con el motor de la Ninja/Z 500 y rueda de 21. 451cc, 46cv y 194 kg.", rating:4.3 },

  // ═══════════════════════════════════════════════════════
  // SUZUKI — huecos de gama (agregado 09-oct-2026)
  // Specs y fotos de suzukimotosarg.com.ar (Suzuki Motos Argentina / Grupo La Emilia;
  // suzukimotos.com.ar hoy redirige a un sitio de casinos). Suzuki no publica consumo.
  // Las fotos oficiales muestran la moto con el baúl Shad que viene incluido.
  // ═══════════════════════════════════════════════════════
  { id:"suz6", marca:"Suzuki", modelo:"Gixxer 150i", año:2024, cc:149, tipo:"Naked", peso:140, pot:13.4, par:13.8, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906137/catalogo/suz6.png", pros:["Inyección electrónica y faro LED","Disco adelante y atrás","Viene con baúl Shad SH34"], contras:["Sin ABS","Caja de 5 velocidades"], desc:"La naked chica de Suzuki para ciudad, fabricada en San Nicolás. 149cc con inyección y 140 kg.", rating:3.9 },
  { id:"suz7", marca:"Suzuki", modelo:"Gixxer 150i ABS", año:2026, cc:149, tipo:"Naked", peso:141, pot:13.4, par:13.8, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906139/catalogo/suz7.png", pros:["ABS, raro en el segmento 150","Inyección electrónica","Viene con baúl incluido"], contras:["Caja de 5 velocidades","Misma potencia que la versión sin ABS"], desc:"La Gixxer 150i con ABS. 149cc, 13,4cv y 141 kg.", rating:4 },
  { id:"suz8", marca:"Suzuki", modelo:"V-Strom 250 SX", año:2026, cc:249, tipo:"Adventure", peso:167, pot:26, par:22.2, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906141/catalogo/suz8.png", pros:["Monocilíndrico de 249cc con 6 velocidades","ABS y frenos ByBre","Rueda de 19 adelante y viene con top case Shad TR41"], contras:["Refrigeración por aceite, motor justo en ruta"], desc:"La trail de entrada de Suzuki. 249cc, 26cv, ABS y 167 kg.", rating:4 },
  { id:"suz9", marca:"Suzuki", modelo:"V-Strom 650 XT", año:2024, cc:645, tipo:"Adventure", peso:216, pot:70, par:62, consumo:"", img:"https://suzukimotosarg.com.ar/wp-content/uploads/2021/09/SUZUKI-VSTROM-AMARILLO.png", pros:["V-twin de 645cc con 70cv","ABS doble canal y control de tracción","Llantas rayadas y baúl Shad SH58X incluido"], contras:["216 kg","Suspensión no regulable adelante"], desc:"La ruteras media de Suzuki, con el V-twin de 645cc probado hace años. 70cv y ABS.", rating:4.3 },
  { id:"suz10", marca:"Suzuki", modelo:"V-Strom 800 DE", año:2025, cc:776, tipo:"Adventure", peso:230, pot:83, par:78, consumo:"", img:"https://suzukimotosarg.com.ar/wp-content/uploads/2026/09/800DE.jpg", pros:["Bicilíndrico de 776cc con 83cv","Suspensión Showa regulable y modos de manejo","Rueda de 21 adelante y modo Gravel"], contras:["230 kg"], desc:"La trail media nueva de Suzuki, con rueda de 21 y electrónica completa. 776cc, 83cv y 78 Nm.", rating:4.4 },
  { id:"suz11", marca:"Suzuki", modelo:"DR 650", año:2024, cc:644, tipo:"Trail", peso:147, pot:43, par:"", consumo:"", img:"https://suzukimotosarg.com.ar/wp-content/uploads/2024/01/SUZUKI650-BAUL-NEGRA.png", pros:["147 kg en seco, liviana para su cilindrada","Rueda de 21 y 270 mm de despeje","Mecánica simple con carburador"], contras:["Sin ABS y caja de 5 velocidades","Suzuki no publica el torque"], desc:"La doble propósito grande de Suzuki, sin cambios en años. Monocilíndrico de 644cc, 43cv y 147 kg.", rating:4.1 },
  { id:"suz12", marca:"Suzuki", modelo:"AX 100", año:2024, cc:98, tipo:"Naked", peso:86, pot:10, par:9.5, consumo:"", img:"https://suzukimotosarg.com.ar/wp-content/uploads/2021/08/ax100-roja.png", pros:["86 kg, muy liviana","Motor 2 tiempos de bajo consumo","Fabricada en San Nicolás"], contras:["Frenos a tambor","Arranque a patada y 4 velocidades"], desc:"La 2 tiempos de trabajo de Suzuki, fabricada en Argentina. 98cc, 10cv y 86 kg.", rating:3.7 },

  // ═══════════════════════════════════════════════════════
  // BENELLI — huecos de gama (agregado 09-oct-2026)
  // Specs y fotos de benelli.com/ar-es (sitio oficial, benelli.com.ar redirige ahí).
  // Benelli publica peso en seco y potencia en kW (convertida a cv), no consumo.
  // ═══════════════════════════════════════════════════════
  { id:"ben2", marca:"Benelli", modelo:"TNT 15", año:2024, cc:150, tipo:"Naked", peso:"", pot:12.2, par:11, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/14538/conversions/2560x2180-md.png", pros:["Frenos a disco con CBS","Tanque de 16 litros","Estética de naked grande"], contras:["Sin ABS","Benelli no publica el peso"], desc:"La naked chica de Benelli para ciudad. 150cc, 12cv y frenos combinados.", rating:3.8 },
  { id:"ben3", marca:"Benelli", modelo:"TRK 251", año:2024, cc:249, tipo:"Adventure", peso:164, pot:25.8, par:21.1, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906142/catalogo/ben3.png", pros:["Monocilíndrico de 249cc refrigerado por líquido","Tanque de 18 litros","Chasis de enrejado y llantas de aleación"], contras:["Ruedas de 17, más ruta que tierra","La ficha no menciona ABS"], desc:"La trail chica de Benelli, para ruta y ciudad. 249cc, 26cv y 164 kg en seco.", rating:3.9 },
  { id:"ben4", marca:"Benelli", modelo:"TRK 502", año:2024, cc:500, tipo:"Adventure", peso:210, pot:47.6, par:46, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/7144/conversions/2560x2180-md.png", pros:["Bicilíndrico de 500cc con 48cv","Tanque de 20 litros","Doble disco adelante"], contras:["210 kg en seco","Ruedas de 17, versión de asfalto"], desc:"La ruteras media de Benelli en versión asfalto. Bicilíndrico de 500cc, 48cv y 20 litros de tanque.", rating:4 },
  { id:"ben5", marca:"Benelli", modelo:"TRK 502 X", año:2024, cc:500, tipo:"Adventure", peso:213, pot:47.6, par:46, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/7186/conversions/2560x2180-md.png", pros:["Rueda de 19 adelante y llantas rayadas","Tanque de 20 litros","Bicilíndrico de 500cc con 46 Nm"], contras:["213 kg en seco","Asiento alto (860 mm)"], desc:"La TRK 502 con ruedas rayadas y rueda de 19 para tierra. 500cc, 48cv y 213 kg.", rating:4.1 },
  { id:"ben6", marca:"Benelli", modelo:"TRK 702", año:2024, cc:698, tipo:"Adventure", peso:219, pot:76.2, par:68.2, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/7783/conversions/2560x2180-(2)-md.png", pros:["Bicilíndrico de 698cc con 76cv","Tanque de 20 litros","Asiento accesible (810 mm)"], contras:["219 kg en seco","Ruedas de 17, pensada para asfalto"], desc:"La hermana de asfalto de la TRK 702 X. 698cc, 76cv y ruedas de 17.", rating:4.2 },
  { id:"ben7", marca:"Benelli", modelo:"Leoncino 250", año:2024, cc:249, tipo:"Scrambler", peso:155, pot:25.8, par:21, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/69/conversions/2560x2180-copy-2-md.png", pros:["155 kg en seco","ABS de serie","Cubiertas Metzeler"], contras:["Tanque de 12,5 litros"], desc:"La scrambler chica de Benelli. 249cc, 26cv, ABS y 155 kg.", rating:4 },
  { id:"ben8", marca:"Benelli", modelo:"302S", año:2024, cc:300, tipo:"Naked", peso:195, pot:38.1, par:25.6, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/2143/conversions/2560x2180-md.png", pros:["Bicilíndrico de 300cc con 38cv","Doble disco adelante con ABS","Cubiertas Pirelli"], contras:["195 kg en seco, pesada para 300cc"], desc:"La naked media-chica de Benelli (sucesora de la TNT 300). Bicilíndrico de 300cc, 38cv y ABS.", rating:4 },
  { id:"ben9", marca:"Benelli", modelo:"Leoncino 800 Trail", año:2024, cc:754, tipo:"Scrambler", peso:235, pot:77.3, par:67, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/11701/conversions/2560x2180-(1)-md.png", pros:["Bicilíndrico de 754cc con 77cv","Rueda de 19 adelante y llantas rayadas","Doble disco adelante"], contras:["235 kg en seco","Tanque de 15 litros"], desc:"La scrambler grande de Benelli. 754cc, 77cv y rueda de 19.", rating:4.1 },
  { id:"ben10", marca:"Benelli", modelo:"TNT 600i", año:2024, cc:600, tipo:"Naked", peso:234, pot:81.6, par:55, consumo:"", img:"https://cdn.keeway.com/benelli-3-0/media/6613/conversions/2560x2180-(1)-md.png", pros:["Tetracilíndrico de 600cc con 82cv","Doble disco adelante","Sonido de 4 cilindros a precio contenido"], contras:["234 kg en seco","Tanque de 15 litros"], desc:"La naked de cuatro cilindros de Benelli. 600cc, 82cv y 234 kg.", rating:4 },

  // ═══════════════════════════════════════════════════════
  // ROYAL ENFIELD — huecos de gama (agregado 09-oct-2026)
  // Specs y fotos de royalenfield.com/ar/es (sitio oficial Argentina); las fichas técnicas
  // están en PDF o imagen. En Argentina la Guerrilla 450 se vende como "GRR 450".
  // ═══════════════════════════════════════════════════════
  { id:"re5", marca:"Royal Enfield", modelo:"Classic 350", año:2024, cc:346, tipo:"Clásica", peso:"", pot:"", par:"", consumo:"", img:"https://www.royalenfield.com/content/dam/royal-enfield/argentina/motorcycles/classic-350/colors/studio-shots/dual-channel/chrome-red/01-chrome-red.png", pros:["ABS doble canal","Disco de 280 mm adelante","Ruedas de 19 y 18, estética de posguerra"], contras:["Royal Enfield Argentina no publica potencia ni peso","Tanque de 12 litros"], desc:"La clásica por excelencia de Royal Enfield. Monocilíndrico de 350 con ABS doble canal.", rating:4.2 },
  { id:"re6", marca:"Royal Enfield", modelo:"Interceptor 650", año:2024, cc:648, tipo:"Clásica", peso:202, pot:47, par:52, consumo:"", img:"https://www.royalenfield.com/content/dam/royal-enfield/mexico/motorcycles/interceptor/colours/new/studio-shots/barcelona-blue/side-view.png", pros:["Bicilíndrico de 648cc con 47cv y 52 Nm","ABS doble canal Bosch","Chasis desarrollado con Harris Performance"], contras:["202 kg en seco","Suspensión básica"], desc:"La roadster clásica de Royal Enfield, inspirada en la Interceptor de los 60. 648cc, 47cv y ABS.", rating:4.3 },
  { id:"re7", marca:"Royal Enfield", modelo:"GRR 450", año:2025, cc:452, tipo:"Naked", peso:184, pot:40, par:40, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906144/catalogo/re7.png", pros:["Motor Sherpa de 452cc refrigerado por líquido, 40cv","Modos de manejo, acelerador electrónico y TFT de 4 pulgadas","Asiento a 780 mm, ABS doble canal"], contras:["Tanque de 11 litros"], desc:"La roadster moderna de Royal Enfield (Guerrilla 450 en el resto del mundo). 452cc, 40cv y 184 kg en orden de marcha.", rating:4.3 },
  { id:"re8", marca:"Royal Enfield", modelo:"Himalayan 411", año:2024, cc:411, tipo:"Adventure", peso:199, pot:24.5, par:32, consumo:"", img:"https://www.royalenfield.com/content/dam/royal-enfield/colombia/motorcycles/himalayan/colours/new-colors/studio-shots/glacier-blue/side-view.png", pros:["Rueda de 21 adelante y 200 mm de recorrido","ABS desconectable atrás","Mecánica simple, refrigerada por aire"], contras:["Solo 24,5cv","199 kg en orden de marcha"], desc:"La trail original de Royal Enfield, que sigue en venta junto a la 450. 411cc, 24,5cv y rueda de 21.", rating:4 },
  { id:"re9", marca:"Royal Enfield", modelo:"Shotgun 650", año:2024, cc:648, tipo:"Custom", peso:"", pot:47.1, par:52.3, consumo:"", img:"https://www.royalenfield.com/content/dam/royal-enfield/shotgun-650/colors/drill-green/drill-green-000.png", pros:["Bicilíndrico de 648cc con 47cv y 52 Nm","Horquilla invertida Showa y ABS doble canal","Asiento a 795 mm y puerto USB"], contras:["Royal Enfield Argentina no publica el peso en orden de marcha","Tanque de 13,8 litros"], desc:"La bobber moderna de Royal Enfield sobre el motor 650. 648cc, 47cv y horquilla invertida.", rating:4.2 },
  { id:"re10", marca:"Royal Enfield", modelo:"Super Meteor 650", año:2024, cc:648, tipo:"Custom", peso:"", pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906146/catalogo/re10.png", pros:["Bicilíndrico de 648cc en paralelo","Tanque de 15,7 litros","Postura cruiser relajada para viajar"], contras:["Royal Enfield Argentina no publica potencia ni peso"], desc:"La cruiser grande de Royal Enfield con el motor 650. Pensada para viajes largos y tranquilos.", rating:4.2 },

  // ═══════════════════════════════════════════════════════
  // HERO — línea Argentina (agregado 09-oct-2026)
  // Specs y fotos de heromotos.com.ar (Hero MotoCorp Argentina / Gilera Motors).
  // Hero no publica consumo. Pesos = "peso sin carga" de la ficha oficial.
  // ═══════════════════════════════════════════════════════
  { id:"her1", marca:"Hero", modelo:"Hunk 125R", año:2026, cc:125, tipo:"Naked", peso:133, pot:10.7, par:10.4, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2026/07/hunk125R-BluePerfil.png", pros:["Suspensión Showa de 37 mm","Luces full LED con freno combinado IBS","Caja de 5 velocidades y tanque de 10 L"], contras:["Alimentación por carburador","Freno trasero a tambor"], desc:"La entrada a la familia Hunk: 125cc con suspensión Showa y luces LED, pensada para el uso diario.", rating:3.9 },
  { id:"her2", marca:"Hero", modelo:"Hunk 150 DD", año:2024, cc:149, tipo:"Naked", peso:145, pot:15.2, par:13.5, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2024/09/hunk150DD-negroRojo.png", pros:["Disco adelante y atrás (240/220 mm)","Tecnología i3S de ahorro de combustible","Tanque de 12,4 L y tablero análogo-digital"], contras:["Sin ABS","Luz halógena"], desc:"Naked de 150cc con doble disco y motor de 15 cv. Garantía de 4 años o 40.000 km como toda la línea Hero.", rating:3.9 },
  { id:"her3", marca:"Hero", modelo:"Hunk 150 Xtec", año:2025, cc:149, tipo:"Naked", peso:146.5, pot:14.2, par:12.6, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2025/08/HUNK150Xtec_Black.png", pros:["ABS con disco de 276 mm adelante","Tablero digital y luces full LED","Cubiertas 100/80 y 130/70 R17 radial"], contras:["Motor de 2 válvulas, algo justo en ruta"], desc:"La Hunk 150 renovada: ABS, luces LED y tablero digital en un paquete de 150cc para la ciudad.", rating:4 },
  { id:"her4", marca:"Hero", modelo:"Hunk 160R", año:2024, cc:163, tipo:"Naked", peso:139.5, pot:15, par:14, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2024/06/hunk160r_NegraAr.png", pros:["ABS monocanal con disco de 276 mm","Horquilla de 37 mm y mono ajustable en 7 posiciones","Luces LED y peso contenido (139,5 kg)"], contras:["Refrigeración por aire, 2 válvulas"], desc:"La naked de 160cc más liviana de Hero, con ABS y suspensión ajustable. Buena relación equipamiento-cilindrada.", rating:4 },
  { id:"her5", marca:"Hero", modelo:"Hunk 160R 4V", año:2025, cc:163, tipo:"Naked", peso:146, pot:16.6, par:14.6, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2025/09/Hunk160R-4VPro_negroRojo.png", pros:["Motor 4 válvulas refrigerado por aceite con inyección","ABS monocanal y disco de 276 mm","Tablero digital y cubierta trasera radial 130/70"], contras:["ABS solo adelante"], desc:"La versión 4 válvulas e inyección de la Hunk 160R: más potencia y mejor respuesta, mismo chasis ágil.", rating:4.1 },
  { id:"her6", marca:"Hero", modelo:"Xpulse 200 Advance", año:2024, cc:200, tipo:"Trail", peso:156, pot:18.1, par:17.1, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2024/08/Xpulse200Ad_NegroRojoAr.png", pros:["Rueda de 21 adelante y 18 atrás","Discos de 276/220 mm con ABS","Tanque de 13 L y luces full LED"], contras:["Motor de 2 válvulas, carburado","No es liviana para su potencia (156 kg)"], desc:"La doble propósito de Hero con rueda de 21. Heredera del programa de rally de la marca, pensada para ripio y ruta tranquila.", rating:4 },
  { id:"her7", marca:"Hero", modelo:"Xpulse 200 4V", año:2026, cc:200, tipo:"Trail", peso:159, pot:18.9, par:17.35, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2025/09/xpulse_negro.png", pros:["Motor 4 válvulas con inyección y refrigeración por aire y aceite","Tablero digital con Bluetooth y navegación giro a giro","ABS monocanal, rueda de 21 y 13 L de tanque"], contras:["ABS solo adelante","159 kg sin carga"], desc:"La Xpulse con motor 4V e inyección: la trail chica de Hero más completa, con conectividad y ABS.", rating:4.2 },
  { id:"her8", marca:"Hero", modelo:"Dash 125", año:2024, cc:125, tipo:"Scooter", peso:112, pot:9.1, par:10.2, consumo:"", img:"https://heromotos.com.ar/wp-content/uploads/2024/08/Dash_negraAr.png", pros:["Transmisión automática CVT","Disco adelante de 190 mm y luz LED","Solo 112 kg, fácil de mover"], contras:["Carburador","Tambor atrás y tanque de 5,5 L"], desc:"Scooter urbano de 125cc, liviano y simple. Para moverse en ciudad sin complicaciones.", rating:3.8 },
  // ═══════════════════════════════════════════════════════
  // HUSQVARNA — calle, travel y enduro (agregado 09-oct-2026)
  // Specs y fotos de husqvarna-motorcycles.com/es-ar (importa Grupo Simpa).
  // Pesos = "sin combustible". Las enduro (FE/TE) no publican potencia ni par; la 401 no publica par.
  // ═══════════════════════════════════════════════════════
  { id:"hus1", marca:"Husqvarna", modelo:"Svartpilen 401", año:2026, cc:399, tipo:"Naked", peso:159, pot:44.9, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906147/catalogo/hus1.png", pros:["Monocilíndrico DOHC de 399cc y 33 kW","Frenos ByBre radiales con discos de 320/240 mm","Ride-by-wire, 6 marchas y 159 kg sin nafta"], contras:["No publican el par","Asiento de 820 mm, alto para algunos"], desc:"La scrambler urbana de Husqvarna sobre la base del motor 399 de KTM. Diseño sueco, electrónica moderna y peso contenido.", rating:4.3 },
  { id:"hus2", marca:"Husqvarna", modelo:"Svartpilen 801", año:2026, cc:799, tipo:"Naked", peso:181, pot:105, par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906147/catalogo/hus2.png", pros:["Bicilíndrico paralelo de 799cc y 105 cv","ABS Bosch de doble canal y pinzas radiales de 4 pistones","Regulación del freno motor y 181 kg sin nafta"], contras:["No publican el par en la ficha local"], desc:"La Svartpilen grande, con el bicilíndrico LC8c de 799cc. Naked de carácter con electrónica completa.", rating:4.4 },
  { id:"hus3", marca:"Husqvarna", modelo:"Norden 901", año:2026, cc:889, tipo:"Adventure", peso:204, pot:104.7, par:100, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906149/catalogo/hus3.png", pros:["Bicilíndrico de 889cc, 77 kW y 100 Nm","ABS offroad Bosch de doble canal y 19 L de tanque","Chasis de cromo-molibdeno y asiento regulable 859/879 mm"], contras:["204 kg sin nafta, pesada en trial técnico"], desc:"La adventure de Husqvarna para viajar lejos con ripio en el medio. Misma base mecánica que la KTM 890 Adventure.", rating:4.4 },
  { id:"hus4", marca:"Husqvarna", modelo:"701 Enduro", año:2027, cc:693, tipo:"Adventure", peso:152, pot:79, par:73, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906149/catalogo/hus4.png", pros:["Monocilíndrico de 693cc con 79 cv y 73 Nm","Solo 152 kg sin nafta para una 700","Subchasis autoportante con tanque de 13,5 L"], contras:["Asiento de 935 mm","Monocilíndrico grande: vibra en ruta larga"], desc:"La enduro de viaje de Husqvarna: motor de 693cc en un chasis liviano. Para tierra en serio con patente.", rating:4.3 },
  { id:"hus5", marca:"Husqvarna", modelo:"701 Supermoto", año:2027, cc:693, tipo:"Naked", peso:152, pot:79, par:73, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906150/catalogo/hus5.png", pros:["79 cv y 73 Nm en 152 kg sin nafta","ABS Supermoto y Sport desconectable","Frenos Brembo y ruedas de 17"], contras:["Asiento de 935 mm","Autonomía justa (13,5 L)"], desc:"La supermoto de 693cc de Husqvarna. Misma mecánica que la 701 Enduro, con ruedas de 17 y modo ABS Supermoto.", rating:4.3 },
  { id:"hus6", marca:"Husqvarna", modelo:"FE 350", año:2027, cc:350, tipo:"Enduro", peso:109.2, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906150/catalogo/hus6.png", pros:["Monocilíndrico 4T de 350cc en 109 kg sin nafta","6 marchas y tanque de 8 L","Frenos hidráulicos y gestión electrónica del motor"], contras:["No publican potencia ni par","Asiento de 952 mm"], desc:"La enduro 4T intermedia de Husqvarna: agilidad de 250 con empuje de 450. Homologable para competir.", rating:4.4 },
  { id:"hus7", marca:"Husqvarna", modelo:"FE 450", año:2027, cc:450, tipo:"Enduro", peso:109.6, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906152/catalogo/hus7.png", pros:["Monocilíndrico 4T de 450cc en 109,6 kg","6 marchas y arranque eléctrico","Tanque de 8 L y asiento de 952 mm"], contras:["No publican potencia ni par","Exige mano en terreno técnico"], desc:"Enduro 4T de 450cc para quien quiere par de sobra en subida y velocidad en lo abierto.", rating:4.3 },
  { id:"hus8", marca:"Husqvarna", modelo:"FE 501", año:2027, cc:511, tipo:"Enduro", peso:110.6, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906152/catalogo/hus8.png", pros:["La 4T más grande de la gama: 511cc","110,6 kg sin nafta y 6 marchas","Gestión electrónica y arranque eléctrico"], contras:["No publican potencia ni par","Cilindrada grande para iniciarse"], desc:"La enduro 4T de mayor cilindrada de Husqvarna, para rally y travesías rápidas.", rating:4.3 },
  { id:"hus9", marca:"Husqvarna", modelo:"TE 250", año:2027, cc:249, tipo:"Enduro", peso:106.8, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906153/catalogo/hus9.png", pros:["2T de 249cc con inyección y arranque eléctrico","106,8 kg sin nafta","Sensor de vuelco que corta el motor en caídas"], contras:["No publican potencia ni par","Mezcla de aceite automática: más sistemas que mantener"], desc:"Enduro 2T de 250cc con inyección. Liviana y con respuesta de dos tiempos, para enduro técnico.", rating:4.3 },
  { id:"hus10", marca:"Husqvarna", modelo:"TE 300", año:2027, cc:293, tipo:"Enduro", peso:106.8, pot:"", par:"", consumo:"", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_HQV-Enduro-TE-300-MY27-90-right-PO_%23SALL_%23AEPI_%23V1.png", pros:["2T de 293cc con inyección electrónica","106,8 kg sin nafta y tanque de 8,5 L","Frenos hidráulicos y gestión de motor Vitesco"], contras:["No publican potencia ni par","Asiento de 952 mm"], desc:"La enduro 2T más buscada de Husqvarna: 300cc de tracción en cualquier pendiente con peso de 250.", rating:4.4 },
  // ═══════════════════════════════════════════════════════
  // GASGAS — supermoto, travel, enduro y motocross (agregado 09-oct-2026)
  // Specs y fotos de gasgas.com/es-ar (importa Grupo Simpa).
  // Pesos = "sin combustible". GasGas no publica potencia en la ficha local (ni en las 700).
  // ═══════════════════════════════════════════════════════
  { id:"gas1", marca:"GasGas", modelo:"SM 700", año:2024, cc:693, tipo:"Naked", peso:148.5, pot:"", par:73.5, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906155/catalogo/gas1.png", pros:["Monocilíndrico de 693cc con 73,5 Nm","ABS Bosch en curvas con modo Supermoto desconectable","Pinza Brembo radial de 4 pistones y 148,5 kg sin nafta"], contras:["No publican la potencia","Asiento de 898 mm"], desc:"La supermoto de GasGas sobre la base del 690 de KTM. Liviana, con electrónica de curva y freno motor regulable.", rating:4.3 },
  { id:"gas2", marca:"GasGas", modelo:"ES 700", año:2024, cc:693, tipo:"Adventure", peso:147.5, pot:"", par:73.5, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906156/catalogo/gas2.png", pros:["693cc y 73,5 Nm en 147,5 kg sin nafta","ABS en curvas con modo offroad desconectable","Frenos Brembo y tanque de 13,5 L"], contras:["No publican la potencia","Asiento de 935 mm"], desc:"La enduro grande de GasGas para ruta y tierra con patente. Misma plataforma que la 701 Enduro de Husqvarna.", rating:4.3 },
  { id:"gas3", marca:"GasGas", modelo:"EC 300", año:2027, cc:293, tipo:"Enduro", peso:107.6, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906156/catalogo/gas3.png", pros:["2T de 293cc con eje de balance (menos vibración)","107,6 kg sin nafta y tanque de 9 L","Arranque eléctrico y gestión electrónica"], contras:["No publican potencia ni par","Asiento de 956 mm"], desc:"Enduro 2T de 300cc de GasGas. La elección clásica para enduro técnico y travesías de tierra.", rating:4.3 },
  { id:"gas4", marca:"GasGas", modelo:"EC 350F", año:2027, cc:350, tipo:"Enduro", peso:110.2, pot:"", par:"", consumo:"", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_EC-350F-MY26-90-right_%23SALL_%23AEPI_%23V3.jpg", pros:["4T de 350cc con eje de balance multifunción","110,2 kg sin nafta y 6 marchas","Arranque eléctrico y tanque de 9 L"], contras:["No publican potencia ni par"], desc:"La enduro 4T intermedia de GasGas: potencia manejable y peso bajo para enduro y cross-country.", rating:4.3 },
  { id:"gas5", marca:"GasGas", modelo:"EX 300", año:2027, cc:293, tipo:"Enduro", peso:102.3, pot:"", par:"", consumo:"", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_GASGAS-EX-300-MY26-90-right_%23SALL_%23AEPI_%23V1.jpg", pros:["2T de 293cc en solo 102,3 kg","Frenos hidráulicos y arranque eléctrico","Tanque de 9 L y 6 marchas"], contras:["No publican potencia ni par","Sin luces: no es para calle"], desc:"La cross-country 2T de GasGas: una EC más liviana y rápida, pensada para carreras.", rating:4.2 },
  { id:"gas6", marca:"GasGas", modelo:"EX 350F", año:2024, cc:350, tipo:"Enduro", peso:105, pot:"", par:"", consumo:"", img:"https://azwecdnepstoragewebsiteuploads.azureedge.net/PHO_BIKE_90_RE_GASGAS-EX-350F-MY24-90-right_%23SALL_%23AEPI_%23V1.jpg", pros:["4T de 350cc en 105 kg sin nafta","6 marchas y tanque de 9 L","Cárteres compactos y gestión electrónica"], contras:["No publican potencia ni par","Sin luces: no es para calle"], desc:"Cross-country 4T de 350cc de GasGas, para carreras de enduro abierto y rally corto.", rating:4.2 },
  { id:"gas7", marca:"GasGas", modelo:"MC 250F", año:2027, cc:250, tipo:"Enduro", peso:102, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906157/catalogo/gas7.png", pros:["Motocross 4T de 250cc en 102 kg","Arranque eléctrico y gestión electrónica","Escape compacto de aluminio"], contras:["No publican potencia ni par","Solo para circuito: sin luces ni patente"], desc:"La motocross 4T de 250cc de GasGas. Para pista de motocross y nada más.", rating:4.2 },
  { id:"gas8", marca:"GasGas", modelo:"MC 450F", año:2027, cc:450, tipo:"Enduro", peso:103.6, pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906158/catalogo/gas8.png", pros:["Motocross 4T de 450cc en 103,6 kg","Culata SOHC compacta y 5 marchas","Arranque eléctrico"], contras:["No publican potencia ni par","Solo para circuito: sin luces ni patente"], desc:"La motocross grande de GasGas, de 450cc. Motor de competición en un chasis de 104 kg.", rating:4.2 },
  // ═══════════════════════════════════════════════════════
  // APRILIA — línea Argentina (agregado 09-oct-2026)
  // Specs de las fichas PDF y fotos de apriliaoficial.com.ar (importa Grupo Simpa, red Motoplex).
  // Pesos de RS/Tuono 457 = en orden de marcha; Tuareg = peso según ficha. Las scooter no publican peso.
  // ═══════════════════════════════════════════════════════
  { id:"apr1", marca:"Aprilia", modelo:"RS 457", año:2025, cc:457, tipo:"Sport", peso:175, pot:47.6, par:43.5, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906162/catalogo/apr1.webp", pros:["Bicilíndrico DOHC de 457cc, 47,6 cv y 43,5 Nm","Chasis de aluminio de doble viga con el motor portante","Ride-by-wire, control de tracción y ABS de doble canal"], contras:["175 kg en orden de marcha","Tanque de 13 L"], desc:"La deportiva chica de Aprilia con ADN de superbike: bicilíndrico de 457cc, chasis de aluminio y electrónica de las grandes.", rating:4.4 },
  { id:"apr2", marca:"Aprilia", modelo:"RS 457 GP Replica", año:2026, cc:457, tipo:"Sport", peso:175, pot:47.6, par:43.5, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906172/catalogo/apr2.png", pros:["Decoración réplica del equipo de MotoGP","Misma mecánica: 457cc, 47,6 cv, chasis de aluminio","Mapas de motor, ABS doble canal y faro full LED"], contras:["Mismas prestaciones que la RS 457 estándar"], desc:"La RS 457 vestida de MotoGP. Idéntica en mecánica y electrónica a la RS 457 estándar.", rating:4.4 },
  { id:"apr3", marca:"Aprilia", modelo:"Tuono 457", año:2025, cc:457, tipo:"Naked", peso:175, pot:47.6, par:43.5, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906176/catalogo/apr3.webp", pros:["Bicilíndrico de 457cc con 47,6 cv y 43,5 Nm","Chasis de aluminio y horquilla USD de 41 mm regulable","ABS doble canal con pinza radial ByBre y quickshifter"], contras:["175 kg con líquidos","Tanque de 12,7 L"], desc:"La naked hermana de la RS 457: mismo motor y chasis, manubrio alto y postura más cómoda para la ciudad.", rating:4.4 },
  { id:"apr4", marca:"Aprilia", modelo:"Tuareg 660", año:2025, cc:659, tipo:"Adventure", peso:204, pot:80, par:70, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906178/catalogo/apr4.webp", pros:["Bicilíndrico paralelo de 659cc, 80 cv y 70 Nm","Tanque de 18 L y asiento de 860 mm","ABS de 3 modos, control de tracción, crucero y TFT de 5 pulgadas"], contras:["204 kg","Cilindrada media para dos con equipaje"], desc:"La adventure liviana de Aprilia, con vocación de tierra en serio. Electrónica completa y tanque de 18 L.", rating:4.4 },
  { id:"apr5", marca:"Aprilia", modelo:"Tuareg Rally 660", año:2025, cc:659, tipo:"Adventure", peso:199, pot:80, par:70, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906184/catalogo/apr5.webp", pros:["Versión rally: 199 kg, 5 kg menos que la Tuareg","Asiento de 913 mm y suspensiones de mayor recorrido","Mismo bicilíndrico de 80 cv y electrónica APRC"], contras:["Asiento de 913 mm, muy alta","Más orientada a tierra que a ruta"], desc:"La Tuareg preparada para rally: más liviana, más alta y con detalles de competición. Para quien busca lo off-road.", rating:4.4 },
  { id:"apr6", marca:"Aprilia", modelo:"SR GT 200 Sport", año:2026, cc:174, tipo:"Scooter", peso:"", pot:17.7, par:16.5, consumo:"2.6", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906188/catalogo/apr6.webp", pros:["Motor i-get de 174cc con 13 kW y 16,5 Nm","Disco ondulado de 260 mm con ABS","Rueda de 14 adelante, apta para calle rota"], contras:["No publican el peso","ABS monocanal"], desc:"Scooter de aire crossover de Aprilia: ruedas grandes, suspensión larga y motor de 174cc. Consumo declarado de 2,6 L/100 km.", rating:4.1 },
  { id:"apr7", marca:"Aprilia", modelo:"SR 160", año:2025, cc:160, tipo:"Scooter", peso:"", pot:11.3, par:13.4, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906197/catalogo/apr7.jpg", pros:["Motor i-get de 160cc con inyección","Frenos con ABS y ruedas de 14","Suspensión trasera ajustable"], contras:["No publican el peso","Tanque de 6 L"], desc:"Scooter urbano de 160cc con ABS y ruedas de 14, el más accesible de la gama Aprilia.", rating:3.9 },
  // ═══════════════════════════════════════════════════════
  // MOTO GUZZI — línea Argentina (agregado 09-oct-2026)
  // Specs de las fichas PDF y fotos de motoguzzioficial.com.ar (importa Grupo Simpa, red Motoplex).
  // Pesos = en seco. V7 Stone y V7 Special no tienen ficha técnica en el sitio local (quedan sin cc/cv/Nm).
  // ═══════════════════════════════════════════════════════
  { id:"mgz1", marca:"Moto Guzzi", modelo:"V7 Stone", año:2025, cc:"", tipo:"Clásica", peso:"", pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906203/catalogo/mgz1.webp", pros:["Bicilíndrico en V transversal refrigerado por aire","Transmisión final por cardán, sin cadena","Iluminación full LED y tablero digital circular con Moto Guzzi MIA"], contras:["El sitio local no publica cilindrada, potencia ni peso","Frenado Brembo con disco simple adelante"], desc:"La Guzzi de entrada: motor en V a la vista, cardán y estética minimalista en negro. Fácil de llevar y con carácter.", rating:4.2 },
  { id:"mgz2", marca:"Moto Guzzi", modelo:"V7 Special", año:2025, cc:"", tipo:"Clásica", peso:"", pot:"", par:"", consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906206/catalogo/mgz2.webp", pros:["Bicilíndrico en V transversal refrigerado por aire","Transmisión por cardán","Acabados cromados y rayos, estilo años 70"], contras:["El sitio local no publica cilindrada, potencia ni peso"], desc:"La V7 más clásica: cromados, llantas con rayos y el bicilíndrico transversal de siempre.", rating:4.2 },
  { id:"mgz3", marca:"Moto Guzzi", modelo:"V7 Stone Corsa", año:2025, cc:853, tipo:"Clásica", peso:"", pot:65, par:73, consumo:"", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906209/catalogo/mgz3.webp", pros:["V-Twin de 853cc con 65 cv y 73 Nm","Transmisión por cardán y freno Brembo","Semicarenado y estética bicolor café racer"], contras:["No publican el peso","Un solo disco adelante"], desc:"La V7 con aire de café racer: cupulina, colores de competición y el mismo bicilíndrico de 853cc y 65 cv.", rating:4.2 },
  { id:"mgz4", marca:"Moto Guzzi", modelo:"V85 TT", año:2025, cc:853, tipo:"Adventure", peso:209, pot:80, par:83, consumo:"4.9", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906216/catalogo/mgz4.webp", pros:["Bicilíndrico de 853cc con 80 cv y 83 Nm","Tanque de 23 L y consumo declarado de 4,9 L/100 km","ABS en curva, control de tracción y cardán"], contras:["209 kg en seco, 230 en marcha","Refrigeración por aire: motor de carácter, no de picos"], desc:"La trail clásica de Guzzi: estética retro-rally, cardán y autonomía de 23 L. Pensada para viajar lejos sin apuro.", rating:4.3 },
  { id:"mgz5", marca:"Moto Guzzi", modelo:"Stelvio", año:2025, cc:1042, tipo:"Adventure", peso:222, pot:115, par:105, consumo:"5.1", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906220/catalogo/mgz5.webp", pros:["Bicilíndrico compacto de 1042cc, 115 cv y 105 Nm","Cardán, ABS en curva, control de tracción y 5 modos de manejo","Sachs de 46 mm con 170 mm de recorrido, tanque de 21 L"], contras:["222 kg en seco, 246 en marcha","Rueda de 19 adelante, más ruta que tierra"], desc:"La adventure grande de Moto Guzzi con el nuevo motor de 1042cc refrigerado por líquido. Para ruta larga con algo de ripio.", rating:4.4 },
  { id:"mgz6", marca:"Moto Guzzi", modelo:"Stelvio PFF", año:2025, cc:1042, tipo:"Adventure", peso:226, pot:115, par:105, consumo:"5.1", img:"https://res.cloudinary.com/dhwhk48wb/image/upload/f_auto,q_auto,w_900,c_limit/v1791906223/catalogo/mgz6.webp", pros:["Radar con control crucero adaptativo (PFF Rider Assistance)","Detección de ángulo muerto y asistente de cambio de carril","Mismo motor de 1042cc, 115 cv, quickshifter y TPMS"], contras:["226 kg en seco, 250 en marcha","Más electrónica, más precio"], desc:"La Stelvio con el paquete de radar PFF: crucero adaptativo y asistencias de seguridad. Única en el segmento con esta tecnología.", rating:4.5 },

];

// Subtítulo corto de una moto del catálogo. Las eléctricas no tienen cilindrada:
// sin esto mostraban "undefinedcc" en el buscador, en el selector del garage y en
// el texto que se comparte.
const fichaCorta = (m) => [m.cc ? `${m.cc}cc` : (m.autonomia ? `${m.autonomia} km` : null), m.tipo].filter(Boolean).join(" · ");

const ROBADAS = [];

const diffColor = d => ({ "Fácil": C.green, "Moderada": C.yellow, "Difícil": C.red, "Muy difícil": C.red }[d] || C.muted);

// Código WMO de Open-Meteo → ícono + texto corto (para pronóstico de salidas)
const climaWMO = (code) => {
  if (code == null) return { icon: "🌡️", txt: "" };
  if (code === 0) return { icon: "☀️", txt: "Despejado" };
  if (code <= 2) return { icon: "🌤️", txt: "Algo nublado" };
  if (code === 3) return { icon: "☁️", txt: "Nublado" };
  if (code <= 48) return { icon: "🌫️", txt: "Niebla" };
  if (code <= 57) return { icon: "🌦️", txt: "Llovizna" };
  if (code <= 67) return { icon: "🌧️", txt: "Lluvia" };
  if (code <= 77) return { icon: "🌨️", txt: "Nieve" };
  if (code <= 82) return { icon: "🌧️", txt: "Chaparrones" };
  if (code <= 86) return { icon: "🌨️", txt: "Nevadas" };
  return { icon: "⛈️", txt: "Tormenta" };
};

// ─── LAYOUT ──────────────────────────────────────────────────────────────────
const PageWrap = ({ children }) => (
  <div style={{ paddingBottom: 80, minHeight: "100vh" }}>{children}</div>
);

const PageHeader = ({ label, title, sub, seccion, titulo, children, onAI }) => (
  <div style={{ padding: "var(--header-pt) 18px 16px", borderBottom: `1px solid ${C.border}` }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
          Olbos · {seccion || label}
        </div>
      </div>
      {onAI && (
        <button onClick={onAI} style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <Search size={16} strokeWidth={2} color={C.text} />
        </button>
      )}
    </div>
    <h1 style={{ fontSize: 26, fontWeight: 900, color: C.text, lineHeight: 1.1, letterSpacing: -0.5, marginBottom: children || sub ? 12 : 0 }}>
      {titulo || title}
    </h1>
    {sub && <p style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{sub}</p>}
    {children}
  </div>
);

// ─── VIDEO DE AYUDA POR SECCIÓN ──────────────────────────────────────────────
// "Mirá cómo se usa": video corto por sección, subido por el equipo (admin o staff) desde la app.
// Tabla videos_ayuda (seccion pk, video_url). RLS: lo ven todos, escribe el equipo (desde el 30-oct
// también el staff: Matías arma los instructivos). Sin video y sin ser del equipo, no se renderiza nada.
let videosAyudaCache = null;
let esAdminAyudaCache = null;

const VideoAyuda = ({ seccion, style }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);
  const [show, setShow] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      if (!videosAyudaCache) {
        const { data } = await supabase.from("videos_ayuda").select("seccion, video_url");
        videosAyudaCache = data || [];
      }
      if (esAdminAyudaCache === null) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) esAdminAyudaCache = false;
        else {
          const { data: perfil } = await supabase.from("perfiles").select("es_admin, rol").eq("id", user.id).single();
          esAdminAyudaCache = !!perfil?.es_admin || perfil?.rol === "staff";
        }
      }
      if (cancelado) return;
      setVideoUrl(videosAyudaCache.find(v => v.seccion === seccion)?.video_url || null);
      setEsAdmin(esAdminAyudaCache);
    })();
    return () => { cancelado = true; };
  }, [seccion]);

  const subirVideo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error("falló la subida a Cloudinary");
      const { error } = await supabase.from("videos_ayuda")
        .upsert({ seccion, video_url: data.secure_url, updated_at: new Date().toISOString() });
      if (error) throw error;
      videosAyudaCache = (videosAyudaCache || []).filter(v => v.seccion !== seccion)
        .concat({ seccion, video_url: data.secure_url });
      setVideoUrl(data.secure_url);
    } catch (e) {
      alert("Error al subir el video: " + e.message);
    }
    setSubiendo(false);
  };

  if (!videoUrl && !esAdmin) return null;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, ...style }}>
        {videoUrl && (
          <button onClick={() => setShow(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 100,
              background: C.surface, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 500, color: C.text, letterSpacing: "-0.005em",
            }}>
            <Play size={11} strokeWidth={2.5} fill="#111111" /> Mirá cómo se usa
          </button>
        )}
        {esAdmin && (
          <label style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "6px 12px", borderRadius: 100,
            background: `${C.gold}14`, border: `1px dashed ${C.gold}55`,
            fontSize: 11.5, fontWeight: 500, color: "#B07D1A",
            cursor: subiendo ? "wait" : "pointer", letterSpacing: "-0.005em",
          }}>
            {subiendo ? "Subiendo…" : videoUrl ? "Reemplazar video" : "🎬 Subir video de ayuda"}
            <input type="file" accept="video/*" style={{ display: "none" }} disabled={subiendo}
              onChange={e => { subirVideo(e.target.files[0]); e.target.value = ""; }} />
          </label>
        )}
      </div>
      {show && videoUrl && (
        <div onClick={e => e.target === e.currentTarget && setShow(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,.88)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}>
          <div style={{ position: "relative", width: "100%", maxWidth: 380 }}>
            <button onClick={() => setShow(false)}
              style={{
                position: "absolute", top: -44, right: 0, width: 34, height: 34, borderRadius: "50%",
                background: "rgba(255,255,255,.15)", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}>
              <X size={16} strokeWidth={2.5} color="#fff" />
            </button>
            <video src={videoUrl} controls autoPlay playsInline
              style={{ width: "100%", maxHeight: "75vh", borderRadius: 16, background: "#000", display: "block" }} />
          </div>
        </div>
      )}
    </>
  );
};

// ─── NAVEGADOR INTERNO (in-app browser) ──────────────────────────────────────
// WhatsApp/Instagram/etc. abren los links en un webview propio donde Google bloquea
// el login. Detectamos eso y avisamos al usuario que abra en su navegador real.
const esNavegadorInApp = () => {
  if (typeof navigator === "undefined") return false;
  // En la app nativa (Capacitor) el login con Google sí anda (deep link), aunque el UA
  // tenga "wv" de WebView. No mostrar el aviso ahí.
  if (esNativo()) return false;
  const ua = navigator.userAgent || "";
  return /(FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Snapchat|MicroMessenger|TikTok|; wv\))/i.test(ua);
};

const AvisoNavegadorInterno = () => {
  const [oculto, setOculto] = useState(() => {
    if (typeof window === "undefined" || !esNavegadorInApp()) return true;
    try { return sessionStorage.getItem("olbos_aviso_nav") === "1"; } catch { return false; }
  });
  if (oculto) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: C.accent, color: "#fff",
      padding: "calc(env(safe-area-inset-top) + 11px) 14px 11px",
      display: "flex", alignItems: "flex-start", gap: 10,
      boxShadow: "0 2px 14px rgba(0,0,0,.35)",
    }}>
      <span style={{ fontSize: 17, lineHeight: 1.1, flexShrink: 0 }}>🌐</span>
      <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
        <strong>Para iniciar sesión, abrí Olbos en tu navegador.</strong><br />
        Tocá los <strong>⋮ (arriba a la derecha)</strong> → <strong>"Abrir en Chrome"</strong> (o Safari).
        Google no permite el login desde el navegador interno de WhatsApp.
      </div>
      <button onClick={() => { try { sessionStorage.setItem("olbos_aviso_nav", "1"); } catch {} setOculto(true); }}
        style={{ background: "rgba(255,255,255,.16)", border: "none", color: "#fff", borderRadius: "50%", width: 26, height: 26, flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
};

// ─── AVATAR / ACCESO A MI PERFIL ─────────────────────────────────────────────
// Componente autocontenido: lee el avatar del usuario logueado. Al tocarlo, va al perfil.
// ¿Estamos en una pantalla de escritorio? Se re-evalúa al cambiar el tamaño.
const useEsEscritorio = () => {
  const [es, setEs] = useState(() => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setEs(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", h); else mq.removeListener(h); };
  }, []);
  return es;
};

// La barra lateral del escritorio: reemplaza a la barra inferior en pantallas anchas
const NavEscritorio = ({ active, setActive, session, novedades = {}, orden = null, onLogin, onPerfil }) => {
  const [yo, setYo] = useState(null);
  useEffect(() => {
    if (!session?.user?.id) { setYo(null); return; }
    let vivo = true;
    supabase.from("perfiles").select("username, nombre, avatar_url").eq("id", session.user.id).maybeSingle()
      .then(({ data }) => { if (vivo) setYo(data || null); }, () => {});
    return () => { vivo = false; };
  }, [session?.user?.id]);
  return (
    <aside style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 200, background: C.card, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", padding: "22px 12px 18px", zIndex: 100 }}>
      <div style={{ padding: "0 10px 18px", display: "flex", alignItems: "center", gap: 9 }}>
        <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover" }} />
        <div>
          <div style={{ fontSize: 17, fontWeight: 900, color: C.text, letterSpacing: "0.02em", lineHeight: 1 }}>OLBOS<sup style={{ fontSize: 8 }}>®</sup></div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>El camino es el destino</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {navOrdenada(orden).map(i => {
          const on = active === i.id;
          return (
            <button key={i.id} onClick={() => setActive(i.id)} className="nav-escritorio-item"
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: 12, background: on ? C.surface : "transparent", color: on ? C.text : C.muted, border: "none", cursor: "pointer", textAlign: "left", transition: "background .15s" }}>
              <span style={{ position: "relative", display: "flex" }}>
                <i.Icon size={21} strokeWidth={on ? 2.3 : 1.7} />
                {novedades[i.id] && <span style={{ position: "absolute", top: -3, right: -5, width: 8, height: 8, borderRadius: "50%", background: C.accent, border: `1.5px solid ${C.card}` }} />}
              </span>
              <span style={{ fontSize: 15, fontWeight: on ? 800 : 600, letterSpacing: "-0.01em" }}>{i.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1 }} />
      {session ? (
        <button onClick={onPerfil} className="nav-escritorio-item"
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 10px", borderRadius: 12, background: active === "perfil" ? C.surface : "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", background: C.surface, border: `1px solid ${C.border}`, flexShrink: 0 }}>
            {yo?.avatar_url
              ? <img loading="lazy" decoding="async" src={cldThumb(yo.avatar_url, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: C.muted }}>{(yo?.username || "?")[0].toUpperCase()}</div>}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{yo?.username || "rider"}</div>
            <div style={{ fontSize: 11, color: C.muted }}>Mi perfil y garage</div>
          </div>
        </button>
      ) : (
        <button onClick={onLogin}
          style={{ padding: "12px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
          Iniciar sesión
        </button>
      )}
      <a href="/app" style={{ display: "block", marginTop: 12, padding: "10px 12px", borderRadius: 12, background: C.surface, color: C.muted, fontSize: 11.5, lineHeight: 1.45, textDecoration: "none" }}>
        📱 <strong style={{ color: C.text }}>Olbos en tu teléfono</strong><br />Para rodar, grabar y hablar con el grupo.
      </a>
    </aside>
  );
};

const AvatarNav = ({ onClick, size = 34 }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !vivo) return;
      const { data } = await supabase.from("perfiles").select("avatar_url").eq("id", user.id).single();
      if (vivo) setUrl(data?.avatar_url || null);
    })();
    return () => { vivo = false; };
  }, []);
  return (
    <button onClick={onClick} title="Mi perfil"
      style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", border: `1px solid ${C.border}`, background: C.surface, padding: 0, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {url
        ? <img loading="lazy" decoding="async" src={url} alt="Mi perfil" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <User size={Math.round(size * 0.5)} strokeWidth={2} color={C.muted} />}
    </button>
  );
};

// ─── TRANSFERIR PROPIEDAD (ruta / evento / traza) ────────────────────────────
// Botón autocontenido: aparece solo si sos el autor o admin. Busca el perfil destino
// por username y llama a la RPC SECURITY DEFINER correspondiente.
const BotonTransferir = ({ tipo, objetoId, autorId, onListo, titulo }) => {
  const [yo, setYo] = useState(null);
  const [miUsername, setMiUsername] = useState(null);
  const [esAdmin, setEsAdmin] = useState(false);
  const [miRol, setMiRol] = useState(null); // 'staff' = equipo de Olbos: puede transferir eventos de la agenda (@olbos), nada mas
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [duenoMatch, setDuenoMatch] = useState(null); // la busqueda matcheo al dueño actual
  const [sel, setSel] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !vivo) return;
      setYo(user.id);
      const { data } = await supabase.from("perfiles").select("es_admin, username, rol").eq("id", user.id).single();
      if (vivo) { setEsAdmin(!!data?.es_admin); setMiUsername(data?.username || null); setMiRol(data?.rol || null); }
      // Link "?evento=ID&transferir=UID" (lo manda el organizador por WhatsApp al
      // reclamar un evento de la agenda): se abre con ese perfil ya elegido.
      const pre = tipo === "evento" ? window.olbosTransferirA : null;
      if (pre && vivo) {
        window.olbosTransferirA = null;
        const { data: pp } = await supabase.from("perfiles").select("id, username, avatar_url").eq("id", pre).maybeSingle();
        if (pp && vivo) { setSel(pp); setResultados([pp]); setQ("@" + pp.username); setAbierto(true); }
      }
    })();
    return () => { vivo = false; };
  }, []);

  const esDeLaAgenda = autorId === "571720dc-a089-4e8a-a01b-7d1f92d99dbe"; // cuenta @olbos (bot de la agenda)
  const puede = esAdmin || (yo && autorId && yo === autorId) || (miRol === "staff" && tipo === "evento" && esDeLaAgenda);
  if (!puede) return null;

  const buscar = (texto) => {
    setQ(texto); setSel(null);
    clearTimeout(timerRef.current);
    if (texto.trim().length < 2) { setResultados([]); setBuscando(false); return; }
    setBuscando(true);
    timerRef.current = setTimeout(async () => {
      const { data } = await supabase.from("perfiles")
        .select("id, username, avatar_url")
        .ilike("username", `%${texto.trim().replace(/^@/, "").replace(/\s+/g, "")}%`)
        .limit(8);
      setResultados((data || []).filter(p => p.id !== autorId));
      setDuenoMatch((data || []).find(p => p.id === autorId) || null);
      setBuscando(false);
    }, 280);
  };

  const confirmar = async () => {
    if (!sel) return;
    // Un toque sobre el nombre y listo era demasiado poco para algo que no se deshace solo
    const avisoPagos = tipo === "evento" ? "\n\nSi el evento cobra inscripciones, los datos de pago y la cuenta de Mercado Pago quedan como están: el nuevo dueño tiene que cargar los suyos." : "";
    if (!window.confirm(`¿Transferir ${tipo === "ruta" ? "la ruta" : tipo === "evento" ? "el evento" : "el recorrido"}${titulo ? ` "${titulo}"` : ""} a @${sel.username}?\n\nVos dejás de poder editarlo.${avisoPagos}`)) return;
    setCargando(true); setError("");
    const rpc = tipo === "ruta" ? "transferir_ruta" : tipo === "evento" ? "transferir_evento" : "transferir_traza";
    const key = tipo === "ruta" ? "p_ruta_id" : tipo === "evento" ? "p_evento_id" : "p_traza_id";
    const { data: raw, error: err } = await supabase.rpc(rpc, { [key]: objetoId, p_nuevo_user_id: sel.id });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    setCargando(false);
    if (err || data?.error) {
      const msgs = { no_encontrada: "No se encontró el elemento", no_autorizado: "No tenés permiso para transferir esto", destino_inexistente: "Ese perfil no existe" };
      setError(msgs[data?.error] || err?.message || "No se pudo transferir");
      return;
    }
    // Avisar al nuevo dueño por la campanita (fire-and-forget, nunca rompe el flujo)
    const queEs = tipo === "ruta" ? "la ruta" : tipo === "evento" ? "el evento" : "el recorrido";
    supabase.from("notificaciones").insert({
      user_id: sel.id, tipo: "transferencia",
      from_username: miUsername || "olbos",
      texto: `te transfirió ${queEs}${titulo ? ` "${titulo}"` : ""} — ahora lo administrás vos 🎉`,
    }).then(() => {}, () => {});
    const linkObjeto = tipo === "evento" ? `${SITE_URL}/evento/${objetoId}` : SITE_URL;
    // Y por EMAIL (server-side: el mail del destinatario es privado). Si el server no
    // tiene la key de Resend configurada, falla en silencio y queda la campanita.
    supabase.auth.getSession().then(({ data: { session: se } }) => {
      if (!se?.access_token) return;
      fetch(`${API_BASE}/api/notificar-transferencia`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${se.access_token}` },
        body: JSON.stringify({ destinatario_id: sel.id, titulo: titulo || "", tipo, link: linkObjeto }),
      }).then(() => {}, () => {});
    }, () => {});
    setAbierto(false);
    onListo?.(sel);
    // WhatsApp: el aviso más efectivo es el personal — mensaje armado, elegís el contacto
    if (window.confirm(`Listo ✅ Transferido a @${sel.username}. Le avisamos por notificación y por email.\n\n¿Querés avisarle también por WhatsApp con un mensaje ya escrito?`)) {
      const msj = `¡Hola! Te acabo de transferir ${queEs}${titulo ? ` "${titulo}"` : ""} en Olbos 🏍️ Ya sos el organizador: inscripciones, entradas QR, sorteo y todo lo demás lo manejás vos. Entrá con tu cuenta y lo ves acá: ${linkObjeto} — cualquier duda me escribís!`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msj)}`, "_blank");
    }
  };

  const nombreTipo = tipo === "ruta" ? "la ruta" : tipo === "evento" ? "el evento" : "el recorrido";

  return (
    <>
      <button onClick={() => { setAbierto(true); setQ(""); setResultados([]); setSel(null); setError(""); }}
        style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", letterSpacing: "-0.005em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        🔄 Transferir a otro perfil
      </button>
      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "max(24px, calc(var(--sat) + 10px))" }}
          onClick={e => e.target === e.currentTarget && setAbierto(false)}>
          <div style={{ background: C.card, borderRadius: 20, width: "calc(100% - 24px)", maxWidth: 430, padding: "20px 20px 24px", maxHeight: "70vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Transferir {nombreTipo}</h2>
              <button onClick={() => setAbierto(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
              Buscá el perfil que va a quedar como dueño. La propiedad pasa a ese perfil y vos dejás de poder editarla.
            </p>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <Search size={15} strokeWidth={2} color={C.muted} style={{ position: "absolute", left: 12, top: 12 }} />
              <input value={q} onChange={e => buscar(e.target.value)} autoFocus placeholder="Buscar por usuario…" type="search" enterKeyHint="search" onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
            {/* Feedback siempre visible: buscando / sin resultados / lista — el teclado
                del celu tapaba la mitad y no se sabía si estaba trayendo algo */}
            {buscando && q.trim().length >= 2 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 4px", marginBottom: 12 }}>
                <div style={{ width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: C.text, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <span style={{ fontSize: 12.5, color: C.muted }}>Buscando…</span>
              </div>
            )}
            {!buscando && q.trim().length >= 2 && resultados.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12.5, color: duenoMatch ? "#1F8347" : C.muted, marginBottom: 12, background: duenoMatch ? `${C.green}14` : "transparent", borderRadius: 10 }}>
                {duenoMatch ? `✓ @${duenoMatch.username} ya es el dueño actual — no hace falta transferir nada.` : "No se encontró ningún perfil con ese nombre. Probá con menos letras."}
              </div>
            )}
            {resultados.length > 0 && (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
                {resultados.map(p => (
                  <button key={p.id} onClick={() => setSel(p)}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px", background: sel?.id === p.id ? "#1111110C" : C.card, border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", overflow: "hidden", background: C.surface, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {p.avatar_url ? <img loading="lazy" decoding="async" src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={15} color={C.muted} />}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>@{p.username}</span>
                    {sel?.id === p.id && <CheckCircle size={16} color="#27AE60" strokeWidth={2} style={{ marginLeft: "auto" }} />}
                  </button>
                ))}
              </div>
            )}
            {error && <div style={{ padding: 10, background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 8, fontSize: 12, color: C.red, marginBottom: 12 }}>{error}</div>}
            <button onClick={confirmar} disabled={!sel || cargando}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: sel ? "#111111" : C.surface, color: sel ? "#fff" : C.muted, border: "none", fontSize: 14, fontWeight: 700, cursor: sel && !cargando ? "pointer" : "default", opacity: cargando ? 0.6 : 1 }}>
              {cargando ? "Transfiriendo…" : sel ? `Transferir a @${sel.username}` : "Elegí un perfil"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── INSCRIPTOS DE UN EVENTO (vista del organizador) ─────────────────────────
// Autocontenido: si el RPC autoriza (organizador o admin) muestra el botón + lista.
const INSC_LABELS = {
  nombre_completo: "Nombre y apellido", dni: "DNI", telefono: "Teléfono", casco: "Casco",
  emergencia_nombre: "Contacto emergencia", emergencia_relacion: "Relación", emergencia_telefono: "Tel. emergencia",
  condicion_medica: "Condición médica", medicacion: "Medicación",
  punto_suma: "Se suma en",
};
// ── Entradas QR (control de acceso estilo ticketera, sin hardware) ───────────
// La entrada del rider: QR único (id de inscripción) en pantalla negra de marca.
const MiEntradaModal = ({ evento, onClose }) => {
  const [qr, setQr] = useState(null);
  const [insc, setInsc] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [err, setErr] = useState(null);
  const esPago = Number(evento.precio) > 0 || Number(evento.precio_entrada) > 0;

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setErr("Necesitás iniciar sesión."); return; }
      const { data: ins } = await supabase.from("inscripciones").select("id, pago_confirmado").eq("evento_id", evento.id).eq("user_id", user.id).maybeSingle();
      if (!ins) { if (vivo) setErr("No encontramos tu inscripción a este evento."); return; }
      const { data: p } = await supabase.from("perfiles").select("username, nombre").eq("id", user.id).maybeSingle();
      const QR = (await import("qrcode")).default;
      const url = await QR.toDataURL("olbos-entrada:" + ins.id, { width: 640, margin: 1, color: { dark: "#111111", light: "#F9F5F2" } });
      if (vivo) { setInsc(ins); setPerfil(p); setQr(url); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  return (
    <div style={{ position: "fixed", inset: 0, background: C.accent, zIndex: 1300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <X size={18} strokeWidth={2} color="#F9F5F2" />
      </button>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Mi entrada</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#F9F5F2", textAlign: "center", letterSpacing: "-0.02em", lineHeight: 1.2, marginBottom: 4, maxWidth: 340 }}>{evento.title || evento.titulo}</div>
      {evento.date && <div style={{ fontSize: 13, color: C.muted, marginBottom: 22 }}>{evento.date}</div>}
      {err ? (
        <div style={{ color: "#F9F5F2", fontSize: 14, textAlign: "center", padding: 20 }}>{err}</div>
      ) : !qr ? (
        <div style={{ color: C.muted, fontSize: 14, padding: 30 }}>Generando tu entrada…</div>
      ) : (
        <>
          <div style={{ background: "#F9F5F2", borderRadius: 18, padding: 14, boxShadow: "0 12px 48px rgba(0,0,0,.5)" }}>
            <img loading="lazy" decoding="async" src={qr} alt="Entrada QR" style={{ width: "min(72vw, 300px)", height: "min(72vw, 300px)", display: "block", borderRadius: 8 }} />
          </div>
          <div style={{ marginTop: 18, fontSize: 15, fontWeight: 700, color: "#F9F5F2", textAlign: "center" }}>
            {perfil?.nombre || ""} {perfil?.username ? <span style={{ color: C.muted, fontWeight: 500 }}>@{perfil.username}</span> : null}
          </div>
          {esPago && (
            <div style={{ marginTop: 10, padding: "6px 14px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, background: insc?.pago_confirmado ? "rgba(47,125,79,.3)" : "rgba(138,109,59,.3)", color: insc?.pago_confirmado ? "#8FD6AC" : "#D6BC8A" }}>
              {insc?.pago_confirmado ? "💳 Pago confirmado" : "🟡 Pago pendiente"}
            </div>
          )}
          <div style={{ marginTop: 16, fontSize: 12.5, color: C.muted, textAlign: "center", lineHeight: 1.6 }}>
            Mostrá este código en el acceso.<br />El organizador lo escanea y entrás.
          </div>
        </>
      )}
    </div>
  );
};

// El escáner del organizador: cualquier celu es un lector de entradas.
const EscanerEntradas = ({ eventoId, esPago, onClose, onCambio }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const ultimoRef = useRef({ code: null, t: 0 });
  const ocupadoRef = useRef(false);
  const [resultado, setResultado] = useState(null);
  const [errCam, setErrCam] = useState(null);
  const [stats, setStats] = useState(null);

  const cargarStats = async () => {
    const { data: raw, error } = await supabase.rpc("get_entradas_evento", { p_evento_id: eventoId });
    if (error) return;
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) setStats({ entraron: data.filter(e => e.entrada_usada_at).length, total: data.length });
  };

  const validar = async (code) => {
    ocupadoRef.current = true;
    try { navigator.vibrate?.(80); } catch {}
    const { data: raw, error } = await supabase.rpc("validar_entrada", { p_evento_id: eventoId, p_codigo: code });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || !r) {
      setResultado({ estado: "error", detalle: error?.message || "No se pudo validar la entrada." });
    } else {
      setResultado(r);
      if (r.estado === "valida") { cargarStats(); if (onCambio) onCambio(); try { navigator.vibrate?.([60, 40, 60]); } catch {} }
    }
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      cargarStats();
      let lector;
      try { lector = (await import("jsqr")).default; } catch { if (vivo) setErrCam("No se pudo cargar el lector de QR."); return; }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!vivo) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); }
        timerRef.current = setInterval(() => {
          if (ocupadoRef.current) return;
          const video = videoRef.current, canvas = canvasRef.current;
          if (!video || !canvas || video.readyState < 2) return;
          const w = 420, h = Math.round(420 * (video.videoHeight / video.videoWidth || 1)) || 420;
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          let code = null;
          try { code = lector(ctx.getImageData(0, 0, w, h).data, w, h); } catch {}
          const txt = code?.data || "";
          if (!txt.startsWith("olbos-entrada:")) return;
          const ahora = Date.now();
          if (ultimoRef.current.code === txt && ahora - ultimoRef.current.t < 4000) return;
          ultimoRef.current = { code: txt, t: ahora };
          validar(txt);
        }, 350);
      } catch (e) {
        if (vivo) setErrCam("No pudimos acceder a la cámara. Dale permiso al navegador, o marcá los ingresos a mano desde \"Ver inscriptos\".");
      }
    })();
    return () => {
      vivo = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const seguir = () => { setResultado(null); ocupadoRef.current = false; };
  const fmtHora = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }); };
  const colorDe = { valida: "#2F7D4F", ya_usada: "#8A6D3B", invalida: "#B3372C", error: "#B3372C" };

  return (
    <div style={{ position: "fixed", inset: 0, background: C.accent, zIndex: 1300, display: "flex", flexDirection: "column", paddingTop: "var(--sat)", paddingBottom: "var(--sab)" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#F9F5F2", letterSpacing: "-0.01em" }}>🎫 Escanear entradas</div>
          {stats && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{stats.entraron} de {stats.total} ingresaron</div>}
        </div>
        <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={18} strokeWidth={2} color="#F9F5F2" />
        </button>
      </div>
      {/* cámara */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {errCam ? (
          <div style={{ color: "#F9F5F2", fontSize: 14, textAlign: "center", padding: "0 32px", lineHeight: 1.6 }}>{errCam}</div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            {/* marco guía */}
            <div style={{ position: "relative", width: "min(64vw, 280px)", height: "min(64vw, 280px)", border: "3px solid rgba(249,245,242,.85)", borderRadius: 22, boxShadow: "0 0 0 2000px rgba(17,17,17,.45)" }} />
            <div style={{ position: "absolute", bottom: resultado ? 190 : 40, left: 0, right: 0, textAlign: "center", fontSize: 13, color: "rgba(249,245,242,.85)", fontWeight: 600 }}>
              Apuntá al QR de la entrada
            </div>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      {/* resultado */}
      {resultado && (
        <div style={{ position: "absolute", left: 14, right: 14, bottom: 20, background: colorDe[resultado.estado] || "#B3372C", borderRadius: 16, padding: "16px 18px", boxShadow: "0 8px 32px rgba(0,0,0,.5)" }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>
            {resultado.estado === "valida" && <>✓ Entrada válida</>}
            {resultado.estado === "ya_usada" && <>⚠️ Ya ingresó{resultado.usada_at ? ` a las ${fmtHora(resultado.usada_at)}` : ""}</>}
            {resultado.estado === "invalida" && <>✕ Entrada inválida</>}
            {resultado.estado === "error" && <>✕ Error</>}
          </div>
          {(resultado.nombre || resultado.username) && (
            <div style={{ fontSize: 14.5, color: "rgba(255,255,255,.92)", marginTop: 4, fontWeight: 600 }}>
              {resultado.nombre || ""} {resultado.username ? `· @${resultado.username}` : ""}
            </div>
          )}
          {resultado.estado === "valida" && esPago && (
            <div style={{ fontSize: 13, color: "#fff", marginTop: 6, fontWeight: 700 }}>
              {resultado.pago_confirmado ? "💳 Pago confirmado" : "🟡 PAGO PENDIENTE — cobrale en puerta"}
            </div>
          )}
          {resultado.estado === "error" && resultado.detalle && (
            <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.9)", marginTop: 4 }}>{resultado.detalle}</div>
          )}
          <button onClick={seguir} style={{ marginTop: 12, width: "100%", padding: "11px", borderRadius: 10, background: "rgba(255,255,255,.95)", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 800, color: C.text }}>
            Escanear siguiente
          </button>
        </div>
      )}
    </div>
  );
};

// ─── MAIL MARKETING PROPIO ───────────────────────────────────────────────────
// Los organizadores les escriben a los anotados de su evento y el admin a toda
// la base, desde adentro de Olbos, con vista previa en vivo del mail, prueba a
// uno mismo, envío o programación, y después entregados / abiertos / clicks y
// quién abrió. Manda api/campanias.js (Edge) por Resend; las tablas son mail_*.
const MAIL_ESC = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const mailInline = (s) => MAIL_ESC(s)
  .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
  .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" style="color:#111111;font-weight:bold">$1</a>')
  .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g, '$1<a href="$2" style="color:#111111">$2</a>');
// El mismo markdown-lite que usa el servidor: lo que ves en la vista previa es lo que llega
const mailCuerpoAHtml = (texto) => {
  const lineas = String(texto || "").replace(/\r\n/g, "\n").split("\n");
  const out = []; let lista = [];
  const cerrarLista = () => { if (lista.length) { out.push(`<ul style="margin:0 0 14px;padding-left:20px">${lista.map(l => `<li style="margin:4px 0">${l}</li>`).join("")}</ul>`); lista = []; } };
  for (const l of lineas) {
    const t = l.trim();
    if (!t) { cerrarLista(); continue; }
    if (/^[-•*]\s+/.test(t)) { lista.push(mailInline(t.replace(/^[-•*]\s+/, ""))); continue; }
    cerrarLista();
    if (/^#\s+/.test(t)) { out.push(`<h2 style="font-size:19px;margin:18px 0 8px;letter-spacing:-0.01em">${mailInline(t.replace(/^#\s+/, ""))}</h2>`); continue; }
    out.push(`<p style="font-size:15px;line-height:1.65;margin:0 0 14px">${mailInline(t)}</p>`);
  }
  cerrarLista();
  return out.join("");
};
const MailPreview = ({ cuerpo, botonTexto, botonUrl, remitente, evento, username }) => (
  <div style={{ background: "#f9f5f2", padding: "18px 10px", borderRadius: 14, border: `1px solid ${C.border}` }}>
    <div style={{ fontFamily: "Arial, Helvetica, sans-serif", maxWidth: 560, margin: "0 auto", color: "#111111" }}>
      <div style={{ background: "#111111", color: "#f9f5f2", padding: "18px 22px", borderRadius: "14px 14px 0 0" }}>
        <div style={{ fontSize: 17, fontWeight: "bold", letterSpacing: 1 }}>OLBOS<sup style={{ fontSize: 8 }}>®</sup></div>
        {evento && <div style={{ fontSize: 12, color: "#bdb7b0", marginTop: 5 }}>{evento}</div>}
      </div>
      <div style={{ background: "#fff", border: "1px solid #e7e1da", borderTop: "none", borderRadius: "0 0 14px 14px", padding: 22 }}>
        <p style={{ fontSize: 15, lineHeight: 1.6, margin: "0 0 14px" }}>Hola @{username || "rider"} 👋</p>
        {cuerpo.trim()
          ? <div dangerouslySetInnerHTML={{ __html: mailCuerpoAHtml(cuerpo) }} />
          : <p style={{ fontSize: 15, lineHeight: 1.65, margin: "0 0 14px", color: "#bdb7b0" }}>Acá va tu texto…</p>}
        {botonTexto && botonUrl && (
          <p style={{ margin: "6px 0 18px" }}><span style={{ display: "inline-block", background: "#111111", color: "#fff", padding: "12px 24px", borderRadius: 12, fontWeight: "bold", fontSize: 15 }}>{botonTexto}</span></p>
        )}
        <p style={{ fontSize: 12.5, color: "#8a857f", lineHeight: 1.6, margin: "18px 0 0", borderTop: "1px solid #e7e1da", paddingTop: 12 }}>
          {remitente ? <>Te escribe <strong style={{ color: "#111" }}>{remitente}</strong>{evento ? <> por <strong style={{ color: "#111" }}>{evento}</strong>, donde estás anotado</> : null}.</> : "Recibís este correo porque tenés cuenta en Olbos."}
          <br />— El camino es el destino
        </p>
        <p style={{ fontSize: 10.5, color: "#8a857f", lineHeight: 1.5, margin: "10px 0 0" }}><u>No quiero recibir más estos correos</u> · Ley 25.326</p>
      </div>
    </div>
  </div>
);

// Plantillas: lo que un organizador manda siempre, listo para retocar
const MAIL_PLANTILLAS = (ev) => {
  const t = ev?.titulo || "el evento";
  const link = ev?.id ? `https://olbos.ai/?evento=${ev.id}` : "https://olbos.ai";
  return [
    { id: "recordatorio", n: "⏰ Recordatorio", asunto: `Mañana es ${t}`, cuerpo: `Ya casi. Mañana nos vemos en **${t}**.\n\nRepasá antes de salir:\n- Punto de encuentro y horario en la ficha del evento\n- Tanque lleno y documentación\n- Abrí Olbos al llegar y hacé el **check-in** para aparecer en el mapa de la rodada\n\nCualquier cambio te lo avisamos por acá.`, boton: ["Ver el evento", link] },
    { id: "cambio", n: "🔁 Cambio de horario o lugar", asunto: `Cambio importante en ${t}`, cuerpo: `Atención: hubo un cambio en **${t}**.\n\n# Lo nuevo\n- Horario: \n- Lugar: \n\nEl resto sigue igual. Si no podés venir con el cambio, respondé este mail y lo vemos.`, boton: ["Ver los detalles", link] },
    { id: "gracias", n: "🙌 Gracias por venir", asunto: `Gracias por rodar en ${t}`, cuerpo: `Qué día. Gracias por ser parte de **${t}**.\n\nTus kilómetros ya están sumados en Olbos y las fotos y recorridos del grupo están en la ficha del evento.\n\nNos vemos en la próxima.`, boton: ["Ver las fotos y recorridos", link] },
    { id: "pago", n: "💳 Pago pendiente", asunto: `Tu lugar en ${t}: falta el pago`, cuerpo: `Tenés tu inscripción a **${t}**, pero todavía no nos llegó el pago.\n\nPara confirmar tu lugar, entrá a la ficha del evento y completalo desde ahí. Si ya pagaste, respondé este mail con el comprobante y lo marcamos.`, boton: ["Completar el pago", link] },
  ];
};

// Elegir un evento por título, para los segmentos "participaron de…" y "anotados a…"
const BuscarEventoMini = ({ valor, onElegir, placeholder, futuros = false }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (!valor) { setSel(null); return; }
    supabase.from("eventos").select("id, titulo, fecha").eq("id", valor).maybeSingle().then(({ data }) => setSel(data || null), () => {});
  }, [valor]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      let qq = supabase.from("eventos").select("id, titulo, fecha").ilike("titulo", `%${q.trim()}%`).order("fecha", { ascending: !futuros ? false : true }).limit(8);
      qq = futuros ? qq.gte("fecha", hoyLocalISO()) : qq.lt("fecha", hoyLocalISO());
      const { data } = await qq;
      setRes(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [q, futuros]);
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, background: C.card, boxSizing: "border-box" };
  if (sel) return (
    <div style={{ ...inp, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 700 }}>{sel.titulo} <span style={{ color: C.muted, fontWeight: 500 }}>· {fmtDia(sel.fecha)}</span></span>
      <button onClick={() => { onElegir(""); setSel(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14 }}>✕</button>
    </div>
  );
  return (
    <div style={{ position: "relative" }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} style={inp} />
      {res.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 20, maxHeight: 220, overflowY: "auto", boxShadow: "0 6px 20px rgba(0,0,0,.12)" }}>
          {res.map(e => (
            <div key={e.id} onClick={() => { onElegir(e.id); setQ(""); setRes([]); }} style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 700, color: C.text }}>{e.titulo}</span> <span style={{ color: C.muted }}>· {fmtDia(e.fecha)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MailComposer = ({ userId, evento = null, esAdmin = false, campania = null, onClose, onEnviada = null }) => {
  const [id, setId] = useState(campania?.id || null);
  const [asunto, setAsunto] = useState(campania?.asunto || "");
  const [cuerpo, setCuerpo] = useState(campania?.cuerpo || "");
  const [botonTexto, setBotonTexto] = useState(campania?.boton_texto || "");
  const [botonUrl, setBotonUrl] = useState(campania?.boton_url || "");
  const [remitente, setRemitente] = useState(campania?.remitente_nombre || "");
  const [replyTo, setReplyTo] = useState(campania?.reply_to || "");
  const [audiencia, setAudiencia] = useState(campania?.audiencia || (evento ? { tipo: "evento", filtro: "todos" } : { tipo: "base", activos_dias: "", ciudad: "", anotados_futuros: "" }));
  const [cuantos, setCuantos] = useState(null);
  const [miUsername, setMiUsername] = useState("");
  const [vista, setVista] = useState("editar"); // editar | previa (en pantallas angostas)
  const [guardado, setGuardado] = useState("");   // "Guardando…" | "Guardado ✓"
  const [enviando, setEnviando] = useState(false);
  const [prueba, setPrueba] = useState(false);
  const [programar, setProgramar] = useState(false);
  const [fechaProg, setFechaProg] = useState("");
  const [enviada, setEnviada] = useState(null); // resultado del envío
  const [mostrarBoton, setMostrarBoton] = useState(!!(campania?.boton_texto));
  const textareaRef = useRef(null);
  const guardarTimer = useRef(null);
  const ancho = typeof window !== "undefined" && window.innerWidth >= 900;
  useBackGuard(true, onClose);

  // Un borrador desde el primer segundo: así hay id para contar la audiencia y mandar la prueba
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: p } = await supabase.from("perfiles").select("username, nombre").eq("id", userId).maybeSingle();
      if (vivo && p) { setMiUsername(p.username || ""); if (!remitente) setRemitente(p.nombre || (p.username ? "@" + p.username : "")); }
      const { data: { user } } = await supabase.auth.getUser();
      if (vivo && user?.email && !replyTo) setReplyTo(user.email);
      if (!id) {
        const { data, error } = await supabase.from("mail_campanias").insert({
          creador_id: userId, evento_id: evento?.id || null, audiencia, asunto: "", cuerpo: "",
          remitente_nombre: p?.nombre || (p?.username ? "@" + p.username : null), reply_to: user?.email || null,
        }).select("id").maybeSingle();
        if (vivo && data?.id) setId(data.id);
        if (error) alert("No se pudo crear el borrador: " + error.message);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autoguardado con respiro
  useEffect(() => {
    if (!id) return;
    setGuardado("Guardando…");
    if (guardarTimer.current) clearTimeout(guardarTimer.current);
    guardarTimer.current = setTimeout(async () => {
      const { error } = await supabase.from("mail_campanias").update({
        asunto, cuerpo, boton_texto: mostrarBoton ? botonTexto || null : null, boton_url: mostrarBoton ? botonUrl || null : null,
        remitente_nombre: remitente || null, reply_to: replyTo || null, audiencia, updated_at: new Date().toISOString(),
      }).eq("id", id);
      setGuardado(error ? "No se guardó" : "Guardado ✓");
    }, 700);
    return () => { if (guardarTimer.current) clearTimeout(guardarTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, asunto, cuerpo, botonTexto, botonUrl, mostrarBoton, remitente, replyTo, audiencia]);

  // ¿A cuántos les llega? Se recalcula cuando cambia la audiencia (después de guardar)
  useEffect(() => {
    if (!id) return;
    let vivo = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc("mail_contar", { p_campania_id: id });
      if (vivo) setCuantos(typeof data === "number" ? data : null);
    }, 1000);
    return () => { vivo = false; clearTimeout(t); };
  }, [id, audiencia, guardado === "Guardado ✓"]); // eslint-disable-line react-hooks/exhaustive-deps

  const token = async () => (await supabase.auth.getSession()).data?.session?.access_token;
  const llamar = async (accion) => {
    const tk = await token();
    const r = await fetch(`${API_BASE}/api/campanias?accion=${accion}`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` }, body: JSON.stringify({ campania_id: id }) });
    return r.json().catch(() => ({ error: "respuesta inválida" }));
  };
  const listoParaMandar = asunto.trim().length >= 3 && cuerpo.trim().length >= 10 && guardado === "Guardado ✓";
  const enviarPrueba = async () => {
    if (!listoParaMandar || prueba) return;
    setPrueba(true);
    const r = await llamar("prueba");
    setPrueba(false);
    if (r.ok) alert(`Te mandamos la prueba a tu mail (${replyTo || "tu casilla"}). Fijate cómo se ve antes de enviar.`);
    else alert("No se pudo mandar la prueba: " + (r.error || JSON.stringify(r.fallidos || r)));
  };
  const enviar = async () => {
    if (!listoParaMandar || enviando) return;
    const n = cuantos == null ? "los destinatarios" : `${cuantos} rider${cuantos === 1 ? "" : "s"}`;
    if (!window.confirm(`¿Enviar "${asunto.trim()}" a ${n}? Una vez enviado no se puede detener.`)) return;
    setEnviando(true);
    const r = await llamar("enviar");
    setEnviando(false);
    if (r.ok) { setEnviada(r); if (onEnviada) onEnviada(id); }
    else alert("No se pudo enviar: " + (r.error || JSON.stringify(r)));
  };
  const programarEnvio = async () => {
    if (!listoParaMandar || !fechaProg) return;
    const cuando = new Date(fechaProg);
    if (isNaN(cuando.getTime()) || cuando.getTime() < Date.now() + 4 * 60000) { alert("Elegí una fecha y hora de al menos 5 minutos en adelante."); return; }
    const { error } = await supabase.from("mail_campanias").update({ estado: "programada", programada_para: cuando.toISOString(), updated_at: new Date().toISOString() }).eq("id", id);
    if (error) { alert("No se pudo programar: " + error.message); return; }
    alert(`Programado para el ${cuando.toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}. Sale solo; podés cancelarlo desde la lista hasta ese momento.`);
    if (onEnviada) onEnviada(id);
    onClose();
  };

  // Barra del editor: negrita, link, plantillas
  const envolver = (antes, despues = antes, placeholder = "texto") => {
    const ta = textareaRef.current; if (!ta) { setCuerpo(c => c + antes + placeholder + despues); return; }
    const a = ta.selectionStart, b = ta.selectionEnd;
    const sel = cuerpo.slice(a, b) || placeholder;
    const nuevo = cuerpo.slice(0, a) + antes + sel + despues + cuerpo.slice(b);
    setCuerpo(nuevo);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(a + antes.length, a + antes.length + sel.length); }, 0);
  };
  const insertarLink = () => { const u = window.prompt("Dirección del link (https://…):", "https://"); if (!u || !/^https?:\/\//.test(u)) return; envolver("[", `](${u})`, "texto del link"); };
  const aplicarPlantilla = (p) => {
    if ((asunto.trim() || cuerpo.trim()) && !window.confirm("¿Reemplazar lo que escribiste por la plantilla?")) return;
    setAsunto(p.asunto); setCuerpo(p.cuerpo);
    if (p.boton) { setMostrarBoton(true); setBotonTexto(p.boton[0]); setBotonUrl(p.boton[1]); }
  };

  const inp = { width: "100%", padding: "11px 12px", borderRadius: 11, border: `1px solid ${C.border}`, fontSize: 14.5, color: C.text, background: C.card, boxSizing: "border-box", fontFamily: "inherit" };
  const chip = (activo) => ({ padding: "7px 12px", borderRadius: 100, border: `1.5px solid ${activo ? C.text : C.border}`, background: activo ? C.text : C.card, color: activo ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" });
  const filtrosEvento = [["todos", "Todos los anotados"], ["checkin", "Con check-in"], ["pagaron", "Pagaron"], ["sin_pagar", "Sin pagar"]];

  const editor = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* A quién */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Para</div>
        {audiencia.tipo === "evento" ? (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }} className="sin-scrollbar">
            {filtrosEvento.map(([f, l]) => <button key={f} onClick={() => setAudiencia({ tipo: "evento", filtro: f })} style={chip(audiencia.filtro === f)}>{l}</button>)}
          </div>
        ) : (
          // Segmentos combinables (pedido de Carlos, 18-sep): ciudad, marca, cilindrada,
          // km grabados, eventos en los que estuvieron o a los que van, actividad
          (() => {
            const setA = (k, v) => setAudiencia({ ...audiencia, [k]: v });
            const lab = { fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4, display: "block" };
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div><label style={lab}>Actividad</label>
                  <select value={audiencia.activos_dias || ""} onChange={e => setA("activos_dias", e.target.value)} style={inp}>
                    <option value="">Cualquiera</option><option value="7">Activos últimos 7 días</option><option value="30">Activos últimos 30 días</option><option value="90">Activos últimos 90 días</option>
                  </select></div>
                <div><label style={lab}>Se registraron</label>
                  <select value={audiencia.registrados_dias || ""} onChange={e => setA("registrados_dias", e.target.value)} style={inp}>
                    <option value="">Cuando sea</option><option value="7">Últimos 7 días</option><option value="30">Últimos 30 días</option><option value="90">Últimos 90 días</option>
                  </select></div>
                <div><label style={lab}>Ciudad o provincia</label><input value={audiencia.ciudad || ""} onChange={e => setA("ciudad", e.target.value)} placeholder="Ej: Córdoba" style={inp} /></div>
                <div><label style={lab}>Marca de moto</label><input value={audiencia.marca || ""} onChange={e => setA("marca", e.target.value)} placeholder="Ej: Honda, Royal Enfield" style={inp} /></div>
                <div><label style={lab}>Cilindrada mínima (cc)</label><input value={audiencia.cc_min || ""} onChange={e => setA("cc_min", e.target.value.replace(/[^\d]/g, ""))} placeholder="Ej: 250" inputMode="numeric" style={inp} /></div>
                <div><label style={lab}>Cilindrada máxima (cc)</label><input value={audiencia.cc_max || ""} onChange={e => setA("cc_max", e.target.value.replace(/[^\d]/g, ""))} placeholder="Ej: 650" inputMode="numeric" style={inp} /></div>
                <div><label style={lab}>Km grabados, mínimo</label><input value={audiencia.km_min || ""} onChange={e => setA("km_min", e.target.value.replace(/[^\d]/g, ""))} placeholder="Ej: 100" inputMode="numeric" style={inp} /></div>
                <div><label style={lab}>Moto cargada</label>
                  <select value={audiencia.moto || ""} onChange={e => setA("moto", e.target.value)} style={inp}>
                    <option value="">Da igual</option><option value="con">Con moto en el garage</option><option value="sin">Sin moto cargada</option>
                  </select></div>
                {/* Teléfono y versión (28-oct): avisarles a los iPhone que quedaron en una versión con falla */}
                <div><label style={lab}>Teléfono</label>
                  <select value={audiencia.plataforma || ""} onChange={e => setA("plataforma", e.target.value)} style={inp}>
                    <option value="">Cualquiera</option><option value="ios">iPhone (app)</option><option value="android">Android (app)</option>
                  </select></div>
                <div><label style={lab}>Sin actualizar a la versión</label><input value={audiencia.version_menor || ""} onChange={e => setA("version_menor", e.target.value.replace(/[^\d.]/g, ""))} placeholder="Ej: 1.10.40" inputMode="decimal" style={inp} /></div>
                <div style={{ gridColumn: "1 / -1" }}><label style={lab}>Participaron de este evento (check-in o inscripción)</label>
                  <BuscarEventoMini valor={audiencia.evento_pasado || ""} onElegir={id => setA("evento_pasado", id)} placeholder="Buscá un evento ya realizado…" /></div>
                <div style={{ gridColumn: "1 / -1" }}><label style={lab}>Anotados a este evento que viene</label>
                  <BuscarEventoMini valor={audiencia.evento_futuro || ""} onElegir={id => setA("evento_futuro", id)} placeholder="Buscá un evento futuro…" futuros /></div>
                <label style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
                  <input type="checkbox" checked={audiencia.anotados_futuros === "true"} onChange={e => setA("anotados_futuros", e.target.checked ? "true" : "")} />
                  Solo anotados a algún evento futuro (cualquiera)
                </label>
                <div style={{ gridColumn: "1 / -1", fontSize: 11.5, color: C.muted }}>Los filtros se combinan: recibe el mail quien cumple todos los que cargues. Dejá vacío lo que no importe.</div>
              </div>
            );
          })()
        )}
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>
          {cuantos == null ? "Contando…" : cuantos === 0 ? "Nadie recibiría este mail con este filtro." : <><strong style={{ color: C.text }}>{cuantos}</strong> rider{cuantos === 1 ? "" : "s"} lo van a recibir{audiencia.tipo === "base" ? " (los que pidieron no recibir novedades quedan afuera)" : ""}.</>}
        </div>
      </div>
      {/* Plantillas */}
      {evento && !enviada && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Empezar con una plantilla</div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2 }} className="sin-scrollbar">
            {MAIL_PLANTILLAS(evento).map(p => <button key={p.id} onClick={() => aplicarPlantilla(p)} style={chip(false)}>{p.n}</button>)}
          </div>
        </div>
      )}
      {/* Asunto */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Asunto</div>
        <input value={asunto} onChange={e => setAsunto(e.target.value)} maxLength={120} placeholder="Corto y claro: es lo que se ve en la bandeja" style={{ ...inp, fontWeight: 700 }} />
      </div>
      {/* Texto */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>Mensaje</div>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => envolver("**", "**", "negrita")} title="Negrita" style={{ ...chip(false), padding: "5px 10px", fontWeight: 900 }}>B</button>
            <button onClick={insertarLink} title="Link" style={{ ...chip(false), padding: "5px 10px" }}>🔗</button>
            <button onClick={() => envolver("\n- ", "", "punto")} title="Lista" style={{ ...chip(false), padding: "5px 10px" }}>• Lista</button>
          </div>
        </div>
        <textarea ref={textareaRef} value={cuerpo} onChange={e => setCuerpo(e.target.value)} rows={ancho ? 12 : 8}
          placeholder={"Escribí como le hablarías al grupo. Un párrafo por idea.\n\n**negrita**, - lista, # título, y los links van solos."}
          style={{ ...inp, lineHeight: 1.55, resize: "vertical" }} />
      </div>
      {/* Botón */}
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: C.text, cursor: "pointer", marginBottom: mostrarBoton ? 8 : 0 }}>
          <input type="checkbox" checked={mostrarBoton} onChange={e => setMostrarBoton(e.target.checked)} /> Agregar un botón
        </label>
        {mostrarBoton && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 8 }}>
            <input value={botonTexto} onChange={e => setBotonTexto(e.target.value)} maxLength={40} placeholder="Texto del botón" style={inp} />
            <input value={botonUrl} onChange={e => setBotonUrl(e.target.value)} placeholder="https://…" style={inp} />
            {evento?.id && <button onClick={() => { setBotonTexto(botonTexto || "Ver el evento"); setBotonUrl(`https://olbos.ai/?evento=${evento.id}`); }} style={{ ...chip(false), gridColumn: "1 / -1", justifySelf: "start" }}>Usar el link del evento</button>}
          </div>
        )}
      </div>
      {/* Remitente */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Firma</div>
          <input value={remitente} onChange={e => setRemitente(e.target.value)} maxLength={60} placeholder="Tu nombre o el del grupo" style={inp} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Las respuestas van a</div>
          <input value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder="tu@mail.com" style={inp} />
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
        Sale desde <strong>hola@olbos.ai</strong> con tu firma. Cada mail lleva el link de baja que exige la ley; los que se dan de baja no reciben más.
      </div>
    </div>
  );

  const previa = (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Así les llega</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 8, padding: "8px 12px", background: C.surface, borderRadius: 10 }}>
        <div><span style={{ color: C.muted }}>De:</span> <strong style={{ color: C.text }}>{remitente || "Olbos"} · Olbos</strong> &lt;hola@olbos.ai&gt;</div>
        <div><span style={{ color: C.muted }}>Asunto:</span> <strong style={{ color: C.text }}>{asunto || "(sin asunto)"}</strong></div>
      </div>
      <MailPreview cuerpo={cuerpo} botonTexto={mostrarBoton ? botonTexto : ""} botonUrl={mostrarBoton ? botonUrl : ""} remitente={remitente} evento={evento?.titulo} username={miUsername} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1250, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, background: C.card, flexShrink: 0 }}>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} strokeWidth={2.5} color={C.text} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{evento ? `📣 Comunicado · ${evento.titulo}` : "📣 Campaña a la base"}</div>
          <div style={{ fontSize: 11.5, color: guardado === "No se guardó" ? C.red : C.muted }}>{guardado || "Borrador"}</div>
        </div>
        {!ancho && !enviada && (
          <div style={{ display: "flex", background: C.surface, borderRadius: 10, padding: 3 }}>
            {[["editar", "Editar"], ["previa", "Vista previa"]].map(([v, l]) => (
              <button key={v} onClick={() => setVista(v)} style={{ padding: "6px 10px", borderRadius: 8, border: "none", background: vista === v ? C.card : "transparent", color: vista === v ? C.text : C.muted, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {enviada ? (
        <div style={{ flex: 1, overflowY: "auto", padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>📬</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 6 }}>Enviado a {enviada.enviados} rider{enviada.enviados === 1 ? "" : "s"}</div>
          <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.55, maxWidth: 380, margin: "0 auto 18px" }}>
            En unos minutos vas a ver quién lo abrió y quién hizo click. {enviada.fallidos?.length ? `Hubo ${enviada.fallidos.length} tanda${enviada.fallidos.length === 1 ? "" : "s"} con error; revisalo en las estadísticas.` : ""}
          </div>
          <button onClick={onClose} style={{ padding: "13px 28px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>Ver las estadísticas</button>
        </div>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {ancho ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, maxWidth: 1100, margin: "0 auto" }}>
                <div>{editor}</div>
                <div style={{ position: "sticky", top: 0 }}>{previa}</div>
              </div>
            ) : (vista === "editar" ? editor : previa)}
          </div>
          <div style={{ padding: "10px 16px calc(12px + var(--sab))", borderTop: `1px solid ${C.border}`, background: C.card, display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            <button onClick={enviarPrueba} disabled={!listoParaMandar || prueba}
              style={{ flex: 1, minWidth: 140, padding: "12px", borderRadius: 12, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", opacity: listoParaMandar ? 1 : 0.5 }}>
              {prueba ? "Mandando…" : "✉️ Enviarme una prueba"}
            </button>
            <button onClick={() => setProgramar(p => !p)} disabled={!listoParaMandar}
              style={{ padding: "12px 14px", borderRadius: 12, background: programar ? C.text : C.surface, color: programar ? "#fff" : C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", opacity: listoParaMandar ? 1 : 0.5 }}>
              🕒
            </button>
            {programar ? (
              <div style={{ flexBasis: "100%", display: "flex", gap: 8 }}>
                <input type="datetime-local" value={fechaProg} onChange={e => setFechaProg(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button onClick={programarEnvio} disabled={!fechaProg} style={{ padding: "12px 16px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: fechaProg ? 1 : 0.5 }}>Programar</button>
              </div>
            ) : (
              <button onClick={enviar} disabled={!listoParaMandar || enviando || cuantos === 0}
                style={{ flex: 1.4, minWidth: 160, padding: "12px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: listoParaMandar && cuantos !== 0 ? 1 : 0.5 }}>
                {enviando ? "Enviando…" : `Enviar${cuantos != null ? ` a ${cuantos}` : ""}`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Entregados / abiertos / clicks y quién abrió
const MailStats = ({ campania, onClose }) => {
  const [st, setSt] = useState(null);
  const [filtro, setFiltro] = useState("todos"); // todos | abrieron | click | no_abrieron
  useBackGuard(true, onClose);
  const cargar = async () => {
    const { data: raw } = await supabase.rpc("mail_stats", { p_campania_id: campania.id });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (d && !d.error) setSt(d);
  };
  useEffect(() => { cargar(); const t = setInterval(cargar, 20000); return () => clearInterval(t); }, [campania.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;
  const hora = (iso) => iso ? new Date(iso).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const envios = (st?.envios || []).filter(e => filtro === "todos" ? true : filtro === "abrieron" ? !!e.abierto_en : filtro === "click" ? !!e.click_en : !e.abierto_en && e.estado !== "rebotado");
  const numero = (n, label, sub, color = C.text) => (
    <div style={{ flex: 1, minWidth: 90, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 12px" }}>
      <div style={{ fontSize: 24, fontWeight: 900, color, letterSpacing: "-0.02em", lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1250, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, background: C.card, flexShrink: 0 }}>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} strokeWidth={2.5} color={C.text} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{campania.asunto || "Sin asunto"}</div>
          <div style={{ fontSize: 11.5, color: C.muted }}>{st?.enviada_en ? `Enviado el ${hora(st.enviada_en)}` : campania.estado}</div>
        </div>
        <button onClick={cargar} style={{ padding: "7px 12px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer" }}>Actualizar</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {!st ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 40 }}>Cargando…</div> : (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {numero(st.total, "Enviados")}
              {numero(`${pct(st.entregados, st.total)}%`, "Entregados", `${st.entregados} de ${st.total}`)}
              {numero(`${pct(st.abiertos, st.total)}%`, "Abrieron", `${st.abiertos}`, "#2F7D4F")}
              {numero(`${pct(st.clicks, st.total)}%`, "Hicieron click", `${st.clicks}`, "#8A6D3B")}
              {(st.rebotados > 0 || st.bajas > 0) && numero(st.rebotados + st.bajas, "Rebotes y bajas", `${st.rebotados} rebotó · ${st.bajas} baja`, C.red)}
            </div>
            {/* barra de embudo */}
            <div style={{ height: 10, borderRadius: 5, background: `${C.text}12`, overflow: "hidden", display: "flex", marginBottom: 6 }}>
              <div style={{ width: `${pct(st.clicks, st.total)}%`, background: "#8A6D3B" }} />
              <div style={{ width: `${Math.max(0, pct(st.abiertos, st.total) - pct(st.clicks, st.total))}%`, background: "#2F7D4F" }} />
              <div style={{ width: `${Math.max(0, pct(st.entregados, st.total) - pct(st.abiertos, st.total))}%`, background: `${C.text}55` }} />
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 16 }}>
              Los abiertos se cuentan cuando el mail carga imágenes: algunos clientes de correo los bloquean, así que el número real suele ser mayor. Los datos entran en los minutos siguientes al envío.
            </div>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }} className="sin-scrollbar">
              {[["todos", `Todos (${st.envios?.length || 0})`], ["abrieron", `Abrieron (${st.abiertos})`], ["click", `Click (${st.clicks})`], ["no_abrieron", "No abrieron todavía"]].map(([f, l]) => (
                <button key={f} onClick={() => setFiltro(f)} style={{ padding: "7px 12px", borderRadius: 100, border: `1.5px solid ${filtro === f ? C.text : C.border}`, background: filtro === f ? C.text : C.card, color: filtro === f ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{l}</button>
              ))}
            </div>
            {envios.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>Nada por acá todavía.</div> : envios.map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, overflow: "hidden", flexShrink: 0 }}>
                  {e.avatar_url ? <img loading="lazy" src={cldThumb(e.avatar_url, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: C.muted }}>{(e.username || "?")[0].toUpperCase()}</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{e.username || "rider"}{e.email ? <span style={{ fontWeight: 500, color: C.muted }}> · {e.email}</span> : null}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    {e.baja_en ? "Se dio de baja" : e.estado === "rebotado" ? "Rebotó (mail inválido)" : e.click_en ? `Hizo click ${hora(e.click_en)}` : e.abierto_en ? `Abrió ${hora(e.abierto_en)}` : e.estado === "entregado" ? "Entregado, sin abrir" : e.estado === "fallido" ? "No se pudo enviar" : "Enviado"}
                  </div>
                </div>
                <span style={{ fontSize: 14 }}>{e.baja_en ? "🚫" : e.estado === "rebotado" ? "⚠️" : e.click_en ? "🔗" : e.abierto_en ? "👀" : "✉️"}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

// Lista de comunicados (de un evento, o de todo para el admin) + acceso al editor y a las estadísticas
const MailCampanias = ({ userId, evento = null, esAdmin = false, soloLectura = false, onClose }) => {
  const [lista, setLista] = useState(null);
  const [editar, setEditar] = useState(null);   // null | "nueva" | campania
  const [stats, setStats] = useState(null);
  useBackGuard(!editar && !stats, onClose);
  const cargar = async () => {
    let q = supabase.from("mail_campanias").select("*").order("created_at", { ascending: false }).limit(100);
    if (evento) q = q.eq("evento_id", evento.id); else q = q.is("evento_id", null);
    const { data } = await q;
    setLista(data || []);
  };
  useEffect(() => { cargar(); }, [evento?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const abrir = (c) => { if (c.estado === "borrador" && !soloLectura) setEditar(c); else setStats(c); };
  const cancelarProgramada = async (c) => {
    if (!window.confirm("¿Cancelar el envío programado? Vuelve a borrador.")) return;
    await supabase.from("mail_campanias").update({ estado: "borrador", programada_para: null }).eq("id", c.id);
    cargar();
  };
  const borrar = async (c) => {
    if (!window.confirm("¿Borrar este borrador?")) return;
    await supabase.from("mail_campanias").delete().eq("id", c.id);
    cargar();
  };
  const hora = (iso) => iso ? new Date(iso).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;
  const ESTADO = { borrador: ["Borrador", C.muted], programada: ["Programado", "#8A6D3B"], enviando: ["Enviando…", "#8A6D3B"], enviada: ["Enviado", "#2F7D4F"], cancelada: ["Cancelado", C.muted] };
  if (editar) return <MailComposer userId={userId} evento={evento} esAdmin={esAdmin} campania={editar === "nueva" ? null : editar} onClose={() => { setEditar(null); cargar(); }} onEnviada={() => cargar()} />;
  if (stats) return <MailStats campania={stats} onClose={() => { setStats(null); cargar(); }} />;
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1240, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, background: C.card, flexShrink: 0 }}>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} strokeWidth={2.5} color={C.text} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>{evento ? "📣 Comunicados a los anotados" : "📣 Mails a la base"}</div>
          {evento && <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{evento.titulo}</div>}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {!soloLectura && (
          <button onClick={() => setEditar("nueva")}
            style={{ width: "100%", padding: "14px", borderRadius: 13, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 16 }}>
            ✍️ {evento ? "Escribirles a los anotados" : "Nueva campaña"}
          </button>
        )}
        {lista === null ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 30 }}>Cargando…</div>
          : lista.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 20px", color: C.muted, fontSize: 13.5, lineHeight: 1.55 }}>
              {evento ? "Todavía no les escribiste. Un recordatorio el día anterior, un cambio de horario o el gracias con las fotos: lo que les mandes llega con tu firma y ves quién lo abrió." : "Todavía no hay campañas."}
            </div>
          ) : lista.map(c => (
            <div key={c.id} onClick={() => abrir(c)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 10, cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: (ESTADO[c.estado] || ESTADO.borrador)[1], textTransform: "uppercase", letterSpacing: 0.5 }}>{(ESTADO[c.estado] || ESTADO.borrador)[0]}</span>
                <span style={{ fontSize: 11, color: C.muted }}>{c.estado === "programada" ? `sale el ${hora(c.programada_para)}` : c.enviada_en ? hora(c.enviada_en) : hora(c.updated_at)}</span>
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 4 }}>{c.asunto || <span style={{ color: C.muted, fontWeight: 500 }}>Sin asunto todavía</span>}</div>
              {c.estado === "enviada" ? (
                <div style={{ fontSize: 12.5, color: C.muted }}>
                  <strong style={{ color: C.text }}>{c.total}</strong> enviados · <strong style={{ color: "#2F7D4F" }}>{pct(c.abiertos, c.total)}%</strong> abrieron · <strong style={{ color: "#8A6D3B" }}>{pct(c.clicks, c.total)}%</strong> click{c.bajas > 0 ? ` · ${c.bajas} baja${c.bajas === 1 ? "" : "s"}` : ""}
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(c.cuerpo || "").replace(/[*#\[\]]/g, "").slice(0, 90) || "…"}</div>
              )}
              {!soloLectura && (c.estado === "borrador" || c.estado === "programada") && (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }} onClick={e => e.stopPropagation()}>
                  {c.estado === "programada" && <button onClick={() => cancelarProgramada(c)} style={{ padding: "6px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer" }}>Cancelar envío</button>}
                  {c.estado === "borrador" && <button onClick={() => borrar(c)} style={{ padding: "6px 10px", borderRadius: 8, background: "transparent", border: "none", fontSize: 12, fontWeight: 700, color: C.red, cursor: "pointer" }}>Borrar</button>}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

// El admin: campañas a toda la base, desde el panel
const MailsAdminTab = ({ soloLectura = false }) => {
  const [abrir, setAbrir] = useState(false);
  const [uid, setUid] = useState(null);
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setUid(data?.user?.id || null), () => {}); }, []);
  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>📣 Mails a la base</div>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
        Campañas a todos los riders o a un segmento (activos, ciudad, anotados a eventos futuros), con vista previa, prueba, programación y seguimiento de abiertos y clicks. Los organizadores tienen lo mismo para sus anotados, desde cada evento.
      </div>
      <button onClick={() => setAbrir(true)} disabled={!uid}
        style={{ padding: "12px 18px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
        Abrir campañas
      </button>
      {abrir && uid && <MailCampanias userId={uid} esAdmin soloLectura={soloLectura} onClose={() => setAbrir(false)} />}
    </div>
  );
};

// ─── SELLOS DEL ORGANIZADOR ──────────────────────────────────────────────────
// Un organizador (o grupo) crea sus propios sellos y los da a los riders que
// participan de sus rodadas: automáticos ("a las 5 rodadas conmigo") contando
// check-ins y entradas usadas en sus eventos, o a mano desde la lista de
// anotados. Aparecen en el pasaporte del rider, aparte de los sellos de Olbos.
// (Pedido de Moteros Platenses vía Carlos, 21-sep-2026.)
const SELLOS_EMOJIS = ["🏅", "🎖️", "⭐", "🔥", "🦅", "🐺", "🛡️", "🏍️", "🌄", "🏆", "🤝", "🧭", "🌙", "⚡", "🍖", "🇦🇷"];
const SellosOrganizadorModal = ({ evento, anotados, userIdDe, onClose }) => {
  const [sellos, setSellos] = useState(null);
  const [otorgados, setOtorgados] = useState({}); // sello_id → Set(user_id)
  const [crear, setCrear] = useState(false);
  const [form, setForm] = useState({ nombre: "", emoji: "🏅", descripcion: "", modo: "auto", n: 5, km: 500 });
  const [dar, setDar] = useState(null); // sello elegido para otorgar a mano
  const [uid, setUid] = useState(null);
  useBackGuard(true, () => (dar ? setDar(null) : crear ? setCrear(false) : onClose()));
  const cargar = async (u) => {
    const { data } = await supabase.from("sellos_org").select("*").eq("owner_id", u).eq("activo", true).order("created_at");
    setSellos(data || []);
    const ids = (data || []).map(s => s.id);
    if (ids.length) {
      const { data: ot } = await supabase.from("sellos_otorgados").select("sello_id, user_id").in("sello_id", ids);
      const m = {}; (ot || []).forEach(o => { (m[o.sello_id] = m[o.sello_id] || new window.Set()).add(o.user_id); });
      setOtorgados(m);
    } else setOtorgados({});
  };
  useEffect(() => { supabase.auth.getUser().then(({ data }) => { const u = data?.user?.id; setUid(u || null); if (u) cargar(u); else setSellos([]); }, () => setSellos([])); }, []);
  const guardar = async () => {
    if (!form.nombre.trim()) { alert("Poné un nombre al sello."); return; }
    const fila = { owner_id: uid, nombre: form.nombre.trim(), emoji: form.emoji, descripcion: form.descripcion.trim() || null,
      regla_asistencias: form.modo === "auto" ? Math.max(1, Number(form.n) || 1) : null,
      regla_km: form.modo === "km" ? Math.max(1, Math.round(Number(form.km) || 1)) : null };
    const { error } = await supabase.from("sellos_org").insert(fila);
    if (error) { alert("No se pudo crear: " + error.message); return; }
    setCrear(false); setForm({ nombre: "", emoji: "🏅", descripcion: "", modo: "auto", n: 5, km: 500 });
    cargar(uid);
    // Los automáticos se evalúan enseguida para los que ya vinieron
    supabase.rpc("sellos_evaluar_organizador", { p_owner: uid }).then(() => cargar(uid), () => {});
  };
  const borrar = async (s) => {
    if (!window.confirm(`¿Borrar el sello "${s.nombre}"? Los riders que lo tienen lo pierden.`)) return;
    await supabase.from("sellos_org").update({ activo: false }).eq("id", s.id);
    cargar(uid);
  };
  const otorgar = async (s, userId) => {
    if (!userId) return;
    const tiene = otorgados[s.id]?.has(userId);
    if (tiene) {
      await supabase.from("sellos_otorgados").delete().eq("sello_id", s.id).eq("user_id", userId);
    } else {
      const { error } = await supabase.from("sellos_otorgados").insert({ sello_id: s.id, user_id: userId, otorgado_por: uid, evento_id: evento?.id || null });
      if (error) { alert("No se pudo dar el sello: " + error.message); return; }
      supabase.from("notificaciones").insert({ user_id: userId, tipo: "sello", from_username: "olbos", texto: `te dieron el sello ${s.emoji} «${s.nombre}»${evento?.titulo ? ` en «${evento.titulo}»` : ""}. Está en tu pasaporte.` }).then(() => {}, () => {});
    }
    cargar(uid);
  };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" };
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1350, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, background: C.card }}>
        <button onClick={() => (dar ? setDar(null) : crear ? setCrear(false) : onClose())} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} strokeWidth={2.5} color={C.text} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>🛂 {dar ? `Dar «${dar.nombre}»` : crear ? "Nuevo sello" : "Tus sellos"}</div>
          <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dar ? "Tocá a los que se lo ganaron" : "Los riders los ven en su pasaporte"}</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {dar ? (
          <>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Anotados a «{evento?.titulo}». ✓ = ya tiene el sello. Los que hicieron check-in o usaron su entrada están marcados como presentes.</div>
            {(anotados || []).length === 0 ? <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>Todavía no hay anotados.</div> : (anotados || []).map(a => {
              const id = userIdDe(a);
              const tiene = id && otorgados[dar.id]?.has(id);
              return (
                <div key={a.username} onClick={() => otorgar(dar, id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: tiene ? "rgba(47,125,79,.08)" : C.card, border: `1px solid ${tiene ? "#2F7D4F" : C.border}`, borderRadius: 12, marginBottom: 8, cursor: id ? "pointer" : "default", opacity: id ? 1 : .5 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>@{a.username}{a.nombre ? <span style={{ fontWeight: 400, color: C.muted }}> · {a.nombre}</span> : null}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{a.presente || a.entrada_usada_at ? "✓ presente" : "sin check-in"}{a.moto ? ` · ${a.moto}` : ""}</div>
                  </div>
                  <div style={{ fontSize: 18 }}>{tiene ? "✓" : dar.emoji}</div>
                </div>
              );
            })}
          </>
        ) : crear ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Emoji</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {SELLOS_EMOJIS.map(e => <button key={e} onClick={() => setForm(f => ({ ...f, emoji: e }))} style={{ width: 40, height: 40, borderRadius: 10, border: `2px solid ${form.emoji === e ? C.accent : C.border}`, background: C.card, fontSize: 20, cursor: "pointer" }}>{e}</button>)}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Nombre</div>
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej. Platense de ley" maxLength={40} style={{ ...inp, marginBottom: 12 }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Qué hay que hacer para ganarlo</div>
            <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Ej. Venir a 5 rodadas de Moteros Platenses" maxLength={120} style={{ ...inp, marginBottom: 12 }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Cómo se da</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              <div onClick={() => setForm(f => ({ ...f, modo: "auto" }))} style={{ padding: "11px 12px", borderRadius: 12, border: `1.5px solid ${form.modo === "auto" ? C.accent : C.border}`, background: C.card, cursor: "pointer" }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>Solo, al llegar a
                  <input type="number" min={1} max={200} value={form.n} onClick={e => e.stopPropagation()} onChange={e => setForm(f => ({ ...f, n: e.target.value }))} style={{ width: 58, padding: "4px 8px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 800 }} /> rodadas tuyas
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Cuenta los eventos tuyos en los que el rider hizo check-in o usó su entrada. Se otorga solo, sin que hagas nada.</div>
              </div>
              <div onClick={() => setForm(f => ({ ...f, modo: "km" }))} style={{ padding: "11px 12px", borderRadius: 12, border: `1.5px solid ${form.modo === "km" ? C.accent : C.border}`, background: C.card, cursor: "pointer" }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>Solo, al sumar
                  <input type="number" min={1} max={100000} step={50} value={form.km} onClick={e => e.stopPropagation()} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} style={{ width: 74, padding: "4px 8px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 800 }} /> km en rodadas tuyas
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Suma los km que el rider grabó con el GPS dentro de tus rodadas (los recorridos que quedan pegados al evento). Se otorga solo.</div>
              </div>
              <div onClick={() => setForm(f => ({ ...f, modo: "manual" }))} style={{ padding: "11px 12px", borderRadius: 12, border: `1.5px solid ${form.modo === "manual" ? C.accent : C.border}`, background: C.card, cursor: "pointer" }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Lo doy yo</div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Vos elegís a quién, desde la lista de anotados de cada evento (el que ayudó, el que vino de más lejos, el fundador).</div>
              </div>
            </div>
            <button onClick={guardar} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>Crear sello</button>
          </>
        ) : (
          <>
            <button onClick={() => setCrear(true)} style={{ width: "100%", padding: 14, borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 14 }}>+ Crear un sello</button>
            {sellos === null ? <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>Cargando…</div>
              : sellos.length === 0 ? (
                <div style={{ padding: "26px 18px", textAlign: "center", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                  <div style={{ fontSize: 34, marginBottom: 8 }}>🛂</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Todavía no tenés sellos</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>Creá uno como «Platense de ley: 5 rodadas» y se lo va dando solo a los que vienen. O uno que des vos a mano a quien quieras. Los riders lo lucen en su pasaporte.</div>
                </div>
              ) : sellos.map(s => (
                <div key={s.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{s.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{s.nombre}</div>
                      <div style={{ fontSize: 11.5, color: C.muted }}>{s.regla_asistencias ? `Solo, a las ${s.regla_asistencias} rodadas` : s.regla_km ? `Solo, a los ${Number(s.regla_km).toLocaleString("es-AR")} km en tus rodadas` : "Lo das vos"} · {(otorgados[s.id]?.size || 0)} rider{(otorgados[s.id]?.size || 0) === 1 ? "" : "s"} lo tienen</div>
                      {s.descripcion && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{s.descripcion}</div>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={() => setDar(s)} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Dar a un anotado</button>
                    <button onClick={() => borrar(s)} style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.red, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Borrar</button>
                  </div>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

// En el pasaporte: los sellos que le dieron organizadores y grupos, y los que le faltan con progreso
const SellosOrgPasaporte = ({ userId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    supabase.rpc("sellos_de_rider", { p_user: userId }).then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setData(d || { otorgados: [], pendientes: [] });
    }, () => setData({ otorgados: [], pendientes: [] }));
  }, [userId]);
  if (!data || (!data.otorgados?.length && !data.pendientes?.length)) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Sellos de organizadores y grupos · {data.otorgados.length}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
        {data.otorgados.map(s => (
          <div key={s.id} title={s.descripcion || ""} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 5 }}>{s.emoji}</div>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{s.nombre}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>@{s.owner_username}</div>
          </div>
        ))}
        {data.pendientes.map(s => (
          <div key={s.id} title={s.descripcion || ""} style={{ background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 12, padding: "12px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 5, filter: "grayscale(1)", opacity: .35 }}>{s.emoji}</div>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: C.muted, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{s.nombre}</div>
            <div style={{ fontSize: 10, color: C.faint, marginTop: 3 }}>{s.regla_km ? `${Math.round(Number(s.km || 0)).toLocaleString("es-AR")} de ${Number(s.regla_km).toLocaleString("es-AR")} km` : `${s.asistencias} de ${s.regla_asistencias} rodadas`} · @{s.owner_username}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>Los dan los organizadores de las rodadas a las que vas: algunos solos al sumar rodadas o km con ellos, otros a mano.</div>
    </div>
  );
};

const BotonInscriptos = ({ eventoId, titulo, esPago }) => {
  const [anotados, setAnotados] = useState(null); // array si autorizado, null si no
  const [abierto, setAbierto] = useState(false);
  // Comunicados por mail a los anotados (mail marketing propio)
  const [mails, setMails] = useState(false);
  const [uidMails, setUidMails] = useState(null);
  // Username del organizador: es la dirección de su página pública de comunidad
  const [miUsername, setMiUsername] = useState(null);
  const [linkCopiado, setLinkCopiado] = useState(false);
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data?.user?.id || null;
      setUidMails(uid);
      if (!uid) return;
      const { data: p } = await supabase.from("perfiles").select("username").eq("id", uid).maybeSingle();
      if (p?.username) setMiUsername(p.username);
    }, () => {});
  }, []);
  const [pagos, setPagos] = useState({}); // username → { user_id, pago_confirmado }
  const [entradas, setEntradas] = useState({}); // username → { user_id, entrada_usada_at }
  const [escanear, setEscanear] = useState(false);
  const [sellosOrg, setSellosOrg] = useState(false); // sellos del organizador (pasaporte)
  const [buscaInsc, setBuscaInsc] = useState(""); // buscador de la lista (pedido de Matías N7)

  const cargarPagos = async () => {
    const { data: raw } = await supabase.rpc("get_pagos_evento", { p_evento_id: eventoId });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) {
      const map = {};
      data.forEach(p => { map[p.username] = p; });
      setPagos(map);
    }
  };

  const cargarEntradas = async () => {
    const { data: raw, error } = await supabase.rpc("get_entradas_evento", { p_evento_id: eventoId });
    if (error) return; // RPC todavía no creado → la columna muestra "—" y listo
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) {
      const map = {};
      data.forEach(e => { map[e.username] = e; });
      setEntradas(map);
    }
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: raw } = await supabase.rpc("get_anotados_datos", { p_evento_id: eventoId });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo && Array.isArray(data)) setAnotados(data);
      if (vivo && esPago) cargarPagos();
      if (vivo) cargarEntradas();
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const toggleIngreso = async (username) => {
    const e = entradas[username];
    if (!e || !e.user_id) return;
    const nuevo = !e.entrada_usada_at;
    setEntradas(prev => ({ ...prev, [username]: { ...e, entrada_usada_at: nuevo ? new Date().toISOString() : null } })); // optimista
    const { error } = await supabase.rpc("set_ingreso_inscripto", { p_evento_id: eventoId, p_user_id: e.user_id, p_ingreso: nuevo });
    if (error) {
      setEntradas(prev => ({ ...prev, [username]: e })); // revertir
      alert("No se pudo actualizar el ingreso: " + error.message);
    }
  };

  const togglePago = async (username) => {
    const p = pagos[username];
    if (!p || !p.user_id) return;
    const nuevo = !p.pago_confirmado;
    setPagos(prev => ({ ...prev, [username]: { ...p, pago_confirmado: nuevo } })); // optimista
    const { data: raw, error } = await supabase.rpc("set_pago_inscripto", { p_evento_id: eventoId, p_user_id: p.user_id, p_pagado: nuevo });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) {
      setPagos(prev => ({ ...prev, [username]: { ...p, pago_confirmado: !nuevo } })); // revertir
      alert("No se pudo actualizar el pago: " + (error?.message || r?.error));
    }
  };

  if (!anotados) return null;

  // Columnas: datos clave del inscripto + las preguntas propias del evento (si tiene)
  const extraCols = [...new Set(anotados.flatMap(a => Object.keys(a.datos?.extras || {})))];
  // Rodadas que levantan gente: cada inscripto eligió desde qué parada se suma
  // (queda en datos.punto_suma). Si alguno lo tiene, va la columna + el conteo por parada.
  const hayPuntoSuma = anotados.some(a => a.datos?.punto_suma);
  const porParada = hayPuntoSuma
    ? anotados.reduce((acc, a) => { const k = a.datos?.punto_suma || "Sin definir"; const ex = acc.find(x => x.nombre === k); if (ex) ex.n += 1; else acc.push({ nombre: k, n: 1 }); return acc; }, [])
        .sort((a, b) => (a.nombre === "Sin definir") - (b.nombre === "Sin definir") || b.n - a.n)
    : [];
  const hayUbicacion = anotados.some(a => a.datos?.localidad || a.datos?.direccion);
  const headers = ["Ingresó", ...(esPago ? ["Pagó"] : []), "#", "Usuario", "Nombre y apellido", "Moto", ...(hayPuntoSuma ? ["Se suma en"] : []), "Teléfono", ...(hayUbicacion ? ["Localidad", "Dirección"] : []), "Contacto de emergencia", "Fecha de inscripción", "Comprobante", ...extraCols];
  const fmtFecha = (s) => { if (!s) return ""; const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }); };
  const emergenciaDe = (a) => [a.emergencia_nombre, a.emergencia_telefono].filter(Boolean).join(" · ");
  const filaDe = (a, i) => {
    const ex = a.datos?.extras || {};
    return [entradas[a.username]?.entrada_usada_at ? "Sí" : "No", ...(esPago ? [pagos[a.username]?.pago_confirmado ? "Sí" : "No"] : []), String(i + 1), "@" + (a.username || "rider"), a.nombre || "", a.moto || "", ...(hayPuntoSuma ? [a.datos?.punto_suma || ""] : []), a.telefono || "", ...(hayUbicacion ? [a.datos?.localidad || "", a.datos?.direccion || ""] : []), emergenciaDe(a), fmtFecha(a.fecha_inscripcion), a.datos?.comprobante_url || "", ...extraCols.map(c => ex[c] || "")];
  };
  const presentesCount = anotados.filter(a => a.presente).length;
  const pagosCount = anotados.filter(a => pagos[a.username]?.pago_confirmado).length;
  const ingresadosCount = anotados.filter(a => entradas[a.username]?.entrada_usada_at).length;
  const colUsuario = esPago ? 3 : 2; // índice de la columna Usuario (para la negrita)
  // Buscador: nombre, apellido, @usuario, DNI, teléfono, moto o cualquier respuesta del
  // formulario. Sin tildes ni mayúsculas, y el DNI con o sin puntos. El # sigue siendo el
  // orden de inscripción; el CSV y Copiar siguen bajando la lista completa.
  const normInsc = (s) => String(s == null ? "" : s).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/(\d)[.\s-](?=\d)/g, "$1");
  const qInsc = normInsc(buscaInsc.trim());
  const filasVisibles = anotados.map((a, i) => ({ a, i })).filter(({ a, i }) => {
    if (!qInsc) return true;
    const pajar = normInsc([...filaDe(a, i), a.email, a.dni, a.datos?.dni, a.deslinde_dni, a.deslinde_nombre].filter(Boolean).join(" | "));
    return qInsc.split(/\s+/).every(t => pajar.includes(t));
  });
  const copiar = async () => {
    const tsv = [headers.join("\t"), ...anotados.map((a, i) => filaDe(a, i).join("\t"))].join("\n");
    try { await navigator.clipboard.writeText(tsv); alert("Copiado ✓ Pegalo en Excel / Sheets / WhatsApp."); } catch { alert(tsv); }
  };
  const descargarCSV = () => {
    const esc = (v) => { const s = String(v ?? ""); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    // CRLF para compatibilidad con Excel/Sheets. Sin BOM (se veía "ï»¿" en la 1ra celda).
    const csv = [headers, ...anotados.map((a, i) => filaDe(a, i))].map(f => f.map(esc).join(",")).join("\r\n");
    const slug = (titulo || "evento").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "evento";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `inscriptos-${slug}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const cell = { padding: "8px 11px", borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, color: C.text, whiteSpace: "nowrap" };

  return (
    <>
      <button onClick={() => setAbierto(true)}
        style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        📋 Ver inscriptos ({anotados.length})
      </button>
      <button onClick={() => setEscanear(true)}
        style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.text, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        🎫 Escanear entradas (puerta)
      </button>
      <button onClick={() => setMails(true)} disabled={!uidMails}
        style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        📣 Escribirles a los anotados
      </button>
      <button onClick={() => setSellosOrg(true)} disabled={!uidMails}
        style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        🛂 Sellos para los que vienen
      </button>
      {miUsername && (
        <button onClick={async () => {
          const url = `${window.location.origin}/comunidad/${miUsername}`;
          try {
            if (navigator.share) { await navigator.share({ title: "Mi comunidad en Olbos", url }); return; }
            await navigator.clipboard.writeText(url);
            setLinkCopiado(true); setTimeout(() => setLinkCopiado(false), 2500);
          } catch (e) { /* canceló el compartir */ }
        }}
          style={{ width: "100%", padding: "11px", borderRadius: 12, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          🔗 {linkCopiado ? "Link copiado ✓" : "Compartir mi página de comunidad"}
        </button>
      )}
      {miUsername && (
        <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
          olbos.ai/comunidad/{miUsername} — tus salidas, tus rutas y tu historia en una sola dirección, para mandarle a un municipio o a un sponsor.
        </p>
      )}
      {mails && uidMails && (
        <MailCampanias userId={uidMails} evento={{ id: eventoId, titulo }} onClose={() => setMails(false)} />
      )}
      {sellosOrg && uidMails && (
        <SellosOrganizadorModal evento={{ id: eventoId, titulo }} anotados={(anotados || []).map(a => ({ ...a, entrada_usada_at: entradas[a.username]?.entrada_usada_at || a.entrada_usada_at }))}
          userIdDe={(a) => a.user_id || pagos[a.username]?.user_id || entradas[a.username]?.user_id || null} onClose={() => setSellosOrg(false)} />
      )}
      {escanear && (
        <EscanerEntradas eventoId={eventoId} esPago={esPago}
          onCambio={cargarEntradas}
          onClose={() => { setEscanear(false); cargarEntradas(); }} />
      )}
      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setAbierto(false)}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 760, height: "90vh", display: "flex", flexDirection: "column" }} className="fade-up">
            {/* header fijo */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 12px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Inscriptos ({anotados.length}){presentesCount > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: "#1F8347", marginLeft: 6 }}>· {presentesCount} presente{presentesCount !== 1 ? "s" : ""}</span>}{esPago && <span style={{ fontSize: 12, fontWeight: 700, color: "#8A6D3B", marginLeft: 6 }}>· 💳 {pagosCount}/{anotados.length} pagaron</span>}{ingresadosCount > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: C.text, marginLeft: 6 }}>· 🎫 {ingresadosCount} adentro</span>}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                {anotados.length > 0 && (
                  <>
                    <button onClick={descargarCSV} style={{ padding: "7px 12px", borderRadius: 8, background: C.text, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 5 }}>
                      <Download size={13} strokeWidth={2.2} /> CSV
                    </button>
                    <button onClick={copiar} style={{ padding: "7px 12px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 5 }}>
                      <Share2 size={13} strokeWidth={2} /> Copiar
                    </button>
                  </>
                )}
                <button onClick={() => setAbierto(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={15} strokeWidth={2} color={C.muted} />
                </button>
              </div>
            </div>
            {/* Conteo por parada: cuánta gente se suma en cada punto de la rodada */}
            {hayPuntoSuma && anotados.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "10px 16px", borderBottom: `1px solid ${C.border}`, background: C.surface, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", alignSelf: "center", marginRight: 2 }}>Se suman en</span>
                {porParada.map(pp => (
                  <span key={pp.nombre} style={{ fontSize: 12, fontWeight: 600, color: pp.nombre === "Sin definir" ? C.muted : C.text, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 9px", whiteSpace: "nowrap" }}>
                    {pp.nombre} <strong style={{ fontWeight: 800 }}>{pp.n}</strong>
                  </span>
                ))}
              </div>
            )}
            {anotados.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: C.surface, borderRadius: 10, padding: "0 10px", border: `1px solid ${C.border}` }}>
                  <Search size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
                  <input value={buscaInsc} onChange={e => setBuscaInsc(e.target.value)} placeholder="Buscar por nombre, apellido, DNI, usuario…"
                    style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: 14, padding: "9px 0", color: C.text, fontFamily: "inherit" }} />
                  {buscaInsc && (
                    <button onClick={() => setBuscaInsc("")} title="Borrar" style={{ border: "none", background: "none", padding: 2, cursor: "pointer", display: "flex" }}>
                      <X size={14} strokeWidth={2} color={C.muted} />
                    </button>
                  )}
                </div>
                {qInsc && <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, whiteSpace: "nowrap" }}>{filasVisibles.length} de {anotados.length}</span>}
              </div>
            )}
            {anotados.length === 0 ? (
              <div style={{ flex: 1, fontSize: 13, color: C.muted, textAlign: "center", padding: "40px 0" }}>Todavía no se anotó nadie.</div>
            ) : filasVisibles.length === 0 ? (
              <div style={{ flex: 1, fontSize: 13, color: C.muted, textAlign: "center", padding: "40px 16px" }}>Nadie coincide con “{buscaInsc.trim()}”.</div>
            ) : (
              <div style={{ flex: 1, overflow: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 12.5, width: "max-content", minWidth: "100%" }}>
                  <thead>
                    <tr>
                      {headers.map((h, ci) => (
                        <th key={ci} style={{ position: "sticky", top: 0, zIndex: 1, background: C.surface, padding: "9px 11px", borderBottom: `2px solid ${C.border}`, borderRight: `1px solid ${C.border}`, textAlign: "left", fontWeight: 700, color: C.muted, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filasVisibles.map(({ a, i }, k) => (
                      <tr key={i} style={{ background: k % 2 ? C.surface : C.card }}>
                        {filaDe(a, i).map((v, ci) => (
                          ci === 0 ? (
                            <td key={ci} style={{ ...cell, textAlign: "center" }}>
                              <button onClick={() => toggleIngreso(a.username)}
                                title={v === "Sí" ? "Marcar como NO ingresado" : "Marcar ingreso manual"}
                                style={{ padding: "4px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, background: v === "Sí" ? C.text : C.surface, color: v === "Sí" ? "#fff" : C.muted }}>
                                {v === "Sí" ? "🎫 Adentro" : "Afuera"}
                              </button>
                            </td>
                          ) : esPago && ci === 1 ? (
                            <td key={ci} style={{ ...cell, textAlign: "center" }}>
                              <button onClick={() => togglePago(a.username)}
                                title={v === "Sí" ? "Marcar como NO pagado" : "Marcar como pagado"}
                                style={{ padding: "4px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800, background: v === "Sí" ? "#2F7D4F" : C.surface, color: v === "Sí" ? "#fff" : C.muted }}>
                                {v === "Sí" ? "✓ Pagó" : "Pendiente"}
                              </button>
                            </td>
                          ) : typeof v === "string" && v.startsWith("http") ? (
                            <td key={ci} style={{ ...cell, textAlign: "center" }}>
                              <a href={v} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 800, color: "#1F8347", textDecoration: "none" }}>📎 Ver</a>
                            </td>
                          ) : (
                            <td key={ci} style={{ ...cell, fontWeight: ci === colUsuario ? 700 : 400 }}>
                              {v || "—"}
                            </td>
                          )
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── REDES Y LINKS DEL PERFIL ────────────────────────────────────────────────
// Cada tipo: ícono (chip color de marca) + cómo se arma la URL desde lo que carga el usuario.
// Logos oficiales de cada red (paths de Simple Icons, viewBox 24x24), en blanco sobre el color de marca.
const LOGOS_REDES = {
  instagram: "M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077",
  whatsapp: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z",
  tiktok: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  youtube: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  facebook: "M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z",
};
const LINK_TIPOS = {
  instagram: { label: "Instagram", logo: "instagram", color: "linear-gradient(45deg,#F9CE34 0%,#EE2A7B 50%,#6228D7 100%)" },
  tiktok:    { label: "TikTok",    logo: "tiktok",    color: "#000000" },
  youtube:   { label: "YouTube",   logo: "youtube",   color: "#FF0000" },
  facebook:  { label: "Facebook",  logo: "facebook",  color: "#1877F2" },
  whatsapp:  { label: "WhatsApp",  logo: "whatsapp",  color: "#25D366" },
  web:       { label: "Sitio web", Icon: Globe,       color: C.text },
};
const LINK_TIPOS_ORDEN = ["instagram", "tiktok", "youtube", "facebook", "whatsapp", "web"];

// Arma un link de WhatsApp que ANDE a partir de un número escrito como lo escribe
// cualquiera. La agenda guarda el teléfono tal como lo publica el organizador
// ("1165695346", "11 6678-1852", "02323 518420", "2241-410235") y wa.me sin código
// de país no abre ninguna conversación: el botón "Contactar por WhatsApp" quedaba
// muerto en 26 de las 58 salidas futuras (lo encontró Carlos con el evento de
// Tierra Adentro). Normaliza a 549 + área + número: saca el 0 de larga distancia,
// el 15 viejo, y no duplica el 9 si ya venía. Un número con + de OTRO país se
// respeta como está (hay organizadores de Uruguay y Chile).
const waHref = (tel, texto) => {
  const crudo = String(tel || "").trim();
  let d = crudo.replace(/[^0-9]/g, "");
  if (!d) return null;
  if (!(crudo.startsWith("+") && !d.startsWith("54"))) {
    if (d.startsWith("54")) d = d.slice(2);
    d = d.replace(/^0/, "");
    if (d.length === 11 && d.startsWith("9")) d = d.slice(1);
    d = d.replace(/^(\d{2,4})15(\d{6,8})$/, "$1$2");
    d = "549" + d;
  }
  return `https://wa.me/${d}${texto ? `?text=${encodeURIComponent(texto)}` : ""}`;
};

// Construye la URL final a partir de lo que el usuario cargó (URL completa o solo @usuario/número)
const construirUrlLink = (tipo, valor) => {
  const t = (valor || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  const u = t.replace(/^@/, "").replace(/\/+$/, "");
  switch (tipo) {
    case "instagram": return `https://instagram.com/${u}`;
    case "tiktok":    return `https://www.tiktok.com/@${u}`;
    case "youtube":   return `https://youtube.com/@${u}`;
    case "facebook":  return `https://facebook.com/${u}`;
    case "whatsapp":  return waHref(t) || "";
    case "web":       return `https://${t.replace(/^\/+/, "")}`;
    default:          return t;
  }
};

// Fila de íconos clickeables para mostrar en el perfil (propio o de otros)
const LinksPerfil = ({ links, style }) => {
  const arr = Array.isArray(links) ? links.filter(l => l && l.tipo && l.url && LINK_TIPOS[l.tipo]) : [];
  if (arr.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 9, flexWrap: "wrap", ...style }}>
      {arr.map((l, i) => {
        const cfg = LINK_TIPOS[l.tipo];
        const href = construirUrlLink(l.tipo, l.url);
        if (!href) return null;
        return (
          <a key={i} href={href} target="_blank" rel="noreferrer" title={cfg.label}
            style={{ width: 34, height: 34, borderRadius: "50%", background: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", flexShrink: 0 }}>
            {cfg.logo
              ? <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d={LOGOS_REDES[cfg.logo]} /></svg>
              : <cfg.Icon size={16} strokeWidth={2.2} color="#fff" />}
          </a>
        );
      })}
    </div>
  );
};

// Badge para perfiles de comunidad/tienda (organizan salidas, no son riders individuales)
const BadgeComunidad = ({ tipo }) => {
  if (tipo === "embajador") {
    // Embajadores fundadores: los primeros que le pusieron el cuerpo a Olbos
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 100, background: "#8A6D3B14", border: "1px solid #8A6D3B55", fontSize: 10.5, fontWeight: 700, color: "#8A6D3B", letterSpacing: "-0.005em", whiteSpace: "nowrap" }}>
        <Star size={10} strokeWidth={2.5} /> Embajador fundador
      </span>
    );
  }
  if (tipo !== "comunidad") return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 100, background: "#1111110F", border: "1px solid #11111133", fontSize: 10.5, fontWeight: 700, color: C.text, letterSpacing: "-0.005em", whiteSpace: "nowrap" }}>
      <Users size={10} strokeWidth={2.5} /> Comunidad
    </span>
  );
};

// ─── INVITÁ Y SUMÁ: link de referido + tabla de embajadores ──────────────────
// Cada rider tiene su link (olbos.ai/r/usuario). El que entra por ahí y se crea
// cuenta queda atribuido (perfiles.referido_por, via RPC registrar_referido).
// La tabla muestra quién trajo más riders — estatus, no plata (por ahora).
const InvitaModal = ({ session, onClose }) => {
  const [verPerfil, setVerPerfil] = useState(null); // username a mostrar
  const [miPerfil, setMiPerfil] = useState(null);
  const [misReferidos, setMisReferidos] = useState(null);
  const [miRed, setMiRed] = useState(null); // { total, niveles: [{nivel, cantidad}] }
  const [tabla, setTabla] = useState([]);
  useEffect(() => {
    (async () => {
      const uid = session?.user?.id;
      if (uid) {
        const { data } = await supabase.from("perfiles").select("username, embajador_fundador").eq("id", uid).maybeSingle();
        setMiPerfil(data || null);
        const { count } = await supabase.from("perfiles").select("id", { count: "exact", head: true }).eq("referido_por", uid);
        setMisReferidos(count ?? 0);
        // Red completa: directos + los que trajeron tus referidos (recursivo, server-side)
        const { data: redRaw } = await supabase.rpc("red_embajador", { p_user_id: uid }).then(r => r, () => ({ data: null }));
        const red = typeof redRaw === "string" ? JSON.parse(redRaw) : redRaw;
        if (red && !red.error) setMiRed(red);
      }
      const { data: raw } = await supabase.rpc("tabla_embajadores").then(r => r, () => ({ data: null }));
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(d)) setTabla(d);
    })();
  }, [session?.user?.id]);

  const miLink = miPerfil?.username ? `${SITE_URL}/r/${miPerfil.username}` : null;
  const msjInvita = miLink ? `🏍️ Sumate a Olbos, la app de la comunidad motera: eventos, rodadas, tu garage y mucho más. Entrá con mi link:\n${miLink}` : "";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,.5)", backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-start", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "0 0 20px 20px", maxHeight: "85vh", overflowY: "auto", padding: "calc(var(--sat) + 16px) 20px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Invitá y sumá</h2>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={14} strokeWidth={2.5} color={C.muted} />
          </button>
        </div>
        <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Compartí tu link: cada rider que se sume con él queda contado como tuyo. Los que más comunidad traen, arriba de todo.
        </p>

        {miLink ? (
          <>
            <div style={{ padding: "10px 12px", background: C.surface, borderRadius: 10, fontSize: 12, color: C.text, fontFamily: "monospace", wordBreak: "break-all", border: `1px solid ${C.border}`, marginBottom: 10 }}>{miLink}</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button onClick={() => compartirNativo({ texto: msjInvita, titulo: "Sumate a Olbos" })}
                style={{ flex: 1, padding: "12px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                WhatsApp
              </button>
              <button onClick={async () => { try { await navigator.clipboard.writeText(miLink); alert("Link copiado"); } catch { /* */ } }}
                style={{ flex: 1, padding: "12px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Copiar link
              </button>
            </div>
            {misReferidos !== null && (
              <div style={{ fontSize: 12.5, color: misReferidos > 0 ? "#1F8347" : C.muted, fontWeight: 600, marginBottom: miRed && miRed.total > misReferidos ? 4 : 16 }}>
                {misReferidos > 0 ? `🏍️ Ya trajiste ${misReferidos} rider${misReferidos !== 1 ? "s" : ""} a Olbos` : "Todavía no trajiste riders — compartí tu link y arrancá."}
              </div>
            )}
            {miRed && miRed.total > misReferidos && (
              <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, marginBottom: 16 }}>
                🌐 Tu red completa: {miRed.total} riders — los que trajiste, más los que trajeron ellos, y así siguiendo.
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: "12px 14px", background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 10, fontSize: 12.5, color: C.gold, marginBottom: 16 }}>
            Iniciá sesión para tener tu link de invitación.
          </div>
        )}

        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
          Tabla de embajadores
        </div>
        {tabla.length === 0 && <div style={{ fontSize: 12.5, color: C.muted, padding: "8px 0" }}>Todavía no hay referidos contados. La tabla arranca con el primero.</div>}
        {tabla.map((e, i) => (
          <div key={i} onClick={() => setVerPerfil(e.username)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderBottom: i < tabla.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer" }}>
            <div style={{ width: 24, fontSize: 13, fontWeight: 800, color: i < 3 ? "#8A6D3B" : C.muted, textAlign: "center", flexShrink: 0 }}>{i + 1}</div>
            <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", background: C.surface, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {e.avatar_url ? <img loading="lazy" decoding="async" src={e.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={15} color={C.muted} />}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>@{e.username}</span>
              {e.embajador_fundador && <BadgeComunidad tipo="embajador" />}
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, flexShrink: 0 }}>{e.referidos}</div>
          </div>
        ))}
      </div>
      {verPerfil && <PerfilPublico username={verPerfil} onClose={() => setVerPerfil(null)} />}
    </div>
  );
};

// ─── CHECK-IN PRESENCIAL (Capa 2) ────────────────────────────────────────────
// Inscripto: hace check-in validando GPS de proximidad al punto de encuentro.
// ── Helpers de estado de salida (espejo de la lógica del servidor) ──
// Inicio del evento en ms: fecha + hora (fallback mediodía si no hay hora cargada).
const inicioEventoTs = (evento) => {
  if (!evento || !evento.fecha) return null;
  const hora = (evento.hora && /^\d{1,2}:\d{2}/.test(evento.hora)) ? evento.hora.slice(0, 5) : "12:00";
  const t = new Date(`${String(evento.fecha).slice(0, 10)}T${hora}:00`).getTime();
  return isNaN(t) ? null : t;
};
// Fin del último día de la rodada (fecha_fin o fecha) = arranque del día siguiente.
const finUltimoDiaTs = (evento) => {
  const f = evento && (evento.fecha_fin || evento.fecha);
  if (!f) return null;
  const d = new Date(String(f).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 1);
  return d.getTime();
};
// ¿La rodada ya terminó? estado finalizada/sorteo, o auto-finalizada al pasar el último día.
const rodadaTerminada = (evento) => {
  if (!evento) return false;
  if (["finalizada", "sorteo_en_curso"].includes(evento.estado)) return true;
  const fin = finUltimoDiaTs(evento);
  return fin != null && Date.now() > fin;
};
// ¿Inscripción cerrada? SOLO si el organizador la cerró a mano, o si el evento ya terminó
// (medianoche del último día). Que la rodada arranque NO cierra la inscripción: en un evento
// grande la gente se sigue sumando durante el día, después de la largada.
const inscripcionCerrada = (evento) => !!evento && (evento.inscripcion_cerrada === true || rodadaTerminada(evento));
// ¿Es "el día del evento" para el anotado? Desde 4 h antes de la hora de inicio hasta
// que la rodada termina. En esa ventana las dos acciones que importan (check-in y
// mapa en vivo) se muestran sin desplegar nada: estaban escondidas en "En la rodada"
// (usuarios a Matías, 09-oct).
const esDiaDelEvento = (evento) => {
  const ini = inicioEventoTs(evento);
  if (ini == null) return false;
  if (rodadaTerminada(evento)) return false;
  return Date.now() >= ini - 4 * 3600 * 1000;
};
// Quién GESTIONA un evento: dueño, admin o co-organizador (inscripcion_config.co_organizadores).
// Un solo criterio para largar, abrir el check-in, asignar roles, el intercom y los cobros.
// Hasta el 09-oct cada bloque miraba solo creator_id: un co-org o el admin podían editar la
// ficha pero no largar la rodada (y la Nocturna la carga el bot @olbos).
const gestionaEvento = (evento, uid, esAdmin = false) => {
  if (!evento || !uid) return false;
  if (esAdmin) return true;
  if (evento.creator_id === uid) return true;
  const co = evento.inscripcion_config && Array.isArray(evento.inscripcion_config.co_organizadores) ? evento.inscripcion_config.co_organizadores : [];
  return co.some(c => c && (c.user_id === uid || c === uid));
};
// ¿Check-in abierto? modo 'abierto'/'cerrado' forzado, o 'auto' (4h antes y rodada no terminada).
const checkinAbierto = (evento) => {
  if (!evento) return false;
  if (evento.checkin_modo === "abierto") return true;
  if (evento.checkin_modo === "cerrado") return false;
  const inicio = inicioEventoTs(evento);
  if (inicio == null) return false;            // sin fecha → solo manual
  if (rodadaTerminada(evento)) return false;
  return Date.now() >= inicio - 4 * 3600 * 1000;
};

// ── COMPARTIDO DE POSICIÓN A NIVEL APP ───────────────────────────────────────
// UN solo watch global por evento, que arranca solo al hacer CHECK-IN (estás en
// el punto de encuentro = tu posición aparece en el mapa de la rodada) y no
// depende de ninguna pantalla abierta. Reemplaza al botón "Compartir mi
// ubicación", que era un paso más que nadie entendía por qué existía.
const _shareUbi = { watch: null, hb: null, eventoId: null, userId: null, ultimaPos: null, ultimaSubida: 0, pts: [] };

const detenerCompartirUbicacion = async (borrarPin = true) => {
  if (_shareUbi.watch) { try { await _shareUbi.watch.stop(); } catch (e) {} }
  if (_shareUbi.hb) clearInterval(_shareUbi.hb);
  if (borrarPin && _shareUbi.eventoId && _shareUbi.userId) {
    supabase.from("salida_ubicaciones").delete().eq("evento_id", _shareUbi.eventoId).eq("user_id", _shareUbi.userId).then(() => {}, () => {});
  }
  // El camino andado compartiendo posición aporta anónimo a la red (con opt-out)
  const pts = _shareUbi.pts;
  _shareUbi.pts = [];
  try { aportarTramoARed(pts); } catch (e) { /* */ }
  if (_shareUbi.eventoId) bgSesionActualizar({ evento_id: null });
  _shareUbi.watch = null; _shareUbi.hb = null; _shareUbi.eventoId = null; _shareUbi.userId = null; _shareUbi.ultimaPos = null;
};

const compartiendoUbicacionDe = (eventoId) => _shareUbi.eventoId === eventoId && !!_shareUbi.watch;

// Cada publicación de mi posición en una rodada avisa si salió o no: antes los errores se
// tragaban y el rider creía que lo veían cuando no (el mapa lo muestra como cartel rojo)
const reportarPublicacionRodada = (eventoId, error) => {
  try {
    window.dispatchEvent(new CustomEvent("olbos-publicacion-rodada", { detail: {
      eventoId, ok: !error,
      codigo: error ? String(error.code || "") : "",
      mensaje: error ? String(error.message || "").slice(0, 200) : "",
    } }));
  } catch (e) { /* */ }
};

const iniciarCompartirUbicacion = async (eventoId) => {
  if (compartiendoUbicacionDe(eventoId)) return true;
  await detenerCompartirUbicacion();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const enviar = (gp) => {
    if (!gp || gp.lat == null) return;
    _shareUbi.ultimaPos = gp;
    supabase.from("salida_ubicaciones").upsert({
      evento_id: eventoId, user_id: user.id,
      lat: gp.lat, lng: gp.lng, heading: gp.head != null ? gp.head : null,
      updated_at: new Date().toISOString(),
    }).then(({ error }) => reportarPublicacionRodada(eventoId, error), () => {});
  };
  try {
    _shareUbi.eventoId = eventoId;
    _shareUbi.userId = user.id;
    bgSesionActualizar({ evento_id: eventoId });
    _shareUbi.watch = await startGeoWatch((gp) => {
      if (gp && gp.lat != null) _shareUbi.pts.push({ lat: gp.lat, lng: gp.lng, ts: gp.t || Date.now() });
      const ahora = Date.now();
      if (ahora - _shareUbi.ultimaSubida < 5000) return;
      _shareUbi.ultimaSubida = ahora;
      enviar(gp);
    }, () => {});
    // Latido: aunque no te muevas, tu pin no se pone viejo
    _shareUbi.hb = setInterval(() => { if (_shareUbi.ultimaPos) enviar(_shareUbi.ultimaPos); }, 30000);
    // Se corta solo: al terminar el evento (medianoche del último día) o a las 12 h como
    // tope. Antes seguía hasta que alguien tocara "dejar de compartir", y nadie lo tocaba:
    // el GPS quedaba prendido el resto del día (Matías, 08-oct).
    (async () => {
      let fin = Date.now() + 12 * 3600 * 1000;
      try {
        const { data: ev } = await supabase.from("eventos").select("fecha, fecha_fin, hora, estado").eq("id", eventoId).maybeSingle();
        const f = ev ? finUltimoDiaTs(ev) : null;
        if (f != null && f < fin) fin = Math.max(f, Date.now() + 10 * 60000);
      } catch (e) { /* queda el tope de 12 h */ }
      const chequeo = setInterval(() => {
        if (_shareUbi.eventoId !== eventoId || !_shareUbi.watch) { clearInterval(chequeo); return; }
        if (Date.now() >= fin) { clearInterval(chequeo); detenerCompartirUbicacion(); }
      }, 60000);
    })();
    return true;
  } catch (e) {
    _shareUbi.eventoId = null; _shareUbi.userId = null;
    return false;
  }
};

// Compartir mi ubicación en una salida SIN tener que abrir el mapa en vivo.
// eslint-disable-next-line no-unused-vars -- reemplazado por el compartido automático al hacer check-in
const BotonCompartirUbicacionSalida = ({ evento }) => {
  const [compartiendo, setCompartiendo] = useState(false);
  const watchRef = useRef(null);
  const ultimaSubidaRef = useRef(0);
  const ultimaPosRef = useRef(null);
  const hbRef = useRef(null);

  const enviar = async (gp) => {
    if (!gp || gp.lat == null) return;
    ultimaPosRef.current = gp;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    supabase.from("salida_ubicaciones").upsert({
      evento_id: evento.id, user_id: user.id,
      lat: gp.lat, lng: gp.lng, heading: gp.head != null ? gp.head : null,
      updated_at: new Date().toISOString(),
    }).then(() => {}, () => {});
  };

  const empezar = async () => {
    try {
      watchRef.current = await startGeoWatch((gp) => {
        const ahora = Date.now();
        if (ahora - ultimaSubidaRef.current < 5000) return;
        ultimaSubidaRef.current = ahora;
        enviar(gp);
      }, () => {});
      setCompartiendo(true);
      hbRef.current = setInterval(() => { if (ultimaPosRef.current) enviar(ultimaPosRef.current); }, 30000);
    } catch (e) { alert("No se pudo iniciar el compartido: " + (e.message || "")); }
  };
  const detener = async () => {
    if (watchRef.current) { try { await watchRef.current.stop(); } catch (e) {} watchRef.current = null; }
    if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
    setCompartiendo(false);
  };
  useEffect(() => () => { if (watchRef.current) watchRef.current.stop(); if (hbRef.current) clearInterval(hbRef.current); }, []);

  return (
    <button onClick={() => (compartiendo ? detener() : empezar())}
      style={{ width: "100%", padding: "12px", borderRadius: 10, marginBottom: 8, background: compartiendo ? "#2F7D4F" : C.surface, color: compartiendo ? "#fff" : C.text, border: compartiendo ? "none" : `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
      {compartiendo
        ? <><span style={{ width: 9, height: 9, borderRadius: "50%", background: C.card, animation: "pulse 1.4s infinite" }} /> Compartiendo · tocá para detener</>
        : <>📍 Compartir mi ubicación</>}
    </button>
  );
};

// Organizador: ve los presentes y puede activar check-in manual (si el GPS falla).
// Check-in en un toque: lo usan BotonCheckin (plegable "En la rodada") y las acciones
// del día del evento (pie de la ficha y tarjeta de la lista). Devuelve lo que contesta
// el RPC hacer_checkin ({ ok } o { error, distancia }).
const ejecutarCheckin = async (evento) => {
  // Por obtenerPosicion: en la app nativa va por el plugin de GPS (el navigator del WebView
  // mostraba en iPhone el cartel "localhost quiere usar tu ubicación")
  const geo = await obtenerPosicion({ enableHighAccuracy: true, timeout: 12000 }).then(p => ({ lat: p.lat, lng: p.lng }), () => null);
  const { data: raw, error } = await supabase.rpc("hacer_checkin", { p_evento_id: evento.id, p_lat: geo?.lat ?? null, p_lng: geo?.lng ?? null });
  const r = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (error) return { ok: false, error: "red" };
  if (r?.ok) {
    try { navigator.vibrate?.(150); } catch (e) { /* */ }
    supabase.rpc("sellos_evaluar_evento", { p_evento_id: evento.id }).then(() => {}, () => {});
    // Check-in hecho = tu posición entra al mapa de la rodada, sin pasos extra (watch global)
    iniciarCompartirUbicacion(evento.id).then(() => {}, () => {});
    window.dispatchEvent(new CustomEvent("olbos-checkin-hecho", { detail: { eventoId: evento.id } }));
    registrarUso("checkin_ok", { id: evento.id });
  }
  return r || { ok: false };
};
const mensajeCheckin = (r) => ({
  cerrado: "El check-in todavía no está abierto. Se habilita solo 4 h antes de la salida.",
  lejos: `Estás a ~${r?.distancia} m del punto de encuentro. Acercate para hacer check-in (o pedile al organizador que active el check-in manual).`,
  sin_gps: "No pudimos leer tu ubicación. Activá el GPS y probá de nuevo.",
  no_login: "Necesitás una cuenta para hacer check-in.",
  red: "Parece que no hay señal. Probá de nuevo en unos segundos.",
}[r?.error] || "No se pudo hacer check-in. Probá de nuevo.");

// ¿Alcanza con la cuenta para anotarse? Sí cuando es gratis, el organizador no pidió
// datos ni escribió un deslinde propio y no hay preguntas ni puntos de suma. Los
// eventos de la agenda (los carga el bot @olbos, no el organizador) también van
// directo: los "campos" que traen son de la carga, no un pedido del organizador.
// Decisión de Carlos (09-oct): anotarse a un evento gratis pide SOLO la cuenta.
const inscripcionDirectaPosible = (e) => {
  const cfg = e.inscripcion_config || {};
  const precio = Number(e.precio) > 0 ? Number(e.precio) : Number(e.precio_entrada || 0);
  if (precio > 0 || e.pago_link || e.pago_info) return false;
  if (e.inscripcion === "aprobacion") return false;
  if (String(cfg.deslinde || "").trim()) return false;
  if (Array.isArray(cfg.extras) && cfg.extras.some(x => x && x.label && String(x.label).trim())) return false;
  if (Array.isArray(cfg.puntos_suma) && cfg.puntos_suma.some(p => p && p.nombre && String(p.nombre).trim())) return false;
  const pideCampos = Object.values(cfg.campos || {}).some(Boolean);
  if (pideCampos && e.creator_id !== BOT_OLBOS_ID) return false;
  return true;
};

// Alta a la rodada desde el link del grupo. Va por el RPC sumarme_a_rodada (security
// definer: la primera inscripción de una cuenta recién creada no va por insert directo,
// ver CLAUDE.md). Si el SQL todavía no se corrió, cae al insert de siempre.
const inscribirDirecto = async (eventoId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { ok: false, motivo: "no_login" };
  const { error: errPerfil } = await supabase.rpc("asegurar_perfil");
  const { data: raw, error: errRpc } = await supabase.rpc("sumarme_a_rodada", { p_evento_id: eventoId });
  const r = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (!errRpc && r && r.ok) {
    if (!r.ya_estaba) { registrarUso("inscripcion_ok", { id: eventoId, desde: "sumate" }); window.dispatchEvent(new CustomEvent("olbos-recargar-eventos")); }
    return { ok: true, yaEstaba: !!r.ya_estaba };
  }
  if (!errRpc && r && r.error) return { ok: false, motivo: r.error };
  const { data: ya } = await supabase.from("inscripciones").select("id").eq("evento_id", eventoId).eq("user_id", user.id).maybeSingle();
  if (ya) return { ok: true, yaEstaba: true };
  const insertar = () => supabase.from("inscripciones").insert({ evento_id: eventoId, user_id: user.id, moto_id: null, estado: "confirmado" });
  let { error } = await insertar();
  if (error && /foreign key|fkey/i.test(error.message || "") && !/evento_id_fkey/i.test(error.message || "")) {
    await supabase.rpc("asegurar_perfil");
    await new Promise(res => setTimeout(res, 500));
    ({ error } = await insertar());
  }
  if (error) {
    reportarError(`sumate inscripcion: ${error.message}${errPerfil ? ` | perfil: ${errPerfil.message}` : ""}`, null, "inscribirDirecto", { evento: eventoId });
    return { ok: false, motivo: /evento_id_fkey/i.test(error.message || "") ? "no_evento" : "error" };
  }
  registrarUso("inscripcion_ok", { id: eventoId, desde: "sumate" });
  window.dispatchEvent(new CustomEvent("olbos-recargar-eventos"));
  return { ok: true };
};

// El link que el organizador manda al grupo de WhatsApp. Va por /evento/<id> a propósito:
// las apps instaladas que todavía no conocen ?sumate abren igual la ficha de la salida.
const linkSumateRodada = (evento) => `${SITE_URL}/evento/${evento.id}?sumate=1`;
const InvitarGrupoWhatsApp = ({ evento, compacto = false }) => {
  const invitar = (ev) => {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    const titulo = String(evento.titulo || evento.title || "la rodada").trim();
    const d = evento.fecha ? new Date(String(evento.fecha).slice(0, 10) + "T12:00:00") : null;
    const cuando = d && !isNaN(d.getTime()) ? d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" }) : "";
    const linea2 = [cuando, evento.hora ? `${String(evento.hora).slice(0, 5)} h` : "", evento.desde || evento.lugar || ""].filter(Boolean).join(" · ");
    const texto = `🏍️ ${titulo}${linea2 ? `\n${linea2}` : ""}\n\nPara ir todos en el mapa en vivo: tocá el link antes de salir. Te anota, te revisa el teléfono y el día de la salida te mete directo al mapa del grupo.`;
    registrarUso("sumate_invitar", { id: evento.id });
    compartirNativo({ texto, url: linkSumateRodada(evento), titulo });
  };
  if (compacto) return (
    <button onClick={invitar} style={{ width: "100%", padding: "12px", borderRadius: 12, background: "#25D366", color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
      📲 Mandar el link al grupo de WhatsApp
    </button>
  );
  return (
    <div style={{ background: C.text, borderRadius: 14, padding: "15px 16px", marginTop: 10, marginBottom: 4 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: "#F9F5F2", letterSpacing: "-0.01em", marginBottom: 4 }}>Que todo el grupo vaya en el mapa</div>
      <div style={{ fontSize: 12.5, color: "#E7E1DA", lineHeight: 1.5, marginBottom: 12 }}>
        Mandá este link al grupo de WhatsApp. Cada uno lo toca, queda anotado, Olbos le revisa los permisos del teléfono y el día de la salida entra directo al mapa en vivo.
      </div>
      <button onClick={invitar} style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#25D366", color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
        📲 Mandar el link al grupo
      </button>
    </div>
  );
};

// La pantalla del chequeo: un semáforo de lo que el teléfono necesita para que el grupo
// te vea, con los pasos exactos de tu marca. modo "dia" = después se entra al mapa;
// "antes" = la salida es otro día, se deja el teléfono listo y listo.
const ChequeoRodada = ({ evento, modo = "dia", recienAnotado = false, onListo, onCerrar }) => {
  const [est, setEst] = useState(null);
  const [revisando, setRevisando] = useState(true);
  const [abierto, setAbierto] = useState(null);
  const [arreglando, setArreglando] = useState(null);
  const vistaRegistradaRef = useRef(false);
  const ultimoGuardadoRef = useRef("");
  const revisar = async (pedir = false) => {
    setRevisando(true);
    const e = await leerEstadoTelefono({ conFix: true, pedir });
    setEst(e);
    setRevisando(false);
    return e;
  };
  useEffect(() => {
    revisar(false);
    // Al volver de Ajustes se mira de nuevo solo
    let sub = null, vivo = true;
    const alVolver = () => { if (document.visibilityState === "visible") revisar(false); };
    document.addEventListener("visibilitychange", alVolver);
    if (esNativo()) {
      (async () => {
        try {
          const { App: CapApp } = await import("@capacitor/app");
          const s = await CapApp.addListener("appStateChange", (st) => { if (st.isActive) revisar(false); });
          if (vivo) sub = s; else s.remove();
        } catch (x) { /* */ }
      })();
    }
    return () => { vivo = false; document.removeEventListener("visibilitychange", alVolver); try { sub && sub.remove(); } catch (x) { /* */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useBackGuard(true, onCerrar);
  const checks = est ? checksTelefono(est) : [];
  const pendientes = checks.filter(c => c.ok === false && !c.aviso);
  const todoOk = !!est && pendientes.length === 0;
  // Lo que quedó se guarda para el panel del organizador (y para medir qué falla más)
  useEffect(() => {
    if (!est || revisando) return;
    const resumen = { plat: est.plat, so: est.so, marca: est.marca, modelo: est.modelo, ubicacion: est.ubicacion, precisa: est.precisa, bateria: est.bateria, ahorro: est.ahorro, pend: pendientes.map(c => c.id) };
    if (!vistaRegistradaRef.current) { vistaRegistradaRef.current = true; registrarUso("chequeo_rodada", { id: evento.id, modo, ...resumen }); }
    const clave = JSON.stringify(resumen);
    if (clave === ultimoGuardadoRef.current || !evento.id) return;
    ultimoGuardadoRef.current = clave;
    supabase.rpc("rodada_chequeo_guardar", { p_evento_id: evento.id, p_estado: resumen, p_ok: todoOk }).then(() => {}, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [est, revisando]);
  const tocarArreglo = async (c) => {
    if (!est) return;
    registrarUso("chequeo_arreglar", { id: c.id, marca: est.marca, plat: est.plat });
    setArreglando(c.id);
    await arreglarTelefono(c.id, est);
    setArreglando(null);
    setTimeout(() => revisar(c.id === "permiso" || c.id === "precisa"), 500);
  };
  const etiquetaBoton = (c) => {
    if (!est) return null;
    const nat = esNativo(), ios = est.so === "ios";
    if (c.id === "permiso") return (!nat && (est.ubicacion === "denegada" || est.enAppAjena)) ? null : (nat && ios && est.ubicacion === "denegada" ? "Abrir Ajustes" : "Dar permiso");
    if (c.id === "fondo") return ios ? "Abrir Ajustes" : "Abrir ajustes";
    if (c.id === "precisa") return nat ? (ios ? "Abrir Ajustes" : "Arreglar") : null;
    if (c.id === "bateria") return "Arreglar";
    return null;
  };
  const tienda = est && est.so === "ios" ? "https://apps.apple.com/app/id6794469275" : "https://play.google.com/store/apps/details?id=ai.olbos.app";
  const primero = pendientes[0] || checks.find(c => c.ok === false) || null;
  const titulo = String(evento.titulo || evento.title || "").trim();
  const nombreTel = est ? [est.marca ? NOMBRE_MARCA[est.marca] : null, est.so === "ios" ? (est.marca === "apple" ? null : "iPhone") : est.so === "android" ? "Android" : null, esNativo() ? "app de Olbos" : "navegador"].filter(Boolean).join(" · ") : "";
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9000, background: C.bg, display: "flex", flexDirection: "column", maxWidth: 520, margin: "0 auto" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", display: "flex", alignItems: "flex-start", gap: 10, borderBottom: `1px solid ${C.border}`, background: C.card }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{modo === "dia" ? "Antes de entrar al mapa" : "Dejá el teléfono listo"}</div>
          {titulo && <div style={{ fontSize: 12.5, color: C.muted, fontWeight: 600, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{titulo}</div>}
        </div>
        <button onClick={onCerrar} aria-label="Cerrar" style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <X size={15} strokeWidth={2.5} color={C.muted} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px 20px" }}>
        {recienAnotado && (
          <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}44`, color: C.green, borderRadius: 12, padding: "11px 13px", fontSize: 13.5, fontWeight: 700, marginBottom: 12 }}>
            ✓ Quedaste anotado a la rodada
          </div>
        )}
        <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, marginBottom: 12 }}>
          {revisando && !est ? "Revisando tu teléfono…"
            : todoOk ? "Tu teléfono está listo: el grupo te va a ver en el mapa todo el camino."
            : `Te ${pendientes.length === 1 ? "falta 1 cosa" : `faltan ${pendientes.length} cosas`} para que el grupo te vea en el mapa. Son un par de toques.`}
          {nombreTel && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Tu teléfono: {nombreTel}</div>}
        </div>
        {checks.map(c => {
          const color = c.ok === true ? C.green : c.ok === false ? (c.aviso ? C.gold : C.red) : C.faint;
          const desplegado = c.ok === false && (abierto === c.id || (abierto == null && primero && primero.id === c.id));
          const boton = etiquetaBoton(c);
          return (
            <div key={c.id} style={{ background: C.card, border: `1px solid ${c.ok === false && !c.aviso ? `${C.red}55` : C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 8 }}>
              <div onClick={() => { if (c.ok === false) setAbierto(desplegado ? "-" : c.id); }} style={{ display: "flex", alignItems: "center", gap: 11, cursor: c.ok === false ? "pointer" : "default" }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: color, color: "#fff", fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {c.ok === true ? "✓" : c.ok === false ? "!" : "·"}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: C.text }}>{c.titulo}</div>
                  {c.ok === false && <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.45, marginTop: 1 }}>{c.detalle}</div>}
                  {c.ok === null && <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{revisando ? "Revisando…" : "Se confirma cuando des el permiso de ubicación"}</div>}
                </div>
                {c.ok === false && <ChevronDown size={16} strokeWidth={2} color={C.muted} style={{ transform: desplegado ? "rotate(180deg)" : "none", flexShrink: 0 }} />}
              </div>
              {desplegado && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                  <ol style={{ margin: "0 0 10px", paddingLeft: 20, fontSize: 13.5, color: C.text, lineHeight: 1.55 }}>
                    {pasosArreglo(c.id, est).map((p, i) => <li key={i} style={{ marginBottom: 3 }}>{p}</li>)}
                  </ol>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {boton && (
                      <button onClick={() => tocarArreglo(c)} disabled={arreglando === c.id}
                        style={{ flex: "1 1 140px", padding: "11px", borderRadius: 11, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: arreglando === c.id ? 0.6 : 1 }}>
                        {arreglando === c.id ? "Abriendo…" : boton}
                      </button>
                    )}
                    {c.id === "bateria" && MARCAS_CON_AHORRO_PROPIO.includes(est.marca) && (
                      <button onClick={async () => { const ok = await abrirAhorroDeLaMarca(); if (!ok) alert("En este teléfono no hay un ajuste extra: con «Sin restricciones» alcanza."); }}
                        style={{ flex: "1 1 140px", padding: "11px", borderRadius: 11, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        Ahorro de {NOMBRE_MARCA[est.marca]}
                      </button>
                    )}
                    {c.id === "web" && (
                      <a href={tienda} target="_blank" rel="noopener noreferrer"
                        style={{ flex: "1 1 140px", padding: "11px", borderRadius: 11, background: C.accent, color: "#F9F5F2", fontSize: 14, fontWeight: 800, textAlign: "center", textDecoration: "none" }}>
                        Bajar la app
                      </a>
                    )}
                    {!boton && c.id !== "web" && (
                      <button onClick={() => revisar(c.id === "permiso" || c.id === "precisa")}
                        style={{ flex: "1 1 140px", padding: "11px", borderRadius: 11, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        Revisar de nuevo
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {modo === "antes" && (
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginTop: 10 }}>
            El día de la salida tocá el mismo link del grupo (o «Mapa en vivo» en la salida) y entrás directo al mapa.
          </div>
        )}
      </div>
      <div style={{ padding: "10px 16px calc(14px + var(--sab, 0px))", borderTop: `1px solid ${C.border}`, background: C.card }}>
        {modo === "dia" && !todoOk && est && (
          <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", marginBottom: 7 }}>Si entrás así, puede que el grupo no te vea en todo el camino.</div>
        )}
        <button onClick={() => { registrarUso("chequeo_listo", { id: evento.id, modo, ok: todoOk, pend: pendientes.map(c => c.id) }); onListo(); }}
          style={{ width: "100%", padding: "14px", borderRadius: 13, background: todoOk || modo === "antes" ? C.accent : C.surface, color: todoOk || modo === "antes" ? "#F9F5F2" : C.text, border: todoOk || modo === "antes" ? "none" : `1px solid ${C.border}`, fontSize: 15.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", letterSpacing: "-0.01em" }}>
          {modo === "dia" ? (todoOk ? "🏍️ Entrar al mapa de la rodada" : "Entrar igual al mapa") : "Listo"}
        </button>
      </div>
    </div>,
    document.body
  );
};

// Panel del organizador: quién está en el mapa, quién sin señal y quién nunca entró, con
// lo que le falta a cada teléfono. Sale de rodada_panel; sin el SQL corrido, de lo que ya
// trae el mapa (anotados + posiciones), sin el detalle de los permisos.
const ETIQUETA_PENDIENTE = { permiso: "sin permiso de ubicación", fondo: "sin «todo el tiempo»", gps: "ubicación apagada", precisa: "ubicación aproximada", bateria: "batería restringida" };
const PanelConectadosRodada = ({ evento, anotados, ubicacionesRef, onCerrar }) => {
  const [riders, setRiders] = useState(null);
  const [completo, setCompleto] = useState(true);
  useEffect(() => {
    let vivo = true;
    const traer = async () => {
      const { data: raw, error } = await supabase.rpc("rodada_panel", { p_evento_id: evento.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!vivo) return;
      if (!error && d && d.ok && Array.isArray(d.riders)) { setRiders(d.riders); setCompleto(true); return; }
      const pos = {};
      (ubicacionesRef.current || []).forEach(u => { pos[u.user_id] = u.updated_at; });
      setRiders((anotados || []).map(a => ({ user_id: a.user_id, username: a.username, avatar_url: a.avatar_url, ultima_pos: pos[a.user_id] || null })));
      setCompleto(false);
    };
    traer();
    const t = setInterval(traer, 10000);
    return () => { vivo = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);
  useBackGuard(true, onCerrar);
  const ahora = Date.now();
  const clasif = (r) => {
    if (!r.ultima_pos) return "nunca";
    return ahora - Date.parse(r.ultima_pos) > 120000 ? "sin_senal" : "en_mapa";
  };
  const lista = (riders || []).map(r => ({ ...r, est: clasif(r) }));
  const orden = { nunca: 0, sin_senal: 1, en_mapa: 2 };
  lista.sort((a, b) => orden[a.est] - orden[b.est] || String(a.username || "").localeCompare(String(b.username || "")));
  const n = (k) => lista.filter(r => r.est === k).length;
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 8500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "16px 16px calc(20px + var(--sab, 0px))", maxHeight: "82vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Quién está en el mapa</div>
          <button onClick={onCerrar} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={14} strokeWidth={2.5} color={C.muted} />
          </button>
        </div>
        {riders && (
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>
            <strong style={{ color: C.green }}>{n("en_mapa")} en el mapa</strong> · {n("sin_senal")} sin señal · <strong style={{ color: n("nunca") ? C.red : C.muted }}>{n("nunca")} no entraron</strong>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", margin: "0 -4px" }}>
          {!riders && <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>}
          {riders && lista.length === 0 && <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 13 }}>Todavía no hay nadie anotado.</div>}
          {lista.map(r => {
            const pend = r.chequeo && Array.isArray(r.chequeo.pend) ? r.chequeo.pend.filter(p => ETIQUETA_PENDIENTE[p]) : [];
            const min = r.ultima_pos ? Math.round((ahora - Date.parse(r.ultima_pos)) / 60000) : null;
            const txt = r.est === "en_mapa" ? "En el mapa" : r.est === "sin_senal" ? `Sin señal hace ${min >= 60 ? `${Math.floor(min / 60)} h` : `${min} min`}` : "Todavía no entró al mapa";
            const col = r.est === "en_mapa" ? C.green : r.est === "sin_senal" ? C.gold : C.red;
            return (
              <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: col, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{r.username || "rider"}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    {txt}{pend.length ? ` · ⚠️ ${pend.map(p => ETIQUETA_PENDIENTE[p]).join(", ")}` : ""}
                    {r.chequeo && r.chequeo.marca && NOMBRE_MARCA[r.chequeo.marca] ? ` · ${NOMBRE_MARCA[r.chequeo.marca]}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!completo && riders && <div style={{ fontSize: 11, color: C.muted, margin: "8px 0 0" }}>El detalle de los permisos de cada teléfono aparece cuando se active el panel completo.</div>}
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45, margin: "10px 0 8px" }}>A los que no entraron, mandales el link otra vez: al tocarlo entran directo y Olbos les dice qué tocar en su teléfono.</div>
        <InvitarGrupoWhatsApp evento={evento} compacto />
      </div>
    </div>,
    document.body
  );
};

// ─── ENTRAR A LA RODADA COMO INVITADO (03-nov-2026, parte B) ─────────────────
// Desde el link del grupo, sin crear cuenta: solo el nombre con el que te ven en el mapa.
// Supabase crea un usuario anónimo y el trigger de siempre le arma el perfil (perfiles.invitado
// lo deja fuera de las métricas). Sirve también adentro del navegador de WhatsApp, donde el
// login de Google no anda. La cuenta se guarda después, desde el perfil (GuardarCuentaInvitado).
const esInvitado = (sess) => !!(sess && sess.user && sess.user.is_anonymous);
const EntrarComoInvitado = ({ eventoId, onCuenta, onCerrar }) => {
  const [evento, setEvento] = useState(null);
  const [nombre, setNombre] = useState("");
  const [entrando, setEntrando] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let vivo = true;
    supabase.from("eventos").select("id, titulo, fecha, hora, desde, lugar").eq("id", eventoId).maybeSingle()
      .then(({ data }) => { if (vivo && data) setEvento(data); }, () => {});
    registrarUso("invitado_ofrecido", { id: eventoId, en_app_ajena: esNavegadorInApp() });
    return () => { vivo = false; };
  }, [eventoId]);
  useBackGuard(true, onCerrar);
  const entrar = async () => {
    const nom = nombre.trim();
    if (nom.length < 2) { setError("Poné tu nombre: así te ve el grupo en el mapa."); return; }
    setEntrando(true); setError("");
    // La intención queda guardada: apenas llega la sesión, el "sumate" anota y sigue solo
    try { localStorage.setItem("olbos_pending_sumate", JSON.stringify({ id: eventoId, t: Date.now() })); } catch (e) { /* */ }
    const { error: err } = await supabase.auth.signInAnonymously({ options: { data: { nombre: nom } } });
    if (err) {
      setEntrando(false);
      reportarError("invitado: " + String(err.message || err).slice(0, 200), null, "EntrarComoInvitado", { evento: eventoId });
      setError(/disabled|not enabled/i.test(err.message || "")
        ? "Por ahora hace falta una cuenta para sumarse. Creala abajo: es un minuto."
        : /rate|too many/i.test(err.message || "") ? "Hay mucha gente entrando a la vez. Esperá un minuto y probá de nuevo." : "No pudimos entrar. Revisá la señal y probá de nuevo.");
      return;
    }
    registrarUso("invitado_creado", { id: eventoId });
    // El nombre al perfil y la marca de invitado (el perfil lo crea el trigger: un respiro)
    for (let i = 0; i < 3; i++) {
      const { data: raw } = await supabase.rpc("invitado_sincronizar", { p_nombre: nom });
      const r = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (r && r.ok) break;
      await new Promise(res => setTimeout(res, 600));
    }
    onCerrar(true); // la sesión ya está: el efecto del "sumate" toma la posta
  };
  const titulo = evento ? String(evento.titulo || "").trim() : "";
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 9500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && !entrando && onCerrar()}>
      <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(22px + var(--sab, 0px))" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 4 }}>🏍️ Sumate a la rodada</div>
        {titulo && <div style={{ fontSize: 13, color: C.muted, fontWeight: 600, marginBottom: 14 }}>{titulo}</div>}
        <label style={{ fontSize: 12.5, fontWeight: 700, color: C.text, display: "block", marginBottom: 6 }}>¿Cómo te llamás? Así te ve el grupo en el mapa</label>
        <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre o apodo" autoFocus maxLength={40}
          onKeyDown={e => { if (e.key === "Enter") entrar(); }}
          style={{ width: "100%", boxSizing: "border-box", padding: "13px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 16, fontFamily: "inherit", marginBottom: 10 }} />
        {error && <div style={{ fontSize: 12.5, color: C.red, marginBottom: 10, lineHeight: 1.45 }}>{error}</div>}
        <button onClick={entrar} disabled={entrando}
          style={{ width: "100%", padding: "14px", borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 15.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: entrando ? 0.7 : 1 }}>
          {entrando ? "Entrando…" : "Entrar a la rodada"}
        </button>
        <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", lineHeight: 1.5, margin: "9px 0 12px" }}>
          Sin mail ni contraseña. Si después querés guardar tus recorridos, creás la cuenta desde tu perfil. Al entrar aceptás los <a href="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: C.muted }}>Términos</a> y la <a href="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: C.muted }}>Privacidad</a>.
        </div>
        <button onClick={onCuenta} disabled={entrando}
          style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Ya tengo cuenta / Crear cuenta
        </button>
      </div>
    </div>,
    document.body
  );
};

// El invitado guarda su cuenta con mail y contraseña: es el mismo usuario, así que no pierde
// nada (inscripciones, recorridos, fotos). Va arriba de todo en su perfil.
const GuardarCuentaInvitado = () => {
  const [soyInvitado, setSoyInvitado] = useState(false);
  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => { if (vivo) setSoyInvitado(esInvitado(data && data.session)); }, () => {});
    return () => { vivo = false; };
  }, []);
  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState("");
  const [clave, setClave] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo: "ok"|"error"|"mail", texto }
  const guardar = async () => {
    const em = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setMsg({ tipo: "error", texto: "Revisá el mail." }); return; }
    if (clave.length < 6) { setMsg({ tipo: "error", texto: "La contraseña tiene que tener al menos 6 caracteres." }); return; }
    setGuardando(true); setMsg(null);
    const { data, error } = await supabase.auth.updateUser({ email: em, password: clave });
    setGuardando(false);
    if (error) {
      setMsg({ tipo: "error", texto: /already|registered|exists/i.test(error.message || "") ? "Ese mail ya tiene una cuenta en Olbos. Para usarla, cerrá sesión y entrá con ese mail." : "No se pudo guardar: " + error.message });
      return;
    }
    const u = data && data.user;
    if (u && !u.is_anonymous && u.email) {
      supabase.rpc("invitado_sincronizar", { p_nombre: null }).then(() => {}, () => {});
      registrarUso("invitado_convertido", {});
      setMsg({ tipo: "ok", texto: "¡Listo! Tu cuenta quedó guardada. Ya podés entrar con tu mail desde cualquier teléfono." });
      setTimeout(() => window.location.reload(), 2200);
    } else {
      registrarUso("invitado_convertir_mail", {});
      setMsg({ tipo: "mail", texto: `Te mandamos un mail a ${em}. Tocá el link y tu cuenta queda guardada.` });
    }
  };
  if (!soyInvitado) return null;
  return (
    <div style={{ margin: "12px 16px 0", background: C.text, borderRadius: 14, padding: "14px 16px" }}>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: "#F9F5F2", marginBottom: 3 }}>Entraste como invitado</div>
      <div style={{ fontSize: 12.5, color: "#E7E1DA", lineHeight: 1.5, marginBottom: abierto ? 10 : 12 }}>
        Guardá tu cuenta para no perder tus rodadas y recorridos si cambiás de teléfono o cerrás sesión.
      </div>
      {!abierto ? (
        <button onClick={() => setAbierto(true)} style={{ width: "100%", padding: "11px", borderRadius: 11, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
          Guardar mi cuenta
        </button>
      ) : (
        <>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Tu mail" autoComplete="email"
            style={{ width: "100%", boxSizing: "border-box", padding: "11px 12px", borderRadius: 10, border: "none", fontSize: 15, fontFamily: "inherit", marginBottom: 8 }} />
          <input type="password" value={clave} onChange={e => setClave(e.target.value)} placeholder="Elegí una contraseña" autoComplete="new-password"
            style={{ width: "100%", boxSizing: "border-box", padding: "11px 12px", borderRadius: 10, border: "none", fontSize: 15, fontFamily: "inherit", marginBottom: 8 }} />
          {msg && <div style={{ fontSize: 12.5, color: msg.tipo === "error" ? "#F2B8B0" : "#B9E3C6", lineHeight: 1.45, marginBottom: 8 }}>{msg.texto}</div>}
          <button onClick={guardar} disabled={guardando} style={{ width: "100%", padding: "11px", borderRadius: 11, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: guardando ? 0.7 : 1 }}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </>
      )}
    </div>
  );
};

// Las dos acciones del día del evento para el anotado: "Hacer check-in" y "Mapa en
// vivo", a la vista (pie fijo de la ficha y tarjeta de la lista). `enLista` = compacto.
const AccionesDiaEvento = ({ evento, enLista = false }) => {
  const [presente, setPresente] = useState(false);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !vivo) return;
      const { data: ck } = await supabase.from("salida_checkins").select("user_id").eq("evento_id", evento.id).eq("user_id", user.id).maybeSingle();
      if (vivo) setPresente(!!ck);
    })();
    const h = (e) => { if (e.detail && e.detail.eventoId === evento.id) setPresente(true); };
    window.addEventListener("olbos-checkin-hecho", h);
    return () => { vivo = false; window.removeEventListener("olbos-checkin-hecho", h); };
  }, [evento.id]);
  const checkin = async (ev) => {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    if (presente || cargando) return;
    setCargando(true);
    const r = await ejecutarCheckin(evento);
    setCargando(false);
    if (r.ok) setPresente(true);
    else alert(mensajeCheckin(r));
  };
  const mapa = (ev) => {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    registrarUso("mapa_vivo_tap", { id: evento.id, desde: enLista ? "lista" : "ficha" });
    window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento } }));
  };
  const base = { flex: 1, padding: enLista ? "10px 8px" : "13px 10px", borderRadius: 12, fontSize: enLista ? 13 : 14.5, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap" };
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <button onClick={checkin} disabled={presente || cargando}
        style={{ ...base, background: presente ? `${C.green}14` : C.surface, color: presente ? C.green : C.text, border: presente ? `1px solid ${C.green}44` : `1px solid ${C.border}`, cursor: presente ? "default" : "pointer", opacity: cargando ? .7 : 1 }}>
        {presente ? "✓ Check-in hecho" : cargando ? "Ubicándote…" : "📍 Hacer check-in"}
      </button>
      <button onClick={mapa} style={{ ...base, background: C.accent, color: "#F9F5F2", border: "none" }}>
        🏍️ Mapa en vivo
      </button>
    </div>
  );
};

const BotonCheckin = ({ evento, gestiona = false }) => {
  const [yo, setYo] = useState(null);
  const [esOrg, setEsOrg] = useState(false);
  useEffect(() => { if (gestiona) setEsOrg(true); }, [gestiona]);
  const [inscripto, setInscripto] = useState(false);
  const [presente, setPresente] = useState(false);
  const [presentes, setPresentes] = useState(0);
  const [manual, setManual] = useState(!!evento.checkin_manual);
  const [modo, setModo] = useState(evento.checkin_modo || "auto"); // 'auto' | 'abierto' | 'cerrado'
  const [cargando, setCargando] = useState(false);
  const [compartiendoPos, setCompartiendoPos] = useState(() => compartiendoUbicacionDe(evento.id));
  // La ficha recarga la fila fresca: el modo del check-in la sigue
  useEffect(() => { setModo(evento.checkin_modo || "auto"); setManual(!!evento.checkin_manual); }, [evento.checkin_modo, evento.checkin_manual]);
  const abierto = checkinAbierto({ ...evento, checkin_modo: modo });

  const refrescarPresentes = async () => {
    const { count } = await supabase.from("salida_checkins").select("*", { count: "exact", head: true }).eq("evento_id", evento.id);
    setPresentes(count || 0);
  };
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setYo(user.id);
      setEsOrg(gestiona || gestionaEvento(evento, user.id));
      const { data: ck } = await supabase.from("salida_checkins").select("user_id").eq("evento_id", evento.id).eq("user_id", user.id).maybeSingle();
      setPresente(!!ck);
      const { data: ins } = await supabase.from("inscripciones").select("id").eq("evento_id", evento.id).eq("user_id", user.id).maybeSingle();
      setInscripto(!!ins);
      refrescarPresentes();
    })();
    // Check-in hecho desde el pie de la ficha o la lista: este bloque se entera
    const h = (e) => { if (e.detail && e.detail.eventoId === evento.id) { setPresente(true); refrescarPresentes(); } };
    window.addEventListener("olbos-checkin-hecho", h);
    return () => window.removeEventListener("olbos-checkin-hecho", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  const hacerCheckin = async () => {
    setCargando(true);
    // Por obtenerPosicion: en la app nativa va por el plugin de GPS (el navigator del WebView
    // mostraba en iPhone el cartel "localhost quiere usar tu ubicación")
    const geo = await obtenerPosicion({ enableHighAccuracy: true, timeout: 12000 }).then(p => ({ lat: p.lat, lng: p.lng }), () => null);
    const { data: raw } = await supabase.rpc("hacer_checkin", { p_evento_id: evento.id, p_lat: geo?.lat ?? null, p_lng: geo?.lng ?? null });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setCargando(false);
    if (r?.ok) {
      setPresente(true); refrescarPresentes(); try { navigator.vibrate?.(150); } catch {}
      // Sellos del organizador ("a las 5 rodadas conmigo"): se evalúan con cada check-in
      supabase.rpc("sellos_evaluar_evento", { p_evento_id: evento.id }).then(() => {}, () => {});
      // Check-in hecho = tu posición entra al mapa de la rodada, sin pasos extra.
      // El watch es global: sigue aunque cierres esta pantalla.
      iniciarCompartirUbicacion(evento.id).then(ok => setCompartiendoPos(!!ok), () => {});
    }
    else {
      const msg = {
        cerrado: "El check-in todavía no está abierto. Se habilita solo 4hs antes de la salida.",
        lejos: `Estás a ~${r.distancia}m del punto de encuentro. Acercate para hacer check-in (o pedile al organizador que active el check-in manual).`,
        sin_gps: "No pudimos leer tu ubicación. Activá el GPS y probá de nuevo.",
        no_login: "Necesitás iniciar sesión.",
      };
      alert(msg[r?.error] || "No se pudo hacer check-in.");
    }
  };
  // Si el RPC devuelve {ok:false} (sin permiso, SQL sin correr) la UI vuelve atrás: antes
  // quedaba "abierto" en pantalla con el server cerrado
  const toggleManual = async () => {
    const nuevo = !manual; setManual(nuevo);
    const { data: raw, error } = await supabase.rpc("set_checkin_manual", { p_evento_id: evento.id, p_manual: nuevo });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) { setManual(!nuevo); alert("No se pudo cambiar el check-in. " + (error?.message || r?.error || "Probá de nuevo.")); return; }
    window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: evento.id } }));
  };
  const cambiarModo = async (nuevoModo) => {
    const prev = modo;
    setModo(nuevoModo);
    const { data: raw, error } = await supabase.rpc("set_checkin_modo", { p_evento_id: evento.id, p_modo: nuevoModo });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) { setModo(prev); alert("No se pudo cambiar el check-in. " + (error?.message || r?.error || "Probá de nuevo.")); return; }
    window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: evento.id } }));
    // Abrir el check-in a mano avisa a los anotados (campanita + mail), una sola vez por
    // evento. En modo auto lo manda la base sola 4 h antes de la largada (Carlos, 10-oct).
    if (nuevoModo === "abierto") {
      try {
        const { data: a } = await supabase.rpc("avisar_checkin_abierto", { p_evento_id: evento.id });
        const ra = typeof a === "string" ? JSON.parse(a) : a;
        if (ra && ra.ok && !ra.ya_avisado && ra.avisados > 0) alert(`Check-in abierto. Les avisamos a ${ra.avisados} anotado${ra.avisados === 1 ? "" : "s"} por la campanita y por mail.`);
      } catch (e) { /* el aviso nunca frena el cambio */ }
    }
  };

  if (!yo || (!inscripto && !esOrg)) return null;

  return (
    <div style={{ marginTop: 8, padding: 12, borderRadius: 12, background: "#f6f1eb", border: "1px solid #D6E8FB" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 0.5 }}>
          📍 Check-in {abierto ? "· abierto" : "· cerrado"}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{presentes} presente{presentes !== 1 ? "s" : ""}</div>
      </div>

      {/* INSCRIPTO */}
      {inscripto && (
        presente ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1F8347", display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={15} strokeWidth={2.5} /> Estás presente ✓
            </div>
            {/* El check-in ya te puso en el mapa: se avisa y se puede cortar */}
            {compartiendoPos && (
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#2F7D4F", animation: "pulse 1.4s infinite", flexShrink: 0 }} />
                Tu posición se ve en el mapa de la rodada
                <button onClick={async () => { await detenerCompartirUbicacion(); setCompartiendoPos(false); }}
                  style={{ background: "none", border: "none", color: C.muted, fontSize: 11.5, fontWeight: 700, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                  dejar de compartir
                </button>
              </div>
            )}
          </div>
        ) : abierto ? (
          <button onClick={hacerCheckin} disabled={cargando}
            style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: cargando ? 0.6 : 1 }}>
            {cargando ? "Tomando ubicación…" : "📍 Hacer check-in"}
          </button>
        ) : (
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
            El check-in se habilita solo <strong>4hs antes</strong> de la salida. Cuando llegues al punto de encuentro vas a poder marcarte presente. 👀
          </div>
        )
      )}

      {/* ORGANIZADOR */}
      {esOrg && (
        <div style={{ marginTop: inscripto ? 10 : 0, paddingTop: inscripto ? 10 : 0, borderTop: inscripto ? "1px solid #D6E8FB" : "none" }}>
          <div style={{ fontSize: 11.5, color: C.text, marginBottom: 8, lineHeight: 1.45 }}>
            {modo === "auto"
              ? (abierto ? "✅ Check-in abierto (automático). No tenés que hacer nada — solo dar inicio a la rodada." : "⏳ En automático: se abre solo 4hs antes de la salida.")
              : modo === "abierto" ? "🔓 Abierto manualmente (forzado)."
              : "🔒 Cerrado manualmente."}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {modo !== "abierto" && (
              <button onClick={() => cambiarModo("abierto")}
                style={{ flex: 1, minWidth: 100, padding: "9px", borderRadius: 9, background: C.accent, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>🔓 Abrir ahora</button>
            )}
            {abierto && modo !== "cerrado" && (
              <button onClick={() => cambiarModo("cerrado")}
                style={{ flex: 1, minWidth: 80, padding: "9px", borderRadius: 9, background: C.card, color: C.text, border: "1px solid #111111", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
            )}
            {modo !== "auto" && (
              <button onClick={() => cambiarModo("auto")}
                style={{ flex: 1, minWidth: 100, padding: "9px", borderRadius: 9, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>↻ Automático</button>
            )}
          </div>
          {abierto && (
            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={manual} onChange={toggleManual} style={{ width: 17, height: 17, accentColor: "#111111" }} />
              <span style={{ fontSize: 12, color: C.text, lineHeight: 1.4 }}>Check-in manual (sin validar GPS) — si la señal falla en el punto de encuentro.</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

// ─── RODADA EN VIVO + ESTADOS (Capa 3) ───────────────────────────────────────
// Mapa con todos los presentes moviéndose en vivo (Realtime + polling). Cada uno
// transmite su GPS mientras la pantalla está abierta.
const ROLES_RODADA = {
  lider: { color: "#2F7D4F", etiqueta: "Adelante" },
  barredor: { color: "#8A6D3B", etiqueta: "Atrás" },
  apoyo: { color: "#3A6EA5", etiqueta: "🚐 Apoyo" },
  grabador: { color: "#B3372C", etiqueta: "Graba" },
};

// ── MOTO O AUTO (25-oct-2026) ─────────────────────────────────────────────────
// El navegador anda bien también en auto (Carlos lo probó): se elige "Voy en moto / en auto"
// y se recuerda. En auto (o como camioneta de apoyo de una rodada) los km se GUARDAN en
// trazas.km_auto pero no entran en ningún ranking: lo hace un disparador de la base
// (docs/sql/vehiculo_auto_y_apoyo.sql), así ninguna función de ranking hay que tocarla.
const VEHICULO_CLAVE = "olbos_vehiculo";
const vehiculoActual = () => { try { return localStorage.getItem(VEHICULO_CLAVE) === "auto" ? "auto" : "moto"; } catch (e) { return "moto"; } };
const elegirVehiculo = (v) => {
  try { localStorage.setItem(VEHICULO_CLAVE, v === "auto" ? "auto" : "moto"); } catch (e) { /* */ }
  window.dispatchEvent(new CustomEvent("olbos-vehiculo", { detail: v }));
};
// En moto no se manda nada: es el default de la columna (y las versiones viejas siguen igual)
const conVehiculo = (obj, v = vehiculoActual()) => (v && v !== "moto" ? { ...obj, vehiculo: v } : obj);
const kmTraza = (tr) => { const d = Number(tr && tr.distancia_km) || 0; return d > 0 ? d : (Number(tr && tr.km_auto) || 0); };
const enAuto = (tr) => !!tr && (tr.vehiculo === "auto" || tr.vehiculo === "apoyo");

// eslint-disable-next-line no-unused-vars -- reemplazado por TrackRecorder en modo grupo (GrupoRodadaOverlay); se conserva por si hace falta volver atras rapido
const RodadaEnVivo = ({ evento, onClose, minimizado = false, onMinimizar = null }) => {
  const [mostrarVoz, setMostrarVoz] = useState(false); // intercomunicador sobre el mapa
  const yoRef = useRef(null);
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markers = useRef({});
  const watchId = useRef(null);
  const lastPush = useRef(0);
  const ajustado = useRef(false);
  const puntosRef = useRef([]); // mi recorrido, se acumula y se guarda como traza al salir
  const esOrgRef = useRef(false); // organizador: puede asignar roles
  const lastTrailPush = useRef(0);
  const trailRef = useRef(null); // polyline del recorrido de la rodada
  const trailTs = useRef(0);     // cuándo cambió la línea por última vez (para el relevo)
  const trailLen = useRef(0);
  const [count, setCount] = useState(0);
  const [cargando, setCargando] = useState(true);
  // Roles de la rodada: los mismos del intercomunicador (una sola asignación
  // sirve para hablar y para verse en el mapa) + quién graba la línea del grupo.
  const [roles, setRoles] = useState(() => evento.voz_config || {});
  const rolesRef = useRef(evento.voz_config || {});
  useEffect(() => { rolesRef.current = roles; }, [roles]);
  const [showRoles, setShowRoles] = useState(false);
  const [anotados, setAnotados] = useState([]);
  const [banner, setBanner] = useState(null); // "🏁 ¡La rodada arrancó!"
  const [estadoRodada, setEstadoRodada] = useState(evento.estado || "");
  const estadoRef = useRef(evento.estado || "");
  useEffect(() => { estadoRef.current = estadoRodada; }, [estadoRodada]);
  const [grabando, setGrabando] = useState(false); // ¿soy quien dibuja la línea ahora?
  const [soyOrg, setSoyOrg] = useState(false);

  // El grabador designado: el elegido, o el organizador si no se eligió a nadie
  const grabadorDesignado = () => rolesRef.current.grabador_id || evento.creator_id;

  // Botón atrás con el mapa abierto: minimiza (se sigue grabando y compartiendo),
  // no cierra. Salir de verdad es la X, que pide confirmación.
  useBackGuard(!minimizado && !!onMinimizar, () => onMinimizar && onMinimizar());
  const salir = () => {
    if (window.confirm("¿Salir de la rodada? Se deja de compartir tu ubicación y tu recorrido queda guardado.\n\nSi querés seguir en la app sin cortar nada, usá el botón de minimizar.")) onClose();
  };

  // Anotados: para los nombres de la leyenda y el selector de roles
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: raw } = await supabase.rpc("get_anotados_publico", { p_evento_id: evento.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo && Array.isArray(d)) setAnotados(d);
    })();
    return () => { vivo = false; };
  }, [evento.id]);

  // Guardar roles: misma config que usa el intercomunicador (voz_config), así
  // una sola asignación sirve para hablar y para el mapa
  const guardarRoles = (nuevosRaw) => {
    // Asignar líder o barredor es querer que se escuche a ellos: si nunca se eligió
    // modalidad, pasa a "solo líderes" sola (antes quedaba "todos" y no cambiaba nada)
    const nuevos = (nuevosRaw.lider_id || nuevosRaw.barredor_id) && !nuevosRaw.modalidad ? { ...nuevosRaw, modalidad: "lideres" } : nuevosRaw;
    setRoles(nuevos);
    supabase.from("eventos").update({ voz_config: nuevos }).eq("id", evento.id).then(() => {}, () => {});
  };

  // La largada, desde el mapa: el organizador ya está viendo al grupo y toca
  // acá. Su traza ya se viene grabando desde que entró — pasa a ser la línea.
  const [arrancando, setArrancando] = useState(false);
  const arrancarRodada = async () => {
    const avisos = [];
    if (evento.fecha) {
      const hoy = new Date();
      const f = new Date(String(evento.fecha).slice(0, 10) + "T12:00:00");
      if (!isNaN(f.getTime())) {
        const dias = Math.round((new Date(f.getFullYear(), f.getMonth(), f.getDate()) - new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())) / 86400000);
        if (dias > 0) avisos.push(`${dias === 1 ? "Falta" : "Faltan"} ${dias} día${dias !== 1 ? "s" : ""} para la fecha de la rodada.`);
        if (dias < 0) avisos.push(`La fecha de la rodada ya pasó (hace ${Math.abs(dias)} día${Math.abs(dias) !== 1 ? "s" : ""}).`);
      }
    }
    if (!checkinAbierto(evento))
      avisos.push("El check-in todavía no está abierto, así que no vas a registrar presentes (ni para el sorteo). Se habilita solo 4hs antes de la salida.");
    if (avisos.length && !window.confirm("⚠️ " + avisos.join("\n\n") + "\n\n¿Querés arrancar la rodada igual?")) return;
    setArrancando(true);
    const { data: raw, error } = await supabase.rpc("cambiar_estado_salida", { p_evento_id: evento.id, p_estado: "en_curso" });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setArrancando(false);
    if (error || (r && r.ok === false)) {
      alert("No se pudo arrancar la rodada. " + (error?.message || r?.error || "Probá de nuevo."));
      return;
    }
    setEstadoRodada("en_curso");
    setBanner("🏁 ¡La rodada arrancó! La línea marca el camino del grupo");
    setTimeout(() => setBanner(null), 7000);
  };

  // Relevo automático de la línea: si el designado deja de transmitir, el resto
  // toma la posta en cadena — líder a los 45s, barredor a los 70s, cualquiera a
  // los 100s. El escalonado evita que todos pusheen a la vez; el servidor se
  // queda con el trazado más fresco, así que un empate breve no rompe nada.
  const meTocaGrabar = () => {
    const yo = yoRef.current;
    if (!yo) return false;
    if (yo === grabadorDesignado()) return true;
    const sinLinea = Date.now() - (trailTs.current || 0);
    if (trailTs.current === 0 || sinLinea < 45000) return false;
    if (yo === rolesRef.current.lider_id) return true;
    if (yo === rolesRef.current.barredor_id && sinLinea > 70000) return true;
    return sinLinea > 100000; // último recurso: cualquiera del grupo
  };

  const refetch = async () => {
    // Trazado de la rodada: lo dibuja quien tiene el rol de grabador (o su
    // relevo). El que se retrasó ve por dónde fue el grupo, no solo dónde está.
    const soyLaLinea = meTocaGrabar();
    setGrabando(soyLaLinea);
    let camino = soyLaLinea ? puntosRef.current : null;
    if (!camino) {
      const { data: recRaw } = await supabase.rpc("get_rodada_recorrido", { p_evento_id: evento.id });
      const rec = typeof recRaw === "string" ? JSON.parse(recRaw) : recRaw;
      if (Array.isArray(rec)) camino = rec;
    }
    if (camino && camino.length > 1 && mapInst.current && window.google) {
      const path = camino.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
      // Registrar cuándo creció la línea: es la señal que dispara el relevo
      if (path.length !== trailLen.current) { trailLen.current = path.length; trailTs.current = Date.now(); }
      if (!trailRef.current) {
        trailRef.current = new window.google.maps.Polyline({ path, strokeColor: "#111111", strokeOpacity: 0.9, strokeWeight: 4, map: mapInst.current });
      } else {
        trailRef.current.setPath(path);
      }
    }
    const { data: raw } = await supabase.rpc("get_rodada_ubicaciones", { p_evento_id: evento.id });
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr) || !mapInst.current || !window.google) return;
    setCount(arr.length);
    const vistos = new Set();
    const bounds = new window.google.maps.LatLngBounds();
    arr.forEach(u => {
      if (!u.lat || !u.lng) return;
      vistos.add(u.user_id);
      const pos = { lat: Number(u.lat), lng: Number(u.lng) };
      bounds.extend(pos);
      const esMio = u.user_id === yoRef.current;
      // El rol manda el anillo del pin: verde adelante, dorado atrás, rojo graba
      const r = rolesRef.current;
      const rol = u.user_id === r.lider_id ? "lider"
        : u.user_id === r.barredor_id ? "barredor"
        : u.user_id === grabadorDesignado() ? "grabador" : null;
      const ring = rol ? ROLES_RODADA[rol].color : null;
      const tituloPin = "@" + (u.username || "rider") + (rol ? ` · ${ROLES_RODADA[rol].etiqueta}` : "");
      if (markers.current[u.user_id]) {
        const mk = markers.current[u.user_id];
        mk.setPosition(pos);
        mk.setTitle(tituloPin);
        // Refrescar el anillo por si el organizador reasignó roles en el camino
        iconoAvatarMapa(u.avatar_url, (icon) => {
          if (icon && markers.current[u.user_id] === mk) mk.setIcon(icon);
        }, rol ? 46 : esMio ? 44 : 36, ring);
      } else {
        const mk = new window.google.maps.Marker({
          position: pos, map: mapInst.current, title: tituloPin,
          label: { text: (u.username || "?").slice(0, 2).toUpperCase(), color: "#fff", fontSize: "10px", fontWeight: "bold" },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: esMio ? 12 : 9, fillColor: ring || "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
          zIndex: rol ? 1200 : esMio ? 1000 : 1,
        });
        markers.current[u.user_id] = mk;
        // La foto de perfil como pin (cacheada — no pesa más que el punto)
        iconoAvatarMapa(u.avatar_url, (icon) => {
          if (icon && markers.current[u.user_id] === mk) { mk.setIcon(icon); mk.setLabel(null); }
        }, rol ? 46 : esMio ? 44 : 36, ring);
      }
    });
    Object.keys(markers.current).forEach(uid => { if (!vistos.has(uid)) { markers.current[uid].setMap(null); delete markers.current[uid]; } });
    // Encuadre inicial: con varios en el mapa, que entren todos. Con UNO solo
    // (o todos casi encimados) fitBounds hacía zoom al máximo sobre un punto y
    // la pantalla quedaba en blanco, sin calles ni referencia de dónde estabas.
    if (!bounds.isEmpty() && !ajustado.current) {
      const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
      const juntos = Math.abs(ne.lat() - sw.lat()) < 0.004 && Math.abs(ne.lng() - sw.lng()) < 0.004;
      if (juntos) { mapInst.current.setCenter(bounds.getCenter()); mapInst.current.setZoom(15); }
      else mapInst.current.fitBounds(bounds, 60);
      ajustado.current = true;
    }
  };

  useEffect(() => {
    let activo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      yoRef.current = user?.id || null;
      esOrgRef.current = !!user && user.id === evento.creator_id;
      setSoyOrg(esOrgRef.current);
      await loadGoogleMaps();
      if (!activo || !mapRef.current || !window.google) return;
      mapInst.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: Number(evento.lat) || -34.6037, lng: Number(evento.lng) || -58.3816 },
        zoom: 13, disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      await refetch();
      setCargando(false);
      // Transmitir mi ubicación (throttle 5s) — startGeoWatch sigue en background (pantalla apagada)
      if (user) {
        bgSesionActualizar({ evento_id: evento.id });
        watchId.current = await startGeoWatch((gp) => {
          const ahora = gp.t || Date.now();
          if (ahora - lastPush.current < 5000) return;
          lastPush.current = ahora;
          puntosRef.current.push({ lat: gp.lat, lng: gp.lng, ts: ahora, v: gp.v });
          const payload = {
            evento_id: evento.id, user_id: user.id,
            lat: gp.lat, lng: gp.lng, heading: gp.head != null ? gp.head : null,
            updated_at: new Date().toISOString(),
          };
          // Quien dibuja la línea (el grabador o su relevo) comparte su trazado
          // acumulado (liviano, cada 15s)
          if (meTocaGrabar() && ahora - lastTrailPush.current > 15000) {
            lastTrailPush.current = ahora;
            payload.recorrido = downsampleRecorrido(puntosRef.current);
          }
          supabase.from("salida_ubicaciones").upsert(payload).then(() => {}, () => {});
        }, () => {});
      }
    })();
    const ch = supabase.channel(`rodada-${evento.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "salida_ubicaciones", filter: `evento_id=eq.${evento.id}` }, () => refetch())
      // Cambios del evento en vivo: roles reasignados en el camino y la largada
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "eventos", filter: `id=eq.${evento.id}` }, (payload) => {
        const n = payload.new || {};
        if (n.voz_config) setRoles(n.voz_config);
        if (n.estado && n.estado !== estadoRef.current) {
          setEstadoRodada(n.estado);
          if (n.estado === "en_curso") {
            setBanner("🏁 ¡La rodada arrancó! La línea marca el camino del grupo");
            setTimeout(() => setBanner(null), 7000);
          }
          if (n.estado === "finalizada") {
            setBanner("🏁 La rodada terminó — ¡gracias por rodar!");
            setTimeout(() => setBanner(null), 7000);
          }
        }
      })
      .subscribe();
    const poll = setInterval(refetch, 6000);
    return () => {
      activo = false;
      if (watchId.current) { try { watchId.current.stop(); } catch (e) {} watchId.current = null; }
      supabase.removeChannel(ch);
      clearInterval(poll);
      if (!compartiendoUbicacionDe(evento.id)) bgSesionActualizar({ evento_id: null });
      // Guardar el recorrido grabado durante la rodada en vivo: queda como traza vinculada
      // a la salida (evento_id), suma al pasaporte y alimenta el "track oficial" (Capa B).
      if (yoRef.current && puntosRef.current.length >= 2) {
        const stats = calcEstadisticas(puntosRef.current);
        if (stats.dist >= 0.3) { // al menos 300m, evita guardar aperturas de prueba
          supabase.from("trazas").insert({
            user_id: yoRef.current,
            titulo: evento.titulo || evento.title || "Rodada",
            puntos: puntosRef.current.map(p => ({ lat: p.lat, lng: p.lng })),
            distancia_km: stats.dist, duracion_seg: stats.dur,
            velocidad_max: stats.velMax, velocidad_prom: stats.velProm,
            evento_id: evento.id, estado: "finalizada", publica: false,
            iniciada_en: new Date(puntosRef.current[0].ts).toISOString(),
            finalizada_en: new Date().toISOString(),
          }).then(() => {}, () => {});
        }
      }
      // borrar mi ubicación al salir (no quedar como fantasma)
      if (yoRef.current) supabase.from("salida_ubicaciones").delete().eq("evento_id", evento.id).eq("user_id", yoRef.current).then(() => {}, () => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 650, background: "#0d0d12", display: minimizado ? "none" : "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", paddingTop: "var(--sat)", paddingBottom: "var(--sab)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <button onClick={salir} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.1)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} color="#fff" strokeWidth={2.5} />
        </button>
        {/* Minimizar: navegás la app y todo sigue andando (ubicación + traza) */}
        {onMinimizar && (
          <button onClick={onMinimizar} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.1)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronDown size={17} color="#fff" strokeWidth={2.5} />
          </button>
        )}
        <div style={{ flex: 1, color: "#fff" }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>📡 Rodada en vivo</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>{count} en el mapa · la línea es el recorrido de la rodada</div>
        </div>
        {/* REC: yo soy quien está dibujando la línea del grupo */}
        {grabando && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 100, background: "#B3372C", color: "#fff", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.card, animation: "pulse 1.5s ease-in-out infinite" }} /> REC
          </span>
        )}
        {soyOrg && (
          <button onClick={() => setShowRoles(true)}
            style={{ padding: "6px 12px", borderRadius: 100, background: "rgba(255,255,255,.12)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
            Roles
          </button>
        )}
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#27AE60", animation: "pulse 1.5s ease-in-out infinite" }} />
      </div>
      <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={mapRef} style={{ flex: 1, background: "#1a1a22" }} />

        {/* Banner de largada / cierre: lo ven todos, sin tocar nada */}
        {banner && (
          <div className="fade-up" style={{ position: "absolute", top: 12, left: 12, right: 12, background: C.accent, color: "#F9F5F2", borderRadius: 12, padding: "12px 16px", fontSize: 13.5, fontWeight: 800, textAlign: "center", boxShadow: "0 6px 24px rgba(0,0,0,.4)", zIndex: 5 }}>
            {banner}
          </div>
        )}

        {/* La largada, acá: el organizador ve al grupo en el mapa y larga */}
        {soyOrg && estadoRodada === "inscripcion_abierta" && (
          <button onClick={arrancarRodada} disabled={arrancando}
            style={{ position: "absolute", bottom: 52, left: 14, right: 14, padding: "15px", borderRadius: 13, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15.5, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em", boxShadow: "0 6px 24px rgba(0,0,0,.45)", zIndex: 6, opacity: arrancando ? 0.6 : 1 }}>
            {arrancando ? "Arrancando…" : "🏁 Arrancar la rodada"}
          </button>
        )}

        {/* Leyenda de roles: quién va adelante, quién cierra, quién graba */}
        {(() => {
          const chips = [];
          const nombreDe = (uid) => {
            const a = anotados.find(x => x.user_id === uid);
            return a ? "@" + (a.username || "rider") : null;
          };
          if (roles.lider_id) { const n = nombreDe(roles.lider_id); if (n) chips.push({ rol: "lider", n }); }
          if (roles.barredor_id) { const n = nombreDe(roles.barredor_id); if (n) chips.push({ rol: "barredor", n }); }
          const g = grabadorDesignado(); const ng = nombreDe(g);
          if (ng && g !== roles.lider_id && g !== roles.barredor_id) chips.push({ rol: "grabador", n: ng });
          if (!chips.length) return null;
          return (
            <div style={{ position: "absolute", bottom: 10, left: 10, right: 10, display: "flex", gap: 6, flexWrap: "wrap", zIndex: 4 }}>
              {chips.map(c => (
                <span key={c.rol} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 10.5, fontWeight: 700 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: ROLES_RODADA[c.rol].color }} />
                  {ROLES_RODADA[c.rol].etiqueta} {c.n}
                </span>
              ))}
            </div>
          );
        })()}
      </div>
      {cargando && <div style={{ position: "absolute", inset: 0, top: 60, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.5)", fontSize: 13 }}>Cargando el mapa…</div>}

      {/* El intercomunicador vive ACÁ, sobre el mapa: antes estaba en otra sección
          del evento y había que salir del mapa para hablar con el grupo. */}
      {mostrarVoz && (
        <div style={{ background: C.card, borderTop: `1px solid ${C.border}`, padding: "12px 14px", maxHeight: "48vh", overflowY: "auto" }}>
          <CanalDeVoz tipo="evento" id={evento.id} creatorId={evento.creator_id} vozConfig={roles} tabla="eventos" />
        </div>
      )}

      <div style={{ padding: "10px 12px calc(10px + var(--sab))", background: "#0d0d12", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => setMostrarVoz(v => !v)}
          style={{ flex: 1, padding: "11px", borderRadius: 10, background: mostrarVoz ? C.card : "rgba(255,255,255,.12)", color: mostrarVoz ? "#111" : "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          🎙️ {mostrarVoz ? "Ocultar intercomunicador" : "Intercomunicador"}
        </button>
      </div>
      <div style={{ padding: "0 16px 8px", background: "#0d0d12", fontSize: 10.5, color: "rgba(255,255,255,.4)", textAlign: "center" }}>
        🛰️ Compartiendo tu ubicación con el grupo
      </div>

      {/* Asignación de roles (solo organizador): adelante, atrás y quién graba.
          Son los mismos roles del intercomunicador — una sola asignación. */}
      {showRoles && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 20, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowRoles(false)}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "20px 18px calc(26px + var(--sab))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Roles de la rodada</div>
              <button onClick={() => setShowRoles(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} color={C.muted} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
              Se ven identificados en el mapa y son los que hablan si el intercomunicador está en modo líderes. Si el que graba pierde señal, la línea pasa sola al de adelante, después al de atrás, y si no a cualquiera del grupo.
            </p>
            {[
              { campo: "lider_id", rol: "lider", desc: "Abre la rodada, nadie lo pasa" },
              { campo: "barredor_id", rol: "barredor", desc: "Cierra el grupo, no deja a nadie" },
              { campo: "apoyo_id", rol: "apoyo", desc: "Camioneta o auto que acompaña: todos lo ven y lo pueden llamar" },
              { campo: "grabador_id", rol: "grabador", desc: "Su recorrido es la línea que ven todos" },
            ].map(({ campo, rol, desc }) => (
              <div key={campo} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: ROLES_RODADA[rol].color }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>{ROLES_RODADA[rol].etiqueta}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>· {desc}</span>
                </div>
                <select value={roles[campo] || ""} onChange={e => guardarRoles({ ...roles, [campo]: e.target.value || null })}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13.5, fontWeight: 600, boxSizing: "border-box" }}>
                  <option value="">{campo === "grabador_id" ? "— El organizador (vos) —" : "— Sin asignar —"}</option>
                  {anotados.map(a => <option key={a.user_id} value={a.user_id}>@{a.username || "rider"}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Control de estados de la salida + acceso al mapa en vivo
// ─── DESLINDES MODELO ────────────────────────────────────────────────────────
// Dos textos según el tipo: un encuentro en un predio no tiene los mismos riesgos
// que una rodada en ruta. El organizador los usa con un toque y puede editarlos.
const DESLINDE_MODELO_EVENTO = `Declaro que participo de este evento por mi propia voluntad y bajo mi exclusiva responsabilidad. La organización dispone el predio y la logística, pero no me traslada: la conducción de mi vehículo hacia, durante y desde el evento es responsabilidad exclusivamente mía, y me comprometo a respetar las normas de tránsito y las indicaciones de la organización dentro del predio.

Declaro contar con licencia de conducir vigente, seguro obligatorio y casco homologado, que me comprometo a usar en todo momento arriba de la moto. Deslindo a la organización, sus sponsors y los responsables del predio de toda responsabilidad por daños a mi persona o a mis bienes, robos o hurtos ocurridos durante el evento, salvo dolo o culpa grave. Autorizo el uso de imágenes del evento en las que pueda aparecer, con fines de difusión.`;

const DESLINDE_MODELO_SALIDA = `Declaro que participo de esta rodada por mi propia voluntad y bajo mi exclusiva responsabilidad. Entiendo que se transita por caminos y rutas abiertos al tránsito, con los riesgos propios de la conducción de motovehículos (estado del camino, clima, terceros). Conduzco mi propio vehículo, soy el único responsable de mi manejo, y me comprometo a respetar las normas de tránsito y las indicaciones de los organizadores (ritmo, reagrupes y paradas).

Declaro contar con licencia de conducir vigente, seguro obligatorio, casco homologado — que me comprometo a usar durante todo el recorrido — y un vehículo en condiciones de circular. La organización coordina el recorrido pero no garantiza asistencia mecánica ni médica en ruta. Deslindo a los organizadores, guías y sponsors de toda responsabilidad por daños a mi persona o a mis bienes derivados de mi participación, salvo dolo o culpa grave. Autorizo el uso de imágenes con fines de difusión.`;

// Leyenda obligatoria en todo formulario que recolecta datos personales
// (Disposición DNPDP 10/2008, Ley 25.326). Va en el registro y en la política.
const LEYENDA_25326 = "El titular de los datos personales tiene la facultad de ejercer el derecho de acceso a los mismos en forma gratuita a intervalos no inferiores a seis meses, salvo que se acredite un interés legítimo al efecto conforme lo establecido en el artículo 14, inciso 3 de la Ley Nº 25.326. La AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, Órgano de Control de la Ley Nº 25.326, tiene la atribución de atender las denuncias y reclamos que se interpongan con relación al incumplimiento de las normas sobre protección de datos personales.";

// "Verificado" con proceso real (mitigación legal 04-sep-2026): antes la ficha le ponía
// el tilde a TODOS los negocios (91 de 99 eran de scouting, sin que nadie los confirmara).
// Ahora el tilde solo va si el negocio fue confirmado (negocios.verificado_en, lo carga
// el equipo tras hablar con el dueño) o si tiene dueño con cuenta en Olbos (no el bot).
// El resto se muestra como "cargado por Olbos, sin verificar" con el botón de reclamo.
const BOT_OLBOS_ID = "571720dc-a089-4e8a-a01b-7d1f92d99dbe";

// Los dos WhatsApp de Olbos. El HUMANO es el de siempre, lo atiende Carlos.
// El AUTOMÁTICO es el que va a estar conectado a la Cloud API de Meta y solo entiende
// el código "OLBOS-VERIFICA": verifica reclamos de fichas y contesta solo.
// ⚠️ Cuando esté el chip nuevo: cambiar WA_OLBOS_AUTOMATICO por ese número (sin + ni
// espacios, con 549 adelante) y actualizar WA_PHONE_ID en Vercel. Es lo único a tocar.
const WA_OLBOS_HUMANO = "5491130718112";
const WA_OLBOS_AUTOMATICO = "5491130718112";
const negocioVerificado = (n) => !!n && (!!n.verificado_en || (!!n.user_id && n.user_id !== BOT_OLBOS_ID));

// Edad mínima para usar Olbos (Términos, punto 2). Edad a partir de una fecha ISO.
const EDAD_MINIMA = 16;
const edadDesde = (iso) => {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
  if (isNaN(d.getTime())) return null;
  const h = new Date();
  let e = h.getFullYear() - d.getFullYear();
  const m = h.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && h.getDate() < d.getDate())) e--;
  return e;
};

// ─── ACCESO AL HISTORIAL PEDIDO POR EL TALLER ────────────────────────────────
// El camino inverso al QR del dueño: el taller busca al rider por @username, elige
// la moto y manda la solicitud. Al dueño le llega al Garage (Aprobar/Rechazar) y
// al aprobar el taller recibe el link firmable. Tabla: historia_accesos (via RPCs).
const AccesoHistoriaTaller = () => {
  const [session, setSession] = useState(null);
  useEffect(() => { supabase.auth.getSession().then(({ data: { session: se } }) => setSession(se)); }, []);
  const [abierto, setAbierto] = useState(false);
  const [username, setUsername] = useState("");
  const [motosDelRider, setMotosDelRider] = useState(null); // null = sin buscar, [] = sin motos
  const [buscando, setBuscando] = useState(false);
  const [pidiendo, setPidiendo] = useState(false);
  const [misSolicitudes, setMisSolicitudes] = useState([]);

  const cargarMias = async () => {
    const { data: raw } = await supabase.rpc("mis_solicitudes_taller").then(r => r, () => ({ data: null }));
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(d)) setMisSolicitudes(d);
  };
  useEffect(() => { if (session?.user && abierto) cargarMias(); }, [abierto, session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarMotos = async () => {
    const q = username.trim().replace(/^@/, "");
    if (!q) return;
    setBuscando(true);
    // Busca por @usuario (lista sus motos) o por patente / nº de motor / chasis
    // (coincidencia EXACTA: hay que tener la moto adelante, no sirve para espiar)
    const { data: raw, error } = await supabase.rpc("buscar_moto_taller", { p_query: q });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setBuscando(false);
    if (error || d?.error) { alert(error?.message || d?.error || "No se pudo buscar"); return; }
    setMotosDelRider(Array.isArray(d) ? d : []);
  };

  const pedir = async (m) => {
    setPidiendo(true);
    const { data: raw, error } = await supabase.rpc("pedir_acceso_historia", { p_username: m.owner_username, p_moto_id: String(m.id) });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setPidiendo(false);
    if (error || r?.error) { alert("No se pudo pedir: " + (error?.message || r?.error)); return; }
    alert("✓ Solicitud enviada. Cuando el dueño la apruebe te llega una notificación con el acceso.");
    setMotosDelRider(null); setUsername("");
    cargarMias();
  };

  if (!session?.user) return null;
  return (
    <>
      <button onClick={() => setAbierto(true)}
        style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
        <Wrench size={14} strokeWidth={2} /> ¿Sos taller? Pedí acceso al historial de una moto
      </button>

      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setAbierto(false)}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(32px + var(--sab))", maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Acceso al historial de una moto</h3>
              <button onClick={() => setAbierto(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} strokeWidth={2.5} />
              </button>
            </div>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
              Buscá al cliente por su usuario de Olbos, elegí la moto y pedile acceso. Cuando apruebe, vas a poder ver el historial completo y cargar lo que le hiciste, firmado por tu cuenta.
            </p>

            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input value={username} onChange={e => { setUsername(e.target.value); setMotosDelRider(null); }}
                onKeyDown={e => e.key === "Enter" && buscarMotos()}
                placeholder="@usuario, patente o nº de motor"
                style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" }} />
              <button onClick={buscarMotos} disabled={buscando}
                style={{ padding: "0 18px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: buscando ? 0.7 : 1 }}>
                {buscando ? "…" : "Buscar"}
              </button>
            </div>

            {motosDelRider && motosDelRider.length === 0 && (
              <div style={{ fontSize: 12.5, color: C.muted, padding: "8px 0 12px" }}>
                No se encontró nada. Probá con el @usuario exacto, o la patente / nº de motor tal cual figura (sin espacios ni guiones).
              </div>
            )}
            {motosDelRider && motosDelRider.map((m, i) => (
              <button key={i} onClick={() => !pidiendo && pedir(m)}
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 8, cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{m.marca} {m.modelo}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>{[m.owner_username ? `de @${m.owner_username}` : null, m.año, m.cc ? `${m.cc}cc` : null].filter(Boolean).join(" · ")}</div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#1F8347" }}>{pidiendo ? "…" : "Pedir acceso →"}</span>
              </button>
            ))}

            {misSolicitudes.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, margin: "14px 0 8px" }}>Mis solicitudes</div>
                {misSolicitudes.map((sol, i) => (
                  <div key={i} style={{ padding: "10px 12px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{sol.moto} · @{sol.owner_username}</div>
                      <div style={{ fontSize: 11, color: sol.estado === "aprobado" ? "#1F8347" : sol.estado === "rechazado" ? C.red : C.muted }}>
                        {sol.estado === "aprobado" ? "✓ Aprobada" : sol.estado === "rechazado" ? "Rechazada" : "Pendiente de aprobación"}
                      </div>
                    </div>
                    {sol.estado === "aprobado" && sol.token && (
                      <button onClick={() => { window.location.href = `/historial/${sol.token}`; }}
                        style={{ flexShrink: 0, padding: "8px 12px", borderRadius: 8, background: C.accent, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Abrir
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Solicitudes pendientes que le llegan al DUEÑO (se muestra arriba del garage)
const SolicitudesHistoriaGarage = ({ userId }) => {
  const [solicitudes, setSolicitudes] = useState([]);
  const cargar = async () => {
    const { data: raw } = await supabase.rpc("solicitudes_historia_pendientes").then(r => r, () => ({ data: null }));
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(d)) setSolicitudes(d);
  };
  useEffect(() => { if (userId) cargar(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const responder = async (sol, aprobar) => {
    const { data: raw, error } = await supabase.rpc("responder_acceso_historia", { p_id: sol.id, p_aprobar: aprobar });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) { alert("No se pudo responder: " + (error?.message || r?.error)); return; }
    cargar();
  };

  if (solicitudes.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      {solicitudes.map((sol, i) => (
        <div key={i} style={{ padding: "14px 16px", borderRadius: 14, background: "#FFF6F0", border: "1px solid #FBD8C0", marginBottom: 8 }}>
          <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, marginBottom: 10 }}>
            <strong>@{sol.taller_username}</strong> pide acceso al historial de service de tu <strong>{sol.moto}</strong> para poder cargarte lo que le haga.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => responder(sol, true)}
              style={{ flex: 2, padding: "10px", borderRadius: 10, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
              Aprobar acceso
            </button>
            <button onClick={() => responder(sol, false)}
              style={{ flex: 1, padding: "10px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Rechazar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── QR GENÉRICO ─────────────────────────────────────────────────────────────
const QRCanvas = ({ texto, size = 200 }) => {
  const ref = useRef(null);
  useEffect(() => {
    (async () => {
      try {
        const QR = (await import("qrcode")).default;
        if (ref.current) QR.toCanvas(ref.current, texto, { width: size, margin: 1, color: { dark: "#111111", light: "#ffffff" } });
      } catch (e) { /* sin QR queda el link */ }
    })();
  }, [texto, size]);
  return <canvas ref={ref} style={{ borderRadius: 12, display: "block" }} />;
};

// ─── HISTORIA CLÍNICA DE UNA MOTO (vista del taller) ─────────────────────────
// El dueño comparte un link/QR desde su garage; el taller lo abre, ve TODO el
// historial de service y puede cargar lo que le hizo — el registro queda firmado
// con su cuenta y aparece al instante en el garage del dueño. El dueño puede
// revocar el link cuando quiera. Los service firmados por talleres son los que
// después valen en la publicación de venta ("historial verificado").
// ─── Ficha completa de una moto en venta / alquiler (pedido de Carlos 02-sep) ──
// Se abre tocando la card del Mercado. Lo que vende es la "historia clínica":
// todo lo que se le hizo a la moto, registrado en Olbos, con quién lo cargó.
// El historial sale en vivo del RPC historia_moto_venta (si existe y la venta
// sigue activa); si no, del snapshot guardado al publicar.
// "Consultar" de compra/venta: mensaje interno al vendedor (las publicaciones no guardan
// teléfono, y el wa.me sin número dejaba al rider eligiendo un contacto cualquiera; reporte
// de usuarios vía Matías, 15-oct-2026). El texto viene armado y se puede editar.
const ConsultarVendedor = ({ venta: v, chico = false, style = null }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [yo, setYo] = useState(null);
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setYo(data?.user?.id || null), () => {}); }, []);
  if (yo && v.user_id === yo) return null; // es tu propia publicación
  const textoBase = v.tipo === "alquiler"
    ? `Hola @${v.username}! Vi tu ${v.marca} ${v.modelo} en alquiler en Olbos y me interesa. ¿Sigue disponible?`
    : `Hola @${v.username}! Vi tu ${tituloVenta(v)}${v.año ? " " + v.año : ""} en venta en Olbos y me interesa. ¿Sigue disponible?`;
  const abrir = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const { data } = await supabase.auth.getUser();
    if (!data?.user) { window.dispatchEvent(new CustomEvent("olbos-abrir-registro")); return; }
    setYo(data.user.id);
    if (v.user_id === data.user.id) return;
    setTexto(textoBase); setListo(false); setAbierto(true);
  };
  const enviar = async () => {
    if (!texto.trim() || !v.user_id) return;
    setEnviando(true);
    const { data: raw, error } = await supabase.rpc("enviar_mensaje", { p_destino_id: v.user_id, p_texto: texto.trim() });
    const r = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch (e) { return null; } })() : raw;
    setEnviando(false);
    if (error || (r && r.ok === false)) { alert("No se pudo enviar: " + (error?.message || r?.error || "probá de nuevo")); return; }
    registrarUso("venta_consulta", { id: v.id, tipo: v.tipo || "venta" });
    setListo(true);
  };
  return (
    <>
      <button onClick={abrir}
        style={style || (chico
          ? { padding: "8px 14px", borderRadius: 10, background: C.accent, color: "#F9F5F2", fontWeight: 700, fontSize: 12, border: "none", cursor: "pointer", fontFamily: "inherit" }
          : { flex: 1, padding: "13px 0", borderRadius: 12, background: C.accent, color: "#F9F5F2", fontWeight: 800, fontSize: 14, border: "none", cursor: "pointer", fontFamily: "inherit" })}>
        💬 Consultar
      </button>
      {abierto && (
        <div onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) setAbierto(false); }}
          style={{ position: "fixed", inset: 0, zIndex: 1700, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 460, background: C.card, borderRadius: "18px 18px 0 0", padding: "18px 18px calc(18px + var(--sab, 0px))" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>Consultar a @{v.username}</div>
              <button onClick={() => setAbierto(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            {listo ? (
              <>
                <div style={{ padding: "12px 14px", background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 12, fontSize: 13.5, color: C.green, lineHeight: 1.5, marginBottom: 12 }}>
                  Listo, tu consulta le llegó a @{v.username}. La respuesta te aparece en <strong>Mensajes</strong>.
                </div>
                <button onClick={() => setAbierto(false)} style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Cerrar</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8, lineHeight: 1.45 }}>{v.marca} {v.modelo}{v.año ? ` · ${v.año}` : ""}{v.precio ? ` · ${v.moneda || "ARS"} ${Number(v.precio).toLocaleString("es-AR")}` : ""}</div>
                <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={4} autoFocus
                  style={{ width: "100%", padding: "12px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical", marginBottom: 10 }} />
                <button onClick={enviar} disabled={!texto.trim() || enviando}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: (!texto.trim() || enviando) ? 0.6 : 1 }}>
                  {enviando ? "Enviando…" : "Enviar consulta"}
                </button>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.45, textAlign: "center" }}>Le llega como mensaje en Olbos. Si no se siguen, le aparece como solicitud.</div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ── COTIZÁ TU SEGURO (20-oct-2026) ───────────────────────────────────────────────
// El rider pide desde el garage que un productor asesor matriculado lo contacte con una
// cotización para su moto. Olbos no vende seguros ni intermedia: junta el pedido con el
// consentimiento explícito y se lo pasa al productor (docs/RIESGOS_LEGALES.md). Apagado
// para el público hasta que Carlos ponga "seguros": true en public/version.json; el equipo
// (staff y admin) lo ve siempre para probarlo. SQL: docs/sql/seguros_cotizacion.sql.
let _versionRemotaProm = null;
const versionRemota = () => (_versionRemotaProm = _versionRemotaProm ||
  fetch(`${API_BASE}/version.json?t=${Date.now()}`, { cache: "no-store" }).then(r => r.json()).catch(() => ({})));
const CONSENTIMIENTO_SEGURO = "Acepto que Olbos le pase mis datos de contacto y los de mi moto a un productor asesor de seguros matriculado para que me contacte con una cotización. Olbos no es aseguradora ni productor: solo nos pone en contacto. Puedo pedir que borren estos datos cuando quiera.";
const CotizarSeguroModal = ({ moto, perfil, email, onClose }) => {
  useBackGuard(true, onClose);
  const [f, setF] = useState({ anio: moto?.año ? String(moto.año) : "", ciudad: perfil?.ciudad || "", uso: "particular", cobertura: "asesorame", nombre: perfil?.nombre || "", telefono: "", email: email || "", comentario: "" });
  const [acepta, setAcepta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  useEffect(() => { registrarUso("seguro_abierto", { marca: moto?.marca || null }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const enviar = async () => {
    setError("");
    if (!f.nombre.trim()) { setError("Poné tu nombre y apellido."); return; }
    if (f.telefono.replace(/[^0-9]/g, "").length < 8) { setError("Poné un teléfono con WhatsApp para que te contacten."); return; }
    if (!acepta) { setError("Para pedir la cotización tenés que aceptar que te contacten."); return; }
    setEnviando(true);
    const { data: raw, error: e } = await supabase.rpc("pedir_cotizacion_seguro", { p: {
      moto_id: String(moto?.id || ""), marca: moto?.marca || "", modelo: moto?.modelo || "", anio: f.anio,
      ciudad: f.ciudad.trim(), uso: f.uso, cobertura: f.cobertura, nombre: f.nombre.trim(), telefono: f.telefono.trim(),
      email: f.email.trim(), comentario: f.comentario.trim(), acepta: true, consentimiento: CONSENTIMIENTO_SEGURO,
    } });
    setEnviando(false);
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (e || (d && d.error)) {
      const motivo = e ? e.message : d.error;
      setError(motivo === "demasiados" ? "Ya pediste varias cotizaciones hoy. Te van a contactar con la primera."
        : motivo === "faltan_datos" ? "Revisá el nombre y el teléfono."
        : /pedir_cotizacion_seguro|schema cache|function/i.test(motivo) ? "Todavía no está habilitado. Probá en un rato."
        : "No se pudo enviar: " + motivo);
      reportarError("cotizar seguro: " + motivo, null, "CotizarSeguroModal");
      return;
    }
    registrarUso("seguro_pedido", { marca: moto?.marca || null, cobertura: f.cobertura, uso: f.uso });
    setListo(true);
  };
  const chip = (on) => ({ padding: "8px 12px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, border: `1px solid ${on ? C.text : C.border}`, background: on ? C.text : C.card, color: on ? "#fff" : C.text, cursor: "pointer" });
  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560, maxHeight: "92vh", overflow: "auto", padding: "20px 20px calc(30px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Seguro de tu moto</div>
            <h3 style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.015em", margin: "4px 0 0" }}>{moto?.marca} {moto?.modelo}</h3>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}><X size={15} strokeWidth={2.5} /></button>
        </div>
        {listo ? (
          <div style={{ padding: "18px 0 6px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🛡️</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>Listo, pedido enviado</div>
            <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, margin: "0 0 18px" }}>Un productor asesor de seguros te va a escribir por WhatsApp con la cotización, en uno o dos días hábiles. No tenés ningún compromiso.</p>
            <Btn full variant="accent" onClick={onClose}>Volver al garage</Btn>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, margin: "0 0 16px" }}>
              Un productor asesor de seguros matriculado te contacta con una cotización para esta moto. Los datos de la moto ya los tenemos: completá cómo contactarte. Sin compromiso.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}><Input label="Año" placeholder="2021" value={f.anio} onChange={set("anio")} inputMode="numeric" /></div>
              <div style={{ flex: 2 }}><Input label="¿Dónde la usás?" placeholder="Ciudad" value={f.ciudad} onChange={set("ciudad")} /></div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>Uso</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[["particular", "Particular"], ["trabajo", "Trabajo o delivery"]].map(([id, l]) => <button key={id} onClick={() => setF(x => ({ ...x, uso: id }))} style={chip(f.uso === id)}>{l}</button>)}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={lbl}>¿Qué cobertura te interesa?</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[["rc", "Solo responsabilidad civil"], ["terceros_completo", "Terceros completo"], ["todo_riesgo", "Todo riesgo"], ["asesorame", "No sé, que me asesoren"]].map(([id, l]) => <button key={id} onClick={() => setF(x => ({ ...x, cobertura: id }))} style={chip(f.cobertura === id)}>{l}</button>)}
              </div>
            </div>
            <Input label="Nombre y apellido" value={f.nombre} onChange={set("nombre")} />
            <Input label="WhatsApp" placeholder="Ej: 11 2345 6789" value={f.telefono} onChange={set("telefono")} inputMode="tel" />
            <Input label="Mail (opcional)" value={f.email} onChange={set("email")} inputMode="email" />
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>¿Algo más? (opcional)</div>
              <textarea value={f.comentario} onChange={set("comentario")} placeholder="Ej: hoy la tengo asegurada en otra compañía, vence en diciembre"
                style={{ width: "100%", minHeight: 64, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", background: C.surface, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
              <input type="checkbox" checked={acepta} onChange={e => setAcepta(e.target.checked)} style={{ width: 18, height: 18, marginTop: 1, accentColor: "#111111", flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{CONSENTIMIENTO_SEGURO}</span>
            </label>
            {error && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 12, lineHeight: 1.5 }}>{error}</div>}
            <Btn full variant="accent" onClick={enviar} disabled={enviando}>{enviando ? "Enviando…" : "Pedir cotización"}</Btn>
          </>
        )}
      </div>
    </div>
  );
};
// ¿Se muestra una función nueva? Prendida en version.json (clave), el usuario es del equipo, o
// es una cuenta de prueba de esa función ("testers" en version.json: { clave: [usernames] }).
// Los testers ven SOLO esa función, sin los permisos del equipo (03-nov-2026: la cuenta
// @revisionmeta del revisor de Meta tiene que ver "Conectá tu Instagram").
const funcionVisible = async (clave) => {
  const v = await versionRemota();
  if (v && v[clave] === true) return true;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase.from("perfiles").select("rol, es_admin, username").eq("id", user.id).maybeSingle();
    if (!data) return false;
    const testers = v && v.testers && Array.isArray(v.testers[clave]) ? v.testers[clave].map(u => String(u).toLowerCase()) : [];
    return data.rol === "staff" || !!data.es_admin || testers.includes(String(data.username || "").toLowerCase());
  } catch (e) { return false; }
};
// ── CONECTÁ TU INSTAGRAM (24-oct-2026) ─────────────────────────────────────────
// El organizador o influencer conecta su cuenta profesional una vez y cada reel nuevo aparece
// solo en Olbos (api/instagram.js lo trae cada hora). Bandera "instagram" en version.json: el
// equipo lo ve siempre; para todos, cuando Meta apruebe la app. SQL: docs/sql/instagram_conexiones.sql
const traerMisReels = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  await fetch(`${API_BASE}/api/instagram`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ accion: "sincronizar_mio" }) }).then(r => r.text(), () => null);
  window.dispatchEvent(new CustomEvent("olbos-ig-conectado"));
};
const alVolverDeInstagram = (estado, motivo) => {
  if (estado === "ok") {
    registrarUso("ig_conectado", {});
    window.dispatchEvent(new CustomEvent("olbos-ig-conectado"));
    alert("¡Listo! Tu Instagram quedó conectado. En unos minutos tus reels de los últimos 30 días aparecen en tu perfil, y de ahora en más cada reel nuevo llega solo.");
    traerMisReels();
  } else if (estado !== "cancelado") {
    alert("No se pudo conectar Instagram" + (motivo ? " (" + motivo + ")" : "") + ". Probá de nuevo; tiene que ser una cuenta profesional (de creador o de empresa).");
  }
};
const ConectarInstagram = () => {
  const [visible, setVisible] = useState(false);
  const [publicando, setPublicando] = useState(null); // reel pendiente que se está publicando
  const [estado, setEstado] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("instagram_mi_conexion");
    if (error) { setEstado(null); return; }
    setEstado(typeof raw === "string" ? JSON.parse(raw) : raw);
  };
  useEffect(() => {
    funcionVisible("instagram").then(v => { setVisible(v); if (v) cargar(); }, () => {});
    const h = () => cargar();
    window.addEventListener("olbos-ig-conectado", h);
    return () => window.removeEventListener("olbos-ig-conectado", h);
  }, []);
  if (!visible || !estado) return null;
  const conectar = async () => {
    if (ocupado) return;
    setOcupado(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE}/api/instagram`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` }, body: JSON.stringify({ accion: "iniciar", nativo: esNativo() }) });
      const d = await r.json().catch(() => ({}));
      if (!d.url) { alert("No se pudo abrir Instagram: " + (d.error || r.status)); return; }
      registrarUso("ig_conectar_inicio", {});
      if (esNativo()) { const { Browser } = await import("@capacitor/browser"); await Browser.open({ url: d.url }); }
      else window.location.href = d.url;
    } finally { setOcupado(false); }
  };
  // Todo automático o reel por reel (Carlos, 26-oct-2026: no todo se quiere publicar en los dos lados)
  const cambiarModo = async (modo) => {
    const { data: raw, error } = await supabase.rpc("instagram_modo", { p_modo: modo });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { alert("No se pudo cambiar: " + (error?.message || d.error)); return; }
    registrarUso("ig_modo", { modo });
    cargar();
  };
  const publicarPendiente = async (id) => {
    setPublicando(id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE}/api/instagram`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` }, body: JSON.stringify({ accion: "publicar_reel", media_id: id }) });
      const d = JSON.parse((await r.text()).trim() || "{}");
      if (!r.ok || d.error) alert("No se pudo publicar: " + (d.error || r.status));
      else registrarUso("ig_reel_publicado", {});
    } catch (e) { alert("No se pudo publicar. Probá de nuevo."); }
    setPublicando(null);
    cargar();
  };
  const descartarPendiente = async (id) => {
    await supabase.rpc("instagram_descartar", { p_media: id });
    registrarUso("ig_reel_descartado", {});
    cargar();
  };
  const desconectar = async () => {
    if (!window.confirm("¿Desconectar tu Instagram? Los reels que ya están en Olbos quedan; los nuevos dejan de llegar.")) return;
    await supabase.rpc("instagram_desconectar");
    registrarUso("ig_desconectado", {});
    cargar();
  };
  const caja = { margin: "14px 16px 0", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px" };
  if (!estado.conectado) return (
    <div style={caja}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>Tus reels de Instagram, también en Olbos</div>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, margin: "4px 0 12px" }}>
        Conectá tu cuenta una sola vez y cada reel que publiques en Instagram aparece solo acá, con tu nombre y el link al original. Más riders te ven sin que tengas que subir nada dos veces.
      </div>
      <button onClick={conectar} disabled={ocupado}
        style={{ width: "100%", padding: 12, borderRadius: 10, background: C.text, color: "#F9F5F2", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>
        {ocupado ? "Abriendo Instagram…" : "Conectá tu Instagram"}
      </button>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.45 }}>Funciona con cuentas profesionales (de creador o de empresa). Olbos solo lee tus publicaciones; nunca publica en tu Instagram. <a href={`${API_BASE}/instagram`} target="_blank" rel="noopener noreferrer" style={{ color: C.text, fontWeight: 700 }}>Cómo se conecta ›</a></div>
    </div>
  );
  return (
    <div style={caja}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>Instagram conectado · @{estado.ig_username}</div>
        <button onClick={desconectar} style={{ background: "none", border: "none", color: C.muted, fontSize: 12.5, cursor: "pointer", textDecoration: "underline" }}>Desconectar</button>
      </div>
      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
        {estado.importados ? `${estado.importados} reel${estado.importados === 1 ? "" : "s"} traído${estado.importados === 1 ? "" : "s"}. ` : "Todavía no trajimos reels. "}
        {estado.modo === "elegir" ? "Te avisamos cuando haya un reel nuevo y elegís si va a Olbos." : "Cada reel nuevo llega solo, en menos de una hora."}
        {estado.ultimo_error ? " Hubo un problema la última vez; si sigue, desconectá y volvé a conectar." : ""}
      </div>
      {estado.modo !== undefined && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {[["todo", "Publicar todos"], ["elegir", "Elegir cuáles"]].map(([m, l]) => (
            <button key={m} onClick={() => estado.modo !== m && cambiarModo(m)}
              style={{ flex: 1, padding: "8px 6px", borderRadius: 10, border: `1px solid ${estado.modo === m ? C.text : C.border}`, background: estado.modo === m ? C.text : C.card, color: estado.modo === m ? "#F9F5F2" : C.text, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
      )}
      {Array.isArray(estado.pendientes) && estado.pendientes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text, marginBottom: 6 }}>Reels nuevos para elegir</div>
          {estado.pendientes.map(r => (
            <div key={r.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
              {r.thumb_url
                ? <img loading="lazy" decoding="async" src={r.thumb_url} alt="" style={{ width: 48, height: 64, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: C.surface }} onError={e => { e.currentTarget.style.visibility = "hidden"; }} />
                : <div style={{ width: 48, height: 64, borderRadius: 8, background: C.surface, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.caption || "Reel sin texto"}</div>
                {r.permalink && <a href={r.permalink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, color: C.muted }}>Ver en Instagram ↗</a>}
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <button onClick={() => publicarPendiente(r.id)} disabled={!!publicando}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: C.text, color: "#F9F5F2", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>{publicando === r.id ? "Publicando…" : "Publicar en Olbos"}</button>
                  <button onClick={() => descartarPendiente(r.id)} disabled={!!publicando}
                    style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Este no</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <button onClick={() => { setOcupado(true); traerMisReels().then(() => setOcupado(false), () => setOcupado(false)); }} disabled={ocupado}
        style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
        {ocupado ? "Buscando…" : "Buscar reels nuevos ahora"}
      </button>
    </div>
  );
};
// ── OLBOS TE PAGA (25-oct-2026) ────────────────────────────────────────────────
// Fondo Olbos mensual en el perfil propio: puntos de km y de comunidad del mes, meta semanal,
// valor estimado del punto y, cuando hay una liquidación confirmada, la elección cobrar / canjear /
// donar. Todo sale de fondo_mis_puntos() (solo datos del propio rider). SQL: docs/sql/fondo_olbos.sql
// Bases: olbos.ai/olbos-te-paga. Si el SQL no está corrido, no se muestra nada.
const pesosFondo = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-AR");
const ptsFondo = (n) => (Math.round((Number(n) || 0) * 10) / 10).toLocaleString("es-AR");
const BarraFondo = ({ v, max, oscuro }) => (
  <div style={{ height: 8, borderRadius: 8, background: oscuro ? "rgba(249,245,242,.16)" : C.surface, overflow: "hidden", marginTop: 8 }}>
    <div style={{ width: `${Math.max(0, Math.min(100, max > 0 ? (Number(v) || 0) / max * 100 : 0))}%`, height: "100%", borderRadius: 8, background: oscuro ? "#F9F5F2" : C.text, transition: "width .4s" }} />
  </div>
);
const ComoFuncionaTePaga = ({ oscuro = false, style = {} }) => {
  const col = oscuro ? "#E7E1DA" : C.text;
  const filas = [
    ["🏆", "Cada semana", "los 3 que más km hacen en moto ganan el premio de la semana."],
    ["🎯", "Desafío semanal", "hacé 150 km y sumás un extra para fin de mes."],
    ["💰", "Cada mes", "el Fondo Olbos se reparte entre todos los que anduvieron al menos 100 km."],
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {filas.map(([e, a, b]) => (
        <div key={a} style={{ fontSize: 13.5, color: col, lineHeight: 1.45 }}>{e} <b>{a}:</b> {b}</div>
      ))}
    </div>
  );
};

// ── EL POZO DEL MES, VISIBLE (30-oct-2026) ─────────────────────────────────────
// Carlos: hacer explícito el pozo que se reparte entre los riders y que crece con los sponsors
// (se dice "sponsors", no "publicidad"). Bandera "pozo" en version.json: el equipo lo ve siempre;
// para todos, cuando Carlos lo apruebe. Muestra el pozo, nunca cuánto factura Olbos. SQL: fondo_pozo()
// en docs/sql/fondo_al_dia_pozo_y_videos.sql (sin correrlo, no se muestra nada).
const PozoOlbos = ({ estilo = {} }) => {
  const [d, setD] = useState(null);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    let vivo = true;
    funcionVisible("pozo").then(ok => {
      if (!ok || !vivo) return;
      supabase.rpc("fondo_pozo").then(({ data, error }) => {
        if (!vivo || error) return;
        const x = typeof data === "string" ? JSON.parse(data) : data;
        if (x && !x.error) setD(x);
      }, () => {});
    }, () => {});
    return () => { vivo = false; };
  }, []);
  if (!d) return null;
  const arranca = d.arranca ? new Date(String(d.arranca).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long" }) : "";
  return (
    <div onClick={() => { if (!abierto) registrarUso("pozo_abrir", {}); setAbierto(a => !a); }}
      style={{ background: "#111111", color: "#F9F5F2", borderRadius: 14, padding: "14px 16px", cursor: "pointer", ...estilo }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "#bdb6ab", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Pozo de {d.mes_nombre}</div>
        <div style={{ fontSize: 12, color: "#bdb6ab", fontWeight: 600 }}>{abierto ? "Cerrar" : "¿Cómo crece? ›"}</div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>{pesosFondo(d.pozo)}</div>
      <div style={{ fontSize: 13, color: "#d8d1c7", marginTop: 4, lineHeight: 1.45 }}>
        para repartir {d.activo ? "este mes" : `desde el ${arranca}`} entre los riders que andan en moto y hacen crecer la comunidad.
      </div>
      {abierto && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(249,245,242,.15)", fontSize: 13, lineHeight: 1.55, color: "#E7E1DA" }}>
          <div>💰 <b style={{ color: "#F9F5F2" }}>Lo pone Olbos y lo agrandan los sponsors.</b> Hay {pesosFondo(d.minimo)} garantizados cada mes, y un porcentaje de lo que aportan las marcas que acompañan a Olbos va al pozo: cuando eso supera lo garantizado, se reparte más.</div>
          <div style={{ marginTop: 8 }}>🏍️ <b style={{ color: "#F9F5F2" }}>Cuanto más activa la comunidad, más marcas se suman.</b> Cada salida, cada foto y cada rider que invitás hace crecer el pozo.</div>
          <ComoFuncionaTePaga oscuro style={{ marginTop: 10 }} />
        </div>
      )}
    </div>
  );
};

const OlbosTePaga = () => {
  const [d, setD] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [cambiando, setCambiando] = useState(false);
  const [opcion, setOpcion] = useState("cobrar");
  const [alias, setAlias] = useState("");
  const [canjeSel, setCanjeSel] = useState(null);
  const [causaSel, setCausaSel] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const cargar = () => supabase.rpc("fondo_mis_puntos").then(({ data, error: e }) => {
    if (e || !data) return;
    const x = typeof data === "string" ? JSON.parse(data) : data;
    if (x && !x.error) setD(x);
  }, () => {});
  useEffect(() => { cargar(); }, []);
  useBackGuard(abierto, () => setAbierto(false));
  if (!d) return null;

  const p = d.puntos || {};
  const liqs = d.liquidaciones || [];
  const aElegir = liqs.find(l => l.estado === "confirmada") || liqs.find(l => l.estado === "elegida") || null;
  const pendiente = aElegir && aElegir.estado === "confirmada";
  const fechaArranque = d.arranca ? new Date(d.arranca + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long" }) : "";
  const faltanPts = Math.max(0, Math.ceil((Number(d.minimo_puntos) || 0) - (Number(d.pts_total) || 0)));
  // Para el rider todo va en km y en plata: un punto de km son 10 km
  const faltanKmMin = faltanPts * 10;
  const vk = Number(d.valor_punto_km) || 0, vc = Number(d.valor_punto_comunidad) || 0;
  const extraDesafio = d.semana ? (Number(d.semana.bonus) || 0) * vk : 0;
  // Sin moto cargada en el perfil no se cobra (26-oct-2026). Solo si el SQL ya devuelve el dato.
  const sinMoto = d.tiene_moto === false && d.participa !== false;
  const linea = !d.activo ? `Arranca el ${fechaArranque}: ${pesosFondo(d.fondo)} por mes para los que ruedan.`
    : sinMoto ? "Completá tu perfil (foto, ciudad y tu moto en el garage): sin eso no se cobra."
    : Number(d.faltan_km) > 0 ? `Te faltan ${ptsFondo(d.faltan_km)} km en moto para cobrar este mes.`
    : !d.pasa_minimo ? `Te faltan unos ${ptsFondo(faltanKmMin)} km (o algo de actividad en la comunidad) para cobrar.`
    : `Si el mes cerrara hoy: ~${pesosFondo(d.estimado)}`;
  const abrir = () => { setAbierto(true); setError(""); registrarUso("fondo_abrir", {}); };

  const opciones = [["cobrar", "Cobrarlo"], ...((d.canjes || []).length ? [["canjear", "Canjearlo"]] : []), ...((d.causas || []).length ? [["donar", "Donarlo"]] : [])];
  const elegir = () => {
    if (!aElegir || guardando) return;
    setError("");
    if (opcion === "cobrar" && !alias.trim()) { setError("Escribí tu alias o CVU de Mercado Pago."); return; }
    if (opcion === "canjear" && !canjeSel) { setError("Elegí un canje."); return; }
    if (opcion === "donar" && !causaSel) { setError("Elegí una causa."); return; }
    setGuardando(true);
    supabase.rpc("fondo_elegir", { p_liquidacion: aElegir.id, p_eleccion: opcion, p_alias: opcion === "cobrar" ? alias.trim() : null, p_canje: opcion === "canjear" ? canjeSel : null, p_causa: opcion === "donar" ? causaSel : null })
      .then(({ data, error: e }) => {
        setGuardando(false);
        const r = typeof data === "string" ? JSON.parse(data) : data;
        if (e || !r || r.error) { setError((r && r.error) || "No se pudo guardar. Probá de nuevo."); return; }
        registrarUso("fondo_eleccion", { eleccion: opcion });
        setCambiando(false);
        cargar();
      }, () => { setGuardando(false); setError("No se pudo guardar. Probá de nuevo."); });
  };

  const categorias = [
    ["pts_likes", "Me gusta en tus publicaciones", "like"],
    ["pts_comentarios", "Comentarios", "comentario"],
    ["pts_vistas", "Vistas", "vista"],
    ["pts_anotados", "Anotados en tus salidas", "anotado"],
    ["pts_checkins", "Check-ins en tus salidas", "checkin"],
    ["pts_resenas", "Reseñas de tus rutas", "resena"],
    ["pts_invitados", "Riders que invitaste y andan", "invitado"],
    ["pts_seguidores", "Seguidores nuevos", "seguidor"],
    ["pts_pitstop", "Pit Stop: reseñas, lugares y turnos", "resena_negocio"],
  ];
  const maxCat = Math.max(1, ...categorias.map(([k]) => Number(p[k]) || 0));
  const pesoTxt = (k) => { const w = Number((d.pesos || {})[k]); const vcx = Number(d.valor_punto_comunidad) || 0; return w && vcx ? `~${pesosFondo(w * vcx)} c/u` : ""; };
  const caja = { margin: "12px 16px 0", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px" };
  const tit = { fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" };
  const sub = { fontSize: 12.5, color: C.muted, lineHeight: 1.5 };
  const descEleccion = (l) => l.eleccion === "cobrar" ? `Elegiste cobrarlo a ${l.alias_pago}.`
    : l.eleccion === "canjear" ? `Elegiste canjearlo${((d.canjes || []).find(k => k.id === l.canje_id) || {}).titulo ? ` por ${(d.canjes || []).find(k => k.id === l.canje_id).titulo}` : ""}.`
    : `Elegiste donarlo${((d.causas || []).find(k => k.id === l.causa_id) || {}).nombre ? ` a ${(d.causas || []).find(k => k.id === l.causa_id).nombre}` : ""}.`;

  return (
    <>
      <div onClick={abrir} style={{ margin: "14px 16px 0", background: "#111111", color: "#F9F5F2", borderRadius: 14, padding: "14px 16px", cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#bdb6ab", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Olbos te paga</div>
          <div style={{ fontSize: 12, color: "#bdb6ab", fontWeight: 600 }}>Ver ›</div>
        </div>
        {pendiente ? (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", marginTop: 6 }}>Ganaste {pesosFondo(aElegir.monto)} en {aElegir.mes_nombre}</div>
            <div style={{ fontSize: 13, color: "#d8d1c7", marginTop: 2 }}>Elegí qué hacer: cobrarlo{(d.canjes || []).length ? ", canjearlo" : ""}{(d.causas || []).length ? " o donarlo" : ""}.</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", marginTop: 6 }}>
              {d.activo ? (d.pasa_minimo ? `Llevás ~${pesosFondo(d.estimado)} en ${d.mes_nombre}` : `Todavía no cobrás en ${d.mes_nombre}`) : "Andá en moto y cobrá por eso"}
            </div>
            <div style={{ fontSize: 13, color: "#d8d1c7", marginTop: 2 }}>{linea}</div>
            <BarraFondo v={d.km} max={d.km_minimo} oscuro />
          </>
        )}
      </div>

      {abierto && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, background: C.bg, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ maxWidth: 560, margin: "0 auto", paddingBottom: "calc(40px + var(--sab))" }}>
            {/* Momento oscuro: el monto */}
            <div style={{ background: "#111111", color: "#F9F5F2", padding: "max(18px, calc(var(--sat) + 12px)) 18px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: "#bdb6ab", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Fondo Olbos · {d.mes_nombre}</div>
                <button onClick={() => setAbierto(false)} aria-label="Cerrar" style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={18} strokeWidth={2} color="#F9F5F2" />
                </button>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1, marginTop: 10 }}>Olbos te paga por andar en moto</div>
              <div style={{ fontSize: 13.5, color: "#d8d1c7", marginTop: 6, lineHeight: 1.5 }}>
                Cada mes repartimos {pesosFondo(d.fondo)} entre los que hacen {ptsFondo(d.km_minimo)} km o más en moto: la mitad por tus kilómetros y la mitad por lo que movés en la comunidad.
              </div>
              <ComoFuncionaTePaga oscuro style={{ marginTop: 12 }} />
              {!d.activo && (
                <div style={{ fontSize: 12.5, color: "#F9F5F2", background: "rgba(249,245,242,.1)", borderRadius: 10, padding: "8px 10px", marginTop: 12, lineHeight: 1.45 }}>
                  Arranca el {fechaArranque}. Lo de este mes es una vista previa: así te iría.
                </div>
              )}
              {d.participa === false && (
                <div style={{ fontSize: 12.5, color: "#F9F5F2", background: "rgba(249,245,242,.1)", borderRadius: 10, padding: "8px 10px", marginTop: 12, lineHeight: 1.45 }}>
                  Tu cuenta no participa del fondo. Esto es lo que ve cada rider.
                </div>
              )}
              <div style={{ marginTop: 18 }}>
                {sinMoto ? (
                  <>
                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em" }}>Completá tu perfil para cobrar</div>
                    <div style={{ fontSize: 13, color: "#d8d1c7", marginTop: 4, lineHeight: 1.5 }}>Para recibir cualquier premio, del fondo o del podio semanal, tu perfil tiene que estar completo: foto, ciudad y al menos una moto cargada en el garage. Tus km y tus puntos siguen sumando igual.</div>
                  </>
                ) : d.pasa_minimo ? (
                  <>
                    <div style={{ fontSize: 12, color: "#bdb6ab", fontWeight: 600 }}>Si el mes cerrara hoy</div>
                    <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.05 }}>~{pesosFondo(d.estimado)}</div>
                  </>
                ) : Number(d.faltan_km) > 0 ? (
                  <>
                    <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em" }}>Te faltan {ptsFondo(d.faltan_km)} km para cobrar este mes</div>
                    <BarraFondo v={d.km} max={d.km_minimo} oscuro />
                    <div style={{ fontSize: 12, color: "#bdb6ab", marginTop: 6 }}>{ptsFondo(d.km)} de {ptsFondo(d.km_minimo)} km en moto</div>
                  </>
                ) : (
                  <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em" }}>Te faltan unos {ptsFondo(faltanKmMin)} km para cobrar este mes</div>
                )}
              </div>
              {Number(d.fijo) > 0 && (
                <div style={{ fontSize: 12.5, color: "#F9F5F2", background: "rgba(249,245,242,.1)", borderRadius: 10, padding: "8px 10px", marginTop: 12, lineHeight: 1.45 }}>
                  Tenés asegurados {pesosFondo(d.fijo)} este mes. Si tu parte del fondo es mayor, cobrás tu parte.
                </div>
              )}
              <div style={{ fontSize: 13, color: "#d8d1c7", marginTop: 14, lineHeight: 1.5 }}>
                {vk || vc
                  ? <>Hoy, cada 10 km en moto valen ~{pesosFondo(vk)}{vc ? <> y cada rider que hace check-in en tu salida ~{pesosFondo(vc * (Number((d.pesos || {}).checkin) || 10))}</> : null}. Cambia según cuánto anden todos hasta fin de mes.</>
                  : <>Todavía nadie anduvo este mes: los primeros en rodar se llevan más.</>}
              </div>
            </div>

            {/* Lo ganado: cobrar, canjear o donar */}
            {aElegir && (
              <div style={{ ...caja, border: `1.5px solid ${C.text}` }}>
                <div style={tit}>Ganaste {pesosFondo(aElegir.monto)} en {aElegir.mes_nombre}</div>
                {aElegir.estado === "elegida" && !cambiando ? (
                  <>
                    <div style={{ ...sub, marginTop: 4 }}>{descEleccion(aElegir)} Te avisamos cuando esté hecho.</div>
                    <button onClick={() => { setCambiando(true); setOpcion(aElegir.eleccion || "cobrar"); setAlias(aElegir.alias_pago || ""); setCanjeSel(aElegir.canje_id); setCausaSel(aElegir.causa_id); }}
                      style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, background: C.surface, color: C.text, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cambiar</button>
                  </>
                ) : (
                  <>
                    <div style={{ ...sub, marginTop: 4 }}>¿Qué querés hacer con lo que ganaste?</div>
                    {opciones.length > 1 && (
                      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                        {opciones.map(([k, t]) => (
                          <button key={k} onClick={() => { setOpcion(k); setError(""); }}
                            style={{ flex: 1, padding: "9px 6px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 700, background: opcion === k ? C.accent : C.surface, color: opcion === k ? "#F9F5F2" : C.text }}>{t}</button>
                        ))}
                      </div>
                    )}
                    {opcion === "cobrar" && (
                      <>
                        <input value={alias} onChange={e => setAlias(e.target.value)} placeholder="Tu alias o CVU de Mercado Pago" autoCapitalize="none" autoCorrect="off"
                          style={{ width: "100%", boxSizing: "border-box", marginTop: 10, padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg, fontSize: 15, color: C.text, fontFamily: "inherit" }} />
                        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Te lo transferimos a esa cuenta, que tiene que estar a tu nombre.</div>
                      </>
                    )}
                    {opcion === "canjear" && (d.canjes || []).map(k => {
                      const noAlcanza = k.valor != null && Number(k.valor) > Number(aElegir.monto);
                      return (
                        <button key={k.id} disabled={noAlcanza} onClick={() => setCanjeSel(k.id)}
                          style={{ display: "block", width: "100%", textAlign: "left", marginTop: 8, padding: "10px 12px", borderRadius: 10, cursor: noAlcanza ? "default" : "pointer", opacity: noAlcanza ? 0.45 : 1, border: `1.5px solid ${canjeSel === k.id ? C.text : C.border}`, background: C.card, fontFamily: "inherit" }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{k.titulo}</div>
                          <div style={{ fontSize: 12, color: C.muted }}>{k.marca}{k.valor != null ? ` · ${pesosFondo(k.valor)}` : ""}{noAlcanza ? " · no te alcanza este mes" : ""}</div>
                        </button>
                      );
                    })}
                    {opcion === "donar" && (d.causas || []).map(k => (
                      <button key={k.id} onClick={() => setCausaSel(k.id)}
                        style={{ display: "block", width: "100%", textAlign: "left", marginTop: 8, padding: "10px 12px", borderRadius: 10, cursor: "pointer", border: `1.5px solid ${causaSel === k.id ? C.text : C.border}`, background: C.card, fontFamily: "inherit" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{k.nombre}</div>
                        {k.descripcion && <div style={{ fontSize: 12, color: C.muted }}>{k.descripcion}</div>}
                      </button>
                    ))}
                    {error && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginTop: 8, lineHeight: 1.45 }}>{error}</div>}
                    <button onClick={elegir} disabled={guardando}
                      style={{ width: "100%", marginTop: 12, padding: 12, borderRadius: 10, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>
                      {guardando ? "Guardando…" : "Listo"}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Meta de la semana */}
            {d.semana && (
              <div style={caja}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <div style={tit}>Desafío de la semana</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{ptsFondo(d.semana.km)} / {ptsFondo(d.semana.meta)} km</div>
                </div>
                <BarraFondo v={d.semana.km} max={d.semana.meta} />
                <div style={{ ...sub, marginTop: 8 }}>
                  {d.semana.cumplida ? `¡Cumplido! Sumaste un extra${extraDesafio ? ` de ~${pesosFondo(extraDesafio)}` : ""} para fin de mes.` : `Hacé ${ptsFondo(d.semana.meta)} km en moto de lunes a domingo y sumás un extra${extraDesafio ? ` de ~${pesosFondo(extraDesafio)}` : ""} para fin de mes.`}
                </div>
              </div>
            )}

            {/* Bolsillo de km */}
            <div style={caja}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div style={tit}>Tus kilómetros</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>{vk ? `~${pesosFondo((Number(d.pts_km) || 0) * vk)}` : `${ptsFondo(d.km)} km`}</div>
              </div>
              <BarraFondo v={p.pts_km_base} max={d.km_tope_pts} />
              <div style={{ ...sub, marginTop: 8 }}>
                {ptsFondo(d.km)} km en moto este mes{Number(p.km_grupo) > 0 ? ` (${ptsFondo(p.km_grupo)} en grupo, que valen doble)` : ""}.
                {Number(p.semanas_meta) > 0 ? ` Desafíos semanales cumplidos: ${p.semanas_meta}.` : ""}
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>Cuantos más km hacés en moto, más cobrás (después de los 500 km rinden la mitad, y hasta 2.000). Las rodadas en grupo valen doble.</div>
            </div>

            {/* Bolsillo de comunidad */}
            <div style={caja}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <div style={tit}>Tu aporte a la comunidad</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>{vc ? `~${pesosFondo((Number(d.pts_comunidad) || 0) * vc)}` : ""}</div>
              </div>
              {categorias.map(([k, t, w]) => {
                const v = Number(p[k]) || 0;
                return (
                  <div key={k} style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: v ? C.text : C.muted }}>
                      <span>{t} <span style={{ color: C.muted, fontSize: 11.5 }}>· {pesoTxt(w)}</span></span>
                      <span style={{ fontWeight: 700 }}>{vc ? `~${pesosFondo(v * vc)}` : ""}</span>
                    </div>
                    {v > 0 && <BarraFondo v={v} max={maxCat} />}
                  </div>
                );
              })}
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.45 }}>Cuentan solo riders reales (que usan la app) y nunca vos mismo, con un tope por cada rider que te sigue.</div>
            </div>

            <div style={{ ...caja, background: C.surface, border: "none" }}>
              <div style={{ ...sub, color: C.text }}>
                Para cobrar: tu moto cargada en el garage, al menos {ptsFondo(d.km_minimo)} km en moto en el mes, y llegar a unos {ptsFondo((Number(d.minimo_puntos) || 0) * 10)} km o sumar algo de actividad en la comunidad. Nadie se lleva más del {d.es_equipo ? "10" : "25"}% del fondo. El cierre es el día 1 y te avisamos cuánto ganaste.
              </div>
              {liqs.filter(l => l.estado === "pagada").length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {liqs.filter(l => l.estado === "pagada").map(l => (
                    <div key={l.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.text, padding: "4px 0" }}>
                      <span style={{ textTransform: "capitalize" }}>{l.mes_nombre}</span>
                      <span style={{ fontWeight: 700 }}>{pesosFondo(l.monto)} · {l.eleccion === "donar" ? "donado" : l.eleccion === "canjear" ? "canjeado" : "cobrado"}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                El fondo de cada mes es un porcentaje de los ingresos de Olbos, con un mínimo de $500.000. No es un derecho adquirido: puede cambiar o suspenderse, avisando con anticipación.
              </div>
              <a href={`${API_BASE}/olbos-te-paga`} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", marginTop: 10, fontSize: 13, fontWeight: 700, color: C.text }}>Bases y condiciones ›</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
// ¿Se muestra el seguro? Prendido en version.json, o el usuario es del equipo
const seguroVisible = async () => {
  const v = await versionRemota();
  if (v && v.seguros === true) return true;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase.from("perfiles").select("rol, es_admin").eq("id", user.id).maybeSingle();
    return !!(data && (data.rol === "staff" || data.es_admin));
  } catch (e) { return false; }
};
// Desde el carrusel de la portada: abre el formulario con la moto principal del garage
const CotizarSeguroGlobal = () => {
  const [datos, setDatos] = useState(null);
  useEffect(() => {
    const h = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { alert("Para cotizar el seguro de tu moto, primero entrá a tu cuenta."); irATab("perfil"); return; }
      const { data: p } = await supabase.from("perfiles").select("nombre, ciudad, motos").eq("id", user.id).maybeSingle();
      const motos = (Array.isArray(p?.motos) ? p.motos : []).filter(m => m && !["eliminada", "retirada", "vendida"].includes(m.estado));
      if (!motos.length) { alert("Primero cargá tu moto en el garage: con la marca, el modelo y el año la cotización sale más rápido."); irATab("perfil"); return; }
      setDatos({ moto: motos[0], perfil: p, email: user.email });
    };
    window.addEventListener("olbos-cotizar-seguro", h);
    return () => window.removeEventListener("olbos-cotizar-seguro", h);
  }, []);
  if (!datos) return null;
  return <CotizarSeguroModal moto={datos.moto} perfil={datos.perfil} email={datos.email} onClose={() => setDatos(null)} />;
};
// El botón del garage: solo aparece si está prendido (version.json) o si sos del equipo
const BotonCotizarSeguro = ({ moto, perfil, email, estilo }) => {
  const [visible, setVisible] = useState(false);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    let vivo = true;
    seguroVisible().then(v => { if (vivo && v) setVisible(true); }, () => {});
    return () => { vivo = false; };
  }, []);
  if (!visible || !moto) return null;
  return (
    <>
      <button onClick={() => setAbierto(true)} style={estilo}>🛡️ Cotizá el seguro de esta moto</button>
      {abierto && <CotizarSeguroModal moto={moto} perfil={perfil} email={email} onClose={() => setAbierto(false)} />}
    </>
  );
};

// ── COMPRA/VENTA: categorías (pedido de Matías N8, 20-oct-2026) ──────────────────
// Los avisos viven en motos_venta. Una moto usa marca/modelo/año/km; un accesorio,
// indumentaria o repuesto guarda el título en "modelo" y la marca (opcional) en
// "marca", que en la base no admite null (por eso va "" cuando no hay).
const CATEGORIAS_VENTA = { moto: "Moto", accesorio: "Accesorio", indumentaria: "Indumentaria", repuesto: "Repuesto" };
const categoriaVenta = (v) => (v && CATEGORIAS_VENTA[v.categoria] ? v.categoria : "moto");
const tituloVenta = (v) => categoriaVenta(v) === "moto" ? `${v.marca || ""} ${v.modelo || ""}`.trim() : (v.modelo || "");
const detalleVenta = (v) => categoriaVenta(v) === "moto"
  ? [v.año, v.cc ? `${v.cc}cc` : null, v.color, v.km ? `${Number(v.km).toLocaleString("es-AR")} km` : null].filter(Boolean).join(" · ")
  : [CATEGORIAS_VENTA[categoriaVenta(v)], v.marca || null, v.condicion === "nuevo" ? "Nuevo" : v.condicion === "usado" ? "Usado" : null].filter(Boolean).join(" · ");

// Editar un aviso propio de Compra-Venta (precio, título, ciudad y descripción). Pedido de Matías, 21-oct.
const EditarAvisoModal = ({ aviso: v, onClose, onGuardado }) => {
  useBackGuard(true, onClose);
  const esMoto = categoriaVenta(v) === "moto";
  const [form, setForm] = useState({ titulo: v.modelo || "", precio: String(v.precio || ""), moneda: v.moneda || "ARS", ciudad: v.ciudad || "", descripcion: v.descripcion || "" });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const inp = { width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14, fontFamily: "inherit", marginBottom: 10, boxSizing: "border-box" };
  const guardar = async () => {
    setError("");
    const precio = Number(String(form.precio).replace(/[^0-9]/g, ""));
    if (!precio) { setError("Poné el precio."); return; }
    if (!esMoto && !form.titulo.trim()) { setError("Poné un título para el aviso."); return; }
    setGuardando(true);
    const cambios = { precio, moneda: form.moneda, ciudad: form.ciudad.trim() || null, descripcion: form.descripcion.trim() || null, ...(esMoto ? {} : { modelo: form.titulo.trim() }) };
    const { data, error: e } = await supabase.from("motos_venta").update(cambios).eq("id", v.id).select().maybeSingle();
    setGuardando(false);
    if (e || !data) { setError("No se pudo guardar" + (e ? ": " + e.message : ". Probá de nuevo.")); return; }
    onGuardado(data);
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bg, width: "100%", maxWidth: 480, borderRadius: "18px 18px 0 0", padding: "18px 18px calc(18px + var(--sab, 0px))", maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: C.text, marginBottom: 12, letterSpacing: "-0.015em" }}>Editar aviso</div>
        {!esMoto && <input value={form.titulo} onChange={set("titulo")} placeholder="Título" style={inp} />}
        <div style={{ display: "flex", gap: 8 }}>
          <select value={form.moneda} onChange={set("moneda")} style={{ ...inp, width: 90, flexShrink: 0 }}><option value="ARS">ARS</option><option value="USD">USD</option></select>
          <input value={form.precio} onChange={set("precio")} inputMode="numeric" placeholder="Precio" style={inp} />
        </div>
        <input value={form.ciudad} onChange={set("ciudad")} placeholder="Ciudad" style={inp} />
        <textarea value={form.descripcion} onChange={set("descripcion")} rows={4} placeholder="Descripción" style={{ ...inp, resize: "vertical" }} />
        {error && <div style={{ fontSize: 13, color: C.red, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: C.surface, color: C.text, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: C.accent, color: "#F9F5F2", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", opacity: guardando ? 0.6 : 1 }}>{guardando ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
};

const PublicarAvisoModal = ({ onClose, onPublicado }) => {
  useBackGuard(true, onClose);
  const [cat, setCat] = useState(null);
  const [form, setForm] = useState({ titulo: "", marca: "", modelo: "", año: "", km: "", condicion: "usado", precio: "", moneda: "ARS", ciudad: "", descripcion: "" });
  const [fotos, setFotos] = useState([]); // urls ya subidas
  const [subiendo, setSubiendo] = useState(0);
  const [publicando, setPublicando] = useState(false);
  const [error, setError] = useState("");
  // Publicar como un local (concesionarias, 30-oct): el aviso sale a nombre de la cuenta del local
  const [cuentasComo, setCuentasComo] = useState([]);
  const [como, setComo] = useState(null); // null = mi cuenta | { id, username }
  useEffect(() => {
    supabase.rpc("mis_cuentas_delegadas").then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setCuentasComo(Array.isArray(d) ? d.filter(c => c.puede_posts) : []);
    }, () => setCuentasComo([]));
  }, []);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const esMoto = cat === "moto";
  const subirFotos = async (files) => {
    const lista = Array.from(files || []).slice(0, Math.max(0, 6 - fotos.length));
    if (!lista.length) return;
    setSubiendo(n => n + lista.length); setError("");
    for (const file of lista) {
      try {
        const fd = new FormData();
        fd.append("file", await achicarImagen(file));
        fd.append("upload_preset", "olbos_uploads");
        const r = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
        const d = await r.json();
        if (d.secure_url) setFotos(prev => [...prev, d.secure_url].slice(0, 6));
        else setError("Una foto no se pudo subir. Probá de nuevo.");
      } catch (e) { setError("Una foto no se pudo subir. Revisá la conexión y probá de nuevo."); }
      setSubiendo(n => Math.max(0, n - 1));
    }
  };
  const publicar = async () => {
    setError("");
    if (!cat) { setError("Elegí qué vendés."); return; }
    if (esMoto ? (!form.marca.trim() || !form.modelo.trim()) : !form.titulo.trim()) { setError(esMoto ? "Completá marca y modelo." : "Poné un título para el aviso."); return; }
    const precio = Number(String(form.precio).replace(/[^0-9]/g, ""));
    if (!precio) { setError("Poné el precio."); return; }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setError("Para publicar tenés que iniciar sesión."); return; }
    setPublicando(true);
    try {
      const username = como ? como.username : await usernameReal(user);
      const fila = {
        user_id: como ? como.id : user.id, username,
        categoria: cat,
        marca: esMoto ? form.marca.trim() : form.marca.trim(),
        modelo: esMoto ? form.modelo.trim() : form.titulo.trim(),
        año: esMoto && form.año ? Number(form.año) || null : null,
        km: esMoto && form.km ? Number(String(form.km).replace(/[^0-9]/g, "")) || null : null,
        condicion: esMoto ? null : form.condicion,
        precio, moneda: form.moneda,
        ciudad: form.ciudad.trim() || null,
        descripcion: form.descripcion.trim() || null,
        foto_url: fotos[0] || null, fotos,
        tipo: "venta", estado: "activa",
      };
      const { data, error: e } = await supabase.from("motos_venta").insert(fila).select().single();
      if (e) {
        setError(/categoria|condicion|schema cache/i.test(e.message || "")
          ? "Todavía no se pueden publicar avisos con categoría: falta un último paso en el servidor. Probá en un rato."
          : "No se pudo publicar: " + e.message);
        reportarError("publicar aviso: " + e.message, null, "PublicarAvisoModal");
        return;
      }
      registrarUso("aviso_publicado", { categoria: cat, fotos: fotos.length });
      onPublicado && onPublicado(data || fila);
      onClose();
    } finally { setPublicando(false); }
  };
  const chip = (activo) => ({ padding: "9px 16px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, border: `1px solid ${activo ? C.text : C.border}`, background: activo ? C.text : C.card, color: activo ? "#fff" : C.text, cursor: "pointer" });
  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", padding: "20px 20px calc(36px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700 }}>Publicar un aviso</h3>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>
        {cuentasComo.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={lbl}>Publicar como</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setComo(null)} style={chip(!como)}>Mi cuenta</button>
              {cuentasComo.map(c => <button key={c.id} onClick={() => setComo({ id: c.id, username: c.username })} style={chip(como?.id === c.id)}>@{c.username}</button>)}
            </div>
            {como && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>El aviso sale a nombre de @{como.username}. Lo pueden editar o retirar todos los que manejan esa cuenta.</div>}
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <div style={lbl}>¿Qué vendés?</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.entries(CATEGORIAS_VENTA).map(([id, label]) => (
              <button key={id} onClick={() => { setCat(id); setError(""); }} style={chip(cat === id)}>{label}</button>
            ))}
          </div>
          {esMoto && (
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginTop: 10, background: C.surface, borderRadius: 10, padding: "9px 12px" }}>
              Si la moto está en tu garage, conviene publicarla desde ahí: el aviso suma solo su historia clínica de services, que es lo que más confianza le da al que compra.
            </div>
          )}
        </div>
        {cat && (
          <>
            {esMoto ? (
              <>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}><Input label="Marca" placeholder="Ej: Honda" value={form.marca} onChange={set("marca")} /></div>
                  <div style={{ flex: 1 }}><Input label="Modelo" placeholder="Ej: XR 250 Tornado" value={form.modelo} onChange={set("modelo")} /></div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}><Input label="Año" placeholder="2021" value={form.año} onChange={set("año")} /></div>
                  <div style={{ flex: 1 }}><Input label="Kilómetros" placeholder="18000" value={form.km} onChange={set("km")} /></div>
                </div>
              </>
            ) : (
              <>
                <Input label="Título del aviso" placeholder={cat === "indumentaria" ? "Ej: Campera Alpinestars talle L" : cat === "repuesto" ? "Ej: Kit de arrastre XR 250" : "Ej: Baúl Givi 45 litros"} value={form.titulo} onChange={set("titulo")} />
                <Input label="Marca (opcional)" placeholder="Ej: Givi" value={form.marca} onChange={set("marca")} />
                <div style={{ marginBottom: 14 }}>
                  <div style={lbl}>Estado</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[["nuevo", "Nuevo"], ["usado", "Usado"]].map(([id, l]) => (
                      <button key={id} onClick={() => setForm(f => ({ ...f, condicion: id }))} style={chip(form.condicion === id)}>{l}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}><Input label="Precio" placeholder="Ej: 150000" value={form.precio} onChange={set("precio")} /></div>
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {["ARS", "USD"].map(m => <button key={m} onClick={() => setForm(f => ({ ...f, moneda: m }))} style={{ ...chip(form.moneda === m), padding: "11px 12px" }}>{m}</button>)}
              </div>
            </div>
            <Input label="¿Dónde está?" placeholder="Ej: La Plata, Buenos Aires" value={form.ciudad} onChange={set("ciudad")} />
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Descripción</div>
              <textarea value={form.descripcion} onChange={set("descripcion")} placeholder={esMoto ? "Estado, services, qué incluye, si acepta permuta…" : "Estado, talle o medida, para qué moto sirve, si hacés envíos…"}
                style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8 }}>Fotos (hasta 6)</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {fotos.map((u, i) => (
                  <div key={u} style={{ width: 72, height: 72, borderRadius: 10, overflow: "hidden", position: "relative", border: `1px solid ${C.border}` }}>
                    <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button onClick={() => setFotos(prev => prev.filter(x => x !== u))} style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      <X size={11} strokeWidth={2.5} color="#fff" />
                    </button>
                    {i === 0 && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 9, fontWeight: 700, textAlign: "center", padding: "2px 0" }}>PORTADA</div>}
                  </div>
                ))}
                {Array.from({ length: subiendo }).map((_, i) => (
                  <div key={"s" + i} style={{ width: 72, height: 72, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ width: 16, height: 16, border: `2px solid ${C.border}`, borderTopColor: "#111", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                  </div>
                ))}
                {fotos.length + subiendo < 6 && (
                  <label style={{ width: 72, height: 72, borderRadius: 10, border: `1.5px dashed ${C.border}`, background: C.surface, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                    <Camera size={20} strokeWidth={1.5} color={C.muted} />
                    <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>Agregar</span>
                    <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { subirFotos(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
          </>
        )}
        {error && <div style={{ fontSize: 13, color: C.red, fontWeight: 600, marginBottom: 12, lineHeight: 1.5 }}>{error}</div>}
        <Btn full variant="accent" onClick={publicar} disabled={publicando || subiendo > 0 || !cat}>{publicando ? "Publicando…" : subiendo > 0 ? "Subiendo fotos…" : "Publicar aviso"}</Btn>
      </div>
    </div>
  );
};

const FichaMotoVenta = ({ venta: v, onClose }) => {
  useBackGuard(true, onClose);
  const [vivo, setVivo] = useState(null);
  useEffect(() => {
    let ok = true;
    supabase.rpc("historia_moto_venta", { p_venta_id: v.id }).then(({ data: raw, error }) => {
      if (!ok || error) return;
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(d) && d.length) setVivo(d);
    }, () => {});
    return () => { ok = false; };
  }, [v.id]);
  const esAlquiler = v.tipo === "alquiler";
  const historial = (vivo || (Array.isArray(v.historial_mantenimiento) ? v.historial_mantenimiento : []))
    .slice().sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
  const services = historial.filter(r => r.tipo !== "Actualización km");
  const fotos = Array.isArray(v.fotos) && v.fotos.length ? v.fotos : (v.foto_url ? [v.foto_url] : []);
  const kmMax = historial.reduce((m, r) => Math.max(m, Number(r.km) || 0), Number(v.km) || 0);
  const gasto = historial.reduce((s, r) => s + ((!r.moneda || r.moneda === "ARS") ? (Number(r.costo) || 0) : 0), 0);
  const firmados = historial.filter(r => r.firmado_por_nombre).length;
  const fmtFecha = (f) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? f : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" }); };
  // Grilla 2×2: cuatro cajas en fila se truncaban ("23 de…", "$733…") en pantallas de 360 px
  const Stat = ({ label, val }) => (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 12px", minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginTop: 2, letterSpacing: "-0.01em" }}>{val}</div>
    </div>
  );
  const fmtCorta = (f) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? f : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "2-digit" }); };
  return (
    <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.bg, paddingBottom: 110 }}>
      <div style={{ padding: "max(48px, calc(var(--sat) + 12px)) 18px 12px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, display: "flex", alignItems: "center", padding: 0 }}><ChevronLeft size={22} strokeWidth={2} /></button>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>{esAlquiler ? "Moto en alquiler" : `${CATEGORIAS_VENTA[categoriaVenta(v)]} en venta`}</div>
      </div>

      {fotos.length > 0 && (
        <div style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", gap: 6, padding: "0 18px 12px", WebkitOverflowScrolling: "touch" }}>
          {fotos.map((u, i) => (
            <img loading="lazy" decoding="async" key={i} src={u} alt={`${v.marca} ${v.modelo}`} style={{ width: fotos.length > 1 ? "88%" : "100%", flexShrink: 0, height: 240, objectFit: "cover", borderRadius: 14, scrollSnapAlign: "center", border: `1px solid ${C.border}` }} />
          ))}
        </div>
      )}

      <div style={{ padding: "4px 18px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{tituloVenta(v)}</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{detalleVenta(v)}</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 20, color: C.green, letterSpacing: "-0.01em" }}>{v.moneda} {Number(v.precio).toLocaleString("es-AR")}</div>
            {esAlquiler && <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{v.periodo === "semana" ? "por semana" : v.periodo === "fin_de_semana" ? "por finde" : "por día"}</div>}
          </div>
        </div>
        {v.ciudad && <div style={{ fontSize: 13, color: C.text, marginTop: 8 }}>📍 Está en <strong>{v.ciudad}</strong></div>}
        <div style={{ fontSize: 12, color: C.muted, marginTop: v.ciudad ? 4 : 8 }}>Publicada por <strong style={{ color: C.text }}>@{v.username}</strong> · {new Date(v.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
        {v.descripcion && <p style={{ fontSize: 14, color: C.text, lineHeight: 1.6, margin: "12px 0 0" }}>{v.descripcion}</p>}
        {esAlquiler && v.zona && <div style={{ fontSize: 13, color: C.muted, marginTop: 10 }}>📍 Se entrega en {v.zona}</div>}
        {esAlquiler && v.requisitos && (
          <div style={{ background: C.surface, borderRadius: 10, padding: "9px 12px", marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Qué pide el dueño</div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{v.requisitos}</div>
          </div>
        )}

        {/* ── Historia clínica (solo motos) ── */}
        {categoriaVenta(v) === "moto" && <div style={{ marginTop: 22, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Historia clínica</div>}
        {categoriaVenta(v) !== "moto" ? null : historial.length === 0 ? (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
            Esta moto no tiene historial de service cargado en Olbos. Preguntale al dueño por los últimos services antes de decidir.
          </div>
        ) : (
          <>
            <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 12, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: C.green }}>✓ Historial registrado en Olbos{vivo ? " · en vivo" : ""}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
                {services.length === 1 ? "1 service cargado" : `${services.length} services cargados`}{firmados ? ` · ${firmados} firmado${firmados === 1 ? "" : "s"} por talleres` : ""}. Cada registro tiene fecha y kilometraje: lo que ves es lo que se le hizo.
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
              <Stat label="Registros" val={historial.length} />
              <Stat label="Último service" val={historial[0]?.fecha ? fmtCorta(historial[0].fecha) : "—"} />
              {kmMax > 0 && <Stat label="Kilometraje" val={`${kmMax.toLocaleString("es-AR")} km`} />}
              {gasto > 0 && <Stat label="Invertido en mantenimiento" val={`$${gasto.toLocaleString("es-AR")}`} />}
            </div>
            {historial.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 0 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 14, flexShrink: 0 }}>
                  <div style={{ width: 9, height: 9, borderRadius: "50%", background: r.tipo === "Actualización km" ? C.border : C.text, marginTop: 15, flexShrink: 0 }} />
                  {i < historial.length - 1 && <div style={{ width: 1.5, flex: 1, background: C.border, marginTop: 4 }} />}
                </div>
                <div style={{ flex: 1, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "11px 13px", marginBottom: 8, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{r.tipo}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, flexShrink: 0 }}>{fmtFecha(r.fecha)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    {[r.km ? `${Number(r.km).toLocaleString("es-AR")} km` : null, r.lugar, r.costo ? `${r.moneda && r.moneda !== "ARS" ? r.moneda + " " : "$"}${Number(r.costo).toLocaleString("es-AR")}` : null].filter(Boolean).join(" · ")}
                  </div>
                  {r.descripcion && <div style={{ fontSize: 12.5, color: C.text, marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-line" }}>{r.descripcion}</div>}
                  {r.firmado_por_nombre && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, padding: "3px 9px", borderRadius: 100, background: `${C.green}14`, border: `1px solid ${C.green}33`, fontSize: 11, fontWeight: 700, color: C.green }}>
                      ✓ Cargado por @{r.firmado_por_nombre}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 12px", marginTop: 14, fontSize: 11.5, color: C.muted, lineHeight: 1.6 }}>
          {esAlquiler
            ? "El acuerdo, el pago y la cobertura son entre las partes — Olbos no cobra ni garantiza. Chequeá el seguro antes de salir a la ruta."
            : "Compra-venta entre riders de la comunidad. El contacto y la transacción son entre las partes — Olbos no interviene."}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <ConsultarVendedor venta={v} />
          <BtnCompartir texto={`🏍️ ${tituloVenta(v)} ${v.año || ""} ${esAlquiler ? "en alquiler" : "en venta"}\n${v.moneda} ${Number(v.precio).toLocaleString()}\nVen en Olbos 👇`} tipo="venta" id={v.id} small arriba />
        </div>
      </div>
    </div>
  );
};

const HistoriaMotoPublica = ({ token, onVolver }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [agregando, setAgregando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ tipo: "Service general", fecha: new Date().toISOString().slice(0, 10), km: "", descripcion: "", costo: "" });

  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("get_historia_moto", { p_token: token });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || d?.error) setErr(d?.error === "no encontrada" ? "Este link ya no está activo: el dueño dejó de compartir el historial." : (error?.message || d?.error));
    else setData(d);
  };
  useEffect(() => {
    cargar();
    supabase.auth.getSession().then(({ data: { session } }) => setSesion(session));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const guardar = async () => {
    if (!form.descripcion.trim() && !form.tipo) { alert("Contá qué se le hizo a la moto."); return; }
    setGuardando(true);
    const { data: raw, error } = await supabase.rpc("agregar_service_moto", {
      p_token: token,
      p_tipo: form.tipo || "Service",
      p_fecha: form.fecha || new Date().toISOString().slice(0, 10),
      p_km: form.km ? Number(form.km) : null,
      p_descripcion: form.descripcion.trim() || null,
      p_costo: form.costo ? Number(form.costo) : null,
    });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setGuardando(false);
    if (error || r?.error) { alert("No se pudo guardar: " + (error?.message || r?.error)); return; }
    setForm({ tipo: "Service general", fecha: new Date().toISOString().slice(0, 10), km: "", descripcion: "", costo: "" });
    setAgregando(false);
    cargar();
    alert("✓ Service cargado. Queda firmado con tu cuenta en el historial de la moto.");
  };

  const inputS = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" };
  const fmtFecha = (f) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? f : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" }); };

  return (
    <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.bg, paddingBottom: 40 }}>
      <div style={{ padding: "max(48px, calc(var(--sat) + 12px)) 18px 14px", background: C.accent, color: "#f9f5f2" }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted, marginBottom: 6 }}>Olbos · Historial de service</div>
        {data ? (
          <>
            <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: "-0.02em" }}>{data.moto?.marca} {data.moto?.modelo}</div>
            <div style={{ fontSize: 12.5, color: "#c9c3ba", marginTop: 3 }}>
              {[data.moto?.año, data.moto?.cc ? `${data.moto.cc}cc` : null, data.moto?.color].filter(Boolean).join(" · ")} · de @{data.username}
            </div>
          </>
        ) : <div style={{ fontSize: 15, fontWeight: 700 }}>{err ? "Link no disponible" : "Cargando…"}</div>}
      </div>

      <div style={{ padding: "16px 18px" }}>
        {err && <div style={{ padding: "14px 16px", background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 12, fontSize: 13, color: C.red, lineHeight: 1.6 }}>{err}</div>}

        {data && (
          <>
            {/* cargar service (para el taller) */}
            {sesion ? (
              agregando ? (
                <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 12 }}>Cargar service realizado</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={{ ...inputS, background: C.card }}>
                      {["Service general", "Cambio de aceite y filtro", "Cubiertas", "Frenos", "Kit de transmisión", "Batería", "Reparación", "Otro"].map(t => <option key={t}>{t}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={{ ...inputS, flex: 1 }} />
                      <input type="number" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} placeholder="Km actuales" style={{ ...inputS, flex: 1 }} />
                    </div>
                    <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={3}
                      placeholder="Qué se hizo, repuestos usados, observaciones…" style={{ ...inputS, resize: "none", fontFamily: "inherit" }} />
                    <input type="number" value={form.costo} onChange={e => setForm(f => ({ ...f, costo: e.target.value }))} placeholder="Costo total en $ (opcional)" style={inputS} />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button onClick={() => setAgregando(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
                    <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "12px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: guardando ? 0.7 : 1 }}>
                      {guardando ? "Guardando…" : "Guardar en el historial"}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAgregando(true)}
                  style={{ width: "100%", padding: "14px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", marginBottom: 16 }}>
                  🔧 Cargar un service a esta moto
                </button>
              )
            ) : (
              <div style={{ padding: "12px 14px", background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 12, fontSize: 12.5, color: C.gold, lineHeight: 1.6, marginBottom: 16 }}>
                Para cargarle un service, iniciá sesión con la cuenta de tu taller en <strong>olbos.ai</strong> y volvé a abrir este link.
              </div>
            )}

            {/* historial */}
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
              Historial ({(data.registros || []).length})
            </div>
            {(data.registros || []).length === 0 && (
              <div style={{ padding: "24px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>Sin registros todavía — sé el primero en cargar uno.</div>
            )}
            {(data.registros || []).map((r, i) => (
              <div key={i} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: "12px 14px", marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{r.tipo}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, flexShrink: 0 }}>{fmtFecha(r.fecha)}</div>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {[r.km ? `${Number(r.km).toLocaleString()} km` : null, r.lugar, r.costo ? `$${Number(r.costo).toLocaleString()}` : null].filter(Boolean).join(" · ")}
                </div>
                {r.descripcion && <div style={{ fontSize: 12.5, color: C.text, marginTop: 6, lineHeight: 1.5 }}>{r.descripcion}</div>}
                {r.firmado_por_nombre && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, padding: "3px 9px", borderRadius: 100, background: `${C.green}14`, border: `1px solid ${C.green}33`, fontSize: 11, fontWeight: 700, color: "#1F8347" }}>
                    ✓ Cargado por @{r.firmado_por_nombre}
                  </div>
                )}
              </div>
            ))}

            <p style={{ fontSize: 11, color: C.faint, textAlign: "center", marginTop: 18, lineHeight: 1.6 }}>
              Este historial lo compartió su dueño desde Olbos.<br />El acceso puede revocarse en cualquier momento.
            </p>
          </>
        )}

        <button onClick={onVolver} style={{ width: "100%", marginTop: 16, padding: "12px", borderRadius: 12, background: "transparent", border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Ir a Olbos
        </button>
      </div>
    </div>
  );
};

// ─── KM SOLIDARIOS: contador colectivo de una rodada ─────────────────────────
// Suma los km de todos los recorridos grabados en la rodada (trazas con evento_id)
// contra una meta definida por el organizador. La idea de Matías (14-ago-2026):
// "X km entre todos = tal aporte a la colecta" — medible, colectivo y motero.
const KmSolidarios = ({ eventoId, meta, texto }) => {
  const [km, setKm] = useState(null);
  useEffect(() => {
    let vivo = true;
    supabase.rpc("km_solidarios_evento", { p_evento_id: eventoId }).then(({ data }) => {
      if (vivo && data != null && !data.error) setKm(Number(typeof data === "object" ? data.km : data) || 0);
    }, () => {});
    return () => { vivo = false; };
  }, [eventoId]);
  const pct = km == null ? 0 : Math.min(100, Math.round((km / meta) * 100));
  return (
    <div style={{ padding: "13px 14px", borderRadius: 12, background: C.accent, color: "#f9f5f2", margin: "4px 0 10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "#c9c3ba" }}>🏍 Km solidarios</span>
        <span style={{ fontSize: 15, fontWeight: 800 }}>{km == null ? "…" : Math.round(km).toLocaleString()} <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>/ {meta.toLocaleString()} km</span></span>
      </div>
      <div style={{ height: 8, borderRadius: 100, background: "rgba(249,245,242,.15)", overflow: "hidden", marginBottom: 6 }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 100, background: pct >= 100 ? "#2F7D4F" : "#f9f5f2", transition: "width .6s ease" }} />
      </div>
      <div style={{ fontSize: 11.5, color: "#c9c3ba", lineHeight: 1.5 }}>
        {pct >= 100 ? "🎉 ¡Meta cumplida! " : ""}{texto || "Cada recorrido grabado en la rodada suma al contador."}
        {" "}Grabá tu recorrido desde la rodada y sumá tus km.
      </div>
    </div>
  );
};

// ─── EMERGENCIAS: IDENTIFICAR RIDER POR PATENTE ──────────────────────────────
// Un rider caído/inconsciente se identifica por la patente de su moto del garage:
// devuelve su ficha privada de emergencia (contacto, grupo sanguíneo, condición).
// El RPC valida server-side que quien pregunta sea organizador de eventos o admin.
const identificarPorPatente = async () => {
  const pat = window.prompt("Patente de la moto (ej: AB123CD):");
  if (!pat) return;
  const { data: raw, error } = await supabase.rpc("identificar_por_patente", { p_patente: pat });
  const r = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (error) { alert("No se pudo buscar: " + error.message); return; }
  if (!r || r.error || !r.username) { alert(`No se encontró ninguna moto con patente "${pat}" en Olbos.\n\nProbá sin espacios ni guiones.`); return; }
  const lineas = [
    `🏍️ ${r.moto || "Moto"} — patente ${String(pat).toUpperCase()}`,
    `👤 @${r.username}${r.nombre ? ` (${r.nombre})` : ""}`,
    r.grupo_sanguineo ? `🩸 Grupo sanguíneo: ${r.grupo_sanguineo}` : null,
    r.condicion_medica ? `⚕️ Condición: ${r.condicion_medica}` : null,
    r.medicacion ? `💊 Medicación: ${r.medicacion}` : null,
    r.contacto_nombre || r.contacto_tel ? `🆘 Emergencia: ${r.contacto_nombre || ""}${r.contacto_relacion ? ` (${r.contacto_relacion})` : ""} ${r.contacto_tel || "(sin teléfono)"}` : "⚠️ No cargó contacto de emergencia",
  ].filter(Boolean);
  if (r.contacto_tel && window.confirm(lineas.join("\n") + "\n\n¿Llamar al contacto de emergencia ahora?")) {
    window.open(`tel:${String(r.contacto_tel).replace(/[^0-9+]/g, "")}`, "_self");
  } else if (!r.contacto_tel) {
    alert(lineas.join("\n"));
  }
};

// Botón autónomo: aparece para organizadores/admins en CUALQUIER evento de la base
// (los eventos tipo expo no tienen panel de rodada, y con 1000 motos es donde más sirve).
const BotonEmergenciaPatente = ({ evento }) => {
  const [esOrg, setEsOrg] = useState(false);
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: mi } = await supabase.from("perfiles").select("es_admin").eq("id", user.id).maybeSingle();
      setEsOrg(user.id === evento.creator_id || !!mi?.es_admin);
    })();
  }, [evento.id, evento.creator_id]);
  if (!esOrg) return null;
  return (
    <button onClick={identificarPorPatente}
      style={{ width: "100%", padding: "10px", borderRadius: 10, marginTop: 8, background: C.card, color: "#B3372C", border: "1px solid #E8C4BF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
      🆘 Identificar rider por patente
    </button>
  );
};

const RodadaPanel = ({ evento, gestiona = false }) => {
  const [yo, setYo] = useState(null);
  const [esOrg, setEsOrg] = useState(false);
  useEffect(() => { if (gestiona) setEsOrg(true); }, [gestiona]);
  const [inscripto, setInscripto] = useState(false);
  const [estado, setEstado] = useState(evento.estado || "inscripcion_abierta");
  const [cerrada, setCerrada] = useState(!!evento.inscripcion_cerrada);
  // La ficha recarga la fila fresca (largada desde el mapa, Realtime): este panel la sigue
  useEffect(() => { if (evento.estado) setEstado(evento.estado); setCerrada(!!evento.inscripcion_cerrada); }, [evento.estado, evento.inscripcion_cerrada]);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      // El admin y los co-organizadores gestionan la rodada aunque el evento lo haya
      // cargado la cuenta @olbos (criterio único: gestionaEvento)
      const { data: mi } = await supabase.from("perfiles").select("es_admin").eq("id", user.id).maybeSingle();
      setYo(user.id); setEsOrg(gestiona || gestionaEvento(evento, user.id, !!mi?.es_admin));
      const { data: ev } = await supabase.from("eventos").select("estado, inscripcion_cerrada").eq("id", evento.id).maybeSingle();
      if (ev?.estado) setEstado(ev.estado);
      if (ev) setCerrada(!!ev.inscripcion_cerrada);
      const { data: ins } = await supabase.from("inscripciones").select("id").eq("evento_id", evento.id).eq("user_id", user.id).maybeSingle();
      setInscripto(!!ins);
    })();
  }, [evento.id]);

  const cambiar = async (nuevo) => {
    const prev = estado;
    setEstado(nuevo);
    const { data: raw, error } = await supabase.rpc("cambiar_estado_salida", { p_evento_id: evento.id, p_estado: nuevo });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) {
      setEstado(prev);
      alert("No se pudo cambiar el estado de la rodada. " + (error?.message || r?.error || "Probá de nuevo."));
      return;
    }
    // La ficha y la lista se enteran (estado, "Recorrido sorpresa", check-in)
    window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: evento.id, estado: nuevo } }));
  };

  // La largada vive en el mapa en vivo (RodadaEnVivo tiene su propio botón de
  // arrancar, con las mismas validaciones). Acá quedan solo los estados posteriores.

  // Revertir: volver la rodada a "por largar" (cancelar largada / reabrir finalizada)
  const revertir = (mensaje) => { if (window.confirm(mensaje)) cambiar("inscripcion_abierta"); };

  // Cerrar/reabrir inscripción a mano, independiente del estado de la rodada
  const toggleInscripcion = async () => {
    const nuevo = !cerrada;
    if (nuevo && !window.confirm("¿Cerrar la inscripción? Nadie más va a poder anotarse a este evento.")) return;
    setCerrada(nuevo);
    const { data: raw, error } = await supabase.rpc("set_inscripcion_cerrada", { p_evento_id: evento.id, p_cerrada: nuevo });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) { setCerrada(!nuevo); alert("No se pudo cambiar (¿está corrido el SQL de inscripción?): " + (error?.message || r?.error || "")); return; }
    window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: evento.id } }));
  };

  if (!yo || (!esOrg && !inscripto)) return null;
  const enCurso = estado === "en_curso";

  return (
    <div style={{ marginTop: 8 }}>
      {/* Estado, en chiquito. El recuadro naranja con tres botones apilados hacía
          ver esto como lo más importante de la pantalla, y no lo es. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
        🏁 La rodada · {estado === "inscripcion_abierta" ? "por largar" : enCurso ? "en curso" : estado === "finalizada" ? "finalizada" : estado.replace(/_/g, " ")}
        {cerrada && <span style={{ fontWeight: 500 }}> · inscripción cerrada</span>}
      </div>

      {/* La largada se hace DESDE el mapa en vivo: el organizador entra, ve al
          grupo y toca "Arrancar la rodada" ahí. Acá no va ningún botón más. */}

      {/* Gestión del organizador: lo que no se usa en el momento de largar va
          acá adentro, plegado, para que no compita con el botón de arriba. */}
      {esOrg && !rodadaTerminada(evento) && (
        <SeccionPlegable titulo="Gestión de la rodada" Icono={Shield}>
          <button onClick={identificarPorPatente}
            style={{ width: "100%", padding: "10px", borderRadius: 10, marginBottom: 8, background: C.card, color: "#B3372C", border: "1px solid #E8C4BF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            🆘 Identificar rider por patente
          </button>
          <button onClick={toggleInscripcion}
            style={{ width: "100%", padding: "10px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {cerrada ? "🔓 Reabrir inscripción" : "🔒 Cerrar inscripción"}
          </button>
        </SeccionPlegable>
      )}
      {esOrg && enCurso && (
        <>
          <button onClick={() => { if (window.confirm("¿Marcar la rodada como terminada? Después podés iniciar el sorteo.")) cambiar("finalizada"); }}
            style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer", marginTop: 8 }}>
            Terminó la rodada
          </button>
          <button onClick={() => revertir("¿Cancelar la largada y volver la rodada a 'por largar'? (por si la iniciaste por error)")}
            style={{ width: "100%", padding: "8px", borderRadius: 8, background: "transparent", color: C.muted, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
            ↩︎ Cancelar largada (volver a "por largar")
          </button>
        </>
      )}
      {esOrg && estado === "finalizada" && (
        <>
          <div style={{ fontSize: 12.5, color: "#1F8347", fontWeight: 600 }}>✓ Rodada finalizada — ya podés iniciar el sorteo abajo.</div>
          <button onClick={() => revertir("¿Reabrir la rodada y volverla a 'por largar'? (por si la finalizaste por error o era una prueba)")}
            style={{ width: "100%", padding: "8px", borderRadius: 8, background: "transparent", color: C.muted, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
            ↩︎ Reabrir rodada (volver a "por largar")
          </button>
        </>
      )}

      {/* (el mapa en vivo se ofrece arriba, en "Posición del grupo en tiempo real" — evitamos duplicarlo) */}

      {/* grabar el recorrido: durante la rodada, cada uno graba su track (suma al pasaporte) */}
      {/* Con la rodada en curso, TODO pasa en el mapa en vivo: ahí se comparte
          ubicación, se graba tu recorrido (suma al pasaporte) y se habla por el
          intercomunicador. Antes había un botón aparte que abría OTRO grabador. */}
      {enCurso && (
        <>
          <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento } }))}
            style={{ width: "100%", padding: "12px", borderRadius: 10, marginTop: 8, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            📡 Abrir el mapa de la rodada
          </button>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>
            Ahí ves al grupo, se graba tu recorrido (suma a tu pasaporte) y podés hablar por el intercomunicador.
          </div>
        </>
      )}
      {!esOrg && estado === "inscripcion_abierta" && (
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>La rodada todavía no largó. Podés entrar al mapa en vivo igual: cuando el organizador la arranque te aparece el aviso ahí y empieza a grabarse tu recorrido.</div>
      )}
    </div>
  );
};

// ─── TRACK OFICIAL DE LA RODADA (Capa B) ─────────────────────────────────────
// Muestra el recorrido de la rodada = la grabación individual MÁS COMPLETA (no se
// fusionan los GPS). El organizador puede cambiar cuál es la oficial. Compartible.
const TrackOficialRodada = ({ evento, gestiona = false }) => {
  const [yo, setYo] = useState(null);
  const [esOrg, setEsOrg] = useState(false);
  useEffect(() => { if (gestiona) setEsOrg(true); }, [gestiona]);
  const [data, setData] = useState(null);
  const [elegir, setElegir] = useState(false);
  const [compartiendo, setCompartiendo] = useState(false);

  const cargar = async () => {
    const { data: raw } = await supabase.rpc("get_trazas_rodada", { p_evento_id: evento.id });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (d && !d.error) setData(d);
  };
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setYo(user.id); setEsOrg(gestiona || gestionaEvento(evento, user.id));
      cargar();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  if (!data || !Array.isArray(data.trazas) || data.trazas.length === 0) return null;
  const trazas = data.trazas; // ya vienen ordenadas por km desc (más completa primero)
  const oficial = (data.track_oficial_id && trazas.find(t => t.id === data.track_oficial_id)) || trazas[0];
  const puntos = Array.isArray(oficial.puntos) ? oficial.puntos : [];

  const elegirOficial = async (trazaId) => {
    const { data: raw, error } = await supabase.rpc("set_track_oficial", { p_evento_id: evento.id, p_traza_id: trazaId });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) alert("No se pudo cambiar el recorrido oficial. " + (error?.message || r?.error || ""));
    setElegir(false);
    cargar();
  };
  const compartir = async () => {
    if (compartiendo) return;
    if (kmTraza(oficial) < KM_MIN_PUBLICAR) { alert(msgMinKm(kmTraza(oficial))); return; }
    setCompartiendo(true);
    const { error } = await compartirTrazaEnFeed({ ...oficial, grupo_id: null }, yo);
    setCompartiendo(false);
    if (error) { alert("No se pudo compartir: " + (error.message || error)); return; }
    alert("¡Listo! El recorrido de la rodada se publicó en el feed 🏍️");
  };
  // Recap al WhatsApp: marca la traza oficial como pública (dueño u organizador) y arma el
  // mensaje con el link /ruta/:id, cuyo preview ya muestra el mapa + km + tiempo.
  const compartirRecapWA = async () => {
    await supabase.rpc("set_traza_publica", { p_traza_id: oficial.id }).then(() => {}, () => {});
    const url = `${SITE_URL}/ruta/${oficial.id}`;
    const titulo = evento.titulo || evento.title || "la rodada";
    const km = oficial.distancia_km ? `${Number(oficial.distancia_km).toFixed(1)} km` : "";
    const dur = oficial.duracion_seg ? formatDur(oficial.duracion_seg) : "";
    const vmax = oficial.velocidad_max ? `vel. máx ${Math.round(oficial.velocidad_max)} km/h` : "";
    const stats = [km && `📍 ${km}`, dur && `⏱ ${dur}`, vmax && `🏁 ${vmax}`].filter(Boolean).join(" · ");
    compartirNativo({ texto: `🏁 ${titulo} — ¡rodada terminada!\n${stats}\nMirá el recorrido completo 👇`, url, titulo });
  };

  return (
    <div style={{ marginTop: 8, padding: 12, borderRadius: 12, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1F8347", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
        🛣️ Recorrido de la rodada {trazas.length > 1 ? `· ${trazas.length} grabaciones` : ""}
      </div>
      {puntos.length >= 2 ? (
        <div style={{ height: 160, borderRadius: 10, overflow: "hidden", marginBottom: 8 }}>
          <MapaPreview puntos={puntos} alto={160} sinInteraccion track />
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>La grabación elegida todavía no tiene puntos suficientes para el mapa.</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.muted, marginBottom: 10 }}>
        <span><strong style={{ color: C.text }}>{oficial.distancia_km ? Number(oficial.distancia_km).toFixed(1) : "—"}</strong> km</span>
        {oficial.duracion_seg ? <span><strong style={{ color: C.text }}>{formatDur(oficial.duracion_seg)}</strong></span> : null}
        <span style={{ marginLeft: "auto" }}>Grabado por @{oficial.username || "rider"}</span>
      </div>
      <button onClick={compartirRecapWA}
        style={{ width: "100%", padding: "11px", borderRadius: 10, marginBottom: 8, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        📲 Compartir el resumen al WhatsApp
      </button>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={compartir} disabled={compartiendo}
          style={{ flex: 1, padding: "10px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: compartiendo ? 0.6 : 1 }}>
          {compartiendo ? "Publicando…" : "📤 Compartir en el feed"}
        </button>
        {esOrg && trazas.length > 1 && (
          <button onClick={() => setElegir(e => !e)}
            style={{ padding: "10px 14px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Cambiar
          </button>
        )}
      </div>
      {esOrg && elegir && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Elegí cuál es el recorrido oficial de la rodada:</div>
          {trazas.map(t => {
            const esOf = t.id === oficial.id;
            return (
              <button key={t.id} onClick={() => elegirOficial(t.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, background: esOf ? `${C.green}14` : C.card, border: `1px solid ${esOf ? "#1F8347" : C.border}`, cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: t.avatar_url ? `url(${t.avatar_url}) center/cover` : C.surface, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>@{t.username || "rider"}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{t.distancia_km ? Number(t.distancia_km).toFixed(1) : "—"} km · {t.cant_puntos || 0} puntos</div>
                </div>
                {esOf && <CheckCircle size={16} strokeWidth={2.5} color="#1F8347" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── SORTEO EN VIVO (Capa 1) ─────────────────────────────────────────────────
// Organizador: carga premios, inicia y sortea. Participante: lo ve en vivo en su cel.
// Random server-side (RPC), Realtime para sincronizar pantallas + polling de respaldo.
const SorteoSeccion = ({ evento, onClose }) => {
  const [yo, setYo] = useState(null);
  const [data, setData] = useState(null);       // { estado, tiene_sorteo, premios: [] }
  const [org, setOrg] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [fotoNueva, setFotoNueva] = useState("");
  const [editandoId, setEditandoId] = useState(null); // premio en edición
  const [editNombre, setEditNombre] = useState("");
  const [editFoto, setEditFoto] = useState("");
  const [esAdmin, setEsAdmin] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [accion, setAccion] = useState(false);
  const [sorteandoId, setSorteandoId] = useState(null);
  const [celebrar, setCelebrar] = useState(null);
  const prevRef = useRef({});
  const initRef = useRef(false);
  const eventoId = evento.id;
  // Quiénes participan del sorteo: inscriptos (default) / presentes (check-in) / ingresados (entrada escaneada)
  const [eleg, setEleg] = useState(evento.sorteo_elegibilidad || "inscriptos");
  const cambiarElegibilidad = async (v) => {
    const prev = eleg;
    setEleg(v);
    const { error } = await supabase.from("eventos").update({ sorteo_elegibilidad: v }).eq("id", eventoId);
    if (error) { setEleg(prev); alert("No se pudo cambiar (¿está corrido el SQL de sorteos?)"); }
  };
  const ELEG_OPCIONES = [
    { id: "inscriptos", label: "Todos los inscriptos" },
    { id: "presentes", label: "Solo presentes (check-in)" },
    { id: "ingresados", label: "Solo con entrada escaneada" },
  ];

  const refetch = async () => {
    const { data: raw } = await supabase.rpc("get_sorteo_estado", { p_evento_id: eventoId });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (d && !d.error) setData(d);
  };

  useEffect(() => {
    let activo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (activo) setYo(user?.id || null);
      // El admin gestiona como organizador: los eventos de la agenda los carga la cuenta @olbos
      if (user) {
        const { data: mi } = await supabase.from("perfiles").select("es_admin").eq("id", user.id).maybeSingle();
        if (activo) setEsAdmin(!!mi?.es_admin);
      }
      const { data: o } = await supabase.from("perfiles").select("username, avatar_url").eq("id", evento.creator_id).maybeSingle();
      if (activo) setOrg(o || null);
      await refetch();
      if (activo) setCargando(false);
    })();
    const ch = supabase.channel(`sorteo-${eventoId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "sorteo_premios", filter: `evento_id=eq.${eventoId}` }, () => refetch())
      .subscribe();
    const poll = setInterval(refetch, 5000); // respaldo si Realtime no conecta
    return () => { activo = false; supabase.removeChannel(ch); clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  // Detectar ganadores nuevos → animación de suspenso + celebración si gané yo
  useEffect(() => {
    if (!data?.premios) return;
    if (!initRef.current) {
      data.premios.forEach(pr => { prevRef.current[pr.id] = pr.ganador_user_id || null; });
      initRef.current = true;
      return;
    }
    data.premios.forEach(pr => {
      if (pr.ganador_user_id && !prevRef.current[pr.id]) {
        setSorteandoId(pr.id);
        setTimeout(() => {
          setSorteandoId(s => (s === pr.id ? null : s));
          try { navigator.vibrate?.(200); } catch {}
          if (pr.ganador_user_id === yo) setCelebrar(pr);
        }, 1800);
      }
      prevRef.current[pr.id] = pr.ganador_user_id || null;
    });
  }, [data, yo]);

  // destino: 'nueva' (premio a agregar) o 'edicion' (premio que se está editando)
  const subirFoto = async (file, destino = "nueva") => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) { destino === "edicion" ? setEditFoto(j.secure_url) : setFotoNueva(j.secure_url); }
    } catch (e) { /* */ }
    setSubiendoFoto(false);
  };
  const agregarPremio = async () => {
    if (!nuevoNombre.trim()) return;
    setAccion(true);
    await supabase.from("sorteo_premios").insert({ evento_id: eventoId, nombre: nuevoNombre.trim(), foto_url: fotoNueva || null, orden: (data?.premios?.length || 0) });
    setNuevoNombre(""); setFotoNueva("");
    await refetch(); setAccion(false);
  };
  // Editar un premio ya cargado (cambiar el nombre o la foto)
  const guardarEdicionPremio = async (id) => {
    if (!editNombre.trim()) return;
    setAccion(true);
    const { error } = await supabase.from("sorteo_premios")
      .update({ nombre: editNombre.trim(), foto_url: editFoto || null }).eq("id", id);
    setAccion(false);
    if (error) { alert("No se pudo guardar el cambio: " + error.message); return; }
    setEditandoId(null); setEditNombre(""); setEditFoto("");
    refetch();
  };

  const borrarPremio = async (id) => {
    if (!window.confirm("¿Borrar este premio?")) return;
    await supabase.from("sorteo_premios").delete().eq("id", id);
    refetch();
  };
  const iniciarSorteo = async () => {
    setAccion(true);
    const { data: raw, error } = await supabase.rpc("cambiar_estado_salida", { p_evento_id: eventoId, p_estado: "sorteo_en_curso" });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) alert("No se pudo iniciar el sorteo. " + (error?.message || r?.error || "Probá de nuevo."));
    else window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: eventoId, estado: "sorteo_en_curso" } }));
    await refetch(); setAccion(false);
  };
  const sortear = async (premioId) => {
    setAccion(true);
    const { data: raw } = await supabase.rpc("sortear_premio", { p_premio_id: premioId });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (r?.error) {
      const msgs = { sin_participantes: "No hay participantes elegibles (todos los inscriptos ya ganaron, o no hay inscriptos).", ya_sorteado: "Ese premio ya fue sorteado.", no_autorizado: "Solo el organizador puede sortear." };
      alert(msgs[r.error] || "No se pudo sortear: " + r.error);
    }
    await refetch(); setAccion(false);
  };

  const soyOrg = !!(yo && ((evento.creator_id && yo === evento.creator_id) || esAdmin));
  const premios = data?.premios || [];
  const empezado = data?.estado === "sorteo_en_curso" || premios.some(p => p.ganador_user_id);
  const cerrar = () => onClose();

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 600, background: "#0d0d12", color: "#fff", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", paddingTop: "var(--sat)", paddingBottom: "var(--sab)", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>
      {/* header con marca del organizador */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 16px 12px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <button onClick={cerrar} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.1)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <X size={16} color="#fff" strokeWidth={2.5} />
        </button>
        {org?.avatar_url && <img loading="lazy" decoding="async" src={org.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover" }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🎁 Sorteo · {evento.title}</div>
          {org?.username && <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>por @{org.username}</div>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {cargando ? (
          <div style={{ textAlign: "center", color: "rgba(255,255,255,.5)", padding: 40, fontSize: 13 }}>Cargando…</div>
        ) : (
          <>
            {/* estado / instrucción */}
            {!empezado && (
              <div style={{ textAlign: "center", padding: "8px 0 18px" }}>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.5 }}>
                  {soyOrg ? "Cargá los premios y, cuando termine la rodada, iniciá el sorteo." : "El sorteo arranca cuando el organizador lo inicie. Quedate atento 👀"}
                </div>
              </div>
            )}

            {/* quiénes participan */}
            {soyOrg ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.5)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Participan del sorteo</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ELEG_OPCIONES.map(o => (
                    <button key={o.id} onClick={() => cambiarElegibilidad(o.id)}
                      style={{ padding: "8px 12px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", border: `1px solid ${eleg === o.id ? "#F9F5F2" : "rgba(255,255,255,.2)"}`, background: eleg === o.id ? "#F9F5F2" : "transparent", color: eleg === o.id ? "#111111" : "rgba(255,255,255,.7)" }}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {eleg !== "inscriptos" && (
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.45)", marginTop: 8, lineHeight: 1.4 }}>
                    {eleg === "presentes" ? "Hay que estar para ganar: solo entran los que hicieron check-in en la salida." : "Solo entran los que pasaron por la puerta con su entrada QR escaneada."}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,.45)", marginBottom: 14 }}>
                🎟️ Participan: {(ELEG_OPCIONES.find(o => o.id === eleg) || ELEG_OPCIONES[0]).label.toLowerCase()}
              </div>
            )}

            {/* lista de premios */}
            {premios.map((pr, i) => {
              const sorteando = sorteandoId === pr.id;
              const ganado = !!pr.ganador_user_id && !sorteando;
              const gane = ganado && pr.ganador_user_id === yo;
              return (
                <div key={pr.id} style={{ borderRadius: 16, padding: 14, marginBottom: 12, background: gane ? "linear-gradient(135deg,#111111,#2A2A2A)" : "rgba(255,255,255,.06)", border: `1px solid ${gane ? "transparent" : "rgba(255,255,255,.1)"}`, transition: "all .3s" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 56, height: 56, borderRadius: 12, overflow: "hidden", background: "rgba(255,255,255,.1)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {pr.foto_url ? <img loading="lazy" decoding="async" src={pr.foto_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Award size={26} color="rgba(255,255,255,.5)" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: gane ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Premio {i + 1}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{pr.nombre}</div>
                      {sorteando ? (
                        <div style={{ fontSize: 13, color: "#FFD27A", fontWeight: 700, marginTop: 2, animation: "pulse 0.8s ease-in-out infinite" }}>🎲 Sorteando…</div>
                      ) : ganado ? (
                        <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, color: gane ? "#fff" : "#7CE38B" }}>
                          {gane ? "🎉 ¡Ganaste vos!" : (
                            <>🏆 {pr.ganador_nombre || `@${pr.ganador_username || "ganador"}`}
                              {pr.ganador_nombre && pr.ganador_username && (
                                <span style={{ fontWeight: 500, opacity: 0.7 }}> · @{pr.ganador_username}</span>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 2 }}>Sin sortear</div>
                      )}
                    </div>
                    {/* placa compartible del ganador (para stories) */}
                    {ganado && (
                      <button onClick={() => compartirPlacaSorteo({ premioNombre: pr.nombre, premioFoto: pr.foto_url, ganadorNombre: pr.ganador_nombre, ganadorUsername: pr.ganador_username, contexto: evento.title || evento.titulo, porQuien: org?.username })}
                        title="Compartir placa del ganador"
                        style={{ padding: "8px 12px", borderRadius: 10, background: "rgba(255,255,255,.12)", color: "#fff", border: "1px solid rgba(255,255,255,.2)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                        📤 Placa
                      </button>
                    )}
                    {/* acciones organizador */}
                    {soyOrg && !empezado && (
                      <>
                        <button onClick={() => { setEditandoId(pr.id); setEditNombre(pr.nombre); setEditFoto(pr.foto_url || ""); }}
                          title="Editar premio"
                          style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.25)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Edit2 size={13} color="#fff" strokeWidth={2.2} />
                        </button>
                        <button onClick={() => borrarPremio(pr.id)} style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,69,0,.15)", border: "1px solid rgba(255,69,0,.4)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <X size={13} color="#FF7A4D" strokeWidth={2.5} />
                        </button>
                      </>
                    )}
                    {soyOrg && empezado && !pr.ganador_user_id && (
                      <button onClick={() => sortear(pr.id)} disabled={accion}
                        style={{ padding: "9px 14px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", flexShrink: 0, opacity: accion ? 0.6 : 1 }}>
                        Sortear
                      </button>
                    )}
                  </div>

                  {/* edición del premio (nombre y foto) */}
                  {editandoId === pr.id && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,.12)" }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                        <label style={{ width: 50, height: 50, borderRadius: 10, background: "rgba(255,255,255,.08)", border: "1px dashed rgba(255,255,255,.25)", cursor: subiendoFoto ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                          {editFoto ? <img loading="lazy" decoding="async" src={editFoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Camera size={18} color="rgba(255,255,255,.5)" />}
                          <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendoFoto}
                            onChange={e => { subirFoto(e.target.files[0], "edicion"); e.target.value = ""; }} />
                        </label>
                        <input value={editNombre} onChange={e => setEditNombre(e.target.value)} placeholder="Nombre del premio"
                          style={{ flex: 1, minWidth: 0, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => { setEditandoId(null); setEditFoto(""); setEditNombre(""); }}
                          style={{ flex: 1, padding: "10px", borderRadius: 10, background: "transparent", color: "rgba(255,255,255,.7)", border: "1px solid rgba(255,255,255,.2)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                          Cancelar
                        </button>
                        <button onClick={() => guardarEdicionPremio(pr.id)} disabled={accion || subiendoFoto || !editNombre.trim()}
                          style={{ flex: 2, padding: "10px", borderRadius: 10, background: "#F9F5F2", color: C.text, border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: (!editNombre.trim() || subiendoFoto) ? 0.5 : 1 }}>
                          {accion ? "Guardando…" : "Guardar cambios"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {premios.length === 0 && !soyOrg && (
              <div style={{ textAlign: "center", color: "rgba(255,255,255,.4)", fontSize: 13, padding: 30 }}>Todavía no hay premios cargados.</div>
            )}

            {/* organizador: cargar premios (antes de empezar) */}
            {soyOrg && !empezado && (
              <div style={{ marginTop: 8, padding: 14, borderRadius: 16, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,.6)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Agregar premio</div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                  <label style={{ width: 56, height: 56, borderRadius: 12, background: "rgba(255,255,255,.08)", border: "1px dashed rgba(255,255,255,.25)", cursor: subiendoFoto ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                    {fotoNueva ? <img loading="lazy" decoding="async" src={fotoNueva} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Camera size={20} color="rgba(255,255,255,.5)" />}
                    <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendoFoto} onChange={e => { subirFoto(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  <input value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} placeholder="Ej: Casco LS2"
                    style={{ flex: 1, minWidth: 0, padding: "11px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, boxSizing: "border-box" }} />
                </div>
                <button onClick={agregarPremio} disabled={accion || subiendoFoto || !nuevoNombre.trim()}
                  style={{ width: "100%", padding: "11px", borderRadius: 10, background: "rgba(255,255,255,.12)", color: "#fff", border: "none", fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (!nuevoNombre.trim() || subiendoFoto) ? 0.5 : 1 }}>
                  <Plus size={15} strokeWidth={2.5} /> Agregar premio
                </button>
              </div>
            )}

            {/* organizador: iniciar sorteo */}
            {soyOrg && !empezado && premios.length > 0 && (
              <button onClick={iniciarSorteo} disabled={accion}
                style={{ width: "100%", marginTop: 16, padding: "15px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", fontSize: 16, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 20px rgba(255,69,0,.35)", opacity: accion ? 0.6 : 1 }}>
                🎬 Iniciar sorteo
              </button>
            )}
            {soyOrg && empezado && premios.every(p => p.ganador_user_id) && premios.length > 0 && (
              <div style={{ textAlign: "center", color: "#7CE38B", fontWeight: 700, fontSize: 14, marginTop: 12 }}>✓ Todos los premios entregados</div>
            )}
          </>
        )}
      </div>

      {/* celebración si gané yo */}
      {celebrar && (
        <div onClick={() => setCelebrar(null)} style={{ position: "fixed", inset: 0, zIndex: 700, background: "linear-gradient(135deg,#111111,#FF8A00)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center", maxWidth: 430, margin: "0 auto" }} className="fade-up">
          <div style={{ fontSize: 80, marginBottom: 10 }}>🎉</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,.85)", textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>¡Ganaste!</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: "#fff", lineHeight: 1.1, marginBottom: 16 }}>{celebrar.nombre}</div>
          {celebrar.foto_url && <img loading="lazy" decoding="async" src={celebrar.foto_url} alt="" style={{ width: 160, height: 160, borderRadius: 20, objectFit: "cover", marginBottom: 20, boxShadow: "0 12px 40px rgba(0,0,0,.4)" }} />}
          <div style={{ fontSize: 14, color: "rgba(255,255,255,.9)", marginBottom: 24, lineHeight: 1.5 }}>Mostrale esta pantalla al organizador para retirar tu premio.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={e => { e.stopPropagation(); compartirPlacaSorteo({ premioNombre: celebrar.nombre, premioFoto: celebrar.foto_url, ganadorNombre: celebrar.ganador_nombre, ganadorUsername: celebrar.ganador_username, contexto: evento.title || evento.titulo, porQuien: org?.username }); }}
              style={{ padding: "13px 22px", borderRadius: 100, background: "rgba(255,255,255,.2)", color: "#fff", border: "1px solid rgba(255,255,255,.5)", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>📤 Compartir</button>
            <button onClick={() => setCelebrar(null)} style={{ padding: "13px 28px", borderRadius: 100, background: C.card, color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>¡Bien! 🤘</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SORTEOS DE LA COMUNIDAD (independientes de eventos) ─────────────────────
// Marcas y sponsors sortean premios sin necesidad de un evento; cualquier rider
// participa con un toque. v1: los crea el admin (Carlos) para sponsors/comercios.
// ─── SORTEO EN VIVO (pantalla para grabar y subir a redes) ───────────────────
// Tómbola animada: pasan los @usernames de los participantes cada vez más lento y
// clava en el ganador (que ya eligió el SERVER con sortear_sorteo_comunidad — la
// animación es puesta en escena, el resultado es real y queda persistido).
const SorteoEnVivoScreen = ({ sorteo, onClose, onSorteado }) => {
  const [fase, setFase] = useState("listo"); // listo | girando | ganador
  const [nombres, setNombres] = useState([]);
  const [nombreActual, setNombreActual] = useState("");
  const [ganador, setGanador] = useState(null);
  const [revelados, setRevelados] = useState([]); // varios ganadores: los que ya salieron
  const timerRef = useRef(null);
  const varios = (Number(sorteo.cantidad_ganadores) || 1) > 1;

  useEffect(() => {
    (async () => {
      const { data: raw } = await supabase.rpc("get_participantes_sorteo", { p_sorteo_id: sorteo.id }).then(r => r, () => ({ data: null }));
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      // Sin el RPC (o sin permiso) la tómbola gira con nombres enmascarados igual
      setNombres(Array.isArray(d) && d.length ? d.map(p => "@" + (p.username || "rider")) : ["@•••••", "@•••••", "@•••••"]);
    })();
    return () => clearInterval(timerRef.current);
  }, [sorteo.id]);

  const sortear = async () => {
    setFase("girando");
    // 1) El server elige y persiste el ganador
    const { data: raw, error } = await supabase.rpc("sortear_sorteo_comunidad", { p_sorteo_id: sorteo.id });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error || !r?.ganador_username) {
      alert("No se pudo sortear: " + (error?.message || r?.error || "sin participantes"));
      setFase("listo");
      return;
    }
    // 2) Avisar a todos los participantes (RPC opcional; si no existe todavía, no rompe)
    supabase.rpc("notificar_ganador_sorteo", { p_sorteo_id: sorteo.id }).then(() => {}, () => {});
    // 3) Puesta en escena: gira rápido ~4s, desacelera ~3s, clava en el ganador.
    // Con varios ganadores (Expo Moto: 10) los revela de a uno; del segundo en adelante
    // la vuelta es más corta para que el video no se haga eterno.
    const lista = Array.isArray(r.ganadores) && r.ganadores.length ? r.ganadores : [{ username: r.ganador_username, nombre: r.ganador_nombre }];
    const revelar = (i) => {
      const inicio = Date.now();
      const total = i === 0 ? 7000 : 3200, rapido = i === 0 ? 4000 : 1600;
      const girar = () => {
        const t = Date.now() - inicio;
        if (t > total) {
          setNombreActual("@" + lista[i].username);
          setRevelados(prev => [...prev, lista[i]]);
          if (i + 1 < lista.length) { timerRef.current = setTimeout(() => revelar(i + 1), 1100); return; }
          setGanador({ ...r, ganadores: lista });
          setFase("ganador");
          onSorteado?.(r);
          return;
        }
        setNombreActual(nombres[Math.floor(Math.random() * nombres.length)] || "@•••••");
        const delay = t < rapido ? 70 : 70 + ((t - rapido) / (total - rapido)) * 430; // 70ms → 500ms
        timerRef.current = setTimeout(girar, delay);
      };
      girar();
    };
    revelar(0);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: C.accent, color: "#f9f5f2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center", fontFamily: "Archivo, -apple-system, sans-serif" }}>
      {fase !== "girando" && (
        <button onClick={onClose} style={{ position: "absolute", top: "max(40px, calc(var(--sat) + 10px))", right: 18, width: 34, height: 34, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", color: "#f9f5f2", cursor: "pointer", fontSize: 16 }}>✕</button>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: C.muted, marginBottom: 6 }}>OLBOS® presenta</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15, marginBottom: 4 }}>{sorteo.titulo}</div>
      {sorteo.patrocinador && <div style={{ fontSize: 14, color: "#c9c3ba", marginBottom: 8 }}>{sorteo.patrocinador}</div>}
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 40 }}>🎟️ {sorteo.participantes || nombres.length} riders participando</div>

      {fase === "listo" && (
        <button onClick={sortear} style={{ padding: "18px 44px", borderRadius: 100, background: "#f9f5f2", color: C.text, border: "none", fontSize: 18, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em" }}>
          🎲 Sortear ahora
        </button>
      )}

      {fase === "girando" && (
        <>
          {varios && <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "#8A6D3B", marginBottom: 10 }}>Ganador {revelados.length + 1} de {sorteo.cantidad_ganadores}</div>}
          <div style={{ width: "100%", maxWidth: 340, padding: "34px 20px", borderRadius: 20, background: "rgba(249,245,242,.06)", border: "1px solid rgba(249,245,242,.15)" }}>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", minHeight: 36 }}>{nombreActual}</div>
          </div>
          {revelados.length > 0 && (
            <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 6, maxWidth: 360 }}>
              {revelados.map((g, i) => <span key={i} style={{ padding: "5px 10px", borderRadius: 100, background: "#f9f5f2", color: C.text, fontSize: 12.5, fontWeight: 800, animation: "fadeUp .4s ease" }}>🏆 @{g.username}</span>)}
            </div>
          )}
        </>
      )}

      {fase === "ganador" && ganador && Array.isArray(ganador.ganadores) && ganador.ganadores.length > 1 && (
        <div style={{ width: "100%", maxWidth: 360, animation: "fadeUp .5s ease" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "#8A6D3B", marginBottom: 10 }}>¡Tenemos {ganador.ganadores.length} ganadores!</div>
          <div style={{ padding: "14px 16px", borderRadius: 20, background: "#f9f5f2", color: C.text, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 10px", textAlign: "left" }}>
            {ganador.ganadores.map((g, i) => <div key={i} style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{g.username}</div>)}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 14, lineHeight: 1.6 }}>
            {sorteo.premio_por_ganador ? `Cada uno se lleva ${sorteo.premio_por_ganador}. ` : ""}Todos los participantes reciben el anuncio en la app.<br />Nos contactamos con los ganadores para coordinar la entrega.
          </div>
        </div>
      )}

      {fase === "ganador" && ganador && !(Array.isArray(ganador.ganadores) && ganador.ganadores.length > 1) && (
        <div style={{ width: "100%", maxWidth: 340, animation: "fadeUp .5s ease" }}>
          <div style={{ fontSize: 46, marginBottom: 10 }}>🏆</div>
          <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "#8A6D3B", marginBottom: 10 }}>¡Tenemos ganador!</div>
          <div style={{ padding: "26px 20px", borderRadius: 20, background: "#f9f5f2", color: C.text }}>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em" }}>@{ganador.ganador_username}</div>
            {ganador.ganador_nombre && <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>{ganador.ganador_nombre}</div>}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 14, lineHeight: 1.6 }}>
            Todos los participantes reciben el anuncio en la app.<br />Nos contactamos con el ganador para coordinar la entrega.
          </div>
          <button onClick={() => compartirPlacaSorteo({ premioNombre: sorteo.titulo, premioFoto: sorteo.foto_url, ganadorNombre: ganador.ganador_nombre, ganadorUsername: ganador.ganador_username, contexto: sorteo.patrocinador ? `Sorteo de ${sorteo.patrocinador}` : "Sorteo de Olbos", porQuien: null })}
            style={{ marginTop: 18, padding: "12px 24px", borderRadius: 100, background: "rgba(249,245,242,.12)", border: "1px solid rgba(249,245,242,.25)", color: "#f9f5f2", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Compartir placa del ganador
          </button>
        </div>
      )}

      <div style={{ position: "absolute", bottom: "max(26px, var(--sab))", fontSize: 11, color: "#5a564f", fontStyle: "italic" }}>— El camino es el destino</div>
    </div>
  );
};

// ─── 📣 ESPACIOS DE MARCA SELF-SERVICE (pedido de Carlos 02/03-sep-2026) ─────
// Cada espacio es un slot con capacidad y precio por semana. Vacío, se vende solo
// ("este espacio está disponible"); ocupado, muestra el anuncio y mide impresiones
// (una por rider por día, dedupe en el server) y clicks. Se compra con Mercado
// Pago (cobra la cuenta de Olbos, endpoint api/anuncio-pago) y se activa solo con
// el webhook, por el tiempo contratado. Sin planes, sin hablar con nadie.
// ON HOLD (decisión de Carlos 03-sep-2026): los espacios NO se venden todavía —
// primero crecer la comunidad y definir el modelo. Con false se ocultan los
// "Espacio disponible", el botón Anunciar/Destacarla y la compra; los anuncios ya
// activos (si los hubiera) y las novedades del carrusel se siguen mostrando.
const ESPACIOS_VENTA_ACTIVA = false;
const ESPACIOS_DEF = {
  feed: { nombre: "Sponsor del feed", desc: "Tu marca debajo del contador de km, en la portada social de todos los riders.", precio_semana: 25000, capacidad: 1 },
  pitstop: { nombre: "Destacado del Pit Stop", desc: "Arriba de todo en Servicios, donde los riders buscan talleres y negocios.", precio_semana: 15000, capacidad: 3 },
  mercado: { nombre: "Destacado del Mercado", desc: "Primero en Compra-Venta: motos, accesorios y repuestos.", precio_semana: 12000, capacidad: 2 },
  sorteo: { nombre: "Sorteo patrocinado", desc: "Ponés el premio: los riders ven tu anuncio para participar. Máximo 3 por semana en toda la app.", precio_semana: 20000, capacidad: 3 },
  // Publicación de moto destacada: la compra el vendedor desde su garage y su moto va primera en Compra-Venta
  moto: { nombre: "Moto destacada", desc: "Tu publicación primera en Compra-Venta, con la etiqueta Destacada, por el tiempo que elijas.", precio_semana: 5000, capacidad: 5 },
  // Portada: tarjeta intercalada en el carrusel de Eventos (lo primero que se ve al abrir la app)
  carrusel: { nombre: "Portada de Eventos", desc: "Una tarjeta tuya intercalada en el carrusel de la portada, lo primero que ve cada rider al abrir la app.", precio_semana: 30000, capacidad: 2 },
};
const DESC_SEMANAS = { 1: 0, 2: 0.1, 4: 0.2 };
const precioEspacio = (precioSemana, semanas) => Math.round(precioSemana * semanas * (1 - (DESC_SEMANAS[semanas] || 0)));
const anunciosCache = {};
const cargarAnunciosSlot = async (slot) => {
  const c = anunciosCache[slot];
  if (c && Date.now() - c.t < 5 * 60 * 1000) return c.lista;
  try {
    const { data: raw, error } = await supabase.rpc("anuncios_activos", { p_slot: slot });
    if (error) return null; // SQL sin correr: el espacio se comporta como vacío
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    const lista = Array.isArray(d) ? d : [];
    anunciosCache[slot] = { t: Date.now(), lista };
    return lista;
  } catch (e) { return null; }
};
const impresionesHechas = {};
const registrarAnuncioEvento = (id, tipo) => {
  if (!id) return;
  if (tipo === "imp") { if (impresionesHechas[id]) return; impresionesHechas[id] = true; }
  supabase.rpc("anuncio_evento", { p_anuncio: id, p_tipo: tipo }).then(() => {}, () => {});
};
// Un anuncio con imagen (flyer) se abre a pantalla completa con su botón de contacto;
// sin imagen va directo al link. (Hotel Amatista, primer anunciante por WhatsApp, 20-sep.)
const abrirLinkAnuncio = (a) => {
  if (a.imagen_url) { window.dispatchEvent(new CustomEvent("olbos-ver-anuncio", { detail: a })); return; }
  registrarAnuncioEvento(a.id, "click");
  if (a.link) window.open(a.link, "_blank", "noopener");
};
const VisorAnuncio = ({ a, onClose }) => {
  useBackGuard(true, onClose);
  const ir = () => { registrarAnuncioEvento(a.id, "click"); if (a.link) window.open(a.link, "_blank", "noopener"); };
  const esWa = /wa\.me|whatsapp/i.test(a.link || "");
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(17,17,17,.97)", display: "flex", flexDirection: "column" }}>
      <button onClick={onClose} style={{ position: "absolute", top: "max(46px, calc(var(--sat) + 14px))", right: 16, width: 38, height: 38, borderRadius: "50%", background: "rgba(249,245,242,.14)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
        <X size={19} strokeWidth={2} color="#F9F5F2" />
      </button>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "calc(var(--sat, 0px) + 60px) 12px 12px" }}>
        <ImgZoom src={cldThumb(a.imagen_url, 1200)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 14 }} />
      </div>
      <div style={{ padding: "0 16px calc(var(--sab, 0px) + 16px)" }}>
        {a.id && <div style={{ fontSize: 10, fontWeight: 700, color: "#D9B368", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 8, textAlign: "center" }}>Partner Olbos</div>}
        {a.link && (
          <button onClick={ir} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: "#F9F5F2", color: "#111111", fontSize: 15, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em" }}>
            {esWa ? "Escribirles por WhatsApp" : "Ver más"}
          </button>
        )}
      </div>
    </div>
  );
};
// "Link o WhatsApp": un número se vuelve wa.me, una URL sin protocolo recibe https
const normalizarLinkAnuncio = (txt) => {
  const t = String(txt || "").trim();
  if (!t) return null;
  const digitos = t.replace(/[^0-9]/g, "");
  if (/^[+0-9()\s-]+$/.test(t) && digitos.length >= 8) return `https://wa.me/${digitos.startsWith("54") ? digitos : "54" + digitos.replace(/^0/, "")}`;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^wa\.me\//i.test(t)) return "https://" + t;
  return "https://" + t;
};

// Tarjeta de un anuncio (misma pieza en los tres slots): sobria, monocromática, con
// etiqueta "Publicidad" — los riders tienen que saber qué es pago y qué no.
const TarjetaAnuncio = ({ a, onClick }) => (
  <div onClick={onClick} style={{ background: C.accent, borderRadius: 16, padding: 14, cursor: a.link ? "pointer" : "default", color: "#F9F5F2", display: "flex", gap: 14, alignItems: "center" }}>
    {a.imagen_url && (
      <div style={{ width: 62, height: 62, borderRadius: 14, background: C.card, overflow: "hidden", flexShrink: 0 }}>
        <img loading="lazy" decoding="async" src={cldOpt ? cldOpt(a.imagen_url, 300) : a.imagen_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 3 }}>Partner Olbos</div>
      <div style={{ fontSize: 15.5, fontWeight: 800, letterSpacing: "-0.01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{a.titulo}</div>
      {a.texto && <div style={{ fontSize: 12, color: "#c9c3ba", marginTop: 2, lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{a.texto}</div>}
    </div>
    {a.link && <ChevronRight size={16} color="#c9c3ba" style={{ flexShrink: 0 }} />}
  </div>
);

const EspacioAnuncio = ({ slot, alternativa = null, estilo = {} }) => {
  const [lista, setLista] = useState(undefined);
  const [comprar, setComprar] = useState(false);
  useEffect(() => {
    let ok = true;
    cargarAnunciosSlot(slot).then(l => { if (ok) setLista(l); });
    return () => { ok = false; };
  }, [slot]);
  const a = Array.isArray(lista) && lista.length ? lista[Math.floor(Date.now() / 10000) % lista.length] : null;
  useEffect(() => { if (a) registrarAnuncioEvento(a.id, "imp"); }, [a?.id]);
  if (lista === undefined) return null;
  if (a) return <div style={estilo}><TarjetaAnuncio a={a} onClick={() => abrirLinkAnuncio(a)} /></div>;
  if (alternativa) return <div style={estilo}>{alternativa}</div>;
  // En la app de las tiendas NO se vende el espacio: Apple (3.1.1) y Google exigen
  // su propio sistema de pago para servicios digitales comprados adentro de la app
  // (a Meta la obligaron con los posts promocionados). La contratación es web-only;
  // el anuncio ya activo se muestra igual en todos lados.
  if (esNativo() || !ESPACIOS_VENTA_ACTIVA) return null;
  const def = ESPACIOS_DEF[slot];
  return (
    <div style={estilo}>
      <div onClick={() => setComprar(true)} style={{ border: `1.5px dashed ${C.border}`, borderRadius: 14, padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, background: C.surface }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em" }}>Espacio disponible</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>Anunciá tu negocio acá <span style={{ fontWeight: 500, color: C.muted }}>· desde ${def.precio_semana.toLocaleString("es-AR")} por semana</span></div>
        </div>
        <ChevronRight size={15} color={C.muted} style={{ flexShrink: 0 }} />
      </div>
      {comprar && <ComprarEspacioModal slotInicial={slot} onClose={() => setComprar(false)} />}
    </div>
  );
};

// Compra de un espacio: espacio → duración → contenido → pagar con Mercado Pago.
// Sin aprobación manual: el webhook activa el anuncio cuando MP confirma el pago.
const ComprarEspacioModal = ({ slotInicial = null, base = null, ventaId = null, onClose }) => {
  const [paso, setPaso] = useState(slotInicial ? 2 : 1);
  const [slot, setSlot] = useState(slotInicial);
  const [espacios, setEspacios] = useState(null);
  const [semanas, setSemanas] = useState(1);
  const [desde, setDesde] = useState("");
  const [form, setForm] = useState({ titulo: base?.titulo || "", texto: base?.texto || "", imagen: base?.imagen_url || "", link: base?.link || "", premio: base?.premio || "" });
  const [subiendo, setSubiendo] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [sesion, setSesion] = useState(null);
  useBackGuard(true, onClose);
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSesion(session);
      const { data: raw, error } = await supabase.rpc("espacios_disponibles");
      if (error) { setEspacios(Object.entries(ESPACIOS_DEF).map(([s, d]) => ({ slot: s, ...d, sin_sql: true }))); return; }
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setEspacios(Array.isArray(d) ? d : []);
      // Precargar con mi negocio registrado (logo, nombre, WhatsApp) si no vino base
      if (!base && session?.user) {
        const { data: n } = await supabase.from("negocios").select("nombre, descripcion, logo_url, whatsapp").eq("user_id", session.user.id).maybeSingle();
        if (n) setForm(f => ({ ...f, titulo: f.titulo || n.nombre || "", texto: f.texto || (n.descripcion || "").slice(0, 120), imagen: f.imagen || n.logo_url || "", link: f.link || (n.whatsapp ? String(n.whatsapp) : "") }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const esp = espacios?.find(e => e.slot === slot) || (slot ? { slot, ...ESPACIOS_DEF[slot] } : null);
  useEffect(() => { if (esp && !desde) setDesde(esp.proxima_libre || new Date(Date.now() + 86400000).toISOString().slice(0, 10)); }, [esp?.slot]); // eslint-disable-line react-hooks/exhaustive-deps
  const precio = esp ? precioEspacio(Number(esp.precio_semana), semanas) : 0;
  const hasta = desde ? new Date(new Date(desde + "T12:00:00").getTime() + (semanas * 7 - 1) * 86400000).toISOString().slice(0, 10) : "";
  const fmt = (f) => f ? new Date(f + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : "";
  const subirImagen = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setForm(f => ({ ...f, imagen: j.secure_url }));
    } catch (e) { alert("No se pudo subir la imagen."); }
    setSubiendo(false);
  };
  const esSorteo = slot === "sorteo";
  const contenidoOk = form.titulo.trim() && (!esSorteo || form.premio.trim());
  const pagar = async () => {
    if (!sesion?.user) { alert("Iniciá sesión para contratar un espacio."); return; }
    if (pagando) return;
    setPagando(true);
    try {
      const { data: raw, error } = await supabase.rpc("crear_anuncio", {
        p_slot: slot, p_semanas: semanas, p_desde: desde,
        p_titulo: form.titulo.trim(), p_texto: form.texto.trim() || null, p_imagen: form.imagen || null,
        p_link: normalizarLinkAnuncio(form.link), p_premio: esSorteo ? form.premio.trim() : null,
        ...(ventaId ? { p_venta: ventaId } : {}), // moto destacada: el anuncio apunta a la publicación
      });
      const r = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (error || r?.error) { alert("No se pudo reservar el espacio: " + (error?.message || r?.error)); setPagando(false); return; }
      const res = await fetch(`${API_BASE}/api/anuncio-pago`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${sesion.access_token}` }, body: JSON.stringify({ anuncio_id: r.id }) });
      const j = await res.json().catch(() => ({}));
      if (!j.url) { alert("No se pudo iniciar el pago: " + (j.error || "intentá de nuevo")); setPagando(false); return; }
      window.open(j.url, "_blank");
      setPaso(5);
    } catch (e) { alert("No se pudo iniciar el pago."); }
    setPagando(false);
  };
  const inputS = { width: "100%", padding: "11px 13px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" };
  const label = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6, display: "block" };
  const btnPri = { width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer" };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9650, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: "100%", maxWidth: 430, maxHeight: "92vh", overflowY: "auto", background: C.bg, borderRadius: "20px 20px 0 0", padding: "18px 18px calc(24px + env(safe-area-inset-bottom, 0px))" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          {paso > 1 && paso < 5 && <button onClick={() => setPaso(p => (p === 2 && slotInicial) ? 2 : (p === 4 && slot === "moto") ? 2 : p - 1)} style={{ background: "none", border: "none", cursor: "pointer", color: C.text, padding: 0, display: "flex" }}><ChevronLeft size={20} /></button>}
          <div style={{ flex: 1, fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>
            {paso === 1 ? "📣 Anunciá en Olbos" : paso === 2 ? esp?.nombre : paso === 3 ? "Tu anuncio" : paso === 4 ? "Confirmar y pagar" : "Casi listo"}
          </div>
          <button onClick={onClose} style={{ width: 26, height: 26, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.muted, fontSize: 12 }}>✕</button>
        </div>

        {paso === 1 && (
          <>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>Elegí dónde querés aparecer. Se contrata por semana, se paga con Mercado Pago y queda activo solo. Ves las vistas y los contactos en tiempo real.</div>
            {espacios === null ? <div style={{ fontSize: 13, color: C.muted }}>Cargando…</div> : espacios.map(e => (
              <button key={e.slot} onClick={() => { setSlot(e.slot); setDesde(""); setPaso(2); }}
                style={{ width: "100%", textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 14px", marginBottom: 10, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{e.nombre}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.text, flexShrink: 0 }}>${Number(e.precio_semana).toLocaleString("es-AR")}<span style={{ fontWeight: 500, color: C.muted }}>/sem</span></div>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{e.desc || e.descripcion}</div>
                {e.proxima_libre && <div style={{ fontSize: 11.5, color: C.green, fontWeight: 700, marginTop: 5 }}>Disponible desde el {fmt(e.proxima_libre)}</div>}
              </button>
            ))}
          </>
        )}

        {paso === 2 && esp && (
          <>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>{esp.desc || esp.descripcion}</div>
            <span style={label}>Duración</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[1, 2, 4].map(n => (
                <button key={n} onClick={() => setSemanas(n)} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1px solid ${semanas === n ? C.accent : C.border}`, background: semanas === n ? C.accent : C.card, color: semanas === n ? "#fff" : C.text, cursor: "pointer" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800 }}>{n} semana{n > 1 ? "s" : ""}</div>
                  <div style={{ fontSize: 11, opacity: .8 }}>{DESC_SEMANAS[n] ? `-${DESC_SEMANAS[n] * 100}%` : "precio base"}</div>
                </button>
              ))}
            </div>
            <span style={label}>Empieza el</span>
            <input type="date" value={desde} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={e => setDesde(e.target.value)} style={{ ...inputS, marginBottom: 6 }} />
            {esp.proxima_libre && <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14 }}>Próxima fecha libre: {fmt(esp.proxima_libre)}. Si la que elegís está ocupada, te avisamos al confirmar.</div>}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 13, color: C.muted }}>{fmt(desde)} → {fmt(hasta)}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>${precio.toLocaleString("es-AR")}</div>
            </div>
            {/* la moto destacada no tiene contenido propio: usa la publicación tal cual */}
            <button onClick={() => setPaso(slot === "moto" ? 4 : 3)} disabled={!desde} style={{ ...btnPri, opacity: desde ? 1 : .5 }}>Siguiente</button>
          </>
        )}

        {paso === 3 && (
          <>
            {esSorteo && (
              <>
                <span style={label}>Premio</span>
                <input value={form.premio} onChange={e => setForm(f => ({ ...f, premio: e.target.value }))} placeholder="Ej: Casco LS2 Rookie" style={{ ...inputS, marginBottom: 12 }} />
              </>
            )}
            <span style={label}>{esSorteo ? "Quién lo sortea (tu marca o negocio)" : "Título"}</span>
            <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder={esSorteo ? "Ej: RodPro Indumentaria" : "Ej: Taller Motocanal — service en el día"} maxLength={60} style={{ ...inputS, marginBottom: 12 }} />
            <span style={label}>Texto corto</span>
            <textarea value={form.texto} onChange={e => setForm(f => ({ ...f, texto: e.target.value }))} rows={2} maxLength={140} placeholder={esSorteo ? "Qué hacés y por qué te tienen que conocer" : "Una frase: qué ofrecés, dónde, una promo"} style={{ ...inputS, resize: "none", marginBottom: 12 }} />
            <span style={label}>{esSorteo ? "Foto del premio" : "Logo o imagen"}</span>
            <label style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 12, border: `1.5px dashed ${C.border}`, cursor: "pointer", background: C.surface, marginBottom: 12 }}>
              {form.imagen ? <img loading="lazy" decoding="async" src={form.imagen} alt="" style={{ width: 46, height: 46, borderRadius: 10, objectFit: "cover" }} /> : <Camera size={20} color={C.muted} />}
              <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>{subiendo ? "Subiendo…" : form.imagen ? "Cambiar imagen" : "Subir imagen (cuadrada queda mejor)"}</span>
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendo} onChange={e => { subirImagen(e.target.files[0]); e.target.value = ""; }} />
            </label>
            <span style={label}>Link o WhatsApp (adónde va el que toca)</span>
            <input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="11 5555 5555 · instagram.com/tumarca · tusitio.com" style={{ ...inputS, marginBottom: 14 }} />
            <span style={label}>Así se va a ver</span>
            <div style={{ marginBottom: 16 }}><TarjetaAnuncio a={{ titulo: form.titulo || "Tu título", texto: form.texto, imagen_url: form.imagen, link: form.link }} onClick={() => {}} /></div>
            <button onClick={() => setPaso(4)} disabled={!contenidoOk || subiendo} style={{ ...btnPri, opacity: contenidoOk && !subiendo ? 1 : .5 }}>Siguiente</button>
          </>
        )}

        {paso === 4 && esp && (
          <>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 14px", marginBottom: 12, fontSize: 13, color: C.text, lineHeight: 1.8 }}>
              <div><strong>{esp.nombre}</strong> · {semanas} semana{semanas > 1 ? "s" : ""}</div>
              <div style={{ color: C.muted }}>Del {fmt(desde)} al {fmt(hasta)}</div>
              {esSorteo && <div style={{ color: C.muted }}>Premio: {form.premio}</div>}
              <div style={{ fontSize: 18, fontWeight: 900, marginTop: 4 }}>${precio.toLocaleString("es-AR")} <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>con Mercado Pago</span></div>
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
              Se activa solo cuando Mercado Pago confirma el pago y termina automáticamente al final del período. Olbos puede pausar un anuncio que no sea para riders (te devolvemos la plata).
              {esSorteo && " El sorteo lo realiza Olbos al terminar la semana entre los riders que participaron viendo tu anuncio; el premio lo entregás vos."}
            </div>
            <button onClick={pagar} disabled={pagando} style={{ ...btnPri, opacity: pagando ? .6 : 1 }}>{pagando ? "Abriendo Mercado Pago…" : "💳 Pagar con Mercado Pago"}</button>
          </>
        )}

        {paso === 5 && (
          <div style={{ textAlign: "center", padding: "12px 0 6px" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💳</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Terminá el pago en la pestaña de Mercado Pago</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>Apenas se acredite, tu espacio queda activo y te avisamos por la campanita. Las vistas y los contactos los seguís desde <strong>Mi negocio → Mis anuncios</strong>.</div>
            <button onClick={onClose} style={btnPri}>Listo</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Panel del anunciante: sus espacios con vistas, clicks, CTR, días restantes,
// pagar si quedó pendiente y renovar (mismo contenido, período nuevo).
const MisAnuncios = () => {
  const [lista, setLista] = useState(null);
  const [renovar, setRenovar] = useState(null);
  const [comprar, setComprar] = useState(false);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("mis_anuncios");
    if (error) { setLista([]); return; }
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const pagarPendiente = async (a) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API_BASE}/api/anuncio-pago`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ anuncio_id: a.id }) });
    const j = await res.json().catch(() => ({}));
    if (j.url) window.open(j.url, "_blank"); else alert("No se pudo iniciar el pago: " + (j.error || ""));
  };
  const fmt = (f) => f ? new Date(String(f).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : "";
  const hoy = new Date().toISOString().slice(0, 10);
  const estadoTxt = (a) => a.estado === "activo" ? (a.hasta < hoy ? "Terminado" : a.desde > hoy ? `Empieza el ${fmt(a.desde)}` : "Activo") : a.estado === "pendiente_pago" ? "Falta pagar" : a.estado === "pausado" ? "Pausado" : a.estado;
  if (lista === null) return null;
  // Contratar/pagar/renovar solo en la web (política de pagos de las tiendas); en la
  // app nativa el panel muestra las métricas y nada más
  const nativo = esNativo() || !ESPACIOS_VENTA_ACTIVA;
  if (nativo && lista.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Mis anuncios</div>
        {!nativo && <button onClick={() => setComprar(true)} style={{ padding: "6px 11px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>📣 Anunciar</button>}
      </div>
      {lista.length === 0 && <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>Todavía no contrataste ningún espacio. Aparecé en el feed, en el Pit Stop o en el Mercado, o sorteá algo entre los riders.</div>}
      {lista.map(a => {
        const ctr = a.impresiones ? Math.round((a.clicks / a.impresiones) * 1000) / 10 : 0;
        const activoHoy = a.estado === "activo" && a.desde <= hoy && a.hasta >= hoy;
        const diasRest = activoHoy ? Math.max(0, Math.round((new Date(a.hasta + "T12:00:00") - new Date(hoy + "T12:00:00")) / 86400000)) + 1 : null;
        return (
          <div key={a.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "11px 13px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ESPACIOS_DEF[a.slot]?.nombre || a.slot}</div>
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 100, flexShrink: 0, background: activoHoy ? `${C.green}18` : C.surface, color: activoHoy ? C.green : C.muted }}>{estadoTxt(a)}</span>
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{a.titulo} · {fmt(a.desde)} → {fmt(a.hasta)}{diasRest != null ? ` · ${diasRest} día${diasRest === 1 ? "" : "s"} más` : ""}</div>
            {a.estado === "activo" && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {[["Vistas", Number(a.impresiones || 0).toLocaleString("es-AR")], ["Contactos", Number(a.clicks || 0).toLocaleString("es-AR")], ["CTR", `${ctr}%`], ...(a.participantes != null ? [["Participan", a.participantes]] : [])].map(([k, v]) => (
                  <div key={k} style={{ flex: 1, background: C.surface, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            {!nativo && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {a.estado === "pendiente_pago" && <button onClick={() => pagarPendiente(a)} style={{ padding: "7px 12px", borderRadius: 100, background: C.accent, border: "none", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>💳 Pagar ahora</button>}
                {a.estado === "activo" && <button onClick={() => setRenovar(a)} style={{ padding: "7px 12px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>↻ Renovar</button>}
              </div>
            )}
          </div>
        );
      })}
      {comprar && <ComprarEspacioModal onClose={() => { setComprar(false); cargar(); }} />}
      {renovar && <ComprarEspacioModal slotInicial={renovar.slot} base={renovar} onClose={() => { setRenovar(null); cargar(); }} />}
    </div>
  );
};

// Pantalla del sorteo patrocinado: el rider ve el anuncio completo y recién
// después puede participar (es el trato con el anunciante). Cuenta como impresión;
// "Conocé más" cuenta como click.
const PantallaAnuncioSorteo = ({ anuncio: a, sorteo, onParticipar, onClose }) => {
  const [seg, setSeg] = useState(4);
  useEffect(() => { registrarAnuncioEvento(a.id, "imp"); const iv = setInterval(() => setSeg(s => (s <= 1 ? (clearInterval(iv), 0) : s - 1)), 1000); return () => clearInterval(iv); }, [a.id]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: C.accent, color: "#F9F5F2", overflowY: "auto" }}>
      <div style={{ maxWidth: 430, margin: "0 auto", padding: "max(48px, calc(var(--sat) + 12px)) 22px 40px", textAlign: "center" }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#8A6D3B", marginBottom: 14 }}>Sorteo patrocinado · Publicidad</div>
        {a.imagen_url && <img loading="lazy" decoding="async" src={cldOpt ? cldOpt(a.imagen_url, 800) : a.imagen_url} alt="" style={{ width: "100%", maxWidth: 300, aspectRatio: "1", objectFit: "cover", borderRadius: 20, marginBottom: 16 }} />}
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{sorteo?.titulo || a.premio}</div>
        <div style={{ fontSize: 13.5, color: "#c9c3ba", marginTop: 6 }}>lo sortea <strong style={{ color: "#F9F5F2" }}>{a.titulo}</strong></div>
        {a.texto && <p style={{ fontSize: 15, lineHeight: 1.65, color: "#e6e0d8", margin: "18px 0 0" }}>{a.texto}</p>}
        {a.link && (
          <button onClick={() => abrirLinkAnuncio(a)} style={{ marginTop: 18, padding: "11px 20px", borderRadius: 100, background: "rgba(249,245,242,.12)", border: "1px solid rgba(249,245,242,.3)", color: "#F9F5F2", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
            Conocé más de {a.titulo} →
          </button>
        )}
        <button onClick={onParticipar} disabled={seg > 0}
          style={{ marginTop: 26, width: "100%", padding: "15px", borderRadius: 14, background: seg > 0 ? "rgba(249,245,242,.25)" : "#F9F5F2", color: seg > 0 ? "#F9F5F2" : "#111", border: "none", fontSize: 16, fontWeight: 800, cursor: seg > 0 ? "default" : "pointer", transition: "background .3s" }}>
          {seg > 0 ? `🎟️ Participar en ${seg}…` : "🎟️ Participar del sorteo"}
        </button>
        <button onClick={onClose} style={{ marginTop: 14, background: "none", border: "none", color: "#c9c3ba", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cerrar</button>
      </div>
    </div>
  );
};

// Bases y condiciones de un sorteo. Se arman con los datos del propio sorteo.
// Legal: quien organiza y entrega el premio es el PATROCINADOR; Olbos solo difunde y
// ejecuta el sorteo. Participar es gratis y sin obligación de compra. Ver
// docs/RIESGOS_LEGALES.md (sorteos: Ley 20.630 y normas provinciales de juegos).
// Fecha del sorteo legible ("martes 10 de noviembre"); mediodía local para no caer en el día anterior
const fechaSorteoTxt = (f) => { try { return f ? new Date(String(f).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" }) : ""; } catch (e) { return ""; } };
const basesSorteo = (s) => {
  const patro = (s?.patrocinador || "").trim() || "el patrocinador del premio";
  const n = Number(s?.cantidad_ganadores) || 1;
  const fecha = fechaSorteoTxt(s?.fecha_sorteo);
  return `1. Quién organiza. El premio "${s?.titulo || ""}" es ofrecido y entregado por ${patro}. Olbos (olbos.ai) participa únicamente como plataforma de difusión y ejecución del sorteo: no vende el premio ni cobra nada por participar.${n > 1 ? ` Hay ${n} ganadores distintos${s?.premio_por_ganador ? ` y cada uno se lleva ${s.premio_por_ganador}` : ""}.` : ""}${fecha ? ` El sorteo se realiza el ${fecha}.` : ""}

2. Participación libre y gratuita. Participar no exige compra ni pago de ningún tipo. Pueden participar personas mayores de 18 años, con cuenta activa en Olbos y residentes en la República Argentina.

3. Cómo se participa. Con la cuenta creada, se participa tocando "Participar". En los sorteos destacados que se anuncian en el inicio, la cuenta queda inscripta al abrirlos. Corresponde una sola participación por persona: se excluyen cuentas duplicadas, falsas o creadas con datos de terceros.

4. Plazo. La participación se cierra en el momento del sorteo, que se realiza en la fecha anunciada en la app y en las redes de Olbos y de ${patro}.

5. Cómo se elige al ganador. Se elige al azar entre todas las participaciones válidas (con varios ganadores, cada persona puede ganar una sola vez), mediante un procedimiento aleatorio que se ejecuta en los servidores de Olbos. La animación que se muestra o transmite es una puesta en escena del resultado ya determinado. El resultado es inapelable.

6. Aviso y entrega. Al ganador se le avisa por notificación dentro de la app y por mensaje privado. Tiene 15 días corridos para responder; pasado ese plazo se sortea de nuevo entre el resto de los participantes. La entrega del premio la coordina ${patro}.

7. El premio no es canjeable por dinero ni transferible a otra persona.

8. Impuestos. Los tributos que correspondan al premio, incluido el impuesto de la Ley 20.630 cuando resulte aplicable, están a cargo de quien ofrece el premio.

9. Imagen. Quien gane autoriza que se difundan su nombre de usuario y, si presta su conformidad, fotos de la entrega, en la app y en las redes de Olbos y de ${patro}.

10. Datos personales. Los datos se tratan conforme a la Política de Privacidad de Olbos (olbos.ai/privacidad) y a la Ley 25.326.

11. Autorizaciones. Cuando la normativa de una provincia exija autorización previa para la promoción, obtenerla es responsabilidad de quien ofrece el premio.

12. Participar implica aceptar estas bases. Consultas: hola@olbos.ai`;
};

// Pide el teléfono una sola vez y lo guarda en la ficha del rider. Se usa donde
// alguien entra a un sorteo sin haber pasado por una inscripción: si gana, hay
// que poder avisarle.
const PedirTelefonoModal = ({ titulo, onListo, onClose }) => {
  const [tel, setTel] = useState("");
  const [guardando, setGuardando] = useState(false);
  const limpio = tel.replace(/[^\d]/g, "");
  const ok = limpio.length >= 8;
  const guardar = async () => {
    if (!ok || guardando) return;
    setGuardando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setGuardando(false); return; }
      await supabase.from("rider_fichas").upsert({ user_id: user.id, telefono: limpio, updated_at: new Date().toISOString() });
      onListo(limpio);
    } catch (e) { alert("No se pudo guardar el teléfono. Probá de nuevo."); }
    setGuardando(false);
  };
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "22px 20px calc(30px + var(--sab))" }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 6 }}>{titulo || "Un teléfono para avisarte"}</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
          Si salís sorteado te contactamos por WhatsApp. Queda guardado en tu ficha, no se muestra a nadie.
        </div>
        <input value={tel} onChange={e => setTel(e.target.value)} inputMode="tel" placeholder="Cód. país + número, ej: 5491112345678"
          style={{ width: "100%", padding: "12px 13px", borderRadius: 11, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.surface, boxSizing: "border-box", marginBottom: 12 }} />
        <button onClick={guardar} disabled={!ok || guardando}
          style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: ok ? "pointer" : "default", opacity: ok && !guardando ? 1 : 0.5 }}>
          {guardando ? "Guardando…" : "Guardar y participar"}
        </button>
      </div>
    </div>
  );
};

const SorteosComunidad = ({ session, isAdmin, requireAuth }) => {
  const [sorteos, setSorteos] = useState([]);
  const [pedirTel, setPedirTel] = useState(null); // sorteo que espera el teléfono
  const [verBases, setVerBases] = useState(null); // sorteo cuyas bases se están mirando
  // Sorteos patrocinados: anuncio por sorteo_id (para exigir ver el anuncio antes de participar)
  const [anunciosSorteo, setAnunciosSorteo] = useState({});
  const [verAnuncio, setVerAnuncio] = useState(null);
  useEffect(() => {
    cargarAnunciosSlot("sorteo").then(l => {
      const m = {}; (l || []).forEach(a => { if (a.sorteo_id) m[a.sorteo_id] = a; }); setAnunciosSorteo(m);
    });
  }, []);
  const [detalle, setDetalle] = useState(null);
  const [crear, setCrear] = useState(false);
  const [accion, setAccion] = useState(false);
  const [form, setForm] = useState({ titulo: "", patrocinador: "", foto: "" });
  const [subiendo, setSubiendo] = useState(false);
  const [sorteoVivo, setSorteoVivo] = useState(null); // pantalla de sorteo animada (admin, para grabar)
  const [verEntregados, setVerEntregados] = useState(false); // historial plegado
  const [idxCard, setIdxCard] = useState(0); // tarjeta visible del carrusel de sorteos abiertos
  // Equipo (admin o staff): crea sorteos desde la app, los muestra/oculta y los sortea (29-oct, pedido de Carlos)
  const [esStaff, setEsStaff] = useState(false);
  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from("perfiles").select("rol").eq("id", session.user.id).maybeSingle()
      .then(({ data }) => setEsStaff(data?.rol === "staff"), () => {});
  }, [session?.user?.id]);
  const esEquipo = !!isAdmin || esStaff;
  const cambiarVisible = async (s, visible) => {
    setAccion(true);
    const { data: raw, error } = await supabase.rpc("sorteo_visible", { p_id: s.id, p_visible: visible });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setAccion(false);
    if (error || r?.error) { alert("No se pudo cambiar: " + (error?.message || r?.error)); return; }
    setDetalle(d => d && { ...d, visible });
    cargar();
  };

  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("get_sorteos_comunidad");
    if (error) return; // RPC todavía no creado → sección oculta y listo
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(d)) setSorteos(d);
  };
  useEffect(() => { cargar(); }, []);

  // ── Sorteo DESTACADO (campaña de captación, ej. Motos Benito ago-2026) ──────
  // Se detecta por el patrocinador/título. Todo el que entra logueado queda
  // participando automáticamente ("creá tu cuenta y ya estás participando", como
  // se anuncia en el escenario) — una sola vez por sorteo, con marca en el teléfono.
  const destacado = sorteos.find(s => !s.ganador_username && /benito/i.test(`${s.patrocinador || ""} ${s.titulo || ""}`)) || null;
  useEffect(() => {
    if (!destacado || !session?.user) return;
    const marca = `olbos_sorteo_auto_${destacado.id}`;
    if (destacado.participo || localStorage.getItem(marca)) return;
    // La marca se graba recién cuando el RPC confirma: si falla, se reintenta al volver a entrar
    supabase.rpc("participar_sorteo", { p_sorteo_id: destacado.id }).then(({ error }) => {
      if (!error) { localStorage.setItem(marca, "1"); cargar(); }
    }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destacado?.id, session?.user?.id]);

  const participar = async (s, telListo = false) => {
    if (!session?.user) { alert("Iniciá sesión para participar del sorteo."); return; }
    // Sin un teléfono en la ficha no se participa: si gana, hay que poder avisarle
    if (!telListo) {
      try {
        const { data: fi } = await supabase.from("rider_fichas").select("telefono").eq("user_id", session.user.id).maybeSingle();
        if (!fi || !String(fi.telefono || "").replace(/[^\d]/g, "")) { setPedirTel(s); return; }
      } catch (e) { /* si no se pudo leer la ficha, no se bloquea la participación */ }
    }
    setAccion(true);
    const { error } = await supabase.rpc("participar_sorteo", { p_sorteo_id: s.id });
    if (error) alert("No se pudo registrar la participación: " + error.message);
    await cargar();
    setDetalle(d => d && { ...d, participo: true, participantes: (d.participantes || 0) + (d.participo ? 0 : 1) });
    setAccion(false);
  };

  const sortearGanador = async (s) => {
    const n = Number(s.cantidad_ganadores) || 1;
    if (!window.confirm(`¿Sortear ${n > 1 ? `los ${n} ganadores` : "el ganador"} de "${s.titulo}" entre ${s.participantes || 0} participantes?`)) return;
    setAccion(true);
    const { data: raw, error } = await supabase.rpc("sortear_sorteo_comunidad", { p_sorteo_id: s.id });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) alert("No se pudo sortear: " + (error?.message || r?.error));
    else supabase.rpc("notificar_ganador_sorteo", { p_sorteo_id: s.id }).then(() => {}, () => {});
    await cargar();
    if (r?.ganador_username) setDetalle(d => d && { ...d, ganador_username: r.ganador_username, ganador_nombre: r.ganador_nombre, ganadores: r.ganadores || d.ganadores });
    setAccion(false);
  };

  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setForm(f => ({ ...f, foto: j.secure_url }));
    } catch (e) { alert("No se pudo subir la foto."); }
    setSubiendo(false);
  };

  // Crear desde la app (equipo) por RPC: nace OCULTO, se revisa y se activa con "Mostrar a todos"
  const crearSorteo = async () => {
    if (!form.titulo.trim()) return;
    setAccion(true);
    const { data: raw, error } = await supabase.rpc("sorteo_crear", {
      p_titulo: form.titulo.trim(),
      p_patrocinador: (form.patrocinador || "").trim() || null,
      p_foto_url: form.foto || null,
      p_descripcion: (form.descripcion || "").trim() || null,
      p_cantidad: Math.max(1, Number(form.cantidad) || 1),
      p_premio_por_ganador: Number(form.cantidad) > 1 ? ((form.premioPorGanador || "").trim() || null) : null,
      p_fecha: form.fecha || null,
    });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) alert("No se pudo crear: " + (error?.message || r?.error));
    else {
      setCrear(false); setForm({ titulo: "", patrocinador: "", foto: "" }); await cargar();
      alert("Sorteo creado. Queda OCULTO hasta que lo actives: abrilo y tocá «Mostrar a todos».");
    }
    setAccion(false);
  };

  if (sorteos.length === 0 && !esEquipo) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Sorteos abiertos como TARJETA destacada (pedido de Matías, 30-oct): el premio es el protagonista,
          con cuenta regresiva y botón grande. Con varios, carrusel horizontal con puntitos.
          El detalle (sortear, placa, bases) vive en el modal de siempre. */}
      {(() => {
        const abiertos = sorteos.filter(s => !s.ganador_username);
        if (abiertos.length === 0) return null;
        const cuando = (s) => {
          if (!s.fecha_sorteo) return "";
          const hoy = new Date(); hoy.setHours(12, 0, 0, 0);
          const dias = Math.round((new Date(String(s.fecha_sorteo).slice(0, 10) + "T12:00:00") - hoy) / 86400000);
          const rel = dias < 0 ? "" : dias === 0 ? "Sortea hoy" : dias === 1 ? "Sortea mañana" : `Sortea en ${dias} días`;
          const f = fechaSorteoTxt(s.fecha_sorteo);
          return rel ? `${rel} · ${f}` : `Se sortea el ${f}`;
        };
        return (
          <div style={{ marginBottom: 12 }}>
            <div onScroll={e => { const el = e.currentTarget; setIdxCard(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}
              style={{ display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none" }}>
              {abiertos.map(s => (
                <div key={s.id} role="button" tabIndex={0}
                  onClick={() => { if (!session?.user && requireAuth) { requireAuth(); return; } setDetalle(s); }}
                  style={{ flex: "0 0 100%", scrollSnapAlign: "start", boxSizing: "border-box", background: C.accent, color: "#F9F5F2", borderRadius: 16, padding: "16px 16px 14px", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: "#C2A26B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 6 }}>
                    <Award size={13} strokeWidth={2} /> Sorteo{s.patrocinador ? ` · ${s.patrocinador}` : ""}
                    {s.visible === false && <span style={{ marginLeft: 4, padding: "1px 5px", borderRadius: 5, border: "1px solid rgba(249,245,242,.35)", letterSpacing: "0.06em" }}>Oculto</span>}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.titulo}</div>
                  <div style={{ fontSize: 12.5, color: "#c9c3ba", marginTop: 6, lineHeight: 1.4 }}>
                    {cuando(s) || "Participá gratis"}{session?.user ? ` · ${s.participantes || 0} anotados` : ""}
                  </div>
                  {s.participo ? (
                    <div style={{ marginTop: 12, padding: "12px", borderRadius: 12, background: "rgba(249,245,242,.12)", textAlign: "center", fontSize: 14, fontWeight: 800 }}>Ya estás participando ✓</div>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); if (!session?.user && requireAuth) { requireAuth(); return; } participar(s); }}
                      disabled={accion}
                      style={{ width: "100%", marginTop: 12, padding: "13px", borderRadius: 12, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: accion ? 0.6 : 1 }}>
                      Participar
                    </button>
                  )}
                </div>
              ))}
            </div>
            {abiertos.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }}>
                {abiertos.map((s, i) => <span key={s.id} style={{ width: 6, height: 6, borderRadius: 3, background: i === idxCard ? C.text : C.border }} />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* Los ya entregados, plegados en una línea. Nombrar al último ganador es lo que
          hace que alguien quiera estar atento al próximo: ver que le tocó a una persona
          de verdad, con su @usuario, vale más que cualquier cartel que diga "participá". */}
      {(() => {
        const entregados = sorteos.filter(s => s.ganador_username);
        if (entregados.length === 0) return null;
        const ultimo = entregados[0];
        return (
          <div style={{ marginBottom: 4 }}>
            <button onClick={() => setVerEntregados(v => !v)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 7, padding: "8px 2px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 11.5, color: C.muted, flex: 1, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                Ya entregamos {entregados.length} premio{entregados.length === 1 ? "" : "s"} · {ultimo.cantidad_ganadores > 1
                  ? <>el último lo ganaron <strong style={{ color: C.text, fontWeight: 700 }}>@{ultimo.ganador_username} y {ultimo.cantidad_ganadores - 1} más</strong></>
                  : <>el último lo ganó <strong style={{ color: C.text, fontWeight: 700 }}>@{ultimo.ganador_username}</strong></>}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, flexShrink: 0 }}>{verEntregados ? "Ocultar" : "Ver"}</span>
            </button>
            {verEntregados && entregados.map(s => (
              <button key={s.id} onClick={() => { if (!session?.user && requireAuth) { requireAuth(); return; } setDetalle(s); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 2px 7px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {s.patrocinador ? `${s.patrocinador}: ` : ""}{s.titulo}
                </span>
                <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{s.cantidad_ganadores > 1 ? `${s.cantidad_ganadores} ganadores` : `@${s.ganador_username}`}</span>
                <ChevronRight size={13} color={C.faint} style={{ flexShrink: 0 }} />
              </button>
            ))}
          </div>
        );
      })()}

      {esEquipo && (
        <button onClick={() => setCrear(true)}
          style={{ padding: "5px 10px", borderRadius: 8, background: "transparent", color: C.muted, border: `1px dashed ${C.border}`, fontSize: 11, fontWeight: 700, cursor: "pointer", marginBottom: 4 }}>
          + Nuevo sorteo
        </button>
      )}

      {/* pantalla de sorteo en vivo (admin, pensada para grabarse) */}
      {sorteoVivo && (
        <SorteoEnVivoScreen sorteo={sorteoVivo} onClose={() => { setSorteoVivo(null); cargar(); }} onSorteado={() => cargar()} />
      )}

      {/* Bases y condiciones del sorteo (se abre desde el detalle) */}
      {verBases && (
        <div onClick={e => e.target === e.currentTarget && setVerBases(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(17,17,17,.94)", zIndex: 1400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 460, background: C.card, borderRadius: "20px 20px 0 0", maxHeight: "88vh", overflowY: "auto", padding: "18px 20px calc(24px + var(--sab))" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>Bases y condiciones</h3>
              <button onClick={() => setVerBases(null)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>{verBases.titulo}{verBases.patrocinador ? ` · ofrecido por ${verBases.patrocinador}` : ""}</div>
            <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.65, whiteSpace: "pre-wrap", letterSpacing: "-0.005em" }}>{basesSorteo(verBases)}</div>
            <button onClick={() => setVerBases(null)}
              style={{ width: "100%", marginTop: 16, padding: "13px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* detalle del sorteo */}
      {detalle && (
        <div onClick={e => e.target === e.currentTarget && setDetalle(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(17,17,17,.92)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.accent, border: "1px solid rgba(249,245,242,.15)", borderRadius: 20, maxWidth: 360, width: "100%", padding: 22, textAlign: "center", color: "#F9F5F2" }}>
            {detalle.foto_url
              ? <img loading="lazy" decoding="async" src={cldOpt(detalle.foto_url, 700)} alt="" style={{ width: 170, height: 170, borderRadius: 18, objectFit: "cover", marginBottom: 14 }} />
              : <div style={{ fontSize: 64, marginBottom: 8 }}>🎁</div>}
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{detalle.titulo}</div>
            {detalle.patrocinador && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>ofrecido por {detalle.patrocinador}</div>}
            {(detalle.cantidad_ganadores > 1 || detalle.fecha_sorteo) && (
              <div style={{ fontSize: 13, color: "#F9F5F2", marginTop: 10, lineHeight: 1.5 }}>
                {detalle.cantidad_ganadores > 1 && <div><strong>{detalle.cantidad_ganadores} ganadores</strong>{detalle.premio_por_ganador ? ` · cada uno se lleva ${detalle.premio_por_ganador}` : ""}</div>}
                {detalle.fecha_sorteo && !detalle.ganador_username && <div style={{ color: C.muted }}>Se sortea el {fechaSorteoTxt(detalle.fecha_sorteo)}</div>}
              </div>
            )}
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 10 }}>{detalle.participantes || 0} rider{(detalle.participantes || 0) !== 1 ? "s" : ""} participando</div>
            {detalle.ganador_username && Array.isArray(detalle.ganadores) && detalle.ganadores.length > 1 ? (
              <>
                {detalle.gane && <div style={{ marginTop: 14, padding: "12px", borderRadius: 12, background: "rgba(47,125,79,.25)", fontSize: 15, fontWeight: 800, color: "#8FD6AC" }}>🏆 ¡Ganaste! Te vamos a contactar</div>}
                <div style={{ marginTop: 12, textAlign: "left", maxHeight: 220, overflowY: "auto", borderRadius: 12, background: "rgba(249,245,242,.06)", padding: "6px 4px" }}>
                  {detalle.ganadores.map((g, i) => (
                    <button key={g.username || i} onClick={() => compartirPlacaSorteo({ premioNombre: detalle.premio_por_ganador ? `${detalle.titulo} · ${detalle.premio_por_ganador}` : detalle.titulo, premioFoto: detalle.foto_url, ganadorNombre: g.nombre, ganadorUsername: g.username, contexto: detalle.patrocinador ? `Sorteo de ${detalle.patrocinador}` : "Sorteo abierto de Olbos", porQuien: null })}
                      title="Compartir la placa de este ganador"
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "transparent", border: "none", color: "#F9F5F2", cursor: "pointer", textAlign: "left" }}>
                      <span style={{ fontSize: 12, color: C.muted, width: 18 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>@{g.username}</span>
                      <span style={{ fontSize: 11, color: C.muted }}>placa</span>
                    </button>
                  ))}
                </div>
              </>
            ) : detalle.ganador_username ? (
              <>
                <div style={{ marginTop: 14, padding: "12px", borderRadius: 12, background: "rgba(47,125,79,.25)", fontSize: 15, fontWeight: 800, color: "#8FD6AC" }}>
                  🏆 {detalle.ganador_nombre || "@" + detalle.ganador_username}
                </div>
                <button onClick={() => compartirPlacaSorteo({ premioNombre: detalle.titulo, premioFoto: detalle.foto_url, ganadorNombre: detalle.ganador_nombre, ganadorUsername: detalle.ganador_username, contexto: detalle.patrocinador ? `Sorteo de ${detalle.patrocinador}` : "Sorteo abierto de Olbos", porQuien: null })}
                  style={{ marginTop: 12, width: "100%", padding: "12px", borderRadius: 12, background: "rgba(249,245,242,.12)", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  📤 Compartir placa
                </button>
              </>
            ) : detalle.participo ? (
              <div style={{ marginTop: 14, padding: "12px", borderRadius: 12, background: "rgba(249,245,242,.1)", fontSize: 14, fontWeight: 700 }}>✓ Estás participando — mucha suerte 🤞</div>
            ) : anunciosSorteo[detalle.id] ? (
              <button onClick={() => setVerAnuncio(anunciosSorteo[detalle.id])} disabled={accion}
                style={{ marginTop: 14, width: "100%", padding: "13px", borderRadius: 12, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: accion ? 0.6 : 1 }}>
                🎟️ Ver el sorteo y participar
              </button>
            ) : (
              <button onClick={() => participar(detalle)} disabled={accion}
                style={{ marginTop: 14, width: "100%", padding: "13px", borderRadius: 12, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: accion ? 0.6 : 1 }}>
                🎟️ Participar
              </button>
            )}
            {/* Bases y condiciones: obligatorias y visibles ANTES de participar */}
            <button onClick={() => setVerBases(detalle)}
              style={{ marginTop: 12, width: "100%", padding: "9px", borderRadius: 10, background: "transparent", color: C.muted, border: "none", fontSize: 12.5, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>
              Bases y condiciones
            </button>
            {esEquipo && detalle.visible !== undefined && (
              <button onClick={() => cambiarVisible(detalle, detalle.visible === false)} disabled={accion}
                style={{ marginTop: 10, width: "100%", padding: "12px", borderRadius: 12, background: detalle.visible === false ? "#F9F5F2" : "transparent", color: detalle.visible === false ? C.text : "#F9F5F2", border: detalle.visible === false ? "none" : "1px solid rgba(249,245,242,.3)", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: accion ? 0.6 : 1 }}>
                {detalle.visible === false ? "👁️ Mostrar a todos" : "🙈 Ocultar (solo lo ve el equipo)"}
              </button>
            )}
            {esEquipo && !detalle.ganador_username && (
              <button onClick={() => { const s = detalle; setDetalle(null); setSorteoVivo(s); }}
                style={{ marginTop: 10, width: "100%", padding: "12px", borderRadius: 12, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
                🎲 Sortear en vivo
              </button>
            )}
            {esEquipo && !detalle.ganador_username && (
              <button onClick={() => sortearGanador(detalle)} disabled={accion || !(detalle.participantes > 0)}
                style={{ marginTop: 10, width: "100%", padding: "12px", borderRadius: 12, background: "transparent", color: C.muted, border: "1px dashed rgba(249,245,242,.3)", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: detalle.participantes > 0 ? 1 : 0.5 }}>
                🎲 Sortear {detalle.cantidad_ganadores > 1 ? `${detalle.cantidad_ganadores} ganadores` : "ganador"} ({detalle.participantes || 0})
              </button>
            )}
            {isAdmin && (
              <button onClick={async () => {
                if (!window.confirm(`¿Eliminar el sorteo "${detalle.titulo}"? Se borran también las participaciones.`)) return;
                setAccion(true);
                const { error } = await supabase.from("sorteos").delete().eq("id", detalle.id);
                if (error) alert("No se pudo eliminar: " + error.message);
                else { setDetalle(null); cargar(); }
                setAccion(false);
              }} disabled={accion}
                style={{ marginTop: 10, width: "100%", padding: "11px", borderRadius: 12, background: "transparent", color: "#D98880", border: "1px solid rgba(179,55,44,.45)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                🗑️ Eliminar sorteo
              </button>
            )}
            <button onClick={() => setDetalle(null)} style={{ marginTop: 12, background: "none", border: "none", color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cerrar</button>
          </div>
        </div>
      )}

      {/* falta el teléfono en la ficha: se pide y recién ahí se participa */}
      {pedirTel && (
        <PedirTelefonoModal titulo="Un teléfono para avisarte si ganás" onClose={() => setPedirTel(null)}
          onListo={async () => { const s = pedirTel; setPedirTel(null); await participar(s, true); }} />
      )}
      {/* sorteo patrocinado: primero el anuncio, después participar */}
      {verAnuncio && detalle && (
        <PantallaAnuncioSorteo anuncio={verAnuncio} sorteo={detalle} onClose={() => setVerAnuncio(null)}
          onParticipar={async () => { const s = detalle; setVerAnuncio(null); await participar(s); }} />
      )}

      {/* crear sorteo (admin) */}
      {crear && (
        <div onClick={e => e.target === e.currentTarget && setCrear(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(32px + var(--sab))" }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 14 }}>🎁 Nuevo sorteo abierto</h2>
            <Input label="Premio" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Casco LS2 Rookie" />
            <Input label="Patrocinador (opcional)" value={form.patrocinador} onChange={e => setForm(f => ({ ...f, patrocinador: e.target.value }))} placeholder="Ej: RodPro / Expo Moto" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Input label="Cantidad de ganadores" value={form.cantidad || ""} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value.replace(/[^\d]/g, "").slice(0, 3) }))} placeholder="1" inputMode="numeric" />
              <Input label="Se sortea el (opcional)" type="date" value={form.fecha || ""} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </div>
            {Number(form.cantidad) > 1 && (
              <Input label="Qué se lleva cada ganador" value={form.premioPorGanador || ""} onChange={e => setForm(f => ({ ...f, premioPorGanador: e.target.value }))} placeholder="Ej: un par de entradas" />
            )}
            <Input label="Descripción (opcional)" value={form.descripcion || ""} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Qué se sortea, cuándo, dónde" />
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Foto del premio</div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 12, border: `1.5px dashed ${C.border}`, cursor: "pointer", background: C.surface }}>
                {form.foto ? <img loading="lazy" decoding="async" src={form.foto} alt="" style={{ width: 46, height: 46, borderRadius: 10, objectFit: "cover" }} /> : <Camera size={20} color={C.muted} />}
                <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>{subiendo ? "Subiendo…" : form.foto ? "Cambiar foto" : "Subir foto"}</span>
                <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendo} onChange={e => { subirFoto(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <button onClick={crearSorteo} disabled={accion || subiendo || !form.titulo.trim()}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.text, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: (!form.titulo.trim() || accion) ? 0.5 : 1 }}>
              {accion ? "Creando…" : "Crear sorteo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── NAV ─────────────────────────────────────────────────────────────────────
// Orden por importancia real para los cinco perfiles de usuario (rider, organizador,
// taller, marca, concesionario) — ago 2026. Eventos es la portada: es lo que está lleno,
// el motor de adquisición y lo único que ningún competidor tiene. El Social baja porque
// no puede competir con Instagram y no es la razón de entrada. Pit Stop sube al menú
// (estaba escondido en "Más") porque es la única puerta de talleres, marcas y
// concesionarios. Grupos baja: depende de masa crítica que todavía no hay.
const NAV_ITEMS = [
  { id: "eventos", Icon: Calendar,      label: "Eventos" },
  { id: "feed",    Icon: Camera,        label: "Comunidad" },
  { id: "perfil",  Icon: User,          label: "Perfil" },
  { id: "rutas",   Icon: Map,           label: "Mapa" },
  { id: "mercado", Icon: Wrench,        label: "Servicios" },
  { id: "mas",     Icon: MoreHorizontal, label: "Más" },
];

// El orden de la barra lo elige cada rider (se guarda en su perfil). "Más" queda
// siempre última: no es una sección, es el menú de todo lo demás.
const NAV_FIJA = "mas";
const ordenPorDefecto = () => NAV_ITEMS.filter(i => i.id !== NAV_FIJA).map(i => i.id);
// Tolerante a propósito: si mañana se agrega o se saca una sección, un orden guardado
// viejo sigue sirviendo — se respetan las que siguen existiendo y las nuevas se suman
// al final, en vez de romper la barra o volver todo al default.
const ordenValido = (guardado) => {
  const validos = ordenPorDefecto();
  const limpio = (Array.isArray(guardado) ? guardado : []).filter(id => validos.includes(id));
  const sinRepetir = limpio.filter((id, i) => limpio.indexOf(id) === i);
  const faltantes = validos.filter(id => !sinRepetir.includes(id));
  return [...sinRepetir, ...faltantes, NAV_FIJA];
};
const navOrdenada = (orden) => ordenValido(orden).map(id => NAV_ITEMS.find(i => i.id === id)).filter(Boolean);
const leerOrdenLocal = () => {
  try { return ordenValido(JSON.parse(localStorage.getItem("olbos_orden_nav") || "null")); }
  catch (e) { return ordenValido(null); }
};

// ─── AVISO DE NUEVA VERSIÓN ──────────────────────────────────────────────────
// ⚠️ RITUAL DE RELEASE:
//   1. En public/version.json: subir "web" (sale con el deploy, avisa a los de la web).
//      APP_VERSION se sincroniza SOLO desde ahí en cada build (scripts/sync-version.js,
//      enganchado como "prebuild"). NO editarlo a mano: se desincronizó tres versiones
//      seguidas —1.10.14, 1.10.15 y 1.10.16— y el cartel de actualizar les quedó clavado
//      a todos los de la web, porque el build se comparaba contra sí mismo.
//   2. Bumpear versionName/versionCode en android/app/build.gradle y la versión en Xcode.
//   3. Cuando la versión YA esté publicada en Play/App Store: subir "stores_android" y
//      "stores_ios" y actualizar "notas". Antes NO: los de la app nativa verían el aviso
//      y llegarían a una tienda que todavía no la tiene.
const APP_VERSION = "1.10.44";
// ── MONITOREO (09-oct-2026): hasta hoy nos enterábamos de las pantallas blancas y de
// los km inflados por WhatsApp. Esto manda cada error de JS y cada acción de uso a la
// base (tablas errores_app y eventos_uso, 30 y 90 días), sin datos personales: lo que
// el admin ve en "Salud" del panel. Nunca puede romper nada: todo en try y fire-and-forget.
const plataformaApp = () => { try { const p = window.Capacitor && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : "web"; return p && p !== "web" ? p : "web"; } catch (e) { return "web"; } };
const SESION_USO = (() => { try { let x = sessionStorage.getItem("olbos_sesion"); if (!x) { x = Math.random().toString(36).slice(2, 10) + Date.now().toString(36); sessionStorage.setItem("olbos_sesion", x); } return x; } catch (e) { return "s" + Date.now().toString(36); } })();
const _erroresEnviados = {};
const FEED_PAGINA = 50; // publicaciones por tipo en la primera carga del feed
// ── NOTIFICACIONES PUSH (documento de Matías, A6) ─────────────────────────────
// Cada aviso de la campanita (tabla notificaciones) también llega al teléfono: la base le
// avisa a /api/push y ese endpoint lo manda por Firebase Cloud Messaging a los dispositivos
// del usuario (tabla dispositivos_push). Plugin @capacitor-firebase/messaging: en Android y
// en iPhone devuelve directamente el token de FCM (en iOS convierte el de Apple adentro).
const guardarTokenPush = async (token) => {
  try {
    if (!token) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try { localStorage.setItem("olbos_push_token", token); } catch (e) { /* */ }
    const { error } = await supabase.rpc("registrar_dispositivo_push", { p_token: token, p_plataforma: Capacitor.getPlatform(), p_version: APP_VERSION });
    if (error) reportarError("push token: " + error.message, null, "registrarPush");
  } catch (e) { /* */ }
};
const registrarPush = async () => {
  try {
    if (!esNativo()) return;
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    if (!window._olbosPushListeners) {
      window._olbosPushListeners = true;
      // Firebase renueva el token cada tanto: se vuelve a guardar
      await FirebaseMessaging.addListener("tokenReceived", ({ token }) => { guardarTokenPush(token); });
      // Tocar la notificación abre la campanita (y lo que corresponda según el aviso)
      await FirebaseMessaging.addListener("notificationActionPerformed", (accion) => {
        const datos = (accion && accion.notification && accion.notification.data) || {};
        if (datos.tipo === "mensaje") {
          // Aviso de mensaje privado: va directo a Mensajes (y al chat de quien escribió)
          window.__olbosChatDeUsername = datos.from_username || "";
          window.__olbosAbrirMensajes = true;
          window.dispatchEvent(new CustomEvent("olbos-abrir-dm"));
          window.dispatchEvent(new CustomEvent("olbos-chat-de-username"));
          return;
        }
        window._olbosAbrirCampanita = true;
        window.dispatchEvent(new CustomEvent("olbos-abrir-campanita", { detail: datos }));
      });
      if (Capacitor.getPlatform() === "android") {
        try { await FirebaseMessaging.createChannel({ id: "olbos", name: "Olbos", description: "Avisos de tus rodadas, eventos y mensajes", importance: 4, visibility: 1, vibration: true }); } catch (e) { /* */ }
      }
    }
    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") perm = await FirebaseMessaging.requestPermissions();
    registrarUso("push_permiso", { estado: perm.receive, plataforma: Capacitor.getPlatform() });
    if (perm.receive !== "granted") return;
    const { token } = await FirebaseMessaging.getToken();
    await guardarTokenPush(token);
  } catch (e) {
    try { reportarError("push registro: " + ((e && e.message) || e), null, "registrarPush"); } catch (e2) { /* */ }
  }
};
// Al cerrar sesión el teléfono deja de recibir los avisos de esa cuenta
const olvidarPush = () => {
  try {
    const tk = localStorage.getItem("olbos_push_token");
    if (tk) { supabase.rpc("olvidar_dispositivo_push", { p_token: tk }).then(() => {}, () => {}); localStorage.removeItem("olbos_push_token"); }
  } catch (e) { /* */ }
};

const reportarError = (mensaje, stack, componente, extra) => {
  try {
    const m = String(mensaje || "").slice(0, 500);
    if (!m || /ResizeObserver loop|Loading chunk|ChunkLoadError|Script error\.?$|Network request failed|Failed to fetch$/i.test(m)) return;
    const k = m.slice(0, 80);
    const ahora = Date.now();
    if (_erroresEnviados[k] && ahora - _erroresEnviados[k] < 60000) return; // el mismo error, una vez por minuto
    _erroresEnviados[k] = ahora;
    supabase.auth.getSession().then(({ data }) => {
      supabase.from("errores_app").insert({
        user_id: data?.session?.user?.id || null, version: APP_VERSION, plataforma: plataformaApp(),
        url: String(window.location.href).slice(0, 300), mensaje: m,
        stack: String(stack || "").slice(0, 4000), componente: componente ? String(componente).slice(0, 2000) : null,
        extra: { ua: navigator.userAgent.slice(0, 300), ...(extra || {}) },
      }).then(() => {}, () => {});
    }, () => {});
  } catch (e) { /* nunca */ }
};
const registrarUso = (evento, datos) => {
  try {
    supabase.auth.getSession().then(({ data }) => {
      supabase.from("eventos_uso").insert({
        user_id: data?.session?.user?.id || null, sesion: SESION_USO, evento: String(evento).slice(0, 60),
        datos: datos || null, version: APP_VERSION, plataforma: plataformaApp(),
      }).then(() => {}, () => {});
    }, () => {});
  } catch (e) { /* nunca */ }
};
// ─── ORIGEN DE CADA CUENTA (tablero olbos.ai/estadisticas para la agencia) ───
// Al entrar con ?utm_source/utm_medium/utm_campaign/utm_content/utm_term o ?ref=
// se guarda en localStorage (gana el último link, vence a los 30 días) y se cuenta
// la visita una vez por día por teléfono. Cuando hay sesión, el RPC registrar_origen
// lo graba en perfiles.origen (solo cuentas de menos de 14 días y una sola vez).
// Corre a nivel módulo, antes que React: el efecto de ?ref= limpia la URL después.
const ORIGEN_CLAVE = "olbos_origen";
(() => {
  try {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const o = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"].forEach((k) => {
      const v = (sp.get(k) || "").trim();
      if (v) o[k] = v.slice(0, 100);
    });
    if (sp.get("fbclid")) o.fbclid = "1";
    if (sp.get("gclid")) o.gclid = "1";
    let host = "";
    try {
      const h = document.referrer ? new URL(document.referrer).hostname : "";
      if (h && !/(^|\.)olbos\.ai$|localhost|(^|\.)google\.[a-z.]+$|supabase|mercadopago/.test(h)) host = h.slice(0, 100);
    } catch (e) { /* */ }
    let previo = null;
    try { previo = JSON.parse(localStorage.getItem(ORIGEN_CLAVE) || "null"); } catch (e) { /* */ }
    if (previo && Date.now() - (previo.ts || 0) > 30 * 86400000) { previo = null; localStorage.removeItem(ORIGEN_CLAVE); }
    if (Object.keys(o).length) {
      o.landing = window.location.pathname.slice(0, 120);
      if (host) o.referrer = host;
      localStorage.setItem(ORIGEN_CLAVE, JSON.stringify({ ...o, ts: Date.now() }));
      const clave = "olbos_origen_visita_" + [o.utm_source, o.utm_medium, o.utm_campaign, o.ref].join("|").toLowerCase().slice(0, 160);
      const hoy = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(clave) !== hoy) {
        localStorage.setItem(clave, hoy);
        supabase.rpc("registrar_visita_origen", { p: o }).then(() => {}, () => {});
      }
    } else if (!previo && host) {
      localStorage.setItem(ORIGEN_CLAVE, JSON.stringify({ referrer: host, landing: window.location.pathname.slice(0, 120), ts: Date.now() }));
    }
  } catch (e) { /* la métrica nunca frena la entrada */ }
})();
let origenEnviando = false;
const enviarOrigen = () => {
  let o = null;
  try { o = JSON.parse(localStorage.getItem(ORIGEN_CLAVE) || "null"); } catch (e) { return; }
  if (!o || origenEnviando) return;
  origenEnviando = true;
  const { ts, ...datos } = o;
  datos.primera_visita = new Date(ts || Date.now()).toISOString();
  supabase.rpc("registrar_origen", { p: datos }).then(({ data, error }) => {
    origenEnviando = false;
    try {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      // ok (grabado, ya tenía o cuenta vieja) o vacío: listo. sin_perfil / error: reintenta en la próxima sesión
      if (!error && d && (d.ok || d.error === "vacio")) localStorage.removeItem(ORIGEN_CLAVE);
    } catch (e) { /* */ }
  }, () => { origenEnviando = false; });
};
try { supabase.auth.onAuthStateChange((_ev, s) => { if (s && s.user) setTimeout(enviarOrigen, 4000); }); } catch (e) { /* */ }
// Vistas por sección para el tablero (qué pantallas se abren más): una sola vez por
// sección y por sesión de la app, así no infla eventos_uso. Ej. "tab:garage", "pitstop:talleres".
const SECCIONES_VISTAS = {};
const registrarSeccion = (nombre) => {
  try {
    const k = String(nombre || "").slice(0, 60);
    if (!k || SECCIONES_VISTAS[k]) return;
    SECCIONES_VISTAS[k] = 1;
    registrarUso("seccion", { s: k });
  } catch (e) { /* */ }
};
// Toque en WhatsApp / llamar / web / Instagram de una ficha del Pit Stop (delegado en el contenedor)
const registrarContactoNegocio = (ev, negocioId) => {
  try {
    const a = ev && ev.target && ev.target.closest ? ev.target.closest("a") : null;
    if (!a || !negocioId) return;
    const h = String(a.getAttribute("href") || "");
    const via = /wa\.me|whatsapp/i.test(h) ? "whatsapp" : /^tel:/i.test(h) ? "telefono" : /instagram\.com/i.test(h) ? "instagram" : "web";
    registrarUso("negocio_contacto", { id: negocioId, via });
  } catch (e) { /* */ }
};
if (typeof window !== "undefined") {
  window.addEventListener("error", (ev) => { const e = ev && ev.error; reportarError((e && e.message) || (ev && ev.message), e && e.stack, "window.onerror", { linea: ev && ev.lineno, col: ev && ev.colno }); });
  window.addEventListener("unhandledrejection", (ev) => { const r = ev && ev.reason; reportarError((r && r.message) || String(r), r && r.stack, "unhandledrejection"); });
}
const cmpVersion = (a, b) => {
  const pa = String(a || "0").split(".").map(Number), pb = String(b || "0").split(".").map(Number);
  for (let i = 0; i < 3; i++) { const d = (pa[i] || 0) - (pb[i] || 0); if (d) return d; }
  return 0;
};
const AvisoActualizacion = () => {
  const [nueva, setNueva] = useState(null);
  const [notas, setNotas] = useState("");
  const ultimoChequeoRef = useRef(0);
  useEffect(() => {
    // El chequeo corría UNA sola vez, al montar. En la app nativa el proceso puede
    // quedar vivo días (el usuario vuelve desde "recientes" y el WebView no remonta),
    // así que la versión nueva salía a la tienda y ese usuario no se enteraba nunca.
    // Ahora también se re-chequea al volver a primer plano, como mucho 1 vez por hora.
    const chequear = async () => {
      if (Date.now() - ultimoChequeoRef.current < 3600000) return;
      ultimoChequeoRef.current = Date.now();
      try {
        // version.json viaja con el deploy: 'web' se actualiza sola en cada release y
        // 'stores' solo cuando la versión ya está publicada en Play/TestFlight, así
        // nadie ve el aviso antes de que exista la actualización que le corresponde.
        const r = await fetch(`${API_BASE}/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const cfg = await r.json();
        guardarNavV2Remoto(cfg); // el interruptor remoto del navegador nuevo viaja en el mismo archivo
        const esNativo = !!(window.Capacitor?.isNativePlatform?.());
        const plat = window.Capacitor?.getPlatform?.() || "web";
        // Cada tienda va a su ritmo: Play puede estar publicada y Apple todavía en
        // revisión. Con un solo campo, avisarle a los de Android le mentía a los de
        // iPhone. Si falta el específico, cae al general de siempre.
        const ultima = esNativo
          ? (plat === "ios" ? (cfg.stores_ios || cfg.stores) : (cfg.stores_android || cfg.stores))
          : cfg.web;
        if (!ultima) return;
        let vista = null;
        try { vista = localStorage.getItem("olbos_update_visto"); } catch (e) {}
        if (cmpVersion(ultima, APP_VERSION) > 0 && vista !== ultima) {
          setNueva(ultima);
          setNotas(cfg.notas || "");
        }
      } catch (e) { /* sin conexión, sin aviso */ }
    };
    chequear();
    const alVolver = () => { if (document.visibilityState === "visible") chequear(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, []);
  if (!nueva) return null;
  const plataforma = window.Capacitor?.getPlatform?.() || "web";
  const actualizar = () => {
    if (plataforma === "android") window.open("https://play.google.com/store/apps/details?id=ai.olbos.app", "_blank");
    else if (plataforma === "ios") window.open("https://apps.apple.com/app/id6794469275", "_blank");
    else {
      // En la web, recargar YA es la actualización. Se deja constancia antes de recargar:
      // si el navegador devuelve el mismo build cacheado, el rider no queda atrapado
      // viendo el mismo cartel una y otra vez (reporte de Carlos).
      try { localStorage.setItem("olbos_update_visto", nueva); } catch (e) { /* */ }
      window.location.reload();
    }
  };
  const cerrar = () => { try { localStorage.setItem("olbos_update_visto", nueva); } catch (e) {} setNueva(null); };
  return (
    <div className="fade-up" style={{ position: "fixed", top: "calc(var(--sat) + 8px)", left: 12, right: 12, zIndex: 2000, background: C.accent, color: "#F9F5F2", borderRadius: 14, padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 30px rgba(0,0,0,.35)", maxWidth: 406, margin: "0 auto" }}>
      <span style={{ fontSize: 18 }}>🚀</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "-0.01em" }}>Hay una versión nueva de Olbos</div>
        <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {notas || (plataforma === "web" ? "Recargá para tenerla al toque" : "Actualizala y mirá lo nuevo")}
        </div>
      </div>
      <button onClick={actualizar} style={{ padding: "8px 13px", borderRadius: 10, background: "#F9F5F2", color: C.text, border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
        {plataforma === "web" ? "Recargar" : "Actualizar"}
      </button>
      <button onClick={cerrar} style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(249,245,242,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <X size={13} color="#F9F5F2" strokeWidth={2.5} />
      </button>
    </div>
  );
};

const Nav = ({ active, setActive, session, novedades = {}, orden = null }) => (
  <nav style={{
    position: "fixed", bottom: 0, left: 0, right: 0, maxWidth: 430, margin: "0 auto",
    background: C.card, borderTop: `1px solid ${C.border}`,
    display: "flex", zIndex: 100, paddingBottom: "env(safe-area-inset-bottom)",
  }}>
    {navOrdenada(orden).map(i => (
      <button key={i.id} onClick={() => setActive(i.id)} style={{
        flex: 1, padding: "9px 0 7px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        borderTop: active === i.id ? `2px solid ${C.text}` : "2px solid transparent",
        color: active === i.id ? C.text : C.muted, transition: "all .2s",
        background: "transparent", border: "none", cursor: "pointer",
      }}>
        <div style={{ position: "relative" }}>
          <i.Icon size={18} strokeWidth={active === i.id ? 2.2 : 1.6} />
          {novedades[i.id] && (
            <div style={{
              position: "absolute", top: -3, right: -4,
              width: 7, height: 7, borderRadius: "50%",
              background: C.accent,
              border: "1.5px solid #fff",
            }} />
          )}
        </div>
        <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: 0 }}>{i.label}</span>
      </button>
    ))}
  </nav>
);

// ─── NOTICIAS ─────────────────────────────────────────────────────────────────
const Noticias = ({ onAI }) => {
  const [articulos, setArticulos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [categoria, setCategoria] = useState("todas");

  const categorias = [
    { id: "todas", label: "🏍️ Todas" },
    { id: "ar", label: "🇦🇷 Argentina" },
    { id: "es", label: "🇪🇸 España" },
    { id: "global", label: "🌎 Global" },
  ];

  useEffect(() => { cargarNoticias(); }, [categoria]);

  const cargarNoticias = async () => {
    setError(null);
    // 1) Pintar AL INSTANTE lo último que vimos (cache local) y refrescar de fondo.
    //    Sin esto, un caché frío del CDN te deja mirando esqueletos 15 segundos.
    const cacheKey = `olbos_news_${categoria}`;
    let pintoCache = false;
    try {
      const c = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (c?.articles?.length) { setArticulos(c.articles); setCargando(false); pintoCache = true; }
    } catch (e) { /* sin cache */ }
    if (!pintoCache) setCargando(true);
    // 2) Buscar lo fresco y actualizar cuando llegue
    try {
      const url = `${API_BASE}/api/news${categoria !== "todas" ? `?pais=${categoria}` : ""}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.articles && data.articles.length > 0) {
        const arts = data.articles.filter(a => a.title);
        setArticulos(arts);
        try { localStorage.setItem(cacheKey, JSON.stringify({ articles: arts.slice(0, 40), t: Date.now() })); } catch (e) {}
      } else if (!pintoCache) {
        setError("No se pudieron cargar las noticias");
      }
    } catch (e) {
      if (!pintoCache) setError("Error de conexión");
    }
    setCargando(false);
  };

  const tiempoAtras = (fecha) => {
    const diff = (Date.now() - new Date(fecha)) / 1000 / 60;
    if (diff < 60) return `hace ${Math.floor(diff)}m`;
    if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
    return `hace ${Math.floor(diff / 1440)}d`;
  };

  return (
    <PageWrap>
      <PageHeader seccion="Noticias" titulo="El mundo moto" onAI={onAI}>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
          {categorias.map(c => (
            <button key={c.id} onClick={() => setCategoria(c.id)}
              style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: categoria === c.id ? C.text : C.surface, color: categoria === c.id ? "#fff" : C.muted, border: `1px solid ${categoria === c.id ? "transparent" : C.border}`, cursor: "pointer" }}>
              {c.label}
            </button>
          ))}
        </div>
      </PageHeader>

      <div style={{ padding: "14px 18px 100px", overflowY: "auto" }}>
        {cargando ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ background: C.surface, borderRadius: 14, height: 100, animation: "pulse 1.5s infinite" }} />
            ))}
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📡</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>Sin conexión</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>{error}</div>
            <button onClick={cargarNoticias} style={{ padding: "10px 24px", borderRadius: 20, background: C.text, color: "#fff", fontWeight: 700, border: "none", cursor: "pointer" }}>Reintentar</button>
          </div>
        ) : articulos.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", textDecoration: "none", marginBottom: 14 }}>
            <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,.05)", display: "flex" }}>
              {(a.image || a.urlToImage) && (
                <div style={{ width: 110, flexShrink: 0, overflow: "hidden" }}>
                  <img loading="lazy" decoding="async" src={a.image || a.urlToImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={e => e.target.style.display = "none"} />
                </div>
              )}
              <div style={{ flex: 1, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {a.flag ? `${a.flag} ` : ""}{a.source?.name}
                  </span>
                  <span style={{ fontSize: 10, color: C.faint }}>·</span>
                  <span style={{ fontSize: 10, color: C.muted }}>{tiempoAtras(a.publishedAt)}</span>
                </div>
                <p style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.4, margin: 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {a.resumen_es || a.title}
                </p>
                {a.resumen_es && (
                  <p style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.35, margin: "4px 0 0", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {a.title}
                  </p>
                )}
              </div>
            </div>
          </a>
        ))}
      </div>
    </PageWrap>
  );
};

// ─── MÁS ──────────────────────────────────────────────────────────────────────
// ─── SOBRE OLBOS ─────────────────────────────────────────────────────────────
const SobreOlbos = ({ onBack }) => {
  const VERDE = "#111111";
  const VERDE_OSCURO = "#8A6D3B";
  return (
    <PageWrap>
      <div style={{ background: `linear-gradient(160deg, #f1ece6 0%, #fff 100%)`, padding: "56px 24px 32px", textAlign: "center", borderBottom: `1px solid ${VERDE}22`, position: "relative" }}>
        <button onClick={onBack} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
          <ChevronLeft size={20} strokeWidth={2} color="#111" />
        </button>
        <div style={{ width: 120, height: 120, borderRadius: 28, overflow: "hidden", margin: "0 auto 22px", boxShadow: `0 10px 36px ${VERDE}55` }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 900, color: C.text, letterSpacing: -1, marginBottom: 14 }}>Olbos</h1>
        <p style={{ fontSize: 19, color: C.muted, lineHeight: 1.5, fontStyle: "italic", fontWeight: 500 }}>El camino es el destino</p>
      </div>

      {/* MANIFIESTO — momento oscuro (alma de la marca, manual Bo Project) */}
      <div style={{ background: C.accent, color: "#f9f5f2", padding: "42px 26px 46px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.3em", color: C.muted, marginBottom: 24, textAlign: "center" }}>MANIFIESTO</div>
        <p style={{ fontSize: 16, lineHeight: 1.85, color: "#e9e3db", marginBottom: 18 }}>
          No me subo a la moto para escapar del mundo, me subo para encontrar esa plenitud genuina que solo aparece en la ruta. Los que estamos en esto sabemos que no nos define la marca que llevamos en el tanque; lo que de verdad nos une es el viaje, el camino compartido y la compañía de los que sienten lo mismo.
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.85, color: "#e9e3db", marginBottom: 24 }}>
          Dejamos atrás los clichés y la complicación de organizar una salida con herramientas genéricas. Por eso armamos Olbos. Es nuestro propio espacio, creado para fortalecer ese lazo que ya existe: para acompañarnos en cada rodada, cuidar nuestras motos avisándonos entre todos, y compartir ese taller de confianza que alguien de nosotros ya probó. Este ecosistema es nuestro, y funciona porque nos respaldamos mutuamente, cuidando al que rueda al lado y dándole tranquilidad a los que nos esperan en casa.
        </p>
        <div style={{ width: 44, height: 2, background: "#8a6d3b", margin: "2px 0 18px" }} />
        <p style={{ fontSize: 18, lineHeight: 1.5, color: "#f9f5f2", fontWeight: 700, letterSpacing: "-0.01em" }}>
          Olbos. Para quienes sabemos que el camino es el destino.
        </p>
      </div>

      <div style={{ padding: "28px 24px 100px" }}>

        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: VERDE, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 18 }}>🏛️</span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>¿Qué significa Olbos?</h2>
          </div>
          <div style={{ background: "#f9f5f2", borderRadius: 14, padding: 18, borderLeft: `4px solid ${VERDE}` }}>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              La palabra griega <strong>ὄλβος (ólbos)</strong> significa prosperidad, dicha y plenitud. No era solo riqueza material — era el término que Homero y Hesíodo usaban para describir a quienes vivían una vida verdaderamente plena, bendecida, sin carencias del alma.
            </p>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              Los griegos distinguían entre la <em>eudaimonía</em> — el florecimiento interior a través de la virtud — y el <em>olbos</em> — la dicha que viene de afuera: la salud, la libertad, el camino abierto, la compañía de quienes sienten lo mismo que vos.
            </p>
            <p style={{ fontSize: 14, color: VERDE_OSCURO, lineHeight: 1.8, fontWeight: 600 }}>
              Eso es lo que sentimos cuando arrancamos la moto y salimos a la ruta. Eso es Olbos.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>El saludo de los dos dedos</h2>
          </div>
          <div style={{ background: "#f9f5f2", borderRadius: 14, padding: 18, borderLeft: "4px solid #111111" }}>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              Cuando dos motos se cruzan y uno extiende la mano izquierda con dos dedos apuntando al asfalto, está pasando algo que tiene casi 80 años de historia.
            </p>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              El origen es la <strong>Segunda Guerra Mundial</strong>. Los mensajeros del ejército británico cruzaban ciudades bombardeadas en moto llevando órdenes. Era una misión de alto riesgo — muchos no volvían. Cuando dos de ellos se cruzaban en la ruta, se saludaban con una "V": <em>seguimos vivos, seguimos rodando</em>.
            </p>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              En los 70, el campeón Barry Sheene lo popularizó cruzando las metas con los dedos en alto. Desde entonces se convirtió en el código universal entre motociclistas. Los dedos apuntan hacia abajo porque simbolizan <strong>las dos ruedas tocando el asfalto</strong> — el vínculo compartido de estar en la ruta.
            </p>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, fontWeight: 600 }}>
              Ese gesto es lo que el logo de Olbos representa. Es lo que le deseamos a cada rider: que siempre tenga las dos ruedas en el asfalto, y que el camino que venga sea bueno.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 18 }}>🛣️</span>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>El camino es el destino</h2>
          </div>
          <div style={{ background: "#f9f5f2", borderRadius: 14, padding: 18, borderLeft: "4px solid #111111" }}>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 12 }}>
              Los riders saben algo que el resto del mundo tarda en aprender: no se trata de llegar. Se trata de lo que pasa en el camino — la curva que no esperabas, el paisaje que te corta el aliento, el taller donde te ayudó alguien que no conocías, la charla con otro rider parado en la banquina.
            </p>
            <p style={{ fontSize: 15, color: C.text, lineHeight: 1.8, fontWeight: 700, fontStyle: "italic", textAlign: "center", padding: "8px 0" }}>
              "El camino es el destino."
            </p>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8 }}>
              Eso es Olbos. No una app de mapas. No una red social más. Un lugar donde la comunidad que vive esto se encuentra, comparte y crece.
            </p>
          </div>
        </div>

        {/* Contacto con la comunidad */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Hablemos</h2>
          </div>
          <div style={{ background: "#f9f5f2", borderRadius: 14, padding: 18, borderLeft: "4px solid #25D366" }}>
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.8, marginBottom: 14 }}>
              Olbos se construye día a día con la comunidad. Si tenés una idea, un bug, una sugerencia, una historia, o simplemente querés decir hola — escribime por WhatsApp. Acá hay alguien del otro lado, no un bot.
            </p>
            <button onClick={() => {
              const msg = encodeURIComponent("Hola Charly, quería contarte algo sobre Olbos");
              window.open(`https://wa.me/5491130718112?text=${msg}`, "_blank", "noopener,noreferrer");
            }}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 12,
                background: "#25D366", color: "#fff",
                fontSize: 14, fontWeight: 600, border: "none",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Escribime por WhatsApp
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "20px 0", borderTop: `1px solid ${C.border}` }}>
          <p style={{ fontSize: 12, color: VERDE_OSCURO, fontWeight: 600, fontStyle: "italic" }}>El camino es el destino</p>
        </div>
      </div>
    </PageWrap>
  );
};

// ─── PRODUCTOS RECOMENDADOS ──────────────────────────────────────────────────
// Listas curadas de Mercado Libre. Cada lista es un link a una página oficial
// del programa de afiliados de ML, generada por el admin desde su panel.

// Los productos de la lista, ADENTRO de la app (pedido de Carlos 03-sep): el
// endpoint api/ml-lista lee la página pública de la lista de afiliados y devuelve
// las fichas; el link de cada producto lleva el matt_tool de afiliado, así que la
// comisión se acredita igual. Solo el pago final pasa por Mercado Libre.
const ProductosML = ({ onAbrirLista }) => {
  const [datos, setDatos] = useState(undefined); // undefined=cargando, null=error
  useEffect(() => {
    let ok = true;
    fetch(`${API_BASE}/api/ml-lista`).then(r => r.json()).then(j => { if (ok) setDatos(j && j.ok ? j.listas : null); }).catch(() => { if (ok) setDatos(null); });
    return () => { ok = false; };
  }, []);
  if (datos === undefined) return <div style={{ padding: "18px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando productos…</div>;
  if (!datos) return null;
  const fmtPrecio = (p, m) => p == null ? "" : `${m === "ARS" ? "$" : m + " "}${Math.round(p).toLocaleString("es-AR")}`;
  return (
    <div style={{ marginBottom: 18 }}>
      {datos.filter(l => l.items && l.items.length).map(l => (
        <div key={l.id} style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>{l.titulo}</div>
            <button onClick={() => onAbrirLista(l)} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 }}>Ver en Mercado Libre →</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {l.items.map(it => (
              <div key={it.id} onClick={() => { registrarUso("ml_producto_click", { id: it.id, titulo: String(it.titulo || "").slice(0, 80), lista: l.titulo, rating: it.rating, resenas: it.resenas, precio: it.precio }); window.open(it.link, "_blank", "noopener,noreferrer"); }}
                style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", cursor: "pointer" }}>
                <div style={{ aspectRatio: "1", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {it.imagen && <img src={it.imagen} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
                </div>
                <div style={{ padding: "9px 10px 11px" }}>
                  <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 34 }}>{it.titulo}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginTop: 5, letterSpacing: "-0.01em" }}>{fmtPrecio(it.precio, it.moneda)}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
                    {it.rating != null && <span>★ {it.rating}{it.resenas ? ` (${it.resenas})` : ""}</span>}
                    {it.envio_gratis && <span style={{ color: C.green, fontWeight: 700 }}>Envío gratis</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const Productos = ({ session, isAdmin }) => {
  const [listas, setListas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [editando, setEditando] = useState(null); // null = crear nueva, objeto = editar
  const [showSugerir, setShowSugerir] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);
  const [cargandoSugerencias, setCargandoSugerencias] = useState(false);

  const cargarListas = async () => {
    setCargando(true);
    const { data } = await supabase.from("mercadolibre_listas")
      .select("*")
      .eq("activo", true)
      .order("orden", { ascending: true })
      .order("created_at", { ascending: false });
    setListas(data || []);
    setCargando(false);
  };

  // Admin ve también las inactivas
  const cargarTodas = async () => {
    setCargando(true);
    const { data } = await supabase.from("mercadolibre_listas")
      .select("*")
      .order("orden", { ascending: true })
      .order("created_at", { ascending: false });
    setListas(data || []);
    setCargando(false);
  };

  const cargarSugerencias = async () => {
    setCargandoSugerencias(true);
    const { data } = await supabase.from("producto_sugerencias")
      .select("*")
      .order("created_at", { ascending: false });
    setSugerencias(data || []);
    setCargandoSugerencias(false);
  };

  useEffect(() => {
    if (showAdmin && isAdmin) {
      cargarTodas();
      cargarSugerencias();
    } else {
      cargarListas();
    }
  }, [showAdmin]);

  const abrirLista = async (lista) => {
    // Incrementar contador de clicks (no esperamos respuesta). Sin .then() el builder de
    // supabase nunca ejecutaba el update; el registro en eventos_uso es lo que mide el tablero.
    supabase.from("mercadolibre_listas")
      .update({ clicks_count: (lista.clicks_count || 0) + 1 })
      .eq("id", lista.id).then(() => {}, () => {});
    registrarUso("ml_lista_click", { id: lista.id, titulo: String(lista.titulo || "").slice(0, 80) });
    window.open(lista.link, "_blank", "noopener,noreferrer");
  };

  return (
    <PageWrap>
      {/* Header */}
      <div style={{ padding: "var(--header-pt) 24px 18px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Productos</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: C.text,
            letterSpacing: "-0.03em", lineHeight: 1.1,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}>Productos</h1>
          {isAdmin && (
            <button onClick={() => setShowAdmin(!showAdmin)} style={{
              padding: "6px 12px", borderRadius: 100,
              background: showAdmin ? "#111111" : C.surface,
              color: showAdmin ? "#fff" : "#111111",
              border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: 500, letterSpacing: "-0.005em",
            }}>
              {showAdmin ? "Vista pública" : "Admin"}
            </button>
          )}
        </div>
        <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
          Recomendaciones curadas para riders. Los links te llevan a Mercado Libre.
        </p>
      </div>

      <div style={{ padding: "20px 18px 100px" }}>
        {/* Botón "Sugerir un producto" — siempre visible, no en modo admin */}
        {!showAdmin && (
          <button onClick={() => {
            if (!session) {
              alert("Tenés que iniciar sesión para sugerir un producto");
              return;
            }
            setShowSugerir(true);
          }} style={{
            width: "100%", padding: "14px", borderRadius: 14,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer", marginBottom: 18,
            fontSize: 14.5, fontWeight: 500, letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "background .15s ease",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#111111"; }}>
            <Plus size={16} strokeWidth={2} /> Sugerir un producto
          </button>
        )}

        {/* Los productos de las listas, acá adentro (foto, precio, reseñas, envío) */}
        {!showAdmin && <ProductosML onAbrirLista={abrirLista} />}

        {/* Aviso */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: "12px 14px", marginBottom: 18,
        }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
            <Info size={14} strokeWidth={1.8} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5, letterSpacing: "-0.005em", marginBottom: 6, fontWeight: 500 }}>
                No son productos vendidos por Olbos.
              </p>
              <p style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.55, letterSpacing: "-0.005em", marginBottom: 6 }}>
                Si tenés un local y querés que aparezcan tus productos en una lista, contactate con nosotros y armamos una lista especial con todo tu catálogo de Mercado Libre.
              </p>
              <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em", fontStyle: "italic" }}>
                Comprando a través de estas listas ayudás al mantenimiento de la app.
              </p>
            </div>
          </div>
          <button onClick={() => {
            const msg = encodeURIComponent("Hola, tengo un local y me interesa armar una lista de productos en Olbos. ¿Cómo lo hacemos?");
            window.open(`https://wa.me/5491130718112?text=${msg}`, "_blank", "noopener,noreferrer");
          }} style={{
            width: "100%", padding: "10px 0", borderRadius: 10,
            background: "#25D366", color: "#fff",
            border: "none", cursor: "pointer",
            fontSize: 12.5, fontWeight: 500, letterSpacing: "-0.005em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Contactanos por WhatsApp
          </button>
        </div>

        {/* Sugerencias pendientes (solo admin) */}
        {isAdmin && showAdmin && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>
                Sugerencias de la comunidad
                {sugerencias.filter(s => s.estado === "pendiente").length > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 600,
                    padding: "2px 8px", borderRadius: 100,
                    background: "#FF3B30", color: "#fff",
                    marginLeft: 8, letterSpacing: "0.02em",
                  }}>
                    {sugerencias.filter(s => s.estado === "pendiente").length}
                  </span>
                )}
              </h3>
            </div>
            {cargandoSugerencias ? (
              <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 12 }}>Cargando…</div>
            ) : sugerencias.length === 0 ? (
              <div style={{
                padding: "14px 16px", background: C.surface,
                border: `1px solid ${C.border}`, borderRadius: 12,
                fontSize: 12, color: C.muted, textAlign: "center",
              }}>
                Sin sugerencias todavía
              </div>
            ) : (
              <div style={{
                background: C.card, borderRadius: 12,
                border: `1px solid ${C.border}`, overflow: "hidden",
              }}>
                {sugerencias.map((s, i) => (
                  <div key={s.id} style={{
                    padding: "12px 14px",
                    borderBottom: i < sugerencias.length - 1 ? `1px solid ${C.border}` : "none",
                    background: s.estado === "pendiente" ? `${C.gold}14` : C.card,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 2, letterSpacing: "-0.01em" }}>
                          {s.tipo === "tienda" ? "Sugerencia de tienda" : "Sugerencia de producto"}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>
                          de @{s.username || "rider"} · {new Date(s.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 9, fontWeight: 600,
                        padding: "2px 7px", borderRadius: 100,
                        background: s.estado === "pendiente" ? "#FFB800" : s.estado === "agregada" ? "#27AE60" : C.muted,
                        color: "#fff", letterSpacing: "0.04em", textTransform: "uppercase", flexShrink: 0,
                      }}>
                        {s.estado}
                      </span>
                    </div>
                    <a href={s.link} target="_blank" rel="noopener noreferrer" style={{
                      display: "block",
                      fontSize: 11.5, color: C.text, textDecoration: "none",
                      letterSpacing: "-0.005em", marginBottom: 6,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{s.link}</a>
                    {s.comentario && (
                      <div style={{
                        padding: "8px 10px", background: C.surface, borderRadius: 8,
                        fontSize: 11.5, color: C.text, lineHeight: 1.4, marginBottom: 8,
                        letterSpacing: "-0.005em",
                      }}>"{s.comentario}"</div>
                    )}
                    {s.estado === "pendiente" && (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={async () => {
                          await supabase.from("producto_sugerencias")
                            .update({ estado: "agregada", updated_at: new Date().toISOString() })
                            .eq("id", s.id);
                          cargarSugerencias();
                        }} style={{
                          flex: 1, padding: "6px 0", borderRadius: 8,
                          background: "#27AE60", color: "#fff",
                          border: "none", cursor: "pointer",
                          fontSize: 11, fontWeight: 500, letterSpacing: "-0.005em",
                        }}>
                          ✓ Marcar como agregada
                        </button>
                        <button onClick={async () => {
                          await supabase.from("producto_sugerencias")
                            .update({ estado: "rechazada", updated_at: new Date().toISOString() })
                            .eq("id", s.id);
                          cargarSugerencias();
                        }} style={{
                          flex: 1, padding: "6px 0", borderRadius: 8,
                          background: C.surface, color: C.text,
                          border: "none", cursor: "pointer",
                          fontSize: 11, fontWeight: 500, letterSpacing: "-0.005em",
                        }}>
                          Descartar
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm("¿Eliminar esta sugerencia?")) return;
                          await supabase.from("producto_sugerencias").delete().eq("id", s.id);
                          cargarSugerencias();
                        }} style={{
                          padding: "6px 10px", borderRadius: 8,
                          background: "#FFF1F0", color: "#FF3B30",
                          border: "none", cursor: "pointer",
                        }}>
                          <Trash2 size={11} strokeWidth={2} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Botón admin para crear nueva */}
        {isAdmin && showAdmin && (
          <button onClick={() => setEditando({})} style={{
            width: "100%", padding: "12px", borderRadius: 12,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer", marginBottom: 16,
            fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <Plus size={16} strokeWidth={2} /> Agregar nueva lista
          </button>
        )}

        {/* Estado: cargando */}
        {cargando && (
          <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        )}

        {/* Estado: vacío */}
        {!cargando && listas.length === 0 && (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: C.surface,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <ShoppingBag size={24} strokeWidth={1.5} color={C.muted} />
            </div>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>Próximamente</h3>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
              Estamos curando las mejores recomendaciones para riders. Pronto tendrás acceso a equipamiento, accesorios y herramientas seleccionadas.
            </p>
          </div>
        )}

        {/* Lista de listas: solo para el admin, que las administra. Al rider ya le
           mostramos los productos directo arriba, así que el recuadro era redundante
           (pedido de Carlos) y encima su descripción afirmaba que probamos todos los
           productos, cosa que no es cierta. */}
        {showAdmin && !cargando && listas.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {listas.map(lista => (
              <div key={lista.id} style={{
                background: C.card,
                borderRadius: 14,
                border: lista.activo ? `1px solid ${C.border}` : `1px solid ${C.border}`,
                overflow: "hidden",
                opacity: lista.activo ? 1 : 0.55,
                position: "relative",
              }}>
                <div onClick={() => abrirLista(lista)} style={{ cursor: "pointer" }}>
                  {/* Foto opcional */}
                  {lista.foto_url && (
                    <div style={{
                      width: "100%", aspectRatio: "16 / 9",
                      background: `url(${lista.foto_url}) center/cover #f1ece6`,
                    }} />
                  )}

                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <h3 style={{
                        fontSize: 16, fontWeight: 600, color: C.text,
                        letterSpacing: "-0.015em", lineHeight: 1.25,
                        flex: 1,
                      }}>{lista.titulo}</h3>
                      <div style={{
                        flexShrink: 0,
                        width: 28, height: 28, borderRadius: "50%",
                        background: C.surface,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <ChevronRight size={14} strokeWidth={2} color="#111111" />
                      </div>
                    </div>

                    {lista.descripcion && (
                      <p style={{
                        fontSize: 13, color: C.muted,
                        letterSpacing: "-0.005em", lineHeight: 1.5,
                        marginBottom: 8,
                      }}>{lista.descripcion}</p>
                    )}

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 500,
                        padding: "3px 8px", borderRadius: 100,
                        background: `${C.gold}14`, color: C.gold,
                        letterSpacing: "0.02em",
                      }}>
                        Mercado Libre
                      </span>
                      {!lista.activo && (
                        <span style={{
                          fontSize: 10, fontWeight: 500,
                          padding: "3px 8px", borderRadius: 100,
                          background: C.surface, color: C.muted,
                          letterSpacing: "0.02em",
                        }}>
                          Inactiva
                        </span>
                      )}
                      {isAdmin && showAdmin && lista.clicks_count > 0 && (
                        <span style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>
                          {lista.clicks_count} clicks
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Acciones admin */}
                {isAdmin && showAdmin && (
                  <div style={{
                    display: "flex", gap: 4,
                    padding: "8px 10px 10px",
                    borderTop: `1px solid ${C.border}`,
                  }}>
                    <button onClick={() => setEditando(lista)} style={{
                      flex: 1, padding: "7px 0", borderRadius: 8,
                      background: C.surface, border: "none",
                      color: C.text, fontSize: 12, fontWeight: 500,
                      cursor: "pointer", letterSpacing: "-0.005em",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                    }}>
                      <Edit2 size={11} strokeWidth={2} /> Editar
                    </button>
                    <button onClick={async () => {
                      await supabase.from("mercadolibre_listas")
                        .update({ activo: !lista.activo, updated_at: new Date().toISOString() })
                        .eq("id", lista.id);
                      cargarTodas();
                    }} style={{
                      flex: 1, padding: "7px 0", borderRadius: 8,
                      background: lista.activo ? C.surface : "#27AE60",
                      border: "none",
                      color: lista.activo ? "#111111" : "#fff",
                      fontSize: 12, fontWeight: 500,
                      cursor: "pointer", letterSpacing: "-0.005em",
                    }}>
                      {lista.activo ? "Desactivar" : "Activar"}
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`¿Eliminar la lista "${lista.titulo}"?`)) return;
                      await supabase.from("mercadolibre_listas").delete().eq("id", lista.id);
                      cargarTodas();
                    }} style={{
                      padding: "7px 12px", borderRadius: 8,
                      background: "#FFF1F0", border: "none",
                      color: "#FF3B30", fontSize: 12, fontWeight: 500,
                      cursor: "pointer",
                    }}>
                      <Trash2 size={12} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal admin: crear/editar lista */}
      {editando !== null && (
        <ListaAdminModal
          session={session}
          lista={editando}
          onClose={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            cargarTodas();
          }}
        />
      )}

      {/* Modal sugerir producto */}
      {showSugerir && (
        <SugerirProductoModal
          session={session}
          onClose={() => setShowSugerir(false)}
        />
      )}
    </PageWrap>
  );
};

// ─── MODAL SUGERIR PRODUCTO ──────────────────────────────────────────────────
const SugerirProductoModal = ({ session, onClose }) => {
  const [tipo, setTipo] = useState("producto"); // 'producto' | 'tienda'
  const [link, setLink] = useState("");
  const [comentario, setComentario] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState(false);
  const [error, setError] = useState(null);

  const enviar = async () => {
    setError(null);
    if (!link.trim()) { setError("Pegá el link del producto o tienda"); return; }
    // Validar que sea de ML
    if (!link.includes("mercadolibre") && !link.includes("mercadolivre")) {
      setError("El link tiene que ser de Mercado Libre");
      return;
    }
    setGuardando(true);
    // Obtener username
    const { data: perfil } = await supabase.from("perfiles")
      .select("username")
      .eq("id", session.user.id)
      .single();

    const { error: errIns } = await supabase.from("producto_sugerencias").insert({
      user_id: session.user.id,
      username: perfil?.username || null,
      link: link.trim(),
      comentario: comentario.trim() || null,
      tipo,
      estado: "pendiente",
    });
    if (errIns) {
      setError("Error al enviar: " + errIns.message);
      setGuardando(false);
      return;
    }
    setExito(true);
    setGuardando(false);
    setTimeout(onClose, 2000);
  };

  const enviarPorWhatsApp = () => {
    const tipoTexto = tipo === "tienda" ? "una tienda completa" : "un producto";
    const msg = encodeURIComponent(
      `Hola Charly, quiero recomendar ${tipoTexto} para que agregues a Olbos:\n\n${link}\n\n${comentario ? "Comentario: " + comentario : ""}`
    );
    window.open(`https://wa.me/5491130718112?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        {/* header */}
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: C.text,
            fontSize: 14, fontWeight: 400, cursor: "pointer",
          }}>Cancelar</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Sugerir un producto</div>
          <div style={{ width: 60 }} />
        </div>

        <div style={{ padding: "20px 20px 28px" }}>
          {exito ? (
            <div style={{ padding: "32px 0", textAlign: "center" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: `${C.green}14`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px",
              }}>
                <CheckCircle size={28} strokeWidth={1.8} color="#27AE60" />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>¡Gracias!</h3>
              <p style={{ fontSize: 13.5, color: C.muted, letterSpacing: "-0.005em" }}>
                Recibimos tu sugerencia. Si encaja con la comunidad la sumamos pronto.
              </p>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 18, letterSpacing: "-0.005em" }}>
                Pegá el link de un producto o tienda de Mercado Libre que pensás que le va a servir a otros riders.
              </p>

              {/* Tipo de sugerencia */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                  ¿Qué querés sugerir?
                </label>
                <div style={{
                  display: "flex", gap: 3, padding: 3,
                  background: C.surface, borderRadius: 10,
                }}>
                  <button onClick={() => setTipo("producto")} style={{
                    flex: 1, padding: "8px 0",
                    background: tipo === "producto" ? C.card : "transparent",
                    border: "none", borderRadius: 8,
                    color: tipo === "producto" ? "#111111" : C.muted,
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                    letterSpacing: "-0.005em",
                    boxShadow: tipo === "producto" ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                    transition: "all .15s ease",
                  }}>
                    Un producto
                  </button>
                  <button onClick={() => setTipo("tienda")} style={{
                    flex: 1, padding: "8px 0",
                    background: tipo === "tienda" ? C.card : "transparent",
                    border: "none", borderRadius: 8,
                    color: tipo === "tienda" ? "#111111" : C.muted,
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                    letterSpacing: "-0.005em",
                    boxShadow: tipo === "tienda" ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                    transition: "all .15s ease",
                  }}>
                    Una tienda completa
                  </button>
                </div>
                <p style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4, letterSpacing: "-0.005em" }}>
                  {tipo === "producto"
                    ? "Pegá el link de un producto puntual"
                    : "Si conocés una tienda con buen catálogo motero, puedo armar una lista con todos sus productos"}
                </p>
              </div>

              {/* Link */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                  Link de Mercado Libre
                </label>
                <input type="url" value={link} onChange={e => setLink(e.target.value)}
                  placeholder="https://www.mercadolibre.com.ar/..."
                  style={{
                    width: "100%", padding: "11px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, fontSize: 14,
                    color: C.text, boxSizing: "border-box",
                    fontFamily: "inherit", letterSpacing: "-0.005em",
                  }} />
              </div>

              {/* Comentario */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                  Por qué lo recomendás (opcional)
                </label>
                <textarea value={comentario} onChange={e => setComentario(e.target.value)}
                  placeholder="Lo usé y anduvo bárbaro, está barato, etc."
                  rows={3}
                  style={{
                    width: "100%", padding: "11px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, fontSize: 14,
                    color: C.text, boxSizing: "border-box",
                    fontFamily: "inherit", letterSpacing: "-0.005em", resize: "vertical",
                  }} />
              </div>

              {error && (
                <div style={{
                  padding: "10px 12px", borderRadius: 10,
                  background: "#FFF1F0", border: "1px solid #FFD7D2",
                  marginBottom: 14,
                  fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em",
                }}>{error}</div>
              )}

              {/* Botones */}
              <button onClick={enviar} disabled={guardando || !link.trim()}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 12,
                  background: C.accent, color: "#fff",
                  border: "none", fontSize: 14.5, fontWeight: 500,
                  cursor: guardando ? "default" : "pointer",
                  opacity: (guardando || !link.trim()) ? 0.5 : 1,
                  letterSpacing: "-0.01em", marginBottom: 10,
                }}>
                {guardando ? "Enviando…" : "Enviar sugerencia"}
              </button>

              <div style={{ position: "relative", textAlign: "center", margin: "14px 0 10px" }}>
                <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: `${C.border}` }} />
                <span style={{ position: "relative", background: C.card, padding: "0 12px", fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>
                  o también podés
                </span>
              </div>

              <button onClick={enviarPorWhatsApp} disabled={!link.trim()}
                style={{
                  width: "100%", padding: "12px 0", borderRadius: 12,
                  background: "#25D366", color: "#fff",
                  border: "none", fontSize: 13.5, fontWeight: 500,
                  cursor: !link.trim() ? "default" : "pointer",
                  opacity: !link.trim() ? 0.5 : 1,
                  letterSpacing: "-0.005em",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Mandar por WhatsApp
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal para crear/editar listas (solo admin)
const ListaAdminModal = ({ session, lista, onClose, onGuardado }) => {
  const esNueva = !lista.id;
  const [titulo, setTitulo] = useState(lista.titulo || "");
  const [descripcion, setDescripcion] = useState(lista.descripcion || "");
  const [link, setLink] = useState(lista.link || "");
  const [fotoUrl, setFotoUrl] = useState(lista.foto_url || "");
  const [categoria, setCategoria] = useState(lista.categoria || "general");
  const [orden, setOrden] = useState(lista.orden || 0);
  const [activo, setActivo] = useState(lista.activo !== false);
  const [guardando, setGuardando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) setFotoUrl(data.secure_url);
    } catch (err) {
      alert("Error subiendo la imagen");
    }
    setSubiendoFoto(false);
  };

  const guardar = async () => {
    if (!titulo.trim() || !link.trim()) {
      alert("El título y el link son obligatorios");
      return;
    }
    setGuardando(true);
    const payload = {
      titulo: titulo.trim(),
      descripcion: descripcion.trim() || null,
      link: link.trim(),
      foto_url: fotoUrl || null,
      categoria,
      orden: parseInt(orden) || 0,
      activo,
      updated_at: new Date().toISOString(),
    };
    if (esNueva) {
      payload.created_by = session?.user?.id;
      await supabase.from("mercadolibre_listas").insert(payload);
    } else {
      await supabase.from("mercadolibre_listas").update(payload).eq("id", lista.id);
    }
    setGuardando(false);
    onGuardado();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        {/* header */}
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: C.text,
            fontSize: 14, fontWeight: 400, cursor: "pointer",
          }}>Cancelar</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>
            {esNueva ? "Nueva lista" : "Editar lista"}
          </div>
          <button onClick={guardar} disabled={guardando} style={{
            background: "transparent", border: "none", color: C.text,
            fontSize: 14, fontWeight: 600, cursor: guardando ? "default" : "pointer",
            opacity: guardando ? 0.5 : 1,
          }}>{guardando ? "..." : "Guardar"}</button>
        </div>

        <div style={{ padding: "18px 20px 30px" }}>
          {/* Título */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Título</label>
            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="Ej: Esenciales para off-road"
              style={{
                width: "100%", padding: "11px 12px", borderRadius: 10,
                border: `1px solid ${C.border}`, fontSize: 14.5,
                color: C.text, boxSizing: "border-box",
                fontFamily: "inherit", letterSpacing: "-0.005em",
              }} />
          </div>

          {/* Link */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Link de Mercado Libre (afiliado)</label>
            <input type="url" value={link} onChange={e => setLink(e.target.value)}
              placeholder="https://www.mercadolibre.com.ar/..."
              style={{
                width: "100%", padding: "11px 12px", borderRadius: 10,
                border: `1px solid ${C.border}`, fontSize: 14.5,
                color: C.text, boxSizing: "border-box",
                fontFamily: "inherit", letterSpacing: "-0.005em",
              }} />
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Descripción</label>
            <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)}
              placeholder="Breve descripción de qué incluye esta lista"
              rows={3}
              style={{
                width: "100%", padding: "11px 12px", borderRadius: 10,
                border: `1px solid ${C.border}`, fontSize: 14.5,
                color: C.text, boxSizing: "border-box",
                fontFamily: "inherit", letterSpacing: "-0.005em", resize: "vertical",
              }} />
          </div>

          {/* Foto */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Imagen de portada (opcional)</label>
            {fotoUrl ? (
              <div style={{ position: "relative", marginBottom: 8 }}>
                <img loading="lazy" decoding="async" src={fotoUrl} alt="" style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: 10, background: C.surface }} />
                <button onClick={() => setFotoUrl("")} style={{
                  position: "absolute", top: 8, right: 8,
                  width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(0,0,0,.6)", border: "none", color: "#fff",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <label style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", aspectRatio: "16/9", borderRadius: 10,
                border: `1.5px dashed ${C.border}`, background: C.surface,
                cursor: "pointer", color: C.muted, fontSize: 13,
                letterSpacing: "-0.005em",
              }}>
                {subiendoFoto ? "Subiendo..." : "Tocá para subir imagen"}
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => subirFoto(e.target.files[0])} />
              </label>
            )}
          </div>

          {/* Categoría y Orden */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Categoría</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} style={{
                width: "100%", padding: "11px 12px", borderRadius: 10,
                border: `1px solid ${C.border}`, fontSize: 14.5,
                color: C.text, background: C.card, boxSizing: "border-box",
                fontFamily: "inherit",
              }}>
                <option value="general">General</option>
                <option value="cascos">Cascos</option>
                <option value="indumentaria">Indumentaria</option>
                <option value="accesorios">Accesorios</option>
                <option value="tecnologia">Tecnología</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="seguridad">Seguridad</option>
                <option value="off-road">Off-road</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Orden</label>
              <input type="number" value={orden} onChange={e => setOrden(e.target.value)}
                style={{
                  width: "100%", padding: "11px 12px", borderRadius: 10,
                  border: `1px solid ${C.border}`, fontSize: 14.5,
                  color: C.text, boxSizing: "border-box",
                  fontFamily: "inherit",
                }} />
            </div>
          </div>

          {/* Activo */}
          <label style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 14px", borderRadius: 10,
            border: `1px solid ${C.border}`, cursor: "pointer",
            background: activo ? `${C.green}14` : C.card,
          }}>
            <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: "#27AE60" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>{activo ? "Activa" : "Inactiva"}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, letterSpacing: "-0.005em" }}>
                {activo ? "Visible para todos los usuarios" : "Oculta — no aparece en la vista pública"}
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

// ─── GRUPOS ────────────────────────────────────────────────────────────────
// Componente principal "Mis Grupos" + descubrir grupos públicos
const MisGrupos = ({ session, requireAuth }) => {
  const [tab, setTab] = useState("mis"); // 'mis' | 'descubrir' | 'invitaciones'
  const [misGrupos, setMisGrupos] = useState([]);
  const [grupos, setGrupos] = useState([]); // descubrir
  const [invitaciones, setInvitaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [showCrear, setShowCrear] = useState(false);

  const cargarMisGrupos = async () => {
    if (!session) { setMisGrupos([]); return; }
    setCargando(true);
    const { data: raw, error } = await supabase.rpc("get_mis_grupos");
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error) {
      console.error("Error cargando mis grupos:", error);
      setMisGrupos([]);
    } else {
      setMisGrupos(data || []);
    }
    setCargando(false);
  };

  const cargarDescubrir = async () => {
    setCargando(true);
    const { data } = await supabase.from("grupos")
      .select("*")
      .in("privacidad", ["publico", "solicitud"])
      .order("created_at", { ascending: false })
      .limit(50);
    // contar miembros
    const conMiembros = await Promise.all((data || []).map(async g => {
      const { count } = await supabase.from("grupo_miembros")
        .select("id", { count: "exact", head: true }).eq("grupo_id", g.id);
      return { ...g, miembros_count: count || 0 };
    }));
    setGrupos(conMiembros);
    setCargando(false);
  };

  const cargarInvitaciones = async () => {
    if (!session) { setInvitaciones([]); return; }
    setCargando(true);
    const { data } = await supabase.from("grupo_invitaciones")
      .select("*, grupos(id, nombre, foto_url, descripcion, privacidad, ciudad)")
      .eq("user_id", session.user.id)
      .eq("tipo", "invitacion")
      .eq("estado", "pendiente")
      .order("created_at", { ascending: false });
    setInvitaciones(data || []);
    setCargando(false);
  };

  useEffect(() => {
    if (tab === "mis") cargarMisGrupos();
    else if (tab === "descubrir") cargarDescubrir();
    else if (tab === "invitaciones") cargarInvitaciones();
  }, [tab, session]);

  // Listener para abrir un grupo específico desde un evento global (ej: aceptar invitación por link)
  const [tabInicialGrupo, setTabInicialGrupo] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.grupoId) {
        setGrupoSeleccionado(e.detail.grupoId);
        setTabInicialGrupo(e.detail.tab || null);
      }
    };
    window.addEventListener("olbos-abrir-grupo", handler);
    return () => window.removeEventListener("olbos-abrir-grupo", handler);
  }, []);

  if (grupoSeleccionado) {
    return <GrupoDetalle
      grupoId={grupoSeleccionado}
      session={session}
      onBack={() => { setGrupoSeleccionado(null); setTabInicialGrupo(null); cargarMisGrupos(); }}
      tabInicial={tabInicialGrupo}
    />;
  }

  const numInvitaciones = invitaciones.length;

  return (
    <PageWrap>
      {/* Header */}
      <div style={{ padding: "var(--header-pt) 24px 18px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Grupos</div>
        </div>
        <h1 style={{
          fontSize: 32, fontWeight: 700, color: C.text,
          letterSpacing: "-0.03em", lineHeight: 1.1, marginBottom: 8,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
        }}>Mis Grupos</h1>
        <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
          Tus grupos privados. Rutas, eventos y feed solo para los tuyos.
        </p>
        <VideoAyuda seccion="grupos" style={{ marginTop: 10 }} />
      </div>

      {/* Tabs */}
      <div style={{ padding: "12px 16px 8px", background: C.card, position: "sticky", top: 0, zIndex: 10, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", gap: 3, padding: 3, background: C.surface, borderRadius: 10 }}>
          {[
            ["mis", "Mis grupos"],
            ["descubrir", "Descubrir"],
            ["invitaciones", `Invitaciones${numInvitaciones > 0 ? ` (${numInvitaciones})` : ""}`],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: "8px 0",
              background: tab === id ? C.card : "transparent",
              border: "none", borderRadius: 8,
              color: tab === id ? "#111111" : C.muted,
              fontSize: 12.5, fontWeight: 500, cursor: "pointer",
              letterSpacing: "-0.005em",
              boxShadow: tab === id ? "0 1px 3px rgba(0,0,0,.06)" : "none",
              transition: "all .15s ease",
            }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "18px 18px 100px" }}>
        {/* Botón crear (solo en Mis grupos) */}
        {tab === "mis" && session && (
          <button onClick={() => setShowCrear(true)} style={{
            width: "100%", padding: "14px", borderRadius: 14,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer", marginBottom: 18,
            fontSize: 14.5, fontWeight: 500, letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <Plus size={16} strokeWidth={2} /> Crear nuevo grupo
          </button>
        )}

        {/* Loading */}
        {cargando && (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        )}

        {/* TAB: Mis grupos */}
        {!cargando && tab === "mis" && (
          !session ? (
            <EmptyGrupos
              icon={Users}
              titulo="Iniciá sesión para ver tus grupos"
              texto="Los grupos son privados, necesitás una cuenta para crear o unirte a uno."
              cta="Iniciar sesión"
              onCta={() => requireAuth && requireAuth(() => {})}
            />
          ) : misGrupos.length === 0 ? (
            <EmptyGrupos
              icon={Users}
              titulo="Todavía no estás en ningún grupo"
              texto="Creá tu primer grupo para planificar rutas con tus amigos, o descubrí grupos abiertos en tu zona."
              cta="Explorar grupos públicos"
              onCta={() => setTab("descubrir")}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {misGrupos.map(g => (
                <GrupoCard key={g.id} grupo={g} onAbrir={() => setGrupoSeleccionado(g.id)} mostrarRol={true} />
              ))}
            </div>
          )
        )}

        {/* TAB: Descubrir */}
        {!cargando && tab === "descubrir" && (
          grupos.length === 0 ? (
            <EmptyGrupos
              icon={Search}
              titulo="Sin grupos públicos por ahora"
              texto="¿Sos pionero? Creá un grupo y dejá que otros riders se sumen."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {grupos.map(g => (
                <GrupoCard key={g.id} grupo={g} onAbrir={() => setGrupoSeleccionado(g.id)} />
              ))}
            </div>
          )
        )}

        {/* TAB: Invitaciones */}
        {!cargando && tab === "invitaciones" && (
          invitaciones.length === 0 ? (
            <EmptyGrupos
              icon={Bell}
              titulo="No tenés invitaciones pendientes"
              texto="Cuando un grupo te invite, vas a verlo acá."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {invitaciones.map(inv => (
                <InvitacionCard key={inv.id} invitacion={inv} onResponder={async (aceptar) => {
                  if (aceptar) {
                    // crear miembro
                    await supabase.from("grupo_miembros").insert({
                      grupo_id: inv.grupo_id,
                      user_id: session.user.id,
                      rol: "miembro",
                    });
                  }
                  await supabase.from("grupo_invitaciones")
                    .update({ estado: aceptar ? "aceptada" : "rechazada", updated_at: new Date().toISOString() })
                    .eq("id", inv.id);
                  cargarInvitaciones();
                  if (aceptar) {
                    setTab("mis");
                  }
                }} />
              ))}
            </div>
          )
        )}
      </div>

      {/* Modal crear grupo */}
      {showCrear && (
        <CrearGrupoModal
          session={session}
          onClose={() => setShowCrear(false)}
          onCreado={(grupoId) => {
            setShowCrear(false);
            setGrupoSeleccionado(grupoId);
          }}
        />
      )}
    </PageWrap>
  );
};

// ─── Card de grupo en la lista ───────────────────────────────────────────
const GrupoCard = ({ grupo, onAbrir, mostrarRol = false }) => {
  const PRIV_COLORS = {
    privado: { bg: "#FFF1F0", color: "#B82F26", label: "Privado" },
    solicitud: { bg: `${C.gold}14`, color: C.gold, label: "Por solicitud" },
    publico: { bg: `${C.green}14`, color: "#1F8347", label: "Público" },
  };
  const priv = PRIV_COLORS[grupo.privacidad] || PRIV_COLORS.privado;

  return (
    <div onClick={onAbrir} style={{
      background: C.card, borderRadius: 14,
      border: `1px solid ${C.border}`,
      cursor: "pointer",
      display: "flex", alignItems: "center", gap: 12,
      padding: 12,
      transition: "all .15s ease",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${C.border}`; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.04)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = "none"; }}>
      {/* Foto */}
      <div style={{
        width: 54, height: 54, borderRadius: 12,
        background: grupo.foto_url ? `url(${grupo.foto_url}) center/cover` : "linear-gradient(135deg, #111111 0%, #4a4a4a 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, overflow: "hidden",
      }}>
        {!grupo.foto_url && <Users size={22} strokeWidth={1.5} color="#fff" />}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.015em", lineHeight: 1.2, marginBottom: 3 }}>
          {grupo.nombre}
        </div>
        {grupo.descripcion && (
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4, letterSpacing: "-0.005em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>
            {grupo.descripcion}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 10, fontWeight: 500,
            padding: "2px 7px", borderRadius: 100,
            background: priv.bg, color: priv.color,
          }}>{priv.label}</span>
          <span style={{ fontSize: 10.5, color: C.muted, letterSpacing: "-0.005em" }}>
            {grupo.miembros_count} {grupo.miembros_count === 1 ? "miembro" : "miembros"}
          </span>
          {mostrarRol && grupo.mi_rol === "admin" && (
            <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 100, background: C.accent, color: "#fff" }}>Admin</span>
          )}
          {grupo.ciudad && (
            <span style={{ fontSize: 10.5, color: C.muted, letterSpacing: "-0.005em" }}>· {grupo.ciudad}</span>
          )}
        </div>
      </div>

      <ChevronRight size={16} strokeWidth={2} color="#c0c0c5" />
    </div>
  );
};

// ─── Card de invitación pendiente ────────────────────────────────────────
const InvitacionCard = ({ invitacion, onResponder }) => {
  const g = invitacion.grupos;
  if (!g) return null;
  return (
    <div style={{
      background: C.card, borderRadius: 14,
      border: `1px solid ${C.border}`, padding: 14,
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: g.foto_url ? `url(${g.foto_url}) center/cover` : "linear-gradient(135deg, #111111 0%, #4a4a4a 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, overflow: "hidden",
        }}>
          {!g.foto_url && <Users size={20} strokeWidth={1.5} color="#fff" />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginBottom: 2, letterSpacing: "-0.005em" }}>
            Te invitaron a unirte a
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.015em", marginBottom: 2 }}>
            {g.nombre}
          </div>
          {g.descripcion && (
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4, letterSpacing: "-0.005em" }}>
              {g.descripcion}
            </div>
          )}
        </div>
      </div>
      {invitacion.mensaje && (
        <div style={{
          padding: "8px 10px", background: C.surface, borderRadius: 8,
          fontSize: 12, color: C.text, lineHeight: 1.4, marginBottom: 10,
          letterSpacing: "-0.005em",
        }}>"{invitacion.mensaje}"</div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onResponder(true)} style={{
          flex: 1, padding: "10px 0", borderRadius: 10,
          background: C.accent, color: "#fff",
          border: "none", cursor: "pointer",
          fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
        }}>
          Aceptar
        </button>
        <button onClick={() => onResponder(false)} style={{
          flex: 1, padding: "10px 0", borderRadius: 10,
          background: C.surface, color: C.text,
          border: "none", cursor: "pointer",
          fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
        }}>
          Rechazar
        </button>
      </div>
    </div>
  );
};

// ─── Empty state genérico ────────────────────────────────────────────────
const EmptyGrupos = ({ icon: Icon, titulo, texto, cta, onCta }) => (
  <div style={{ padding: "var(--header-pt) 24px", textAlign: "center" }}>
    <div style={{
      width: 60, height: 60, borderRadius: "50%",
      background: C.surface,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 16px",
    }}>
      <Icon size={26} strokeWidth={1.5} color={C.muted} />
    </div>
    <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>{titulo}</h3>
    <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em", marginBottom: cta ? 18 : 0, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
      {texto}
    </p>
    {cta && (
      <button onClick={onCta} style={{
        padding: "10px 22px", borderRadius: 100,
        background: C.accent, color: "#fff",
        border: "none", cursor: "pointer",
        fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
      }}>{cta}</button>
    )}
  </div>
);

// ─── MODAL CREAR GRUPO ────────────────────────────────────────────────────
const CrearGrupoModal = ({ session, onClose, onCreado }) => {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [privacidad, setPrivacidad] = useState("privado");
  const [fotoUrl, setFotoUrl] = useState("");
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) setFotoUrl(data.secure_url);
    } catch {
      alert("Error subiendo la imagen");
    }
    setSubiendoFoto(false);
  };

  const crear = async () => {
    setError(null);
    if (!nombre.trim()) { setError("Necesitás un nombre para el grupo"); return; }
    setGuardando(true);

    // Llamamos a la función RPC que crea el grupo + nos agrega como admin en una sola operación
    const { data: grupoId, error: errRpc } = await supabase.rpc("crear_grupo", {
      p_nombre: nombre.trim(),
      p_descripcion: descripcion.trim() || null,
      p_ciudad: ciudad.trim() || null,
      p_foto_url: fotoUrl || null,
      p_privacidad: privacidad,
    });

    if (errRpc) {
      setError("Error al crear: " + errRpc.message);
      setGuardando(false);
      return;
    }

    setGuardando(false);
    onCreado(grupoId);
  };

  const PRIVACIDAD_OPCIONES = [
    { id: "privado",   t: "Privado",       d: "Solo por invitación de un admin" },
    { id: "solicitud", t: "Por solicitud", d: "Cualquiera puede pedir entrar, los admins aprueban" },
    { id: "publico",   t: "Público",       d: "Cualquier rider se une sin aprobación" },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.text, fontSize: 14, fontWeight: 400, cursor: "pointer" }}>Cancelar</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Nuevo grupo</div>
          <button onClick={crear} disabled={guardando || !nombre.trim()} style={{ background: "transparent", border: "none", color: C.text, fontSize: 14, fontWeight: 600, cursor: guardando ? "default" : "pointer", opacity: (guardando || !nombre.trim()) ? 0.5 : 1 }}>
            {guardando ? "..." : "Crear"}
          </button>
        </div>

        <div style={{ padding: "20px 20px 30px" }}>
          {/* Foto */}
          <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
            {fotoUrl ? (
              <div style={{ position: "relative" }}>
                <img loading="lazy" decoding="async" src={fotoUrl} alt="" style={{ width: 84, height: 84, borderRadius: 20, objectFit: "cover" }} />
                <button onClick={() => setFotoUrl("")} style={{
                  position: "absolute", top: -6, right: -6,
                  width: 24, height: 24, borderRadius: "50%",
                  background: C.accent, border: "none", color: "#fff",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={12} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <label style={{
                width: 84, height: 84, borderRadius: 20,
                border: `1.5px dashed ${C.border}`, background: C.surface,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: C.muted,
              }}>
                {subiendoFoto ? <span style={{ fontSize: 11 }}>...</span> : <Camera size={22} strokeWidth={1.5} />}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => subirFoto(e.target.files[0])} />
              </label>
            )}
          </div>

          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Nombre del grupo</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder="Ej: Los Cuervos del Sur"
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14.5, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em" }} />
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Descripción (opcional)</label>
            <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)}
              placeholder="De qué se trata el grupo, qué motos usan, qué les gusta..."
              rows={3}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em", resize: "vertical" }} />
          </div>

          {/* Ciudad */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Ciudad o zona (opcional)</label>
            <input type="text" value={ciudad} onChange={e => setCiudad(e.target.value)}
              placeholder="Ej: Buenos Aires, Mendoza..."
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em" }} />
          </div>

          {/* Privacidad */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 8 }}>Privacidad</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {PRIVACIDAD_OPCIONES.map(o => (
                <label key={o.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "11px 12px", borderRadius: 10,
                  border: `1.5px solid ${privacidad === o.id ? "#111111" : C.border}`,
                  cursor: "pointer",
                  background: privacidad === o.id ? C.surface : C.card,
                  transition: "all .15s ease",
                }}>
                  <input type="radio" name="priv" checked={privacidad === o.id} onChange={() => setPrivacidad(o.id)}
                    style={{ accentColor: "#111111", marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>{o.t}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4, letterSpacing: "-0.005em" }}>{o.d}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ padding: "10px 12px", borderRadius: 10, background: "#FFF1F0", border: "1px solid #FFD7D2", fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em" }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DETALLE DE GRUPO ─────────────────────────────────────────────────────
const GrupoDetalle = ({ grupoId, session, onBack, tabInicial }) => {
  const [grupo, setGrupo] = useState(null);
  const [miembros, setMiembros] = useState([]);
  const [miRol, setMiRol] = useState(null);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showInvitar, setShowInvitar] = useState(false);
  const [showEditar, setShowEditar] = useState(false);
  const [confirmaSalida, setConfirmaSalida] = useState(false);
  // Tabs y contenido
  const [tabActiva, setTabActiva] = useState(tabInicial || "info"); // 'info' | 'chat' | 'rutas' | 'eventos' | 'feed'
  const [noLeidosChat, setNoLeidosChat] = useState(0);

  // Contador de mensajes no leídos del chat grupal (para badge en la pill)
  useEffect(() => {
    if (!session || !grupoId) return;
    if (tabActiva === "chat") { setNoLeidosChat(0); return; } // estoy adentro, no hay badge
    const cargarNoLeidos = async () => {
      const { data: lectura } = await supabase.from("grupo_lectura")
        .select("ultimo_leido_at").eq("grupo_id", grupoId).eq("user_id", session.user.id).maybeSingle();
      let q = supabase.from("grupo_mensajes")
        .select("id", { count: "exact", head: true })
        .eq("grupo_id", grupoId)
        .neq("sender_id", session.user.id);
      if (lectura?.ultimo_leido_at) q = q.gt("created_at", lectura.ultimo_leido_at);
      const { count } = await q;
      setNoLeidosChat(count || 0);
    };
    cargarNoLeidos();
    const ch = supabase
      .channel(`grupo_chat_badge_${grupoId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "grupo_mensajes", filter: `grupo_id=eq.${grupoId}` }, () => {
        cargarNoLeidos();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session, grupoId, tabActiva]);
  const [contenidoGrupo, setContenidoGrupo] = useState({ rutas: [], rutas_planificadas: [], eventos: [], posts: [] });
  const [cargandoContenido, setCargandoContenido] = useState(false);
  const [showElegirTipoRuta, setShowElegirTipoRuta] = useState(false);

  const cargarContenido = async () => {
    setCargandoContenido(true);
    const { data: raw } = await supabase.rpc("get_grupo_contenido", { p_grupo_id: grupoId });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) {
      setContenidoGrupo({
        rutas: data.rutas || [],
        rutas_planificadas: data.rutas_planificadas || [],
        eventos: data.eventos || [],
        posts: data.posts || [],
      });
    }
    setCargandoContenido(false);
  };

  useEffect(() => {
    if (miRol && tabActiva !== "info") cargarContenido();
  }, [tabActiva, miRol]);

  const cargar = async () => {
    setCargando(true);
    const { data: raw, error } = await supabase.rpc("get_grupo_detalle", { p_grupo_id: grupoId });

    // RPC con JSONB puede devolver el objeto directo o como string, según versión del cliente
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;

    if (error || !data || data.error) {
      console.error("Error cargando grupo:", error || data?.error, "raw:", raw);
      setGrupo(null);
      setCargando(false);
      return;
    }

    setGrupo(data.grupo);
    const ms = data.miembros || [];
    setMiembros(ms);

    if (session) {
      const yo = ms.find(m => m.user_id === session.user.id);
      setMiRol(yo?.rol || null);
      setSolicitudesPendientes(data.solicitudes || []);
    } else {
      setMiRol(null);
      setSolicitudesPendientes([]);
    }
    setCargando(false);
  };

  useEffect(() => { cargar(); }, [grupoId, session]);

  const unirsePublico = async () => {
    if (!session) { alert("Iniciá sesión para unirte"); return; }
    await supabase.from("grupo_miembros").insert({
      grupo_id: grupoId, user_id: session.user.id, rol: "miembro",
    });
    cargar();
  };

  const solicitarUnirse = async () => {
    if (!session) { alert("Iniciá sesión para solicitar"); return; }
    const { error } = await supabase.from("grupo_invitaciones").insert({
      grupo_id: grupoId, user_id: session.user.id, tipo: "solicitud", estado: "pendiente",
    });
    if (error) {
      if (error.code === "23505") alert("Ya enviaste una solicitud a este grupo");
      else alert("Error: " + error.message);
      return;
    }
    alert("Solicitud enviada. Un admin la va a revisar pronto.");
    cargar();
  };

  const aceptarSolicitud = async (sol) => {
    await supabase.from("grupo_miembros").insert({
      grupo_id: grupoId, user_id: sol.user_id, rol: "miembro",
    });
    await supabase.from("grupo_invitaciones")
      .update({ estado: "aceptada", updated_at: new Date().toISOString() })
      .eq("id", sol.id);
    cargar();
  };

  const rechazarSolicitud = async (sol) => {
    await supabase.from("grupo_invitaciones")
      .update({ estado: "rechazada", updated_at: new Date().toISOString() })
      .eq("id", sol.id);
    cargar();
  };

  const salirDelGrupo = async () => {
    await supabase.from("grupo_miembros")
      .delete()
      .eq("grupo_id", grupoId)
      .eq("user_id", session.user.id);
    onBack();
  };

  const eliminarGrupo = async () => {
    if (!window.confirm(`¿Eliminar definitivamente "${grupo.nombre}"? Se borran todos los datos del grupo.`)) return;
    await supabase.from("grupos").delete().eq("id", grupoId);
    onBack();
  };

  const sacarMiembro = async (m) => {
    if (!window.confirm(`¿Sacar a @${m.perfiles?.username || "este rider"} del grupo?`)) return;
    await supabase.from("grupo_miembros")
      .delete()
      .eq("id", m.id);
    cargar();
  };

  if (cargando) {
    return (
      <PageWrap>
        <div style={{ padding: 60, textAlign: "center", color: C.muted }}>Cargando…</div>
      </PageWrap>
    );
  }

  if (!grupo) {
    return (
      <PageWrap>
        <div style={{ padding: 40, textAlign: "center" }}>
          <p style={{ color: C.muted, marginBottom: 12 }}>No se pudo cargar el grupo</p>
          <button onClick={onBack} style={{ padding: "8px 18px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 13 }}>Volver</button>
        </div>
      </PageWrap>
    );
  }

  const PRIV_LABELS = { privado: "Privado", solicitud: "Por solicitud", publico: "Público" };
  const esAdmin = miRol === "admin";
  const esMiembro = !!miRol;

  return (
    <PageWrap>
      {/* Header */}
      <div style={{ padding: "var(--header-pt) 16px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{
          width: 32, height: 32, borderRadius: "50%",
          background: C.surface, border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ChevronLeft size={18} strokeWidth={2} color="#111111" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>{PRIV_LABELS[grupo.privacidad]}</div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", lineHeight: 1.1, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {grupo.nombre}
          </h1>
        </div>
        {esAdmin && (
          <button onClick={() => setShowEditar(true)} style={{
            padding: "6px 12px", borderRadius: 100,
            background: C.surface, border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 500, color: C.text, letterSpacing: "-0.005em",
            marginRight: 44, // el avatar flotante vive arriba a la derecha — no pisarse
          }}>
            Editar
          </button>
        )}
      </div>

      <div style={{ padding: "20px 18px 100px" }}>
        {/* Foto + info del grupo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{
            width: 88, height: 88, borderRadius: 22,
            background: grupo.foto_url ? `url(${grupo.foto_url}) center/cover` : "linear-gradient(135deg, #111111 0%, #4a4a4a 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px", overflow: "hidden",
          }}>
            {!grupo.foto_url && <Users size={36} strokeWidth={1.2} color="#fff" />}
          </div>
          {grupo.descripcion && (
            <p style={{ fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 8, letterSpacing: "-0.005em", maxWidth: 340, marginLeft: "auto", marginRight: "auto" }}>
              {grupo.descripcion}
            </p>
          )}
          {grupo.ciudad && (
            <div style={{ fontSize: 12, color: C.muted, letterSpacing: "-0.005em", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <MapPin size={11} strokeWidth={2} /> {grupo.ciudad}
            </div>
          )}
        </div>

        {/* Acciones según rol */}
        {!esMiembro && (
          <div style={{ marginBottom: 24 }}>
            {grupo.privacidad === "publico" && (
              <button onClick={unirsePublico} style={{
                width: "100%", padding: "13px", borderRadius: 12,
                background: C.accent, color: "#fff",
                border: "none", cursor: "pointer",
                fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em",
              }}>
                Unirme al grupo
              </button>
            )}
            {grupo.privacidad === "solicitud" && (
              <button onClick={solicitarUnirse} style={{
                width: "100%", padding: "13px", borderRadius: 12,
                background: C.accent, color: "#fff",
                border: "none", cursor: "pointer",
                fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em",
              }}>
                Solicitar unirme
              </button>
            )}
            {grupo.privacidad === "privado" && (
              <div style={{
                padding: 14, borderRadius: 12, background: C.surface,
                border: `1px solid ${C.border}`, textAlign: "center",
                fontSize: 12.5, color: C.muted, letterSpacing: "-0.005em",
              }}>
                Este grupo es privado. Solo se entra por invitación de un admin.
              </div>
            )}
          </div>
        )}

        {/* Botones de admin */}
        {esAdmin && (
          <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
            <button onClick={() => setShowInvitar(true)} style={{
              flex: 1, padding: "11px", borderRadius: 12,
              background: C.accent, color: "#fff",
              border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <Plus size={14} strokeWidth={2} /> Invitar miembros
            </button>
          </div>
        )}

        {/* Solicitudes pendientes (solo admin de grupos por solicitud) */}
        {esAdmin && grupo.privacidad === "solicitud" && solicitudesPendientes.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
              Solicitudes pendientes ({solicitudesPendientes.length})
            </h3>
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              {solicitudesPendientes.map((sol, i) => (
                <div key={sol.id} style={{
                  padding: "12px 14px",
                  borderBottom: i < solicitudesPendientes.length - 1 ? `1px solid ${C.border}` : "none",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: sol.perfiles?.avatar_url ? `url(${sol.perfiles.avatar_url}) center/cover` : C.surface,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {!sol.perfiles?.avatar_url && <User size={16} strokeWidth={1.5} color={C.muted} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>@{sol.perfiles?.username || "rider"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => aceptarSolicitud(sol)} style={{
                      padding: "5px 10px", borderRadius: 8,
                      background: "#27AE60", color: "#fff",
                      border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500,
                    }}>Aceptar</button>
                    <button onClick={() => rechazarSolicitud(sol)} style={{
                      padding: "5px 10px", borderRadius: 8,
                      background: C.surface, color: C.text,
                      border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500,
                    }}>Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de miembros */}
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            {miembros.length} {miembros.length === 1 ? "miembro" : "miembros"}
          </h3>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            {miembros.map((m, i) => (
              <div key={m.id} style={{
                padding: "10px 14px",
                borderBottom: i < miembros.length - 1 ? `1px solid ${C.border}` : "none",
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: m.perfiles?.avatar_url ? `url(${m.perfiles.avatar_url}) center/cover` : C.surface,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {!m.perfiles?.avatar_url && <User size={16} strokeWidth={1.5} color={C.muted} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>
                    @{m.perfiles?.username || "rider"}
                    {m.user_id === session?.user?.id && <span style={{ color: C.muted, fontWeight: 400, marginLeft: 4 }}>(vos)</span>}
                  </div>
                  {m.perfiles?.ciudad && (
                    <div style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>{m.perfiles.ciudad}</div>
                  )}
                </div>
                {m.rol === "admin" && (
                  <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 8px", borderRadius: 100, background: C.accent, color: "#fff" }}>Admin</span>
                )}
                {esAdmin && m.user_id !== session?.user?.id && (
                  <button onClick={() => sacarMiembro(m)} style={{
                    padding: 5, borderRadius: 6,
                    background: "transparent", border: "none", cursor: "pointer",
                  }}>
                    <X size={14} strokeWidth={2} color={C.muted} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* TABS de contenido — solo para miembros */}
        {esMiembro && (
          <>
            <div style={{
              display: "flex", gap: 3, padding: 3,
              background: C.surface, borderRadius: 10,
              marginBottom: 16,
            }}>
              {[
                ["info", `Miembros (${miembros.length})`],
                ["chat", noLeidosChat > 0 ? `Chat (${noLeidosChat > 99 ? "99+" : noLeidosChat})` : "Chat"],
                ["rutas", "Rutas"],
                ["eventos", "Eventos"],
                ["feed", "Feed"],
                ["vivo", "En vivo"],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setTabActiva(id)} style={{
                  flex: 1, padding: "8px 0",
                  background: tabActiva === id ? C.card : "transparent",
                  border: "none", borderRadius: 8,
                  color: tabActiva === id ? "#111111" : (id === "chat" && noLeidosChat > 0 ? "#111111" : C.muted),
                  fontSize: 11.5, fontWeight: id === "chat" && noLeidosChat > 0 ? 700 : 500, cursor: "pointer",
                  letterSpacing: "-0.005em",
                  boxShadow: tabActiva === id ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                }}>{label}</button>
              ))}
            </div>

            {/* TAB: INFO (miembros) — el bloque de miembros ya está renderizado arriba */}
            {tabActiva === "info" && null}

            {/* TAB: RUTAS */}
            {tabActiva === "rutas" && (
              <>
                {esMiembro && (
                  <button onClick={() => setShowElegirTipoRuta(true)} style={{
                    width: "100%", padding: "12px", borderRadius: 12,
                    background: C.accent, color: "#fff",
                    border: "none", cursor: "pointer", marginBottom: 14,
                    fontSize: 13.5, fontWeight: 500, letterSpacing: "-0.005em",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <Plus size={15} strokeWidth={2} /> Crear ruta del grupo
                  </button>
                )}
                {cargandoContenido ? (
                  <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
                ) : (contenidoGrupo.rutas.length === 0 && contenidoGrupo.rutas_planificadas.length === 0) ? (
                  <div style={{ padding: "30px 24px", textAlign: "center", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                      <Map size={20} strokeWidth={1.5} color={C.muted} />
                    </div>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.015em" }}>Todavía no hay rutas del grupo</h4>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
                      Tocá el botón de arriba para planificar una ruta o grabar una con GPS.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Rutas planificadas con waypoints */}
                    {contenidoGrupo.rutas_planificadas.map(r => (
                      <div key={"plan-"+r.id} style={{
                        background: C.card, borderRadius: 12,
                        border: `1px solid ${C.border}`, padding: 12,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 100, background: "#E6F2FF", color: C.text, textTransform: "uppercase", letterSpacing: "0.04em" }}>Planificada</span>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>{r.nombre}</div>
                        </div>
                        {r.desc_texto && <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>{r.desc_texto}</div>}
                        <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
                          {r.km && <span>📍 {Number(r.km).toFixed(1)} km</span>}
                          {r.diff && <span>· {r.diff}</span>}
                          {r.camino && <span>· {r.camino}</span>}
                          <span>· por @{r.username || "rider"}</span>
                        </div>
                      </div>
                    ))}
                    {/* Rutas grabadas (trazas) */}
                    {contenidoGrupo.rutas.map(r => (
                      <div key={"trz-"+r.id} style={{
                        background: C.card, borderRadius: 12,
                        border: `1px solid ${C.border}`, padding: 12,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 100, background: `${C.green}14`, color: "#1F8347", textTransform: "uppercase", letterSpacing: "0.04em" }}>Grabada</span>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>{r.titulo}</div>
                        </div>
                        {r.descripcion && <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>{r.descripcion}</div>}
                        <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted }}>
                          {r.distancia_km && <span>📍 {r.distancia_km.toFixed(1)} km</span>}
                          {r.duracion_seg && <span>⏱ {Math.floor(r.duracion_seg / 60)} min</span>}
                          <span>· por @{r.username || "rider"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* TAB: EVENTOS */}
            {tabActiva === "eventos" && (
              <>
                {esMiembro && (
                  <button onClick={() => {
                    window.dispatchEvent(new CustomEvent("olbos-abrir-crear-evento", { detail: { grupoId, grupoNombre: grupo.nombre } }));
                  }} style={{
                    width: "100%", padding: "12px", borderRadius: 12,
                    background: C.accent, color: "#fff",
                    border: "none", cursor: "pointer", marginBottom: 14,
                    fontSize: 13.5, fontWeight: 500, letterSpacing: "-0.005em",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <Plus size={15} strokeWidth={2} /> Crear evento del grupo
                  </button>
                )}
                {cargandoContenido ? (
                  <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
                ) : contenidoGrupo.eventos.length === 0 ? (
                  <div style={{ padding: "30px 24px", textAlign: "center", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                      <Calendar size={20} strokeWidth={1.5} color={C.muted} />
                    </div>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.015em" }}>Sin eventos del grupo</h4>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
                      Tocá el botón de arriba para crear el primer evento privado del grupo.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Cada evento del grupo abre el MISMO detalle que un evento
                        público: inscripción, chat, rodada, todo. Antes era una
                        tarjeta muerta que no se podía ni tocar. */}
                    {contenidoGrupo.eventos.map(e => (
                      <button key={e.id}
                        onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: e.id } }))}
                        style={{
                          background: C.card, borderRadius: 12, textAlign: "left", width: "100%",
                          border: `1px solid ${C.border}`, padding: 12, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.01em" }}>{e.titulo}</div>
                          {e.descripcion && <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>{e.descripcion}</div>}
                          <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
                            {e.fecha && <span>📅 {new Date(String(e.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}</span>}
                            {e.lugar && <span>📍 {e.lugar}</span>}
                            <span>· por @{e.username || "rider"}</span>
                          </div>
                        </div>
                        <ChevronRight size={17} strokeWidth={2} color="#c9c2b8" style={{ flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* TAB: FEED */}
            {tabActiva === "feed" && (
              <>
                {esMiembro && (
                  <button onClick={() => {
                    window.dispatchEvent(new CustomEvent("olbos-abrir-publicar", { detail: { grupoId, grupoNombre: grupo.nombre } }));
                  }} style={{
                    width: "100%", padding: "12px", borderRadius: 12,
                    background: C.accent, color: "#fff",
                    border: "none", cursor: "pointer", marginBottom: 14,
                    fontSize: 13.5, fontWeight: 500, letterSpacing: "-0.005em",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <Plus size={15} strokeWidth={2} /> Publicar en el grupo
                  </button>
                )}
                {cargandoContenido ? (
                  <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
                ) : contenidoGrupo.posts.length === 0 ? (
                  <div style={{ padding: "30px 24px", textAlign: "center", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                      <Camera size={20} strokeWidth={1.5} color={C.muted} />
                    </div>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.015em" }}>Sin posts del grupo</h4>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
                    Cuando publiques algo desde el feed general, podés elegir publicarlo solo para este grupo.
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {contenidoGrupo.posts.map(p => {
                    let fotosPost = [];
                    try { const mu = typeof p.media_urls === "string" ? JSON.parse(p.media_urls) : p.media_urls; if (Array.isArray(mu)) fotosPost = mu.filter(m => m && m.url); } catch (e) {}
                    if (!fotosPost.length) { const u = p.media_url || p.imagen_url; if (u) fotosPost = [{ url: u, type: p.media_type }]; }
                    return (
                    <div key={p.id} style={{
                      background: C.card, borderRadius: 12,
                      border: `1px solid ${C.border}`, padding: 12,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : C.surface,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {!p.avatar_url && <User size={14} strokeWidth={1.5} color={C.muted} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>@{p.username || "rider"}</div>
                          <div style={{ fontSize: 10, color: C.muted }}>{new Date(p.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
                        </div>
                        {p.compartido_feed_general && (
                          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 100, background: `${C.gold}14`, color: C.gold, fontWeight: 500 }}>
                            También en feed general
                          </span>
                        )}
                      </div>
                      {p.texto && <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: fotosPost.length ? 8 : 0, letterSpacing: "-0.005em", whiteSpace: "pre-wrap" }}>{p.texto}</p>}
                      {fotosPost.map((m, i) => m.type === "video"
                        ? <video key={i} src={m.url} controls playsInline preload="metadata" style={{ width: "100%", borderRadius: 10, marginTop: 4 }} />
                        : <img key={i} loading="lazy" decoding="async" src={cldThumb(m.url, 800)} alt="" onClick={() => window.dispatchEvent(new CustomEvent("olbos-ver-anuncio", { detail: { id: null, imagen_url: m.url, link: null } }))} style={{ width: "100%", borderRadius: 10, marginTop: 4, cursor: "pointer" }} />)}
                    </div>
                    );
                  })}
                </div>
                )}
              </>
            )}

            {/* TAB: EN VIVO */}
            {tabActiva === "chat" && (
              <ChatGrupo
                grupoId={grupoId}
                grupoNombre={grupo.nombre}
                session={session}
                esMiembro={esMiembro}
              />
            )}

            {tabActiva === "vivo" && (
              /* El intercomunicador ahora vive DENTRO de EnVivoGrupo y aparece solo
                 cuando hay 2+ compartiendo (con uno solo no hay con quién hablar) */
              <EnVivoGrupo
                grupoId={grupoId}
                grupoNombre={grupo.nombre}
                session={session}
                esMiembro={esMiembro}
                creatorId={grupo.creador_id}
                vozConfig={grupo.voz_config}
                miembros={miembros}
              />
            )}
          </>
        )}

        <div style={{ marginBottom: 16 }} />

        {/* Acciones miembro */}
        {esMiembro && !esAdmin && (
          <button onClick={() => setConfirmaSalida(true)} style={{
            width: "100%", padding: "11px", borderRadius: 12,
            background: "#FFF1F0", color: "#FF3B30",
            border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
          }}>
            Salir del grupo
          </button>
        )}

        {/* Acciones admin */}
        {esAdmin && grupo.creador_id === session?.user?.id && (
          <button onClick={eliminarGrupo} style={{
            width: "100%", padding: "11px", borderRadius: 12,
            background: "#FFF1F0", color: "#FF3B30",
            border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
          }}>
            Eliminar grupo
          </button>
        )}
        {esAdmin && grupo.creador_id !== session?.user?.id && (
          <button onClick={() => setConfirmaSalida(true)} style={{
            width: "100%", padding: "11px", borderRadius: 12,
            background: "#FFF1F0", color: "#FF3B30",
            border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
          }}>
            Salir del grupo
          </button>
        )}
      </div>

      {/* Modal invitar */}
      {showInvitar && (
        <InvitarMiembrosModal
          grupoId={grupoId}
          grupoNombre={grupo.nombre}
          session={session}
          miembrosIds={miembros.map(m => m.user_id)}
          onClose={() => setShowInvitar(false)}
        />
      )}

      {/* Modal editar grupo */}
      {showEditar && (
        <EditarGrupoModal
          grupo={grupo}
          onClose={() => setShowEditar(false)}
          onActualizado={() => { setShowEditar(false); cargar(); }}
        />
      )}

      {/* Modal elegir tipo de ruta */}
      {showElegirTipoRuta && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }} onClick={e => e.target === e.currentTarget && setShowElegirTipoRuta(false)}>
          <div style={{
            width: "100%", maxWidth: 430,
            background: C.card, borderRadius: "20px 20px 0 0",
            padding: "20px 20px 30px",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Crear ruta del grupo</h3>
              <button onClick={() => setShowElegirTipoRuta(false)} style={{
                width: 28, height: 28, borderRadius: "50%",
                background: C.surface, border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 18, letterSpacing: "-0.005em" }}>
              ¿Cómo querés crear la ruta?
            </p>

            <button onClick={() => {
              setShowElegirTipoRuta(false);
              window.dispatchEvent(new CustomEvent("olbos-abrir-publicar-ruta", { detail: { grupoId, grupoNombre: grupo.nombre } }));
            }} style={{
              width: "100%", padding: "14px", borderRadius: 12,
              background: C.card, color: C.text,
              border: `1.5px solid ${C.border}`, cursor: "pointer", marginBottom: 10,
              display: "flex", alignItems: "center", gap: 12,
              textAlign: "left",
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: "#E6F2FF",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Map size={20} strokeWidth={1.8} color="#111111" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.005em", marginBottom: 2 }}>Planificar con waypoints</div>
                <div style={{ fontSize: 12, color: C.muted, letterSpacing: "-0.005em", lineHeight: 1.4 }}>Para preparar un recorrido por anticipado con paradas en el mapa</div>
              </div>
            </button>

            <button onClick={() => {
              setShowElegirTipoRuta(false);
              window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { grupoId } }));
            }} style={{
              width: "100%", padding: "14px", borderRadius: 12,
              background: C.card, color: C.text,
              border: `1.5px solid ${C.border}`, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 12,
              textAlign: "left",
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${C.green}14`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Navigation size={20} strokeWidth={1.8} color="#1F8347" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.005em", marginBottom: 2 }}>Grabar con GPS</div>
                <div style={{ fontSize: 12, color: C.muted, letterSpacing: "-0.005em", lineHeight: 1.4 }}>Para registrar una ruta mientras la estás haciendo en moto</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Confirmación de salida */}
      {confirmaSalida && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={e => e.target === e.currentTarget && setConfirmaSalida(false)}>
          <div style={{
            background: C.card, borderRadius: 16, padding: 22, maxWidth: 340, width: "100%",
            fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8, letterSpacing: "-0.01em" }}>
              ¿Salir del grupo?
            </h3>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 18, letterSpacing: "-0.005em" }}>
              Vas a dejar de ver el contenido privado del grupo. Si querés volver, vas a tener que ser invitado de nuevo (a menos que sea un grupo público).
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmaSalida(false)} style={{
                flex: 1, padding: "11px 0", borderRadius: 10,
                background: C.surface, color: C.text,
                border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 500,
              }}>Cancelar</button>
              <button onClick={salirDelGrupo} style={{
                flex: 1, padding: "11px 0", borderRadius: 10,
                background: "#FF3B30", color: "#fff",
                border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 500,
              }}>Salir</button>
            </div>
          </div>
        </div>
      )}
    </PageWrap>
  );
};

// ─── MODAL INVITAR MIEMBROS ───────────────────────────────────────────────
const InvitarMiembrosModal = ({ grupoId, grupoNombre, session, miembrosIds, onClose }) => {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [invitados, setInvitados] = useState(new Set()); // IDs ya invitados en esta sesión
  const [mensaje, setMensaje] = useState("");
  const [yaInvitadosBD, setYaInvitadosBD] = useState(new Set());
  // Link de invitación externo (WhatsApp)
  const [linkInfo, setLinkInfo] = useState(null);
  const [cargandoLink, setCargandoLink] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // Generar / recuperar el link al abrir el modal
  useEffect(() => {
    (async () => {
      setCargandoLink(true);
      // Asegurar sesión activa primero
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCargandoLink(false);
        return;
      }
      const { data: raw, error } = await supabase.rpc("get_o_crear_link_grupo", { p_grupo_id: grupoId });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!error && data?.token) setLinkInfo(data);
      setCargandoLink(false);
    })();
  }, [grupoId]);

  const urlInvitacion = linkInfo ? `https://olbos.ai/?join=${linkInfo.token}` : "";

  const compartirWhatsApp = () => {
    compartirNativo({
      texto: `Hola! Te invito a sumarte a "${grupoNombre}" en Olbos, la app para riders. Tocá el link para unirte directo al grupo (si no tenés cuenta, te la crea en segundos):`,
      url: urlInvitacion, titulo: grupoNombre,
    });
  };

  const copiarLink = () => {
    if (!urlInvitacion) return;
    navigator.clipboard.writeText(urlInvitacion);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  useEffect(() => {
    // Cargar invitaciones previas pendientes para no duplicar
    (async () => {
      const { data } = await supabase.from("grupo_invitaciones")
        .select("user_id")
        .eq("grupo_id", grupoId)
        .eq("tipo", "invitacion")
        .eq("estado", "pendiente");
      setYaInvitadosBD(new Set((data || []).map(d => d.user_id)));
    })();
  }, [grupoId]);

  useEffect(() => {
    if (!query || query.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      const { data } = await supabase.from("perfiles")
        .select("id, username, avatar_url, ciudad")
        .ilike("username", `%${String(query).trim().replace(/^@/, "")}%`)
        .limit(15);
      setResultados((data || []).filter(p => p.id !== session.user.id));
      setBuscando(false);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const invitar = async (userId) => {
    const { error } = await supabase.from("grupo_invitaciones").insert({
      grupo_id: grupoId,
      user_id: userId,
      tipo: "invitacion",
      invitado_por: session.user.id,
      estado: "pendiente",
      mensaje: mensaje.trim() || null,
    });
    if (error) {
      if (error.code === "23505") alert("Ese rider ya fue invitado");
      else alert("Error: " + error.message);
      return;
    }
    setInvitados(prev => new Set([...prev, userId]));
  };

  return (
    // Anclado ARRIBA: como bottom-sheet, el teclado del celu tapaba los resultados
    // de la búsqueda (mismo caso que el modal de transferir — feedback 10-ago-2026)
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "0 0 20px 20px",
        maxHeight: "72vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.text, fontSize: 14, cursor: "pointer" }}>Listo</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Invitar a {grupoNombre}</div>
          <div style={{ width: 40 }} />
        </div>

        <div style={{ padding: "16px 20px 28px" }}>
          {/* INVITAR POR LINK (WhatsApp, copiar) */}
          <div style={{ marginBottom: 18, padding: "14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 14 }}>💬</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>Invitar por WhatsApp</div>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, letterSpacing: "-0.005em" }}>Compartí un link con cualquier amigo, tenga o no cuenta en Olbos</div>
              </div>
            </div>
            {cargandoLink ? (
              <div style={{ padding: "8px 0", fontSize: 12, color: C.muted, textAlign: "center" }}>Generando link…</div>
            ) : urlInvitacion ? (
              <>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", background: C.card,
                  border: `1px solid ${C.border}`, borderRadius: 8,
                  marginBottom: 8, marginTop: 4,
                }}>
                  <div style={{ flex: 1, fontSize: 11.5, color: C.muted, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {urlInvitacion}
                  </div>
                  <button onClick={copiarLink} style={{
                    padding: "4px 10px", borderRadius: 6,
                    background: copiado ? "#27AE60" : C.surface,
                    color: copiado ? "#fff" : "#111111",
                    border: "none", cursor: "pointer", fontSize: 10.5, fontWeight: 500, flexShrink: 0,
                  }}>
                    {copiado ? "✓ Copiado" : "Copiar"}
                  </button>
                </div>
                <button onClick={compartirWhatsApp} style={{
                  width: "100%", padding: "10px", borderRadius: 8,
                  background: "#25D366", color: "#fff",
                  border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                  Compartir por WhatsApp
                </button>
                {linkInfo?.veces_usado > 0 && (
                  <div style={{ fontSize: 10.5, color: C.muted, textAlign: "center", marginTop: 8, letterSpacing: "-0.005em" }}>
                    {linkInfo.veces_usado} {linkInfo.veces_usado === 1 ? "persona se unió" : "personas se unieron"} con este link
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: "8px 0", fontSize: 12, color: C.muted, textAlign: "center" }}>No se pudo generar el link</div>
            )}
          </div>

          {/* Separador */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500 }}>O invitar a riders de Olbos</div>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          {/* Mensaje opcional */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Mensaje (opcional)</label>
            <input type="text" value={mensaje} onChange={e => setMensaje(e.target.value)}
              placeholder="Sumate a nuestro grupo..."
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em" }} />
          </div>

          {/* Buscador */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Buscar por username</label>
            <div style={{ position: "relative" }}>
              <input type="search" enterKeyHint="search" onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Ej: maccmix_motovlog"
                style={{ width: "100%", padding: "11px 12px 11px 36px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit" }} />
              <Search size={14} strokeWidth={2} color={C.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            </div>
          </div>

          {/* Resultados */}
          <div>
            {query.length < 2 ? (
              <p style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: "20px 0", letterSpacing: "-0.005em" }}>
                Escribí al menos 2 letras para buscar
              </p>
            ) : buscando ? (
              <p style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: "20px 0" }}>Buscando…</p>
            ) : resultados.length === 0 ? (
              <p style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: "20px 0", letterSpacing: "-0.005em" }}>
                Ningún rider matchea "{query}"
              </p>
            ) : (
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                {resultados.map((p, i) => {
                  const yaEsMiembro = miembrosIds.includes(p.id);
                  const yaInvitado = invitados.has(p.id) || yaInvitadosBD.has(p.id);
                  return (
                    <div key={p.id} style={{
                      padding: "10px 14px",
                      borderBottom: i < resultados.length - 1 ? `1px solid ${C.border}` : "none",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : C.surface,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {!p.avatar_url && <User size={16} strokeWidth={1.5} color={C.muted} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>@{p.username}</div>
                        {p.ciudad && <div style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>{p.ciudad}</div>}
                      </div>
                      {yaEsMiembro ? (
                        <span style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>Ya es miembro</span>
                      ) : yaInvitado ? (
                        <span style={{ fontSize: 11, color: "#27AE60", letterSpacing: "-0.005em", fontWeight: 500 }}>✓ Invitado</span>
                      ) : (
                        <button onClick={() => invitar(p.id)} style={{
                          padding: "5px 12px", borderRadius: 100,
                          background: C.accent, color: "#fff",
                          border: "none", cursor: "pointer",
                          fontSize: 11.5, fontWeight: 500, letterSpacing: "-0.005em",
                        }}>
                          Invitar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL EDITAR GRUPO ──────────────────────────────────────────────────
const EditarGrupoModal = ({ grupo, onClose, onActualizado }) => {
  const [nombre, setNombre] = useState(grupo.nombre || "");
  const [descripcion, setDescripcion] = useState(grupo.descripcion || "");
  const [ciudad, setCiudad] = useState(grupo.ciudad || "");
  const [privacidad, setPrivacidad] = useState(grupo.privacidad || "privado");
  const [fotoUrl, setFotoUrl] = useState(grupo.foto_url || "");
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) setFotoUrl(data.secure_url);
    } catch {
      alert("Error subiendo la imagen");
    }
    setSubiendoFoto(false);
  };

  const guardar = async () => {
    setError(null);
    if (!nombre.trim()) { setError("Necesitás un nombre para el grupo"); return; }
    setGuardando(true);
    const { error: errRpc } = await supabase.rpc("editar_grupo", {
      p_grupo_id: grupo.id,
      p_nombre: nombre.trim(),
      p_descripcion: descripcion.trim() || null,
      p_ciudad: ciudad.trim() || null,
      p_foto_url: fotoUrl || null,
      p_privacidad: privacidad,
    });
    if (errRpc) {
      setError("Error al guardar: " + errRpc.message);
      setGuardando(false);
      return;
    }
    setGuardando(false);
    onActualizado();
  };

  const PRIVACIDAD_OPCIONES = [
    { id: "privado",   t: "Privado",       d: "Solo por invitación de un admin" },
    { id: "solicitud", t: "Por solicitud", d: "Cualquiera puede pedir entrar, los admins aprueban" },
    { id: "publico",   t: "Público",       d: "Cualquier rider se une sin aprobación" },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.text, fontSize: 14, cursor: "pointer" }}>Cancelar</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Editar grupo</div>
          <button onClick={guardar} disabled={guardando || !nombre.trim()} style={{
            background: "transparent", border: "none", color: C.text,
            fontSize: 14, fontWeight: 600,
            cursor: guardando ? "default" : "pointer",
            opacity: (guardando || !nombre.trim()) ? 0.5 : 1,
          }}>
            {guardando ? "..." : "Guardar"}
          </button>
        </div>

        <div style={{ padding: "20px 20px 30px" }}>
          {/* Foto */}
          <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
            {fotoUrl ? (
              <div style={{ position: "relative" }}>
                <img loading="lazy" decoding="async" src={fotoUrl} alt="" style={{ width: 84, height: 84, borderRadius: 20, objectFit: "cover" }} />
                <button onClick={() => setFotoUrl("")} style={{
                  position: "absolute", top: -6, right: -6,
                  width: 24, height: 24, borderRadius: "50%",
                  background: C.accent, border: "none", color: "#fff",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={12} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <label style={{
                width: 84, height: 84, borderRadius: 20,
                border: `1.5px dashed ${C.border}`, background: C.surface,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: C.muted,
              }}>
                {subiendoFoto ? <span style={{ fontSize: 11 }}>...</span> : <Camera size={22} strokeWidth={1.5} />}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => subirFoto(e.target.files[0])} />
              </label>
            )}
          </div>

          {/* Nombre */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Nombre del grupo</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14.5, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em" }} />
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Descripción</label>
            <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em", resize: "vertical" }} />
          </div>

          {/* Ciudad */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Ciudad o zona</label>
            <input type="text" value={ciudad} onChange={e => setCiudad(e.target.value)}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em" }} />
          </div>

          {/* Privacidad */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 8 }}>Privacidad</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {PRIVACIDAD_OPCIONES.map(o => (
                <label key={o.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "11px 12px", borderRadius: 10,
                  border: `1.5px solid ${privacidad === o.id ? "#111111" : C.border}`,
                  cursor: "pointer",
                  background: privacidad === o.id ? C.surface : C.card,
                }}>
                  <input type="radio" name="priv-edit" checked={privacidad === o.id} onChange={() => setPrivacidad(o.id)}
                    style={{ accentColor: "#111111", marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>{o.t}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4, letterSpacing: "-0.005em" }}>{o.d}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ padding: "10px 12px", borderRadius: 10, background: "#FFF1F0", border: "1px solid #FFD7D2", fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em" }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── BUSCAR PERFILES (estilo Instagram) ───────────────────────────────────
const normBuscar = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const BuscarPerfilesModal = ({ onClose, session, setTab }) => {
  const [query, setQuery] = useState("");
  const [tabB, setTabB] = useState("riders"); // riders | eventos | pitstop
  const [riders, setRiders] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [perfilVer, setPerfilVer] = useState(null);
  const [recientes, setRecientes] = useState([]);
  const [historial, setHistorial] = useState(() => {
    try { return JSON.parse(localStorage.getItem("olbos_busquedas") || "[]"); } catch (e) { return []; }
  });

  // Riders recientes para la pantalla inicial
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("perfiles")
        .select("id, username, nombre, avatar_url, ciudad, bio")
        .order("numero_registro", { ascending: false })
        .limit(20);
      setRecientes(data || []);
    })();
  }, []);

  const guardarHistorial = (texto) => {
    const t = texto.trim();
    if (t.length < 2) return;
    const nuevo = [t, ...historial.filter(h => h.toLowerCase() !== t.toLowerCase())].slice(0, 8);
    setHistorial(nuevo);
    try { localStorage.setItem("olbos_busquedas", JSON.stringify(nuevo)); } catch (e) {}
  };

  // Búsqueda en las tres fuentes, con debounce. Sin acentos y exigiendo todas las palabras.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setRiders([]); setEventos([]); setNegocios([]); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      const tokens = normBuscar(q).split(/\s+/).filter(Boolean);
      const matchea = (campos) => {
        const heno = normBuscar(campos.filter(Boolean).join(" "));
        return tokens.every(tk => heno.includes(tk));
      };
      const like = `%${q}%`;
      const [rp, re, rn] = await Promise.all([
        supabase.from("perfiles").select("id, username, nombre, avatar_url, ciudad, bio")
          .or(`username.ilike.${like},nombre.ilike.${like},ciudad.ilike.${like}`).limit(40),
        supabase.from("eventos").select("id, titulo, fecha, fecha_fin, lugar, desde, hasta, tipo, foto_url")
          .or(`titulo.ilike.${like},lugar.ilike.${like},desde.ilike.${like},hasta.ilike.${like}`)
          .order("fecha", { ascending: true }).limit(40),
        supabase.from("negocios").select("id, nombre, categoria, ciudad, direccion, descripcion, logo_url")
          .or(`nombre.ilike.${like},categoria.ilike.${like},ciudad.ilike.${like},descripcion.ilike.${like}`).limit(40),
      ]);
      // Refuerzo en cliente: cubre acentos y búsquedas de varias palabras que el ilike no resuelve
      setRiders((rp.data || []).filter(p => matchea([p.username, p.nombre, p.ciudad, p.bio])));
      setEventos((re.data || []).filter(e => matchea([e.titulo, e.lugar, e.desde, e.hasta])));
      setNegocios((rn.data || []).filter(n => matchea([n.nombre, n.categoria, n.ciudad, n.direccion, n.descripcion])));
      setBuscando(false);
    }, 280);
    return () => clearTimeout(t);
  }, [query]);

  const hayBusqueda = query.trim().length >= 2;
  const subTabDeCategoria = (cat) => {
    const c = String(cat || "").toLowerCase();
    if (c.startsWith("concesionario")) return "concesionarios";
    if (c.startsWith("seguro") || c.startsWith("gestor")) return "tramites";
    if (c.startsWith("alquiler")) return "alquiler";
    if (c.startsWith("marca")) return "marcas";
    if (c.startsWith("restaurante") || c.startsWith("comida") || c.startsWith("parador")) return "comida";
    if (c.startsWith("hospedaje") || c.startsWith("hotel")) return "hospedajes";
    return "talleres";
  };
  const abrirEvento = (ev) => {
    guardarHistorial(query);
    window.olbosAbrirEventoId = ev.id;
    onClose();
    setTab && setTab("eventos");
  };
  const abrirNegocio = (n) => {
    guardarHistorial(query);
    window.olbosPitStopTab = subTabDeCategoria(n.categoria);
    // Pit Stop abre la ficha de este negocio al cargar (antes quedaba solo en el rubro)
    window.olbosAbrirNegocioId = n.id;
    onClose();
    setTab && setTab("mercado");
  };
  const fechaCorta = (f) => {
    if (!f) return "";
    const d = new Date(String(f).slice(0, 10) + "T12:00:00");
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
  };

  if (perfilVer) {
    return <DetallePerfilModal perfil={perfilVer} onClose={() => setPerfilVer(null)} session={session} />;
  }

  const TABS = [
    ["riders", "Riders", riders.length],
    ["eventos", "Eventos", eventos.length],
    ["pitstop", "Pit Stop", negocios.length],
  ];
  const listaRiders = hayBusqueda ? riders : recientes;

  return (
    // Anclado ARRIBA: como bottom-sheet, el teclado del celu tapaba los resultados
    // de la búsqueda (mismo caso que el modal de transferir — feedback 10-ago-2026)
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "0 0 20px 20px",
        maxHeight: "72vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        <div style={{
          padding: "calc(var(--sat) + 14px) 18px 0",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Buscar en Olbos</div>
            <button onClick={onClose} style={{
              width: 28, height: 28, borderRadius: "50%",
              background: C.surface, border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
          <div style={{ position: "relative" }}>
            <input type="search" enterKeyHint="search" onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && guardarHistorial(query)}
              placeholder="Riders, eventos, talleres, gestores…"
              autoFocus
              style={{
                width: "100%", padding: "10px 12px 10px 36px",
                borderRadius: 10, border: `1px solid ${C.border}`,
                fontSize: 14, color: C.text, boxSizing: "border-box",
                fontFamily: "inherit", letterSpacing: "-0.005em",
              }} />
            <Search size={14} strokeWidth={2} color={C.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            {query && (
              <button onClick={() => setQuery("")} style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                width: 22, height: 22, borderRadius: "50%",
                background: C.muted, color: "#fff",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={11} strokeWidth={3} />
              </button>
            )}
          </div>
          {/* solapas por tipo, con el contador de resultados */}
          {hayBusqueda && (
            <div style={{ display: "flex", gap: 4, marginTop: 10 }}>
              {TABS.map(([id, label, n]) => (
                <button key={id} onClick={() => setTabB(id)}
                  style={{
                    flex: 1, padding: "9px 4px", background: "none", border: "none", cursor: "pointer",
                    fontSize: 12.5, fontWeight: tabB === id ? 800 : 500,
                    color: tabB === id ? "#111111" : C.muted,
                    borderBottom: tabB === id ? "2px solid #111111" : "2px solid transparent",
                  }}>
                  {label}{n > 0 ? ` ${n}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px calc(28px + var(--sab))" }}>
          {/* búsquedas recientes */}
          {!hayBusqueda && historial.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Búsquedas recientes</div>
                <button onClick={() => { setHistorial([]); try { localStorage.removeItem("olbos_busquedas"); } catch (e) {} }}
                  style={{ background: "none", border: "none", fontSize: 11.5, color: C.muted, cursor: "pointer", fontWeight: 600 }}>Borrar</button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {historial.map((h, i) => (
                  <button key={i} onClick={() => setQuery(h)}
                    style={{ padding: "6px 12px", borderRadius: 100, background: C.surface, border: "none", fontSize: 12.5, color: C.text, fontWeight: 600, cursor: "pointer" }}>
                    {h}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!hayBusqueda && recientes.length > 0 && (
            <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              Riders recientes
            </div>
          )}

          {buscando ? (
            <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>Buscando…</div>
          ) : (
            <>
              {/* RIDERS */}
              {(!hayBusqueda || tabB === "riders") && (
                listaRiders.length === 0 && hayBusqueda ? (
                  <div style={{ padding: "30px 16px", textAlign: "center" }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                      <Search size={20} strokeWidth={1.5} color={C.muted} />
                    </div>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 4 }}>Ningún rider matchea "{query}"</div>
                    <div style={{ fontSize: 11.5, color: C.muted }}>Probá en las otras solapas</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {listaRiders.map(p => (
                      <button key={p.id} onClick={() => { guardarHistorial(query); setPerfilVer(p); }} style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 4px",
                        background: "transparent", border: "none", cursor: "pointer",
                        textAlign: "left", width: "100%",
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: "50%",
                          background: p.avatar_url ? `url(${cldThumb(p.avatar_url, 120)}) center/cover` : C.surface,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          {!p.avatar_url && <User size={18} strokeWidth={1.5} color={C.muted} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>@{p.username}</div>
                          {(p.nombre || p.ciudad || p.bio) && (
                            <div style={{ fontSize: 11.5, color: C.muted, letterSpacing: "-0.005em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                              {[p.nombre, p.ciudad].filter(Boolean).join(" · ")}
                              {(p.nombre || p.ciudad) && p.bio ? " · " : ""}{p.bio?.slice(0, 40)}{p.bio?.length > 40 ? "…" : ""}
                            </div>
                          )}
                        </div>
                        <ChevronRight size={15} strokeWidth={2} color="#c0c0c5" />
                      </button>
                    ))}
                  </div>
                )
              )}

              {/* EVENTOS */}
              {hayBusqueda && tabB === "eventos" && (
                eventos.length === 0 ? (
                  <div style={{ padding: "30px 16px", textAlign: "center", fontSize: 13, color: C.muted }}>Ningún evento matchea "{query}"</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {eventos.map(ev => (
                      <button key={ev.id} onClick={() => abrirEvento(ev)} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 4px",
                        background: "transparent", border: "none", cursor: "pointer", textAlign: "left", width: "100%",
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: ev.foto_url ? `#111 url(${cldThumb(ev.foto_url, 120)}) center/cover` : C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {!ev.foto_url && <Calendar size={18} strokeWidth={1.5} color={C.muted} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.titulo}</div>
                          <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                            {fechaCorta(ev.fecha)}{ev.lugar || ev.desde ? ` · ${ev.lugar || ev.desde}` : ""}
                          </div>
                        </div>
                        <ChevronRight size={15} strokeWidth={2} color="#c0c0c5" />
                      </button>
                    ))}
                  </div>
                )
              )}

              {/* PIT STOP */}
              {hayBusqueda && tabB === "pitstop" && (
                negocios.length === 0 ? (
                  <div style={{ padding: "30px 16px", textAlign: "center", fontSize: 13, color: C.muted }}>Ningún negocio matchea "{query}"</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {negocios.map(n => (
                      <button key={n.id} onClick={() => abrirNegocio(n)} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 4px",
                        background: "transparent", border: "none", cursor: "pointer", textAlign: "left", width: "100%",
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: n.logo_url ? `url(${cldThumb(n.logo_url, 120)}) center/cover` : C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {!n.logo_url && <Wrench size={18} strokeWidth={1.5} color={C.muted} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.nombre}</div>
                          <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                            {[n.categoria, n.ciudad].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <ChevronRight size={15} strokeWidth={2} color="#c0c0c5" />
                      </button>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DETALLE DE PERFIL (sub-modal del buscador) ───────────────────────────
const DetallePerfilModal = ({ perfil, onClose, session }) => {
  const [perfilCompleto, setPerfilCompleto] = useState(perfil);
  const [posts, setPosts] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Social
  const [social, setSocial] = useState({ seguidores: 0, siguiendo: 0, yo_lo_sigo: false, el_me_sigue: false, bloqueado: false });
  const [procesandoSeguir, setProcesandoSeguir] = useState(false);
  const [showOpciones, setShowOpciones] = useState(false);
  const [showReportar, setShowReportar] = useState(false);
  const [showMandarMensaje, setShowMandarMensaje] = useState(false);
  const [textoMensaje, setTextoMensaje] = useState("");
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);

  const esMiPerfil = session?.user?.id === perfil.id;

  const cargarSocial = async () => {
    const { data: raw } = await supabase.rpc("get_perfil_social", { p_user_id: perfil.id });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) setSocial(data);
    // Vistas de TODO lo que publicó (acá solo se cargan los últimos 20 posts)
    supabase.rpc("perfil_vistas_total", { p_user_id: perfil.id }).then(({ data: v, error }) => { if (!error) setVistasTot(Number(v) || 0); }, () => {});
  };
  const [vistasTot, setVistasTot] = useState(null);

  useEffect(() => {
    (async () => {
      setCargando(true);
      // Cargar perfil completo (con motos)
      const { data: pData } = await supabase.from("perfiles")
        .select("*").eq("id", perfil.id).single();
      if (pData) setPerfilCompleto(pData);

      // Cargar últimos posts
      const { data: postsData } = await supabase.from("posts")
        .select("*")
        .eq("user_id", perfil.id)
        .or("and(grupo_id.is.null,evento_id.is.null),compartido_feed_general.eq.true")
        .order("created_at", { ascending: false })
        .limit(20);
      setPosts(postsData || []);

      // Cargar stats sociales
      await cargarSocial();

      setCargando(false);
    })();
  }, [perfil.id]);

  const toggleSeguir = async () => {
    if (!session) { alert("Iniciá sesión para seguir riders"); return; }
    setProcesandoSeguir(true);
    const { error } = await supabase.rpc("toggle_seguir", { p_following_id: perfil.id });
    if (error) alert("Error: " + error.message);
    else await cargarSocial();
    setProcesandoSeguir(false);
  };

  const enviarMensaje = async () => {
    if (!textoMensaje.trim()) return;
    setEnviandoMensaje(true);
    const { error } = await supabase.rpc("enviar_mensaje", {
      p_destino_id: perfil.id,
      p_texto: textoMensaje.trim(),
    });
    if (error) {
      alert("Error: " + error.message);
      setEnviandoMensaje(false);
      return;
    }
    setTextoMensaje("");
    setShowMandarMensaje(false);
    setEnviandoMensaje(false);
    alert(social.el_me_sigue || social.yo_lo_sigo
      ? "Mensaje enviado. Lo vas a ver en 'Mensajes'."
      : "Mensaje enviado. Como no se siguen, va a quedar como solicitud para él hasta que la acepte.");
  };

  const reportar = async (motivo) => {
    if (!session) return;
    await supabase.from("reportes").insert({
      reporter_id: session.user.id,
      reported_id: perfil.id,
      tipo: "usuario",
      referencia_id: perfil.id,
      motivo,
    });
    setShowReportar(false);
    setShowOpciones(false);
    alert("Reportado. Vamos a revisarlo. Gracias por avisar.");
  };

  const toggleBloquear = async () => {
    if (!session) return;
    const accion = social.bloqueado ? "desbloquear" : "bloquear";
    if (!window.confirm(`¿${accion === "bloquear" ? "Bloquear" : "Desbloquear"} a @${perfilCompleto.username}?`)) return;
    const { error } = await supabase.rpc("toggle_bloquear", { p_blocked_id: perfil.id });
    if (error) alert("Error: " + error.message);
    else {
      // Al bloquear (no al desbloquear) le avisamos al equipo: queda en la bandeja de
      // reportes del AdminPanel. Lo exige Apple en la guideline 1.2.
      if (accion === "bloquear") {
        supabase.from("reportes").insert({
          reporter_id: session.user.id, reported_id: perfil.id,
          tipo: "bloqueo", referencia_id: perfil.id,
          motivo: "Usuario bloqueado desde su perfil",
        }).then(() => {}, () => {});
      }
      invalidarOcultos(); // el feed deja de mostrar su contenido al instante
      await cargarSocial();
      setShowOpciones(false);
    }
  };

  // Silenciar: dejar de ver su contenido sin bloquear (él no se entera)
  const [silenciado, setSilenciado] = useState(false);
  useEffect(() => {
    if (!session) return;
    (async () => { const o = await getOcultos(); setSilenciado(o.silenciados.includes(perfil.id)); })();
  }, [perfil.id, session]);

  const toggleSilenciar = async () => {
    if (!session) { alert("Iniciá sesión."); return; }
    const { data: raw, error } = await supabase.rpc("toggle_silenciar", { p_id: perfil.id });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) { alert("Error: " + (error?.message || r?.error)); return; }
    invalidarOcultos();
    setSilenciado(!!r?.silenciado);
    setShowOpciones(false);
    alert(r?.silenciado
      ? `Listo: no vas a ver más publicaciones de @${perfilCompleto.username}. Él no se entera, y podés revertirlo cuando quieras.`
      : `Vas a volver a ver publicaciones de @${perfilCompleto.username}.`);
  };

  // Solo motos activas: el borrado es suave (quedan en el JSON con estado "eliminada")
  const motos = (perfilCompleto?.motos || []).filter(m => m && (!m.estado || m.estado === "activa"));

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 600,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        <div style={{
          padding: "calc(var(--sat) + 12px) 16px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{
            display: "flex", alignItems: "center", gap: 4,
            background: "transparent", border: "none", cursor: "pointer",
            color: C.text, fontSize: 14,
          }}>
            <ChevronLeft size={18} strokeWidth={2} /> Volver
          </button>
          {!esMiPerfil && session && (
            <button onClick={() => setShowOpciones(true)} style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.text, fontSize: 18, fontWeight: 700,
            }}>⋯</button>
          )}
          {(esMiPerfil || !session) && <div style={{ width: 24 }} />}
        </div>

        <div style={{ padding: "24px 20px 30px" }}>
          {/* Avatar + info */}
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{
              width: 96, height: 96, borderRadius: "50%",
              background: perfilCompleto.avatar_url ? `url(${perfilCompleto.avatar_url}) center/cover` : C.surface,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              {!perfilCompleto.avatar_url && <User size={36} strokeWidth={1.2} color={C.muted} />}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 4 }}>
              @{perfilCompleto.username}
            </h2>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}><SelloConcesionario userId={perfilCompleto.id} grande /></div>
            {perfilCompleto.ciudad && (
              <div style={{ fontSize: 13, color: C.muted, letterSpacing: "-0.005em", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 8 }}>
                <MapPin size={11} strokeWidth={2} /> {perfilCompleto.ciudad}
              </div>
            )}
            {perfilCompleto.bio && (
              <p style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, letterSpacing: "-0.005em", marginTop: 10, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                {perfilCompleto.bio}
              </p>
            )}
            {perfilCompleto.numero_registro && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 10, letterSpacing: "0.04em" }}>
                Rider #{perfilCompleto.numero_registro}
                {perfilCompleto.numero_registro <= 300 && (
                  <span style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 100, background: C.accent, color: "#fff", fontSize: 9, fontWeight: 600, letterSpacing: "0.04em" }}>⚡ ORIGINAL</span>
                )}
              </div>
            )}
          </div>

          {/* Stats sociales */}
          <div style={{ display: "flex", justifyContent: "center", gap: 28, marginBottom: 18, padding: "12px 0", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{posts.length}</div>
              <div style={{ fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>Publicaciones</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{social.seguidores}</div>
              <div style={{ fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>Seguidores</div>
            </div>
            {/* "Siguiendo" no se muestra (Carlos, 30-oct): en su lugar, las vistas de lo que publicó */}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{(vistasTot != null ? vistasTot : posts.reduce((a, p) => a + Number(p.vistas || 0), 0)).toLocaleString("es-AR")}</div>
              <div style={{ fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>Vistas</div>
            </div>
          </div>

          {/* Acciones: Seguir + Mensaje (solo si no es mi perfil y hay sesión) */}
          {!esMiPerfil && session && (
            <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
              <button onClick={toggleSeguir} disabled={procesandoSeguir} style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: social.yo_lo_sigo ? C.surface : "#111111",
                color: social.yo_lo_sigo ? "#111111" : "#fff",
                border: "none", cursor: procesandoSeguir ? "default" : "pointer",
                fontSize: 13.5, fontWeight: 500, letterSpacing: "-0.005em",
                opacity: procesandoSeguir ? 0.6 : 1,
              }}>
                {social.yo_lo_sigo ? "Siguiendo" : social.el_me_sigue ? "Seguirlo también" : "Seguir"}
              </button>
              <button onClick={() => setShowMandarMensaje(true)} style={{
                flex: 1, padding: "10px 0", borderRadius: 10,
                background: C.surface, color: C.text,
                border: "none", cursor: "pointer",
                fontSize: 13.5, fontWeight: 500, letterSpacing: "-0.005em",
              }}>
                Mensaje
              </button>
            </div>
          )}

          {/* Motos */}
          {motos.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                {motos.length === 1 ? "Su moto" : `Sus motos (${motos.length})`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {motos.slice(0, 5).map((m, i) => (
                  <div key={i} style={{
                    padding: "10px 12px", borderRadius: 10,
                    background: C.surface, border: `1px solid ${C.border}`,
                    fontSize: 13, color: C.text, letterSpacing: "-0.005em",
                  }}>
                    {m.marca} {m.modelo} {m.anio && `· ${m.anio}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Posts */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              {posts.length === 0 ? "Sin publicaciones" : `Publicaciones (${posts.length})`}
            </div>
            {cargando ? (
              <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 12 }}>Cargando…</div>
            ) : posts.length === 0 ? (
              <div style={{ padding: "20px 16px", textAlign: "center", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.muted, letterSpacing: "-0.005em" }}>
                Este rider todavía no publicó nada
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
                {posts.filter(p => p.media_url).slice(0, 12).map(p => {
                  const esVid = p.media_type === "video" || /\.(mp4|mov|webm)$/i.test(p.media_url || "");
                  const thumb = esVid ? cldPoster(p.media_url, 400) : cldThumb(p.media_url);
                  return (
                  <div key={p.id} style={{
                    aspectRatio: "1", overflow: "hidden", borderRadius: 4,
                    background: `#f1ece6 url(${thumb}) center/cover`,
                  }} />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* MODAL Opciones */}
        {showOpciones && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 700,
            background: "rgba(0,0,0,.45)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }} onClick={e => e.target === e.currentTarget && setShowOpciones(false)}>
            <div style={{
              width: "100%", maxWidth: 430,
              background: C.card, borderRadius: "20px 20px 0 0",
              padding: "12px 0 20px",
            }}>
              <div style={{ width: 36, height: 4, background: `${C.border}`, borderRadius: 100, margin: "0 auto 12px" }} />
              <button onClick={toggleSilenciar} style={{
                width: "100%", padding: "14px 20px", textAlign: "left",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 14.5, color: C.text, fontWeight: 500, letterSpacing: "-0.005em",
              }}>🔇 {silenciado ? "Dejar de silenciar" : "Silenciar"} a @{perfilCompleto.username}</button>
              <button onClick={() => { setShowOpciones(false); setShowReportar(true); }} style={{
                width: "100%", padding: "14px 20px", textAlign: "left",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 14.5, color: "#FF3B30", fontWeight: 500, letterSpacing: "-0.005em",
                borderTop: `1px solid ${C.border}`,
              }}>Reportar a @{perfilCompleto.username}</button>
              <button onClick={toggleBloquear} style={{
                width: "100%", padding: "14px 20px", textAlign: "left",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 14.5, color: "#FF3B30", fontWeight: 500, letterSpacing: "-0.005em",
                borderTop: `1px solid ${C.border}`,
              }}>{social.bloqueado ? "Desbloquear" : "Bloquear"} a @{perfilCompleto.username}</button>
              <button onClick={() => setShowOpciones(false)} style={{
                width: "100%", padding: "14px 20px", textAlign: "center",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 14.5, color: C.text, fontWeight: 600, letterSpacing: "-0.005em",
                borderTop: `8px solid ${C.border}`,
              }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* MODAL Reportar */}
        {showReportar && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 700,
            background: "rgba(0,0,0,.45)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }} onClick={e => e.target === e.currentTarget && setShowReportar(false)}>
            <div style={{
              width: "100%", maxWidth: 430,
              background: C.card, borderRadius: "20px 20px 0 0",
              padding: "16px 0 20px",
            }}>
              <div style={{ padding: "0 20px", marginBottom: 12 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4, letterSpacing: "-0.01em" }}>Reportar a @{perfilCompleto.username}</h3>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>¿Cuál es el motivo? Vamos a revisar el reporte.</p>
              </div>
              {["Spam o publicidad", "Acoso o lenguaje violento", "Información falsa o engañosa", "Estafa o intento de fraude", "Contenido inapropiado", "Otro motivo"].map(m => (
                <button key={m} onClick={() => reportar(m)} style={{
                  width: "100%", padding: "14px 20px", textAlign: "left",
                  background: "transparent", border: "none", cursor: "pointer",
                  fontSize: 14, color: C.text, fontWeight: 400, letterSpacing: "-0.005em",
                  borderTop: `1px solid ${C.border}`,
                }}>{m}</button>
              ))}
              <button onClick={() => setShowReportar(false)} style={{
                width: "100%", padding: "14px 20px", textAlign: "center",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 14.5, color: C.text, fontWeight: 600, letterSpacing: "-0.005em",
                borderTop: `8px solid ${C.border}`,
              }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* MODAL Mandar mensaje (mensaje inicial rápido) */}
        {showMandarMensaje && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 700,
            background: "rgba(0,0,0,.45)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }} onClick={e => e.target === e.currentTarget && setShowMandarMensaje(false)}>
            <div style={{
              width: "100%", maxWidth: 430,
              background: C.card, borderRadius: "20px 20px 0 0",
              padding: "16px 20px 24px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Mensaje a @{perfilCompleto.username}</h3>
                <button onClick={() => setShowMandarMensaje(false)} style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: C.surface, border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
              {!social.yo_lo_sigo && !social.el_me_sigue && (
                <div style={{ padding: "8px 10px", background: `${C.gold}14`, border: "1px solid #FFE89A", borderRadius: 8, fontSize: 11.5, color: C.gold, lineHeight: 1.5, marginBottom: 12, letterSpacing: "-0.005em" }}>
                  Como no se siguen, tu mensaje va a quedar como <strong>solicitud</strong> en su bandeja hasta que la acepte.
                </div>
              )}
              <textarea value={textoMensaje} onChange={e => setTextoMensaje(e.target.value)}
                placeholder="Escribí tu mensaje..."
                rows={4} autoFocus
                style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", letterSpacing: "-0.005em", resize: "vertical", marginBottom: 12 }} />
              <button onClick={enviarMensaje} disabled={!textoMensaje.trim() || enviandoMensaje} style={{
                width: "100%", padding: "12px", borderRadius: 12,
                background: !textoMensaje.trim() ? `${C.border}` : "#111111",
                color: "#fff", border: "none",
                cursor: !textoMensaje.trim() || enviandoMensaje ? "default" : "pointer",
                fontSize: 14, fontWeight: 500, letterSpacing: "-0.005em",
                opacity: enviandoMensaje ? 0.6 : 1,
              }}>
                {enviandoMensaje ? "Enviando..." : "Enviar mensaje"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MENSAJES (lista de conversaciones) ──────────────────────────────────
const Mensajes = ({ session, requireAuth, setMainTab }) => {
  const [tab, setTab] = useState("principales"); // 'principales' | 'solicitudes' | 'grupos'
  const [principales, setPrincipales] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [gruposChat, setGruposChat] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [conversacionAbierta, setConversacionAbierta] = useState(null);
  // Atrás vuelve a la lista de chats, no cierra la app
  useBackGuard(!!conversacionAbierta, () => setConversacionAbierta(null));
  const [mostrarTerminos, setMostrarTerminos] = useState(() => !localStorage.getItem("olbos_terminos_msg_aceptados"));
  const [verTerminosCompletos, setVerTerminosCompletos] = useState(false);
  // Nuevo mensaje (iniciar chat con cualquier rider)
  const [nuevoChat, setNuevoChat] = useState(false);
  const [buscarQ, setBuscarQ] = useState("");
  const [buscarRes, setBuscarRes] = useState([]);
  const buscarTimer = useRef(null);
  // Contactar al dueño de una moto por patente: el que escribe se identifica,
  // el dueño NO se revela — le llega el mensaje y decide si responder.
  const [contactarMoto, setContactarMoto] = useState(false);
  const [cdPatente, setCdPatente] = useState("");
  const [cdTexto, setCdTexto] = useState("");
  const [cdEnviando, setCdEnviando] = useState(false);
  const enviarAlDueno = async () => {
    if (!session?.user) { requireAuth?.(); return; }
    if (!cdPatente.trim() || !cdTexto.trim()) { alert("Completá la patente y el mensaje."); return; }
    setCdEnviando(true);
    await supabase.rpc("contactar_dueno_moto", { p_patente: cdPatente.trim(), p_texto: cdTexto.trim() }).then(() => {}, () => {});
    setCdEnviando(false);
    // SIEMPRE la misma respuesta, exista o no la moto: si confirmáramos la existencia,
    // cualquiera podría averiguar qué patentes están en Olbos probando.
    alert("Si esa moto está en Olbos, el dueño ya recibió tu mensaje con tu @usuario. Si quiere, te va a escribir.");
    setContactarMoto(false); setCdPatente(""); setCdTexto(""); setNuevoChat(false);
  };

  const buscarUsuarios = (q) => {
    setBuscarQ(q);
    clearTimeout(buscarTimer.current);
    if (q.trim().length < 2) { setBuscarRes([]); return; }
    buscarTimer.current = setTimeout(async () => {
      const { data } = await supabase.from("perfiles").select("id, username, avatar_url")
        .ilike("username", `%${q.trim().replace(/^@/, "")}%`).neq("id", session?.user?.id || "").limit(10);
      setBuscarRes(data || []);
    }, 280);
  };
  const abrirChatCon = (u) => {
    const existente = [...principales, ...solicitudes].find(c => c.otro_id === u.id);
    setNuevoChat(false); setBuscarQ(""); setBuscarRes([]);
    setConversacionAbierta(existente || { id: null, otro_id: u.id, username: u.username, avatar_url: u.avatar_url });
  };

  // Llegó desde el botón "Mensaje" de un perfil: abrir directo el chat con esa persona
  useEffect(() => {
    const p = window.__olbosDMPendiente;
    if (p && session?.user) {
      window.__olbosDMPendiente = null;
      setConversacionAbierta({ id: null, otro_id: p.id, username: p.username, avatar_url: p.avatar_url });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Push de mensaje tocada: abrir el chat de quien escribió (cuando la lista ya cargó)
  const abrirChatDeUsername = () => {
    const u = String(window.__olbosChatDeUsername || "").replace(/^@/, "").toLowerCase();
    if (!u) return;
    const c = [...principales, ...solicitudes].find(x => String(x.username || "").toLowerCase() === u);
    if (c) { window.__olbosChatDeUsername = ""; setConversacionAbierta(c); }
  };
  useEffect(() => {
    abrirChatDeUsername();
    const h = () => setTimeout(abrirChatDeUsername, 0);
    window.addEventListener("olbos-chat-de-username", h);
    return () => window.removeEventListener("olbos-chat-de-username", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principales, solicitudes]);

  const aceptarTerminos = () => {
    localStorage.setItem("olbos_terminos_msg_aceptados", "true");
    setMostrarTerminos(false);
  };

  const cargar = async () => {
    if (!session) { setCargando(false); return; }
    setCargando(true);
    const [convsRaw, gruposRaw] = await Promise.all([
      supabase.rpc("get_mis_conversaciones"),
      supabase.rpc("get_mis_grupos_chat"),
    ]);
    const dataConvs = typeof convsRaw.data === "string" ? JSON.parse(convsRaw.data) : convsRaw.data;
    if (dataConvs && !dataConvs.error) {
      setPrincipales(dataConvs.principales || []);
      setSolicitudes(dataConvs.solicitudes || []);
    }
    const dataGrupos = typeof gruposRaw.data === "string" ? JSON.parse(gruposRaw.data) : gruposRaw.data;
    if (dataGrupos && !dataGrupos.error) {
      setGruposChat(dataGrupos.grupos || []);
    }
    setCargando(false);
  };

  useEffect(() => { cargar(); }, [session]);

  // Suscribirse a cambios en mensajes (DMs + grupos) para refrescar lista
  useEffect(() => {
    if (!session) return;
    const ch = supabase
      .channel("mis_mensajes_listado")
      .on("postgres_changes", { event: "*", schema: "public", table: "mensajes" }, () => {
        if (!conversacionAbierta) cargar();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "grupo_mensajes" }, () => {
        if (!conversacionAbierta) cargar();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "grupo_lectura" }, () => {
        if (!conversacionAbierta) cargar();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session, conversacionAbierta]);

  if (conversacionAbierta) {
    return <ChatConversacion
      conversacion={conversacionAbierta}
      session={session}
      onBack={() => {
        const idAbierta = conversacionAbierta && conversacionAbierta.id;
        setConversacionAbierta(null);
        // Lo leído se ve al toque en la lista, sin esperar a la base
        if (idAbierta) {
          const cero = l => l.map(c => c.id === idAbierta ? { ...c, no_leidos: 0 } : c);
          setPrincipales(cero); setSolicitudes(cero);
        }
        // Recargar recién cuando terminó de marcar como leídos (antes corría en carrera y el contador volvía)
        Promise.resolve(window.__olbosMarcandoLeidos).then(() => cargar(), () => cargar());
      }}
    />;
  }

  const renderItem = (c, esSolicitud) => {
    const tieneNoLeidos = c.no_leidos > 0;
    return (
      <button key={c.id} onClick={async () => {
        // Abrir SIEMPRE primero: así ningún tracking/RPC puede bloquear la apertura
        setConversacionAbierta(c);
        if (esSolicitud) {
          // Aceptar solicitud automáticamente al abrir
          supabase.rpc("aceptar_solicitud", { p_conversacion_id: c.id }).then(() => {}, () => {});
        }
        // Si es el bot @olbos, trackear apertura para métricas
        // (el builder de Supabase es un thenable sin .catch → usar .then(ok, err))
        if (c.otro_id === "571720dc-a089-4e8a-a01b-7d1f92d99dbe") {
          supabase.rpc("marcar_dm_bot_abierto").then(() => {}, () => {});
        }
      }} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "12px 18px",
        background: tieneNoLeidos ? C.surface : "transparent",
        border: "none", borderBottom: `1px solid ${C.border}`,
        cursor: "pointer", textAlign: "left",
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          background: c.avatar_url ? `url(${c.avatar_url}) center/cover` : C.surface,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {!c.avatar_url && <User size={20} strokeWidth={1.5} color={C.muted} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: tieneNoLeidos ? 700 : 500, color: C.text, letterSpacing: "-0.005em" }}>
              @{c.username || "rider"}
            </span>
            {c.ultimo_at && (
              <span style={{ fontSize: 10.5, color: C.muted, letterSpacing: "-0.005em", flexShrink: 0, marginLeft: 8 }}>
                {formatRelativo(c.ultimo_at)}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{
              fontSize: 12.5, color: tieneNoLeidos ? "#111111" : C.muted,
              fontWeight: tieneNoLeidos ? 500 : 400,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              letterSpacing: "-0.005em", flex: 1,
            }}>
              {c.ultimo_sender === session?.user?.id && "Vos: "}
              {c.ultimo_texto || (c.ultimo_media_type === "video" ? "🎥 Video" : c.ultimo_media_type === "image" ? "📷 Foto" : "(sin mensajes)")}
            </span>
            {tieneNoLeidos && (
              <span style={{
                fontSize: 9.5, fontWeight: 700,
                padding: "2px 7px", borderRadius: 100,
                background: C.accent, color: "#fff",
                flexShrink: 0,
              }}>{c.no_leidos}</span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 24px 18px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Mensajes</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: C.text,
            letterSpacing: "-0.03em", lineHeight: 1.1,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}>Mensajes</h1>
          {session && (
            <button onClick={() => setNuevoChat(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, flexShrink: 0, letterSpacing: "-0.005em" }}>
              <Edit2 size={14} strokeWidth={2.5} /> Nuevo
            </button>
          )}
        </div>
        <VideoAyuda seccion="mensajes" style={{ marginTop: 8 }} />
      </div>

      {/* Banner términos primera vez */}
      {session && mostrarTerminos && (
        <div style={{ padding: "12px 16px", background: `${C.gold}14`, borderBottom: "1px solid #FFE89A" }}>
          <div style={{ fontSize: 12, color: C.gold, lineHeight: 1.5, letterSpacing: "-0.005em", marginBottom: 8 }}>
            Al usar los mensajes aceptás las <button onClick={() => setVerTerminosCompletos(true)} style={{ background: "transparent", border: "none", color: C.gold, textDecoration: "underline", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>normas de la comunidad</button>: respeto, sin spam, sin estafas, sin acoso. Reportá cualquier abuso y bloqueá lo que no quieras ver.
          </div>
          <button onClick={aceptarTerminos} style={{
            padding: "6px 14px", borderRadius: 100,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer",
            fontSize: 11.5, fontWeight: 500, letterSpacing: "-0.005em",
          }}>Entendido</button>
        </div>
      )}

      {/* Modal Términos completos */}
      {verTerminosCompletos && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 800,
          background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }} onClick={e => e.target === e.currentTarget && setVerTerminosCompletos(false)}>
          <div style={{
            width: "100%", maxWidth: 430,
            background: C.card, borderRadius: "20px 20px 0 0",
            maxHeight: "85vh", overflowY: "auto",
            padding: "16px 20px 28px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Normas de la comunidad</h3>
              <button onClick={() => setVerTerminosCompletos(false)} style={{
                width: 28, height: 28, borderRadius: "50%",
                background: C.surface, border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, letterSpacing: "-0.005em" }}>
              <p style={{ marginBottom: 12 }}>Olbos es una comunidad de riders. Al usar el chat aceptás lo siguiente:</p>

              <p style={{ marginBottom: 8 }}><strong>1. Respeto.</strong> Nada de insultos, amenazas, discriminación, ni acoso. La comunidad es plural — riders de todas las edades, géneros, marcas, estilos.</p>

              <p style={{ marginBottom: 8 }}><strong>2. Sin spam.</strong> No mandes mensajes masivos, no promociones cosas sin permiso, no inundes a otros con contenido no solicitado.</p>

              <p style={{ marginBottom: 8 }}><strong>3. Sin estafas.</strong> No hagas ofertas falsas, no pidas plata con engaños, no te hagas pasar por otra persona o marca.</p>

              <p style={{ marginBottom: 8 }}><strong>4. Contenido inapropiado.</strong> Nada sexual no solicitado, nada violento, nada que pongan en riesgo a otros.</p>

              <p style={{ marginBottom: 8 }}><strong>5. Privacidad.</strong> No compartas datos personales de otros sin su permiso.</p>

              <p style={{ marginBottom: 12 }}><strong>6. Reportes.</strong> Si ves algo que no corresponde, usá el botón de reportar. Lo revisamos.</p>

              <p style={{ marginBottom: 12, padding: "10px 12px", background: `${C.gold}14`, borderRadius: 8, color: C.gold, fontSize: 12.5 }}>
                Las violaciones graves pueden derivar en advertencias, suspensión o eliminación de cuenta. La moderación es a discreción del equipo de Olbos.
              </p>

              <p style={{ fontSize: 11.5, color: C.muted, marginTop: 16 }}>
                Estas normas son una versión inicial y se van a actualizar. Si tenés sugerencias, escribinos. <br/>
                Olbos · 2026
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {session && (
        <div style={{ padding: "12px 16px 8px", background: C.card, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", gap: 3, padding: 3, background: C.surface, borderRadius: 10 }}>
            {[
              ["principales", `Principal${principales.length > 0 ? ` (${principales.length})` : ""}`],
              ["solicitudes", `Solicitudes${solicitudes.length > 0 ? ` (${solicitudes.length})` : ""}`],
              ["grupos", (() => {
                const totalNoLeidos = gruposChat.reduce((acc, g) => acc + (g.no_leidos || 0), 0);
                return `Grupos${totalNoLeidos > 0 ? ` (${totalNoLeidos})` : ""}`;
              })()],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                flex: 1, padding: "8px 0",
                background: tab === id ? C.card : "transparent",
                border: "none", borderRadius: 8,
                color: tab === id ? "#111111" : C.muted,
                fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                letterSpacing: "-0.005em",
                boxShadow: tab === id ? "0 1px 3px rgba(0,0,0,.06)" : "none",
              }}>{label}</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ paddingBottom: 100 }}>
        {!session ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <MessageCircle size={24} strokeWidth={1.5} color={C.muted} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>Iniciá sesión</h3>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 16, maxWidth: 300, marginLeft: "auto", marginRight: "auto" }}>
              Para tener mensajes con otros riders necesitás una cuenta.
            </p>
            <button onClick={() => requireAuth && requireAuth(() => {})} style={{
              padding: "10px 22px", borderRadius: 100,
              background: C.accent, color: "#fff",
              border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500,
            }}>Iniciar sesión</button>
          </div>
        ) : cargando ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        ) : tab === "principales" ? (
          principales.length === 0 ? (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <MessageCircle size={22} strokeWidth={1.5} color={C.muted} />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Sin mensajes todavía</h3>
              <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto", letterSpacing: "-0.005em" }}>
                Cuando empieces a charlar con otros riders, las conversaciones aparecen acá.
              </p>
            </div>
          ) : (
            <div>{principales.map(c => renderItem(c, false))}</div>
          )
        ) : tab === "solicitudes" ? (
          solicitudes.length === 0 ? (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Bell size={22} strokeWidth={1.5} color={C.muted} />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Sin solicitudes</h3>
              <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto", letterSpacing: "-0.005em" }}>
                Si un rider que no seguís te manda un mensaje, va a aparecer acá para que decidas si aceptar la conversación.
              </p>
            </div>
          ) : (
            <>
              <div style={{ padding: "10px 18px 6px", fontSize: 11, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
                Estas son personas que no seguís. Tocá una para ver el mensaje y decidir si aceptar la conversación.
              </div>
              {solicitudes.map(c => renderItem(c, true))}
            </>
          )
        ) : (
          // Tab Grupos
          gruposChat.length === 0 ? (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Users size={22} strokeWidth={1.5} color={C.muted} />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Sin grupos todavía</h3>
              <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto", letterSpacing: "-0.005em" }}>
                Cuando te sumes a un grupo de riders, sus chats grupales aparecen acá.
              </p>
            </div>
          ) : (
            <div>
              {gruposChat.map(g => (
                <button key={g.grupo_id}
                  onClick={() => {
                    // 1. Navegar a la sección Mis Grupos
                    if (setMainTab) setMainTab("grupos");
                    // 2. Disparar evento para abrir ese grupo (con pequeño delay para asegurar mount)
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent("olbos-abrir-grupo", {
                        detail: { grupoId: g.grupo_id, tab: "chat" }
                      }));
                    }, 50);
                  }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 18px",
                    background: g.no_leidos > 0 ? C.surface : "transparent",
                    border: "none", borderBottom: `1px solid ${C.border}`,
                    cursor: "pointer", textAlign: "left",
                  }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%",
                    background: g.imagen_url ? `url(${g.imagen_url}) center/cover` : "linear-gradient(135deg, #1F8347, #2BA85F)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {!g.imagen_url && <Users size={20} strokeWidth={2} color="#fff" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.005em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {g.nombre}
                      </div>
                      {g.ultimo_at && (
                        <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, letterSpacing: "-0.005em" }}>
                          {formatRelativo(g.ultimo_at)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 2 }}>
                      <span style={{
                        fontSize: 12.5, color: g.no_leidos > 0 ? "#111111" : C.muted,
                        fontWeight: g.no_leidos > 0 ? 500 : 400,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        letterSpacing: "-0.005em", flex: 1,
                      }}>
                        {g.ultimo_sender_username && `${g.ultimo_sender_username}: `}
                        {g.ultimo_texto || (g.ultimo_media_type === "video" ? "🎥 Video" : g.ultimo_media_type === "image" ? "📷 Foto" : "(sin mensajes)")}
                      </span>
                      {g.no_leidos > 0 && (
                        <span style={{
                          fontSize: 9.5, fontWeight: 700,
                          padding: "2px 7px", borderRadius: 100,
                          background: C.accent, color: "#fff",
                          flexShrink: 0,
                        }}>
                          {g.no_leidos > 99 ? "99+" : g.no_leidos}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </div>

      {/* Nuevo mensaje: buscar un rider e iniciar chat */}
      {nuevoChat && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "flex-start", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setNuevoChat(false)}>
          <div style={{ background: C.card, borderRadius: "0 0 20px 20px", width: "100%", maxWidth: 430, padding: "calc(var(--sat) + 16px) 18px 20px", maxHeight: "72vh", display: "flex", flexDirection: "column", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }} className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Nuevo mensaje</h2>
              <button onClick={() => setNuevoChat(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            {!contactarMoto && (
              <div style={{ position: "relative", marginBottom: 12 }}>
                <Search size={16} strokeWidth={2} color={C.muted} style={{ position: "absolute", left: 13, top: 13 }} />
                <input autoFocus value={buscarQ} onChange={e => buscarUsuarios(e.target.value)} placeholder="Buscar un rider por su @usuario…" type="search" enterKeyHint="search" onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                  style={{ width: "100%", padding: "12px 12px 12px 38px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.surface, boxSizing: "border-box" }} />
              </div>
            )}

            {/* Contactar al dueño de una moto: viste una moto en la calle (en venta,
                mal estacionada, con las luces prendidas…) y querés avisarle al dueño.
                Vos te identificás; el dueño no se revela hasta que decida responderte. */}
            {!contactarMoto ? (
              <button onClick={() => setContactarMoto(true)}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 14px", borderRadius: 12, background: "#f9f5f2", border: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left", marginBottom: 12 }}>
                <Bike size={18} strokeWidth={1.8} color="#111111" style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>Contactar al dueño de una moto</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>Por la patente — él decide si te responde</div>
                </div>
              </button>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <button onClick={() => setContactarMoto(false)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 12.5, fontWeight: 600, padding: "0 0 10px" }}>
                  <ChevronLeft size={14} strokeWidth={2} /> Volver a buscar riders
                </button>
                <input value={cdPatente} onChange={e => setCdPatente(e.target.value.toUpperCase())} placeholder="Patente de la moto (ej: AB123CD)"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.surface, boxSizing: "border-box", marginBottom: 8 }} />
                <textarea value={cdTexto} onChange={e => setCdTexto(e.target.value)} rows={3}
                  placeholder="Tu mensaje. Ej: 'Vi tu moto en la puerta del super con las luces prendidas' o 'Me interesa tu moto, ¿está en venta?'"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box", resize: "none", fontFamily: "inherit", marginBottom: 4 }} />
                <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                  El dueño va a ver tu @usuario y tu mensaje. Vos no vas a saber quién es, salvo que él te responda.
                </p>
                <button onClick={enviarAlDueno} disabled={cdEnviando}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: cdEnviando ? 0.7 : 1 }}>
                  {cdEnviando ? "Enviando…" : "Enviar al dueño"}
                </button>
              </div>
            )}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {!contactarMoto && buscarRes.map(u => (
                <button key={u.id} onClick={() => abrirChatCon(u)}
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 6px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", overflow: "hidden", background: C.surface, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {u.avatar_url ? <img loading="lazy" decoding="async" src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={18} color={C.muted} />}
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>@{u.username}</span>
                </button>
              ))}
              {!contactarMoto && buscarQ.trim().length >= 2 && buscarRes.length === 0 && (
                <div style={{ fontSize: 13.5, color: C.muted, textAlign: "center", padding: "24px 0" }}>No se encontró ningún rider con ese nombre.</div>
              )}
              {!contactarMoto && buscarQ.trim().length < 2 && (
                <div style={{ fontSize: 13, color: "#aeaeb2", textAlign: "center", padding: "24px 0", lineHeight: 1.5 }}>Escribí al menos 2 letras del @usuario para buscar.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageWrap>
  );
};

// Helper para fecha relativa
const formatRelativo = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
};

// ─── CHAT 1:1 ─────────────────────────────────────────────────────────────
const ChatConversacion = ({ conversacion, session, onBack }) => {
  const [verPerfilChat, setVerPerfilChat] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const scrollRef = useRef(null);
  // Estilo WhatsApp: el chat abre con el ULTIMO mensaje a la vista, siempre.
  // Reintentos escalonados porque las fotos cargan despues y empujan el alto;
  // si el usuario subio a leer historia, no se lo arrastra (salvo primera carga).
  const primeraCargaChatRef = useRef(true);
  // Mientras esté en true, cualquier cambio de alto del contenido vuelve al fondo.
  // Se apaga solo cuando el rider sube a leer historia, y se enciende de nuevo si baja.
  const pegadoAlFondoRef = useRef(true);
  // Marca al final de la conversación: si la que scrollea es la página (la caja crece con los
  // mensajes), scrollIntoView igual lleva al último. Carlos: "hay que scrollear infinito para abajo"
  const finRef = useRef(null);
  const scrollAbajo = (forzar = false) => {
    const el = scrollRef.current;
    if (!el) return;
    const cerca = el.scrollHeight - el.scrollTop - el.clientHeight < 250;
    if (forzar || cerca) { el.scrollTop = el.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" }); pegadoAlFondoRef.current = true; }
  };
  // Observa el alto real del contenido: cubre las fotos que cargan tarde, los videos
  // que reservan espacio al tener metadata y el teclado del teléfono.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const alFondo = () => { if (pegadoAlFondoRef.current) el.scrollTop = el.scrollHeight; };
    const ro = new ResizeObserver(alFondo);
    ro.observe(el);
    Array.from(el.children).forEach(c => ro.observe(c));
    const onScroll = () => {
      pegadoAlFondoRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", onScroll); };
  }, [conversacion.id]);
  useEffect(() => {
    if (!mensajes.length) return;
    const forzar = primeraCargaChatRef.current;
    if (forzar) primeraCargaChatRef.current = false;
    scrollAbajo(forzar);
    const t1 = setTimeout(() => scrollAbajo(forzar), 250);
    const t2 = setTimeout(() => scrollAbajo(forzar), 800);
    const t3 = setTimeout(() => scrollAbajo(forzar), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensajes.length]);
  const inputRef = useRef(null);
  // El teclado achica el viewport: al abrirse (o al cambiar su alto) se vuelve al último mensaje
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const alFondo = () => {
      if (!pegadoAlFondoRef.current) return;
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" }); }, 60);
    };
    vv.addEventListener("resize", alFondo);
    return () => vv.removeEventListener("resize", alFondo);
  }, []);

  const otroId = conversacion.otro_id;

  const cargar = async () => {
    setCargando(true);
    const { data: raw } = await supabase.rpc("get_mensajes_conversacion", { p_conversacion_id: conversacion.id });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) setMensajes(data.mensajes || []);
    setCargando(false);
    // Marcar como leídos los que ya estaban esperando. Sin esto solo se marcaban
    // los que llegaban con el chat abierto, así que el punto de "no leídos" no se
    // iba nunca por más que entraras a la conversación.
    if (session?.user?.id) {
      window.__olbosMarcandoLeidos = supabase.from("mensajes").update({ leido: true })
        .eq("conversacion_id", conversacion.id)
        .neq("sender_id", session.user.id)
        .eq("leido", false)
        .then(({ error }) => {
          if (error) reportarError("marcar leídos: " + error.message, null, "ChatConversacion");
          window.dispatchEvent(new CustomEvent("olbos-msgs-leidos"));
        }, () => {});
    }
    // Al fondo como WhatsApp: varios reintentos porque las fotos cargan DESPUÉS
    // del primer scroll y empujan el contenido — un solo salto a los 100ms te
    // dejaba a mitad de la conversación (reporte de Carlos)
    [50, 300, 800, 1600].forEach(t => setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, t));
  };

  useEffect(() => { cargar(); }, [conversacion.id]);

  // Suscripción Realtime a nuevos mensajes
  useEffect(() => {
    const ch = supabase
      .channel(`chat_${conversacion.id}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "mensajes", filter: `conversacion_id=eq.${conversacion.id}` },
        async (payload) => {
          setMensajes(prev => {
            // Evitar duplicados (si yo mismo lo agregué optimista)
            if (prev.some(m => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
          // Si llega un mensaje de otro, marcarlo leído
          if (payload.new.sender_id !== session?.user?.id) {
            await supabase.from("mensajes").update({ leido: true }).eq("id", payload.new.id);
          }
          setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
          }, 50);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [conversacion.id, session]);

  // Subir media (imagen o video) y enviar
  const enviarMedia = async (file) => {
    if (!file || enviando) return;
    if (!(await videoDentroDelLimite(file))) return; // tope de 90 s
    setEnviando(true);
    const isVideo = file.type.startsWith("video");
    const optimisticId = "temp_" + Date.now();
    const previewUrl = URL.createObjectURL(file);

    // Optimistic update con preview local
    setMensajes(prev => [...prev, {
      id: optimisticId,
      conversacion_id: conversacion.id,
      sender_id: session.user.id,
      texto: null,
      media_url: previewUrl,
      media_type: isVideo ? "video" : "image",
      leido: false,
      created_at: new Date().toISOString(),
      _optimistic: true,
    }]);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, 50);

    // Subir a Cloudinary
    const fd = new FormData();
    fd.append("file", await achicarImagen(file));
    fd.append("upload_preset", "olbos_uploads");
    try {
      const endpoint = isVideo
        ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
        : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error("Upload failed");

      const { data: raw, error } = await supabase.rpc("enviar_mensaje", {
        p_destino_id: otroId,
        p_texto: null,
        p_media_url: data.secure_url,
        p_media_type: isVideo ? "video" : "image",
      });
      if (error) throw error;
      const respuesta = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMensajes(prev => prev.map(m => m.id === optimisticId
        ? { ...m, id: respuesta.mensaje_id, media_url: data.secure_url, _optimistic: false }
        : m));
    } catch (err) {
      alert("Error al enviar la imagen: " + (err.message || err));
      setMensajes(prev => prev.filter(m => m.id !== optimisticId));
    }
    setEnviando(false);
  };

  const enviar = async () => {
    if (!texto.trim() || enviando) return;
    const textoEnviar = texto.trim();
    setTexto("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    setEnviando(true);
    // Optimistic update
    const optimisticId = "temp_" + Date.now();
    setMensajes(prev => [...prev, {
      id: optimisticId,
      conversacion_id: conversacion.id,
      sender_id: session.user.id,
      texto: textoEnviar,
      leido: false,
      created_at: new Date().toISOString(),
      _optimistic: true,
    }]);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, 50);

    const { data: raw, error } = await supabase.rpc("enviar_mensaje", {
      p_destino_id: otroId,
      p_texto: textoEnviar,
      p_media_url: null,
      p_media_type: null,
    });
    if (error) {
      alert("Error al enviar: " + error.message);
      // Revertir optimistic
      setMensajes(prev => prev.filter(m => m.id !== optimisticId));
      setTexto(textoEnviar);
    } else {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      // Reemplazar optimistic con el real
      setMensajes(prev => prev.map(m => m.id === optimisticId ? { ...m, id: data.mensaje_id, _optimistic: false } : m));
    }
    setEnviando(false);
    inputRef.current?.focus();
  };

  return (
    <PageWrap>
      {/* Header */}
      <div style={{
        padding: "var(--header-pt) 14px 12px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 10,
        background: C.card,
        // Fijo arriba: el chat baja hasta el último mensaje y el botón de volver no puede irse de la vista
        position: "sticky", top: 0, zIndex: 20,
      }}>
        <button onClick={onBack} style={{
          width: 32, height: 32, borderRadius: "50%",
          background: C.surface, border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ChevronLeft size={18} strokeWidth={2} color="#111111" />
        </button>
        {/* Foto y nombre llevan al perfil del otro rider (pedido de @MatiAguilar_26) */}
        <div onClick={() => conversacion.otro_id && setVerPerfilChat(true)} style={{
          width: 38, height: 38, borderRadius: "50%",
          background: conversacion.avatar_url ? `url(${conversacion.avatar_url}) center/cover` : C.surface,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, cursor: conversacion.otro_id ? "pointer" : "default",
        }}>
          {!conversacion.avatar_url && <User size={16} strokeWidth={1.5} color={C.muted} />}
        </div>
        <div onClick={() => conversacion.otro_id && setVerPerfilChat(true)} style={{ flex: 1, minWidth: 0, cursor: conversacion.otro_id ? "pointer" : "default" }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>
            @{conversacion.username || "rider"}
          </div>
        </div>
      </div>
      {verPerfilChat && conversacion.otro_id && <PerfilPublico userId={conversacion.otro_id} onClose={() => setVerPerfilChat(false)} />}

      {/* Mensajes */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: "auto",
        padding: "14px 14px 8px",
        background: C.surface,
        minHeight: "calc(100vh - 200px)",
      }}>
        {cargando ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        ) : mensajes.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: C.muted, fontSize: 12.5, letterSpacing: "-0.005em", lineHeight: 1.5 }}>
            Empezá la conversación. Sé respetuoso, esta es una comunidad chica.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {mensajes.map((m, i) => {
              const esMio = m.sender_id === session?.user?.id;
              const prev = i > 0 ? mensajes[i-1] : null;
              const mismoSender = prev && prev.sender_id === m.sender_id;
              const diffTiempo = prev ? (new Date(m.created_at) - new Date(prev.created_at)) / 60000 : 999;
              const agrupado = mismoSender && diffTiempo < 5;
              return (
                <div key={m.id} style={{
                  display: "flex", justifyContent: esMio ? "flex-end" : "flex-start",
                  marginTop: agrupado ? 0 : 8,
                }}>
                  <div style={{
                    maxWidth: "78%",
                    padding: m.media_url && !m.texto ? "4px 4px 4px 4px" : "8px 12px",
                    borderRadius: 16,
                    background: esMio ? "#111111" : C.card,
                    color: esMio ? "#fff" : "#111111",
                    border: esMio ? "none" : `1px solid ${C.border}`,
                    fontSize: 14, lineHeight: 1.4,
                    letterSpacing: "-0.005em",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    opacity: m._optimistic ? 0.7 : 1,
                    overflow: "hidden",
                  }}>
                    {m.media_url && (
                      <div style={{ marginBottom: m.texto ? 6 : 0, borderRadius: 12, overflow: "hidden", maxWidth: 240, background: "#000" }}>
                        {m.media_type === "video" ? (
                          <video src={cldOpt(m.media_url, 720)} poster={cldPoster(m.media_url, 480)} preload="metadata" controls style={{ width: "100%", display: "block", maxHeight: 320 }} />
                        ) : (
                          <img src={cldOpt(m.media_url, 720)} alt="" loading="lazy" decoding="async" onClick={() => window.open(m.media_url, "_blank")}
                            onError={e => { if (e.target.src !== m.media_url) e.target.src = m.media_url; }}
                            onLoad={() => { const el = scrollRef.current; if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 600) el.scrollTop = el.scrollHeight; }}
                            style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover", cursor: "pointer" }} />
                        )}
                      </div>
                    )}
                    {m.texto && (
                      <div style={{ padding: m.media_url ? "0 8px 4px 8px" : 0 }}>
                        {m.texto}
                      </div>
                    )}
                    {/* Link /vivo en el mensaje: se abre ADENTRO de la app. Con
                        ?rodar=1 es invitación (grabador); sin eso es seguimiento (visor). */}
                    {(() => {
                      const mVivo = m.texto && m.texto.match(/\/vivo\/([\w-]+)(\?[^\s]*)?/);
                      if (!mVivo) return null;
                      const esRodar = /rodar=1/.test(mVivo[2] || "");
                      return (
                        <button onClick={() => window.dispatchEvent(new CustomEvent(esRodar ? "olbos-abrir-vivo" : "olbos-ver-vivo", { detail: { token: mVivo[1] } }))}
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", marginTop: 6, padding: "9px 12px", borderRadius: 10, background: esMio ? "rgba(249,245,242,.15)" : "#111111", color: esMio ? "#fff" : "#F9F5F2", border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#27AE60", animation: "pulse 1.5s ease-in-out infinite" }} />
                          {esRodar ? "Entrar al mapa en vivo" : "Ver en vivo"}
                        </button>
                      );
                    })()}
                    <div style={{
                      fontSize: 9.5, opacity: 0.7, marginTop: 2,
                      textAlign: esMio ? "right" : "left",
                      letterSpacing: "0",
                      padding: m.media_url && !m.texto ? "0 8px 4px 8px" : 0,
                    }}>
                      {new Date(m.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                      {esMio && m.leido && " · leído"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Espacio para que el último mensaje quede ARRIBA del composer fijo (y del teclado) */}
        <div ref={finRef} style={{ height: "calc(84px + env(safe-area-inset-bottom))" }} />
      </div>

      {/* Composer */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 101,
        background: C.card, borderTop: `1px solid ${C.border}`,
        padding: "10px 12px calc(16px + env(safe-area-inset-bottom))",
        display: "flex", gap: 6, alignItems: "flex-end",
        maxWidth: 430, margin: "0 auto",
      }}>
        {/* Botón adjuntar imagen/video */}
        <label style={{
          width: 40, height: 40, borderRadius: "50%",
          background: C.surface, border: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, cursor: enviando ? "default" : "pointer",
          opacity: enviando ? 0.5 : 1,
        }}>
          <Camera size={16} strokeWidth={2} color="#111111" />
          <input type="file" accept="image/*,video/*" disabled={enviando}
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files[0];
              if (file) enviarMedia(file);
              e.target.value = ""; // reset
            }} />
        </label>
        <textarea
          ref={inputRef}
          value={texto}
          onChange={e => { setTexto(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px"; }}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
          placeholder="Escribir mensaje..."
          rows={1}
          style={{
            flex: 1, resize: "none",
            padding: "10px 14px", borderRadius: 20,
            border: `1px solid ${C.border}`, fontSize: 14,
            color: C.text, fontFamily: "inherit",
            letterSpacing: "-0.005em", lineHeight: 1.4,
            maxHeight: 100,
          }}
        />
        <button onClick={enviar} disabled={!texto.trim() || enviando}
          style={{
            width: 40, height: 40, borderRadius: "50%",
            background: texto.trim() ? "#111111" : `${C.border}`,
            color: "#fff", border: "none",
            cursor: texto.trim() && !enviando ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
          <Send size={16} strokeWidth={2} />
        </button>
      </div>
    </PageWrap>
  );
};

// ─── CHAT GRUPAL (dentro del grupo) ────────────────────────────────────────
const ChatGrupo = ({ grupoId, grupoNombre, session, esMiembro }) => {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const scrollRef = useRef(null);
  // Estilo WhatsApp: el chat abre con el ULTIMO mensaje a la vista, siempre.
  // Reintentos escalonados porque las fotos cargan despues y empujan el alto;
  // si el usuario subio a leer historia, no se lo arrastra (salvo primera carga).
  const primeraCargaChatRef = useRef(true);
  // Mientras esté en true, cualquier cambio de alto del contenido vuelve al fondo.
  // Se apaga solo cuando el rider sube a leer historia, y se enciende de nuevo si baja.
  const pegadoAlFondoRef = useRef(true);
  // Marca al final de la conversación: si la que scrollea es la página (la caja crece con los
  // mensajes), scrollIntoView igual lleva al último. Carlos: "hay que scrollear infinito para abajo"
  const finRef = useRef(null);
  const scrollAbajo = (forzar = false) => {
    const el = scrollRef.current;
    if (!el) return;
    const cerca = el.scrollHeight - el.scrollTop - el.clientHeight < 250;
    if (forzar || cerca) { el.scrollTop = el.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" }); pegadoAlFondoRef.current = true; }
  };
  // Observa el alto real del contenido: cubre las fotos que cargan tarde, los videos
  // que reservan espacio al tener metadata y el teclado del teléfono.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const alFondo = () => { if (pegadoAlFondoRef.current) el.scrollTop = el.scrollHeight; };
    const ro = new ResizeObserver(alFondo);
    ro.observe(el);
    Array.from(el.children).forEach(c => ro.observe(c));
    const onScroll = () => {
      pegadoAlFondoRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", onScroll); };
  }, [grupoId]);
  useEffect(() => {
    if (!mensajes.length) return;
    const forzar = primeraCargaChatRef.current;
    if (forzar) primeraCargaChatRef.current = false;
    scrollAbajo(forzar);
    const t1 = setTimeout(() => scrollAbajo(forzar), 250);
    const t2 = setTimeout(() => scrollAbajo(forzar), 800);
    const t3 = setTimeout(() => scrollAbajo(forzar), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensajes.length]);
  const inputRef = useRef(null);

  const cargar = async () => {
    setCargando(true);
    const { data: raw } = await supabase.rpc("get_mensajes_grupo", { p_grupo_id: grupoId, p_limit: 100 });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) setMensajes(data.mensajes || []);
    setCargando(false);
    // Marcar el chat como leído al abrirlo
    if (session?.user?.id) {
      supabase.from("grupo_lectura").upsert({
        grupo_id: grupoId,
        user_id: session.user.id,
        ultimo_leido_at: new Date().toISOString(),
      }).then(() => {}, () => {});
    }
    // Al fondo como WhatsApp: reintentos escalonados por las fotos que cargan tarde
    [50, 300, 800, 1600].forEach(t => setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, t));
  };

  useEffect(() => { if (esMiembro) cargar(); }, [grupoId, esMiembro]);

  // Realtime: nuevos mensajes en el grupo
  useEffect(() => {
    if (!esMiembro) return;
    const ch = supabase
      .channel(`chat_grupo_${grupoId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "grupo_mensajes", filter: `grupo_id=eq.${grupoId}` },
        async (payload) => {
          // Cargar el username/avatar del sender porque el INSERT no los trae
          const { data: perfil } = await supabase.from("perfiles")
            .select("username, avatar_url").eq("id", payload.new.sender_id).single();
          setMensajes(prev => {
            if (prev.some(m => m.id === payload.new.id)) return prev;
            return [...prev, {
              ...payload.new,
              sender_username: perfil?.username,
              sender_avatar: perfil?.avatar_url,
            }];
          });
          // Marcar como leído si no es mío
          if (payload.new.sender_id !== session?.user?.id) {
            await supabase.from("grupo_lectura").upsert({
              grupo_id: grupoId,
              user_id: session.user.id,
              ultimo_leido_at: new Date().toISOString(),
            });
          }
          setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
          }, 50);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [grupoId, esMiembro, session]);

  const enviarMedia = async (file) => {
    if (!file || enviando) return;
    if (!(await videoDentroDelLimite(file))) return; // tope de 90 s
    setEnviando(true);
    const isVideo = file.type.startsWith("video");
    const optimisticId = "temp_" + Date.now();
    const previewUrl = URL.createObjectURL(file);

    setMensajes(prev => [...prev, {
      id: optimisticId,
      grupo_id: grupoId,
      sender_id: session.user.id,
      texto: null,
      media_url: previewUrl,
      media_type: isVideo ? "video" : "image",
      created_at: new Date().toISOString(),
      sender_username: "vos",
      _optimistic: true,
    }]);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, 50);

    const fd = new FormData();
    fd.append("file", await achicarImagen(file));
    fd.append("upload_preset", "olbos_uploads");
    try {
      const endpoint = isVideo
        ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
        : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error("Upload failed");

      const { data: raw, error } = await supabase.rpc("enviar_mensaje_grupo", {
        p_grupo_id: grupoId,
        p_texto: null,
        p_media_url: data.secure_url,
        p_media_type: isVideo ? "video" : "image",
      });
      if (error) throw error;
      const respuesta = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMensajes(prev => prev.map(m => m.id === optimisticId
        ? { ...m, id: respuesta.mensaje_id, media_url: data.secure_url, _optimistic: false }
        : m));
    } catch (err) {
      alert("Error al enviar: " + (err.message || err));
      setMensajes(prev => prev.filter(m => m.id !== optimisticId));
    }
    setEnviando(false);
  };

  const enviar = async () => {
    if (!texto.trim() || enviando) return;
    const textoEnviar = texto.trim();
    setTexto("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    setEnviando(true);
    const optimisticId = "temp_" + Date.now();
    setMensajes(prev => [...prev, {
      id: optimisticId,
      grupo_id: grupoId,
      sender_id: session.user.id,
      texto: textoEnviar,
      media_url: null,
      created_at: new Date().toISOString(),
      sender_username: "vos",
      _optimistic: true,
    }]);
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
    }, 50);

    const { data: raw, error } = await supabase.rpc("enviar_mensaje_grupo", {
      p_grupo_id: grupoId,
      p_texto: textoEnviar,
      p_media_url: null,
      p_media_type: null,
    });
    if (error) {
      alert("Error al enviar: " + error.message);
      setMensajes(prev => prev.filter(m => m.id !== optimisticId));
      setTexto(textoEnviar);
    } else {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMensajes(prev => prev.map(m => m.id === optimisticId ? { ...m, id: data.mensaje_id, _optimistic: false } : m));
    }
    setEnviando(false);
    inputRef.current?.focus();
  };

  if (!esMiembro) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <MessageCircle size={20} strokeWidth={1.5} color={C.muted} />
        </div>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto", letterSpacing: "-0.005em" }}>
          Solo los miembros del grupo pueden ver el chat.
        </p>
      </div>
    );
  }

  return (
    <>
      <div ref={scrollRef} style={{
        background: C.surface, borderRadius: 12,
        padding: "12px 10px 8px",
        marginLeft: -8, marginRight: -8, marginBottom: 140,
        maxHeight: "60vh", overflowY: "auto",
        border: `1px solid ${C.border}`,
      }}>
        {cargando ? (
          <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        ) : mensajes.length === 0 ? (
          <div style={{ padding: "30px 20px", textAlign: "center", color: C.muted, fontSize: 12.5, lineHeight: 1.5 }}>
            No hay mensajes en este grupo todavía.<br/>Sé el primero en escribir.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {mensajes.map((m, i) => {
              const esMio = m.sender_id === session?.user?.id;
              const prev = i > 0 ? mensajes[i-1] : null;
              const mismoSender = prev && prev.sender_id === m.sender_id;
              const diffTiempo = prev ? (new Date(m.created_at) - new Date(prev.created_at)) / 60000 : 999;
              const agrupado = mismoSender && diffTiempo < 5;
              return (
                <div key={m.id} style={{
                  display: "flex", justifyContent: esMio ? "flex-end" : "flex-start",
                  marginTop: agrupado ? 0 : 8, gap: 6,
                }}>
                  {!esMio && !agrupado && (
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: m.sender_avatar ? `url(${m.sender_avatar}) center/cover` : C.surface,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginTop: 4,
                    }}>
                      {!m.sender_avatar && <User size={12} strokeWidth={1.5} color={C.muted} />}
                    </div>
                  )}
                  {!esMio && agrupado && <div style={{ width: 28, flexShrink: 0 }} />}
                  <div style={{
                    maxWidth: "75%",
                    padding: m.media_url && !m.texto ? "3px" : "6px 11px",
                    borderRadius: 14,
                    background: esMio ? "#111111" : C.card,
                    color: esMio ? "#fff" : "#111111",
                    border: esMio ? "none" : `1px solid ${C.border}`,
                    fontSize: 13.5, lineHeight: 1.4,
                    letterSpacing: "-0.005em",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    opacity: m._optimistic ? 0.7 : 1,
                    overflow: "hidden",
                  }}>
                    {!esMio && !agrupado && m.sender_username && (
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#1F8347", marginBottom: 2, letterSpacing: "-0.005em" }}>
                        @{m.sender_username}
                      </div>
                    )}
                    {m.media_url && (
                      <div style={{ marginBottom: m.texto ? 6 : 0, borderRadius: 11, overflow: "hidden", maxWidth: 220, background: "#000" }}>
                        {m.media_type === "video" ? (
                          <video src={cldOpt(m.media_url, 720)} poster={cldPoster(m.media_url, 480)} preload="metadata" controls style={{ width: "100%", display: "block", maxHeight: 280 }} />
                        ) : (
                          <img src={cldOpt(m.media_url, 720)} alt="" loading="lazy" decoding="async" onClick={() => window.open(m.media_url, "_blank")}
                            onError={e => { if (e.target.src !== m.media_url) e.target.src = m.media_url; }}
                            onLoad={() => { const el = scrollRef.current; if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 600) el.scrollTop = el.scrollHeight; }}
                            style={{ width: "100%", display: "block", maxHeight: 280, objectFit: "cover", cursor: "pointer" }} />
                        )}
                      </div>
                    )}
                    {m.texto && (
                      <div style={{ padding: m.media_url ? "0 6px 3px 6px" : 0 }}>
                        {m.texto}
                      </div>
                    )}
                    <div style={{
                      fontSize: 9.5, opacity: 0.6, marginTop: 2,
                      textAlign: esMio ? "right" : "left",
                      letterSpacing: "0",
                      padding: m.media_url && !m.texto ? "0 6px 3px 6px" : 0,
                    }}>
                      {new Date(m.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer fijo (arriba de la nav inferior) */}
      <div style={{
        position: "fixed", bottom: 56, left: 0, right: 0,
        background: C.card, borderTop: `1px solid ${C.border}`,
        padding: "10px 12px 12px",
        display: "flex", gap: 6, alignItems: "flex-end",
        maxWidth: 430, margin: "0 auto",
        zIndex: 200,
      }}>
        <label style={{
          width: 40, height: 40, borderRadius: "50%",
          background: C.surface, border: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, cursor: enviando ? "default" : "pointer",
          opacity: enviando ? 0.5 : 1,
        }}>
          <Camera size={16} strokeWidth={2} color="#111111" />
          <input type="file" accept="image/*,video/*" disabled={enviando}
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files[0];
              if (file) enviarMedia(file);
              e.target.value = "";
            }} />
        </label>
        <textarea
          ref={inputRef}
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); }
          }}
          placeholder={`Mensaje a ${grupoNombre}...`}
          rows={1}
          style={{
            flex: 1, resize: "none",
            padding: "10px 14px", borderRadius: 20,
            border: `1px solid ${C.border}`, fontSize: 14,
            color: C.text, fontFamily: "inherit",
            letterSpacing: "-0.005em", lineHeight: 1.4,
            maxHeight: 100,
          }}
        />
        <button onClick={enviar} disabled={!texto.trim() || enviando}
          style={{
            width: 40, height: 40, borderRadius: "50%",
            background: texto.trim() ? "#111111" : `${C.border}`,
            color: "#fff", border: "none",
            cursor: texto.trim() && !enviando ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
          <Send size={16} strokeWidth={2} />
        </button>
      </div>
    </>
  );
};

// ─── EN VIVO DEL GRUPO ────────────────────────────────────────────────────
// Sistema de compartir ubicación en tiempo real entre miembros del grupo.
// Updates cada 15s mientras está activo. Auto-stop tras 10min sin update.
const EnVivoGrupo = ({ grupoId, grupoNombre, session, esMiembro, creatorId = null, vozConfig = null, miembros = [] }) => {
  const [compartiendo, setCompartiendo] = useState(false);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [miUbicacion, setMiUbicacion] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const watchIdRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const ultimaPosRef = useRef(null); // última posición (para el heartbeat aunque esté parado)
  const wakeLockRef = useRef(null);
  // El "Mapa en vivo" abre EL mapa grande de siempre (grabador/navegador) con la
  // capa del grupo encima (GrupoVivoMapaOverlay) — acá queda el resumen pasivo.

  // Wake Lock para mantener la pantalla encendida mientras se comparte ubicación
  const activarWakeLock = async () => {
    try {
      if ("wakeLock" in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      }
    } catch (err) {
      console.warn("Wake Lock no disponible:", err);
    }
  };

  const liberarWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch {}
  };

  // Re-activar Wake Lock cuando la pestaña vuelve a foco
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === "visible" && compartiendo) {
        await activarWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [compartiendo]);

  // Cargar ubicaciones del grupo
  const cargarUbicaciones = async () => {
    const { data: raw } = await supabase.rpc("get_ubicaciones_grupo", { p_grupo_id: grupoId });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) {
      setUbicaciones(data.ubicaciones || []);
    }
  };

  useEffect(() => {
    cargarUbicaciones();
    // Polling cada 10s mientras estamos en la pantalla
    const interval = setInterval(cargarUbicaciones, 10000);
    return () => clearInterval(interval);
  }, [grupoId]);

  // Realtime: suscribirse a cambios en la tabla
  useEffect(() => {
    const channel = supabase
      .channel(`ubic_grupo_${grupoId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'ubicaciones_vivo_grupo', filter: `grupo_id=eq.${grupoId}` },
        () => { cargarUbicaciones(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [grupoId]);

  // Enviar mi ubicación al servidor (gp = {lat,lng,v,acc} de startGeoWatch)
  const enviarMiUbicacion = async (gp) => {
    if (!gp || gp.lat == null) return;
    ultimaPosRef.current = gp;
    let bateria = null;
    try {
      const battery = await navigator.getBattery?.();
      if (battery) bateria = Math.round(battery.level * 100);
    } catch { /* silenciar */ }

    const { error: errRpc } = await supabase.rpc("compartir_ubicacion", {
      p_grupo_id: grupoId,
      p_lat: gp.lat,
      p_lng: gp.lng,
      p_velocidad: gp.v ? gp.v * 3.6 : null, // m/s a km/h
      p_bateria: bateria,
    });
    if (errRpc) {
      console.error("Error compartiendo ubicación:", errRpc);
      setError("Error al compartir ubicación: " + errRpc.message);
      detenerCompartir();
    } else {
      setMiUbicacion({ lat: gp.lat, lng: gp.lng });
    }
  };

  const empezarACompartir = async () => {
    setCargando(true);
    setError(null);
    await activarWakeLock();
    try {
      // startGeoWatch sigue transmitiendo con la pantalla apagada / app en background (nativo).
      watchIdRef.current = await startGeoWatch(
        (gp) => { enviarMiUbicacion(gp); },
        (err) => { console.error("Watch error:", err); setError("No se pudo obtener tu ubicación."); }
      );
      setCompartiendo(true);
      setCargando(false);
      // Heartbeat: re-enviar la última posición cada 30s para mantenerla "viva" aunque estés parado.
      updateIntervalRef.current = setInterval(() => {
        if (ultimaPosRef.current) enviarMiUbicacion(ultimaPosRef.current);
      }, 30000);
    } catch (e) {
      setCargando(false);
      setError("No se pudo iniciar el compartido: " + (e.message || ""));
    }
  };

  const detenerCompartir = async () => {
    if (watchIdRef.current) {
      try { await watchIdRef.current.stop(); } catch (e) {}
      watchIdRef.current = null;
    }
    if (updateIntervalRef.current !== null) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    await liberarWakeLock();
    await supabase.rpc("dejar_de_compartir", { p_grupo_id: grupoId });
    setCompartiendo(false);
    setMiUbicacion(null);
    cargarUbicaciones();
  };

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (watchIdRef.current) watchIdRef.current.stop();
      if (updateIntervalRef.current !== null) clearInterval(updateIntervalRef.current);
      liberarWakeLock();
    };
  }, []);

  // Detectar si yo ya estoy en la lista (compartiendo desde otro device)
  const yaEstoyCompartiendo = ubicaciones.some(u => u.user_id === session?.user?.id);
  const otrosCompartiendo = ubicaciones.filter(u => u.user_id !== session?.user?.id);

  return (
    <>
      {/* Banner de aviso si estás compartiendo */}
      {compartiendo && (
        <div style={{
          padding: "10px 12px",
          background: "linear-gradient(135deg, #1F8347 0%, #27AE60 100%)",
          color: "#fff", borderRadius: 12, marginBottom: 14,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.card,
            animation: "pulse 1.4s ease-in-out infinite",
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "-0.005em" }}>
              Estás compartiendo tu ubicación
            </div>
            <div style={{ fontSize: 11, opacity: 0.92, letterSpacing: "-0.005em" }}>
              con los miembros de {grupoNombre}
            </div>
          </div>
          <button onClick={detenerCompartir} style={{
            padding: "6px 12px", borderRadius: 100,
            background: "rgba(255,255,255,0.18)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.3)",
            fontSize: 11.5, fontWeight: 500, cursor: "pointer", letterSpacing: "-0.005em",
          }}>
            Detener
          </button>
        </div>
      )}

      {/* UN solo botón (pedido de Carlos): abre EL mapa de siempre en modo grupo —
          comparte tu ubicación y dibuja a los demás encima. Se deja de compartir
          al cerrar el mapa. Grabar recorrido ya vive dentro de ese mapa. */}
      {esMiembro && (
        <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { grupoId, grupoEnVivo: true } }))} style={{
          width: "100%", padding: "14px", borderRadius: 12,
          background: C.accent, color: "#fff",
          border: "none", cursor: "pointer", marginBottom: 14,
          fontSize: 14.5, fontWeight: 800, letterSpacing: "-0.01em",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <Navigation size={16} strokeWidth={2} />
          Mapa en vivo
        </button>
      )}

      {/* Intercomunicador: solo con 2+ compartiendo — con uno solo no hay con quién hablar */}
      {esMiembro && ubicaciones.filter(u => (Date.now() - new Date(u.actualizado_en).getTime()) < 180000).length >= 2 && (
        <CanalDeVoz tipo="grupo" id={grupoId} creatorId={creatorId} vozConfig={vozConfig} tabla="grupos" miembros={miembros} />
      )}

      {error && (
        <div style={{ padding: "10px 12px", borderRadius: 10, background: "#FFF1F0", border: "1px solid #FFD7D2", fontSize: 12.5, color: "#B82F26", marginBottom: 12, letterSpacing: "-0.005em" }}>
          {error}
        </div>
      )}

      {/* Lista de quienes están compartiendo */}
      {ubicaciones.length === 0 ? (
        <div style={{ padding: "30px 24px", textAlign: "center", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Navigation size={20} strokeWidth={1.5} color={C.muted} />
          </div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.015em" }}>
            Nadie comparte ubicación ahora
          </h4>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
            Cuando un miembro empiece a compartir, lo vas a ver acá en tiempo real con su última posición y batería.
          </p>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            {ubicaciones.length} en vivo ahora
          </div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
            {ubicaciones.map((u, i) => {
              const minutosDesdeUpdate = Math.floor((Date.now() - new Date(u.actualizado_en).getTime()) / 60000);
              const esStale = minutosDesdeUpdate >= 3;
              const soyYo = u.user_id === session?.user?.id;
              return (
                <div key={u.user_id} style={{
                  padding: "12px 14px",
                  borderBottom: i < ubicaciones.length - 1 ? `1px solid ${C.border}` : "none",
                  display: "flex", alignItems: "center", gap: 10,
                  opacity: esStale ? 0.55 : 1,
                }}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%",
                      background: u.avatar_url ? `url(${u.avatar_url}) center/cover` : C.surface,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {!u.avatar_url && <User size={16} strokeWidth={1.5} color={C.muted} />}
                    </div>
                    {!esStale && (
                      <div style={{
                        position: "absolute", bottom: -1, right: -1,
                        width: 10, height: 10, borderRadius: "50%",
                        background: "#27AE60",
                        border: "2px solid #fff",
                        animation: "pulse 1.6s ease-in-out infinite",
                      }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>
                      @{u.username || "rider"}
                      {soyYo && <span style={{ color: C.muted, fontWeight: 400, marginLeft: 4 }}>(vos)</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted, marginTop: 2, letterSpacing: "-0.005em" }}>
                      <span>{esStale ? `hace ${minutosDesdeUpdate} min` : "hace segundos"}</span>
                      {u.velocidad != null && !esStale && <span>· {Math.round(u.velocidad)} km/h</span>}
                      {u.bateria != null && <span>· 🔋 {u.bateria}%</span>}
                    </div>
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${u.lat},${u.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      padding: "5px 10px", borderRadius: 100,
                      background: C.surface, color: C.text,
                      textDecoration: "none", fontSize: 11, fontWeight: 500,
                      letterSpacing: "-0.005em", whiteSpace: "nowrap",
                    }}>
                    Ver mapa
                  </a>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10, padding: "0 4px", lineHeight: 1.5, letterSpacing: "-0.005em" }}>
            Las posiciones se actualizan en tiempo real. Las que no se actualizan en 10 minutos desaparecen automáticamente.
          </div>
        </div>
      )}
    </>
  );
};

// Reordenar con flechas y no arrastrando: con guantes, en un teléfono y a veces al
// costado de la ruta, arrastrar es un gesto incómodo y fácil de errar.
const OrdenSecciones = ({ session, onCerrar, onCambio }) => {
  const [orden, setOrden] = useState(() => leerOrdenLocal().filter(id => id !== NAV_FIJA));
  const [guardando, setGuardando] = useState(false);

  const mover = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= orden.length) return;
    const nuevo = [...orden];
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    setOrden(nuevo);
  };

  const guardar = async () => {
    setGuardando(true);
    try { localStorage.setItem("olbos_orden_nav", JSON.stringify(orden)); } catch (e) { /* */ }
    onCambio && onCambio(orden);
    // En el perfil, para que lo lleve a cualquier teléfono donde entre
    if (session?.user?.id) {
      await supabase.from("perfiles").update({ orden_secciones: orden }).eq("id", session.user.id).then(() => {}, () => {});
    }
    setGuardando(false);
    onCerrar();
  };

  const restaurar = () => setOrden(ordenPorDefecto());

  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onCerrar} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={17} strokeWidth={2.4} color={C.text} />
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>Ordenar las secciones</h1>
      </div>

      <div style={{ padding: "8px 16px 120px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
          Poné arriba lo que más usás. La primera de la lista es la que se abre cuando entrás. «Más» queda siempre al final, porque es el menú de todo lo demás.
        </p>

        <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 16 }}>
          {orden.map((id, i) => {
            const it = NAV_ITEMS.find(x => x.id === id);
            if (!it) return null;
            const Icono = it.Icon;
            return (
              <div key={id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: i < orden.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <div style={{ width: 26, textAlign: "center", fontSize: 12, fontWeight: 800, color: C.muted }}>{i + 1}</div>
                <Icono size={18} strokeWidth={1.9} color={C.text} />
                <div style={{ flex: 1, fontSize: 14.5, fontWeight: 700, color: C.text }}>
                  {it.label}
                  {i === 0 && <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginLeft: 8 }}>se abre acá</span>}
                </div>
                <button onClick={() => mover(i, -1)} disabled={i === 0}
                  style={{ width: 34, height: 34, borderRadius: 9, background: C.surface, border: `1px solid ${C.border}`, cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.3 : 1, fontSize: 15, color: C.text }}>↑</button>
                <button onClick={() => mover(i, 1)} disabled={i === orden.length - 1}
                  style={{ width: 34, height: 34, borderRadius: 9, background: C.surface, border: `1px solid ${C.border}`, cursor: i === orden.length - 1 ? "default" : "pointer", opacity: i === orden.length - 1 ? 0.3 : 1, fontSize: 15, color: C.text }}>↓</button>
              </div>
            );
          })}
        </div>

        <button onClick={guardar} disabled={guardando}
          style={{ width: "100%", padding: "14px", borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 15, fontWeight: 800, cursor: guardando ? "default" : "pointer", opacity: guardando ? 0.6 : 1, marginBottom: 10 }}>
          {guardando ? "Guardando…" : "Guardar el orden"}
        </button>
        <button onClick={restaurar}
          style={{ width: "100%", padding: "12px", borderRadius: 13, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
          Volver al orden original
        </button>
      </div>
    </PageWrap>
  );
};

// ── PEDIDOS AL EQUIPO (20-oct-2026) ─────────────────────────────────────────────
// Canal directo del equipo (rol staff o admin) con el desarrollo: cada pedido lo toma
// una rutina en la nube varias veces por día, resuelve lo que puede y contesta acá
// mismo (con aviso a la campanita y al teléfono). Lo que necesita a Carlos queda
// "Espera a Carlos". SQL en docs/sql/pedidos_equipo.sql.
const ESTADOS_PEDIDO = {
  nuevo: { l: "En la fila", c: "#8A857F" }, en_curso: { l: "En curso", c: "#8A6D3B" }, hecho: { l: "Listo", c: "#2F7D4F" },
  necesita_info: { l: "Necesita un dato", c: "#B3372C" }, espera_carlos: { l: "Espera a Carlos", c: "#8A6D3B" }, descartado: { l: "Descartado", c: "#8A857F" },
};
const PedidosEquipo = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [lista, setLista] = useState(null);
  const [err, setErr] = useState("");
  const [tipo, setTipo] = useState("falla");
  const [texto, setTexto] = useState("");
  const [foto, setFoto] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const [resp, setResp] = useState("");
  const [soyAdmin, setSoyAdmin] = useState(false);
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data } = await supabase.from("perfiles").select("es_admin").eq("id", user.id).maybeSingle();
      setSoyAdmin(!!(data && data.es_admin));
    }, () => {});
  }, []);
  const decidir = async (p, aprueba) => {
    const texto = aprueba ? "✅ Aprobado. Hacelo." : "✗ No por ahora.";
    const { data: raw, error } = await supabase.rpc("pedido_equipo_contestar", { p_id: p.id, p_texto: texto });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { alert("No se pudo: " + (error?.message || d.error)); return; }
    registrarUso("pedido_equipo_decision", { aprueba });
    cargar();
  };
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("pedidos_equipo_lista");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error) { setErr("Todavía no está habilitado (falta un paso en el servidor)."); setLista([]); return; }
    if (d && d.error) { setErr(d.error === "no_equipo" ? "Esta sección es solo para el equipo de Olbos." : d.error); setLista([]); return; }
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const r = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const d = await r.json();
      if (d.secure_url) setFoto(d.secure_url); else alert("No se pudo subir la captura.");
    } catch (e) { alert("No se pudo subir la captura. Revisá la conexión."); }
    setSubiendo(false);
  };
  const enviar = async () => {
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    const { data: raw, error } = await supabase.rpc("pedido_equipo_crear", { p_texto: texto.trim(), p_tipo: tipo, p_foto_url: foto || "" });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setEnviando(false);
    if (error || (d && d.error)) { alert("No se pudo enviar: " + (error?.message || d.error)); return; }
    registrarUso("pedido_equipo", { tipo });
    setTexto(""); setFoto(null);
    cargar();
  };
  const contestar = async (id) => {
    if (!resp.trim()) return;
    const { data: raw, error } = await supabase.rpc("pedido_equipo_contestar", { p_id: id, p_texto: resp.trim() });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { alert("No se pudo enviar: " + (error?.message || d.error)); return; }
    setResp(""); cargar();
  };
  const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };
  const chip = (on) => ({ padding: "7px 12px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, border: `1px solid ${on ? C.text : C.border}`, background: on ? C.text : C.card, color: on ? "#fff" : C.text, cursor: "pointer" });
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={18} strokeWidth={2} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Pedidos al equipo</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 12px" }}>
          Contá qué no anda o qué mejorarías, con una captura si ayuda. Se revisan cada hora, de 9 a 23: lo que se puede resolver se arregla y se te contesta acá, con aviso al teléfono. Si algo de lo entregado no quedó bien, contestalo en el mismo pedido y vuelve a la fila. Lo que necesita una decisión de Carlos queda marcado.
        </p>
        {err ? (
          <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div>
        ) : (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 18 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {[["falla", "Algo no anda"], ["mejora", "Mejora"], ["idea", "Idea nueva"]].map(([id, l]) => <button key={id} onClick={() => setTipo(id)} style={chip(tipo === id)}>{l}</button>)}
            </div>
            <textarea value={texto} onChange={e => setTexto(e.target.value)}
              placeholder={tipo === "falla" ? "Qué hiciste, qué esperabas y qué pasó. En qué pantalla y en qué teléfono." : "Qué te gustaría que haga la app y para qué le sirve al rider."}
              style={{ width: "100%", minHeight: 110, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", background: C.surface }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              {foto ? (
                <div style={{ position: "relative", width: 52, height: 52, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
                  <img src={foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <button onClick={() => setFoto(null)} style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><X size={10} strokeWidth={2.5} color="#fff" /></button>
                </div>
              ) : (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: C.text, cursor: "pointer", padding: "8px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}` }}>
                  <Camera size={15} strokeWidth={2} /> {subiendo ? "Subiendo…" : "Captura"}
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { subirFoto(e.target.files && e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
              <div style={{ flex: 1 }} />
              <button onClick={enviar} disabled={!texto.trim() || enviando || subiendo}
                style={{ padding: "10px 18px", borderRadius: 10, background: texto.trim() ? C.text : C.border, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: texto.trim() ? "pointer" : "default" }}>
                {enviando ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        )}
        {lista === null && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 20 }}>Cargando…</div>}
        {lista && lista.slice().sort((a, b) => (soyAdmin ? (b.estado === "espera_carlos") - (a.estado === "espera_carlos") : 0)).map(p => {
          const est = ESTADOS_PEDIDO[p.estado] || ESTADOS_PEDIDO.nuevo;
          const hilo = Array.isArray(p.hilo) ? p.hilo : [];
          const abiertoEste = abierto === p.id;
          return (
            <div key={p.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
              <div onClick={() => { setAbierto(abiertoEste ? null : p.id); setResp(""); }} style={{ padding: "11px 13px", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: est.c, textTransform: "uppercase", letterSpacing: "0.04em" }}>{est.l}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>· @{p.username} · {fmt(p.created_at)}{hilo.length ? ` · ${hilo.length} respuesta${hilo.length === 1 ? "" : "s"}` : ""}</span>
                </div>
                <div style={{ fontSize: 14, color: C.text, lineHeight: 1.5, whiteSpace: "pre-line", ...(abiertoEste ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }) }}>{p.texto}</div>
              </div>
              {abiertoEste && (
                <div style={{ padding: "0 13px 12px" }}>
                  {p.foto_url && <img src={p.foto_url} alt="captura" onClick={() => window.open(p.foto_url, "_blank")} style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 10, cursor: "zoom-in" }} />}
                  {hilo.map((h, i) => (
                    <div key={i} style={{ background: h.de === "claude" ? C.surface : C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 11px", marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, marginBottom: 3 }}>{h.de === "claude" ? "Desarrollo" : h.de === "carlos" ? "Carlos" : "@" + p.username} · {fmt(h.en)}</div>
                      <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, whiteSpace: "pre-line" }}>{h.texto}</div>
                    </div>
                  ))}
                  {soyAdmin && p.estado === "espera_carlos" && (
                    <div style={{ display: "flex", gap: 8, margin: "4px 0 10px" }}>
                      <button onClick={() => decidir(p, true)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.text, color: "#F9F5F2", border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>✅ Aprobar</button>
                      <button onClick={() => decidir(p, false)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>✗ No por ahora</button>
                    </div>
                  )}
                  {p.estado !== "descartado" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <input value={resp} onChange={e => setResp(e.target.value)} placeholder={p.estado === "necesita_info" ? "Respondé lo que te preguntaron…" : p.estado === "hecho" ? "¿Algo más? Si no quedó bien, contalo acá" : "Agregar algo"}
                        style={{ flex: 1, minWidth: 0, padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, background: C.surface, color: C.text, fontFamily: "inherit" }} />
                      <button onClick={() => contestar(p.id)} disabled={!resp.trim()} style={{ padding: "0 14px", borderRadius: 10, background: resp.trim() ? C.text : C.border, color: "#fff", border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>Enviar</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {lista && lista.length === 0 && !err && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "20px 0" }}>Todavía no hay pedidos.</div>}
      </div>
    </PageWrap>
  );
};

// ── VERSIONES PARA LAS TIENDAS (23-oct-2026) ───────────────────────────────────
// Carlos aprueba desde el celular la versión que se preparó (Android en prueba interna,
// iPhone compilado por Xcode Cloud en TestFlight) y /api/tiendas la pasa a producción en
// Play y la manda a revisión en App Store. SQL: docs/sql/versiones_app.sql.
const ESTADO_TIENDA = {
  prueba_interna: "En prueba interna", compilando: "Compilando", procesando: "Apple la está procesando",
  enviada: "Enviada a revisión ✓", error: "Con error",
};
const VersionesTiendas = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [lista, setLista] = useState(null);
  const [err, setErr] = useState("");
  const [trabajando, setTrabajando] = useState(null);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("versiones_app_lista");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { setErr(error ? "Todavía no está habilitado (falta un paso en el servidor)." : "Solo para el admin."); setLista([]); return; }
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); const iv = setInterval(cargar, 30000); return () => clearInterval(iv); }, []);
  const aprobar = async (v) => {
    if (!window.confirm(`¿Publicar la ${v.version}? Android pasa a producción y la de iPhone se manda a revisión de Apple.`)) return;
    setTrabajando(v.version);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE}/api/tiendas`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` }, body: JSON.stringify({ accion: "aprobar", version: v.version }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) alert("No se pudo: " + (d.error || r.status));
      else if (d.errores && d.errores.length) alert("Quedó aprobada, con un problema:\n" + d.errores.join("\n"));
      registrarUso("version_aprobada", { version: v.version });
    } catch (e) { alert("No se pudo conectar. Probá de nuevo."); }
    setTrabajando(null);
    cargar();
  };
  const chip = (txt, ok) => <span style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 9px", borderRadius: 100, background: ok ? `${C.green}18` : C.surface, color: ok ? C.green : C.text, border: `1px solid ${ok ? C.green + "44" : C.border}` }}>{txt}</span>;
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={18} strokeWidth={2} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Versiones para las tiendas</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 14px" }}>
          Cada versión llega preparada: Android en la prueba interna de Play y la de iPhone en TestFlight. Al aprobarla, Android pasa a producción y la de iPhone se manda a revisión de Apple. Si Apple todavía la está procesando, se manda sola cuando termine.
        </p>
        {err && <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div>}
        {lista === null && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 20 }}>Cargando…</div>}
        {lista && lista.map(v => (
          <div key={v.version} style={{ background: C.card, border: `1px solid ${v.estado === "preparada" ? C.text : C.border}`, borderRadius: 14, padding: 14, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.015em" }}>{v.version}</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>Android {v.android_code}{v.ios_build ? ` · iOS ${v.ios_build}` : ""}</div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0" }}>
              {chip(`Play: ${ESTADO_TIENDA[v.android_estado] || v.android_estado}`, v.android_estado === "enviada")}
              {chip(`App Store: ${ESTADO_TIENDA[v.ios_estado] || v.ios_estado}`, v.ios_estado === "enviada")}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>Notas de la versión</div>
            <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, marginTop: 4, whiteSpace: "pre-wrap" }}>{v.notas}</div>
            {v.promo && <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 10 }}>Texto promocional (App Store)</div>
              <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, marginTop: 4 }}>{v.promo}</div>
            </>}
            {v.detalle && <div style={{ fontSize: 12, color: C.red, marginTop: 10, lineHeight: 1.5 }}>{v.detalle}</div>}
            {v.estado === "preparada" && (
              <button onClick={() => aprobar(v)} disabled={trabajando === v.version}
                style={{ width: "100%", marginTop: 14, padding: 13, borderRadius: 12, background: C.text, color: "#F9F5F2", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
                {trabajando === v.version ? "Publicando…" : "Aprobar y publicar en las dos tiendas"}
              </button>
            )}
            {(v.estado === "aprobada" || v.estado === "error") && (
              <button onClick={() => aprobar(v)} disabled={trabajando === v.version}
                style={{ width: "100%", marginTop: 14, padding: 11, borderRadius: 12, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                {trabajando === v.version ? "Reintentando…" : "Reintentar ahora"}
              </button>
            )}
            {v.estado === "publicada" && <div style={{ fontSize: 12.5, color: C.green, fontWeight: 700, marginTop: 12 }}>Enviada a las dos tiendas{v.aprobada_en ? ` el ${new Date(v.aprobada_en).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}` : ""}.</div>}
          </div>
        ))}
        {lista && lista.length === 0 && !err && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "20px 0" }}>Todavía no hay versiones preparadas.</div>}
      </div>
    </PageWrap>
  );
};

// ── SUGERIR CUENTA PARA EL SCOUTING (23-oct-2026) ───────────────────────────────
// Carlos y Matías ven organizadores o influencers mirando reels: copian el link (o el @) y lo
// pegan acá. La rutina de scouting de eventos lo toma en su próxima corrida (9 y 19 h), lo suma
// al radar, carga sus eventos y le avisa a quien lo mandó. SQL: docs/sql/aprobaciones_y_sugerencias.sql.
// ─── MIS LOCALES (autogestión de concesionarias, 30-oct-2026) ─────────────────────
// Carlos: "que los concesionarios puedan hacerlo fácil sin que tenga que intervenir cada vez
// alguien del staff". Desde su cuenta, la concesionaria (ej. @benitomotos):
//   · crea la cuenta de cada local ("Zanella Junín") — api/cuenta-local, sin mail ni contraseña;
//   · suma a su gente para que publique como el local ("Publicar como"), sin compartir contraseñas;
//   · pide la validación de concesionario oficial (la aprueba la marca, o el equipo de Olbos
//     con un toque mientras la marca no esté en Olbos).
// La cuenta de un local que YA existe con su propio login (ej. @zanellajunn) usa la sección
// "Tu cuenta": suma a @benitomotos y pide su validación. SQL: docs/sql/cuentas_de_locales.sql
const slugUsuario = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9_.]/g, "").slice(0, 30);
const MisLocales = ({ session, onBack }) => {
  useBackGuard(true, onBack);
  const [cuentas, setCuentas] = useState(null);
  const [miCuenta, setMiCuenta] = useState({ gente: [], marcas: [] });
  const [miUsuario, setMiUsuario] = useState("");
  const [marcas, setMarcas] = useState([]);
  const [err, setErr] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [creando, setCreando] = useState(false);
  const [form, setForm] = useState({ nombre: "", username: "", ciudad: "", logo: "", marca: "", marcaNueva: "" });
  const [userEditado, setUserEditado] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [sumar, setSumar] = useState({}); // cuenta_id -> @usuario a sumar
  const [pedir, setPedir] = useState({}); // cuenta_id -> { marca, nueva }
  // Editar un local (Fran de Motos Benito, 30-oct: "no los puedo editar para agregarle foto"):
  // la cuenta del local no tiene contraseña, así que su foto, nombre, ciudad y descripción se
  // cambian desde acá (cuenta_local_editar, solo quien la administra)
  const [editando, setEditando] = useState(null); // { cuenta_id, nombre, ciudad, bio, logo }
  // Tienda del local (30-oct): la misma de "Mi negocio", sobre la ficha de Servicios del local
  const [tienda, setTienda] = useState(null); // { cuenta_id, negocio }
  const abrirTienda = async (c) => {
    if (tienda?.cuenta_id === c.cuenta_id) { setTienda(null); return; }
    const { data } = await supabase.from("negocios").select("*").eq("user_id", c.cuenta_id).order("created_at", { ascending: true }).limit(1);
    if (!data || !data[0]) { alert("Este local todavía no tiene ficha en Servicios. Escribinos y la armamos."); return; }
    registrarUso("tienda_local_abrir", {});
    setTienda({ cuenta_id: c.cuenta_id, negocio: data[0] });
  };
  const [subiendoEd, setSubiendoEd] = useState(false);
  const abrirEditar = async (c) => {
    const [{ data }, { data: neg }] = await Promise.all([
      supabase.from("perfiles").select("bio").eq("id", c.cuenta_id).maybeSingle(),
      supabase.from("negocios").select("whatsapp").eq("user_id", c.cuenta_id).neq("categoria", "Marca").limit(1),
    ]);
    setEditando({ cuenta_id: c.cuenta_id, username: c.username, nombre: c.nombre || "", ciudad: c.ciudad || "", bio: data?.bio || "", logo: c.avatar_url || "", whatsapp: (neg && neg[0] && neg[0].whatsapp) || "" });
  };
  const subirLogoEd = async (file) => {
    if (!file) return;
    setSubiendoEd(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setEditando(e => e && { ...e, logo: j.secure_url });
    } catch (e) { alert("No se pudo subir la foto."); }
    setSubiendoEd(false);
  };
  const guardarEdicion = async () => {
    if (!editando || ocupado) return;
    if (!editando.nombre.trim()) { fallo("falta_el_nombre"); return; }
    setOcupado(true);
    const r = await supabase.rpc("cuenta_local_editar", { p_cuenta: editando.cuenta_id, p_nombre: editando.nombre.trim(), p_ciudad: editando.ciudad.trim(), p_logo: editando.logo || null, p_bio: editando.bio.trim() });
    const d = leer(r.data);
    setOcupado(false);
    if (r.error || d?.error) { fallo(d?.error || r.error?.message); return; }
    // El WhatsApp va a la ficha del local (si el SQL no está corrido, el resto igual se guardó)
    await supabase.rpc("cuenta_local_whatsapp", { p_cuenta: editando.cuenta_id, p_whatsapp: editando.whatsapp || "" }).then(() => {}, () => {});
    registrarUso("cuenta_local_editada", {});
    setEditando(null);
    cargar();
  };
  const leer = (raw) => (typeof raw === "string" ? JSON.parse(raw) : raw);
  const cargar = async () => {
    const uid = session?.user?.id;
    if (!uid) return;
    const [c, m, mc, p] = await Promise.all([
      supabase.rpc("mis_cuentas_locales"), supabase.rpc("marcas_oficiales"), supabase.rpc("mi_cuenta_gestion"),
      supabase.from("perfiles").select("username").eq("id", uid).maybeSingle(),
    ]);
    if (c.error) { setErr("Todavía no está habilitado. Probá en un rato."); setCuentas([]); return; }
    setCuentas(Array.isArray(leer(c.data)) ? leer(c.data) : []);
    setMarcas(Array.isArray(leer(m.data)) ? leer(m.data) : []);
    if (!mc.error && mc.data) setMiCuenta(leer(mc.data) || { gente: [], marcas: [] });
    setMiUsuario(p.data?.username || "");
  };
  useEffect(() => { cargar(); }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const MOTIVOS = {
    usuario_ocupado: "Ese usuario ya existe. Probá con otro.",
    usuario_invalido: "El usuario va de 3 a 30 letras o números, sin espacios ni tildes.",
    tope: "Llegaste al tope de cuentas de locales. Escribinos y lo ampliamos.",
    falta_el_nombre: "Poné el nombre del local.",
    no_existe_el_usuario: "No encontramos ese usuario. Revisá el @.",
    no_autorizado: "No tenés permiso para esto.",
    es_el_administrador: "A quien administra la cuenta no se lo puede sacar desde acá.",
    es_la_misma_cuenta: "Es la misma cuenta.",
    falta_la_marca: "Elegí la marca o escribí su nombre.",
    sin_sesion: "Volvé a iniciar sesión y probá de nuevo.",
    no_habilitado: "Todavía no está habilitado. Probá en un rato.",
  };
  const fallo = (e) => alert(MOTIVOS[e] || "No se pudo: " + e);
  const subirLogo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setForm(f => ({ ...f, logo: j.secure_url }));
    } catch (e) { alert("No se pudo subir el logo."); }
    setSubiendo(false);
  };
  const crearLocal = async () => {
    if (ocupado || !form.nombre.trim()) return;
    setOcupado(true);
    try {
      const { data: s } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE}/api/cuenta-local`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${s?.session?.access_token || ""}` },
        body: JSON.stringify({
          nombre: form.nombre.trim(), username: form.username || slugUsuario(form.nombre), ciudad: form.ciudad.trim(), logo_url: form.logo,
          marca_id: form.marca && form.marca !== "nueva" ? form.marca : null, marca_nueva: form.marca === "nueva" ? form.marcaNueva.trim() : "",
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { fallo(d.error || "error"); setOcupado(false); return; }
      registrarUso("cuenta_local_creada", { con_marca: !!(form.marca) });
      alert(`Listo: creamos @${d.username}. Para publicar como el local, al publicar tocá "Publicar como" y elegilo.${form.marca ? " El pedido de concesionario oficial ya está en revisión: te avisamos cuando lo validen." : ""}`);
      setForm({ nombre: "", username: "", ciudad: "", logo: "", marca: "", marcaNueva: "" });
      setUserEditado(false); setCreando(false);
      await cargar();
    } catch (e) { fallo("error"); }
    setOcupado(false);
  };
  const sumarGente = async (cuentaUser, cuentaId) => {
    const u = (sumar[cuentaId] || "").trim();
    if (!u || ocupado) return;
    setOcupado(true);
    const r = await supabase.rpc("delegado_agregar", { p_cuenta_username: cuentaUser, p_usuario_username: u });
    const d = leer(r.data);
    setOcupado(false);
    if (r.error || d?.error) { fallo(d?.error || r.error?.message); return; }
    setSumar(s => ({ ...s, [cuentaId]: "" }));
    cargar();
  };
  const sacarGente = async (cuentaId, g, cuentaUser) => {
    if (!window.confirm(`¿Sacarle a @${g.username} el acceso a @${cuentaUser}?`)) return;
    setOcupado(true);
    const r = await supabase.rpc("delegado_quitar", { p_cuenta: cuentaId, p_usuario: g.user_id });
    const d = leer(r.data);
    setOcupado(false);
    if (r.error || d?.error) { fallo(d?.error || r.error?.message); return; }
    cargar();
  };
  const pedirValidacion = async (cuentaId) => {
    const p = pedir[cuentaId] || {};
    if (!p.marca || (p.marca === "nueva" && !(p.nueva || "").trim())) { fallo("falta_la_marca"); return; }
    setOcupado(true);
    const r = await supabase.rpc("concesionario_pedir", { p_cuenta: cuentaId, p_marca: p.marca !== "nueva" ? p.marca : null, p_marca_nueva: p.marca === "nueva" ? p.nueva.trim() : null });
    const d = leer(r.data);
    setOcupado(false);
    if (r.error || d?.error) { fallo(d?.error || r.error?.message); return; }
    registrarUso("concesionario_pedido", {});
    alert(d?.estado === "aprobado" ? "Esa cuenta ya estaba validada ✓" : "Listo, pedido enviado. Te avisamos cuando lo validen.");
    setPedir(s => ({ ...s, [cuentaId]: {} }));
    cargar();
  };
  const inp = { width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.surface, color: C.text, fontFamily: "inherit", boxSizing: "border-box" };
  const caja = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14 };
  const btn = (primario) => ({ padding: "11px 14px", borderRadius: 10, border: primario ? "none" : `1px solid ${C.border}`, background: primario ? C.accent : C.card, color: primario ? "#F9F5F2" : C.text, fontWeight: 800, fontSize: 13, cursor: ocupado ? "default" : "pointer", opacity: ocupado ? 0.6 : 1 });
  const lab = { fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", margin: "12px 0 6px" };
  const estadoMarca = (m) => (
    <span key={m.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: m.estado === "aprobado" ? C.text : C.muted }}>
      {m.logo_url && <img src={cldOpt(m.logo_url, 60)} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />}
      {m.estado === "aprobado" ? <>Concesionario oficial {m.marca} <span style={{ color: C.green }}>✓</span></> : m.estado === "pendiente" ? `${m.marca}: validación en revisión` : `${m.marca}: no validado`}
    </span>
  );
  // Bloque reutilizable: gente que publica como la cuenta + pedir validación
  const bloqueGestion = (cuentaId, cuentaUser, gente, marcasCuenta, puedoSumar, puedoSacar, puedoSacarAdmin = false) => (
    <>
      <div style={lab}>Concesionario oficial</div>
      {marcasCuenta.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>{marcasCuenta.map(estadoMarca)}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <select value={(pedir[cuentaId] || {}).marca || ""} onChange={e => setPedir(s => ({ ...s, [cuentaId]: { ...(s[cuentaId] || {}), marca: e.target.value } }))} style={{ ...inp, flex: 1, minWidth: 0 }}>
          <option value="">{marcasCuenta.length ? "Pedir otra marca…" : "¿De qué marca es concesionario oficial?"}</option>
          {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          <option value="nueva">Otra marca…</option>
        </select>
        <button onClick={() => pedirValidacion(cuentaId)} disabled={ocupado || !(pedir[cuentaId] || {}).marca} style={{ ...btn(true), flexShrink: 0 }}>Pedir</button>
      </div>
      {(pedir[cuentaId] || {}).marca === "nueva" && (
        <input value={(pedir[cuentaId] || {}).nueva || ""} onChange={e => setPedir(s => ({ ...s, [cuentaId]: { ...(s[cuentaId] || {}), nueva: e.target.value } }))} placeholder="Nombre de la marca (ej: Zanella)" style={{ ...inp, marginTop: 6 }} />
      )}
      <div style={lab}>Quién publica como @{cuentaUser}</div>
      {gente.length === 0 && <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>Nadie más todavía.</div>}
      {gente.map(g => (
        <div key={g.user_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13 }}>
          <span style={{ flex: 1, color: C.text }}>@{g.username}{g.admin ? <span style={{ color: C.muted }}> · administra</span> : null}</span>
          {puedoSacar && (!g.admin || puedoSacarAdmin) && <button onClick={() => sacarGente(cuentaId, g, cuentaUser)} disabled={ocupado} style={{ ...btn(false), padding: "5px 10px", fontSize: 12 }}>Sacar</button>}
        </div>
      ))}
      {puedoSumar && (
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <input value={sumar[cuentaId] || ""} onChange={e => setSumar(s => ({ ...s, [cuentaId]: e.target.value }))} placeholder="@usuario de alguien de tu equipo" autoCapitalize="none" autoCorrect="off" style={{ ...inp, flex: 1, minWidth: 0 }} />
          <button onClick={() => sumarGente(cuentaUser, cuentaId)} disabled={ocupado || !(sumar[cuentaId] || "").trim()} style={{ ...btn(false), flexShrink: 0 }}>Sumar</button>
        </div>
      )}
    </>
  );
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Mis locales</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 14px" }}>
          Para concesionarias y comercios con más de un local o marca: cada local tiene su propia cuenta (por ejemplo "Zanella Junín") y la manejás desde la tuya. Al publicar, tocá <b>Publicar como</b> y elegí la cuenta. Si el local es concesionario oficial de una marca, pedí la validación: aprobada, lleva "Concesionario oficial ✓" en todo lo que publica.
        </p>
        {err ? <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div> : cuentas === null ? (
          <div style={{ fontSize: 13, color: C.muted, padding: 20, textAlign: "center" }}>Cargando…</div>
        ) : (
          <>
            {cuentas.map(c => (
              <div key={c.cuenta_id} style={caja}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={c.avatar_url || avatarInicial(c.nombre || c.username)} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: C.surface }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{c.nombre || c.username}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>@{c.username}{c.ciudad ? ` · ${c.ciudad}` : ""}</div>
                  </div>
                  {c.soy_admin && editando?.cuenta_id !== c.cuenta_id && (
                    <button onClick={() => abrirEditar(c)} style={{ ...btn(false), padding: "7px 12px", fontSize: 12.5, flexShrink: 0 }}>{c.avatar_url ? "Editar" : "Editar · poner foto"}</button>
                  )}
                </div>
                <button onClick={() => abrirTienda(c)} style={{ ...btn(tienda?.cuenta_id === c.cuenta_id), width: "100%", marginTop: 10 }}>
                  🛍️ {tienda?.cuenta_id === c.cuenta_id ? "Cerrar la tienda" : "Tienda del local: productos y pedidos"}
                </button>
                {tienda?.cuenta_id === c.cuenta_id && (
                  <div style={{ marginTop: 8 }}>
                    <MisProductos negocio={tienda.negocio} />
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>La tienda aparece en la ficha del local apenas publicás el primer producto (acá arriba) o una moto. Hasta entonces el local se ve como una ficha común. Las motos usadas o 0 km se publican en Compra-Venta: al publicar el aviso, elegí @{c.username} en "Publicar como".</div>
                  </div>
                )}
                {editando?.cuenta_id === c.cuenta_id && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                    <label style={{ ...btn(false), display: "flex", alignItems: "center", gap: 8, marginBottom: 8, justifyContent: "center" }}>
                      {editando.logo ? <img src={editando.logo} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} /> : <Camera size={16} />}
                      {subiendoEd ? "Subiendo…" : editando.logo ? "Cambiar foto o logo" : "Poner foto o logo"}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { subirLogoEd(e.target.files[0]); e.target.value = ""; }} />
                    </label>
                    <input value={editando.nombre} onChange={e => setEditando(x => ({ ...x, nombre: e.target.value }))} placeholder="Nombre del local" maxLength={80} style={{ ...inp, marginBottom: 8 }} />
                    <input value={editando.ciudad} onChange={e => setEditando(x => ({ ...x, ciudad: e.target.value }))} placeholder="Ciudad" maxLength={80} style={{ ...inp, marginBottom: 8 }} />
                    <input value={editando.whatsapp} onChange={e => setEditando(x => ({ ...x, whatsapp: e.target.value }))} placeholder="WhatsApp del local (ej: 5492364123456)" inputMode="tel" style={{ ...inp, marginBottom: 8 }} />
                    <textarea value={editando.bio} onChange={e => setEditando(x => ({ ...x, bio: e.target.value }))} placeholder="Descripción: qué venden, dirección, horarios…" maxLength={300} rows={3} style={{ ...inp, marginBottom: 8, resize: "vertical" }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditando(null)} disabled={ocupado} style={{ ...btn(false), flex: 1 }}>Cancelar</button>
                      <button onClick={guardarEdicion} disabled={ocupado || subiendoEd} style={{ ...btn(true), flex: 2 }}>{ocupado ? "Guardando…" : "Guardar"}</button>
                    </div>
                  </div>
                )}
                {bloqueGestion(c.cuenta_id, c.username, (c.gente || []).filter(g => g.user_id !== session?.user?.id), c.marcas || [], !!c.soy_admin, !!c.soy_admin)}
              </div>
            ))}

            {creando ? (
              <div style={caja}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 4 }}>Crear la cuenta de un local</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>Usá el nombre con el que lo conocen los clientes. No hace falta mail ni contraseña: la manejás desde tu cuenta.</div>
                <input value={form.nombre} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, nombre: v, username: userEditado ? f.username : slugUsuario(v) })); }} placeholder="Nombre del local (ej: Zanella Junín)" maxLength={80} style={{ ...inp, marginBottom: 8 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 14, color: C.muted, fontWeight: 700 }}>@</span>
                  <input value={form.username} onChange={e => { setUserEditado(true); setForm(f => ({ ...f, username: slugUsuario(e.target.value) })); }} placeholder="usuario" autoCapitalize="none" autoCorrect="off" style={{ ...inp, flex: 1 }} />
                </div>
                <input value={form.ciudad} onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))} placeholder="Ciudad" maxLength={80} style={{ ...inp, marginBottom: 8 }} />
                <label style={{ ...btn(false), display: "flex", alignItems: "center", gap: 8, marginBottom: 8, justifyContent: "center" }}>
                  {form.logo ? <img src={form.logo} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover" }} /> : <Camera size={16} />}
                  {subiendo ? "Subiendo…" : form.logo ? "Cambiar logo o foto" : "Logo o foto del local"}
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { subirLogo(e.target.files[0]); e.target.value = ""; }} />
                </label>
                <select value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} style={{ ...inp, marginBottom: 8 }}>
                  <option value="">¿Es concesionario oficial de una marca? (opcional)</option>
                  {marcas.map(m => <option key={m.id} value={m.id}>Sí, de {m.nombre}</option>)}
                  <option value="nueva">Sí, de otra marca…</option>
                </select>
                {form.marca === "nueva" && <input value={form.marcaNueva} onChange={e => setForm(f => ({ ...f, marcaNueva: e.target.value }))} placeholder="Nombre de la marca (ej: Zanella)" style={{ ...inp, marginBottom: 8 }} />}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setCreando(false)} disabled={ocupado} style={{ ...btn(false), flex: 1 }}>Cancelar</button>
                  <button onClick={crearLocal} disabled={ocupado || subiendo || !form.nombre.trim() || (form.username || "").length < 3} style={{ ...btn(true), flex: 2 }}>{ocupado ? "Creando…" : "Crear la cuenta"}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setCreando(true)} style={{ ...btn(true), width: "100%", marginBottom: 14, padding: 14 }}>+ Crear la cuenta de un local</button>
            )}

            <div style={caja}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 2 }}>Tu cuenta{miUsuario ? ` (@${miUsuario})` : ""}</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>Si esta cuenta es la de un local, desde acá sumás a quien la maneja (por ejemplo la cuenta de la concesionaria) y pedís la validación de concesionario oficial.</div>
              {bloqueGestion(session?.user?.id, miUsuario, miCuenta.gente || [], miCuenta.marcas || [], true, true, true)}
            </div>
          </>
        )}
      </div>
    </PageWrap>
  );
};

// ─── NEGOCIOS PARA APROBAR (equipo, 31-oct-2026) ─────────────────────────────────
// Carlos: "que Matías también pueda aprobar locales y negocios". Antes solo desde Admin (el admin).
// Los negocios que se registran solos quedan "pendiente" hasta que alguien del equipo los aprueba.
// SQL: docs/sql/equipo_aprueba_negocios.sql
const NegociosParaAprobar = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [lista, setLista] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("negocios_para_revisar");
    if (error) { setLista([]); return; }
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const resolver = async (n, estado) => {
    if (estado === "suspendido" && !window.confirm(`¿Rechazar "${n.nombre}"? No se publica en Servicios.`)) return;
    setOcupado(true);
    const { data: raw, error } = await supabase.rpc("admin_set_negocio_estado", { p_id: n.id, p_estado: estado });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setOcupado(false);
    if (error || r?.error) { alert("No se pudo: " + (error?.message || r?.error)); return; }
    registrarUso("negocio_revisado", { estado });
    cargar();
  };
  const fecha = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short" }); };
  const btn = (primario) => ({ flex: 1, padding: "10px", borderRadius: 10, border: primario ? "none" : `1px solid ${C.border}`, background: primario ? C.accent : C.card, color: primario ? "#F9F5F2" : C.text, fontWeight: 800, fontSize: 13, cursor: ocupado ? "default" : "pointer", opacity: ocupado ? 0.6 : 1 });
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Negocios para aprobar</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 14px" }}>
          Los negocios que se registran solos quedan esperando acá hasta que alguien del equipo los aprueba. Revisá que sea un negocio real (Instagram, web o WhatsApp) antes de publicarlo en Servicios. Los concesionarios oficiales se validan aparte, en Más → Concesionarios oficiales.
        </p>
        {lista === null ? <div style={{ fontSize: 13, color: C.muted, padding: 20, textAlign: "center" }}>Cargando…</div>
          : lista.length === 0 ? <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>No hay negocios esperando aprobación.</div>
          : lista.map(n => (
            <div key={n.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: C.surface, overflow: "hidden", flexShrink: 0 }}>
                  {n.logo_url && <img src={cldOpt(n.logo_url, 120)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{n.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{n.categoria}{n.ciudad ? ` · ${n.ciudad}` : ""} · {fecha(n.created_at)}{n.dueno ? ` · @${n.dueno}` : ""}</div>
                </div>
              </div>
              {n.descripcion && <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5, marginTop: 8 }}>{n.descripcion}</div>}
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginTop: 6 }}>
                {n.direccion && <div>📍 {n.direccion}</div>}
                {(n.whatsapp || n.telefono) && <div>📞 {n.whatsapp || n.telefono}</div>}
                {n.instagram && <div>📸 <a href={`https://instagram.com/${String(n.instagram).replace(/^@/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//, "")}`} target="_blank" rel="noopener noreferrer" style={{ color: C.text }}>{n.instagram}</a></div>}
                {n.web && <div>🌐 <a href={/^https?:/.test(n.web) ? n.web : `https://${n.web}`} target="_blank" rel="noopener noreferrer" style={{ color: C.text }}>{n.web}</a></div>}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={() => resolver(n, "suspendido")} disabled={ocupado} style={btn(false)}>Rechazar</button>
                <button onClick={() => resolver(n, "verificado")} disabled={ocupado} style={btn(true)}>Aprobar y publicar</button>
              </div>
            </div>
          ))}
      </div>
    </PageWrap>
  );
};

// ─── CONCESIONARIOS OFICIALES (equipo, 30-oct-2026) ─────────────────────────────
// Caso Motos Benito: una cuenta por local ("Zanella Junín"), manejada por la gente de la
// concesionaria con "Publicar como", y validada como concesionario oficial de la marca.
// Mientras las marcas no tengan cuenta en Olbos, valida el equipo. SQL: docs/sql/concesionarios_oficiales.sql
const ConcesionariosOficiales = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [lista, setLista] = useState(null);
  const [marcas, setMarcas] = useState([]);
  const [delegs, setDelegs] = useState([]);
  const [err, setErr] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [marcaId, setMarcaId] = useState("");
  const [marcaNueva, setMarcaNueva] = useState("");
  const [logoNuevo, setLogoNuevo] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [dCuenta, setDCuenta] = useState("");
  const [dUsuario, setDUsuario] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const leer = (raw) => (typeof raw === "string" ? JSON.parse(raw) : raw);
  const cargar = async () => {
    const [l, m, d] = await Promise.all([
      supabase.rpc("concesionarios_lista"), supabase.rpc("marcas_oficiales"), supabase.rpc("delegaciones_lista"),
    ]);
    if (l.error) { setErr("Todavía no está habilitado (falta correr el SQL en el servidor)."); setLista([]); return; }
    setLista(Array.isArray(leer(l.data)) ? leer(l.data) : []);
    setMarcas(Array.isArray(leer(m.data)) ? leer(m.data) : []);
    setDelegs(Array.isArray(leer(d.data)) ? leer(d.data) : []);
  };
  useEffect(() => { cargar(); }, []);
  const resultado = (r, okTxt) => {
    const d = leer(r.data);
    if (r.error || (d && d.error)) {
      const motivos = { no_existe_la_cuenta: "No encontramos esa cuenta. Revisá el @usuario.", no_existe_el_usuario: "No encontramos ese usuario. Revisá el @usuario.", no_autorizado: "No tenés permiso para esto.", es_la_misma_cuenta: "La cuenta y el usuario son el mismo." };
      alert(motivos[d?.error] || "No se pudo: " + (r.error?.message || d?.error));
      return false;
    }
    if (okTxt) alert(okTxt);
    return true;
  };
  const subirLogo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setLogoNuevo(j.secure_url);
    } catch (e) { alert("No se pudo subir el logo."); }
    setSubiendo(false);
  };
  const validar = async () => {
    if (!cuenta.trim() || ocupado) return;
    setOcupado(true);
    let idMarca = marcaId;
    if (marcaId === "nueva") {
      if (!marcaNueva.trim()) { alert("Escribí el nombre de la marca."); setOcupado(false); return; }
      const r = await supabase.rpc("marca_crear", { p_nombre: marcaNueva.trim(), p_logo_url: logoNuevo || null });
      if (!resultado(r)) { setOcupado(false); return; }
      idMarca = leer(r.data).id;
    }
    if (!idMarca) { alert("Elegí la marca."); setOcupado(false); return; }
    const r2 = await supabase.rpc("concesionario_validar", { p_cuenta_username: cuenta.trim(), p_marca: idMarca });
    if (resultado(r2, "Listo: la cuenta ya lleva el distintivo de concesionario oficial ✓")) {
      registrarUso("concesionario_validado", {});
      setCuenta(""); setMarcaId(""); setMarcaNueva(""); setLogoNuevo("");
      cargarConcesionarios(true);
      await cargar();
    }
    setOcupado(false);
  };
  const resolver = async (c, estado) => {
    const txt = { aprobado: `¿Validar a @${c.cuenta} como concesionario oficial de ${c.marca}?`, rechazado: `¿Rechazar el pedido de @${c.cuenta}?`, baja: `¿Sacarle a @${c.cuenta} el distintivo de concesionario oficial de ${c.marca}? Desaparece de todo lo que publicó.` }[estado];
    if (!window.confirm(txt)) return;
    setOcupado(true);
    if (resultado(await supabase.rpc("concesionario_resolver", { p_id: c.id, p_estado: estado, p_nota: null }))) { cargarConcesionarios(true); await cargar(); }
    setOcupado(false);
  };
  const darAcceso = async () => {
    if (!dCuenta.trim() || !dUsuario.trim() || ocupado) return;
    setOcupado(true);
    if (resultado(await supabase.rpc("delegado_agregar", { p_cuenta_username: dCuenta.trim(), p_usuario_username: dUsuario.trim() }),
      `Listo: @${dUsuario.trim().replace(/^@/, "")} ya puede publicar como @${dCuenta.trim().replace(/^@/, "")} (al publicar elige la cuenta en "Publicar como").`)) {
      setDCuenta(""); setDUsuario("");
      await cargar();
    }
    setOcupado(false);
  };
  const quitarAcceso = async (d) => {
    if (!window.confirm(`¿Sacarle a @${d.usuario} el acceso a @${d.cuenta}?`)) return;
    setOcupado(true);
    if (resultado(await supabase.rpc("delegado_quitar", { p_cuenta: d.cuenta_id, p_usuario: d.user_id }))) await cargar();
    setOcupado(false);
  };
  const inp = { width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.surface, color: C.text, fontFamily: "inherit", boxSizing: "border-box" };
  const caja = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14 };
  const titulo = { fontSize: 14.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 4 };
  const ayuda = { fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 };
  const btn = (primario) => ({ padding: "11px 14px", borderRadius: 10, border: primario ? "none" : `1px solid ${C.border}`, background: primario ? C.accent : C.card, color: primario ? "#F9F5F2" : C.text, fontWeight: 800, fontSize: 13, cursor: ocupado ? "default" : "pointer", opacity: ocupado ? 0.6 : 1 });
  const estadoTxt = { pendiente: "Pendiente", aprobado: "Validado ✓", rechazado: "Rechazado", baja: "Dado de baja" };
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Concesionarios oficiales</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 14px" }}>
          Cada local de una concesionaria tiene su propia cuenta (por ejemplo "Zanella Junín"). Para dar de alta uno: <b>1)</b> en Más → Crear perfil de organizador armá la cuenta con el nombre del local y mandale el link de reclamo a la concesionaria. <b>2)</b> Cuando la reclamen, acá abajo validala como concesionario oficial de la marca y decí quién de la concesionaria la maneja. Nadie publica como la marca: la cuenta de la marca es solo de la marca.
        </p>
        {err ? <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div> : lista === null ? (
          <div style={{ fontSize: 13, color: C.muted, padding: 20, textAlign: "center" }}>Cargando…</div>
        ) : (
          <>
            <div style={caja}>
              <div style={titulo}>Validar un concesionario oficial</div>
              <div style={ayuda}>La cuenta del local pasa a llevar "Concesionario oficial [marca] ✓" en su perfil, en todo lo que publica y en su ficha de Pit Stop.</div>
              <input value={cuenta} onChange={e => setCuenta(e.target.value)} placeholder="@ de la cuenta del local (ej: zanellajunin)" autoCapitalize="none" autoCorrect="off" style={{ ...inp, marginBottom: 8 }} />
              <select value={marcaId} onChange={e => setMarcaId(e.target.value)} style={{ ...inp, marginBottom: 8 }}>
                <option value="">Elegí la marca…</option>
                {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                <option value="nueva">+ Otra marca (todavía no está en Olbos)</option>
              </select>
              {marcaId === "nueva" && (
                <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <input value={marcaNueva} onChange={e => setMarcaNueva(e.target.value)} placeholder="Nombre de la marca (ej: Zanella)" style={{ ...inp, flex: 1 }} />
                  <label style={{ ...btn(false), display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {logoNuevo ? <img src={logoNuevo} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} /> : <Camera size={15} />}
                    {subiendo ? "…" : "Logo"}
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { subirLogo(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
              )}
              <button onClick={validar} disabled={ocupado || !cuenta.trim() || !marcaId} style={{ ...btn(true), width: "100%" }}>Validar como concesionario oficial</button>
            </div>

            <div style={caja}>
              <div style={titulo}>Quién maneja cada cuenta</div>
              <div style={ayuda}>La gente de la concesionaria publica desde su propio usuario eligiendo la cuenta en "Publicar como". Sin compartir contraseñas.</div>
              <input value={dCuenta} onChange={e => setDCuenta(e.target.value)} placeholder="@ de la cuenta (ej: zanellajunin)" autoCapitalize="none" autoCorrect="off" style={{ ...inp, marginBottom: 8 }} />
              <input value={dUsuario} onChange={e => setDUsuario(e.target.value)} placeholder="@ de quien la maneja (ej: motosbenito)" autoCapitalize="none" autoCorrect="off" style={{ ...inp, marginBottom: 8 }} />
              <button onClick={darAcceso} disabled={ocupado || !dCuenta.trim() || !dUsuario.trim()} style={{ ...btn(true), width: "100%", marginBottom: delegs.length ? 10 : 0 }}>Darle acceso</button>
              {delegs.map(d => (
                <div key={d.cuenta_id + d.user_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
                  <span style={{ flex: 1, minWidth: 0, color: C.text }}><b>@{d.usuario}</b> <span style={{ color: C.muted }}>publica como</span> <b>@{d.cuenta}</b></span>
                  <button onClick={() => quitarAcceso(d)} disabled={ocupado} style={{ ...btn(false), padding: "6px 10px", fontSize: 12 }}>Quitar</button>
                </div>
              ))}
            </div>

            <div style={caja}>
              <div style={titulo}>Concesionarios</div>
              {lista.length === 0 ? <div style={{ fontSize: 13, color: C.muted }}>Todavía no hay ninguno.</div> : lista.map(c => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: `1px solid ${C.border}` }}>
                  {c.logo_url ? <img src={cldOpt(c.logo_url, 80)} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} /> : <Award size={20} color={C.muted} style={{ flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{c.cuenta} · {c.marca}</div>
                    <div style={{ fontSize: 11.5, color: c.estado === "aprobado" ? C.green : C.muted }}>{estadoTxt[c.estado] || c.estado}</div>
                  </div>
                  {c.estado === "pendiente" && <>
                    <button onClick={() => resolver(c, "aprobado")} disabled={ocupado} style={{ ...btn(true), padding: "7px 10px", fontSize: 12 }}>Validar</button>
                    <button onClick={() => resolver(c, "rechazado")} disabled={ocupado} style={{ ...btn(false), padding: "7px 10px", fontSize: 12 }}>Rechazar</button>
                  </>}
                  {c.estado === "aprobado" && <button onClick={() => resolver(c, "baja")} disabled={ocupado} style={{ ...btn(false), padding: "7px 10px", fontSize: 12 }}>Dar de baja</button>}
                  {(c.estado === "baja" || c.estado === "rechazado") && <button onClick={() => resolver(c, "aprobado")} disabled={ocupado} style={{ ...btn(false), padding: "7px 10px", fontSize: 12 }}>Validar</button>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </PageWrap>
  );
};

const SugerirCuentaScouting = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [entrada, setEntrada] = useState("");
  const [nota, setNota] = useState("");
  const [lista, setLista] = useState(null);
  const [err, setErr] = useState("");
  const [enviando, setEnviando] = useState(false);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("scouting_sugerencias_lista");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { setErr(error ? "Todavía no está habilitado (falta un paso en el servidor)." : "Esta sección es solo para el equipo de Olbos."); setLista([]); return; }
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const pegar = async () => {
    try { const x = await navigator.clipboard.readText(); if (x) setEntrada(x.trim()); } catch (e) { /* el navegador no deja leer el portapapeles: se pega a mano */ }
  };
  const enviar = async () => {
    if (!entrada.trim() || enviando) return;
    setEnviando(true);
    const { data: raw, error } = await supabase.rpc("scouting_sugerir", { p_entrada: entrada.trim(), p_nota: nota.trim() });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setEnviando(false);
    if (error || (d && d.error)) { alert("No se pudo enviar: " + (error?.message || d.error)); return; }
    registrarUso("scouting_sugerencia", {});
    setEntrada(""); setNota("");
    cargar();
  };
  const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); };
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Sugerir cuenta para el scouting</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 12px" }}>
          ¿Viste un organizador, un influencer o un comercio que arma salidas? En Instagram tocá los tres puntitos o "Compartir" → <b>Copiar enlace</b>, y pegalo acá. El scouting lo revisa en su próxima corrida (9 y 19 h), lo suma al radar, carga sus eventos y te avisa qué hizo.
        </p>
        {err ? <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div> : (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 18 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={entrada} onChange={e => setEntrada(e.target.value)} placeholder="Link de Instagram o @usuario" autoCapitalize="none" autoCorrect="off"
                style={{ flex: 1, minWidth: 0, padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.surface, color: C.text, fontFamily: "inherit" }} />
              <button onClick={pegar} style={{ padding: "0 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Pegar</button>
            </div>
            <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota (opcional): qué es, de dónde, qué viste"
              style={{ width: "100%", boxSizing: "border-box", marginTop: 8, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, background: C.surface, color: C.text, fontFamily: "inherit" }} />
            <button onClick={enviar} disabled={!entrada.trim() || enviando}
              style={{ width: "100%", marginTop: 10, padding: 12, borderRadius: 10, background: entrada.trim() ? C.text : C.border, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: entrada.trim() ? "pointer" : "default" }}>
              {enviando ? "Enviando…" : "Mandar al scouting"}
            </button>
          </div>
        )}
        {lista && lista.map(s => (
          <div key={s.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: s.estado === "procesada" ? C.green : s.estado === "descartada" ? C.muted : "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {s.estado === "procesada" ? "Revisada" : s.estado === "descartada" ? "Descartada" : "En la fila"} <span style={{ color: C.muted, textTransform: "none", fontWeight: 600 }}>· @{s.username} · {fmt(s.created_at)}</span>
            </div>
            <div style={{ fontSize: 13.5, color: C.text, marginTop: 3, wordBreak: "break-all" }}>{s.entrada}</div>
            {s.nota && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{s.nota}</div>}
            {s.resultado && <div style={{ fontSize: 13, color: C.text, marginTop: 6, lineHeight: 1.5 }}>{s.resultado}</div>}
          </div>
        ))}
      </div>
    </PageWrap>
  );
};

// ── ANUNCIANTES: 100 CLICKS GRATIS (24-oct-2026) ──────────────────────────────
// La rutina de scouting de anunciantes encuentra cuentas que venden productos para riders y
// les genera olbos.ai/regalo/<codigo>. Acá se reclama (una vez, con cuenta) y el equipo ve a
// quién falta escribirle por Instagram. SQL: docs/sql/anunciantes_regalo.sql
const mensajeAnunciante = (a, riders) =>
  `Hola! Somos Olbos (olbos.ai), la app de los riders: salidas, rutas, recorridos y una comunidad de más de ${riders} motoqueros que crece todos los días. Vimos ${a.producto ? "sus " + a.producto : "lo que venden"} y creemos que a nuestra comunidad le interesan. Les regalamos ${a.clicks_regalo || 100} clicks gratis para que prueben publicar su producto en Olbos con un botón directo a su WhatsApp o tienda: cada vez que un rider lo toca se usa un click, y estos corren por nuestra cuenta. Se activan acá: olbos.ai/regalo/${a.codigo}. Cualquier duda, respondemos por acá.`;
const ridersRedondo = async () => {
  try {
    const { count } = await supabase.from("perfiles").select("id", { count: "exact", head: true });
    return count >= 100 ? (Math.floor(count / 100) * 100).toLocaleString("es-AR") : "cientos de";
  } catch (e) { return "1.700"; }
};

const RegaloClicks = ({ codigo, session, onLogin, onCerrar, onPublicar }) => {
  const [info, setInfo] = useState(null);
  const [estado, setEstado] = useState(null); // null | "activando" | {ok} | {error}
  const baja = (() => { try { return new URLSearchParams(window.location.search).get("baja") === "1"; } catch (e) { return false; } })();
  useEffect(() => {
    if (baja) {
      supabase.rpc("regalo_baja", { p_codigo: codigo }).then(() => {}, () => {});
      try { window.history.replaceState({}, "", "/"); } catch (e) { /* */ }
      return;
    }
    supabase.rpc("regalo_ver", { p_codigo: codigo }).then(({ data: raw, error }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setInfo(error ? { error: "red" } : d);
    }, () => setInfo({ error: "red" }));
    registrarUso("regalo_visto", { codigo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);
  const activar = async () => {
    setEstado("activando");
    const { data: raw, error } = await supabase.rpc("regalo_reclamar", { p_codigo: codigo });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error) { setEstado({ error: error.message }); return; }
    setEstado(d);
    if (d && d.ok) registrarUso("regalo_reclamado", { codigo });
  };
  const MOTIVOS = { ya_reclamado: "Este regalo ya lo activó otra cuenta.", ya_es_tuyo: "Ya lo activaste: los clicks están en tu saldo.", ya_tenes_uno: "Tu cuenta ya activó un regalo de clicks.", no_existe: "Este link no existe o venció.", sesion: "Entrá con tu cuenta para activarlo." };
  const fondo = { position: "fixed", inset: 0, background: "rgba(17,17,17,.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const caja = { width: "100%", maxWidth: 420, background: "#111", color: "#F9F5F2", borderRadius: 18, padding: "26px 22px", boxSizing: "border-box", maxHeight: "92vh", overflowY: "auto" };
  const btn = { width: "100%", padding: 14, borderRadius: 12, border: "none", background: "#F9F5F2", color: "#111", fontWeight: 800, fontSize: 15.5, cursor: "pointer", marginTop: 16 };
  const sec = { background: "none", border: "none", color: "#bdb6ab", fontSize: 13, marginTop: 12, cursor: "pointer", width: "100%" };
  if (baja) return (
    <div style={fondo}><div style={caja}>
      <div style={{ fontSize: 19, fontWeight: 800 }}>Listo, no les escribimos más</div>
      <div style={{ fontSize: 14, color: "#bdb6ab", marginTop: 8, lineHeight: 1.55 }}>Sacamos su cuenta de nuestra lista de contacto. Si algún día quieren probar Olbos, los esperamos.</div>
      <button onClick={onCerrar} style={btn}>Cerrar</button>
    </div></div>
  );
  const exito = estado && estado.ok;
  const err = estado && estado.error ? (MOTIVOS[estado.error] || "No se pudo activar: " + estado.error) : (info && info.error ? (MOTIVOS[info.error] || "No pudimos cargar el regalo. Probá de nuevo en un rato.") : null);
  return (
    <div style={fondo}><div style={caja}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", color: "#bdb6ab", textTransform: "uppercase" }}>Olbos · regalo para anunciantes</div>
      {exito ? (
        <>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 10, letterSpacing: "-0.02em" }}>¡Listo! Tenés {estado.clicks} clicks gratis</div>
          <div style={{ fontSize: 14.5, color: "#E7E1DA", marginTop: 10, lineHeight: 1.6 }}>
            Ahora publicá tu producto: tocá <b>Publicar</b> en el feed, subí la foto con una descripción y activá <b>"Agregar un botón con link (promocionado)"</b> con tu WhatsApp, tu tienda o tu publicación de Mercado Libre. Cada rider que lo toca usa un click. Los ves en Más → Links promocionados.
          </div>
          <button onClick={onPublicar} style={btn}>Publicar mi producto</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 10, letterSpacing: "-0.02em" }}>
            {info && !info.error ? `${info.clicks} clicks gratis para ${info.nombre}` : "100 clicks gratis para tu marca"}
          </div>
          <div style={{ fontSize: 14.5, color: "#E7E1DA", marginTop: 10, lineHeight: 1.6 }}>
            Olbos es la app de los riders: salidas, rutas, recorridos y una comunidad que crece todos los días. Publicá tu producto en el feed con un botón directo a tu WhatsApp o tienda: cada vez que un rider lo toca se usa un click, y estos corren por nuestra cuenta.
          </div>
          <div style={{ fontSize: 13.5, color: "#bdb6ab", marginTop: 14, lineHeight: 1.7 }}>
            1. {session ? "Activá los clicks con tu cuenta." : "Creá tu cuenta gratis (o entrá con la que tenés)."}<br />
            2. Publicá tu producto con foto.<br />
            3. Agregale el botón con tu link y listo.
          </div>
          {err && <div style={{ fontSize: 13.5, color: "#F9F5F2", background: "rgba(249,245,242,.1)", borderRadius: 10, padding: "10px 12px", marginTop: 14 }}>{err}</div>}
          {info && info.mio ? <button onClick={onPublicar} style={btn}>Publicar mi producto</button>
            : !(info && (info.reclamado || info.error)) && (session
              ? <button onClick={activar} disabled={estado === "activando"} style={btn}>{estado === "activando" ? "Activando…" : "Activar mis clicks gratis"}</button>
              : <button onClick={onLogin} style={btn}>Crear cuenta o entrar</button>)}
        </>
      )}
      <button onClick={onCerrar} style={sec}>{exito ? "Cerrar" : "Ahora no"}</button>
    </div></div>
  );
};

const AnunciantesContactar = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [lista, setLista] = useState(null);
  const [err, setErr] = useState("");
  const [riders, setRiders] = useState("1.700");
  const [filtro, setFiltro] = useState("pendientes");
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("anunciantes_equipo_lista");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { setErr(error ? "Todavía no está habilitado (falta un paso en el servidor)." : "Esta sección es solo para el equipo de Olbos."); setLista([]); return; }
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); ridersRedondo().then(setRiders, () => {}); }, []);
  const marcar = async (a, estado) => {
    const { data: raw, error } = await supabase.rpc("anunciante_equipo_estado", { p_id: a.id, p_estado: estado });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { alert("No se pudo: " + (error?.message || d.error)); return; }
    registrarUso("anunciante_" + estado, {});
    cargar();
  };
  const copiarYAbrir = async (a, donde) => {
    const msg = mensajeAnunciante(a, riders);
    try { await navigator.clipboard.writeText(msg); } catch (e) { /* si no deja copiar, el texto está a la vista para copiarlo a mano */ }
    const url = donde === "wa" ? `https://wa.me/${String(a.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(msg)}` : `https://instagram.com/${a.ig_usuario}`;
    window.open(url, "_blank", "noopener");
  };
  const ver = (lista || []).filter(a => filtro === "pendientes" ? a.estado === "nuevo" : filtro === "contactados" ? ["mail_enviado", "dm_enviado"].includes(a.estado) : filtro === "reclamados" ? a.estado === "reclamado" : true);
  const ETQ = { nuevo: "Para contactar", mail_enviado: "Mail enviado", dm_enviado: "Mensaje enviado", reclamado: "Activó los clicks ✅", descartado: "Descartado", baja: "Pidió no recibir más" };
  const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short" }); };
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Anunciantes para contactar</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: "0 0 12px" }}>
          El scouting encuentra cuentas que venden cosas para riders y les arma un link con 100 clicks gratis. A los que tienen mail les escribe solo; a estos hay que mandarles el mensaje por Instagram: tocá <b>Copiar y abrir Instagram</b>, entrá a Mensaje, pegá y enviá. Después marcá <b>Enviado</b>.
        </p>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {[["pendientes", "Para contactar"], ["contactados", "Contactados"], ["reclamados", "Activaron"], ["todos", "Todos"]].map(([k, l]) => (
            <button key={k} onClick={() => setFiltro(k)} style={{ padding: "6px 12px", borderRadius: 999, border: `1px solid ${filtro === k ? C.text : C.border}`, background: filtro === k ? C.text : C.card, color: filtro === k ? "#F9F5F2" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
        {err && <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div>}
        {lista && !err && ver.length === 0 && <div style={{ fontSize: 13, color: C.muted, padding: "10px 2px" }}>No hay cuentas en esta lista. El scouting suma nuevas todos los días.</div>}
        {ver.map(a => (
          <div key={a.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{a.nombre || "@" + a.ig_usuario}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: a.estado === "reclamado" ? C.green : C.muted, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{ETQ[a.estado] || a.estado}</div>
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
              @{a.ig_usuario}{a.seguidores ? ` · ${Number(a.seguidores).toLocaleString("es-AR")} seguidores` : ""}{a.ciudad ? ` · ${a.ciudad}` : ""} · {fmt(a.created_at)}{a.visitas ? ` · abrió el link ${a.visitas} ${a.visitas === 1 ? "vez" : "veces"}` : ""}
            </div>
            {a.producto && <div style={{ fontSize: 13, color: C.text, marginTop: 4 }}>Vende: {a.producto}</div>}
            {a.notas && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{a.notas}</div>}
            {a.estado === "nuevo" && (
              <>
                <div style={{ fontSize: 12.5, color: C.text, background: C.surface, borderRadius: 10, padding: "8px 10px", marginTop: 8, lineHeight: 1.5, userSelect: "text" }}>{mensajeAnunciante(a, riders)}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <button onClick={() => copiarYAbrir(a, "ig")} style={{ flex: "1 1 auto", padding: "10px 12px", borderRadius: 10, border: "none", background: C.text, color: "#F9F5F2", fontWeight: 800, fontSize: 13.5, cursor: "pointer" }}>Copiar y abrir Instagram</button>
                  {a.whatsapp && <button onClick={() => copiarYAbrir(a, "wa")} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>WhatsApp</button>}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={() => marcar(a, "dm_enviado")} style={{ flex: 1, padding: "9px 0", borderRadius: 10, border: `1px solid ${C.text}`, background: C.card, color: C.text, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>✓ Enviado</button>
                  <button onClick={() => marcar(a, "descartado")} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.muted, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>No va</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </PageWrap>
  );
};

// ─── FINANZAS (Más → Finanzas, solo admin) ───────────────────────────────────
// Gastos y pagos de Olbos (tabla finanzas_gastos, todo por RPC finanzas_*) y los
// ingresos de la tabla ingresos, la misma que alimenta el 5% del Fondo Olbos.
// SQL en docs/sql/finanzas.sql. Pedido de Carlos, 26-oct-2026.
const FIN_CATS = [["equipo", "Equipo"], ["infra", "Infra"], ["herramientas", "Herramientas"], ["marketing", "Marketing"], ["eventos", "Eventos"], ["premios", "Premios"], ["marca", "Marca"], ["tiendas", "Tiendas"], ["legal", "Legal"], ["otro", "Otro"]];
const finCatLabel = (c) => (FIN_CATS.find(x => x[0] === c) || [c, c])[1];
const finHoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const finUSD = (n) => { const v = Number(n) || 0; return (v < 0 ? "−" : "") + "USD " + Math.abs(v).toLocaleString("es-AR", { maximumFractionDigits: Math.abs(v) < 100 && v % 1 ? 2 : 0 }); };
// "1.800.000" o "1800000,50" (formato argentino) y también "8.40" (punto decimal)
const finNum = (x) => { let t = String(x == null ? "" : x).trim().replace(/\s/g, ""); if (t.includes(",")) t = t.replace(/\./g, "").replace(",", "."); else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, ""); return Number(t); };
const finARS = (n) => { const v = Number(n) || 0; return (v < 0 ? "−$" : "$") + Math.round(Math.abs(v)).toLocaleString("es-AR"); };
const Finanzas = ({ onBack }) => {
  const [mes, setMes] = useState(() => finHoy().slice(0, 8) + "01");
  const [cat, setCat] = useState("");
  const [lista, setLista] = useState(null);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [mep, setMep] = useState(() => { try { return Number(localStorage.getItem("olbos_mep")) || 1540; } catch (e) { return 1540; } });
  const [form, setForm] = useState(null); // { modo: "gasto" | "ingreso", ...campos }
  const [masDatos, setMasDatos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  useBackGuard(true, () => { if (form) setForm(null); else onBack(); });
  const cargar = async () => {
    const [l, r] = await Promise.all([
      supabase.rpc("finanzas_listar", { p_mes: mes, p_categoria: cat || null }),
      supabase.rpc("finanzas_resumen", { p_mes: mes, p_mep: Number(mep) || 1540 }),
    ]);
    if (l.error || r.error) {
      const m = (l.error || r.error).message || "";
      setErr(/function|does not exist|schema cache/i.test(m) ? "Falta correr el SQL de Finanzas (docs/sql/finanzas.sql)." : (/admin/i.test(m) ? "Solo para el admin." : "No se pudo cargar: " + m));
      setLista({ gastos: [], ingresos: [], series: [] }); setRes(null); return;
    }
    setErr("");
    const lj = typeof l.data === "string" ? JSON.parse(l.data) : l.data;
    const rj = typeof r.data === "string" ? JSON.parse(r.data) : r.data;
    setLista(lj || { gastos: [], ingresos: [], series: [] });
    setRes(rj || null);
  };
  useEffect(() => { cargar(); }, [mes, cat]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { localStorage.setItem("olbos_mep", String(mep)); } catch (e) { /* */ } }, [mep]);

  const moverMes = (d) => { const f = new Date(mes + "T12:00:00"); f.setMonth(f.getMonth() + d); setMes(`${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-01`); };
  const nombreMes = new Date(mes + "T12:00:00").toLocaleDateString("es-AR", { month: "long", year: "numeric" });
  const abrirGasto = (g) => { setMasDatos(!!(g && (g.medio_pago || g.a_quien || g.comprobante_url))); setForm(g ? { modo: "gasto", ...g, monto: String(g.monto), cotizacion: String(g.cotizacion || mep), hasta: g.hasta || "", nota: g.nota || "", medio_pago: g.medio_pago || "", a_quien: g.a_quien || "", comprobante_url: g.comprobante_url || "" }
    : { modo: "gasto", fecha: finHoy(), monto: "", moneda: "ARS", cotizacion: String(mep), concepto: "", categoria: "marketing", estado: "pagado", tipo: "unico", serie: "", hasta: "", nota: "", medio_pago: "", a_quien: "", comprobante_url: "" }); };
  const abrirCuota = (r) => { setMasDatos(false); setForm({ modo: "gasto", fecha: finHoy(), monto: String(r.monto), moneda: r.moneda, cotizacion: String(mep), concepto: r.concepto, categoria: r.categoria, estado: "pagado", tipo: r.tipo, serie: r.serie, hasta: "", nota: "", medio_pago: "", a_quien: "", comprobante_url: "" }); };
  const abrirIngreso = (i) => setForm(i ? { modo: "ingreso", id: i.id, fecha: i.fecha, monto: String(i.monto_ars), moneda: "ARS", concepto: i.concepto, categoria: i.categoria, nota: i.notas || "" }
    : { modo: "ingreso", fecha: finHoy(), monto: "", moneda: "ARS", concepto: "", categoria: "sponsors", nota: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    const monto = finNum(form.monto);
    if (!form.concepto.trim() || !(monto > 0)) { alert("Poné monto y concepto."); return; }
    setGuardando(true);
    if (form.modo === "gasto") {
      const p = { ...form, monto, cotizacion: form.moneda === "ARS" ? finNum(form.cotizacion) || Number(mep) : null };
      delete p.modo;
      const { error } = await supabase.rpc("finanzas_guardar", { p });
      setGuardando(false);
      if (error) { alert("No se pudo guardar: " + error.message); return; }
    } else {
      const m = Number(mep) || 1540;
      const fila = { fecha: form.fecha, concepto: form.concepto.trim(), categoria: form.categoria,
        monto_ars: form.moneda === "USD" ? Math.round(monto * m) : monto,
        monto_usd: form.moneda === "USD" ? monto : Math.round(monto / m * 100) / 100,
        notas: (form.nota || "").trim() || null };
      let error;
      if (form.id) ({ error } = await supabase.from("ingresos").update(fila).eq("id", form.id));
      else {
        const { data: { user } } = await supabase.auth.getUser();
        ({ error } = await supabase.from("ingresos").insert({ ...fila, creado_por: user?.id || null }));
      }
      setGuardando(false);
      if (error) { alert("No se pudo guardar: " + error.message); return; }
    }
    setForm(null);
    cargar();
  };
  const borrar = async () => {
    if (!window.confirm(form.modo === "gasto" ? "¿Borrar este gasto?" : "¿Borrar este ingreso? También deja de sumar al Fondo Olbos.")) return;
    setGuardando(true);
    const { error } = form.modo === "gasto" ? await supabase.rpc("finanzas_borrar", { p_id: form.id }) : await supabase.from("ingresos").delete().eq("id", form.id);
    setGuardando(false);
    if (error) { alert("No se pudo borrar: " + error.message); return; }
    setForm(null);
    cargar();
  };
  const subirComprobante = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try { const url = await subirFotoProducto(file); set("comprobante_url", url); } catch (e) { alert("No se pudo subir: " + (e.message || e)); }
    setSubiendo(false);
  };

  const inp = { width: "100%", boxSizing: "border-box", padding: "12px 13px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, fontSize: 15, color: C.text, fontFamily: "inherit" };
  const lbl = { fontSize: 11.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", margin: "14px 0 6px" };
  const chip = (activo) => ({ padding: "8px 12px", borderRadius: 100, border: `1px solid ${activo ? C.text : C.border}`, background: activo ? C.text : C.card, color: activo ? "#F9F5F2" : C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" });
  const header = (titulo, back) => (
    <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
      <button onClick={back} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronLeft size={18} strokeWidth={2} />
      </button>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: "-0.015em" }}>{titulo}</div>
    </div>
  );

  // ── Formulario (pantalla propia, sin overlays fijos) ──
  if (form) {
    const esGasto = form.modo === "gasto";
    const cats = esGasto ? FIN_CATS : INGRESOS_CATS;
    const series = (lista && lista.series) || [];
    return (
      <PageWrap>
        {header(form.id ? (esGasto ? "Editar gasto" : "Editar ingreso") : (esGasto ? "Anotar gasto" : "Anotar ingreso"), () => setForm(null))}
        <div style={{ padding: "4px 16px 120px", maxWidth: 560, margin: "0 auto" }}>
          <div style={lbl}>Monto</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input inputMode="decimal" autoFocus={!form.id} value={form.monto} onChange={e => set("monto", e.target.value)} placeholder="0" style={{ ...inp, fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums" }} />
            {["ARS", "USD"].map(m => <button key={m} onClick={() => set("moneda", m)} style={{ ...chip(form.moneda === m), padding: "0 14px", borderRadius: 12 }}>{m}</button>)}
          </div>
          {esGasto && form.moneda === "ARS" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: C.muted }}>
              Dólar MEP usado
              <input inputMode="decimal" value={form.cotizacion} onChange={e => set("cotizacion", e.target.value)} style={{ ...inp, width: 90, padding: "6px 8px", fontSize: 13 }} />
              {finNum(form.monto) > 0 && finNum(form.cotizacion) > 0 && <span>≈ {finUSD(finNum(form.monto) / finNum(form.cotizacion))}</span>}
            </div>
          )}
          <div style={lbl}>Concepto</div>
          <input value={form.concepto} onChange={e => set("concepto", e.target.value)} placeholder={esGasto ? "Ej: Supabase de noviembre" : "Ej: Sponsor Motos Benito"} style={inp} />
          <div style={lbl}>Categoría</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {cats.map(([k, l]) => <button key={k} onClick={() => set("categoria", k)} style={chip(form.categoria === k)}>{l}</button>)}
          </div>
          <div style={lbl}>Fecha</div>
          <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)} style={inp} />
          {esGasto && <>
            <div style={lbl}>Estado</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[["pagado", "Pagado"], ["comprometido", "Comprometido"], ...(form.estado === "presupuesto" ? [["presupuesto", "Presupuesto"]] : [])].map(([k, l]) => <button key={k} onClick={() => set("estado", k)} style={{ ...chip(form.estado === k), flex: 1 }}>{l}</button>)}
            </div>
            <div style={lbl}>Se repite</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[["unico", "Una vez"], ["mensual", "Mensual"], ["anual", "Anual"]].map(([k, l]) => <button key={k} onClick={() => set("tipo", k)} style={{ ...chip(form.tipo === k), flex: 1 }}>{l}</button>)}
            </div>
            {form.tipo !== "unico" && (
              <div style={{ background: C.surface, borderRadius: 12, padding: "10px 12px", marginTop: 8 }}>
                <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>¿Es la cuota de un recurrente que ya está anotado?</div>
                <select value={form.serie || ""} onChange={e => { const s = series.find(x => x.serie === e.target.value); setForm(f => s ? { ...f, serie: s.serie, concepto: f.concepto || s.concepto, categoria: s.categoria, moneda: s.moneda, monto: f.monto || String(s.monto) } : { ...f, serie: "" }); }} style={{ ...inp, padding: "9px 10px", fontSize: 14 }}>
                  <option value="">No, es un recurrente nuevo</option>
                  {series.map(s => <option key={s.serie} value={s.serie}>{s.concepto}</option>)}
                </select>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: C.muted }}>
                  Termina (opcional)
                  <input type="date" value={form.hasta || ""} onChange={e => set("hasta", e.target.value)} style={{ ...inp, width: "auto", flex: 1, padding: "7px 9px", fontSize: 13 }} />
                </div>
              </div>
            )}
          </>}
          <div style={lbl}>Nota</div>
          <textarea value={form.nota} onChange={e => set("nota", e.target.value)} rows={2} placeholder="Opcional" style={{ ...inp, resize: "vertical" }} />
          {esGasto && (
            <>
              <button onClick={() => setMasDatos(v => !v)} style={{ background: "none", border: "none", padding: "12px 0 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{masDatos ? "− Menos datos" : "+ Medio de pago, a quién, comprobante"}</button>
              {masDatos && <>
                <div style={lbl}>Medio de pago</div>
                <input value={form.medio_pago} onChange={e => set("medio_pago", e.target.value)} placeholder="Transferencia, Visa, efectivo…" style={inp} />
                <div style={lbl}>A quién</div>
                <input value={form.a_quien} onChange={e => set("a_quien", e.target.value)} placeholder="Proveedor o persona" style={inp} />
                <div style={lbl}>Comprobante</div>
                {form.comprobante_url
                  ? <div style={{ display: "flex", gap: 10, alignItems: "center" }}><a href={form.comprobante_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Ver comprobante</a><button onClick={() => set("comprobante_url", "")} style={{ background: "none", border: "none", color: C.muted, fontSize: 12.5, cursor: "pointer" }}>Quitar</button></div>
                  : <label style={{ ...chip(false), display: "inline-block" }}>{subiendo ? "Subiendo…" : "Subir foto"}<input type="file" accept="image/*" onChange={e => subirComprobante(e.target.files && e.target.files[0])} style={{ display: "none" }} /></label>}
              </>}
            </>
          )}
          {!esGasto && <div style={{ fontSize: 12, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>Se guarda en Ingresos: suma al Fondo Olbos de ese mes.{form.moneda === "USD" ? ` Se convierte a pesos al MEP de hoy (${mep}).` : ""}</div>}
          <button onClick={guardar} disabled={guardando} style={{ width: "100%", marginTop: 20, padding: "15px 0", borderRadius: 14, background: C.text, color: "#F9F5F2", border: "none", fontSize: 15.5, fontWeight: 700, cursor: "pointer", opacity: guardando ? 0.6 : 1, fontFamily: "inherit" }}>{guardando ? "Guardando…" : "Guardar"}</button>
          {form.id && <button onClick={borrar} disabled={guardando} style={{ width: "100%", marginTop: 10, padding: "12px 0", borderRadius: 14, background: "none", color: C.red, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}><Trash2 size={15} /> Borrar</button>}
        </div>
      </PageWrap>
    );
  }

  const gastos = (lista && lista.gastos) || [];
  const ingresos = (lista && lista.ingresos) || [];
  const cats = (res && res.por_categoria) || [];
  const maxCat = Math.max(1, ...cats.map(c => Number(c.usd_total) || 0));
  const comp = (res && res.comprometido_mar27) || {};
  const resultado = res ? Number(res.resultado_mes_usd) : 0;
  return (
    <PageWrap>
      {header("Finanzas", onBack)}
      <div style={{ padding: "4px 16px 120px", maxWidth: 640, margin: "0 auto" }}>
        {/* Momento oscuro: el resultado del mes y lo invertido */}
        <div style={{ background: "#111111", color: "#F9F5F2", borderRadius: 18, padding: "16px 18px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={() => moverMes(-1)} style={{ background: "none", border: "none", color: "#F9F5F2", cursor: "pointer", padding: 4 }}><ChevronLeft size={18} /></button>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c9c3ba" }}>Resultado de {nombreMes}</div>
            <button onClick={() => moverMes(1)} style={{ background: "none", border: "none", color: "#F9F5F2", cursor: "pointer", padding: 4 }}><ChevronRight size={18} /></button>
          </div>
          <div style={{ textAlign: "center", fontSize: 36, fontWeight: 700, letterSpacing: "-0.025em", margin: "6px 0 2px", fontVariantNumeric: "tabular-nums" }}>{res ? finUSD(resultado) : "—"}</div>
          <div style={{ textAlign: "center", fontSize: 12.5, color: "#c9c3ba", fontVariantNumeric: "tabular-nums" }}>
            {res ? `Ingresos ${finUSD(res.ingresos_mes_usd)} · Gastos ${finUSD(res.gasto_mes_usd)} · ${finARS(res.resultado_mes_ars)}` : "Cargando…"}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14, borderTop: "1px solid #2a2a2a", paddingTop: 12 }}>
            {[["Invertido en total", res ? finUSD(res.total_invertido_usd) : "—", res ? finARS(res.acumulado_ars) : ""],
              ["Gasto fijo por mes", res ? finUSD(res.run_rate_usd) : "—", "recurrentes activos"],
              ["Comprometido a mar-27", res ? finUSD(comp.total_usd) : "—", comp.presupuesto_usd ? `+ ${finUSD(comp.presupuesto_usd)} de presupuesto` : ""]].map(([l, v, s]) => (
              <div key={l} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.015em", fontVariantNumeric: "tabular-nums" }}>{v}</div>
                <div style={{ fontSize: 10.5, color: "#c9c3ba", fontWeight: 600 }}>{l}</div>
                {s && <div style={{ fontSize: 10, color: "#8A857F" }}>{s}</div>}
              </div>
            ))}
          </div>
        </div>

        {err && <div style={{ background: C.surface, borderRadius: 12, padding: "12px 14px", fontSize: 13, color: C.red, marginTop: 12 }}>{err}</div>}

        <button onClick={() => abrirGasto(null)} style={{ width: "100%", marginTop: 14, padding: "16px 0", borderRadius: 14, background: C.text, color: "#F9F5F2", border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", letterSpacing: "-0.01em", fontFamily: "inherit" }}>+ Anotar gasto</button>
        <button onClick={() => abrirIngreso(null)} style={{ width: "100%", marginTop: 8, padding: "13px 0", borderRadius: 14, background: C.surface, color: C.text, border: "none", fontSize: 14.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Anotar ingreso</button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: C.muted }}>
          Dólar MEP de hoy
          <input inputMode="decimal" value={mep} onChange={e => setMep(Number(e.target.value) || 0)} onBlur={cargar} style={{ ...inp, width: 90, padding: "6px 8px", fontSize: 13.5, fontVariantNumeric: "tabular-nums" }} />
        </div>

        {/* Lista del mes */}
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "20px 0 8px", letterSpacing: "-0.01em" }}>Gastos de {nombreMes}</div>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, marginBottom: 6 }}>
          <button onClick={() => setCat("")} style={chip(!cat)}>Todas</button>
          {FIN_CATS.map(([k, l]) => <button key={k} onClick={() => setCat(k)} style={chip(cat === k)}>{l}</button>)}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
          {lista === null ? <div style={{ padding: 18, textAlign: "center", fontSize: 13, color: C.muted }}>Cargando…</div>
            : gastos.length === 0 ? <div style={{ padding: 18, textAlign: "center", fontSize: 13, color: C.muted }}>Nada anotado {cat ? `en ${finCatLabel(cat)} ` : ""}este mes.</div>
            : gastos.map((g, i) => (
              <button key={g.id} onClick={() => abrirGasto(g)} style={{ width: "100%", display: "flex", gap: 10, alignItems: "center", padding: "12px 14px", background: C.card, border: "none", borderBottom: i < gastos.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.concepto}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>
                    {g.fecha.slice(8, 10)}/{g.fecha.slice(5, 7)} · {finCatLabel(g.categoria)}{g.tipo !== "unico" ? ` · ${g.tipo}` : ""}
                    {g.estado !== "pagado" && <span style={{ marginLeft: 6, padding: "1px 7px", borderRadius: 100, border: `1px solid ${C.gold}`, color: C.gold, fontWeight: 700, fontSize: 10.5 }}>{g.estado}</span>}
                  </div>
                </div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{g.moneda === "USD" ? finUSD(g.monto) : finARS(g.monto)}</div>
                  {g.moneda === "ARS" && <div style={{ fontSize: 11, color: C.muted }}>{finUSD(g.monto_usd)}</div>}
                </div>
              </button>
            ))}
        </div>

        {!cat && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "20px 0 8px", letterSpacing: "-0.01em" }}>Ingresos de {nombreMes}</div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              {ingresos.length === 0 ? <div style={{ padding: 16, textAlign: "center", fontSize: 13, color: C.muted }}>Sin ingresos anotados este mes.</div>
                : ingresos.map((x, i) => (
                  <button key={x.id} onClick={() => abrirIngreso(x)} style={{ width: "100%", display: "flex", gap: 10, alignItems: "center", padding: "12px 14px", background: C.card, border: "none", borderBottom: i < ingresos.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.concepto}</div>
                      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>{x.fecha.slice(8, 10)}/{x.fecha.slice(5, 7)} · {(INGRESOS_CATS.find(c => c[0] === x.categoria) || [0, x.categoria])[1]}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.green, fontVariantNumeric: "tabular-nums" }}>+{finARS(x.monto_ars)}</div>
                  </button>
                ))}
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Los espacios cobrados por Mercado Pago se ven en el panel de admin → Ingresos.</div>
          </>
        )}

        {/* Por categoría */}
        {cats.length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 14px 8px", marginTop: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2, letterSpacing: "-0.01em" }}>Por categoría</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10 }}>Pagado acumulado · en gris, lo de {nombreMes}</div>
            {cats.map(c => (
              <div key={c.categoria} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: C.text, marginBottom: 3, fontVariantNumeric: "tabular-nums" }}>
                  <span style={{ fontWeight: 600 }}>{finCatLabel(c.categoria)}</span>
                  <span>{finUSD(c.usd_total)}{Number(c.usd_mes) > 0 ? <span style={{ color: C.muted }}> · mes {finUSD(c.usd_mes)}</span> : null}</span>
                </div>
                <div style={{ height: 8, background: C.surface, borderRadius: 4, overflow: "hidden", position: "relative" }}>
                  <div style={{ width: `${Number(c.usd_total) / maxCat * 100}%`, height: "100%", background: C.text, borderRadius: 4 }} />
                  {Number(c.usd_mes) > 0 && <div style={{ position: "absolute", left: 0, top: 0, width: `${Number(c.usd_mes) / maxCat * 100}%`, height: "100%", background: C.muted, borderRadius: 4 }} />}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recurrentes y compromisos */}
        {res && (res.recurrentes || []).length > 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 14px 6px", marginTop: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>Gastos fijos · {finUSD(res.run_rate_usd)}/mes</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>Los pesos, al MEP de hoy. Tocá "Pagado" para anotar la cuota de este mes.</div>
            {res.recurrentes.map(r => (
              <div key={r.serie} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.concepto}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{finUSD(r.usd_mes)}/mes{r.tipo === "anual" ? " (anual)" : ""}{r.hasta ? ` · hasta ${r.hasta.slice(5, 7)}/${r.hasta.slice(2, 4)}` : ""}{r.contrato ? " · contrato" : ""}</div>
                </div>
                <button onClick={() => abrirCuota(r)} style={{ ...chip(false), padding: "6px 11px", fontSize: 12 }}>Pagado</button>
              </div>
            ))}
          </div>
        )}
        {res && (
          <div style={{ background: C.surface, borderRadius: 14, padding: "14px 14px 10px", marginTop: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8, letterSpacing: "-0.01em" }}>Comprometido hasta marzo 2027</div>
            {[["Contratos (agencia, acuerdos)", comp.contratos_usd], ["Pagos únicos pendientes", comp.unicos_usd], ["Gastos fijos que vienen", comp.recurrentes_usd]].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.text, padding: "3px 0", fontVariantNumeric: "tabular-nums" }}><span>{l}</span><span>{finUSD(v)}</span></div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: C.text, padding: "6px 0 2px", borderTop: `1px solid ${C.border}`, marginTop: 4, fontVariantNumeric: "tabular-nums" }}><span>Total</span><span>{finUSD(comp.total_usd)}</span></div>
            {Number(comp.presupuesto_usd) > 0 && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>Aparte, {finUSD(comp.presupuesto_usd)} de presupuesto aprobado (pauta): se anota como gasto a medida que se ejecuta.</div>}
            {Number(res.ingresos_total_usd) > 0 && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>Ingresos acumulados: {finUSD(res.ingresos_total_usd)} ({finARS(res.ingresos_total_ars)}).</div>}
          </div>
        )}
      </div>
    </PageWrap>
  );
};

const Mas = ({ setTab, session, requireAuth, onRegistroNegocio, onAI, onOrdenNav, esAgencia = false, onAgencia = null }) => {
  const [showOrden, setShowOrden] = useState(false);
  const [showInvita, setShowInvita] = useState(false);
  const [showSobre, setShowSobre] = useState(false);
  const [showInvitarPerfil, setShowInvitarPerfil] = useState(false); // staff: perfiles pre-armados
  const [showPedidos, setShowPedidos] = useState(false); // staff: pedidos al equipo de desarrollo
  const [showVersiones, setShowVersiones] = useState(false); // admin: aprobar versiones para las tiendas
  const [showFinanzas, setShowFinanzas] = useState(false); // admin: gastos, pagos e ingresos de Olbos
  const [showPromos, setShowPromos] = useState(false); // links promocionados: saldo y números
  const [showSugerir, setShowSugerir] = useState(false); // equipo: cuentas de Instagram para el scouting
  const [showAnunciantes, setShowAnunciantes] = useState(false); // equipo: anunciantes con 100 clicks gratis
  const [showConcesionarios, setShowConcesionarios] = useState(false); // equipo: concesionarios oficiales y quién maneja cada cuenta
  const [showMisLocales, setShowMisLocales] = useState(false); // concesionarias: cuentas de sus locales y concesionario oficial
  const [showNegociosAprobar, setShowNegociosAprobar] = useState(false); // equipo: negocios nuevos esperando aprobación
  const [promosOk, setPromosOk] = useState(false);
  useEffect(() => { funcionVisible("promos").then(v => setPromosOk(!!v), () => {}); }, [session]);
  const [esAdminMas, setEsAdminMas] = useState(false);
  const [esStaff, setEsStaff] = useState(false);
  useEffect(() => {
    if (!session?.user) { setEsStaff(false); return; }
    supabase.from("perfiles").select("rol, es_admin").eq("id", session.user.id).single()
      .then(({ data }) => { setEsStaff(data?.rol === "staff" || !!data?.es_admin); setEsAdminMas(!!data?.es_admin); }, () => {});
  }, [session]);
  if (showOrden) return <OrdenSecciones session={session} onCerrar={() => setShowOrden(false)} onCambio={onOrdenNav} />;
  if (showSobre) return <SobreOlbos onBack={() => setShowSobre(false)} />;
  if (showPedidos) return <PedidosEquipo onBack={() => setShowPedidos(false)} />;
  if (showVersiones) return <VersionesTiendas onBack={() => setShowVersiones(false)} />;
  if (showFinanzas) return <Finanzas onBack={() => setShowFinanzas(false)} />;
  if (showPromos) return <MisLinksPromo onBack={() => setShowPromos(false)} />;
  if (showSugerir) return <SugerirCuentaScouting onBack={() => setShowSugerir(false)} />;
  if (showAnunciantes) return <AnunciantesContactar onBack={() => setShowAnunciantes(false)} />;
  if (showConcesionarios) return <ConcesionariosOficiales onBack={() => setShowConcesionarios(false)} />;
  if (showMisLocales) return <MisLocales session={session} onBack={() => setShowMisLocales(false)} />;
  if (showNegociosAprobar) return <NegociosParaAprobar onBack={() => setShowNegociosAprobar(false)} />;
  if (showInvita) return <><InvitaModal session={session} onClose={() => setShowInvita(false)} /></>;
  if (showInvitarPerfil) return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => setShowInvitarPerfil(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={18} strokeWidth={2} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Crear perfil de organizador</div>
      </div>
      <div style={{ padding: "0 4px 100px" }}>
        <InvitarPerfilTab />
      </div>
    </PageWrap>
  );
  const MAS_ITEMS = [
    { id: "orden-secciones", label: "Ordenar las secciones", desc: "Poné arriba lo que más usás", Ico: Edit2 },
    { id: "mensajes",     label: "Mensajes",       desc: "Tus conversaciones con otros riders", Ico: MessageCircle },
    { id: "robadas",      label: "Alertas",        desc: "Robos, hallazgos y avisos en ruta", Ico: AlertTriangle },
    // Compra-Venta vive dentro de Servicios (Pit Stop) pero merece puerta propia (pedido de Carlos 03-sep)
    { id: "compraventa",  label: "Compra-Venta",   desc: "Motos, accesorios y repuestos entre riders", Ico: Tag },
    { id: "grupos",       label: "Grupos",         desc: "Tu comunidad y sus chats",          Ico: Users },
    { id: "productos",    label: "Productos",      desc: "Recomendaciones para riders",       Ico: ShoppingBag },
    { id: "motos",        label: "Motos",          desc: "Base de datos de motos",            Ico: Bike },
    { id: "noticias",     label: "Noticias",       desc: "El mundo moto, acá y afuera",       Ico: Globe },
    { id: "invita",       label: "Invitá y sumá",  desc: "Tu link de rider y la tabla de embajadores", Ico: Share2 },
    // Concesionarias y comercios con varios locales o marcas (autogestión, 30-oct-2026)
    ...(session ? [{ id: "mis-locales", label: "Mis locales", desc: "Concesionarias y comercios: una cuenta por local y concesionario oficial", Ico: ShoppingBag }] : []),
    // Solo equipo (staff/admin): perfiles pre-armados para organizadores, con link de reclamo
    ...(promosOk && session ? [{ id: "promos", label: "Mis links promocionados", desc: "Tu saldo, los clicks y cuánto gastaste", Ico: Tag }] : []),
    ...(esAdminMas ? [{ id: "finanzas", label: "💼 Finanzas", desc: "Gastos, pagos e ingresos de Olbos (solo vos)", Ico: TrendingUp }] : []),
    ...(esAdminMas ? [{ id: "versiones", label: "Versiones", desc: "Aprobar la versión nueva para Play y App Store", Ico: Send }] : []),
    ...(esStaff ? [{ id: "sugerir-scouting", label: "Sugerir cuenta para el scouting", desc: "Un organizador o influencer que viste en Instagram", Ico: Search }] : []),
    ...(esStaff ? [{ id: "anunciantes", label: "Anunciantes para contactar", desc: "Cuentas que venden para riders: 100 clicks gratis", Ico: Tag }] : []),
    ...(esStaff ? [{ id: "pedidos-equipo", label: "Pedidos al equipo", desc: "Algo que no anda o una mejora: se resuelve y te contestan acá", Ico: Wrench }] : []),
    ...(esStaff ? [{ id: "invitar-perfil", label: "Crear perfil de organizador", desc: "Perfil pre-armado + link de reclamo (equipo Olbos)", Ico: Award }] : []),
    ...(esStaff ? [{ id: "negocios-aprobar", label: "Negocios para aprobar", desc: "Los que se registraron en Servicios y esperan revisión", Ico: ShoppingBag }] : []),
    ...(esStaff ? [{ id: "concesionarios", label: "Concesionarios oficiales", desc: "Validar locales de una marca y quién maneja cada cuenta", Ico: CheckCircle }] : []),
    { id: "sobre-olbos",  label: "Sobre Olbos",    desc: "Qué es, cómo nació, qué significa", Ico: Info },
  ];

  return (
    <PageWrap>
      {/* Header Apple-style: tipografía SF, mucho espacio, sin chip color */}
      <div style={{ padding: "var(--header-pt) 24px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Menú</div>
        </div>
        <h1 style={{
          fontSize: 32, fontWeight: 700, color: C.text,
          letterSpacing: "-0.03em", lineHeight: 1.1,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
        }}>Más secciones</h1>
      </div>

      <div style={{ padding: "8px 16px 100px" }}>
        {/* Perfil de agencia (rol "agencia" en perfiles): métricas, campañas y mails
            en modo lectura, desde la compu. Pedido de Carlos, 18-sep-2026. */}
        {esAgencia && onAgencia && (
          <button onClick={onAgencia}
            style={{ width: "100%", marginBottom: 12, padding: "14px 16px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>📈</span>
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>Panel de agencia</span>
              <span style={{ display: "block", fontSize: 12, color: "#c9c3ba", marginTop: 2 }}>Métricas de la comunidad, links de campaña y mails. Se usa desde la compu.</span>
            </span>
            <ChevronRight size={18} strokeWidth={2} color="#c9c3ba" />
          </button>
        )}
        {/* Premio semanal: tarjeta del ganador para pasar su alias/CVU y, para el admin, revisar el podio */}
        <PremioSemanalMas session={session} esAdmin={esAdminMas} />
        {/* Lista Apple-style: grupo con bordes redondeados, separadores internos sutiles */}
        <div style={{
          background: C.card,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          overflow: "hidden",
        }}>
          {MAS_ITEMS.map((item, i) => (
            <button key={item.id} onClick={() => {
              if (item.id === "orden-secciones") { setShowOrden(true); return; }
              if (item.id === "sobre-olbos") { setShowSobre(true); return; }
              if (item.id === "invita") { setShowInvita(true); return; }
              if (item.id === "invitar-perfil") { setShowInvitarPerfil(true); return; }
              if (item.id === "pedidos-equipo") { setShowPedidos(true); return; }
              if (item.id === "versiones") { setShowVersiones(true); return; }
              if (item.id === "finanzas") { setShowFinanzas(true); return; }
              if (item.id === "promos") { setShowPromos(true); return; }
              if (item.id === "sugerir-scouting") { setShowSugerir(true); return; }
              if (item.id === "anunciantes") { setShowAnunciantes(true); return; }
              if (item.id === "concesionarios") { setShowConcesionarios(true); return; }
              if (item.id === "mis-locales") { setShowMisLocales(true); return; }
              if (item.id === "negocios-aprobar") { setShowNegociosAprobar(true); return; }
              if (item.id === "compraventa") { window.olbosPitStopTab = "compraventa"; setTab("mercado"); return; }
              if (item.id === "perfil") requireAuth ? requireAuth(() => setTab("perfil")) : setTab("perfil");
              else setTab(item.id);
            }} style={{
              width: "100%",
              display: "flex",
              gap: 14,
              alignItems: "center",
              padding: "14px 16px",
              background: C.card,
              border: "none",
              borderBottom: i < MAS_ITEMS.length - 1 ? `1px solid ${C.border}` : "none",
              cursor: "pointer",
              textAlign: "left",
              transition: "background .15s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = C.surface; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: C.surface,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <item.Ico size={17} strokeWidth={1.8} color="#111111" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 15, fontWeight: 500, color: C.text,
                  letterSpacing: "-0.01em", marginBottom: 1,
                }}>{item.label}</div>
                <div style={{
                  fontSize: 12.5, color: C.muted,
                  letterSpacing: "-0.005em",
                }}>{item.desc}</div>
              </div>
              <ChevronRight size={16} strokeWidth={2} color="#c9c3ba" />
            </button>
          ))}
        </div>

        {/* Card "Registrar negocio" — separado, más sutil */}
        <div style={{
          marginTop: 24,
          padding: "18px 18px 16px",
          background: C.surface,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: C.text,
            letterSpacing: "-0.01em", marginBottom: 4,
          }}>¿Tenés un taller, concesionario o local?</div>
          <div style={{
            fontSize: 12.5, color: C.muted,
            letterSpacing: "-0.005em", marginBottom: 14,
          }}>Sumate al Pit Stop y aparecé en el mapa de los riders.</div>
          <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-registro-negocio"))}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10,
              background: C.accent, color: "#fff",
              fontSize: 14, fontWeight: 500, border: "none",
              cursor: "pointer", letterSpacing: "-0.01em",
              transition: "background .15s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#111111"; }}>
            Registrar mi negocio
          </button>
        </div>

        {/* Card contacto WhatsApp para riders */}
        <div style={{
          marginTop: 14,
          padding: "18px 18px 16px",
          background: C.surface,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: C.text,
            letterSpacing: "-0.01em", marginBottom: 4,
          }}>¿Tenés una idea o querés contarnos algo?</div>
          <div style={{
            fontSize: 12.5, color: C.muted,
            letterSpacing: "-0.005em", marginBottom: 14,
          }}>Olbos se construye con la comunidad. Escribinos cualquier sugerencia, bug o duda.</div>
          <button onClick={() => {
            const msg = encodeURIComponent("Hola Charly, quería contarte algo sobre Olbos");
            window.open(`https://wa.me/5491130718112?text=${msg}`, "_blank", "noopener,noreferrer");
          }}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10,
              background: "#25D366", color: "#fff",
              fontSize: 14, fontWeight: 500, border: "none",
              cursor: "pointer", letterSpacing: "-0.01em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "background .15s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#1FB855"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#25D366"; }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Escribinos por WhatsApp
          </button>
        </div>
      </div>
    </PageWrap>
  );
};


// ─── MODAL ───────────────────────────────────────────────────────────────────
// `footer` (opcional): una barra que queda SIEMPRE visible al pie de la hoja mientras el
// contenido scrollea (el "Anotarme" de la ficha del evento). Sin footer, la hoja es la de
// siempre: una sola caja que scrollea entera.
const Modal = ({ title, onClose, children, footer }) => (
  <div style={{
    position: "fixed", inset: 0, background: "#000000CC", zIndex: 200,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  }} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{
      background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430,
      maxHeight: "90vh",
      ...(footer
        ? { display: "flex", flexDirection: "column", overflow: "hidden", padding: 0 }
        // safe area de abajo: sin esto la barra de navegación de Android/iOS tapa el final del modal
        : { overflow: "auto", padding: 20, paddingBottom: "calc(20px + var(--sab))" }),
      borderTop: `2px solid ${C.accent}`, boxShadow: "0 -4px 24px rgba(0,0,0,0.08)",
    }} className="fade-up">
      {footer ? (
        <>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 20, paddingBottom: 24, WebkitOverflowScrolling: "touch" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>{title}</h3>
              <button onClick={onClose} style={{ color: C.muted, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: C.surface, flexShrink: 0 }}>
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
            {children}
          </div>
          <div style={{ flexShrink: 0, padding: "10px 16px calc(10px + var(--sab))", background: C.card, borderTop: `1px solid ${C.border}`, boxShadow: "0 -6px 18px rgba(0,0,0,.06)" }}>
            {footer}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>{title}</h3>
            <button onClick={onClose} style={{ color: C.muted, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: C.surface }}>
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
          {children}
        </>
      )}
    </div>
  </div>
);

// ─── AI SEARCH MODAL ─────────────────────────────────────────────────────────
const AISearchModal = ({ onClose, onNavigate, eventos }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  // Los eventos REALES de la base (antes buscaba sobre el array demo, que está vacío)
  const listaTodos = (Array.isArray(eventos) && eventos.length > 0) ? eventos : EVENTS;
  // Solo eventos que todavía no pasaron (fecha_fin o fecha >= hoy), ordenados por fecha
  const hoyIA = hoyLocalISO();
  const lista = listaTodos
    .filter(e => String(e.fecha_fin || e.fecha || e.date || "").slice(0, 10) >= hoyIA)
    .sort((a, b) => String(a.fecha || a.date || "").localeCompare(String(b.fecha || b.date || "")));

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults(null);
    setError("");
    try {
      // Vía el proxy /api/chat: desde el navegador no hay API key (antes fallaba siempre)
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: `Sos el asistente de Olbos, una app para la comunidad motera.
El usuario busca eventos de moto. Estos son los eventos disponibles: ${JSON.stringify(lista.slice(0, 60).map(e => ({ id: e.id, title: e.title || e.titulo, date: e.date, fecha: e.fecha, fecha_fin: e.fecha_fin, lugar: e.lugar, from: e.from, to: e.to, km: e.km, diff: e.diff, precio: e.precio, tipo: e.tipo })))}.
Respondé SOLO en JSON con este formato exacto, sin texto adicional, sin markdown:
{"mensaje": "respuesta breve y copada en español rioplatense", "eventos_ids": ["id1", "id2"], "sugerencia": "tip adicional corto"}
Los ids son los del campo id, copiados TAL CUAL. Hoy es ${hoyIA} (formato AAAA-MM-DD) y la lista ya trae solo eventos que no pasaron. Respetá la fecha que pide el usuario ("hoy", "mañana", "este finde"): devolvé únicamente eventos que caigan en esa fecha. Si no hay ninguno para esa fecha, devolvé eventos_ids vacío y decilo claro en mensaje (ej: "No encontré eventos para hoy cerca de X"); no sugieras otros de fechas distintas salvo mencionarlos en el mensaje como alternativa. Si no hay eventos relevantes, devolvé eventos_ids vacío y explicá en mensaje.`,
          messages: [{ role: "user", content: query }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResults(parsed);
    } catch (e) {
      setError("No se pudo conectar con el asistente. Probá con el buscador de arriba.");
    }
    setLoading(false);
  };

  const foundEvents = results?.eventos_ids?.map(id => lista.find(e => String(e.id) === String(id))).filter(Boolean) || [];

  return (
    <Modal title="🤖 Buscar con IA" onClose={onClose}>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
        Contame qué estás buscando: destino, fecha, tipo de moto, nivel de dificultad...
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          placeholder="Ej: quiero ir a Mendoza en julio con mi 650cc..."
          style={{
            flex: 1, padding: "10px 14px", borderRadius: 8,
            background: C.card, border: `1px solid ${C.border}`,
            color: C.text, fontSize: 14, outline: "none",
          }}
        />
        <Btn onClick={search} style={{ flexShrink: 0 }}>Buscar</Btn>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <div style={{ fontSize: 28, marginBottom: 8, animation: "pulse 1.2s infinite" }}>🤖</div>
          <p style={{ color: C.muted, fontSize: 13 }}>Analizando eventos...</p>
        </div>
      )}

      {error && <p style={{ color: C.red, fontSize: 13, textAlign: "center" }}>{error}</p>}

      {results && (
        <div className="fade-up">
          <div style={{ background: C.accentLo, borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: `1px solid ${C.accent}33` }}>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>💬 {results.mensaje}</p>
            {results.sugerencia && <p style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>💡 {results.sugerencia}</p>}
          </div>
          {foundEvents.length > 0 && (
            <>
              <p style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 0, textTransform: "uppercase", marginBottom: 10 }}>Eventos sugeridos</p>
              {foundEvents.map(e => (
                <div key={e.id} style={{ background: C.card, borderRadius: 10, padding: "12px 14px", marginBottom: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.5, marginBottom: 4 }}>{e.emoji} {e.title}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{e.from} → {e.to} · {e.date} · {e.diff}</div>
                  {e.precio > 0 && <div style={{ fontSize: 12, color: C.gold, marginTop: 2 }}>Costo: ${e.precio.toLocaleString()}</div>}
                  <div style={{ marginTop: 10 }}>
                    <Btn small onClick={() => (onNavigate ? onNavigate(e) : onClose())} style={{ width: "100%" }}>Ver evento</Btn>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <Divider />
      <p style={{ fontSize: 11, color: C.faint, textAlign: "center" }}>Búsqueda potenciada por IA · Olbos</p>
    </Modal>
  );
};

// ─── MOCK ANOTADOS ────────────────────────────────────────────────────────────
const ANOTADOS_MOCK = {
  1: [
    { user: "PatagoniaRider", moto: "KTM 790 Adventure", avatar: "🧔" },
    { user: "CamiRutera", moto: "Royal Enfield Meteor", avatar: "👩" },
    { user: "AndeanRider", moto: "Triumph Tiger 900", avatar: "🧑" },
    { user: "NomadaMoto", moto: "Honda Africa Twin", avatar: "👨" },
    { user: "GS_Cris", moto: "BMW R1250GS", avatar: "🧔" },
  ],
  2: [
    { user: "NomadaMoto", moto: "Honda Africa Twin", avatar: "👨" },
    { user: "FedeMoto_GS", moto: "BMW R1250GS", avatar: "🧔" },
    { user: "RaidPatagonia", moto: "KTM 1290", avatar: "🧑" },
  ],
  3: [
    { user: "CamiRutera", moto: "Royal Enfield Meteor", avatar: "👩" },
    { user: "MotoSalta", moto: "Honda CB500X", avatar: "👩" },
  ],
  4: [
    { user: "PatagoniaRider", moto: "KTM 790 Adventure", avatar: "🧔" },
    { user: "NomadaMoto", moto: "Honda Africa Twin", avatar: "👨" },
  ],
};

// ─── MODAL: VER ANOTADOS ─────────────────────────────────────────────────────
// Lista pública de anotados (solo @usuario + avatar, sin datos privados) — la ve cualquiera.
const AnotadosModal = ({ evento, onClose }) => {
  const [lista, setLista] = useState(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: raw } = await supabase.rpc("get_anotados_publico", { p_evento_id: evento.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo) setLista(Array.isArray(d) ? d : []);
    })();
    return () => { vivo = false; };
  }, [evento.id]);
  return (
    <Modal title={`Anotados — ${evento.title}`} onClose={onClose}>
      {lista === null ? (
        <div style={{ padding: "24px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
      ) : lista.length === 0 ? (
        <div style={{ padding: "24px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>Todavía no se anotó nadie. ¡Sé el primero! 🏍️</div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
            {lista.length} {evento.cupos_ilimitado ? "" : `de ${evento.max}`} {lista.length === 1 ? "rider anotado" : "riders anotados"}
          </p>
          {lista.map((a) => (
            <div key={a.user_id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: a.avatar_url ? `url(${a.avatar_url}) center/cover` : C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {!a.avatar_url && <User size={18} strokeWidth={1.5} color={C.muted} />}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>@{a.username || "rider"}</div>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

// ─── CANAL DE VOZ (LiveKit) ──────────────────────────────────────────────────
// Llamada grupal de audio para una salida/grupo (room = evento_<id> / grupo_<id>).
// Los riders con intercomunicador conectado al celu quedan comunicados en vivo.
// livekit-client se carga dinámicamente (solo al unirse) para no pesar en el bundle.
const CanalDeVoz = ({ tipo = "evento", id, creatorId, vozConfig, tabla = "eventos", miembros = null, alCambiarEstado = null, gestiona = false, sinConfig = false }) => {
  const roomId = `${tipo}_${id}`;
  const [miId, setMiId] = useState(null);
  const [cfg, setCfg] = useState(vozConfig || {});
  const [estado, setEstado] = useState("idle"); // idle | conectando | conectado | reconectando
  // Intercom robusto (BikerFest): en la ruta el enlace se corta cada tanto y hasta
  // ahora eso te SACABA del canal — había que volver a entrar a mano, arriba de la
  // moto. Ahora se reconecta solo, en silencio, con esperas crecientes, y solo se
  // corta de verdad si el rider toca Salir.
  const estadoRef = useRef("idle");
  useEffect(() => { estadoRef.current = estado; }, [estado]);
  const salidaManualRef = useRef(false);
  const reintentoRef = useRef({ n: 0, timer: null });
  const limpiarReintento = () => {
    if (reintentoRef.current.timer) clearTimeout(reintentoRef.current.timer);
    reintentoRef.current = { n: 0, timer: null };
  };
  const [participantes, setParticipantes] = useState([]);
  const [muteado, setMuteado] = useState(false);
  const [pttHablando, setPttHablando] = useState(false);
  const [verConfig, setVerConfig] = useState(false);
  const [anotados, setAnotados] = useState([]);
  const roomRef = useRef(null);
  const audioBox = useRef(null);
  // "Seguir con la pantalla apagada" (10-oct-2026): con el switch prendido (default), al
  // conectar se levanta el servicio nativo que mantiene vivo el micrófono con el teléfono
  // bloqueado. Apagado, todo como antes.
  const [seguirApagada, setSeguirApagada] = useState(() => { try { return localStorage.getItem("olbos_intercom_fondo") !== "0"; } catch (e) { return true; } });
  const seguirApagadaRef = useRef(seguirApagada);
  const claveFondoRef = useRef("voz_" + Math.random().toString(36).slice(2, 10));

  useEffect(() => { supabase.auth.getUser().then(({ data }) => setMiId(data?.user?.id || null), () => {}); }, []);
  useEffect(() => { setCfg(vozConfig || {}); }, [vozConfig]);
  // Quien monta el canal (la pastilla de audio del mapa) muestra el estado afuera
  useEffect(() => { if (alCambiarEstado) alCambiarEstado(estado, participantes.length); }, [estado, participantes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modalidad: "todos" (todos hablan) | "lideres" (solo líder/barredor; el resto escucha + push-to-talk)
  const modalidad = cfg.modalidad || "todos";
  const esOrg = gestiona || (!!miId && miId === creatorId); // co-organizadores y admin también (gestionaEvento, desde el mapa)
  const esVocero = modalidad === "todos" || miId === cfg.lider_id || miId === cfg.barredor_id || (modalidad === "lideres" && !cfg.lider_id && esOrg);
  // Valores VIVOS para la reconexión (documento de Matías, A2): el reintento corre desde un
  // timer armado en el render del primer ingreso y leía muteado/esVocero viejos, así que al
  // volver la señal el micrófono quedaba en un estado distinto al que mostraba la pantalla.
  const muteadoRef = useRef(false);
  muteadoRef.current = muteado;
  const esVoceroRef = useRef(esVocero);
  esVoceroRef.current = esVocero;
  const [avisoReconexion, setAvisoReconexion] = useState(null);
  const avisarReconexion = (abierto) => {
    setAvisoReconexion(abierto ? "Volviste al canal · tu micrófono está abierto" : "Volviste al canal · tu micrófono está cerrado");
    setTimeout(() => setAvisoReconexion(null), 5000);
  };
  const rolDe = (id) => (id && id === cfg.lider_id) ? "Líder" : (id && id === cfg.barredor_id) ? "Barredor" : "";

  const refrescar = (room) => {
    if (!room) return;
    const lp = room.localParticipant;
    const arr = [{ id: lp.identity, name: (lp.name || "Vos") + " · vos", speaking: lp.isSpeaking, muted: !lp.isMicrophoneEnabled, rol: rolDe(lp.identity) }];
    room.remoteParticipants.forEach(p => arr.push({ id: p.identity, name: p.name || "rider", speaking: p.isSpeaking, muted: !p.isMicrophoneEnabled, rol: rolDe(p.identity) }));
    setParticipantes(arr);
  };

  // Espera creciente entre reintentos: 2, 4, 8, 16, 32, 64, 120 s… con ±25 % al azar para
  // que miles de teléfonos no golpeen /api/livekit-token a la vez cuando vuelve la señal
  // en Altas Cumbres. Nunca se rinde sola: en una rodada podés estar veinte minutos sin
  // señal y querés volver al canal solo.
  const programarReintento = () => {
    if (salidaManualRef.current) return;
    const n = Math.min(reintentoRef.current.n + 1, 7);
    const base = Math.min(120000, 2000 * Math.pow(2, n - 1));
    const espera = Math.round(base * (0.75 + Math.random() * 0.5));
    setEstado("reconectando");
    if (reintentoRef.current.timer) clearTimeout(reintentoRef.current.timer);
    reintentoRef.current = { n, timer: setTimeout(() => { unirse(true); }, espera) };
  };

  const unirse = async (esReintento = false) => {
    if (estadoRef.current === "conectado" || estadoRef.current === "conectando") return;
    if (!esReintento) { salidaManualRef.current = false; limpiarReintento(); }
    setEstado("conectando");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert("Iniciá sesión para usar el intercomunicador."); setEstado("idle"); return; }
      const res = await fetch(API_BASE + "/api/livekit-token", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ room: roomId, name: session.user?.email?.split("@")[0] || "rider" }),
      });
      const data = await res.json();
      if (!res.ok || !data.token) throw new Error(data.error || "no se pudo obtener acceso");

      const { Room, RoomEvent, AudioPresets } = await import("livekit-client");
      // Audio pensado para señal de ruta, no para una oficina: preset "telephone"
      // (~12 kbps, aguanta EDGE), DTX para no transmitir en los silencios y RED
      // (paquetes redundantes) para que un corte breve no se escuche como un tajo.
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          audioPreset: (AudioPresets && AudioPresets.telephone) || undefined,
          dtx: true,
          red: true,
        },
        audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      roomRef.current = room;
      room
        .on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === "audio") { const el = track.attach(); el.autoplay = true; audioBox.current && audioBox.current.appendChild(el); }
        })
        .on(RoomEvent.TrackUnsubscribed, (track) => { track.detach().forEach(el => el.remove()); })
        .on(RoomEvent.ParticipantConnected, () => refrescar(room))
        .on(RoomEvent.ParticipantDisconnected, () => refrescar(room))
        .on(RoomEvent.ActiveSpeakersChanged, () => refrescar(room))
        .on(RoomEvent.TrackMuted, () => refrescar(room))
        .on(RoomEvent.TrackUnmuted, () => refrescar(room))
        // LiveKit intenta recuperarse solo primero: mientras tanto, "Reconectando…"
        // en vez de sacar al rider del canal.
        .on(RoomEvent.Reconnecting, () => setEstado("reconectando"))
        .on(RoomEvent.Reconnected, async () => {
          limpiarReintento(); setEstado("conectado");
          const quiere = !muteadoRef.current && esVoceroRef.current;
          try { if (room.localParticipant.isMicrophoneEnabled !== quiere) await room.localParticipant.setMicrophoneEnabled(quiere); } catch (e) { /* */ }
          refrescar(room); avisarReconexion(quiere);
        })
        .on(RoomEvent.Disconnected, () => {
          roomRef.current = null;
          setParticipantes([]);
          if (salidaManualRef.current) { setEstado("idle"); return; }
          programarReintento();   // se cayó sin que el rider lo pidiera: volver solo
        });
      await room.connect(data.url, data.token);
      // LiveKit se desconecta solo ante el evento "freeze" de la página (que en Android puede
      // llegar con la pantalla apagada). Con el switch prendido se ignora: si de verdad se
      // cortó, LiveKit lo detecta al volver y nuestra reconexión lo levanta.
      if (seguirApagadaRef.current) { try { if (room.onPageLeave) window.removeEventListener("freeze", room.onPageLeave); } catch (e) { /* */ } }
      // Vocero (líder/barredor o modo "todos") entra con mic abierto; oyente entra en silencio.
      // Al reconectar se respeta el mute que tenía el rider, no se le abre el mic solo
      const abrir = esReintento ? (!muteadoRef.current && esVoceroRef.current) : esVoceroRef.current;
      await room.localParticipant.setMicrophoneEnabled(abrir);
      if (!esReintento) setMuteado(!abrir);
      else { setMuteado(!abrir); avisarReconexion(abrir); }
      limpiarReintento();
      setEstado("conectado");
      refrescar(room);
      // Después del getUserMedia (Android 14 exige el permiso de micrófono ya dado)
      if (seguirApagadaRef.current) intercomFondo.prender(claveFondoRef.current);
    } catch (e) {
      console.error("CanalDeVoz:", e);
      // Chunk viejo tras un deploy (index cacheado pide un .chunk.js de livekit con un
      // hash que ya no existe) → recargar una vez para tomar la versión nueva.
      if (recargarSiChunkViejo(e)) return;
      // En un reintento no se molesta al rider con carteles: se vuelve a intentar.
      if (esReintento || salidaManualRef.current === false) {
        if (esReintento) { programarReintento(); return; }
      }
      alert("No se pudo conectar al intercomunicador. " + (e.message || ""));
      setEstado("idle");
    }
  };

  // Si con el canal abierto la modalidad pasa a "solo líderes" (o te sacan el rol), el
  // mic se cierra solo: antes el cambio recién valía para el que volvía a entrar.
  useEffect(() => {
    const r = roomRef.current;
    if (!r || estado !== "conectado" || esVocero) return;
    r.localParticipant.setMicrophoneEnabled(false).catch(() => {});
    setMuteado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esVocero]);

  const salir = async () => {
    salidaManualRef.current = true;
    limpiarReintento();
    intercomFondo.apagar(claveFondoRef.current);
    try { await roomRef.current?.disconnect(); } catch (e) {}
    roomRef.current = null; setEstado("idle"); setParticipantes([]); setPttHablando(false);
  };
  const cambiarSeguirApagada = (v) => {
    setSeguirApagada(v); seguirApagadaRef.current = v;
    try { localStorage.setItem("olbos_intercom_fondo", v ? "1" : "0"); } catch (e) { /* */ }
    const r = roomRef.current;
    if (v) {
      if (estadoRef.current === "conectado" || estadoRef.current === "reconectando") intercomFondo.prender(claveFondoRef.current);
      try { if (r && r.onPageLeave) window.removeEventListener("freeze", r.onPageLeave); } catch (e) { /* */ }
    } else {
      intercomFondo.apagar(claveFondoRef.current);
      try { if (r && r.onPageLeave) window.addEventListener("freeze", r.onPageLeave); } catch (e) { /* */ }
    }
  };
  const toggleMute = async () => {
    const room = roomRef.current; if (!room) return;
    const m = !muteado; setMuteado(m);
    await room.localParticipant.setMicrophoneEnabled(!m);
    refrescar(room);
  };
  // Push-to-talk (oyentes en modo "solo líderes"): mantener apretado para hablar
  const pttDown = async () => { const r = roomRef.current; if (!r || pttHablando) return; setPttHablando(true); await r.localParticipant.setMicrophoneEnabled(true); };
  const pttUp = async () => { const r = roomRef.current; if (!r) return; setPttHablando(false); await r.localParticipant.setMicrophoneEnabled(false); };

  // Configuración del canal (solo organizador): modalidad + líder + barredor
  const abrirConfig = async () => {
    setVerConfig(v => !v);
    if (anotados.length > 0) return;
    if (miembros) {
      // Grupo: los miembros ya vienen cargados del parent (user_id + perfiles.username)
      setAnotados(miembros.map(m => ({ user_id: m.user_id, username: m.perfiles?.username })));
    } else {
      // Evento: traer los anotados de la salida
      const { data: raw } = await supabase.rpc("get_anotados_datos", { p_evento_id: id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(d)) setAnotados(d);
    }
  };
  const guardarCfg = (nuevo) => {
    setCfg(nuevo);
    supabase.from(tabla).update({ voz_config: nuevo }).eq("id", id).then(() => {}, () => {});
  };

  useEffect(() => () => { salidaManualRef.current = true; limpiarReintento(); intercomFondo.apagar(claveFondoRef.current); roomRef.current?.disconnect().catch(() => {}); }, []);

  const selStyle = { width: "100%", padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, fontWeight: 600, boxSizing: "border-box" };
  return (
    <div style={{ marginTop: 8, padding: 12, borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, color: C.text }}>
      <div ref={audioBox} style={{ display: "none" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
          🎙️ Intercomunicador {estado === "conectado" && participantes.length > 0 ? `· ${participantes.length} en línea` : modalidad === "lideres" ? "· solo líderes" : ""}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {esOrg && !sinConfig && (
            <button onClick={abrirConfig} style={{ fontSize: 11, fontWeight: 700, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>⚙️ Configurar</button>
          )}
          {estado === "conectado" && (
            <button onClick={salir} style={{ fontSize: 11, fontWeight: 700, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>Salir</button>
          )}
        </div>
      </div>

      {/* Panel de configuración (solo organizador) */}
      {esOrg && !sinConfig && verConfig && (
        <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>Modalidad del intercomunicador</div>
          <div style={{ display: "flex", gap: 8, marginBottom: modalidad === "lideres" ? 12 : 0 }}>
            {[["todos", "🎙️ Todos abiertos"], ["lideres", "📢 Solo líderes"]].map(([v, l]) => (
              <button key={v} onClick={() => guardarCfg({ ...cfg, modalidad: v })}
                style={{ flex: 1, padding: "9px 6px", borderRadius: 8, border: modalidad === v ? "none" : "1px solid rgba(255,255,255,.2)", background: modalidad === v ? "#f9f5f2" : "transparent", color: modalidad === v ? "#111" : "#f9f5f2", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{l}</button>
            ))}
          </div>
          {modalidad === "lideres" && (
            <>
              <div style={{ fontSize: 11.5, color: C.muted, margin: "0 0 6px" }}>Líder (mic abierto, adelante)</div>
              <select value={cfg.lider_id || ""} onChange={e => guardarCfg({ ...cfg, lider_id: e.target.value || null })} style={selStyle}>
                <option value="">— Elegí el líder —</option>
                {anotados.map(a => <option key={a.user_id} value={a.user_id}>@{a.username || "rider"}</option>)}
              </select>
              <div style={{ fontSize: 11.5, color: C.muted, margin: "10px 0 6px" }}>Barredor / último (opcional, también con mic abierto)</div>
              <select value={cfg.barredor_id || ""} onChange={e => guardarCfg({ ...cfg, barredor_id: e.target.value || null })} style={selStyle}>
                <option value="">— Ninguno —</option>
                {anotados.map(a => <option key={a.user_id} value={a.user_id}>@{a.username || "rider"}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      {estado !== "conectado" ? (
        <>
          {estado === "idle" && (
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, margin: "6px 0 10px" }}>
              {modalidad === "lideres"
                ? "Modo solo líderes: habla el líder (y el barredor, si hay). Si necesitás avisar algo, frená y mantené apretado el botón de hablar."
                : "Hablá con el grupo en vivo, como una llamada. Con el intercomunicador conectado al celu, todos quedan comunicados en la ruta."}
            </div>
          )}
          {estado === "reconectando" && (
            <div style={{ fontSize: 12.5, color: C.gold, fontWeight: 700, lineHeight: 1.5, margin: "6px 0 10px", background: `${C.gold}14`, border: `1px solid ${C.gold}33`, borderRadius: 10, padding: "9px 11px" }}>
              📶 Sin señal · reconectando solo. No hace falta que toques nada: cuando vuelva la señal entrás de nuevo al canal.
            </div>
          )}
          <button onClick={() => unirse(false)} disabled={estado === "conectando"}
            style={{ width: "100%", padding: "13px", borderRadius: 12, background: estado === "reconectando" ? C.surface : C.text, color: estado === "reconectando" ? C.text : "#fff", border: estado === "reconectando" ? `1px solid ${C.border}` : "none", fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.01em", cursor: estado === "conectando" ? "default" : "pointer", opacity: estado === "conectando" ? 0.6 : 1 }}>
            {estado === "conectando" ? "Conectando…" : estado === "reconectando" ? "Reintentar ahora" : "🎙️ Activar intercomunicador"}
          </button>
          {estado === "reconectando" && (
            <button onClick={salir}
              style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 12, background: "transparent", color: C.muted, border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              Salir del canal
            </button>
          )}
        </>
      ) : (
        <>
          {avisoReconexion && (
            <div style={{ fontSize: 12.5, color: C.green, fontWeight: 700, lineHeight: 1.45, marginBottom: 10, background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 10, padding: "8px 11px" }}>
              📶 {avisoReconexion}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
            {/* Como mucho 20 filas: con cientos de participantes la solapa se re-renderizaba
                entera en cada ActiveSpeakersChanged */}
            {participantes.slice(0, 20).map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", background: p.speaking ? "#2F7D4F" : C.border, boxShadow: p.speaking ? "0 0 0 3px rgba(47,125,79,.18)" : "none", flexShrink: 0, transition: "all .15s" }} />
                <span style={{ flex: 1, color: C.text }}>{p.name}</span>
                {p.rol && <span style={{ fontSize: 9, fontWeight: 800, color: "#8A6D3B", background: "#F5EFE4", borderRadius: 5, padding: "1px 6px", textTransform: "uppercase", letterSpacing: 0.4 }}>{p.rol}</span>}
                {p.muted && <span style={{ fontSize: 11, color: "#888" }}>🔇</span>}
              </div>
            ))}
          </div>
          {esVocero ? (
            <button onClick={toggleMute}
              style={{ width: "100%", padding: "11px", borderRadius: 10, background: muteado ? C.surface : C.text, color: muteado ? C.text : "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
              {muteado ? "🔇 Micrófono silenciado — tocá para hablar" : "🎙️ Micrófono activo — tocá para silenciar"}
            </button>
          ) : (
            <>
              <button onPointerDown={pttDown} onPointerUp={pttUp} onPointerLeave={pttUp} onPointerCancel={pttUp}
                style={{ width: "100%", padding: "16px", borderRadius: 10, background: pttHablando ? "#3DDC84" : "#f9f5f2", color: C.text, border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}>
                {pttHablando ? "🎙️ HABLANDO — soltá para terminar" : "📢 MANTENÉ APRETADO PARA HABLAR"}
              </button>
              <div style={{ fontSize: 11, color: "#999", textAlign: "center", marginTop: 6 }}>Estás escuchando. Frená para hablar.</div>
            </>
          )}
        </>
      )}

      {intercomFondo.disponible() && (
        <button onClick={() => cambiarSeguirApagada(!seguirApagada)} role="switch" aria-checked={seguirApagada}
          style={{ width: "100%", marginTop: 10, padding: "10px 2px 2px", background: "none", border: "none", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left" }}>
          <span style={{ flex: 1 }}>
            <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>Seguir con la pantalla apagada</span>
            <span style={{ display: "block", fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Hablás y escuchás con el teléfono bloqueado, como en una llamada.</span>
          </span>
          <span style={{ width: 40, height: 24, borderRadius: 12, background: seguirApagada ? C.text : C.border, position: "relative", flexShrink: 0, transition: "background .15s" }}>
            <span style={{ position: "absolute", top: 3, left: seguirApagada ? 19 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
          </span>
        </button>
      )}
    </div>
  );
};

// Sección plegable (acordeón) para declutter de pantallas largas. Arranca cerrada.
// Título en sentence case con ícono de línea (no emoji) y contador opcional a la derecha:
// más cerca del look de iOS que de una app de 2015.
const SeccionPlegable = ({ titulo, children, abiertoInicial = false, Icono, contador }) => {
  const [abierto, setAbierto] = useState(abiertoInicial);
  return (
    <div style={{ marginBottom: 10, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", background: C.card }}>
      <button onClick={() => setAbierto(a => !a)}
        style={{ width: "100%", padding: "14px", background: "none", border: "none", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
        {Icono && <Icono size={17} strokeWidth={1.8} color={C.muted} style={{ flexShrink: 0 }} />}
        <span style={{ flex: 1, textAlign: "left", fontSize: 14.5, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>{titulo}</span>
        {contador != null && contador !== "" && (
          <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{contador}</span>
        )}
        <ChevronDown size={17} strokeWidth={2} color={C.faint} style={{ transform: abierto ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }} />
      </button>
      {abierto && <div style={{ padding: "0 14px 14px" }}>{children}</div>}
    </div>
  );
};

// ─── MODAL: DETALLE EVENTO ────────────────────────────────────────────────────
// 📸 Galería de la salida: fotos y videos que suben los participantes, también DESPUÉS
// de que la rodada terminó. Cada item es un post con evento_id; opcionalmente se comparte
// al feed general (toggle, default OFF). Subir: inscriptos + admin. Ver: todos.
const GaleriaEvento = ({ evento, isIn, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState("");
  const [compartirFeed, setCompartirFeed] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [miId, setMiId] = useState(null);
  const fileRef = useRef(null);

  const cargar = async () => {
    const { data } = await supabase.from("posts")
      .select("*").eq("evento_id", evento.id)
      .order("created_at", { ascending: false });
    setItems(data || []); setCargando(false);
  };
  useEffect(() => {
    cargar();
    supabase.auth.getUser().then(({ data }) => setMiId(data?.user?.id || null), () => {});
  }, [evento.id]);

  // Tiempo real (Originarios, 10-oct-2026: 4.000 motos y la foto del otro no aparecía
  // hasta cerrar y abrir la app). Canal filtrado por ESTE evento, nunca la tabla entera;
  // se refresca al volver la app al frente, y si Realtime no responde, poll de 60 s.
  useEffect(() => {
    if (!evento?.id) return;
    let rtOk = false;
    let ultimo = Date.now();
    const ch = supabase.channel("galeria_" + evento.id + "_" + Math.random().toString(36).slice(2, 8))
      .on("system", {}, (p) => { if (p && p.extension === "postgres_changes") rtOk = p.status === "ok"; })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts", filter: `evento_id=eq.${evento.id}` }, (payload) => {
        const p = payload && payload.new;
        if (!p || !p.id) return;
        setItems(its => its.some(x => x.id === p.id) ? its : [p, ...its]);
      })
      .subscribe((status) => { if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") rtOk = false; });
    const alVolver = () => {
      if (document.visibilityState !== "visible" || Date.now() - ultimo < 10000) return;
      ultimo = Date.now();
      cargar();
    };
    document.addEventListener("visibilitychange", alVolver);
    const iv = setInterval(() => {
      if (document.visibilityState !== "visible" || rtOk) return;
      ultimo = Date.now();
      cargar();
    }, 60000);
    return () => { supabase.removeChannel(ch); document.removeEventListener("visibilitychange", alVolver); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  // Desde el día del evento, CUALQUIER usuario logueado puede subir: en una rodada
  // grande la mayoría de los que sacaron fotos nunca se anotó en la app (pedido de
  // Carlos 11-ago-2026). Antes del evento, solo inscriptos y admin.
  const eventoYaFue = (() => {
    if (!evento.fecha) return false;
    const d = new Date(String(evento.fecha).slice(0, 10) + "T00:00:00");
    return !isNaN(d.getTime()) && new Date() >= d;
  })();
  const puedeSubir = isIn || isAdmin || (!!miId && eventoYaFue);

  const onArchivos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    { const ok = []; for (const f of files) { if (await videoDentroDelLimite(f)) ok.push(f); } files.length = 0; ok.forEach(f => files.push(f)); }
    if (!files.length) return;
    setSubiendo(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSubiendo(false); alert("Sesión expirada. Volvé a iniciar sesión."); return; }
    const subidas = [];
    for (let i = 0; i < files.length; i++) {
      setProgreso(`${i + 1}/${files.length}`);
      const file = files[i];
      const esVideo = (file.type || "").startsWith("video");
      try {
        const fd = new FormData();
        fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads"); fd.append("cloud_name", "dhwhk48wb");
        const endpoint = esVideo
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const res = await fetch(endpoint, { method: "POST", body: fd });
        const data = await res.json();
        if (data.secure_url) subidas.push({ url: data.secure_url, type: esVideo ? "video" : "image" });
      } catch (err) { console.error("Error subiendo a galería:", err); }
    }
    if (subidas.length) {
      // UN solo post con TODAS las medias → en el feed sale como carrusel, no N posts sueltos.
      await supabase.from("posts").insert({
        user_id: user.id,
        username: await usernameReal(user),
        texto: "",
        media_url: subidas[0].url, media_type: subidas[0].type,
        media_urls: JSON.stringify(subidas),
        likes: 0, evento_id: evento.id,
        compartido_feed_general: compartirFeed,
      }).then(() => {}, () => {});
    }
    setSubiendo(false); setProgreso("");
    if (subidas.length) cargar();
  };

  // Compartir / quitar del feed general una publicación YA subida (autor o admin).
  const toggleFeed = (post) => {
    const nuevo = !post.compartido_feed_general;
    supabase.from("posts").update({ compartido_feed_general: nuevo }).eq("id", post.id).then(() => {}, () => {});
    setItems(its => its.map(p => p.id === post.id ? { ...p, compartido_feed_general: nuevo } : p));
    setViewer(v => (v && v.post.id === post.id) ? { ...v, post: { ...v.post, compartido_feed_general: nuevo } } : v);
  };

  const borrar = async (post) => {
    if (!window.confirm("¿Borrar esta foto/video de la galería?")) return;
    supabase.from("posts").delete().eq("id", post.id).then(() => {}, () => {});
    setViewer(null);
    setItems(its => its.filter(p => p.id !== post.id));
  };

  const mediaDe = (post) => {
    try { const m = post.media_urls ? JSON.parse(post.media_urls) : null; if (m && m.length) return m; } catch (e) {}
    return post.media_url ? [{ url: post.media_url, type: post.media_type }] : [];
  };
  const todos = items.flatMap(p => mediaDe(p).map((m, i) => ({ ...m, post: p, key: `${p.id}_${i}` })));

  return (
    <div style={{ marginTop: 8 }}>
      <SeccionPlegable titulo={evento?.tipo === "evento" ? "Galería del evento" : "Galería de la salida"} Icono={Camera} contador={todos.length || null}>
        {puedeSubir && (
          <div style={{ marginBottom: 12 }}>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple style={{ display: "none" }} onChange={onArchivos} />
            <button disabled={subiendo} onClick={() => fileRef.current && fileRef.current.click()}
              style={{ width: "100%", padding: "12px", borderRadius: 10, background: subiendo ? C.surface : "#111111", color: subiendo ? C.muted : "#fff", border: "none", fontSize: 13.5, fontWeight: 700, cursor: subiendo ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {subiendo ? `Subiendo ${progreso}…` : "📸 Agregar fotos / videos"}
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12, color: C.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={compartirFeed} onChange={e => setCompartirFeed(e.target.checked)} style={{ width: 15, height: 15 }} />
              Compartir estas al feed de la comunidad al subirlas
            </label>
          </div>
        )}
        {todos.length > 0 && (
          <button onClick={() => {
            const url = `${SITE_URL}/evento/${evento.id}`;
            const titulo = evento.titulo || evento.title || "la salida";
            compartirNativo({ texto: `📸 Fotos de ${titulo}\nMirá la galería y subí las tuyas 👇`, url, titulo });
          }}
            style={{ width: "100%", marginBottom: 12, padding: "11px", borderRadius: 10, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            📲 Compartir la galería al WhatsApp
          </button>
        )}
        {cargando ? (
          <div style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: 10 }}>Cargando…</div>
        ) : todos.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: 14, lineHeight: 1.5 }}>
            Todavía no hay fotos ni videos.{puedeSubir ? " ¡Subí las tuyas!" : (!miId && eventoYaFue) ? " ¿Estuviste? Iniciá sesión y subí las tuyas." : ""}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
            {todos.map(m => {
              const esVideo = m.type === "video" || /\.(mp4|mov|webm)$/i.test(m.url || "");
              const thumb = esVideo ? cldPoster(m.url, 400) : cldThumb(m.url);
              return (
                <button key={m.key} onClick={() => setViewer(m)}
                  style={{ position: "relative", aspectRatio: "1", border: "none", padding: 0, borderRadius: 8, overflow: "hidden", cursor: "pointer", background: C.surface }}>
                  <img src={thumb} alt="" loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  {esVideo && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.18)" }}><Play size={22} color="#fff" fill="#fff" /></span>}
                  {m.post.compartido_feed_general && <span style={{ position: "absolute", top: 4, left: 4, background: "#2F7D4F", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 6, lineHeight: 1 }}>feed</span>}
                </button>
              );
            })}
          </div>
        )}
      </SeccionPlegable>

      {viewer && (
        <div onClick={() => setViewer(null)} style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,.93)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <button onClick={() => setViewer(null)} style={{ position: "absolute", top: "max(16px, calc(var(--sat) + 8px))", right: 16, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={22} /></button>
          {(viewer.type === "video" || /\.(mp4|mov|webm)$/i.test(viewer.url || "")) ? (
            <video src={cldOpt(viewer.url)} poster={cldPoster(viewer.url)} controls autoPlay playsInline onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "80%", borderRadius: 10 }} />
          ) : (
            <ImgZoom src={cldOpt(viewer.url, 1440)} alt="" onClick={e => e.stopPropagation()} onError={e => { if (e.target.src !== viewer.url) e.target.src = viewer.url; }} style={{ maxWidth: "100%", maxHeight: "80%", objectFit: "contain", borderRadius: 10 }} />
          )}
          <div onClick={e => e.stopPropagation()} style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, color: "#fff" }}>
            <span style={{ fontSize: 13, opacity: .85 }}>@{viewer.post.username}</span>
            {(isAdmin || viewer.post.user_id === miId) && (
              <button onClick={() => toggleFeed(viewer.post)} style={{ display: "flex", alignItems: "center", gap: 6, background: viewer.post.compartido_feed_general ? "#2F7D4F" : "rgba(255,255,255,.12)", border: "none", color: "#fff", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
                {viewer.post.compartido_feed_general ? "✓ En el feed" : "Compartir al feed"}
              </button>
            )}
            {(isAdmin || viewer.post.user_id === miId) && (
              <button onClick={() => borrar(viewer.post)} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.12)", border: "none", color: "#fff", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12.5 }}>
                <Trash2 size={14} /> Borrar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Confluencias: las rodadas anexas llegando al evento, todas en un mapa ────
// El organizador del evento (y cualquier inscripto del evento o de una rodada)
// ve a los distintos grupos acercándose en vivo, cada uno con su color y avatares.
const COLORES_CONFLUENCIA = ["#111111", "#B3372C", "#2F7D4F", "#8A6D3B", "#1D6FB8", "#8E44AD", "#C2185B"];
const MapaConfluencias = ({ evento, onClose }) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markers = useRef({});
  const trails = useRef({});
  const [rodadas, setRodadas] = useState([]);
  const [nPos, setNPos] = useState(0);
  const [sinAcceso, setSinAcceso] = useState(false);
  useEffect(() => {
    let activo = true;
    const colorDe = {};
    const refetch = async () => {
      const { data: raw } = await supabase.rpc("get_confluencias", { p_evento_id: evento.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!activo) return;
      if (!d || d.error) { setSinAcceso(true); return; }
      setRodadas(d.rodadas || []);
      if (!mapInst.current || !window.google) return;
      (d.rodadas || []).forEach((r, i) => { colorDe[r.id] = COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length]; });
      // El trazado que va grabando el organizador de cada rodada, con su color
      (d.recorridos || []).forEach(rc => {
        const path = (Array.isArray(rc.recorrido) ? rc.recorrido : []).filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
        if (path.length < 2) return;
        if (trails.current[rc.rodada_id]) trails.current[rc.rodada_id].setPath(path);
        else trails.current[rc.rodada_id] = new window.google.maps.Polyline({ path, strokeColor: colorDe[rc.rodada_id] || "#111111", strokeOpacity: 0.85, strokeWeight: 4, map: mapInst.current });
      });
      // Cada rider con su foto de perfil
      const posiciones = d.posiciones || [];
      setNPos(posiciones.length);
      const vistos = {};
      posiciones.forEach(u => {
        if (!u.lat || !u.lng) return;
        vistos[u.user_id] = true;
        const pos = { lat: Number(u.lat), lng: Number(u.lng) };
        if (markers.current[u.user_id]) {
          markers.current[u.user_id].setPosition(pos);
        } else {
          const mk = new window.google.maps.Marker({
            position: pos, map: mapInst.current, title: "@" + (u.username || "rider"),
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: colorDe[u.rodada_id] || "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
          });
          markers.current[u.user_id] = mk;
          iconoAvatarMapa(u.avatar_url, icon => { if (icon && markers.current[u.user_id]) mk.setIcon(icon); }, 32);
        }
      });
      Object.keys(markers.current).forEach(id => { if (!vistos[id]) { markers.current[id].setMap(null); delete markers.current[id]; } });
    };
    (async () => {
      await loadGoogleMaps();
      if (!activo || !mapRef.current || !window.google) return;
      mapInst.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: Number(evento.lat) || -34.6037, lng: Number(evento.lng) || -58.3816 }, zoom: 8,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      if (evento.lat && evento.lng) {
        new window.google.maps.Marker({
          position: { lat: Number(evento.lat), lng: Number(evento.lng) }, map: mapInst.current,
          label: { text: "🏁", fontSize: "20px" },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0 },
          title: evento.title || evento.titulo,
        });
      }
      refetch();
    })();
    const iv = setInterval(refetch, 10000);
    return () => { activo = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 650, background: "#0d0d12", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", paddingTop: "var(--sat)", paddingBottom: "var(--sab)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.1)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} color="#fff" strokeWidth={2.5} />
        </button>
        <div style={{ flex: 1, color: "#fff" }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>🏁 Rodadas en camino</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>{nPos > 0 ? `${nPos} riders en vivo` : "Los grupos aparecen cuando abren su mapa en vivo"}</div>
        </div>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#27AE60", animation: "pulse 1.5s ease-in-out infinite" }} />
      </div>
      {sinAcceso ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,.6)", fontSize: 13, padding: 30, textAlign: "center" }}>
          Este mapa es para inscriptos y organizadores del evento o de sus rodadas.
        </div>
      ) : (
        <div ref={mapRef} style={{ flex: 1, background: "#1a1a22" }} />
      )}
      {rodadas.length > 0 && !sinAcceso && (
        <div style={{ padding: "10px 16px", background: "#0d0d12", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
          {rodadas.map((r, i) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,.75)" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length], flexShrink: 0 }} />
              <span style={{ maxWidth: 160, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{r.titulo}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Telaraña de caravanas: el mapa de lo PLANIFICADO ────────────────────────
// El de arriba (MapaConfluencias) muestra dónde están las columnas EN VIVO el
// día del evento. Este muestra las caravanas anunciadas antes, cada una como un
// arco desde su ciudad hasta la meta: es la imagen para difundir que pidió
// Carlos — el organizador ve de dónde le viene la gente y cuántos son.
// Arco entre dos puntos: bezier cuadrática con el control desplazado
// perpendicular, así las líneas se abren en abanico en vez de superponerse.
const arcoEntre = (a, b, curva = 0.22) => {
  const pts = [];
  const mx = (a.lat + b.lat) / 2, my = (a.lng + b.lng) / 2;
  const dx = b.lat - a.lat, dy = b.lng - a.lng;
  const cx = mx - dy * curva, cy = my + dx * curva; // punto de control
  for (let i = 0; i <= 24; i++) {
    const t = i / 24, u = 1 - t;
    pts.push({
      lat: u * u * a.lat + 2 * u * t * cx + t * t * b.lat,
      lng: u * u * a.lng + 2 * u * t * cy + t * t * b.lng,
    });
  }
  return pts;
};
const MapaTelarana = ({ evento, rodadas, conteos, onClose }) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  // Caravanas cargadas sin trazado (solo punto de salida): se les calcula el camino real
  // acá mismo con Directions y se guarda, así dejan de verse como un arco (Carlos vio las
  // dos de Corrientes a Originarios dibujadas como curvas, 23-sep).
  const [rutasCalc, setRutasCalc] = useState({});
  const meta = { lat: Number(evento.lat), lng: Number(evento.lng) };
  const conOrigen = (rodadas || []).filter(r => r.lat && r.lng);
  const totalRiders = conOrigen.reduce((s, r) => s + (Number(conteos?.por_rodada?.[r.id]) || 0), 0);
  useEffect(() => {
    let activo = true;
    (async () => {
      await loadGoogleMaps();
      if (!activo || !mapRef.current || !window.google) return;
      const g = window.google;
      mapInst.current = new g.maps.Map(mapRef.current, {
        center: meta, zoom: 7, disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
        styles: [{ elementType: "labels.icon", stylers: [{ visibility: "off" }] }],
      });
      const bounds = new g.maps.LatLngBounds();
      bounds.extend(meta);
      const faltantes = [];
      conOrigen.forEach((r, i) => {
        const color = COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length];
        const origen = { lat: Number(r.lat), lng: Number(r.lng) };
        bounds.extend(origen);
        // El camino REAL que planeó cada caravana. Solo si no trazó recorrido
        // cae al arco, para que igual se vea de dónde viene.
        let path = [];
        if (r.polyline) { try { path = decodificarPolyline(r.polyline); } catch (e) { path = []; } }
        const esReal = path.length > 1;
        if (!esReal) path = arcoEntre(origen, meta, 0.18 + (i % 3) * 0.06);
        path.forEach(p => bounds.extend(p));
        const linea = new g.maps.Polyline({
          path, map: mapInst.current, strokeColor: color,
          strokeOpacity: esReal ? 0.95 : 0.55, strokeWeight: esReal ? 4 : 3,
          icons: esReal
            ? [{ icon: { path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.4, fillColor: color, fillOpacity: 1, strokeWeight: 0 }, offset: "50%", repeat: "22%" }]
            : [{ icon: { path: "M 0,-1 0,1", strokeColor: color, strokeOpacity: 0.9, scale: 3 }, offset: "0", repeat: "12px" }],
        });
        if (!esReal) faltantes.push({ r, color, arco: linea, origen });
        const n = Number(conteos?.por_rodada?.[r.id]) || 0;
        new g.maps.Marker({
          position: origen, map: mapInst.current,
          icon: { path: g.maps.SymbolPath.CIRCLE, scale: n > 0 ? Math.min(16, 8 + n * 0.7) : 7, fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5, labelOrigin: new g.maps.Point(0, 0) },
          label: n > 0 ? { text: String(n), color: "#fff", fontSize: "10px", fontWeight: "800" } : null,
          title: `${r.desde || r.titulo}${n ? ` · ${n} riders` : ""}`,
        });
      });
      new g.maps.Marker({
        position: meta, map: mapInst.current, zIndex: 99,
        label: { text: "🏁", fontSize: "26px" },
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0 },
      });
      if (conOrigen.length > 0) mapInst.current.fitBounds(bounds, 70);
      // Camino real para las que no lo tienen: Directions desde su punto de salida hasta la
      // meta; se dibuja apenas llega y se guarda en la caravana para la próxima vez.
      for (const f of faltantes) {
        try {
          const qs = `origin=${encodeURIComponent(`${f.origen.lat},${f.origen.lng}`)}&destination=${encodeURIComponent(`${meta.lat},${meta.lng}`)}`;
          const res = await fetch(`${API_BASE}/api/directions?${qs}`);
          const data = await res.json();
          const ruta = data?.routes?.[0];
          const poly = ruta?.overview_polyline?.points || data?.polyline || null;
          if (!poly || !activo) continue;
          const real = decodificarPolyline(poly);
          if (real.length < 2) continue;
          f.arco.setMap(null);
          new g.maps.Polyline({
            path: real, map: mapInst.current, strokeColor: f.color, strokeOpacity: 0.95, strokeWeight: 4,
            icons: [{ icon: { path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.4, fillColor: f.color, fillOpacity: 1, strokeWeight: 0 }, offset: "50%", repeat: "22%" }],
          });
          real.forEach(p => bounds.extend(p));
          mapInst.current.fitBounds(bounds, 70);
          setRutasCalc(prev => ({ ...prev, [f.r.id]: poly }));
          const metros = (ruta?.legs || []).reduce((a, l) => a + (l.distance?.value || 0), 0);
          supabase.rpc("actualizar_ruta_caravana", { p_caravana_id: f.r.id, p_polyline: poly, p_km: metros > 0 ? Math.round(metros / 1000) : null }).then(() => {}, () => {});
        } catch (e) { /* queda el arco */ }
      }
    })();
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, rodadas.length]);
  // La misma telaraña como IMAGEN, para mandar por WhatsApp o subir a redes
  const imagenParaDifundir = () => {
    const paths = conOrigen.slice(0, 8).map((r, i) => {
      const color = COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length].replace("#", "0x");
      // Static Maps entiende la polyline codificada tal cual: el camino real
      // entra entero sin inflar la URL
      const pl = r.polyline || rutasCalc[r.id];
      if (pl) return `path=color:${color}ff|weight:4|enc:${encodeURIComponent(pl)}`;
      const pts = arcoEntre({ lat: Number(r.lat), lng: Number(r.lng) }, meta, 0.18 + (i % 3) * 0.06)
        .filter((_, k) => k % 2 === 0)
        .map(p => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`).join("|");
      return `path=color:${color}bb|weight:3|${pts}`;
    }).join("&");
    const marcas = conOrigen.slice(0, 8).map((r, i) => {
      const color = COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length].replace("#", "0x");
      return `markers=size:small|color:${color}|${Number(r.lat).toFixed(4)},${Number(r.lng).toFixed(4)}`;
    }).join("&");
    const url = `https://maps.googleapis.com/maps/api/staticmap?size=640x640&scale=2&maptype=roadmap`
      + `&markers=size:mid|color:0x111111|label:M|${meta.lat},${meta.lng}`
      + `&${marcas}&${paths}&key=${GMAPS_KEY}&language=es`;
    window.open(url, "_blank");
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 655, background: "#0d0d12", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", paddingTop: "var(--sat)", paddingBottom: "var(--sab)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.1)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} color="#fff" strokeWidth={2.5} />
        </button>
        <div style={{ flex: 1, color: "#fff" }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>🕸️ Las caravanas que vienen</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>
            {conOrigen.length} caravana{conOrigen.length !== 1 ? "s" : ""}{totalRiders > 0 ? ` · ${totalRiders} riders anotados` : ""}
          </div>
        </div>
      </div>
      <div ref={mapRef} style={{ flex: 1, background: "#1a1a22" }} />
      <div style={{ maxHeight: "34%", overflowY: "auto", background: "#0d0d12", borderTop: "1px solid rgba(255,255,255,.08)", padding: "8px 14px 4px" }}>
        {conOrigen.map((r, i) => {
          const n = Number(conteos?.por_rodada?.[r.id]) || 0;
          return (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
              <span style={{ width: 11, height: 11, borderRadius: "50%", background: COLORES_CONFLUENCIA[i % COLORES_CONFLUENCIA.length], flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {r.desde ? `Desde ${r.desde}` : r.titulo}
                </div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.5)" }}>
                  {r.hora ? `Sale ${String(r.hora).slice(0, 5)} hs` : "Sin horario"}
                  {r.km ? ` · ${Math.round(Number(r.km))} km` : ""}{n > 0 ? ` · ${n} anotados` : ""}
                  {!(r.polyline || rutasCalc[r.id]) ? " · sin recorrido cargado" : ""}
                </div>
              </div>
            </div>
          );
        })}
        <button onClick={imagenParaDifundir}
          style={{ width: "100%", margin: "10px 0", padding: "11px", borderRadius: 10, background: "#F9F5F2", color: C.text, border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
          🖼️ Bajar la imagen para difundir
        </button>
      </div>
    </div>
  );
};

// Lista de rodadas anexas en la página del evento + acceso al mapa de confluencias.
// Y el botón para que CUALQUIERA arme su caravana desde acá, con el destino ya puesto.
// `plegado`: en la ficha del evento el bloque arranca cerrado, como UNA línea
// ("12 grupos vienen en caravana ›"). Diez rodadas con el mismo título antes del
// botón de anotarse empujaban "Anotarme" dos pantallas para abajo (auditoría 09-oct).
const RodadasConfluentes = ({ evento, plegado = false, puedeVerVivo = false }) => {
  const [rodadas, setRodadas] = useState([]);
  const [verMapa, setVerMapa] = useState(false);
  const [verTelarana, setVerTelarana] = useState(false);
  const [abierto, setAbierto] = useState(false);
  // Cuántos vienen en camino: anotados de las rodadas hijas, SIN contar a los
  // que además ya están anotados al evento (para no duplicar). Sale del RPC
  // conteo_confluentes; si el SQL no está corrido, simplemente no se muestra.
  const [confluentes, setConfluentes] = useState(null);
  // Ciudad de salida de cada caravana: sale del punto S del mapa de la rodada (el nombre del
  // punto de encuentro suele ser solo "YPF"). Se geocodifica la coordenada del punto S.
  const [ciudadesSalida, setCiudadesSalida] = useState({});
  useEffect(() => {
    if (rodadas.length === 0) return;
    let vivo = true;
    (async () => {
      let geocoder = null;
      try { await loadGoogleMaps(); geocoder = new window.google.maps.Geocoder(); } catch (e) { /* queda el nombre del punto S */ }
      const res = {};
      for (const r of rodadas) {
        const pts = Array.isArray(r.puntos) ? r.puntos : [];
        const s0 = pts.find(x => x && x.tipo === "inicio") || pts[0];
        let ciudad = "";
        if (s0 && geocoder && s0.lat && s0.lng) {
          ciudad = await new Promise(ok => {
            try {
              geocoder.geocode({ location: { lat: Number(s0.lat), lng: Number(s0.lng) }, language: "es" }, (results, status) => {
                const comp = status === "OK" && results && results[0] ? results[0].address_components : [];
                const c = comp.find(x => x.types.includes("locality")) || comp.find(x => x.types.includes("administrative_area_level_2"));
                ok(c ? c.long_name : "");
              });
            } catch (e) { ok(""); }
          });
        }
        if (!ciudad && s0 && s0.nombre) ciudad = String(s0.nombre).split(",")[0].trim();
        res[r.id] = ciudad;
      }
      if (vivo) setCiudadesSalida(res);
    })();
    return () => { vivo = false; };
  }, [rodadas]);
  // "Sale de YPF, Junín": la ciudad se suma solo si el nombre del punto de encuentro todavía no la tiene (sin tildes ni mayúsculas)
  const saleDe = (r) => {
    const base = String(r.desde || "").trim();
    const ciudad = ciudadesSalida[r.id] || "";
    if (!base) return ciudad;
    if (!ciudad) return base;
    const n = (t) => String(t).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return n(base).includes(n(ciudad)) ? base : `${base}, ${ciudad}`;
  };
  useEffect(() => {
    if (!evento || !evento.id) return;
    supabase.from("eventos").select("id, titulo, fecha, hora, desde, lat, lng, polyline, km, puntos")
      .eq("evento_padre_id", evento.id)
      .then(({ data }) => setRodadas(data || []), () => {});
    supabase.rpc("conteo_confluentes", { p_evento_id: evento.id })
      .then(({ data }) => {
        const d = typeof data === "string" ? JSON.parse(data) : data;
        if (d && !d.error) setConfluentes(d);
      }, () => {});
  }, [evento && evento.id]);
  // Solo tiene sentido armar caravanas hacia algo con lugar fijo y ubicación: los
  // eventos, y también las SALIDAS PÚBLICAS masivas (el BikerFest está cargado
  // como salida y por eso nunca mostró el bloque — reporte de Carlos). Las
  // salidas privadas de un grupo no lo muestran: ahí la caravana ya es la salida.
  // Pero NO en una rodada que ya es la caravana hacia otro evento, ni en una
  // salida con recorrido propio: ahí ya estás en la rodada, no hay que armar
  // otra (reporte de Carlos, 18-sep: "Rodada a Giros Controlados" mostraba el
  // cartel de armar la caravana). El BikerFest sigue mostrándolo porque es una
  // salida de lugar fijo, sin trazado.
  const esCaravana = !!evento.evento_padre_id;
  const tieneRecorrido = !!evento.polyline || (Array.isArray(evento.puntos) && evento.puntos.length > 1) || Number(evento.km) > 0;
  const esConvocatoria = !esCaravana && (evento.tipo === "evento"
    || (evento.tipo === "salida" && (evento.visibilidad_tipo || "publico") === "publico" && !tieneRecorrido));
  const puedeArmar = esConvocatoria && evento.lat && evento.lng
    && String(evento.fecha_fin || evento.fecha || "").slice(0, 10) >= hoyLocalISO();
  const armarRodada = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { alert("Creá tu cuenta para armar una rodada hacia este evento."); return; }
    window.dispatchEvent(new CustomEvent("olbos-rodada-hacia", { detail: {
      id: evento.id, titulo: evento.titulo || evento.title,
      lugar: evento.lugar, lat: evento.lat, lng: evento.lng,
      fecha: String(evento.fecha || "").slice(0, 10),
    } }));
  };
  // El link que se reparte a los organizadores de grupos: cae directo en el
  // creador con este evento ya puesto como destino. Un toque y arman la suya.
  const invitarGrupos = () => {
    const titulo = evento.titulo || evento.title || "el evento";
    compartirNativo({
      texto: `🏍️ ¿Van en grupo a ${titulo}? Armá la rodada de tu grupo con este link — sale ya cargada con el evento como destino, tu gente se anota, y el día del evento todos los grupos se ven llegando en vivo en el mapa:`,
      url: `${SITE_URL}/?crear_rodada=${evento.id}`,
      titulo: `Armá tu rodada a ${titulo}`,
    });
  };
  if (rodadas.length === 0) {
    return puedeArmar ? (
      <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: C.accent, color: "#F9F5F2" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 5 }}>Vamos juntos</div>
        <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: "-0.01em", marginBottom: 4 }}>¿Vas desde tu ciudad? Armá la caravana</div>
        <div style={{ fontSize: 12, color: "#c9c3ba", lineHeight: 1.5, marginBottom: 12 }}>
          Creás una rodada con este evento como destino (ya te lo cargamos), invitás a los de tu zona y el día del evento todos ven los grupos llegando en vivo.
        </div>
        <button onClick={armarRodada}
          style={{ width: "100%", padding: "12px", borderRadius: 10, background: "#F9F5F2", color: C.text, border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
          🛣️ Crear una rodada hacia este evento
        </button>
        <button onClick={invitarGrupos}
          style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 10, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
          🔗 Pasale el link a los grupos para que armen la suya
        </button>
      </div>
    ) : null;
  }
  if (plegado && !abierto) {
    const nR = Number(confluentes?.riders) || 0;
    return (
      <button onClick={() => setAbierto(true)}
        style={{ width: "100%", marginTop: 12, padding: "12px 14px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>🛣️</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
            {rodadas.length} grupo{rodadas.length !== 1 ? "s" : ""} {rodadas.length !== 1 ? "vienen" : "viene"} en caravana
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>
            {nR > 0 ? `${nR} rider${nR !== 1 ? "s" : ""} en camino · ` : ""}Desde {rodadas.map(r => r.desde).filter(Boolean).slice(0, 3).join(", ")}{rodadas.filter(r => r.desde).length > 3 ? "…" : ""}
          </div>
        </div>
        <ChevronRight size={16} strokeWidth={2} color={C.faint} style={{ flexShrink: 0 }} />
      </button>
    );
  }
  return (
    <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: C.card, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>🛣️ Rodadas que llegan a este evento</div>
      {confluentes && Number(confluentes.riders) > 0 && (
        <div style={{ fontSize: 12.5, color: "#2F7D4F", fontWeight: 700, marginBottom: 6 }}>
          +{Number(confluentes.riders)} rider{Number(confluentes.riders) !== 1 ? "s" : ""} vienen en camino con las rodadas
        </div>
      )}
      {rodadas.map(r => (
        // Abre la rodada DENTRO de la app: el <a href> de antes navegaba a la
        // landing pública y te sacaba de Olbos estando adentro.
        <button key={r.id} onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: r.id } }))}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", width: "100%", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{r.titulo}</div>
            <div style={{ fontSize: 11, color: C.muted }}>
              {saleDe(r) ? `Sale de ${saleDe(r)}` : "Rodada anexa"}{r.hora ? ` · ${String(r.hora).slice(0, 5)} hs` : ""}
              {confluentes?.por_rodada?.[r.id] > 0 ? ` · ${confluentes.por_rodada[r.id]} anotados` : ""}
            </div>
          </div>
          <ChevronRight size={14} color={C.muted} style={{ flexShrink: 0 }} />
        </button>
      ))}
      {/* El mapa de lo planificado: sirve desde el día uno y es la imagen que se
          difunde. El de "en vivo" recién tiene qué mostrar el día del evento. */}
      {rodadas.some(r => r.lat && r.lng) && evento.lat && evento.lng && (
        <button onClick={() => setVerTelarana(true)}
          style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
          🕸️ Ver el mapa de las caravanas
        </button>
      )}
      {/* El mapa en vivo es para inscriptos y organizadores: al resto le abría una
          pantalla negra que decía justamente eso. Se les dice en una línea. */}
      {puedeVerVivo ? (
        <button onClick={() => setVerMapa(true)}
          style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          🏁 Ver los grupos en camino (en vivo)
        </button>
      ) : (
        <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>Anotate al evento y el día de la rodada ves a los grupos llegando en vivo en el mapa.</div>
      )}
      {puedeArmar && (
        <button onClick={armarRodada}
          style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          🛣️ Armar otra rodada desde mi ciudad
        </button>
      )}
      {puedeArmar && (
        <button onClick={invitarGrupos}
          style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 10, background: "transparent", color: C.muted, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
          🔗 Pasale el link a los grupos para que armen la suya
        </button>
      )}
      {verMapa && <MapaConfluencias evento={evento} onClose={() => setVerMapa(false)} />}
      {verTelarana && <MapaTelarana evento={evento} rodadas={rodadas} conteos={confluentes} onClose={() => setVerTelarana(false)} />}
    </div>
  );
};

// ─── ENTRADAS POR TANDAS ─────────────────────────────────────────────────────
// Tres modos, los elige el organizador: precio único de siempre, por cantidad
// ("las primeras 50 a tanto") o por fecha ("hasta el 1 de octubre a tanto").
// Vive en inscripcion_config.tandas, que ya es jsonb: no hizo falta tocar la base.
const tandasDe = (evento) => {
  const t = evento?.inscripcion_config?.tandas;
  if (!t || !t.modo || t.modo === "unico") return null;
  const lista = (Array.isArray(t.lista) ? t.lista : []).filter(x => x && Number(x.precio) >= 0);
  return lista.length > 0 ? { modo: t.modo, lista } : null;
};

// Qué tanda rige ahora. 'vendidas' es cuántos ya se anotaron (solo importa por cantidad).
const tandaActiva = (evento, vendidas) => {
  const t = tandasDe(evento);
  const base = Number(evento?.precio_entrada || evento?.precio || 0);
  if (!t) return { precio: base, nombre: null, restantes: null, hasta: null, agotada: false, hayTandas: false };

  if (t.modo === "cantidad") {
    if (vendidas == null) {
      // Todavía no sabemos cuántos se anotaron: se muestra el precio de la primera,
      // que es el piso, en vez de arriesgar un número equivocado.
      const p = t.lista[0];
      return { precio: Number(p.precio), nombre: p.nombre || null, restantes: null, hasta: null, agotada: false, hayTandas: true, desde: true };
    }
    let acum = 0;
    for (const p of t.lista) {
      const cupo = Number(p.cupo) || 0;
      if (cupo <= 0) continue;
      acum += cupo;
      if (vendidas < acum) {
        return { precio: Number(p.precio), nombre: p.nombre || null, restantes: acum - vendidas, hasta: null, agotada: false, hayTandas: true };
      }
    }
    // Se agotaron todas las tandas con cupo: rige la última (si la última no tiene
    // cupo, es la tanda "abierta" que el organizador dejó sin tope).
    const ultima = t.lista[t.lista.length - 1];
    const sinTope = !Number(ultima.cupo);
    return { precio: Number(ultima.precio), nombre: ultima.nombre || null, restantes: null, hasta: null, agotada: !sinTope, hayTandas: true };
  }

  // Por fecha: rige la primera cuyo "hasta" todavía no pasó
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  for (const p of t.lista) {
    if (!p.hasta) return { precio: Number(p.precio), nombre: p.nombre || null, restantes: null, hasta: null, agotada: false, hayTandas: true };
    const lim = new Date(String(p.hasta).slice(0, 10) + "T23:59:59");
    if (hoy <= lim) return { precio: Number(p.precio), nombre: p.nombre || null, restantes: null, hasta: p.hasta, agotada: false, hayTandas: true };
  }
  const ultima = t.lista[t.lista.length - 1];
  return { precio: Number(ultima.precio), nombre: ultima.nombre || null, restantes: null, hasta: null, agotada: false, hayTandas: true };
};

const textoTanda = (t) => {
  if (!t || !t.hayTandas) return null;
  if (t.agotada) return "Se agotaron las entradas de todas las tandas";
  const partes = [];
  if (t.nombre) partes.push(t.nombre);
  if (t.restantes != null) partes.push(`quedan ${t.restantes} a este precio`);
  if (t.hasta) partes.push(`hasta el ${new Date(String(t.hasta).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long" })}`);
  return partes.join(" · ") || null;
};

// Lo que ve el organizador. Se guarda en inscripcion_config.tandas.
const EditorTandas = ({ valor, onChange }) => {
  const t = valor || { modo: "unico", lista: [] };
  const set = (campos) => onChange({ ...t, ...campos });
  const setFila = (i, campos) => set({ lista: t.lista.map((x, j) => j === i ? { ...x, ...campos } : x) });
  const inp = { padding: "9px 10px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box", width: "100%" };

  return (
    <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: C.surface, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>🎫 Precio de la entrada</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {[["unico", "Un solo precio"], ["cantidad", "Por cantidad"], ["fecha", "Por fecha"]].map(([k, l]) => (
          <button key={k} type="button"
            onClick={() => set({ modo: k, lista: k === "unico" ? [] : (t.lista.length ? t.lista : [{ nombre: "Primera tanda", precio: "", cupo: "", hasta: "" }]) })}
            style={{ padding: "7px 12px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", border: t.modo === k ? "none" : `1px solid ${C.border}`, background: t.modo === k ? C.text : C.card, color: t.modo === k ? "#fff" : C.text }}>
            {l}
          </button>
        ))}
      </div>

      {t.modo === "unico" ? (
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Se cobra el precio que pusiste arriba, siempre el mismo.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 9 }}>
            {t.modo === "cantidad"
              ? "Cuando se agota el cupo de una tanda, el precio pasa solo a la siguiente. Si a la última no le ponés cupo, queda abierta sin tope."
              : "Cada tanda vale hasta la fecha que le pongas, y después toma el precio la siguiente. La última podés dejarla sin fecha para que valga hasta el día del evento."}
          </div>
          {t.lista.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 7 }}>
              <input value={p.nombre || ""} onChange={e => setFila(i, { nombre: e.target.value })} placeholder="Nombre" style={{ ...inp, flex: 1.3 }} />
              <input type="number" value={p.precio} onChange={e => setFila(i, { precio: e.target.value })} placeholder="Precio" style={{ ...inp, flex: 1 }} />
              {t.modo === "cantidad"
                ? <input type="number" value={p.cupo || ""} onChange={e => setFila(i, { cupo: e.target.value })} placeholder="Cantidad" style={{ ...inp, flex: 1 }} />
                : <input type="date" value={p.hasta || ""} onChange={e => setFila(i, { hasta: e.target.value })} style={{ ...inp, flex: 1.2 }} />}
              <button type="button" onClick={() => set({ lista: t.lista.filter((_, j) => j !== i) })}
                style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: C.card, color: C.red, cursor: "pointer", flexShrink: 0, fontSize: 15 }}>×</button>
            </div>
          ))}
          <button type="button" onClick={() => set({ lista: [...t.lista, { nombre: "", precio: "", cupo: "", hasta: "" }] })}
            style={{ padding: "8px 12px", borderRadius: 9, border: `1px dashed ${C.border}`, background: C.card, fontSize: 12.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
            + Agregar tanda
          </button>
        </>
      )}
    </div>
  );
};

// "Recorrido sorpresa" (Originarios, 08-oct): el organizador carga el camino pero no
// quiere mostrarlo hasta la largada. Se guarda en inscripcion_config.ocultar_ruta y
// se destapa solo cuando la rodada está en curso o terminó.
const rutaOcultaDelEvento = (ev) => {
  if (!ev || !ev.inscripcion_config || !ev.inscripcion_config.ocultar_ruta) return false;
  const est = ev.estado || "";
  return est !== "en_curso" && est !== "finalizada";
};

// La descripción del evento, con sus saltos de línea, plegada a unos renglones con
// "Leer más". Hasta el 09-oct la ficha NO la mostraba: para la Nocturna la descripción
// es el cronograma (apertura, sorteo, hora de la rodada) y nadie lo veía.
const DescripcionEvento = ({ texto }) => {
  const [abierta, setAbierta] = useState(false);
  const t = String(texto || "").trim();
  if (!t) return null;
  const larga = t.length > 260 || t.split("\n").length > 5;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ position: "relative", maxHeight: abierta || !larga ? "none" : 118, overflow: "hidden" }}>
        <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", letterSpacing: "-0.005em" }}>{t}</div>
        {larga && !abierta && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 48, background: `linear-gradient(to bottom, rgba(255,255,255,0), ${C.card})`, pointerEvents: "none" }} />
        )}
      </div>
      {larga && (
        <button onClick={() => setAbierta(a => !a)}
          style={{ marginTop: 6, padding: 0, background: "none", border: "none", fontSize: 13, fontWeight: 700, color: C.text, cursor: "pointer", textDecoration: "underline", textDecorationColor: C.border }}>
          {abierta ? "Ver menos" : "Leer más"}
        </button>
      )}
    </div>
  );
};

const DetalleEventoModal = ({ evento, joined, onClose, onAnotarme, onCancelar, userMotos, isAdmin, onEditar, onEliminar, guardado = false, onGuardar = null }) => {
  // Cuántos se anotaron: solo hace falta para las tandas por cantidad, así que la
  // consulta se hace únicamente si el evento las tiene configuradas.
  const [vendidas, setVendidas] = useState(null);
  useEffect(() => {
    const t = tandasDe(evento);
    if (!t || t.modo !== "cantidad" || !evento?.id) return;
    supabase.from("inscripciones").select("id", { count: "exact", head: true }).eq("evento_id", evento.id)
      .then(({ count }) => setVendidas(count ?? 0), () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento?.id]);
  const [showAnotados, setShowAnotados] = useState(false);
  const [showSorteo, setShowSorteo] = useState(false);
  // El mapa en vivo se monta en la RAÍZ de la app (sobrevive a la navegación);
  // desde acá solo se dispara el evento global que lo abre.
  const abrirMapaVivo = () => window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento } }));
  const [verEntrada, setVerEntrada] = useState(false);
  const [destacadoLocal, setDestacadoLocal] = useState(!!evento.destacado);
  const isIn = !!joined[evento.id];
  const puedeReembolso = true;
  // Quién puede editar: dueño, co-organizadores (los suma el dueño) y admin
  const [uidActual, setUidActual] = useState(null);
  useEffect(() => { supabase.auth.getUser().then(({ data: { user } }) => setUidActual(user?.id || null), () => {}); }, []);
  // Quién organiza: hasta ahora el evento no decía de quién era, y para anotarse
  // a una salida saber quién la lleva es la mitad de la decisión.
  const [organizador, setOrganizador] = useState(null);
  const [verOrganizador, setVerOrganizador] = useState(false);
  // La portada y las fotos del evento se abren a pantalla completa con pinch-zoom
  // (pedido de Carlos, 18-sep: un flyer con horarios y precios no se lee a 280 px)
  const [fotoZoom, setFotoZoom] = useState(null);
  useBackGuard(!!fotoZoom, () => setFotoZoom(null));
  // Abrir el detalle cuenta como visita (una por persona, no por vez)
  useEffect(() => {
    if (!evento?._fromDB || !evento?.id) return;
    supabase.rpc("registrar_vista_evento", { p_evento: evento.id }).then(() => {}, () => {});
    registrarUso("evento_abierto", { id: evento.id });
  }, [evento?.id, evento?._fromDB]);
  useEffect(() => {
    if (!evento.creator_id) return;
    supabase.from("perfiles").select("id, username, avatar_url, motos").eq("id", evento.creator_id).maybeSingle()
      .then(({ data }) => setOrganizador(data || null), () => {});
  }, [evento.creator_id]);
  const coOrgs = Array.isArray(evento.inscripcion_config?.co_organizadores) ? evento.inscripcion_config.co_organizadores : [];
  const esDueno = !!uidActual && evento.creator_id === uidActual;
  const esCoOrg = !!uidActual && coOrgs.some(c => c && c.user_id === uidActual);
  // El equipo (rol staff) mantiene los eventos que cargamos nosotros por
  // scouting — los del bot @olbos — sin necesidad de ser admin. Sobre los
  // eventos de un organizador real no toca nada.
  const [miRolStaff, setMiRolStaff] = useState(false);
  useEffect(() => {
    if (!uidActual) { setMiRolStaff(false); return; }
    supabase.from("perfiles").select("rol").eq("id", uidActual).maybeSingle()
      .then(({ data }) => setMiRolStaff(data?.rol === "staff"), () => {});
  }, [uidActual]);
  const esDeLaAgendaOlbos = evento.creator_id === "571720dc-a089-4e8a-a01b-7d1f92d99dbe";
  const esStaffDeAgenda = miRolStaff && esDeLaAgendaOlbos;
  const puedeEditar = isAdmin || esDueno || esCoOrg || esStaffDeAgenda;
  // Quién GESTIONA la rodada (largar, check-in, roles): dueño, co-org o admin — el mismo
  // criterio que gestionaEvento() y que los RPC de la base. El staff edita la ficha, no larga.
  const gestiona = isAdmin || esDueno || esCoOrg;

  // "¿Sos el organizador? Reclamalo": el mensaje llegaba sin saber quién era: había que
  // preguntarle el @ y el organizador no lo sabe (Carlos, 08-oct). Va con el usuario y un
  // link que abre el evento con la transferencia a ese perfil ya elegida: un toque.
  const reclamarEvento = async () => {
    const titulo = evento.title || evento.titulo;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      if (window.confirm("Para pasarte la administración del evento necesitás una cuenta en Olbos (gratis, un minuto).\n\n¿La creás ahora? Después volvé a esta ficha y tocá el botón de nuevo.")) {
        try { localStorage.setItem("olbos_ref_evento", evento.id); localStorage.setItem("olbos_ref_evento_ts", String(Date.now())); } catch (e) { /* */ }
        window.dispatchEvent(new CustomEvent("olbos-abrir-registro"));
      }
      return;
    }
    const { data: p } = await supabase.from("perfiles").select("username").eq("id", user.id).maybeSingle();
    const msj = `Hola! Soy organizador de "${titulo}" que aparece en la agenda de Olbos. Quiero administrarlo / actualizarlo / darlo de baja.\n\nMi usuario en Olbos: @${p?.username || user.email}\nPara transferírmelo: https://olbos.ai/?evento=${evento.id}&transferir=${user.id}`;
    window.open(`https://wa.me/5491130718112?text=${encodeURIComponent(msj)}`, "_blank", "noopener,noreferrer");
  };

  // ── Acción principal, SIEMPRE visible al pie de la hoja (footer del Modal) ──
  // Antes "Anotarme" era un botón gris a dos pantallas y media de scroll.
  const linkExterno = /^https?:\/\//i.test(String(evento.inscripcion_config?.link_externo || ""));
  const lleno = evento.riders >= evento.max && !evento.cupos_ilimitado;
  const nAnotados = Number(evento.riders || 0);
  const precioFooter = tandaActiva(evento, vendidas).precio;
  const footerAccion = isIn && evento._fromDB && esDiaDelEvento(evento) ? (
    // El día del evento (desde 4 h antes): check-in y mapa en vivo, sin desplegar nada
    <AccionesDiaEvento evento={evento} />
  ) : isIn ? (
    <button onClick={() => { registrarUso("mapa_vivo_tap", { id: evento.id, desde: "ficha" }); abrirMapaVivo(); }}
      style={{ width: "100%", padding: "14px 16px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em" }}>
      <CheckCircle size={17} strokeWidth={2.2} /> Estás anotado <span style={{ opacity: .65, fontWeight: 500 }}>· Ver el mapa de la rodada</span>
    </button>
  ) : linkExterno ? (
    <div>
      <a href={evento.inscripcion_config.link_externo} target="_blank" rel="noopener noreferrer"
        style={{ display: "block", width: "100%", padding: "14px", borderRadius: 14, background: C.accent, color: "#F9F5F2", fontSize: 15, fontWeight: 700, textAlign: "center", textDecoration: "none", boxSizing: "border-box" }}>
        📝 Inscribirme en la web del organizador
      </a>
      <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 5, lineHeight: 1.4 }}>La inscripción la toma el organizador en su formulario: es la que vale para la acreditación.</div>
    </div>
  ) : inscripcionCerrada(evento) ? (
    <div style={{ padding: "12px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, fontSize: 13, color: C.muted, textAlign: "center" }}>
      🏁 La rodada ya arrancó — la inscripción está cerrada.
    </div>
  ) : (
    <button onClick={() => onAnotarme(evento)}
      style={{ width: "100%", padding: "13px 16px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
      <div style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{lleno ? "Unirme a lista de espera" : "Anotarme"}</div>
      <div style={{ fontSize: 12, fontWeight: 500, opacity: .72, marginTop: 3, letterSpacing: "-0.005em" }}>
        {precioFooter > 0 ? `$${Number(precioFooter).toLocaleString("es-AR")}` : "Gratis"}
        {nAnotados > 0 ? ` · ${nAnotados.toLocaleString("es-AR")} anotado${nAnotados !== 1 ? "s" : ""}` : ""}
      </div>
    </button>
  );

  // Paradas reales del evento (desde puntos guardados o paradas)
  const rutaOculta = rutaOcultaDelEvento(evento);
  const puntosEventoTodos = evento.puntos || [];
  // Recorrido sorpresa: hasta la largada solo se muestra el punto de encuentro
  const puntosEvento = rutaOculta
    ? puntosEventoTodos.filter(p => p.tipo === "inicio" || p.tipo === "salida").slice(0, 1).concat(puntosEventoTodos.length && !puntosEventoTodos.some(p => p.tipo === "inicio" || p.tipo === "salida") ? [puntosEventoTodos[0]] : [])
    : puntosEventoTodos;
  const paradasRaw = puntosEvento.filter(p => !["inicio", "salida", "fin", "destino"].includes(p.tipo));
  const puntoInicioRaw = puntosEvento.find(p => p.tipo === "inicio" || p.tipo === "salida") || (evento.from ? { nombre: evento.from, tipo: "inicio" } : null);
  const puntoFinRaw = rutaOculta ? null : (puntosEvento.find(p => p.tipo === "fin" || p.tipo === "destino") || (evento.to ? { nombre: evento.to, tipo: "fin" } : null));
  const todosLosPuntosRaw = puntosEvento.length > 0 ? puntosEvento : [
    puntoInicioRaw, ...paradasRaw, puntoFinRaw
  ].filter(Boolean);

  // cache de coords geocodeadas para puntos que no tienen lat/lng en BD
  const [coordsExtra, setCoordsExtra] = useState({}); // { nombre: { lat, lng } }
  const [polylineExtra, setPolylineExtra] = useState(null); // polyline lazy para eventos sin polyline guardado
  const [clima, setClima] = useState(null); // pronóstico por punto: { available, points } | { available:false, reason }

  useEffect(() => {
    let cancelado = false;
    (async () => {
      // 1. Geocodificar puntos sin coords
      const aGeocodear = todosLosPuntosRaw.filter(p => p && p.nombre && (!p.lat || !p.lng));
      let coordsLocal = {};
      if (aGeocodear.length > 0) {
        await Promise.all(aGeocodear.map(async (p) => {
          try {
            const res = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(p.nombre + ", Argentina")}`);
            const data = await res.json();
            // Si el punto está escrito como texto descriptivo y no como un lugar
            // ("Islas de Otamendi — caminos rurales (200 km aprox.)"), Google no lo
            // encuentra y devuelve el CENTRO del país. Tomarlo dibujaba un recorrido
            // absurdo — el evento de Tierra Adentro trazaba de Pilar a La Pampa.
            // Mismo criterio que ya usa el buscador de destinos.
            const esPais = (data?.types || []).includes("country") || String(data?.formatted || "").trim() === "Argentina";
            if (data && data.lat && data.lng && !esPais) coordsLocal[p.nombre] = { lat: data.lat, lng: data.lng };
          } catch (e) { /* silenciar */ }
        }));
        if (cancelado) return;
        if (Object.keys(coordsLocal).length > 0) setCoordsExtra(prev => ({ ...prev, ...coordsLocal }));
      }

      // 2. Si ya hay polyline guardado o no es una salida, terminamos acá
      if (evento.polyline || evento.tipo === "evento") return;

      // 3. Armar lista con coords ya resueltas (originales + geocodeadas)
      const conCoords = todosLosPuntosRaw.map(p => {
        if (p.lat && p.lng) return p;
        const extra = coordsLocal[p.nombre];
        return extra ? { ...p, lat: extra.lat, lng: extra.lng } : p;
      }).filter(p => p.lat && p.lng);

      if (conCoords.length < 2) return;

      // 4. Fetchear ruta real
      try {
        const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
        const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
        const waypoints = conCoords.slice(1, -1).slice(0, 23).map(p => `${p.lat},${p.lng}`).join("|");
        const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`;
        const res = await fetch(`${API_BASE}/api/directions?${qs}`);
        const data = await res.json();
        if (cancelado) return;
        const enc = data?.routes?.[0]?.overview_polyline?.points || data?.polyline || null;
        if (enc) {
          setPolylineExtra(enc);
          // Si al crear la salida falló el cálculo, la línea negra no se guardaba y el detalle
          // mostraba solo los puntos (documento de Matías, N9). Se guarda acá para la próxima;
          // la RLS deja hacerlo solo al dueño, a los demás les falla sin ruido.
          if (evento._fromDB && evento.id && !rutaOculta && puntosEvento.length >= 2)
            supabase.from("eventos").update({ polyline: enc }).eq("id", evento.id).is("polyline", null).then(() => {}, () => {});
        }
      } catch (e) { /* silenciar — queda con líneas rectas */ }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, evento.polyline, JSON.stringify(puntosEvento.map(p => [p.lat, p.lng, p.nombre]))]);

  // helper: mergear coords del cache si faltan
  const conCoords = (p) => {
    if (!p) return null;
    if (p.lat && p.lng) return p;
    const extra = coordsExtra[p.nombre];
    return extra ? { ...p, lat: extra.lat, lng: extra.lng } : p;
  };

  const todosLosPuntos = todosLosPuntosRaw.map(conCoords);
  // Cuando hay bloque de recorrido, la ubicación y los puntos de suma se muestran
  // ahí y NO se repiten arriba: era la misma dirección tres veces en la pantalla.
  const hayBloqueRecorrido = evento.tipo !== "evento" && todosLosPuntos.length > 0;
  const puntoInicio = conCoords(puntoInicioRaw); // eslint-disable-line no-unused-vars
  const puntoFin = conCoords(puntoFinRaw); // eslint-disable-line no-unused-vars

  // ── Pronóstico del tiempo por punto (solo salidas con fecha y recorrido) ──
  const puntosClima = todosLosPuntos.filter(p => p && p.lat && p.lng);
  const climaKey = puntosClima.map(p => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`).join("|") + "@" + (evento.fecha || "");

  useEffect(() => {
    let cancelado = false;
    setClima(null);
    if (evento.tipo === "evento" || !evento.fecha || puntosClima.length === 0) return;
    (async () => {
      try {
        const lats = puntosClima.map(p => p.lat).join(",");
        const lngs = puntosClima.map(p => p.lng).join(",");
        const fechaSolo = String(evento.fecha).slice(0, 10);
        const res = await fetch(`${API_BASE}/api/weather?lats=${lats}&lngs=${lngs}&date=${fechaSolo}`);
        const data = await res.json();
        if (!cancelado) setClima(data);
      } catch (e) {
        if (!cancelado) setClima({ available: false, reason: "error" });
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [climaKey]);

  // Mapear el pronóstico a cada punto por coordenada
  const climaPorPunto = {};
  if (clima?.available && Array.isArray(clima.points)) {
    puntosClima.forEach((p, idx) => {
      climaPorPunto[`${p.lat.toFixed(3)},${p.lng.toFixed(3)}`] = clima.points[idx];
    });
  }
  const climaPunto = (p) => (p && p.lat && p.lng) ? climaPorPunto[`${p.lat.toFixed(3)},${p.lng.toFixed(3)}`] : null;
  const maxPrecip = clima?.available && Array.isArray(clima.points)
    ? Math.max(0, ...clima.points.map(w => w?.precip ?? 0))
    : 0;

  const TIPO_ICON = { inicio: "🟢", salida: "🟢", parada: "📍", almuerzo: "🍽️", noche: "🌙", destino: "🔴", fin: "🔴" };

  return (
    <>
    <Modal title={evento.title} onClose={onClose} footer={onGuardar && !isIn && evento._fromDB ? (
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <button onClick={onGuardar} aria-label={guardado ? "Quitar de guardados" : "Guardar para después"}
          style={{ width: 52, flexShrink: 0, borderRadius: 14, border: `1.5px solid ${guardado ? C.text : C.border}`, background: guardado ? C.text : C.card, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Bookmark size={20} strokeWidth={2} color={guardado ? "#F9F5F2" : C.text} fill={guardado ? "#F9F5F2" : "none"} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>{footerAccion}</div>
      </div>
    ) : footerAccion}>

      {/* Quién lo organiza — tocá para ver su perfil. EXCEPTO los eventos de la
          agenda (cargados por el bot @olbos): mostrarlos como "organiza @olbos"
          daba a entender que el evento era nuestro y a los organizadores reales
          les chocó (feedback del motoencuentro de Lobos vía Matías, 31-ago).
          Olbos DIFUNDE esos eventos, no los organiza. El cartel "Agenda motera · No
          lo organizamos nosotros" que iba acá arriba se sacó (Carlos, 09-oct): era
          lo primero que leía el rider y está escrito para el organizador. El
          "¿Sos el organizador? Reclamalo" sigue, como una línea al pie de la ficha. */}
      {organizador && !esDeLaAgendaOlbos && (
        <button onClick={() => setVerOrganizador(true)}
          style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "9px 12px", marginBottom: 14, cursor: "pointer", textAlign: "left" }}>
          <img loading="lazy" decoding="async" src={organizador.avatar_url || avatarInicial(organizador.username)} alt=""
            style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Organiza</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              @{organizador.username || "rider"}
            </div>
          </div>
          {coOrgs.length > 0 && (
            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>+{coOrgs.length} co-org.</span>
          )}
          <ChevronRight size={16} strokeWidth={2} color={C.faint} />
        </button>
      )}
      {verOrganizador && organizador && (
        <PerfilPublico userId={organizador.id} onClose={() => setVerOrganizador(false)} />
      )}

      {/* Para quien gestiona: qué hacer y dónde, arriba de todo. La largada estaba a cinco
          toques, detrás de un plegable, en otra pantalla y solo si se había anotado a su
          propia rodada (auditoría 09-oct). Momento oscuro: negro + crema. */}
      {gestiona && evento._fromDB && evento.id && !rodadaTerminada(evento) && (() => {
        const est = evento.estado || "inscripcion_abierta";
        const enCurso = est === "en_curso";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.text, color: "#F9F5F2", borderRadius: 12, padding: "10px 12px", marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: .7 }}>
                {isAdmin && !esDueno && !esCoOrg ? "Admin" : esCoOrg && !esDueno ? "Co-organizás" : "Organizás"} {evento.tipo === "evento" ? "este evento" : "esta rodada"}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.01em", marginTop: 2 }}>
                {enCurso ? "🏁 En curso" : "⏳ Por largar"}{nAnotados > 0 ? ` · ${nAnotados.toLocaleString("es-AR")} anotado${nAnotados !== 1 ? "s" : ""}` : ""}
              </div>
            </div>
            <button onClick={() => { registrarUso("mapa_vivo_tap", { id: evento.id, desde: "organiza" }); abrirMapaVivo(); }}
              style={{ flexShrink: 0, padding: "10px 12px", borderRadius: 10, background: "#F9F5F2", color: C.text, border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {enCurso ? "📡 Mapa en vivo" : "🏁 Largar desde el mapa"}
            </button>
          </div>
        );
      })()}

      {/* Evento privado: el creador maneja la lista de invitados; el invitado ve el aviso */}
      {evento.visibilidad_tipo === "invitados" && (
        esDueno
          ? <InvitadosEvento evento={evento} />
          : <div style={{ background: "#F5EFE5", border: "1px solid #8A6D3B33", borderRadius: 12, padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: "#8A6D3B", fontWeight: 600 }}>
              🔒 Evento privado — estás invitado
            </div>
      )}

      {/* Galería: flyer principal + medias adicionales */}
      {(evento.img || (evento.medias && evento.medias.length > 0)) && (
        <div style={{ marginBottom: 18, marginLeft: -18, marginRight: -18, marginTop: -8 }}>
          {evento.img && (
            <div style={{ width: "100%", maxHeight: 240, overflow: "hidden", background: "#000", marginBottom: (evento.medias && evento.medias.length > 0) ? 6 : 0, position: "relative" }}>
              {esVideoPortada(evento.img) ? (
                <video src={cldOpt(evento.img, 1080)} controls autoPlay muted loop playsInline poster={cldPoster(evento.img) || posterDeVideo(evento.img)}
                  style={{ width: "100%", maxHeight: 240, objectFit: "contain", display: "block" }} />
              ) : (
                <>
                  <img loading="lazy" decoding="async" src={cldOpt(evento.img, 1080)} alt={evento.title}
                    onError={ev => { if (ev.target.src !== evento.img) ev.target.src = evento.img; }}
                    onClick={() => setFotoZoom(evento.img)} title="Tocá para ampliar"
                    style={{ width: "100%", maxHeight: 240, objectFit: "contain", display: "block", cursor: "zoom-in", background: cldBlur(evento.img) ? `#000 url(${cldBlur(evento.img)}) center/cover` : "#000" }} />
                  <button onClick={() => setFotoZoom(evento.img)}
                    style={{ position: "absolute", right: 10, bottom: 10, padding: "5px 10px", borderRadius: 100, background: "rgba(0,0,0,.55)", color: "#fff", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", backdropFilter: "blur(4px)" }}>
                    Ver flyer entero
                  </button>
                </>
              )}
            </div>
          )}
          {evento.medias && evento.medias.length > 0 && (
            <div style={{
              display: "flex", gap: 4, overflowX: "auto", padding: "0 18px",
              scrollSnapType: "x mandatory",
            }}>
              {evento.medias.map((m, idx) => (
                <div key={idx} style={{
                  flexShrink: 0, width: 140, height: 140,
                  borderRadius: 10, overflow: "hidden", background: "#000",
                  scrollSnapAlign: "start",
                }}>
                  {m.type === "video" ? (
                    <video src={cldOpt(m.url, 720)} poster={cldPoster(m.url, 480)} preload="none" controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <img loading="lazy" decoding="async" src={m.url} alt="" onClick={() => setFotoZoom(m.url)} style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── FICHA: lo esencial de un vistazo, sin scroll ── */}
      {(() => {
        const fmtFechaLarga = (f) => {
          if (!f) return null;
          const d = new Date(String(f).slice(0, 10) + "T12:00:00");
          return isNaN(d.getTime()) ? null : d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
        };
        const ini = fmtFechaLarga(evento.fecha);
        const fin = evento.fecha_fin && evento.fecha_fin !== evento.fecha ? fmtFechaLarga(evento.fecha_fin) : null;
        const tanda = tandaActiva(evento, vendidas);
        const precio = tanda.precio;
        const subTanda = textoTanda(tanda);
        const Fila = ({ Icono, titulo, sub, extra }) => (
          <div style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "11px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 30, height: 30, borderRadius: 9, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {Icono ? <Icono size={15} strokeWidth={1.9} color={C.text} /> : null}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.01em", lineHeight: 1.3, textTransform: titulo === ini ? "capitalize" : "none" }}>{titulo}</div>
              {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{sub}</div>}
              {extra}
            </div>
          </div>
        );
        return (
          <div style={{ marginBottom: 16 }}>
            {/* cuándo */}
            <Fila
              Icono={Calendar}
              titulo={fin ? `Del ${ini} al ${fin}` : (ini || "Fecha a confirmar")}
              sub={[evento.hora ? `${String(evento.hora).slice(0, 5)} hs` : null,
                   evento.tipo !== "evento" && evento.ida_vuelta ? "Ida y vuelta" : null].filter(Boolean).join(" · ") || null}
            />
            {/* Dónde. Con bloque de recorrido, el lugar se muestra SOLO ahí abajo:
                acá quedan los datos de la salida (km, dificultad, superficie). */}
            <Fila
              Icono={MapPin}
              titulo={hayBloqueRecorrido
                ? ([evento.km > 0 ? `${evento.km} km` : null,
                    evento.diff || evento.dificultad || null,
                    evento.camino || null].filter(Boolean).join(" · ") || "Recorrido")
                : evento.tipo === "evento"
                ? (evento.lugar || evento.from || "Lugar a confirmar")
                : `${evento.from || "Punto de encuentro a confirmar"}${evento.to && !rutaOculta ? ` → ${evento.to}` : ""}`}
              sub={hayBloqueRecorrido
                ? (evento.to && !rutaOculta ? `Hasta ${evento.to}` : rutaOculta ? "Recorrido sorpresa" : null) /* con ruta oculta el destino no se spoilea */
                : [evento.km > 0 ? `${evento.km} km` : null,
                    evento.diff || evento.dificultad || null,
                    evento.camino || null].filter(Boolean).join(" · ") || null}
              extra={!hayBloqueRecorrido && ((evento.lat && evento.lng) || (puntoFin?.lat && puntoFin?.lng)) ? (
                <>
                  {evento.lat && evento.lng && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                      {puntoFin?.lat && <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, width: 52 }}>Salida</span>}
                      <BotonOlbosNav lat={evento.lat} lng={evento.lng} nombre={evento.titulo || "el punto de encuentro"} />
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${evento.lat},${evento.lng}`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 12, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                        <Map size={13} strokeWidth={2} /> Cómo llegar
                      </a>
                      <a href={`${WAZE_BASE}ll=${evento.lat},${evento.lng}&navigate=yes`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: "#0099BB", fontWeight: 700, fontSize: 12, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                        <Navigation size={13} strokeWidth={2} /> Waze
                      </a>
                    </div>
                  )}
                  {/* Salidas con destino con coordenadas: navegar también al punto de llegada */}
                  {puntoFin?.lat && puntoFin?.lng && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, width: 52 }}>Destino</span>
                      <BotonOlbosNav lat={puntoFin.lat} lng={puntoFin.lng} nombre={puntoFin.nombre || "el destino de la salida"} />
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${puntoFin.lat},${puntoFin.lng}`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 12, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                        <Map size={13} strokeWidth={2} /> Cómo llegar
                      </a>
                      <a href={`${WAZE_BASE}ll=${puntoFin.lat},${puntoFin.lng}&navigate=yes`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: "#0099BB", fontWeight: 700, fontSize: 12, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                        <Navigation size={13} strokeWidth={2} /> Waze
                      </a>
                    </div>
                  )}
                </>
              ) : null}
            />

            {/* Rodada que levanta gente: solo si NO hay bloque de recorrido, porque
                ahí cada punto ya muestra su horario y si se suma gente. */}
            {(() => {
              if (hayBloqueRecorrido) return null;
              const ps = Array.isArray(evento.inscripcion_config?.puntos_suma) ? evento.inscripcion_config.puntos_suma.filter(pt => pt && pt.nombre) : [];
              return ps.length > 0 ? (
                <Fila Icono={Users} titulo={`La rodada levanta gente en ${ps.length} puntos`}
                  extra={<div style={{ marginTop: 6 }}>{ps.map((pt, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: C.text, padding: "3px 0", display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span>{i + 1}. {pt.nombre}</span>{pt.hora && <span style={{ color: C.muted, flexShrink: 0 }}>{pt.hora} hs</span>}
                    </div>
                  ))}</div>} />
              ) : null;
            })()}

            {/* colaboradores del evento: dónde se come, hospedaje, etc. */}
            {(() => {
              const cbs = Array.isArray(evento.inscripcion_config?.colaboradores) ? evento.inscripcion_config.colaboradores.filter(cb => cb && cb.nombre) : [];
              return cbs.length > 0 ? (
                <Fila Icono={Star} titulo="Colaboran"
                  extra={<div style={{ marginTop: 6 }}>{cbs.map((cb, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: C.text, padding: "3px 0" }}>
                      {cb.nombre}{cb.categoria ? <span style={{ color: C.muted }}> · {cb.categoria}</span> : null}
                    </div>
                  ))}</div>} />
              ) : null;
            })()}

            {/* cuánto */}
            <Fila
              Icono={Tag}
              titulo={precio > 0 ? `${precio.toLocaleString("es-AR")}` : "Sin cargo"}
              sub={precio > 0 ? (subTanda || "Se paga directo al organizador") : "Entrada libre y gratuita"}
            />
            {/* inscripción */}
            <Fila
              Icono={Users}
              titulo={inscripcionCerrada(evento) ? "Inscripción cerrada" : "Inscripción abierta"}
              sub={evento.cupos_ilimitado ? "Sin límite de cupos"
                : evento.max ? `${Math.max((evento.max || 0) - (evento.riders || 0), 0)} lugares disponibles` : null}
            />
          </div>
        );
      })()}

        {/* De qué se trata: la descripción, con sus saltos de línea (cronograma, qué
            incluye, cómo es la inscripción). No se mostraba hasta el 09-oct. */}
        <DescripcionEvento texto={evento.descripcion} />

        {/* Rodadas anexas: las que declararon a este evento como destino. Plegadas
            en una línea: el detalle se abre tocando. */}
        {evento._fromDB && evento.id && <RodadasConfluentes evento={evento} plegado puedeVerVivo={isIn || gestiona} />}

        {/* Salida/evento PAGO: cómo abonar. El pago va DIRECTO al organizador (link de
            Mercado Pago o alias/CBU) — Olbos no interviene en la transacción. */}
        {evento._fromDB && (Number(evento.precio) > 0 || Number(evento.precio_entrada) > 0) && (evento.pago_link || evento.pago_info) && (
          <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1F8347", textTransform: "uppercase", letterSpacing: 0.5 }}>💳 Inscripción</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.text }}>${Number(evento.precio_entrada || evento.precio).toLocaleString()}</div>
            </div>
            {evento.pago_info && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: evento.pago_link ? 8 : 0 }}>
                <div style={{ flex: 1, fontSize: 12.5, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{evento.pago_info}</div>
                <button onClick={() => { try { navigator.clipboard.writeText(evento.pago_info); alert("Datos de pago copiados ✓"); } catch (e) { /* */ } }}
                  style={{ flexShrink: 0, padding: "7px 10px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                  Copiar
                </button>
              </div>
            )}
            {evento.pago_link && (
              <button onClick={() => window.open(evento.pago_link, "_blank")}
                style={{ width: "100%", padding: "12px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                💳 Pagar inscripción
              </button>
            )}
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
              El pago se realiza directamente al organizador. Ante cualquier duda, contactalo por el chat del evento.
            </div>
          </div>
        )}

      {/* acción principal */}
      {isIn ? (
        <div>
          <div style={{ padding: "10px 14px", background: `${C.green}14`, border: `1px solid ${C.green}22`, borderRadius: 10, marginBottom: 10, fontSize: 13, color: C.green, display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle size={14} strokeWidth={2} />
            Vas con {userMotos.find(m => m.id === joined[evento.id])?.modelo || "tu moto"}
          </div>
          {/* La entrada QR aparece si el evento es pago o si el organizador activó "Control de acceso" */}
          {evento._fromDB && evento.id && (Number(evento.precio) > 0 || Number(evento.precio_entrada) > 0 || evento.inscripcion_config?.entradas) && (
            <button onClick={() => setVerEntrada(true)}
              style={{ width: "100%", padding: "14px", borderRadius: 14, marginBottom: 10, background: C.text, color: "#fff", border: "none", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: "-0.01em" }}>
              <Tag size={16} strokeWidth={1.9} /> Mi entrada
            </button>
          )}
          {verEntrada && <MiEntradaModal evento={evento} onClose={() => setVerEntrada(false)} />}
          {evento.precio > 0 && (
            <div style={{ padding: "10px 14px", background: puedeReembolso ? `${C.green}14` : `${C.red}14`, borderRadius: 10, marginBottom: 10, fontSize: 12, color: puedeReembolso ? C.green : C.red, lineHeight: 1.6 }}>
              {puedeReembolso ? "Podés cancelar con reembolso completo hasta 48hs antes" : "Ya no hay reembolso disponible (menos de 48hs para la salida)"}
            </div>
          )}
          {(() => {
            const cfg = evento.inscripcion_config || {};
            const pideDatos = Object.values(cfg.campos || {}).some(Boolean) || String(cfg.deslinde || "").trim() || (Array.isArray(cfg.extras) && cfg.extras.length > 0);
            // Solo si el organizador pide datos o deslinde propio hay algo que completar.
            return pideDatos ? (
              <Btn full variant="outlineDark" onClick={() => { onAnotarme(evento, { forzarModal: true }); onClose(); }} style={{ marginBottom: 10 }}>
                📝 Completar / actualizar mis datos
              </Btn>
            ) : null;
          })()}
          <Btn full variant="outline" onClick={() => { onCancelar(evento.id); onClose(); }}>
            {evento.precio > 0 && !puedeReembolso ? "Cancelar (sin reembolso)" : "Cancelar inscripción"}
          </Btn>
        </div>
      ) : null /* el botón de anotarse vive en el pie fijo de la hoja (footer del Modal) */}

      {waHref(evento.telefono) && (
        <a href={waHref(evento.telefono, `Hola, te escribo por el evento "${evento.title}" en Olbos`)}
          target="_blank" rel="noreferrer"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10, padding: "12px 16px", borderRadius: 10, background: "#2F7D4F", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.711.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
          Contactar por WhatsApp
        </a>
      )}

      {/* PUNTO DE ENCUENTRO Y RECORRIDO — todo junto y a la vista.
          Antes el mismo lugar aparecía hasta tres veces: en los datos de arriba,
          dentro del desplegable "Recorrido" y otra vez en un bloque "Punto de
          encuentro". Ahora es un solo bloque, sin plegar, con el mapa arriba y
          debajo la secuencia: salida con su horario, las paradas del medio
          (marcando dónde se suma gente) y la llegada. */}
      {evento.tipo !== "evento" && todosLosPuntos.length > 0 && (
        <div style={{ marginBottom: 14, border: `1px solid ${C.border}`, borderRadius: 14, background: C.card, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
            <Map size={17} strokeWidth={1.8} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 14.5, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
              {todosLosPuntos.length > 1 ? "Punto de encuentro y recorrido" : "Punto de encuentro"}
            </span>
          </div>

          {/* mapa del recorrido completo */}
          {todosLosPuntos.some(p => p.lat && p.lng) && (
            <div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 12, border: `1px solid ${C.border}` }}>
              <MapaPreview puntos={todosLosPuntos} alto={180} polyline={rutaOculta ? null : (evento.polyline || polylineExtra)} />
            </div>
          )}
          {rutaOculta && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 12px", marginBottom: 12, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
              🙈 <strong>Recorrido sorpresa.</strong> El organizador revela el camino en la largada; por ahora solo se muestra el punto de encuentro.
            </div>
          )}

          {/* aviso de lluvia en el recorrido */}
          {clima?.available && maxPrecip >= 50 && (
            <div style={{ background: "#EAF3FF", border: "1px solid #B6D4F7", borderRadius: 10, padding: "9px 12px", marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>🌧️</span>
              <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                <strong>Atención al clima:</strong> hay hasta {Math.round(maxPrecip)}% de probabilidad de lluvia en el recorrido ese día. Revisá el equipo de lluvia.
              </span>
            </div>
          )}
          {/* pronóstico todavía no disponible (salida a más de 15 días) */}
          {clima?.available === false && clima.reason === "too_far" && (
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 12, display: "flex", gap: 6, alignItems: "center" }}>
              <span>🌦️</span> El pronóstico del tiempo aparece acá 15 días antes de la salida.
            </div>
          )}

          {todosLosPuntos.map((p, i) => {
            const esInicio = i === 0;
            const esFin = i === todosLosPuntos.length - 1;
            const esPaso = !esInicio && !esFin && p.tipo === "paso";
            const numParada = todosLosPuntos.slice(0, i + 1).filter((x, j) => j > 0 && j < todosLosPuntos.length - 1 && x.tipo !== "paso").length;
            return (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 28 }}>
                <div style={{ width: esPaso ? 16 : 28, height: esPaso ? 16 : 28, borderRadius: "50%",
                  background: esInicio ? "#27AE60" : esFin ? "#E74C3C" : esPaso ? C.card : "#111111",
                  border: esPaso ? "2.5px solid #111111" : "none",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                  <span style={{ color: "#fff", fontWeight: 800 }}>
                    {esInicio ? "S" : esFin ? "D" : esPaso ? "" : numParada}
                  </span>
                </div>
                {i < todosLosPuntos.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 12, background: C.border, margin: "2px 0" }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>{p.nombre}</div>
                  {/* la hora del punto: la propia, o la del evento en la salida */}
                  {(p.hora_suma || (esInicio && evento.hora)) && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.text, flexShrink: 0 }}>
                      {String(p.hora_suma || evento.hora).slice(0, 5)}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "capitalize" }}>
                  {esPaso ? "🔵 Punto de paso" : `${TIPO_ICON[p.tipo] || "📍"} ${p.tipo === "noche" ? "Pernocte" : p.tipo === "almuerzo" ? "Almuerzo" : p.tipo === "inicio" || p.tipo === "salida" ? "Salida" : p.tipo === "fin" || p.tipo === "destino" ? "Destino final" : "Parada"}`}
                </div>
                {/* dónde se puede enganchar la gente que no arranca desde el principio */}
                {(p.suma || esInicio) && !esFin && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, padding: "2px 8px", borderRadius: 100, background: `${C.green}14`, border: "1px solid #2F7D4F22" }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: "#2F7D4F" }}>👋 Acá se suma gente</span>
                  </div>
                )}
                {(() => {
                  const w = climaPunto(p);
                  if (!w || w.tempMax == null) return null;
                  const cl = climaWMO(w.code);
                  return (
                    <div style={{ fontSize: 11, color: C.text, fontWeight: 600, marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span>{cl.icon} {cl.txt}</span>
                      <span style={{ color: C.muted, fontWeight: 500 }}>·</span>
                      <span>{Math.round(w.tempMin)}°–{Math.round(w.tempMax)}°</span>
                      {w.precip != null && (
                        <>
                          <span style={{ color: C.muted, fontWeight: 500 }}>·</span>
                          <span style={{ color: w.precip >= 50 ? "#B82F26" : "#111111" }}>💧 {w.precip}%</span>
                        </>
                      )}
                    </div>
                  );
                })()}
                {/* botones nav: con coords usa coords, sino busca por nombre */}
                {(p.nombre || (p.lat && p.lng)) && (
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <button onClick={() => (p.lat && p.lng) ? navegarConOlbos(p.lat, p.lng, p.nombre || "la parada") : navegarConOlbosBusqueda(p.nombre)}
                      style={{ padding: "5px 10px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      🏍️ Olbos
                    </button>
                    <a href={p.lat && p.lng
                        ? `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.nombre)}`}
                      target="_blank" rel="noreferrer"
                      style={{ padding: "5px 10px", borderRadius: 7, background: "#f6f1eb", border: "1px solid #11111122", color: C.text, fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Map size={12} strokeWidth={2} /> Maps
                    </a>
                    <a href={p.lat && p.lng
                        ? `${WAZE_BASE}ll=${p.lat},${p.lng}&navigate=yes`
                        : `${WAZE_BASE}q=${encodeURIComponent(p.nombre)}&navigate=yes`}
                      target="_blank" rel="noreferrer"
                      style={{ padding: "5px 10px", borderRadius: 7, background: "#f6f1eb", border: "1px solid #05C8F722", color: "#0099BB", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Navigation size={12} strokeWidth={2} /> Waze
                    </a>
                  </div>
                )}
              </div>
            </div>
            );
          })}

          {/* ida y vuelta */}
          {evento.ida_vuelta && (
            <div style={{ background: "#FFF9EE", border: `1px solid ${C.gold}55`, borderRadius: 8, padding: "8px 12px", marginTop: 8, fontSize: 12, color: "#8B6914" }}>
              🔄 Ida y vuelta — las paradas se repiten en el regreso en orden inverso
            </div>
          )}
        </div>
      )}

      {/* equipamiento */}
      {evento.tipo !== "evento" && evento.equipo && (
        <SeccionPlegable titulo="Equipamiento recomendado" Icono={Shield}>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.8 }}>
            {evento.equipo.split(/[·\n,]/).filter(Boolean).map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: C.muted, flexShrink: 0, marginTop: 7, display: "inline-block" }} />
                {item.trim()}
              </div>
            ))}
          </div>
        </SeccionPlegable>
      )}

      {/* apoyo en ruta — solo si está incluido */}
      {evento.tipo !== "evento" && evento.apoyo_ruta && (
        <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Equipo de apoyo en ruta</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Vehículo de soporte y asistencia mecánica</div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#2F7D4F" }}>✓ Incluido</div>
        </div>
      )}

      {/* anotados */}
      <div style={{ marginBottom: 10 }}>

        {/* Cuánta gente lo miró — solo para quien organiza. Puesto al lado de los
            anotados a propósito: 200 visitas con 5 anotados no es un problema de
            difusión, es de propuesta. */}
        {puedeEditar && Number(evento.vistas || 0) > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 14px", marginBottom: 8 }}>
            <TrendingUp size={16} strokeWidth={2} color={C.muted} />
            <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
              {Number(evento.vistas).toLocaleString("es-AR")} {Number(evento.vistas) === 1 ? "rider vio" : "riders vieron"} este evento
            </span>
            <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>solo lo ves vos</span>
          </div>
        )}

        <button onClick={() => setShowAnotados(true)} style={{
          width: "100%", background: C.card, borderRadius: 12, padding: "13px 14px",
          border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left",
        }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.border, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Users size={18} strokeWidth={2} color={C.text} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              {evento.cupos_ilimitado
                ? (evento.riders > 5 ? `${evento.riders} participantes anotados` : "Lista de anotados")
                : `${Math.max((evento.max || 0) - (evento.riders || 0), 0)} lugares libres`}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Ver quiénes van</div>
          </div>
          <ChevronRight size={16} strokeWidth={1.8} color={C.muted} />
        </button>
      </div>


      {/* El "Chat del evento" se sacó (09-oct): era un mock local — no guardaba nada, nadie
          lo recibía y se perdía al cerrar. Para hablar con el organizador está el WhatsApp;
          para el grupo, el intercomunicador del mapa en vivo. */}

      <Divider />


      {/* El link para el grupo de WhatsApp: con ese link cada uno entra directo a la rodada
          (feedback de Emilia, 03-nov: nunca lograba que todo el grupo quedara en el mapa) */}
      {evento._fromDB && evento.id && evento.tipo !== "evento" && (isIn || gestiona) && !rodadaTerminada(evento) && (
        <InvitarGrupoWhatsApp evento={evento} />
      )}

      {/* ── EN LA RODADA: todo lo del día agrupado en una sola sección plegable ── */}
      {evento._fromDB && evento.id && evento.tipo !== "evento" && (
        <SeccionPlegable titulo="En la rodada" Icono={Flag}>

      {/* mapa del grupo en vivo (movido acá para agrupar con el resto de la rodada).
          También para quien gestiona sin estar anotado: la largada vive en el mapa. */}
      {(isIn || gestiona) && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Navigation size={17} strokeWidth={2} color={C.text} />
              </div>
              <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
                Entrá al mapa y vas a ver a todo el grupo moverse en tiempo real. Al entrar compartís tu ubicación con los que van en la rodada, y podés hablar por el intercomunicador o grabar tu recorrido desde ahí mismo.
              </div>
            </div>
            {/* UNA sola puerta de entrada: el mapa. Antes había tres botones que
                abrían tres mapas distintos (grupo en vivo, largada, grabador) y
                nadie entendía cuál era cuál. */}
            <button onClick={abrirMapaVivo}
              style={{ width: "100%", padding: "14px", borderRadius: 12, background: C.text, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: "-0.01em" }}>
              📡 Ver el mapa en vivo
            </button>
            <div style={{ fontSize: 11, color: C.muted, textAlign: "center", margin: "7px 0 0" }}>
              Al entrar empezás a compartir tu ubicación con el grupo. Y con el check-in hecho ya estás en el mapa, sin tocar nada más.
            </div>
            {/* El botón "Compartir mi ubicación" se eliminó: el check-in ya te
                pone en el mapa (watch global) y el mapa comparte al entrar */}
          </div>
        </div>
      )}

      {/* Check-in presencial (Capa 2) — inscriptos y organizador */}
      {evento._fromDB && evento.id && evento.tipo !== "evento" && <BotonCheckin evento={evento} gestiona={gestiona} />}

      {/* Estados de la rodada + ubicación en vivo (Capa 3) */}
      {evento._fromDB && evento.id && evento.tipo !== "evento" && <RodadaPanel evento={evento} gestiona={gestiona} />}
      {/* eventos tipo expo no tienen panel de rodada, pero el botón de emergencia va igual */}
      {evento._fromDB && evento.id && evento.tipo === "evento" && <BotonEmergenciaPatente evento={evento} />}

      {/* El intercomunicador vive DENTRO del mapa en vivo, no acá: hablás con el
          grupo desde la misma pantalla donde lo ves moverse. */}

      {/* Track oficial de la rodada (Capa B): el recorrido grabado más completo */}
      {evento._fromDB && evento.id && evento.tipo !== "evento" && <TrackOficialRodada evento={evento} gestiona={gestiona} />}
        </SeccionPlegable>
      )}

      {/* 📸 Galería: fotos/videos de los participantes (también después de terminada la salida) */}
      {evento._fromDB && evento.id && <GaleriaEvento evento={evento} isIn={isIn} isAdmin={isAdmin} />}

      {/* Sorteo en vivo (si la salida lo tiene activado) */}
      {evento._fromDB && evento.id && evento.tiene_sorteo && (
        <button onClick={() => setShowSorteo(true)}
          style={{ width: "100%", padding: "14px", borderRadius: 14, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
          <Award size={17} strokeWidth={1.8} color={C.muted} /><span style={{ flex: 1, textAlign: "left" }}>Sorteo en vivo</span><ChevronRight size={17} strokeWidth={2} color={C.faint} />

        </button>
      )}

      {/* Compartir el evento (link público) — se oculta una vez que la rodada arrancó (ya no tiene sentido invitar) */}
      {evento._fromDB && evento.id && !inscripcionCerrada(evento) && (
        <button onClick={() => {
          // ?v=2: WhatsApp cachea la preview; con la versión nueva de la OG card hay que cambiar el link
          const url = `${SITE_URL}/evento/${evento.id}?v=2`;
          const fmtCorta = (f) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "long" }).replace(".", ""); };
          const fIni = evento.fecha ? fmtCorta(evento.fecha) : "";
          const fFin = evento.fecha_fin && evento.fecha_fin !== evento.fecha ? fmtCorta(evento.fecha_fin) : "";
          const cuando = fIni ? (fFin ? `${fIni.split(" ")[0]} ${fIni.split(" ")[1]} y ${fFin}` : fIni) : "";
          const lugar = evento.lugar || evento.from || "";
          const precio = Number(evento.precio_entrada || evento.precio || 0);
          const linea2 = [cuando, evento.hora ? `desde las ${String(evento.hora).slice(0, 5)}` : "", lugar, precio > 0 ? `$${precio.toLocaleString("es-AR")}` : "gratis"].filter(Boolean).join(" · ");
          const texto = `🏍️ ${String(evento.title || "").trim()}\n${linea2}\nNos vemos ahí. Anotate acá y mirá quién va:`;
          compartirNativo({ texto, url, titulo: evento.title || "Evento en Olbos" });
        }}
          style={{ width: "100%", padding: "14px", borderRadius: 14, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
          <Share2 size={17} strokeWidth={1.8} color={C.muted} /><span style={{ flex: 1, textAlign: "left" }}>Compartir / invitar</span><ChevronRight size={17} strokeWidth={2} color={C.faint} />
        </button>
      )}

      {/* Copiar el link, aparte de compartir: el menú del sistema no siempre ofrece
          copiar, y cuando no está disponible se va derecho a WhatsApp. Sin esto no
          había forma de pegar el link en un grupo, un mail o un posteo (reporte de Matías). */}
      {evento._fromDB && evento.id && !inscripcionCerrada(evento) && (
        <button onClick={async () => {
          const url = `${SITE_URL}/evento/${evento.id}`;
          try {
            await navigator.clipboard.writeText(url);
            alert("Link copiado:\n" + url);
          } catch (e) {
            window.prompt("Copiá el link del evento:", url);
          }
        }}
          style={{ width: "100%", padding: "14px", borderRadius: 14, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
          <Bookmark size={17} strokeWidth={1.8} color={C.muted} /><span style={{ flex: 1, textAlign: "left" }}>Copiar el link</span>
        </button>
      )}

      {/* Flyer para difundir: imagen lista para historias y estados, armada con los
          datos que ya cargó el organizador. Compartir manda un link; esto es la pieza
          que se reenvía sola por los grupos. */}
      {evento._fromDB && evento.id && !inscripcionCerrada(evento) && (
        <button onClick={() => window.open(`${API_BASE}/api/flyer?evento=${evento.id}`, "_blank")}
          style={{ width: "100%", padding: "14px", borderRadius: 14, marginTop: 8, background: C.card, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 14.5, fontWeight: 600, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
          <Download size={17} strokeWidth={1.8} color={C.muted} />
          <span style={{ flex: 1, textAlign: "left" }}>Generar flyer para difundir</span>
          <ChevronRight size={17} strokeWidth={2} color={C.faint} />
        </button>
      )}

      {/* Inscriptos con sus datos + transferir propiedad — agrupado y plegado (secundario) */}
      {evento._fromDB && evento.id && <CircuitoDelEvento evento={evento} puedeEditar={puedeEditar} />}
      {/* Solo para quien gestiona (o el staff de la agenda): a un rider común le mostraba
          "Transferir" y un botón de Mercado Pago que no eran para él */}
      {evento._fromDB && evento.id && (gestiona || esStaffDeAgenda) && (
        <div style={{ marginTop: 10 }}>
          <SeccionPlegable titulo={evento.tipo === "evento" ? "Gestión del evento" : "Gestión de la salida"} Icono={Shield} abiertoInicial={!!window.olbosTransferirA}>
            <BotonInscriptos eventoId={evento.id} titulo={evento.titulo || evento.title} esPago={Number(evento.precio) > 0 || Number(evento.precio_entrada) > 0} />
            <BotonTransferir tipo="evento" objetoId={evento.id} autorId={evento.creator_id} titulo={evento.title || evento.titulo} onListo={onClose} />
            {/* Cobros online: el organizador conecta su MP y las inscripciones se pagan en la app */}
            <MercadoPagoConnect soloDe={evento.creator_id} />
          </SeccionPlegable>
        </div>
      )}

      {puedeEditar && (
        <div style={{ marginTop: 16, padding: "12px 0 0", borderTop: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Shield size={11} strokeWidth={2.5} /> {isAdmin ? "Admin" : esStaffDeAgenda && !esDueno && !esCoOrg ? "Equipo Olbos" : esCoOrg && !esDueno ? "Co-organización" : "Organización"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onEditar && onEditar(evento)}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Edit2 size={14} strokeWidth={2} /> Editar
            </button>
            {/* Publicar un evento YA creado en el feed (antes solo se podía al crearlo) */}
            <button onClick={async () => {
              if (!window.confirm(`¿Publicar "${evento.title || evento.titulo}" en el feed? El post lleva el botón para ver el evento y anotarse.`)) return;
              const { data: { user: u } } = await supabase.auth.getUser();
              if (!u) return;
              const f = evento.fecha ? new Date(String(evento.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : "";
              const { error } = await supabase.from("posts").insert({
                user_id: u.id,
                texto: `📅 ${evento.title || evento.titulo}${f ? `\n${f}` : ""}${evento.lugar ? `\n📍 ${evento.lugar}` : ""}${evento.descripcion ? `\n\n${evento.descripcion}` : ""}`,
                media_url: evento.foto_url || evento.flyer || null,
                media_type: (evento.foto_url || evento.flyer) ? (esVideoPortada(evento.foto_url || evento.flyer) ? "video" : "image") : null,
                categoria: "evento",
                evento_id: evento.id,
              });
              alert(error ? "No se pudo publicar: " + error.message : "✓ Publicado en el feed");
            }}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              📣 Al feed
            </button>
            {(isAdmin || esDueno) && (
            <button onClick={() => onEliminar && onEliminar(evento.id)}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid #FECACA`, background: `${C.red}14`, color: "#B91C1C", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Trash2 size={14} strokeWidth={2} /> Eliminar
            </button>
            )}
          </div>
          {(isAdmin || esDueno) && evento._fromDB && evento.id && (
            <CoOrganizadoresEvento evento={evento} coOrgs={coOrgs} />
          )}
          {isAdmin && evento._fromDB && evento.id && (
            <button onClick={async () => {
              const nuevo = !destacadoLocal;
              setDestacadoLocal(nuevo);
              const { error } = await supabase.rpc("set_evento_destacado", { p_evento_id: evento.id, p_destacado: nuevo });
              if (error) { setDestacadoLocal(!nuevo); alert("No se pudo cambiar (¿está corrido el SQL de destacados?)"); }
            }}
              style={{ width: "100%", marginTop: 8, padding: "10px 14px", borderRadius: 10, border: `1px solid ${destacadoLocal ? C.text : C.border}`, background: destacadoLocal ? C.text : C.card, color: destacadoLocal ? "#fff" : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {destacadoLocal ? "📌 Destacado — quitar" : "📌 Fijar arriba de todo"}
            </button>
          )}
        </div>
      )}

      {/* Eventos de la agenda (los carga Olbos, no el organizador): una línea al pie,
          chica, para que el organizador real pueda reclamarlo y administrarlo. */}
      {esDeLaAgendaOlbos && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px dashed ${C.border}`, fontSize: 11.5, color: C.muted, lineHeight: 1.55, textAlign: "center", letterSpacing: "-0.005em" }}>
          Publicado por Olbos en la agenda de la comunidad; lo organiza quien figura en la descripción.{" "}
          <button onClick={reclamarEvento}
            style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer", textDecoration: "underline", textDecorationColor: C.border, fontFamily: "inherit" }}>
            ¿Sos el organizador? Reclamalo
          </button>
        </div>
      )}

      {showAnotados && <AnotadosModal evento={evento} onClose={() => setShowAnotados(false)} />}
    </Modal>
    {/* Portada / foto a pantalla completa, con pinch-zoom; tocá afuera o la X para cerrar */}
    {fotoZoom && (
      <div onClick={() => setFotoZoom(null)}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.94)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
        <ImgZoom src={cldOpt(fotoZoom, 1600)} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "100vh", objectFit: "contain" }} />
        <button onClick={() => setFotoZoom(null)}
          style={{ position: "absolute", top: "calc(var(--sat) + 14px)", right: 14, width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        <div style={{ position: "absolute", bottom: "calc(var(--sab) + 18px)", left: 0, right: 0, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,.6)", pointerEvents: "none" }}>Pellizcá para acercar</div>
      </div>
    )}
    {/* Fuera del Modal: si va adentro, el transform de .fade-up atrapa su position:fixed */}
    {showSorteo && <SorteoSeccion evento={evento} onClose={() => setShowSorteo(false)} />}
    </>
  );
};

// ─── EDITAR EVENTO MODAL (admin) ──────────────────────────────────────────────
const EditarEventoModal = ({ evento, onClose, onGuardar }) => {
  const [form, setForm] = useState({
    titulo: evento.titulo || evento.title || "",
    descripcion: evento.descripcion || "",
    fecha: evento.fecha || "",
    fecha_fin: evento.fecha_fin || "",
    hora: evento.hora ? String(evento.hora).slice(0, 5) : "",
    desde: evento.desde || "",
    hasta: evento.hasta || "",
    lugar: evento.lugar || "",
    lugar_lat: evento.lat || null,
    lugar_lng: evento.lng || null,
    km: evento.km || "",
    dificultad: evento.dificultad || evento.diff || "",
    tipo_camino: evento.tipo_camino || evento.camino || "",
    max_riders: evento.max_riders || "",
    precio: evento.precio != null ? String(evento.precio) : "",
    precio_entrada: evento.precio_entrada != null ? String(evento.precio_entrada) : "",
    pago_link: evento.pago_link || "",
    pago_info: evento.pago_info || "",
    telefono: evento.telefono || "",
    equipo: evento.equipo || "",
    apoyo_ruta: evento.apoyo_ruta || false,
    ida_vuelta: evento.ida_vuelta || false,
    foto_url: evento.foto_url || evento.flyer || "",
    tiene_sorteo: !!evento.tiene_sorteo,
    // Se pueden RE-SETEAR al editar (pedido de Carlos): quién lo ve y cómo se anota
    inscripcion: evento.inscripcion || "abierta",
    visibilidad_tipo: evento.visibilidad_tipo || "publico",
    // Lugar registrado y quién cobra: antes el modal los mostraba pero no los cargaba ni
    // los guardaba, y "no se vincula el negocio" (Matías, 25-sep, almuerzo en El Encuentro)
    negocio_id: evento.negocio_id || null, negocio_nombre: "",
    cobra_negocio_id: evento.cobra_negocio_id || null, cobra_negocio_nombre: "",
  });
  useEffect(() => {
    const ids = [evento.negocio_id, evento.cobra_negocio_id].filter(Boolean);
    if (!ids.length) return;
    supabase.from("negocios").select("id, nombre").in("id", ids).then(({ data }) => {
      const nom = {}; (data || []).forEach(n => { nom[n.id] = n.nombre; });
      setForm(f => ({ ...f, negocio_nombre: nom[f.negocio_id] || f.negocio_nombre, cobra_negocio_nombre: nom[f.cobra_negocio_id] || f.cobra_negocio_nombre }));
    }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [guardando, setGuardando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fotoRef = useRef(null);
  // Config de inscripción: qué datos se piden (y son obligatorios) al anotarse + deslinde
  const [inscConfig, setInscConfig] = useState(() => {
    const c = evento.inscripcion_config || {};
    // OJO: el spread inicial preserva las claves que este modal NO edita (ej:
    // co_organizadores). Antes se reconstruía el objeto solo con las claves
    // conocidas y guardar una edición BORRABA los co-organizadores en silencio
    // (bug desde el 14-ago, lo encontró Matías al editar la hora de salida).
    return { ...c, campos: c.campos || {}, extras: Array.isArray(c.extras) ? c.extras : [], deslinde: c.deslinde || "", entradas: !!c.entradas, puntos_suma: Array.isArray(c.puntos_suma) ? c.puntos_suma : [], km_meta: c.km_meta || "", km_meta_texto: c.km_meta_texto || "", colaboradores: Array.isArray(c.colaboradores) ? c.colaboradores : [], ocultar_ruta: !!c.ocultar_ruta };
  });
  const setICampo = (k, v) => setInscConfig(c => ({ ...c, campos: { ...c.campos, [k]: v } }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const esEventoFijo = evento.tipo === "evento";
  // Edición del recorrido en el mapa (antes había que borrar y recrear la salida).
  // Los eventos viejos guardan tipo "salida"/"destino"; el editor usa "inicio"/"fin".
  const [editarRecorrido, setEditarRecorrido] = useState(false);
  const recorridoTocadoRef = useRef(false); // solo recalculamos ruta/km si abrió el editor
  const [puntosEdit, setPuntosEdit] = useState(() => {
    const cfgPS = Array.isArray((evento.inscripcion_config || {}).puntos_suma) ? evento.inscripcion_config.puntos_suma : [];
    return (Array.isArray(evento.puntos) ? evento.puntos : []).map(p => {
      const match = cfgPS.find(ps => ps && ps.nombre && p.nombre && String(ps.nombre).trim() === String(p.nombre).trim());
      return {
        ...p,
        tipo: p.tipo === "salida" ? "inicio" : p.tipo === "destino" ? "fin" : (p.tipo || "parada"),
        ...(match ? { suma: true, hora_suma: match.hora || "" } : {}),
      };
    });
  });

  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const esVideo = (file.type || "").startsWith("video");
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      fd.append("cloud_name", "dhwhk48wb");
      const endpoint = esVideo
        ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
        : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) set("foto_url", data.secure_url);
      else alert("No se pudo subir. Probá de nuevo.");
    } catch (e) { alert("No se pudo subir. Probá de nuevo."); }
    setSubiendoFoto(false);
  };

  const guardar = async () => {
    setGuardando(true);
    const cambios = {
      titulo: form.titulo,
      descripcion: form.descripcion || null,
      fecha: form.fecha,
      fecha_fin: form.fecha_fin || null,
      hora: form.hora || null,
      desde: form.desde || null,
      hasta: form.hasta || null,
      lugar: form.lugar || null,
      km: form.km ? Number(form.km) : null,
      dificultad: form.dificultad || null,
      tipo_camino: form.tipo_camino || null,
      max_riders: form.max_riders ? Number(form.max_riders) : null,
      precio: form.precio ? Number(form.precio) : 0,
      precio_entrada: form.precio_entrada ? Number(form.precio_entrada) : null,
      pago_link: (form.pago_link || "").trim() || null,
      pago_info: (form.pago_info || "").trim() || null,
      telefono: form.telefono || null,
      equipo: form.equipo || null,
      apoyo_ruta: form.apoyo_ruta,
      ida_vuelta: form.ida_vuelta,
      foto_url: form.foto_url || null,
      tiene_sorteo: !!form.tiene_sorteo,
      inscripcion: form.inscripcion || "abierta",
      // Merge sobre el config ACTUAL del evento: si se sumó un co-organizador con
      // este modal ya abierto, el snapshot de inscConfig no lo tiene y lo pisaría
      inscripcion_config: { ...(evento.inscripcion_config || {}), ...inscConfig, extras: inscConfig.extras.filter(x => x.label && x.label.trim()) },
      negocio_id: form.negocio_id || null,
      cobra_negocio_id: form.cobra_negocio_id || null,
    };
    // La visibilidad solo se re-setea en eventos que no son de grupo
    if (!evento.grupo_id) cambios.visibilidad_tipo = form.visibilidad_tipo || "publico";
    // Si editó el lugar de un evento fijo, actualizar coords
    if (esEventoFijo && form.lugar_lat && form.lugar_lng) {
      cambios.lat = form.lugar_lat;
      cambios.lng = form.lugar_lng;
    }
    // Recorrido editado en el mapa: guardar los puntos y recalcular la ruta real + km.
    // Los puntos tildados con "acá se suma gente" reescriben los puntos de suma.
    if (!esEventoFijo && recorridoTocadoRef.current && puntosEdit.length >= 2) {
      cambios.puntos = puntosEdit;
      const sumaPts = puntosEdit.filter(p => p.suma && p.tipo !== "fin").map(p => ({ nombre: p.nombre, hora: p.hora_suma || "" }));
      if (sumaPts.length > 0) cambios.inscripcion_config = { ...cambios.inscripcion_config, puntos_suma: sumaPts };
      const conCoords = puntosEdit.filter(p => p.lat && p.lng);
      if (conCoords.length >= 2) {
        cambios.lat = conCoords[0].lat;
        cambios.lng = conCoords[0].lng;
        try {
          const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
          const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
          const intermedios = conCoords.slice(1, -1).slice(0, 23).map(p => `${p.lat},${p.lng}`).join("|");
          const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${intermedios ? `&waypoints=${encodeURIComponent(intermedios)}` : ""}`;
          const res = await fetch(`${API_BASE}/api/directions?${qs}`);
          const data = await res.json();
          if (data?.polyline) cambios.polyline = data.polyline;
          if (data?.distancia_km) cambios.km = data.distancia_km;
        } catch (e) { /* sin ruta nueva: quedan los puntos y la línea vieja */ }
      }
    }
    const ok = await onGuardar(cambios);
    if (!ok) setGuardando(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "92vh", overflow: "auto", padding: "20px 20px calc(36px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={16} strokeWidth={2.5} color={C.accent} /> Editar evento
          </h2>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={18} strokeWidth={2} color={C.muted} />
          </button>
        </div>

        <Input label="Nombre del evento" value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej: Expo Motos 2026" />

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción</div>
          <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }} />
        </div>

        {/* Foto del evento */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Foto o video del evento</div>
          {form.foto_url ? (
            <div>
              {esVideoPortada(form.foto_url) ? (
                <video src={cldOpt(form.foto_url, 720)} autoPlay muted loop playsInline poster={posterDeVideo(form.foto_url)}
                  style={{ width: "100%", borderRadius: 12, maxHeight: 200, objectFit: "cover", display: "block" }} />
              ) : (
                <img loading="lazy" decoding="async" src={form.foto_url} alt="" style={{ width: "100%", borderRadius: 12, maxHeight: 200, objectFit: "cover", display: "block" }} />
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => !subiendoFoto && fotoRef.current?.click()} disabled={subiendoFoto}
                  style={{ flex: 1, padding: "9px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: C.text, cursor: subiendoFoto ? "default" : "pointer" }}>
                  {subiendoFoto ? "Subiendo…" : "Cambiar"}
                </button>
                <button onClick={() => set("foto_url", "")}
                  style={{ padding: "9px 14px", borderRadius: 10, background: `${C.red}14`, border: `1px solid ${C.red}22`, fontSize: 13, fontWeight: 600, color: C.red, cursor: "pointer" }}>
                  Quitar
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => !subiendoFoto && fotoRef.current?.click()} disabled={subiendoFoto}
              style={{ width: "100%", padding: "16px", borderRadius: 12, background: C.surface, border: `1px dashed ${C.border}`, fontSize: 13, fontWeight: 600, color: C.muted, cursor: subiendoFoto ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {subiendoFoto ? "Subiendo…" : "📷 Subir foto o video del evento"}
            </button>
          )}
          <input ref={fotoRef} type="file" accept="image/*,video/*" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subirFoto(f); }} />
        </div>

        {esEventoFijo ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Lugar del evento</div>
            <LugarPicker
              value={form.lugar_lat ? { nombre: form.lugar, lat: form.lugar_lat, lng: form.lugar_lng } : (form.lugar ? { nombre: form.lugar } : null)}
              onChange={val => setForm(f => ({ ...f, lugar: val.nombre, lugar_lat: val.lat || null, lugar_lng: val.lng || null }))}
              placeholder="Buscar lugar..."
              label=""
            />
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginTop: 12, marginBottom: 6 }}>¿Se hace en un lugar registrado? · opcional</div>
            <SelectorSedeNegocio
              valor={form.negocio_id || null}
              nombre={form.negocio_nombre || ""}
              onChange={(id, nom) => setForm(f => ({ ...f, negocio_id: id, negocio_nombre: nom }))}
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>Si lo vinculás, el evento aparece también en el perfil de ese lugar dentro de Servicios.</div>

            {/* Quién cobra las entradas. Es OTRA cosa que dónde se hace: un evento puede
                hacerse en un circuito y cobrarlo una productora. Vacío = cobra el
                organizador con su propio Mercado Pago, que es lo de siempre. */}
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginTop: 14, marginBottom: 6 }}>¿Cobra otro? · opcional</div>
            <SelectorSedeNegocio
              valor={form.cobra_negocio_id || null}
              nombre={form.cobra_negocio_nombre || ""}
              onChange={(id, nom) => setForm(f => ({ ...f, cobra_negocio_id: id, cobra_negocio_nombre: nom }))}
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
              Si una productora o el lugar cobra las entradas por vos, elegilo acá y la plata va directo a su Mercado Pago. Tiene que tener su ficha en Olbos y su cuenta conectada. Si lo dejás vacío, cobrás vos.
            </div>
          </div>
        ) : (
          <>
            <Input label="Punto de salida" value={form.desde} onChange={e => set("desde", e.target.value)} placeholder="Ej: Olivos" />
            <Input label="Destino final" value={form.hasta} onChange={e => set("hasta", e.target.value)} placeholder="Ej: Tigre" />
            <div style={{ marginBottom: 14 }}>
              <button onClick={() => { setEditarRecorrido(v => !v); recorridoTocadoRef.current = true; }}
                style={{ width: "100%", padding: "11px", borderRadius: 10, background: editarRecorrido ? "#111111" : C.surface, color: editarRecorrido ? "#fff" : C.text, border: `1px solid ${editarRecorrido ? "#111111" : C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                🗺️ {editarRecorrido ? "Listo, cerrar el editor del mapa" : `Editar el recorrido en el mapa${puntosEdit.length > 0 ? ` (${puntosEdit.length} puntos)` : ""}`}
              </button>
              {editarRecorrido && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
                    Editá los puntos igual que al crear la salida: encuentro, paradas, puntos de paso y destino. Al guardar se recalculan la ruta real y los km.
                  </div>
                  <MapaCreadorRuta puntos={puntosEdit} setPuntos={setPuntosEdit} />
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha {esEventoFijo ? "inicio" : "salida"}</div>
            <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha {esEventoFijo ? "fin" : "regreso"}</div>
            <input type="date" value={form.fecha_fin} onChange={e => set("fecha_fin", e.target.value)} min={form.fecha}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
        </div>

        {/* La hora siempre se pudo guardar pero el campo no existía en este modal:
            un evento creado sin hora quedaba sin hora para siempre (reporte de Matías) */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Hora de salida</div>
          <input type="time" value={form.hora} onChange={e => set("hora", e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5, lineHeight: 1.4 }}>El check-in se habilita automáticamente 4hs antes de esta hora.</div>
        </div>

        {!esEventoFijo && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Distancia (km)</div>
                <input type="number" value={form.km} onChange={e => set("km", e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Dificultad</div>
                <select value={form.dificultad} onChange={e => set("dificultad", e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" }}>
                  <option value="">—</option>
                  <option value="Fácil">Fácil</option>
                  <option value="Media">Media</option>
                  <option value="Difícil">Difícil</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Tipo de camino</div>
              <select value={form.tipo_camino} onChange={e => set("tipo_camino", e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" }}>
                <option value="">—</option>
                <option value="Asfalto">Asfalto</option>
                <option value="Off-road">Off-road</option>
                <option value="Mixto">Mixto</option>
              </select>
            </div>
          </>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Cupos (vacío = ilimitado)</div>
            <input type="number" value={form.max_riders} onChange={e => set("max_riders", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{esEventoFijo ? "Precio entrada" : "Precio por rider"}</div>
            <input type="number" value={esEventoFijo ? form.precio_entrada : form.precio}
              onChange={e => set(esEventoFijo ? "precio_entrada" : "precio", e.target.value)}
              placeholder="0 = gratis"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
        </div>

        {/* Salida paga: cómo cobra el organizador (el pago va directo a su cuenta) */}
        {Number(esEventoFijo ? form.precio_entrada : form.precio) > 0 && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1F8347", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>💳 Cobro de la inscripción</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 4 }}>
              Conectá tu Mercado Pago y las inscripciones se pagan en la app: la plata va directo a tu cuenta y cada pago se confirma solo — sin comprobantes ni tildes a mano.
            </div>
            <MercadoPagoConnect />
            <div style={{ fontSize: 11, color: C.muted, margin: "12px 0 8px", fontWeight: 700 }}>¿Preferís transferencia o link propio? Dejá tus datos (opcional):</div>
            <Input label="Link de pago (Mercado Pago)" value={form.pago_link} onChange={e => set("pago_link", e.target.value)} placeholder="https://mpago.la/..." />
            <Input label="Datos de pago (alias, CBU, instrucciones)" value={form.pago_info} onChange={e => set("pago_info", e.target.value)} placeholder="Ej: Alias: mi.alias.mp" />
          </div>
        )}

        {/* Inscripción externa: cuando el organizador acredita con su propio formulario, la
            inscripción en Olbos no le sirve. Con el link cargado, "Anotarme" lleva a su web.
            (Originarios Band of Brothers toma las inscripciones por Google Forms, 26-sep.) */}
        <div style={{ marginBottom: 14 }}>
          <Input label="Inscripción en otra web (opcional)" value={inscConfig.link_externo || ""} onChange={e => setInscConfig(c => ({ ...c, link_externo: e.target.value.trim() }))} placeholder="https://forms.gle/... o la web del evento" />
          {!esEventoFijo && (
            <div onClick={() => setInscConfig(c => ({ ...c, ocultar_ruta: !c.ocultar_ruta }))} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: inscConfig.ocultar_ruta ? "#FFF3EE" : C.surface, border: `1px solid ${inscConfig.ocultar_ruta ? "#FBD38D" : C.border}`, cursor: "pointer", marginBottom: 14 }}>
              <span style={{ fontSize: 20 }}>🙈</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>Recorrido sorpresa</div>
                <div style={{ fontSize: 11, color: C.muted }}>Hasta la largada se muestra solo el punto de encuentro; el camino se revela cuando arrancás la rodada</div>
              </div>
              <input type="checkbox" checked={!!inscConfig.ocultar_ruta} onChange={() => {}} style={{ accentColor: "#111111" }} />
            </div>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginTop: -6, lineHeight: 1.45 }}>Si el organizador toma las inscripciones en su propio formulario, pegalo acá: el botón "Anotarme" lleva ahí en vez de anotar en Olbos.</div>
        </div>

        <Input label="WhatsApp de contacto" value={form.telefono} onChange={e => set("telefono", e.target.value)} placeholder="Ej: +54 9 11 1234 5678" />

        {!esEventoFijo && (
          <Input label="Equipo recomendado" value={form.equipo} onChange={e => set("equipo", e.target.value)} placeholder="Ej: Casco integral, guantes, botas" />
        )}

        {/* ── Datos que se piden (y son obligatorios) al anotarse ── */}
        <div style={{ marginBottom: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Datos obligatorios al anotarse</div>
          <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
            Tildá los datos que el rider DEBE completar para anotarse (los carga con su ficha guardada, no recarga nada si ya la tiene). Vos los ves en la planilla de inscriptos.
          </div>
          {[
            ["nombre_completo", "Nombre y apellido"],
            ["telefono", "Teléfono"],
            ["emergencia", "Contacto de emergencia"],
            ["dni", "DNI"],
            ["condicion_medica", "Condición médica / medicación"],
            ["localidad", "Localidad (de dónde viene)"],
            ["direccion", "Dirección"],
          ].map(([k, label]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={!!inscConfig.campos[k]} onChange={e => setICampo(k, e.target.checked)} style={{ width: 18, height: 18, accentColor: "#111111" }} />
              <span style={{ fontSize: 13.5, color: C.text }}>{label}</span>
            </label>
          ))}

          {/* preguntas propias del evento */}
          <EditorTandas valor={inscConfig.tandas} onChange={t => setInscConfig(c => ({ ...c, tandas: t }))} />

          {inscConfig.extras.map((ex, i) => (
            <div key={i} style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input value={ex.label} onChange={e => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))}
                placeholder="Tu pregunta (ej: ¿Te quedás a almorzar?)"
                style={{ flex: 1, minWidth: 0, padding: "9px 11px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box" }} />
              <select value={ex.tipo} onChange={e => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x) }))}
                style={{ padding: "9px 6px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.text, background: C.card, flexShrink: 0 }}>
                <option value="sino">Sí / No</option>
                <option value="texto">Texto</option>
                <option value="opciones">Opciones</option>
              </select>
              <button onClick={() => setInscConfig(c => ({ ...c, extras: c.extras.filter((_, j) => j !== i) }))}
                style={{ width: 30, height: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={13} strokeWidth={2.5} color={C.red} />
              </button>
            </div>
            {ex.tipo === "opciones" && (
              <EditorOpcionesCampo
                opciones={ex.opciones}
                onChange={ops => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, opciones: ops } : x) }))}
              />
            )}
            </div>
          ))}
          <button onClick={() => setInscConfig(c => ({ ...c, extras: [...c.extras, { label: "", tipo: "sino" }] }))}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
            <Plus size={14} strokeWidth={2.5} /> Agregar pregunta propia
          </button>

          {/* deslinde */}
          <div style={{ marginTop: 12 }}>
            {/* Sección aparte SOLO para eventos fijos: en las salidas los puntos de suma
                se tildan en cada punto del mapa (una sola lista, cero duplicación) */}
            {esEventoFijo && (<>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Puntos donde se suma gente (opcional)</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
              Para caravanas que levantan gente camino al evento (ej: Shell Paraná 07:30 → YPF Wilde 08:00). Cada uno elige desde qué punto se suma al anotarse, y vos ves cuántos van en cada parada.
            </div>
            {(inscConfig.puntos_suma || []).map((pt, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input value={pt.nombre || ""} onChange={e => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x) }))}
                  placeholder={`Punto ${i + 1} (ej: Shell Av. Paraná, CABA)`}
                  style={{ flex: 2, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box", minWidth: 0 }} />
                <input value={pt.hora || ""} onChange={e => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.map((x, j) => j === i ? { ...x, hora: e.target.value } : x) }))}
                  placeholder="07:30"
                  style={{ flex: 1, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box", minWidth: 0 }} />
                <button onClick={() => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.filter((_, j) => j !== i) }))}
                  style={{ width: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", flexShrink: 0, color: C.red, fontWeight: 700 }}>✕</button>
              </div>
            ))}
            <button onClick={() => setInscConfig(c => ({ ...c, puntos_suma: [...(c.puntos_suma || []), { nombre: "", hora: "" }] }))}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
              <Plus size={14} strokeWidth={2.5} /> Agregar punto de encuentro
            </button>
            </>)}
            <ColaboradoresEditor value={inscConfig.colaboradores || []} onChange={(v) => setInscConfig(c => ({ ...c, colaboradores: v }))} />
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Deslinde de responsabilidad</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>Todos los que se anoten lo firman electrónicamente (nombre + DNI). Si lo dejás vacío, se usa el modelo de Olbos según el tipo de evento.</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button type="button" onClick={() => setInscConfig(c => ({ ...c, deslinde: DESLINDE_MODELO_EVENTO }))}
                style={{ flex: 1, padding: "8px 6px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                Usar modelo · encuentro en predio
              </button>
              <button type="button" onClick={() => setInscConfig(c => ({ ...c, deslinde: DESLINDE_MODELO_SALIDA }))}
                style={{ flex: 1, padding: "8px 6px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                Usar modelo · salida / travesía
              </button>
            </div>
            <textarea value={inscConfig.deslinde} onChange={e => setInscConfig(c => ({ ...c, deslinde: e.target.value }))}
              placeholder="Vacío = modelo de Olbos. Si escribís el tuyo, queda registrada la firma con fecha, hora, nombre y DNI."
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }} />
          </div>

          {/* control de acceso con entrada QR (en eventos pagos va siempre) */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={!!inscConfig.entradas} onChange={e => setInscConfig(c => ({ ...c, entradas: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: "#111111", marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text }}>🎫 Control de acceso con entrada QR</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Cada inscripto recibe su entrada con QR y la escaneás en la puerta desde tu celu. En eventos pagos está activo siempre.</div>
            </div>
          </label>

          {/* sorteo en vivo (activable también después de creado el evento) */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.tiene_sorteo} onChange={e => set("tiene_sorteo", e.target.checked)}
              style={{ width: 18, height: 18, accentColor: "#111111", marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text }}>🎁 Sorteo en vivo</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Cargás premios y sorteás en vivo entre los inscriptos, desde el detalle del evento. Cada uno lo ve en su celu.</div>
            </div>
          </label>
        </div>

        {/* Visibilidad e inscripción: se pueden RE-SETEAR sobre un evento ya creado */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Inscripción</div>
          {[["abierta", "🔓 Abierta — cualquiera puede anotarse"], ["aprobacion", "✋ Con aprobación — vos aceptás cada solicitud"]].map(([val, label]) => (
            <div key={val} onClick={() => set("inscripcion", val)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
              background: form.inscripcion === val ? "#11111108" : C.surface,
              border: `1.5px solid ${form.inscripcion === val ? "#111111" : C.border}`,
            }}>
              <span style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0, border: `2px solid ${form.inscripcion === val ? "#111111" : C.border}`, background: form.inscripcion === val ? "#111111" : "transparent" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
            </div>
          ))}
        </div>
        {!evento.grupo_id && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Visibilidad</div>
            {[["publico", "🌎 Público — aparece en la agenda para todos"], ["invitados", "🔒 Solo invitados — nadie más lo ve"]].map(([val, label]) => (
              <div key={val} onClick={() => set("visibilidad_tipo", val)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
                background: form.visibilidad_tipo === val ? "#11111108" : C.surface,
                border: `1.5px solid ${form.visibilidad_tipo === val ? "#111111" : C.border}`,
              }}>
                <span style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0, border: `2px solid ${form.visibilidad_tipo === val ? "#111111" : C.border}`, background: form.visibilidad_tipo === val ? "#111111" : "transparent" }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
              </div>
            ))}
            {form.visibilidad_tipo === "invitados" && evento.visibilidad_tipo !== "invitados" && (
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>Al pasarlo a solo invitados, desaparece de la agenda pública. Los ya anotados no se borran; invitá gente desde el detalle del evento.</div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={guardando || !form.titulo || !form.fecha}
            style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: guardando ? "default" : "pointer", opacity: (guardando || !form.titulo || !form.fecha) ? 0.55 : 1 }}>
            {guardando ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── CREAR EVENTO MODAL ───────────────────────────────────────────────────────
// ─── CREAR SALIDA GRUPAL (reutiliza MapaCreadorRuta) ─────────────────────────
const CrearSalidaModal = ({ onClose, onCrear, session, onFlyer, extrayendoFlyer, rodadaHacia }) => {
  // "Crear una rodada hacia este evento": arranca con el destino y la fecha puestos
  const [ruta, setRuta] = useState(() => rodadaHacia && rodadaHacia.lat && rodadaHacia.lng
    ? { nombre: `Rodada a ${rodadaHacia.titulo || "el evento"}`, puntos: [{ lat: Number(rodadaHacia.lat), lng: Number(rodadaHacia.lng), nombre: rodadaHacia.lugar || rodadaHacia.titulo, tipo: "fin" }], km: "", diff: "", pctOffroad: 0, desc: "", equipo: "", apoyo_ruta: false, ida_vuelta: false, ocultar_ruta: false }
    : { nombre: "", puntos: [], km: "", diff: "", pctOffroad: 0, desc: "", equipo: "", apoyo_ruta: false, ida_vuelta: false, ocultar_ruta: false });
  const [fotoEvento, setFotoEvento] = useState(null); // foto/flyer del evento (opcional)
  const [subiendoFotoEv, setSubiendoFotoEv] = useState(false);
  const fotoEvRef = useRef(null);
  // Rodada anexa: esta salida llega a un evento (ej: las 5 rodadas al BikerFest).
  // Al elegirlo, el lugar del evento se convierte en el punto final del recorrido.
  const [eventoPadre, setEventoPadre] = useState(rodadaHacia || null); // { id, titulo, lugar, lat, lng }
  const [qPadre, setQPadre] = useState("");
  const [resPadre, setResPadre] = useState([]);
  const timerPadreRef = useRef(null);
  const buscarPadre = (texto) => {
    setQPadre(texto);
    if (timerPadreRef.current) clearTimeout(timerPadreRef.current);
    if (texto.trim().length < 2) { setResPadre([]); return; }
    timerPadreRef.current = setTimeout(async () => {
      const { data } = await supabase.from("eventos").select("id, titulo, fecha, lugar, lat, lng")
        .is("grupo_id", null).eq("tipo", "evento")
        .gte("fecha", hoyLocalISO())
        .ilike("titulo", `%${texto.trim()}%`).limit(5);
      setResPadre(data || []);
    }, 280);
  };
  const vincularPadre = (ev) => {
    setEventoPadre(ev);
    setQPadre(""); setResPadre([]);
    if (ev.lat && ev.lng) {
      setRuta(r => ({
        ...r,
        puntos: [...r.puntos.filter(p => p.tipo !== "fin"), { lat: Number(ev.lat), lng: Number(ev.lng), nombre: ev.lugar || ev.titulo, tipo: "fin" }],
      }));
    }
  };
  const subirFotoEvento = async (file) => {
    if (!file) return;
    setSubiendoFotoEv(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setFotoEvento(j.secure_url);
      else alert("No se pudo subir la foto.");
    } catch (e) { alert("No se pudo subir la foto."); }
    setSubiendoFotoEv(false);
  };
  const [evento, setEvento] = useState({ fecha: (rodadaHacia && rodadaHacia.fecha) || "", fecha_fin: "", cupos: "", cupos_ilimitado: true, precio: "0", pago_link: "", pago_info: "", inscripcion: "abierta", telefono: "" });
  // Caravana hacia un evento: el formulario arranca con lo mínimo (nombre, mapa, fecha,
  // hora, WhatsApp) y todo lo demás queda detrás de "Más opciones". Sugerencia de un
  // dev usuario (Paraná, 22-sep-2026): llegaba al final sin leer nada por la cantidad
  // de campos, aunque casi todos fueran opcionales.
  const [masOpciones, setMasOpciones] = useState(!rodadaHacia);
  const simple = !masOpciones;
  // Config de inscripción: qué datos se piden al anotarse + deslinde
  const [inscConfig, setInscConfig] = useState({
    campos: { nombre_completo: true, dni: true, telefono: true, emergencia: true, condicion_medica: true, casco: false },
    extras: [],   // preguntas propias del evento: [{ label, tipo: "sino"|"texto" }]
    deslinde: "",
    entradas: false, // control de acceso con entrada QR (en pagos va siempre)
    puntos_suma: [], // rodada que levanta gente: [{ nombre, hora }] — cada uno elige desde dónde se suma
    colaboradores: [], // negocios de Pit Stop que participan: [{ negocio_id, user_id, nombre, categoria }]
    km_meta: "",        // meta solidaria de km: los recorridos grabados en la rodada suman a un contador colectivo
    km_meta_texto: "",  // qué se logra al llegar (ej: "1 juguete cada 25 km para la colecta")
  });
  const setICampo = (k, v) => setInscConfig(c => ({ ...c, campos: { ...c.campos, [k]: v } }));
  const [tieneSorteo, setTieneSorteo] = useState(false);
  const setR = (k, v) => setRuta(r => ({ ...r, [k]: v }));
  const setE = (k, v) => setEvento(e => ({ ...e, [k]: v }));

  // ── Borrador automático ────────────────────────────────────────────────────
  // El formulario sobrevive a TODO: conectar Mercado Pago en otra pestaña, el botón
  // atrás de Android, que el sistema mate la app (nos pasó creando la Mega Rodada).
  // Se guarda con cada cambio, se ofrece retomar al reabrir y caduca a las 24 h.
  const draftListo = useRef(false);
  useEffect(() => {
    // Si viene con destino precargado (rodada hacia un evento), no ofrecer el borrador viejo
    if (rodadaHacia) { draftListo.current = true; return; }
    try {
      const raw = localStorage.getItem("olbos_draft_salida");
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.t && Date.now() - d.t < 24 * 3600 * 1000 && ((d.ruta && d.ruta.nombre) || (d.ruta && (d.ruta.puntos || []).length > 0))) {
          if (window.confirm(`Tenías una salida a medio crear${d.ruta && d.ruta.nombre ? ` («${d.ruta.nombre}»)` : ""}. ¿Retomarla donde la dejaste?`)) {
            if (d.ruta) setRuta(d.ruta);
            if (d.evento) setEvento(d.evento);
            if (d.inscConfig) setInscConfig(c => ({ ...c, ...d.inscConfig }));
            setTieneSorteo(!!d.tieneSorteo);
          } else {
            localStorage.removeItem("olbos_draft_salida");
          }
        }
      }
    } catch (e) { /* */ }
    draftListo.current = true;
  }, []);
  useEffect(() => {
    if (!draftListo.current) return;
    try { localStorage.setItem("olbos_draft_salida", JSON.stringify({ ruta, evento, inscConfig, tieneSorteo, t: Date.now() })); } catch (e) { /* */ }
  }, [ruta, evento, inscConfig, tieneSorteo]);

  // Auto-calcular km cuando cambian los puntos
  useEffect(() => {
    const km = distanciaTotal(ruta.puntos);
    if (km > 0) setR("km", String(km));
  }, [ruta.puntos]);

  // UN punto en el mapa = evento en lugar fijo · 2 o más = salida con recorrido.
  // Con evento padre NUNCA es fijo: la caravana arranca con el destino puesto y un toque
  // de más publicaba un EVENTO duplicado encima de la Nocturna (ya pasó dos veces).
  const esFijo = !eventoPadre && ruta.puntos.length === 1;
  // En una caravana hacia un evento no se exigen km ni dificultad: el destino ya está y el mapa habla solo
  const canSubmit = ruta.nombre && evento.fecha && ruta.puntos.length >= 1 && (esFijo || (ruta.puntos.length >= 2 && (eventoPadre || (ruta.km && ruta.diff))));
  // Publicar espera al insert y se bloquea: el doble toque creaba dos eventos iguales
  const [publicando, setPublicando] = useState(false);
  const publicar = async () => {
    if (!canSubmit || publicando) return;
    setPublicando(true);
    try {
      const r = await onCrear({ ruta, evento, inscConfig, tieneSorteo, foto: fotoEvento, eventoPadreId: eventoPadre?.id || null });
      if (r && r.ok === false) setPublicando(false); // quedó abierto con todo cargado: se puede reintentar
    } catch (e) {
      alert("No se pudo publicar. Revisá la señal y probá de nuevo: lo que cargaste sigue acá.");
      setPublicando(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && confirmarCierre(onClose, ruta.puntos)}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "92vh", overflow: "auto", padding: "20px 20px calc(36px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700 }}>
            {eventoPadre ? "🛣️ Tu rodada al evento" : ruta.puntos.length === 0 ? "✨ Nuevo evento o salida" : esFijo ? "📍 Nuevo evento" : "🛣️ Nueva salida grupal"}
          </h3>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        {/* atajo del flyer: sube y la IA autocompleta */}
        {onFlyer && (
          <button onClick={onFlyer} disabled={extrayendoFlyer}
            style={{ width: "100%", marginBottom: 14, padding: "11px 14px", borderRadius: 12, border: `1.5px dashed ${C.border}`, background: C.surface, fontSize: 13, fontWeight: 700, color: extrayendoFlyer ? C.muted : C.text, cursor: extrayendoFlyer ? "default" : "pointer", textAlign: "left" }}>
            {extrayendoFlyer ? "⏳ Leyendo el flyer…" : "📷 ¿Tenés el flyer? Subilo y autocompleto todo con IA"}
          </button>
        )}

        {/* nombre */}
        <Input label="Nombre" value={ruta.nombre} onChange={e => setR("nombre", e.target.value)} placeholder="Ej: Vuelta por los 7 Lagos / Expo Motos" />

        {/* mapa igual que en rutas */}
        <MapaCreadorRuta
          puntos={ruta.puntos}
          setPuntos={updater => setRuta(r => ({ ...r, puntos: typeof updater === "function" ? updater(r.puntos) : updater }))}
          onKmReal={km => setR("km", String(km))}
        />

        {/* Clasificación automática: se explica sola según los puntos cargados */}
        <div style={{ padding: "9px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
          {eventoPadre
            ? (ruta.puntos.length >= 2
                ? <>🛣️ Se publica como <strong style={{ color: C.text }}>rodada hacia {eventoPadre.titulo}</strong>, vinculada al evento.</>
                : <>🛣️ El destino ya está. <strong style={{ color: C.text }}>Marcá en el mapa de dónde salen</strong> (tu ciudad o el punto de encuentro) y queda como rodada vinculada al evento.</>)
            : ruta.puntos.length === 0
            ? "Cargá dónde pasa: con UN punto queda como evento en ese lugar (expo, encuentro, curso); sumá más puntos y se convierte en salida con recorrido."
            : esFijo
              ? <>📍 Se publica como <strong style={{ color: C.text }}>evento en {ruta.puntos[0]?.nombre || "ese lugar"}</strong>. Agregá más puntos si tiene recorrido.</>
              : <>🛣️ Se publica como <strong style={{ color: C.text }}>salida con recorrido</strong> ({ruta.puntos.length} puntos).</>}
        </div>

        {/* Rodada anexa a un evento: el destino se completa solo y quedan vinculadas */}
        {!esFijo && (
          <div style={{ marginBottom: 14 }}>
            {eventoPadre ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>🎯</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1F8347", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>Rodada al evento: {eventoPadre.titulo}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>El destino quedó apuntado al evento; van a aparecer vinculados.</div>
                </div>
                <button onClick={() => setEventoPadre(null)} style={{ background: "transparent", border: "none", color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>✕</button>
              </div>
            ) : (
              <>
                <input value={qPadre} onChange={e => buscarPadre(e.target.value)}
                  placeholder="🎯 ¿Va a un evento? Buscalo y quedan vinculados (opcional)"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px dashed ${C.border}`, fontSize: 13, color: C.text, background: C.surface, boxSizing: "border-box" }} />
                {resPadre.map(ev => (
                  <button key={ev.id} onClick={() => vincularPadre(ev)}
                    style={{ width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, marginTop: 6, cursor: "pointer", fontSize: 13, color: C.text }}>
                    <strong>{ev.titulo}</strong>
                    <span style={{ color: C.muted, fontSize: 11.5 }}> · {ev.fecha ? new Date(ev.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : ""}{ev.lugar ? ` · ${ev.lugar}` : ""}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {/* foto o flyer del evento (opcional, sin IA) */}
        <div style={{ marginBottom: 14 }}>
          {fotoEvento ? (
            <div style={{ position: "relative", borderRadius: 12, overflow: "hidden" }}>
              <img loading="lazy" decoding="async" src={cldThumb(fotoEvento, 600)} alt="" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
              <button onClick={() => setFotoEvento(null)}
                style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "#fff", border: "none", cursor: "pointer" }}>✕</button>
            </div>
          ) : (
            <button onClick={() => !subiendoFotoEv && fotoEvRef.current?.click()} disabled={subiendoFotoEv}
              style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.surface, border: `1.5px dashed ${C.border}`, fontSize: 13, fontWeight: 600, color: C.muted, cursor: subiendoFotoEv ? "default" : "pointer" }}>
              {subiendoFotoEv ? "Subiendo…" : "🖼️ Foto o flyer del evento (opcional)"}
            </button>
          )}
          <input ref={fotoEvRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subirFotoEvento(f); }} />
        </div>

        {!esFijo && !simple && (<>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input label="Distancia (km)" value={ruta.km} onChange={e => setR("km", e.target.value)} placeholder="Ej: 340" />
          </div>
          <div style={{ flex: 1, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Dificultad</div>
            <select value={ruta.diff} onChange={e => setR("diff", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card }}>
              <option value="">Elegir</option>
              {["Fácil", "Moderada", "Difícil", "Muy difícil"].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* superficie */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Superficie</div>
          <input type="range" min={0} max={100} step={10} value={ruta.pctOffroad}
            onChange={e => setR("pctOffroad", Number(e.target.value))}
            style={{ width: "100%", accentColor: C.text, cursor: "pointer" }} />
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 8, marginTop: 6, border: `1px solid ${C.border}` }}>
            <div style={{ width: `${100 - ruta.pctOffroad}%`, background: C.accent }} />
            <div style={{ width: `${ruta.pctOffroad}%`, background: "#8B6914" }} />
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: C.muted }}>
            {ruta.pctOffroad === 0 ? "100% asfalto" : ruta.pctOffroad === 100 ? "100% off-road" : `${100 - ruta.pctOffroad}% asfalto · ${ruta.pctOffroad}% off-road`}
          </div>
        </div>
        </>)}

        {!simple && (<>
        {/* descripción */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Descripción</div>
          <textarea value={ruta.desc} onChange={e => setR("desc", e.target.value)}
            placeholder="Contá de qué va la salida, qué la hace especial..."
            style={{ width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box" }} />
        </div>

        {/* equipamiento */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Equipamiento recomendado</div>
          <textarea value={ruta.equipo} onChange={e => setR("equipo", e.target.value)}
            placeholder="Ej: Casco integral, protecciones, ropa de lluvia, hidratación..."
            style={{ width: "100%", minHeight: 60, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box" }} />
        </div>
        </>)}

        {/* apoyo en ruta + ida y vuelta (solo salidas) */}
        {!esFijo && !simple && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div onClick={() => setR("apoyo_ruta", !ruta.apoyo_ruta)} style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: ruta.apoyo_ruta ? `${C.green}14` : C.surface, border: `1px solid ${ruta.apoyo_ruta ? `${C.green}33` : C.border}`, cursor: "pointer" }}>
            <span style={{ fontSize: 20 }}>🚐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>Apoyo en ruta</div>
              <div style={{ fontSize: 11, color: C.muted }}>Vehículo de soporte</div>
            </div>
            <input type="checkbox" checked={ruta.apoyo_ruta} onChange={() => {}} style={{ accentColor: "#27AE60" }} />
          </div>
          <div onClick={() => setR("ida_vuelta", !ruta.ida_vuelta)} style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: ruta.ida_vuelta ? "#FFF3EE" : C.surface, border: `1px solid ${ruta.ida_vuelta ? "#FBD38D" : C.border}`, cursor: "pointer" }}>
            <span style={{ fontSize: 20 }}>🔄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>Ida y vuelta</div>
              <div style={{ fontSize: 11, color: C.muted }}>Regreso mismo día</div>
            </div>
            <input type="checkbox" checked={ruta.ida_vuelta} onChange={() => {}} style={{ accentColor: "#111111" }} />
          </div>
        </div>
        )}
        {/* Recorrido sorpresa (Originarios, 08-oct): cargan el camino pero no lo muestran
            hasta la largada. Antes directamente no lo cargaban. */}
        {!esFijo && !simple && (
          <div onClick={() => setR("ocultar_ruta", !ruta.ocultar_ruta)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: ruta.ocultar_ruta ? "#FFF3EE" : C.surface, border: `1px solid ${ruta.ocultar_ruta ? "#FBD38D" : C.border}`, cursor: "pointer", marginBottom: 14 }}>
            <span style={{ fontSize: 20 }}>🙈</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>Recorrido sorpresa</div>
              <div style={{ fontSize: 11, color: C.muted }}>Hasta la largada se muestra solo el punto de encuentro; el camino se revela cuando arrancás la rodada</div>
            </div>
            <input type="checkbox" checked={!!ruta.ocultar_ruta} onChange={() => {}} style={{ accentColor: "#111111" }} />
          </div>
        )}

        {/* separador — datos del evento */}
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, marginTop: 4, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          {esFijo ? "Datos del evento" : "Datos de la salida"}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de salida *</div>
            <input type="date" value={evento.fecha} onChange={e => setE("fecha", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
          {!simple && (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de regreso</div>
            <input type="date" value={evento.fecha_fin} onChange={e => setE("fecha_fin", e.target.value)}
              min={evento.fecha}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Hora de salida</div>
          <input type="time" value={evento.hora || ""} onChange={e => setE("hora", e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5, lineHeight: 1.4 }}>El check-in se habilita solo y automáticamente 4hs antes de esta hora.</div>
        </div>

        {!simple && (<>
        <div style={{ marginBottom: 14, marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Cupos</div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={evento.cupos_ilimitado}
                onChange={e => setE("cupos_ilimitado", e.target.checked)}
                style={{ accentColor: "#111111" }} />
              Sin límite
            </label>
          </div>
          {!evento.cupos_ilimitado && (
            <input type="number" value={evento.cupos} onChange={e => setE("cupos", e.target.value)}
              placeholder="Máximo de riders"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          )}
        </div>

        {/* Precio y cobro — faltaban al crear (solo estaban en Editar): confundía */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{esFijo ? "Precio de entrada" : "Precio por rider"}</div>
          <input type="number" value={evento.precio === "0" ? "" : evento.precio} onChange={e => setE("precio", e.target.value || "0")}
            placeholder="0 = gratis"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
        </div>
        {Number(evento.precio) > 0 && (
          <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1F8347", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>💳 Cobro de la inscripción</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 4 }}>
              Conectá tu Mercado Pago y las inscripciones se pagan en la app: la plata va directo a tu cuenta y cada pago se confirma solo — sin comprobantes ni tildes a mano.
            </div>
            <MercadoPagoConnect />
            <div style={{ fontSize: 11, color: C.muted, margin: "12px 0 8px", fontWeight: 700 }}>¿Preferís transferencia o link propio? Dejá tus datos (opcional):</div>
            <Input label="Link de pago (Mercado Pago)" value={evento.pago_link} onChange={e => setE("pago_link", e.target.value)} placeholder="https://mpago.la/..." />
            <Input label="Datos de pago (alias, CBU, instrucciones)" value={evento.pago_info} onChange={e => setE("pago_info", e.target.value)} placeholder="Ej: Alias: mi.alias.mp" />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Inscripción</div>
          {[["abierta", "🔓 Abierta — cualquiera puede anotarse"], ["aprobacion", "✋ Con aprobación — vos aceptás cada solicitud"]].map(([val, label]) => (
            <div key={val} onClick={() => setE("inscripcion", val)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
              background: evento.inscripcion === val ? "#FFF3EE" : C.surface,
              border: `1px solid ${evento.inscripcion === val ? "#111111" : C.border}`,
            }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", border: `2px solid ${evento.inscripcion === val ? "#111111" : C.faint}`, background: evento.inscripcion === val ? "#111111" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {evento.inscripcion === val && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.card }} />}
              </div>
              <span style={{ fontSize: 13, color: C.text }}>{label}</span>
            </div>
          ))}
        </div>
        </>)}

        <div style={{ marginBottom: 14, marginTop: simple ? 10 : 0 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>WhatsApp de contacto (opcional)</div>
          <input type="tel" value={evento.telefono} onChange={e => setE("telefono", e.target.value)}
            placeholder="Ej: +54 9 11 1234 5678"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
        </div>

        {/* Caravana: lo mínimo alcanza; el resto se abre a pedido y también se puede completar después desde Editar */}
        {rodadaHacia && (
          <button type="button" onClick={() => setMasOpciones(v => !v)}
            style={{ width: "100%", marginBottom: 14, padding: "11px 14px", borderRadius: 12, border: `1.5px dashed ${C.border}`, background: C.surface, fontSize: 13, fontWeight: 700, color: C.text, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚙️</span>
            <span style={{ flex: 1 }}>
              {masOpciones ? "Ocultar opciones avanzadas" : "Más opciones"}
              <div style={{ fontSize: 11, fontWeight: 500, color: C.muted, marginTop: 2 }}>{masOpciones ? "Volver a lo mínimo" : "Km, dificultad, descripción, cupos, precio, inscripción, datos a pedir, deslinde, sorteo. Con lo de arriba ya alcanza para la caravana; esto lo podés completar después desde Editar."}</div>
            </span>
            <span style={{ color: C.muted }}>{masOpciones ? "▲" : "▼"}</span>
          </button>
        )}
        {!simple && (<>
        {/* ── Inscripción: qué pedir al anotarse + deslinde ── */}
        <div style={{ marginBottom: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Datos a pedir al anotarse</div>
          <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
            Los riders los completan con su ficha guardada — no recargan nada si ya la tienen. Vos ves la lista con todo.
          </div>
          {[
            ["nombre_completo", "Nombre y apellido"],
            ["dni", "DNI"],
            ["telefono", "Teléfono"],
            ["emergencia", "Contacto de emergencia"],
            ["condicion_medica", "Condición médica / medicación"],
            ["localidad", "Localidad (de dónde viene)"],
            ["direccion", "Dirección"],
          ].map(([k, label]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={!!inscConfig.campos[k]} onChange={e => setICampo(k, e.target.checked)} style={{ width: 18, height: 18, accentColor: "#111111" }} />
              <span style={{ fontSize: 13.5, color: C.text }}>{label}</span>
            </label>
          ))}

          {/* preguntas propias del evento */}
          {inscConfig.extras.map((ex, i) => (
            <div key={i} style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input value={ex.label} onChange={e => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))}
                placeholder="Tu pregunta (ej: ¿Te quedás a almorzar?)"
                style={{ flex: 1, minWidth: 0, padding: "9px 11px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box" }} />
              <select value={ex.tipo} onChange={e => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x) }))}
                style={{ padding: "9px 6px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.text, background: C.card, flexShrink: 0 }}>
                <option value="sino">Sí / No</option>
                <option value="texto">Texto</option>
                <option value="opciones">Opciones</option>
              </select>
              <button onClick={() => setInscConfig(c => ({ ...c, extras: c.extras.filter((_, j) => j !== i) }))}
                style={{ width: 30, height: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={13} strokeWidth={2.5} color={C.red} />
              </button>
            </div>
            {ex.tipo === "opciones" && (
              <EditorOpcionesCampo
                opciones={ex.opciones}
                onChange={ops => setInscConfig(c => ({ ...c, extras: c.extras.map((x, j) => j === i ? { ...x, opciones: ops } : x) }))}
              />
            )}
            </div>
          ))}
          <button onClick={() => setInscConfig(c => ({ ...c, extras: [...c.extras, { label: "", tipo: "sino" }] }))}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
            <Plus size={14} strokeWidth={2.5} /> Agregar pregunta propia
          </button>

          {/* deslinde de responsabilidad */}
          <div style={{ marginTop: 12 }}>
            {/* Sección aparte SOLO para eventos fijos: en las salidas los puntos de suma
                se tildan en cada punto del mapa (una sola lista, cero duplicación) */}
            {false && (<>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Puntos donde se suma gente (opcional)</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
              Para caravanas que levantan gente camino al evento (ej: Shell Paraná 07:30 → YPF Wilde 08:00). Cada uno elige desde qué punto se suma al anotarse, y vos ves cuántos van en cada parada.
            </div>
            {(inscConfig.puntos_suma || []).map((pt, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input value={pt.nombre || ""} onChange={e => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x) }))}
                  placeholder={`Punto ${i + 1} (ej: Shell Av. Paraná, CABA)`}
                  style={{ flex: 2, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box", minWidth: 0 }} />
                <input value={pt.hora || ""} onChange={e => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.map((x, j) => j === i ? { ...x, hora: e.target.value } : x) }))}
                  placeholder="07:30"
                  style={{ flex: 1, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box", minWidth: 0 }} />
                <button onClick={() => setInscConfig(c => ({ ...c, puntos_suma: c.puntos_suma.filter((_, j) => j !== i) }))}
                  style={{ width: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", flexShrink: 0, color: C.red, fontWeight: 700 }}>✕</button>
              </div>
            ))}
            <button onClick={() => setInscConfig(c => ({ ...c, puntos_suma: [...(c.puntos_suma || []), { nombre: "", hora: "" }] }))}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
              <Plus size={14} strokeWidth={2.5} /> Agregar punto de encuentro
            </button>
            </>)}
            <ColaboradoresEditor value={inscConfig.colaboradores || []} onChange={(v) => setInscConfig(c => ({ ...c, colaboradores: v }))} />
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Deslinde de responsabilidad</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>Todos los que se anoten lo firman electrónicamente (nombre + DNI). Si lo dejás vacío, se usa el modelo de Olbos según el tipo de evento.</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button type="button" onClick={() => setInscConfig(c => ({ ...c, deslinde: DESLINDE_MODELO_EVENTO }))}
                style={{ flex: 1, padding: "8px 6px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                Usar modelo · encuentro en predio
              </button>
              <button type="button" onClick={() => setInscConfig(c => ({ ...c, deslinde: DESLINDE_MODELO_SALIDA }))}
                style={{ flex: 1, padding: "8px 6px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                Usar modelo · salida / travesía
              </button>
            </div>
            <textarea value={inscConfig.deslinde} onChange={e => setInscConfig(c => ({ ...c, deslinde: e.target.value }))}
              placeholder="Vacío = modelo de Olbos. Si escribís el tuyo, queda registrada la firma con fecha, hora, nombre y DNI."
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>

          {/* control de acceso con entrada QR */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={!!inscConfig.entradas} onChange={e => setInscConfig(c => ({ ...c, entradas: e.target.checked }))}
              style={{ width: 18, height: 18, accentColor: "#111111", marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🎫 Control de acceso con entrada QR</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Cada inscripto recibe su entrada con QR y la escaneás en la puerta desde tu celu. En salidas pagas está activo siempre.</div>
            </div>
          </label>
        </div>

        {/* ── Sorteo en vivo (opcional) ── */}
        <div onClick={() => setTieneSorteo(v => !v)}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, marginBottom: 14, cursor: "pointer", background: tieneSorteo ? "#FFF3EE" : C.surface, border: `1px solid ${tieneSorteo ? "#FBD38D" : C.border}` }}>
          <span style={{ fontSize: 22 }}>🎁</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>¿Esta salida tiene sorteo?</div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>Sorteás premios en vivo al final, entre los presentes, desde el cel de cada uno.</div>
          </div>
          <input type="checkbox" checked={tieneSorteo} onChange={() => {}} style={{ width: 18, height: 18, accentColor: "#111111", flexShrink: 0 }} />
        </div>
        </>)}

        {!canSubmit && (
          <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#B07D1A", marginBottom: 4 }}>Faltan datos:</div>
            {[
              !ruta.nombre && (esFijo ? "Nombre del evento" : "Nombre de la salida"),
              ruta.puntos.length === 0 && !eventoPadre && "Dónde pasa (un punto en el mapa)",
              ruta.puntos.length < 2 && eventoPadre && "Falta tu punto de encuentro (de dónde salen)",
              !esFijo && !eventoPadre && ruta.puntos.length === 1 && "Al menos origen y destino en el mapa",
              !esFijo && !eventoPadre && !ruta.km && "Distancia en km",
              !esFijo && !eventoPadre && !ruta.diff && "Dificultad",
              !evento.fecha && (esFijo ? "Fecha" : "Fecha de salida"),
            ].filter(Boolean).map((m, i) => (
              <div key={i} style={{ fontSize: 12, color: "#B07D1A", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#B07D1A", flexShrink: 0 }} />
                {m}
              </div>
            ))}
          </div>
        )}

        <Btn full onClick={publicar} disabled={!canSubmit || publicando} style={{ opacity: canSubmit && !publicando ? 1 : 0.5 }}>
          {publicando ? "Publicando…" : esFijo ? "Publicar evento 📍" : eventoPadre ? "Publicar la rodada 🏍️" : "Publicar salida grupal 🏍️"}
        </Btn>
      </div>
    </div>
  );
};

// Intercepta el botón "atrás" de Android/navegador mientras `activo`: en vez de salir de la
// app (perdiendo lo que se está creando), llama a onBack. onBack devuelve false para "quedarse"
// (ej: el usuario canceló la confirmación) → se re-arma la guarda.
function useBackGuard(activo, onBack) {
  const cb = useRef(onBack);
  cb.current = onBack;
  useEffect(() => {
    if (!activo) return;
    window.history.pushState({ olbosGuard: true }, "");
    const handler = () => {
      // Re-armar la guarda SIEMPRE y de inmediato (antes de cualquier confirm), así
      // nunca quedamos sin red — el back se puede apretar las veces que sea.
      window.history.pushState({ olbosGuard: true }, "");
      if (cb.current) cb.current();
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [activo]);
}

// Parser de fecha flexible para flyers: "15 de junio", "15/06/2026", "2026-06-15".
// Si no trae año, asume el actual; si esa fecha ya pasó, usa el próximo.
const parsearFechaFlyer = (txtFecha, txtHora) => {
  if (!txtFecha) return "";
  const txt = String(txtFecha).toLowerCase().trim();
  const meses = {
    enero: "01", febrero: "02", marzo: "03", abril: "04", mayo: "05", junio: "06",
    julio: "07", agosto: "08", septiembre: "09", setiembre: "09", octubre: "10", noviembre: "11", diciembre: "12",
  };
  let y, m, d;
  const iso = txt.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) { y = iso[1]; m = iso[2].padStart(2, "0"); d = iso[3].padStart(2, "0"); }
  else {
    const dmy = txt.match(/(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?/);
    if (dmy) {
      d = dmy[1].padStart(2, "0");
      m = dmy[2].padStart(2, "0");
      y = dmy[3] ? (dmy[3].length === 2 ? "20" + dmy[3] : dmy[3]) : null;
    } else {
      const palabra = txt.match(/(\d{1,2})\s*(?:de\s+)?([a-záéíóú]+)(?:\s*(?:de\s+)?(\d{4}))?/);
      if (palabra && meses[palabra[2]]) {
        d = palabra[1].padStart(2, "0");
        m = meses[palabra[2]];
        y = palabra[3] || null;
      }
    }
  }
  if (!m || !d) return "";
  if (!y) {
    const hoy = new Date();
    const hoyInicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const tentativa = new Date(`${hoy.getFullYear()}-${m}-${d}T12:00:00`);
    y = (tentativa < hoyInicio) ? hoy.getFullYear() + 1 : hoy.getFullYear();
  }
  let hh = "10", mm = "00";
  if (txtHora) {
    const h = String(txtHora).match(/(\d{1,2})[:\.h]?\s*(\d{0,2})?/);
    if (h) { hh = h[1].padStart(2, "0"); mm = (h[2] || "00").padStart(2, "0"); }
  }
  return `${y}-${m}-${d}T${hh}:${mm}`;
};

// La portada (foto_url) de un evento puede ser foto o video (Cloudinary).
const esVideoPortada = (u) => /\/video\/upload\//.test(u || "") || /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(u || "");
// Thumbnail jpg del primer frame de un video de Cloudinary (para OG y posters).
const posterDeVideo = (u) => (u || "").replace("/video/upload/", "/video/upload/so_0/").replace(/\.[a-z0-9]+(\?.*)?$/i, ".jpg");

// ─── Tope de duración de video (como Instagram) ──────────────────────────────
// 90 segundos: más largo tarda una eternidad en subir con datos y casi nadie lo
// mira entero. Se avisa con buena onda y se pide recortarlo antes de subir.
const MAX_SEG_VIDEO = 90;
const duracionVideo = (file) => new Promise((resolve) => {
  try {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); resolve(isFinite(d) ? d : 0); };
    v.onerror = () => resolve(0); // si no se puede leer, dejamos pasar
    v.src = URL.createObjectURL(file);
  } catch (e) { resolve(0); }
});
// true = se puede subir · false = muy largo (ya avisó)
const videoDentroDelLimite = async (file) => {
  if (!file || !(file.type || "").startsWith("video")) return true;
  const seg = await duracionVideo(file);
  if (seg > MAX_SEG_VIDEO + 2) { // margen de 2s por redondeos
    const m = Math.floor(seg / 60), s = Math.round(seg % 60);
    alert(`Tu video dura ${m > 0 ? `${m} min ${s} s` : `${s} segundos`} y el máximo son 90 segundos.\n\nEs el mismo límite que usa Instagram: los videos más largos casi nadie los mira completos, y tardan un montón en subir.\n\nRecortalo desde la galería de tu celu (dejá lo mejor) y subilo de nuevo 🏍️`);
    return false;
  }
  return true;
};

// ─── Achicar videos pesados en el teléfono (pedido de Matías N4, 20-oct-2026) ──
// Cloudinary (plan gratis) acepta hasta 100 MB por video y un video corto en 4K ya
// pasa. En vez de rechazarlo, se vuelve a grabar en el teléfono a 1080p y ~5 Mbps:
// el video se reproduce sin sonido por los parlantes, se dibuja en un canvas a la
// medida nueva y MediaRecorder graba el canvas + el audio original (por WebAudio).
// Tarda lo que dura el video (máximo 90 s). Si el teléfono no puede, se rechaza.
const TOPE_VIDEO_MB = 95;
const achicarVideo = (file, onProgreso) => new Promise((resolve, reject) => {
  let url = null, ctxAudio = null, terminado = false;
  const limpiar = () => {
    try { if (url) URL.revokeObjectURL(url); } catch (e) { /* */ }
    try { if (ctxAudio) ctxAudio.close(); } catch (e) { /* */ }
  };
  const fallar = (msg) => { if (terminado) return; terminado = true; limpiar(); reject(new Error(msg)); };
  try {
    if (typeof MediaRecorder === "undefined") { fallar("sin_mediarecorder"); return; }
    const v = document.createElement("video");
    url = URL.createObjectURL(file);
    v.src = url; v.playsInline = true; v.setAttribute("playsinline", ""); v.preload = "auto";
    v.onerror = () => fallar("no_se_pudo_leer");
    v.onloadedmetadata = async () => {
      try {
        const W = v.videoWidth || 1920, H = v.videoHeight || 1080;
        const esc = Math.min(1, 1080 / Math.min(W, H));
        const cw = Math.round((W * esc) / 2) * 2, ch = Math.round((H * esc) / 2) * 2;
        const canvas = document.createElement("canvas");
        canvas.width = cw; canvas.height = ch;
        const g = canvas.getContext("2d");
        const pistas = [...canvas.captureStream(30).getVideoTracks()];
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) {
            ctxAudio = new AC();
            const fuente = ctxAudio.createMediaElementSource(v);
            const destino = ctxAudio.createMediaStreamDestination();
            fuente.connect(destino); // no va a los parlantes: se graba en silencio
            pistas.push(...destino.stream.getAudioTracks());
            if (ctxAudio.state === "suspended") await ctxAudio.resume().catch(() => {});
          }
        } catch (e) { /* sin audio: el video sale mudo antes que no salir */ }
        const tipos = ["video/mp4;codecs=avc1.640028,mp4a.40.2", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
        const mime = tipos.find(x => { try { return MediaRecorder.isTypeSupported(x); } catch (e) { return false; } }) || "";
        const rec = new MediaRecorder(new MediaStream(pistas), { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 });
        const trozos = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) trozos.push(e.data); };
        rec.onstop = () => {
          if (terminado) return;
          terminado = true;
          const tipo = (rec.mimeType || mime || "video/webm").split(";")[0];
          const blob = new Blob(trozos, { type: tipo });
          limpiar();
          if (!blob.size) { reject(new Error("vacio")); return; }
          const ext = tipo.includes("mp4") ? "mp4" : "webm";
          resolve(new File([blob], (file.name || "video").replace(/\.[^.]+$/, "") + "-olbos." + ext, { type: tipo }));
        };
        const dibujar = () => {
          if (terminado || v.ended) return;
          try { g.drawImage(v, 0, 0, cw, ch); } catch (e) { /* */ }
          if (v.duration && onProgreso) onProgreso(Math.min(99, Math.round((v.currentTime / v.duration) * 100)));
          if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(dibujar); else requestAnimationFrame(dibujar);
        };
        v.onended = () => { try { g.drawImage(v, 0, 0, cw, ch); } catch (e) { /* */ } setTimeout(() => { try { rec.state !== "inactive" && rec.stop(); } catch (e) { fallar("stop"); } }, 250); };
        rec.start(1000);
        await v.play();
        dibujar();
      } catch (e) { fallar("no_se_pudo_reproducir"); }
    };
  } catch (e) { fallar("error"); }
});
// Si el video pasa el tope, lo achica; si ya entraba, lo devuelve como está.
// Devuelve null si no se pudo (el que llama avisa).
const videoParaSubir = async (file, onProgreso) => {
  if (!file || file.size <= TOPE_VIDEO_MB * 1024 * 1024) return file;
  try {
    const chico = await achicarVideo(file, onProgreso);
    registrarUso("video_achicado", { mb_antes: Math.round(file.size / 1048576), mb_despues: Math.round(chico.size / 1048576), tipo: chico.type });
    return chico.size <= TOPE_VIDEO_MB * 1024 * 1024 ? chico : null;
  } catch (e) {
    registrarUso("video_achicado_fallo", { mb: Math.round(file.size / 1048576), motivo: e.message });
    return null;
  }
};
// Un video grabado en webm se pide a Cloudinary como mp4 (iPhone no reproduce webm)
const urlVideoMp4 = (u) => /\.webm(\?|$)/i.test(u || "") ? u.replace("/video/upload/", "/video/upload/f_mp4/").replace(/\.webm(\?.*)?$/i, ".mp4") : u;

// Clasifica un flyer: rodada/salida (habilita check-in y rodada) vs evento de lugar.
const flyerEsSalida = (data) => {
  const txt = `${data?.nombre || ""} ${data?.descripcion || ""}`.toLowerCase();
  return /\b(rodada|rodadas|salida|salidas|paseo|caravana|ruta|traves[ií]a|rodadita)\b/.test(txt);
};

const CrearEventoModal = ({ onClose, onCrear, session, grupoIdPreseleccionado, grupoNombrePreseleccionado, flyerData, rodadaHacia }) => {
  // Creador UNIFICADO: se entra directo al wizard con mapa; la clasificación
  // evento-fijo vs salida la decide la cantidad de puntos (1 = evento, 2+ = salida).
  // El flujo del flyer con IA sigue existiendo y conmuta solo al form clásico.
  const [tipoEvento, setTipoEvento] = useState("salida");
  const [step, setStep] = useState(0);
  // Botón atrás → cerrar el creador con confirmación, no salir de la app
  useBackGuard(true, () => {
    if (window.confirm("¿Salir del creador? Vas a perder lo que estás armando.")) onClose();
  });
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(grupoIdPreseleccionado || ""); // "" = público
  // Eventos de grupo: 'grupo' (solo miembros) | 'publica' (agenda general) — la Mega Rodada quedó
  // presa del grupo por no preguntar esto (13-ago). Una caravana hacia un evento nace PÚBLICA:
  // si no, no aparece en la ficha del evento ni cuenta entre las rodadas que llegan.
  const [visibilidadGrupo, setVisibilidadGrupo] = useState(rodadaHacia ? "publica" : "grupo");
  const [misGrupos, setMisGrupos] = useState([]);
  // Con grupo preseleccionado el paso previo QUEDA (pregunta la visibilidad); solo desde un
  // flyer leído por la IA se saltea. Antes se salteaba con grupo y el evento nacía privado.
  const [stepPrevio, setStepPrevio] = useState(!flyerData);
  const [publicandoEv, setPublicandoEv] = useState(false);
  // Publicar el evento a nombre de una cuenta delegada (ej. @olbos): el evento queda
  // creado por esa cuenta y el que lo cargó entra como co-organizador para poder editarlo
  const [cuentasDelegadasEv, setCuentasDelegadasEv] = useState([]);
  const [publicarComoEv, setPublicarComoEv] = useState(null);
  useEffect(() => {
    supabase.rpc("mis_cuentas_delegadas").then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setCuentasDelegadasEv(Array.isArray(d) ? d.filter(c => c.puede_eventos) : []);
    }, () => setCuentasDelegadasEv([]));
  }, []);
  const [form, setForm] = useState({
    titulo: "", fecha: "", fecha_fin: "", descripcion: "", cupos: "", cupos_ilimitado: false,
    precio: "", inscripcion: "abierta", visibilidad: "todos", flyer: null,
    pago_link: "", pago_info: "",
    medias: [], // [{ url, type: "image"|"video" }] — múltiples fotos/videos
    publicar_en_feed: false, // publicar también un post en el feed cuando se crea el evento
    lugar: "", lugar_lat: null, lugar_lng: null, precio_entrada: "", telefono: "",
    desde: "", desde_lat: null, desde_lng: null,
    hasta: "", hasta_lat: null, hasta_lng: null,
    km: "", km_calculado: null, idaVuelta: false,
    puntos: [],
    cc_min: "", camino: "Asfalto", pctOffroad: 0,
    tiposOffroad: [], genero: "Mixto", apoyo_ruta: false, equipo: "",
    paradas: [],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Estado del upload de flyer desde el flujo "Crear"
  const [extrayendoFlyer, setExtrayendoFlyer] = useState(false);
  const flyerInputRef = useRef(null);

  // Aplica los datos de un flyer al formulario (desde el asistente o desde "Crear").
  // imagenUrl (opcional) se usa como foto del evento. Se queda SIEMPRE en el form de
  // CrearEventoModal (tipoEvento "evento"), pero marca el tipo del registro: si es una
  // rodada/salida lo guarda como "salida" para que tenga check-in y rodada en vivo.
  // "11hs" / "11:00" / "11.30" / "11 AM" → "HH:MM" para el input type=time
  const normalizarHora = (h) => {
    if (!h) return "";
    const m = String(h).match(/(\d{1,2})[:.h](\d{2})/i) || String(h).match(/(\d{1,2})\s*(?:hs?|am|pm)?/i);
    if (!m) return "";
    let hh = parseInt(m[1], 10);
    const mm = (m[2] && /^\d{2}$/.test(m[2])) ? m[2] : "00";
    if (/pm/i.test(String(h)) && hh < 12) hh += 12;
    if (/am/i.test(String(h)) && hh === 12) hh = 0;
    if (hh < 0 || hh > 23) return "";
    return `${String(hh).padStart(2, "0")}:${mm}`;
  };
  const aplicarFlyer = (data, imagenUrl) => {
    if (!data) return;
    // "11 y 12 de octubre" / "10 al 11 de octubre": primer día = fecha, segundo = fecha_fin.
    // Antes caía en meses["y"] y la fecha quedaba vacía sin aviso.
    const rango = String(data.fecha || "").match(/(\d{1,2})\s*(?:y|al|a|-|–|hasta)\s*(\d{1,2})\s*(?:de\s+)?([a-záéíóú]+)(?:\s*(?:de\s+)?(\d{4}))?/i);
    const fechaParseada = rango
      ? parsearFechaFlyer(`${rango[1]} de ${rango[3]}${rango[4] ? ` de ${rango[4]}` : ""}`, data.hora)
      : parsearFechaFlyer(data.fecha, data.hora);
    const fechaFinParseada = rango ? parsearFechaFlyer(`${rango[2]} de ${rango[3]}${rango[4] ? ` de ${rango[4]}` : ""}`, data.hora) : "";
    const desc = [];
    if (data.descripcion) desc.push(data.descripcion);
    if (data.organizador) desc.push(`Organiza: ${data.organizador}`);
    if (data.instagram) desc.push(`Instagram: ${data.instagram}`);
    const esSalida = flyerEsSalida(data);
    const ubic = [data.lugar, data.ciudad].filter(Boolean).join(", ");
    setForm(f => ({
      ...f,
      titulo: data.nombre || "",
      // El input es type="date": va YYYY-MM-DD (con la hora pegada quedaba vacío y no se podía verificar)
      fecha: String(fechaParseada || "").slice(0, 10),
      fecha_fin: String(fechaFinParseada || "").slice(0, 10) || f.fecha_fin,
      hora: normalizarHora(data.hora) || (fechaParseada && fechaParseada.includes("T") ? fechaParseada.slice(11, 16) : ""),
      lugar: ubic,
      desde: esSalida ? (data.lugar || ubic) : f.desde,
      descripcion: desc.join("\n\n"),
      precio: data.precio || "",
      precio_entrada: data.precio || "",
      telefono: data.whatsapp || "",
      tipo: esSalida ? "salida" : "evento",
      flyer: imagenUrl || f.flyer,
    }));
    setTipoEvento("evento"); // siempre el form de evento; el tipo del registro lo da form.tipo
    setStep(1);
  };

  // Subir un flyer en el flujo "Crear": lo sube a Cloudinary (foto del evento) y lo
  // manda a la IA para extraer los datos y pre-cargar el formulario.
  const subirYExtraerFlyer = async (file) => {
    if (!file || extrayendoFlyer) return;
    setExtrayendoFlyer(true);
    // Video como portada: se sube directo, sin lectura de IA (la IA solo lee imágenes)
    if ((file.type || "").startsWith("video")) {
      try {
        const fd = new FormData();
        fd.append("file", await achicarImagen(file));
        fd.append("upload_preset", "olbos_uploads");
        const up = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload", { method: "POST", body: fd });
        const upData = await up.json();
        if (upData.secure_url) {
          setForm(f => ({ ...f, flyer: upData.secure_url }));
          setStep(1);
        } else alert("No se pudo subir el video. Probá de nuevo.");
      } catch (e) { alert("No se pudo subir el video. Probá de nuevo."); }
      setExtrayendoFlyer(false);
      return;
    }
    try {
      // 1) Subir a Cloudinary → foto del evento
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      fd.append("cloud_name", "dhwhk48wb");
      const up = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const upData = await up.json();
      const imagenUrl = upData.secure_url || null;
      // 2) Base64 para la IA — del archivo ACHICADO (≤ 1600 px, jpg): una foto de 6 MB o un
      // HEIC de iPhone en crudo fallaban con "No pude leer los datos del flyer"
      const chico = await achicarImagen(file, 1600);
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = e => res(e.target.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(chico);
      });
      const mediaTypeIA = (chico.type || file.type || "image/jpeg");
      const res = await fetch(API_BASE + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: "Sos un asistente que extrae datos de flyers de eventos de moto. Respondés ÚNICAMENTE con el JSON pedido, sin texto ni backticks.",
          messages: [{ role: "user", content: [
            { type: "image", source: { type: "base64", media_type: mediaTypeIA, data: base64 } },
            { type: "text", text: `Extraé la info del flyer y respondé EXACTAMENTE con este JSON: {"esFlyer":true,"nombre":"","fecha":"","hora":"","lugar":"","ciudad":"","descripcion":"","precio":"","organizador":"","instagram":"","whatsapp":""}. Campos ausentes como "". Si no es un flyer de evento, respondé {"esFlyer":false}.` },
          ] }],
        }),
      });
      const data = await res.json();
      const flyer = extraerFlyer(data.content?.[0]?.text || "");
      if (flyer && flyer.esFlyer) {
        aplicarFlyer(flyer, imagenUrl);
      } else {
        // No se pudo leer: igual usar la imagen como foto y seguir a mano
        setForm(f => ({ ...f, flyer: imagenUrl }));
        setStep(1);
        alert("No pude leer los datos del flyer, pero lo cargué como foto. Completá los datos a mano.");
      }
    } catch (e) {
      alert("No se pudo procesar el flyer. Probá de nuevo o cargá los datos a mano.");
    }
    setExtrayendoFlyer(false);
  };

  // Prellenar form desde datos del flyer detectado por el asistente (prop flyerData)
  useEffect(() => {
    if (flyerData) aplicarFlyer(flyerData, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyerData]);

  useEffect(() => {
    if (!session) { setStepPrevio(false); return; }
    // Con grupo preseleccionado el step previo QUEDA: pregunta la visibilidad
    // (antes se salteaba y el evento nacía privado sin que nadie lo eligiera)
    if (grupoIdPreseleccionado) return;
    (async () => {
      const { data: raw } = await supabase.rpc("get_mis_grupos");
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMisGrupos(data || []);
      // Si no tengo grupos, saltear el step previo
      if (!data || data.length === 0) setStepPrevio(false);
    })();
  }, [session, grupoIdPreseleccionado]);

  // Wrapper de onCrear para anexar el grupo_id seleccionado.
  // Si el evento es de un grupo pero se eligió "pública", va SIN grupo_id: sale en
  // la agenda general y cualquiera puede anotarse.
  // Invitados para el evento privado: se buscan por @usuario y quedan como chips
  const [invitados, setInvitados] = useState([]);
  const [buscaInv, setBuscaInv] = useState("");
  const [resultInv, setResultInv] = useState([]);
  const buscarInvitados = async (q) => {
    setBuscaInv(q);
    if (q.trim().length < 2) { setResultInv([]); return; }
    const { data } = await supabase.from("perfiles")
      .select("id, username, avatar_url")
      .ilike("username", `%${q.trim()}%`)
      .limit(8);
    const yaIds = new Set(invitados.map(i => i.id));
    setResultInv((data || []).filter(p => !yaIds.has(p.id)));
  };

  const esModoInvitados = grupoSeleccionado === "__invitados__";
  const crearConGrupo = (formCompletoOriginal) => {
    // Si la rodada se está creando HACIA un evento, ese vínculo tiene que viajar sí o sí.
    // Por el camino del flyer se perdía: al leerlo, la app cambia al formulario genérico
    // de evento y ahí nadie llevaba el evento_padre_id, así que la rodada quedaba suelta
    // en la agenda y no aparecía entre las rodadas vinculadas del evento. Cargándola a
    // mano sí andaba, porque ese camino no pasa por el formulario genérico. (Reporte de
    // Matías, justo el caso de las caravanas al BikerFest.)
    const formCompleto = {
      ...formCompletoOriginal,
      evento_padre_id: formCompletoOriginal.evento_padre_id || rodadaHacia?.id || null,
      publicar_como: publicarComoEv || null,
    };
    if (esModoInvitados) {
      // Privado con lista: sin grupo, sin agenda pública, sin feed
      return onCrear({ ...formCompleto, grupo_id: null, visibilidad_tipo: "invitados", invitados_ids: invitados.map(u => u.id), publicar_en_feed: false });
    }
    const grupoFinal = visibilidadGrupo === "publica" ? null : (grupoSeleccionado || null);
    return onCrear({ ...formCompleto, grupo_id: grupoFinal }); // devuelve {ok} para que el botón espere
  };

  // Step previo: elegir dónde se publica. Se muestra SIEMPRE (antes solo si
  // tenías grupos): la opción "solo invitados" tiene que estar para cualquiera.
  if (stepPrevio) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 400,
        background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{
          width: "100%", maxWidth: 430,
          background: C.card, borderRadius: "20px 20px 0 0",
          maxHeight: "92vh", overflowY: "auto",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
        }}>
          <div style={{
            padding: "16px 20px 14px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.text, fontSize: 14, cursor: "pointer" }}>Cancelar</button>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>{grupoIdPreseleccionado ? "¿Quién puede verla?" : "¿Para qué grupo?"}</div>
            <div style={{ width: 60 }} />
          </div>

          <div style={{ padding: "20px 20px 30px" }}>
            <p style={{ fontSize: 13.5, color: C.muted, marginBottom: 16, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
              {grupoIdPreseleccionado ? "La estás creando desde tu grupo. Elegí quién la puede ver." : "Elegí dónde se publica este evento."}
            </p>

            {cuentasDelegadasEv.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Publicar como</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[{ id: null, username: "mi cuenta" }, ...cuentasDelegadasEv].map(c => (
                    <button key={c.id || "yo"} type="button" onClick={() => setPublicarComoEv(c.id)}
                      style={{ padding: "7px 12px", borderRadius: 100, border: `1.5px solid ${publicarComoEv === c.id ? "#111111" : C.border}`, background: publicarComoEv === c.id ? "#111111" : C.card, color: publicarComoEv === c.id ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                      {c.id ? "@" + c.username : c.username}
                    </button>
                  ))}
                </div>
                {publicarComoEv && <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>El evento sale a nombre de esa cuenta y vos quedás como co-organizador: lo podés editar y ver los anotados.</div>}
              </div>
            )}
            {!grupoIdPreseleccionado && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
              <label style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "12px 14px", borderRadius: 12,
                border: `1.5px solid ${grupoSeleccionado === "" ? "#111111" : C.border}`,
                cursor: "pointer",
                background: grupoSeleccionado === "" ? C.surface : C.card,
              }}>
                <input type="radio" name="grupo" checked={grupoSeleccionado === ""} onChange={() => setGrupoSeleccionado("")}
                  style={{ accentColor: "#111111" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>Evento público</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4, letterSpacing: "-0.005em" }}>Cualquier rider puede verlo y sumarse</div>
                </div>
              </label>

              {/* Privado con lista: para 3 amigos sin armar un grupo ni salir en la agenda */}
              <label style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "12px 14px", borderRadius: 12,
                border: `1.5px solid ${esModoInvitados ? "#111111" : C.border}`,
                cursor: "pointer",
                background: esModoInvitados ? C.surface : C.card,
              }}>
                <input type="radio" name="grupo" checked={esModoInvitados} onChange={() => setGrupoSeleccionado("__invitados__")}
                  style={{ accentColor: "#111111" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>🔒 Solo invitados</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4, letterSpacing: "-0.005em" }}>No sale en la agenda ni en el feed: lo ven solo las personas que elijas</div>
                </div>
              </label>

              {misGrupos.map(g => (
                <label key={g.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 14px", borderRadius: 12,
                  border: `1.5px solid ${grupoSeleccionado === g.id ? "#111111" : C.border}`,
                  cursor: "pointer",
                  background: grupoSeleccionado === g.id ? C.surface : C.card,
                }}>
                  <input type="radio" name="grupo" checked={grupoSeleccionado === g.id} onChange={() => setGrupoSeleccionado(g.id)}
                    style={{ accentColor: "#111111" }} />
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: g.foto_url ? `url(${g.foto_url}) center/cover` : "linear-gradient(135deg, #111111 0%, #4a4a4a 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                    flexShrink: 0,
                  }}>
                    {!g.foto_url && <Users size={16} strokeWidth={1.5} color="#fff" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>{g.nombre}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, letterSpacing: "-0.005em" }}>Del grupo — abajo elegís si es solo interna o pública</div>
                  </div>
                </label>
              ))}
            </div>
            )}

            {/* Con "solo invitados": elegir a quién. Se puede sumar gente después
                desde el detalle del evento, así que arrancar con 1 o 2 está bien. */}
            {esModoInvitados && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 8, letterSpacing: "-0.01em" }}>¿A quién invitás?</div>
                {invitados.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {invitados.map(u => (
                      <span key={u.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 100, background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 600 }}>
                        @{u.username}
                        <button onClick={() => setInvitados(prev => prev.filter(x => x.id !== u.id))}
                          style={{ background: "none", border: "none", color: "rgba(255,255,255,.7)", cursor: "pointer", padding: 0, display: "flex" }}>
                          <X size={12} strokeWidth={2.5} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input value={buscaInv} onChange={e => buscarInvitados(e.target.value)}
                  placeholder="Buscá por @usuario…"
                  style={{ width: "100%", padding: "11px 13px", borderRadius: 11, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", background: C.surface }} />
                {resultInv.length > 0 && (
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 11, marginTop: 6, overflow: "hidden", background: C.card }}>
                    {resultInv.map(p => (
                      <button key={p.id} onClick={() => { setInvitados(prev => [...prev, p]); setBuscaInv(""); setResultInv([]); }}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", background: "none", border: "none", borderBottom: "1px solid #f1ece6", cursor: "pointer", textAlign: "left" }}>
                        <img loading="lazy" decoding="async" src={p.avatar_url || avatarInicial(p.username)} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: C.text }}>@{p.username}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                  Solo vos podés invitar. Después podés sumar o sacar gente desde el evento.
                </div>
              </div>
            )}

            {/* Visibilidad: la pregunta que faltaba — un evento de grupo puede ser
                interno o PÚBLICO (sale en la agenda y se anota cualquiera) */}
            {!esModoInvitados && (grupoSeleccionado || grupoIdPreseleccionado) ? (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 8, letterSpacing: "-0.01em" }}>¿Quién puede verla y anotarse?</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${visibilidadGrupo === "grupo" ? "#111111" : C.border}`, cursor: "pointer", background: visibilidadGrupo === "grupo" ? C.surface : C.card }}>
                    <input type="radio" name="visibilidad" checked={visibilidadGrupo === "grupo"} onChange={() => setVisibilidadGrupo("grupo")} style={{ accentColor: "#111111" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>🔒 Solo el grupo</div>
                      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Nadie fuera del grupo la ve ni se puede anotar</div>
                    </div>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${visibilidadGrupo === "publica" ? "#111111" : C.border}`, cursor: "pointer", background: visibilidadGrupo === "publica" ? C.surface : C.card }}>
                    <input type="radio" name="visibilidad" checked={visibilidadGrupo === "publica"} onChange={() => setVisibilidadGrupo("publica")} style={{ accentColor: "#111111" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.text, letterSpacing: "-0.01em" }}>🌍 Pública</div>
                      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Sale en la agenda de Olbos y CUALQUIERA puede anotarse con el link. Ideal para rodadas abiertas.</div>
                    </div>
                  </label>
                </div>
              </div>
            ) : null}

            <button onClick={() => setStepPrevio(false)} style={{
              width: "100%", padding: "13px 0", borderRadius: 12,
              background: C.accent, color: "#fff",
              border: "none", cursor: "pointer",
              fontSize: 14.5, fontWeight: 500, letterSpacing: "-0.01em",
            }}>
              Continuar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (tipoEvento === "salida") return (
    <>
    {/* input del flyer disponible también en el wizard unificado */}
    <input ref={flyerInputRef} type="file" accept="image/*,video/*" style={{ display: "none" }}
      onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subirYExtraerFlyer(f); }} />
    <CrearSalidaModal
      onClose={onClose}
      session={session}
      rodadaHacia={rodadaHacia}
      onFlyer={() => !extrayendoFlyer && flyerInputRef.current?.click()}
      extrayendoFlyer={extrayendoFlyer}
      onCrear={({ ruta, evento, inscConfig, tieneSorteo, foto, eventoPadreId }) => {
        const salida = ruta.puntos.find(p => p.tipo === "salida") || ruta.puntos[0];
        const destino = ruta.puntos.find(p => p.tipo === "destino") || ruta.puntos[ruta.puntos.length - 1];
        // UN punto = evento en lugar fijo; 2+ = salida con recorrido; con evento padre, nunca fijo
        const esFijo = !eventoPadreId && (ruta.puntos || []).length === 1;
        const p0 = ruta.puntos && ruta.puntos[0];
        // Puntos de suma: salen de los puntos del mapa tildados con "acá se suma gente"
        const sumaPts = (ruta.puntos || []).filter(p => p.suma && p.tipo !== "fin" && p.tipo !== "destino").map(p => ({ nombre: p.nombre, hora: p.hora_suma || "" }));
        try { localStorage.removeItem("olbos_draft_salida"); } catch (e) { /* publicada: chau borrador */ }
        return crearConGrupo({
          tipo: esFijo ? "evento" : "salida",
          ...(esFijo ? { lugar: p0?.nombre || "", lugar_lat: p0?.lat || null, lugar_lng: p0?.lng || null, precio_entrada: evento.precio || "0" } : {}),
          evento_padre_id: eventoPadreId || null,
          flyer: foto || null,
          inscripcion_config: { ...(inscConfig || {}), ...(sumaPts.length > 0 ? { puntos_suma: sumaPts } : {}), ...(ruta.ocultar_ruta ? { ocultar_ruta: true } : {}) },
          tiene_sorteo: !!tieneSorteo,
          titulo: ruta.nombre,
          descripcion: ruta.desc,
          desde: salida?.nombre || "",
          hasta: destino?.nombre || "",
          km: ruta.km,
          dificultad: ruta.diff,
          camino: ruta.pctOffroad > 50 ? "Off-road" : "Asfalto",
          equipo: ruta.equipo,
          apoyo_ruta: ruta.apoyo_ruta,
          ida_vuelta: ruta.ida_vuelta,
          puntos: ruta.puntos,
          fecha: evento.fecha,
          fecha_fin: evento.fecha_fin,
          hora: evento.hora || null,
          cupos: evento.cupos_ilimitado ? null : evento.cupos,
          cupos_ilimitado: evento.cupos_ilimitado,
          precio: evento.precio || "0",
          pago_link: evento.pago_link || "",
          pago_info: evento.pago_info || "",
          inscripcion: evento.inscripcion,
          telefono: evento.telefono,
          puntos: ruta.puntos,
        });
      }}
    />
    </>
  );

  // ── PASO 0: elegir tipo ────────────────────────────────────────────────────
  if (step === 0) return (
    <Modal title={grupoIdPreseleccionado && grupoNombrePreseleccionado ? `Evento para ${grupoNombrePreseleccionado}` : "Nuevo evento"} onClose={onClose}>
      {grupoIdPreseleccionado && (
        <div style={{ padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 14, fontSize: 12, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
          Este evento va a ser <strong style={{ color: C.text }}>privado del grupo</strong>. Solo los miembros van a verlo.
        </div>
      )}
      <p style={{ fontSize: 14, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
        ¿Qué tipo de evento querés crear?
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button onClick={() => { setTipoEvento("evento"); set("tipo", "evento"); setStep(1); }}
          style={{ padding: "18px 20px", borderRadius: 16, border: `1.5px solid ${C.border}`, background: C.card, textAlign: "left", cursor: "pointer", display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FFF3EE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>📍</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 4 }}>Evento en un lugar</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>Expo, concentración, feria, charla, track day. Puede durar varios días en el mismo lugar.</div>
          </div>
        </button>
        <button onClick={() => { setTipoEvento("salida"); set("tipo", "salida"); setStep(1); }}
          style={{ padding: "18px 20px", borderRadius: 16, border: `1.5px solid ${C.border}`, background: C.card, textAlign: "left", cursor: "pointer", display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>🛣️</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 4 }}>Salida grupal</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>Viaje en grupo con ruta, paradas, noches en el camino y destino final. Puede ser de 1 o varios días.</div>
          </div>
        </button>

        {/* Atajo: subir un flyer y autocompletar (detecta si es rodada o evento) */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0" }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>o más rápido</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
        <button onClick={() => !extrayendoFlyer && flyerInputRef.current?.click()} disabled={extrayendoFlyer}
          style={{ padding: "16px 20px", borderRadius: 16, border: `1.5px dashed ${C.accent}`, background: "#FFF8F5", textAlign: "left", cursor: extrayendoFlyer ? "default" : "pointer", display: "flex", gap: 14, alignItems: "center", opacity: extrayendoFlyer ? 0.7 : 1 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#FFE9DF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{extrayendoFlyer ? "⏳" : "📷"}</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.accent, marginBottom: 4 }}>{extrayendoFlyer ? "Procesando…" : "Subir un flyer o video"}</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{extrayendoFlyer ? "Un momento…" : "Si es imagen, autocompleto los datos con IA. Si es video, queda como portada del evento y cargás los datos a mano."}</div>
          </div>
        </button>
        <input ref={flyerInputRef} type="file" accept="image/*,video/*" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subirYExtraerFlyer(f); }} />
      </div>
    </Modal>
  );

  // ── PASO 1: info básica según tipo ────────────────────────────────────────
  if (step === 1) return (
    <Modal title={tipoEvento === "evento" ? "📍 Evento — Info básica" : "🛣️ Salida grupal — Info básica"} onClose={onClose}>
      {flyerData && (
        <div style={{ padding: "10px 12px", background: "#E8F5EC", border: "1px solid #B5DFC0", borderRadius: 10, marginBottom: 14, fontSize: 12, color: "#1F8347", lineHeight: 1.5, letterSpacing: "-0.005em" }}>
          ✨ <strong>Datos prellenados desde el flyer.</strong> Revisá y corregí lo que necesites antes de continuar.
        </div>
      )}
      <Input label="Nombre del evento" value={form.titulo} onChange={e => set("titulo", e.target.value)}
        placeholder={tipoEvento === "evento" ? "Ej: Expo Motos Gualeguaychú 2026" : "Ej: Vuelta por los 7 Lagos"} />

      {tipoEvento === "evento" ? (
        <>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Lugar del evento</div>
            <LugarPicker
              value={form.lugar_lat ? { nombre: form.lugar, lat: form.lugar_lat, lng: form.lugar_lng } : (form.lugar ? { nombre: form.lugar } : null)}
              onChange={val => setForm(f => ({ ...f, lugar: val.nombre, lugar_lat: val.lat || null, lugar_lng: val.lng || null }))}
              placeholder="Ej: Galpones Puerto Gualeguaychú"
              label=""
            />
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginTop: 12, marginBottom: 6 }}>¿Se hace en un lugar registrado? · opcional</div>
            <SelectorSedeNegocio
              valor={form.negocio_id || null}
              nombre={form.negocio_nombre || ""}
              onChange={(id, nom) => setForm(f => ({ ...f, negocio_id: id, negocio_nombre: nom }))}
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>Si lo vinculás, el evento aparece también en el perfil de ese lugar dentro de Servicios.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de inicio</div>
              <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de fin</div>
              <input type="date" value={form.fecha_fin} onChange={e => set("fecha_fin", e.target.value)}
                min={form.fecha}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
          </div>
          <Input label="Precio de entrada (dejá vacío si es gratis)" value={form.precio_entrada}
            onChange={e => set("precio_entrada", e.target.value)} placeholder="Ej: 5000" />
          {(Number(form.precio_entrada) > 0 || Number(form.precio) > 0) && (
            <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#F6FBF7", border: `1px solid ${C.green}33` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1F8347", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>💳 Cobro de la inscripción</div>
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 4 }}>
                Conectá tu Mercado Pago y las inscripciones se pagan en la app: la plata va directo a tu cuenta y cada pago se confirma solo — sin comprobantes ni tildes a mano.
              </div>
              <MercadoPagoConnect />
              <div style={{ fontSize: 11, color: C.muted, margin: "12px 0 8px", fontWeight: 700 }}>¿Preferís transferencia o link propio? Dejá tus datos (opcional):</div>
              <Input label="Link de pago (Mercado Pago)" value={form.pago_link} onChange={e => set("pago_link", e.target.value)} placeholder="https://mpago.la/..." />
              <Input label="Datos de pago (alias, CBU, instrucciones)" value={form.pago_info} onChange={e => set("pago_info", e.target.value)} placeholder="Ej: Alias: mi.alias.mp" />
            </div>
          )}
          <Input label="WhatsApp de contacto (opcional)" value={form.telefono}
            onChange={e => set("telefono", e.target.value)} placeholder="Ej: +54 9 11 1234 5678" />
        </>
      ) : (
        <>
          {/* Selector de puntos de la ruta */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Puntos de la ruta</div>
            <SelectorRuta
              puntos={form.puntos || []}
              onChange={(puntos, kmCalculado) => {
                const salida = puntos.find(p => p.tipo === "salida");
                const destino = puntos.find(p => p.tipo === "destino");
                const paradas = puntos.filter(p => !["salida","destino"].includes(p.tipo));
                setForm(f => ({
                  ...f,
                  puntos,
                  desde: salida?.nombre || f.desde,
                  desde_lat: salida?.lat || f.desde_lat,
                  desde_lng: salida?.lng || f.desde_lng,
                  hasta: destino?.nombre || f.hasta,
                  hasta_lat: destino?.lat || f.hasta_lat,
                  hasta_lng: destino?.lng || f.hasta_lng,
                  paradas: paradas.map(p => ({ nombre: p.nombre, tipo: p.tipo })),
                  km: kmCalculado ? (f.idaVuelta ? String(kmCalculado * 2) : String(kmCalculado)) : f.km,
                  km_calculado: kmCalculado || f.km_calculado,
                }));
              }}
            />
          </div>

          {/* Checkbox ida y vuelta + km */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>
                  Km totales
                  {form.km_calculado && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, color: C.text, fontSize: 10 }}>Google Maps</span>}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.muted, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.idaVuelta || false}
                    onChange={e => {
                      const idaVuelta = e.target.checked;
                      setForm(f => ({
                        ...f, idaVuelta,
                        km: f.km_calculado ? String(idaVuelta ? f.km_calculado * 2 : f.km_calculado) : f.km,
                      }));
                    }} style={{ accentColor: "#111111" }} />
                  Ida y vuelta
                </label>
              </div>
              <input type="number" value={form.km} onChange={e => set("km", e.target.value)}
                placeholder="Ej: 800"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", background: form.km_calculado ? `${C.green}14` : C.card }} />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="CC mínima" value={form.cc_min} onChange={e => set("cc_min", e.target.value)} placeholder="Ej: 300" />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de salida</div>
              <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha de regreso</div>
              <input type="date" value={form.fecha_fin} onChange={e => set("fecha_fin", e.target.value)}
                min={form.fecha}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
          </div>

          <div style={{ marginBottom: 4, marginTop: 10 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Hora de salida</div>
            <input type="time" value={form.hora || ""} onChange={e => set("hora", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5, lineHeight: 1.4 }}>El check-in se habilita automáticamente 4hs antes de esta hora.</div>
          </div>

          {/* paradas */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Paradas del viaje</div>
            {form.paradas.map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <select value={p.tipo} onChange={e => {
                  const ps = [...form.paradas]; ps[i] = { ...ps[i], tipo: e.target.value }; set("paradas", ps);
                }} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, flexShrink: 0 }}>
                  <option value="parada">📍 Parada</option>
                  <option value="almuerzo">🍽️ Almuerzo</option>
                  <option value="noche">🌙 Noche</option>
                  <option value="destino">🏁 Destino final</option>
                </select>
                <input value={p.nombre} onChange={e => {
                  const ps = [...form.paradas]; ps[i] = { ...ps[i], nombre: e.target.value }; set("paradas", ps);
                }} placeholder="Nombre del lugar" style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} />
                <button onClick={() => set("paradas", form.paradas.filter((_, j) => j !== i))}
                  style={{ width: 28, height: 28, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  <X size={12} strokeWidth={2.5} color={C.red} />
                </button>
              </div>
            ))}
            <button onClick={() => set("paradas", [...form.paradas, { nombre: "", tipo: "parada" }])}
              style={{ fontSize: 13, color: C.blue, fontWeight: 600, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
              <Plus size={14} strokeWidth={2.5} /> Agregar parada
            </button>
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <Btn full variant="outline" onClick={() => setStep(0)}>← Atrás</Btn>
        <Btn full onClick={() => setStep(2)} disabled={!form.titulo || !form.fecha}>Continuar →</Btn>
      </div>
    </Modal>
  );

  // ── PASO 2: detalles y descripción ────────────────────────────────────────
  if (step === 2) return (
    <Modal title="Detalles del evento" onClose={onClose}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción</div>
        <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)}
          placeholder="Contá de qué se trata, qué van a hacer, qué incluye..."
          style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
      </div>

      {tipoEvento === "salida" && (
        <>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Equipamiento recomendado</div>
            <textarea value={form.equipo} onChange={e => set("equipo", e.target.value)}
              placeholder="Casco integral, protecciones, ropa de lluvia, carpa..."
              style={{ width: "100%", minHeight: 60, padding: "10px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, background: C.surface, borderRadius: 10, padding: "12px 14px" }}>
            <span style={{ fontSize: 20 }}>🚐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Equipo de apoyo en ruta</div>
              <div style={{ fontSize: 12, color: C.muted }}>¿Cuenta con asistencia mecánica o vehículo de soporte?</div>
            </div>
            <input type="checkbox" checked={form.apoyo_ruta} onChange={e => set("apoyo_ruta", e.target.checked)} style={{ accentColor: C.accent, width: 18, height: 18 }} />
          </div>
        </>
      )}

      {/* cupos */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Cupos</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <input type="checkbox" checked={form.cupos_ilimitado} onChange={e => set("cupos_ilimitado", e.target.checked)} id="cupos_il" style={{ accentColor: C.accent }} />
          <label htmlFor="cupos_il" style={{ fontSize: 13, color: C.text }}>Sin límite de cupos</label>
        </div>
        {!form.cupos_ilimitado && (
          <Input label="" value={form.cupos} onChange={e => set("cupos", e.target.value)} placeholder="Máximo de participantes" />
        )}
      </div>

      {/* inscripción */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Inscripción</div>
        {[["abierta", "🔓 Abierta — cualquiera puede anotarse"], ["aprobacion", "✋ Con aprobación — vos aceptás cada solicitud"]].map(([val, label]) => (
          <div key={val} onClick={() => set("inscripcion", val)} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
            background: form.inscripcion === val ? C.accentLo : C.surface,
            border: `1px solid ${form.inscripcion === val ? C.accent : C.border}`,
          }}>
            <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${form.inscripcion === val ? C.accent : C.faint}`, background: form.inscripcion === val ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {form.inscripcion === val && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.card }} />}
            </div>
            <span style={{ fontSize: 13, color: C.text }}>{label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Btn full variant="outline" onClick={() => setStep(1)}>← Atrás</Btn>
        <Btn full onClick={() => setStep(3)}>Continuar →</Btn>
      </div>
    </Modal>
  );

  // ── PASO 3: imagen y publicar ─────────────────────────────────────────────
  if (step === 3) return (
    <Modal title="Imagen y publicar" onClose={onClose}>
      {/* subir flyer principal */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Flyer / portada (foto o video)</div>
        {form.flyer ? (
          <div style={{ position: "relative" }}>
            {esVideoPortada(form.flyer) ? (
              <video src={cldOpt(form.flyer, 720)} autoPlay muted loop playsInline poster={posterDeVideo(form.flyer)}
                style={{ width: "100%", borderRadius: 12, maxHeight: 200, objectFit: "cover", display: "block" }} />
            ) : (
              <img loading="lazy" decoding="async" src={form.flyer} alt="flyer" style={{ width: "100%", borderRadius: 12, maxHeight: 200, objectFit: "cover" }} />
            )}
            <button onClick={() => set("flyer", null)}
              style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={13} strokeWidth={2.5} color="#fff" />
            </button>
          </div>
        ) : (
          <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px", borderRadius: 12, border: `1.5px dashed ${C.border}`, cursor: "pointer", background: C.surface }}>
            <Camera size={22} strokeWidth={1.5} color={C.muted} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>Subir flyer, foto o video</div>
              <div style={{ fontSize: 12, color: C.muted }}>JPG, PNG o video · máx 10MB</div>
            </div>
            <input type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              // Subir a Cloudinary
              const esVideo = (file.type || "").startsWith("video");
              const fd = new FormData();
              fd.append("file", await achicarImagen(file));
              fd.append("upload_preset", "olbos_uploads");
              try {
                const endpoint = esVideo
                  ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
                  : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
                const res = await fetch(endpoint, { method: "POST", body: fd });
                const data = await res.json();
                if (data.secure_url) set("flyer", data.secure_url);
              } catch {
                alert("Error subiendo el archivo");
              }
            }} />
          </label>
        )}
      </div>

      {/* Más fotos / videos */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Más fotos y videos (opcional)</div>
        {form.medias.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 8 }}>
            {form.medias.map((m, idx) => (
              <div key={idx} style={{ position: "relative", aspectRatio: "1", borderRadius: 10, overflow: "hidden", background: "#000" }}>
                {m.type === "video" ? (
                  <video src={cldOpt(m.url, 400)} poster={cldPoster(m.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                ) : (
                  <img loading="lazy" decoding="async" src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                )}
                {m.type === "video" && (
                  <div style={{ position: "absolute", top: 4, left: 4, padding: "2px 6px", background: "rgba(0,0,0,.6)", borderRadius: 4, fontSize: 9, color: "#fff", fontWeight: 700 }}>VIDEO</div>
                )}
                {m.uploading && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10 }}>Subiendo...</div>
                )}
                <button onClick={() => set("medias", form.medias.filter((_, i) => i !== idx))}
                  style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,.7)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={11} strokeWidth={3} color="#fff" />
                </button>
              </div>
            ))}
          </div>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px dashed ${C.border}`, cursor: "pointer", background: C.surface }}>
          <span style={{ fontSize: 20 }}>📷</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: 13, color: C.text }}>Agregar fotos o videos</div>
            <div style={{ fontSize: 11, color: C.muted }}>Podés subir varios a la vez</div>
          </div>
          <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }} onChange={async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0) return;
            // Placeholders mientras se sube
            const placeholders = files.map(f => ({
              url: URL.createObjectURL(f),
              type: f.type.startsWith("video") ? "video" : "image",
              uploading: true,
            }));
            const currentLen = form.medias.length;
            set("medias", [...form.medias, ...placeholders]);

            // Subir cada uno a Cloudinary
            for (let i = 0; i < files.length; i++) {
              const file = files[i];
              const isVideo = file.type.startsWith("video");
              const fd = new FormData();
              fd.append("file", await achicarImagen(file));
              fd.append("upload_preset", "olbos_uploads");
              try {
                const endpoint = isVideo
                  ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
                  : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
                const res = await fetch(endpoint, { method: "POST", body: fd });
                const data = await res.json();
                if (data.secure_url) {
                  setForm(f => {
                    const next = [...f.medias];
                    next[currentLen + i] = { url: data.secure_url, type: isVideo ? "video" : "image" };
                    return { ...f, medias: next };
                  });
                }
              } catch {
                // Marcar como fallido
                setForm(f => {
                  const next = [...f.medias];
                  next[currentLen + i] = { ...next[currentLen + i], uploading: false, failed: true };
                  return { ...f, medias: next };
                });
              }
            }
          }} />
        </label>
      </div>

      {/* Toggle: publicar también en el feed */}
      <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", marginBottom: 18 }}>
        <input type="checkbox" checked={form.publicar_en_feed} onChange={e => set("publicar_en_feed", e.target.checked)}
          style={{ marginTop: 2, accentColor: C.text }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>Publicar también en el feed</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
            Crea un post automático en el feed para difundir el evento entre todos los riders.
          </div>
        </div>
      </label>

      {/* Qué se publica: evento en un lugar o rodada. La IA lo deduce del flyer, pero puede
          equivocarse (Matías, 25-sep: "Recorré San Luis" se publicaba como rodada) */}
      {tipoEvento === "evento" && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Se publica como</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[["evento", "📍 Evento en un lugar", "Encuentro, expo, almuerzo, curso"], ["salida", "🛣️ Rodada / salida", "Con check-in y mapa en vivo"]].map(([v, l, d]) => {
              const act = (form.tipo || "evento") === v;
              return (
                <button key={v} type="button" onClick={() => set("tipo", v)} style={{ flex: 1, padding: "10px 8px", borderRadius: 12, border: `1.5px solid ${act ? C.text : C.border}`, background: act ? C.text : C.card, color: act ? "#F9F5F2" : C.text, cursor: "pointer", textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{l}</div>
                  <div style={{ fontSize: 10.5, opacity: 0.75, marginTop: 2 }}>{d}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* resumen */}
      <div style={{ background: C.surface, borderRadius: 12, padding: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Resumen</div>
        {[
          ["Nombre", form.titulo],
          tipoEvento === "evento" ? ["Lugar", form.lugar] : ["Ruta", `${form.desde} → ${form.hasta}`],
          ["Inicio", form.fecha],
          form.fecha_fin ? ["Fin", form.fecha_fin] : null,
          tipoEvento === "salida" && form.km ? ["Km", `${form.km} km`] : null,
          form.cupos_ilimitado ? ["Cupos", "Sin límite"] : form.cupos ? ["Cupos", form.cupos] : null,
        ].filter(Boolean).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
            <span style={{ color: C.muted }}>{k}</span>
            <span style={{ color: C.text, fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Btn full variant="outline" onClick={() => setStep(2)}>← Atrás</Btn>
        <Btn full disabled={publicandoEv} style={{ opacity: publicandoEv ? 0.6 : 1 }} onClick={async () => {
          // Espera el insert y se bloquea: el doble toque creaba dos eventos; si falla, el
          // formulario queda abierto con todo cargado (antes se cerraba y el alert llegaba solo)
          if (publicandoEv) return;
          setPublicandoEv(true);
          try {
            const r = await crearConGrupo(form);
            if (r && r.ok === false) { setPublicandoEv(false); return; }
            onClose();
          } catch (e) { alert("No se pudo publicar. Revisá la señal y probá de nuevo: lo que cargaste sigue acá."); setPublicandoEv(false); }
        }}>{publicandoEv ? "Publicando…" : "Publicar evento 🏍️"}</Btn>
      </div>
    </Modal>
  );

  return null;
};

// ─── ANOTAR MODAL ─────────────────────────────────────────────────────────────
const AnotarModal = ({ evento, motos, onClose, onConfirm }) => {
  const [motoId, setMotoId] = useState(motos[0]?.id || null);
  const precioReal = Number(evento.precio) > 0 ? Number(evento.precio) : (Number(evento.precio_entrada) || 0);
  const esPago = precioReal > 0;
  const cfg = evento.inscripcion_config || {};
  // El teléfono se pide SIEMPRE, lo haya configurado el organizador o no: el
  // ganador del sorteo del BikerFest (@enriquejavierdiaz) se anotó sin dejar
  // ningún medio de contacto y no hubo forma de avisarle (Carlos, 15-sep). Viene
  // precargado de la ficha, así que al que ya lo tiene solo le cuesta un vistazo.
  const campos = { ...(cfg.campos || {}), telefono: true };
  const extrasCfg = Array.isArray(cfg.extras) ? cfg.extras.filter(e => e && e.label && e.label.trim()) : [];
  // Deslinde OBLIGATORIO (mitigación legal 04-sep-2026): si el organizador no escribió
  // uno, aplica el modelo de Olbos según el tipo (predio vs. salida en ruta). Antes era
  // opcional y la mayoría de las salidas se publicaban sin ninguno.
  // Sin deslinde modelo por defecto (decisión de Carlos, 09-oct): solo el que escribió
  // el organizador. Si no hay, no se firma nada ni se pide DNI.
  const deslindeCfg = (cfg.deslinde || "").trim();
  const deslinde = deslindeCfg;
  const deslindeEsModelo = false;
  const puntosSuma = Array.isArray(cfg.puntos_suma) ? cfg.puntos_suma.filter(pt => pt && pt.nombre && pt.nombre.trim()) : [];
  const pideAlgo = Object.values(campos).some(Boolean) || extrasCfg.length > 0 || !!deslinde || puntosSuma.length > 0;

  const [datos, setDatos] = useState({ extras: {} });
  const [deslindeOk, setDeslindeOk] = useState(false);
  const [puntoSel, setPuntoSel] = useState(0); // desde qué punto se suma (rodadas con paradas)
  const [subiendoComp, setSubiendoComp] = useState(false); // comprobante de pago adjunto
  const setD = (k, v) => setDatos(d => ({ ...d, [k]: v }));
  const tienePago = esPago || Number(evento.precio_entrada) > 0 || !!evento.pago_info || !!evento.pago_link;
  // ¿El organizador cobra online con Mercado Pago? → modo ticketera: pagás y quedás confirmado
  // mpVerificando: mientras no vuelve la respuesta NO se deja anotar por la vía manual (antes
  // el botón "Confirmar lugar" aparecía de entrada y quien tocaba rápido, o con mala señal,
  // quedaba anotado sin pasar por Mercado Pago — reporte de Matías, 24-oct).
  const [mpCobra, setMpCobra] = useState(false);
  const [mpVerificando, setMpVerificando] = useState(!!(esPago && evento.id && evento._fromDB));
  useEffect(() => {
    if (!tienePago || !evento.id || !evento._fromDB) { setMpVerificando(false); return; }
    let vivo = true;
    const consultar = (intento) => {
      supabase.rpc("mp_evento_cobra", { p_evento_id: evento.id }).then(({ data, error }) => {
        if (!vivo) return;
        if (error && intento < 2) { setTimeout(() => consultar(intento + 1), 1200); return; }
        setMpCobra(data === true || data === "true");
        setMpVerificando(false);
      }, () => {
        if (!vivo) return;
        if (intento < 2) setTimeout(() => consultar(intento + 1), 1200); else setMpVerificando(false);
      });
    };
    consultar(0);
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id]);

  const subirComprobante = async (file) => {
    if (!file) return;
    setSubiendoComp(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setDatos(d => ({ ...d, comprobante_url: j.secure_url, comprobante_subido_en: new Date().toISOString() }));
      else alert("No se pudo subir el comprobante. Probá de nuevo.");
    } catch (e) { alert("No se pudo subir el comprobante. Probá de nuevo."); }
    setSubiendoComp(false);
  };

  // Precargar con la ficha guardada del rider
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: f } = await supabase.from("rider_fichas").select("*").eq("user_id", user.id).maybeSingle();
      if (f) setDatos(d => ({
        ...d,
        nombre_completo: f.nombre_completo || "", dni: f.dni || "", telefono: f.telefono || "", casco: f.casco || "",
        emergencia_nombre: f.emergencia_nombre || "", emergencia_relacion: f.emergencia_relacion || "", emergencia_telefono: f.emergencia_telefono || "",
        condicion_medica: f.condicion_medica || "", medicacion: f.medicacion || "",
        localidad: f.localidad || "", direccion: f.direccion || "",
      }));
    })();
  }, []);

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" };
  const campo = (k, label, ph) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>{label}</div>
      <input value={datos[k] || ""} onChange={e => setD(k, e.target.value)} placeholder={ph} style={inputStyle} />
    </div>
  );

  // Validación: campos personales pedidos no vacíos + deslinde FIRMADO (check + nombre + DNI)
  const reqOk =
    (!campos.nombre_completo || (datos.nombre_completo || "").trim()) &&
    (!campos.dni || (datos.dni || "").trim()) &&
    (!campos.telefono || (datos.telefono || "").trim()) &&
    (!campos.localidad || (datos.localidad || "").trim()) &&
    (!campos.direccion || (datos.direccion || "").trim()) &&
    (!campos.emergencia || ((datos.emergencia_nombre || "").trim() && (datos.emergencia_telefono || "").trim()));
  // El deslinde propio del organizador se firma con el check y el nombre; el DNI es opcional
  const firmaOk = !deslinde || (deslindeOk && (datos.nombre_completo || "").trim());
  const puedeConfirmar = reqOk && firmaOk;

  const armarDatos = () => {
    const out = { extras: datos.extras || {} };
    // Comprobante de pago adjunto: lo ven el organizador y sus colaboradores
    if (datos.comprobante_url) {
      out.comprobante_url = datos.comprobante_url;
      out.comprobante_subido_en = datos.comprobante_subido_en || new Date().toISOString();
    }
    if (puntosSuma.length > 0 && puntosSuma[puntoSel]) {
      out.punto_suma = `${puntosSuma[puntoSel].nombre.trim()}${puntosSuma[puntoSel].hora ? ` (${puntosSuma[puntoSel].hora})` : ""}`;
    }
    // La firma del deslinde viaja completa en el snapshot: texto exacto aceptado,
    // nombre, DNI y momento — es lo que le da valor probatorio.
    if (deslinde && deslindeOk) {
      out.deslinde_texto = deslinde;
      out.deslinde_nombre = (datos.nombre_completo || "").trim();
      out.deslinde_dni = (datos.dni || "").trim();
      out.deslinde_firmado_en = new Date().toISOString();
      out.deslinde_modelo = deslindeEsModelo; // true = modelo de Olbos, false = texto del organizador
      out.deslinde_dispositivo = typeof navigator !== "undefined" ? String(navigator.userAgent || "").slice(0, 200) : null;
      out.nombre_completo = out.nombre_completo || out.deslinde_nombre;
      out.dni = out.dni || out.deslinde_dni;
    }
    if (campos.nombre_completo) out.nombre_completo = (datos.nombre_completo || "").trim();
    if (campos.dni) out.dni = (datos.dni || "").trim();
    if (campos.telefono) out.telefono = (datos.telefono || "").trim();
    if (campos.localidad) out.localidad = (datos.localidad || "").trim();
    if (campos.direccion) out.direccion = (datos.direccion || "").trim();
    if (campos.casco) out.casco = (datos.casco || "").trim();
    if (campos.emergencia) { out.emergencia_nombre = (datos.emergencia_nombre || "").trim(); out.emergencia_relacion = (datos.emergencia_relacion || "").trim(); out.emergencia_telefono = (datos.emergencia_telefono || "").trim(); }
    if (campos.condicion_medica) { out.condicion_medica = (datos.condicion_medica || "").trim(); out.medicacion = (datos.medicacion || "").trim(); }
    return out;
  };
  const confirmar = () => {
    if (!puedeConfirmar) return;
    onConfirm(motoId, (pideAlgo || datos.comprobante_url) ? armarDatos() : null, deslindeOk);
    onClose();
  };

  // Modo ticketera: PRIMERO se arma el checkout de Mercado Pago y recién con el link en la
  // mano queda anotado (con pago pendiente) y se abre el pago; el webhook lo confirma solo.
  // Antes se anotaba primero y, si el checkout fallaba, se cerraba en silencio: el rider
  // quedaba anotado sin haber pasado nunca por Mercado Pago (documento de Matías, A7).
  // En la app nativa el pago se abre en la pestaña del navegador del sistema y vuelve a
  // Olbos por el link del evento (?pago=ok); el WebView no se va del bundle.
  const [pagando, setPagando] = useState(false);
  const confirmarYPagar = async () => {
    if (!puedeConfirmar || pagando) return;
    setPagando(true);
    let url = null, motivo = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setPagando(false); window.dispatchEvent(new CustomEvent("olbos-abrir-registro")); return; }
      const res = await fetch(`${API_BASE}/api/mp-pago`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ evento_id: evento.id, elegidas: datos.extras || {} }),
      });
      const j = await res.json().catch(() => ({}));
      url = j.url || null;
      motivo = j.error || (res.ok ? null : "http_" + res.status);
    } catch (e) { motivo = "sin_conexion"; }
    if (!url) {
      setPagando(false);
      registrarUso("pago_mp_error", { evento: evento.id, motivo: String(motivo || "sin_url").slice(0, 60) });
      alert(/sin_mp/.test(motivo || "")
        ? "El organizador todavía no tiene Mercado Pago conectado, así que no se puede pagar online. Escribile para coordinar el pago."
        : motivo === "sin_conexion"
          ? "Sin conexión. Probá de nuevo en un momento."
          : "No pudimos abrir Mercado Pago. Probá de nuevo en un momento.");
      return; // sin checkout no queda anotado como si hubiera pagado
    }
    onConfirm(motoId, (pideAlgo || datos.comprobante_url) ? armarDatos() : null, deslindeOk);
    registrarUso("pago_mp_checkout", { evento: evento.id });
    if (esNativo()) {
      try { const { Browser } = await import("@capacitor/browser"); await Browser.open({ url }); setPagando(false); onClose(); return; }
      catch (e) { /* sin el plugin, cae al de siempre */ }
    }
    window.location.href = url;
  };

  return (
    <Modal title={`Anotarme — ${evento.title}`} onClose={onClose}>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
        {evento.from} → {evento.to} · {evento.date} · {evento.km} km
      </p>

      {evento.inscripcion === "aprobacion" && (
        <div style={{ background: "#1111110C", border: `1px solid ${C.blue}22`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: C.blue }}>
          ✋ Este evento requiere aprobación del organizador. Tu solicitud quedará pendiente hasta que sea aceptada.
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>¿Con qué moto vas?</div>
        {/* Solo las activas: las eliminadas/vendidas quedan en el JSON (borrado suave)
            pero no son motos con las que puedas ir a una rodada (reporte de Matías) */}
        {(motos || []).filter(m => m && (!m.estado || m.estado === "activa")).map(m => (
          <div key={m.id} onClick={() => setMotoId(m.id)} style={{
            background: motoId === m.id ? C.accentLo : C.surface,
            border: `1px solid ${motoId === m.id ? C.accent : C.border}`,
            borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer",
            display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all .2s",
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{m.marca} {m.modelo}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{m.año} · {m.cc}cc · {m.color}</div>
            </div>
            {motoId === m.id && <span style={{ color: C.accent, fontSize: 18 }}>✓</span>}
          </div>
        ))}
      </div>

      {/* Datos pedidos por el organizador (precargados con tu ficha) */}
      {pideAlgo && (
        <div style={{ marginBottom: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{evento?.tipo === "evento" ? "Tus datos para este evento" : "Tus datos para esta salida"}</div>
          <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
            Vienen de tu ficha. Editá si hace falta — los cambios se guardan para la próxima. Solo los ve el organizador del evento.
          </div>
          {campos.nombre_completo && campo("nombre_completo", "Nombre y apellido", "Como en tu DNI")}
          {campos.dni && campo("dni", "DNI", "Sin puntos")}
          {campos.telefono && campo("telefono", "Teléfono / WhatsApp · para que el organizador pueda contactarte (y avisarte si ganás)", "Cód. país + número, ej: 5491112345678")}
          {campos.localidad && campo("localidad", "Localidad · de dónde venís", "Ciudad y provincia")}
          {campos.direccion && campo("direccion", "Dirección", "Calle y número")}
          {campos.casco && campo("casco", "Casco (marca y modelo)", "Ej: LS2 Storm")}
          {campos.emergencia && (
            <>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "6px 0 8px" }}>Contacto de emergencia</div>
              {campo("emergencia_nombre", "Nombre y apellido", "A quién avisamos")}
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>{campo("emergencia_relacion", "Relación", "Pareja, hermano…")}</div>
                <div style={{ flex: 1 }}>{campo("emergencia_telefono", "Teléfono", "Su número")}</div>
              </div>
            </>
          )}
          {campos.condicion_medica && (
            <>
              {campo("condicion_medica", "Condición médica (vacío = ninguna)", "Alergias, etc.")}
              {campo("medicacion", "Medicación (vacío = ninguna)", "Medicamentos")}
            </>
          )}

          {/* preguntas propias del evento: sección propia, separada de los datos
              personales (reporte de Carlos, 17-sep: no se distinguían) */}
          {extrasCfg.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Lo que quiere saber el organizador sobre vos</div>
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
                Preguntas propias de este evento. Las respuestas solo las ve el organizador.
              </div>
            </div>
          )}
          {extrasCfg.map((ex, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 6 }}>{ex.label}</div>
              {ex.tipo === "opciones" ? (
                // Cada opción puede sumar plata; se muestra el "+ $" al lado para que
                // nadie se entere del monto recién en el checkout de Mercado Pago.
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(Array.isArray(ex.opciones) ? ex.opciones : []).filter(o => o && String(o.texto || "").trim()).map(op => {
                    const sel = datos.extras?.[ex.label] === op.texto;
                    const suma = Number(op.extra) || 0;
                    return (
                      <button key={op.texto} onClick={() => setDatos(d => ({ ...d, extras: { ...d.extras, [ex.label]: op.texto } }))}
                        style={{ padding: "9px 12px", borderRadius: 9, border: sel ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: sel ? "#1111110C" : C.card, color: sel ? C.accent : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        {op.texto}{suma > 0 ? ` · +${suma.toLocaleString("es-AR")}` : ""}
                      </button>
                    );
                  })}
                </div>
              ) : ex.tipo === "texto" ? (
                <input value={datos.extras?.[ex.label] || ""} onChange={e => setDatos(d => ({ ...d, extras: { ...d.extras, [ex.label]: e.target.value } }))} style={inputStyle} />
              ) : (
                <div style={{ display: "flex", gap: 6 }}>
                  {["Sí", "No"].map(op => {
                    const sel = datos.extras?.[ex.label] === op;
                    return (
                      <button key={op} onClick={() => setDatos(d => ({ ...d, extras: { ...d.extras, [ex.label]: op } }))}
                        style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: sel ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: sel ? "#1111110C" : C.card, color: sel ? C.accent : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        {op}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Rodada que levanta gente: elegir desde qué punto te sumás */}
          {puntosSuma.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>¿Desde qué punto te sumás?</div>
              {puntosSuma.map((pt, i) => (
                <button key={i} onClick={() => setPuntoSel(i)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 10, marginBottom: 6, background: puntoSel === i ? "#1111110C" : C.surface, border: puntoSel === i ? `2px solid ${C.accent}` : `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0, border: `2px solid ${puntoSel === i ? C.accent : C.border}`, background: puntoSel === i ? C.accent : C.card, boxShadow: puntoSel === i ? "inset 0 0 0 3px #fff" : "none" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{pt.nombre}</div>
                    {pt.hora && <div style={{ fontSize: 11.5, color: C.muted }}>{pt.hora} hs</div>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Deslinde de responsabilidad — FIRMA ELECTRÓNICA (ley 25.506): además del
              check se exige nombre completo + DNI, y se guarda el texto EXACTO aceptado.
              Reemplaza la hoja de papel de la mesa de acreditación. */}
          {deslinde && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Deslinde de responsabilidad</div>
              <div style={{ maxHeight: 150, overflowY: "auto", padding: "10px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 8 }}>{deslinde}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 3 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Firmo con mi nombre completo</div>
                  <input value={datos.nombre_completo || ""} onChange={e => setD("nombre_completo", e.target.value)} placeholder="Como en tu DNI" style={inputStyle} />
                </div>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>DNI (opcional)</div>
                  <input value={datos.dni || ""} onChange={e => setD("dni", e.target.value)} placeholder="Sin puntos" style={inputStyle} />
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={deslindeOk} onChange={e => setDeslindeOk(e.target.checked)} style={{ width: 18, height: 18, accentColor: "#111111", marginTop: 1, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>Leí el deslinde y lo <strong>firmo electrónicamente</strong> con mi nombre y DNI. Vale como la firma en papel de la acreditación.</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Comprobante de pago: reemplaza el "mandalo por WhatsApp" — queda adjunto a la
          inscripción y lo ven el organizador y sus colaboradores. Con Mercado Pago
          activo no hace falta: el pago se acredita y confirma solo. */}
      {tienePago && !mpCobra && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Comprobante de pago (opcional)</div>
          {datos.comprobante_url ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: `${C.green}14`, border: `1px solid ${C.green}33` }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#1F8347" }}>✓ Comprobante adjunto</span>
              <a href={datos.comprobante_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Ver</a>
              <button onClick={() => setDatos(d => ({ ...d, comprobante_url: "", comprobante_subido_en: "" }))}
                style={{ background: "transparent", border: "none", color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Quitar</button>
            </div>
          ) : (
            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", borderRadius: 10, border: `1.5px dashed ${C.border}`, background: C.surface, cursor: "pointer", fontSize: 13, fontWeight: 700, color: subiendoComp ? C.muted : C.text }}>
              {subiendoComp ? "Subiendo…" : "📎 Adjuntar comprobante (foto o captura)"}
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendoComp}
                onChange={e => { subirComprobante(e.target.files[0]); e.target.value = ""; }} />
            </label>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Queda adjunto a tu inscripción: lo ven el organizador y sus colaboradores. No hace falta mandarlo por WhatsApp.</div>
        </div>
      )}

      {esPago ? (
        <div style={{ background: C.goldLo, border: `1px solid ${C.gold}22`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: C.gold, marginBottom: 6, fontSize: 14 }}>💰 Evento con costo</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.text, marginBottom: 4 }}>${precioReal.toLocaleString("es-AR")} ARS</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
            {mpCobra
              ? <>Pago online seguro con Mercado Pago, directo al organizador. Tu lugar queda <strong style={{ color: C.text }}>confirmado al instante</strong>.</>
              : <>El pago va directo al organizador (alias/transferencia). Podés adjuntar el comprobante más arriba y queda todo en tu inscripción.</>}
          </div>
          {mpCobra ? (
            <button onClick={confirmarYPagar} disabled={!puedeConfirmar || pagando}
              style={{ width: "100%", padding: "14px", borderRadius: 12, background: "#009EE3", color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: (puedeConfirmar && !pagando) ? 1 : 0.5 }}>
              {pagando ? "Abriendo Mercado Pago…" : "💳 Pagar con Mercado Pago"}
            </button>
          ) : mpVerificando ? (
            <Btn full variant="gold" onClick={() => {}} style={{ opacity: 0.5 }}>Verificando el medio de pago…</Btn>
          ) : (
            <Btn full variant="gold" onClick={confirmar} style={{ opacity: puedeConfirmar ? 1 : 0.5 }}>
              {evento.inscripcion === "aprobacion" ? "Solicitar lugar · el pago lo coordinás con el organizador" : "Confirmar lugar · el pago va al organizador"}
            </Btn>
          )}
        </div>
      ) : (
        <Btn full onClick={confirmar} style={{ opacity: puedeConfirmar ? 1 : 0.5 }}>
          {evento.inscripcion === "aprobacion" ? "Enviar solicitud" : "Confirmar lugar"}
        </Btn>
      )}
      {!puedeConfirmar && pideAlgo && (
        <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 8 }}>
          {deslinde && !deslindeOk ? "Aceptá el deslinde para continuar." : "Completá los datos pedidos para continuar."}
        </div>
      )}
    </Modal>
  );
};

// ─── Pantalla de resultado al anotarse ──────────────────────────────────────
// Una sola vista, oscura: "¡Estás anotado!" con el evento, la fecha y la hora, y
// los dos caminos que importan (el mapa de la rodada y compartir). Antes el modal
// se cerraba y solo cambiaba un badge chiquito en la tarjeta; nadie sabía si
// había quedado anotado (auditoría 09-oct). Y el error, en lenguaje humano.
const ResultadoInscripcion = ({ r, onClose, onMapa, onReintentar, padre = null, onAnotarPadre = null }) => {
  const ev = r.evento || {};
  const titulo = String(ev.title || ev.titulo || "").trim();
  const fecha = ev.fecha ? new Date(String(ev.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" }) : null;
  const hora = ev.hora ? `${String(ev.hora).slice(0, 5)} hs` : null;
  const btn = { width: "100%", padding: "15px", borderRadius: 14, border: "none", fontSize: 15.5, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: "inherit" };
  const compartir = () => compartirNativo({
    texto: `🏍️ Me anoté a ${titulo} en Olbos.${fecha ? ` ${fecha.charAt(0).toUpperCase() + fecha.slice(1)}${hora ? ` · ${hora}` : ""}.` : ""} ¿Venís?`,
    url: ev.id ? `${SITE_URL}/evento/${ev.id}` : SITE_URL,
    titulo: titulo || "Evento en Olbos",
  });
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "#111111", color: "#F9F5F2", display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 28px calc(28px + var(--sab))", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 380, margin: "0 auto", textAlign: "center" }}>
        {r.cargando ? (
          <>
            <div style={{ width: 30, height: 30, margin: "0 auto 18px", border: "3px solid rgba(249,245,242,.25)", borderTopColor: "#F9F5F2", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: "#bdb6ab" }}>Anotándote…</div>
          </>
        ) : r.ok ? (
          <>
            <div style={{ width: 76, height: 76, borderRadius: "50%", background: "#F9F5F2", color: "#111111", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px" }}>
              <CheckCircle size={40} strokeWidth={2.2} />
            </div>
            <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 14px" }}>¡Estás anotado!</h1>
            <div style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.015em", marginBottom: 6 }}>{titulo}</div>
            {(fecha || hora) && (
              <div style={{ fontSize: 14.5, color: "#bdb6ab", marginBottom: 34 }}>{[fecha ? fecha.charAt(0).toUpperCase() + fecha.slice(1) : null, hora].filter(Boolean).join(" · ")}</div>
            )}
            {/* Caravana hacia un evento: la inscripción a la caravana y al evento son dos
                distintas; sin esto el rider llegaba al predio sin check-in ni sorteo */}
            {padre && onAnotarPadre && (
              <button onClick={onAnotarPadre} style={{ ...btn, background: "#F9F5F2", color: "#111111", marginBottom: 10 }}>
                <div>Anotarme también a {String(padre.title || padre.titulo || "").trim()}</div>
                <div style={{ fontSize: 12, fontWeight: 500, opacity: .65, marginTop: 2 }}>El evento al que va la rodada · un toque</div>
              </button>
            )}
            <button onClick={onMapa} style={{ ...btn, background: padre && onAnotarPadre ? "transparent" : "#F9F5F2", color: padre && onAnotarPadre ? "#F9F5F2" : "#111111", border: padre && onAnotarPadre ? "1px solid rgba(249,245,242,.3)" : "none", marginBottom: 10 }}>Ver el mapa de la rodada</button>
            <button onClick={compartir} style={{ ...btn, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontWeight: 600, marginBottom: 10 }}>Compartir</button>
            <button onClick={onClose} style={{ ...btn, background: "transparent", color: "#bdb6ab", fontWeight: 600, padding: "12px" }}>Listo</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🙈</div>
            <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 12px" }}>No pudimos anotarte</h1>
            <p style={{ fontSize: 14.5, color: "#bdb6ab", lineHeight: 1.55, margin: "0 0 30px" }}>{r.motivo || "Probá de nuevo en unos segundos."}</p>
            <button onClick={onReintentar} style={{ ...btn, background: "#F9F5F2", color: "#111111", marginBottom: 10 }}>Probar de nuevo</button>
            <button onClick={onClose} style={{ ...btn, background: "transparent", color: "#bdb6ab", fontWeight: 600, padding: "12px" }}>Cerrar</button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Banners de novedades del carrusel (pedido de Carlos 03-sep) ─────────────
// Las funciones más copadas de la app, bien claras, intercaladas entre los
// eventos más próximos de la portada. Cada una lleva a la función. `hasta`
// (opcional) para las que vencen (sorteos, fechas). Editar acá cuando salga algo.
const irATab = (tab, extra = {}) => window.dispatchEvent(new CustomEvent("olbos-navegar", { detail: { tab, ...extra } }));
const NOVEDADES_CARRUSEL = [
  { id: "sorteo-benito", tag: "Sorteo", emoji: "🎁", titulo: "Sorteo Benito Motos: este sábado", bajada: "Orden de compra de $250.000. Si tenés cuenta, ya estás participando.", cta: "Ver el sorteo", hasta: "2026-09-05",
    accion: () => irATab("eventos") },
  { id: "video-recorrido", tag: "Nuevo", emoji: "🎬", titulo: "Tu rodada, en video", bajada: "Grabá el recorrido y la app arma un video del trazado con tus fotos, listo para compartir.", cta: "Grabar una rodada",
    accion: () => window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} })) },
  { id: "km-comunidad", tag: "Comunidad", emoji: "🏆", titulo: "Cada km que grabás suma", bajada: "Kilómetros de la comunidad, ranking de riders y metas con sorteos cuando las alcanzamos.", cta: "Ver el ranking",
    accion: () => irATab("feed") },
  { id: "modo-oscuro", tag: "Nuevo", emoji: "🌙", titulo: "Modo oscuro", bajada: "Cuero de noche para la ruta. Activalo en Cuenta → Apariencia.", cta: "Activarlo",
    accion: () => irATab("perfil") },
  { id: "viajes", tag: "Función", emoji: "🧳", titulo: "Viajes en grupo", bajada: "Etapas con su tramo en el mapa, logística y gastos compartidos con el balance de quién le debe a quién.", cta: "Armar un viaje",
    accion: () => irATab("rutas", { vistaMapa: "viajes" }) },
  { id: "navegador", tag: "Función", emoji: "🧭", titulo: "Navegador con voz", bajada: "Rutas alternativas, caminos de riders, alertas y clima. Tocá «Olbos» en cualquier evento y te lleva.", cta: "Abrir el mapa",
    accion: () => irATab("rutas") },
  { id: "historia-clinica", tag: "Compra-Venta", emoji: "🔧", titulo: "Vendé tu moto con su historia clínica", bajada: "Cada service registrado en Olbos viaja con la publicación. El comprador ve todo lo que se le hizo.", cta: "Ver Compra-Venta",
    accion: () => irATab("mercado", { pitStopTab: "compraventa" }) },
  { id: "qr-casco", tag: "Seguridad", emoji: "🆘", titulo: "QR de emergencia para el casco", bajada: "Quien lo escanea ve tu grupo sanguíneo y a quién llamar. Se genera desde tu perfil.", cta: "Generar mi QR",
    accion: () => irATab("perfil") },
];

// ─── Productos y motos en venta en el carrusel (pedido de Carlos 23-oct) ───────
// Productos: de cada lista de afiliados de ML, el MÁS CARO entre los mejor calificados
// (pedido de Carlos 23-oct): primero los de 4,7★ o más con al menos 20 reseñas; si no
// hay, se baja a 4,5★; si tampoco, el más caro con calificación.
// Motos: las publicaciones activas de Compra-Venta con foto, las destacadas primero.
// Las dos se piden una sola vez por sesión (caché de 10 minutos).
let _carruselExtrasCache = null;
const cargarExtrasCarrusel = async () => {
  if (_carruselExtrasCache && Date.now() - _carruselExtrasCache.t < 10 * 60 * 1000) return _carruselExtrasCache.d;
  const [productos, ventas] = await Promise.all([
    fetch(`${API_BASE}/api/ml-lista`).then(r => r.json()).then(j => {
      const listas = j && j.ok && Array.isArray(j.listas) ? j.listas : [];
      const rating = (it) => Number(it.rating) || 0, resenas = (it) => Number(it.resenas) || 0;
      return listas.filter(l => l.items && l.items.length).map(l => {
        const conFoto = l.items.filter(it => it.imagen && it.precio != null);
        const pool = [
          conFoto.filter(it => rating(it) >= 4.7 && resenas(it) >= 20),
          conFoto.filter(it => rating(it) >= 4.5),
          conFoto.filter(it => rating(it) > 0),
          conFoto,
        ].find(p => p.length) || [];
        const mejor = pool.slice().sort((a, b) => (Number(b.precio) || 0) - (Number(a.precio) || 0))[0];
        return mejor ? { ...mejor, lista: l.titulo } : null;
      }).filter(Boolean).sort((a, b) => (Number(b.precio) || 0) - (Number(a.precio) || 0)).slice(0, 4);
    }).catch(() => []),
    supabase.from("motos_venta").select("id, marca, modelo, año, km, precio, moneda, ciudad, foto_url, fotos, categoria, tipo, created_at")
      .eq("estado", "activa").eq("tipo", "venta").order("created_at", { ascending: false }).limit(20)
      .then(({ data }) => (data || []).filter(v => (!v.categoria || v.categoria === "moto") && (v.foto_url || (Array.isArray(v.fotos) && v.fotos[0]))), () => []),
  ]);
  let destacadas = {};
  try { (await cargarAnunciosSlot("moto") || []).forEach(a => { if (a.venta_id) destacadas[a.venta_id] = true; }); } catch (e) { /* */ }
  const motos = ventas.sort((a, b) => (destacadas[b.id] ? 1 : 0) - (destacadas[a.id] ? 1 : 0)).slice(0, 4);
  const d = { productos, motos };
  _carruselExtrasCache = { t: Date.now(), d };
  return d;
};

// ─── Carrusel de destacados (cabecera de Eventos) ────────────────────────────
// El primer golpe de vista al entrar a la app: eventos destacados y los más
// próximos, rotando solos cada 5s (con pausa si el usuario pasa a mano). A futuro,
// acá también van novedades y el espacio para marcas.
// Carrusel de destacados con SCROLL NATIVO + snap: el dedo arrastra de verdad y
// la física del navegador hace la fluidez (antes cambiaba de golpe por umbral)
const DestacadosCarrusel = ({ eventos, onAbrir, arriba }) => {
  const [idx, setIdx] = useState(0);
  const scrollerRef = useRef(null);
  const pausaRef = useRef(0);
  // Intercalado (idea de Carlos 03-sep): entre los eventos van tarjetas del slot
  // "carrusel" (publicidad paga) y, si no hay anunciante, una novedad de Olbos
  // (las notas de version.json) que además ofrece el espacio. Segunda posición y
  // después cada 3 eventos, para que la portada siga siendo de los eventos.
  const [anuncios, setAnuncios] = useState([]);
  const [comprar, setComprar] = useState(false);
  const [extrasCom, setExtrasCom] = useState({ productos: [], motos: [] });
  const [verSeguro, setVerSeguro] = useState(false);
  useEffect(() => {
    let ok = true;
    cargarAnunciosSlot("carrusel").then(l => { if (ok) setAnuncios(Array.isArray(l) ? l : []); });
    cargarExtrasCarrusel().then(d => { if (ok && d) setExtrasCom(d); }, () => {});
    seguroVisible().then(v => { if (ok) setVerSeguro(!!v); }, () => {});
    return () => { ok = false; };
  }, []);
  const hoyISO = new Date().toISOString().slice(0, 10);
  const novedades = NOVEDADES_CARRUSEL.filter(n => !n.hasta || n.hasta >= hoyISO);
  const items = (() => {
    const evs = eventos || [];
    // Anuncios pagos primero; después se alternan moto en venta, producto y novedad
    const colas = [
      extrasCom.motos.map(v => ({ _tipo: "venta", v, id: `venta-${v.id}` })),
      extrasCom.productos.map(p => ({ _tipo: "producto", p, id: `prod-${p.id}` })),
      novedades.map(n => ({ _tipo: "novedad", n, id: `nov-${n.id}` })),
    ];
    const mezcla = [];
    for (let r = 0; colas.some(c => r < c.length); r++) colas.forEach(c => { if (r < c.length) mezcla.push(c[r]); });
    const extras = [
      ...anuncios.map(a => ({ _tipo: "anuncio", a, id: `ad-${a.id}` })),
      ...(verSeguro ? [{ _tipo: "seguro", id: "seguro-moto" }] : []),
      ...mezcla,
    ];
    if (!extras.length || !evs.length) return evs;
    const out = []; let k = 0;
    evs.forEach((e, i) => { out.push(e); if (i === 0 || (i > 0 && (i - 0) % 3 === 0)) { if (k < extras.length) out.push(extras[k++]); } });
    while (k < extras.length) out.push(extras[k++]);
    return out;
  })();
  // Impresión del anuncio recién cuando su tarjeta está a la vista (no al montar)
  useEffect(() => { const it = items[idx]; if (it && it._tipo === "anuncio") registrarAnuncioEvento(it.a.id, "imp"); }, [idx, items.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const irA = (i, suave = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: suave ? "smooth" : "auto" });
  };
  useEffect(() => {
    if (items.length <= 1) return;
    const iv = setInterval(() => {
      if (Date.now() - pausaRef.current < 8000) return; // el que está mirando manda
      const el = scrollerRef.current;
      if (!el) return;
      const actual = Math.round(el.scrollLeft / el.clientWidth);
      irA((actual + 1) % items.length);
    }, 5000);
    return () => clearInterval(iv);
  }, [items.length]);
  if (items.length === 0) return null;
  return (
    <div style={{ padding: arriba ? "calc(var(--sat) + 58px) 18px 0" : "14px 18px 0" }}>
      <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", background: C.accent }}>
        <div ref={scrollerRef} data-noswipe
          onScroll={e => {
            const el = e.currentTarget;
            const i = Math.round(el.scrollLeft / el.clientWidth);
            if (i !== idx) setIdx(i);
          }}
          onTouchStart={() => { pausaRef.current = Date.now(); }}
          style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", height: 158 }}
          className="sin-scrollbar">
          {items.map((e, i) => {
            if (e._tipo === "anuncio") {
              const a = e.a;
              return (
                <div key={e.id} onClick={() => abrirLinkAnuncio(a)}
                  style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: a.link ? "pointer" : "default" }}>
                  {a.imagen_url && <img src={cldThumb(a.imagen_url, 800)} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }} />}
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(17,17,17,.92) 15%, rgba(17,17,17,.2) 70%)" }} />
                  <div style={{ position: "absolute", left: 16, right: 16, bottom: 14, color: "#F9F5F2" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#D9B368", marginBottom: 4 }}>Partner Olbos</div>
                    <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{a.titulo}</div>
                    {a.texto && <div style={{ fontSize: 11.5, color: "#c9c3ba", marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{a.texto}</div>}
                  </div>
                </div>
              );
            }
            if (e._tipo === "seguro") {
              return (
                <div key={e.id} onClick={() => { registrarUso("carrusel_seguro", {}); window.dispatchEvent(new CustomEvent("olbos-cotizar-seguro")); }}
                  style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: "pointer", background: "#111111", overflow: "hidden" }}>
                  <div style={{ position: "absolute", right: 14, top: 10, fontSize: 64, lineHeight: 1, opacity: 0.9 }}>🛡️</div>
                  <div style={{ position: "absolute", left: 16, right: 96, top: 14, bottom: 14, color: "#F9F5F2", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#D9B368", marginBottom: 5 }}>Seguro de moto</div>
                    <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.15 }}>Cotizá el seguro de tu moto</div>
                    <div style={{ fontSize: 12, color: "#c9c3ba", marginTop: 4, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>Un productor matriculado te manda la cotización por WhatsApp, con los datos de tu garage. Sin compromiso.</div>
                    <div style={{ fontSize: 11.5, color: "#F9F5F2", marginTop: 7, fontWeight: 800 }}>Pedir cotización →</div>
                  </div>
                </div>
              );
            }
            if (e._tipo === "venta") {
              const v = e.v;
              const foto = v.foto_url || (Array.isArray(v.fotos) && v.fotos[0]);
              const detalle = [v.año, v.km ? `${Number(v.km).toLocaleString("es-AR")} km` : null, v.ciudad].filter(Boolean).join(" · ");
              return (
                <div key={e.id} onClick={() => { registrarUso("carrusel_venta", { id: v.id }); irATab("mercado", { pitStopTab: "compraventa", ventaId: v.id }); }}
                  style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: "pointer" }}>
                  {foto && <img src={cldThumb(foto, 800)} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }} />}
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(17,17,17,.92) 15%, rgba(17,17,17,.2) 70%)" }} />
                  <div style={{ position: "absolute", left: 16, right: 16, bottom: 14, color: "#F9F5F2" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#D9B368", marginBottom: 4 }}>En venta · Compra-Venta</div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.2, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{v.marca} {v.modelo}</div>
                      <div style={{ fontSize: 16, fontWeight: 900, flexShrink: 0, letterSpacing: "-0.01em" }}>{v.moneda === "USD" ? "USD " : "$"}{Number(v.precio).toLocaleString("es-AR")}</div>
                    </div>
                    {detalle && <div style={{ fontSize: 11.5, color: "#c9c3ba", marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{detalle}</div>}
                  </div>
                </div>
              );
            }
            if (e._tipo === "producto") {
              const p = e.p;
              return (
                <div key={e.id} onClick={() => { registrarUso("carrusel_producto", { id: p.id, lista: p.lista, titulo: String(p.titulo || "").slice(0, 80), rating: p.rating, resenas: p.resenas, precio: p.precio }); window.open(p.link, "_blank", "noopener,noreferrer"); }}
                  style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: "pointer", background: "#111111", overflow: "hidden", display: "flex" }}>
                  <div style={{ flex: 1, minWidth: 0, padding: "14px 12px 14px 16px", color: "#F9F5F2", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#D9B368", marginBottom: 5, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>Recomendado · {p.lista}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.titulo}</div>
                    <div style={{ fontSize: 16, fontWeight: 900, marginTop: 6, letterSpacing: "-0.01em" }}>{p.precio != null ? `${p.moneda === "ARS" ? "$" : p.moneda + " "}${Math.round(p.precio).toLocaleString("es-AR")}` : ""}</div>
                    <div style={{ fontSize: 11, color: "#c9c3ba", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[p.rating != null ? `★ ${p.rating}${p.resenas ? ` (${p.resenas})` : ""}` : null, p.envio_gratis ? "Envío gratis" : null].filter(Boolean).join(" · ") || "Ver en Mercado Libre"}</div>
                  </div>
                  <div style={{ width: 132, flexShrink: 0, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={p.imagen} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  </div>
                </div>
              );
            }
            if (e._tipo === "novedad") {
              const n = e.n;
              return (
                <div key={e.id} onClick={() => { if (n.accion) n.accion(); }}
                  style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: n.accion ? "pointer" : "default", background: "#111111", overflow: "hidden" }}>
                  {/* emoji grande a la derecha como "ilustración", en cuero de noche */}
                  <div style={{ position: "absolute", right: 14, top: 10, fontSize: 64, lineHeight: 1, opacity: 0.9 }}>{n.emoji}</div>
                  <div style={{ position: "absolute", left: 16, right: 96, top: 14, bottom: 14, color: "#F9F5F2", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#D9B368", marginBottom: 5 }}>{n.tag}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.15, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{n.titulo}</div>
                    <div style={{ fontSize: 12, color: "#c9c3ba", marginTop: 4, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{n.bajada}</div>
                    {n.cta && <div style={{ fontSize: 11.5, color: "#F9F5F2", marginTop: 7, fontWeight: 800 }}>{n.cta} →</div>}
                  </div>
                </div>
              );
            }
            const fecha = e.fecha ? new Date(String(e.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" }) : "";
            return (
              <div key={e.id || i} onClick={() => onAbrir(e)}
                style={{ position: "relative", flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", cursor: "pointer" }}>
                {e.img && <img src={cldThumb(e.img, 800)} alt="" loading={i === 0 ? "eager" : "lazy"} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.55 }} />}
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(17,17,17,.92) 15%, rgba(17,17,17,.25) 70%)" }} />
                <div style={{ position: "absolute", left: 16, right: 16, bottom: 14, color: "#F9F5F2" }}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "#c9c3ba", marginBottom: 4 }}>
                    {e.destacado ? "📌 Destacado" : "Se viene"}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{e.title}</div>
                  <div style={{ fontSize: 11.5, color: "#c9c3ba", marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                    {fecha}{e.lugar ? ` · ${e.lugar}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 12 && (
          <div style={{ position: "absolute", right: 12, top: 10, padding: "2px 8px", borderRadius: 100, background: "rgba(17,17,17,.55)", color: "#F9F5F2", fontSize: 10.5, fontWeight: 700, pointerEvents: "none", fontVariantNumeric: "tabular-nums" }}>{idx + 1} / {items.length}</div>
        )}
        {items.length > 1 && items.length <= 12 && (
          <div style={{ position: "absolute", right: 14, top: 12, display: "flex", gap: 4, pointerEvents: "none" }}>
            {items.map((_, i) => (
              <span key={i} style={{ width: i === idx ? 14 : 5, height: 5, borderRadius: 3, background: i === idx ? "#F9F5F2" : "rgba(249,245,242,.45)", transition: "all .25s" }} />
            ))}
          </div>
        )}
      </div>
      {comprar && <ComprarEspacioModal slotInicial="carrusel" onClose={() => setComprar(false)} />}
    </div>
  );
};

// ─── SECCIÓN: EVENTOS ─────────────────────────────────────────────────────────
// ─── VISTA CALENDARIO DE EVENTOS (agenda mensual consolidada) ────────────────
const VistaCalendarioEventos = ({ eventos, onAbrir }) => {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const [mes, setMes] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [diaSel, setDiaSel] = useState(null);
  const clave = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Índice día → eventos (los multi-día pintan todos sus días)
  const idx = {};
  eventos.forEach(e => {
    if (!e.fecha) return;
    const ini = new Date(String(e.fecha).slice(0, 10) + "T12:00:00");
    const fin = e.fecha_fin ? new Date(String(e.fecha_fin).slice(0, 10) + "T12:00:00") : ini;
    let guard = 0;
    for (let d = new Date(ini); d <= fin && guard < 45; d.setDate(d.getDate() + 1), guard++) {
      const k = clave(d);
      (idx[k] = idx[k] || []).push(e);
    }
  });

  const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const offset = (primero.getDay() + 6) % 7; // semana arranca lunes
  const diasEnMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const celdas = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d++) celdas.push(new Date(mes.getFullYear(), mes.getMonth(), d));
  while (celdas.length % 7 !== 0) celdas.push(null);
  const nombreMes = mes.toLocaleDateString("es-AR", { month: "long", year: "numeric" });
  const evDiaSel = diaSel ? (idx[clave(diaSel)] || []) : [];

  return (
    <div>
      {/* header del mes */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <button onClick={() => { setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1)); setDiaSel(null); }}
          style={{ width: 34, height: 34, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={17} strokeWidth={2} color={C.text} />
        </button>
        <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", textTransform: "capitalize" }}>{nombreMes}</div>
        <button onClick={() => { setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1)); setDiaSel(null); }}
          style={{ width: 34, height: 34, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronRight size={17} strokeWidth={2} color={C.text} />
        </button>
      </div>
      {/* días de la semana */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 10.5, fontWeight: 700, color: C.muted }}>{d}</div>
        ))}
      </div>
      {/* grilla */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {celdas.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = clave(d);
          const evs = idx[k] || [];
          const esHoy = d.getTime() === hoy.getTime();
          const pasado = d < hoy;
          const sel = diaSel && d.getTime() === diaSel.getTime();
          return (
            <button key={i} onClick={() => setDiaSel(sel ? null : d)}
              style={{
                minHeight: 58, padding: "3px 2px", borderRadius: 8, cursor: evs.length ? "pointer" : "default",
                background: sel ? "#111111" : esHoy ? C.surface : C.card,
                border: `1px solid ${sel ? "#111111" : esHoy ? "#111111" : C.border}`,
                display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2, overflow: "hidden",
                opacity: pasado && !sel ? 0.55 : 1,
              }}>
              <div style={{ fontSize: 10.5, fontWeight: esHoy || sel ? 800 : 600, color: sel ? "#F9F5F2" : C.text, textAlign: "center", lineHeight: 1 }}>{d.getDate()}</div>
              {evs.slice(0, 2).map((e, j) => (
                <div key={j} style={{
                  fontSize: 7.5, fontWeight: 700, lineHeight: 1.15, padding: "1.5px 3px", borderRadius: 3,
                  background: sel ? "rgba(249,245,242,.25)" : "#111111", color: "#F9F5F2",
                  overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", textAlign: "left",
                }}>{(e.title || e.titulo || "").replace(/[🏍️🌙📍🎁]/g, "").trim()}</div>
              ))}
              {evs.length > 2 && <div style={{ fontSize: 7.5, fontWeight: 800, color: sel ? "#F9F5F2" : C.muted, textAlign: "left", paddingLeft: 3 }}>+{evs.length - 2} más</div>}
            </button>
          );
        })}
      </div>
      {/* detalle del día seleccionado */}
      {diaSel && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
            {diaSel.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          {evDiaSel.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted }}>Sin eventos este día.</div>
          ) : evDiaSel.map((e, i) => (
            <button key={i} onClick={() => onAbrir(e)}
              style={{ width: "100%", textAlign: "left", padding: "11px 13px", borderRadius: 12, marginBottom: 8, background: C.card, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{e.tipo === "evento" ? "📍" : "🛣️"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{e.title || e.titulo}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{e.hora ? `${String(e.hora).slice(0, 5)} hs · ` : ""}{e.lugar || e.from || ""}</div>
              </div>
              <ChevronRight size={15} color={C.muted} />
            </button>
          ))}
        </div>
      )}
      {!diaSel && <div style={{ marginTop: 10, fontSize: 11.5, color: C.muted, textAlign: "center" }}>Tocá un día para ver sus eventos</div>}
    </div>
  );
};

// ─── VISTA MAPA DE EVENTOS (pins de todos los próximos) ──────────────────────
const VistaMapaEventos = ({ eventos, onAbrir }) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markersRef = useRef([]);
  const [listo, setListo] = useState(false);
  const conCoords = eventos.filter(e => e.lat && e.lng);
  const firma = conCoords.map(e => e.id).join(",");

  useEffect(() => {
    let activo = true;
    (async () => {
      await loadGoogleMaps();
      if (!activo || !mapRef.current || !window.google) return;
      mapInst.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: -36.5, lng: -64.5 }, zoom: 4,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: "greedy", // mover con UN dedo (sin greedy pide dos)
      });
      setListo(true);
    })();
    return () => { activo = false; };
  }, []);

  useEffect(() => {
    if (!listo || !mapInst.current || !window.google) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (conCoords.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    conCoords.forEach(e => {
      const pos = { lat: Number(e.lat), lng: Number(e.lng) };
      const m = new window.google.maps.Marker({
        position: pos, map: mapInst.current, title: e.title || e.titulo,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#111111", fillOpacity: 1, strokeColor: "#F9F5F2", strokeWeight: 2.5 },
      });
      m.addListener("click", () => onAbrir(e));
      bounds.extend(pos);
      markersRef.current.push(m);
    });
    mapInst.current.fitBounds(bounds, 46);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listo, firma]);

  return (
    <div>
      <div ref={mapRef} style={{ width: "100%", height: "58vh", borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}`, background: C.surface }} />
      <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted, textAlign: "center" }}>
        {conCoords.length} evento{conCoords.length !== 1 ? "s" : ""} en el mapa · tocá un pin para abrirlo
      </div>
    </div>
  );
};

// ─── Tarjeta fija de la rodada en vivo ──────────────────────────────────────
// Para los eventos en los que estoy anotado y que tienen rodada: desde 1 h antes de
// la hora de largada hasta el fin del día, arriba de todo en Eventos y en Mapa:
// "La Nocturna larga a las 20:00 · Entrar al mapa en vivo". La hora de la rodada de
// un evento de predio va en inscripcion_config.hora_rodada (y fecha_rodada si no es
// el último día); una salida usa su fecha y hora.
const ventanaRodada = (e) => {
  const cfg = e.inscripcion_config || {};
  const fechaR = String(cfg.fecha_rodada || (e.tipo === "evento" ? (e.fecha_fin || e.fecha) : e.fecha) || "").slice(0, 10);
  const horaR = String(cfg.hora_rodada || e.hora || "").slice(0, 5);
  if (!fechaR || !/^\d{1,2}:\d{2}$/.test(horaR)) return null;
  if (e.tipo === "evento" && !cfg.hora_rodada) return null; // un evento de predio sin hora de rodada no "larga"
  const ini = new Date(`${fechaR}T${horaR}:00`).getTime();
  if (isNaN(ini)) return null;
  const hasta = new Date(`${fechaR}T23:59:59`).getTime();
  return { ini, desde: ini - 3600000, hasta, horaR };
};
const TarjetaRodadaEnVivo = ({ session, style }) => {
  const uid = session?.user?.id;
  const [evs, setEvs] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!uid) { setEvs([]); return; }
    let vivo = true;
    (async () => {
      try {
        const { data: insc } = await supabase.from("inscripciones").select("evento_id").eq("user_id", uid).eq("estado", "confirmado");
        const ids = (insc || []).map(i => i.evento_id).filter(Boolean);
        if (!ids.length) { if (vivo) setEvs([]); return; }
        const desde = hoyLocalISO(new Date(Date.now() - 7 * 86400000));
        const { data } = await supabase.from("eventos").select("id, titulo, fecha, fecha_fin, hora, tipo, inscripcion_config, estado, lat, lng, polyline, puntos")
          .in("id", ids.slice(0, 300)).gte("fecha", desde);
        if (vivo) setEvs(data || []);
      } catch (e) { /* sin red: no hay tarjeta */ }
    })();
    return () => { vivo = false; };
  }, [uid, tick > 0 && Math.floor(tick / 10)]); // eslint-disable-line react-hooks/exhaustive-deps
  const ahora = Date.now() + tick * 0; // el tick fuerza el re-render cada minuto
  const activos = evs.map(e => ({ e, v: ventanaRodada(e) })).filter(x => x.v && ahora >= x.v.desde && ahora <= x.v.hasta && !rodadaTerminada(x.e));
  if (!activos.length) return null;
  const { e, v } = activos[0];
  const tituloCorto = (() => { const s = String(e.titulo || "").split(/\s[—–-]\s|:|\(/)[0].trim(); return s.length > 40 ? s.slice(0, 38).trim() + "…" : s; })();
  const faltaMin = Math.round((v.ini - ahora) / 60000);
  const sub = faltaMin > 0 ? `Larga a las ${v.horaR} · faltan ${faltaMin} min` : "En curso · entrá y compartí tu posición con el grupo";
  return (
    <button onClick={() => { registrarUso("mapa_vivo_tap", { id: e.id, desde: "tarjeta" }); window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento: { ...e, title: e.titulo } } })); }}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 15px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", textAlign: "left", boxSizing: "border-box", ...(style || {}) }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#B3372C", flexShrink: 0, animation: "pulse 1.4s infinite" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.25 }}>{tituloCorto}{faltaMin > 0 ? ` larga a las ${v.horaR}` : " en vivo"}</div>
        <div style={{ fontSize: 12, color: "#bdb6ab", marginTop: 2 }}>{sub} · <strong style={{ color: "#F9F5F2" }}>Entrar al mapa en vivo</strong></div>
      </div>
      <ChevronRight size={18} strokeWidth={2} color="#F9F5F2" style={{ flexShrink: 0 }} />
    </button>
  );
};

const Eventos = ({ userMotos, onAI, session, isAdmin, requireAuth, userPos }) => {
  const [joined, setJoined] = useState({});
  // Guardados ("me interesa"): eventos que querés tener a mano sin anotarte todavía.
  // Antes la única forma de que un evento te quedara en algún lado era anotarte
  // (sugerencia de un usuario, vía Matías, 10-oct-2026). Tabla eventos_guardados.
  const [guardados, setGuardados] = useState({});
  const [filter, setFilter] = useState("Todos");
  const [showAI, setShowAI] = useState(false);
  const [showCrear, setShowCrear] = useState(false);
  const [grupoIdParaEvento, setGrupoIdParaEvento] = useState(null);
  const [grupoNombreParaEvento, setGrupoNombreParaEvento] = useState("");
  const [rodadaHacia, setRodadaHacia] = useState(null); // evento destino al crear una rodada hacia él
  const [anotarEvento, setAnotarEvento] = useState(null);
  const [detalleEvento, setDetalleEvento] = useState(null);
  const [editandoEvento, setEditandoEvento] = useState(null);
  // Pantalla de resultado al anotarse: { evento, cargando } | { evento, ok, motivo }
  const [resultadoAnotar, setResultadoAnotar] = useState(null);
  const [sortBy, setSortBy] = useState("fecha");
  const [vista, setVista] = useState("lista"); // 'lista' | 'calendario' | 'mapa'
  const [zona, setZona] = useState(""); // provincia elegida en los chips ("" = todo el país)
  const [showFiltros, setShowFiltros] = useState(false); // filtros plegados: la cabecera respira (feedback Cata 13-ago)
  // Botón atrás de Android: cierra lo que esté abierto en vez de salir de la app.
  // Estabas mirando un evento, tocabas atrás y se cerraba Olbos entera (15-ago).
  useBackGuard(!!(detalleEvento || anotarEvento || editandoEvento || resultadoAnotar), () => {
    if (resultadoAnotar) return setResultadoAnotar(null);
    if (anotarEvento) return setAnotarEvento(null);
    if (editandoEvento) return setEditandoEvento(null);
    if (detalleEvento) return setDetalleEvento(null);
  });
  // Rango de fechas: "voy a Salta del 10 al 15, ¿coincide algún evento?"
  const [filtroFechas, setFiltroFechas] = useState(false); // muestra/oculta los inputs
  const [rangoDesde, setRangoDesde] = useState("");
  const [rangoHasta, setRangoHasta] = useState("");
  const fmtCorta = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : "…";
  const rangoLabel = `${fmtCorta(rangoDesde)} – ${fmtCorta(rangoHasta)}`;
  // Un evento entra si su rango [fecha, fecha_fin] se superpone con [desde, hasta]
  const enRango = (e) => {
    if (!rangoDesde && !rangoHasta) return true;
    const ini = String(e.fecha || "").slice(0, 10);
    if (!ini) return false;
    const fin = String(e.fecha_fin || e.fecha).slice(0, 10);
    if (rangoDesde && fin < rangoDesde) return false;
    if (rangoHasta && ini > rangoHasta) return false;
    return true;
  };
  const [q, setQ] = useState(""); // búsqueda de texto instantánea
  // Búsqueda por destino: "me voy a Mendoza el finde". Lo que se escribe se geocodifica
  // y además de los que coinciden por texto aparecen los que caen cerca de ese punto,
  // aunque el evento no nombre la ciudad (un motoencuentro en Potrerillos es "Mendoza").
  const [destino, setDestino] = useState(null); // { nombre, lat, lng, formatted }
  const RADIO_DESTINO = 150; // km
  useEffect(() => {
    const t = String(q || "").trim();
    if (t.length < 4) { setDestino(null); return; }
    let vivo = true;
    const id = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(t)}`);
        if (!r.ok) { if (vivo) setDestino(null); return; }
        const d = await r.json();
        // Solo sirve si es un LUGAR (ciudad, partido, provincia). Si es aproximado o
        // devuelve el país entero, no es un destino: es texto que no existe en el mapa.
        const tipos = d?.types || [];
        const esLugar = tipos.some(x => ["locality", "sublocality", "administrative_area_level_1", "administrative_area_level_2", "administrative_area_level_3", "neighborhood", "natural_feature", "tourist_attraction"].includes(x));
        const esPais = tipos.includes("country") || String(d?.formatted || "").trim() === "Argentina";
        if (vivo) setDestino(d?.lat && esLugar && !esPais && !d.partial ? { nombre: t, lat: d.lat, lng: d.lng, formatted: d.formatted } : null);
      } catch (e) { if (vivo) setDestino(null); }
    }, 700);
    return () => { vivo = false; clearTimeout(id); };
  }, [q]);
  const [miPos, setMiPos] = useState(null); // ubicación del usuario para ordenar por cercanía
  const [buscandoPos, setBuscandoPos] = useState(false);

  // Pide la ubicación una vez y ordena por cercanía. Si la niega, vuelve a orden por fecha.
  // La posición global de la app (la pide al arrancar) sirve para el bloque "Cerca tuyo"
  // sin volver a pedir permiso
  useEffect(() => {
    if (userPos && userPos.lat && !miPos) setMiPos({ lat: userPos.lat, lng: userPos.lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPos]);
  const activarCercania = () => {
    if (miPos) { setSortBy("cercania"); setZona(""); return; }
    if (userPos && userPos.lat) { setMiPos({ lat: userPos.lat, lng: userPos.lng }); setSortBy("cercania"); setZona(""); return; }
    if (!navigator.geolocation) { alert("Tu dispositivo no permite ubicación."); return; }
    setBuscandoPos(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setMiPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setSortBy("cercania"); setBuscandoPos(false); },
      () => { setBuscandoPos(false); setSortBy("fecha"); alert("Necesitamos tu ubicación para ordenar los eventos por cercanía. Activala y probá de nuevo."); },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 }
    );
  };
  const kmHasta = (e) => (miPos && e.lat && e.lng) ? distanciaKm(miPos, { lat: Number(e.lat), lng: Number(e.lng) }) : null;
  // ─── Orden por relevancia ──────────────────────────────────────────────────
  // Con 150 eventos, una lista plana por fecha abruma y lo primero que ves puede
  // ser una rodada a 1.400 km. Se agrupa en bloques con título para que se
  // entienda POR QUÉ ves lo que ves (pedido de Carlos). Los follows todavía no
  // existen en la base (0 registros), así que hoy manda la cercanía; el bloque
  // "de quienes seguís" queda listo para cuando haya a quién seguir.
  const RADIO_CERCA = 100; // km
  // A quién sigo, para el primer bloque (hoy la tabla está vacía; queda armado).
  // Declarado ANTES de bloqueDe: si el estado queda debajo de quien lo usa, es
  // ReferenceError en tiempo de render y pantalla blanca (ya nos pasó).
  const [sigo, setSigo] = useState({});
  const diasHasta = (e) => {
    const f = String(e.fecha || "").slice(0, 10);
    if (!f) return null;
    const d = new Date(f + "T12:00:00");
    if (isNaN(d.getTime())) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const dias = Math.round((d.setHours(0, 0, 0, 0) - hoy) / 86400000);
    // Un evento de varios días que ya empezó y no terminó es HOY (día 0): el domingo
    // la Nocturna (10 y 11) caía en "Más adelante" con d = -1
    if (dias < 0 && e.fecha_fin) {
      const ff = new Date(String(e.fecha_fin).slice(0, 10) + "T12:00:00");
      if (!isNaN(ff.getTime()) && ff.setHours(0, 0, 0, 0) >= hoy.getTime()) return 0;
    }
    return dias;
  };
  // ¿Empezó y todavía no terminó? (multi-día, segundo día en adelante)
  const enCursoMultiDia = (e) => {
    if (!e.fecha || !e.fecha_fin || e.fecha_fin === e.fecha) return false;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const ini = new Date(String(e.fecha).slice(0, 10) + "T12:00:00"); ini.setHours(0, 0, 0, 0);
    const fin = new Date(String(e.fecha_fin).slice(0, 10) + "T12:00:00"); fin.setHours(0, 0, 0, 0);
    return ini < hoy && fin >= hoy;
  };
  const bloqueDe = (e) => {
    if (sigo[e.creator_id]) return 0;              // de un organizador que seguís
    const km = kmHasta(e);
    if (km != null && km <= RADIO_CERCA) return 1; // cerca tuyo
    const d = diasHasta(e);
    if (d != null && d >= 0 && d <= 7) return 2;   // pasa esta semana, aunque lejos
    return 3;                                      // el resto
  };
  const TITULO_BLOQUE = {
    0: "De quienes seguís",
    1: `Cerca tuyo · hasta ${RADIO_CERCA} km`,
    2: "Esta semana",
    3: "Más adelante",
  };
  const miUid = session?.user?.id || null;
  useEffect(() => {
    if (!miUid) { setSigo({}); return; }
    supabase.from("seguidores").select("following_id").eq("follower_id", miUid)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(f => { if (f.following_id) m[f.following_id] = true; });
        setSigo(m);
      }, () => setSigo({}));
  }, [miUid]);
  const [eventosDB, setEventosDB] = useState([]);
  const [contadores, setContadores] = useState({}); // { eventoId: count }
  const [flyerDataPendiente, setFlyerDataPendiente] = useState(null);

  // Si vienen datos pre-llenados desde un flyer detectado por la IA
  useEffect(() => {
    if (window.olbosFlyerEvento) {
      const data = window.olbosFlyerEvento;
      window.olbosFlyerEvento = null;
      setFlyerDataPendiente(data);
      setShowCrear(true);
    }
  }, []);

  // Abrir el creador directo (ej. desde la bienvenida post-reclamo: "Crear mi primera salida")
  useEffect(() => {
    if (window.olbosAbrirCrear) {
      window.olbosAbrirCrear = null;
      setShowCrear(true);
    }
    // "Crear una rodada hacia este evento": llega el evento destino ya cargado
    if (window.olbosRodadaHacia) {
      setRodadaHacia(window.olbosRodadaHacia);
      window.olbosRodadaHacia = null;
      setShowCrear(true);
    }
  }, []);
  // Escuchar el pedido mientras Eventos ya está montado (se dispara desde el detalle)
  useEffect(() => {
    const handler = (e) => { setRodadaHacia(e.detail); setDetalleEvento(null); setShowCrear(true); };
    window.addEventListener("olbos-rodada-hacia", handler);
    return () => window.removeEventListener("olbos-rodada-hacia", handler);
  }, []);

  // Si vienen desde un grupo, abrir el modal de crear con el grupo preseleccionado
  useEffect(() => {
    if (window.olbosPendingGrupoEvento) {
      const gId = window.olbosPendingGrupoEvento;
      const gNombre = window.olbosPendingGrupoEventoNombre || "";
      window.olbosPendingGrupoEvento = null;
      window.olbosPendingGrupoEventoNombre = null;
      setGrupoIdParaEvento(gId);
      setGrupoNombreParaEvento(gNombre);
      setShowCrear(true);
    }
  }, []);

  // Cargar inscripciones del usuario y contadores
  useEffect(() => {
    cargarContadores();
  }, []);

  // Cargar inscripciones cuando hay sesión activa
  useEffect(() => {
    if (session?.user) { cargarInscripciones(session.user.id); cargarGuardados(session.user.id); }
  }, [session]);

  const cargarGuardados = async (uid) => {
    const { data } = await supabase.from("eventos_guardados").select("evento_id").eq("user_id", uid);
    if (Array.isArray(data)) { const m = {}; data.forEach(g => { m[g.evento_id] = true; }); setGuardados(m); }
  };
  const toggleGuardado = async (e) => {
    if (!e || !e._fromDB) return;
    const uid = session?.user?.id;
    if (!uid) { if (requireAuth) requireAuth(() => {}); else window.dispatchEvent(new CustomEvent("olbos-abrir-registro")); return; }
    const estaba = !!guardados[e.id];
    setGuardados(g => { const n = { ...g }; if (estaba) delete n[e.id]; else n[e.id] = true; return n; });
    registrarUso("evento_guardado", { id: e.id, guardado: !estaba });
    const { error } = estaba
      ? await supabase.from("eventos_guardados").delete().eq("user_id", uid).eq("evento_id", e.id)
      : await supabase.from("eventos_guardados").upsert({ user_id: uid, evento_id: e.id }, { onConflict: "user_id,evento_id" });
    if (error) setGuardados(g => { const n = { ...g }; if (estaba) n[e.id] = true; else delete n[e.id]; return n; });
  };

  const cargarInscripciones = async (userId) => {
    const uid = userId || (await supabase.auth.getUser()).data?.user?.id;
    if (!uid) return;
    const { data } = await supabase.from("inscripciones")
      .select("evento_id, moto_id").eq("user_id", uid).eq("estado", "confirmado");
    if (data) {
      const map = {};
      data.forEach(i => { map[i.evento_id] = i.moto_id || "confirmado"; });
      setJoined(map);
    }
  };

  const cargarContadores = async () => {
    // RPC SECURITY DEFINER: cuenta TODAS las inscripciones confirmadas por evento.
    // (Con un select directo, RLS solo deja ver las propias → el card mostraba 1.)
    const { data } = await supabase.rpc("conteo_anotados");
    const obj = typeof data === "string" ? JSON.parse(data) : data;
    if (obj && typeof obj === "object") setContadores(obj);
  };

  // Devuelve { ok: true } o { ok: false, motivo } con el motivo en lenguaje humano:
  // el detalle técnico va a errores_app, no a la cara del rider.
  const anotarme = async (eventoId, motoId, datos, deslindeAceptado) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { ok: false, motivo: "Necesitás una cuenta para anotarte. Creala gratis y volvé a tocar Anotarme." };

    // Garantizar que el usuario tenga perfil antes de inscribir (evita el FK violation).
    // Esperamos el resultado de verdad (NO lo tragamos) para saber si falló.
    let { error: errPerfil } = await supabase.rpc("asegurar_perfil");

    // Snapshot privado de los datos + deslinde (solo lo ve el organizador), y de paso
    // actualiza la ficha del rider con lo que cargó, para reusar la próxima vez.
    if (datos) {
      await supabase.from("inscripcion_datos").upsert({
        evento_id: eventoId, user_id: user.id, datos,
        deslinde_aceptado: !!deslindeAceptado,
        deslinde_en: deslindeAceptado ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      }).then(() => {}, () => {});
      const fichaUpd = { user_id: user.id, updated_at: new Date().toISOString() };
      ["nombre_completo", "dni", "telefono", "casco", "emergencia_nombre", "emergencia_relacion", "emergencia_telefono", "condicion_medica", "medicacion", "localidad", "direccion"]
        .forEach(k => { if (datos[k] !== undefined) fichaUpd[k] = datos[k] || null; });
      if (Object.keys(fichaUpd).length > 2) {
        await supabase.from("rider_fichas").upsert(fichaUpd).then(() => {}, () => {});
      }
      // Y el perfil público se completa con lo que cargó, solo donde estaba vacío
      // (pedido de Carlos, 15-sep): hoy muchos perfiles no tienen ni el nombre.
      try {
        const nombre = String(datos.nombre_completo || "").trim();
        const localidad = String(datos.localidad || "").trim();
        if (nombre || localidad) {
          const { data: pf } = await supabase.from("perfiles").select("nombre, ciudad").eq("id", user.id).maybeSingle();
          const upd = {};
          if (pf && nombre && !String(pf.nombre || "").trim()) upd.nombre = nombre.slice(0, 80);
          if (pf && localidad && !String(pf.ciudad || "").trim()) upd.ciudad = localidad.slice(0, 80);
          if (Object.keys(upd).length) await supabase.from("perfiles").update(upd).eq("id", user.id);
        }
      } catch (e) { /* el perfil se completa si se puede; la inscripción sigue */ }
    }

    // Verificar si ya está anotado
    const { data: existing } = await supabase.from("inscripciones")
      .select("id").eq("evento_id", eventoId).eq("user_id", user.id).maybeSingle();
    if (existing) {
      setJoined(j => ({ ...j, [eventoId]: motoId || "confirmado" }));
      return { ok: true, yaEstaba: true };
    }
    const insertInscripcion = () => supabase.from("inscripciones").insert({
      evento_id: eventoId,
      user_id: user.id,
      moto_id: null, // moto_id puede ser null si las motos no tienen UUID
      estado: "confirmado",
    });

    let { error } = await insertInscripcion();

    // Si falló por la foreign key (el perfil todavía no estaba creado), re-asegurar el
    // perfil y reintentar UNA vez. Cubre el timing de un registro recién hecho.
    if (error && /foreign key|fkey|violates|constraint/i.test(error.message || "")) {
      const { error: e2 } = await supabase.rpc("asegurar_perfil");
      errPerfil = e2 || errPerfil;
      await new Promise(r => setTimeout(r, 400)); // pequeño respiro por si la creación tarda
      ({ error } = await insertInscripcion());
    }

    if (!error) {
      setJoined(j => ({ ...j, [eventoId]: motoId || "confirmado" }));
      setContadores(c => ({ ...c, [eventoId]: (c[eventoId] || 0) + 1 }));
      registrarUso("inscripcion_ok", { id: eventoId, con_datos: !!datos });
      return { ok: true };
    }
    if (/evento_id_fkey/i.test(error.message || "")) {
      // El EVENTO ya no existe (lo borró el organizador o se fusionó un duplicado)
      // y el usuario tenía la lista vieja en pantalla. No es un problema del perfil.
      window.dispatchEvent(new CustomEvent("olbos-recargar-eventos"));
      return { ok: false, motivo: "Ese evento ya no está disponible: puede haberse dado de baja o unificado con otro. Te actualizamos la agenda." };
    }
    console.error("Error inscripcion:", error.message, "| asegurar_perfil:", errPerfil?.message);
    reportarError(`inscripcion: ${error.message}${errPerfil ? ` | perfil: ${errPerfil.message}` : ""}`, null, "anotarme", { evento: eventoId });
    const m = String(error.message || "").toLowerCase();
    return {
      ok: false,
      motivo: /fetch|network|failed/.test(m)
        ? "Parece que no hay señal. Probá de nuevo en unos segundos."
        : "No pudimos anotarte: tu cuenta se está terminando de crear. Esperá unos segundos y probá de nuevo. Si sigue sin andar, cerrá sesión y volvé a entrar.",
    };
  };

  // La regla vive en inscripcionDirectaPosible (la usa también el link "sumate")
  const esInscripcionDirecta = inscripcionDirectaPosible;

  // "Anotarme": sin sesión guarda la intención y pide la cuenta; con sesión inscribe
  // directo (un toque) o abre el formulario si el organizador pidió datos.
  const intentarAnotar = async (e, opts = {}) => {
    if (!e) return;
    registrarUso("anotarme_tap", { id: e.id, con_sesion: !!session, directo: esInscripcionDirecta(e) });
    if (!session) {
      try {
        localStorage.setItem("olbos_pending_anotar", e.id);
        localStorage.setItem("olbos_ref_evento", e.id);
        localStorage.setItem("olbos_ref_evento_ts", String(Date.now()));
      } catch (err) { /* */ }
      if (requireAuth) requireAuth(() => {}); else window.dispatchEvent(new CustomEvent("olbos-abrir-registro"));
      return;
    }
    if (!opts.forzarModal && esInscripcionDirecta(e)) {
      setResultadoAnotar({ evento: e, cargando: true });
      const r = await anotarme(e.id, null, null, false);
      setResultadoAnotar({ evento: e, ok: !!r.ok, motivo: r.motivo || "" });
      return;
    }
    setDetalleEvento(null);
    setAnotarEvento(e);
  };
  // Intención de anotarse que quedó pendiente del registro (QR del stand → cuenta →
  // bienvenida → "Anotarme a la Nocturna"): apenas la ficha está abierta con sesión, va.
  const [anotarPendTick, setAnotarPendTick] = useState(0);
  useEffect(() => {
    const h = () => setAnotarPendTick(n => n + 1);
    window.addEventListener("olbos-anotar-pendiente", h);
    return () => window.removeEventListener("olbos-anotar-pendiente", h);
  }, []);
  useEffect(() => {
    const pend = typeof window !== "undefined" ? window.olbosPendingAnotar : null;
    if (!pend || !detalleEvento || detalleEvento.id !== pend || !session) return;
    window.olbosPendingAnotar = null;
    if (joined[pend]) return; // ya estaba anotado
    intentarAnotar(detalleEvento);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleEvento?.id, session?.user?.id, anotarPendTick]);

  const cancelarInscripcion = async (eventoId) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    // RPC SECURITY DEFINER: borra la inscripción (la RLS no deja DELETE directo, por eso
    // antes la baja no se grababa). Solo actualizamos la UI si realmente se borró.
    const { data: raw, error } = await supabase.rpc("cancelar_inscripcion", { p_evento_id: eventoId });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (r && r.ok === false)) {
      alert("No pudimos darte de baja. Probá de nuevo en un momento.");
      return;
    }
    setJoined(j => { const n = { ...j }; delete n[eventoId]; return n; });
    setContadores(c => ({ ...c, [eventoId]: Math.max((c[eventoId] || 1) - 1, 0) }));
  };

  const mapearEvento = (e) => ({
    ...e,
    title: e.titulo,
    from: e.desde || e.lugar || "",
    to: e.hasta || "",
    date: e.fecha ? new Date(e.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" }) : "",
    km: e.km || 0,
    diff: e.dificultad || "",
    precio: e.precio || 0,
    cc_min: 0,
    camino: e.tipo_camino || "",
    distKm: 0,
    org: "olbos",
    org_rank: 5,
    org_eventos: 1,
    inscripcion: "abierta",
    cupos: e.max_riders || null,
    cupos_ilimitado: !e.max_riders,
    riders: 0,
    max: e.max_riders || 999,
    tags: [],
    genero: "Mixto",
    visibilidad: "Visible para todos",
    img: e.foto_url || null,
    medias: e.medias || [],
    emoji: e.tipo === "evento" ? "📍" : "🛣️",
    puntos: e.puntos || [],
    equipo: e.equipo || null,
    apoyo_ruta: e.apoyo_ruta || false,
    ida_vuelta: e.ida_vuelta || false,
    _fromDB: true,
  });

  const [eventosCargados, setEventosCargados] = useState(false); // primera respuesta recibida (con señal mala tarda)
  const [recargaEventos, setRecargaEventos] = useState(0); // bump = volver a pedir la lista (ej: evento borrado detectado al anotarse)
  useEffect(() => {
    const recargar = () => setRecargaEventos(n => n + 1);
    window.addEventListener("olbos-recargar-eventos", recargar);
    return () => window.removeEventListener("olbos-recargar-eventos", recargar);
  }, []);
  // UNA sola forma de armar la agenda: públicos + los "solo invitados" que me
  // tocan (míos o donde me invitaron). Antes la carga inicial hacía esto pero
  // las recargas de después de crear/editar solo traían los públicos, así que
  // un evento privado DESAPARECÍA de la lista al guardarlo, hasta recargar la
  // página (reporte de Matías).
  const traerEventosAgenda = async () => {
    const { data } = await supabase.from("eventos").select(COLS_EVENTO_LISTA).is("grupo_id", null).neq("visibilidad_tipo", "invitados").order("fecha", { ascending: true });
    let lista = (data || []).map(mapearEvento);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const [{ data: invs }, { data: mios }] = await Promise.all([
        supabase.from("evento_invitados").select("evento_id").eq("user_id", user.id),
        supabase.from("eventos").select(COLS_EVENTO_LISTA).eq("creator_id", user.id).eq("visibilidad_tipo", "invitados"),
      ]);
      let privados = mios || [];
      const ids = (invs || []).map(i => i.evento_id);
      if (ids.length) {
        const { data: evs } = await supabase.from("eventos").select(COLS_EVENTO_LISTA).in("id", ids);
        privados = [...privados, ...(evs || [])];
      }
      const yaIds = new Set(lista.map(e => e.id));
      privados.filter(e => !yaIds.has(e.id)).forEach(e => { yaIds.add(e.id); lista.push(mapearEvento(e)); });
      lista.sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
    }
    return lista;
  };
  useEffect(() => {
    (async () => {
      try { setEventosDB(await traerEventosAgenda()); }
      catch (e) { /* sin conexión: el estado de carga igual cierra */ }
      setEventosCargados(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recargaEventos]);

  // La ficha SIEMPRE con la fila fresca: el objeto de la lista se cargó al abrir la app y
  // el estado de la rodada, el check-in, los roles o la config pueden haber cambiado
  // (el domingo a las 20:05 la ficha decía "por largar"). Se trae al abrir y cada vez que
  // un panel de la ficha cambia algo (olbos-evento-cambiado); la fila de la lista también.
  useEffect(() => {
    const id = detalleEvento?.id;
    if (!id || !detalleEvento?._fromDB) return;
    let vivo = true;
    const traer = async () => {
      const { data } = await supabase.from("eventos").select("*").eq("id", id).maybeSingle();
      if (!vivo || !data) return;
      const fresco = mapearEvento(data);
      setDetalleEvento(d => (d && d.id === id) ? { ...d, ...fresco } : d);
      setEventosDB(l => l.some(e => e.id === id) ? l.map(e => e.id === id ? { ...e, ...fresco } : e) : l);
    };
    traer();
    const h = (e) => { if (e.detail && e.detail.id === id) traer(); };
    window.addEventListener("olbos-evento-cambiado", h);
    return () => { vivo = false; window.removeEventListener("olbos-evento-cambiado", h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleEvento?.id]);

  // Abrir un evento pedido desde otra sección (post del feed, evento de grupo).
  // Se busca por id directo en la base: sirve también para eventos de grupo, que
  // no están en el listado público, y abre el MISMO detalle que cualquier evento.
  useEffect(() => {
    const abrirPendiente = async () => {
      const id = window.olbosPendingAbrirEvento;
      if (!id) return;
      window.olbosPendingAbrirEvento = null;
      const { data } = await supabase.from("eventos").select("*").eq("id", id).maybeSingle();
      if (data) setDetalleEvento(mapearEvento(data));
    };
    abrirPendiente();
    window.addEventListener("olbos-eventos-abrir-pendiente", abrirPendiente);
    return () => window.removeEventListener("olbos-eventos-abrir-pendiente", abrirPendiente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-abrir el evento que viene por deep-link desde la página pública (?evento=ID)
  useEffect(() => {
    const id = typeof window !== "undefined" ? window.olbosAbrirEventoId : null;
    if (!id || eventosDB.length === 0) return;
    const ev = eventosDB.find(e => e.id === id);
    if (ev) { setDetalleEvento(ev); window.olbosAbrirEventoId = null; }
  }, [eventosDB]);

  const todosEventos = [...EVENTS, ...eventosDB];

  // Separar pasados de próximos: un evento es pasado si su fecha es anterior a hoy a las 00:00
  const hoyInicio = new Date();
  hoyInicio.setHours(0, 0, 0, 0);
  const esPasado = (e) => {
    const f = e.fecha_fin || e.fecha; // si dura varios días, cuenta la fecha de fin
    if (!f) return false; // sin fecha = lo tratamos como vigente
    // Mediodía LOCAL (no medianoche UTC) para que una salida de HOY no caiga como pasada por TZ
    return new Date(String(f).slice(0, 10) + "T12:00:00") < hoyInicio;
  };

  // "Voy" = donde estoy anotado · "Míos" = los que organizo yo
  const filters = ["Todos", "Eventos", "Salidas", "Gratis", ...(session?.user ? ["Voy", "Guardados", "Míos"] : []), "Realizados"];

  // ── Zonas: de qué provincia es cada evento ──────────────────────────────────
  // El chip de provincia se arma con los eventos que HAY, no con una lista fija:
  // si no hay nada en San Juan, San Juan no aparece. La provincia se deduce del
  // texto del lugar (que casi siempre viene de Google Places con la provincia) y,
  // si el texto no alcanza, de la coordenada contra el centro de cada provincia.
  const ZONAS = [
    { n: "CABA", alias: ["caba", "capital federal", "ciudad autonoma de buenos aires", "ciudad de buenos aires"], lat: -34.6037, lng: -58.3816, r: 20 },
    { n: "Buenos Aires", alias: ["buenos aires", "provincia de buenos aires"], lat: -36.6, lng: -60.0, r: 480 },
    { n: "Córdoba", alias: ["cordoba"], lat: -31.9, lng: -64.2, r: 300 },
    { n: "Santa Fe", alias: ["santa fe"], lat: -30.9, lng: -60.9, r: 320 },
    { n: "Entre Ríos", alias: ["entre rios"], lat: -31.8, lng: -59.0, r: 250 },
    { n: "Mendoza", alias: ["mendoza"], lat: -34.6, lng: -68.5, r: 320 },
    { n: "San Luis", alias: ["san luis"], lat: -33.7, lng: -66.0, r: 250 },
    { n: "San Juan", alias: ["san juan"], lat: -30.9, lng: -68.9, r: 250 },
    { n: "La Rioja", alias: ["la rioja"], lat: -29.7, lng: -67.1, r: 250 },
    { n: "Catamarca", alias: ["catamarca"], lat: -27.3, lng: -66.9, r: 260 },
    { n: "Tucumán", alias: ["tucuman"], lat: -26.9, lng: -65.3, r: 150 },
    { n: "Santiago del Estero", alias: ["santiago del estero"], lat: -27.8, lng: -63.3, r: 280 },
    { n: "Salta", alias: ["salta"], lat: -24.6, lng: -65.0, r: 300 },
    { n: "Jujuy", alias: ["jujuy"], lat: -23.6, lng: -65.4, r: 180 },
    { n: "Chaco", alias: ["chaco"], lat: -26.5, lng: -60.5, r: 260 },
    { n: "Formosa", alias: ["formosa"], lat: -25.0, lng: -59.9, r: 260 },
    { n: "Corrientes", alias: ["corrientes"], lat: -28.7, lng: -57.9, r: 250 },
    { n: "Misiones", alias: ["misiones"], lat: -26.9, lng: -54.6, r: 200 },
    { n: "La Pampa", alias: ["la pampa"], lat: -37.1, lng: -65.5, r: 300 },
    { n: "Neuquén", alias: ["neuquen"], lat: -38.7, lng: -70.0, r: 300 },
    { n: "Río Negro", alias: ["rio negro"], lat: -40.3, lng: -67.0, r: 380 },
    { n: "Chubut", alias: ["chubut"], lat: -43.8, lng: -68.5, r: 400 },
    { n: "Santa Cruz", alias: ["santa cruz"], lat: -48.8, lng: -70.0, r: 450 },
    { n: "Tierra del Fuego", alias: ["tierra del fuego", "ushuaia"], lat: -54.5, lng: -67.5, r: 250 },
  ];
  // Orden de confianza (bug del 17-sep: el Motoencuentro Lobos no aparecía en
  // "Buenos Aires" porque su descripción decía "Cabañas del Lago" y "cabanas"
  // contiene "caba" → CABA, que va primera en la lista). Ahora: (1) el lugar,
  // que casi siempre trae la provincia; (2) la coordenada; (3) el título; y recién
  // (4) la descripción, que puede nombrar cualquier provincia de paso. Y los
  // alias se buscan como palabra entera, no como pedazo de otra palabra.
  const zonaPorTexto = (t) => {
    if (!t) return null;
    for (const z of ZONAS) {
      for (const a of z.alias) {
        const i = t.indexOf(a);
        if (i < 0) continue;
        const antes = i === 0 ? " " : t[i - 1], despues = t[i + a.length] || " ";
        if (!/[a-z0-9]/.test(antes) && !/[a-z0-9]/.test(despues)) return z.n;
      }
    }
    return null;
  };
  const zonaPorCoord = (e) => {
    if (!e.lat || !e.lng) return null;
    const p = { lat: Number(e.lat), lng: Number(e.lng) };
    let mejor = null, mejorD = Infinity;
    for (const z of ZONAS) {
      const d = distanciaKm(p, { lat: z.lat, lng: z.lng });
      if (d <= z.r && d < mejorD) { mejor = z.n; mejorD = d; }
    }
    return mejor;
  };
  const zonaDe = (e) => {
    return zonaPorTexto(normTxt([e.lugar, e.to, e.hasta].filter(Boolean).join(" ")))
      || zonaPorCoord(e)
      || zonaPorTexto(normTxt([e.title, e.titulo, e.from, e.desde].filter(Boolean).join(" ")))
      || zonaPorTexto(normTxt(e.descripcion || ""))
      || null;
  };


  // Búsqueda de texto: sin acentos, todas las palabras tienen que aparecer en algún campo.
  // Cuando hay búsqueda manda sobre los filtros y también mira los eventos pasados.
  const normTxt = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const qn = normTxt(q).trim();
  const matchQ = (e) => {
    if (!qn) return true;
    const heno = normTxt([
      e.title, e.titulo, e.descripcion, e.lugar, e.from, e.to, e.desde, e.hasta,
      e.diff, e.tipo, e.equipo, e.org,
    ].filter(Boolean).join(" "));
    return qn.split(/\s+/).every(t => heno.includes(t));
  };
  const cercaDelDestino = (e) => {
    if (!destino || !e.lat || !e.lng) return false;
    return distanciaKm(destino, { lat: Number(e.lat), lng: Number(e.lng) }) <= RADIO_DESTINO;
  };

  // El orden por relevancia manda solo en la vista limpia: apenas el usuario pone
  // un filtro, una provincia, un rango de fechas o el orden por cercanía, respeta
  // lo que eligió él.
  const porRelevancia = vista === "lista" && !qn && filter === "Todos" && !zona
    && !rangoDesde && !rangoHasta && sortBy === "fecha";
  const sorted = vista !== "lista" ? []
    : qn ? [...todosEventos]
        .filter(e => matchQ(e) || cercaDelDestino(e))
        // Con texto se busca en TODO el país: la provincia preseleccionada (GPS/perfil) escondía
        // las caravanas de otras provincias sin que se viera qué filtraba (chips ocultos con q)
        .filter(enRango)
        .sort((a, b) => {
          const pa = esPasado(a), pb = esPasado(b);
          if (pa !== pb) return pa ? 1 : -1; // próximos primero
          // Los que coinciden por texto van antes que los que solo caen cerca del destino
          const ta = matchQ(a), tb = matchQ(b);
          if (ta !== tb) return ta ? -1 : 1;
          return new Date(a.fecha || 0) - new Date(b.fecha || 0);
        })
    : filter === "Guardados"
    ? [...todosEventos]
        .filter(e => !!guardados[e.id])
        .sort((a, b) => {
          const pa = esPasado(a), pb = esPasado(b);
          if (pa !== pb) return pa ? 1 : -1; // próximos primero, lo que ya pasó abajo
          return pa ? new Date(b.fecha || 0) - new Date(a.fecha || 0) : new Date(a.fecha || 0) - new Date(b.fecha || 0);
        })
    : filter === "Voy"
    ? [...todosEventos]
        .filter(e => !!joined[e.id])
        .sort((a, b) => {
          // Lo que viene primero; abajo el historial de los que ya fueron
          const pa = esPasado(a), pb = esPasado(b);
          if (pa !== pb) return pa ? 1 : -1;
          return pa
            ? new Date(b.fecha || 0) - new Date(a.fecha || 0)
            : new Date(a.fecha || 0) - new Date(b.fecha || 0);
        })
    : filter === "Míos"
    ? [...todosEventos]
        .filter(e => session?.user && e.creator_id === session.user.id)
        .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0)) // todos los propios, próximos y pasados
    : filter === "Realizados"
    ? [...todosEventos]
        .filter(esPasado)
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha)) // más reciente primero
    : [...todosEventos]
        .filter(e => !esPasado(e))
        .filter(e => {
          if (filter === "Todos") return true;
          if (filter === "Eventos") return e.tipo === "evento";
          if (filter === "Salidas") return e.tipo === "salida" || !e.tipo;
          if (filter === "Gratis") return !e.precio || e.precio === 0;
          if (filter === "Cerca") return e.distKm < 100;
          return true;
        })
        .filter(e => !zona || e.destacado || zonaDe(e) === zona) // un destacado se ve en todas las provincias (Carlos, 08-oct)
        .filter(enRango)
        .sort((a, b) => {
          // Destacados (fijados por admin) arriba de todo — SALVO con filtro de fechas:
          // ahí el pinning duplicaba la cabecera del día y parecía que el filtro no andaba
          if (!rangoDesde && !rangoHasta && !!a.destacado !== !!b.destacado) return a.destacado ? -1 : 1;
          // Vista por defecto: primero por bloque de relevancia, y dentro de cada
          // bloque por fecha. Con cualquier filtro puesto manda el criterio elegido.
          if (porRelevancia) {
            const ba = bloqueDe(a), bb = bloqueDe(b);
            if (ba !== bb) return ba - bb;
          }
          // Por cercanía: los que tienen coordenadas primero, del más cerca al más lejos
          if (sortBy === "cercania" && miPos) {
            const da = kmHasta(a), db = kmHasta(b);
            if (da == null && db == null) return 0;
            if (da == null) return 1;
            if (db == null) return -1;
            return da - db;
          }
          if (!a.fecha || !b.fecha) return 0;
          return new Date(a.fecha) - new Date(b.fecha);
        });

  // Los filtros también aplican en mapa y calendario: si filtrás entre fechas o por
  // provincia y cambiás de vista, ves lo mismo filtrado (feedback Cata 13-ago)
  const filtradosVista = todosEventos
    .filter(e => qn ? (matchQ(e) || cercaDelDestino(e)) : true)
    .filter(e => {
      if (qn || filter === "Todos" || filter === "Realizados") return true;
      if (filter === "Voy") return !!joined[e.id];
      if (filter === "Guardados") return !!guardados[e.id];
      if (filter === "Míos") return session?.user && e.creator_id === session.user.id;
      if (filter === "Eventos") return e.tipo === "evento";
      if (filter === "Salidas") return e.tipo === "salida" || !e.tipo;
      if (filter === "Gratis") return !e.precio || e.precio === 0;
      if (filter === "Cerca") return e.distKm < 100;
      return true;
    })
    .filter(e => qn || !zona || e.destacado || zonaDe(e) === zona) // un destacado se ve en todas las provincias (Carlos, 08-oct); con texto, todo el país
    .filter(enRango);

  // Chips de provincia: solo las que tienen eventos próximos, ordenadas por cantidad.
  // Si todo está en una sola provincia no tiene sentido mostrar el filtro.
  const zonasConEventos = (() => {
    const cuenta = {};
    const futurosIds = {};
    todosEventos.forEach(e => { if (!esPasado(e)) futurosIds[e.id] = true; });
    todosEventos.filter(e => !esPasado(e) && !(e.evento_padre_id && futurosIds[e.evento_padre_id])).forEach(e => {
      const z = zonaDe(e);
      if (z) cuenta[z] = (cuenta[z] || 0) + 1;
    });
    return Object.entries(cuenta).sort((a, b) => b[1] - a[1]);
  })();

  // ── Agenda por cercanía ────────────────────────────────────────────────────
  // Si el rider ya cargó su ciudad, la agenda abre en su provincia en lugar de
  // mezclar todo el país. No pide GPS ni permisos: usa el dato del perfil. Se aplica
  // una sola vez por apertura y siempre queda a la vista cómo volver a todo el país.
  const [miCiudad, setMiCiudad] = useState(null);
  const [zonaAuto, setZonaAuto] = useState(null);
  const zonaAutoHechaRef = useRef(false);
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) return;
    supabase.from("perfiles").select("ciudad").eq("id", uid).maybeSingle()
      .then(({ data }) => setMiCiudad((data && data.ciudad) || ""), () => setMiCiudad(""));
  }, [session]);
  // Provincia deducida (perfil o GPS) para el chip, y elección recordada en el teléfono
  const [zonaDetectada, setZonaDetectada] = useState(null);
  const elegirZona = (z) => {
    setZona(z); setZonaAuto(null);
    try { localStorage.setItem("olbos_eventos_zona", z || ""); } catch (e) { /* */ }
  };
  useEffect(() => {
    if (zonasConEventos.length === 0) return;
    // 1) ciudad del perfil; 2) la posición (GPS global de la app, también sin sesión)
    let z = null;
    if (miCiudad) { const t = normTxt(miCiudad); const zz = ZONAS.find(x => x.alias.some(a => t.includes(a))); z = zz ? zz.n : null; }
    if (!z && userPos && userPos.lat) z = zonaPorCoord({ lat: userPos.lat, lng: userPos.lng });
    if (z && zonasConEventos.some(([n]) => n === z)) setZonaDetectada(z);
    if (zonaAutoHechaRef.current || zona) return;
    // Elección recordada: manda sobre todo ("" = eligió a mano todo el país)
    let rec = null;
    try { rec = localStorage.getItem("olbos_eventos_zona"); } catch (e) { /* */ }
    if (rec !== null) {
      zonaAutoHechaRef.current = true;
      if (rec && zonasConEventos.some(([n]) => n === rec)) setZona(rec);
      return;
    }
    if (session?.user && miCiudad === null) return; // la ciudad del perfil todavía no llegó
    if (!z) { if (!userPos && !miCiudad) return; zonaAutoHechaRef.current = true; return; } // sin dato (el GPS puede llegar después)
    zonaAutoHechaRef.current = true;
    // Si en su provincia no hay nada cargado, mejor todo el país que una lista vacía
    if (!zonasConEventos.some(([n]) => n === z)) return;
    setZona(z);
    setZonaAuto(z);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miCiudad, userPos, zonasConEventos.length, session?.user?.id]);

  // Verificación en perfil, no en evento
  const USER_VERIFICADO = true;

  const handleCrearEvento = async (form) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      // Antes: silencio total (se podía llenar todo y Publicar no hacía nada)
      alert("Para publicar necesitás una cuenta en Olbos (gratis, un minuto). Lo que cargaste queda guardado como borrador.");
      if (requireAuth) requireAuth();
      return { ok: false, motivo: "sin_sesion" };
    }

    // Helper: geocodificar un nombre a {lat, lng}
    const geocodear = async (nombre) => {
      if (!nombre) return null;
      try {
        const res = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(nombre + ", Argentina")}`);
        const data = await res.json();
        if (data && data.lat && data.lng) return { lat: data.lat, lng: data.lng };
      } catch (e) { /* silenciar */ }
      return null;
    };

    // Geocodificar puntos de salida que no tengan coords
    let puntosFinal = form.puntos || [];
    if (puntosFinal.length > 0) {
      puntosFinal = await Promise.all(puntosFinal.map(async (p) => {
        if (p.lat && p.lng) return p;
        const c = await geocodear(p.nombre);
        return c ? { ...p, lat: c.lat, lng: c.lng } : p;
      }));
    }

    // Coords top-level: para evento fijo usar las del picker si vienen, sino geocodear
    let lat = null, lng = null;
    if (form.tipo === "evento") {
      if (form.lugar_lat && form.lugar_lng) {
        lat = form.lugar_lat; lng = form.lugar_lng;
      } else if (form.lugar) {
        const c = await geocodear(form.lugar);
        if (c) { lat = c.lat; lng = c.lng; }
      }
    } else if (puntosFinal.length > 0) {
      const inicio = puntosFinal.find(p => p.tipo === "salida" || p.tipo === "inicio") || puntosFinal[0];
      if (inicio && inicio.lat && inicio.lng) { lat = inicio.lat; lng = inicio.lng; }
    }

    // Polyline real de Directions API (solo para salidas con al menos 2 puntos con coords)
    let polyline = null;
    if (form.tipo !== "evento") {
      const conCoords = puntosFinal.filter(p => p.lat && p.lng);
      if (conCoords.length >= 2) {
        // Máx 23 intermedios (límite de Directions, como en la edición) y un reintento:
        // un fallo pasajero dejaba la salida sin línea negra (documento de Matías, N9)
        const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
        const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
        const waypoints = conCoords.slice(1, -1).slice(0, 23).map(p => `${p.lat},${p.lng}`).join("|");
        const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`;
        for (let intento = 0; intento < 2 && !polyline; intento++) {
          try {
            const res = await fetch(`${API_BASE}/api/directions?${qs}`);
            const data = await res.json();
            // Soportar tanto respuesta raw de Google como una versión simplificada del proxy
            polyline = data?.routes?.[0]?.overview_polyline?.points || data?.polyline || null;
          } catch (e) { /* silenciar — fallback a líneas rectas */ }
        }
      }
    }

    const filaEvento = {
      creator_id: user.id,
      titulo: form.titulo,
      tipo: form.tipo || "salida",
      descripcion: form.descripcion,
      fecha: form.fecha,
      fecha_fin: form.fecha_fin || null,
      hora: form.hora || null,
      desde: form.desde || null,
      hasta: form.hasta || null,
      lugar: form.lugar || null,
      lat: lat,
      lng: lng,
      km: form.km ? Number(form.km) : null,
      dificultad: form.dificultad || null,
      tipo_camino: form.camino || null,
      max_riders: (!form.cupos_ilimitado && form.cupos) ? Number(form.cupos) : null,
      precio: form.precio ? Number(form.precio) : 0,
      precio_entrada: form.precio_entrada ? Number(form.precio_entrada) : null,
      pago_link: (form.pago_link || "").trim() || null,
      pago_info: (form.pago_info || "").trim() || null,
      foto_url: form.flyer || null,
      medias: (form.medias || []).filter(m => m.url && !m.failed && !m.uploading).map(m => ({ url: m.url, type: m.type })),
      puntos: puntosFinal.length > 0 ? puntosFinal : null,
      polyline: polyline,
      telefono: form.telefono || null,
      equipo: form.equipo || null,
      apoyo_ruta: form.apoyo_ruta || false,
      ida_vuelta: form.ida_vuelta || false,
      grupo_id: form.grupo_id || null,
      evento_padre_id: form.evento_padre_id || null,
      inscripcion_config: form.inscripcion_config || {},
      tiene_sorteo: !!form.tiene_sorteo,
      visibilidad_tipo: form.visibilidad_tipo || "publico",
      ...(form.negocio_id ? { negocio_id: form.negocio_id } : {}),
      cobra_negocio_id: form.cobra_negocio_id || null,
    };
    let eventoCreado = null, error = null;
    if (form.publicar_como) {
      const r = await supabase.rpc("crear_evento_como", { p_cuenta: form.publicar_como, p_evento: filaEvento });
      error = r.error;
      const d = typeof r.data === "string" ? JSON.parse(r.data) : r.data;
      if (!error && d && d.error) error = { message: d.error };
      eventoCreado = d && !d.error ? d : null;
    } else {
      ({ data: eventoCreado, error } = await supabase.from("eventos").insert(filaEvento).select().single());
    }
    if (error) {
      alert("Error al publicar: " + error.message);
      return { ok: false, motivo: error.message };
    } else {
      // El creador queda anotado a su propia salida: el bloque "En la rodada" y la largada
      // le aparecían solo si se acordaba de anotarse (organizadores de caravanas, 09-oct).
      // Error MIRADO (un insert tragado en silencio ya nos costó los km pasivos).
      if (eventoCreado && !form.publicar_como) {
        const { error: eIns } = await supabase.from("inscripciones").insert({ evento_id: eventoCreado.id, user_id: user.id, moto_id: null, estado: "confirmado" });
        if (eIns) {
          if (!/duplicate|unique/i.test(eIns.message || "")) reportarError(`auto-inscripcion creador: ${eIns.message}`, null, "handleCrearEvento", { evento: eventoCreado.id });
        } else {
          setJoined(j => ({ ...j, [eventoCreado.id]: "confirmado" }));
          setContadores(c => ({ ...c, [eventoCreado.id]: (c[eventoCreado.id] || 0) + 1 }));
        }
      }
      // Evento solo-invitados: dar de alta la lista (solo el creador puede, por RLS)
      if (form.visibilidad_tipo === "invitados" && Array.isArray(form.invitados_ids) && form.invitados_ids.length > 0 && eventoCreado) {
        await supabase.from("evento_invitados").insert(
          form.invitados_ids.map(uid => ({ evento_id: eventoCreado.id, user_id: uid, invitado_por: user.id }))
        ).then(() => {}, () => {});
      }
      // Si el usuario marcó publicar en feed, crear el post automáticamente
      if (form.publicar_en_feed && eventoCreado) {
        const textoPost = `📅 Nuevo evento: ${form.titulo}\n${form.fecha ? new Date(form.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : ""}${form.lugar ? `\n📍 ${form.lugar}` : ""}${form.descripcion ? `\n\n${form.descripcion}` : ""}`;
        const mediaUrl = form.flyer || (form.medias?.[0]?.url) || null;
        const mediaType = form.flyer ? (esVideoPortada(form.flyer) ? "video" : "image") : (form.medias?.[0]?.type || null);
        // Con evento_id el post lleva el botón "Ver el evento y anotarse" —
        // antes decía "buscalo en la sección Eventos" y te mandaba a revolver.
        const postEvento = { texto: textoPost, media_url: mediaUrl, media_type: mediaType, categoria: "evento", evento_id: eventoCreado.id };
        if (form.publicar_como) await supabase.rpc("publicar_como", { p_cuenta: form.publicar_como, p_post: postEvento }).then(() => {}, () => {});
        else await supabase.from("posts").insert({ user_id: user.id, ...postEvento });
      }
      setShowCrear(false);
      setGrupoIdParaEvento(null);
      setGrupoNombreParaEvento("");
      // La rodada hacia un evento y el flyer leído por la IA se limpian: si no, el
      // próximo "+ Crear" volvía a abrir como "Rodada a <evento>" con el destino puesto
      setRodadaHacia(null);
      setFlyerDataPendiente(null);
      traerEventosAgenda().then(l => setEventosDB(l), () => {});
      return { ok: true, evento: eventoCreado };
    }
  };

  // Las caravanas atadas a un evento tienen que seguirlo. Se recalcula el camino de
  // cada una con el destino nuevo: mover el punto sin rehacer el trazado dejaría la
  // línea vieja yendo al lugar viejo, que es peor que no tocar nada.
  const propagarACaravanas = async (id, previo, cambios) => {
    const cambioFecha = cambios.fecha && String(cambios.fecha).slice(0, 10) !== String(previo?.fecha || "").slice(0, 10);
    const cambioLugar = (cambios.lugar != null && cambios.lugar !== previo?.lugar)
      || (cambios.lat != null && Number(cambios.lat) !== Number(previo?.lat))
      || (cambios.lng != null && Number(cambios.lng) !== Number(previo?.lng));
    if (!cambioFecha && !cambioLugar) return;

    const { data: raw } = await supabase.rpc("propagar_a_caravanas", { p_evento_id: id });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    const hijas = (r && Array.isArray(r.caravanas)) ? r.caravanas : [];
    if (hijas.length === 0) return;

    // Rehacer el trazado de cada una con el destino nuevo. Si Google no contesta, la
    // caravana igual quedó con el destino correcto: solo le falta el dibujo, y se
    // regenera sola la próxima vez que alguien la abra.
    if (cambioLugar) {
      try {
        await loadGoogleMaps();
        for (const c of hijas) {
          const pts = (Array.isArray(c.puntos) ? c.puntos : []).filter(p => p && p.lat && p.lng);
          if (pts.length < 2) continue;
          const origin = `${pts[0].lat},${pts[0].lng}`;
          const destination = `${pts[pts.length - 1].lat},${pts[pts.length - 1].lng}`;
          const waypoints = pts.slice(1, -1).map(p => `${p.lat},${p.lng}`).join("|");
          const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`;
          const res = await fetch(`${API_BASE}/api/directions?${qs}`);
          const data = await res.json();
          const ruta = data?.routes?.[0];
          const poly = ruta?.overview_polyline?.points || data?.polyline || null;
          const metros = (ruta?.legs || []).reduce((a, l) => a + (l.distance?.value || 0), 0);
          if (!poly) continue;
          await supabase.rpc("actualizar_ruta_caravana", {
            p_caravana_id: c.id,
            p_polyline: poly,
            p_km: metros > 0 ? Math.round(metros / 1000) : null,
          }).then(() => {}, () => {});
        }
      } catch (e) { /* el destino ya quedó bien; el trazado se rehace al abrirla */ }
    }

    alert(`Actualizamos también ${hijas.length} caravana${hijas.length === 1 ? "" : "s"} que salían hacia este evento, y les avisamos a sus organizadores.`);
  };

  const handleEditarEvento = async (id, cambios) => {
    // Estado previo, para detectar si cambió la fecha y avisar a los anotados
    const { data: previo } = await supabase.from("eventos").select("fecha, titulo, lugar, lat, lng").eq("id", id).maybeSingle();
    // .select() para saber si REALMENTE se guardó: cuando RLS no deja editar,
    // Supabase devuelve éxito con cero filas y antes cantábamos "guardado"
    // sin haber guardado nada (reporte de Matías: los cambios no persistían)
    const { data: filas, error } = await supabase.from("eventos").update(cambios).eq("id", id).select("id");
    if (error) {
      alert("Error al actualizar: " + error.message);
      return false;
    }
    if (!filas || filas.length === 0) {
      alert("No se pudo guardar: esta cuenta no tiene permiso para editar este evento. Pedile al organizador que te sume como co-organizador.");
      return false;
    }
    // Arrastrar las caravanas atadas: destino, fecha y trazado
    propagarACaravanas(id, previo, cambios).catch(() => {});

    // Cambió la FECHA → campanita a todos los inscriptos (menos quien edita).
    // Era una promesa de la ficha de Stribo que nosotros no cumplíamos.
    if (previo && cambios.fecha && String(cambios.fecha).slice(0, 10) !== String(previo.fecha || "").slice(0, 10)) {
      (async () => {
        try {
          const { data: insc } = await supabase.from("inscripciones").select("user_id").eq("evento_id", id);
          if (!insc || !insc.length) return;
          const { data: { user } } = await supabase.auth.getUser();
          const fechaTxt = new Date(String(cambios.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
          const titulo = cambios.titulo || previo.titulo || "un evento al que estás anotado";
          const vistos = {};
          for (const i of insc) {
            if ((user && i.user_id === user.id) || vistos[i.user_id]) continue;
            vistos[i.user_id] = true;
            supabase.from("notificaciones").insert({
              user_id: i.user_id, tipo: "evento", from_username: "olbos",
              texto: `📅 "${titulo}" cambió de fecha: ahora es el ${fechaTxt}`,
            }).then(() => {}, () => {});
          }
        } catch (e) { /* el aviso nunca frena el guardado */ }
      })();
    }
    setEditandoEvento(null);
    setDetalleEvento(null);
    try { setEventosDB(await traerEventosAgenda()); } catch (e) { /* la lista se refresca sola después */ }
    return true;
  };

  const handleEliminarEvento = async (id) => {
    if (!window.confirm("¿Eliminar este evento? También se borrarán las inscripciones asociadas. Esta acción no se puede deshacer.")) return;
    // Antes de borrar: capturar título e inscriptos para avisarles que se canceló
    const { data: evPrevio } = await supabase.from("eventos").select("titulo").eq("id", id).maybeSingle();
    const { data: inscPrevio } = await supabase.from("inscripciones").select("user_id").eq("evento_id", id);
    await supabase.from("inscripciones").delete().eq("evento_id", id);
    const { error } = await supabase.from("eventos").delete().eq("id", id);
    if (error) {
      alert("Error al eliminar: " + error.message);
      return;
    }
    // Campanita a los que estaban anotados: el evento ya no existe
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const vistos = {};
      for (const i of (inscPrevio || [])) {
        if ((user && i.user_id === user.id) || vistos[i.user_id]) continue;
        vistos[i.user_id] = true;
        supabase.from("notificaciones").insert({
          user_id: i.user_id, tipo: "evento", from_username: "olbos",
          texto: `❌ "${evPrevio?.titulo || "Un evento al que estabas anotado"}" fue cancelado por el organizador`,
        }).then(() => {}, () => {});
      }
    } catch (e) { /* el aviso nunca frena el borrado */ }
    setDetalleEvento(null);
    const { data } = await supabase.from("eventos").select(COLS_EVENTO_LISTA).is("grupo_id", null).neq("visibilidad_tipo", "invitados").order("fecha", { ascending: true });
    if (data) setEventosDB(data.map(mapearEvento));
  };

  // Banner "ya está en las tiendas" para los que usan Olbos desde la web
  // (sugerencia de Matías, 24-ago): temporal y descartable; en la app nativa no aparece.
  const [bannerStoresVisto, setBannerStoresVisto] = useState(() => {
    try { return localStorage.getItem("olbos_banner_stores") === "1"; } catch (e) { return true; }
  });

  // Caravanas confluentes: en la lista van agrupadas bajo su evento padre ("12
  // caravanas vienen a este evento ›") en vez de diez tarjetas con el mismo título.
  // La que organizo o en la que estoy anotado sigue a la vista.
  const hijasPorPadre = {};
  todosEventos.forEach(e => { if (e.evento_padre_id) (hijasPorPadre[e.evento_padre_id] = hijasPorPadre[e.evento_padre_id] || []).push(e); });
  const padresEnLista = {};
  sorted.forEach(e => { padresEnLista[e.id] = true; });
  const listaVisible = sorted.filter(e => !(e.evento_padre_id && padresEnLista[e.evento_padre_id] && !joined[e.id] && !(session?.user && e.creator_id === session.user.id)));

  return (
    <PageWrap>
      {/* Rodada en vivo de un evento al que estoy anotado: arriba de todo */}
      {/* paddingRight: deja lugar al avatar flotante de arriba a la derecha */}
      {session && <TarjetaRodadaEnVivo session={session} style={{ margin: "calc(var(--sat) + 10px) 18px -2px", width: "calc(100% - 36px)", paddingRight: 62 }} />}
      {/* El carrusel de novedades va ARRIBA DE TODO — es lo primero que se ve al
          entrar; el título y el buscador bajan debajo (pedido de Carlos 13-ago) */}
      {vista === "lista" && !q && (
        <DestacadosCarrusel
          eventos={todosEventos.filter(e => !esPasado(e) && e.img)
            .sort((a, b) => ((b.destacado ? 1 : 0) - (a.destacado ? 1 : 0)) || String(a.fecha || "").localeCompare(String(b.fecha || "")))
            .slice(0, 6)}
          onAbrir={setDetalleEvento}
          arriba />
      )}
      {vista === "lista" && !q && !esNativo() && !bannerStoresVisto && (
        <div style={{ margin: "12px 18px 0", background: C.accent, borderRadius: 14, padding: "13px 15px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 22, flexShrink: 0 }}>📲</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#F9F5F2", letterSpacing: "-0.01em" }}>La app de Olbos ya está en las tiendas</div>
            <div style={{ fontSize: 11.5, color: "#bdb6ab", marginTop: 2, lineHeight: 1.35 }}>GPS con la pantalla apagada, rodadas en vivo y navegador. Llevala en la moto.</div>
          </div>
          <a href="https://olbos.ai/app" target="_blank" rel="noreferrer"
            style={{ flexShrink: 0, padding: "9px 14px", borderRadius: 100, background: "#F9F5F2", color: C.text, fontSize: 12.5, fontWeight: 800, textDecoration: "none" }}>
            Descargar
          </a>
          <button onClick={() => { setBannerStoresVisto(true); try { localStorage.setItem("olbos_banner_stores", "1"); } catch (e) { /* */ } }}
            style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", color: "#F9F5F2", cursor: "pointer", fontSize: 11 }}>✕</button>
        </div>
      )}
      {/* Cabecera COMPACTA: el carrusel manda y los eventos se ven sin scrollear.
          El título grande + bajada + video de ayuda se fueron (el tab ya dice Eventos). */}
      <div style={{ padding: (vista === "lista" && !q) ? "12px 18px 0" : "calc(var(--sat) + 14px) 18px 0", display: "flex", gap: 8 }}>
        <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center" }}>
          <Search size={16} strokeWidth={1.8} color={C.muted} style={{ position: "absolute", left: 12, pointerEvents: "none" }} />
          <input
            value={q}
            onChange={e => { const v = e.target.value; setQ(v); if (v && vista !== "lista") setVista("lista"); }}
            type="search" enterKeyHint="search" onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
            placeholder="Buscar evento, lugar u organizador…"
            style={{
              width: "100%", padding: "10px 34px 10px 34px", borderRadius: 10,
              background: C.surface, border: `1px solid ${C.border}`,
              color: C.text, fontSize: 13, outline: "none", boxSizing: "border-box",
            }} />
          {q && (
            <button onClick={() => setQ("")} title="Limpiar"
              style={{ position: "absolute", right: 8, width: 20, height: 20, borderRadius: "50%", background: C.border, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={11} strokeWidth={2.5} color={C.muted} />
            </button>
          )}
        </div>
        <button onClick={() => setShowAI(true)} title="Buscar con IA"
          style={{ width: 40, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Zap size={16} strokeWidth={2} color={C.text} />
        </button>
        <Btn small onClick={() => (requireAuth ? requireAuth(() => setShowCrear(true)) : setShowCrear(true))}>＋ Crear</Btn>
      </div>
      {/* sorteos abiertos: independientes de eventos (sponsors/marcas). Van debajo del buscador, bien a la vista */}
      {vista === "lista" && !q && (
        <div style={{ padding: "12px 18px 0" }}>
          <SorteosComunidad session={session} isAdmin={isAdmin} requireAuth={requireAuth} />
        </div>
      )}
      {q && (
        <div style={{ padding: "8px 18px 0", fontSize: 12, color: C.muted }}>
          {sorted.length === 0
            ? <>Sin resultados para «{q}». Probá con menos palabras o el nombre de una ciudad.</>
            : <>
                {sorted.length} resultado{sorted.length !== 1 ? "s" : ""} para «{q}»
                {destino && sorted.some(e => !matchQ(e)) ? ` · incluye lo que hay a menos de ${RADIO_DESTINO} km de ${destino.formatted || destino.nombre}` : ""}
              </>}
        </div>
      )}

      {/* Filtros y vistas PLEGADOS: la cabecera estaba sobrecargada (feedback Cata 13-ago) */}
      <div style={{ padding: "12px 18px 0", display: "flex", alignItems: "center", gap: 10 }}>
        {(() => {
          const n = (vista !== "lista" ? 1 : 0) + (sortBy !== "fecha" ? 1 : 0) + ((rangoDesde || rangoHasta) ? 1 : 0) + (filter !== "Todos" ? 1 : 0) + (zona ? 1 : 0);
          return (<>
            <button onClick={() => setShowFiltros(v => !v)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 100, background: showFiltros ? "#111111" : C.surface, color: showFiltros ? "#fff" : C.text, border: `1px solid ${showFiltros ? "#111111" : C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              🎛️ Filtros y vistas{n > 0 ? ` · ${n}` : ""}
              <ChevronDown size={13} strokeWidth={2.5} style={{ transform: showFiltros ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
            </button>
            {n > 0 && (
              <button onClick={() => { setVista("lista"); setSortBy("fecha"); setRangoDesde(""); setRangoHasta(""); setFiltroFechas(false); setFilter("Todos"); elegirZona(""); }}
                style={{ fontSize: 12, color: C.muted, background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }}>
                Limpiar
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.muted, flexShrink: 0 }}>
              {(() => { const t = todosEventos.filter(e => !esPasado(e)).length; return t > 0 ? `${t} por venir` : ""; })()}
            </span>
          </>);
        })()}
      </div>

      {/* Provincia A LA VISTA: "Cerca tuyo · <Provincia> · Todo el país". Antes vivía
          adentro de "Filtros y vistas" y un rider de Córdoba veía Tierra del Fuego primero. */}
      {vista === "lista" && !q && zonasConEventos.length > 1 && (
        <div style={{ padding: "10px 18px 0", display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2, alignItems: "center" }}>
          <Pill active={sortBy === "cercania" && !zona} onClick={activarCercania}>📍 {buscandoPos ? "Ubicando…" : "Cerca tuyo"}</Pill>
          {zonaDetectada && (
            <Pill active={zona === zonaDetectada} onClick={() => { elegirZona(zonaDetectada); setSortBy("fecha"); }}>{zonaDetectada}</Pill>
          )}
          {zona && zona !== zonaDetectada && (
            <Pill active onClick={() => elegirZona("")}>{zona} ✕</Pill>
          )}
          <Pill active={!zona && sortBy !== "cercania"} onClick={() => { elegirZona(""); setSortBy("fecha"); }}>Todo el país</Pill>
          <button onClick={() => setShowFiltros(true)}
            style={{ flexShrink: 0, fontSize: 12, color: C.muted, background: "none", border: "none", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", padding: "4px 2px" }}>
            Otra provincia ›
          </button>
        </div>
      )}
      {zonaAuto && zona === zonaAuto && vista === "lista" && !q && (
        <div style={{ padding: "6px 18px 0", fontSize: 11, color: C.muted }}>
          Viendo {zona} por {miCiudad ? "la ciudad de tu perfil" : "tu ubicación"}. Tocá "Todo el país" para ver todo.
        </div>
      )}
      {/* Sin ciudad no podemos ordenar la agenda por cercanía: es el lugar donde
          pedirla se explica sola, mucho mejor que en el feed */}
      <PreguntaPerfil session={session} soloCiudad />
      {showFiltros && (<>
      <VideoAyuda seccion="eventos" style={{ margin: "10px 18px 0" }} />
      <div style={{ padding: "12px 18px 0", display: "flex", gap: 6, alignItems: "center", overflowX: "auto", paddingBottom: 2 }}>
        <Pill active={vista === "lista"} onClick={() => setVista("lista")}>📋 Lista</Pill>
        <Pill active={vista === "calendario"} onClick={() => setVista("calendario")}>📅 Calendario</Pill>
        <Pill active={vista === "mapa"} onClick={() => setVista("mapa")}>🗺️ Mapa</Pill>
        <div style={{ width: 1, height: 18, background: C.border, flexShrink: 0, margin: "0 2px" }} />
        {/* El orden solo tiene sentido en la lista; el rango de fechas, en las tres */}
        {vista === "lista" && (
          <>
            <Pill active={sortBy === "fecha"} onClick={() => setSortBy("fecha")}>Fecha</Pill>
            <Pill active={sortBy === "cercania"} onClick={activarCercania}>{buscandoPos ? "Ubicando…" : "Cerca"}</Pill>
          </>
        )}
        <Pill active={filtroFechas || rangoDesde || rangoHasta} onClick={() => { setFiltroFechas(f => !f); if (filtroFechas) { setRangoDesde(""); setRangoHasta(""); } }}>
          📆 {rangoDesde || rangoHasta ? rangoLabel : "Entre fechas"}
        </Pill>
      </div>

      {/* rango de fechas: "viajo a Salta del 10 al 15, ¿qué hay?" — combina con provincia */}
      {filtroFechas && (
        <div style={{ padding: "10px 18px 0", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>Del</span>
          <input type="date" value={rangoDesde} onChange={e => setRangoDesde(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, outline: "none" }} />
          <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>al</span>
          <input type="date" value={rangoHasta} onChange={e => setRangoHasta(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, outline: "none" }} />
          {(rangoDesde || rangoHasta) && (
            <button onClick={() => { setRangoDesde(""); setRangoHasta(""); }} title="Limpiar"
              style={{ width: 26, height: 26, borderRadius: "50%", background: C.border, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <X size={12} strokeWidth={2.5} color={C.muted} />
            </button>
          )}
        </div>
      )}

      {/* filtros (solo en vista lista y sin búsqueda activa) */}
      {!q && (
        <div style={{ padding: "10px 18px 0", display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
          {filters.map(f => (
            <Pill key={f} active={filter === f} onClick={() => setFilter(f)}>
              {f === "Voy" ? "✓ Voy" : f === "Míos" ? "Organizo yo" : f}
            </Pill>
          ))}
        </div>
      )}

      {/* provincias con eventos: para el que quiere ver qué hay en su zona o adonde viaja */}
      {zonasConEventos.length > 1 && (
        <div style={{ padding: "8px 18px 0", display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, alignItems: "center" }}>
          <MapPin size={13} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
          <Pill active={!zona && sortBy !== "cercania"} onClick={() => { elegirZona(""); setSortBy("fecha"); }}>Todo el país</Pill>
          {zonasConEventos.map(([z, n]) => (
            <Pill key={z} active={zona === z} onClick={() => { elegirZona(zona === z ? "" : z); setSortBy("fecha"); }}>{z} · {n}</Pill>
          ))}
        </div>
      )}
      </>)}


      <div style={{ padding: "14px 18px 0" }}>
        {/* 🏍️ Salidas abiertas: "Salgo a rodar" y las de los riders cercanos (sin armar un evento) */}
        <SalidasAbiertas session={session} requireAuth={requireAuth} estilo={{ marginBottom: 12 }} />
        {/* vistas alternativas: agenda mensual y mapa de pins */}
        {vista === "calendario" && <VistaCalendarioEventos eventos={filtradosVista} onAbrir={setDetalleEvento} />}
        {vista === "mapa" && <VistaMapaEventos eventos={filtradosVista.filter(e => !esPasado(e))} onAbrir={setDetalleEvento} />}
        {/* Mientras no llegó la primera respuesta (señal mala) se muestra que está cargando,
            no la pantalla de "no hay eventos" — confundía en el campo (Arribeños, 09-ago) */}
        {vista === "lista" && !eventosCargados && todosEventos.length === 0 && (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ width: 26, height: 26, margin: "0 auto 14px", border: `3px solid ${C.border}`, borderTopColor: C.text, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <p style={{ fontSize: 13, color: C.muted }}>Cargando eventos…</p>
          </div>
        )}
        {vista === "lista" && eventosCargados && sorted.length === 0 && filter !== "Realizados" && todosEventos.length === 0 && (
          <div style={{ padding: "50px 24px", textAlign: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
              <Calendar size={32} strokeWidth={1.5} color="#111111" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>No hay eventos todavía</h3>
            <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
              ¿Organizás salidas grupales? Cargá el primer evento y convocá a la comunidad.
            </p>
            <button onClick={() => (requireAuth ? requireAuth(() => setShowCrear(true)) : setShowCrear(true))}
              style={{ padding: "12px 24px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
              Crear evento 📅
            </button>
            <p style={{ fontSize: 12, color: C.faint, marginTop: 18, fontStyle: "italic" }}>— El camino es el destino</p>
          </div>
        )}
        {vista === "lista" && sorted.length === 0 && filter === "Guardados" && (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <Bookmark size={28} strokeWidth={1.6} color={C.muted} style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.7 }}>
              Todavía no guardaste ningún evento.<br />
              Tocá el <strong style={{ color: C.text, fontWeight: 600 }}>marcador</strong> de cualquier salida que te interese y te queda acá, sin anotarte todavía.
            </p>
          </div>
        )}
        {vista === "lista" && sorted.length === 0 && filter === "Voy" && (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🏍️</div>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.7 }}>
              Todavía no te anotaste en ninguna salida.<br />
              Mirá las de <strong style={{ color: C.text, fontWeight: 600 }}>Todos</strong> y sumate a la primera: acá te van a quedar juntas, con las próximas arriba.
            </p>
          </div>
        )}
        {/* Vacío con filtros (zona + Salidas, Gratis + rango, texto sin resultados…): antes
            quedaba la pantalla en blanco sin decir por qué ni cómo salir */}
        {vista === "lista" && eventosCargados && sorted.length === 0 && todosEventos.length > 0 && filter !== "Voy" && filter !== "Guardados" && filter !== "Realizados" && (
          <div style={{ padding: "36px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>🔍</div>
            <p style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6, letterSpacing: "-0.01em" }}>
              {qn ? `Nada con "${q.trim()}"` : "No hay nada con estos filtros"}
            </p>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
              {qn ? "Buscamos en todo el país, también entre los eventos pasados. Probá con menos letras." : zona ? `Estás viendo solo ${zona}.` : "Probá sacando algún filtro."}
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => { setQ(""); setVista("lista"); setSortBy("fecha"); setRangoDesde(""); setRangoHasta(""); setFiltroFechas(false); setFilter("Todos"); }}
                style={{ padding: "10px 16px", borderRadius: 10, background: C.text, color: "#F9F5F2", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Limpiar filtros
              </button>
              {zona && !qn && (
                <button onClick={() => { elegirZona(""); setQ(""); }}
                  style={{ padding: "10px 16px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Ver todo el país
                </button>
              )}
            </div>
          </div>
        )}
        {/* Buscando con texto y una provincia elegida: se avisa que la búsqueda es nacional */}
        {vista === "lista" && qn && zona && sorted.length > 0 && (
          <div style={{ margin: "0 0 10px", fontSize: 12, color: C.muted }}>Resultados en todo el país, no solo en {zona}.</div>
        )}
        {vista === "lista" && sorted.length === 0 && filter === "Realizados" && (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📅</div>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              Todavía no hay eventos realizados. Cuando pasen las fechas de los eventos, van a quedar guardados acá.
            </p>
          </div>
        )}
        {/* Nada cerca esta semana: el hueco se convierte en invitación a crear la
            propia, que es justo lo único que la gente ya está haciendo sola */}
        {porRelevancia && miPos && sorted.length > 0 && !sorted.some(e => bloqueDe(e) <= 1) && (
          <div style={{ margin: "0 0 12px", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            Nada cerca tuyo por ahora.{" "}
            <button onClick={() => (requireAuth ? requireAuth(() => setShowCrear(true)) : setShowCrear(true))}
              style={{ background: "none", border: "none", padding: 0, color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textDecorationColor: C.border }}>
              Armá una en tu zona
            </button>
          </div>
        )}
        {/* Lista compacta tipo agenda: miniatura + datos, agrupada por día.
            El detalle del evento es el que muestra todo en grande. */}
        {listaVisible.map((e, idx) => {
          const isIn = !!joined[e.id];
          const cuposIlimitado = !e.max_riders;
          const ridersCount = contadores[e.id] || e.riders || 0;
          const maxCupos = e.max_riders || e.max || null;
          const full = !cuposIlimitado && maxCupos && ridersCount >= maxCupos;
          const finalizado = filter === "Realizados";
          const imgEvento = e.img || null;
          const esVid = esVideoPortada(imgEvento);

          // Separador de día: se dibuja cuando cambia la fecha respecto del evento anterior
          const diaDe = (ev) => String(ev?.fecha || "").slice(0, 10);
          // Agrupar por día solo tiene sentido con el orden cronológico
          const porCercania = sortBy === "cercania" && miPos;
          // Título de sección cuando cambia el bloque de relevancia
          const blq = porRelevancia ? bloqueDe(e) : null;
          const nuevoBloque = porRelevancia && (idx === 0 || bloqueDe(listaVisible[idx - 1]) !== blq);
          // Las FECHAS como sub-separador van SIEMPRE que el orden sea cronológico,
          // también dentro de los bloques de relevancia: los títulos solos ("Esta
          // semana", "Más adelante") eran menos claros que las fechas de antes
          // (pedido de Carlos). Al arrancar un bloque, el día se reinicia.
          const nuevoDia = !porCercania && (porRelevancia
            ? (nuevoBloque || diaDe(listaVisible[idx - 1]) !== diaDe(e))
            : (idx === 0 || diaDe(listaVisible[idx - 1]) !== diaDe(e)));
          const km = kmHasta(e);
          const fechaLarga = (() => {
            if (!e.fecha) return "Sin fecha";
            const d = new Date(String(e.fecha).slice(0, 10) + "T12:00:00");
            if (isNaN(d.getTime())) return "Sin fecha";
            const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
            const dd = new Date(d); dd.setHours(0, 0, 0, 0);
            const difDias = Math.round((dd - hoy) / 86400000);
            const txt = d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
            if (difDias === 0) return `Hoy · ${txt}`;
            if (difDias === 1) return `Mañana · ${txt}`;
            // Empezó antes y sigue (dos días): es HOY, no "viernes 10 de octubre" en Más adelante
            if (enCursoMultiDia(e)) return `Hoy · en curso · desde el ${txt}`;
            return txt;
          })();
          // Duración en días para los eventos de varios días
          const dias = (() => {
            if (!e.fecha || !e.fecha_fin || e.fecha_fin === e.fecha) return 0;
            const n = Math.round((new Date(e.fecha_fin + "T12:00:00") - new Date(e.fecha + "T12:00:00")) / 86400000) + 1;
            return n > 1 ? n : 0;
          })();

          return (
            <div key={e.id}>
              {nuevoBloque && (
                <div style={{ margin: idx === 0 ? "2px 0 12px" : "26px 0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
                      {TITULO_BLOQUE[blq]}
                    </span>
                    <div style={{ flex: 1, height: 1, background: C.border }} />
                  </div>
                  {blq === 1 && !miPos && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Activá la ubicación para ver lo que tenés cerca</div>
                  )}
                </div>
              )}
              {nuevoDia && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: (idx === 0 || nuevoBloque) ? "2px 0 10px" : "18px 0 10px" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                    {fechaLarga}
                  </span>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                </div>
              )}

              <button onClick={() => setDetalleEvento(e)}
                style={{
                  width: "100%", display: "flex", gap: 12, alignItems: "stretch", textAlign: "left",
                  background: C.card, border: `1px solid ${isIn ? C.text : C.border}`, borderRadius: 14,
                  padding: 10, marginBottom: 10, cursor: "pointer", opacity: finalizado ? 0.65 : 1,
                  boxShadow: "0 1px 4px rgba(0,0,0,.04)",
                }}>
                {/* miniatura */}
                <div style={{ width: 86, height: 86, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: C.accent, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {imgEvento ? (
                    <img src={esVid ? (cldPoster(imgEvento) || posterDeVideo(imgEvento)) : cldThumb(imgEvento, 300)}
                      alt="" loading="lazy" decoding="async"
                      onError={ev => { if (ev.target.src !== imgEvento) ev.target.src = imgEvento; }}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    e.tipo === "evento" ? <Calendar size={26} strokeWidth={1.5} color="rgba(255,255,255,.6)" /> : <Map size={26} strokeWidth={1.5} color="rgba(255,255,255,.6)" />
                  )}
                  {esVid && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.2)" }}><Play size={18} color="#fff" fill="#fff" /></span>}
                </div>

                {/* datos */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 3 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                    {e.destacado && <span style={{ fontSize: 9.5, fontWeight: 800, color: "#F9F5F2", background: C.accent, padding: "2px 7px", borderRadius: 5 }}>📌 DESTACADO</span>}
                    {e.visibilidad_tipo === "invitados" && <span style={{ fontSize: 9.5, fontWeight: 800, color: "#8A6D3B", background: "#F5EFE5", padding: "2px 7px", borderRadius: 5 }}>🔒 SOLO INVITADOS</span>}
                    {finalizado && <span style={{ fontSize: 9.5, fontWeight: 800, color: "#fff", background: C.muted, padding: "2px 7px", borderRadius: 5 }}>FINALIZADO</span>}
                    {isIn && <span style={{ fontSize: 9.5, fontWeight: 800, color: C.green, background: `${C.green}14`, padding: "2px 7px", borderRadius: 5 }}>✓ ANOTADO</span>}
                    {!isIn && guardados[e.id] && <span style={{ fontSize: 9.5, fontWeight: 800, color: C.gold, background: "#F5EFE5", padding: "2px 7px", borderRadius: 5 }}>GUARDADO</span>}
                  </div>

                  <div style={{ fontSize: 14.5, fontWeight: 700, color: C.text, lineHeight: 1.25, letterSpacing: "-0.01em", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {e.title}
                  </div>

                  <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.tipo === "evento" ? "📍" : "🛣️"}{" "}
                    {porCercania && e.fecha ? `${new Date(String(e.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })} · ` : ""}
                    {e.hora ? `${String(e.hora).slice(0, 5)} hs · ` : ""}
                    {e.lugar || e.from || "A confirmar"}
                    {dias > 0 ? ` · ${dias} días` : ""}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
                    {km != null && (
                      <span style={{ fontWeight: 800, color: C.text, background: C.surface, padding: "1px 7px", borderRadius: 100 }}>
                        {km < 1 ? `${Math.round(km * 1000)} m` : `a ${Math.round(km)} km`}
                      </span>
                    )}
                    {(() => {
                      // Los eventos de lugar fijo guardan el valor en precio_entrada y las
                      // salidas en precio: mirando solo uno, un evento con entradas pagas
                      // aparecía como "Sin cargo" en la agenda (reporte de Matías).
                      // Con tandas se muestra "desde $X": saber qué tanda rige exige contar
                      // los anotados de cada evento, y no vale una consulta por fila —el
                      // precio exacto está en la ficha, que sí los cuenta.
                      const t = tandasDe(e);
                      const valor = t ? (Number(t.lista[0].precio) || 0) : Number(e.precio_entrada || e.precio || 0);
                      return (
                        <span style={{ fontWeight: 700, color: valor === 0 ? C.green : C.text }}>
                          {valor === 0 ? "Sin cargo" : (t ? "desde " : "") + "$" + valor.toLocaleString("es-AR")}
                        </span>
                      );
                    })()}
                    {full && <span style={{ color: C.red, fontWeight: 700 }}>· COMPLETO</span>}
                    {!cuposIlimitado && maxCupos && !full && <span>· {Math.max(maxCupos - ridersCount, 0)} lugares</span>}
                    {cuposIlimitado && ridersCount > 5 && <span>· {ridersCount} anotados</span>}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", flexShrink: 0, alignSelf: "stretch" }}>
                  {e._fromDB && !finalizado && !isIn ? (
                    <span role="button" aria-label={guardados[e.id] ? "Quitar de guardados" : "Guardar"}
                      onClick={ev => { ev.stopPropagation(); ev.preventDefault(); toggleGuardado(e); }}
                      style={{ padding: "2px 2px 6px", cursor: "pointer", display: "inline-flex" }}>
                      <Bookmark size={18} strokeWidth={2} color={guardados[e.id] ? C.text : C.faint} fill={guardados[e.id] ? C.text : "none"} />
                    </span>
                  ) : <span />}
                  <ChevronRight size={16} strokeWidth={2} color={C.faint} />
                  <span />
                </div>
              </button>
              {hijasPorPadre[e.id] && hijasPorPadre[e.id].length > 0 && (
                <button onClick={() => setDetalleEvento(e)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", margin: "-4px 0 10px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>🛣️</span>
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
                    {hijasPorPadre[e.id].length} caravana{hijasPorPadre[e.id].length !== 1 ? "s" : ""} {hijasPorPadre[e.id].length !== 1 ? "vienen" : "viene"} a este evento
                  </span>
                  <ChevronRight size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
                </button>
              )}
              {/* Anotado y es el día del evento: check-in y mapa en vivo a la vista, sin abrir la ficha */}
              {isIn && e._fromDB && !finalizado && esDiaDelEvento(e) && (
                <div style={{ margin: "-4px 0 12px" }}>
                  <AccionesDiaEvento evento={e} enLista />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── EVENTOS REALIZADOS ── */}
      {vista === "lista" && !q && (filter === "Todos" || filter === "Realizados") && (
      <div style={{ padding: "0 18px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "24px 0 16px" }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Eventos realizados</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        {EVENTS_PASADOS.map(e => (
          <div key={e.id} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 14, opacity: 0.9 }}>
            {/* foto con overlay gris */}
            <div style={{ height: 140, position: "relative", overflow: "hidden" }}>
              <img loading="lazy" decoding="async" src={e.img} alt={e.title} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(40%)" }} />
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} />
              {/* badge realizado */}
              <div style={{ position: "absolute", top: 10, left: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: "#276749", color: "#fff", display: "flex", alignItems: "center", gap: 4 }}>
                  ✓ Realizado
                </span>
              </div>
              <div style={{ position: "absolute", top: 10, right: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 6, background: "rgba(0,0,0,.5)", color: "#fff" }}>{e.date}</span>
              </div>
              <div style={{ position: "absolute", bottom: 10, left: 14, right: 14 }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: "#fff", letterSpacing: -0.3 }}>{e.title}</h3>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)" }}>{e.from} → {e.to} · {e.km} km · {e.diff}</div>
              </div>
            </div>

            <div style={{ padding: "12px 14px 14px" }}>
              {/* stats del evento */}
              <div style={{ display: "flex", gap: 16, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{e.riders_fueron}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>riders</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.gold }}>{e.rating_promedio}★</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>rating</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{e.fotos.length}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>fotos</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{e.reviews.length}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>reviews</div>
                </div>
              </div>

              {/* galería de fotos mini */}
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {e.fotos.map((f, i) => (
                  <div key={i} style={{ flex: 1, height: 60, borderRadius: 8, overflow: "hidden" }}>
                    <img loading="lazy" decoding="async" src={f} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ))}
              </div>

              {/* review destacada */}
              {e.reviews[0] && (
                <div style={{ background: C.surface, borderRadius: 10, padding: "10px 12px", marginBottom: 12, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <img loading="lazy" decoding="async" src={e.reviews[0].avatarUrl} alt={e.reviews[0].u} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>@{e.reviews[0].u}</span>
                    <span style={{ fontSize: 11, color: C.gold }}>{"★".repeat(e.reviews[0].r)}</span>
                  </div>
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>"{e.reviews[0].t}"</p>
                </div>
              )}

              {/* organizador y acción */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: C.muted }}>
                  por <span style={{ fontWeight: 700, color: C.text }}>@{e.org}</span>
                </div>
                <button style={{ fontSize: 12, fontWeight: 600, color: C.blue, background: C.blueLo, border: `1px solid ${C.blue}22`, padding: "5px 12px", borderRadius: 8, cursor: "pointer" }}>
                  Ver resumen →
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {showAI && <AISearchModal eventos={todosEventos} onClose={() => setShowAI(false)} onNavigate={(ev) => { setShowAI(false); setDetalleEvento(ev); }} />}
      {showCrear && <CrearEventoModal
        onClose={() => { setShowCrear(false); setGrupoIdParaEvento(null); setGrupoNombreParaEvento(""); setFlyerDataPendiente(null); setRodadaHacia(null); }}
        onCrear={handleCrearEvento}
        session={session}
        grupoIdPreseleccionado={grupoIdParaEvento}
        grupoNombrePreseleccionado={grupoNombreParaEvento}
        flyerData={flyerDataPendiente}
        rodadaHacia={rodadaHacia}
      />}

      {/* El "+ Crear" flotante se sacó: duplicaba al de la fila de búsqueda y se
          superponía a las tarjetas (auditoría 09-oct) */}
      {anotarEvento && (
        <AnotarModal
          evento={anotarEvento}
          motos={userMotos}
          onClose={() => setAnotarEvento(null)}
          onConfirm={(motoId, datos, deslindeOk) => {
            const ev = anotarEvento;
            setResultadoAnotar({ evento: ev, cargando: true });
            anotarme(ev.id, motoId, datos, deslindeOk).then(r => setResultadoAnotar({ evento: ev, ok: !!r.ok, motivo: r.motivo || "" }));
          }}
        />
      )}
      {detalleEvento && (
        <DetalleEventoModal
          evento={{ ...detalleEvento, riders: contadores[detalleEvento.id] ?? detalleEvento.riders ?? 0 }}
          joined={joined}
          userMotos={userMotos}
          isAdmin={isAdmin}
          onClose={() => setDetalleEvento(null)}
          onAnotarme={(e, opts) => intentarAnotar(e, opts)}
          guardado={!!guardados[detalleEvento.id]}
          onGuardar={() => toggleGuardado(detalleEvento)}
          onCancelar={id => cancelarInscripcion(id)}
          onEditar={e => setEditandoEvento(e)}
          onEliminar={id => handleEliminarEvento(id)}
        />
      )}
      {resultadoAnotar && (
        <ResultadoInscripcion
          r={resultadoAnotar}
          padre={(() => { const pid = resultadoAnotar.evento?.evento_padre_id; if (!pid || joined[pid]) return null; return todosEventos.find(e => e.id === pid) || null; })()}
          onAnotarPadre={() => { const pid = resultadoAnotar.evento?.evento_padre_id; const p = todosEventos.find(e => e.id === pid); if (p) intentarAnotar(p); }}
          onClose={() => setResultadoAnotar(null)}
          onReintentar={() => { const ev = resultadoAnotar.evento; setResultadoAnotar(null); intentarAnotar(ev); }}
          onMapa={() => {
            const ev = resultadoAnotar.evento;
            setResultadoAnotar(null);
            registrarUso("mapa_vivo_tap", { id: ev.id, desde: "exito" });
            window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento: ev } }));
          }}
        />
      )}
      {editandoEvento && (
        <EditarEventoModal
          evento={editandoEvento}
          onClose={() => setEditandoEvento(null)}
          onGuardar={cambios => handleEditarEvento(editandoEvento.id, cambios)}
        />
      )}
    </PageWrap>
  );
};

// ─── HELPER COMPARTIR POR WHATSAPP ───────────────────────────────────────────
// ─── Compartir con el menú del sistema ───────────────────────────────────────
// Abre el selector nativo (WhatsApp, Instagram, Telegram, mail, copiar…) en vez de
// forzar WhatsApp. Si el navegador no lo soporta (escritorio viejo), cae a WhatsApp.
// Para mensajes dirigidos a UNA persona puntual (contactar un vendedor, pedir grúa)
// se sigue usando wa.me directo: ahí el destinatario ya está definido.
// ─── GPX ─────────────────────────────────────────────────────────────────────
// GPX es el formato estándar de rutas: lo entienden Wikiloc, Calimoto, Garmin,
// Google Earth y prácticamente cualquier GPS. Es texto plano, así que se arma y se
// lee en el teléfono sin librerías ni servidor.
const escaparXml = (t) => String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const armarGPX = (nombre, puntos) => {
  const pts = (puntos || []).filter(p => p && p.lat != null && p.lng != null);
  const trkpt = pts.map(p => {
    const t = p.ts ? `<time>${new Date(p.ts).toISOString()}</time>` : "";
    const e = (p.ele != null || p.alt != null) ? `<ele>${p.ele != null ? p.ele : p.alt}</ele>` : "";
    return `      <trkpt lat="${Number(p.lat).toFixed(6)}" lon="${Number(p.lng).toFixed(6)}">${e}${t}</trkpt>`;
  }).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Olbos · olbos.ai" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata><name>${escaparXml(nombre)}</name><link href="https://olbos.ai"><text>Olbos</text></link></metadata>
  <trk>
    <name>${escaparXml(nombre)}</name>
    <trkseg>
${trkpt}
    </trkseg>
  </trk>
</gpx>`;
};

// Bajar una imagen (QR para imprimir, placa) también en la app nativa: el <a download>
// no hace nada en el webview de Capacitor (Matías, 10-oct: "desde la app no se puede
// descargar el QR, en la web sí"). Primero el compartir del sistema (va a Fotos,
// WhatsApp, la impresora); si no está, se sube a Cloudinary y se abre en el navegador
// del teléfono como adjunto, que sí sabe descargar. En la compu, la descarga de siempre.
const descargarImagen = async (src, nombre) => {
  try {
    const blob = await (await fetch(src)).blob();
    const file = new File([blob], nombre, { type: blob.type || "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: nombre }); return; } catch (e) { if (e && e.name === "AbortError") return; }
    }
    if (esNativo()) {
      const fd = new FormData();
      fd.append("file", blob, nombre);
      fd.append("upload_preset", "olbos_uploads");
      const up = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd }).then(r => r.json()).catch(() => null);
      if (up && up.secure_url) {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: up.secure_url.replace("/upload/", "/upload/fl_attachment/") });
        return;
      }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = nombre;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    alert("No se pudo descargar la imagen. Probá desde olbos.ai en la compu.");
  }
};

// Compartir o bajar el .gpx. En el teléfono conviene "compartir" (va a WhatsApp, a
// Drive, a la app de GPS que use el rider); en la compu, la descarga de siempre.
const bajarGPX = async (nombre, puntos) => {
  const pts = (puntos || []).filter(p => p && p.lat != null && p.lng != null);
  if (pts.length < 2) { alert("Este recorrido no tiene puntos suficientes para exportar."); return; }
  const limpio = (String(nombre || "recorrido").toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "recorrido").slice(0, 50);
  const archivo = `olbos-${limpio}.gpx`;
  const blob = new Blob([armarGPX(nombre, pts)], { type: "application/gpx+xml" });
  const file = new File([blob], archivo, { type: "application/gpx+xml" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: nombre || "Recorrido de Olbos" }); return; } catch (e) { /* canceló o falló: cae a la descarga */ }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = archivo;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// Lee un GPX de cualquier app: primero busca el track grabado (trkpt), si no hay
// prueba con la ruta planificada (rtept) y por último con los waypoints sueltos (wpt).
const leerGPX = (texto) => {
  const doc = new DOMParser().parseFromString(texto, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) return null;
  let nodos = doc.getElementsByTagName("trkpt");
  if (!nodos.length) nodos = doc.getElementsByTagName("rtept");
  if (!nodos.length) nodos = doc.getElementsByTagName("wpt");
  const puntos = [];
  for (let i = 0; i < nodos.length; i++) {
    const lat = parseFloat(nodos[i].getAttribute("lat"));
    const lng = parseFloat(nodos[i].getAttribute("lon"));
    if (Number.isFinite(lat) && Number.isFinite(lng)) puntos.push({ lat, lng });
  }
  const nomNodo = doc.querySelector("trk > name") || doc.querySelector("rte > name") || doc.querySelector("metadata > name");
  return { nombre: nomNodo ? nomNodo.textContent.trim() : "", puntos };
};

const compartirNativo = async ({ texto = "", url = "", titulo = "Olbos" }) => {
  const completo = url ? `${texto}${texto ? "\n\n" : ""}${url}` : texto;
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share(url ? { title: titulo, text: texto, url } : { title: titulo, text: texto });
      return true;
    } catch (e) {
      if (e && e.name === "AbortError") return false; // el usuario cerró el menú: no insistir
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(completo)}`, "_blank");
  return true;
};

const compartirWhatsApp = ({ texto, tipo, id }) => {
  const url = `https://olbos.ai${id ? `?${tipo}=${id}` : ""}`;
  compartirNativo({ texto: `${texto}\n\n🏍️ Mirá esto en Olbos:`, url });
};

// Compartir con dos caminos a la vista: el menú del sistema Y copiar el link
// directo (pedido de Carlos: para pegar en Instagram sin pasar por WhatsApp)
const BtnCompartir = ({ texto, tipo, id, dark = false, small = false, arriba = false }) => {
  const [menu, setMenu] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const url = `https://olbos.ai${id ? (tipo === "evento" ? `/evento/${id}` : `?${tipo}=${id}`) : ""}`;
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button onClick={e => { e.stopPropagation(); setMenu(m => !m); }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: small ? 30 : 36, height: small ? 30 : 36,
          borderRadius: "50%",
          background: dark ? "rgba(0,0,0,.65)" : "transparent",
          border: "none", cursor: "pointer", flexShrink: 0,
        }}>
        <Share2 size={small ? 15 : 17} strokeWidth={1.8} color={dark ? "#fff" : "#999"} />
      </button>
      {menu && (
        <span onClick={e => e.stopPropagation()}
          style={{ position: "absolute", ...(arriba ? { bottom: "100%", marginBottom: 4 } : { top: "100%" }), right: 0, zIndex: 60, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: "0 6px 24px rgba(0,0,0,.18)", padding: 6, display: "flex", flexDirection: "column", minWidth: 150 }}>
          <button onClick={() => { setMenu(false); compartirWhatsApp({ texto, tipo, id }); }}
            style={{ padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text, textAlign: "left", borderRadius: 8 }}>
            📤 Compartir…
          </button>
          <button onClick={async () => {
            try { await navigator.clipboard.writeText(url); setCopiado(true); setTimeout(() => { setCopiado(false); setMenu(false); }, 1200); }
            catch (e) { alert("El link es: " + url); setMenu(false); }
          }}
            style={{ padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: copiado ? "#2F7D4F" : C.text, textAlign: "left", borderRadius: 8 }}>
            {copiado ? "✓ Copiado" : "🔗 Copiar link"}
          </button>
        </span>
      )}
    </span>
  );
};

// ─── HELPERS DE NAVEGACIÓN (por lat/lng) ──────────────────────────────────────
const wazeURL = (lat, lng) => `https://www.waze.com/ul?ll=${lat}%2C${lng}&navigate=yes`;
const googleMapsURL = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
// Navegar con el navegador PROPIO: guarda el viaje (el mapa lo levanta al abrir,
// vía la restauración del NavegadorOverlay) y abre el mapa de Olbos
// Puntos de paso para seguir un camino con el navegador: uno cada ~1/20 del largo, desde el
// punto del camino más cercano a "yo" (o desde el principio). El navegador descarta los que
// quedaron atrás y los que ya pasaste.
const paradasDeCamino = (camino, yo = null) => {
  if (!Array.isArray(camino) || camino.length < 2) return [];
  let desde = 0;
  if (yo && yo.lat != null) { let bd = Infinity; camino.forEach((p, i) => { const d = haversineKm(yo, p); if (d < bd) { bd = d; desde = i; } }); }
  const resto = camino.slice(desde);
  let kmTot = 0; for (let i = 1; i < resto.length; i++) kmTot += haversineKm(resto[i - 1], resto[i]);
  const cada = Math.max(3, kmTot / 20);
  const out = []; let acum = 0;
  for (let i = 1; i < resto.length - 1; i++) { acum += haversineKm(resto[i - 1], resto[i]); if (acum >= cada) { out.push({ lat: resto[i].lat, lng: resto[i].lng }); acum = 0; } }
  return out.slice(0, 23);
};
const navegarConOlbos = (lat, lng, nombre, paradas = null, grupoId = null, viajeId = null) => {
  try { localStorage.setItem("olbos_nav_activa", JSON.stringify({ lat: Number(lat), lng: Number(lng), nombre: nombre || "tu destino", t: Date.now() })); } catch (e) { /* */ }
  // El destino viaja EXPLÍCITO en el evento: si el mapa se está montando recién,
  // le llega como prop destinoInicial (los otros dos caminos —localStorage y el
  // evento olbos-navegar-a— quedan de respaldo para el mapa ya abierto)
  // grupoId: viaje vinculado a un grupo → el mapa también comparte tu posición y dibuja a los demás
  window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { navegarA: { lat: Number(lat), lng: Number(lng), nombre: nombre || "tu destino", ...(paradas && paradas.length ? { paradas } : {}) }, ...(grupoId ? { grupoId, grupoEnVivo: true } : viajeId ? { viajeId } : {}) } }));
  // Aviso directo por si el mapa YA estaba montado: su restauración desde
  // localStorage corre una sola vez por montaje, así que sin esto el botón no
  // hacía nada si el mapa se había abierto antes en la sesión (reporte de Matías:
  // "ir a un evento con Olbos no muestra cómo llegar").
  window.dispatchEvent(new CustomEvent("olbos-navegar-a", { detail: { lat: Number(lat), lng: Number(lng), nombre: nombre || "tu destino", ...(paradas && paradas.length ? { paradas } : {}) } }));
};
// Botón "navegá con Olbos" para poner al lado de los de Maps/Waze en las fichas
const BotonOlbosNav = ({ lat, lng, nombre }) => (
  <button onClick={() => navegarConOlbos(lat, lng, nombre)}
    style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.accent, border: "none", color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
    🏍️ Olbos
  </button>
);

// ─── COMBUSTIBLE (⛽ cargas de nafta por moto — inspirado en Fuelio) ──────────
// Consumo real con el método tanque-lleno-a-tanque-lleno: entre dos cargas
// "lleno" consecutivas, los litros repuestos son los consumidos en esos km.
// Las cargas parciales del medio se suman al tramo.
const calcularConsumoCargas = (cargas) => {
  const asc = (cargas || []).filter(c => c.km && c.litros)
    .sort((a, b) => (a.km - b.km) || String(a.fecha || "").localeCompare(String(b.fecha || "")));
  let kmTot = 0, ltTot = 0;
  const kmEntreLlenos = [];
  let inicio = null, litrosAcum = 0;
  asc.forEach(c => {
    if (inicio) litrosAcum += Number(c.litros) || 0;
    if (c.lleno) {
      if (inicio && c.km > inicio.km + 5 && litrosAcum > 0) {
        kmTot += c.km - inicio.km;
        ltTot += litrosAcum;
        kmEntreLlenos.push(c.km - inicio.km);
      }
      inicio = c; litrosAcum = 0;
    }
  });
  if (!kmEntreLlenos.length || !ltTot) return null;
  const kml = kmTot / ltTot;
  return {
    kml, l100: 100 / kml, tramos: kmEntreLlenos.length,
    kmPorTanque: Math.round(kmEntreLlenos.reduce((s, v) => s + v, 0) / kmEntreLlenos.length),
  };
};

// Modal de carga: pensado para usarse PARADO EN LA ESTACIÓN — 3 campos ($, litros,
// km del tablero) con los litros autocompletados desde el precio de tu última
// carga. Si viene gpsPos (desde el mapa), guarda dónde cargaste.
const COMBUSTIBLES = ["Súper", "Premium", "Otro"];
const CargaNaftaModal = ({ userId, motos = null, motoPre = null, gpsPos = null, onClose, onGuardada }) => {
  const [misMotos, setMisMotos] = useState(motos);
  const activas = (misMotos || []).filter(m => m && m.estado !== "eliminada" && m.estado !== "retirada");
  const [motoId, setMotoId] = useState(motoPre != null ? String(motoPre) : null);
  const [f, setF] = useState({ monto: "", litros: "", km: "", lleno: true, combustible: "", fecha: hoyLocalISO(), guardarEstacion: !!gpsPos });
  const [ult, setUlt] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const litrosTocadoRef = useRef(false);

  // Sin lista de motos (llamado desde el mapa): traerlas del perfil
  useEffect(() => {
    if (misMotos || !userId) return;
    supabase.from("perfiles").select("motos").eq("id", userId).single().then(({ data }) => {
      setMisMotos((data && data.motos) || []);
    }, () => setMisMotos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Moto por default: la preseleccionada o la principal
  useEffect(() => {
    if (!motoId && activas.length) setMotoId(String(activas[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misMotos]);
  // Última carga de la moto elegida: precio por litro y km de referencia
  useEffect(() => {
    if (!motoId || !userId) return;
    let vivo = true;
    supabase.from("moto_cargas").select("km, precio_litro, combustible, fecha")
      .eq("user_id", userId).eq("moto_id", String(motoId))
      .order("fecha", { ascending: false }).order("created_at", { ascending: false }).limit(1)
      .then(({ data }) => {
        if (!vivo) return;
        const u = (data && data[0]) || null;
        setUlt(u);
        if (u && u.combustible) setF(prev => ({ ...prev, combustible: prev.combustible || u.combustible }));
      }, () => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motoId]);

  const setCampo = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const setMonto = (v) => {
    setF(prev => {
      const sig = { ...prev, monto: v };
      // litros sugeridos desde el precio de la última carga, mientras no los toques
      if (!litrosTocadoRef.current && ult && Number(ult.precio_litro) > 0 && Number(v) > 0) {
        sig.litros = (Number(v) / Number(ult.precio_litro)).toFixed(1);
      }
      return sig;
    });
  };
  const precioLitro = Number(f.monto) > 0 && Number(f.litros) > 0 ? Number(f.monto) / Number(f.litros) : null;

  const guardar = async () => {
    if (!motoId) { alert("Elegí la moto"); return; }
    if (!(Number(f.litros) > 0) && !(Number(f.monto) > 0)) { alert("Cargá al menos los litros o la plata"); return; }
    setGuardando(true);
    const moto = activas.find(m => String(m.id) === String(motoId));
    const { error } = await supabase.from("moto_cargas").insert({
      user_id: userId,
      moto_id: String(motoId),
      fecha: f.fecha || hoyLocalISO(),
      km: Number(f.km) > 0 ? Math.round(Number(f.km)) : null,
      litros: Number(f.litros) > 0 ? Number(f.litros) : null,
      monto: Number(f.monto) > 0 ? Number(f.monto) : null,
      precio_litro: precioLitro ? Math.round(precioLitro * 100) / 100 : null,
      lleno: !!f.lleno,
      combustible: f.combustible || null,
      estacion: f.guardarEstacion && gpsPos && gpsPos.lat ? { lat: gpsPos.lat, lng: gpsPos.lng } : null,
    });
    setGuardando(false);
    if (error) { alert("No se pudo guardar la carga: " + error.message); return; }
    // El km del tablero que se carga con la nafta es el kilometraje de la moto:
    // se anota igual que "Actualizar km" de la ficha (Carlos, 18-sep). Solo si es
    // mayor al último conocido, para no retroceder por un error de tipeo.
    if (Number(f.km) > 0) {
      try {
        const { data: ult } = await supabase.from("moto_mantenimientos").select("km").eq("user_id", userId).eq("moto_id", String(motoId)).not("km", "is", null).order("km", { ascending: false }).limit(1).maybeSingle();
        if (!ult || Number(ult.km) < Math.round(Number(f.km))) {
          await supabase.from("moto_mantenimientos").insert({
            user_id: userId, moto_id: String(motoId),
            moto_nombre: moto ? `${moto.marca || ""} ${moto.modelo || ""}`.trim() || "Moto" : "Moto",
            tipo: "Actualización km", fecha: f.fecha || hoyLocalISO(), km: Math.round(Number(f.km)), moneda: "ARS",
          });
        }
      } catch (e) { /* la carga ya quedó guardada */ }
    }
    if (onGuardada) onGuardada(String(motoId));
    onClose();
  };

  const inp = { width: "100%", padding: "12px 13px", borderRadius: 11, border: `1px solid ${C.border}`, background: C.card, fontSize: 16, fontWeight: 700, color: C.text, outline: "none", boxSizing: "border-box" };
  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5 };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(17,17,17,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, background: C.bg, borderRadius: "18px 18px 0 0", padding: "18px 18px calc(18px + env(safe-area-inset-bottom, 0px))", maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>⛽ Cargué nafta</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 12 }}>✕</button>
        </div>
        {misMotos === null ? (
          <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando tus motos…</div>
        ) : !activas.length ? (
          <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>Primero agregá tu moto al garage.</div>
        ) : (
          <>
            {activas.length > 1 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {activas.map(m => (
                  <button key={m.id} onClick={() => setMotoId(String(m.id))}
                    style={{ padding: "7px 12px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", background: String(motoId) === String(m.id) ? "#111111" : C.card, color: String(motoId) === String(m.id) ? "#fff" : C.text, border: `1px solid ${String(motoId) === String(m.id) ? "#111111" : C.border}` }}>
                    {`${m.marca || ""} ${m.modelo || ""}`.trim() || "Moto"}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={lbl}>$ Total</div>
                <input type="number" inputMode="decimal" value={f.monto} onChange={e => setMonto(e.target.value)} placeholder="15000" style={inp} />
              </div>
              <div>
                <div style={lbl}>Litros</div>
                <input type="number" inputMode="decimal" value={f.litros} onChange={e => { litrosTocadoRef.current = true; setCampo("litros", e.target.value); }} placeholder="10.5" style={inp} />
              </div>
            </div>
            {precioLitro ? (
              <div style={{ fontSize: 11.5, color: C.muted, margin: "-6px 0 10px" }}>≈ ${precioLitro.toFixed(0)}/litro{ult && Number(ult.precio_litro) > 0 ? ` · la vez pasada pagaste $${Number(ult.precio_litro).toFixed(0)}` : ""}</div>
            ) : (ult && Number(ult.precio_litro) > 0 ? <div style={{ fontSize: 11.5, color: C.muted, margin: "-6px 0 10px" }}>La vez pasada pagaste ${Number(ult.precio_litro).toFixed(0)}/litro</div> : null)}
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Km del tablero</div>
              <input type="number" inputMode="numeric" value={f.km} onChange={e => setCampo("km", e.target.value)} placeholder={ult && ult.km ? `Último: ${Number(ult.km).toLocaleString("es-AR")}` : "Ej: 24350"} style={inp} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
              <button onClick={() => setCampo("lleno", !f.lleno)}
                style={{ padding: "8px 13px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: f.lleno ? "#111111" : C.card, color: f.lleno ? "#fff" : C.text, border: `1px solid ${f.lleno ? "#111111" : C.border}` }}>
                {f.lleno ? "✓ Tanque lleno" : "Carga parcial"}
              </button>
              {COMBUSTIBLES.map(cb => (
                <button key={cb} onClick={() => setCampo("combustible", f.combustible === cb ? "" : cb)}
                  style={{ padding: "8px 12px", borderRadius: 100, fontSize: 12, fontWeight: 600, cursor: "pointer", background: f.combustible === cb ? C.surface : C.card, color: C.text, border: `1.5px solid ${f.combustible === cb ? "#111111" : C.border}` }}>
                  {cb}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
              <input type="date" value={f.fecha} onChange={e => setCampo("fecha", e.target.value)} style={{ ...inp, width: "auto", flex: 1, fontSize: 13.5, fontWeight: 600 }} />
              {gpsPos && gpsPos.lat && (
                <button onClick={() => setCampo("guardarEstacion", !f.guardarEstacion)}
                  style={{ flex: 1, padding: "11px 8px", borderRadius: 11, fontSize: 12, fontWeight: 700, cursor: "pointer", background: f.guardarEstacion ? C.surface : C.card, color: f.guardarEstacion ? C.text : C.muted, border: `1.5px solid ${f.guardarEstacion ? "#111111" : C.border}` }}>
                  📍 {f.guardarEstacion ? "Estación guardada" : "Guardar estación"}
                </button>
              )}
            </div>
            <button onClick={guardar} disabled={guardando}
              style={{ width: "100%", padding: "14px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: guardando ? 0.6 : 1 }}>
              {guardando ? "Guardando…" : "Guardar carga"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Bloque ⛽ en la ficha de cada moto del garage: consumo real, gasto y últimas cargas
// Resumen de combustible en la tarjeta de la moto, siempre a la vista: Carlos
// cargó nafta y no encontró dónde ver las cargas ni el consumo (estaban solo
// adentro de la ficha que abre la llave 🔧). Tocarlo abre la ficha completa.
const ResumenCombustibleMini = ({ userId, moto, refrescoKey, onAbrir }) => {
  const [cargas, setCargas] = useState(null);
  useEffect(() => {
    let vivo = true;
    supabase.from("moto_cargas").select("fecha, km, litros, monto")
      .eq("user_id", userId).eq("moto_id", String(moto.id))
      .order("fecha", { ascending: false }).limit(30)
      .then(({ data }) => { if (vivo) setCargas(data || []); }, () => { if (vivo) setCargas([]); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moto.id, refrescoKey]);
  if (!cargas || !cargas.length) return null;
  const stats = calcularConsumoCargas(cargas);
  const ult = cargas[0];
  const hace30 = Date.now() - 30 * 86400000;
  const gasto = cargas.filter(c => new Date((c.fecha || "") + "T12:00:00").getTime() > hace30).reduce((s, c) => s + (Number(c.monto) || 0), 0);
  return (
    <button onClick={onAbrir}
      style={{ margin: "0 14px 10px", padding: "9px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, textAlign: "left", width: "calc(100% - 28px)" }}>
      <span style={{ fontSize: 15 }}>⛽</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {stats ? <strong>{stats.kml.toFixed(1)} km/L</strong> : <strong>{cargas.length} carga{cargas.length > 1 ? "s" : ""}</strong>}
        {gasto > 0 ? ` · $${Math.round(gasto).toLocaleString("es-AR")} en 30 días` : ""}
        {ult?.fecha ? ` · última ${new Date(ult.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}` : ""}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>Ver cargas ›</span>
    </button>
  );
};

const BloqueCombustible = ({ userId, moto, onRegistrar, refrescoKey }) => {
  const [cargas, setCargas] = useState(null);
  useEffect(() => {
    let vivo = true;
    supabase.from("moto_cargas").select("*")
      .eq("user_id", userId).eq("moto_id", String(moto.id))
      .order("fecha", { ascending: false }).order("created_at", { ascending: false }).limit(30)
      .then(({ data, error }) => { if (vivo) setCargas(error ? [] : (data || [])); }, () => { if (vivo) setCargas([]); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moto.id, refrescoKey]);

  const borrar = async (id) => {
    if (!window.confirm("¿Borrar esta carga?")) return;
    const { error } = await supabase.from("moto_cargas").delete().eq("id", id);
    if (error) { alert("No se pudo borrar: " + error.message); return; }
    setCargas(prev => (prev || []).filter(c => c.id !== id));
  };

  const stats = calcularConsumoCargas(cargas || []);

  // Consumo comunitario (v2, opt-in): el consumo calculado se comparte ANÓNIMO
  // para el "consumo real" por modelo del catálogo. Guardamos un resumen por
  // moto (upsert), nunca las cargas: del otro lado solo se lee el promedio.
  const [compartir, setCompartir] = useState(() => {
    try { return localStorage.getItem("olbos_consumo_optin") !== "0"; } catch (e) { return true; }
  });
  useEffect(() => {
    if (!compartir || !stats || !moto.marca || !moto.modelo || !userId) return;
    supabase.from("consumo_modelos").upsert({
      user_id: userId, moto_id: String(moto.id),
      marca: String(moto.marca).trim(), modelo: String(moto.modelo).trim(),
      kml: Math.round(stats.kml * 100) / 100, tramos: stats.tramos,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id,moto_id" }).then(() => {}, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compartir, cargas]);
  const toggleCompartir = () => {
    const nuevo = !compartir;
    setCompartir(nuevo);
    try { localStorage.setItem("olbos_consumo_optin", nuevo ? "1" : "0"); } catch (e) { /* */ }
    if (!nuevo) supabase.from("consumo_modelos").delete().eq("user_id", userId).then(() => {}, () => {});
  };
  const hace30 = Date.now() - 30 * 86400000;
  const gastoMes = (cargas || []).filter(c => new Date((c.fecha || "") + "T12:00:00").getTime() > hace30)
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);
  const chip = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 100, background: C.surface, fontSize: 11.5, fontWeight: 700, color: C.text };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>⛽ Combustible</div>
        <button onClick={onRegistrar}
          style={{ fontSize: 11.5, color: "#fff", fontWeight: 700, background: C.accent, border: "none", cursor: "pointer", padding: "6px 12px", borderRadius: 100 }}>
          ＋ Registrar carga
        </button>
      </div>
      {cargas === null ? (
        <div style={{ fontSize: 12, color: C.muted, padding: "6px 0" }}>Cargando…</div>
      ) : !cargas.length ? (
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, background: C.surface, borderRadius: 10, padding: "12px 14px" }}>
          Registrá cada carga de nafta (plata, litros y km del tablero) y Olbos te calcula el consumo real de tu moto y cuánto gastás por mes.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {stats && <span style={chip}>🔥 {stats.kml.toFixed(1)} km/L</span>}
            {stats && <span style={{ ...chip, color: C.muted, fontWeight: 600 }}>{stats.l100.toFixed(1)} L/100</span>}
            {stats && stats.kmPorTanque > 0 && <span style={chip}>🛣️ ~{stats.kmPorTanque} km por tanque</span>}
            {gastoMes > 0 && <span style={chip}>💸 ${Math.round(gastoMes).toLocaleString("es-AR")} últimos 30 días</span>}
            {!stats && <span style={{ ...chip, fontWeight: 600, color: C.muted }}>Con 2 cargas con "tanque lleno" y km, sale el consumo real</span>}
          </div>
          {stats && (
            <button onClick={toggleCompartir}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "0 2px", marginBottom: 8, fontSize: 11, color: compartir ? "#1F8347" : C.muted, fontWeight: 600 }}>
              {compartir ? "✓" : "○"} Compartir mi consumo (anónimo) en la ficha del modelo del catálogo
            </button>
          )}
          {cargas.slice(0, 6).map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 2px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.muted, width: 46, flexShrink: 0 }}>{new Date((c.fecha || "") + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {[c.litros ? `${Number(c.litros).toFixed(1)} L` : null, c.monto ? `$${Number(c.monto).toLocaleString("es-AR")}` : null, c.km ? `${Number(c.km).toLocaleString("es-AR")} km` : null].filter(Boolean).join(" · ")}
                {!c.lleno && <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}> · parcial</span>}
              </div>
              <button onClick={() => borrar(c.id)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11, flexShrink: 0 }}>✕</button>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// ─── LUGAR PICKER (búsqueda + mapa interactivo arrastrable) ───────────────────
// Carga Leaflet dinámicamente si no está disponible
const loadLeaflet = () => {
  return new Promise((resolve) => {
    if (window.L) return resolve();
    // CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    // CSS override para que los mapas no floten por encima del UI
    if (!document.querySelector('style[data-olbos-leaflet]')) {
      const style = document.createElement("style");
      style.setAttribute("data-olbos-leaflet", "true");
      style.innerHTML = `
        .leaflet-pane, .leaflet-tile, .leaflet-marker-icon, .leaflet-marker-shadow,
        .leaflet-tile-container, .leaflet-pane > svg, .leaflet-pane > canvas,
        .leaflet-zoom-box, .leaflet-image-layer, .leaflet-layer { z-index: 1 !important; }
        .leaflet-top, .leaflet-bottom { z-index: 2 !important; }
        .leaflet-container { z-index: 0 !important; }
      `;
      document.head.appendChild(style);
    }
    // JS
    if (!document.querySelector('script[src*="leaflet.js"]')) {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => resolve();
      document.head.appendChild(script);
    } else {
      const check = setInterval(() => {
        if (window.L) { clearInterval(check); resolve(); }
      }, 100);
    }
  });
};

const GMAPS_KEY = "AIzaSyCcH4XvTNTwUyvEo5WA7NTOoczDdlreF1E";
// Map ID vectorial (con Rotación + Inclinación) — necesario para el course-up tipo Waze
const MAP_ID_NAV = "9d60e12bb8952e0a7c506742";
// Flechitas repartidas a lo largo de una línea: muestran el SENTIDO en que se
// hizo (o se va a hacer) el recorrido — pedido de Carlos mirando un track suyo.
// Se le pasan a cualquier Polyline como `icons`.
const FLECHAS_SENTIDO = [{
  icon: { path: 2 /* FORWARD_CLOSED_ARROW */, scale: 2.6, fillColor: "#fff", fillOpacity: 1, strokeColor: "#111111", strokeWeight: 1.2 },
  offset: "4%", repeat: "9%",
}];

const confirmarCierre = (onClose, puntos) => { if (puntos && puntos.length > 0 && !window.confirm('¿Salir sin guardar? Vas a perder los puntos cargados.')) return; onClose(); };

const loadGoogleMaps = () => new Promise((resolve) => {
  if (window.google?.maps) { resolve(); return; }
  if (document.querySelector('script[data-gmaps="olbos"]')) {
    const check = setInterval(() => { if (window.google?.maps) { clearInterval(check); resolve(); } }, 100);
    return;
  }
  const s = document.createElement("script");
  s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places&language=es`;
  s.async = true;
  s.setAttribute("data-gmaps", "olbos");
  s.onload = resolve;
  document.head.appendChild(s);
});

const LugarPicker = ({ value, onChange, placeholder, label }) => {
  const [query, setQuery] = useState(value?.nombre || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapElRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value?.nombre || ""); }, [value?.nombre]);

  // Cargar Google Maps al montar
  useEffect(() => {
    loadGoogleMaps().then(() => setMapsReady(true));
  }, []);

  // Búsqueda con Google Places AutocompleteService
  const buscarLugares = (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    if (!window.google?.maps?.places) return;
    setLoading(true);
    const svc = new window.google.maps.places.AutocompleteService();
    svc.getPlacePredictions(
      { input: q, componentRestrictions: { country: ["ar","cl","uy"] }, language: "es" },
      (preds, status) => {
        setLoading(false);
        if (status === "OK" && preds) setSuggestions(preds.slice(0, 5));
        else setSuggestions([]);
      }
    );
  };

  const onQueryChange = (val) => {
    setQuery(val);
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarLugares(val), 350);
  };

  const seleccionarLugar = (pred) => {
    setSuggestions([]);
    setShowSuggestions(false);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: pred.place_id, language: "es" }, (results, status) => {
      if (status === "OK" && results[0]) {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        const nombre = pred.structured_formatting?.main_text || pred.description.replace(/, Argentina|, Chile|, Uruguay/g, "");
        onChange({ nombre, lat, lng });
        setQuery(nombre);
        setShowMap(true);
        initMap(lat, lng);
      }
    });
  };

  const reverseGeocode = (lat, lng) => new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng }, language: "es" }, (results, status) => {
      if (status === "OK" && results[0]) {
        const parts = results[0].address_components;
        const ciudad = parts.find(p => p.types.includes("locality"))?.long_name || "";
        const provincia = parts.find(p => p.types.includes("administrative_area_level_1"))?.long_name || "";
        resolve([ciudad, provincia].filter(Boolean).join(", ") || results[0].formatted_address);
      } else resolve(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    });
  });

  const abrirMapaSinLugar = async () => {
    await loadGoogleMaps();
    setMapsReady(true);
    const center = { lat: -34.6037, lng: -58.3816 };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async pos => {
          const lat = pos.coords.latitude, lng = pos.coords.longitude;
          const nombre = await reverseGeocode(lat, lng);
          onChange({ nombre, lat, lng }); setQuery(nombre); setShowMap(true); initMap(lat, lng);
        },
        () => { onChange({ nombre: "Buenos Aires", ...center }); setQuery("Buenos Aires"); setShowMap(true); initMap(center.lat, center.lng); },
        { timeout: 3000 }
      );
    } else {
      onChange({ nombre: "Buenos Aires", ...center }); setQuery("Buenos Aires"); setShowMap(true); initMap(center.lat, center.lng);
    }
  };

  const initMap = (lat, lng) => {
    setTimeout(() => {
      if (!mapElRef.current) return;
      if (mapRef.current) {
        mapRef.current.setCenter({ lat, lng });
        markerRef.current?.setPosition({ lat, lng });
        return;
      }
      const map = new window.google.maps.Map(mapElRef.current, {
        center: { lat, lng }, zoom: 13,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
        styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
      });
      mapRef.current = map;
      const marker = new window.google.maps.Marker({ position: { lat, lng }, map, draggable: true });
      markerRef.current = marker;
      marker.addListener("dragend", async () => {
        const pos = marker.getPosition();
        const nombre = await reverseGeocode(pos.lat(), pos.lng());
        onChange({ nombre, lat: pos.lat(), lng: pos.lng() }); setQuery(nombre);
      });
      map.addListener("click", async (e) => {
        const lat = e.latLng.lat(), lng = e.latLng.lng();
        marker.setPosition({ lat, lng });
        const nombre = await reverseGeocode(lat, lng);
        onChange({ nombre, lat, lng }); setQuery(nombre);
      });
    }, 50);
  };

  useEffect(() => () => { mapRef.current = null; markerRef.current = null; }, []);

  return (
    <div style={{ marginBottom: 14, position: "relative" }}>
      {label && <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>}
      <div style={{ position: "relative" }}>
        <input value={query} onChange={e => onQueryChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          style={{ width: "100%", padding: "10px 14px", paddingRight: value?.lat ? 70 : 80,
            borderRadius: 8, background: C.surface, border: `1px solid ${value?.lat ? "#1A9E5C" : C.border}`,
            color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        {value?.lat ? (
          <button onClick={() => { setShowMap(s => !s); if (!showMap) initMap(value.lat, value.lng); }}
            style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", padding: "4px 8px", borderRadius: 6, background: "#1A9E5C", color: "#fff", fontSize: 10, fontWeight: 700, border: "none", cursor: "pointer" }}>
            {showMap ? "Ocultar" : "Ver mapa"}
          </button>
        ) : (
          <button onClick={abrirMapaSinLugar}
            style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", padding: "4px 8px", borderRadius: 6, background: C.surface, color: C.text, fontSize: 10, fontWeight: 700, border: `1px solid ${C.border}`, cursor: "pointer" }}>
            📍 Marcar
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, boxShadow: "0 4px 12px rgba(0,0,0,.1)", maxHeight: 220, overflowY: "auto" }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => seleccionarLugar(s)}
              style={{ padding: "10px 12px", borderBottom: i < suggestions.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", fontSize: 13, color: C.text, display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = C.surface}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <MapPin size={12} strokeWidth={2} color="#999" />
              <div>
                <div style={{ fontWeight: 600 }}>{s.structured_formatting?.main_text}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{s.structured_formatting?.secondary_text}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Buscando...</div>}

      {showMap && value?.lat && (
        <div style={{ marginTop: 8 }}>
          <div ref={mapElRef} style={{ width: "100%", height: 220, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4, textAlign: "center" }}>
            💡 Tocá el mapa o arrastrá el pin para ajustar la ubicación
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAPA CREADOR DE RUTA (Google Maps) ─────────────────────────────────────
const distanciaKm = (a, b) => {
  const R = 6371;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const distanciaTotal = (puntos) => {
  if (!puntos || puntos.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < puntos.length; i++) total += distanciaKm(puntos[i-1], puntos[i]);
  return Math.round(total);
};

// ─── INPUT DE LUGAR (slot con autocomplete para el creador de rutas) ────────
const InputLugarSlot = ({ icono, etiqueta, punto, placeholder, onSelect, onClear }) => {
  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const timerRef = useRef(null);

  const buscar = (q) => {
    setBusqueda(q);
    clearTimeout(timerRef.current);
    if (q.length < 2) { setSugerencias([]); return; }
    timerRef.current = setTimeout(() => {
      if (window.google?.maps?.places) {
        new window.google.maps.places.AutocompleteService().getPlacePredictions(
          { input: q, componentRestrictions: { country: ["ar", "cl", "uy"] }, language: "es" },
          (preds, status) => {
            if (status === "OK" && preds) setSugerencias(preds.map(p => ({
              nombre: p.structured_formatting?.main_text || p.description,
              subtitulo: p.structured_formatting?.secondary_text || "",
              placeId: p.place_id,
            })));
            else setSugerencias([]);
          }
        );
      }
    }, 350);
  };

  const elegir = (sug) => {
    setSugerencias([]); setBusqueda("");
    if (sug.lat && sug.lng) {
      onSelect(sug.lat, sug.lng, sug.nombre);
    } else if (sug.placeId && window.google?.maps) {
      new window.google.maps.Geocoder().geocode({ placeId: sug.placeId, language: "es" }, (results, status) => {
        if (status === "OK" && results[0]) {
          onSelect(results[0].geometry.location.lat(), results[0].geometry.location.lng(), sug.nombre);
        }
      });
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 26, textAlign: "center", fontSize: 14, flexShrink: 0 }}>{icono}</div>
      <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
        {punto ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 10px", borderRadius: 10,
            background: C.card, border: `1px solid ${C.border}`,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{etiqueta}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {punto.nombre}
              </div>
            </div>
            <button onClick={onClear} style={{
              width: 22, height: 22, borderRadius: "50%", background: C.surface,
              border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <X size={11} strokeWidth={2.5} color={C.muted} />
            </button>
          </div>
        ) : (
          <>
            <input value={busqueda} onChange={e => buscar(e.target.value)}
              onBlur={() => setTimeout(() => setSugerencias([]), 200)}
              placeholder={placeholder}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px dashed ${C.border}`, fontSize: 13, boxSizing: "border-box", background: C.surface }} />
            {sugerencias.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 300, boxShadow: "0 4px 16px rgba(0,0,0,.12)", overflow: "hidden", marginTop: 4 }}>
                {sugerencias.map((s, i) => (
                  <button key={i} onClick={() => elegir(s)}
                    style={{ width: "100%", padding: "10px 14px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, borderBottom: i < sugerencias.length - 1 ? `1px solid ${C.border}` : "none", display: "flex", gap: 8, alignItems: "center" }}>
                    <MapPin size={12} strokeWidth={2} color="#999" />
                    <div>
                      <div style={{ fontWeight: 600 }}>{s.nombre}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{s.subtitulo}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Trae un GPX de Wikiloc, Calimoto, Garmin o del que sea y lo deja listo para publicar.
// Un track grabado trae miles de puntos y el creador trabaja con un puñado (Directions
// no acepta más de 23 paradas intermedias), así que se reduce a 12 CONSERVANDO LA FORMA
// — pero los kilómetros se calculan sobre el trazado COMPLETO, que es más exacto que
// la línea recta entre paradas que estima el creador a mano.
const ImportarGPX = ({ onListo }) => {
  const inputRef = useRef(null);
  const [cargando, setCargando] = useState(false);

  const procesar = async (file) => {
    if (!file) return;
    setCargando(true);
    try {
      const texto = await file.text();
      const g = leerGPX(texto);
      if (!g || g.puntos.length < 2) { alert("No pude leer ese archivo. ¿Es un .gpx con una ruta adentro?"); return; }
      let km = 0;
      for (let i = 1; i < g.puntos.length; i++) km += haversineKm(g.puntos[i - 1], g.puntos[i]);
      const reducido = downsampleRecorrido(g.puntos, 12);
      const conNombre = reducido.map((p, i) => ({
        lat: p.lat, lng: p.lng,
        nombre: `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`,
        tipo: i === 0 ? "inicio" : i === reducido.length - 1 ? "fin" : "paso",
      }));
      onListo({ nombre: g.nombre, puntos: conNombre, km });
      alert(`Listo: ${Math.round(km)} km. Dejamos ${conNombre.length} puntos sobre el mapa — movelos o renombralos como quieras antes de publicar.`);
    } catch (e) {
      alert("No pude leer el archivo: " + (e.message || e));
    } finally {
      setCargando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px dashed ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>¿Ya tenés la ruta en otra app?</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>Traé el archivo .gpx de Wikiloc, Calimoto, Garmin o el que uses.</div>
        </div>
        <button onClick={() => inputRef.current && inputRef.current.click()} disabled={cargando}
          style={{ flexShrink: 0, padding: "8px 12px", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: cargando ? "default" : "pointer" }}>
          {cargando ? "Leyendo…" : "Importar GPX"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept=".gpx,application/gpx+xml,application/xml,text/xml"
        onChange={e => procesar(e.target.files && e.target.files[0])} style={{ display: "none" }} />
    </div>
  );
};

const MapaCreadorRuta = ({ puntos, setPuntos, onKmReal }) => {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const directionsRef = useRef(null);
  const [listo, setListo] = useState(!!window.google?.maps);
  const [editandoNombre, setEditandoNombre] = useState(null);
  // Modo de tap en el mapa: qué tipo se asigna al tocar (parada o punto de paso)
  const [modoTap, setModoTap] = useState("parada");
  const modoTapRef = useRef("parada");
  const cambiarModoTap = (m) => { setModoTap(m); modoTapRef.current = m; };

  // Elegir un camino lo FIJA: se le meten dos puntos de paso tomados de ese trazado.
  // Así el camino elegido queda guardado en la salida y lo ve igual cualquiera que la
  // abra después, sin necesidad de una columna nueva en la base ni de tocar los otros
  // creadores que comparten este mapa.
  const elegirCamino = (alt) => {
    const path = alt.path || [];
    if (path.length < 6) return;
    const puntoDe = (frac) => {
      const p = path[Math.floor(path.length * frac)];
      const lat = typeof p.lat === "function" ? p.lat() : p.lat;
      const lng = typeof p.lng === "function" ? p.lng() : p.lng;
      return { lat, lng, nombre: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, tipo: "paso" };
    };
    setPuntos(prev => {
      const inicio = prev[0];
      const fin = prev[prev.length - 1];
      return [inicio, puntoDe(0.33), puntoDe(0.66), fin];
    });
    setAlternativas([]);
  };
  // Ruta real por calles (Directions)
  const dirKeyRef = useRef("");
  const rutaPathRef = useRef(null);
  const [kmReal, setKmReal] = useState(null);
  const [calculandoRuta, setCalculandoRuta] = useState(false);
  const [showAgregarParada, setShowAgregarParada] = useState(false);
  const [showAgregarPaso, setShowAgregarPaso] = useState(false);
  // Preferencias de camino (pedido de Matías): sin esto había que sembrar el mapa de
  // puntos de paso para que la ruta no se fuera por la autopista. Google no sabe
  // "preferir tierra", pero evitando autopistas y peajes manda por caminos secundarios,
  // que es donde están los tramos lindos y de ripio.
  const [evitar, setEvitar] = useState({ autopistas: false, peajes: false });
  // Caminos posibles entre la salida y el destino. Google solo los devuelve cuando NO
  // hay puntos intermedios: en cuanto se fija uno, el camino queda determinado y la
  // lista desaparece sola, que es exactamente lo que corresponde.
  const [alternativas, setAlternativas] = useState([]);

  // Derivar slots desde el array de puntos
  const puntoEncuentro = puntos.find(p => p.tipo === "inicio") || null;
  const puntoFin = puntos.find(p => p.tipo === "fin") || null;
  const paradasIntermedias = puntos.filter(p => p.tipo !== "inicio" && p.tipo !== "fin");

  const setEncuentroSlot = (lat, lng, nombre) => {
    setPuntos(prev => [{ lat, lng, nombre, tipo: "inicio" }, ...prev.filter(p => p.tipo !== "inicio")]);
    if (mapRef.current) { mapRef.current.setCenter({ lat, lng }); mapRef.current.setZoom(11); }
  };
  const setFinSlot = (lat, lng, nombre) => {
    setPuntos(prev => [...prev.filter(p => p.tipo !== "fin"), { lat, lng, nombre, tipo: "fin" }]);
  };
  const addParadaSlot = (lat, lng, nombre) => {
    setPuntos(prev => {
      const nuevo = { lat, lng, nombre, tipo: "parada" };
      const idxFin = prev.findIndex(p => p.tipo === "fin");
      if (idxFin >= 0) { const c = [...prev]; c.splice(idxFin, 0, nuevo); return c; }
      return [...prev, nuevo];
    });
    setShowAgregarParada(false);
  };
  const addPasoSlot = (lat, lng, nombre) => {
    setPuntos(prev => {
      const nuevo = { lat, lng, nombre, tipo: "paso" };
      const idxFin = prev.findIndex(p => p.tipo === "fin");
      if (idxFin >= 0) { const c = [...prev]; c.splice(idxFin, 0, nuevo); return c; }
      return [...prev, nuevo];
    });
    setShowAgregarPaso(false);
  };

  // Buscar paradas POR MARCA dentro del corredor del camino (YPF, Axion, parrilla…):
  // trae solo lo que está a menos de ~2,5 km del trazado, ordenado según el recorrido
  const [qCorredor, setQCorredor] = useState("");
  const [resCorredor, setResCorredor] = useState(null);
  const [buscandoCorredor, setBuscandoCorredor] = useState(false);
  const buscarEnCorredor = () => {
    if (!qCorredor.trim() || !window.google || !mapRef.current) return;
    const aLL = (p) => (typeof p.lat === "function" ? { lat: p.lat(), lng: p.lng() } : { lat: p.lat, lng: p.lng });
    const camino = (rutaPathRef.current && rutaPathRef.current.length > 1)
      ? rutaPathRef.current.map(aLL)
      : [puntoEncuentro, ...paradasIntermedias, puntoFin].filter(Boolean).map(aLL);
    if (camino.length < 2) { alert("Definí primero el punto de encuentro y el destino."); return; }
    setBuscandoCorredor(true);
    const bounds = new window.google.maps.LatLngBounds();
    camino.forEach(p => bounds.extend(p));
    const svc = new window.google.maps.places.PlacesService(mapRef.current);
    svc.textSearch({ query: qCorredor.trim(), bounds }, (res, status) => {
      setBuscandoCorredor(false);
      if (status !== "OK" || !res) { setResCorredor([]); return; }
      const muestra = camino.filter((_, i) => i % Math.max(1, Math.floor(camino.length / 40)) === 0);
      const enCamino = res.map(r => {
        const pos = { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() };
        let dMin = Infinity, orden = 0;
        muestra.forEach((p, i) => { const d = haversineKm(pos, p); if (d < dMin) { dMin = d; orden = i; } });
        return { nombre: r.name, dir: (r.formatted_address || "").split(",").slice(0, 2).join(","), pos, dKm: dMin, orden, rating: r.rating };
      }).filter(x => x.dKm <= 2.5).sort((a, b) => a.orden - b.orden).slice(0, 8);
      setResCorredor(enCamino);
    });
  };

  // Pines numerados de los resultados: el número es el mismo de la lista, en dorado para no confundirlos con las paradas ya agregadas
  const pinesCorredorRef = useRef([]);
  useEffect(() => {
    pinesCorredorRef.current.forEach(m => m.setMap(null));
    pinesCorredorRef.current = [];
    if (!listo || !mapRef.current || !window.google || !resCorredor) return;
    pinesCorredorRef.current = resCorredor.map((r, i) => new window.google.maps.Marker({
      position: r.pos, map: mapRef.current, zIndex: 900 + i, title: r.nombre,
      label: { text: String(i + 1), color: "#FFFFFF", fontSize: "12px", fontWeight: "700" },
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 12, fillColor: C.gold, fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 2 },
    }));
    return () => { pinesCorredorRef.current.forEach(m => m.setMap(null)); pinesCorredorRef.current = []; };
  }, [resCorredor, listo]);
  // Km por el camino trazado entre cada resultado y las paradas del recorrido (anterior y siguiente).
  // Se mide sobre la línea de la ruta, no en línea recta, y se recalcula solo al sumar o sacar paradas.
  const distanciasCorredor = (r) => {
    const aLL = (p) => (typeof p.lat === "function" ? { lat: p.lat(), lng: p.lng() } : { lat: Number(p.lat), lng: Number(p.lng) });
    const paradas = puntos.filter(p => p.tipo !== "paso" && p.lat && p.lng);
    const path = (rutaPathRef.current && rutaPathRef.current.length > 1) ? rutaPathRef.current.map(aLL) : paradas.map(aLL);
    if (path.length < 2 || paradas.length < 2) return null;
    const cum = [0];
    for (let i = 1; i < path.length; i++) cum.push(cum[i - 1] + haversineKm(path[i - 1], path[i]));
    const escala = kmReal && cum[cum.length - 1] > 0 ? kmReal / cum[cum.length - 1] : 1;
    const kmEn = (pos) => { let best = Infinity, k = 0; path.forEach((q, i) => { const d = haversineKm(pos, q); if (d < best) { best = d; k = cum[i]; } }); return k * escala; };
    const kR = kmEn(r.pos);
    const ks = paradas.map(p => ({ tipo: p.tipo, km: kmEn(aLL(p)) })).sort((a, b) => a.km - b.km);
    const ant = [...ks].reverse().find(x => x.km <= kR) || ks[0];
    const sig = ks.find(x => x.km > kR) || ks[ks.length - 1];
    return {
      antKm: Math.max(0, Math.round(kR - ant.km)), antEs: ant.tipo === "inicio" ? "la salida" : "la parada anterior",
      sigKm: Math.max(0, Math.round(sig.km - kR)), sigEs: sig.tipo === "fin" ? "el destino" : "la siguiente",
    };
  };

  const TIPO_ICONS = { inicio: "🟢", fin: "🔴", parada: "📍", paso: "🔵", noche: "🌙", almuerzo: "🍽️" };

  useEffect(() => {
    loadGoogleMaps().then(() => {
      setListo(true);
    });
  }, []);

  useEffect(() => {
    if (!listo || !mapElRef.current || mapRef.current) return;
    const map = new window.google.maps.Map(mapElRef.current, {
      center: { lat: -34.6037, lng: -58.3816 },
      zoom: 5,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
    });
    mapRef.current = map;

    map.addListener("click", async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng }, language: "es" }, (results, status) => {
        let nombre = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        if (status === "OK" && results[0]) {
          const parts = results[0].address_components;
          const ciudad = parts.find(p => p.types.includes("locality"))?.long_name || "";
          const prov = parts.find(p => p.types.includes("administrative_area_level_1"))?.long_name || "";
          nombre = [ciudad, prov].filter(Boolean).join(", ") || results[0].formatted_address.split(",").slice(0, 2).join(",");
        }
        agregarPunto(lat, lng, nombre);
      });
    });
  }, [listo]);

  // Re-renderizar markers cuando cambian los puntos
  useEffect(() => {
    if (!mapRef.current) return;
    renderizarPuntos();
  }, [puntos, listo, evitar]);

  const agregarPunto = (lat, lng, nombre) => {
    setPuntos(prev => {
      const tieneInicio = prev.some(p => p.tipo === "inicio");
      if (!tieneInicio) return [{ lat, lng, nombre, tipo: "inicio" }, ...prev];
      const nuevo = { lat, lng, nombre, tipo: modoTapRef.current };
      const idxFin = prev.findIndex(p => p.tipo === "fin");
      if (idxFin >= 0) { const c = [...prev]; c.splice(idxFin, 0, nuevo); return c; }
      return [...prev, nuevo];
    });
  };

  const renderizarPuntos = () => {
    if (!mapRef.current || !window.google?.maps) return;
    // Limpiar
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) polylineRef.current.setMap(null);
    if (directionsRef.current) directionsRef.current.setMap(null);

    let paradaNum = 0;
    puntos.forEach((p, i) => {
      const isInicio = p.tipo === "inicio" || i === 0;
      const isFin = p.tipo === "fin" || i === puntos.length - 1;
      const isPaso = !isInicio && !isFin && p.tipo === "paso";
      if (!isInicio && !isFin && !isPaso) paradaNum++;
      // Paradas como PIN numerado en dorado, con la punta en el lugar exacto (pedido de
      // Matías N3): antes eran círculos negros del mismo color que la línea y no se
      // distinguían del trazado. Salida y destino, pin verde y rojo; los puntos de paso
      // (solo moldean el camino) siguen siendo un circulito blanco.
      const color = isInicio ? "#27AE60" : isFin ? "#B3372C" : "#8A6D3B";
      const label = isInicio ? "S" : isFin ? "D" : isPaso ? "" : String(paradaNum);
      const G = window.google.maps;
      const icono = isPaso ? {
        path: G.SymbolPath.CIRCLE, scale: 6, fillColor: "#fff", fillOpacity: 1, strokeColor: "#111111", strokeWeight: 3,
      } : {
        path: "M 0,0 C -2,-14 -11,-17 -11,-27 A 11,11 0 1,1 11,-27 C 11,-17 2,-14 0,0 z",
        fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2, scale: 1.25,
        anchor: new G.Point(0, 0), labelOrigin: new G.Point(0, -27),
      };

      const marker = new G.Marker({
        position: { lat: p.lat, lng: p.lng },
        map: mapRef.current,
        draggable: true,
        title: isPaso ? "Punto de paso" : `${label === "S" ? "Salida" : label === "D" ? "Destino" : "Parada " + label}: ${p.nombre || ""}`,
        label: label ? { text: label, color: "#fff", fontWeight: "bold", fontSize: "12px" } : null,
        icon: icono,
        zIndex: isPaso ? 1 : isInicio || isFin ? 3 : 2,
      });

      marker.addListener("dragend", async () => {
        const pos = marker.getPosition();
        const lat = pos.lat(), lng = pos.lng();
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng }, language: "es" }, (results, status) => {
          let nombre = p.nombre;
          if (status === "OK" && results[0]) {
            const parts = results[0].address_components;
            const ciudad = parts.find(c => c.types.includes("locality"))?.long_name || "";
            const prov = parts.find(c => c.types.includes("administrative_area_level_1"))?.long_name || "";
            nombre = [ciudad, prov].filter(Boolean).join(", ") || results[0].formatted_address.split(",").slice(0,2).join(",");
          }
          setPuntos(prev => prev.map((pt, j) => j === i ? { ...pt, lat, lng, nombre } : pt));
        });
      });

      markersRef.current.push(marker);
    });

    // Ruta real por calles (Directions API del SDK) con cache por coordenadas
    if (puntos.length > 1) {
      const coordsKey = puntos.map(p => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join("|") + `|ev:${evitar.autopistas ? 1 : 0}${evitar.peajes ? 1 : 0}`;
      if (coordsKey === dirKeyRef.current && rutaPathRef.current) {
        // Ya calculada para estas coords: redibujar directo
        polylineRef.current = new window.google.maps.Polyline({
          path: rutaPathRef.current,
          strokeColor: "#111111",
          strokeOpacity: 0.9,
          strokeWeight: 4,
          map: mapRef.current,
          icons: FLECHAS_SENTIDO,
        });
      } else {
        // Dibujar recta tenue provisoria mientras calcula
        polylineRef.current = new window.google.maps.Polyline({
          path: puntos.map(p => ({ lat: p.lat, lng: p.lng })),
          geodesic: true,
          strokeColor: "#111111",
          strokeOpacity: 0.25,
          strokeWeight: 3,
          map: mapRef.current,
        });
        setCalculandoRuta(true);
        const ds = new window.google.maps.DirectionsService();
        const intermedios = puntos.slice(1, -1).slice(0, 23); // límite Google: 25 waypoints
        ds.route({
          origin: { lat: puntos[0].lat, lng: puntos[0].lng },
          destination: { lat: puntos[puntos.length - 1].lat, lng: puntos[puntos.length - 1].lng },
          waypoints: intermedios.map(p => ({ location: { lat: p.lat, lng: p.lng }, stopover: p.tipo !== "paso" })),
          travelMode: window.google.maps.TravelMode.DRIVING,
          avoidHighways: evitar.autopistas,
          avoidTolls: evitar.peajes,
          provideRouteAlternatives: true,
        }, (result, status) => {
          setCalculandoRuta(false);
          // Evitar race: solo aplicar si los puntos no cambiaron mientras tanto.
          // OJO: esta clave tiene que armarse IGUAL que coordsKey, sufijo de "evitar"
          // incluido. Cuando se agregaron los botones de autopistas y peajes, el
          // sufijo se sumó arriba y acá no, así que la comparación daba distinto
          // SIEMPRE y el camino real no se dibujaba nunca: quedaba la recta gris
          // provisoria (reporte de Matías, 08-sep). Afecta también a los creadores
          // de eventos y salidas, que comparten este mismo mapa.
          const keyActual = puntos.map(p => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join("|") + `|ev:${evitar.autopistas ? 1 : 0}${evitar.peajes ? 1 : 0}`;
          if (keyActual !== coordsKey) return;
          if (status === "OK" && result.routes?.[0]) {
            // Solo tiene sentido ofrecer caminos mientras no haya puntos de paso:
            // con uno puesto, el camino ya está decidido por el rider.
            setAlternativas(intermedios.length === 0 && result.routes.length > 1
              ? result.routes.map(r => ({
                  resumen: r.summary || "Camino alternativo",
                  km: Math.round(r.legs.reduce((a, l) => a + (l.distance?.value || 0), 0) / 1000),
                  min: Math.round(r.legs.reduce((a, l) => a + (l.duration?.value || 0), 0) / 60),
                  path: r.overview_path,
                }))
              : []);
            dirKeyRef.current = coordsKey;
            rutaPathRef.current = result.routes[0].overview_path;
            const metros = result.routes[0].legs.reduce((acc, l) => acc + (l.distance?.value || 0), 0);
            setKmReal(Math.round(metros / 1000));
            // Los creadores completan "Distancia (km)" con los km del camino real, no con la línea recta
            if (onKmReal) onKmReal(Math.round(metros / 1000));
            if (polylineRef.current) polylineRef.current.setMap(null);
            polylineRef.current = new window.google.maps.Polyline({
              path: rutaPathRef.current,
              strokeColor: "#111111",
              strokeOpacity: 0.9,
              strokeWeight: 4,
              map: mapRef.current,
              icons: FLECHAS_SENTIDO, // el sentido de la marcha, igual que al redibujar de la caché
            });
          }
          // Si falla (ej: puntos sin camino entre sí), queda la recta tenue como fallback
        });
      }
    } else {
      setKmReal(null);
      rutaPathRef.current = null;
      dirKeyRef.current = "";
      setAlternativas([]);
    }

    // Ajustar vista
    if (puntos.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      puntos.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapRef.current.fitBounds(bounds, { padding: 50 });
    }
  };

  const eliminarPunto = (i) => {
    setPuntos(prev => prev.filter((_, j) => j !== i));
  };

  const cambiarTipo = (i, tipo) => {
    setPuntos(prev => prev.map((p, j) => j === i ? { ...p, tipo } : p));
  };

  const kmTotal = distanciaTotal(puntos);

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Buscadores SOLO para lo que falta: los puntos ya cargados viven en una única
          lista abajo (antes se repetían acá grisados y parecía todo duplicado) */}
      {listo && (
        <div style={{ marginBottom: 10 }}>
          {!puntoEncuentro && (
          <InputLugarSlot
            icono="🟢"
            etiqueta="Punto de encuentro"
            punto={null}
            placeholder="Punto de encuentro (buscá ciudad o lugar)…"
            onSelect={setEncuentroSlot}
            onClear={() => setPuntos(prev => prev.filter(p => p.tipo !== "inicio"))}
          />
          )}

          {showAgregarParada ? (
            <InputLugarSlot
              icono="📍"
              etiqueta="Nueva parada"
              punto={null}
              placeholder="Parada (buscá un lugar)…"
              onSelect={addParadaSlot}
              onClear={() => setShowAgregarParada(false)}
            />
          ) : showAgregarPaso ? (
            <InputLugarSlot
              icono="🔵"
              etiqueta="Nuevo punto de paso"
              punto={null}
              placeholder="Punto de paso (buscá un lugar)…"
              onSelect={addPasoSlot}
              onClear={() => setShowAgregarPaso(false)}
            />
          ) : (
            <div style={{ display: "flex", gap: 14, padding: "8px 0 8px 34px" }}>
              <button onClick={() => setShowAgregarParada(true)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 12.5, fontWeight: 600, color: C.text, textAlign: "left", padding: 0,
                }}>
                <Plus size={13} strokeWidth={2.5} /> Agregar parada
              </button>
              <button onClick={() => setShowAgregarPaso(true)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 12.5, fontWeight: 600, color: C.text, textAlign: "left", padding: 0,
                }}>
                <Plus size={13} strokeWidth={2.5} /> Punto de paso
              </button>
            </div>
          )}

          {/* Con inicio y fin definidos: buscar paradas por marca EN EL CAMINO */}
          {puntoEncuentro && puntoFin && (
            <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Buscar paradas en el camino</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={qCorredor} onChange={e => setQCorredor(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); buscarEnCorredor(); } }}
                  placeholder="YPF, Axion, parrilla, gomería…"
                  style={{ flex: 1, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box" }} />
                <button type="button" onClick={buscarEnCorredor} disabled={buscandoCorredor}
                  style={{ padding: "0 14px", borderRadius: 9, background: C.accent, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer", opacity: buscandoCorredor ? 0.6 : 1 }}>
                  {buscandoCorredor ? "…" : "Buscar"}
                </button>
              </div>
              {resCorredor && (
                <div style={{ marginTop: 8 }}>
                  {resCorredor.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Nada de eso cerca del camino. Probá otra marca o palabra.</div>}
                  {resCorredor.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: i < resCorredor.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <button type="button" onClick={() => { if (mapRef.current) { mapRef.current.panTo(r.pos); mapRef.current.setZoom(13); } }}
                        style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ width: 22, height: 22, borderRadius: 11, background: C.gold, color: "#fff", fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.nombre}{r.rating ? ` · ${r.rating}★` : ""}</div>
                          <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.dir} · a {r.dKm < 1 ? `${Math.round(r.dKm * 1000)} m` : `${r.dKm.toFixed(1)} km`} del camino</div>
                          {(() => { const d = distanciasCorredor(r); return d ? (
                            <div style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>a {d.antKm} km de {d.antEs} · a {d.sigKm} km de {d.sigEs}</div>
                          ) : null; })()}
                        </div>
                      </button>
                      <button type="button" onClick={() => { addParadaSlot(r.pos.lat, r.pos.lng, r.nombre); setResCorredor(resCorredor.filter((_, j) => j !== i)); }}
                        style={{ padding: "6px 11px", borderRadius: 8, background: C.accent, color: "#fff", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                        ＋ Parada
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!puntoFin && (
          <InputLugarSlot
            icono="🔴"
            etiqueta="Punto final"
            punto={null}
            placeholder="Punto final del recorrido…"
            onSelect={setFinSlot}
            onClear={() => setPuntos(prev => prev.filter(p => p.tipo !== "fin"))}
          />
          )}
        </div>
      )}

      {/* selector de modo de tap: qué tipo se asigna al tocar el mapa */}
      {listo && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, flexShrink: 0 }}>Al tocar el mapa:</span>
          <div style={{ display: "flex", background: C.surface, borderRadius: 9, padding: 2, flex: 1 }}>
            {[
              { id: "parada", label: "📍 Parada", color: C.text },
              { id: "paso", label: "🔵 Punto de paso", color: C.text },
            ].map(opt => {
              const activo = modoTap === opt.id;
              return (
                <button key={opt.id} onClick={() => cambiarModoTap(opt.id)}
                  style={{
                    flex: 1, padding: "6px 0", borderRadius: 7, border: "none", cursor: "pointer",
                    fontSize: 12, fontWeight: activo ? 700 : 500,
                    background: activo ? C.card : "transparent",
                    color: activo ? opt.color : C.muted,
                    boxShadow: activo ? "0 1px 3px rgba(0,0,0,.12)" : "none",
                    transition: "all .15s",
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* mapa */}
      <div ref={mapElRef} style={{ width: "100%", height: 280, borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}`, background: C.surface }} />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4, textAlign: "center" }}>
        {listo ? "El primer toque es la salida · después cada toque agrega una " + (modoTap === "paso" ? "punto de paso" : "parada") + " · el camino se traza por las rutas reales" : "Cargando Google Maps..."}
      </div>

      {/* Preferencias de camino: evitan tener que sembrar puntos de paso para que la
          ruta no se vaya por la autopista (pedido de Matías) */}
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {[["autopistas", "🛣️ Evitar autopistas"], ["peajes", "🎫 Evitar peajes"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setEvitar(e => ({ ...e, [k]: !e[k] }))}
            style={{ padding: "7px 12px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", border: evitar[k] ? "none" : `1px solid ${C.border}`, background: evitar[k] ? C.text : C.card, color: evitar[k] ? "#fff" : C.text }}>
            {label}
          </button>
        ))}
      </div>
      {evitar.autopistas && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
          Sin autopistas el trazado se va por caminos secundarios, que es donde están los tramos lindos. Para forzar un camino de tierra puntual, sumá un punto de paso sobre ese camino.
        </div>
      )}

      {/* Caminos posibles: lo mismo que ofrece el navegador al salir, pero al planificar */}
      {alternativas.length > 1 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 7 }}>
            Caminos posibles
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {alternativas.map((alt, i) => (
              <button key={i} type="button" onClick={() => elegirCamino(alt)}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "10px 12px", borderRadius: 10, border: `1px solid ${i === 0 ? C.text : C.border}`, background: C.card, cursor: "pointer" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {i === 0 ? "Por defecto · " : ""}{alt.resumen}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>
                    {alt.km.toLocaleString("es-AR")} km · {Math.floor(alt.min / 60)}h {alt.min % 60}m
                  </div>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>Elegir</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
            Al elegir uno le agregamos dos puntos de paso, así queda fijado y todos los que abran la salida ven ese mismo camino.
          </div>
        </div>
      )}

      {/* lista de puntos */}
      {puntos.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>
              {puntos.length} punto{puntos.length > 1 ? "s" : ""}
              {calculandoRuta
                ? <span style={{ color: C.muted, marginLeft: 6, fontWeight: 500, textTransform: "none" }}>· calculando ruta…</span>
                : kmReal
                  ? <span style={{ color: "#1F8347", marginLeft: 6 }}>· {kmReal} km por ruta</span>
                  : kmTotal > 0 && <span style={{ color: C.text, marginLeft: 6 }}>· {kmTotal} km aprox.</span>}
            </div>
            <button onClick={() => setPuntos([])}
              style={{ fontSize: 11, color: C.red, background: "none", border: "none", cursor: "pointer" }}>
              Limpiar todo
            </button>
          </div>
          {puntos.map((p, i) => {
            const esInicio = i === 0;
            const esFin = i === puntos.length - 1;
            const esPaso = !esInicio && !esFin && p.tipo === "paso";
            const numParada = puntos.slice(0, i + 1).filter((x, j) => j > 0 && j < puntos.length - 1 && x.tipo !== "paso").length;
            return (
            <div key={i} style={{ background: C.surface, borderRadius: 10, marginBottom: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
              {/* header del punto */}
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px" }}>
                <div title="Ver en el mapa" onClick={() => { if (mapRef.current && p.lat && p.lng) { mapRef.current.panTo({ lat: p.lat, lng: p.lng }); if ((mapRef.current.getZoom() || 0) < 13) mapRef.current.setZoom(14); } }}
                  style={{ width: 24, height: 24, borderRadius: "50%", background: esInicio ? "#27AE60" : esFin ? "#B3372C" : esPaso ? C.card : "#8A6D3B", border: esPaso ? "2.5px solid #111111" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: esPaso ? "#111111" : "#fff" }}>{esInicio ? "S" : esFin ? "D" : esPaso ? "" : numParada}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editandoNombre === i ? (
                    <input autoFocus value={p.nombre}
                      onChange={e => setPuntos(prev => prev.map((pt, j) => j === i ? { ...pt, nombre: e.target.value } : pt))}
                      onBlur={() => setEditandoNombre(null)}
                      style={{ width: "100%", padding: "3px 6px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12 }} />
                  ) : (
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}
                      onClick={() => setEditandoNombre(i)}>
                      {p.nombre}
                    </div>
                  )}
                  <select value={p.tipo || "parada"} onChange={e => cambiarTipo(i, e.target.value)}
                    style={{ fontSize: 10, color: C.muted, background: "none", border: "none", padding: 0, cursor: "pointer", marginTop: 2 }}>
                    <option value="inicio">🟢 Inicio</option>
                    <option value="parada">📍 Parada</option>
                    <option value="paso">🔵 Punto de paso</option>
                    <option value="almuerzo">🍽️ Almuerzo</option>
                    <option value="noche">🌙 Noche</option>
                    <option value="fin">🔴 Destino final</option>
                  </select>
                </div>
                <button onClick={() => eliminarPunto(i)}
                  style={{ width: 22, height: 22, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  <X size={10} strokeWidth={2.5} color={C.red} />
                </button>
              </div>
              {/* ¿Acá se suma gente? — el tilde en el propio punto arma los puntos de
                  suma de la inscripción (antes era una sección aparte, confundía) */}
              {!esFin && !esPaso && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 10px 8px 42px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flex: 1 }}>
                    <input type="checkbox" checked={!!p.suma}
                      onChange={e => setPuntos(prev => prev.map((pt, j) => j === i ? { ...pt, suma: e.target.checked } : pt))}
                      style={{ width: 15, height: 15, accentColor: "#111111" }} />
                    <span style={{ fontSize: 11.5, color: C.text, fontWeight: 600 }}>🙋 Acá se suma gente</span>
                  </label>
                  {p.suma && (
                    <input value={p.hora_suma || ""} placeholder="07:30"
                      onChange={e => setPuntos(prev => prev.map((pt, j) => j === i ? { ...pt, hora_suma: e.target.value } : pt))}
                      style={{ width: 64, padding: "4px 8px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, textAlign: "center" }} />
                  )}
                </div>
              )}
              {/* botones de navegación */}
              {p.lat && p.lng && (
                <div style={{ display: "flex", gap: 1, borderTop: `1px solid ${C.border}` }}>
                  <button onClick={() => navegarConOlbos(p.lat, p.lng, p.nombre || "la parada")}
                    style={{ flex: 1, padding: "7px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#fff", background: C.accent, border: "none", cursor: "pointer" }}>
                    🏍️ Olbos
                  </button>
                  <div style={{ width: 1, background: C.border }} />
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                    target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "7px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11, fontWeight: 600, color: C.text, textDecoration: "none", background: "#F8F9FF" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#111111"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    Google Maps
                  </a>
                  <div style={{ width: 1, background: C.border }} />
                  <a href={`${WAZE_BASE}ll=${p.lat},${p.lng}&navigate=yes`}
                    target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "7px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "#33CCFF", textDecoration: "none", background: "#F0FBFF" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#33CCFF"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.5 17.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5zm3.5-7c0 1.657-1.343 3-3 3s-3-1.343-3-3V7h2v3.5c0 .552.448 1 1 1s1-.448 1-1V7h2v3.5z"/></svg>
                    Waze
                  </a>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {puntos.length === 0 && listo && (
        <div style={{ textAlign: "center", padding: "12px 0", fontSize: 13, color: C.muted }}>
          Buscá un lugar arriba o tocá el mapa para empezar
        </div>
      )}
    </div>
  );
};

// ─── MAPA PREVIEW (Google Maps Static) ───────────────────────────────────────
const MapaPreview = ({ puntos, alto = 220, polyline, track = false }) => {
  if (!puntos || puntos.length === 0) return null;
  const validos = puntos.filter(p => p.lat && p.lng);
  if (validos.length === 0) return null;

  // ¿Es un track GPS grabado (muchos puntos seguidos) o una ruta armada a mano
  // (pocos waypoints elegidos)? En un track, un globito por punto era una lluvia
  // de marcadores (feedback 13-ago) — va solo inicio, fin y paradas + la línea.
  // `track` fuerza el modo grabado: un recorrido simplificado de un camino casi recto
  // queda en <=12 puntos y sin esto se dibujaba con globitos numerados y sin la línea.
  const esTrack = (track || validos.length > 12) && !validos.some(p => p.tipo);
  let markers;
  if (esTrack) {
    const inicio = validos[0], fin = validos[validos.length - 1];
    markers = [
      `markers=color:green%7Clabel:S%7C${inicio.lat},${inicio.lng}`,
      `markers=color:red%7Clabel:D%7C${fin.lat},${fin.lng}`,
    ].join("&");
  } else {
    markers = validos.map((p, i) => {
      const esInicio = i === 0, esFin = i === validos.length - 1;
      // Punto de paso: marcador chico azul sin etiqueta (moldea el camino, no es parada)
      if (!esInicio && !esFin && p.tipo === "paso") {
        return `markers=size:tiny%7Ccolor:0x0066CC%7C${p.lat},${p.lng}`;
      }
      const color = esInicio ? "green" : esFin ? "red" : "0x8A6D3B"; // paradas en dorado, como en el mapa de crear
      const numParada = validos.slice(0, i + 1).filter((x, j) => j > 0 && j < validos.length - 1 && x.tipo !== "paso").length;
      const label = esInicio ? "S" : esFin ? "D" : String(numParada);
      return `markers=color:${color}%7Clabel:${label}%7C${p.lat},${p.lng}`;
    }).join("&");
  }

  // Si hay polyline codificado (ruta real de Directions API), dibujarlo.
  // Si NO hay: en un track GPS la línea se dibuja con los propios puntos grabados;
  // en una ruta a mano no se dibuja nada (una recta entre ciudades no es un camino).
  let path = "";
  if (polyline) {
    path = `&path=color:0x111111FF|weight:4|enc:${encodeURIComponent(polyline)}`;
  } else if (esTrack) {
    const max = 90;
    const step = Math.max(1, Math.floor(validos.length / max));
    const pts = validos.filter((_, i) => i % step === 0 || i === validos.length - 1);
    path = `&path=color:0x111111FF|weight:4|` + pts.map(p => `${p.lat},${p.lng}`).join("|");
  }

  const url = `https://maps.googleapis.com/maps/api/staticmap?size=600x${alto * 2}&scale=2&${markers}${path}&key=${GMAPS_KEY}&language=es`;

  return (
    <img loading="lazy" decoding="async" src={url} alt="mapa de ruta" style={{ width: "100%", height: alto, objectFit: "cover", display: "block" }} />
  );
};

// ─── SECCIÓN: RUTAS ───────────────────────────────────────────────────────────
// ─── TRACK RECORDER ──────────────────────────────────────────────────────────
// Grabador GPS de trazas en tiempo real, con autosave a Supabase cada 30s.
// Limitación importante: requiere que la app esté abierta y la pantalla encendida.

// Haversine: distancia entre dos puntos GPS en km
const haversineKm = (p1, p2) => {
  const R = 6371;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) * Math.sin(dLng/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Un hueco del GPS (pantalla bloqueada en la web, túnel, app muerta) deja dos puntos lejanos
// sin nada en el medio. Ese tramo NO es camino andado: no suma km ni se dibuja como recta.
// Caso real (09-oct-2026, rodada a Carlos Paz): 133 km "en 24 min" a 383 km/h de promedio.
const esHuecoGPS = (a, b) => {
  if (!a || !b) return false;
  const km = haversineKm(a, b);
  const ta = a.ts != null ? a.ts : a.t, tb = b.ts != null ? b.ts : b.t;
  const dt = (ta != null && tb != null) ? Math.abs(tb - ta) / 1000 : null;
  if (km > 1.5 && (dt == null || dt > 20)) return true;          // 1,5 km sin ningún punto en el medio
  if (dt != null && dt > 0 && km / (dt / 3600) > 250) return true; // teletransporte
  return false;
};
const partirEnTramos = (pts) => {
  const tramos = []; let actual = [];
  for (let i = 0; i < (pts || []).length; i++) {
    if (i > 0 && esHuecoGPS(pts[i - 1], pts[i])) { if (actual.length > 1) tramos.push(actual); actual = []; }
    actual.push(pts[i]);
  }
  if (actual.length > 1) tramos.push(actual);
  return tramos;
};
const calcEstadisticas = (puntos) => {
  if (!puntos || puntos.length < 2) return { dist: 0, velMax: 0, velProm: 0, dur: 0 };
  let dist = 0;
  let velMax = 0;
  let dur = 0;
  for (let i = 1; i < puntos.length; i++) {
    if (esHuecoGPS(puntos[i-1], puntos[i])) continue; // el salto no es camino: ni km ni tiempo
    const segKm = haversineKm(puntos[i-1], puntos[i]);
    dist += segKm;
    // Tiempo "en movimiento": sumo solo los intervalos cortos. Los huecos largos
    // (pausas para comer/dormir en un viaje de varios días) NO cuentan como tiempo de ruta.
    const dt = (puntos[i].ts - puntos[i-1].ts) / 1000;
    if (dt > 0 && dt < 300) {
      dur += dt;
      // Velocidad máxima por tramo (distancia/tiempo) — coherente con el promedio.
      // Cap a 200 km/h para descartar saltos por glitch del GPS.
      const segKmh = segKm / (dt / 3600);
      if (segKmh > velMax && segKmh < 200) velMax = segKmh;
    }
  }
  dur = Math.max(1, Math.round(dur));
  const velProm = dur > 0 ? (dist / (dur / 3600)) : 0;
  return { dist, velMax, velProm, dur }; // velMax y velProm en km/h
};

// Un recorrido con saltos imposibles no se publica solo en el feed. Caso real
// (16-sep-2026, @braiansosa): una grabación que murió al arrancar quedó como
// "sin terminar", tres días después la retomó desde otra ciudad y el cierre
// publicó 3 puntos en línea recta, 119 km en "40 s" a 10.748 km/h.
// Pedido de @MatiAguilar_26 (26-oct-2026): para que el feed y el catálogo de rutas no se llenen de
// recorridos cortos del mismo rider, no se publica nada de menos de 10 km. Se sigue pudiendo guardar.
const KM_MIN_PUBLICAR = 10;
const msgMinKm = (km) => `Este recorrido tiene ${Number(km || 0).toFixed(1).replace(".", ",")} km y para publicarlo en el feed o en Rutas tiene que tener al menos ${KM_MIN_PUBLICAR} km. Queda guardado en Mis recorridos.`;
const recorridoCreible = (puntos) => {
  if (!Array.isArray(puntos) || puntos.length < 8) return { ok: false, motivo: "tiene muy pocos puntos GPS" };
  for (let i = 1; i < puntos.length; i++) {
    const km = haversineKm(puntos[i - 1], puntos[i]);
    if (km > 15) return { ok: false, motivo: `tiene un salto de ${Math.round(km)} km sin puntos GPS` };
    const dt = ((puntos[i].ts || 0) - (puntos[i - 1].ts || 0)) / 1000;
    if (km >= 1 && dt > 0 && km / (dt / 3600) > 250) return { ok: false, motivo: "tiene un tramo a más de 250 km/h" };
  }
  const st = calcEstadisticas(puntos);
  if (st.velProm > 200) return { ok: false, motivo: "da un promedio imposible" };
  return { ok: true, motivo: "" };
};
// En una rodada el navegador no está activo (el camino ya lo marca la rodada), así
// que el que te mira por el link no veía cuánto falta ni a qué hora llegás — solo
// pasaba con un destino propio (reporte de Carlos, 17-sep). Esto lo calcula sobre
// el recorrido planeado de la rodada: desde tu punto más cercano hasta el final,
// a tu velocidad reciente o, si todavía no hay datos, a 60 km/h.
const _rodadaPathCache = {};
const etaDeRodada = (evento, pos, velProp) => {
  if (!evento || !pos || pos.lat == null) return null;
  let path = _rodadaPathCache[evento.id];
  if (!path) {
    try {
      if (evento.polyline) path = decodificarPolyline(evento.polyline);
      else if (Array.isArray(evento.puntos)) path = evento.puntos.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
    } catch (e) { path = null; }
    if ((!path || path.length < 2) && evento.lat && evento.lng) path = [{ lat: Number(evento.lat), lng: Number(evento.lng) }];
    if (!path || !path.length) return null;
    _rodadaPathCache[evento.id] = path;
  }
  let rest;
  if (path.length === 1) rest = haversineKm(pos, path[0]);
  else {
    let idx = 0, dMin = Infinity;
    for (let i = 0; i < path.length; i++) { const d = haversineKm(pos, path[i]); if (d < dMin) { dMin = d; idx = i; } }
    rest = dMin;
    for (let i = idx; i < path.length - 1; i++) rest += haversineKm(path[i], path[i + 1]);
  }
  const kmh = velProp && velProp.kmh > 3 ? velProp.kmh : 60;
  const min = rest / kmh * 60;
  const eta = new Date(Date.now() + min * 60000);
  return {
    nav: { destino: evento.titulo || evento.title || "la rodada", rest_km: Math.round(rest * 10) / 10, min: Math.round(min), eta: `${eta.getHours()}:${String(eta.getMinutes()).padStart(2, "0")}` },
    path: path.length > 1 ? path : null,
  };
};
const formatDur = (segs) => {
  const h = Math.floor(segs / 3600);
  const m = Math.floor((segs % 3600) / 60);
  const s = Math.floor(segs % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

// ─── Compartir recorridos (trazas) en el feed ────────────────────────────
// Tope de medios en un post del feed: el mapa del trazo + fotos/videos del recorrido.
const MAX_MEDIAS_FEED = 10;

// Genera la URL de imagen estática (Google Static Maps) del recorrido.
const generarImagenTrackEstatica = (puntos) => {
  if (!Array.isArray(puntos) || puntos.length < 2) return null;
  try {
    // Submuestrear (la URL de Static Maps tiene límite ~8K caracteres)
    // Por forma, no 1 de cada N: en un circuito con vueltas el muestreo parejo dibujaba cuerdas
    // Static Maps acepta URLs de ~8K caracteres: probamos con el máximo detalle y
    // achicamos solo si la URL se pasa (con 120 puntos las curvas salían como rectas).
    const encodeNumber = (num) => {
      let sgn = num < 0 ? ~(num << 1) : (num << 1);
      let out = "";
      while (sgn >= 0x20) { out += String.fromCharCode((0x20 | (sgn & 0x1f)) + 63); sgn >>= 5; }
      out += String.fromCharCode(sgn + 63);
      return out;
    };
    const encodePolyline = (pts) => {
      let result = "", prevLat = 0, prevLng = 0;
      for (const p of pts) {
        const lat = Math.round(p.lat * 1e5), lng = Math.round(p.lng * 1e5);
        result += encodeNumber(lat - prevLat) + encodeNumber(lng - prevLng);
        prevLat = lat; prevLng = lng;
      }
      return result;
    };
    const armarUrl = (muestreados) => {
      const inicio = muestreados[0], fin = muestreados[muestreados.length - 1];
      return `https://maps.googleapis.com/maps/api/staticmap?size=600x400&maptype=roadmap` +
        `&markers=color:green%7Csize:mid%7C${inicio.lat},${inicio.lng}` +
        `&markers=color:red%7Csize:mid%7C${fin.lat},${fin.lng}` +
        `&path=color:0x111111FF%7Cweight:4%7Cenc:${encodeURIComponent(encodePolyline(muestreados))}` +
        `&key=AIzaSyCcH4XvTNTwUyvEo5WA7NTOoczDdlreF1E`;
    };
    let n = 700, url = "";
    for (let i = 0; i < 8; i++) {
      url = armarUrl(puntos.length <= n ? puntos : downsampleRecorrido(puntos, n));
      if (url.length <= 8000) break;
      n = Math.floor(n * 0.7);
    }
    return url;
  } catch (e) { console.warn("Error generando imagen track:", e); return null; }
};

// Carrusel de un post de recorrido: primero el mapa del trazo, después las
// fotos/videos subidas al recorrido (con tope MAX_MEDIAS_FEED).
// Recorta ~500 m de cada punta antes de dibujar el mapa que va al feed: el primer y
// el último punto de un recorrido casi siempre son la puerta de la casa del rider.
// Es la misma zona de privacidad que ya se aplica al aporte anónimo a la red de caminos.
const recortarPuntas = (pts, km = 0.5) => {
  const arr = (pts || []).filter(p => p && p.lat && p.lng);
  if (arr.length < 10) return arr;
  const avanzar = (a) => { let acc = 0, i = 0; for (; i < a.length - 1 && acc < km; i++) acc += haversineKm(a[i], a[i + 1]); return i; };
  const ini = avanzar(arr);
  const fin = arr.length - avanzar([...arr].reverse());
  const nucleo = arr.slice(ini, fin);
  return nucleo.length >= 5 ? nucleo : arr; // recorrido corto: mejor entero que roto
};

const construirMediasTraza = (puntos, fotos) => {
  const medias = [];
  const mapa = generarImagenTrackEstatica(puntos);
  if (mapa) medias.push({ url: mapa, type: "image" });
  (Array.isArray(fotos) ? fotos : []).forEach(f => {
    if (f && f.url) medias.push({ url: f.url, type: f.type === "video" ? "video" : "image" });
  });
  return medias.slice(0, MAX_MEDIAS_FEED);
};

// Caption del post de un recorrido (título + descripción + stats).
const construirTextoTraza = (traza) => {
  let texto = `🏍️ ${traza.titulo || "Recorrido"}\n\n`;
  if (traza.descripcion) texto += `${traza.descripcion}\n\n`;
  const dist = kmTraza(traza) ? `${kmTraza(traza).toFixed(1)} km${enAuto(traza) ? " en auto" : ""}` : "—";
  const dur = (traza.duracion_seg != null) ? formatDur(traza.duracion_seg) : null;
  const vel = (traza.velocidad_prom != null) ? `${Math.round(traza.velocidad_prom)} km/h prom` : null;
  texto += [`📍 ${dist}`, dur && `⏱ ${dur}`, vel && `💨 ${vel}`].filter(Boolean).join(" · ");
  return texto;
};

// Crea un post tipo "recorrido" en el feed a partir de una traza. Devuelve el
// resultado de supabase ({ error }). El caller decide cuándo llamarla.
const compartirTrazaEnFeed = async (traza, userId) => {
  const puntos = Array.isArray(traza.puntos) ? traza.puntos : [];
  const medias = construirMediasTraza(recortarPuntas(puntos), traza.fotos);
  // El username va en el post (como en todos los demás): sin esto el feed mostraba "@" vacío
  const { data: perfilAutor } = await supabase.from("perfiles").select("username").eq("id", userId).maybeSingle();
  return await supabase.from("posts").insert({
    user_id: userId,
    username: perfilAutor?.username || null,
    texto: construirTextoTraza(traza),
    media_url: medias[0]?.url || null,
    media_type: medias[0]?.type || null,
    media_urls: medias.length > 0 ? JSON.stringify(medias) : null,
    tipo: "recorrido",
    traza_id: traza.id,
    grupo_id: traza.grupo_id || null,
    // Si el recorrido se grabó desde una rodada, el post también queda en la galería
    // de ese evento (antes solo iba al feed general y la rodada quedaba sin sus fotos).
    evento_id: traza.evento_id || null,
    compartido_feed_general: !traza.grupo_id,
  });
};

// Reduce un recorrido de muchos puntos a un máximo, para mandarlo liviano en vivo
// Reduce un recorrido conservando la FORMA (Douglas-Peucker): descarta solo los
// puntos que están casi en línea recta — las curvas y esquinas quedan intactas,
// así la línea compartida no "corta camino" por encima de una manzana al doblar.
// Si con la tolerancia base (~20 m) sigue pasando el tope, la sube de a poco.
const downsampleRecorrido = (pts, max = 250) => {
  const arr = (pts || []).filter(p => p && p.lat && p.lng).map(p => ({ lat: p.lat, lng: p.lng }));
  if (arr.length <= max) return arr;
  const distSeg = (p, a, b) => {
    const dx = b.lng - a.lng, dy = b.lat - a.lat;
    if (dx === 0 && dy === 0) return Math.hypot(p.lng - a.lng, p.lat - a.lat);
    const t = Math.max(0, Math.min(1, ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(p.lng - (a.lng + t * dx), p.lat - (a.lat + t * dy));
  };
  const rdp = (puntos, tol) => {
    if (puntos.length <= 2) return puntos;
    let maxD = 0, idx = 0;
    for (let i = 1; i < puntos.length - 1; i++) {
      const d = distSeg(puntos[i], puntos[0], puntos[puntos.length - 1]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD <= tol) return [puntos[0], puntos[puntos.length - 1]];
    return [...rdp(puntos.slice(0, idx + 1), tol).slice(0, -1), ...rdp(puntos.slice(idx), tol)];
  };
  let tol = 0.00018; // ≈ 20 metros
  let out = rdp(arr, tol);
  // Tope de ~60 m: más allá, un circuito con vueltas se vuelve cuerdas cruzadas.
  // Si aun así sobran puntos, se ralea parejo sobre el trazado ya simplificado,
  // que conserva la forma mucho mejor que seguir subiendo la tolerancia.
  while (out.length > max && tol < 0.00054) { tol *= 1.5; out = rdp(arr, tol); }
  if (out.length > max) {
    const paso = out.length / max;
    const raleado = [];
    for (let i = 0; i < out.length; i += paso) raleado.push(out[Math.floor(i)]);
    if (raleado[raleado.length - 1] !== out[out.length - 1]) raleado.push(out[out.length - 1]);
    out = raleado;
  }
  return out;
};

// ─── INVITAR RIDERS DE OLBOS (mensaje interno, sin salir a WhatsApp) ─────────
// El link por WhatsApp sigue existiendo para los de afuera; esto es para invitar
// a los que YA están en Olbos: les llega un mensaje directo con el link adentro.
const InvitarRiders = ({ mensaje, onListo }) => {
  const [busca, setBusca] = useState("");
  const [result, setResult] = useState([]);
  const [enviados, setEnviados] = useState([]);

  const buscar = async (q) => {
    setBusca(q);
    if (q.trim().length < 2) { setResult([]); return; }
    const { data } = await supabase.from("perfiles").select("id, username, avatar_url").ilike("username", `%${q.trim()}%`).limit(8);
    setResult((data || []).filter(p => !enviados.includes(p.id)));
  };

  const invitar = async (p) => {
    setBusca(""); setResult([]);
    const { error } = await supabase.rpc("enviar_mensaje", { p_destino_id: p.id, p_texto: mensaje });
    if (error) { alert("No se pudo mandar la invitación a @" + p.username + ". Probá de nuevo."); return; }
    setEnviados(prev => [...prev, p.id]);
    if (onListo) onListo(p);
  };

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Invitar riders de Olbos</div>
      <input value={busca} onChange={e => buscar(e.target.value)}
        placeholder="Buscá por @usuario — le llega por mensaje"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box", background: C.surface }} />
      {result.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 6, overflow: "hidden", background: C.card }}>
          {result.map(p => (
            <button key={p.id} onClick={() => invitar(p)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "8px 12px", background: "none", border: "none", borderBottom: `1px solid ${C.surface}`, cursor: "pointer", textAlign: "left" }}>
              <img loading="lazy" decoding="async" src={p.avatar_url || avatarInicial(p.username)} alt="" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text }}>@{p.username}</span>
              <span style={{ fontSize: 11.5, color: C.muted }}>Invitar</span>
            </button>
          ))}
        </div>
      )}
      {enviados.length > 0 && (
        <div style={{ fontSize: 11.5, color: "#2F7D4F", fontWeight: 700, marginTop: 6 }}>
          ✓ {enviados.length} invitación{enviados.length !== 1 ? "es" : ""} enviada{enviados.length !== 1 ? "s" : ""} por mensaje
        </div>
      )}
    </div>
  );
};

// ─── CAPA DE GRUPO SOBRE EL TRACKRECORDER ───────────────────────────────────
// La rodada grupal vive SOBRE el grabador (no en un mapa aparte): así hereda el
// heading-up, las estadísticas, pausar, fotos, SOS y todo lo que el grabador ya
// hace bien. Esta capa suma: los avatares del grupo, los roles con sus anillos,
// la línea del que graba (con relevo automático), la largada y el intercom.
// ─── NavegadorOverlay ────────────────────────────────────────────────────────
// El turn-by-turn de Olbos, montado sobre el mapa del grabador: buscás el
// destino, "Llevame", y la ruta se dibuja con giros anunciados por la VOZ del
// teléfono (gratis) y recálculo si te desviás. MVP sobre Directions; cuando
// Valhalla esté arriba, el motor se cambia acá adentro sin tocar la UI.
const quitarHtml = (s) => String(s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
// Voz del navegador: se puede silenciar desde el banner de giros (el que va con
// intercomunicador o música no la quiere). La elección queda guardada.
const navVozActiva = () => { try { return localStorage.getItem("olbos_nav_voz") !== "0"; } catch (e) { return true; } };
// El motor de voz de Android (WebView) se traba a mitad del recorrido: el utterance se
// lo lleva el recolector de basura antes de terminar, y un cancel() seguido de speak()
// en el mismo instante deja la cola muerta (arranca bien y después no dice nada más).
// Por eso se guarda la referencia, se espera un instante tras cancelar y se destraba con resume().
let _ttsActual = null, _ttsTimer = null;
// En la app de Android habla el TextToSpeech NATIVO (plugin OlbosCar.hablar, desde la 1.10.42):
// el speechSynthesis del WebView se quedaba mudo (Matías, 22-oct y 30-oct: "no se escucha
// ninguna indicación"). Si el plugin no tiene el método (app vieja), sigue con el del WebView.
let _vozNativa = null; // null = sin probar · true = anda · false = esta app no la tiene
const vozNativaAndroid = () => { try { return _vozNativa !== false && esNativo() && Capacitor.getPlatform() === "android" && Capacitor.isPluginAvailable("OlbosCar"); } catch (e) { return false; } };
const decirTTS = (txt, rate) => {
  if (vozNativaAndroid()) {
    OlbosCar.hablar({ texto: txt, velocidad: rate }).then(() => { _vozNativa = true; }, () => { _vozNativa = false; decirTTSWeb(txt, rate); });
    return;
  }
  decirTTSWeb(txt, rate);
};
const callarTTS = () => {
  try { if (vozNativaAndroid()) OlbosCar.callarVoz().then(() => {}, () => {}); } catch (e) { /* */ }
  try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) { /* */ }
};
let _sinVozAvisado = false;
const decirTTSWeb = (txt, rate) => {
  const ss = window.speechSynthesis;
  if (!ss || typeof SpeechSynthesisUtterance === "undefined") {
    // Sin motor de voz (algunos WebView de Android): que quede registrado, no en silencio
    if (!_sinVozAvisado) { _sinVozAvisado = true; reportarError("tts: sin speechSynthesis", null, "decirTTS"); }
    return;
  }
  if (_ttsTimer) { clearTimeout(_ttsTimer); _ttsTimer = null; }
  if (ss.speaking || ss.pending) ss.cancel();
  const u = new SpeechSynthesisUtterance(txt);
  u.lang = "es-AR"; u.rate = rate;
  _ttsActual = u;
  u.onend = u.onerror = () => { if (_ttsActual === u) _ttsActual = null; };
  _ttsTimer = setTimeout(() => {
    _ttsTimer = null;
    try { ss.resume(); ss.speak(u); } catch (e) { /* */ }
  }, 80);
};
const hablarNav = (txt) => {
  if (!navVozActiva()) return;
  try { decirTTS(txt, 1.04); } catch (e) { /* sin voz, la pantalla igual guía */ }
};
// El banner muestra la FLECHA + la distancia; el verbo ("girá a la derecha")
// ya lo dice el glifo, así que del texto de Google dejamos solo la calle —
// pedido de Carlos: bien resumido, como Waze.
// Los avatares de los demás llegan cada ~5 s y se pintaban de un SALTO al punto
// nuevo: en el BikerFest se veía a cada uno teletransportándose, y el que iba
// adelante aparecía atrás tuyo hasta el aviso siguiente (reporte de Carlos).
// Esto los mueve continuamente: entre aviso y aviso se sigue avanzando con la
// velocidad y el rumbo de los dos últimos (como hace Uber con los autos), con
// tope de 8 s por si el otro se quedó sin señal, y cada posición nueva corrige
// suave en vez de pegar el salto. Un solo requestAnimationFrame para todos.
const crearSuavizadorVivo = () => {
  const est = {}; // clave -> { mk, ant, ult, dib }
  let raf = null, ultFrame = 0;
  const paso = () => {
    raf = null;
    const ahora = Date.now();
    const k = ultFrame ? 1 - Math.exp(-(ahora - ultFrame) / 350) : 0.1;
    ultFrame = ahora;
    let vivos = 0;
    for (const clave of Object.keys(est)) {
      const e = est[clave];
      if (!e.mk || !e.ult) continue;
      vivos++;
      let obj = { lat: e.ult.lat, lng: e.ult.lng };
      if (e.ant && e.ult.ts > e.ant.ts) {
        const dt = (e.ult.ts - e.ant.ts) / 1000;
        const kmh = haversineKm(e.ant, e.ult) / (dt / 3600);
        if (dt >= 1 && dt <= 20 && kmh > 3 && kmh < 160) {
          const fr = Math.min(8, (ahora - e.ult.ts) / 1000) / dt;
          obj = { lat: e.ult.lat + (e.ult.lat - e.ant.lat) * fr, lng: e.ult.lng + (e.ult.lng - e.ant.lng) * fr };
        }
      }
      const d = e.dib || obj;
      const nueva = { lat: d.lat + (obj.lat - d.lat) * k, lng: d.lng + (obj.lng - d.lng) * k };
      e.dib = nueva;
      if (Math.abs(nueva.lat - d.lat) + Math.abs(nueva.lng - d.lng) > 1e-8) e.mk.setPosition(nueva);
    }
    if (vivos > 0 && !document.hidden) raf = requestAnimationFrame(paso);
  };
  const arrancar = () => { if (!raf && !document.hidden) { ultFrame = 0; raf = requestAnimationFrame(paso); } };
  return {
    poner(clave, mk, pos) {
      const t = Date.now();
      const e = est[clave] || (est[clave] = { mk, ant: null, ult: null, dib: null });
      e.mk = mk;
      if (e.ult && Math.abs(e.ult.lat - pos.lat) + Math.abs(e.ult.lng - pos.lng) < 1e-7) return; // el mismo aviso repetido
      // Primera vez, salto grande (estuvo sin señal) o pantalla apagada: directo, sin animar
      if (!e.ult || document.hidden || haversineKm(e.ult, pos) > 1.5) {
        e.ant = null; e.ult = { lat: pos.lat, lng: pos.lng, ts: t }; e.dib = { lat: pos.lat, lng: pos.lng };
        mk.setPosition(pos); arrancar(); return;
      }
      e.ant = e.ult; e.ult = { lat: pos.lat, lng: pos.lng, ts: t };
      arrancar();
    },
    quitar(clave) { delete est[clave]; },
    parar() { if (raf) cancelAnimationFrame(raf); raf = null; for (const clave of Object.keys(est)) delete est[clave]; },
  };
};
// La calle a la que hay que doblar, sacada de las negritas de Google ("Gire a la
// <b>derecha</b> hacia <b>Av. Mitre</b>"): la última negrita que no sea un sentido
// ni el número de salida de una rotonda. Es lo único que va en el banner, como Waze.
const calleDePaso = (html) => {
  const negritas = []; const re = /<b>(.*?)<\/b>/gi; let m;
  while ((m = re.exec(html || ""))) negritas.push(quitarHtml(m[1]).trim());
  const utiles = negritas.filter(t => t
    && !/^(la )?(derecha|izquierda|recto|norte|sur|este|oeste|noreste|noroeste|sureste|suroeste)$/i.test(t)
    && !/^\d+(ª|º|°|\.ª|\.º|a|o)?$/i.test(t));
  return utiles.length ? utiles[utiles.length - 1] : "";
};
// ─── COMANDOS DE VOZ ─────────────────────────────────────────────────────────
// Hablarle a Olbos arriba de la moto (pedido de Carlos tras el BikerFest: "tiene
// que ser excelente, no como Gemini"). Principios: (1) NUNCA escucha sola — se
// activa manteniendo apretado el micrófono del mapa o con el botón play/pausa
// del intercomunicador del casco; (2) entiende en dos capas: un motor local
// instantáneo con sinónimos y tolerancia a errores del reconocedor, y si eso no
// alcanza, la frase va a Claude con un esquema cerrado de intenciones; (3) SIEMPRE
// contesta, con voz y en pantalla, aunque sea para decir que no entendió.
// Reconocimiento: plugin nativo en la app (el WebView de Android no trae
// SpeechRecognition) y Web Speech en el navegador.
const normalizarVoz = (s) => String(s || "").toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[¿?¡!.,;:"']/g, " ").replace(/\s+/g, " ").trim();
// Distancia de edición chica: "fotto", "parad" o "velosidad" también valen
const editDist = (a, b) => {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m || !n) return m + n;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
};
// Cada intención: patrones (ya normalizados al usarlos) y si captura lo que sigue
const VOZ_INTENCIONES = [
  { id: "ayuda", pats: ["ayuda", "que puedo decir", "que podes hacer", "comandos", "que se puede decir"] },
  { id: "foto", pats: ["foto", "fotito", "captura", "capturar", "fotografia", "saca una foto", "sacame una foto", "tomar foto", "toma una foto"] },
  { id: "video", pats: ["video", "filma", "filmar", "graba un video", "grabar video", "clip"] },
  { id: "parada", pats: ["parada", "marca una parada", "marcar parada", "marca parada", "paro aca", "paramos aca", "marcar punto", "marca este lugar", "guarda este lugar"], captura: true, tras: ["parada", "punto", "lugar"] },
  { id: "pausar", pats: ["pausa", "pausar", "pausa la grabacion", "para la grabacion", "frena la grabacion", "detener", "detene la grabacion"] },
  { id: "reanudar", pats: ["reanudar", "reanuda", "segui grabando", "seguir grabando", "continuar", "continua", "retomar", "retoma", "empeza a grabar", "empezar a grabar", "grabar", "graba", "iniciar", "arranca la grabacion", "dale grabando"] },
  { id: "finalizar", pats: ["finalizar", "finaliza", "terminar", "termina", "cerrar el recorrido", "cerra el recorrido", "fin del recorrido", "listo termine", "termine", "llegue", "guardar el recorrido", "guarda el recorrido"] },
  { id: "cuanto_falta", pats: ["cuanto falta", "cuanto queda", "a que hora llego", "a que hora llegamos", "cuando llego", "cuando llegamos", "cuantos kilometros faltan", "falta mucho", "cuanto me falta", "tiempo de llegada"] },
  { id: "velocidad", pats: ["velocidad", "a cuanto voy", "a cuanto vamos", "que velocidad", "cuanto voy", "a cuanto estoy"] },
  { id: "km", pats: ["cuantos kilometros llevo", "cuanto llevo", "cuanto hice", "cuantos kilometros hice", "kilometros recorridos", "distancia recorrida", "cuanto recorri", "cuanto llevamos"] },
  { id: "hora", pats: ["que hora es", "la hora", "hora"] },
  { id: "donde_esta", pats: ["donde esta", "donde anda", "donde quedo", "donde va", "a cuanto esta", "cuan lejos esta"], captura: true, tras: ["esta", "anda", "quedo", "va"] },
  { id: "avisar_grupo", pats: ["avisa al grupo", "avisale al grupo", "avisale a los chicos", "avisa a los chicos", "decile al grupo", "decile a los chicos", "manda al grupo", "mensaje al grupo", "avisa a todos", "avisale a todos", "decile a todos", "mandale al grupo", "avisa que", "avisale que"], captura: true, tras: ["que"] },
  { id: "ir_a", pats: ["llevame a", "llevame hasta", "ir a", "vamos a", "navegar a", "navega a", "navegar hasta", "ruta a", "ruta hasta", "destino", "como llego a", "quiero ir a", "andar a"], captura: true, tras: ["a", "hasta", "destino"] },
  { id: "reportar", pats: ["reporta", "reportar", "avisa un", "marca un", "hay un", "hay una", "hay ", "ojo", "cuidado", "alerta"], captura: true, tras: ["reporta", "reportar", "hay un", "hay una", "hay", "alerta", "marca un", "avisa un"] },
  { id: "nafta", pats: ["cargue nafta", "cargar nafta", "carga de nafta", "cargue combustible", "cargue", "registrar carga", "anotar carga", "cargamos nafta"] },
  { id: "sos", pats: ["emergencia", "auxilio", "necesito ayuda", "s o s", "sos olbos", "me cai", "tuve un accidente", "socorro"] },
  { id: "centrar", pats: ["centrar", "centra", "donde estoy", "volver al mapa", "mostra mi posicion", "centra el mapa", "seguime"] },
  { id: "silenciar", pats: ["silencio", "silenciar", "silencia", "callate", "mutear", "sin voz", "apaga la voz", "no hables mas"] },
  { id: "activar_voz", pats: ["activa la voz", "activar la voz", "habla", "con voz", "prende la voz", "hablame"] },
];
// Los tipos de alerta, con las palabras con que la gente los dice arriba de la moto
const VOZ_ALERTAS = [
  ["pozo", ["pozo", "bache", "hueco", "pozos", "baches"]],
  ["caminera", ["caminera", "policia", "control", "cana", "canas", "retén", "reten", "gendarmeria", "gendarmes", "transito", "radar", "los milicos"]],
  ["accidente", ["accidente", "choque", "chocaron", "volcado", "un auto dado vuelta"]],
  ["animales", ["animal", "animales", "vaca", "vacas", "caballo", "caballos", "perro", "perros", "carpincho", "carpinchos", "oveja", "ovejas"]],
  ["ripio", ["ripio", "tierra", "mal camino", "camino roto", "calle rota", "arena", "barro"]],
  ["lomada", ["lomo de burro", "lomada", "lomo", "reductor", "reductores"]],
  ["banquina", ["banquina", "banquina cortada", "desnivel"]],
  ["combustible", ["estacion de servicio", "nafta aca", "hay nafta", "surtidor"]],
  ["peligro", ["peligro", "aceite", "piedras", "obra", "obras", "derrumbe", "camion parado", "corte", "manifestacion", "piquete", "agua en la ruta", "niebla", "hielo"]],
];
const vozTipoAlerta = (t) => {
  const s = normalizarVoz(t);
  for (const [id, pals] of VOZ_ALERTAS) if (pals.some(p => s.includes(normalizarVoz(p)))) return id;
  return null;
};
// Motor local: busca el patrón más largo que aparezca en la frase (o casi: una
// letra de diferencia por palabra en patrones de una palabra) y captura el resto.
const interpretarVoz = (frase) => {
  const s = normalizarVoz(frase);
  if (!s) return null;
  let mejor = null;
  for (const it of VOZ_INTENCIONES) {
    for (const p0 of it.pats) {
      const p = normalizarVoz(p0);
      let idx = s.indexOf(p);
      if (idx < 0 && !p.includes(" ") && p.length >= 5) {
        // tolerancia: alguna palabra de la frase a 1 edición del patrón
        const pal = s.split(" ").find(w => w.length >= 4 && editDist(w, p) <= 1);
        if (pal) idx = s.indexOf(pal);
      }
      if (idx < 0) continue;
      const score = p.length + (idx === 0 ? 2 : 0);
      if (!mejor || score > mejor.score) mejor = { it, p, idx, score };
    }
  }
  if (!mejor) return null;
  let arg = "";
  if (mejor.it.captura) {
    const resto = s.slice(mejor.idx + mejor.p.length).trim();
    arg = resto;
    // "avisale al grupo QUE paro a cargar": el argumento arranca después del nexo
    for (const nexo of (mejor.it.tras || [])) {
      const rn = new RegExp("^" + nexo + "\\s+");
      if (rn.test(arg)) { arg = arg.replace(rn, ""); break; }
    }
    // "reportá un pozo" / "hay una vaca": el tipo puede estar en toda la frase
    if (mejor.it.id === "reportar") arg = vozTipoAlerta(s) || arg;
    if (mejor.it.id === "reportar" && !vozTipoAlerta(s)) return null; // "hay ..." sin nada conocido: que lo mire Claude
  }
  // "hay nafta" vs "cargué nafta": si dijo cargué, es la carga
  if (mejor.it.id === "reportar" && /cargu?e|cargamos|cargar/.test(s) && /nafta|combustible/.test(s)) return { id: "nafta", arg: "" };
  // La ambigüedad clásica: "parada" también aparece en "la grabación quedó parada"
  return { id: mejor.it.id, arg: arg.trim() };
};
const VOZ_PROMPT_CLAUDE = `Sos el intérprete de comandos de voz de Olbos, una app para motociclistas. El rider habla arriba de la moto, en español rioplatense, y el reconocedor de voz puede haber transcripto mal. Tu única tarea es mapear la frase a UNA intención y devolver SOLO un JSON, sin texto alrededor.
Intenciones posibles (id: significado; argumento):
- foto: sacar una foto ahora
- video: grabar un video corto
- parada: marcar una parada en el lugar actual; argumento = nombre de la parada si lo dijo
- pausar: pausar la grabación del recorrido
- reanudar: seguir/empezar a grabar
- finalizar: terminar y guardar el recorrido
- cuanto_falta: cuánto falta para llegar / a qué hora llega
- velocidad: a qué velocidad va
- km: cuántos kilómetros lleva recorridos
- hora: qué hora es
- donde_esta: dónde está otro rider del grupo; argumento = nombre o apodo
- avisar_grupo: mandar un mensaje al chat del grupo; argumento = el mensaje en primera persona, tal como lo diría el rider
- ir_a: navegar a un destino; argumento = el destino (lugar, ciudad, dirección)
- reportar: reportar algo en la ruta; argumento = uno de: pozo, caminera, accidente, animales, ripio, lomada, banquina, combustible, peligro
- nafta: registrar que cargó combustible
- sos: pedir ayuda de emergencia
- centrar: centrar el mapa en su posición
- silenciar: que la app deje de hablar
- activar_voz: que la app vuelva a hablar
- ayuda: qué se puede decir
- ninguna: no corresponde a nada de lo anterior
Formato: {"intencion":"<id>","argumento":"<texto o vacío>"}`;
const interpretarVozConClaude = async (frase) => {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(API_BASE + "/api/chat", {
      method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal,
      body: JSON.stringify({ model: "claude-haiku-4-5-20251001", max_tokens: 120, system: VOZ_PROMPT_CLAUDE, messages: [{ role: "user", content: String(frase).slice(0, 300) }] }),
    });
    clearTimeout(t);
    const d = await res.json();
    const s = (d && d.content && d.content[0] && d.content[0].text) || "";
    const m = s.match(/\{[\s\S]*\}/);
    if (!m) return null;
    const j = JSON.parse(m[0]);
    if (!j.intencion || j.intencion === "ninguna") return null;
    if (!VOZ_INTENCIONES.some(it => it.id === j.intencion)) return null;
    return { id: j.intencion, arg: String(j.argumento || "").trim(), porClaude: true };
  } catch (e) { return null; }
};
// La voz de las respuestas: independiente de la del navegador (se pueden apagar por separado)
const vozRespuestasActiva = () => { try { return localStorage.getItem("olbos_voz_muda") !== "1"; } catch (e) { return true; } };
const hablarVoz = (txt) => {
  if (!vozRespuestasActiva()) return;
  try { decirTTS(txt, 1.02); } catch (e) { /* sin voz, la pantalla igual contesta */ }
};
// Bip corto al empezar a escuchar: con el casco puesto es lo único que confirma
const bipVoz = (fin = false) => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = bipVoz._ctx || (bipVoz._ctx = new AC());
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.value = fin ? 660 : 880; o.type = "sine";
    g.gain.value = 0.0001;
    o.connect(g); g.connect(ctx.destination);
    const t0 = ctx.currentTime;
    g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    o.start(t0); o.stop(t0 + 0.15);
  } catch (e) { /* */ }
};
// Un WAV de silencio de 1 s para sostener la "sesión de medios": así el botón
// play/pausa del intercomunicador del casco le llega a la app.
const crearSilencioWav = () => {
  const sr = 8000, n = sr, buf = new ArrayBuffer(44 + n), v = new DataView(buf);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + n, true); w(8, "WAVE"); w(12, "fmt "); v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, sr, true); v.setUint32(28, sr, true);
  v.setUint16(32, 1, true); v.setUint16(34, 8, true); w(36, "data"); v.setUint32(40, n, true);
  for (let i = 0; i < n; i++) v.setUint8(44 + i, 128);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
};
const minutosAHablado = (min) => {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m} minuto${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60), r = m % 60;
  return `${h} hora${h === 1 ? "" : "s"}${r ? ` ${r}` : ""}`;
};
const kmAHablado = (km) => {
  const k = Number(km) || 0;
  if (k < 10) return `${k.toFixed(1).replace(".", ",")} kilómetros`;
  return `${Math.round(k)} kilómetros`;
};
// Foto sin tocar la pantalla: la cámara trasera, un instante para que ajuste la
// exposición, y un cuadro al archivo. Con getUserMedia no hace falta el gesto
// que exige el selector de archivos, y funciona en la app y en el navegador.
const capturarFotoConCamara = async () => {
  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
  try {
    const video = document.createElement("video");
    video.setAttribute("playsinline", "true"); video.muted = true; video.srcObject = stream;
    await video.play();
    await new Promise(r => setTimeout(r, 900));
    const c = document.createElement("canvas");
    c.width = video.videoWidth || 1280; c.height = video.videoHeight || 720;
    c.getContext("2d").drawImage(video, 0, 0, c.width, c.height);
    const blob = await new Promise(r => c.toBlob(r, "image/jpeg", 0.9));
    if (!blob) throw new Error("sin cuadro");
    return new File([blob], `voz-${Date.now()}.jpg`, { type: "image/jpeg" });
  } finally {
    stream.getTracks().forEach(t => t.stop());
  }
};
const grabarVideoConCamara = async (segundos = 10, alProgreso = null) => {
  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true });
  try {
    const tipos = ["video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    const mimeType = tipos.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || "";
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const partes = [];
    rec.ondataavailable = e => { if (e.data && e.data.size) partes.push(e.data); };
    const fin = new Promise(r => { rec.onstop = r; });
    rec.start(500);
    for (let i = 1; i <= segundos; i++) { await new Promise(r => setTimeout(r, 1000)); if (alProgreso) alProgreso(segundos - i); }
    rec.stop();
    await fin;
    const tipo = rec.mimeType || mimeType || "video/webm";
    const ext = tipo.includes("mp4") ? "mp4" : "webm";
    return new File(partes, `voz-${Date.now()}.${ext}`, { type: tipo.split(";")[0] });
  } finally {
    stream.getTracks().forEach(t => t.stop());
  }
};

// El micrófono del mapa + el botón del casco + la burbuja de respuesta.
// `acciones` trae las funciones del grabador (foto, parada, pausar, navegar…):
// este componente solo escucha, entiende, ejecuta y contesta.
// compacto: navegando, el micrófono baja a 48 px y va abajo a la derecha, como los demás controles
const ComandosVoz = ({ acciones, visible, compacto = false }) => {
  const [fase, setFase] = useState("idle"); // idle | escuchando | pensando | dicho
  const [texto, setTexto] = useState("");    // lo que se va reconociendo
  const [respuesta, setRespuesta] = useState("");
  const [showAyuda, setShowAyuda] = useState(false);
  const [cascoOn, setCascoOn] = useState(() => { try { return localStorage.getItem("olbos_voz_casco") === "1"; } catch (e) { return false; } });
  const [mudo, setMudo] = useState(() => !vozRespuestasActiva());
  const faseRef = useRef("idle");
  useEffect(() => { faseRef.current = fase; }, [fase]);
  const accionesRef = useRef(acciones);
  accionesRef.current = acciones;
  const recRef = useRef(null);       // Web Speech
  const nativoRef = useRef(null);    // { plugin, listeners }
  const ultimoRef = useRef("");
  const timerRef = useRef(null);
  const holdTsRef = useRef(0);
  const audioRef = useRef(null);
  const respTimerRef = useRef(null);
  const escucharRef = useRef(null);

  const responder = (txt) => {
    if (respTimerRef.current) clearTimeout(respTimerRef.current);
    setRespuesta(txt); setFase("dicho");
    hablarVoz(txt);
    respTimerRef.current = setTimeout(() => { setFase(f => f === "dicho" ? "idle" : f); setRespuesta(""); }, Math.min(9000, 2500 + txt.length * 45));
  };

  // ── Ejecutar una intención ──
  const ejecutar = async (it) => {
    const a = accionesRef.current || {};
    try {
      switch (it.id) {
        case "ayuda": setShowAyuda(true); responder("Mantené apretado el micrófono y hablá. Por ejemplo: sacá una foto, cuánto falta, avisá al grupo que paro a cargar nafta, o llevame a Chascomús."); return;
        case "foto": {
          responder("Sacando la foto.");
          try {
            const f = await capturarFotoConCamara();
            await a.foto(f);
            responder("Foto tomada y guardada en el recorrido.");
          } catch (e) {
            responder("No pude usar la cámara. Sacala con el botón Foto.");
          }
          return;
        }
        case "video": {
          responder("Grabando 10 segundos de video.");
          try {
            const f = await grabarVideoConCamara(10, (r) => setRespuesta(`🎥 Grabando… ${r} s`));
            await a.foto(f);
            responder("Video guardado en el recorrido.");
          } catch (e) {
            responder("No pude grabar el video. Usá el botón Video.");
          }
          return;
        }
        case "parada": {
          const r = a.parada(it.arg || "");
          responder(r || `Parada marcada${it.arg ? `: ${it.arg}` : ""}.`); return;
        }
        case "pausar": responder(a.pausar() || "Grabación en pausa."); return;
        case "reanudar": responder(a.reanudar() || "Grabando."); return;
        case "finalizar": responder(a.finalizar() || "Te abro el cierre del recorrido."); return;
        case "cuanto_falta": responder(a.cuantoFalta()); return;
        case "velocidad": responder(a.velocidad()); return;
        case "km": responder(a.kmLlevo()); return;
        case "hora": { const d = new Date(); responder(`Son las ${d.getHours()} y ${d.getMinutes() === 0 ? "cero" : d.getMinutes()}.`); return; }
        case "donde_esta": {
          if (!it.arg) { responder("¿Dónde está quién? Decime el nombre."); return; }
          setFase("pensando"); responder(await a.dondeEsta(it.arg)); return;
        }
        case "avisar_grupo": {
          if (!it.arg) { responder("¿Qué le aviso al grupo? Decime el mensaje."); return; }
          setFase("pensando"); responder(await a.avisarGrupo(it.arg)); return;
        }
        case "ir_a": {
          if (!it.arg) { responder("¿A dónde vamos? Decime el destino."); return; }
          setFase("pensando"); setRespuesta(`Buscando ${it.arg}…`);
          responder(await a.irA(it.arg)); return;
        }
        case "reportar": {
          const tipo = vozTipoAlerta(it.arg) || it.arg;
          if (!ALERTA_TIPOS.some(t => t.id === tipo)) { responder("¿Qué reporto? Pozo, caminera, accidente, animales, ripio o peligro."); return; }
          setFase("pensando"); responder(await a.reportar(tipo)); return;
        }
        case "nafta": responder(a.nafta() || "Te abro la carga de nafta."); return;
        case "sos": responder(a.sos() || "Mandando el aviso de emergencia."); return;
        case "centrar": responder(a.centrar() || "Mapa centrado."); return;
        case "silenciar": { try { localStorage.setItem("olbos_voz_muda", "1"); localStorage.setItem("olbos_nav_voz", "0"); } catch (e) { /* */ } setMudo(true); setRespuesta("🔇 Voz apagada. Decí 'activá la voz' para volver."); setFase("dicho"); setTimeout(() => setFase("idle"), 3000); return; }
        case "activar_voz": { try { localStorage.setItem("olbos_voz_muda", "0"); localStorage.setItem("olbos_nav_voz", "1"); } catch (e) { /* */ } setMudo(false); responder("Voz activada."); return; }
        default: responder("No entendí. Decí 'ayuda' para ver ejemplos.");
      }
    } catch (e) {
      responder("Algo falló al hacer eso. Probá de nuevo.");
    }
  };

  const procesar = async (crudo) => {
    const t = String(crudo || "").trim();
    if (!t) { responder("No te escuché. Mantené apretado y hablá."); return; }
    setTexto(t);
    let it = interpretarVoz(t);
    if (!it) { setFase("pensando"); it = await interpretarVozConClaude(t); }
    if (!it) { responder(`No entendí "${t}". Decí 'ayuda' para ver ejemplos.`); return; }
    await ejecutar(it);
  };

  // ── Reconocimiento: nativo en la app, Web Speech en el navegador ──
  const terminarEscucha = async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (faseRef.current !== "escuchando") return;
    bipVoz(true);
    const dicho = ultimoRef.current;
    ultimoRef.current = "";
    if (nativoRef.current) {
      const { listeners, plugin } = nativoRef.current;
      nativoRef.current = null;
      try { await plugin.stop(); } catch (e) { /* ya estaba parado */ }
      for (const l of listeners) { try { await l.remove(); } catch (e) { /* */ } }
    }
    recRef.current = null;
    await procesar(dicho);
  };
  const pararEscucha = async () => {
    try {
      if (nativoRef.current) await nativoRef.current.plugin.stop();
      else if (recRef.current) recRef.current.stop();
    } catch (e) { terminarEscucha(); }
    // Si el motor no avisa que paró (pasa en Android), no se queda colgado en "Te escucho":
    // al segundo y medio se cierra a mano con lo que se alcanzó a oír
    setTimeout(() => { if (faseRef.current === "escuchando") terminarEscucha(); }, 1500);
  };
  const escuchar = async () => {
    if (faseRef.current === "escuchando" || faseRef.current === "pensando") return;
    setTexto(""); setRespuesta(""); ultimoRef.current = "";
    callarTTS();
    setFase("escuchando");
    // Red de seguridad: si nada corta la escucha, se corta sola a los 8 s
    timerRef.current = setTimeout(() => pararEscucha(), 8000);
    if (esNativo()) {
      try {
        const { SpeechRecognition } = await import("@capacitor-community/speech-recognition");
        const { available } = await SpeechRecognition.available();
        if (!available) throw new Error("no disponible");
        const perm = await SpeechRecognition.requestPermissions();
        if (perm && perm.speechRecognition && perm.speechRecognition !== "granted") throw new Error("permiso");
        const listeners = [];
        listeners.push(await SpeechRecognition.addListener("partialResults", (d) => {
          const m = d && d.matches && d.matches[0];
          if (m) { ultimoRef.current = m; setTexto(m); }
        }));
        listeners.push(await SpeechRecognition.addListener("listeningState", (d) => {
          if (d && d.status === "stopped") terminarEscucha();
        }));
        nativoRef.current = { plugin: SpeechRecognition, listeners };
        bipVoz();
        const r = await SpeechRecognition.start({ language: "es-AR", maxResults: 3, partialResults: true, popup: false });
        if (r && r.matches && r.matches[0]) { ultimoRef.current = r.matches[0]; setTexto(r.matches[0]); }
        // En iOS start() resuelve al terminar; en Android el fin llega por listeningState
        if (r && r.matches) terminarEscucha();
        return;
      } catch (e) {
        nativoRef.current = null;
        // Sin plugin (app vieja) o sin permiso: se intenta Web Speech igual
      }
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setFase("idle");
      responder(esNativo() ? "Este teléfono no permite el reconocimiento de voz. Revisá el permiso de micrófono de Olbos." : "Este navegador no tiene reconocimiento de voz. Probá en la app o en Chrome.");
      return;
    }
    try {
      const rec = new SR();
      rec.lang = "es-AR"; rec.interimResults = true; rec.maxAlternatives = 3; rec.continuous = false;
      rec.onresult = (e) => {
        let t = "";
        for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
        ultimoRef.current = t; setTexto(t);
      };
      rec.onerror = (e) => {
        if (e && e.error === "not-allowed") { ultimoRef.current = ""; setFase("idle"); responder("Necesito permiso para usar el micrófono."); }
      };
      rec.onend = () => terminarEscucha();
      recRef.current = rec;
      bipVoz();
      rec.start();
    } catch (e) {
      setFase("idle");
      responder("No pude activar el micrófono.");
    }
  };
  escucharRef.current = escuchar;

  // ── Botón del casco: play/pausa del intercomunicador = "te escucho" ──
  const activarCasco = async () => {
    try {
      if (!audioRef.current) {
        const a = new Audio(crearSilencioWav());
        a.loop = true; a.volume = 0.01;
        audioRef.current = a;
      }
      await audioRef.current.play();
      if (navigator.mediaSession) {
        try { navigator.mediaSession.metadata = new window.MediaMetadata({ title: "Olbos · comandos de voz", artist: "Apretá play en el casco para hablar" }); } catch (e) { /* */ }
        ["play", "pause", "nexttrack", "previoustrack"].forEach(k => {
          try { navigator.mediaSession.setActionHandler(k, () => { try { audioRef.current && audioRef.current.play(); } catch (e) { /* */ } if (escucharRef.current) escucharRef.current(); }); } catch (e) { /* */ }
        });
      }
      return true;
    } catch (e) { return false; }
  };
  const desactivarCasco = () => {
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch (e) { /* */ }
    if (navigator.mediaSession) ["play", "pause", "nexttrack", "previoustrack"].forEach(k => { try { navigator.mediaSession.setActionHandler(k, null); } catch (e) { /* */ } });
  };
  const toggleCasco = async () => {
    if (cascoOn) { desactivarCasco(); setCascoOn(false); try { localStorage.setItem("olbos_voz_casco", "0"); } catch (e) { /* */ } return; }
    const ok = await activarCasco();
    if (!ok) { alert("No se pudo activar el botón del casco en este teléfono."); return; }
    setCascoOn(true); try { localStorage.setItem("olbos_voz_casco", "1"); } catch (e) { /* */ }
  };
  // Si ya lo tenía activado, se re-engancha con el primer toque en la pantalla
  useEffect(() => {
    if (!cascoOn || !visible) return;
    const h = () => { activarCasco(); window.removeEventListener("pointerdown", h); };
    window.addEventListener("pointerdown", h);
    return () => window.removeEventListener("pointerdown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascoOn, visible]);
  useEffect(() => () => { desactivarCasco(); if (timerRef.current) clearTimeout(timerRef.current); if (respTimerRef.current) clearTimeout(respTimerRef.current); }, []);

  // ── El botón: mantener apretado para hablar; un toque corto también escucha
  //    (corta sola cuando terminás de hablar). La primera vez, muestra cómo va.
  const onDown = (e) => {
    e.preventDefault();
    holdTsRef.current = Date.now();
    let primera = false;
    try { primera = !localStorage.getItem("olbos_voz_vista"); } catch (e2) { /* */ }
    if (primera) { try { localStorage.setItem("olbos_voz_vista", "1"); } catch (e2) { /* */ } setShowAyuda(true); return; }
    if (faseRef.current === "escuchando") { pararEscucha(); return; }
    escuchar();
  };
  const onUp = () => {
    if (faseRef.current !== "escuchando") return;
    if (Date.now() - holdTsRef.current > 450) pararEscucha(); // lo mantuvo: soltar = terminé
  };

  if (!visible) return null;
  const escuchando = fase === "escuchando";
  return (
    <>
      {/* burbuja: lo que escucha / piensa / contesta */}
      {(fase !== "idle" || respuesta) && (
        <div style={{ position: "absolute", bottom: compacto ? 70 : 150, right: 12, left: 84, zIndex: 8, display: "flex", justifyContent: "flex-end", pointerEvents: "none" }}>
          <div className="fade-up" style={{ maxWidth: "100%", background: "rgba(13,13,18,.94)", color: "#fff", borderRadius: 14, padding: "10px 13px", fontSize: 13.5, lineHeight: 1.4, backdropFilter: "blur(6px)", boxShadow: "0 6px 24px rgba(0,0,0,.35)" }}>
            {escuchando && <div style={{ fontSize: 11, fontWeight: 800, color: "#FF6B6B", letterSpacing: "0.04em", marginBottom: 2 }}>● TE ESCUCHO</div>}
            {fase === "pensando" && <div style={{ fontSize: 11, fontWeight: 800, color: "#bdb7b0", letterSpacing: "0.04em", marginBottom: 2 }}>PENSANDO…</div>}
            {escuchando ? (texto || <span style={{ color: "#bdb7b0" }}>Hablá…</span>)
              : fase === "pensando" ? (respuesta || <span style={{ color: "#bdb7b0" }}>“{texto}”</span>)
              : <span style={{ fontWeight: 700 }}>{respuesta}</span>}
          </div>
        </div>
      )}
      {/* el micrófono */}
      <button
        onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp}
        onContextMenu={e => e.preventDefault()}
        title="Mantené apretado y hablá"
        style={{
          position: "absolute", bottom: compacto ? 12 : 84, right: 12, zIndex: 7, width: compacto ? 48 : 58, height: compacto ? 48 : 58, borderRadius: "50%",
          background: escuchando ? "#B3372C" : C.accent, color: "#fff", border: compacto ? "2px solid #fff" : "3px solid #fff",
          fontSize: compacto ? 20 : 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: escuchando ? "0 0 0 8px rgba(179,55,44,.25), 0 2px 12px rgba(0,0,0,.4)" : "0 2px 12px rgba(0,0,0,.4)",
          touchAction: "none", WebkitTapHighlightColor: "transparent", userSelect: "none", transition: "background .15s, box-shadow .15s",
        }}>
        {escuchando ? "🎙️" : mudo ? "🔇" : "🎤"}
      </button>
      {/* ayuda: primera vez y cuando la pide */}
      {showAyuda && (
        <div onClick={e => e.target === e.currentTarget && setShowAyuda(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "22px 20px calc(28px + var(--sab))", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 4 }}>🎤 Hablale a Olbos</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
              Mantené apretado el micrófono, decí lo que necesitás y soltá. Te contesta con voz, así no mirás la pantalla.
            </div>
            {[
              ["📸", "“Sacá una foto” · “Grabá un video”", "Con la cámara trasera, sin tocar nada"],
              ["📍", "“Marcá una parada YPF”", "Queda en tu recorrido"],
              ["🧭", "“Llevame a Chascomús” · “¿Cuánto falta?”", "Navega y te dice km, tiempo y hora de llegada"],
              ["👥", "“Avisale al grupo que paro a cargar nafta” · “¿Dónde está Matías?”", "En una rodada con grupo"],
              ["⚠️", "“Hay un pozo” · “Caminera” · “Animales en la ruta”", "Reporta la alerta en tu posición"],
              ["⏺", "“Pausá” · “Seguí grabando” · “Terminá el recorrido”", "Controla la grabación"],
              ["⛽", "“Cargué nafta” · “¿A cuánto voy?” · “¿Cuánto llevo?”", ""],
              ["🆘", "“Emergencia”", "Avisa a tu contacto con tu posición"],
            ].map(([e, t, s]) => (
              <div key={t} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 18, width: 26, flexShrink: 0 }}>{e}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, lineHeight: 1.35 }}>{t}</div>
                  {s && <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{s}</div>}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 0 4px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Botón del casco</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45, marginTop: 2 }}>El play/pausa del intercomunicador activa la escucha. Mientras esté prendido, Olbos ocupa el audio y pausa la música.</div>
              </div>
              <button onClick={toggleCasco}
                style={{ width: 52, height: 30, borderRadius: 15, border: "none", background: cascoOn ? "#2F7D4F" : C.border, position: "relative", cursor: "pointer", flexShrink: 0 }}>
                <span style={{ position: "absolute", top: 3, left: cascoOn ? 25 : 3, width: 24, height: 24, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
              </button>
            </div>
            <button onClick={() => setShowAyuda(false)}
              style={{ width: "100%", marginTop: 12, padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const resumirPaso = (txt) => {
  let t = quitarHtml(txt);
  t = t.replace(/^(Gir[ae]|Dobl[ae]|Continú[ae]|Sigue|Segu[ií]|Toma|Tom[áa]|Incorpór[ae]te|Mantente|Manten[ée]te|Dirígete|Ve|And[áa])\b[^,]*?\s(?:hacia|por|en|a|hasta)\s+/i, "");
  t = t.replace(/^(Gir[ae]|Dobl[ae]|Continú[ae]|Sigue|Segu[ií])\s+(a la\s+)?(derecha|izquierda|recto)\b[\s,]*/i, "");
  t = t.replace(/\s*\(.*?\)\s*/g, " ").replace(/\s+/g, " ").trim();
  return t || quitarHtml(txt);
};
const GLIFO_MANIOBRA = { "turn-left": "↰", "turn-right": "↱", "turn-slight-left": "↖", "turn-slight-right": "↗", "turn-sharp-left": "↩", "turn-sharp-right": "↪", "uturn-left": "⤺", "uturn-right": "⤻", "roundabout-left": "↻", "roundabout-right": "↻", "merge": "↑", "straight": "↑", "ramp-left": "↰", "ramp-right": "↱", "fork-left": "↖", "fork-right": "↗", "keep-left": "↖", "keep-right": "↗" };
// Douglas-Peucker ITERATIVO (con pila propia, no recursivo: con decenas de miles de puntos
// la recursión se come la pila, ver el comentario de downsampleRecorrido) en metros
// aproximados. Devuelve [[lat, lng], ...] conservando las esquinas: solo saca los puntos que
// se apartan menos de `tolM` de la recta entre sus vecinos que quedan.
const simplificarRutaAuto = (pts, tolM) => {
  const n = pts.length;
  if (n <= 2) return pts.map(p => [p.lat, p.lng]);
  const kx = 111320 * Math.cos((pts[0].lat * Math.PI) / 180), ky = 110540;
  const xs = new Float64Array(n), ys = new Float64Array(n);
  for (let i = 0; i < n; i++) { xs[i] = pts[i].lng * kx; ys[i] = pts[i].lat * ky; }
  const X = i => xs[i], Y = i => ys[i];
  const queda = new Uint8Array(n);
  queda[0] = 1; queda[n - 1] = 1;
  const pila = [[0, n - 1]];
  const tol2 = tolM * tolM;
  while (pila.length) {
    const [a, b] = pila.pop();
    if (b - a < 2) continue;
    const ax = X(a), ay = Y(a), dx = X(b) - ax, dy = Y(b) - ay;
    const L2 = dx * dx + dy * dy;
    let peor = -1, dPeor = tol2;
    for (let i = a + 1; i < b; i++) {
      const px = X(i) - ax, py = Y(i) - ay;
      let d2;
      if (L2 === 0) d2 = px * px + py * py;
      else {
        const t = Math.max(0, Math.min(1, (px * dx + py * dy) / L2));
        const ex = px - t * dx, ey = py - t * dy;
        d2 = ex * ex + ey * ey;
      }
      if (d2 > dPeor) { dPeor = d2; peor = i; }
    }
    if (peor >= 0) { queda[peor] = 1; pila.push([a, peor], [peor, b]); }
  }
  const r = [];
  for (let i = 0; i < n; i++) if (queda[i]) r.push([pts[i].lat, pts[i].lng]);
  return r;
};
// ── Android Auto (fase 1, docs/ANDROID_AUTO.md) ────────────────────────────────
// Espejo del navegador en la pantalla del auto/moto: ruta, posición, próxima maniobra,
// ETA y distancia restante. Solo en la app nativa de Android con el plugin OlbosCar;
// en web e iOS no hace nada. Posición a 1 Hz como mucho; la ruta viaja solo cuando cambia.
const autoNav = (() => {
  let ok = null, ultimaRuta = null, ultimoEnvio = 0, escuchando = false;
  const disponible = () => {
    if (ok === null) { try { ok = esNativo() && Capacitor.getPlatform() === "android" && Capacitor.isPluginAvailable("OlbosCar"); } catch (e) { ok = false; } }
    return ok;
  };
  return {
    actualizar(d) {
      if (!disponible()) return;
      const rutaNueva = !!(d.path && d.path.length > 1 && d.path !== ultimaRuta);
      const ahora = Date.now();
      if (!rutaNueva && ahora - ultimoEnvio < 900) return;
      ultimoEnvio = ahora;
      const msg = { riders: !!d.riders, maniobra: { tipo: d.tipo || "", distanciaM: isFinite(d.distM) ? Math.round(d.distM) : null, calle: d.calle || "" }, etaMs: d.etaMs || 0, restanteM: isFinite(d.restM) ? Math.round(d.restM) : null, destino: d.destino || "", voz: navVozActiva() };
      // Las coordenadas del destino: quedan en "Destinos" de la pantalla del auto
      if (d.destinoPos && isFinite(d.destinoPos.lat) && isFinite(d.destinoPos.lng)) msg.destinoPos = { lat: d.destinoPos.lat, lng: d.destinoPos.lng };
      if (d.pos && isFinite(d.pos.lat) && isFinite(d.pos.lng)) msg.pos = { lat: d.pos.lat, lng: d.pos.lng, rumbo: typeof d.pos.head === "number" && isFinite(d.pos.head) ? d.pos.head : null };
      if (rutaNueva) {
        ultimaRuta = d.path;
        // Tope ~2500 puntos por el puente. NO quedarse con "uno de cada N": en una cuadrícula
        // se perdían las esquinas y la ruta cruzaba las manzanas en diagonal en la pantalla
        // del auto (visto en el DHU el 27-oct). Se simplifica sacando solo los puntos que
        // están en línea recta, con tolerancia creciente hasta entrar en el tope.
        let tol = 2, r = simplificarRutaAuto(d.path, tol);
        while (r.length > 2500 && tol < 200) { tol *= 2; r = simplificarRutaAuto(d.path, tol); }
        msg.ruta = r;
      }
      try { OlbosCar.actualizarNavegacion(msg).then(() => {}, () => {}); } catch (e) { /* sin Android Auto: nada */ }
    },
    terminar() {
      if (!disponible()) return;
      ultimaRuta = null; ultimoEnvio = 0;
      try { OlbosCar.terminarNavegacion().then(() => {}, () => {}); } catch (e) { /* */ }
    },
    // El rider tocó "Terminar" en la pantalla del auto. Devuelve el handle (promesa) o null.
    alTerminarDesdeAuto(fn) {
      if (!disponible()) return null;
      try { return OlbosCar.addListener("navTerminadaDesdeAuto", fn); } catch (e) { return null; }
    },
    // Destino elegido en la pantalla del auto: lo navega el teléfono (graba los km y usa la
    // ruta de riders). Mientras tanto el auto ya arrancó su navegación propia y le cede la
    // pantalla en cuanto el navegador de acá empieza a mandar datos. Se registra una vez.
    escucharDestinos() {
      if (escuchando || !disponible()) return;
      escuchando = true;
      try {
        OlbosCar.addListener("destinoDesdeAuto", (d) => {
          if (!d || !isFinite(d.lat) || !isFinite(d.lng)) return;
          registrarUso("auto_destino", {});
          navegarConOlbos(d.lat, d.lng, d.nombre || "tu destino");
        });
        // Parlante tocado en el auto: la voz del teléfono sigue la misma llave
        OlbosCar.addListener("vozDesdeAuto", (d) => {
          const activa = !!(d && d.activa);
          try { localStorage.setItem("olbos_nav_voz", activa ? "1" : "0"); } catch (e) { /* */ }
          if (!activa) { callarTTS(); }
          window.dispatchEvent(new CustomEvent("olbos-nav-voz", { detail: activa }));
        });
      } catch (e) { escuchando = false; }
    },
  };
})();
setTimeout(() => autoNav.escucharDestinos(), 0);
// Botón de capas del mapa: normal → satélite → relieve (pedido de un rider que
// anda fuera de ruta y necesita ver el terreno). Cicla con cada toque.
const BotonCapasMapa = ({ mapInstanceRef, style, onCambio = null }) => {
  const [tipo, setTipo] = useState("roadmap");
  const SIG = { roadmap: "hybrid", hybrid: "terrain", terrain: "roadmap" };
  const ICONO = { roadmap: "🗺️", hybrid: "🛰️", terrain: "⛰️" };
  return (
    <button onClick={() => { const n = SIG[tipo]; setTipo(n); if (mapInstanceRef.current) mapInstanceRef.current.setMapTypeId(n); if (onCambio) onCambio(n); }}
      title="Cambiar tipo de mapa"
      style={{ position: "absolute", zIndex: 6, width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,.95)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.25)", fontSize: 18, ...style }}>
      {ICONO[tipo]}
    </button>
  );
};

// destinoInicial: {lat,lng,nombre} — arranca navegando ahí directo, sin buscador.
// efimero: no persiste el viaje en localStorage (usado por "llevame al track").
// alTerminar: aviso al padre cuando la navegación se limpia (llegada o ✕).
// menuRef: el padre recibe acá las acciones que salieron del banner (guardar destino,
// voz, cancelar) para ponerlas en su menú ⋯. topBanner: dónde arranca el banner de
// giros (por defecto, debajo de la fila flotante ✕ · SOS · ⋯ del grabador).
const NavegadorOverlay = ({ mapInstanceRef, posActual, velMovil, alCambiarRuta = null, alActualizarNav = null, destinoInicial = null, efimero = false, ocultarBuscador = false, alTerminar = null, menuRef = null, topBanner = null, abrirBuscadorRef = null, alLlegar = null, alPreview = null, alActualizarParadasNav = null }) => {
  const [buscando, setBuscando] = useState(false);
  // Pedido de Matías (viajes): sumar/quitar paradas con la navegación en curso, sin salir del mapa.
  // modoParada = el buscador agrega una parada en vez de cambiar el destino.
  const [modoParada, setModoParada] = useState(false);
  const [paradasPropias, setParadasPropias] = useState([]); // [{lat,lng,nombre}] sumadas a mano
  const [q, setQ] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [nav, setNav] = useState(null); // {destino, destinoNombre, pasos, path, distTotalKm, durTotalMin}
  const [pasoIdx, setPasoIdx] = useState(0);
  // (km, minutos y hora de llegada salen por alActualizarNav: el grabador los muestra en su barra inferior)
  const rutaPolyRef = useRef(null);
  const idxPathRef = useRef(0); // hasta dónde de la ruta ya pasaste (la línea azul se consume)
  const anuncioRef = useRef({ idx: -1, fase: 0 });
  const recalcRef = useRef({ fuera: 0, ultimo: 0 });
  const traficoRef = useRef({ ultimo: 0, pidiendo: false }); // chequeo de camino más rápido
  const navActualRef = useRef(null);
  navActualRef.current = nav;
  const svcRef = useRef({});
  // Alternativas de ruta antes de arrancar (Google + caminos de la comunidad)
  const [eligiendo, setEligiendo] = useState(null); // {ops: [...], sel: idx}
  const polysAltRef = useRef([]);
  // Eventos de Olbos como destino + lugares guardados
  const [sugEventos, setSugEventos] = useState([]);
  const debounceRef = useRef(null);
  const [guardados, setGuardados] = useState(() => { try { return JSON.parse(localStorage.getItem("olbos_lugares_guardados") || "[]"); } catch (e) { return []; } });
  const [voz, setVoz] = useState(navVozActiva); // indicaciones habladas on/off
  // El parlante de la pantalla del auto también la prende/apaga (ver autoNav)
  useEffect(() => {
    const f = (e) => setVoz(!!e.detail);
    window.addEventListener("olbos-nav-voz", f);
    return () => window.removeEventListener("olbos-nav-voz", f);
  }, []);
  const persistirGuardados = (arr) => { setGuardados(arr); try { localStorage.setItem("olbos_lugares_guardados", JSON.stringify(arr)); } catch (e) { /* */ } };
  const destinoGuardado = nav && guardados.some(g => haversineKm(g, nav.destino) < 0.05);
  // La pastilla "¿A dónde vamos?" del grabador (rodada libre sobre el lienzo v2) abre este buscador
  if (abrirBuscadorRef) abrirBuscadorRef.current = () => setBuscando(true);
  const inicioNavRef = useRef(null); // { t, km, restaurada } de la ruta pedida: para la ficha de llegada

  const buscar = (texto) => {
    setQ(texto);
    if (texto.trim().length < 3) { setSugerencias([]); setSugEventos([]); return; }
    if (window.google) {
      if (!svcRef.current.auto) svcRef.current.auto = new window.google.maps.places.AutocompleteService();
      // Con la ubicación del rider: pedimos distancia y ordenamos por cercanía (más cerca primero)
      const miPos = posActual && posActual.lat != null && posActual.lng != null ? { lat: Number(posActual.lat), lng: Number(posActual.lng) } : null;
      const opts = { input: texto, language: "es" };
      if (miPos) { opts.origin = miPos; opts.location = new window.google.maps.LatLng(miPos.lat, miPos.lng); opts.radius = 50000; }
      svcRef.current.auto.getPlacePredictions(opts, (preds) => {
        const arr = (preds || []).slice();
        if (miPos) arr.sort((x, y) => (x.distance_meters == null ? 1e12 : x.distance_meters) - (y.distance_meters == null ? 1e12 : y.distance_meters));
        setSugerencias(arr);
      });
    }
    // TODO lo de Olbos con ubicación es destino: eventos futuros, talleres y
    // locales verificados, motoposadas y lugares para comer (pedido de Carlos)
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const t = texto.trim().replace(/[%,]/g, "");
        const [ev, neg, lug] = await Promise.all([
          supabase.from("eventos")
            .select("id, titulo, lugar, lat, lng, fecha")
            .not("lat", "is", null)
            .gte("fecha", hoyLocalISO())
            .or(`titulo.ilike.%${t}%,lugar.ilike.%${t}%`)
            .limit(3),
          supabase.from("negocios")
            .select("id, nombre, categoria, ciudad, lat, lng")
            .eq("estado", "verificado")
            .not("lat", "is", null)
            .or(`nombre.ilike.%${t}%,ciudad.ilike.%${t}%,categoria.ilike.%${t}%`)
            .limit(3),
          supabase.from("lugares_comunidad")
            .select("id, nombre, tipo, categoria, ciudad, lat, lng")
            .not("lat", "is", null)
            .or(`nombre.ilike.%${t}%,ciudad.ilike.%${t}%,tipo.ilike.%${t}%`)
            .limit(3),
        ]);
        const olbos = [
          ...(ev.data || []).map(x => ({ id: `ev-${x.id}`, icono: "📅", nombre: x.titulo, sub: `${x.lugar || ""} · ${String(x.fecha).slice(0, 10)}`, lat: x.lat, lng: x.lng })),
          ...(neg.data || []).map(x => ({ id: `ng-${x.id}`, icono: x.categoria === "Taller" ? "🔧" : "🏪", nombre: x.nombre, sub: `${x.categoria || "Negocio"} · ${x.ciudad || ""}`, lat: x.lat, lng: x.lng })),
          ...(lug.data || []).map(x => ({ id: `lg-${x.id}`, icono: x.categoria === "comida" ? "🍴" : "🛏️", nombre: x.nombre, sub: `${x.tipo || ""} · ${x.ciudad || ""}`, lat: x.lat, lng: x.lng })),
        ];
        setSugEventos(olbos);
      } catch (e) { setSugEventos([]); }
    }, 350);
  };

  const limpiarPreviews = () => {
    polysAltRef.current.forEach(p => p.setMap(null));
    polysAltRef.current = [];
    if (alPreview) alPreview(null);
  };

  // ── Alerta de nafta en ruta (v2 Combustible — el "caso Balcarce" de Carlos:
  // Ruta 3 con >200 km sin estaciones y se enteró arriba de la moto). Al arrancar
  // una ruta larga se muestrea el trazado con Places (gas_station) y si hay un
  // tramo largo sin estaciones, se avisa ANTES de agarrar la ruta.
  const [avisoNafta, setAvisoNafta] = useState(null); // {gap, desdeKm, hastaKm}
  const naftaTokenRef = useRef(0);
  const naftaRutaHechaRef = useRef(""); // clave del destino ya analizado (los recálculos no re-consultan)
  const analizarEstacionesRuta = async (path, distKm, claveDestino) => {
    if (naftaRutaHechaRef.current === claveDestino) return;
    naftaRutaHechaRef.current = claveDestino;
    const token = ++naftaTokenRef.current;
    try {
      if (!window.google || !window.google.maps.places || !mapInstanceRef.current) return;
      const svc = new window.google.maps.places.PlacesService(mapInstanceRef.current);
      const paso = Math.max(18, distKm / 24); // km entre muestras (tope ~24 consultas)
      const muestras = [];
      let acum = 0, prox = 0;
      for (let i = 0; i < path.length; i++) {
        if (i > 0) acum += haversineKm(path[i - 1], path[i]);
        if (acum >= prox) { muestras.push({ p: path[i], km: Math.round(acum) }); prox += paso; }
      }
      if (muestras.length < 4) return;
      const resultados = [];
      for (const mu of muestras) {
        if (token !== naftaTokenRef.current) return;
        // eslint-disable-next-line no-await-in-loop
        const hay = await new Promise((res) => {
          try {
            svc.nearbySearch({ location: mu.p, radius: 12000, type: "gas_station" }, (r, st) => {
              if (st === "OK") res(!!(r && r.length));
              else if (st === "ZERO_RESULTS") res(false);
              else res(true); // error/cuota: ante la duda no alarmar
            });
          } catch (e) { res(true); }
        });
        resultados.push({ km: mu.km, hay });
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 140));
      }
      if (token !== naftaTokenRef.current) return;
      // tramo más largo sin estaciones (entre la última muestra CON estación y la siguiente)
      let peor = null, ini = null;
      for (let i = 0; i < resultados.length; i++) {
        if (!resultados[i].hay && ini === null) ini = i;
        if ((resultados[i].hay || i === resultados.length - 1) && ini !== null) {
          const desdeKm = resultados[Math.max(0, ini - 1)].km;
          const hastaKm = resultados[i].km;
          const gap = hastaKm - desdeKm;
          if (!peor || gap > peor.gap) peor = { gap, desdeKm, hastaKm };
          ini = null;
        }
      }
      if (peor && peor.gap >= 100) {
        setAvisoNafta(peor);
        hablarNav(`Atención: en esta ruta hay un tramo de aproximadamente ${Math.round(peor.gap / 10) * 10} kilómetros sin estaciones de servicio. Salí con el tanque lleno.`);
      }
    } catch (e) { /* mejor sin aviso que romper la navegación */ }
  };

  const limpiar = (hablado) => {
    setNav(null); setPasoIdx(0); setEligiendo(null); setParadasPropias([]); setModoParada(false);
    setAvisoNafta(null);
    naftaTokenRef.current++;
    naftaRutaHechaRef.current = "";
    limpiarPreviews();
    if (rutaPolyRef.current) rutaPolyRef.current.setMap(null);
    if (alCambiarRuta) alCambiarRuta(null); // los que te siguen dejan de ver el plan
    if (alActualizarNav) alActualizarNav(null);
    autoNav.terminar(); // Android Auto: la pantalla del auto vuelve a "elegí un destino"
    if (!efimero) { try { localStorage.removeItem("olbos_nav_activa"); } catch (e) { /* */ } }
    if (!hablado) callarTTS();
    if (alTerminar) alTerminar();
  };

  // El SDK de Maps y la instancia del mapa cargan async (await import): si el
  // GPS llegaba antes, calcularRuta hacía `return` MUDO y el destino se perdía
  // para siempre (video de Carlos 02-sep: "Olbos" desde un evento abría el mapa
  // sin marcar el camino en la nativa, donde el SDK carga justo en ese momento;
  // en la web solía zafar porque el SDK ya estaba cargado de una pantalla previa).
  // Los tres arranques de abajo esperan a que el mapa esté de verdad listo.
  // ── MODO TÚNEL ──────────────────────────────────────────────────────────────
  // En la ruta se entra y se sale de zonas sin señal todo el tiempo. Lo único que
  // se cae son las imágenes del mapa: el GPS es satelital y la ruta, los pasos y la
  // voz ya viven en el teléfono desde que se calculó. Entonces, sin señal:
  // la línea se dibuja más gruesa y clara (queda sola sobre el fondo vacío), no se
  // intenta recalcular (fallaría y confundiría) y se avisa que siga la línea.
  const [sinSenal, setSinSenal] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  const avisoTunelRef = useRef(0);
  useEffect(() => {
    const baja = () => setSinSenal(true);
    const sube = () => setSinSenal(false);
    window.addEventListener("offline", baja);
    window.addEventListener("online", sube);
    return () => { window.removeEventListener("offline", baja); window.removeEventListener("online", sube); };
  }, []);
  // La línea se resalta al perder señal y vuelve a su grosor normal al recuperarla
  useEffect(() => {
    if (!rutaPolyRef.current) return;
    rutaPolyRef.current.setOptions(sinSenal
      ? { strokeWeight: 9, strokeOpacity: 1 }
      : { strokeWeight: 6, strokeOpacity: 0.85 });
  }, [sinSenal]);

  const [mapListo, setMapListo] = useState(() => !!(window.google && mapInstanceRef.current));
  useEffect(() => {
    if (mapListo) return;
    const iv = setInterval(() => {
      if (window.google && mapInstanceRef.current) setMapListo(true);
    }, 400);
    return () => clearInterval(iv);
  }, [mapListo]);

  // Modo destino directo ("llevame al track", "navegar con Olbos" desde una ficha):
  // apenas hay GPS, calcula la ruta y arranca — sin buscador ni selector
  const destinoInicialHechoRef = useRef(false);
  // Con paradas (navegar el recorrido de una rodada) el destino puede llegar más tarde y
  // cambiar: se recalcula cada vez que cambia, no solo la primera.
  const destinoInicialClaveRef = useRef("");
  useEffect(() => {
    if (!destinoInicial || !posActual || !mapListo) return;
    const clave = `${destinoInicial.lat},${destinoInicial.lng},${(destinoInicial.paradas || []).length},${destinoInicial.ts || ""}`;
    if (destinoInicialHechoRef.current && destinoInicialClaveRef.current === clave) return;
    destinoInicialHechoRef.current = true;
    destinoInicialClaveRef.current = clave;
    calcularRuta({ lat: destinoInicial.lat, lng: destinoInicial.lng }, destinoInicial.nombre || "tu destino", true, false, false, destinoInicial.paradas || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posActual, mapListo, destinoInicial]);
  // Paradas del recorrido que todavía no pasaste (se descartan al pasar a menos de 250 m):
  // en cada recálculo por desvío la ruta vuelve a pasar por las que faltan.
  const paradasNavRef = useRef([]);
  useEffect(() => {
    if (!posActual || !paradasNavRef.current.length) return;
    paradasNavRef.current = paradasNavRef.current.filter(p => haversineKm(posActual, p) > 0.25);
  }, [posActual]);

  // Restaurar la navegación si el usuario salió del mapa con un viaje en curso
  // (reporte de Carlos: puso destino, arrancó, salió del mapa y al volver el
  // camino ya no estaba). Se restaura una sola vez, cuando llega la señal GPS,
  // recalculando la ruta en modo silencioso (sin selector de alternativas).
  const restauradaRef = useRef(false);
  useEffect(() => {
    if (restauradaRef.current || nav || eligiendo || !posActual || !mapListo || destinoInicial || efimero) return;
    restauradaRef.current = true;
    try {
      const g = JSON.parse(localStorage.getItem("olbos_nav_activa") || "null");
      if (!g || !g.lat || !g.lng) return;
      if (Date.now() - (g.t || 0) > 12 * 60 * 60 * 1000) { localStorage.removeItem("olbos_nav_activa"); return; } // viaje viejo: descartar
      if (haversineKm(posActual, { lat: g.lat, lng: g.lng }) < 0.1) { localStorage.removeItem("olbos_nav_activa"); return; } // ya llegaste
      calcularRuta({ lat: g.lat, lng: g.lng }, g.nombre || "tu destino", true, false, true); // restaurada: NO cuenta como intención
    } catch (e) { /* */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posActual, mapListo]);

  // Búsqueda pre-cargada desde una etapa de viaje ("🧭 Navegar" con destino de
  // texto): abre el buscador del navegador con el nombre ya escrito
  useEffect(() => {
    const revisar = () => {
      try {
        const p = localStorage.getItem("olbos_nav_buscar");
        if (p) { localStorage.removeItem("olbos_nav_buscar"); setBuscando(true); setQ(p); }
      } catch (e) { /* */ }
    };
    revisar();
    window.addEventListener("olbos-abrir-track-recorder", revisar);
    return () => window.removeEventListener("olbos-abrir-track-recorder", revisar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pedido externo ("🏍️ Olbos" desde la ficha de un evento/lugar) con el mapa YA
  // montado: la restauración de arriba corre una sola vez por montaje, así que
  // si el mapa se había abierto antes en la sesión, el botón no hacía nada
  // (reporte de Matías). El evento llega aunque este overlay esté visible hace rato.
  const [pedidoExterno, setPedidoExterno] = useState(null);
  useEffect(() => {
    const h = (e) => { if (e.detail && e.detail.lat && e.detail.lng) setPedidoExterno(e.detail); };
    window.addEventListener("olbos-navegar-a", h);
    return () => window.removeEventListener("olbos-navegar-a", h);
  }, []);
  useEffect(() => {
    if (!pedidoExterno || !posActual || !mapListo) return;
    const p = pedidoExterno;
    setPedidoExterno(null);
    calcularRuta({ lat: Number(p.lat), lng: Number(p.lng) }, p.nombre || "tu destino", true, false, false, p.paradas || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoExterno, posActual, mapListo]);

  // Arranca la navegación con una opción elegida (ruta de Google o camino de riders).
  // esRecalculo: el desvío automático rearma la ruta SIN anuncio de voz ni salto de
  // cámara — antes cada recálculo repetía "Ruta calculada a X, tantos km, unos tantos
  // minutos" y a un rider que iba por un camino paralelo le hablaba cada un minuto
  // (reporte de Mario Llanes, 28-ago: "me había vuelto loco, tuve que parar y cortarlo")
  const arrancarNavegacion = (op, esRecalculo = false, restaurada = false) => {
    limpiarPreviews();
    setEligiendo(null);
    setNav({
      destino: op.destino, destinoNombre: op.destinoNombre,
      pasos: op.pasos || [], path: op.path,
      distTxt: op.distTxt, durTxt: op.durTxt,
      distTotalKm: op.distTotalKm, durTotalMin: op.durTotalMin,
      esRiders: op.tipo === "riders",
    });
    setPasoIdx(0);
    idxPathRef.current = 0;
    anuncioRef.current = { idx: -1, fase: 0 };
    if (!esRecalculo) inicioNavRef.current = { t: Date.now(), km: op.distTotalKm, restaurada };
    // Rutas largas: chequear en segundo plano si hay tramos sin estaciones de
    // servicio. La clave por destino evita re-consultar en cada recálculo.
    if (op.distTotalKm > 80 && op.path && op.path.length > 1 && op.destino) {
      analizarEstacionesRuta(op.path, op.distTotalKm, `${op.destino.lat},${op.destino.lng}`);
    }
    const mapa = mapInstanceRef.current;
    const color = op.tipo === "riders" ? "#8A6D3B" : "#1A73E8";
    if (!rutaPolyRef.current) rutaPolyRef.current = new window.google.maps.Polyline({ path: op.path, strokeColor: color, strokeOpacity: 0.85, strokeWeight: 6, map: mapa, zIndex: 2 });
    else { rutaPolyRef.current.setPath(op.path); rutaPolyRef.current.setOptions({ strokeColor: color }); rutaPolyRef.current.setMap(mapa); }
    if (!esRecalculo) {
      // La voz NO dice cuánto falta (km/minutos quedan en pantalla): solo el destino
      // y la primera indicación — pedido de Carlos tras el reporte de Mario Llanes
      if (op.tipo === "riders") hablarNav(`Arrancamos por el camino de riders a ${op.destinoNombre}. Seguí la línea marrón.`);
      else hablarNav(`Ruta calculada a ${op.destinoNombre}. ${op.pasos[0] ? op.pasos[0].txt : ""}`);
    }
    // El segundo dato dice si esta ruta la acaba de pedir el rider o si se restauró
    // sola de un viaje anterior: solo la primera cuenta como intención de rodar.
    if (alCambiarRuta) alCambiarRuta(op.path, restaurada, op.tipo === "riders"); // los que miran tu link ven también el plan
    // Acercar hasta que se vea bien la calle donde hay que doblar (reporte de Carlos:
    // arrancaba con el mapa lejos y no se entendía el primer giro). En recálculo NO:
    // el mapa pegaba un salto cada vez y encima nunca maduraban los avisos de giro
    if (!esRecalculo && posActual && mapa) { mapa.panTo(posActual); if ((mapa.getZoom() || 0) < 16.5) mapa.setZoom(17); }
    // La navegación sobrevive a salir del mapa: al volver, se restaura sola
    if (!efimero) { try { localStorage.setItem("olbos_nav_activa", JSON.stringify({ lat: op.destino.lat, lng: op.destino.lng, nombre: op.destinoNombre, t: Date.now() })); } catch (e) { /* */ } }
  };

  // Dibujar las alternativas en el mapa (la elegida resaltada) y encuadrarlas
  const dibujarPreviews = (ops, sel) => {
    limpiarPreviews();
    const mapa = mapInstanceRef.current;
    const bounds = new window.google.maps.LatLngBounds();
    ops.forEach((op, i) => {
      const esSel = i === sel;
      const pl = new window.google.maps.Polyline({
        path: op.path, map: mapa,
        strokeColor: op.tipo === "riders" ? "#8A6D3B" : (esSel ? "#1A73E8" : "#9AA0A6"),
        strokeOpacity: esSel ? 0.9 : 0.55,
        strokeWeight: esSel ? 6 : 4,
        zIndex: esSel ? 3 : 1,
      });
      pl.addListener("click", () => setEligiendo(e => { if (e) dibujarPreviews(e.ops, i); return e ? { ...e, sel: i } : e; }));
      polysAltRef.current.push(pl);
      op.path.forEach(p => bounds.extend(p));
    });
    if (!bounds.isEmpty()) mapa.fitBounds(bounds, 60);
    // Navegador v2: las polilíneas de arriba van al mapa de Google, que está tapado por el
    // lienzo MapLibre; el camino elegido se le pasa al lienzo para que se vea (Matías, 10-oct:
    // "no se marca el recorrido cuando buscás un destino").
    if (alPreview && ops[sel] && ops[sel].path) alPreview(ops[sel].path, ops[sel].tipo === "riders");
  };

  const calcularRuta = (destino, nombre, silencioso = false, esRecalculo = false, restaurada = false, paradas = null) => {
    if (!posActual) { alert("Esperando señal GPS…"); return; }
    if (!window.google || !mapInstanceRef.current) return;
    if (!svcRef.current.dir) svcRef.current.dir = new window.google.maps.DirectionsService();
    // Paradas: las que vienen con el pedido (rodada) o, en un recálculo, las que faltan.
    // Un destino nuevo sin paradas las borra.
    if (Array.isArray(paradas)) paradasNavRef.current = paradas.filter(p => p && p.lat != null && p.lng != null).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
    else if (!esRecalculo) { paradasNavRef.current = []; setParadasPropias([]); }
    if (paradasNavRef.current.length > 1) {
      let bi = 0, bd = Infinity;
      paradasNavRef.current.forEach((p, i) => { const d = haversineKm(posActual, p); if (d < bd) { bd = d; bi = i; } });
      // la más cercana cuenta como "la próxima" solo si está más cerca que la que le sigue
      const sig = paradasNavRef.current[bi + 1];
      const desdeIdx = sig && haversineKm(posActual, sig) < haversineKm(paradasNavRef.current[bi], sig) ? bi + 1 : bi;
      if (desdeIdx > 0) paradasNavRef.current = paradasNavRef.current.slice(desdeIdx);
    }
    const porDonde = paradasNavRef.current.filter(p => haversineKm(posActual, p) > 0.25).slice(0, 23);
    const pedido = { origin: posActual, destination: destino, travelMode: window.google.maps.TravelMode.DRIVING, provideRouteAlternatives: !silencioso && porDonde.length === 0, drivingOptions: conTrafico() };
    // stopover:false = puntos de paso: Google devuelve una sola etapa con todos los giros
    if (porDonde.length) pedido.waypoints = porDonde.map(p => ({ location: { lat: p.lat, lng: p.lng }, stopover: false }));
    traficoRef.current.ultimo = Date.now(); // ruta recién pedida: ya viene con el tráfico de ahora
    svcRef.current.dir.route(pedido, async (res, status) => {
      if (status !== "OK" || !res || !res.routes || !res.routes[0]) { if (!silencioso) alert("No se pudo calcular la ruta hasta ahí."); return; }
      const armarOp = (ruta) => armarOpGoogle(ruta, destino, nombre);
      if (silencioso) { arrancarNavegacion(armarOp(res.routes[0]), esRecalculo, restaurada); return; } // directo, sin selector
      const ops = res.routes.slice(0, 3).map(armarOp);
      await sumarCaminosRiders(ops, destino, nombre);
      setBuscando(false); setQ(""); setSugerencias([]); setSugEventos([]);
      if (ops.length === 1) { arrancarNavegacion(ops[0]); return; } // una sola: arrancar directo
      setEligiendo({ ops, sel: 0 });
      dibujarPreviews(ops, 0);
    });
  };

  // Tráfico (28-oct, pedido de Carlos: "en Buenos Aires hace mucha diferencia"): la ruta se
  // pide "saliendo ahora", así Google elige el camino según cómo están las calles y el
  // tiempo viene con el tránsito (duration_in_traffic). Cuesta más por pedido (tarifa
  // Advanced de Google), pero se pide al arrancar, al recalcular y cada 5 min, no más.
  const conTrafico = () => ({ departureTime: new Date(), trafficModel: window.google.maps.TrafficModel.BEST_GUESS });

  const armarOpGoogle = (ruta, destino, nombre) => {
        const leg = ruta.legs[0];
        const dur = leg.duration_in_traffic || leg.duration; // con tráfico si vino
        return {
          tipo: "google", destino, destinoNombre: nombre,
          resumen: ruta.summary || "",
          pasos: leg.steps.map(s => ({ txt: quitarHtml(s.instructions), calle: calleDePaso(s.instructions), manio: s.maneuver || "", fin: { lat: s.end_location.lat(), lng: s.end_location.lng() }, distTxt: (s.distance && s.distance.text) || "" })),
          // El camino REAL, armado con la geometría de cada paso. overview_path viene
          // simplificado —sirve para ver la ruta entera de un vistazo, no para andarla—
          // y con el zoom de navegación cortaba en diagonal por arriba de las manzanas
          // (reporte de Matías).
          // En rutas muy largas se recorta quedándose con uno de cada N puntos. NO se usa
          // downsampleRecorrido acá: simplifica con Douglas-Peucker recursivo, que con 250
          // puntos vuela y con decenas de miles se come la pila y cuelga la pestaña — el
          // mapa se quedaba cargando para siempre (lo rompí así el 11-sep). Para dibujar
          // no hace falta esa precisión: con el tope de 20.000 queda un punto cada pocas
          // decenas de metros incluso en un viaje de 1.400 km.
          path: (() => {
            const fino = [];
            (ruta.legs || []).forEach(l => (l.steps || []).forEach(st => {
              (st.path || []).forEach(p => fino.push({ lat: p.lat(), lng: p.lng() }));
            }));
            if (fino.length < 2) return ruta.overview_path.map(p => ({ lat: p.lat(), lng: p.lng() }));
            if (fino.length <= 20000) return fino;
            const paso = Math.ceil(fino.length / 20000);
            const recortado = fino.filter((_, i) => i % paso === 0);
            if (recortado[recortado.length - 1] !== fino[fino.length - 1]) recortado.push(fino[fino.length - 1]);
            return recortado;
          })(),
          distTxt: leg.distance && leg.distance.text, durTxt: dur && dur.text,
          distTotalKm: leg.distance ? leg.distance.value / 1000 : 0,
          durTotalMin: dur ? dur.value / 60 : 0,
        };
  };

  // Caminos de la COMUNIDAD que sirven para este viaje (si el RPC existe): se suman a ops
  const sumarCaminosRiders = async (ops, destino, nombre) => {
      try {
        const { data: raw } = await supabase.rpc("tracks_para_viaje", { p_o_lng: posActual.lng, p_o_lat: posActual.lat, p_d_lng: destino.lng, p_d_lat: destino.lat });
        const tracks = typeof raw === "string" ? JSON.parse(raw) : raw;
        // El RPC trae los caminos que pasan cerca del origen Y del destino, pero un
        // camino Junín → Buenos Aires "pasa cerca" de Chacabuco y seguía entero,
        // hasta Buenos Aires (reporte de Matías, 18-sep). Acá se recorta al tramo
        // entre tu punto más cercano y el más cercano al destino, en el sentido en
        // que lo vas a andar, y si el camino no llega a menos de 3 km del destino
        // (o no arranca a menos de 8 km de vos) no es un camino para este viaje.
        const masCercano = (p, arr) => { let bi = 0, bd = Infinity; arr.forEach((q, i) => { const d = haversineKm(p, q); if (d < bd) { bd = d; bi = i; } }); return { i: bi, d: bd }; };
        (Array.isArray(tracks) ? tracks : []).forEach(tk => {
          const entero = decodificarPolyline(tk.polyline);
          if (entero.length < 2) return;
          const o = masCercano(posActual, entero), dd = masCercano(destino, entero);
          if (dd.d > 3 || o.d > 8) return;
          const path = o.i <= dd.i ? entero.slice(o.i, dd.i + 1) : entero.slice(dd.i, o.i + 1).reverse();
          if (path.length < 2) return;
          let km = 0;
          for (let i = 1; i < path.length; i++) km += haversineKm(path[i - 1], path[i]);
          if (km < 1) return;
          const vel = Number(tk.vel_prom) || 45;
          ops.push({
            tipo: "riders", destino, destinoNombre: nombre,
            resumen: `Camino de riders${tk.tipo_camino ? ` · ${tk.tipo_camino}` : ""}${tk.estado_camino ? ` (${tk.estado_camino})` : ""}`,
            riders: tk.riders || 0,
            pasos: [], path,
            distTxt: `${km.toFixed(km < 10 ? 1 : 0)} km`, durTxt: `${Math.round(km / Math.max(20, vel) * 60)} min`,
            distTotalKm: km, durTotalMin: km / Math.max(20, vel) * 60,
          });
        });
      } catch (e) { /* sin RPC o sin tracks: solo Google */ }
  };

  // ¿La ruta `path` va por lo que falta de `actual` (desde el índice `desde`)? Casi todos sus
  // puntos a menos de 50 m de algún TRAMO (en autopista Google deja un punto por km).
  const sigueRuta = (path, actual, desde) => {
    if (!path || path.length < 2 || !actual || actual.length < 2) return false;
    const kx = 111320 * Math.cos(path[0].lat * Math.PI / 180), ky = 110540;
    const pasoO = Math.max(1, Math.floor(path.length / 120));
    let cerca = 0, total = 0;
    for (let i = 0; i < path.length; i += pasoO) {
      total++;
      const o = path[i];
      for (let j = Math.max(0, desde); j < actual.length - 1; j++) {
        const ax = (actual[j].lng - o.lng) * kx, ay = (actual[j].lat - o.lat) * ky;
        const bx = (actual[j + 1].lng - o.lng) * kx, by = (actual[j + 1].lat - o.lat) * ky;
        const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
        const t = L2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L2)) : 0;
        const ex = ax + t * dx, ey = ay + t * dy;
        if (ex * ex + ey * ey < 2500) { cerca++; break; }
      }
    }
    return total > 0 && cerca >= total * 0.85;
  };

  // Cada 5 min de viaje: rutas con el tráfico de ahora. Si la más rápida no es la que venimos
  // haciendo y ahorra 5 min o más (o el 15% de lo que falta), se toma y se avisa, como Waze.
  // Con paradas no (Google no da alternativas con puntos de paso) ni por caminos de riders.
  const chequearTrafico = (tRestMin) => {
    const n0 = navActualRef.current;
    if (!n0 || n0.esRiders || !posActual || !svcRef.current.dir || paradasNavRef.current.length) return;
    traficoRef.current.ultimo = Date.now();
    traficoRef.current.pidiendo = true;
    const desde = idxPathRef.current;
    svcRef.current.dir.route({ origin: posActual, destination: n0.destino, travelMode: window.google.maps.TravelMode.DRIVING, provideRouteAlternatives: true, drivingOptions: conTrafico() }, (res, status) => {
      traficoRef.current.pidiendo = false;
      if (navActualRef.current !== n0 || status !== "OK" || !res || !res.routes || !res.routes.length) return;
      const ops = res.routes.slice(0, 3).map(r => armarOpGoogle(r, n0.destino, n0.destinoNombre));
      // La alternativa que va por donde venimos dice cuánto tarda hoy nuestro camino
      const nuestra = ops.find(o => sigueRuta(o.path, n0.path, desde));
      const tActual = nuestra ? nuestra.durTotalMin : tRestMin;
      const mejor = ops.reduce((m, o) => (!m || o.durTotalMin < m.durTotalMin ? o : m), null);
      if (!mejor || mejor === nuestra || sigueRuta(mejor.path, n0.path, desde)) return;
      const ahorro = tActual - mejor.durTotalMin;
      if (!(ahorro >= Math.max(5, tActual * 0.15))) return;
      registrarUso("nav_trafico_desvio", { ahorro_min: Math.round(ahorro) });
      arrancarNavegacion(mejor, true);
      hablarNav(`Hay tránsito más adelante. Encontré un camino ${Math.round(ahorro)} minutos más rápido.`);
    });
  };

  // Pines de las paradas que sumó el rider: en el mapa se distinguen del recorrido
  // (pedido de @MatiAguilar_26). Numeradas, negro sobre crema como el resto de la marca.
  const marcadoresParadasRef = useRef([]);
  useEffect(() => {
    marcadoresParadasRef.current.forEach(m => { try { m.setMap(null); } catch (e) { /* */ } });
    marcadoresParadasRef.current = [];
    const mapa = mapInstanceRef.current;
    if (!nav || !mapa || !window.google || !paradasPropias.length) return;
    marcadoresParadasRef.current = paradasPropias.map((p, i) => new window.google.maps.Marker({
      position: { lat: p.lat, lng: p.lng }, map: mapa, zIndex: 50, title: p.nombre,
      label: { text: String(i + 1), color: "#F9F5F2", fontWeight: "700", fontSize: "13px" },
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 15, fillColor: "#111111", fillOpacity: 1, strokeColor: "#F9F5F2", strokeWeight: 3 },
    }));
    return () => { marcadoresParadasRef.current.forEach(m => { try { m.setMap(null); } catch (e) { /* */ } }); marcadoresParadasRef.current = []; };
  }, [paradasPropias, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  // Navegador v2: el lienzo MapLibre tapa los marcadores de Google, así que las paradas
  // sumadas también viajan al padre para dibujarlas ahí (reporte de @MatiAguilar_26, 04-nov)
  useEffect(() => { if (alActualizarParadasNav) alActualizarParadasNav(nav && paradasPropias.length ? paradasPropias : null); }, [paradasPropias, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suma una parada a la navegación en curso: va en el lugar del recorrido donde menos
  // desvía, y la ruta se recalcula en silencio pasando por ella
  const agregarParada = (coord, nombre) => {
    const n = navActualRef.current;
    setModoParada(false); setBuscando(false); setQ(""); setSugerencias([]); setSugEventos([]);
    if (!n || !posActual) { calcularRuta(coord, nombre); return; }
    const p = { lat: Number(coord.lat), lng: Number(coord.lng) };
    const seq = [{ lat: posActual.lat, lng: posActual.lng }, ...paradasNavRef.current, n.destino];
    let mejor = 0, costo = Infinity;
    for (let i = 0; i < seq.length - 1; i++) {
      const c = haversineKm(seq[i], p) + haversineKm(p, seq[i + 1]) - haversineKm(seq[i], seq[i + 1]);
      if (c < costo) { costo = c; mejor = i; }
    }
    if (paradasNavRef.current.length >= 22) { alert("Ya hay muchas paradas en esta ruta (máximo 22)."); return; }
    paradasNavRef.current = [...paradasNavRef.current.slice(0, mejor), p, ...paradasNavRef.current.slice(mejor)];
    setParadasPropias(l => [...l, { ...p, nombre: nombre || "Parada" }]);
    registrarUso("nav_parada_agregada", { n: paradasNavRef.current.length });
    hablarNav(`Parada agregada: ${nombre || "parada"}.`);
    calcularRuta(n.destino, n.destinoNombre, true, true);
  };
  const quitarUltimaParada = () => {
    const ult = paradasPropias[paradasPropias.length - 1];
    const n = navActualRef.current;
    if (!ult || !n) return;
    paradasNavRef.current = paradasNavRef.current.filter(q2 => !(Math.abs(q2.lat - ult.lat) < 1e-9 && Math.abs(q2.lng - ult.lng) < 1e-9));
    setParadasPropias(l => l.slice(0, -1));
    calcularRuta(n.destino, n.destinoNombre, true, true);
  };
  const irA =(placeId, descripcion) => {
    if (!window.google || !mapInstanceRef.current) return;
    if (!svcRef.current.places) svcRef.current.places = new window.google.maps.places.PlacesService(mapInstanceRef.current);
    svcRef.current.places.getDetails({ placeId, fields: ["geometry", "name"] }, (det, status) => {
      if (status !== "OK" || !det || !det.geometry || !det.geometry.location) { alert("No pudimos ubicar ese destino."); return; }
      const c = { lat: det.geometry.location.lat(), lng: det.geometry.location.lng() };
      if (modoParada) agregarParada(c, det.name || descripcion);
      else calcularRuta(c, det.name || descripcion);
    });
  };

  // El seguimiento: avance de pasos, anuncios por voz, llegada y recálculo.
  // Los caminos de riders no tienen pasos: se guía por la línea, con llegada y desvío igual.
  useEffect(() => {
    if (!nav || !posActual) return;
    const paso = nav.pasos[pasoIdx] || null;
    const dAlGiro = paso ? haversineKm(posActual, paso.fin) * 1000 : Infinity;
    const dDestino = haversineKm(posActual, nav.destino) * 1000;
    if (dDestino < 40 || (paso && pasoIdx >= nav.pasos.length - 1 && dAlGiro < 25)) {
      hablarNav(`Llegaste a ${nav.destinoNombre}. El camino es el destino.`);
      // Ficha de cierre para el grabador: km y minutos del viaje (los reales si la ruta se
      // pidió en esta sesión; los de la ruta si se restauró de una sesión anterior)
      if (alLlegar) {
        const ini = inicioNavRef.current;
        const minReal = ini && !ini.restaurada ? Math.round((Date.now() - ini.t) / 60000) : null;
        try { alLlegar({ nombre: nav.destinoNombre, km: ini && ini.km ? ini.km : nav.distTotalKm, min: minReal != null && minReal >= 1 ? minReal : Math.max(1, Math.round(nav.durTotalMin || 0)) }); } catch (e) { /* */ }
      }
      limpiar(true);
      return;
    }
    if (paso && dAlGiro < 22) {
      const sig = pasoIdx + 1;
      setPasoIdx(sig);
      anuncioRef.current = { idx: sig, fase: 0 };
      if (nav.pasos[sig]) hablarNav(nav.pasos[sig].txt);
      return;
    }
    if (paso) {
      if (anuncioRef.current.idx !== pasoIdx) anuncioRef.current = { idx: pasoIdx, fase: 0 };
      if (dAlGiro < 220 && anuncioRef.current.fase === 0) {
        anuncioRef.current.fase = 1;
        // El giro que viene es el del paso SIGUIENTE (paso.fin es donde arranca):
        // antes se repetía la indicación del paso actual, que ya estaba hecha
        // ("En 200 metros, dirígete al norte por Reynoso" yendo por Reynoso)
        const prox = nav.pasos[pasoIdx + 1];
        hablarNav(`En ${Math.max(50, Math.round(dAlGiro / 10) * 10)} metros, ${prox ? prox.txt : `llegás a ${nav.destinoNombre}`}`);
      }
    }

    // La línea azul se CONSUME al pasar: buscar el punto más cercano de la ruta
    // (desde donde íbamos, ventana hacia adelante) y recortar lo ya recorrido
    let mejorIdx = idxPathRef.current, mejorD = Infinity;
    for (let i = idxPathRef.current; i < Math.min(nav.path.length, idxPathRef.current + 60); i++) {
      const d = haversineKm(posActual, nav.path[i]);
      if (d < mejorD) { mejorD = d; mejorIdx = i; }
    }
    if (mejorIdx > idxPathRef.current && mejorD * 1000 < 120) {
      idxPathRef.current = mejorIdx;
      if (rutaPolyRef.current) rutaPolyRef.current.setPath(nav.path.slice(mejorIdx));
    }
    // Distancia restante por la ruta + ETA: arranca con la estimación de Google y
    // se corrige con TU velocidad promedio en movimiento a medida que hay datos
    let distRest = haversineKm(posActual, nav.path[idxPathRef.current] || nav.destino);
    for (let i = idxPathRef.current; i < nav.path.length - 1; i++) distRest += haversineKm(nav.path[i], nav.path[i + 1]);
    const tGoogleRest = nav.distTotalKm > 0 ? nav.durTotalMin * (distRest / nav.distTotalKm) : 0;
    const propio = velMovil ? velMovil() : null;
    let tRest = tGoogleRest;
    if (propio && propio.kmh > 3) {
      const w = Math.min(1, propio.datosMin / 10); // a los 10 min de datos, manda tu ritmo
      tRest = w * (distRest / propio.kmh * 60) + (1 - w) * tGoogleRest;
    }
    const etaDate = new Date(Date.now() + tRest * 60000);
    // Tráfico: con más de 3 km por delante, sobre la ruta y con señal
    if (!nav.esRiders && !sinSenal && !traficoRef.current.pidiendo && distRest > 3 && mejorD * 1000 < 60
        && Date.now() - traficoRef.current.ultimo > 5 * 60000) chequearTrafico(tGoogleRest);
    // Los datos del viaje van al grabador (barra inferior) y al que te mira por el link compartido
    if (alActualizarNav) alActualizarNav({ destino: nav.destinoNombre, rest_km: Math.round(distRest * 10) / 10, min: Math.round(tRest), eta: `${etaDate.getHours()}:${String(etaDate.getMinutes()).padStart(2, "0")}`, giro_m: paso ? Math.round(dAlGiro) : null, calle_actual: paso ? (paso.calle || null) : null });
    // Android Auto: lo mismo que el banner (la maniobra QUE VIENE, su distancia y su calle) + ETA
    {
      const prox = nav.pasos[pasoIdx + 1] || null;
      autoNav.actualizar({
        path: nav.path, riders: nav.esRiders, pos: posActual,
        tipo: nav.esRiders ? "riders" : prox ? (prox.manio || "straight") : "destino",
        distM: nav.esRiders ? distRest * 1000 : (paso && isFinite(dAlGiro) ? dAlGiro : dDestino),
        calle: nav.esRiders ? "" : prox ? (prox.calle || prox.txt || "") : nav.destinoNombre,
        etaMs: etaDate.getTime(), restM: distRest * 1000, destino: nav.destinoNombre,
        destinoPos: nav.destino && isFinite(nav.destino.lat) ? { lat: nav.destino.lat, lng: nav.destino.lng } : null,
      });
    }

    // Desvío: lejos del trazado en 2 lecturas seguidas → recalcular (con respiro de 10s).
    // Antes eran 3 lecturas a >80 m con respiro de 15 s y el recálculo se sentía
    // lento al salirse de la ruta (reporte de Carlos en la calle).
    const dMin = mejorD * 1000;
    // OJO: un camino de RIDERS es un trazado fijo — recalcular lo pisaría con la
    // primera ruta de Google (bug que encontró Carlos: elegía el camino de riders
    // y a los segundos el detector de desvío se lo cambiaba por el otro). Para
    // riders: umbral más tolerante, solo aviso por voz, y la línea queda.
    if (dMin > (nav.esRiders ? 150 : 50)) {
      recalcRef.current.fuera++;
      if (recalcRef.current.fuera >= 2 && Date.now() - recalcRef.current.ultimo > (nav.esRiders ? 60000 : 10000)) {
        recalcRef.current = { fuera: 0, ultimo: Date.now(), avisoTs: recalcRef.current.avisoTs || 0 };
        if (sinSenal) {
          // Modo túnel: recalcular sin señal falla y deja al rider sin nada. La ruta
          // ya está en el teléfono, así que se le pide volver a la línea y listo.
          if (Date.now() - avisoTunelRef.current > 60000) {
            hablarNav("Sin señal. Seguí la línea de la ruta.");
            avisoTunelRef.current = Date.now();
          }
        } else if (nav.esRiders) {
          hablarNav("Te estás alejando del camino de riders. Buscá la línea marrón.");
        } else {
          // La voz avisa el PRIMER recálculo; si seguís fuera de la ruta (vas por un
          // camino paralelo a propósito), recalcula en silencio y recién vuelve a
          // avisar a los 3 minutos — antes te lo cantaba cada vez (caso Mario Llanes)
          if (Date.now() - (recalcRef.current.avisoTs || 0) > 180000) {
            hablarNav("Recalculando ruta");
            recalcRef.current.avisoTs = Date.now();
          }
          calcularRuta(nav.destino, nav.destinoNombre, true, true);
        }
      }
    } else recalcRef.current.fuera = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posActual]);

  useEffect(() => () => { if (rutaPolyRef.current) rutaPolyRef.current.setMap(null); callarTTS(); autoNav.terminar(); }, []);
  // Android Auto: "Terminar" en la pantalla del auto corta la navegación acá también
  const limpiarAutoRef = useRef(null);
  limpiarAutoRef.current = limpiar;
  useEffect(() => {
    const h = autoNav.alTerminarDesdeAuto(() => { if (limpiarAutoRef.current) limpiarAutoRef.current(false); });
    return () => { if (h) Promise.resolve(h).then((x) => { if (x && x.remove) x.remove(); }, () => {}); };
  }, []);
  // Destino dictado por voz ("llevame a Chascomús"): la primera predicción de
  // Places cerca de donde estás, ruta directa sin selector, y se avisa qué se
  // encontró (nombre, km, minutos) para contestarle al rider con voz.
  const calcularRutaRef = useRef(null);
  calcularRutaRef.current = calcularRuta;
  const vozDestinoPendienteRef = useRef(null);
  useEffect(() => {
    const h = (e) => {
      const texto = (e.detail && e.detail.texto) || "";
      const listo = (d) => window.dispatchEvent(new CustomEvent("olbos-voz-destino-listo", { detail: d }));
      if (!texto || !window.google || !mapInstanceRef.current) { listo({ ok: false }); return; }
      if (!svcRef.current.auto) svcRef.current.auto = new window.google.maps.places.AutocompleteService();
      const sesgo = posActual ? { location: new window.google.maps.LatLng(posActual.lat, posActual.lng), radius: 300000 } : {};
      svcRef.current.auto.getPlacePredictions({ input: texto, language: "es", ...sesgo }, (preds, status) => {
        const p = status === "OK" && preds && preds[0];
        if (!p) { listo({ ok: false }); return; }
        if (!svcRef.current.places) svcRef.current.places = new window.google.maps.places.PlacesService(mapInstanceRef.current);
        svcRef.current.places.getDetails({ placeId: p.place_id, fields: ["geometry", "name"] }, (det, st) => {
          if (st !== "OK" || !det || !det.geometry || !det.geometry.location) { listo({ ok: false }); return; }
          const nombre = det.name || p.description;
          vozDestinoPendienteRef.current = { nombre, ts: Date.now() };
          calcularRutaRef.current({ lat: det.geometry.location.lat(), lng: det.geometry.location.lng() }, nombre, true);
        });
      });
    };
    window.addEventListener("olbos-voz-destino", h);
    return () => window.removeEventListener("olbos-voz-destino", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posActual]);
  useEffect(() => {
    const p = vozDestinoPendienteRef.current;
    if (!p || !nav) return;
    vozDestinoPendienteRef.current = null;
    window.dispatchEvent(new CustomEvent("olbos-voz-destino-listo", { detail: { ok: true, nombre: p.nombre, km: nav.distTotalKm, min: nav.durTotalMin } }));
  }, [nav]);

  const dGiro = nav && posActual && nav.pasos[pasoIdx] ? Math.round(haversineKm(posActual, nav.pasos[pasoIdx].fin) * 1000) : null;
  // El banner muestra la maniobra QUE VIENE: la del paso siguiente, que ocurre al
  // final del paso actual (ahí apunta dGiro). Pasado el último paso, el destino.
  const pasoActual = nav ? (nav.pasos[pasoIdx] || null) : null;
  const pasoProx = nav ? (nav.pasos[pasoIdx + 1] || null) : null;
  const pasoLuego = nav ? (nav.pasos[pasoIdx + 2] || null) : null;
  // "luego ↱ X": a menos de 300 m del giro, si el siguiente viene a menos de 500 m
  const mLuego = pasoActual && pasoProx ? haversineKm(pasoActual.fin, pasoProx.fin) * 1000 : Infinity;
  const mostrarLuego = !!(pasoLuego && dGiro != null && dGiro < 300 && mLuego < 500);
  const fmtM = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${Math.max(10, Math.round(m / 10) * 10)} m`);
  const bannerTop = topBanner || "calc(var(--sat, 0px) + 60px)";
  // Lo que salió del banner (guardar destino, voz, cancelar) vive en el menú ⋯ del grabador
  if (menuRef) {
    menuRef.current = nav ? {
      destinoGuardado: !!destinoGuardado,
      guardar: () => {
        if (destinoGuardado) return;
        const nom = window.prompt("Guardar este destino como:", nav.destinoNombre);
        if (nom) persistirGuardados([...guardados, { nombre: nom.trim() || nav.destinoNombre, lat: nav.destino.lat, lng: nav.destino.lng }]);
      },
      voz,
      alternarVoz: () => {
        const nueva = !voz;
        setVoz(nueva);
        try { localStorage.setItem("olbos_nav_voz", nueva ? "1" : "0"); } catch (e) { /* */ }
        if (!nueva) { callarTTS(); }
      },
      cancelar: () => limpiar(false),
      agregarParada: () => { setModoParada(true); setBuscando(true); },
      paradasPropias,
      quitarParada: quitarUltimaParada,
    } : null;
  }

  return (
    <>
      {/* Sin ruta: buscador chico abajo a la izquierda. Con ruta: banner de giros ARRIBA, estilo Waze */}
      {!nav ? (ocultarBuscador ? null :
        <button onClick={() => setBuscando(true)}
          style={{ position: "absolute", bottom: 22, left: 84, maxWidth: "calc(100% - 150px)", zIndex: 6, padding: "10px 14px", borderRadius: 100, background: "rgba(13,13,18,.9)", color: "#fff", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, textAlign: "left", display: "flex", alignItems: "center", gap: 7, backdropFilter: "blur(5px)", whiteSpace: "nowrap" }}>
          🧭 ¿A dónde vas?
        </button>
      ) : null}
      {/* Sin ruta activa y sin señal: avisar que el destino hay que cargarlo con señal */}
      {!nav && sinSenal && !ocultarBuscador && (
        <div style={{ position: "absolute", bottom: 122, left: 12, maxWidth: "70%", zIndex: 6, padding: "8px 12px", borderRadius: 12, background: "rgba(13,13,18,.9)", color: "#F5C518", fontSize: 11.5, fontWeight: 700, backdropFilter: "blur(5px)", lineHeight: 1.4 }}>
          📴 Sin señal. Podés grabar el recorrido igual; para poner un destino nuevo necesitás datos.
        </div>
      )}
      {/* Banner de giro, 84 px, como Waze: a la izquierda la flecha y la distancia
          (lo que se lee a 70 km/h con guantes), a la derecha la calle a la que se
          dobla y, si el giro siguiente viene pegado, "luego ↱ X". Nada más: km, ETA
          y hora van en la barra de abajo; guardar, voz y cancelar en el menú ⋯. */}
      {nav && (
        <div style={{ position: "absolute", top: bannerTop, left: 12, right: 66, zIndex: 9, height: 84, boxSizing: "border-box", background: "rgba(13,13,18,.94)", borderRadius: 16, paddingRight: 14, color: "#fff", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", boxShadow: "0 6px 24px rgba(0,0,0,.25)" }}>
          <div style={{ flexShrink: 0, width: 96, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
            <div style={{ fontSize: 40, lineHeight: 1, fontWeight: 900 }}>{nav.esRiders ? "🏍️" : pasoProx ? (GLIFO_MANIOBRA[pasoProx.manio] || "↑") : "⚑"}</div>
            {dGiro != null && isFinite(dGiro) && !nav.esRiders && (
              <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1, marginTop: 4, letterSpacing: "-0.03em", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtM(dGiro)}</div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {(() => {
              // Un paso sin calle en negrita ("Continúe recto", "Tome la salida") dejaba el
              // banner con la distancia y nada más: va la instrucción entera
              const calle = nav.esRiders ? "Seguí la línea marrón" : pasoProx ? (pasoProx.calle || pasoProx.txt || "") : nav.destinoNombre;
              return calle ? (
                <div style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: "-0.01em" }}>{calle}</div>
              ) : null;
            })()}
            {mostrarLuego && (
              <div style={{ fontSize: 13, fontWeight: 700, color: "#bdb7b0", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                luego {GLIFO_MANIOBRA[pasoLuego.manio] || "↑"} {pasoLuego.calle || ""}
              </div>
            )}
            {/* Modo túnel: el mapa se queda sin imágenes pero la navegación sigue */}
            {sinSenal && (
              <div style={{ fontSize: 12, fontWeight: 800, color: "#F5C518", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📴 Sin señal · la ruta sigue guardada</div>
            )}
          </div>
        </div>
      )}

      {/* alerta de nafta: tramo largo sin estaciones en la ruta elegida */}
      {nav && avisoNafta && (
        <div style={{ position: "absolute", top: `calc(${bannerTop} + 94px)`, left: 12, right: 66, zIndex: 8, background: "rgba(13,13,18,.93)", borderRadius: 12, padding: "10px 13px", display: "flex", gap: 10, alignItems: "center", color: "#fff", backdropFilter: "blur(6px)" }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⛽</span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45 }}>
            <b>~{avisoNafta.gap} km sin estaciones de servicio</b> (del km {avisoNafta.desdeKm} al {avisoNafta.hastaKm} de tu ruta). Salí con el tanque lleno{avisoNafta.gap > 180 ? " y evaluá llevar bidón" : ""}.
          </div>
          <button onClick={() => setAvisoNafta(null)}
            style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11 }}>✕</button>
        </div>
      )}

      {/* selector de ALTERNATIVAS de ruta (Google + caminos de riders) */}
      {eligiendo && (
        <div style={{ position: "absolute", bottom: 12, left: 12, right: 12, zIndex: 9, background: C.card, borderRadius: 16, padding: "12px 14px", boxShadow: "0 6px 30px rgba(0,0,0,.4)" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 8 }}>Elegí el camino</div>
          {eligiendo.ops.map((op, i) => (
            <button key={i} onClick={() => { setEligiendo({ ...eligiendo, sel: i }); dibujarPreviews(eligiendo.ops, i); }}
              style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", padding: "9px 10px", marginBottom: 6, borderRadius: 11, background: eligiendo.sel === i ? "#11111108" : C.card, border: `1.5px solid ${eligiendo.sel === i ? "#111111" : C.border}`, cursor: "pointer" }}>
              <span style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, background: op.tipo === "riders" ? "#8A6D3B" : "#1A73E8", opacity: eligiendo.sel === i ? 1 : 0.4 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {op.tipo === "riders" ? `🏍️ ${op.resumen}` : (op.resumen ? `Por ${op.resumen}` : "Ruta")}
                </span>
                <span style={{ display: "block", fontSize: 11.5, color: C.muted }}>
                  {op.distTxt} · {op.durTxt}{op.tipo === "riders" && op.riders > 0 ? ` · ${op.riders} rider${op.riders !== 1 ? "s" : ""} lo hicieron` : ""}
                </span>
              </span>
            </button>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={() => { limpiarPreviews(); setEligiendo(null); }}
              style={{ flex: 1, padding: "11px", borderRadius: 11, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Cancelar
            </button>
            <button onClick={() => arrancarNavegacion(eligiendo.ops[eligiendo.sel])}
              style={{ flex: 2, padding: "11px", borderRadius: 11, background: C.accent, border: "none", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
              🧭 Arrancar
            </button>
          </div>
        </div>
      )}

      {/* sheet de búsqueda de destino */}
      {buscando && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 420, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && (setBuscando(false), setModoParada(false))}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "18px 16px calc(24px + var(--sab))" }} className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{modoParada ? "📍 ¿Dónde querés parar?" : "¿A dónde vas?"}</div>
              <button onClick={() => { setBuscando(false); setModoParada(false); }} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} color={C.muted} />
              </button>
            </div>
            <input autoFocus value={q} onChange={e => buscar(e.target.value)} placeholder="Ciudad, dirección, lugar o evento…"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.surface, boxSizing: "border-box", marginBottom: 8 }} />
            {/* Sin ningún lugar guardado la función era invisible: nadie sabía
                que existía ni cómo guardar el primero — reporte de Carlos */}
            {guardados.length === 0 && q.trim().length < 3 && (
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8, padding: "8px 10px", borderRadius: 10, background: C.surface }}>
                ⭐ Mientras vas hacia un destino, tocá la estrella del cartel de arriba y el lugar queda guardado acá para ir de un toque la próxima.
              </div>
            )}
            {/* Lugares guardados: un toque y vas */}
            {guardados.length > 0 && q.trim().length < 3 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {guardados.map((g, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 10px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, color: C.text }}>
                    <button onClick={() => modoParada ? agregarParada({ lat: g.lat, lng: g.lng }, g.nombre) : calcularRuta({ lat: g.lat, lng: g.lng }, g.nombre)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: C.text, padding: 0 }}>⭐ {g.nombre}</button>
                    <button onClick={() => persistirGuardados(guardados.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 11, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {/* Lo de Olbos primero: eventos, talleres, locales, motoposadas, lugares */}
              {sugEventos.map(s => (
                <button key={s.id} onClick={() => modoParada ? agregarParada({ lat: Number(s.lat), lng: Number(s.lng) }, s.nombre) : calcularRuta({ lat: Number(s.lat), lng: Number(s.lng) }, s.nombre)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 6px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.icono} {s.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{s.sub}</div>
                </button>
              ))}
              {sugerencias.map(s => (
                <button key={s.place_id} onClick={() => irA(s.place_id, s.structured_formatting?.main_text || s.description)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 6px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.structured_formatting?.main_text || s.description}</div>
                  {(s.structured_formatting?.secondary_text || s.distance_meters != null) && <div style={{ fontSize: 12, color: C.muted }}>{s.distance_meters != null ? `${s.distance_meters < 1000 ? Math.round(s.distance_meters) + " m" : (s.distance_meters / 1000).toFixed(s.distance_meters < 10000 ? 1 : 0) + " km"} · ` : ""}{s.structured_formatting?.secondary_text || ""}</div>}
                </button>
              ))}
              {q.trim().length >= 3 && sugerencias.length === 0 && sugEventos.length === 0 && (
                <div style={{ padding: "14px 6px", fontSize: 12.5, color: C.muted }}>Sin resultados todavía… seguí escribiendo.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── AlertasMapaOverlay ──────────────────────────────────────────────────────
// La capa de alertas comunitarias del navegador: pins de caminera/pozo/ripio/
// robo/etc sobre el mapa, botón Reportar en la posición actual, y voto
// "¿sigue ahí?" al tocar un pin. Vive sobre el mapa del grabador en todos los modos.
const ALERTA_TIPOS = [
  { id: "caminera", e: "🚔", n: "Caminera / control" },
  { id: "pozo", e: "🕳️", n: "Pozo" },
  { id: "ripio", e: "🪨", n: "Ripio / mal camino" },
  { id: "animales", e: "🐄", n: "Animales sueltos" },
  { id: "accidente", e: "💥", n: "Accidente" },
  { id: "combustible", e: "⛽", n: "Combustible" },
  { id: "peligro", e: "⚠️", n: "Peligro en la vía" },
  { id: "robo", e: "🚨", n: "Robo / zona insegura" },
  // Los que le duelen a la moto y no al auto (Waze los sumó a su modo moto en
  // julio 2026; nuestra ventaja es que los carga la comunidad, no un editor)
  { id: "lomada", e: "🚧", n: "Lomo de burro" },
  { id: "banquina", e: "🛣️", n: "Banquina que corta" },
  { id: "resbaloso", e: "💧", n: "Piso resbaloso / aceite" },
];
// Navegador v2: con `soloDatos` los pins no se dibujan en Google (tapado) sino que se
// publican por `alActualizar` y el lienzo los dibuja; tocar uno ahí abre la misma ficha
// de siempre vía `seleccionarRef`. La zona a consultar sale de la posición (±35 km) en
// vez de los bounds del mapa de Google, que ya no se mueve.
const AlertasMapaOverlay = ({ mapInstanceRef, posRef, userId, ocultarBoton = false, abrirRef = null, alActualizar = null, seleccionarRef = null, soloDatos = false }) => {
  const [showReportar, setShowReportar] = useState(false);
  // Modo navegación limpio: el botón rojo se esconde y el padre abre la hoja desde el menú ⋯
  useEffect(() => {
    if (!abrirRef) return;
    abrirRef.current = () => setShowReportar(true);
    return () => { abrirRef.current = null; };
  }, [abrirRef]);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (!seleccionarRef) return;
    seleccionarRef.current = (a) => { if (a) setSel(a); };
    return () => { seleccionarRef.current = null; };
  }, [seleccionarRef]);
  const alActualizarRef = useRef(alActualizar);
  useEffect(() => { alActualizarRef.current = alActualizar; }, [alActualizar]);
  useEffect(() => () => { if (alActualizarRef.current) alActualizarRef.current([]); }, []);
  const soloDatosRef = useRef(soloDatos);
  soloDatosRef.current = soloDatos;
  const [reportando, setReportando] = useState(false);
  const markers = useRef({});
  const datos = useRef({});

  const tipoDe = (t) => ALERTA_TIPOS.find(x => x.id === t) || { e: "⚠️", n: t };
  const haceCuanto = (ts) => {
    const m = Math.max(1, Math.round((Date.now() - new Date(ts).getTime()) / 60000));
    return m < 60 ? `hace ${m} min` : m < 2880 ? `hace ${Math.round(m / 60)} h` : `hace ${Math.round(m / 1440)} días`;
  };

  const refetch = async () => {
    const mapa = mapInstanceRef.current;
    let zona = null;
    const p = posRef && posRef.current;
    if (soloDatosRef.current && p && p.lat != null) {
      const dLat = 0.32, dLng = 0.32 / Math.max(0.2, Math.cos(p.lat * Math.PI / 180));
      zona = { p_lat_min: p.lat - dLat, p_lat_max: p.lat + dLat, p_lng_min: p.lng - dLng, p_lng_max: p.lng + dLng };
    } else {
      if (!mapa || !window.google || !mapa.getBounds) return;
      const b = mapa.getBounds();
      if (!b) return;
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      zona = { p_lat_min: sw.lat(), p_lat_max: ne.lat(), p_lng_min: sw.lng(), p_lng_max: ne.lng() };
    }
    const { data: raw } = await supabase.rpc("alertas_en_zona", zona);
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr)) return;
    if (alActualizarRef.current) alActualizarRef.current(arr);
    const vistos = new Set();
    arr.forEach(a => {
      vistos.add(a.id);
      datos.current[a.id] = a;
      if (soloDatosRef.current || !mapa || !window.google) return; // el lienzo v2 los dibuja
      if (markers.current[a.id]) return;
      const mk = new window.google.maps.Marker({
        position: { lat: Number(a.lat), lng: Number(a.lng) }, map: mapa, zIndex: 600,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: a.tipo === "robo" ? "#B3372C" : "#111111", fillOpacity: 0.92, strokeColor: "#fff", strokeWeight: 2, labelOrigin: new window.google.maps.Point(0, 0) },
        label: { text: tipoDe(a.tipo).e, fontSize: "13px" },
        title: tipoDe(a.tipo).n,
      });
      mk.addListener("click", () => setSel(datos.current[a.id]));
      markers.current[a.id] = mk;
    });
    Object.keys(markers.current).forEach(id => { if (!vistos.has(id) || soloDatosRef.current) { markers.current[id].setMap(null); delete markers.current[id]; } });
  };

  useEffect(() => {
    refetch();
    const timer = setInterval(refetch, 25000);
    let idleL = null, deb = null;
    const mapa = mapInstanceRef.current;
    if (mapa && window.google) {
      idleL = mapa.addListener("idle", () => { clearTimeout(deb); deb = setTimeout(refetch, 800); });
    }
    return () => {
      clearInterval(timer); clearTimeout(deb);
      if (idleL) window.google.maps.event.removeListener(idleL);
      Object.values(markers.current).forEach(mk => mk.setMap(null)); markers.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportar = async (tipo) => {
    if (!userId) { alert("Iniciá sesión para reportar."); return; }
    const pos = posRef && posRef.current;
    if (!pos) { alert("Esperando señal GPS…"); return; }
    if (tipo === "robo" && !window.confirm("¿Reportar robo / zona insegura en tu posición actual? Queda visible 7 días para toda la comunidad.")) return;
    setReportando(true);
    const { data: raw, error } = await supabase.rpc("reportar_alerta", { p_tipo: tipo, p_lat: pos.lat, p_lng: pos.lng, p_descripcion: null });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setReportando(false);
    if (error || (r && r.error)) {
      alert(r?.error === "muy_seguido" ? "Esperá un par de minutos entre reportes." : "No se pudo reportar." + (error?.message ? "\n" + error.message : ""));
      return;
    }
    setShowReportar(false);
    refetch();
  };

  const votar = (sigue) => {
    if (!sel) return;
    if (!userId) { alert("Iniciá sesión para confirmar alertas."); return; }
    supabase.rpc("votar_alerta", { p_id: sel.id, p_sigue: sigue }).then(() => refetch(), () => {});
    setSel(null);
  };

  return (
    <>
      {/* botón Reportar — debajo de la brújula (navegando se va al menú ⋯) */}
      {!ocultarBoton && (
        <button onClick={() => setShowReportar(true)}
          style={{ position: "absolute", top: 64, right: 12, zIndex: 5, width: 44, height: 44, borderRadius: "50%", background: "#B3372C", border: "2px solid #fff", color: "#fff", fontSize: 19, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.35)" }}
          title="Reportar alerta">
          ⚠️
        </button>
      )}

      {/* detalle de una alerta tocada */}
      {sel && (
        <div style={{ position: "absolute", bottom: 12, left: 12, right: 12, zIndex: 7, background: "rgba(13,13,18,.93)", borderRadius: 14, padding: "13px 15px", color: "#fff", backdropFilter: "blur(6px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <span style={{ fontSize: 22 }}>{tipoDe(sel.tipo).e}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{tipoDe(sel.tipo).n}</div>
              <div style={{ fontSize: 11.5, color: "#bbb" }}>{haceCuanto(sel.created_at)}{sel.confirmaciones > 0 ? ` · ${sel.confirmaciones} confirmaron` : ""}</div>
            </div>
            <button onClick={() => setSel(null)} style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
          {sel.descripcion && <div style={{ fontSize: 12.5, color: "#ddd", marginBottom: 8 }}>{sel.descripcion}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => votar(true)} style={{ flex: 1, padding: "9px", borderRadius: 10, background: "#2F7D4F", border: "none", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>✓ Sigue ahí</button>
            <button onClick={() => votar(false)} style={{ flex: 1, padding: "9px", borderRadius: 10, background: "rgba(255,255,255,.14)", border: "none", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Ya no está</button>
          </div>
        </div>
      )}

      {/* sheet: elegir qué reportar (un toque = reportado en tu posición) */}
      {showReportar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 420, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowReportar(false)}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "18px 16px calc(24px + var(--sab))" }} className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>¿Qué hay en el camino?</div>
              <button onClick={() => setShowReportar(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} color={C.muted} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>Un toque y queda marcado en tu posición para todos los que vienen atrás.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {ALERTA_TIPOS.map(t => (
                <button key={t.id} onClick={() => reportar(t.id)} disabled={reportando}
                  style={{ display: "flex", alignItems: "center", gap: 9, padding: "12px 12px", borderRadius: 12, background: t.id === "robo" ? "#B3372C0F" : C.surface, border: `1px solid ${t.id === "robo" ? "#B3372C44" : C.border}`, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left", opacity: reportando ? 0.6 : 1 }}>
                  <span style={{ fontSize: 19 }}>{t.e}</span> {t.n}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── SALIDAS ABIERTAS ("Salgo a rodar") ──────────────────────────────────────
// Pedido de Matías (idea de @RebelRabbit) y de Carlos, 29-oct: avisar "salgo a rodar ahora"
// sin crear un evento, que lo vean los riders de alrededor (30 km) y se sumen en el momento.
// Se arma sobre la rodada espontánea: una salida abierta es un compartido en vivo
// (ubicaciones_vivo) con abierta = true; "Me sumo" entra al mismo mapa compartido que el
// link /vivo. Se puede publicar desde Eventos, Social y el mapa. Vence sola a las 3 horas.
// SQL: docs/sql/salidas_abiertas.sql (si no está corrido, todo esto se oculta solo).
const cargarSalidasCerca = async (pos) => {
  if (!pos) return null;
  try {
    const { data: raw, error } = await supabase.rpc("salidas_abiertas_cerca", { p_lat: pos.lat, p_lng: pos.lng, p_radio_km: 30 });
    if (error) return null;
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(d) ? d : [];
  } catch (e) { return null; }
};
const cuandoSaleTxt = (saleA) => {
  if (!saleA) return "sale ahora";
  const min = Math.round((new Date(saleA).getTime() - Date.now()) / 60000);
  if (min > 1) return min < 60 ? `sale en ${min} min` : `sale a las ${new Date(saleA).getHours()}:${String(new Date(saleA).getMinutes()).padStart(2, "0")}`;
  if (min >= -2) return "sale ahora";
  return `salió hace ${-min < 60 ? `${-min} min` : `${Math.floor(-min / 60)} h`}`;
};
// Publica (o actualiza) MI salida abierta. Si ya estoy compartiendo en vivo (grabando con el
// link activo), esa misma sesión pasa a ser la salida: así no quedan dos puntos míos en el mapa.
const publicarSalidaAbierta = async ({ userId, cuandoMin, destino, texto }) => {
  const pos = await obtenerPosicion();
  const saleA = new Date(Date.now() + (cuandoMin || 0) * 60000);
  const datos = {
    abierta: true, sale_a: saleA.toISOString(),
    destino_nombre: (destino || "").trim() || null, texto: (texto || "").trim().slice(0, 140) || null,
    expira_en: new Date(saleA.getTime() + 3 * 3600000).toISOString(),
    lat: pos.lat, lng: pos.lng, ultimo_update: new Date().toISOString(), activo: true,
  };
  const { data: previas } = await supabase.from("ubicaciones_vivo").select("id, share_token")
    .eq("user_id", userId).eq("activo", true).gt("expira_en", new Date().toISOString())
    .order("ultimo_update", { ascending: false }).limit(1);
  let fila = null;
  if (previas && previas[0]) {
    const { data, error } = await supabase.from("ubicaciones_vivo").update(datos).eq("id", previas[0].id).select().single();
    if (error) throw error;
    fila = data;
  } else {
    const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", userId).maybeSingle();
    const { data, error } = await supabase.from("ubicaciones_vivo").insert({
      ...datos, user_id: userId, username: perfil?.username || "Rider",
      share_token: `vivo_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
      titulo: "Salida abierta",
    }).select().single();
    if (error) throw error;
    fila = data;
  }
  registrarUso("salida_abierta_publicada", { cuando: cuandoMin || 0, con_destino: !!datos.destino_nombre });
  // El grabador (si está montado) la toma como su compartido en vivo
  window.dispatchEvent(new CustomEvent("olbos-salida-abierta"));
  return fila;
};

const SalgoARodarSheet = ({ session, onClose }) => {
  useBackGuard(true, onClose);
  const [cuando, setCuando] = useState(0);
  const [destino, setDestino] = useState("");
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(null); // la salida publicada, para la pantalla de confirmación
  const publicar = async () => {
    if (!session?.user?.id) return;
    setEnviando(true);
    try {
      const fila = await publicarSalidaAbierta({ userId: session.user.id, cuandoMin: cuando, destino, texto });
      if (cuando === 0) {
        // Salgo AHORA: directo al mapa, que ya queda compartiendo en vivo
        onClose();
        window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }));
        return;
      }
      setListo(fila);
    } catch (e) {
      const msj = String(e?.message || e || "");
      alert(/abierta|sale_a|column/i.test(msj) ? "Las salidas abiertas todavía no están activas. Probá en un rato." : /ubicaci|location|denied|permis/i.test(msj) ? "Necesitamos tu ubicación para que te encuentren los de cerca. Activala y probá de nuevo." : "No se pudo publicar la salida: " + msj);
    }
    setEnviando(false);
  };
  const chip = (activo) => ({ flex: 1, padding: "11px 6px", borderRadius: 12, border: "none", background: activo ? C.accent : C.surface, color: activo ? "#F9F5F2" : C.text, fontSize: 13.5, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em" });
  const inp = { width: "100%", padding: "12px 13px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.card, boxSizing: "border-box", fontFamily: "inherit" };
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(17,17,17,.6)", zIndex: 2600, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: C.bg, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 460, padding: "20px 18px calc(26px + var(--sab, 0px))", boxSizing: "border-box" }}>
        {listo ? (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 6 }}>Tu salida está publicada</div>
            <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.55, marginBottom: 16 }}>
              Los riders a menos de 30 km la ven en Eventos, en Social y en el mapa, y se pueden sumar. Cuando arranques, abrí el mapa y tocá <strong style={{ color: C.text }}>Grabar</strong>: los que se sumen te ven en vivo. Se cierra sola a las 3 horas.
            </div>
            <button onClick={() => { onClose(); window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} })); }}
              style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}>Abrir el mapa</button>
            <button onClick={onClose} style={{ width: "100%", padding: 12, borderRadius: 12, border: "none", background: "transparent", color: C.muted, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Listo</button>
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>🏍️ Salgo a rodar</div>
              <button onClick={onClose} aria-label="Cerrar" style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: C.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={15} strokeWidth={2.5} /></button>
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 16 }}>Avisá que salís y los riders de tu zona se pueden sumar. Sin armar un evento.</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>¿Cuándo salís?</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[[0, "Ahora"], [15, "En 15 min"], [30, "En 30 min"]].map(([m, l]) => <button key={m} onClick={() => setCuando(m)} style={chip(cuando === m)}>{l}</button>)}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>¿A dónde? (opcional)</div>
            <input value={destino} onChange={e => setDestino(e.target.value)} placeholder="Sin destino, a dar una vuelta" maxLength={80} style={{ ...inp, marginBottom: 12 }} />
            <input value={texto} onChange={e => setTexto(e.target.value)} placeholder="Un mensaje corto (opcional): ritmo tranqui, mate en la costanera…" maxLength={140} style={{ ...inp, marginBottom: 16 }} />
            <button onClick={publicar} disabled={enviando}
              style={{ width: "100%", padding: 15, borderRadius: 12, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 15.5, fontWeight: 800, cursor: enviando ? "default" : "pointer", opacity: enviando ? 0.6 : 1, letterSpacing: "-0.01em" }}>
              {enviando ? "Publicando…" : cuando === 0 ? "Publicar y abrir el mapa" : "Publicar salida"}
            </button>
            <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", marginTop: 10, lineHeight: 1.45 }}>Te ven los riders a menos de 30 km. Se cierra sola a las 3 horas, o cuando terminás.</div>
          </>
        )}
      </div>
    </div>
  );
};

// "Salidas ahora cerca tuyo" + el botón "Salgo a rodar". Va arriba en Eventos y en Social, y
// en el mapa (posFija = la posición del GPS). Sin SQL corrido, no muestra nada.
const SalidasAbiertas = ({ session, requireAuth, posFija = null, estilo = {}, onSalidas = null }) => {
  const [pos, setPos] = useState(() => posFija || posReciente());
  const [salidas, setSalidas] = useState(null); // null = cargando o sin SQL
  const [sheet, setSheet] = useState(false);
  const [buscandoPos, setBuscandoPos] = useState(false);
  useEffect(() => { if (posFija) setPos(posFija); }, [posFija?.lat, posFija?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let vivo = true;
    // Sin posición se consulta igual (en 0,0, no devuelve nada): sirve para saber si el SQL está corrido
    const traer = async () => { const l = await cargarSalidasCerca(pos || { lat: 0, lng: 0 }); if (vivo) { setSalidas(l); if (onSalidas) onSalidas(l); } };
    traer();
    const t = setInterval(traer, 60000);
    const h = () => traer();
    window.addEventListener("olbos-salida-abierta", h);
    return () => { vivo = false; clearInterval(t); window.removeEventListener("olbos-salida-abierta", h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos?.lat, pos?.lng]);
  const abrirSheet = () => {
    if (!session?.user) { if (requireAuth) requireAuth(); return; }
    registrarUso("salida_abierta_abrir", {});
    setSheet(true);
  };
  const sumarme = (s) => {
    if (!session?.user) { if (requireAuth) requireAuth(); return; }
    registrarUso("salida_abierta_sumarse", { distancia_km: s.distancia_km });
    window.dispatchEvent(new CustomEvent("olbos-abrir-vivo", { detail: { token: s.share_token } }));
  };
  const cerrarMia = async (s) => {
    if (!window.confirm("¿Cerrar tu salida? Deja de verse para los demás.")) return;
    await supabase.from("ubicaciones_vivo").update({ abierta: false }).eq("id", s.id).then(() => {}, () => {});
    window.dispatchEvent(new CustomEvent("olbos-salida-abierta"));
  };
  const verCerca = async () => {
    setBuscandoPos(true);
    try { const p = await obtenerPosicion({ enableHighAccuracy: false, timeout: 10000 }); setPos({ lat: p.lat, lng: p.lng }); }
    catch (e) { alert("No pudimos leer tu ubicación. Activala para ver quién sale cerca tuyo."); }
    setBuscandoPos(false);
  };
  // Cargando, o el RPC no existe (SQL sin correr): no se muestra nada
  if (salidas === null) return null;
  const lista = salidas || [];
  return (
    <div style={estilo}>
      {lista.length > 0 ? (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 12px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, animation: "pulse 1.5s ease-in-out infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>Salidas ahora cerca tuyo</span>
          </div>
          {lista.slice(0, 4).map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                {s.avatar_url ? <img loading="lazy" decoding="async" src={cldOpt(s.avatar_url, 100)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🏍️"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {s.es_mia ? "Tu salida" : `@${s.username}`} <span style={{ fontWeight: 600, color: C.muted }}>· {cuandoSaleTxt(s.sale_a)}{s.es_mia ? "" : ` · a ${String(s.distancia_km).replace(".", ",")} km`}</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginTop: 1 }}>
                  {s.destino_nombre ? `Hacia ${s.destino_nombre}` : "Sin destino fijo"}{s.texto ? ` · ${s.texto}` : ""}{s.sumados > 0 ? ` · ${s.sumados} sumado${s.sumados === 1 ? "" : "s"}` : ""}
                </div>
              </div>
              {s.es_mia
                ? <button onClick={() => cerrarMia(s)} style={{ flexShrink: 0, padding: "8px 11px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
                : <button onClick={() => sumarme(s)} style={{ flexShrink: 0, padding: "9px 13px", borderRadius: 10, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>Me sumo</button>}
            </div>
          ))}
          {!lista.some(s => s.es_mia) && (
            <button onClick={abrirSheet} style={{ width: "100%", marginTop: 6, padding: "10px", borderRadius: 10, border: "none", background: C.surface, color: C.text, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>🏍️ Yo también salgo a rodar</button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 10px 10px 13px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>¿Salís a rodar?</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 1, lineHeight: 1.35 }}>
              {pos ? "Avisá y que se sumen los riders de tu zona." : <>Avisá y que se sumen los de tu zona. <button onClick={verCerca} disabled={buscandoPos} style={{ background: "none", border: "none", padding: 0, color: C.text, fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontSize: 12 }}>{buscandoPos ? "Buscando…" : "Ver quién sale cerca"}</button></>}
            </div>
          </div>
          <button onClick={abrirSheet} style={{ flexShrink: 0, padding: "10px 13px", borderRadius: 11, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 13, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>🏍️ Salgo a rodar</button>
        </div>
      )}
      {sheet && <SalgoARodarSheet session={session} onClose={() => setSheet(false)} />}
    </div>
  );
};

// ─── VivoEspontaneoOverlay ───────────────────────────────────────────────────
// El que abre un link /vivo/<token> CON cuenta ya no cae en el visor web: entra
// al grabador de la app y esta capa le pinta al que lo invitó (anillo dorado +
// su recorrido) y al resto de los sumados, mientras publica su propia posición
// en vivo_participantes. La rodada espontánea, adentro del mismo mapa.
// Navegador v2: los demás (el que invitó con anillo dorado y los sumados) se publican por
// `alActualizarOtros` como riders del lienzo; el recorrido del que invitó va por
// `alActualizarLinea` y su camino planeado por `alActualizarPlan`. Con `soloDatos` no se
// dibuja nada en Google (tapado).
// Intercom de un viaje compartido en vivo (documento de Matías, C1): el que comparte su
// ubicación (navegando o grabando, sin organizar una rodada) y los que entran por su link
// hablan en el mismo canal (sala vivo_<token del link>). El que solo mira el link también
// puede sumarse. Todos hablan (no hay roles). La hoja se oculta pero el canal sigue montado.
const VOZ_TODOS = { modalidad: "todos" };
const IntercomVivo = ({ token, creatorId = null, texto = null, sinSesion = null }) => {
  const [abierto, setAbierto] = useState(false);
  const [montado, setMontado] = useState(false);
  const [voz, setVoz] = useState({ estado: "idle", n: 0 });
  if (!token) return null;
  const abrir = async () => {
    const { data } = await supabase.auth.getSession();
    if (!data || !data.session) { if (sinSesion) sinSesion(); else window.dispatchEvent(new CustomEvent("olbos-abrir-registro")); return; }
    setMontado(true); setAbierto(true);
  };
  const conectado = voz.estado === "conectado";
  return (
    <>
      <button onClick={abrir}
        style={{ padding: "5px 11px", borderRadius: 100, background: conectado ? "#2F7D4F" : voz.estado === "reconectando" ? "#8A6D3B" : "rgba(13,13,18,.85)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
        {conectado ? `🎙️ Audio activo${voz.n > 1 ? ` · ${voz.n}` : ""}` : voz.estado === "reconectando" ? "📶 Reconectando" : voz.estado === "conectando" ? "Conectando…" : (texto || "🎙️ Hablar")}
      </button>
      {/* Portal al body: adentro del mapa el botón vive en un contenedor zIndex 5 y la hoja
          quedaba debajo de km/h, desnivel, avisos y micrófono (documento de Matías, C1) */}
      {montado && createPortal(
        <div onClick={e => { if (e.target === e.currentTarget) setAbierto(false); }}
          style={{ display: abierto ? "flex" : "none", position: "fixed", inset: 0, zIndex: 1600, background: "rgba(0,0,0,.55)", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 460, background: C.card, borderRadius: "18px 18px 0 0", padding: "16px 16px calc(20px + var(--sab, 0px))", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 12, color: C.muted, lineHeight: 1.45 }}>Hablan todos los que siguen este viaje con el link. Podés cerrar esta hoja: el audio sigue.</div>
              <button onClick={() => setAbierto(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 8 }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <CanalDeVoz tipo="vivo" id={token} creatorId={creatorId} vozConfig={VOZ_TODOS} sinConfig alCambiarEstado={(estado, n) => setVoz({ estado, n })} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const VivoEspontaneoOverlay = ({ token, mapInstanceRef, puntosRef, posRef = null, userId, topUi = 12, alActualizarOtros = null, alActualizarPlan = null, alActualizarLinea = null, soloDatos = false }) => {
  const [creador, setCreador] = useState(null);
  const [cortado, setCortado] = useState(false);
  const [count, setCount] = useState(0);
  const yoRef = useRef(null); // {username} para publicar mi posición
  const markers = useRef({});
  const polyRef = useRef(null);
  const planPolyRef = useRef(null); // el camino planeado del creador (punteado)
  const avataresRef = useRef({}); // user_id → avatar_url
  const lastPush = useRef(0);
  const encuadrado = useRef(false);
  const cbV2Ref = useRef({ alActualizarOtros, alActualizarPlan, alActualizarLinea });
  cbV2Ref.current = { alActualizarOtros, alActualizarPlan, alActualizarLinea };
  const soloDatosRef = useRef(soloDatos);
  soloDatosRef.current = soloDatos;
  useEffect(() => () => {
    const cb = cbV2Ref.current;
    if (cb.alActualizarOtros) cb.alActualizarOtros([]);
    if (cb.alActualizarPlan) cb.alActualizarPlan(null);
    if (cb.alActualizarLinea) cb.alActualizarLinea(null);
  }, []);

  useEffect(() => {
    let vivo = true;
    if (userId) {
      supabase.from("perfiles").select("username").eq("id", userId).single()
        .then(({ data }) => { if (vivo) yoRef.current = data || { username: "rider" }; }, () => { if (vivo) yoRef.current = { username: "rider" }; });
    }
    return () => { vivo = false; };
  }, [userId]);

  useEffect(() => {
    let activo = true;
    const suav = crearSuavizadorVivo();

    const pintarMarker = (clave, pos, { avatar, titulo, ring, tam, z, iniciales }) => {
      const mapa = mapInstanceRef.current;
      if (!mapa || !window.google || soloDatosRef.current) return; // el lienzo v2 los dibuja
      if (markers.current[clave]) {
        const mk = markers.current[clave];
        suav.poner(clave, mk, pos); mk.setTitle(titulo); mk.setZIndex(z);
        iconoAvatarMapa(avatar, icon => { if (icon && markers.current[clave] === mk) mk.setIcon(icon); }, tam, ring);
      } else {
        const mk = new window.google.maps.Marker({
          position: pos, map: mapa, title: titulo,
          label: { text: (iniciales || "?").slice(0, 2).toUpperCase(), color: "#fff", fontSize: "10px", fontWeight: "bold" },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: ring || "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
          zIndex: z,
        });
        markers.current[clave] = mk;
        suav.poner(clave, mk, pos);
        iconoAvatarMapa(avatar, icon => { if (icon && markers.current[clave] === mk) { mk.setIcon(icon); mk.setLabel(null); } }, tam, ring);
      }
    };

    const refetch = async () => {
      const mapa = mapInstanceRef.current;
      if (!activo || !mapa || !window.google) return;
      const { data: reg } = await supabase.from("ubicaciones_vivo").select("*").eq("share_token", token).maybeSingle();
      if (!activo) return;
      if (!reg) { setCortado(true); return; }
      const muerto = !reg.activo || (reg.expira_en && new Date(reg.expira_en) < new Date());
      setCortado(muerto);
      setCreador(reg);
      if (reg.user_id && !(reg.user_id in avataresRef.current)) {
        avataresRef.current[reg.user_id] = null;
        supabase.from("perfiles").select("id, avatar_url").eq("id", reg.user_id).single()
          .then(({ data }) => { if (data) avataresRef.current[reg.user_id] = data.avatar_url || null; }, () => {});
      }
      const vistos = new Set();
      const soyElCreador = reg.user_id === userId;
      const otrosV2 = []; // para el lienzo del navegador v2
      const cbV2 = cbV2Ref.current;
      if (!muerto && reg.lat && reg.lng && !soyElCreador) {
        vistos.add("creador");
        otrosV2.push({ user_id: reg.user_id || "creador", username: reg.username || "rider", avatar_url: avataresRef.current[reg.user_id] || null, lat: Number(reg.lat), lng: Number(reg.lng), color: "#8A6D3B" });
        pintarMarker("creador", { lat: Number(reg.lat), lng: Number(reg.lng) }, {
          avatar: avataresRef.current[reg.user_id], titulo: "@" + (reg.username || "rider") + " · te invitó",
          ring: "#8A6D3B", tam: 44, z: 1200, iniciales: reg.username,
        });
      }
      // El recorrido del que invitó, para ver por dónde vino y alcanzarlo
      const rec = Array.isArray(reg.recorrido) ? reg.recorrido.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })) : [];
      const hayRec = !muerto && !soyElCreador && rec.length > 1;
      if (cbV2.alActualizarLinea) cbV2.alActualizarLinea(hayRec ? rec : null);
      if (hayRec && !soloDatosRef.current) {
        if (!polyRef.current) polyRef.current = new window.google.maps.Polyline({ path: rec, strokeColor: "#111111", strokeOpacity: 0.85, strokeWeight: 4, map: mapa });
        else { polyRef.current.setPath(rec); if (!polyRef.current.getMap()) polyRef.current.setMap(mapa); }
      } else if (polyRef.current) { polyRef.current.setMap(null); }
      // Y su CAMINO PLANEADO (punteado azul): sabés a dónde va, no solo por dónde vino
      let plan = [];
      if (!muerto && reg.ruta_planeada) { try { plan = decodificarPolyline(reg.ruta_planeada); } catch (e) { plan = []; } }
      if (cbV2.alActualizarPlan) cbV2.alActualizarPlan(plan.length > 1 ? plan : null);
      if (plan.length > 1 && !soloDatosRef.current) {
        if (!planPolyRef.current) planPolyRef.current = new window.google.maps.Polyline({ path: plan, map: mapa, strokeOpacity: 0, icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 0.8, strokeColor: "#1A73E8", scale: 3 }, offset: "0", repeat: "14px" }] });
        else { planPolyRef.current.setPath(plan); if (!planPolyRef.current.getMap()) planPolyRef.current.setMap(mapa); }
      } else if (planPolyRef.current) { planPolyRef.current.setMap(null); }
      const { data: parts } = await supabase.from("vivo_participantes").select("*")
        .eq("share_token", token)
        .gt("updated_at", new Date(Date.now() - 5 * 60000).toISOString());
      if (!activo) return;
      const arr = Array.isArray(parts) ? parts : [];
      setCount(arr.length + (muerto || soyElCreador ? 0 : 1));
      const faltan = arr.map(p => p.user_id).filter(id => !(id in avataresRef.current));
      if (faltan.length) {
        faltan.forEach(id => { avataresRef.current[id] = null; });
        supabase.from("perfiles").select("id, avatar_url").in("id", faltan)
          .then(({ data: ps }) => { (ps || []).forEach(p => { avataresRef.current[p.id] = p.avatar_url || null; }); }, () => {});
      }
      arr.forEach(p => {
        if (!p.lat || !p.lng) return;
        vistos.add(p.user_id);
        const esMio = p.user_id === userId;
        // En el lienzo v2 a mí ya me dibuja la flecha: solo los demás
        if (!esMio) otrosV2.push({ user_id: p.user_id, username: p.username || "rider", avatar_url: avataresRef.current[p.user_id] || null, lat: Number(p.lat), lng: Number(p.lng), color: null });
        // Mismos criterios que la rodada de evento: tamaños parejos y el MÍO
        // abajo de todos para no tapar al que tengo al lado.
        pintarMarker(p.user_id, { lat: Number(p.lat), lng: Number(p.lng) }, {
          avatar: avataresRef.current[p.user_id], titulo: esMio ? "Vos" : "@" + (p.username || "rider"),
          ring: null, tam: 36, z: esMio ? 400 : 800, iniciales: p.username,
        });
      });
      Object.keys(markers.current).forEach(k => { if (!vistos.has(k) || soloDatosRef.current) { markers.current[k].setMap(null); delete markers.current[k]; } });
      if (cbV2.alActualizarOtros) cbV2.alActualizarOtros(otrosV2);
      // Primer encuadre: que entren el que invitó y yo en la misma pantalla
      if (!encuadrado.current && !muerto && reg.lat && reg.lng && !soyElCreador) {
        encuadrado.current = true;
        const b = new window.google.maps.LatLngBounds();
        b.extend({ lat: Number(reg.lat), lng: Number(reg.lng) });
        const pts = puntosRef.current || [];
        const ult = pts[pts.length - 1] || (posRef && posRef.current);
        if (ult) b.extend({ lat: ult.lat, lng: ult.lng });
        mapa.fitBounds(b, 80);
      }
    };

    // Mi posición al mapa compartido, leyendo los puntos que el grabador ya
    // junta — un solo GPS para todo (throttle 10s, mismo ritmo que el visor web)
    if (userId) bgSesionActualizar({ share_token: token }); // el plugin publica solo, aun con la pantalla bloqueada
    const pushInterval = setInterval(() => {
      if (!userId) return;
      const pts = puntosRef.current || [];
      // Sin puntos grabados (modo lista): la posición en vivo del watch liviano
      const ult = pts[pts.length - 1] || (posRef && posRef.current);
      if (!ult) return;
      const ahora = Date.now();
      if (ahora - lastPush.current < 10000) return;
      lastPush.current = ahora;
      supabase.from("vivo_participantes").upsert({
        share_token: token, user_id: userId, username: (yoRef.current && yoRef.current.username) || "rider",
        lat: ult.lat, lng: ult.lng, updated_at: new Date().toISOString(),
      }, { onConflict: "share_token,user_id" }).then(() => {}, () => {});
    }, 2500);

    const poll = setInterval(refetch, 10000);
    refetch();
    // Al volver del fondo (pantalla bloqueada), refrescar al toque — los
    // intervalos pausados dejan el mapa congelado hasta el próximo tick
    const alVolver = () => { if (document.visibilityState === "visible") refetch(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      activo = false;
      clearInterval(pushInterval); clearInterval(poll);
      document.removeEventListener("visibilitychange", alVolver);
      Object.values(markers.current).forEach(mk => mk.setMap(null)); markers.current = {};
      suav.parar();
      if (polyRef.current) { polyRef.current.setMap(null); polyRef.current = null; }
      if (planPolyRef.current) { planPolyRef.current.setMap(null); planPolyRef.current = null; }
      // No quedar como fantasma en el mapa de los demás
      if (userId) supabase.from("vivo_participantes").delete().eq("share_token", token).eq("user_id", userId).then(() => {}, () => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  return (
    <div style={{ position: "absolute", top: topUi, left: 12, display: "flex", gap: 6, alignItems: "center", zIndex: 5, flexWrap: "wrap", maxWidth: "calc(100% - 80px)" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 11.5, fontWeight: 800 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: cortado ? "#999" : "#27AE60", animation: cortado ? "none" : "pulse 1.5s ease-in-out infinite" }} />
        {cortado
          ? "El compartido terminó"
          : creador
            ? `Rodada de @${creador.username || "rider"} · ${count} en el mapa`
            : "Conectando al mapa…"}
      </span>
      {!cortado && <IntercomVivo token={token} creatorId={(creador && creador.user_id) || null} />}
    </div>
  );
};

// Navegador v2: `alActualizarOtros` (riders), `alActualizarPlan` (el recorrido planeado, con
// su regla de "sorpresa") y `alActualizarLinea` (la línea que graba otro) le dan al lienzo
// lo mismo que acá se dibuja en Google; con `soloDatos` en Google no se dibuja nada (tapado).
const GrupoRodadaOverlay = ({ evento, mapInstanceRef, puntosRef, posRef = null, userId, estado, alArrancar = null, alCambiarEstado = null, alActualizarOtros = null, alActualizarResumen = null, alActualizarPlan = null, alActualizarParadasPlan = null, alActualizarLinea = null, soloDatos = false, uiRef = null, compacto = false, topUi = 54 }) => {
  const alArrancarRef = useRef(alArrancar);
  useEffect(() => { alArrancarRef.current = alArrancar; }, [alArrancar]);
  const ultPosRef = useRef({}); // última posición de cada uno: para "el apoyo está a X km"
  // El grabador decide con esto si graba o no (antes de la largada no se graba)
  const alCambiarEstadoRef = useRef(alCambiarEstado);
  useEffect(() => { alCambiarEstadoRef.current = alCambiarEstado; }, [alCambiarEstado]);
  const alActualizarPlanRef = useRef(alActualizarPlan);
  useEffect(() => { alActualizarPlanRef.current = alActualizarPlan; }, [alActualizarPlan]);
  const alActualizarParadasPlanRef = useRef(alActualizarParadasPlan);
  useEffect(() => { alActualizarParadasPlanRef.current = alActualizarParadasPlan; }, [alActualizarParadasPlan]);
  const alActualizarLineaRef = useRef(alActualizarLinea);
  useEffect(() => { alActualizarLineaRef.current = alActualizarLinea; }, [alActualizarLinea]);
  useEffect(() => () => { if (alActualizarLineaRef.current) alActualizarLineaRef.current(null); }, []);
  const soloDatosRef = useRef(soloDatos);
  soloDatosRef.current = soloDatos;
  // Modo limpio del grabador (navegando o rodada libre): la barra inferior muestra
  // "N en el mapa" y "📵 N sin señal" con lo que se publica acá; la fila de chips baja
  // a `topUi` (debajo del banner de giros o de la pastilla "¿A dónde vamos?")
  const alActualizarResumenRef = useRef(alActualizarResumen);
  useEffect(() => { alActualizarResumenRef.current = alActualizarResumen; }, [alActualizarResumen]);
  useEffect(() => () => { if (alActualizarResumenRef.current) alActualizarResumenRef.current(null); }, []);
  // Navegador v2: los demás riders también se dibujan en el lienzo MapLibre, que no
  // ve mapInstanceRef; se le publican con cada refetch y se vacían al desmontar
  const alActualizarOtrosRef = useRef(alActualizarOtros);
  useEffect(() => { alActualizarOtrosRef.current = alActualizarOtros; }, [alActualizarOtros]);
  useEffect(() => () => { if (alActualizarOtrosRef.current) alActualizarOtrosRef.current([]); }, []);
  const [roles, setRoles] = useState(() => evento.voz_config || {});
  const rolesRef = useRef(evento.voz_config || {});
  useEffect(() => { rolesRef.current = roles; }, [roles]);
  const [estadoRodada, setEstadoRodada] = useState(evento.estado || "");
  const estadoRef = useRef(evento.estado || "");
  useEffect(() => { estadoRef.current = estadoRodada; }, [estadoRodada]);
  // Lo fresco del evento: el objeto que llega viene de la lista cargada al abrir la app
  // (el domingo a las 20:05, con la app abierta desde las 19:00, decía "por largar"
  // aunque ya había largado). Se trae al montar, cada 15 s y por Realtime.
  const [evFresco, setEvFresco] = useState(null);
  const [enCursoDesde, setEnCursoDesde] = useState(null); // hora en que VIMOS la largada (no hay columna en la base)
  const [banner, setBanner] = useState(null);
  const [anotados, setAnotados] = useState([]);
  const [count, setCount] = useState(0);
  const [grabandoLinea, setGrabandoLinea] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [showVoz, setShowVoz] = useState(false);
  // Editar paradas con la rodada en curso, sin salir del mapa (documento de Matías, B2)
  const [editRodada, setEditRodada] = useState(null);
  const [vozEstado, setVozEstado] = useState({ estado: "idle", n: 0 }); // lo reporta CanalDeVoz
  const [arrancando, setArrancando] = useState(false);
  const markers = useRef({});
  const suavRef = useRef(null);
  if (!suavRef.current) suavRef.current = crearSuavizadorVivo();
  const trailRef = useRef(null);
  // Arranca con "ahora": si el grabador designado nunca manda un punto (sin fix de GPS al
  // largar), con 0 nadie relevaba la línea jamás; así a los 45/70/100 s pasa al siguiente
  const trailTs = useRef(Date.now());
  const trailLen = useRef(0);
  const lastPush = useRef(0);
  const lastTrailPush = useRef(0);
  const countRef = useRef(0);        // cuántos devolvió el último poll (decide la cadencia)
  const ultRecorridoRef = useRef(0); // última vez que se pidió el recorrido del grupo
  const ubicacionesRef = useRef([]); // lo último que devolvió get_rodada_ubicaciones (panel sin el SQL)
  const [showConectados, setShowConectados] = useState(false);
  // "No te están viendo" (03-nov): la base rechaza mi posición (no estoy anotado) o, a los
  // 40 s de abrir el mapa, todavía no hay ni un punto de GPS. Antes no se decía nada.
  const [noTeVen, setNoTeVen] = useState(null); // null | "no_anotado" | "sin_gps"
  const fallosPubRef = useRef(0);
  const [anotandome, setAnotandome] = useState(false);
  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      if (d.eventoId !== evento.id) return;
      if (d.ok) { fallosPubRef.current = 0; setNoTeVen(v => (v === "no_anotado" || v === "sin_gps") ? null : v); return; }
      const rls = d.codigo === "42501" || /row-level security|permission denied/i.test(d.mensaje || "");
      if (!rls) return; // sin red: ya lo dice el chip "Sin red"
      fallosPubRef.current += 1;
      if (fallosPubRef.current >= 2) setNoTeVen("no_anotado");
    };
    window.addEventListener("olbos-publicacion-rodada", h);
    const t = setTimeout(() => {
      const pts = puntosRef.current || [];
      const ult = pts[pts.length - 1] || (posRef && posRef.current);
      if (!ult && userId) { setNoTeVen(v => v || "sin_gps"); registrarUso("rodada_sin_gps", { id: evento.id }); }
    }, 40000);
    return () => { window.removeEventListener("olbos-publicacion-rodada", h); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, userId]);
  // Llegó un punto después del aviso de "sin GPS": se va solo
  useEffect(() => {
    if (noTeVen !== "sin_gps") return;
    const t = setInterval(() => {
      const pts = puntosRef.current || [];
      if (pts[pts.length - 1] || (posRef && posRef.current)) setNoTeVen(null);
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noTeVen]);
  const anotarmeDesdeMapa = async () => {
    setAnotandome(true);
    const r = await inscribirDirecto(evento.id);
    setAnotandome(false);
    if (r.ok) { fallosPubRef.current = 0; setNoTeVen(null); registrarUso("rodada_anotado_desde_mapa", { id: evento.id }); return; }
    if (r.motivo === "requiere_formulario") { window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: evento.id } })); return; }
    alert("No pudimos anotarte. Probá de nuevo en unos segundos.");
  };

  // Quién gestiona: dueño, admin o co-organizador (antes solo el dueño: un co-org o
  // Carlos como admin no podían largar ni asignar roles)
  const [esAdminYo, setEsAdminYo] = useState(false);
  useEffect(() => {
    if (!userId) return;
    supabase.from("perfiles").select("es_admin").eq("id", userId).maybeSingle().then(({ data }) => setEsAdminYo(!!data?.es_admin), () => {});
  }, [userId]);
  const evActual = evFresco ? { ...evento, ...evFresco } : evento;
  const soyOrg = gestionaEvento(evActual, userId, esAdminYo);
  const grabadorDesignado = () => rolesRef.current.grabador_id || evento.creator_id;
  // Navegar el recorrido: destino = final del plan; puntos de paso = paradas del organizador
  // que quedan adelante, más puntos tomados del propio camino para que Google no se vaya por
  // otra ruta (como mucho 23, el tope de Google).
  const navegarRecorrido = () => {
    const camino = planPathRef.current;
    if (!camino || camino.length < 2) { alert("Esta rodada todavía no tiene el recorrido cargado."); return; }
    const yo = (posRef && posRef.current) || null;
    let desde = 0;
    if (yo && yo.lat) { let bd = Infinity; camino.forEach((p, i) => { const d = haversineKm(yo, p); if (d < bd) { bd = d; desde = i; } }); }
    const resto = camino.slice(desde);
    if (resto.length < 2) { alert("Ya estás al final del recorrido."); return; }
    const fin = resto[resto.length - 1];
    let kmTot = 0; for (let i = 1; i < resto.length; i++) kmTot += haversineKm(resto[i - 1], resto[i]);
    const cada = Math.max(3, kmTot / 20);
    const paradas = []; let acum = 0;
    for (let i = 1; i < resto.length - 1; i++) {
      acum += haversineKm(resto[i - 1], resto[i]);
      if (acum >= cada) { paradas.push(resto[i]); acum = 0; }
    }
    const nombreFin = (Array.isArray(evActual.puntos) && evActual.puntos.length ? (evActual.puntos[evActual.puntos.length - 1].nombre || "") : "") || evActual.hasta || "el final del recorrido";
    registrarUso("rodada_navegar_recorrido", { id: evento.id, paradas: paradas.length });
    window.dispatchEvent(new CustomEvent("olbos-navegar-rodada", { detail: { lat: fin.lat, lng: fin.lng, nombre: nombreFin, paradas: paradas.slice(0, 23) } }));
  };
  const abrirEditarParadas = async () => {
    const { data, error } = await supabase.from("eventos").select("*").eq("id", evento.id).maybeSingle();
    if (error || !data) { alert("No se pudo abrir la edición. Probá de nuevo."); return; }
    setEditRodada(data);
  };
  const guardarEditRodada = async (cambios) => {
    const { data: filas, error } = await supabase.from("eventos").update(cambios).eq("id", evento.id).select("id");
    if (error) { alert("Error al guardar: " + error.message); return false; }
    if (!filas || !filas.length) { alert("Esta cuenta no tiene permiso para editar la rodada."); return false; }
    registrarUso("rodada_editada_en_curso", { id: evento.id, estado: estadoRef.current });
    setEditRodada(null);
    traerEstado(true); // el plan y las paradas se redibujan con la fila nueva
    return true;
  };

  // Un solo lugar por donde entra un estado nuevo (fetch al montar, poll, Realtime o la
  // largada propia): avisa al grabador, muestra el banner y dispara la grabación.
  const aplicarEstado = (nuevo) => {
    if (!nuevo || nuevo === estadoRef.current) return;
    estadoRef.current = nuevo;
    setEstadoRodada(nuevo);
    if (alCambiarEstadoRef.current) alCambiarEstadoRef.current(nuevo);
    if (nuevo === "en_curso") {
      setEnCursoDesde(new Date());
      setBanner("🏁 ¡La rodada arrancó! La línea marca el camino del grupo"); setTimeout(() => setBanner(null), 7000);
      // La largada dispara la grabación de todos los que estaban en el mapa esperando
      if (alArrancarRef.current) alArrancarRef.current();
    }
    if (nuevo === "finalizada") {
      setBanner("🏁 La rodada terminó — ¡gracias por rodar!"); setTimeout(() => setBanner(null), 7000);
      // Fin de la rodada = fin del compartido automático del check-in
      if (compartiendoUbicacionDe(evento.id)) detenerCompartirUbicacion();
    }
  };
  const aplicarFila = (ev, completo) => {
    if (!ev) return;
    const { estado: est, voz_config, ...resto } = ev;
    // Solo la fila completa (al montar o por Realtime) pisa el evento: el poll chico de
    // estado no, para no redibujar el plan cada 15 s
    if (completo) setEvFresco(f => ({ ...(f || {}), ...resto, ...(voz_config ? { voz_config } : {}) }));
    if (voz_config && JSON.stringify(voz_config) !== JSON.stringify(rolesRef.current)) setRoles(voz_config);
    if (est) aplicarEstado(est);
  };
  const traerEstado = async (completo) => {
    const cols = completo
      ? "estado, voz_config, inscripcion_config, hora, fecha, fecha_fin, checkin_modo, checkin_manual, inscripcion_cerrada, creator_id, polyline, puntos, tipo"
      : "estado, voz_config";
    const { data } = await supabase.from("eventos").select(cols).eq("id", evento.id).maybeSingle();
    if (data) aplicarFila(data, !!completo);
  };

  // EL RECORRIDO QUE HAY QUE HACER, en azul y por debajo de todo: la rodada se
  // planeó con un camino y hasta ahora el mapa solo mostraba lo YA recorrido, así
  // que no se sabía por dónde seguir (reporte de Matías, que quería usar Olbos
  // como mapa de la rodada). Se dibuja una vez y queda fijo.
  const planRodadaRef = useRef(null);
  const planPathRef = useRef(null); // el camino del plan (el detallado apenas llega)
  const [hayPlan, setHayPlan] = useState(false); // para la leyenda "El camino" (en el v2 no hay polyline de Google)
  useEffect(() => {
    if (rutaOcultaDelEvento({ ...evActual, estado: estadoRodada })) return; // recorrido sorpresa: se dibuja al largar
    let path = [];
    try {
      if (evActual.polyline) path = decodificarPolyline(evActual.polyline);
      else if (Array.isArray(evActual.puntos)) path = evActual.puntos.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
    } catch (e) { path = []; }
    if (path.length < 2) return;
    setHayPlan(true);
    planPathRef.current = path;
    // Lienzo v2: la misma línea, por el padre (fuente olbos-plan)
    if (alActualizarPlanRef.current) alActualizarPlanRef.current(path);
    const mapa = mapInstanceRef.current;
    if (mapa && window.google && !soloDatos && !planRodadaRef.current) {
      planRodadaRef.current = new window.google.maps.Polyline({
        path, map: mapa, strokeColor: "#1A73E8", strokeOpacity: 0.75, strokeWeight: 7, zIndex: 1,
        icons: FLECHAS_SENTIDO,
      });
    }
    // Y enseguida la versión pegada al camino (ver caminoDetalladoRodada)
    let vivoPlan = true;
    caminoDetalladoRodada(evActual, path).then((det) => {
      if (!vivoPlan || !det || det.length < 2) return;
      planPathRef.current = det;
      if (alActualizarPlanRef.current) alActualizarPlanRef.current(det);
      if (planRodadaRef.current) { try { planRodadaRef.current.setPath(det); } catch (e) { /* */ } }
    }, () => {});
    return () => {
      vivoPlan = false;
      setHayPlan(false);
      if (planRodadaRef.current) { planRodadaRef.current.setMap(null); planRodadaRef.current = null; }
      if (alActualizarPlanRef.current) alActualizarPlanRef.current(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, evActual.polyline, evFresco, mapInstanceRef.current, estadoRodada, soloDatos]);

  // Las PARADAS del plan con su pin (pedido de Matías, 21-oct: en el mapa de la rodada la parada
  // no se distinguía del recorrido). Van por el padre al lienzo v2 y, en el mapa de Google, como marcador.
  const paradasPlanMarkersRef = useRef([]);
  useEffect(() => {
    const limpiar = () => {
      paradasPlanMarkersRef.current.forEach(m => { try { m.setMap(null); } catch (e) { /* */ } });
      paradasPlanMarkersRef.current = [];
      if (alActualizarParadasPlanRef.current) alActualizarParadasPlanRef.current(null);
    };
    if (rutaOcultaDelEvento({ ...evActual, estado: estadoRodada })) { limpiar(); return; }
    const paradasPlan = (Array.isArray(evActual.puntos) ? evActual.puntos : [])
      .filter(p => p && !["inicio", "salida", "fin", "destino"].includes(p.tipo) && p.lat != null && p.lng != null && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng)))
      .map((p, i) => ({ lat: Number(p.lat), lng: Number(p.lng), nombre: p.nombre || `Parada ${i + 1}`, ts: `plan-${i}` }));
    if (alActualizarParadasPlanRef.current) alActualizarParadasPlanRef.current(paradasPlan.length ? paradasPlan : null);
    const mapa = mapInstanceRef.current;
    if (mapa && window.google && !soloDatos) {
      paradasPlanMarkersRef.current = paradasPlan.map(p => new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng }, map: mapa, title: p.nombre, zIndex: 5,
        label: { text: "📍", fontSize: "18px" },
      }));
    }
    return limpiar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, evActual.puntos, estadoRodada, soloDatos, mapInstanceRef.current]);

  // Relevo de la línea: designado siempre; si su trazado dejó de crecer, líder
  // a los 45s, barredor a los 70s, cualquiera a los 100s (escalonado a propósito)
  const meTocaGrabar = () => {
    if (!userId) return false;
    if (userId === grabadorDesignado()) return true;
    const sinLinea = Date.now() - (trailTs.current || 0);
    if (trailTs.current === 0 || sinLinea < 45000) return false;
    if (userId === rolesRef.current.lider_id) return true;
    if (userId === rolesRef.current.barredor_id && sinLinea > 70000) return true;
    return sinLinea > 100000;
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: raw } = await supabase.rpc("get_anotados_publico", { p_evento_id: evento.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo && Array.isArray(d)) setAnotados(d);
    })();
    return () => { vivo = false; };
  }, [evento.id]);

  const guardarRoles = (nuevosRaw) => {
    // Asignar líder o barredor es querer que se escuche a ellos: si nunca se eligió
    // modalidad, pasa a "solo líderes" sola (antes quedaba "todos" y no cambiaba nada)
    const nuevos = (nuevosRaw.lider_id || nuevosRaw.barredor_id) && !nuevosRaw.modalidad ? { ...nuevosRaw, modalidad: "lideres" } : nuevosRaw;
    const previos = rolesRef.current;
    setRoles(nuevos);
    // Si la base lo rechaza (RLS: no sos el dueño), la UI vuelve atrás en vez de mentir
    supabase.from("eventos").update({ voz_config: nuevos }).eq("id", evento.id).select("id").then(({ error, data }) => {
      if (error || !data || data.length === 0) { setRoles(previos); alert("No se pudieron guardar los roles" + (error ? ": " + error.message : " (sin permiso sobre este evento).")); }
    }, () => setRoles(previos));
  };

  const refetch = async () => {
    const soyLaLinea = meTocaGrabar();
    setGrabandoLinea(soyLaLinea);
    const mapa = mapInstanceRef.current;
    if (!mapa || !window.google) return;
    // La línea de la rodada: si la dibuja otro, se trae y se pinta; si soy yo,
    // mi propio trazado del grabador ya está en pantalla — no se duplica.
    // El recorrido del grupo cambia lento (el grabador lo sube cada 15 s): se pide cada
    // 20 s, no en cada poll de posiciones.
    if (!soyLaLinea && Date.now() - ultRecorridoRef.current >= 20000) {
      ultRecorridoRef.current = Date.now();
      const { data: recRaw } = await supabase.rpc("get_rodada_recorrido", { p_evento_id: evento.id });
      const rec = typeof recRaw === "string" ? JSON.parse(recRaw) : recRaw;
      if (Array.isArray(rec) && rec.length > 1) {
        const path = rec.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }));
        if (path.length !== trailLen.current) {
          trailLen.current = path.length; trailTs.current = Date.now();
          if (alActualizarLineaRef.current) alActualizarLineaRef.current(path); // el lienzo v2 (setData con throttle)
        }
        if (soloDatosRef.current) { if (trailRef.current) { trailRef.current.setMap(null); trailRef.current = null; } }
        else if (!trailRef.current) trailRef.current = new window.google.maps.Polyline({ path, strokeColor: "#111111", strokeOpacity: 0.85, strokeWeight: 4, map: mapa });
        else { trailRef.current.setPath(path); if (!trailRef.current.getMap()) trailRef.current.setMap(mapa); }
      }
    } else {
      trailTs.current = Date.now(); // yo soy la línea: siempre fresca
      if (trailRef.current) { trailRef.current.setMap(null); trailRef.current = null; }
      if (trailLen.current) { trailLen.current = 0; if (alActualizarLineaRef.current) alActualizarLineaRef.current(null); }
    }
    // Mi posición: la base devuelve a los 40 más cercanos más los roles (líder, barredor,
    // grabador). Con 4.000 motos ver a todos era inviable; los que te rodean es lo útil.
    const miPos = (puntosRef.current && puntosRef.current[puntosRef.current.length - 1]) || (posRef && posRef.current) || null;
    const { data: raw } = await supabase.rpc("get_rodada_ubicaciones", { p_evento_id: evento.id, p_lat: miPos ? miPos.lat : null, p_lng: miPos ? miPos.lng : null, p_max: 40 });
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr)) return;
    ubicacionesRef.current = arr;
    setCount(arr.length); countRef.current = arr.length;
    const vistos = new Set();
    const r = rolesRef.current;
    // Si me eligieron como apoyo, voy en auto: mis km no suman al ranking de motos
    if (r.apoyo_id && r.apoyo_id === userId && vehiculoActual() !== "auto") elegirVehiculo("auto");
    const ahoraMs = Date.now();
    const sinSenal = [];
    const otrosV2 = []; // para el lienzo del navegador v2
    arr.forEach(u => {
      if (!u.lat || !u.lng) return;
      // Más de 2 minutos sin publicar = sin señal (o la app cerrada): se muestra la
      // última posición, atenuada y con la hora, en vez de un pin "vivo" que miente
      const haceMs = u.updated_at ? ahoraMs - Date.parse(u.updated_at) : 0;
      const viejo = haceMs > 120000;
      const haceMin = Math.round(haceMs / 60000);
      if (viejo && u.user_id !== userId) sinSenal.push({ user_id: u.user_id, username: u.username || "rider", min: haceMin, lat: Number(u.lat), lng: Number(u.lng) });
      // A MÍ ya me dibuja el grabador con la flecha, en tiempo real desde el GPS
      // del teléfono. Mi avatar viene del servidor (se publica cada 5s y vuelve
      // con la demora de la red), así que aparecían los dos: la flecha adelante
      // y la foto arrastrándose atrás, unidas por la línea (reporte de Matías).
      if (u.user_id === userId) return;
      vistos.add(u.user_id);
      const pos = { lat: Number(u.lat), lng: Number(u.lng) };
      ultPosRef.current[u.user_id] = pos;
      const esMio = false;
      const rol = u.user_id === r.lider_id ? "lider" : u.user_id === r.barredor_id ? "barredor" : u.user_id === r.apoyo_id ? "apoyo" : u.user_id === grabadorDesignado() ? "grabador" : null;
      const ring = viejo ? "#8A857F" : (rol ? ROLES_RODADA[rol].color : null);
      otrosV2.push({ user_id: u.user_id, username: u.username || "rider", avatar_url: u.avatar_url || null, lat: pos.lat, lng: pos.lng, color: ring || null });
      if (soloDatosRef.current) return; // el lienzo v2 los dibuja; en Google (tapado) no se gasta
      const tituloPin = (esMio ? "Vos" : "@" + (u.username || "rider")) + (rol ? ` · ${ROLES_RODADA[rol].etiqueta}` : "") + (viejo ? ` · sin señal hace ${haceMin} min` : "");
      // TODOS del mismo tamaño (36; con rol 44), y el MÍO abajo de todos: en la
      // prueba de campo el avatar propio, más grande y arriba, tapaba por
      // completo al del acompañante cuando estaban juntos.
      const tam = rol ? 44 : 36;
      const z = rol ? 1200 : esMio ? 400 : 800;
      if (markers.current[u.user_id]) {
        const mk = markers.current[u.user_id];
        suavRef.current.poner(u.user_id, mk, pos); mk.setTitle(tituloPin); mk.setZIndex(viejo ? 600 : z); mk.setOpacity(viejo ? 0.5 : 1);
        iconoAvatarMapa(u.avatar_url, (icon) => { if (icon && markers.current[u.user_id] === mk) mk.setIcon(icon); }, tam, ring);
      } else {
        const mk = new window.google.maps.Marker({
          position: pos, map: mapa, title: tituloPin,
          label: { text: (u.username || "?").slice(0, 2).toUpperCase(), color: "#fff", fontSize: "10px", fontWeight: "bold" },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: ring || "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
          zIndex: z,
        });
        markers.current[u.user_id] = mk;
        if (viejo) mk.setOpacity(0.5);
        suavRef.current.poner(u.user_id, mk, pos);
        iconoAvatarMapa(u.avatar_url, (icon) => { if (icon && markers.current[u.user_id] === mk) { mk.setIcon(icon); mk.setLabel(null); } }, tam, ring);
      }
    });
    Object.keys(markers.current).forEach(uid => { if (!vistos.has(uid) || soloDatosRef.current) { markers.current[uid].setMap(null); delete markers.current[uid]; suavRef.current.quitar(uid); } });
    sinSenal.sort((a, b) => b.min - a.min);
    setSinSenalLista(sinSenal);
    if (alActualizarOtrosRef.current) alActualizarOtrosRef.current(otrosV2);
    if (alActualizarResumenRef.current) alActualizarResumenRef.current({ count: arr.length, sinSenal: sinSenal.length });
  };

  // Realtime con freno: cada rider publica su posición cada 5s y ANTES cada una
  // de esas escrituras disparaba un refetch (2 consultas) en el teléfono de
  // TODOS los demás. El trabajo crecía al cuadrado: 5 riders ≈ 10 consultas/s,
  // 20 ≈ 160/s, 50 ≈ 1000/s — teléfonos calientes, batería quemada y el mapa
  // dejando de actualizarse justo en una rodada grande (el BikerFest). Con este
  // freno cada teléfono refresca como mucho cada 3 s, sin importar cuántos sean,
  // y el timer del final garantiza que la última posición siempre entre.
  const ultRefetchRef = useRef(0);
  const pendRefetchRef = useRef(null);
  const refetchConFreno = () => {
    const ahora = Date.now();
    const pasado = ahora - ultRefetchRef.current;
    if (pasado >= 5000) { ultRefetchRef.current = ahora; refetch(); return; }
    if (pendRefetchRef.current) return; // ya hay uno agendado: este evento se absorbe
    pendRefetchRef.current = setTimeout(() => {
      pendRefetchRef.current = null;
      ultRefetchRef.current = Date.now();
      refetch();
    }, 5000 - pasado);
  };

  useEffect(() => {
    // Empujar mi posición (5s) + mi trazado si me toca la línea (15s), leyendo
    // los puntos que el grabador ya junta — un solo GPS para todo.
    if (userId) bgSesionActualizar({ evento_id: evento.id }); // el plugin publica solo, aun con la pantalla bloqueada
    const pushInterval = setInterval(() => {
      if (!userId) return;
      const pts = puntosRef.current || [];
      // Sin puntos grabados todavía (modo lista): la posición en vivo del watch
      // liviano — igual te ven en el mapa aunque no estés grabando
      const ult = pts[pts.length - 1] || (posRef && posRef.current);
      if (!ult) return;
      const ahora = Date.now();
      if (ahora - lastPush.current < 5000) return;
      // UN solo publicador por teléfono: si el compartido del check-in ya manda la posición
      // cada 5 s, acá no se duplica (con 4.000 riders eran el doble de escrituras). Solo se
      // publica desde acá si hay que mandar la línea (el recorrido, que el check-in no lleva).
      const tocaLinea = meTocaGrabar() && pts.length > 1 && ahora - lastTrailPush.current > 15000;
      if (compartiendoUbicacionDe(evento.id) && !tocaLinea) return;
      lastPush.current = ahora;
      const payload = { evento_id: evento.id, user_id: userId, lat: ult.lat, lng: ult.lng, heading: null, updated_at: new Date().toISOString() };
      if (meTocaGrabar() && pts.length > 1 && ahora - lastTrailPush.current > 15000) {
        lastTrailPush.current = ahora;
        payload.recorrido = downsampleRecorrido(pts);
      }
      supabase.from("salida_ubicaciones").upsert(payload).then(({ error }) => reportarPublicacionRodada(evento.id, error), () => {});
    }, 2500);

    // Sin cambios en la base (todos sin señal) no llega ningún evento: el reloj de
    // "hace X min" se actualiza igual cada 30 s
    const envejecer = setInterval(() => refetchConFreno(), 30000);
    // Las posiciones NO se escuchan en tiempo real: con N riders cada upsert llegaba a los
    // N teléfonos (N² mensajes); se consultan cada 5 s. Los cambios del evento (largada,
    // roles, fin) sí, que son pocos y hay que verlos al instante.
    const ch = supabase.channel(`rodada-track-${evento.id}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "eventos", filter: `id=eq.${evento.id}` }, (payload) => {
        // La fila completa: estado, roles y, si el organizador tocó el recorrido, el plan
        aplicarFila(payload.new || {}, true);
      })
      .subscribe();
    // Poll adaptativo: 5 s con pocos riders, 15 s cuando la base ya devuelve 40+ (4.000
    // teléfonos a 5 s eran ~800 RPC/s contra Supabase; a 15 s, un tercio)
    let pollTimer = null;
    const programarPoll = () => {
      pollTimer = setTimeout(() => { refetchConFreno(); programarPoll(); }, countRef.current >= 40 ? 15000 : 5000);
    };
    programarPoll();
    // El estado NO puede depender solo de Realtime (sin él, nadie veía "arrancó" ni se
    // le prendía la grabación): al montar viene la fila fresca y cada 15 s el estado.
    traerEstado(true);
    let vueltasPoll = 0;
    const pollEstado = setInterval(() => { if (estadoRef.current !== "finalizada") traerEstado(++vueltasPoll % 8 === 0); }, 15000);
    refetch();
    const alVolver = () => { if (document.visibilityState === "visible") { refetch(); traerEstado(false); } };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      clearInterval(pushInterval); if (pollTimer) clearTimeout(pollTimer); clearInterval(envejecer); clearInterval(pollEstado);
      if (pendRefetchRef.current) { clearTimeout(pendRefetchRef.current); pendRefetchRef.current = null; }
      document.removeEventListener("visibilitychange", alVolver);
      supabase.removeChannel(ch);
      Object.values(markers.current).forEach(mk => mk.setMap(null)); markers.current = {};
      suavRef.current.parar();
      if (trailRef.current) { trailRef.current.setMap(null); trailRef.current = null; }
      // No quedar como fantasma en el mapa del grupo — SALVO que el compartido
      // del check-in siga activo: en ese caso el pin es legítimo y se queda.
      if (userId && !compartiendoUbicacionDe(evento.id)) {
        bgSesionActualizar({ evento_id: null });
        supabase.from("salida_ubicaciones").delete().eq("evento_id", evento.id).eq("user_id", userId).then(() => {}, () => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento.id, userId]);

  // La largada, desde acá: mismo criterio y validaciones que siempre
  const arrancarRodada = async () => {
    const avisos = [];
    if (evActual.fecha) {
      const hoy = new Date();
      const f = new Date(String(evActual.fecha).slice(0, 10) + "T12:00:00");
      if (!isNaN(f.getTime())) {
        const dias = Math.round((new Date(f.getFullYear(), f.getMonth(), f.getDate()) - new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())) / 86400000);
        if (dias > 0) avisos.push(`${dias === 1 ? "Falta" : "Faltan"} ${dias} día${dias !== 1 ? "s" : ""} para la fecha de la rodada.`);
        if (dias < 0) avisos.push(`La fecha de la rodada ya pasó (hace ${Math.abs(dias)} día${Math.abs(dias) !== 1 ? "s" : ""}).`);
      }
    }
    if (!checkinAbierto(evActual)) avisos.push("El check-in todavía no está abierto: no vas a registrar presentes (ni para el sorteo). Se habilita 4hs antes.");
    if (avisos.length && !window.confirm("⚠️ " + avisos.join("\n\n") + "\n\n¿Querés arrancar la rodada igual?")) return;
    setArrancando(true);
    const { data: raw, error } = await supabase.rpc("cambiar_estado_salida", { p_evento_id: evento.id, p_estado: "en_curso" });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setArrancando(false);
    if (error || (r && r.ok === false)) { alert("No se pudo arrancar la rodada. " + (error?.message || r?.error || "Probá de nuevo.")); return; }
    // El organizador larga → banner + su grabación arranca sola (su trazado es la línea)
    aplicarEstado("en_curso");
    window.dispatchEvent(new CustomEvent("olbos-evento-cambiado", { detail: { id: evento.id, estado: "en_curso" } }));
  };
  const textoEstado = estadoRodada === "en_curso"
    ? `🏁 En curso${enCursoDesde ? ` · ${enCursoDesde.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false })}` : ""}`
    : (estadoRodada === "finalizada" || estadoRodada === "sorteo_en_curso") ? "✅ Terminó"
    : "⏳ Esperando la largada";

  const nombreDe = (uid) => { const a = anotados.find(x => x.user_id === uid); return a ? "@" + (a.username || "rider") : null; };
  const chips = [];
  // Sin señal en la montaña (Carlos, viaje NOA oct-2026): el pin de un rider que perdió
  // la red quedaba congelado como si estuviera ahí. Ahora se atenúa, dice hace cuánto
  // se lo vio por última vez, y abajo queda la lista de los que están sin señal.
  const [sinSenalLista, setSinSenalLista] = useState([]);
  const [sinRedYo, setSinRedYo] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const baja = () => setSinRedYo(true), sube = () => setSinRedYo(false);
    window.addEventListener("offline", baja); window.addEventListener("online", sube);
    return () => { window.removeEventListener("offline", baja); window.removeEventListener("online", sube); };
  }, []);
  // Plegada por default: abierta tapaba medio mapa en el predio (Carlos, 10-oct, Originarios).
  // Se abre tocando "N sin señal" (chip arriba o barra de abajo) y se cierra desde su cabecera.
  const [verSinSenal, setVerSinSenal] = useState(false);
  if (uiRef) uiRef.current = { toggleSinSenal: () => setVerSinSenal(v => !v) };
  const topLista = compacto ? `calc(${topUi} + 40px)` : 54;
  if (roles.lider_id) { const n = nombreDe(roles.lider_id); if (n) chips.push({ rol: "lider", n }); }
  if (roles.barredor_id) { const n = nombreDe(roles.barredor_id); if (n) chips.push({ rol: "barredor", n }); }
  if (roles.apoyo_id) { const n = nombreDe(roles.apoyo_id); if (n) chips.push({ rol: "apoyo", n }); }
  const g = grabadorDesignado(); const ng = nombreDe(g);
  if (ng && g !== roles.lider_id && g !== roles.barredor_id && g !== roles.apoyo_id) chips.push({ rol: "grabador", n: ng });
  // El apoyo: a cuántos km está de mí, y el botón para llamarlo
  const posApoyo = roles.apoyo_id && roles.apoyo_id !== userId ? ultPosRef.current[roles.apoyo_id] : null;
  const miPosAhora = (puntosRef.current && puntosRef.current[puntosRef.current.length - 1]) || (posRef && posRef.current) || null;
  const kmApoyo = posApoyo && miPosAhora ? haversineKm(miPosAhora, posApoyo) : null;
  const verApoyo = () => { if (posApoyo && mapInstanceRef.current) { try { mapInstanceRef.current.panTo(posApoyo); } catch (e) { /* */ } } };
  const pedirApoyo = async () => {
    if (!miPosAhora) { alert("Todavía no tenemos tu ubicación. Esperá unos segundos y probá de nuevo."); return; }
    if (!window.confirm("¿Avisarle al apoyo que lo necesitás? Le llega tu ubicación al teléfono.")) return;
    const { data: raw, error } = await supabase.rpc("rodada_pedir_apoyo", { p_evento_id: evento.id, p_lat: miPosAhora.lat, p_lng: miPosAhora.lng });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (d && d.error)) { alert("No se pudo avisar al apoyo: " + (error?.message || d.error)); return; }
    registrarUso("rodada_pedir_apoyo", {});
    alert(d && d.ya ? "Ya le avisamos hace un momento: tiene tu ubicación." : "Listo: el apoyo ya tiene tu ubicación.");
  };

  return (
    <>
      {/* Los que están sin señal: última posición conocida y hace cuánto. Tocar uno
          centra el mapa ahí: es el tramo donde hay que buscarlo. */}
      {sinSenalLista.length > 0 && verSinSenal && (
        <div style={{ position: "absolute", top: topLista, left: 12, right: 66, zIndex: 5, background: "rgba(13,13,18,.9)", borderRadius: 12, padding: "8px 10px", color: "#fff", backdropFilter: "blur(5px)" }}>
          <div onClick={() => setVerSinSenal(false)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 4 }}>
            <span style={{ flex: 1, fontSize: 10.5, fontWeight: 800, color: "#c9c3ba", textTransform: "uppercase", letterSpacing: 0.5 }}>Sin señal · última posición conocida</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#c9c3ba", padding: "2px 8px", borderRadius: 100, background: "rgba(255,255,255,.12)" }}>Ocultar ▲</span>
          </div>
          {sinSenalLista.slice(0, 6).map(u => (
            <div key={u.user_id} onClick={() => { const m = mapInstanceRef.current; if (m) { m.panTo({ lat: u.lat, lng: u.lng }); if (m.getZoom() < 13) m.setZoom(13); } }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 12.5 }}>
              <span style={{ flex: 1, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{u.username}</span>
              <span style={{ color: "#c9c3ba", whiteSpace: "nowrap" }}>hace {u.min >= 60 ? `${Math.floor(u.min / 60)} h ${u.min % 60} min` : `${u.min} min`}</span>
              <span style={{ color: "#c9c3ba" }}>›</span>
            </div>
          ))}
          {sinSenalLista.length > 6 && <div style={{ fontSize: 11, color: "#c9c3ba" }}>y {sinSenalLista.length - 6} más</div>}
        </div>
      )}
      {/* fila superior izquierda: cuántos somos + REC + roles (la brújula vive a la derecha).
          En modo limpio baja debajo del banner/pastilla y "N en el mapa" / "sin señal" van en la barra inferior */}
      {/* Una sola fila que se desliza (sin wrap): la segunda línea quedaba tapada por los avisos */}
      <div className="sin-scrollbar" style={{ position: "absolute", top: compacto ? topUi : 12, left: 12, display: "flex", gap: 6, alignItems: "center", zIndex: 5, flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "calc(100% - 80px)" }}>
        {/* "¿Largamos o no?" es LA pregunta a las 20:00: el estado queda fijo, no un banner de 7 s */}
        <span style={{ padding: "5px 11px", borderRadius: 100, background: estadoRodada === "en_curso" ? "#2F7D4F" : "rgba(13,13,18,.85)", color: "#fff", fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap" }}>
          {textoEstado}
        </span>
        {!compacto && (
          <span style={{ padding: "5px 11px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 11.5, fontWeight: 800 }}>
            🏍️ {count >= 40 ? "40+ cerca tuyo" : `${count} en el mapa`}
          </span>
        )}
        {!compacto && sinSenalLista.length > 0 && (
          <button onClick={() => setVerSinSenal(v => !v)}
            style={{ padding: "5px 11px", borderRadius: 100, background: "#8A6D3B", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
            📵 {sinSenalLista.length} sin señal
          </button>
        )}
        {sinRedYo && (
          <span style={{ padding: "5px 11px", borderRadius: 100, background: "#B3372C", color: "#fff", fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap", flexShrink: 0 }}>
            📵 Sin red · te ven en tu último punto
          </span>
        )}
        {grabandoLinea && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 100, background: "#B3372C", color: "#fff", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.card, animation: "pulse 1.5s ease-in-out infinite" }} /> LÍNEA
          </span>
        )}
        {soyOrg && (
          <button onClick={() => { registrarUso("rodada_panel_conectados", { id: evento.id }); setShowConectados(true); }}
            style={{ padding: "5px 11px", borderRadius: 100, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
            👥 Quién está
          </button>
        )}
        {soyOrg && (
          <button onClick={() => setShowRoles(true)}
            style={{ padding: "5px 11px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
            Roles
          </button>
        )}
        {hayPlan && (
          <button onClick={navegarRecorrido}
            style={{ padding: "5px 11px", borderRadius: 100, background: "#1A73E8", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
            🧭 Navegar el recorrido
          </button>
        )}
        {soyOrg && (
          <button onClick={abrirEditarParadas}
            style={{ padding: "5px 11px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
            ✏️ Paradas
          </button>
        )}
        {/* El micrófono solo no decía qué era (Carlos, BikerFest): ahora dice qué
            hace y en qué estado está el intercomunicador */}
        <button onClick={() => setShowVoz(true)}
          style={{ padding: "5px 11px", borderRadius: 100, background: vozEstado.estado === "conectado" ? "#2F7D4F" : vozEstado.estado === "reconectando" ? "#8A6D3B" : "rgba(13,13,18,.85)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
          {vozEstado.estado === "conectado" ? `🎙️ Audio activo${vozEstado.n > 1 ? ` · ${vozEstado.n}` : ""}`
            : vozEstado.estado === "conectando" ? "Conectando…"
            : vozEstado.estado === "reconectando" ? "📶 Reconectando"
            : "🎙️ Activar audio"}
        </button>
      </div>

      {/* "No te están viendo": lo que antes fallaba en silencio, con el arreglo a un toque */}
      {noTeVen && !(sinSenalLista.length > 0 && verSinSenal) && (
        <div style={{ position: "absolute", top: compacto ? `calc(${topUi} + 40px)` : 50, left: 12, right: 12, zIndex: 8, background: "#B3372C", color: "#fff", borderRadius: 12, padding: "11px 13px", boxShadow: "0 6px 24px rgba(0,0,0,.35)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.45 }}>
              <strong>El grupo no te está viendo.</strong>{" "}
              {noTeVen === "no_anotado" ? "No estás anotado a esta salida: anotate y aparecés en el mapa." : "Todavía no tenemos tu ubicación. Revisá los permisos del teléfono."}
            </div>
            <button onClick={() => setNoTeVen(null)} aria-label="Cerrar" style={{ background: "rgba(255,255,255,.18)", border: "none", color: "#fff", borderRadius: "50%", width: 24, height: 24, flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={13} strokeWidth={2.5} />
            </button>
          </div>
          <button onClick={() => noTeVen === "no_anotado" ? anotarmeDesdeMapa() : window.dispatchEvent(new CustomEvent("olbos-chequeo-rodada", { detail: { evento } }))} disabled={anotandome}
            style={{ marginTop: 8, width: "100%", padding: "10px", borderRadius: 10, background: "#fff", color: "#B3372C", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", opacity: anotandome ? 0.7 : 1 }}>
            {noTeVen === "no_anotado" ? (anotandome ? "Anotándote…" : "Anotarme a la rodada") : "Revisar mi teléfono"}
          </button>
        </div>
      )}

      {showConectados && (
        <PanelConectadosRodada evento={evActual} anotados={anotados} ubicacionesRef={ubicacionesRef} onCerrar={() => setShowConectados(false)} />
      )}

      {/* banner de largada / cierre */}
      {banner && (
        <div className="fade-up" style={{ position: "absolute", top: compacto ? `calc(${topUi} + 88px)` : 64, left: 12, right: 12, background: C.accent, color: "#F9F5F2", borderRadius: 12, padding: "12px 16px", fontSize: 13, fontWeight: 800, textAlign: "center", boxShadow: "0 6px 24px rgba(0,0,0,.4)", zIndex: 9 }}>
          {banner}
        </div>
      )}

      {/* leyenda de roles + qué es cada línea del mapa */}
      {/* Una sola fila que se desliza, arriba del velocímetro: antes se apilaban
          en varias líneas atrás del velocímetro y del buscador (foto de Carlos) */}
      {(chips.length > 0 || hayPlan) && (
        <div className="sin-scrollbar" style={{ position: "absolute", bottom: 84, left: 76, right: 64, display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto", zIndex: 5, WebkitOverflowScrolling: "touch" }}>
          {hayPlan && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
              <span style={{ width: 12, height: 3, borderRadius: 2, background: "#1A73E8" }} /> El camino
            </span>
          )}
          {chips.map(c => (
            <span key={c.rol} onClick={c.rol === "apoyo" ? verApoyo : undefined} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, cursor: c.rol === "apoyo" ? "pointer" : "default" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: ROLES_RODADA[c.rol].color }} />
              {ROLES_RODADA[c.rol].etiqueta} {c.n}{c.rol === "apoyo" && kmApoyo != null ? ` · a ${kmApoyo < 1 ? Math.round(kmApoyo * 1000) + " m" : kmApoyo.toFixed(1).replace(".", ",") + " km"}` : ""}
            </span>
          ))}
          {posApoyo && (
            <span onClick={pedirApoyo} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 100, background: "#B3372C", color: "#fff", fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer" }}>
              🆘 Necesito al apoyo
            </span>
          )}
        </div>
      )}

      {/* la largada, para el organizador */}
      {soyOrg && estadoRodada === "inscripcion_abierta" && (
        <button onClick={arrancarRodada} disabled={arrancando}
          style={{ position: "absolute", bottom: 122, left: 14, right: 14, padding: "14px", borderRadius: 13, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em", boxShadow: "0 6px 24px rgba(0,0,0,.45)", zIndex: 6, opacity: arrancando ? 0.6 : 1 }}>
          {arrancando ? "Arrancando…" : "🏁 Arrancar la rodada"}
        </button>
      )}

      {/* intercomunicador, sobre el mapa. La solapa queda SIEMPRE montada y solo se
          oculta: CanalDeVoz se desconecta al desmontarse, así que cerrarla para ver
          el mapa cortaba el audio (por eso en el BikerFest "no andaba"). */}
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 420, display: showVoz ? "flex" : "none", alignItems: "flex-end", justifyContent: "center" }}
        onClick={e => e.target === e.currentTarget && setShowVoz(false)}>
        <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "16px 16px calc(24px + var(--sab))", maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>Cerrá esta solapa y el audio sigue de fondo</div>
            <button onClick={() => setShowVoz(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={14} strokeWidth={2.5} color={C.muted} />
            </button>
          </div>
          <CanalDeVoz tipo="evento" id={evento.id} creatorId={evento.creator_id} vozConfig={roles} tabla="eventos" gestiona={soyOrg}
            alCambiarEstado={(estado, n) => setVozEstado({ estado, n })} />
        </div>
      </div>

      {/* asignación de roles (organizador) */}
      {editRodada && (
        <EditarEventoModal evento={editRodada} onClose={() => setEditRodada(null)} onGuardar={guardarEditRodada} />
      )}
      {showRoles && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 420, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowRoles(false)}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "18px 18px 0 0", padding: "20px 18px calc(26px + var(--sab))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Roles de la rodada</div>
              <button onClick={() => setShowRoles(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} color={C.muted} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
              Se ven identificados en el mapa y son los que hablan si el intercomunicador está en modo líderes. Si el que graba pierde señal, la línea pasa sola al de adelante, después al de atrás, y si no a cualquiera del grupo.
            </p>
            {[
              { campo: "lider_id", rol: "lider", desc: "Abre la rodada, nadie lo pasa" },
              { campo: "barredor_id", rol: "barredor", desc: "Cierra el grupo, no deja a nadie" },
              { campo: "apoyo_id", rol: "apoyo", desc: "Camioneta o auto que acompaña: todos lo ven y lo pueden llamar" },
              { campo: "grabador_id", rol: "grabador", desc: "Su recorrido es la línea que ven todos" },
            ].map(({ campo, rol, desc }) => (
              <div key={campo} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: ROLES_RODADA[rol].color }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>{ROLES_RODADA[rol].etiqueta}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>· {desc}</span>
                </div>
                <select value={roles[campo] || ""} onChange={e => guardarRoles({ ...roles, [campo]: e.target.value || null })}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13.5, fontWeight: 600, boxSizing: "border-box" }}>
                  <option value="">{campo === "grabador_id" ? "— El organizador —" : "— Sin asignar —"}</option>
                  {anotados.map(a => <option key={a.user_id} value={a.user_id}>@{a.username || "rider"}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ─── GRUPO EN VIVO SOBRE EL MAPA GRANDE ──────────────────────────────────────
// "Mapa en vivo" del grupo abre EL MISMO mapa de siempre (grabador/navegador) y
// esta capa comparte tu posición al grupo y dibuja a los demás encima (pedido de
// Carlos: un solo mapa para todo, nada de mapitas embebidos). Reutiliza
// compartir_ubicacion / get_ubicaciones_grupo / realtime de grupos. Compartís
// mientras el mapa esté abierto (minimizado con la barra roja también); cerrarlo
// deja de compartir.
// A partir de acá se avisa que alguien quedó atrás, y por debajo de acá se
// considera que volvió con el grupo. La banda entre las dos evita el aviso que
// aparece y desaparece cuando el que viene último oscila alrededor del límite.
const KM_REZAGADO = 2.5;
const KM_DE_VUELTA = 1.2;

const GrupoVivoMapaOverlay = ({ grupoId, viajeId = null, mapInstanceRef, posRef, userId, alActualizarOtros = null, soloDatos = false }) => {
  const [otros, setOtros] = useState([]);
  // Navegador v2: los demás también se dibujan en el lienzo MapLibre, que no ve mapInstanceRef
  const alActualizarOtrosRef = useRef(alActualizarOtros);
  useEffect(() => { alActualizarOtrosRef.current = alActualizarOtros; }, [alActualizarOtros]);
  useEffect(() => {
    if (!alActualizarOtrosRef.current) return;
    alActualizarOtrosRef.current(otros.filter(u => u.lat != null && u.lng != null).map(u => ({ user_id: u.user_id, username: u.username || "rider", avatar_url: u.avatar_url || null, lat: Number(u.lat), lng: Number(u.lng), color: "#1A73E8" })));
  }, [otros]);
  useEffect(() => () => { if (alActualizarOtrosRef.current) alActualizarOtrosRef.current([]); }, []);
  const markersRef = useRef({});
  const suavRef = useRef(null);
  if (!suavRef.current) suavRef.current = crearSuavizadorVivo();
  const rezagadosRef = useRef({});     // user_id -> true mientras está lejos
  const baseHechaRef = useRef(false);  // la primera lectura solo toma estado, no avisa
  const [avisoGrupo, setAvisoGrupo] = useState(null); // { tipo: 'lejos'|'volvio', texto }
  const [caidaGrupo, setCaidaGrupo] = useState(null); // alguien del grupo se cayó
  const [cuantosLejos, setCuantosLejos] = useState(0); // para el contador del pill

  // Compartir MI posición: usa el GPS que el mapa ya tiene corriendo (posRef)
  useEffect(() => {
    const push = () => {
      const p = posRef?.current;
      if (!p || p.lat == null) return;
      if (viajeId) supabase.rpc("compartir_ubicacion_viaje", { p_viaje_id: viajeId, p_lat: p.lat, p_lng: p.lng }).then(() => {}, () => {});
      else supabase.rpc("compartir_ubicacion", { p_grupo_id: grupoId, p_lat: p.lat, p_lng: p.lng, p_velocidad: null, p_bateria: null }).then(() => {}, () => {});
    };
    push();
    const id = setInterval(push, 10000);
    return () => { clearInterval(id); (viajeId ? supabase.rpc("dejar_de_compartir_viaje", { p_viaje_id: viajeId }) : supabase.rpc("dejar_de_compartir", { p_grupo_id: grupoId })).then(() => {}, () => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoId, viajeId]);

  // Alguien del grupo se cayó. Llega por realtime y además se chequea al entrar, por
  // si el aviso salió mientras este rider tenía el mapa cerrado o estaba sin señal.
  useEffect(() => {
    if (viajeId) return; // los viajes sin grupo no tienen alertas de caída
    let vivo = true;
    const traer = async () => {
      const desde = new Date(Date.now() - 30 * 60000).toISOString();
      const { data } = await supabase.from("alertas_caida")
        .select("id, user_id, username, lat, lng, created_at, atendida")
        .eq("grupo_id", grupoId).gte("created_at", desde).eq("atendida", false)
        .order("created_at", { ascending: false }).limit(1);
      if (!vivo) return;
      const a = data && data[0];
      if (!a || a.user_id === userId) return;
      setCaidaGrupo(a);
      try { navigator.vibrate && navigator.vibrate([500, 250, 500, 250, 500]); } catch (e) { /* */ }
    };
    traer();
    const canal = supabase.channel(`caidas_${grupoId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "alertas_caida", filter: `grupo_id=eq.${grupoId}` }, traer)
      .subscribe();
    return () => { vivo = false; supabase.removeChannel(canal); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoId, userId]);

  // Ver a los demás: carga inicial + realtime + polling de respaldo
  useEffect(() => {
    let vivo = true;
    const cargar = async () => {
      const { data: raw } = viajeId
        ? await supabase.rpc("get_ubicaciones_viaje", { p_viaje_id: viajeId })
        : await supabase.rpc("get_ubicaciones_grupo", { p_grupo_id: grupoId });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo && data && !data.error) setOtros((data.ubicaciones || []).filter(u => u.user_id !== userId));
    };
    cargar();
    // Viaje sin grupo: la tabla no tiene lectura directa (RLS), así que solo polling
    const id = setInterval(cargar, viajeId ? 6000 : 10000);
    const canal = viajeId ? null : supabase.channel(`vivo_mapa_${grupoId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "ubicaciones_vivo_grupo", filter: `grupo_id=eq.${grupoId}` }, cargar)
      .subscribe();
    return () => { vivo = false; clearInterval(id); if (canal) supabase.removeChannel(canal); };
  }, [grupoId, viajeId, userId]);

  // Marcadores de los demás (vos ya sos la flecha del mapa). Con el v2 arriba los dibuja
  // el lienzo (alActualizarOtros) y acá se vacían.
  useEffect(() => {
    const mapa = mapInstanceRef.current;
    if (!mapa || !window.google) return;
    if (soloDatos) {
      for (const id of Object.keys(markersRef.current)) { markersRef.current[id].setMap(null); delete markersRef.current[id]; suavRef.current.quitar(id); }
      return;
    }
    const vivos = new Set();
    for (const u of otros) {
      vivos.add(u.user_id);
      const pos = { lat: Number(u.lat), lng: Number(u.lng) };
      if (!markersRef.current[u.user_id]) {
        const mk = new window.google.maps.Marker({
          map: mapa, position: pos, zIndex: 5,
          label: { text: (u.username || "rider").slice(0, 12), fontSize: "11px", fontWeight: "700", color: C.text },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#1A73E8", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5, labelOrigin: new window.google.maps.Point(0, -2.4) },
        });
        markersRef.current[u.user_id] = mk;
        iconoAvatarMapa(u.avatar_url, (icon) => { if (icon && markersRef.current[u.user_id] === mk) { mk.setIcon(icon); mk.setLabel(null); } }, 36, "#1A73E8");
      }
      suavRef.current.poner(u.user_id, markersRef.current[u.user_id], pos);
    }
    for (const id of Object.keys(markersRef.current)) {
      if (!vivos.has(id)) { markersRef.current[id].setMap(null); delete markersRef.current[id]; suavRef.current.quitar(id); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otros, soloDatos]);
  useEffect(() => () => {
    for (const id of Object.keys(markersRef.current)) markersRef.current[id].setMap(null);
    markersRef.current = {};
    suavRef.current.parar();
  }, []);

  // ¿Se quedó alguien atrás? Se mide contra MI posición, que es la referencia que
  // cada uno tiene a mano. La primera lectura no avisa: solo anota quién ya venía
  // lejos, para no disparar carteles apenas abrís el mapa mientras el grupo se junta.
  useEffect(() => {
    const yo = posRef?.current;
    if (!yo || yo.lat == null) return;
    // Una posición vieja no dice nada: puede haber cerrado la app o estar sin señal
    const frescos = otros.filter(u => {
      if (u.lat == null || u.lng == null) return false;
      const t = u.actualizado_en ? Date.parse(u.actualizado_en) : 0;
      return !t || (Date.now() - t) < 150000;
    });
    if (frescos.length === 0) return;
    let nuevo = null;
    frescos.forEach(u => {
      const d = haversineKm({ lat: yo.lat, lng: yo.lng }, { lat: Number(u.lat), lng: Number(u.lng) });
      const estabaLejos = !!rezagadosRef.current[u.user_id];
      if (!estabaLejos && d >= KM_REZAGADO) {
        rezagadosRef.current[u.user_id] = true;
        if (baseHechaRef.current) nuevo = { tipo: "lejos", texto: `@${u.username || "un rider"} quedó a ${d.toFixed(1)} km` };
      } else if (estabaLejos && d <= KM_DE_VUELTA) {
        delete rezagadosRef.current[u.user_id];
        if (baseHechaRef.current) nuevo = { tipo: "volvio", texto: `@${u.username || "un rider"} volvió con el grupo` };
      }
    });
    baseHechaRef.current = true;
    setCuantosLejos(Object.keys(rezagadosRef.current).length);
    if (!nuevo) return;
    setAvisoGrupo(nuevo);
    // Con el casco puesto y guantes, la vibración es lo único que se percibe
    if (nuevo.tipo === "lejos") { try { navigator.vibrate && navigator.vibrate([120, 80, 120]); } catch (e) { /* */ } }
    const t = setTimeout(() => setAvisoGrupo(null), 9000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otros]);

  return (
    <>
      {/* Alguien del grupo se cayó: es lo más importante que puede pasar en una rodada,
          así que tapa todo lo demás hasta que alguien lo marque como atendido. */}
      {caidaGrupo && (
        <div style={{ position: "absolute", top: 90, left: 12, right: 12, zIndex: 30, background: "#B3372C", color: "#F9F5F2", borderRadius: 14, padding: "14px 16px", boxShadow: "0 10px 34px rgba(0,0,0,.4)" }}>
          <div style={{ fontSize: 15, fontWeight: 900, letterSpacing: "-0.01em", marginBottom: 4 }}>
            🆘 @{caidaGrupo.username || "un rider"} puede haberse caído
          </div>
          <div style={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.5, marginBottom: 11 }}>
            La app detectó un golpe fuerte y no contestó en 30 segundos. Frená en un lugar seguro y andá a ver.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {caidaGrupo.lat != null && (
              <button onClick={() => navegarConOlbos(Number(caidaGrupo.lat), Number(caidaGrupo.lng), "@" + (caidaGrupo.username || "rider"))}
                style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                Ir hasta ahí
              </button>
            )}
            <button onClick={() => {
              supabase.from("alertas_caida").update({ atendida: true }).eq("id", caidaGrupo.id).then(() => {}, () => {});
              setCaidaGrupo(null);
            }}
              style={{ flex: 1, padding: "11px", borderRadius: 10, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.45)", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
              Ya está atendido
            </button>
          </div>
        </div>
      )}

      {avisoGrupo && (
        <div style={{
          position: "absolute", bottom: 190, left: 12, right: 70, zIndex: 7,
          padding: "11px 14px", borderRadius: 12,
          background: avisoGrupo.tipo === "lejos" ? "#B82F26" : "#1F8347",
          color: "#fff", fontSize: 13, fontWeight: 700, letterSpacing: "-0.005em",
          boxShadow: "0 4px 16px rgba(0,0,0,.3)",
        }}>
          {avisoGrupo.tipo === "lejos" ? "⚠️ " : "✓ "}{avisoGrupo.texto}
          {avisoGrupo.tipo === "lejos" && (
            <div style={{ fontSize: 11.5, fontWeight: 500, opacity: 0.9, marginTop: 2 }}>Aflojá o pará en un lugar seguro para esperarlo.</div>
          )}
        </div>
      )}
      <div style={{ position: "absolute", bottom: 148, left: 12, zIndex: 6, background: "rgba(13,13,18,.9)", color: "#fff", borderRadius: 100, padding: "8px 13px", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 7, backdropFilter: "blur(5px)" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: cuantosLejos > 0 ? "#E2A03F" : "#27AE60", animation: "pulse 1.4s ease-in-out infinite" }} />
        {otros.length ? `${otros.length + 1} del grupo en vivo` : "Compartiendo con tu grupo"}
        {cuantosLejos > 0 && <span style={{ color: "#E2A03F" }}>· {cuantosLejos} atrás</span>}
      </div>
    </>
  );
};

// ─── MODO CIRCUITO ───────────────────────────────────────────────────────────
// Pista y off-road: un circuito es una línea de meta y un trazado. Con eso se
// cuentan vueltas por cruce de la meta (a menos de 30 m después de haberse
// alejado más de 300 m), y en pista se analizan las curvas de cada vuelta:
// velocidad de entrada, mínima y salida, radio e inclinación. La inclinación
// estimada sale de física (tan θ = v²/(g·r)); la medida, del giroscopio del
// teléfono en la app nativa. Pedido de Carlos tras el curso "Giros controlados"
// en Roque Pérez (19-sep-2026); su sesión real dio 22 vueltas, mejor 1:26.
const CIRC_RADIO_META_M = 30;
const CIRC_MIN_LEJOS_M = 300;
const fmtSegVuelta = (seg) => {
  if (seg == null || !isFinite(seg)) return "—";
  const m = Math.floor(seg / 60), s = seg - m * 60;
  return `${m}:${s < 10 ? "0" : ""}${s.toFixed(1)}`;
};
// Momento exacto del cruce: se interpola entre el punto anterior (todavía lejos)
// y el que ya está adentro del radio, así el tiempo no depende del muestreo.
// A 100 km/h con un punto cada ~45 m, un radio de 30 m alrededor de la meta se
// puede saltear: el punto anterior cae afuera y el siguiente también. Se mide
// la distancia del SEGMENTO entre los dos puntos a la meta (metros) y dónde
// cae el cruce dentro del segmento — así ninguna vuelta se pierde a alta
// velocidad (con la meta en la recta principal de Roque Pérez se perdían 5 de 23).
const segmentoAMeta = (a, b, meta) => {
  if (!a) return { d: haversineKm(b, meta) * 1000, t: 1 };
  const kx = Math.cos(meta.lat * Math.PI / 180) * 111320, ky = 110540;
  const ax = (a.lng - meta.lng) * kx, ay = (a.lat - meta.lat) * ky, bx = (b.lng - meta.lng) * kx, by = (b.lat - meta.lat) * ky;
  const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? -(ax * dx + ay * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return { d: Math.hypot(ax + t * dx, ay + t * dy), t };
};
const tsCruce = (pAnt, pCur, meta) => {
  if (!pAnt) return pCur.ts;
  const dA = haversineKm(pAnt, meta) * 1000, dC = haversineKm(pCur, meta) * 1000;
  if (dA <= dC) return pCur.ts;
  const f = Math.min(1, Math.max(0, (dA - CIRC_RADIO_META_M) / (dA - dC)));
  return pAnt.ts + (pCur.ts - pAnt.ts) * f;
};
const velKmhEn = (pts, i) => {
  const p = pts[i];
  if (p.v != null && p.v >= 0) return p.v * 3.6;
  if (i === 0) return 0;
  const dt = (p.ts - pts[i - 1].ts) / 1000;
  return dt > 0 ? haversineKm(pts[i - 1], p) / (dt / 3600) : 0;
};
// Vueltas de un recorrido completo contra una línea de meta
const rumboEntre = (a, b) => {
  const y = Math.sin((b.lng - a.lng) * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180);
  const x = Math.cos(a.lat * Math.PI / 180) * Math.sin(b.lat * Math.PI / 180) - Math.sin(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.cos((b.lng - a.lng) * Math.PI / 180);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};
const detectarVueltas = (pts, meta) => {
  const vueltas = [];
  if (!Array.isArray(pts) || pts.length < 10 || !meta) return vueltas;
  let lejos = false, iniIdx = -1, iniTs = 0, kmAcum = 0;
  for (let i = 0; i < pts.length; i++) {
    if (i > 0) kmAcum += haversineKm(pts[i - 1], pts[i]);
    if (haversineKm(pts[i], meta) * 1000 > CIRC_MIN_LEJOS_M) lejos = true;
    const seg = segmentoAMeta(i > 0 ? pts[i - 1] : null, pts[i], meta);
    if (seg.d < CIRC_RADIO_META_M && (iniIdx < 0 || lejos)) {
      const t = i > 0 ? pts[i - 1].ts + (pts[i].ts - pts[i - 1].ts) * seg.t : pts[i].ts;
      if (iniIdx >= 0) {
        let vmax = 0, inclMax = 0;
        for (let j = iniIdx; j <= i; j++) { vmax = Math.max(vmax, velKmhEn(pts, j)); if (pts[j].i != null) inclMax = Math.max(inclMax, Math.abs(pts[j].i)); }
        const seg = (t - iniTs) / 1000;
        vueltas.push({ n: vueltas.length + 1, ini: iniIdx, fin: i, seg: Math.round(seg * 10) / 10, km: Math.round((kmAcum - vueltas.kmIni) * 100) / 100, vmax: Math.round(vmax), vprom: seg > 0 ? Math.round((kmAcum - vueltas.kmIni) / (seg / 3600)) : 0, incl_max: inclMax ? Math.round(inclMax) : null });
      }
      iniIdx = i; iniTs = t; vueltas.kmIni = kmAcum; lejos = false;
    }
  }
  delete vueltas.kmIni;
  return vueltas;
};
// Curvas de un tramo (una vuelta): giro acumulado en el mismo sentido
const analizarCurvas = (pts, ini, fin) => {
  const out = [];
  if (fin - ini < 4) return out;
  const cum = [0];
  for (let i = ini + 1; i <= fin; i++) cum.push(cum[cum.length - 1] + haversineKm(pts[i - 1], pts[i]) * 1000);
  const hd = [];
  for (let i = ini + 1; i <= fin; i++) hd.push(rumboEntre(pts[i - 1], pts[i]));
  const curvas = []; let cur = null;
  for (let k = 1; k < hd.length; k++) {
    let d = hd[k] - hd[k - 1]; d = ((d + 540) % 360) - 180;
    const idx = ini + k; // índice global del punto donde termina el segmento k
    const segm = Math.max(1, cum[k + 1] - cum[k]);
    const rate = Math.abs(d) / segm, sig = Math.sign(d);
    if (rate > 0.4 && Math.abs(d) < 120) {
      if (!cur || cur.sig !== sig) { if (cur) curvas.push(cur); cur = { ini: idx, fin: idx + 1, ang: 0, sig, pasos: [] }; }
      cur.fin = idx + 1; cur.ang += Math.abs(d); cur.pasos.push([idx + 1, Math.abs(d)]);
    } else if (cur && (cum[k + 1] - cum[cur.fin - ini]) > 30) { curvas.push(cur); cur = null; }
  }
  if (cur) curvas.push(cur);
  // Una horquilla (más de 120° seguidos) son dos curvas: se parte donde va la
  // mitad del giro. Así Roque Pérez da sus 8 curvas y no 5 tramos (Carlos, 19-sep).
  const partidas = [];
  curvas.filter(c => c.ang >= 30).forEach(c => {
    if (c.ang <= 120) { partidas.push(c); return; }
    let acc = 0, corte = c.ini;
    for (const [idx, a] of c.pasos) { acc += a; if (acc >= c.ang / 2) { corte = idx; break; } }
    partidas.push({ ini: c.ini, fin: corte, ang: c.ang / 2, sig: c.sig });
    partidas.push({ ini: corte, fin: c.fin, ang: c.ang / 2, sig: c.sig });
  });
  partidas.forEach((c, n) => {
    const largo = Math.max(5, cum[c.fin - ini] - cum[c.ini - ini]);
    const radio = largo / (c.ang * Math.PI / 180);
    let vmin = Infinity, inclMed = 0;
    for (let j = c.ini; j <= c.fin; j++) { vmin = Math.min(vmin, velKmhEn(pts, j)); if (pts[j].i != null) inclMed = Math.max(inclMed, Math.abs(pts[j].i)); }
    const inclEst = Math.atan(Math.pow(vmin / 3.6, 2) / (9.81 * radio)) * 180 / Math.PI;
    out.push({ n: n + 1, lado: c.sig > 0 ? "D" : "I", ang: Math.round(c.ang), radio_m: Math.round(radio), largo_m: Math.round(largo),
      v_ent: Math.round(velKmhEn(pts, c.ini)), v_min: Math.round(vmin), v_sal: Math.round(velKmhEn(pts, c.fin)),
      incl_est: Math.round(inclEst), incl_med: inclMed ? Math.round(inclMed) : null, en_m: Math.round(cum[c.ini - ini]), ini: c.ini, fin: c.fin });
  });
  return out;
};
// Curvas FIJAS del circuito (Carlos, 20-sep): antes cada vuelta se analizaba sola y
// con un punto GPS cada ~45 m el corte caía en lugares distintos, así que al cambiar
// de vuelta las curvas se corrían y hasta cambiaba la cantidad. Ahora se detectan
// una vez sobre una vuelta de referencia (la mejor), quedan en circuitos.curvas con
// su posición (vértice, entrada y salida), y cada vuelta se MIDE en esas mismas curvas.
const curvasDeReferencia = (pts, ini, fin) => analizarCurvas(pts, ini, fin).map(c => {
  const ap = pts[Math.round((c.ini + c.fin) / 2)] || pts[c.ini];
  const pIni = pts[c.ini], pFin = pts[c.fin];
  return { n: c.n, lat: ap.lat, lng: ap.lng, lat_ini: pIni.lat, lng_ini: pIni.lng, lat_fin: pFin.lat, lng_fin: pFin.lng,
    lado: c.lado, ang: c.ang, radio_m: c.radio_m, largo_m: c.largo_m, en_m: c.en_m };
});
const medirCurvasEnVuelta = (pts, ini, fin, curvasCirc) => {
  if (!Array.isArray(curvasCirc) || !curvasCirc.length || fin - ini < 4) return [];
  const masCerca = (obj, desde, hasta) => { let mi = desde, md = Infinity; for (let j = desde; j <= hasta; j++) { const d = haversineKm(pts[j], obj); if (d < md) { md = d; mi = j; } } return { idx: mi, d: md * 1000 }; };
  return curvasCirc.map(c => {
    const base = { n: c.n, lado: c.lado, ang: c.ang, radio_m: c.radio_m, largo_m: c.largo_m, en_m: c.en_m, lat: c.lat, lng: c.lng, fijas: true };
    const ap = masCerca(c, ini, fin);
    if (ap.d > 60) return { ...base, v_ent: null, v_min: null, v_sal: null, incl_est: null, incl_med: null };
    const desde = c.lat_ini != null ? masCerca({ lat: c.lat_ini, lng: c.lng_ini }, ini, ap.idx).idx : ap.idx;
    const hasta = c.lat_fin != null ? masCerca({ lat: c.lat_fin, lng: c.lng_fin }, ap.idx, fin).idx : ap.idx;
    let vmin = Infinity, inclMed = 0;
    for (let j = desde; j <= hasta; j++) { vmin = Math.min(vmin, velKmhEn(pts, j)); if (pts[j].i != null) inclMed = Math.max(inclMed, Math.abs(pts[j].i)); }
    if (!isFinite(vmin)) vmin = velKmhEn(pts, ap.idx);
    const inclEst = Math.atan(Math.pow(vmin / 3.6, 2) / (9.81 * Math.max(5, c.radio_m || 5))) * 180 / Math.PI;
    return { ...base, v_ent: Math.round(velKmhEn(pts, desde)), v_min: Math.round(vmin), v_sal: Math.round(velKmhEn(pts, hasta)),
      incl_est: Math.round(inclEst), incl_med: inclMed ? Math.round(inclMed) : null };
  });
};
// Sesión completa: vueltas + curvas de cada una medidas en las curvas fijas del circuito + mejor.
// Si el circuito todavía no tiene curvas (recién creado), salen de la mejor vuelta y vuelven en curvas_circuito para guardarlas.
const armarSesionCircuito = (pts, circuito) => {
  const vueltas = detectarVueltas(pts, circuito.meta);
  const mejor0 = vueltas.reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
  let curvasCirc = Array.isArray(circuito.curvas) && circuito.curvas.length ? circuito.curvas : null;
  let curvasNuevas = null;
  if (circuito.tipo === "pista" && !curvasCirc && mejor0) { curvasCirc = curvasDeReferencia(pts, mejor0.ini, mejor0.fin); curvasNuevas = curvasCirc.length ? curvasCirc : null; }
  const conCurvas = vueltas.map(v => ({ ...v, curvas: circuito.tipo === "pista" && curvasCirc ? medirCurvasEnVuelta(pts, v.ini, v.fin, curvasCirc) : [] }));
  const mejor = conCurvas.reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
  return { vueltas: conCurvas, n_vueltas: conCurvas.length, mejor_seg: mejor ? mejor.seg : null, mejor_n: mejor ? mejor.n : null, curvas_circuito: curvasNuevas };
};
// GPS a 1 Hz en la app mientras dura el modo circuito (por defecto 1 punto cada 8 m).
// Solo toca la configuración del plugin al ENTRAR a un circuito y al SALIR de uno: antes
// se llamaba con "false" en cada apertura del mapa y reescribía la config nativa aunque
// nadie usara circuitos (revisado tras el reporte de GPS impreciso de Matías, 26-sep).
let _gpsCircuitoActivo = false;
const configurarGpsCircuito = async (activo) => {
  if (!esNativo()) return;
  if (!activo && !_gpsCircuitoActivo) return;
  _gpsCircuitoActivo = !!activo;
  try {
    const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
    await BG.setConfig(activo
      ? { distanceFilter: 0, locationUpdateInterval: 1000, fastestLocationUpdateInterval: 1000 }
      : { distanceFilter: 8, locationUpdateInterval: 1000, fastestLocationUpdateInterval: 1000 });
  } catch (e) { /* sin plugin: sigue con el muestreo normal */ }
};
const colorVel = (v, vmin, vmax) => {
  const f = Math.max(0, Math.min(1, (v - vmin) / Math.max(1, vmax - vmin)));
  return `hsl(${Math.round(120 - f * 120)}, 80%, 42%)`; // verde lento → rojo rápido
};

// Cronómetro de pista sobre el mapa: vuelta, tiempo en curso, mejor y delta
const PanelCircuitoVivo = ({ circuito, vueltas, vueltaIniTs, mejorSeg, ultimaDelta, onSalir, saltos = [] }) => {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick(t => t + 1), 200); return () => clearInterval(id); }, []);
  const enCurso = vueltaIniTs ? (Date.now() - vueltaIniTs) / 1000 : null;
  const ult = vueltas[vueltas.length - 1];
  return (
    <div style={{ position: "absolute", top: 64, left: 12, right: 66, zIndex: 8, background: "rgba(13,13,18,.93)", borderRadius: 14, padding: "10px 14px", color: "#fff", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flexShrink: 0, textAlign: "center" }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#bdb7b0", letterSpacing: "0.1em" }}>VUELTA</div>
        <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}>{vueltas.length + (vueltaIniTs ? 1 : 0)}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          {enCurso != null ? fmtSegVuelta(enCurso) : "— : —"}
        </div>
        <div style={{ fontSize: 11.5, color: "#bdb7b0", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {vueltaIniTs ? "" : "Cruzá la largada para arrancar · "}
          {mejorSeg != null ? `mejor ${fmtSegVuelta(mejorSeg)}` : circuito.nombre}
          {ult ? ` · última ${fmtSegVuelta(ult.seg)}` : ""}
          {saltos.length ? ` · 🦘 ${saltos.length} salto${saltos.length === 1 ? "" : "s"}` : ""}
        </div>
      </div>
      {ultimaDelta != null && (
        <div style={{ flexShrink: 0, fontSize: 18, fontWeight: 900, color: ultimaDelta <= 0 ? "#6FBF8A" : "#E07A6F", fontVariantNumeric: "tabular-nums" }}>
          {ultimaDelta <= 0 ? "" : "+"}{ultimaDelta.toFixed(1)}
        </div>
      )}
      <button onClick={onSalir} title="Salir del modo circuito" style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>✕</button>
    </div>
  );
};

// Mapa del circuito coloreado por velocidad (SVG puro, sin Google) + curvas numeradas
const puntoDeCurva = (pts, ini, c) => {
  if (c.lat != null && c.lng != null) return { lat: Number(c.lat), lng: Number(c.lng) };
  if (c.ini != null) return pts[c.fin != null ? Math.round((c.ini + c.fin) / 2) : c.ini] || null;
  const objetivo = (c.en_m || 0) + (c.largo_m || 0) / 2;
  let acc = 0;
  for (let j = ini + 1; j < pts.length; j++) { acc += haversineKm(pts[j - 1], pts[j]) * 1000; if (acc >= objetivo) return pts[j]; }
  return null;
};
const MapaVueltaSVG = ({ pts, ini, fin, meta, curvas = [], saltos = [], alto = 220 }) => {
  if (!pts || fin - ini < 3) return null;
  const tramo = pts.slice(ini, fin + 1);
  const lats = tramo.map(p => p.lat), lngs = tramo.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
  const w = Math.max(1e-6, (maxLng - minLng) * kx), hgt = Math.max(1e-6, maxLat - minLat);
  const W = 320, H = alto, pad = 16;
  const esc = Math.min((W - 2 * pad) / w, (H - 2 * pad) / hgt);
  const X = (p) => pad + ((p.lng - minLng) * kx) * esc + ((W - 2 * pad) - w * esc) / 2;
  const Y = (p) => H - pad - (p.lat - minLat) * esc - ((H - 2 * pad) - hgt * esc) / 2;
  const vels = tramo.map((_, k) => velKmhEn(pts, ini + k));
  const vmin = Math.min(...vels), vmax = Math.max(...vels);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block", background: C.surface, borderRadius: 12 }}>
      {tramo.slice(1).map((p, k) => (
        <line key={k} x1={X(tramo[k])} y1={Y(tramo[k])} x2={X(p)} y2={Y(p)} stroke={colorVel(vels[k + 1], vmin, vmax)} strokeWidth={5} strokeLinecap="round" />
      ))}
      {meta && <g><circle cx={X(meta)} cy={Y(meta)} r={7} fill="#111" stroke="#fff" strokeWidth={2} /><text x={X(meta) + 10} y={Y(meta) + 4} fontSize={11} fontWeight={800} fill="#111">LARGADA</text></g>}
      {saltos.filter(s => s.lat && s.lng).map((s, i) => (
        <g key={"s" + i}><circle cx={X(s)} cy={Y(s)} r={10} fill="#8A6D3B" stroke="#fff" strokeWidth={2} /><text x={X(s)} y={Y(s) + 4} fontSize={10} fontWeight={800} fill="#fff" textAnchor="middle">{s.t_aire.toFixed(1)}</text></g>
      ))}
      {curvas.map(c => { const p = puntoDeCurva(pts, ini, c); if (!p) return null; return (
        <g key={c.n}><circle cx={X(p)} cy={Y(p)} r={9} fill="#fff" stroke="#111" strokeWidth={1.5} /><text x={X(p)} y={Y(p) + 4} fontSize={10} fontWeight={800} fill="#111" textAnchor="middle">{c.n}</text></g>
      ); })}
      <text x={pad} y={H - 4} fontSize={9} fill="#8A857F">verde = lento · rojo = rápido · {Math.round(vmin)}–{Math.round(vmax)} km/h</text>
    </svg>
  );
};

// Resumen de una sesión en circuito: vueltas, curvas de la mejor, mapa y ranking
// ─── SECCIÓN CIRCUITOS (pestaña en Rutas) ─────────────────────────────────────
// Dónde buscar circuitos y ver las estadísticas generales de cada uno: récord,
// riders, sesiones, vueltas, ranking, tus sesiones, y arrancar a grabar ahí.
const pathSuave = (xy) => {
  const n = xy.length;
  if (n < 3) return xy.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const d0 = Math.hypot(xy[0][0] - xy[n - 1][0], xy[0][1] - xy[n - 1][1]);
  const cerrado = d0 < 12;
  const pts = cerrado && d0 > 0.5 ? xy.concat([xy[0]]) : xy;
  const m = pts.length;
  const at = (i) => cerrado ? pts[((i % (m - 1)) + (m - 1)) % (m - 1)] : pts[Math.max(0, Math.min(m - 1, i))];
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < m - 1; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${c2[0].toFixed(1)} ${c2[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
};
const TrazadoCircuitoSVG = ({ puntos, meta, alto = 200 }) => {
  if (!puntos || puntos.length < 3) return null;
  const lats = puntos.map(p => p.lat), lngs = puntos.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
  const w = Math.max(1e-6, (maxLng - minLng) * kx), hgt = Math.max(1e-6, maxLat - minLat);
  const W = 320, H = alto, pad = 18;
  const esc = Math.min((W - 2 * pad) / w, (H - 2 * pad) / hgt);
  const X = (p) => pad + ((p.lng - minLng) * kx) * esc + ((W - 2 * pad) - w * esc) / 2;
  const Y = (p) => H - pad - (p.lat - minLat) * esc - ((H - 2 * pad) - hgt * esc) / 2;
  const d = pathSuave(puntos.map(p => [X(p), Y(p)]));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block", background: C.surface, borderRadius: 12 }}>
      <path d={d} fill="none" stroke="rgba(17,17,17,.14)" strokeWidth={12} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke="#111" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
      {meta && <g><circle cx={X(meta)} cy={Y(meta)} r={7} fill="#111" stroke="#fff" strokeWidth={2} /><text x={X(meta) + 10} y={Y(meta) + 4} fontSize={11} fontWeight={800} fill="#111">LARGADA</text></g>}
    </svg>
  );
};

// Miniatura del trazado para la tarjeta de la lista (banderita hasta que haya polyline)
const MiniTrazado = ({ polyline, tipo, size = 64 }) => {
  const pts = useMemo(() => { try { return polyline ? decodificarPolyline(polyline) : []; } catch (e) { return []; } }, [polyline]);
  if (pts.length < 3) return <span style={{ fontSize: 26 }}>{tipo === "offroad" ? "🏍️" : "🏁"}</span>;
  const lats = pts.map(p => p.lat), lngs = pts.map(p => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
  const w = Math.max(1e-6, (maxLng - minLng) * kx), h = Math.max(1e-6, maxLat - minLat);
  const S = 100, pad = 10, esc = Math.min((S - 2 * pad) / w, (S - 2 * pad) / h);
  const X = (p) => pad + ((p.lng - minLng) * kx) * esc + ((S - 2 * pad) - w * esc) / 2;
  const Y = (p) => S - pad - (p.lat - minLat) * esc - ((S - 2 * pad) - h * esc) / 2;
  const d = pathSuave(pts.map(p => [X(p), Y(p)]));
  return (
    <svg viewBox={`0 0 ${S} ${S}`} width={size} height={size} style={{ display: "block" }}>
      <path d={d} fill="none" stroke="rgba(17,17,17,.15)" strokeWidth={9} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke="#111" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const circuitoVencido = (c) => c && c.vigencia === "temporal" && !!c.hasta && String(c.hasta).slice(0, 10) < new Date().toISOString().slice(0, 10);
const etiquetaTemporal = (c) => {
  if (!c || c.vigencia !== "temporal") return "";
  const f = c.hasta ? new Date(String(c.hasta).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : "";
  return circuitoVencido(c) ? ` · ⏳ temporal, terminó el ${f}` : ` · ⏳ temporal${f ? " hasta el " + f : ""}`;
};
const FichaCircuito = ({ c, userId, onClose }) => {
  // c._pista: es un lugar de Servicios (categoría Pista) sin trazado grabado todavía; la
  // ficha arranca en Lugar y la solapa Trazado invita a marcarlo dando una vuelta.
  const [tab, setTab] = useState(c._pista ? "lugar" : "circuito");
  const [ranking, setRanking] = useState(null);
  const [mias, setMias] = useState(null);
  const [negocio, setNegocio] = useState(undefined); // undefined = cargando, null = sin lugar vinculado
  const [muro, setMuro] = useState(null);
  const [resenas, setResenas] = useState(null);
  const [miResena, setMiResena] = useState({ estrellas: 0, texto: "", enviando: false });
  useBackGuard(true, onClose);
  // ¿Puedo dar de baja este circuito? El que lo creó, o un admin. No había forma de
  // sacar un circuito (Carlos, 08-oct): un trazado de prueba quedaba para siempre.
  const [puedoBajar, setPuedoBajar] = useState(false);
  useEffect(() => {
    if (!userId || c._pista) { setPuedoBajar(false); return; }
    (async () => {
      const [{ data: fila }, { data: yo }] = await Promise.all([
        supabase.from("circuitos").select("user_id").eq("id", c.id).maybeSingle(),
        supabase.from("perfiles").select("es_admin").eq("id", userId).maybeSingle(),
      ]);
      setPuedoBajar(!!(fila && fila.user_id === userId) || !!(yo && yo.es_admin));
    })();
  }, [c.id, userId]);
  const darDeBaja = async () => {
    if (!window.confirm(`¿Dar de baja el circuito "${c.nombre}"?\n\nDeja de aparecer en Circuitos y en el evento. Las vueltas que ya se grabaron quedan en Mis recorridos de cada rider.`)) return;
    const { error, data } = await supabase.from("circuitos").update({ publico: false, evento_id: null }).eq("id", c.id).select("id");
    if (error || !data || !data.length) { alert("No se pudo dar de baja: " + (error ? error.message : "sin permiso")); return; }
    window.dispatchEvent(new CustomEvent("olbos-circuitos-actualizados"));
    onClose();
  };
  const meta = { lat: Number(c.meta_lat), lng: Number(c.meta_lng) };
  const trazado = useMemo(() => { try { return c.polyline ? decodificarPolyline(c.polyline) : []; } catch (e) { return []; } }, [c.polyline]);
  const circuitoObj = { id: c.id, nombre: c.nombre, tipo: c.tipo || "pista", meta, largo_m: c.largo_m, curvas: c.curvas || null };
  useEffect(() => {
    if (c._pista) {
      setRanking([]); setMuro([]); setResenas([]); setMias([]);
      supabase.from("negocios").select("*").eq("id", c.negocio_id).maybeSingle().then(({ data }) => setNegocio(data || null), () => setNegocio(null));
      return;
    }
    supabase.rpc("ranking_circuito", { p_circuito_id: c.id }).then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setRanking(Array.isArray(d) ? d : []);
    }, () => setRanking([]));
    // El lugar (ficha de Servicios) al que pertenece el circuito: dueño, horarios, tarifas, turnos
    if (c.negocio_id) {
      supabase.from("negocios").select("*").eq("id", c.negocio_id).maybeSingle().then(({ data }) => setNegocio(data || null), () => setNegocio(null));
    } else setNegocio(null);
    // Muro: placas que los riders eligieron mostrar en este circuito
    supabase.from("sesiones_circuito").select("id, user_id, n_vueltas, mejor_seg, placa_url, created_at, perfiles(username, avatar_url)")
      .eq("circuito_id", c.id).eq("publica", true).not("placa_url", "is", null).order("created_at", { ascending: false }).limit(60)
      .then(({ data }) => setMuro(data || []), () => setMuro([]));
    supabase.from("circuito_resenas").select("id, user_id, estrellas, texto, created_at, perfiles(username, avatar_url)")
      .eq("circuito_id", c.id).order("created_at", { ascending: false }).limit(100)
      .then(({ data }) => {
        setResenas(data || []);
        const mia = (data || []).find(r => r.user_id === userId);
        if (mia) setMiResena(m => ({ ...m, estrellas: mia.estrellas || 0, texto: mia.texto || "" }));
      }, () => setResenas([]));
    if (!userId) { setMias([]); return; }
    supabase.from("sesiones_circuito").select("id, traza_id, n_vueltas, mejor_seg, vueltas, km, vmax, publica, created_at, trazas(*)")
      .eq("circuito_id", c.id).eq("user_id", userId).order("created_at", { ascending: false })
      .then(({ data }) => setMias(data || []), () => setMias([]));
  }, [c.id, c.negocio_id, userId]);
  const verSesion = (s) => {
    if (!s.trazas) return;
    const mejor = (s.vueltas || []).reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
    window.dispatchEvent(new CustomEvent("olbos-ver-sesion-circuito", { detail: { traza: s.trazas, sesion: { ...s, mejor_n: mejor ? mejor.n : 1 }, circuito: circuitoObj } }));
  };
  const cerca = c.dist_km != null && Number(c.dist_km) <= 5;
  const grabarAca = () => {
    if (!userId) { alert("Iniciá sesión para grabar tus vueltas."); return; }
    window.olbosPendingCircuito = { id: c.id, nombre: c.nombre, tipo: c.tipo, meta_lat: c.meta_lat, meta_lng: c.meta_lng, largo_m: c.largo_m, curvas: c.curvas || null };
    window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }));
    onClose();
  };
  const enviarResena = async () => {
    if (!userId) { alert("Iniciá sesión para dejar tu reseña."); return; }
    if (!miResena.estrellas && !miResena.texto.trim()) { alert("Poné estrellas o escribí algo."); return; }
    setMiResena(m => ({ ...m, enviando: true }));
    const fila = { circuito_id: c.id, user_id: userId, estrellas: miResena.estrellas || null, texto: miResena.texto.trim() || null };
    const { error } = await supabase.from("circuito_resenas").upsert(fila, { onConflict: "circuito_id,user_id" });
    if (error) { alert("No se pudo guardar la reseña: " + error.message); setMiResena(m => ({ ...m, enviando: false })); return; }
    const { data } = await supabase.from("circuito_resenas").select("id, user_id, estrellas, texto, created_at, perfiles(username, avatar_url)").eq("circuito_id", c.id).order("created_at", { ascending: false }).limit(100);
    setResenas(data || []);
    setMiResena(m => ({ ...m, enviando: false }));
  };
  const promedio = resenas && resenas.filter(r => r.estrellas).length ? resenas.filter(r => r.estrellas).reduce((a, r) => a + r.estrellas, 0) / resenas.filter(r => r.estrellas).length : null;
  const tiles = [
    ["Récord", c.record_seg != null ? fmtSegVuelta(Number(c.record_seg)) : "—", c.record_username ? `@${c.record_username}` : "todavía sin vueltas"],
    ["Tu mejor", c.mi_mejor_seg != null ? fmtSegVuelta(Number(c.mi_mejor_seg)) : "—", c.mis_sesiones ? `${c.mis_sesiones} sesión${c.mis_sesiones === 1 ? "" : "es"}` : "sin sesiones acá"],
    ["Riders", c.n_riders || 0, "con vueltas"],
    ["Vueltas", c.n_vueltas || 0, `en ${c.n_sesiones || 0} sesión${c.n_sesiones === 1 ? "" : "es"}`],
    ["Largo", c.largo_m ? `${(c.largo_m / 1000).toFixed(2).replace(".", ",")} km` : "—", c.tipo === "offroad" ? "off-road" : "pista"],
    ["Km rodados", c.km_total ? Math.round(Number(c.km_total)) : 0, "entre todos"],
  ];
  const Estrellas = ({ n, size = 14, onPick }) => (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => <span key={i} onClick={onPick ? () => onPick(i) : undefined} style={{ fontSize: size, color: i <= n ? "#8A6D3B" : C.border, cursor: onPick ? "pointer" : "default", lineHeight: 1 }}>★</span>)}
    </span>
  );
  const seccion = (t) => <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, margin: "14px 0 6px" }}>{t}</div>;
  const tabs = c._pista ? [["lugar", "Lugar"], ["circuito", "Trazado"]] : [["circuito", "Circuito"], ["lugar", "Lugar"], ["muro", `Muro${muro && muro.length ? ` · ${muro.length}` : ""}`], ["resenas", `Reseñas${resenas && resenas.length ? ` · ${resenas.length}` : ""}`]];
  const marcarTrazado = () => {
    if (!userId) { alert("Iniciá sesión para marcar el trazado."); return; }
    window.olbosPendingCircuitoNuevo = true;
    window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }));
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1250, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 10px", borderBottom: `1px solid ${C.border}`, background: C.card }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} strokeWidth={2.5} color={C.text} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🏁 {c.nombre}</div>
            <div style={{ fontSize: 11.5, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
              {c._pista ? "Pista registrada" : c.tipo === "offroad" ? "Circuito off-road" : "Circuito de pista"}{etiquetaTemporal(c)}{c.dist_km != null ? ` · a ${Number(c.dist_km) < 1 ? "menos de 1" : Math.round(Number(c.dist_km))} km` : ""}
              {promedio != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Estrellas n={Math.round(promedio)} size={11} /> {promedio.toFixed(1)}</span>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", marginTop: 10, background: C.surface, borderRadius: 12, padding: 3, border: `1px solid ${C.border}` }}>
          {tabs.map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 800, whiteSpace: "nowrap", background: tab === t ? C.card : "transparent", color: tab === t ? C.text : C.muted }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {tab === "circuito" && c._pista && (
          <div style={{ padding: "26px 20px", textAlign: "center", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>🏁</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Esta pista todavía no tiene el trazado marcado</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>Si estás en el predio, das una vuelta completa con la moto y queda como circuito para todos: cuenta de vueltas, récord, ranking y placas. Se vincula solo a este lugar.</div>
            <button onClick={marcarTrazado} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 14.5, fontWeight: 800, background: C.accent, color: "#F9F5F2", marginBottom: 8 }}>Marcar el trazado dando una vuelta</button>
            {!cerca && meta.lat && (
              <button onClick={() => { navegarConOlbos(meta.lat, meta.lng, c.nombre); onClose(); }} style={{ width: "100%", padding: "13px", borderRadius: 12, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 14, fontWeight: 700, background: C.card, color: C.text }}>
                Ir con Olbos{c.dist_km != null ? ` · ${Math.round(Number(c.dist_km))} km` : ""}
              </button>
            )}
          </div>
        )}
        {tab === "circuito" && !c._pista && (
          <>
            {trazado.length >= 3 && <div style={{ marginBottom: 12 }}><TrazadoCircuitoSVG puntos={trazado} meta={meta} /></div>}
            {c.evento_id && (
              <div onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: c.evento_id } }))} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, marginBottom: 12, cursor: "pointer" }}>
                <span style={{ fontSize: 18 }}>📅</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Trazado del evento</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.evento_titulo || "Ver el evento"}{c.evento_fecha ? ` · ${new Date(String(c.evento_fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}` : ""}</div>
                </div>
                <ChevronRight size={16} color={C.muted} />
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
              {tiles.map(([l, val, sub]) => (
                <div key={l} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "9px 10px", minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 900, color: l === "Récord" && c.record_seg != null ? "#2F7D4F" : C.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{val}</div>
                  <div style={{ fontSize: 10.5, color: C.text, fontWeight: 700 }}>{l}</div>
                  <div style={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
                </div>
              ))}
            </div>
        {/* Largar: abre el mapa con este circuito ya elegido y empieza a grabar. Si todavía
                estás lejos, graba igual: las vueltas se cuentan recién al cruzar la largada y el
                tramo previo se descarta al guardar (pedido de Carlos, 22-sep). */}
            <button onClick={grabarAca} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 14.5, fontWeight: 800, background: C.accent, color: "#F9F5F2", marginBottom: cerca ? 16 : 8 }}>🏁 Largar: grabar mis vueltas acá</button>
            {!cerca && (
              <>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginBottom: 8, textAlign: "center" }}>Estás a {c.dist_km != null ? Math.round(Number(c.dist_km)) : "?"} km. Podés largar igual: el tramo hasta cruzar la largada no cuenta ni queda en el recorrido.</div>
                <button onClick={() => { navegarConOlbos(meta.lat, meta.lng, c.nombre); onClose(); }} style={{ width: "100%", padding: "13px", borderRadius: 12, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 13.5, fontWeight: 700, background: C.card, color: C.text, marginBottom: 16 }}>🧭 Cómo llegar</button>
              </>
            )}
            {puedoBajar && (
              <button onClick={darDeBaja} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 700, background: "transparent", color: C.red, marginBottom: 14 }}>🗑️ Dar de baja este circuito</button>
            )}
            {mias && mias.length > 0 && (
              <>
                {seccion("Tus sesiones acá")}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 6, overflow: "hidden" }}>
                  {mias.map(s => (
                    <div key={s.id} onClick={() => verSesion(s)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{new Date(s.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "long" })}{s.publica ? <span style={{ fontSize: 10, fontWeight: 800, color: "#8A6D3B", marginLeft: 6 }}>EN EL MURO</span> : null}</div>
                        <div style={{ fontSize: 11.5, color: C.muted }}>{s.n_vueltas} vuelta{s.n_vueltas === 1 ? "" : "s"}{s.km ? ` · ${Number(s.km).toFixed(1)} km` : ""}{s.vmax ? ` · vmáx ${Math.round(Number(s.vmax))}` : ""}</div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: C.text, fontVariantNumeric: "tabular-nums" }}>{s.mejor_seg != null ? fmtSegVuelta(Number(s.mejor_seg)) : "—"}</div>
                      <span style={{ color: C.muted }}>›</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {seccion("Ranking · mejor vuelta por rider")}
            {ranking === null ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando…</div>
              : ranking.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>Todavía nadie grabó vueltas acá. Sé el primero.</div>
              : (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "0 12px" }}>
                  {ranking.map((r, i) => (
                    <div key={r.username + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: i < ranking.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ width: 26, fontSize: 14, fontWeight: 900, color: i < 3 ? "#8A6D3B" : C.muted, textAlign: "center" }}>{i + 1}</div>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, overflow: "hidden", flexShrink: 0 }}>
                        {r.avatar_url ? <img loading="lazy" src={cldThumb(r.avatar_url, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>@{r.username}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{r.fecha ? new Date(r.fecha).toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : ""}{r.n_vueltas ? ` · ${r.n_vueltas} vueltas` : ""}</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 900, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmtSegVuelta(Number(r.mejor_seg))}</div>
                    </div>
                  ))}
                </div>
              )}
          </>
        )}
        {tab === "lugar" && (
          negocio === undefined ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando…</div>
          : !negocio ? (
            <div style={{ padding: "30px 20px", textAlign: "center", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📍</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>Este circuito todavía no está vinculado a un lugar</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>Si sos el dueño o lo administrás, registrá el lugar en Servicios → Pistas (es gratis) y escribinos por WhatsApp para vincularlo: ahí van tus horarios, tarifas, turnos y contacto.</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: C.surface, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
                  {negocio.logo_url ? <img loading="lazy" src={cldThumb(negocio.logo_url, 120)} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : "🏁"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text }}>{negocio.nombre}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>{[negocio.direccion, negocio.ciudad].filter(Boolean).join(" · ")}</div>
                  <div style={{ fontSize: 11, color: negocio.user_id ? "#2F7D4F" : C.muted, fontWeight: 700, marginTop: 2 }}>{negocio.user_id ? "✓ Administrado por su dueño" : "Ficha sin dueño todavía · ¿es tuyo? Reclamalo en Servicios"}</div>
                </div>
              </div>
              {negocio.descripcion && <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>{negocio.descripcion}</p>}
              {negocio.horarios && !negocio.horarios_semana && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, background: C.surface, padding: "10px 14px", borderRadius: 10 }}>
                  <Clock size={15} strokeWidth={2} color={C.muted} /><span style={{ fontSize: 13, color: C.muted }}>{negocio.horarios}</span>
                </div>
              )}
              <BeneficioFicha negocio={negocio} />
              <HorariosTarifasFicha negocio={negocio} />
              <EventosDelNegocio negocio={negocio} />
              <TurnosTallerFicha negocio={negocio} />
              <div style={{ display: "flex", gap: 8, marginBottom: 10, marginTop: 8 }}>
                {negocio.whatsapp && <a href={`https://wa.me/${negocio.whatsapp}?text=Hola! Te contacto desde Olbos 🏍️`} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "12px 0", borderRadius: 12, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 14, textAlign: "center", textDecoration: "none" }}>💬 WhatsApp</a>}
                {negocio.telefono && <a href={`tel:${negocio.telefono}`} style={{ flex: 1, padding: "12px 0", borderRadius: 12, background: C.surface, color: C.text, fontWeight: 700, fontSize: 14, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>📞 Llamar</a>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {negocio.web && <a href={negocio.web.startsWith("http") ? negocio.web : `https://${negocio.web}`} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 600, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>🌐 Sitio web</a>}
                {negocio.instagram && <a href={`https://instagram.com/${negocio.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, color: "#E1306C", fontWeight: 600, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>📷 Instagram</a>}
              </div>
            </>
          )
        )}
        {tab === "muro" && (
          muro === null ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando…</div>
          : muro.length === 0 ? (
            <div style={{ padding: "30px 20px", textAlign: "center", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>El muro está vacío</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>Cuando grabes tus vueltas acá, desde el resumen podés mostrar tu placa en el muro del circuito para que la vean los demás riders.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {muro.map(s => (
                <div key={s.id} onClick={() => window.dispatchEvent(new CustomEvent("olbos-ver-anuncio", { detail: { id: null, imagen_url: s.placa_url, link: null } }))} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", cursor: "pointer" }}>
                  <img loading="lazy" src={cldThumb(s.placa_url, 600)} alt="" style={{ width: "100%", display: "block", aspectRatio: "1080 / 1500", objectFit: "cover", objectPosition: "top" }} />
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{s.perfiles?.username || "rider"}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{s.n_vueltas} vueltas · {s.mejor_seg != null ? fmtSegVuelta(Number(s.mejor_seg)) : "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        {tab === "resenas" && (
          <>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 6 }}>{resenas && resenas.some(r => r.user_id === userId) ? "Tu reseña" : "Dejá tu reseña"}</div>
              <div style={{ marginBottom: 8 }}><Estrellas n={miResena.estrellas} size={26} onPick={(i) => setMiResena(m => ({ ...m, estrellas: i }))} /></div>
              <textarea value={miResena.texto} onChange={e => setMiResena(m => ({ ...m, texto: e.target.value }))} placeholder="¿Cómo está el piso, la organización, las tandas, los precios? Lo que le sirva al próximo rider." maxLength={600}
                style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
              <button onClick={enviarResena} disabled={miResena.enviando} style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 800, background: C.accent, color: "#F9F5F2", opacity: miResena.enviando ? .6 : 1 }}>{miResena.enviando ? "Guardando…" : "Publicar reseña"}</button>
            </div>
            {resenas === null ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando…</div>
              : resenas.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Todavía no hay reseñas. La tuya puede ser la primera.</div>
              : resenas.map(r => (
                <div key={r.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 12px", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, overflow: "hidden", flexShrink: 0 }}>
                      {r.perfiles?.avatar_url ? <img loading="lazy" src={cldThumb(r.perfiles.avatar_url, 60)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>@{r.perfiles?.username || "rider"}</div>
                      <div style={{ fontSize: 10.5, color: C.muted }}>{new Date(r.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}</div>
                    </div>
                    {r.estrellas ? <Estrellas n={r.estrellas} size={13} /> : null}
                  </div>
                  {r.texto && <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{r.texto}</div>}
                </div>
              ))}
          </>
        )}
        <div style={{ height: 30 }} />
      </div>
    </div>
  );
};

// Desde la ficha de una pista en Servicios: abrir su circuito (vueltas, récord,
// ranking, muro) o, si todavía no tiene trazado, invitar a grabarlo.
const CircuitoDelLugar = ({ negocio }) => {
  const [circ, setCirc] = useState(undefined);
  useEffect(() => {
    if (!negocio?.id) return;
    supabase.from("circuitos").select("id, nombre").eq("negocio_id", negocio.id).limit(1).maybeSingle().then(({ data }) => setCirc(data || null), () => setCirc(null));
  }, [negocio?.id]);
  if (circ === undefined) return null;
  if (circ) return (
    <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-circuito", { detail: { id: circ.id } }))} style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 800, background: C.accent, color: "#F9F5F2", marginBottom: 14, textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 20 }}>🏁</span>
      <span style={{ flex: 1 }}>Ver el circuito<div style={{ fontSize: 11.5, fontWeight: 500, color: "#c9c3ba" }}>Vueltas, récord, ranking, muro y reseñas de los riders</div></span>
      <ChevronRight size={16} color="#c9c3ba" />
    </button>
  );
  return (
    <div onClick={() => { window.olbosPendingCircuitoNuevo = true; window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} })); }} style={{ background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 12, padding: "11px 14px", marginBottom: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 20 }}>🏁</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Esta pista todavía no tiene su circuito en Olbos</div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4 }}>Si estás ahí, grabá el trazado: das una vuelta y queda para todos, con cuenta de vueltas y ranking.</div>
      </div>
      <ChevronRight size={16} color={C.muted} />
    </div>
  );
};

const SeccionCircuitos = ({ userId }) => {
  const [lista, setLista] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [conPos, setConPos] = useState(false);
  const [vista, setVista] = useState("activos"); // activos (permanentes + temporales vigentes) | temporales (todos, como eventos pasados)
  useEffect(() => {
    let vivo = true;
    (async () => {
      let pos = null;
      try { pos = await Promise.race([obtenerPosicion({ enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }), new Promise(r => setTimeout(() => r(null), 6500))]); } catch (e) { pos = null; }
      const { data } = await supabase.rpc("circuitos_listar", { p_lat: pos ? pos.lat : null, p_lng: pos ? pos.lng : null });
      if (!vivo) return;
      const d = typeof data === "string" ? JSON.parse(data) : data;
      const circs = Array.isArray(d) ? d : [];
      // Pistas registradas en Servicios que todavía no tienen el trazado grabado (Carlos,
      // 10-oct: "hay negocios que son circuitos y no aparecen en la solapa"): entran como
      // ficha del lugar, con el botón para marcar el trazado dando una vuelta.
      let pistas = [];
      try {
        const { data: ng } = await supabase.from("negocios").select("id, nombre, ciudad, direccion, lat, lng, logo_url").ilike("categoria", "pista%").eq("estado", "verificado").limit(200);
        const conCirc = new window.Set(circs.map(c => c.negocio_id).filter(Boolean));
        pistas = (ng || []).filter(n => !conCirc.has(n.id)).map(n => ({
          id: "pista-" + n.id, _pista: true, negocio_id: n.id, nombre: n.nombre, tipo: "pista", vigencia: "permanente",
          meta_lat: n.lat, meta_lng: n.lng, ciudad: n.ciudad, logo_url: n.logo_url,
          dist_km: pos && n.lat != null && n.lng != null ? haversineKm(pos, { lat: Number(n.lat), lng: Number(n.lng) }) : null,
        }));
      } catch (e) { /* sin pistas registradas */ }
      if (!vivo) return;
      setConPos(!!pos);
      // Con posición, circuitos y pistas se mezclan por cercanía; sin posición, las pistas van al final
      setLista(pos ? [...circs, ...pistas].sort((a, b) => (a.dist_km != null ? Number(a.dist_km) : 1e9) - (b.dist_km != null ? Number(b.dist_km) : 1e9)) : [...circs, ...pistas]);
    })();
    return () => { vivo = false; };
  }, []);
  const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const nTemporales = (lista || []).filter(c => c.vigencia === "temporal").length;
  const visibles = (lista || [])
    .filter(c => vista === "temporales" ? c.vigencia === "temporal" : !circuitoVencido(c))
    .filter(c => !q.trim() || norm(c.nombre).includes(norm(q)) || norm(c.evento_titulo).includes(norm(q)))
    .sort((a, b) => vista === "temporales" ? String(b.hasta || "").localeCompare(String(a.hasta || "")) : 0);
  return (
    <div style={{ padding: "0 0 80px" }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar circuito por nombre"
        style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, marginBottom: 10, boxSizing: "border-box" }} />
      {nTemporales > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[["activos", "Activos"], ["temporales", `⏳ Temporales · ${nTemporales}`]].map(([k, l]) => (
            <button key={k} onClick={() => setVista(k)} style={{ padding: "8px 14px", borderRadius: 999, border: `1px solid ${vista === k ? C.accent : C.border}`, background: vista === k ? C.accent : C.card, color: vista === k ? "#F9F5F2" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
      )}
      {vista === "temporales" && <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>Trazados armados para un evento (enduro, rally, pruebas). Los que ya terminaron quedan acá como historial, con su ranking y sus placas; los vigentes también aparecen en Activos.</div>}
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
        Pista y off-road. Grabás tus vueltas desde el mapa (botón 🏁) y acá quedan el récord del circuito, el ranking y tus tiempos.{conPos ? " Ordenados por cercanía." : ""}
      </div>
      {lista === null ? <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Buscando circuitos…</div>
        : visibles.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🏁</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{q.trim() ? "No hay circuitos con ese nombre" : vista === "temporales" ? "Todavía no hay circuitos temporales" : "Todavía no hay circuitos cargados"}</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>Si conocés uno, andá con la moto: desde el mapa, botón 🏁 → "Nuevo circuito", das una vuelta y queda guardado para todos.</div>
          </div>
        ) : visibles.map(c => (
          <div key={c.id} onClick={() => setSel(c)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "14px 14px", margin: "0 -12px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 72, height: 72, borderRadius: 14, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {c._pista
                ? (c.logo_url ? <img loading="lazy" src={cldThumb(c.logo_url, 160)} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span style={{ fontSize: 30 }}>🏁</span>)
                : <MiniTrazado polyline={c.polyline} tipo={c.tipo} size={72} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.nombre}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                {c._pista ? "Pista registrada" + (c.ciudad ? ` · ${c.ciudad}` : "") : c.tipo === "offroad" ? "Off-road" : "Pista"}{etiquetaTemporal(c)}{c.largo_m ? ` · ${(c.largo_m / 1000).toFixed(2).replace(".", ",")} km` : ""}{c.dist_km != null ? ` · a ${Number(c.dist_km) < 1 ? "menos de 1" : Math.round(Number(c.dist_km))} km` : ""}
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                {c._pista ? "Sin trazado todavía · tocá para ver la ficha y marcarlo" : c.n_riders ? `${c.n_riders} rider${c.n_riders === 1 ? "" : "s"} · ${c.n_vueltas} vueltas` : "Sin vueltas todavía"}{c.mi_mejor_seg != null ? ` · tu mejor ${fmtSegVuelta(Number(c.mi_mejor_seg))}` : ""}
              </div>
              {c.evento_titulo && <div style={{ fontSize: 11.5, color: C.text, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📅 {c.evento_titulo}</div>}
            </div>
            {c._pista ? <ChevronRight size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} /> : (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: c.record_seg != null ? "#2F7D4F" : C.muted, fontVariantNumeric: "tabular-nums" }}>{c.record_seg != null ? fmtSegVuelta(Number(c.record_seg)) : "—"}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{c.record_username ? `@${c.record_username}` : "récord"}</div>
            </div>
            )}
          </div>
        ))}
      {sel && <FichaCircuito c={sel} userId={userId} onClose={() => setSel(null)} />}
    </div>
  );
};

// En la ficha de un evento: el circuito que tiene adentro (trazado marcado para ese
// evento, o una pista permanente en el predio a menos de 2 km). Si no hay, invita a
// marcarlo dando una vuelta desde el mapa, con el evento ya vinculado. (Carlos, 22-sep:
// "el evento de Moto Beat Arena tiene adentro un circuito".)
const CircuitoDelEvento = ({ evento, puedeEditar = false }) => {
  const [circs, setCircs] = useState(undefined);
  useEffect(() => {
    if (!evento?.id) return;
    let vivo = true;
    (async () => {
      const cols = "id, nombre, tipo, vigencia, hasta, largo_m, evento_id, meta_lat, meta_lng";
      let lista = [];
      try {
        const { data } = await supabase.from("circuitos").select(cols).eq("evento_id", evento.id).eq("publico", true);
        lista = data || [];
      } catch (e) { /* sin columna evento_id todavía */ }
      if (!lista.length && evento.lat != null && evento.lng != null) {
        try {
          const la = Number(evento.lat), lo = Number(evento.lng);
          const { data } = await supabase.from("circuitos").select(cols).eq("publico", true)
            .gte("meta_lat", la - 0.02).lte("meta_lat", la + 0.02).gte("meta_lng", lo - 0.025).lte("meta_lng", lo + 0.025).limit(10);
          // Solo circuitos del predio sin evento propio: el trazado de OTRO evento en el
          // mismo lugar aparecía como si fuera de este (Moto Beat, 08-oct)
          lista = (data || []).filter(c => !c.evento_id && haversineKm({ lat: la, lng: lo }, { lat: Number(c.meta_lat), lng: Number(c.meta_lng) }) <= 2 && !circuitoVencido(c)).map(c => ({ ...c, _enPredio: true }));
        } catch (e) { /* */ }
      }
      if (vivo) setCircs(lista);
    })();
    return () => { vivo = false; };
  }, [evento?.id, evento?.lat, evento?.lng]);
  const marcar = () => {
    window.olbosPendingCircuitoNuevo = true;
    window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { eventoId: evento.id, eventoTitulo: evento.titulo || evento.title || "" } }));
  };
  if (circs === undefined) return null;
  return (
    <div style={{ marginTop: 10 }}>
      {circs.map(c => (
        <div key={c.id} onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-circuito", { detail: { id: c.id } }))} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 14, background: C.accent, color: "#F9F5F2", cursor: "pointer", marginBottom: 8 }}>
          <span style={{ fontSize: 22 }}>🏁</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: "#c9c3ba", textTransform: "uppercase", letterSpacing: 0.5 }}>{c._enPredio ? "Circuito en el predio" : "Circuito del evento"}</div>
            <div style={{ fontSize: 14.5, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.nombre}</div>
            <div style={{ fontSize: 11.5, color: "#c9c3ba" }}>{c.tipo === "offroad" ? "Off-road" : "Pista"}{c.largo_m ? ` · ${(c.largo_m / 1000).toFixed(2).replace(".", ",")} km` : ""}{etiquetaTemporal(c)} · vueltas, récord y ranking</div>
          </div>
          <ChevronRight size={16} color="#c9c3ba" />
        </div>
      ))}
      {/* Marcar el trazado como circuito DEL EVENTO: solo quien lo organiza (cualquiera
          podía, y así quedó "Prueba circuito" en la rodada de Junín) */}
      {circs.length === 0 && puedeEditar && (
        <div onClick={marcar} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 12, background: C.surface, border: `1px dashed ${C.border}`, cursor: "pointer" }}>
          <span style={{ fontSize: 20 }}>🏁</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>¿El evento tiene circuito? Marcá el trazado</div>
            <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4 }}>Si estás en el predio: das una vuelta completa y queda como circuito del evento, con cuenta de vueltas y ranking para todos los que corran ahí.</div>
          </div>
          <ChevronRight size={16} color={C.muted} />
        </div>
      )}
    </div>
  );
};

// ─── PLACA DE LA SESIÓN EN CIRCUITO ──────────────────────────────────────────
// La misma placa que Carlos mandó al grupo de Roque Pérez, generada en el
// teléfono con canvas: circuito coloreado por velocidad con las curvas y la
// largada, los números grandes, el gráfico de vueltas y la tabla de curvas (o de
// saltos en off-road). Se comparte como imagen o se publica en el feed.
const generarPlacaCircuito = async ({ circuito, sesion, traza, username }) => {
  try { await document.fonts.load("900 60px Archivo"); await document.fonts.load("700 30px Archivo"); } catch (e) { /* sale con la fuente del sistema */ }
  const pts = Array.isArray(traza?.puntos) ? traza.puntos : [];
  const vueltas = sesion?.vueltas || [];
  const mejor = vueltas.reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
  const esOff = circuito?.tipo === "offroad";
  const curvas = mejor ? (mejor.curvas && mejor.curvas.length ? mejor.curvas : (pts.length ? analizarCurvas(pts, mejor.ini, mejor.fin) : [])) : [];
  const saltosTot = vueltas.reduce((a, v) => a + ((v.saltos || []).length), 0);
  const curvasTot = vueltas.reduce((a, v) => a + ((v.curvas || []).length), 0);
  const kmTot = Number(traza?.distancia_km || sesion?.km || vueltas.reduce((a, v) => a + (v.km || 0), 0));
  const minTot = Math.round((Number(traza?.duracion_seg) || vueltas.reduce((a, v) => a + v.seg, 0)) / 60);
  const vmaxTot = Math.max(0, ...vueltas.map(v => v.vmax || 0));
  const filasTabla = esOff ? Math.min(8, (mejor?.saltos || []).length) : Math.min(8, curvas.length);
  const W = 1080, H = 1080 + (filasTabla ? 120 + filasTabla * 54 : 0);
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const CREMA = "#F9F5F2", INK = "#111111", MUTED = "#8A857F", BORDE = "#E7E1DA", VERDE = "#2F7D4F";
  const rr = (x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); };
  const tarjeta = (x, y, w, h) => { ctx.fillStyle = "#fff"; rr(x, y, w, h, 22); ctx.fill(); ctx.strokeStyle = BORDE; ctx.lineWidth = 2; ctx.stroke(); };
  const txt = (s, x, y, font, color, align = "left") => { ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align; ctx.fillText(String(s), x, y); };
  const F = (peso, px) => `${peso} ${px}px Archivo, system-ui, sans-serif`;
  ctx.fillStyle = CREMA; ctx.fillRect(0, 0, W, H);
  // cabecera
  ctx.fillStyle = INK; rr(56, 56, W - 112, 200, 28); ctx.fill();
  txt("OLBOS®", 100, 118, F(900, 26), CREMA);
  const tit = String(circuito?.nombre || "Circuito");
  txt(tit.length > 26 ? tit.slice(0, 25) + "…" : tit, 100, 178, F(800, 46), CREMA);
  const fecha = traza?.iniciada_en ? new Date(traza.iniciada_en).toLocaleDateString("es-AR", { day: "numeric", month: "long" }) : "";
  txt(`${username ? "@" + username : "Sesión"}${fecha ? " · " + fecha : ""}`, 100, 222, F(500, 22), "#C9C2BA");
  // mapa de la mejor vuelta
  const mx = 56, my = 282, mw = 440, mh = 380;
  tarjeta(mx, my, mw, mh);
  if (mejor && pts.length > mejor.fin) {
    const tramo = pts.slice(mejor.ini, mejor.fin + 1);
    const lats = tramo.map(p => p.lat), lngs = tramo.map(p => p.lng);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
    const w = Math.max(1e-6, (maxLng - minLng) * kx), h = Math.max(1e-6, maxLat - minLat);
    const pad = 44, esc = Math.min((mw - 2 * pad) / w, (mh - 2 * pad - 30) / h);
    const X = (p) => mx + pad + ((p.lng - minLng) * kx) * esc + ((mw - 2 * pad) - w * esc) / 2;
    const Y = (p) => my + mh - pad - 30 - (p.lat - minLat) * esc - ((mh - 2 * pad - 30) - h * esc) / 2;
    const vels = tramo.map((_, k) => velKmhEn(pts, mejor.ini + k));
    const vmin = Math.min(...vels), vmax = Math.max(...vels);
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = "rgba(17,17,17,.12)"; ctx.lineWidth = 22; ctx.beginPath();
    tramo.forEach((p, k) => { k === 0 ? ctx.moveTo(X(p), Y(p)) : ctx.lineTo(X(p), Y(p)); }); ctx.stroke();
    for (let k = 1; k < tramo.length; k++) { ctx.strokeStyle = colorVel((vels[k - 1] + vels[k]) / 2, vmin, vmax); ctx.lineWidth = 11; ctx.beginPath(); ctx.moveTo(X(tramo[k - 1]), Y(tramo[k - 1])); ctx.lineTo(X(tramo[k]), Y(tramo[k])); ctx.stroke(); }
    if (circuito?.meta) { const a = tramo[0], b = tramo[1] || tramo[0]; const ang = Math.atan2(Y(b) - Y(a), X(b) - X(a)) + Math.PI / 2; ctx.save(); ctx.translate(X(circuito.meta), Y(circuito.meta)); ctx.rotate(ang); ctx.fillStyle = INK; ctx.fillRect(-4, -16, 8, 32); ctx.restore(); txt("LARGADA", X(circuito.meta) + 16, Y(circuito.meta) - 8, F(800, 13), INK); }
    (esOff ? [] : curvas).forEach(c => { const p = puntoDeCurva(pts, mejor.ini, c); if (!p) return; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(X(p), Y(p), 13, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke(); txt(c.n, X(p), Y(p) + 5, F(900, 13), INK, "center"); });
    (mejor.saltos || []).filter(s => s.lat && s.lng).forEach(s => { ctx.fillStyle = "#8A6D3B"; ctx.beginPath(); ctx.arc(X(s), Y(s), 12, 0, Math.PI * 2); ctx.fill(); txt(s.t_aire.toFixed(1), X(s), Y(s) + 4, F(800, 11), "#fff", "center"); });
    txt(`Mejor vuelta (${mejor.n}) · color = velocidad · verde lento, rojo rápido`, mx + 16, my + mh - 14, F(500, 13), MUTED);
  }
  // seis números
  const tiles = [
    [String(vueltas.length), "Vueltas", circuito?.largo_m ? `${(circuito.largo_m / 1000).toFixed(2).replace(".", ",")} km cada una` : ""],
    [mejor ? fmtSegVuelta(mejor.seg).replace(/\.\d$/, "") : "—", "Mejor vuelta", mejor ? `la ${mejor.n} · ${mejor.vprom} km/h prom.` : "", VERDE],
    esOff ? [String(saltosTot), "Saltos", saltosTot && vueltas.length ? `${(saltosTot / vueltas.length).toFixed(1)} por vuelta` : ""] : [String(curvasTot || "—"), "Curvas tomadas", curvas.length ? `${curvas.length} por vuelta` : ""],
    [kmTot ? kmTot.toFixed(1).replace(".", ",") : "—", "Kilómetros", "en pista"],
    [String(minTot || "—"), "Minutos", "de rodaje"],
    [String(vmaxTot || "—"), "Vmáx km/h", ""],
  ];
  tiles.forEach(([v, l, s, color], i) => {
    const col = i % 3, fila = Math.floor(i / 3);
    const tx = 512 + col * 172, ty = 282 + fila * 196, tw = 168, th = 184;
    tarjeta(tx, ty, tw, th);
    txt(v, tx + 16, ty + 82, F(900, v.length > 4 ? 36 : 44), color || INK);
    txt(l, tx + 16, ty + 118, F(700, 16), INK);
    if (s) txt(s.length > 22 ? s.slice(0, 21) + "…" : s, tx + 16, ty + 142, F(500, 13), MUTED);
  });
  // gráfico de vueltas
  const gy = 700;
  txt(`Tiempo por vuelta${mejor && vueltas.length > 1 ? ` — de ${fmtSegVuelta(Math.max(...vueltas.map(v => v.seg))).replace(/\.\d$/, "")} a ${fmtSegVuelta(mejor.seg).replace(/\.\d$/, "")}` : ""}`, 60, gy, F(800, 26), INK);
  tarjeta(60, gy + 20, W - 120, 300);
  if (vueltas.length) {
    const segs = vueltas.map(v => v.seg), best = Math.min(...segs), worst = Math.max(...segs);
    const n = segs.length, gap = 8, bw = Math.max(6, ((W - 120 - 52) - gap * (n - 1)) / n);
    segs.forEach((s, i) => {
      const hh = 30 + (worst > best ? (s - best) / (worst - best) : 0) * 150;
      const bx = 60 + 26 + i * (bw + gap), by = gy + 20 + 300 - 60 - hh;
      ctx.fillStyle = s === best ? VERDE : INK; rr(bx, by, bw, hh, Math.min(8, bw / 2)); ctx.fill();
      if (n <= 26) { txt(fmtSegVuelta(s).replace(/\.\d$/, ""), bx + bw / 2, by - 8, F(800, n > 18 ? 11 : 13), INK, "center"); txt(String(i + 1), bx + bw / 2, gy + 20 + 300 - 30, F(600, 12), MUTED, "center"); }
    });
  }
  // tabla: curvas (pista) o saltos (off-road)
  if (filasTabla) {
    const ty = gy + 360;
    txt(esOff ? `Saltos de la mejor vuelta` : `Las ${curvas.length} curvas de la mejor vuelta`, 60, ty, F(800, 26), INK);
    tarjeta(60, ty + 20, W - 120, 46 + filasTabla * 54);
    ctx.fillStyle = "#F1ECE6"; rr(62, ty + 22, W - 124, 44, 20); ctx.fill(); ctx.fillStyle = "#F1ECE6"; ctx.fillRect(62, ty + 44, W - 124, 22);
    const cols = esOff ? [["#", 80], ["EN EL AIRE", 200], ["ALTURA", 380], ["DISTANCIA", 560], ["DESPEGUE", 740], ["ATERRIZAJE", 900]] : [["CURVA", 80], ["LADO", 200], ["GIRO", 340], ["RADIO", 450], ["ENTRA → MÍN → SALE", 580], ["INCLINACIÓN", 880]];
    cols.forEach(([c, x]) => txt(c, x, ty + 50, F(800, 13), MUTED));
    const filas = esOff ? (mejor.saltos || []).slice(0, 8) : curvas.slice(0, 8);
    filas.forEach((c, i) => {
      const y = ty + 66 + i * 54 + 34;
      if (i) { ctx.strokeStyle = BORDE; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(62, y - 36); ctx.lineTo(W - 62, y - 36); ctx.stroke(); }
      if (esOff) { [[i + 1, 80, 800], [`${c.t_aire.toFixed(2)} s`, 200, 800], [`${c.altura_m.toFixed(1)} m`, 380], [`${c.dist_m} m`, 560], [`${c.v_despegue} km/h`, 740], [`${c.g_aterrizaje} g`, 900]].forEach(([v, x, p]) => txt(v, x, y, F(p || 500, 19), INK)); }
      else { [[c.n, 80, 800], [c.lado === "D" ? "derecha" : "izquierda", 200], [`${c.ang}°`, 340], [`${c.radio_m} m`, 450], [`${c.v_ent} → ${c.v_min} → ${c.v_sal} km/h`, 580], [`${c.incl_med != null ? c.incl_med + "° medida" : c.incl_est + "° est."}`, 880]].forEach(([v, x, p]) => txt(v, x, y, F(p || 500, 19), INK)); }
    });
  }
  txt("Grabado con el GPS del teléfono en Olbos · olbos.ai", W / 2, H - 28, F(500, 15), MUTED, "center");
  return await new Promise(res => cv.toBlob(res, "image/png", 0.95));
};
const compartirPlacaCircuito = async (datos) => {
  try {
    const blob = await generarPlacaCircuito(datos);
    if (!blob) throw new Error("sin blob");
    const file = new File([blob], "circuito-olbos.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: datos.circuito?.nombre || "Mi sesión en circuito" }); return; }
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "circuito-olbos.png";
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (e) { if (e?.name !== "AbortError") alert("No se pudo generar la placa. Probá de nuevo."); }
};
// Al feed: la placa se sube a Cloudinary y sale como post de recorrido
const subirPlacaCircuito = async (datos) => {
  const blob = await generarPlacaCircuito(datos);
  if (!blob) throw new Error("sin placa");
  const fd = new FormData(); fd.append("file", blob, "circuito.png"); fd.append("upload_preset", "olbos_uploads");
  const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
  const j = await res.json();
  if (!j.secure_url) throw new Error("no se pudo subir la imagen");
  return j.secure_url;
};
const publicarPlacaCircuito = async (datos, userId) => {
  const j = { secure_url: await subirPlacaCircuito(datos) };
  const vueltas = datos.sesion?.vueltas || [];
  const mejor = vueltas.reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
  const saltos = vueltas.reduce((a, v) => a + ((v.saltos || []).length), 0);
  const curvas = vueltas.reduce((a, v) => a + ((v.curvas || []).length), 0);
  const texto = `🏁 ${datos.circuito?.nombre || "Circuito"}\n\n📍 ${vueltas.length} vuelta${vueltas.length === 1 ? "" : "s"}${mejor ? ` · ⏱ mejor ${fmtSegVuelta(mejor.seg)}` : ""}${saltos ? ` · 🦘 ${saltos} saltos` : curvas ? ` · ↪ ${curvas} curvas` : ""}`;
  const { error } = await supabase.from("posts").insert({
    user_id: userId, username: datos.username || "rider", texto, categoria: "ruta",
    media_url: j.secure_url, media_type: "image", media_urls: JSON.stringify([{ url: j.secure_url, type: "image" }]),
    tipo: "recorrido", traza_id: datos.traza?.id || null, compartido_feed_general: true, likes: 0,
  });
  if (error) throw error;
  return j.secure_url;
};

const ResumenCircuito = ({ traza, sesion, circuito, onClose }) => {
  const [tab, setTab] = useState("vueltas");
  const [ranking, setRanking] = useState(null);
  const [vueltaSel, setVueltaSel] = useState(sesion?.mejor_n || 1);
  const [placa, setPlaca] = useState({ username: null, esMia: false, busy: "", publicada: false });
  const [enMuro, setEnMuro] = useState(!!sesion?.publica);
  const placaUrlRef = useRef(sesion?.placa_url || null);
  const [vueltasM, setVueltasM] = useState(null); // vueltas re-medidas en las curvas fijas del circuito
  useBackGuard(true, onClose);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [{ data: { user } }, { data: perfil }] = await Promise.all([
          supabase.auth.getUser(),
          traza?.user_id ? supabase.from("perfiles").select("username").eq("id", traza.user_id).maybeSingle() : Promise.resolve({ data: null }),
        ]);
        if (vivo) setPlaca(p => ({ ...p, username: perfil?.username || null, esMia: !!user && !!traza?.user_id && user.id === traza.user_id }));
      } catch (e) { /* sin sesión: la placa sale igual, sin publicar */ }
    })();
    return () => { vivo = false; };
  }, [traza?.user_id]);
  // Se arma recién al tocar el botón: vueltas se declara más abajo (TDZ en render si fuera un objeto acá)
  const datosPlaca = () => ({ circuito, sesion: { ...sesion, vueltas }, traza, username: placa.username });
  const compartirPlaca = async () => {
    if (placa.busy) return;
    setPlaca(p => ({ ...p, busy: "compartir" }));
    await compartirPlacaCircuito(datosPlaca());
    setPlaca(p => ({ ...p, busy: "" }));
  };
  const publicarPlaca = async () => {
    if (placa.busy || placa.publicada) return;
    setPlaca(p => ({ ...p, busy: "publicar" }));
    try {
      const url = await publicarPlacaCircuito(datosPlaca(), traza.user_id);
      placaUrlRef.current = url;
      if (traza?.id) supabase.from("sesiones_circuito").update({ placa_url: url }).eq("traza_id", traza.id).then(() => {}, () => {});
      setPlaca(p => ({ ...p, busy: "", publicada: true }));
    }
    catch (e) { setPlaca(p => ({ ...p, busy: "" })); alert("No se pudo publicar: " + (e.message || e)); }
  };
  // Muro del circuito: la placa queda visible en la ficha del circuito para los demás riders
  const alternarMuro = async () => {
    if (placa.busy || !traza?.id) return;
    setPlaca(p => ({ ...p, busy: "muro" }));
    try {
      if (enMuro) {
        const { error } = await supabase.from("sesiones_circuito").update({ publica: false }).eq("traza_id", traza.id);
        if (error) throw error;
        setEnMuro(false);
      } else {
        const url = placaUrlRef.current || await subirPlacaCircuito(datosPlaca());
        placaUrlRef.current = url;
        const { error } = await supabase.from("sesiones_circuito").update({ publica: true, placa_url: url }).eq("traza_id", traza.id);
        if (error) throw error;
        setEnMuro(true);
      }
    } catch (e) { alert("No se pudo actualizar el muro: " + (e.message || e)); }
    setPlaca(p => ({ ...p, busy: "" }));
  };
  const pts = Array.isArray(traza?.puntos) ? traza.puntos : [];
  const vueltas = vueltasM || sesion?.vueltas || [];
  const v = vueltas.find(x => x.n === vueltaSel) || vueltas[0];
  useEffect(() => {
    if (!circuito?.id || circuito.tipo === "offroad" || !pts.length) return;
    const base = sesion?.vueltas || [];
    if (!base.length) return;
    let vivo = true;
    (async () => {
      let curvasCirc = Array.isArray(circuito.curvas) && circuito.curvas.length ? circuito.curvas : null;
      if (!curvasCirc) {
        try { const { data } = await supabase.from("circuitos").select("curvas").eq("id", circuito.id).maybeSingle(); if (data && Array.isArray(data.curvas) && data.curvas.length) curvasCirc = data.curvas; } catch (e) { /* */ }
      }
      const yaFijas = !!((base[0].curvas || [])[0] || {}).fijas;
      if (yaFijas && curvasCirc) return;
      if (!curvasCirc) {
        const mejorV = base.reduce((m, x) => (m == null || x.seg < m.seg) ? x : m, null);
        if (!mejorV || mejorV.ini == null) return;
        curvasCirc = curvasDeReferencia(pts, mejorV.ini, mejorV.fin);
        if (!curvasCirc.length) return;
        supabase.from("circuitos").update({ curvas: curvasCirc }).eq("id", circuito.id).then(() => {}, () => {});
      }
      const nuevas = base.map(x => x.ini == null ? x : { ...x, curvas: medirCurvasEnVuelta(pts, x.ini, x.fin, curvasCirc) });
      if (!vivo) return;
      setVueltasM(nuevas);
      // Si la sesión es mía, queda guardada así (migración silenciosa de las sesiones viejas)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && traza?.id && user.id === traza.user_id) supabase.from("sesiones_circuito").update({ vueltas: nuevas }).eq("traza_id", traza.id).then(() => {}, () => {});
      } catch (e) { /* sin sesión */ }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circuito?.id, traza?.id]);
  const curvas = v ? (v.curvas && v.curvas.length ? v.curvas : (pts.length ? analizarCurvas(pts, v.ini, v.fin) : [])) : [];
  useEffect(() => {
    if (tab !== "ranking" || ranking || !circuito?.id) return;
    supabase.rpc("ranking_circuito", { p_circuito_id: circuito.id }).then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setRanking(Array.isArray(d) ? d : []);
    }, () => setRanking([]));
  }, [tab, ranking, circuito?.id]);
  const mejor = sesion?.mejor_seg;
  const cell = { padding: "7px 8px", borderBottom: `1px solid ${C.border}`, fontSize: 12.5, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
  const th = { ...cell, fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 };
  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 1300, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "var(--header-pt) 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, background: C.card }}>
        <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={16} strokeWidth={2.5} color={C.text} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🏁 {circuito?.nombre || "Circuito"}</div>
          <div style={{ fontSize: 11.5, color: C.muted }}>{traza?.iniciada_en ? new Date(traza.iniciada_en).toLocaleDateString("es-AR", { day: "numeric", month: "long" }) : ""} · {vueltas.length} vuelta{vueltas.length === 1 ? "" : "s"}{mejor != null ? ` · mejor ${fmtSegVuelta(mejor)}` : ""}</div>
        </div>
      </div>
      <div style={{ display: "flex", margin: "12px 16px 0", background: C.surface, borderRadius: 12, padding: 3, border: `1px solid ${C.border}` }}>
        {[["vueltas", "Vueltas"], ["curvas", "Curvas"], ["saltos", "Saltos"], ["ranking", "Ranking"]]
          .filter(([t]) => (t !== "curvas" || circuito?.tipo !== "offroad") && (t !== "saltos" || vueltas.some(x => (x.saltos || []).length))).map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "9px 6px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 800, background: tab === t ? C.card : "transparent", color: tab === t ? C.text : C.muted }}>{l}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {tab === "vueltas" && (
          <>
            {v && pts.length > 0 && <div style={{ marginBottom: 12 }}><MapaVueltaSVG pts={pts} ini={v.ini} fin={v.fin} meta={circuito?.meta} curvas={circuito?.tipo === "offroad" ? [] : curvas} saltos={v.saltos || []} /></div>}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[["Mejor vuelta", mejor != null ? fmtSegVuelta(mejor) : "—"], ["Vueltas", vueltas.length], ["Curvas tomadas", vueltas.reduce((acc, x) => acc + ((x.curvas || []).length), 0) || "—"], ["Vmáx", (Math.max(0, ...vueltas.map(x => x.vmax || 0)) || "—") + " km/h"]].map(([l, val]) => (
                <div key={l} style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "9px 10px" }}>
                  <div style={{ fontSize: 17, fontWeight: 900, color: C.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{val}</div>
                  <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>#</th><th style={th}>Tiempo</th><th style={th}>Delta</th><th style={th}>Vmáx</th><th style={th}>Prom</th>{vueltas.some(x => x.incl_max) && <th style={th}>Incl. máx</th>}</tr></thead>
                <tbody>
                  {vueltas.map(x => (
                    <tr key={x.n} onClick={() => setVueltaSel(x.n)} style={{ cursor: "pointer", background: x.n === vueltaSel ? C.surface : "transparent" }}>
                      <td style={{ ...cell, fontWeight: 800 }}>{x.n}{x.seg === mejor ? " ★" : ""}</td>
                      <td style={{ ...cell, fontWeight: x.seg === mejor ? 900 : 600, color: x.seg === mejor ? "#2F7D4F" : C.text }}>{fmtSegVuelta(x.seg)}</td>
                      <td style={{ ...cell, color: C.muted }}>{mejor != null && x.seg !== mejor ? `+${(x.seg - mejor).toFixed(1)}` : ""}</td>
                      <td style={cell}>{x.vmax} km/h</td>
                      <td style={cell}>{x.vprom} km/h</td>
                      {vueltas.some(y => y.incl_max) && <td style={cell}>{x.incl_max != null ? `${x.incl_max}°` : "—"}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>Tocá una vuelta para verla en el mapa. ★ = tu mejor vuelta de la sesión.</div>
          </>
        )}
        {tab === "curvas" && (
          <>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>Curvas de la vuelta <strong style={{ color: C.text }}>{v?.n}</strong>{v?.seg != null ? ` (${fmtSegVuelta(v.seg)})` : ""}. Velocidades en km/h; la inclinación estimada sale de la velocidad y el radio (tan θ = v²/g·r){curvas.some(c => c.incl_med) ? "; la medida, del giroscopio del teléfono" : ""}.</div>
            {curvas.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>No se detectaron curvas en esta vuelta.</div> : (
              <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={th}>#</th><th style={th}>Lado</th><th style={th}>Giro</th><th style={th}>Radio</th><th style={th}>Entra</th><th style={th}>Mín</th><th style={th}>Sale</th><th style={th}>Incl. est.</th>{curvas.some(c => c.incl_med) && <th style={th}>Incl. med.</th>}</tr></thead>
                  <tbody>
                    {curvas.map(c => (
                      <tr key={c.n}>
                        <td style={{ ...cell, fontWeight: 800 }}>{c.n}</td>
                        <td style={cell}>{c.lado === "D" ? "↱ der" : "↰ izq"}</td>
                        <td style={cell}>{c.ang}°</td>
                        <td style={cell}>{c.radio_m} m</td>
                        <td style={cell}>{c.v_ent}</td>
                        <td style={{ ...cell, fontWeight: 800 }}>{c.v_min}</td>
                        <td style={cell}>{c.v_sal}</td>
                        <td style={cell}>{c.incl_est}°</td>
                        {curvas.some(x => x.incl_med) && <td style={cell}>{c.incl_med != null ? `${c.incl_med}°` : "—"}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {tab === "saltos" && (() => {
          const todos = vueltas.flatMap(x => (x.saltos || []).map(s => ({ ...s, vuelta: x.n })));
          const masLargo = todos.reduce((m, s) => (!m || s.t_aire > m.t_aire) ? s : m, null);
          const masAlto = todos.reduce((m, s) => (!m || s.altura_m > m.altura_m) ? s : m, null);
          const aireTotal = todos.reduce((a, s) => a + s.t_aire, 0);
          const deVuelta = v ? (v.saltos || []) : [];
          return (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                {[["Saltos", todos.length], ["Más largo", masLargo ? `${masLargo.t_aire.toFixed(1)} s` : "—"], ["Más alto", masAlto ? `${masAlto.altura_m.toFixed(1)} m` : "—"], ["En el aire", `${aireTotal.toFixed(1)} s`]].map(([l, val]) => (
                  <div key={l} style={{ flex: 1, minWidth: 70, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "9px 10px" }}>
                    <div style={{ fontSize: 17, fontWeight: 900, color: C.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{val}</div>
                    <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>Saltos de la vuelta <strong style={{ color: C.text }}>{v?.n}</strong>. El tiempo en el aire sale del acelerómetro (la gravedad desaparece mientras volás); la altura por física, h = g·t²/8; la distancia con la velocidad del GPS al despegar.</div>
              {deVuelta.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Sin saltos en esta vuelta.</div> : (
                <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr><th style={th}>#</th><th style={th}>En el aire</th><th style={th}>Altura</th><th style={th}>Distancia</th><th style={th}>Despegue</th><th style={th}>Aterrizaje</th></tr></thead>
                    <tbody>
                      {deVuelta.map((s, i) => (
                        <tr key={i}>
                          <td style={{ ...cell, fontWeight: 800 }}>{i + 1}</td>
                          <td style={{ ...cell, fontWeight: 800 }}>{s.t_aire.toFixed(2)} s</td>
                          <td style={cell}>{s.altura_m.toFixed(1)} m</td>
                          <td style={cell}>{s.dist_m} m</td>
                          <td style={cell}>{s.v_despegue} km/h</td>
                          <td style={cell}>{s.g_aterrizaje} g</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: 14, fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Por vuelta</div>
              <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={th}>Vuelta</th><th style={th}>Tiempo</th><th style={th}>Saltos</th><th style={th}>Aire total</th><th style={th}>Más largo</th></tr></thead>
                  <tbody>
                    {vueltas.map(x => { const ss = x.saltos || []; const ml = ss.reduce((m, s) => Math.max(m, s.t_aire), 0); return (
                      <tr key={x.n} onClick={() => setVueltaSel(x.n)} style={{ cursor: "pointer", background: x.n === vueltaSel ? C.surface : "transparent" }}>
                        <td style={{ ...cell, fontWeight: 800 }}>{x.n}</td><td style={cell}>{fmtSegVuelta(x.seg)}</td><td style={cell}>{ss.length}</td><td style={cell}>{ss.reduce((a, s) => a + s.t_aire, 0).toFixed(1)} s</td><td style={cell}>{ml ? ml.toFixed(1) + " s" : "—"}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            </>
          );
        })()}
        {tab === "ranking" && (
          <>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>Mejor vuelta de cada rider de Olbos en {circuito?.nombre}.</div>
            {ranking === null ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Cargando…</div>
              : ranking.length === 0 ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>Todavía no hay vueltas registradas.</div>
              : ranking.map((r, i) => (
                <div key={r.username + i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ width: 26, fontSize: 14, fontWeight: 900, color: i < 3 ? "#8A6D3B" : C.muted, textAlign: "center" }}>{i + 1}</div>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, overflow: "hidden", flexShrink: 0 }}>
                    {r.avatar_url ? <img loading="lazy" src={cldThumb(r.avatar_url, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>@{r.username}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{r.fecha ? new Date(r.fecha).toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : ""}{r.n_vueltas ? ` · ${r.n_vueltas} vueltas` : ""}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmtSegVuelta(Number(r.mejor_seg))}</div>
                </div>
              ))}
          </>
        )}
      </div>
      {vueltas.length > 0 && (
        <div style={{ padding: "10px 16px calc(var(--sab, 0px) + 12px)", borderTop: `1px solid ${C.border}`, background: C.card }}>
        {placa.esMia && circuito?.id && (
          <button onClick={alternarMuro} disabled={!!placa.busy} style={{ width: "100%", marginBottom: 8, padding: "10px", borderRadius: 12, border: `1px solid ${enMuro ? "#2F7D4F" : C.border}`, cursor: "pointer", fontSize: 12.5, fontWeight: 800, background: enMuro ? "rgba(47,125,79,.08)" : C.surface, color: enMuro ? "#2F7D4F" : C.text, opacity: placa.busy ? .6 : 1 }}>
            {placa.busy === "muro" ? "Actualizando…" : enMuro ? "✓ Tu placa está en el muro del circuito · tocá para sacarla" : "🏁 Mostrar mi placa en el muro del circuito"}
          </button>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={compartirPlaca} disabled={!!placa.busy} style={{ flex: 1, padding: "12px 10px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 800, background: C.accent, color: "#F9F5F2", opacity: placa.busy ? .6 : 1 }}>
            {placa.busy === "compartir" ? "Armando la placa…" : "Compartir placa"}
          </button>
          {placa.esMia && (
            <button onClick={publicarPlaca} disabled={!!placa.busy || placa.publicada} style={{ flex: 1, padding: "12px 10px", borderRadius: 12, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 13.5, fontWeight: 800, background: placa.publicada ? C.surface : C.card, color: placa.publicada ? "#2F7D4F" : C.text, opacity: placa.busy ? .6 : 1 }}>
              {placa.publicada ? "Publicada en el feed ✓" : placa.busy === "publicar" ? "Publicando…" : "Publicar en el feed"}
            </button>
          )}
        </div>
        </div>
      )}
    </div>
  );
};

// ─── Navegador v2 (MapLibre): el mapa del grabador para todos, con interruptor remoto ──
// Desde el 10-oct (decisión de Carlos, víspera de Originarios) el lienzo vectorial de
// src/navegador-v2 es el DEFAULT: tapa al mapa de Google siempre que el mapa está abierto,
// con o sin destino. Google sigue montado abajo (el resto del grabador depende de
// mapInstanceRef) y vuelve solo si el v2 no arranca. Se carga con import() lazy:
// maplibre-gl no engorda el bundle de nadie. Plan: docs/NAVEGADOR_V2.md.
//
// La decisión por teléfono:  navV2Activo = !navV2Error && (override local ?? remoto)
// - remoto: clave "nav_v2" de public/version.json (true si falta). Carlos la pone en
//   false y en minutos todos vuelven a Google sin deploy ni bundle: se relee al abrir
//   el mapa y cada hora con el aviso de versión, y queda cacheada en localStorage.
// - override local: localStorage.olbos_nav_v2 = "1" fuerza el v2, "0" fuerza Google
//   (switch "Mapa anterior" de Cuenta, ?nav=0, o dos fallos del v2 en 24 h en ese
//   teléfono); ausente = lo que diga el remoto. ?nav=2 / ?nav=0 lo dejan puesto,
//   ?nav=auto lo saca.
// - Fallo: si MapaNav no arranca (onError) esa sesión sigue en Google y el fallo va a
//   errores_app; al segundo fallo en 24 h el teléfono queda en Google ("0") con un aviso
//   de una línea, hasta que el rider lo cambie en Cuenta.
const MapaNav = React.lazy(() => import("./navegador-v2/MapaNav"));
const NAV_V2_KEY = "olbos_nav_v2";
const NAV_V2_REMOTO_KEY = "olbos_nav_v2_remoto";
const NAV_V2_FALLO_KEY = "olbos_nav_v2_fallo";
const NAV_V2_FALLO_HASTA_KEY = "olbos_nav_v2_fallo_hasta";
// El navegador nuevo, default para TODOS (Carlos, 30-oct): el pase a Google por dos fallos
// dura 7 días y después se vuelve a probar el v2. Los teléfonos que quedaron en Google por
// los fallos del 11-oct (sin fecha de vencimiento) vuelven al v2 ya. El "Mapa anterior"
// elegido a mano en Cuenta (sin fallos registrados) se respeta.
try {
  const forzadoGoogle = localStorage.getItem(NAV_V2_KEY) === "0";
  const hasta = Number(localStorage.getItem(NAV_V2_FALLO_HASTA_KEY)) || 0;
  let fallos = [];
  try { fallos = JSON.parse(localStorage.getItem(NAV_V2_FALLO_KEY) || "[]"); } catch (e) { fallos = []; }
  const porFallo = Array.isArray(fallos) && fallos.length > 0;
  if (forzadoGoogle && ((hasta && Date.now() > hasta) || (!hasta && porFallo))) {
    localStorage.removeItem(NAV_V2_KEY);
    localStorage.removeItem(NAV_V2_FALLO_KEY);
    localStorage.removeItem(NAV_V2_FALLO_HASTA_KEY);
  }
} catch (e) { /* */ }
try {
  const navQ = new URLSearchParams(window.location.search).get("nav");
  if (navQ === "2") localStorage.setItem(NAV_V2_KEY, "1");
  else if (navQ === "0") localStorage.setItem(NAV_V2_KEY, "0");
  else if (navQ === "auto") localStorage.removeItem(NAV_V2_KEY);
} catch (e) { /* sin localStorage no hay override */ }
// true = forzado v2, false = forzado Google, null = sin override (manda el remoto)
const navV2Override = () => {
  try { const v = localStorage.getItem(NAV_V2_KEY); return v === "1" ? true : v === "0" ? false : null; } catch (e) { return null; }
};
const navV2RemotoCacheado = () => { try { return localStorage.getItem(NAV_V2_REMOTO_KEY) !== "0"; } catch (e) { return true; } };
const guardarNavV2Remoto = (cfg) => {
  if (!cfg || typeof cfg !== "object") return;
  try { localStorage.setItem(NAV_V2_REMOTO_KEY, cfg.nav_v2 === false ? "0" : "1"); } catch (e) { /* */ }
};
const navV2Preferido = (remoto = navV2RemotoCacheado()) => { const o = navV2Override(); return o != null ? o : remoto; };
// Un fallo del v2: queda registrado (errores_app y localStorage) y, al segundo en 24 h en
// este teléfono, Google pasa a ser el default acá. Devuelve true si eso acaba de pasar.
const registrarFalloNavV2 = (motivo) => {
  reportarError(`nav-v2 fallback: ${motivo || "sin motivo"}`, null, "MapaNav");
  let lista = [];
  try { lista = JSON.parse(localStorage.getItem(NAV_V2_FALLO_KEY) || "[]"); } catch (e) { lista = []; }
  if (!Array.isArray(lista)) lista = [];
  const ahora = Date.now();
  lista = lista.filter(t => typeof t === "number" && ahora - t < 86400000);
  lista.push(ahora);
  try { localStorage.setItem(NAV_V2_FALLO_KEY, JSON.stringify(lista.slice(-5))); } catch (e) { /* */ }
  if (lista.length < 2) return false;
  try { localStorage.setItem(NAV_V2_KEY, "0"); localStorage.setItem(NAV_V2_FALLO_HASTA_KEY, String(ahora + 7 * 86400000)); } catch (e) { /* */ }
  return true;
};
// "hace 12 min" / "hace 2 h" / "hace 3 días", para las etiquetas chicas del mapa
const haceCuantoCorto = (ts) => {
  const t = ts ? Date.parse(ts) : NaN;
  if (!isFinite(t)) return "";
  const m = Math.max(1, Math.round((Date.now() - t) / 60000));
  return m < 60 ? `hace ${m} min` : m < 2880 ? `hace ${Math.round(m / 60)} h` : `hace ${Math.round(m / 1440)} días`;
};

// El glifo ⛰ en Android sale como un triángulo sin relleno (no está en la fuente del
// sistema): en las pastillas de altura del mapa va este SVG chico en su lugar.
const IconoMontana = ({ size = 12, color = "currentColor", style = null }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" aria-hidden="true"
    style={{ display: "inline-block", verticalAlign: -1, flexShrink: 0, ...(style || {}) }}>
    <path d="M2.5 19.5 9 8l4 6.2 2.5-3.4 6 8.7z" />
  </svg>
);

// ── INCLINACIÓN DE LA MOTO, como CHIGEE (27-oct-2026) ──────────────────────────
// Con el teléfono firme en el manubrio, la moto y el celu se inclinan juntos. deviceorientation
// viene fusionado con el giroscopio (no se "endereza" en la curva como el acelerómetro solo).
// Del ángulo de Euler se saca el vector "arriba" en el marco del teléfono y su ángulo en el plano
// de la pantalla (sirve en vertical u horizontal y aunque el soporte lo deje un poco acostado).
// "Calibrar" toma como cero la posición con la moto derecha. Máximas solo andando (más de 15 km/h).
const INCL_CLAVE = "olbos_inclinacion_cero";
const InclinacionMoto = ({ velKmh, alMax = null, abajo = 86 }) => {
  const [ang, setAng] = useState(null);
  const [max, setMax] = useState({ izq: 0, der: 0 });
  const [menu, setMenu] = useState(false);
  const [permiso, setPermiso] = useState(() => !(typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function"));
  const crudoRef = useRef(null);
  const suaveRef = useRef(null);
  const velRef = useRef(0);
  velRef.current = Number(velKmh) || 0;
  const ceroRef = useRef((() => { try { return Number(localStorage.getItem(INCL_CLAVE)) || 0; } catch (e) { return 0; } })());
  useEffect(() => {
    if (!permiso) return;
    const h = (e) => {
      if (e.beta == null || e.gamma == null) return;
      const b = e.beta * Math.PI / 180, g = e.gamma * Math.PI / 180;
      let ux = -Math.sin(g) * Math.cos(b), uy = Math.sin(b);
      // Pantalla girada: el "arriba" de la pantalla no es el del teléfono
      const rot = ((window.screen && window.screen.orientation && window.screen.orientation.angle) || window.orientation || 0) * Math.PI / 180;
      const sx = ux * Math.cos(rot) + uy * Math.sin(rot), sy = -ux * Math.sin(rot) + uy * Math.cos(rot);
      const crudo = Math.atan2(-sx, sy) * 180 / Math.PI;
      crudoRef.current = crudo;
      const a = crudo - ceroRef.current;
      suaveRef.current = suaveRef.current == null ? a : suaveRef.current * 0.7 + a * 0.3;
    };
    window.addEventListener("deviceorientation", h);
    // Se pinta a 8 Hz: suficiente para el ojo, sin re-render en cada evento
    const t = setInterval(() => {
      const v = suaveRef.current;
      if (v == null) return;
      const r = Math.max(-70, Math.min(70, v));
      setAng(Math.round(r));
      if (velRef.current > 15 && Math.abs(r) >= 3) {
        setMax(m => {
          const n = r < 0 ? { ...m, izq: Math.max(m.izq, Math.round(-r)) } : { ...m, der: Math.max(m.der, Math.round(r)) };
          if ((n.izq !== m.izq || n.der !== m.der) && alMax) alMax(n);
          return (n.izq !== m.izq || n.der !== m.der) ? n : m;
        });
      }
    }, 125);
    return () => { window.removeEventListener("deviceorientation", h); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permiso]);
  const pedirPermiso = async () => {
    try { const r = await DeviceOrientationEvent.requestPermission(); if (r === "granted") setPermiso(true); } catch (e) { /* */ }
  };
  const calibrar = () => {
    if (crudoRef.current == null) return;
    ceroRef.current = crudoRef.current;
    suaveRef.current = 0;
    try { localStorage.setItem(INCL_CLAVE, String(crudoRef.current)); } catch (e) { /* */ }
    setMax({ izq: 0, der: 0 });
    registrarUso("inclinacion_calibrada", {});
    setMenu(false);
  };
  // iPhone: hay que pedir permiso a los sensores con un toque
  if (!permiso) return (
    <button onClick={pedirPermiso} style={{ position: "absolute", bottom: abajo, left: 12, zIndex: 6, padding: "6px 10px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>Ver inclinación</button>
  );
  if (ang == null) return null; // sin sensores (computadora): no se muestra
  const lado = ang < -1 ? "I" : ang > 1 ? "D" : "";
  return (
    <>
      <div onClick={() => setMenu(v => !v)} title="Inclinación de la moto (tocá para calibrar)"
        style={{ position: "absolute", bottom: abajo, left: 12, zIndex: 6, width: 64, borderRadius: 14, background: "rgba(13,13,18,.88)", color: "#F9F5F2", padding: "6px 0 5px", display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}>
        <svg width="40" height="22" viewBox="0 0 40 22" aria-hidden="true">
          <path d="M4 20 A16 16 0 0 1 36 20" fill="none" stroke="rgba(249,245,242,.25)" strokeWidth="3" strokeLinecap="round" />
          <line x1="20" y1="20" x2={20 + 15 * Math.sin(ang * Math.PI / 180)} y2={20 - 15 * Math.cos(ang * Math.PI / 180)} stroke="#F9F5F2" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{Math.abs(ang)}°<span style={{ fontSize: 10, marginLeft: 1, opacity: 0.75 }}>{lado}</span></div>
        <div style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.7, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>máx {max.izq}° · {max.der}°</div>
      </div>
      {menu && (
        <div style={{ position: "absolute", bottom: abajo, left: 84, zIndex: 7, background: "#111111", color: "#F9F5F2", borderRadius: 12, padding: "10px 12px", width: 200, boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: "#E7E1DA" }}>Con la moto derecha y el celu firme en el soporte, tocá Calibrar. Las máximas cuentan solo andando.</div>
          <button onClick={calibrar} style={{ width: "100%", marginTop: 8, padding: 8, borderRadius: 8, border: "none", background: "#F9F5F2", color: "#111111", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>Calibrar (moto derecha)</button>
        </div>
      )}
    </>
  );
};

const TrackRecorder = ({ session, onCerrar, grupoIdPreseleccionado, eventoPreseleccionado, minimizado = false, onMinimizar = null, grupoVivo = null, vivoToken = null, grupoEnVivoId = null, viajeEnVivoId = null, navegarA = null, entradaExplicita = false, retomarId = null }) => {
  const userId = session?.user?.id;
  const eventoFinal = eventoPreseleccionado?.id || null; // si se graba el recorrido de una rodada
  const [estado, setEstado] = useState("preparando"); // preparando | lista (en el mapa sin grabar) | grabando | pausada | finalizando
  const [vehiculo, setVehiculo] = useState(vehiculoActual); // moto | auto (se recuerda)
  useEffect(() => {
    const h = (e) => setVehiculo(e.detail === "auto" ? "auto" : "moto");
    window.addEventListener("olbos-vehiculo", h);
    return () => window.removeEventListener("olbos-vehiculo", h);
  }, []);
  const estadoGrabRef = useRef("preparando");
  useEffect(() => {
    estadoGrabRef.current = estado;
    // En una rodada de evento NO se graba hasta la largada: con 4.000 motos en el predio
    // desde las 19:00 eran trazas de una hora parado. El modo lista ya comparte la
    // posición; el overlay avisa cuando pasa a en_curso y recién ahí arranca.
    if (estado === "lista" && enRodada && (!grupoVivo || rodadaEnCursoRef.current)) arrancarSiHayIntencion("rodada");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);
  // Llamada entrante con el mapa abierto (Android): al atender y al cortar, Olbos vuelve al
  // frente solo (plugin nativo OlbosLlamada). Activo mientras está el mapa (lista / grabando /
  // pausada); se apaga al cerrar el grabador.
  const mapaActivoLlamada = estado === "lista" || estado === "grabando" || estado === "pausada";
  useEffect(() => {
    if (!mapaActivoLlamada) return;
    volverDeLlamada.activar();
    return () => volverDeLlamada.desactivar();
  }, [mapaActivoLlamada]);
  // Entrar a una rodada (evento preseleccionado, grupo en vivo o link de rodar) es
  // intención de rodar: se arranca a grabar en cuanto el mapa está listo.
  const enRodada = !!(eventoPreseleccionado || grupoVivo || vivoToken || grupoEnVivoId || viajeEnVivoId);
  const rodadaEnCursoRef = useRef(!!(grupoVivo && grupoVivo.estado === "en_curso"));
  const [puntos, setPuntos] = useState([]);
  const [posActual, setPosActual] = useState(null);
  const posActualRef = useRef(null); // espejo para overlays y para arrancar sin esperar otro fix
  const watchLibreRef = useRef(null); // watch "solo posición" del modo lista
  const estadoRecRef = useRef("preparando");
  const [trazaId, setTrazaId] = useState(null);
  const trazaIdRef = useRef(null);
  useEffect(() => { trazaIdRef.current = trazaId; }, [trazaId]);
  // "Navegar el recorrido" desde el mapa de una rodada (documento de Matías, B1): destino +
  // paradas del recorrido planeado, con las indicaciones del navegador de siempre
  const [navRodada, setNavRodada] = useState(null);
  useEffect(() => {
    const h = (e) => { if (e && e.detail && e.detail.lat != null) setNavRodada({ ...e.detail, ts: Date.now() }); };
    window.addEventListener("olbos-navegar-rodada", h);
    return () => window.removeEventListener("olbos-navegar-rodada", h);
  }, []);
  const [error, setError] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [trazaPrevia, setTrazaPrevia] = useState(null); // recorrido sin terminar (grabando/pausada) detectado al abrir
  // ── Modo circuito ──
  const [circuito, setCircuito] = useState(null);           // {id, nombre, tipo, meta:{lat,lng}, largo_m}
  const circuitoRef = useRef(null);
  useEffect(() => { circuitoRef.current = circuito; configurarGpsCircuito(!!circuito); }, [circuito]);
  const [circuitosCerca, setCircuitosCerca] = useState(null); // los conocidos a menos de 5 km
  const [circuitoDescartado, setCircuitoDescartado] = useState(false);
  const [elegirCircuito, setElegirCircuito] = useState(false);
  const [vueltasCirc, setVueltasCirc] = useState([]);
  const vueltasRef = useRef([]);
  const vueltaIniRef = useRef(null);      // { ts, idx } de la vuelta en curso
  const [vueltaIniTs, setVueltaIniTs] = useState(null);
  const lejosMetaRef = useRef(false);
  const mejorSegRef = useRef(null);
  const [mejorSeg, setMejorSeg] = useState(null);
  const [ultimaDelta, setUltimaDelta] = useState(null);
  const nuevoCircRef = useRef(null);      // { desdeIdx } mientras se traza un circuito nuevo
  const [trazandoNuevo, setTrazandoNuevo] = useState(false);
  const [proponerCirc, setProponerCirc] = useState(null); // { ini, fin, km } al cerrar la primera vuelta
  const [nombreCirc, setNombreCirc] = useState("");
  const [tipoCirc, setTipoCirc] = useState("pista");
  // Permanente (una pista de verdad) o temporal (un trazado de enduro/rally armado para un
  // evento, que después se descarta): el temporal lleva fecha de vencimiento y se deja de
  // listar al pasar esa fecha. Las sesiones grabadas ahí quedan igual. (Carlos, 22-sep)
  const [vigenciaCirc, setVigenciaCirc] = useState(eventoPreseleccionado?.id ? "temporal" : "permanente");
  const [hastaCirc, setHastaCirc] = useState("");
  const hastaSugerida = () => {
    const ev = eventoPreseleccionado || {};
    if (ev.fecha_fin) return String(ev.fecha_fin).slice(0, 10);
    if (ev.fecha) return String(ev.fecha).slice(0, 10);
    const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10);
  };
  const inclActualRef = useRef(null);     // inclinación medida por el giroscopio (grados), en modo circuito
  // Saltos (motocross): en el aire la gravedad "desaparece" en el acelerómetro
  const saltosRef = useRef([]);           // [{ts, lat, lng, t_aire, altura_m, dist_m, v_despegue, g_aterrizaje}]
  const [saltosCirc, setSaltosCirc] = useState([]);
  const vueloRef = useRef(null);          // { inicio, gMin } mientras está en el aire; null en el piso
  const gRefRef = useRef(null);           // vector de gravedad con la moto vertical (calibración)
  const circuitosBuscadosRef = useRef(false);
  // ¿Hay un circuito conocido cerca? Se busca una sola vez con la primera posición
  useEffect(() => {
    if (circuitosBuscadosRef.current || !posActual || !userId) return;
    if (estado !== "lista" && estado !== "grabando") return;
    circuitosBuscadosRef.current = true;
    // Vino de "Grabar vueltas acá" en la ficha del circuito: se activa directo
    const pendiente = window.olbosPendingCircuito;
    if (pendiente && pendiente.id) { window.olbosPendingCircuito = null; activarCircuito(pendiente); return; }
    if (window.olbosPendingCircuitoNuevo) { window.olbosPendingCircuitoNuevo = false; empezarCircuitoNuevo(); return; }
    supabase.rpc("circuitos_cerca", { p_lat: posActual.lat, p_lng: posActual.lng, p_km: 5 }).then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setCircuitosCerca(Array.isArray(d) ? d : []);
    }, () => setCircuitosCerca([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, posActual, userId]);
  const activarCircuito = (c) => {
    setCircuito({ id: c.id, nombre: c.nombre, tipo: c.tipo || "pista", meta: { lat: Number(c.meta_lat), lng: Number(c.meta_lng) }, largo_m: c.largo_m, polyline: c.polyline || null });
    vueltasRef.current = []; setVueltasCirc([]); vueltaIniRef.current = null; setVueltaIniTs(null); lejosMetaRef.current = false; saltosRef.current = []; setSaltosCirc([]); vueloRef.current = null;
    mejorSegRef.current = null; setMejorSeg(null); setUltimaDelta(null); nuevoCircRef.current = null; setTrazandoNuevo(false);
    setElegirCircuito(false); setCircuitoDescartado(true);
    hablarVoz(`Modo circuito en ${c.nombre}. Cruzá la largada para empezar a contar.`);
    if (estadoRecRef.current === "lista") iniciar();
  };
  const salirCircuito = () => { setCircuito(null); vueltaIniRef.current = null; setVueltaIniTs(null); nuevoCircRef.current = null; setTrazandoNuevo(false); };
  const empezarCircuitoNuevo = () => {
    setElegirCircuito(false); setCircuitoDescartado(true);
    nuevoCircRef.current = { desdeIdx: Math.max(0, puntosRef.current.length - 1) };
    lejosMetaRef.current = false; setTrazandoNuevo(true);
    hablarVoz("Circuito nuevo. Dá una vuelta completa: cuando vuelvas a pasar por acá te lo marco.");
    if (estadoRecRef.current === "lista") iniciar();
  };
  const guardarCircuitoNuevo = async () => {
    if (!proponerCirc || !nombreCirc.trim()) { alert("Poné el nombre del circuito."); return; }
    const pts = puntosRef.current;
    const meta = pts[proponerCirc.ini];
    const tramo = pts.slice(proponerCirc.ini, proponerCirc.fin + 1);
    // ¿Hay una pista registrada en Servicios a menos de 3 km? El circuito nace vinculado a ella
    let negocioId = null;
    try {
      const { data: pistas } = await supabase.from("negocios").select("id, lat, lng").ilike("categoria", "pista%")
        .gte("lat", meta.lat - 0.03).lte("lat", meta.lat + 0.03).gte("lng", meta.lng - 0.04).lte("lng", meta.lng + 0.04).limit(20);
      const cerca = (pistas || []).filter(p => p.lat != null && p.lng != null).map(p => ({ id: p.id, d: haversineKm(meta, { lat: Number(p.lat), lng: Number(p.lng) }) })).filter(p => p.d <= 3).sort((a, b) => a.d - b.d);
      if (cerca.length) negocioId = cerca[0].id;
    } catch (e) { /* sin vínculo */ }
    const filaBase = {
      nombre: nombreCirc.trim(), tipo: tipoCirc, meta_lat: meta.lat, meta_lng: meta.lng, negocio_id: negocioId,
      polyline: codificarPolyline(downsampleRecorrido(tramo, 400)), largo_m: Math.round(proponerCirc.km * 1000), creado_por: userId, publico: true,
    };
    const filaVig = { ...filaBase, vigencia: vigenciaCirc, hasta: vigenciaCirc === "temporal" ? (hastaCirc || hastaSugerida()) : null, evento_id: eventoFinal || null };
    let { data, error } = await supabase.from("circuitos").insert(filaVig).select("id, nombre, tipo, meta_lat, meta_lng, largo_m").maybeSingle();
    if (error && /vigencia|hasta|evento_id|column|schema cache/i.test(error.message || "")) {
      ({ data, error } = await supabase.from("circuitos").insert(filaBase).select("id, nombre, tipo, meta_lat, meta_lng, largo_m").maybeSingle());
    }
    if (error || !data) { alert("No se pudo guardar el circuito: " + (error?.message || "")); return; }
    const c = { id: data.id, nombre: data.nombre, tipo: data.tipo, meta: { lat: meta.lat, lng: meta.lng }, largo_m: data.largo_m, polyline: filaBase.polyline };
    setCircuito(c); nuevoCircRef.current = null; setTrazandoNuevo(false); setProponerCirc(null);
    // La vuelta que acabás de dar ya cuenta como la primera
    const v1 = detectarVueltas(pts.slice(proponerCirc.ini), c.meta);
    if (v1.length) { const v = { ...v1[0], ini: v1[0].ini + proponerCirc.ini, fin: v1[0].fin + proponerCirc.ini }; vueltasRef.current = [v]; setVueltasCirc([v]); mejorSegRef.current = v.seg; setMejorSeg(v.seg); }
    const ult = pts[pts.length - 1];
    vueltaIniRef.current = { ts: ult.ts, idx: pts.length - 1 }; setVueltaIniTs(ult.ts); lejosMetaRef.current = false;
    hablarVoz(`Listo, ${c.nombre} guardado. Vuelta uno, ${fmtSegVuelta(vueltasRef.current[0]?.seg).replace(":", " ")}.`);
  };
  // Cada punto nuevo de la grabación pasa por acá: cuenta vueltas o detecta el cierre de un circuito nuevo
  const procesarPuntoCircuito = (punto, idx) => {
    const pts = puntosRef.current;
    const c = circuitoRef.current;
    if (c) {
      if (haversineKm(punto, c.meta) * 1000 > CIRC_MIN_LEJOS_M) lejosMetaRef.current = true;
      const sg = segmentoAMeta(idx > 0 ? pts[idx - 1] : null, punto, c.meta);
      if (sg.d < CIRC_RADIO_META_M && (!vueltaIniRef.current || lejosMetaRef.current)) {
        const t = idx > 0 ? pts[idx - 1].ts + (punto.ts - pts[idx - 1].ts) * sg.t : punto.ts;
        if (vueltaIniRef.current) {
          const ini = vueltaIniRef.current;
          let km = 0, vmax = 0, inclMax = 0;
          for (let j = ini.idx + 1; j <= idx; j++) { km += haversineKm(pts[j - 1], pts[j]); vmax = Math.max(vmax, velKmhEn(pts, j)); if (pts[j].i != null) inclMax = Math.max(inclMax, Math.abs(pts[j].i)); }
          const seg = Math.round((t - ini.ts) / 100) / 10;
          if (seg >= 15) {
            const v = { n: vueltasRef.current.length + 1, ini: ini.idx, fin: idx, seg, km: Math.round(km * 100) / 100, vmax: Math.round(vmax), vprom: Math.round(km / (seg / 3600)), incl_max: inclMax ? Math.round(inclMax) : null };
            vueltasRef.current = [...vueltasRef.current, v]; setVueltasCirc(vueltasRef.current);
            const mejorAntes = mejorSegRef.current;
            const delta = mejorAntes != null ? seg - mejorAntes : null;
            if (mejorAntes == null || seg < mejorAntes) { mejorSegRef.current = seg; setMejorSeg(seg); }
            setUltimaDelta(delta);
            const [m, s] = fmtSegVuelta(seg).split(":");
            hablarVoz(`Vuelta ${v.n}, ${m} ${s}${delta == null ? "" : delta <= 0 ? `, ${Math.abs(delta).toFixed(1)} mejor` : `, ${delta.toFixed(1)} más`}.`);
            try { navigator.vibrate && navigator.vibrate(delta != null && delta < 0 ? [80, 60, 80] : 80); } catch (e) { /* */ }
          }
        } else {
          hablarVoz("Largada. Arranca la vuelta uno.");
        }
        vueltaIniRef.current = { ts: t, idx }; setVueltaIniTs(t); lejosMetaRef.current = false;
      }
      return;
    }
    const nc = nuevoCircRef.current;
    if (nc && !proponerCirc) {
      const ini = pts[nc.desdeIdx];
      if (!ini) return;
      if (haversineKm(punto, ini) * 1000 > CIRC_MIN_LEJOS_M) lejosMetaRef.current = true;
      if (lejosMetaRef.current && segmentoAMeta(idx > 0 ? pts[idx - 1] : null, punto, ini).d < CIRC_RADIO_META_M) {
        let km = 0; for (let j = nc.desdeIdx + 1; j <= idx; j++) km += haversineKm(pts[j - 1], pts[j]);
        if (km >= 0.4) {
          setProponerCirc({ ini: nc.desdeIdx, fin: idx, km: Math.round(km * 100) / 100 });
          hablarVoz(`Cerraste una vuelta de ${kmAHablado(km)}. Ponele nombre al circuito en la pantalla.`);
          try { navigator.vibrate && navigator.vibrate([120, 80, 120]); } catch (e) { /* */ }
        }
      }
    }
  };
  // Inclinación medida (giroscopio/acelerómetro) mientras se graba en un circuito:
  // el vector de gravedad de los primeros segundos (moto vertical) es la referencia;
  // el ángulo contra ese vector es la inclinación del momento. Va en cada punto (p.i).
  useEffect(() => {
    if (estado !== "grabando" || !circuito) { inclActualRef.current = null; return; }
    let muestras = [];
    gRefRef.current = null;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      const v = [a.x, a.y, a.z];
      if (!gRefRef.current) {
        muestras.push(v);
        if (muestras.length >= 40) {
          const s = muestras.reduce((acc, m) => [acc[0] + m[0], acc[1] + m[1], acc[2] + m[2]], [0, 0, 0]);
          gRefRef.current = s.map(x => x / muestras.length);
        }
        return;
      }
      const g = gRefRef.current;
      const dot = v[0] * g[0] + v[1] * g[1] + v[2] * g[2];
      const nv = Math.hypot(...v), ng = Math.hypot(...g);
      if (!nv || !ng) return;
      const ang = Math.acos(Math.max(-1, Math.min(1, dot / (nv * ng)))) * 180 / Math.PI;
      // Suavizado corto: los baches meten picos
      inclActualRef.current = inclActualRef.current == null ? ang : inclActualRef.current * 0.7 + ang * 0.3;
      // ── Saltos ── La firma de un salto es inconfundible: pico al despegar, casi
      // 0 g sostenido mientras vuela (un pozo o una piedra duran décimas y no
      // llegan a 0 g) y un pico de 3 a 6 g al caer. Tiempo en el aire = ventana
      // de baja gravedad; altura por física (h = g·t²/8); distancia = velocidad
      // del GPS al despegar por el tiempo de vuelo.
      const gTot = nv / 9.81, ahora = Date.now();
      const vuelo = vueloRef.current;
      if (!vuelo) {
        if (gTot < 0.35) vueloRef.current = { inicio: ahora, gMin: gTot, gMax: 0, vDespegue: velKmhRef.current || 0, pos: posActualRef.current };
      } else if (vuelo.fin == null) {
        vuelo.gMin = Math.min(vuelo.gMin, gTot);
        if (gTot > 0.9) {
          const tAire = (ahora - vuelo.inicio) / 1000;
          if (tAire >= 0.3 && tAire <= 3.5) { vuelo.fin = ahora; vuelo.tAire = tAire; }
          else vueloRef.current = null; // menos de 0,3 s: bache, no salto
        }
      } else {
        // Ya aterrizó: 300 ms más para medir el pico del aterrizaje
        vuelo.gMax = Math.max(vuelo.gMax, gTot);
        if (ahora - vuelo.fin > 300) {
          const t = vuelo.tAire;
          const salto = {
            ts: vuelo.inicio, lat: vuelo.pos ? vuelo.pos.lat : null, lng: vuelo.pos ? vuelo.pos.lng : null,
            t_aire: Math.round(t * 100) / 100, altura_m: Math.round(9.81 * t * t / 8 * 100) / 100,
            dist_m: Math.round((vuelo.vDespegue / 3.6) * t), v_despegue: Math.round(vuelo.vDespegue), g_aterrizaje: Math.round(vuelo.gMax * 10) / 10,
          };
          saltosRef.current = [...saltosRef.current, salto]; setSaltosCirc(saltosRef.current);
          vueloRef.current = null;
          hablarVoz(`Salto: ${t.toFixed(1).replace(".", " coma ")} segundos, ${salto.altura_m.toFixed(1).replace(".", " coma ")} metros.`);
        }
      }
    };
    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
  }, [estado, circuito]);
  const [previaChequeada, setPreviaChequeada] = useState(false); // gatilla el auto-arranque recién cuando SABEMOS que no hay traza pendiente

  // Form final
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [showSalir, setShowSalir] = useState(false); // menú al salir: seguir después / finalizar / descartar
  // Botón atrás mientras se graba: minimizar y seguir grabando de fondo, no
  // cortar. Salir de verdad es la X, que abre el menú de finalizar/descartar.
  useEffect(() => { estadoRecRef.current = estado; }, [estado]);
  useEffect(() => { posActualRef.current = posActual; }, [posActual]);
  // El estado del grabador se avisa a la raíz: la barra roja de "minimizado"
  // dice la verdad (grabando / en pausa / en el mapa sin grabar)
  useEffect(() => {
    window.olbosRecEstado = estado;
    window.dispatchEvent(new CustomEvent("olbos-rec-estado", { detail: { estado } }));
    return () => { window.olbosRecEstado = ""; };
  }, [estado]);
  const [tituloFinal, setTituloFinal] = useState(eventoPreseleccionado?.titulo || "");
  // Al abrir, el grabador entra SOLO al mapa — pero arranca EN PAUSA: te ves en
  // el mapa (y en una rodada te ven los demás) y el botón Iniciar decide cuándo
  // empieza a grabar. La pantalla de intro quedó solo para cuando hay un
  // recorrido sin terminar que retomar, o falló el GPS.
  const autoIniciado = useRef(false);
  useEffect(() => {
    if (autoIniciado.current || !userId) return;
    if (estado !== "preparando" || !previaChequeada || trazaPrevia || error) return;
    // En la web, tocar la sección Mapa disparaba el pedido de ubicación y abría el
    // navegador a pantalla completa sin que nadie lo hubiera pedido (pedido de Carlos).
    // En el teléfono está bien —si abrís el mapa es porque vas a rodar—, pero sentado
    // en una computadora no. En la web se entra con el botón, salvo que vengas
    // derecho a una rodada concreta (link de rodar, evento o grupo en vivo).
    if (!esNativo() && !enRodada && !entradaExplicita) return;
    autoIniciado.current = true;
    entrarAlMapa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, previaChequeada, trazaPrevia, error, userId]);
  const [descripcionFinal, setDescripcionFinal] = useState("");
  // Lo que el rider deja sobre el CAMINO (alimenta su recorrido y la red anónima)
  const [tipoCamino, setTipoCamino] = useState("");
  const [estadoCamino, setEstadoCamino] = useState("");
  const [publicaFinal, setPublicaFinal] = useState(false);
  const [publicarEnFeed, setPublicarEnFeed] = useState(false);
  const [grupoFinal, setGrupoFinal] = useState(grupoIdPreseleccionado || ""); // "" = sin grupo, o uuid
  const [misGruposFinal, setMisGruposFinal] = useState([]);
  const [guardando, setGuardando] = useState(false);

  // Cargar mis grupos al iniciar el componente
  useEffect(() => {
    (async () => {
      const { data: raw } = await supabase.rpc("get_mis_grupos");
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMisGruposFinal(data || []);
    })();
  }, []);

  // Al abrir: ¿hay un recorrido sin terminar (grabando/pausada) de una sesión anterior?
  // Permite retomar un viaje de varios días aunque se haya cerrado la app.
  // Refs espejo para decidir sin esperar un render (la entrada explícita al mapa
  // llega por evento 60 ms después de montar, antes de que la consulta conteste).
  const trazaPreviaRef = useRef(null);
  useEffect(() => { trazaPreviaRef.current = trazaPrevia; }, [trazaPrevia]);
  const previaChequeadaRef = useRef(false);
  const entradaPendienteRef = useRef(false);
  useEffect(() => {
    if (!userId) return;
    (async () => {
      // Desde Mis recorridos se pide UNO en particular (06-oct-2026: con dos sin
      // terminar, el más nuevo tapaba al otro y ese quedaba en el limbo para siempre).
      let q = supabase.from("trazas").select("*").eq("user_id", userId);
      q = retomarId ? q.eq("id", retomarId) : q.in("estado", ["grabando", "pausada"]).order("updated_at", { ascending: false });
      const { data } = await q.limit(1);
      const t = data && data[0];
      if (t && (t.estado === "grabando" || t.estado === "pausada") && Array.isArray(t.puntos) && t.puntos.length > 0) {
        // Una grabación que murió al arrancar (un par de puntos, ni 300 m) no es un
        // recorrido para retomar: se limpia sola. Antes quedaba como "sin terminar"
        // y al retomarla días después unía dos ciudades con una recta.
        const st = calcEstadisticas(t.puntos);
        const ultimoTs = Math.max(t.puntos[t.puntos.length - 1].ts || 0, Date.parse(t.updated_at || 0) || 0);
        if (st.dist < 0.3 && Date.now() - ultimoTs > 3600000) {
          supabase.from("trazas").delete().eq("id", t.id).then(() => {}, () => {});
        } else {
          trazaPreviaRef.current = t;
          setTrazaPrevia(t);
        }
      }
      previaChequeadaRef.current = true;
      setPreviaChequeada(true);
      // Una entrada explícita que llegó antes de saber si había algo que retomar: ahora sí
      if (entradaPendienteRef.current) {
        entradaPendienteRef.current = false;
        if (!trazaPreviaRef.current && entrarAlMapaRef.current) entrarAlMapaRef.current();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Retomar un recorrido concreto con el grabador YA montado (tocado en Mis recorridos)
  useEffect(() => {
    const h = async (e) => {
      const id = e.detail && e.detail.id;
      if (!id) return;
      const { data: t } = await supabase.from("trazas").select("*").eq("id", id).maybeSingle();
      if (!t || !Array.isArray(t.puntos) || !t.puntos.length) return;
      const est = estadoRecRef.current;
      if (trazaIdRef.current === t.id) return; // ya es este
      if (est === "grabando" || est === "pausada") { alert("Ya hay un recorrido en curso. Finalizalo o descartalo antes de retomar otro."); return; }
      if (est === "preparando") { trazaPreviaRef.current = t; setTrazaPrevia(t); return; }
      cargarTrazaPreviaRef.current && cargarTrazaPreviaRef.current(t); // en el mapa sin grabar: queda en pausa, ▶ para seguir
    };
    window.addEventListener("olbos-retomar-traza", h);
    return () => window.removeEventListener("olbos-retomar-traza", h);
  }, []);
  const cargarTrazaPreviaRef = useRef(null);

  // Si se graba el recorrido de una rodada, traer el título del evento desde la base
  // (más confiable que pasarlo por el evento de apertura) y pre-cargarlo.
  useEffect(() => {
    if (!eventoFinal) return;
    supabase.from("eventos").select("titulo").eq("id", eventoFinal).maybeSingle()
      .then(({ data }) => { if (data?.titulo) setTituloFinal(t => t || data.titulo); }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoFinal]);

  // Refs (evitar re-renders con muchos puntos)
  const puntosRef = useRef([]);
  const watchIdRef = useRef(null);
  const inicioRef = useRef(null);
  const ultimoAutoSaveRef = useRef(0);
  const autoSaveEnVueloRef = useRef(false); // hay un autoguardado viajando: no encimar otro
  // El autoguardado manda el array ENTERO de puntos cada vez, así que en una salida
  // larga el envío crece sin techo (12 horas son decenas de miles de puntos, varios MB).
  // Con señal mala los envíos se empiezan a pisar y el teléfono se ahoga. Se espacia
  // según cuánto llevás grabado.
  const cadaCuantoAutoSave = (n) => (n > 8000 ? 120000 : n > 2000 ? 60000 : 30000);
  const ultimoPuntoTsRef = useRef(0);
  const tiempoPausadoRef = useRef(0);
  const ultimaPausaRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylineRef = useRef(null);
  const tramosGoogleRef = useRef([]); // tramos ya cerrados por un hueco del GPS (una polilínea cada uno)
  const pasivaPolyRef = useRef(null); // tramo andado sin grabar (punteado)
  const markerRef = useRef(null);
  const fotosRef = useRef([]);
  const paradasRef = useRef([]);
  const [paradas, setParadas] = useState([]);
  const wakeLockRef = useRef(null);
  const [subiendoMedia, setSubiendoMedia] = useState(0); // cantidad de fotos/videos subiéndose ahora
  // Modo navegación: course-up + recentrado continuo
  const [modoNavegacion, setModoNavegacion] = useState(true);
  // Espejo en ref: los callbacks del GPS se crean una sola vez y se quedarían
  // con el valor viejo del estado para siempre
  const modoNavegacionRef = useRef(true);
  useEffect(() => { modoNavegacionRef.current = modoNavegacion; }, [modoNavegacion]);
  // ¿Hay un viaje en curso en el navegador? Con destino puesto, el mapa se
  // inclina en perspectiva como Waze; sin destino queda plano, mirando de arriba
  const [navegando, setNavegando] = useState(false);
  const [showCargaNafta, setShowCargaNafta] = useState(false); // ⛽ carga rápida desde el mapa
  // Compartir en vivo (reusa ubicaciones_vivo): link público con mi posición + recorrido
  const [vivoRegistro, setVivoRegistro] = useState(null); // fila ubicaciones_vivo activa
  const [mostrarVivo, setMostrarVivo] = useState(false);   // sheet con el link
  const [iniciandoVivo, setIniciandoVivo] = useState(false);
  const vivoRef = useRef(null);
  const ultimoVivoPushRef = useRef(0);
  // Salida abierta ("Salgo a rodar", 29-oct): si la publicaste desde Eventos, Social o el mapa,
  // el grabador la toma como su compartido en vivo: tu posición se actualiza y ves a los sumados
  useEffect(() => {
    if (!userId || vivoToken || grupoVivo) return;
    const adoptar = async () => {
      if (vivoRef.current) return;
      const { data } = await supabase.from("ubicaciones_vivo").select("*")
        .eq("user_id", userId).eq("activo", true).eq("abierta", true).gt("expira_en", new Date().toISOString())
        .order("ultimo_update", { ascending: false }).limit(1).then(r => r, () => ({ data: null }));
      const v = data && data[0];
      if (!v || vivoRef.current) return;
      vivoRef.current = v;
      setVivoRegistro(v);
      ultimoVivoPushRef.current = 0; // empujar la posición con el próximo fix
    };
    adoptar();
    window.addEventListener("olbos-salida-abierta", adoptar);
    return () => window.removeEventListener("olbos-salida-abierta", adoptar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, vivoToken, grupoVivo]);
  // La salida adoptada queda atada a la traza cuando arrancás a grabar
  useEffect(() => {
    const v = vivoRef.current;
    if (!v || !trazaId || v.traza_id === trazaId) return;
    v.traza_id = trazaId;
    supabase.from("ubicaciones_vivo").update({ traza_id: trazaId }).eq("id", v.id).then(() => {}, () => {});
  }, [trazaId, vivoRegistro]);
  // Salidas abiertas de otros cerca (sin grabar, en el mapa): se avisan en la barra de abajo
  const [salidasCerca, setSalidasCerca] = useState([]);
  const [verSalidas, setVerSalidas] = useState(false);
  const [salgoSheet, setSalgoSheet] = useState(false);
  const posSalidasRef = useRef(0);
  useEffect(() => {
    if (!posActual) return;
    if (Date.now() - posSalidasRef.current < 90000) return;
    posSalidasRef.current = Date.now();
    guardarPosReciente(posActual);
    cargarSalidasCerca(posActual).then(l => setSalidasCerca(Array.isArray(l) ? l.filter(s => !s.es_mia) : []), () => {});
  }, [posActual]);
  const headingRef = useRef(null);
  // Botón atrás: mientras se graba (o pausa) minimiza y sigue de fondo. En modo
  // lista SIN nada activo, atrás CIERRA como la X — minimizar dejaba la barra
  // roja diciendo "grabando" cuando no se grababa nada. Con compartido en vivo
  // o rodada activa sí minimiza, porque el mapa tiene que seguir vivo.
  useBackGuard(!minimizado && !!onMinimizar && (estado === "grabando" || estado === "pausada" || estado === "lista"), () => {
    // Con un destino puesto en el navegador, salir del mapa NO apaga la navegación
    // (pedido de Carlos 04-sep): se minimiza y la voz sigue; se apaga solo con la ✕ del navegador
    if (estado === "lista" && !vivoRegistro && !grupoVivo && !vivoToken && !navegando) onCerrar();
    else onMinimizar();
  });
  // Minimizado "en el mapa sin grabar", sin destino y sin compartido: no hay nada que
  // seguir, pero el GPS quedaba prendido de fondo hasta que alguien volviera a cerrarlo.
  // A los 20 minutos se cierra solo (grabando, navegando o en vivo no se toca).
  useEffect(() => {
    if (!minimizado || estado !== "lista" || vivoRegistro || grupoVivo || vivoToken || navegando) return;
    const t = setTimeout(() => { if (watchLibreRef.current) { try { watchLibreRef.current.stop(); } catch (e) { /* */ } watchLibreRef.current = null; } onCerrar(); }, 20 * 60000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimizado, estado, vivoRegistro, grupoVivo, vivoToken, navegando]);
  const posPrevListaRef = useRef(null); // posición anterior en modo lista, para calcular el rumbo
  const pasivoRef = useRef([]); // tramo andado SIN grabar (modo lista): aporte pasivo a la red
  const rutaPlaneadaRef = useRef(null); // el camino del navegador: viaja con el compartido en vivo
  const navInfoRef = useRef(null); // {destino, rest_km, min, eta} del navegador, para el que te mira
  const [navInfoUi, setNavInfoUi] = useState(null); // lo mismo como estado: la barra inferior (ETA · min · km) y la calle actual del lienzo v2
  const navMenuRef = useRef(null); // acciones del navegador (guardar destino, voz, cancelar) para el menú ⋯
  // El plan del navegador + los datos del viaje acompañan CADA push de posición:
  // antes solo se subían al arrancar la navegación, y si el compartido se creaba
  // en otro orden el que miraba nunca veía la línea azul (reporte de Carlos)
  const pushVivoExtras = () => {
    if (!vivoRef.current) return;
    const id = vivoRef.current.id;
    let plan = rutaPlaneadaRef.current ? codificarPolyline(rutaPlaneadaRef.current) : null;
    let nav = navInfoRef.current;
    // Sin navegador activo pero dentro de una rodada: la ETA y la línea salen del
    // recorrido planeado de la rodada, para que el que te mira vea lo mismo que
    // vería con un destino propio.
    if ((!nav || nav.rest_km == null) && grupoVivo) {
      const r = etaDeRodada(grupoVivo, posActualRef.current, velMovilReciente());
      if (r) { nav = r.nav; if (!plan && r.path) plan = codificarPolyline(r.path); }
    }
    supabase.from("ubicaciones_vivo").update({ ruta_planeada: plan, nav }).eq("id", id).then(({ error }) => {
      // Si la columna nav todavía no existe en la base, que al menos viaje el plan
      if (error) supabase.from("ubicaciones_vivo").update({ ruta_planeada: plan }).eq("id", id).then(() => {}, () => {});
    }, () => {});
  };
  const [avisoRed, setAvisoRed] = useState(false); // aviso de recolección anónima, una sola vez
  const [avisosAbiertos, setAvisosAbiertos] = useState(false); // clima + aporte anónimo: una línea plegada, se abre tocando
  // El mapa te persigue salvo que lo muevas a mano; el botón Centrar te devuelve
  const [seguirMapa, setSeguirMapa] = useState(true);
  const seguirRef = useRef(true);
  useEffect(() => { seguirRef.current = seguirMapa; }, [seguirMapa]);
  // Con un viaje en curso, "dejar de seguir" no puede ser para siempre: un roce
  // en el mapa (guante, palma en el soporte) apagaba el seguimiento y el rider
  // quedaba mirando un mapa muerto con el botón Centrar (video de Matías,
  // 28-ago). Como Waze: a los 10 segundos vuelve a perseguir solo.
  // Aplicaba solo navegando con destino: sin destino, un roce dejaba el mapa
  // sin seguir y sin orientar para siempre ("navega mal si no pongo destino",
  // Carlos 18-sep). Después volvía a perseguir a los 10 s en cualquier modo.
  // Matías (video, 10-oct): "si querés ver el recorrido, pasa un rato y te
  // vuelve solo a tu ubicación". Ahora el reloj de 10 s arranca recién al
  // SOLTAR el dedo (no al tocar) y solo si estás en movimiento (≥ 8 km/h):
  // parado, el mapa queda libre para mirar el recorrido hasta que arranques
  // o toques 📍. En marcha, un roce sigue volviendo solo como en Waze.
  const velVolverRef = useRef(null);
  const volverTimerRef = useRef(null);
  const gestoMapaRef = useRef(false); // dedo apoyado en el mapa
  const cancelarVolver = () => { if (volverTimerRef.current) { clearTimeout(volverTimerRef.current); volverTimerRef.current = null; } };
  const programarVolver = () => {
    cancelarVolver();
    if (seguirRef.current || gestoMapaRef.current) return;
    if ((velVolverRef.current || 0) < 8) return; // parado: libre
    volverTimerRef.current = setTimeout(() => { volverTimerRef.current = null; setSeguirMapa(true); }, 10000);
  };
  useEffect(() => { if (seguirMapa) cancelarVolver(); return cancelarVolver; }, [seguirMapa]); // eslint-disable-line react-hooks/exhaustive-deps
  // Velocímetro siempre visible (pedido de Carlos: como Waze)
  const [velKmh, setVelKmh] = useState(null);
  // Si arrancás a andar con el mapa suelto (lo soltaste parado), a los 10 s vuelve a seguirte
  useEffect(() => {
    velVolverRef.current = velKmh;
    if (!seguirMapa && !volverTimerRef.current && (velKmh || 0) >= 8) programarVolver();
  }, [velKmh]); // eslint-disable-line react-hooks/exhaustive-deps
  // Altura sobre el nivel del mar (del GPS, suavizada) y temperatura (Open-Meteo),
  // en el mapa como pide Carlos (06-oct): en la montaña son dato de manejo.
  const [altM, setAltM] = useState(null);
  const altSuaveRef = useRef(null);
  const actualizarAlt = (gp) => {
    if (gp.alt == null || !isFinite(gp.alt)) return;
    altSuaveRef.current = altSuaveRef.current == null ? gp.alt : altSuaveRef.current * 0.7 + gp.alt * 0.3;
    const r = Math.round(altSuaveRef.current / 5) * 5;
    setAltM(prev => (prev === r ? prev : r));
  };
  const [tempC, setTempC] = useState(null);
  const tempPosRef = useRef(null);
  useEffect(() => {
    if (estado !== "lista" && estado !== "grabando" && estado !== "pausada") return;
    const pedir = async () => {
      const p = posActualRef.current;
      if (!p) return;
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${p.lat}&longitude=${p.lng}&current=temperature_2m`);
        const d = await r.json();
        if (d?.current?.temperature_2m != null) { setTempC(Math.round(d.current.temperature_2m)); tempPosRef.current = p; }
      } catch (e) { /* sin temperatura no pasa nada */ }
    };
    pedir();
    const cada = setInterval(pedir, 15 * 60000); // cada 15 min
    // o antes si te moviste más de 15 km (de un valle a un cerro cambia mucho)
    const mov = setInterval(() => { const p = posActualRef.current; if (p && tempPosRef.current && haversineKm(p, tempPosRef.current) > 15) pedir(); }, 60000);
    return () => { clearInterval(cada); clearInterval(mov); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);
  // Mapa de noche: a mano con el botón 🌙 (queda guardado); sin elección, oscuro si la
  // app está en oscuro o es de noche (19 a 7)
  const [mapaOscuro, setMapaOscuro] = useState(() => {
    try { const v = localStorage.getItem("olbos_mapa_oscuro"); if (v === "1") return true; if (v === "0") return false; } catch (e) { /* */ }
    const h = new Date().getHours();
    return TEMA_ACTIVO === "oscuro" || h >= 19 || h < 7;
  });
  const alternarMapaOscuro = () => setMapaOscuro(m => { const n = !m; try { localStorage.setItem("olbos_mapa_oscuro", n ? "1" : "0"); } catch (e) { /* */ } return n; });
  // En pausa, si te detectamos en movimiento: aviso para retomar (y retoma sola si no
  // respondés). Carlos se olvidó de reanudar una vez y perdió 90 km.
  const watchPausaRef = useRef(null);
  const pausaPosRef = useRef(null);       // dónde se pausó (o desde dónde dijiste "seguir en pausa")
  const umbralPausaKmRef = useRef(0.25);  // cuánto hay que alejarse para avisar
  const bufferPausaRef = useRef([]);      // lo andado desde que se detectó movimiento: entra al recorrido si retomás
  const [avisoRetomar, setAvisoRetomar] = useState(null); // { seg }
  const avisoRetomarRef = useRef(false);
  const timerRetomarRef = useRef(null);
  const [avisoRetomado, setAvisoRetomado] = useState(false);
  const [avisoWebBloqueo, setAvisoWebBloqueo] = useState(false); // web: el GPS se corta con la pantalla bloqueada
  // Nativo: sin el permiso "Permitir todo el tiempo", con la pantalla bloqueada no te ven en el
  // mapa (Carlos, 10-oct, Originarios). El chequeo lo hace startGeoWatch al arrancar el GPS.
  const [avisoPermisoFondo, setAvisoPermisoFondo] = useState(false);
  useEffect(() => {
    const h = (e) => setAvisoPermisoFondo(!!(e.detail && e.detail.ok === false));
    window.addEventListener("olbos-gps-fondo", h);
    // Al volver de Ajustes se vuelve a mirar el permiso: si ya está "todo el tiempo", se va el aviso
    let sub = null;
    let vivo = true;
    (async () => {
      if (!esNativo()) return;
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const s = await CapApp.addListener("appStateChange", async (st) => {
          if (!st.isActive) return;
          try {
            const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
            const p = await BG.getProviderState();
            if (p && p.status === BG.AUTHORIZATION_STATUS_ALWAYS) { setAvisoPermisoFondo(false); setPermisoFondoAyuda(false); }
          } catch (e) { /* */ }
        });
        if (vivo) sub = s; else s.remove();
      } catch (e) { /* */ }
    })();
    return () => { vivo = false; window.removeEventListener("olbos-gps-fondo", h); try { sub && sub.remove(); } catch (e) { /* */ } };
  }, []);
  // Si el sistema ya no muestra el cartel (Android 11+ después de la primera vez, iOS después
  // de la primera pregunta), requestPermission falla callado y "Activar" parecía no hacer nada
  // (documento de Matías, N6). Ahora se muestra cómo hacerlo a mano y, en iPhone, se abren los Ajustes.
  const [permisoFondoAyuda, setPermisoFondoAyuda] = useState(false);
  const pedirPermisoFondo = async () => {
    let ok = false;
    try {
      const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
      const st = await BG.requestPermission(); // en Android 11+ abre el ajuste donde está "Permitir todo el tiempo"
      ok = st === BG.AUTHORIZATION_STATUS_ALWAYS;
    } catch (e) { /* rechaza con el estado cuando no queda en "siempre" */ }
    if (ok) { setAvisoPermisoFondo(false); setPermisoFondoAyuda(false); return; }
    setPermisoFondoAyuda(true);
    if (_plataformaNativa() === "ios") { try { window.location.href = "app-settings:"; } catch (e) { /* */ } }
  };
  // ── Avisos efímeros (idea tomada del scouting de EnMoto, aprobada por Carlos 09-oct):
  // peligro en la ruta, nafta, control o punto de reagrupe, marcados por los riders en su
  // posición y vigentes 24 h. Impersonales: nadie convoca a nadie. Se ven en el mapa con
  // "hace X min", se confirman o se cierran, y el navegador los avisa por voz al acercarse.
  const TIPOS_AVISO = { peligro: ["⚠️", "Peligro en la ruta", "#B3372C"], nafta: ["⛽", "Nafta", "#2F7D4F"], control: ["👮", "Control", "#1A73E8"], reagrupe: ["🏁", "Punto de reagrupe", "#8A6D3B"] };
  const [avisos, setAvisos] = useState([]);
  const [avisoSel, setAvisoSel] = useState(null);
  const [showAvisar, setShowAvisar] = useState(false);
  const [avisoNota, setAvisoNota] = useState("");
  const [avisoEnviando, setAvisoEnviando] = useState(false);
  const avisosMarkersRef = useRef({});
  const avisosAnunciadosRef = useRef(new window.Set());
  // Si el lienzo v2 está tapando a Google (se decide más abajo, en el bloque del navegador
  // v2; acá solo el ref, que los efectos leen en tiempo de ejecución y no en el render).
  const usarNavV2Ref = useRef(false);
  const cargarAvisos = async () => {
    const p = posActualRef.current;
    if (!p) return;
    try {
      const { data } = await supabase.rpc("avisos_cerca", { p_lat: p.lat, p_lng: p.lng, p_radio_km: 60 });
      const d = typeof data === "string" ? JSON.parse(data) : data;
      if (Array.isArray(d)) setAvisos(d);
    } catch (e) { /* sin avisos no pasa nada */ }
  };
  useEffect(() => {
    if (estado !== "lista" && estado !== "grabando" && estado !== "pausada") return;
    cargarAvisos();
    const t = setInterval(cargarAvisos, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);
  // Dibujar/actualizar los marcadores en el mapa de Google (tapado por el v2, se vacía:
  // el lienzo los dibuja con "hace X min" y no vale la pena mantener dos juegos)
  useEffect(() => {
    const mapa = mapInstanceRef.current;
    if (!mapa || !window.google) return;
    if (usarNavV2Ref.current) {
      Object.values(avisosMarkersRef.current).forEach(mk => mk.setMap(null)); avisosMarkersRef.current = {};
      return;
    }
    const vistos = new window.Set();
    avisos.forEach(a => {
      vistos.add(a.id);
      const [emoji, , color] = TIPOS_AVISO[a.tipo] || TIPOS_AVISO.peligro;
      let mk = avisosMarkersRef.current[a.id];
      if (!mk) {
        mk = new window.google.maps.Marker({
          position: { lat: Number(a.lat), lng: Number(a.lng) }, map: mapa, zIndex: 900,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 13, fillColor: "#F9F5F2", fillOpacity: 1, strokeColor: color, strokeWeight: 3 },
          label: { text: emoji, fontSize: "15px" },
          title: TIPOS_AVISO[a.tipo] ? TIPOS_AVISO[a.tipo][1] : "Aviso",
        });
        mk.addListener("click", () => setAvisoSel(a));
        avisosMarkersRef.current[a.id] = mk;
      } else {
        mk.set("avisoDatos", a);
      }
    });
    Object.keys(avisosMarkersRef.current).forEach(id => { if (!vistos.has(id)) { avisosMarkersRef.current[id].setMap(null); delete avisosMarkersRef.current[id]; } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avisos, estado]);
  useEffect(() => () => { Object.values(avisosMarkersRef.current).forEach(mk => mk.setMap(null)); avisosMarkersRef.current = {}; }, []);
  // Aviso por voz al acercarse (350 m), una vez por aviso
  useEffect(() => {
    if (!posActual || !avisos.length) return;
    avisos.forEach(a => {
      if (avisosAnunciadosRef.current.has(a.id)) return;
      const d = haversineKm(posActual, { lat: Number(a.lat), lng: Number(a.lng) });
      if (d <= 0.35) {
        avisosAnunciadosRef.current.add(a.id);
        const [, nombre] = TIPOS_AVISO[a.tipo] || TIPOS_AVISO.peligro;
        hablarNav(`Atención: ${nombre.toLowerCase()} a trescientos metros${a.nota ? ". " + a.nota : ""}.`);
        try { navigator.vibrate && navigator.vibrate([120, 80, 120]); } catch (e) { /* */ }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posActual, avisos]);
  const crearAviso = async (tipo) => {
    const p = posActualRef.current;
    if (!userId) { alert("Iniciá sesión para avisar."); return; }
    if (!p) { alert("Todavía no tengo tu posición."); return; }
    setAvisoEnviando(true);
    const { error } = await supabase.from("avisos_mapa").insert({ user_id: userId, tipo, nota: avisoNota.trim() || null, lat: p.lat, lng: p.lng });
    setAvisoEnviando(false);
    if (error) { alert("No se pudo marcar el aviso: " + error.message); return; }
    setShowAvisar(false); setAvisoNota("");
    cargarAvisos();
  };
  const votarAviso = async (a, sigue) => {
    if (!userId) { alert("Iniciá sesión para confirmar avisos."); return; }
    await supabase.rpc("aviso_votar", { p_id: a.id, p_sigue: sigue }).then(() => {}, () => {});
    setAvisoSel(null);
    cargarAvisos();
  };
  // ── Navegador v2 (MapLibre), el default: ver docs/NAVEGADOR_V2.md ──
  // Siempre que el mapa está abierto (con o sin destino) el lienzo v2 tapa al mapa de
  // Google, que sigue montado abajo (el resto del grabador depende de mapInstanceRef).
  // Si MapLibre no arranca (sin WebGL, sin red la primera vez, worker muerto), vuelve
  // a Google solo. Sin ruta, el motor sigue el rumbo del GPS suavizado.
  const [navV2Error, setNavV2Error] = useState(false);
  // Interruptor remoto ("nav_v2" en version.json): arranca con lo cacheado (sin flash) y
  // se relee al montar. Si Carlos lo apagó, esta misma apertura ya va en Google; si lo
  // volvió a prender, aplica a la próxima (no se le cambia el mapa a nadie en marcha).
  const [navV2Remoto, setNavV2Remoto] = useState(() => navV2RemotoCacheado());
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const cfg = await r.json();
        guardarNavV2Remoto(cfg);
        if (vivo && cfg && cfg.nav_v2 === false) setNavV2Remoto(false);
      } catch (e) { /* sin red: vale lo cacheado */ }
    })();
    return () => { vivo = false; };
  }, []);
  const [avisoMapaAnterior, setAvisoMapaAnterior] = useState(false); // "Usamos el mapa anterior en este teléfono"
  const navV2Deseado = navV2Preferido(navV2Remoto) && !navV2Error;
  const navV2DeseadoRef = useRef(navV2Deseado); // para los callbacks del GPS, que no ven el render
  navV2DeseadoRef.current = navV2Deseado;
  const usarNavV2 = navV2Deseado && (estado === "lista" || estado === "grabando" || estado === "pausada");
  usarNavV2Ref.current = usarNavV2; // Google tapado: no se lo mueve ni se le dibujan riders ni avisos
  // Al pasar a v2 se vacían los avisos efímeros de Google; al volver a Google se redibujan
  useEffect(() => {
    if (usarNavV2) { Object.values(avisosMarkersRef.current).forEach(mk => mk.setMap(null)); avisosMarkersRef.current = {}; }
    else setAvisos(a => a.slice());
  }, [usarNavV2]);
  const modoLibreV2 = usarNavV2 && !navegando; // lienzo v2 sin destino: pastilla "¿A dónde vamos?" arriba
  // MapaNav no arrancó: Google en esta sesión; al segundo fallo en 24 h, Google por defecto acá
  const alFallarNavV2 = (e, motivo) => {
    setNavV2Error(true);
    if (registrarFalloNavV2(motivo || (e && e.message) || "desconocido")) setAvisoMapaAnterior(true);
  };
  useEffect(() => {
    if (!avisoMapaAnterior) return;
    const t = setTimeout(() => setAvisoMapaAnterior(false), 8000);
    return () => clearTimeout(t);
  }, [avisoMapaAnterior]);
  const [fixV2, setFixV2] = useState(null);         // último fix crudo {lat,lng,v,head,acc,t}: el motor necesita velocidad y rumbo
  const [rutaV2, setRutaV2] = useState(null);       // el camino planeado del navegador, como estado para que MapaNav vea el cambio
  const [rutaV2Riders, setRutaV2Riders] = useState(false); // la ruta es un camino de riders de la red: marrón, como en Google
  const [ridersV2, setRidersV2] = useState([]);     // los demás de la rodada / grupo en vivo: los publica el overlay que los trae
  const [espontaneoV2, setEspontaneoV2] = useState([]); // el que invitó y los sumados a un compartido espontáneo (link /vivo)
  const [vivoPartsV2, setVivoPartsV2] = useState([]);   // los que se sumaron a MI compartido en vivo
  const [alertasV2, setAlertasV2] = useState([]);       // pins de alertas comunitarias (AlertasMapaOverlay los trae)
  const alertasSelRef = useRef(null);                    // abre la ficha de una alerta en AlertasMapaOverlay
  const [planRodadaV2, setPlanRodadaV2] = useState(null); // el recorrido planeado de la rodada (azul, fijo)
  const [paradasNavV2, setParadasNavV2] = useState(null); // paradas sumadas a mano durante la navegación
  const [paradasPlanV2, setParadasPlanV2] = useState(null); // las paradas del plan de la rodada (pin con nombre)
  const [lineaGrupoV2, setLineaGrupoV2] = useState(null); // la línea que graba otro (o el recorrido del que invitó)
  const [pasivoV2, setPasivoV2] = useState([]);     // lo andado sin grabar, para el punteado del lienzo v2 (≤ 1 vez/s)
  const ultimoPasivoV2Ref = useRef(0);
  const [rodadaResumen, setRodadaResumen] = useState(null); // { count, sinSenal } que publica GrupoRodadaOverlay, para la barra inferior
  const rodadaUiRef = useRef(null);                  // { toggleSinSenal } del overlay de la rodada
  const [fotoVisor, setFotoVisor] = useState(null);  // foto/video del recorrido a pantalla completa (tocado en el mapa o en la tira)
  const [llegada, setLlegada] = useState(null);      // { nombre, km, min } al llegar al destino: ficha de cierre
  const [rodadaTermino, setRodadaTermino] = useState(false); // la rodada pasó a finalizada mientras grababa: hoja "Guardar mi recorrido"
  const navBuscarRef = useRef(null);                 // abre el buscador del NavegadorOverlay desde la pastilla "¿A dónde vamos?"
  const [corredorPct, setCorredorPct] = useState(null); // null: nada; 0-99 bajando; 100 listo; -1 falló
  const corredorAbortRef = useRef(null);
  // Se alimenta desde los callbacks del GPS (antes de los filtros de grabación, a 1 Hz):
  // el motor de dead reckoning descarta solo los fixes de mala precisión. Sin velocidad
  // del GPS (la web) va null: el motor la deduce del desplazamiento entre fixes.
  const alimentarFixV2 = (gp) => {
    if (!navV2DeseadoRef.current) return;
    setFixV2({ lat: gp.lat, lng: gp.lng, v: gp.v != null && gp.v >= 0 ? gp.v : null, head: gp.head != null && gp.head >= 0 ? gp.head : null, acc: gp.acc, t: gp.t || Date.now() });
  };
  const alimentarPasivoV2 = (ts) => {
    if (!navV2DeseadoRef.current || ts - ultimoPasivoV2Ref.current < 1000) return;
    ultimoPasivoV2Ref.current = ts;
    setPasivoV2(pasivoRef.current.slice(-600));
  };
  // La ficha de llegada se va sola a los 25 s si nadie la toca
  useEffect(() => {
    if (!llegada) return;
    const t = setTimeout(() => setLlegada(null), 25000);
    return () => clearTimeout(t);
  }, [llegada]);
  const cancelarCorredor = () => { if (corredorAbortRef.current) { corredorAbortRef.current.abort(); corredorAbortRef.current = null; } };
  // Corredor sin señal (fase 2): con ruta nueva se bajan al teléfono los tiles del
  // camino (buffer de 2 km, zoom 10-15) para que el mapa siga aunque se corten los datos.
  const descargarCorredorV2 = (path) => {
    cancelarCorredor();
    if (!path || path.length < 2 || !navV2DeseadoRef.current) return;
    const ctrl = new AbortController();
    corredorAbortRef.current = ctrl;
    setCorredorPct(0);
    // Arranca 4 s después de la ruta: el lienzo v2 pide primero sus tiles, sprite y
    // glyphs sin competir por la red y la caché con las 1.500 descargas del corredor
    const arrancar = () => {
      if (ctrl.signal.aborted) return;
      import("./navegador-v2/corredorOffline")
        .then(({ descargarCorredor }) => descargarCorredor(path, { onProgreso: (pct) => { if (!ctrl.signal.aborted) setCorredorPct(pct); }, signal: ctrl.signal }))
        .then((r) => { if (!ctrl.signal.aborted) setCorredorPct(r && r.fallidos ? -1 : 100); }, () => { if (!ctrl.signal.aborted) setCorredorPct(-1); });
    };
    const espera = setTimeout(arrancar, 4000);
    ctrl.signal.addEventListener("abort", () => clearTimeout(espera));
  };
  // El "✓ Mapa guardado" se va solo a los 5 s; un fallo se muestra apenas y se va
  useEffect(() => {
    if (corredorPct !== 100 && corredorPct !== -1) return;
    const t = setTimeout(() => setCorredorPct(null), corredorPct === 100 ? 5000 : 2500);
    return () => clearTimeout(t);
  }, [corredorPct]);
  // Sin destino (se cerró el navegador) o al cerrar el mapa, la descarga en curso se corta
  useEffect(() => { if (!navegando) cancelarCorredor(); }, [navegando]);
  useEffect(() => () => cancelarCorredor(), []);
  // Corredores de más de 7 días se borran al abrir el mapa (si el v2 es el mapa de este teléfono)
  useEffect(() => {
    if (!navV2Preferido()) return;
    import("./navegador-v2/corredorOffline").then(({ limpiarCorredoresViejos }) => limpiarCorredoresViejos()).then(() => {}, () => {});
  }, []);
  // Marcadores del lienzo v2: riders (rodada, grupo en vivo, compartido espontáneo), avisos
  // efímeros con "hace X min", alertas comunitarias y destino. Memoizado porque MapaNav
  // hace el diff por id y no hay que pasarle un array nuevo en cada render.
  const marcadoresV2 = useMemo(() => {
    if (!usarNavV2) return [];
    const out = [];
    for (const u of [...(ridersV2 || []), ...(espontaneoV2 || []), ...(vivoPartsV2 || [])]) {
      if (u.lat == null || u.lng == null) continue;
      out.push({ id: `rider-${u.user_id}`, tipo: "rider", lat: Number(u.lat), lng: Number(u.lng), label: u.username || "rider", avatarUrl: u.avatar_url || null, color: u.color || null });
    }
    for (const a of avisos || []) {
      if (a.lat == null || a.lng == null) continue;
      const def = TIPOS_AVISO[a.tipo];
      out.push({ id: `aviso-${a.id}`, tipo: "aviso", clase: a.tipo, lat: Number(a.lat), lng: Number(a.lng), color: def ? def[2] : null, sub: haceCuantoCorto(a.created_at), aviso: a });
    }
    for (const a of alertasV2 || []) {
      if (a.lat == null || a.lng == null) continue;
      const def = ALERTA_TIPOS.find(x => x.id === a.tipo);
      out.push({ id: `alerta-${a.id}`, tipo: "alerta", lat: Number(a.lat), lng: Number(a.lng), icono: def ? def.e : "⚠️", color: a.tipo === "robo" ? "#B3372C" : "#111111", alerta: a });
    }
    if (rutaV2 && rutaV2.length > 1) {
      const fin = rutaV2[rutaV2.length - 1];
      const info = navInfoRef.current;
      out.push({ id: "destino", tipo: "destino", lat: fin.lat, lng: fin.lng, label: info && info.destino ? info.destino : null });
    }
    // Lo del recorrido: paradas (pin chico con el nombre) y fotos/videos (miniatura
    // circular; tocar abre el visor). Y la meta del circuito activo, con bandera.
    ((paradas || []).concat(paradasPlanV2 || [])).forEach((p, i) => {
      if (p.lat == null || p.lng == null) return;
      out.push({ id: `parada-${p.ts || i}`, tipo: "parada", lat: Number(p.lat), lng: Number(p.lng), label: p.nombre || `Parada ${i + 1}` });
    });
    (paradasNavV2 || []).forEach((p, i) => {
      out.push({ id: `paradanav-${i}`, tipo: "parada", lat: Number(p.lat), lng: Number(p.lng), label: `${i + 1}. ${p.nombre || "Parada"}` });
    });
    (fotos || []).forEach((f, i) => {
      if (f.lat == null || f.lng == null) return;
      out.push({ id: `foto-${f.ts || i}`, tipo: "foto", lat: Number(f.lat), lng: Number(f.lng), avatarUrl: (f.type === "video" ? cldPoster(f.url, 120) : cldOpt(f.url, 120)) || null, icono: f.type === "video" ? "video" : null, foto: f });
    });
    if (circuito && circuito.meta && circuito.meta.lat != null) {
      out.push({ id: "circuito-meta", tipo: "circuito", lat: circuito.meta.lat, lng: circuito.meta.lng, label: circuito.nombre || "Meta" });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usarNavV2, ridersV2, espontaneoV2, vivoPartsV2, avisos, alertasV2, rutaV2, navegando, paradas, paradasPlanV2, paradasNavV2, fotos, circuito]);
  // El trazado del circuito activo sobre el lienzo v2 (si vino con polyline)
  const trazadoCircuitoV2 = useMemo(() => {
    if (!usarNavV2 || !circuito || !circuito.polyline) return null;
    try { const t = decodificarPolyline(circuito.polyline); return t.length > 1 ? t : null; } catch (e) { return null; }
  }, [usarNavV2, circuito]);
  const reanudarRef = useRef(null);
  const pararWatchPausa = () => {
    if (watchPausaRef.current) { const w = watchPausaRef.current; watchPausaRef.current = null; try { w.stop(); } catch (e) { /* */ } }
    if (timerRetomarRef.current) { clearInterval(timerRetomarRef.current); timerRetomarRef.current = null; }
    avisoRetomarRef.current = false;
    bufferPausaRef.current = [];
    setAvisoRetomar(null);
  };
  const iniciarWatchPausa = async () => {
    if (watchPausaRef.current) return;
    pausaPosRef.current = posActualRef.current ? { ...posActualRef.current } : null;
    umbralPausaKmRef.current = 0.25;
    bufferPausaRef.current = [];
    const w = await startGeoWatch((gp) => {
      if (!watchPausaRef.current) return; // ya se retomó
      if (gp.acc != null && gp.acc > 40) return;
      const p = { lat: gp.lat, lng: gp.lng, ts: gp.t || Date.now(), v: gp.v, acc: gp.acc, ...(gp.alt != null ? { a: Math.round(gp.alt) } : {}) };
      posActualRef.current = { lat: p.lat, lng: p.lng };
      setPosActual({ lat: p.lat, lng: p.lng });
      dibujarPosGPS(p);
      alimentarFixV2(gp);
      actualizarVel(gp, null);
      actualizarAlt(gp);
      if (!pausaPosRef.current) { pausaPosRef.current = { lat: p.lat, lng: p.lng }; return; }
      if (haversineKm(pausaPosRef.current, p) < umbralPausaKmRef.current) return; // sigue en la parada
      bufferPausaRef.current.push(p);
      if (bufferPausaRef.current.length > 4000) bufferPausaRef.current.splice(0, 500);
      if (avisoRetomarRef.current) return;
      avisoRetomarRef.current = true;
      try { navigator.vibrate && navigator.vibrate([200, 100, 200, 100, 200]); } catch (e) { /* */ }
      hablarNav("Estás en movimiento con la grabación en pausa. Si no respondés, la retomo en un minuto.");
      let seg = 60;
      setAvisoRetomar({ seg });
      timerRetomarRef.current = setInterval(() => {
        seg -= 1;
        if (seg <= 0) {
          clearInterval(timerRetomarRef.current); timerRetomarRef.current = null;
          if (reanudarRef.current) reanudarRef.current(true);
          return;
        }
        setAvisoRetomar({ seg });
      }, 1000);
    }, () => {});
    if (watchPausaRef.current) { try { w.stop(); } catch (e) { /* */ } } else watchPausaRef.current = w;
  };
  const seguirEnPausa = () => {
    if (timerRetomarRef.current) { clearInterval(timerRetomarRef.current); timerRetomarRef.current = null; }
    avisoRetomarRef.current = false;
    setAvisoRetomar(null);
    bufferPausaRef.current = [];
    pausaPosRef.current = posActualRef.current ? { ...posActualRef.current } : null;
    umbralPausaKmRef.current = 2; // ya dijiste que no: se vuelve a preguntar recién 2 km más allá
  };
  // Unidad del velocímetro: km/h o millas (tocándolo se alterna; queda guardado)
  const [unidadVel, setUnidadVel] = useState(() => { try { return localStorage.getItem("olbos_unidad_vel") || "kmh"; } catch (e) { return "kmh"; } });
  const alternarUnidadVel = () => setUnidadVel(u => { const n = u === "kmh" ? "mph" : "kmh"; try { localStorage.setItem("olbos_unidad_vel", n); } catch (e) { /* */ } return n; });
  const actualizarVel = (gp, buffer) => {
    if (gp.v != null && gp.v >= 0) { setVelKmh(Math.round(gp.v * 3.6)); return; }
    // Sin velocidad del GPS: derivarla de los dos últimos puntos
    const pts = buffer || [];
    if (pts.length >= 2) {
      const a = pts[pts.length - 2], b = pts[pts.length - 1];
      const dt = ((b.ts || 0) - (a.ts || 0)) / 1000;
      if (dt > 0 && dt < 30) setVelKmh(Math.round(haversineKm(a, b) / (dt / 3600)));
    }
  };
  // Velocidad promedio EN MOVIMIENTO de los últimos ~10 min (para la ETA propia:
  // los semáforos y paradas no ensucian el promedio — solo cuenta >10 km/h)
  const velMovilReciente = () => {
    const pts = puntosRef.current.length >= 5 ? puntosRef.current : pasivoRef.current;
    if (!pts || pts.length < 5) return null;
    const ahora = (pts[pts.length - 1].ts) || Date.now();
    let dist = 0, tMov = 0;
    for (let i = pts.length - 1; i > 0; i--) {
      const a = pts[i - 1], b = pts[i];
      if (!a.ts || !b.ts) continue;
      if (ahora - b.ts > 10 * 60000) break;
      const dt = (b.ts - a.ts) / 1000;
      if (dt <= 0 || dt > 60) continue;
      const dKm = haversineKm(a, b);
      if (dKm / (dt / 3600) >= 10) { dist += dKm; tMov += dt; }
    }
    if (tMov < 120) return null; // menos de 2 min en movimiento: todavía sin dato propio
    return { kmh: dist / (tMov / 3600), datosMin: tMov / 60 };
  };
  // Panel de abajo plegable (en horizontal el mapa necesita toda la pantalla)
  const [panelChico, setPanelChico] = useState(false);
  const inputFotoRef = useRef(null);
  const inputVideoRef = useRef(null);
  // Deslizar el panel de abajo con el dedo (además de tocarlo): arriba abre, abajo achica/pliega
  const swipePanelRef = useRef(null);
  const swipeInicio = (e) => { const p = e.touches && e.touches[0]; swipePanelRef.current = p ? { x: p.clientX, y: p.clientY } : null; };
  const swipeFin = (e, alSubir, alBajar) => {
    const ini = swipePanelRef.current; swipePanelRef.current = null;
    const p = e.changedTouches && e.changedTouches[0];
    if (!ini || !p) return false;
    const dy = p.clientY - ini.y, dx = p.clientX - ini.x;
    if (Math.abs(dy) < 35 || Math.abs(dy) < Math.abs(dx) * 1.3) return false;
    if (dy < 0 && alSubir) alSubir(); else if (dy > 0 && alBajar) alBajar();
    return true;
  };
  // ── Modo navegación limpio (con y sin v2) ──
  // Mientras hay destino, sobre el mapa quedan SOLO el banner de giros, el velocímetro
  // (con altura y temperatura adentro), ⚠️ avisar, el micrófono y 📍 recentrar si se
  // soltó el mapa. El resto (rumbo arriba, día/noche, nafta, alerta a la comunidad,
  // circuito, tipo de mapa) va detrás de un único botón ⋯. El panel inferior se
  // pliega a una barra que se abre al tocarla y se vuelve a plegar sola a los 8 s.
  // Sin destino, todo sigue como siempre. Pedido de Carlos: "mucho más clean, como Waze".
  const [menuMapa, setMenuMapa] = useState(false);
  const [panelNavAbierto, setPanelNavAbierto] = useState(false);
  const panelNavTimerRef = useRef(null);
  const alertasAbrirRef = useRef(null); // lo llena AlertasMapaOverlay: abre su hoja de reporte
  // Modo limpio: navegando (con y sin v2) y también en "rodada libre" sobre el lienzo v2
  // (con la bandera, sin destino): mapa a pantalla completa, controles flotantes, barra plegada.
  const modoLimpio = (navegando || usarNavV2) && (estado === "grabando" || estado === "pausada" || estado === "lista");
  const panelPlegado = modoLimpio && !panelNavAbierto;
  // Dónde arrancan los avisos (clima, red, corredor) y los carteles: debajo del banner de
  // giros navegando (84 px) o de la pastilla "¿A dónde vamos?" en rodada libre (44 px)
  const topAvisos = modoLimpio ? (navegando ? "calc(var(--sat, 0px) + 154px)" : "calc(var(--sat, 0px) + 114px)") : 62;
  const tocarPanelNav = () => {
    if (panelNavTimerRef.current) clearTimeout(panelNavTimerRef.current);
    panelNavTimerRef.current = setTimeout(() => setPanelNavAbierto(false), 8000);
  };
  useEffect(() => {
    if (!modoLimpio || !panelNavAbierto) {
      if (panelNavTimerRef.current) { clearTimeout(panelNavTimerRef.current); panelNavTimerRef.current = null; }
      return;
    }
    tocarPanelNav();
    return () => { if (panelNavTimerRef.current) { clearTimeout(panelNavTimerRef.current); panelNavTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoLimpio, panelNavAbierto]);
  useEffect(() => { if (!modoLimpio) { setMenuMapa(false); setPanelNavAbierto(false); } }, [modoLimpio]);
  // ── Zoom con los dedos, en todos los modos (v2 y Google) ──
  // Pinch, doble toque o rueda = el rider eligió el zoom: la cámara deja de forzar el
  // zoom por velocidad (sigue centrando) hasta tocar 📍 / "auto" o 30 s sin tocar el mapa.
  const [zoomManual, setZoomManual] = useState(false);
  const zoomManualRef = useRef(false);
  const zoomManualTimerRef = useRef(null);
  const gestoGoogleRef = useRef(false); // dos dedos sobre el mapa de Google: no se recentra mientras dura
  const mapaContRef = useRef(null);     // el contenedor del mapa (Google abajo, v2 arriba)
  const volverZoomAuto = () => {
    if (zoomManualTimerRef.current) { clearTimeout(zoomManualTimerRef.current); zoomManualTimerRef.current = null; }
    zoomManualRef.current = false;
    setZoomManual(false);
  };
  const marcarZoomManual = () => {
    zoomManualRef.current = true;
    setZoomManual(m => (m ? m : true));
    if (zoomManualTimerRef.current) clearTimeout(zoomManualTimerRef.current);
    zoomManualTimerRef.current = setTimeout(volverZoomAuto, 30000);
  };
  useEffect(() => () => { if (zoomManualTimerRef.current) clearTimeout(zoomManualTimerRef.current); }, []);
  // Los gestos se escuchan en el contenedor (sirve para Google y para el v2): dos dedos,
  // doble toque o rueda sobre el mapa mismo (no sobre un botón que esté encima)
  useEffect(() => {
    const cont = mapaContRef.current;
    if (!cont) return;
    const sobreMapa = (e) => { const t = e.target; return !!(t && t.closest && t.closest(".olbos-mapa-google, .olbos-nav")); };
    const onTouchStart = (e) => { if (e.touches && e.touches.length >= 2 && sobreMapa(e)) { gestoGoogleRef.current = true; marcarZoomManual(); } };
    const onTouchEnd = (e) => { if (!e.touches || e.touches.length === 0) gestoGoogleRef.current = false; };
    const onDbl = (e) => { if (sobreMapa(e)) marcarZoomManual(); };
    const onWheel = (e) => { if (sobreMapa(e)) marcarZoomManual(); };
    cont.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    cont.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
    cont.addEventListener("touchcancel", onTouchEnd, { capture: true, passive: true });
    cont.addEventListener("dblclick", onDbl, true);
    cont.addEventListener("wheel", onWheel, { capture: true, passive: true });
    return () => {
      cont.removeEventListener("touchstart", onTouchStart, true);
      cont.removeEventListener("touchend", onTouchEnd, true);
      cont.removeEventListener("touchcancel", onTouchEnd, true);
      cont.removeEventListener("dblclick", onDbl, true);
      cont.removeEventListener("wheel", onWheel, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  // Clima al activar el mapa: si viene lluvia o viento fuerte, avisar para salir
  // preparado. Open-Meteo: gratis, sin key, una consulta por sesión de mapa.
  const [avisoClima, setAvisoClima] = useState(null);
  const climaPedido = useRef(false);
  useEffect(() => {
    if (climaPedido.current || !posActual) return;
    if (estado !== "lista" && estado !== "grabando") return;
    climaPedido.current = true;
    (async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${posActual.lat}&longitude=${posActual.lng}&current=temperature_2m,precipitation,wind_speed_10m&hourly=precipitation_probability,wind_gusts_10m&forecast_hours=8&timezone=auto`);
        const d = await r.json();
        const probs = (d?.hourly?.precipitation_probability || []).slice(0, 8).filter(x => x != null);
        const rafagas = (d?.hourly?.wind_gusts_10m || []).slice(0, 8).filter(x => x != null);
        const maxProb = probs.length ? Math.max(...probs) : 0;
        const maxRafaga = rafagas.length ? Math.max(...rafagas) : 0;
        const lloviendo = (d?.current?.precipitation || 0) > 0.1;
        let msj = null;
        if (lloviendo) msj = "☔ Está lloviendo en tu zona — piso mojado, ojo con las tapas de cámara y la pintura vial.";
        else if (maxProb >= 60) msj = `☔ Lluvia muy probable en las próximas horas (${maxProb}%). Llevá el impermeable.`;
        else if (maxProb >= 35) msj = `🌦️ Puede llover en las próximas horas (${maxProb}%). Impermeable a mano.`;
        if (maxRafaga >= 50) msj = (msj ? msj + " " : "") + `💨 Ráfagas de hasta ${Math.round(maxRafaga)} km/h — firme con el manubrio.`;
        if (msj) setAvisoClima(msj);
      } catch (e) { /* sin clima no pasa nada */ }
    })();
  }, [posActual, estado]);

  // Wake Lock — mantiene la pantalla encendida durante la grabación
  const activarWakeLock = async () => {
    try {
      if ("wakeLock" in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        // Si el usuario cambia de pestaña/app y vuelve, re-pedir el lock
        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      }
    } catch (err) {
      console.warn("Wake Lock no disponible:", err);
    }
  };

  const liberarWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch {}
  };

  // Re-activar Wake Lock cuando la pestaña vuelve a foco (si estábamos grabando o en el mapa)
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === "visible" && (estado === "grabando" || estado === "lista")) {
        await activarWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [estado]);

  const stats = calcEstadisticas(puntos);
  // El post del recorrido viene marcado por default. La app ya arma el mapa, las fotos
  // y las stats: si además hay que acordarse de tildar una casilla, no lo hace nadie
  // (entran 68 riders por día y publican 3, así el feed se queda quieto toda la semana).
  // Se respeta a quien no quiere: si lo desmarcó una vez, la próxima viene desmarcado.
  // Y no se ofrece en recorridos de menos de 2 km, que son pruebas o vueltas a la esquina.
  useEffect(() => {
    if (!showFinalizar) return;
    let prefiereNo = false;
    try { prefiereNo = localStorage.getItem("olbos_no_publicar_feed") === "1"; } catch (e) { /* */ }
    setPublicarEnFeed(!prefiereNo && stats.dist >= KM_MIN_PUBLICAR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFinalizar]);

  // Timer (1s tick). Se deriva de los puntos (tiempo en movimiento) + el tramo en curso,
  // así al retomar un recorrido pausado —incluso días después— el tiempo sigue siendo correcto.
  useEffect(() => {
    if (estado !== "grabando") return;
    const interval = setInterval(() => {
      const pts = puntosRef.current;
      let t = calcEstadisticas(pts).dur;
      if (pts.length) {
        const gap = (Date.now() - pts[pts.length - 1].ts) / 1000;
        if (gap > 0 && gap < 300) t += gap;
      }
      setTiempoTranscurrido(Math.round(t));
    }, 1000);
    return () => clearInterval(interval);
  }, [estado]);

  // Entrar al mapa SIN grabar todavía: te ves (y te ven, si es una rodada) con
  // la posición en vivo, y el botón Iniciar decide cuándo empieza el recorrido
  // de verdad. Abrir el mapa ya no crea trazas basura en la base.
  const [buscandoGps, setBuscandoGps] = useState(false);
  const entrarAlMapa = () => {
    if (buscandoGps) return; // ya está buscando: no encimar pedidos
    setError(null);
    setBuscandoGps(true);
    // Tope propio: en una computadora sin servicios de ubicación, Chrome puede no
    // contestar NUNCA (ni éxito, ni error, ni el timeout del propio pedido). Sin esto
    // el botón quedaba mudo para siempre.
    const tope = setTimeout(() => {
      setBuscandoGps(false);
      setError("No pudimos obtener tu ubicación. En una computadora, activá los servicios de ubicación del sistema o probá desde el teléfono, que es donde el mapa rinde de verdad.");
      setEstado("preparando");
    }, 20000);
    obtenerPosicion().then(async (pos) => {
      clearTimeout(tope);
      setBuscandoGps(false);
      const p = { lat: pos.lat, lng: pos.lng };
      posActualRef.current = p;
      setPosActual(p);
      setEstado("lista");
      alimentarFixV2({ lat: pos.lat, lng: pos.lng, v: pos.v, acc: pos.acc }); // el lienzo v2 arranca centrado sin esperar al watch
      activarWakeLock();
      // Watch liviano: solo posición. Si hay un compartido en vivo activo, la
      // posición se empuja igual — el que mira el link no te ve congelado.
      // Aviso (una sola vez): el camino aporta anónimo a la red, con opt-out en el perfil
      try {
        if (!localStorage.getItem("olbos_aviso_red")) { setAvisoRed(true); localStorage.setItem("olbos_aviso_red", "1"); }
      } catch (e) { /* */ }
      bgSesionActualizar({ pasivo: true }); // lo andado con la pantalla bloqueada queda en el servidor
      watchLibreRef.current = await startGeoWatch((gp) => {
        const p2 = { lat: gp.lat, lng: gp.lng };
        posActualRef.current = p2;
        setPosActual(p2);
        // Grabando, los km los suma el recorrido: el pasivo no acumula (si no, el mismo
        // viaje contaba dos veces en el ranking; @franvoda, 10-oct: 589 km grabados + 589 pasivos)
        if (estadoGrabRef.current === "grabando" || estadoGrabRef.current === "pausada") return;
        pasivoRef.current.push({ lat: gp.lat, lng: gp.lng, ts: gp.t || Date.now() });
        alimentarPasivoV2(gp.t || Date.now());
        actualizarVel(gp, pasivoRef.current);
        actualizarAlt(gp);
        // La flecha y la cámara también se mueven SIN grabar: navegando en modo
        // lista el mapa quedaba fijo y recién se acomodaba al apretar Grabar
        // (reporte de Carlos). Mismo filtro de precisión que el watch de grabación.
        // Sin grabar no se filtra tan fino como al grabar (ahí 25 m, porque esos
        // puntos van al recorrido y al ranking): acá solo se dibuja y se orienta el
        // mapa, y en la web el GPS del teléfono casi nunca baja de 25 m de precisión.
        // Con el filtro viejo la flecha no se movía ni el mapa rotaba (reporte de
        // Carlos, 11-sep). Lo verdaderamente malo (> 60 m) se sigue descartando.
        if (gp.acc == null || gp.acc <= 60) dibujarPosGPS({ lat: gp.lat, lng: gp.lng, ts: gp.t || Date.now(), v: gp.v, acc: gp.acc });
        alimentarFixV2(gp);
        const ts = gp.t || Date.now();
        if (vivoRef.current && ts - ultimoVivoPushRef.current >= 8000) {
          ultimoVivoPushRef.current = ts;
          supabase.from("ubicaciones_vivo").update({
            lat: p2.lat, lng: p2.lng, ultimo_update: new Date().toISOString(),
          }).eq("id", vivoRef.current.id).then(() => {}, () => {});
          pushVivoExtras();
        }
      }, () => {});
    }, (err) => {
      clearTimeout(tope);
      setBuscandoGps(false);
      setError(err && err.code === 1 ? (esNativo() ? "Olbos no tiene permiso de ubicación. Abrí los ajustes del teléfono, dale permiso a Olbos y volvé a entrar al mapa." : "Permiso de GPS denegado. Activá la ubicación y recargá la página.") : "No se pudo iniciar el GPS");
      setEstado("preparando");
    });
  };
  // Un toque explícito con el grabador ya montado en su pantalla de entrada (web):
  // entra. Se ignora si ya está adentro, grabando, o buscando el GPS.
  const entrarAlMapaRef = useRef(null);
  entrarAlMapaRef.current = entrarAlMapa;
  useEffect(() => {
    const h = () => {
      if (estadoRecRef.current !== "preparando" || !entrarAlMapaRef.current) return;
      // Todavía no sabemos si hay un recorrido sin terminar: entrar recién cuando se sepa.
      // Antes esta entrada salteaba la pantalla de "Tenés un recorrido sin terminar" y
      // el rider arrancaba uno nuevo encima del pausado (06-oct-2026).
      if (!previaChequeadaRef.current) { entradaPendienteRef.current = true; return; }
      if (trazaPreviaRef.current) return;
      entrarAlMapaRef.current();
    };
    window.addEventListener("olbos-entrar-al-mapa", h);
    return () => window.removeEventListener("olbos-entrar-al-mapa", h);
  }, []);

  // Iniciar grabación
  // Arranque automático con INTENCIÓN: el rider puso un destino o entró a una rodada.
  // Solo abrir el mapa no alcanza: ahí no se graba nada. Se avisa con un cartel para
  // que quede claro que empezó a grabar, y al final se puede descartar.
  const autoArranqueRef = useRef(false);
  const [avisoAutoGrabo, setAvisoAutoGrabo] = useState(false);
  const arrancarSiHayIntencion = (motivo) => {
    if (autoArranqueRef.current) return;
    if (estadoGrabRef.current !== "lista") return;
    autoArranqueRef.current = true;
    setAvisoAutoGrabo(motivo);
    setTimeout(() => setAvisoAutoGrabo(false), 6000);
    iniciar();
  };

  const iniciar = () => {
    setError(null);
    // El watch liviano del modo lista se apaga: lo reemplaza el de grabación
    if (watchLibreRef.current) { watchLibreRef.current.stop(); watchLibreRef.current = null; }

    const arrancarCon = async (coords) => {
      const ahora = Date.now();
      inicioRef.current = ahora;
      tiempoPausadoRef.current = 0;
      ultimoPuntoTsRef.current = ahora;

      const puntoInicial = {
        lat: coords.lat,
        lng: coords.lng,
        ts: ahora,
        v: coords.v || 0,
        acc: coords.acc,
      };
      puntosRef.current = [puntoInicial];
      setPuntos([puntoInicial]);
      setPosActual({ lat: puntoInicial.lat, lng: puntoInicial.lng });

      // Crear traza en BD
      const { data, error: errIns } = await supabase.from("trazas").insert(conVehiculo({
        user_id: userId,
        titulo: `${vehiculoActual() === "auto" ? "Viaje en auto" : "Recorrido"} ${new Date().toLocaleDateString("es-AR", { day: "numeric", month: "short" })}`,
        puntos: [puntoInicial],
        estado: "grabando",
        iniciada_en: new Date(ahora).toISOString(),
      })).select().single();

      if (errIns) { setError("Error creando recorrido: " + errIns.message); setEstado("preparando"); return; }
      setTrazaId(data.id);
      bgSesionActualizar({ traza_id: data.id }); // los fixes también quedan en el servidor (bg_puntos)
      tramosGoogleRef.current.forEach(p => { try { p.setMap(null); } catch (e) { /* */ } });
      tramosGoogleRef.current = [];

      // Iniciar watch (sigue grabando en segundo plano en la app nativa)
      watchIdRef.current = await startGeoWatch(
        (gp) => {
          alimentarFixV2(gp); // navegador v2: a 1 Hz, antes del filtro y del espaciado de grabación
          if (gp.acc != null && gp.acc > 25) return; // descartar fixes de baja precisión (jitter)
          const ts = gp.t || Date.now(); // hora real del fix GPS (no del momento en que llega el callback)
          if (ts - ultimoPuntoTsRef.current < ((circuitoRef.current || nuevoCircRef.current) ? 900 : 2000)) return;
          ultimoPuntoTsRef.current = ts;
          const punto = { lat: gp.lat, lng: gp.lng, ts, v: gp.v, acc: gp.acc, ...(gp.alt != null ? { a: Math.round(gp.alt) } : {}), ...(circuitoRef.current && inclActualRef.current != null ? { i: Math.round(inclActualRef.current) } : {}) };
          actualizarAlt(gp);
          puntosRef.current = [...puntosRef.current, punto];
          procesarPuntoCircuito(punto, puntosRef.current.length - 1);
          setPuntos(puntosRef.current);
          setPosActual({ lat: punto.lat, lng: punto.lng });
          actualizarVel(gp, puntosRef.current);
          // La flecha se dibuja DIRECTO acá, sin pasar por el estado de React.
          // Antes iba GPS → estado → re-render → efecto → marker, y si cualquier
          // eslabón fallaba (por ejemplo al pausar y reanudar) la velocidad seguía
          // actualizándose pero la flecha quedaba clavada — reporte de Carlos.
          dibujarPosGPS(punto);

          // Autoguardado: cada 30 s al principio, más espaciado cuando la traza crece
          if (ts - ultimoAutoSaveRef.current >= cadaCuantoAutoSave(puntosRef.current.length) && !autoSaveEnVueloRef.current) {
            ultimoAutoSaveRef.current = ts;
            autoSaveEnVueloRef.current = true;
            supabase.from("trazas").update({
              puntos: puntosRef.current,
              updated_at: new Date().toISOString(),
            }).eq("id", data.id).then(
              (r) => { autoSaveEnVueloRef.current = false; if (r && r.error) ultimoAutoSaveRef.current = 0; /* falló: reintenta con el próximo punto */ },
              () => { autoSaveEnVueloRef.current = false; ultimoAutoSaveRef.current = 0; }
            );
          }

          // Empujar al link "en vivo" DESDE el callback del GPS (no desde un useEffect:
          // los efectos de React no corren con la app en background → los que miraban
          // el link veían el punto viejo y sin la línea del recorrido).
          if (vivoRef.current && ts - ultimoVivoPushRef.current >= 8000) {
            ultimoVivoPushRef.current = ts;
            supabase.from("ubicaciones_vivo").update({
              lat: punto.lat, lng: punto.lng,
              ultimo_update: new Date().toISOString(),
              recorrido: downsampleRecorrido(puntosRef.current),
            }).eq("id", vivoRef.current.id).then(() => {}, () => {});
            pushVivoExtras();
          }
        },
        (err) => console.error("Watch error:", err)
      );

      setEstado("grabando");
      activarWakeLock();
      pedirIgnorarBateria().then(() => pedirPermisoVolverDeLlamada(), () => {});
      registrarUso("grabacion_inicio", { rodada: !!grupoVivo });
      // En el navegador (web / PWA) no hay GPS de fondo: con la pantalla bloqueada se corta
      // y el recorrido queda con huecos. Se avisa una vez por sesión.
      if (!esNativo()) {
        try {
          if (!sessionStorage.getItem("olbos_aviso_bloqueo")) {
            sessionStorage.setItem("olbos_aviso_bloqueo", "1");
            setAvisoWebBloqueo(true);
            setTimeout(() => setAvisoWebBloqueo(false), 15000);
          }
        } catch (e) { /* */ }
      }
    };

    if (posActualRef.current) {
      // Ya estamos en el mapa con posición fresca: arranca al toque
      arrancarCon({ lat: posActualRef.current.lat, lng: posActualRef.current.lng, v: 0, acc: null });
    } else {
      setEstado("preparando");
      obtenerPosicion().then(
        (pos) => arrancarCon({ lat: pos.lat, lng: pos.lng, v: pos.v || 0, acc: pos.acc }),
        (err) => {
          setError(err && err.code === 1 ? (esNativo() ? "Olbos no tiene permiso de ubicación. Abrí los ajustes del teléfono, dale permiso a Olbos y volvé a entrar al mapa." : "Permiso de GPS denegado. Activá la ubicación y recargá la página.") : "No se pudo iniciar el GPS");
          setEstado("preparando");
        });
    }
  };

  const pausar = async () => {
    // Primero el watch liviano de la pausa, DESPUÉS se apaga el de grabación: así el
    // plugin nativo nunca se queda sin consumidores y no se apaga/prende de nuevo.
    await iniciarWatchPausa();
    if (watchIdRef.current !== null) {
      watchIdRef.current.stop();
      watchIdRef.current = null;
    }
    ultimaPausaRef.current = Date.now();
    setEstado("pausada");
    liberarWakeLock();
    // Persistir el estado pausado + todo lo grabado, para poder retomar tras cerrar la app.
    if (trazaId) {
      supabase.from("trazas").update({
        puntos: puntosRef.current,
        paradas: paradasRef.current,
        fotos: fotosRef.current,
        estado: "pausada",
        updated_at: new Date().toISOString(),
      }).eq("id", trazaId).then(() => {}, () => {});
    }
  };

  const reanudar = async (auto = false) => {
    const wPausa = watchPausaRef.current;
    watchPausaRef.current = null; // el watch de pausa deja de acumular ya mismo
    if (timerRetomarRef.current) { clearInterval(timerRetomarRef.current); timerRetomarRef.current = null; }
    avisoRetomarRef.current = false;
    setAvisoRetomar(null);
    // Lo andado desde que te detectamos en movimiento entra al recorrido: no se pierde
    // el tramo entre que arrancaste y que tocaste (o se tocó solo) Retomar.
    const extra = bufferPausaRef.current.filter(p => p.acc == null || p.acc <= 25);
    bufferPausaRef.current = [];
    let finPausa = Date.now();
    if (extra.length) {
      const limpio = [];
      let ultimoTs = 0;
      for (const p of extra) { if (p.ts - ultimoTs >= 2000) { limpio.push(p); ultimoTs = p.ts; } }
      if (limpio.length) {
        puntosRef.current = [...puntosRef.current, ...limpio];
        setPuntos(puntosRef.current);
        finPausa = limpio[0].ts || finPausa;
      }
    }
    if (ultimaPausaRef.current) {
      tiempoPausadoRef.current += Math.max(0, finPausa - ultimaPausaRef.current);
      ultimaPausaRef.current = null;
    }
    if (auto === true) { setAvisoRetomado(true); setTimeout(() => setAvisoRetomado(false), 7000); }
    // Volver a marcar la traza como "grabando" en la base
    if (trazaId) supabase.from("trazas").update({ estado: "grabando" }).eq("id", trazaId).then(() => {}, () => {});
    ultimoPuntoTsRef.current = Date.now();
    watchIdRef.current = await startGeoWatch(
      (gp) => {
        alimentarFixV2(gp); // navegador v2: a 1 Hz, antes del filtro y del espaciado de grabación
        if (gp.acc != null && gp.acc > 25) return; // descartar fixes de baja precisión (jitter)
        const ts = gp.t || Date.now(); // hora real del fix GPS
        if (ts - ultimoPuntoTsRef.current < ((circuitoRef.current || nuevoCircRef.current) ? 900 : 2000)) return;
        ultimoPuntoTsRef.current = ts;
        const punto = { lat: gp.lat, lng: gp.lng, ts, v: gp.v, acc: gp.acc, ...(gp.alt != null ? { a: Math.round(gp.alt) } : {}), ...(circuitoRef.current && inclActualRef.current != null ? { i: Math.round(inclActualRef.current) } : {}) };
        actualizarAlt(gp);
        puntosRef.current = [...puntosRef.current, punto];
        procesarPuntoCircuito(punto, puntosRef.current.length - 1);
        setPuntos(puntosRef.current);
        setPosActual({ lat: punto.lat, lng: punto.lng });
        actualizarVel(gp, puntosRef.current);
        dibujarPosGPS(punto); // la flecha, directo del GPS (ver comentario en iniciar)
        if (ts - ultimoAutoSaveRef.current >= cadaCuantoAutoSave(puntosRef.current.length) && !autoSaveEnVueloRef.current) {
          ultimoAutoSaveRef.current = ts;
          autoSaveEnVueloRef.current = true;
          supabase.from("trazas").update({ puntos: puntosRef.current, updated_at: new Date().toISOString() }).eq("id", trazaId).then(
            (r) => { autoSaveEnVueloRef.current = false; if (r && r.error) ultimoAutoSaveRef.current = 0; },
            () => { autoSaveEnVueloRef.current = false; ultimoAutoSaveRef.current = 0; }
          );
        }
        // Push al link "en vivo" desde el callback (ver comentario en iniciarGrabacion)
        if (vivoRef.current && ts - ultimoVivoPushRef.current >= 8000) {
          ultimoVivoPushRef.current = ts;
          supabase.from("ubicaciones_vivo").update({
            lat: punto.lat, lng: punto.lng,
            ultimo_update: new Date().toISOString(),
            recorrido: downsampleRecorrido(puntosRef.current),
          }).eq("id", vivoRef.current.id).then(() => {}, () => {});
          pushVivoExtras();
        }
      },
      (err) => console.error("Watch error:", err)
    );
    setEstado("grabando");
    activarWakeLock();
    if (wPausa) { try { wPausa.stop(); } catch (e) { /* */ } }
  };
  reanudarRef.current = reanudar;

  // ── Retomar un recorrido sin terminar (de una sesión/día anterior) ──
  // Carga la traza guardada y la deja en pausa; el usuario toca ▶ para seguir grabando.
  const cargarTrazaPrevia = (t) => {
    puntosRef.current = Array.isArray(t.puntos) ? t.puntos : [];
    setPuntos(puntosRef.current);
    paradasRef.current = Array.isArray(t.paradas) ? t.paradas : [];
    setParadas(paradasRef.current);
    fotosRef.current = Array.isArray(t.fotos) ? t.fotos : [];
    setFotos(fotosRef.current);
    setTrazaId(t.id);
    // Si esta traza tenía un link "en vivo" activo, RECONECTARLO: la referencia vive en
    // memoria de la pantalla, así que al cerrar y volver se perdía — el registro seguía
    // activo en la base pero nadie le empujaba posiciones, y los que miraban el link
    // quedaban viendo el mapa congelado en el punto de la pausa (pasó el 08-ago-2026).
    (async () => {
      const { data: vivo } = await supabase.from("ubicaciones_vivo")
        .select("*")
        .eq("traza_id", t.id)
        .eq("activo", true)
        .gt("expira_en", new Date().toISOString())
        .order("ultimo_update", { ascending: false })
        .limit(1);
      const v = vivo && vivo[0];
      if (v) {
        vivoRef.current = v;
        setVivoRegistro(v);
        ultimoVivoPushRef.current = 0; // empujar apenas llegue el próximo fix GPS
      }
    })();
    const ultimo = puntosRef.current[puntosRef.current.length - 1];
    if (ultimo) setPosActual({ lat: ultimo.lat, lng: ultimo.lng });
    inicioRef.current = puntosRef.current[0]?.ts || Date.now();
    ultimaPausaRef.current = Date.now();
    setTiempoTranscurrido(calcEstadisticas(puntosRef.current).dur);
    setTrazaPrevia(null);
    trazaPreviaRef.current = null;
    setEstado("pausada");
    // Queda en pausa: el watch liviano sigue tu posición y avisa si arrancás sin reanudar.
    // Reemplaza al watch del modo lista si venías del mapa sin grabar.
    iniciarWatchPausa().then(() => { if (watchLibreRef.current) { watchLibreRef.current.stop(); watchLibreRef.current = null; } });
    // Recuperar lo andado que NO llegó a la traza: si la app se cerró (limpiar apps abiertas)
    // el último autoguardado puede estar viejo o haber fallado, pero el GPS nativo siguió
    // subiendo los fixes a bg_puntos con el id de esta traza (pedido de @MatiAguilar_26: 20 km perdidos).
    (async () => {
      try {
        const ultTs = (puntosRef.current[puntosRef.current.length - 1] || {}).ts || 0;
        const { data: extra } = await supabase.from("bg_puntos").select("lat,lng,v,acc,alt,t")
          .eq("traza_id", t.id).gt("t", new Date(ultTs).toISOString()).order("t").limit(20000);
        if (!extra || extra.length === 0) return;
        let last = ultTs;
        const nuevos = [];
        extra.forEach(r => {
          const ts = new Date(r.t).getTime();
          if (r.lat == null || !ts || ts - last < 2000) return;
          if (r.acc != null && r.acc > 25) return;
          last = ts;
          nuevos.push({ lat: r.lat, lng: r.lng, ts, v: r.v || 0, acc: r.acc, ...(r.alt != null ? { a: Math.round(r.alt) } : {}) });
        });
        if (nuevos.length === 0) return;
        puntosRef.current = [...puntosRef.current, ...nuevos];
        setPuntos(puntosRef.current);
        const u = nuevos[nuevos.length - 1];
        setPosActual({ lat: u.lat, lng: u.lng });
        setTiempoTranscurrido(calcEstadisticas(puntosRef.current).dur);
        supabase.from("trazas").update({ puntos: puntosRef.current, updated_at: new Date().toISOString() }).eq("id", t.id).then(() => {}, () => {});
      } catch (e) { /* si no se puede recuperar, queda lo que había */ }
    })();
  };
  cargarTrazaPreviaRef.current = cargarTrazaPrevia;

  const retomarTrazaPrevia = () => { if (trazaPrevia) cargarTrazaPrevia(trazaPrevia); };

  // Finalizar directamente el recorrido previo (sin seguir grabando): carga los datos y
  // abre el modal de resumen.
  const finalizarTrazaPrevia = () => {
    if (!trazaPrevia) return;
    const t = trazaPrevia;
    cargarTrazaPrevia(t);
    setTituloFinal(tf => tf || t.titulo || `Recorrido del ${new Date(t.iniciada_en || Date.now()).toLocaleDateString("es-AR", { day: "numeric", month: "long" })}`);
    setShowFinalizar(true);
  };

  const descartarTrazaPrevia = async () => {
    if (!trazaPrevia) return;
    if (!window.confirm("¿Descartar el recorrido sin terminar? No se puede recuperar.")) return;
    await supabase.from("trazas").delete().eq("id", trazaPrevia.id);
    setTrazaPrevia(null);
  };

  // SOS: mandar por WhatsApp la posición exacta + el link en vivo al contacto de
  // emergencia. El número se pide una sola vez y queda en el teléfono (localStorage);
  // funciona sin señal de GPS nueva (usa la última posición conocida) y sin backend.
  const enviarSOS = () => {
    let tel = localStorage.getItem("olbos_sos_tel") || "";
    if (!tel) {
      const ingresado = window.prompt("Número de WhatsApp de tu contacto de emergencia (con código de país, sin + ni espacios — ej: 5491112345678):");
      if (!ingresado) return;
      tel = ingresado.replace(/\D/g, "");
      if (tel.length < 10) { alert("Ese número no parece válido. Probá de nuevo con el código de país adelante."); return; }
      localStorage.setItem("olbos_sos_tel", tel);
    }
    const pos = posActual || (puntosRef.current.length ? puntosRef.current[puntosRef.current.length - 1] : null);
    const partes = ["🆘 Necesito ayuda."];
    if (pos) partes.push(`Estoy acá: https://maps.google.com/?q=${pos.lat},${pos.lng}`);
    if (vivoRef.current?.share_token) partes.push(`Seguime en vivo: ${SITE_URL}/vivo/${vivoRef.current.share_token}`);
    partes.push("(enviado desde Olbos)");
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(partes.join("\n"))}`, "_blank");
  };

  // ─── DETECCIÓN DE CAÍDA ───────────────────────────────────────────────────
  // Tres condiciones ENCADENADAS, y las tres juntas son lo que evita el falso positivo:
  // (1) venías a más de 25 km/h, (2) el acelerómetro marcó un pico fuerte, (3) después
  // del pico pasaste 15 segundos sin moverte. Si se te cae el teléfono en un semáforo
  // falta la primera; si pegaste un pozo falta la tercera.
  const G_IMPACTO = 4.5;        // veces la gravedad
  const KMH_MINIMA = 25;
  const SEG_QUIETO = 15;
  const SEG_CUENTA = 30;        // lo que tiene el rider para decir "estoy bien"
  const [caida, setCaida] = useState(null);   // { fase: 'preguntando'|'avisado', queda }
  const caidaRef = useRef(null);
  const impactoRef = useRef(0);   // cuándo fue el último pico
  const movimientoRef = useRef(0); // último movimiento del teléfono
  const detectarOnRef = useRef(false);

  useEffect(() => {
    try { detectarOnRef.current = localStorage.getItem("olbos_detectar_caidas") === "1"; } catch (e) { /* */ }
    if (!userId) return;
    supabase.from("perfiles").select("detectar_caidas").eq("id", userId).maybeSingle()
      .then(({ data }) => {
        const v = !!(data && data.detectar_caidas);
        detectarOnRef.current = v;
        try { localStorage.setItem("olbos_detectar_caidas", v ? "1" : "0"); } catch (e) { /* */ }
      }, () => {});
  }, [userId]);

  // El acelerómetro solo se escucha mientras se está GRABANDO: fuera de ahí no se sabe
  // si el teléfono está arriba de la moto, y el sensor consume batería.
  useEffect(() => {
    if (estado !== "grabando" || !detectarOnRef.current) return;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a || a.x == null) return;
      const g = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z) / 9.81;
      const ahora = Date.now();
      // Movimiento normal del teléfono: sirve para saber si después del golpe quedó quieto
      if (Math.abs(g - 1) > 0.35) movimientoRef.current = ahora;
      if (g < G_IMPACTO || caidaRef.current) return;
      // ¿Venía andando? Se mira la velocidad de los últimos puntos del GPS
      const pts = puntosRef.current;
      const vel = pts.length ? Number(pts[pts.length - 1].v || 0) * 3.6 : 0;
      if (vel < KMH_MINIMA) return;
      impactoRef.current = ahora;
    };
    window.addEventListener("devicemotion", onMotion);
    // ¿Hubo un impacto y desde entonces el teléfono no se movió más?
    const reloj = setInterval(() => {
      if (caidaRef.current || !impactoRef.current) return;
      const desdeImpacto = (Date.now() - impactoRef.current) / 1000;
      if (desdeImpacto < SEG_QUIETO) return;
      if (movimientoRef.current > impactoRef.current + 1500) { impactoRef.current = 0; return; } // se movió: era un pozo
      const pts = puntosRef.current;
      const vel = pts.length ? Number(pts[pts.length - 1].v || 0) * 3.6 : 0;
      if (vel > 8) { impactoRef.current = 0; return; } // sigue andando
      impactoRef.current = 0;
      arrancarCuentaCaida();
    }, 1000);
    return () => { window.removeEventListener("devicemotion", onMotion); clearInterval(reloj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  const arrancarCuentaCaida = () => {
    if (caidaRef.current) return;
    caidaRef.current = { fase: "preguntando" };
    setCaida({ fase: "preguntando", queda: SEG_CUENTA });
    try { navigator.vibrate && navigator.vibrate([400, 200, 400, 200, 400]); } catch (e) { /* */ }
    let queda = SEG_CUENTA;
    const t = setInterval(() => {
      queda -= 1;
      if (queda <= 0) {
        clearInterval(t);
        avisarCaida();
        return;
      }
      setCaida(c => (c ? { ...c, queda } : c));
      try { navigator.vibrate && navigator.vibrate(250); } catch (e) { /* */ }
    }, 1000);
    caidaRef.current.timer = t;
  };

  const estoyBien = () => {
    if (caidaRef.current?.timer) clearInterval(caidaRef.current.timer);
    caidaRef.current = null;
    setCaida(null);
    // Los falsos positivos se anotan sin identidad: son los que nos dejan afinar
    // los umbrales con datos reales en vez de a ojo.
    supabase.from("caidas_falsas").insert({ velocidad_kmh: null }).then(() => {}, () => {});
  };

  const avisarCaida = () => {
    caidaRef.current = { fase: "avisado" };
    setCaida({ fase: "avisado", queda: 0 });
    const pos = posActual || (puntosRef.current.length ? puntosRef.current[puntosRef.current.length - 1] : null);
    const grupo = grupoVivo || grupoEnVivoId || grupoIdPreseleccionado || null;
    const fila = {
      grupo_id: grupo,
      evento_id: eventoPreseleccionado?.id || null,
      traza_id: trazaId || null,
      lat: pos?.lat ?? null,
      lng: pos?.lng ?? null,
      share_token: vivoRef.current?.share_token || null,
    };
    // Si no hay señal queda en cola y sale sola cuando vuelve, igual que las fotos
    supabase.from("alertas_caida").insert(fila).then(({ error }) => {
      if (!error) return;
      try { localStorage.setItem("olbos_caida_pendiente", JSON.stringify({ ...fila, t: Date.now() })); } catch (e) { /* */ }
    }, () => {
      try { localStorage.setItem("olbos_caida_pendiente", JSON.stringify({ ...fila, t: Date.now() })); } catch (e) { /* */ }
    });
  };

  // La alerta que no salió por falta de señal se reintenta al volver la conexión
  useEffect(() => {
    const reintentar = () => {
      let p = null;
      try { p = JSON.parse(localStorage.getItem("olbos_caida_pendiente") || "null"); } catch (e) { return; }
      if (!p) return;
      const { t, ...fila } = p;
      supabase.from("alertas_caida").insert(fila).then(({ error }) => {
        if (!error) { try { localStorage.removeItem("olbos_caida_pendiente"); } catch (e) { /* */ } }
      }, () => {});
    };
    reintentar();
    window.addEventListener("online", reintentar);
    return () => window.removeEventListener("online", reintentar);
  }, []);

  // Marcar una parada (stop) en la posición actual — navegable después con Waze/Maps
  const marcarParada = (nombreDado = null) => {
    if (!posActual) { alert("Esperando señal GPS…"); return; }
    const nombreDefault = `Parada ${paradasRef.current.length + 1}`;
    // Por voz llega el nombre ya dicho; desde el botón se pregunta (el onClick manda el evento, no un string)
    const nombre = typeof nombreDado === "string" ? nombreDado : window.prompt("Nombre de la parada (ej: YPF, Parrilla, Mirador):", nombreDefault);
    if (nombre === null) return; // canceló
    const parada = {
      nombre: (nombre || nombreDefault).trim() || nombreDefault,
      lat: posActual.lat,
      lng: posActual.lng,
      ts: Date.now(),
    };
    paradasRef.current = [...paradasRef.current, parada];
    setParadas(paradasRef.current);
    if (trazaId) {
      supabase.from("trazas").update({ paradas: paradasRef.current }).eq("id", trazaId).then(() => {}, () => {});
    }
  };

  // ── Compartir en vivo: crea/abre el link público con mi posición + recorrido ──
  // El mismo link sirve para dos cosas distintas, y conviene ofrecerlas por
  // separado: QUE TE SIGAN (familia/amigos mirando por dónde vas, sin cuenta)
  // o SUMARSE a rodar (aparecen en el mapa con vos). Activa el vivo si hace falta.
  const compartirVivo = async (modo) => {
    if (!vivoRef.current) {
      if (!posActual) { alert("Esperando señal GPS… probá en unos segundos."); return; }
      setIniciandoVivo(true);
      try {
        const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", userId).single();
        const token = `vivo_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        const { data, error: errIns } = await supabase.from("ubicaciones_vivo").insert({
          user_id: userId, share_token: token, username: perfil?.username || "Rider",
          lat: posActual.lat, lng: posActual.lng,
          titulo: "Grabando un recorrido en vivo", activo: true,
          expira_en: new Date(Date.now() + 12 * 3600 * 1000).toISOString(),
          ultimo_update: new Date().toISOString(),
          traza_id: trazaId, recorrido: downsampleRecorrido(puntosRef.current),
          ruta_planeada: rutaPlaneadaRef.current ? codificarPolyline(rutaPlaneadaRef.current) : null,
        }).select().single();
        if (errIns) throw errIns;
        vivoRef.current = data;
        bgSesionActualizar({ share_token: data.share_token });
        ultimoVivoPushRef.current = Date.now();
        setVivoRegistro(data);
      } catch (e) {
        alert("No se pudo iniciar el compartido en vivo:\n" + (e.message || e));
        setIniciandoVivo(false);
        return;
      }
      setIniciandoVivo(false);
    }
    // Dos links distintos a propósito: el de SEGUIR abre siempre el visor (la
    // familia mira, aunque tenga cuenta); el de RODAR (?rodar=1) mete al que
    // tiene cuenta directo al grabador con el mapa compartido.
    const linkVivo = `${SITE_URL}/vivo/${vivoRef.current.share_token}`;
    compartirNativo(modo === "seguir"
      ? { texto: "🏍️ Estoy en la ruta. Mirá por dónde voy en tiempo real:", url: linkVivo, titulo: "Seguime en vivo" }
      : { texto: "🏍️ ¡Rodada en vivo! Sumate al mapa y rodamos juntos:", url: `${linkVivo}?rodar=1`, titulo: "Sumate a la rodada" });
  };

  const detenerVivo = async () => {
    const reg = vivoRef.current;
    vivoRef.current = null;
    bgSesionActualizar({ share_token: null });
    setVivoRegistro(null);
    setMostrarVivo(false);
    if (reg) {
      try { await supabase.from("ubicaciones_vivo").update({ activo: false }).eq("id", reg.id); } catch (e) { /* */ }
    }
  };

  // Capturar foto o video con geo-tag.
  // Soporta varios elementos en el mismo lugar (foto + video): cada uno se agrega
  // por separado y nunca se pisan. Avisa mientras sube y si algo falla (no silencioso).
  const tomarFoto = async (file) => {
    if (!file) return;
    if (!posActual) { alert("Esperando señal GPS… probá de nuevo en unos segundos."); return; }
    const isVideo = file.type.startsWith("video");
    // Capturar la posición AHORA (no al terminar de subir, que puede tardar)
    const geo = { lat: posActual.lat, lng: posActual.lng, ts: Date.now() };
    // Se achica ya mismo: si hay que encolarla, guardamos la versión liviana
    const liviana = await achicarImagen(file);
    // Sin señal: directo a la cola, sin intentar (el intento tarda y falla igual)
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      try {
        await colaGuardar({ blob: liviana, isVideo, trazaId: trazaId || null, ...geo, creado: Date.now() });
        alert(`Sin señal: ${isVideo ? "el video queda" : "la foto queda"} guardada en el teléfono y se sube sola cuando vuelvan los datos, en el lugar exacto donde la sacaste.`);
      } catch (e) {
        alert("No se pudo guardar en el teléfono. Probá de nuevo.");
      }
      return;
    }
    setSubiendoMedia(n => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", liviana);
      fd.append("upload_preset", "olbos_uploads");
      const endpoint = isVideo
        ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
        : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data.error?.message || "no se recibió la URL");
      const item = { url: data.secure_url, type: isVideo ? "video" : "image", ...geo };
      // Append sobre el ref vivo → convive con otras subidas en paralelo
      fotosRef.current = [...fotosRef.current, item];
      setFotos(fotosRef.current);
      const tid = trazaIdRef.current || trazaId;
      if (tid) await supabase.from("trazas").update({ fotos: fotosRef.current }).eq("id", tid);
      // En una rodada organizada la foto va también al muro del evento (documento de Matías, A5):
      // antes, si no estabas grabando, quedaba solo en memoria y se perdía al cerrar el mapa, y
      // los demás no la veían. El muro se actualiza en tiempo real en los teléfonos del grupo.
      if (grupoVivo && grupoVivo.id) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { error: errPost } = await supabase.from("posts").insert({
              user_id: user.id, username: await usernameReal(user), texto: "",
              media_url: item.url, media_type: item.type, media_urls: JSON.stringify([{ url: item.url, type: item.type }]),
              likes: 0, evento_id: grupoVivo.id, compartido_feed_general: false,
            });
            if (!errPost) registrarUso("foto_rodada", { evento: grupoVivo.id, grabando: !!tid });
          }
        } catch (e) { /* la foto ya quedó en el recorrido */ }
      }
      if (!tid && !(grupoVivo && grupoVivo.id)) alert(`${isVideo ? "El video quedó" : "La foto quedó"} guardada para este recorrido: se guarda del todo cuando empezás a grabar.`);
    } catch (err) {
      // Falló la subida (señal intermitente): en vez de perderla, va a la cola
      console.error("Error multimedia:", err);
      try {
        await colaGuardar({ blob: liviana, isVideo, trazaId: trazaId || null, ...geo, creado: Date.now() });
        alert(`La señal no alcanzó para subir${isVideo ? " el video" : " la foto"}. Queda guardada en el teléfono y se sube sola cuando mejore, en el lugar exacto donde la sacaste.`);
      } catch (e2) {
        alert(`No se pudo subir ${isVideo ? "el video" : "la foto"}. Revisá la señal y probá de nuevo.\n\n${err.message || ""}`);
      }
    } finally {
      setSubiendoMedia(n => Math.max(0, n - 1));
    }
  };

  // Cuántos archivos están esperando señal (para mostrarlo en el grabador)
  const [pendientesCola, setPendientesCola] = useState(0);
  useEffect(() => {
    const refrescar = () => { colaListar().then(l => setPendientesCola(l.length), () => {}); };
    refrescar();
    window.addEventListener("olbos-cola-fotos", refrescar);
    return () => window.removeEventListener("olbos-cola-fotos", refrescar);
  }, []);

  // Finalizar — abre modal de resumen
  const intentarFinalizar = () => {
    if (subiendoMedia > 0) {
      alert(`Esperá unos segundos: se ${subiendoMedia > 1 ? "están subiendo" : "está subiendo"} ${subiendoMedia} archivo${subiendoMedia > 1 ? "s" : ""} multimedia. No cierres todavía para no perderlo.`);
      return;
    }
    if (puntosRef.current.length < 2) {
      if (!window.confirm("El recorrido tiene muy pocos puntos. ¿Descartar?")) return;
      cancelarTraza();
      return;
    }
    // OJO: acá NO se apaga el GPS. Si el usuario toca "Volver" en el modal,
    // sigue grabando de verdad — antes el watch quedaba muerto y perdía el
    // resto del recorrido creyendo que grababa. El watch se corta en guardarFinal.
    setTituloFinal(`Recorrido del ${new Date().toLocaleDateString("es-AR", { day: "numeric", month: "long" })}`);
    setShowFinalizar(true);
  };

  const cancelarTraza = async () => {
    pararWatchPausa();
    if (watchIdRef.current !== null) {
      watchIdRef.current.stop();
      watchIdRef.current = null;
    }
    liberarWakeLock();
    if (vivoRef.current) {
      await supabase.from("ubicaciones_vivo").update({ activo: false }).eq("id", vivoRef.current.id).then(() => {}, () => {});
      vivoRef.current = null;
    }
    if (trazaId) {
      await supabase.from("trazas").delete().eq("id", trazaId);
    }
    onCerrar();
  };

  // Guardar con reintentos. Terminar una salida en el medio del campo, con una barrita
  // de señal, es lo más normal del mundo: un solo intento fallido no puede ser el final
  // del viaje del rider.
  const esperar = (ms) => new Promise(r => setTimeout(r, ms));
  const guardarConReintentos = async (campos, intentos = 3) => {
    let ultimo = null;
    for (let i = 0; i < intentos; i++) {
      const { error } = await supabase.from("trazas").update(campos).eq("id", trazaId);
      if (!error) return null;
      ultimo = error;
      if (i < intentos - 1) await esperar(i === 0 ? 2000 : 5000);
    }
    return ultimo;
  };

  const guardarFinal = async () => {
    setGuardando(true);
    // Modo circuito: lo que se grabó ANTES de cruzar la largada por primera vez (el viaje
    // hasta la pista, la vuelta de reconocimiento) no es parte de la sesión: se descarta,
    // así los km, el tiempo y el dibujo son solo de las vueltas. Se conserva el punto
    // anterior al cruce para que el instante de la largada siga interpolado.
    if (circuitoRef.current && circuitoRef.current.meta && puntosRef.current.length > 10) {
      try {
        const vPrev = detectarVueltas(puntosRef.current, circuitoRef.current.meta);
        const corte = vPrev.length ? Math.max(0, vPrev[0].ini - 1) : 0;
        if (corte > 0) puntosRef.current = puntosRef.current.slice(corte);
      } catch (e) { /* si algo falla, se guarda entero como siempre */ }
    }
    bgSesionActualizar({ traza_id: null });
    const finalStats = calcEstadisticas(puntosRef.current);
    const tituloUsar = tituloFinal || (circuitoRef.current && vueltasRef.current.length ? `${circuitoRef.current.nombre} · ${vueltasRef.current.length} vueltas` : `Recorrido ${new Date().toLocaleDateString("es-AR")}`);
    const esDeGrupo = !!grupoFinal;
    // Si es ruta de grupo, no es "pública" del feed general (a menos que se elija compartir)
    const muyCorto = finalStats.dist < KM_MIN_PUBLICAR;
    const feedOk = publicarEnFeed && !muyCorto;
    const esPublica = esDeGrupo ? false : ((publicaFinal || publicarEnFeed) && !muyCorto);

    // Se guarda en DOS pasos. Primero lo pesado —los puntos, las fotos y las paradas—,
    // que es lo único que puede no entrar con señal mala; después lo liviano, que es
    // lo que da el recorrido por cerrado. Así, si lo pesado no entra, la traza NO queda
    // marcada como terminada y el rider la puede retomar y guardar cuando vuelva la señal,
    // en vez de quedarse con un recorrido cerrado y a medias.
    let errPesado = await guardarConReintentos({
      puntos: puntosRef.current,
      fotos: fotosRef.current,
      paradas: paradasRef.current,
    });

    // Plan B: si el trazado completo no entra, se manda simplificado. Pesa una fracción
    // y conserva la forma del camino; los kilómetros, el tiempo y las velocidades no
    // cambian, porque van calculados aparte sobre el recorrido completo.
    let simplificado = false;
    if (errPesado) {
      errPesado = await guardarConReintentos({
        puntos: downsampleRecorrido(puntosRef.current, 2000),
        fotos: fotosRef.current,
        paradas: paradasRef.current,
      }, 2);
      simplificado = !errPesado;
    }
    if (errPesado) {
      setGuardando(false);
      alert("No se pudo guardar el recorrido: parece que estás sin señal.\n\nNo perdiste nada. Queda como recorrido sin terminar y lo vas a poder retomar y guardar cuando vuelva la señal. Mientras tanto seguís grabando.");
      return;
    }

    // Paso liviano: pesa unos pocos bytes, es el que cierra el recorrido
    const errFin = await guardarConReintentos({
      titulo: tituloUsar,
      descripcion: descripcionFinal || null,
      tipo_camino: tipoCamino || null,
      estado_camino: estadoCamino || null,
      recomendaciones: descripcionFinal || null,
      distancia_km: finalStats.dist,
      duracion_seg: finalStats.dur,
      velocidad_max: finalStats.velMax,
      velocidad_prom: finalStats.velProm,
      publica: esPublica,
      grupo_id: grupoFinal || null,
      evento_id: eventoFinal || null, // vincula el recorrido a la rodada, si se grabó desde ahí
      estado: "finalizada",
      finalizada_en: new Date().toISOString(),
    });
    if (errFin) {
      setGuardando(false);
      alert("Se guardó el recorrido pero no se pudo cerrar (¿estás sin señal?). No perdiste nada: probá de nuevo en unos segundos.");
      return;
    }
    if (simplificado) {
      alert("Guardado. Estabas con poca señal, así que el trazado se guardó simplificado: los kilómetros y el tiempo son exactos, el dibujo del camino tiene un poco menos de detalle.");
    }

    // Guardado OK: recién ahora se apaga el GPS y se corta el compartido en vivo
    pararWatchPausa();
    if (watchIdRef.current !== null) {
      watchIdRef.current.stop();
      watchIdRef.current = null;
    }
    if (vivoRef.current) {
      supabase.from("ubicaciones_vivo").update({ activo: false }).eq("id", vivoRef.current.id).then(() => {}, () => {});
      vivoRef.current = null;
    }

    // Aporte ANÓNIMO a la red de caminos (fire-and-forget, jamás bloquea el guardado):
    // se recortan ~500 m de cada punta (zona de privacidad — que nadie deduzca dónde
    // vive el rider) y va sin identidad. Es la base de caminos que Google no tiene.
    try {
      const pts = puntosRef.current;
      const recorte = (arr) => { let acc = 0, i = 0; for (; i < arr.length - 1 && acc < 0.5; i++) acc += haversineKm(arr[i], arr[i + 1]); return i; };
      const ini = recorte(pts);
      const fin = pts.length - recorte([...pts].reverse());
      const nucleo = pts.slice(ini, fin);
      if (nucleo.length > 10 && finalStats.dist >= 2) {
        const region = `${Math.round(nucleo[0].lat)},${Math.round(nucleo[0].lng)}`;
        const reducido = downsampleRecorrido(nucleo);
        supabase.rpc("aportar_a_red", {
          p_polyline: codificarPolyline(reducido),
          p_coords: reducido.map(p => [p.lng, p.lat]), // geometría para el conteo de riders por camino
          p_puntos: nucleo.length,
          p_km: finalStats.dist,
          p_vel_prom: finalStats.velProm,
          p_vel_max: finalStats.velMax,
          p_tipo_camino: tipoCamino || null,
          p_estado_camino: estadoCamino || null,
          p_recomendaciones: descripcionFinal || null,
          p_region: region,
        }).then(() => {}, () => {});
      }
    } catch (e) { /* la red nunca rompe un guardado */ }

    // Si elige publicar en feed, crear un post con imagen del recorrido + stats.
    // Si el post falla, el recorrido YA está guardado — no puede tumbar el cierre.
    const credibilidad = recorridoCreible(puntosRef.current);
    if ((publicaFinal || publicarEnFeed) && muyCorto) {
      setTimeout(() => alert("Guardamos el recorrido, pero no lo publicamos: " + msgMinKm(finalStats.dist)), 400);
    } else if (publicarEnFeed && !credibilidad.ok) {
      // Guardado sí, publicado no: un mapa con una recta de 100 km no le sirve a nadie
      setTimeout(() => alert(`Guardamos el recorrido, pero no lo publicamos en el feed porque ${credibilidad.motivo}. Si igual querés compartirlo, podés hacerlo desde Mis recorridos.`), 400);
    }
    if (feedOk && credibilidad.ok) {
      try {
        // Carrusel: imagen del mapa del trazo + fotos/videos del recorrido (con tope).
        await compartirTrazaEnFeed({
          id: trazaId,
          titulo: tituloUsar,
          descripcion: descripcionFinal,
          distancia_km: finalStats.dist,
          duracion_seg: finalStats.dur,
          velocidad_prom: finalStats.velProm,
          puntos: puntosRef.current,
          fotos: fotosRef.current,
          grupo_id: grupoFinal || null,
          evento_id: eventoFinal || null,
        }, userId);
      } catch (e) { /* el recorrido quedó guardado; el post se puede rehacer desde Mis recorridos */ }
    }

    // Sesión en circuito: vueltas (con sus curvas en pista), mejor y ranking
    let sesionGuardada = null;
    if (circuitoRef.current && vueltasRef.current.length) {
      try {
        const c = circuitoRef.current;
        if (c.tipo !== "offroad" && !(Array.isArray(c.curvas) && c.curvas.length)) {
          try { const { data: cc } = await supabase.from("circuitos").select("curvas").eq("id", c.id).maybeSingle(); if (cc && Array.isArray(cc.curvas) && cc.curvas.length) c.curvas = cc.curvas; } catch (e) { /* sin curvas: salen de esta sesión */ }
        }
        const ses = armarSesionCircuito(puntosRef.current, c);
        if (ses.curvas_circuito) { c.curvas = ses.curvas_circuito; supabase.from("circuitos").update({ curvas: ses.curvas_circuito }).eq("id", c.id).then(() => {}, () => {}); }
        // Los saltos van a la vuelta en la que ocurrieron (por instante)
        const pts = puntosRef.current;
        ses.vueltas.forEach(v => { v.saltos = saltosRef.current.filter(s => s.ts >= pts[v.ini].ts && s.ts <= pts[v.fin].ts); });
        if (ses.n_vueltas) {
          const { data } = await supabase.from("sesiones_circuito").upsert({
            traza_id: trazaId, circuito_id: c.id, user_id: userId, vueltas: ses.vueltas, n_vueltas: ses.n_vueltas, mejor_seg: ses.mejor_seg,
            km: finalStats.dist, vmax: finalStats.velMax,
          }, { onConflict: "traza_id" }).select("*").maybeSingle();
          sesionGuardada = data || { ...ses, circuito_id: c.id };
          sesionGuardada.mejor_n = ses.mejor_n;
        }
      } catch (e) { /* la sesión no puede tumbar el guardado */ }
    }
    configurarGpsCircuito(false);
    setGuardando(false);
    liberarWakeLock();
    onCerrar();
    if (sesionGuardada) {
      const c = circuitoRef.current;
      setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-ver-sesion-circuito", { detail: { traza: { id: trazaId, puntos: puntosRef.current, iniciada_en: new Date(inicioRef.current || Date.now()).toISOString() }, sesion: sesionGuardada, circuito: c } })), 300);
    }
  };

  // Km andados con el mapa abierto SIN grabar (modo lista / navegando): se
  // filtran saltos de GPS (>3 km entre fixes o >200 km/h) y se guardan como
  // traza privada de navegación si son al menos 500 m y 2 minutos.
  // Km andados con el mapa abierto SIN grabar (modo lista / navegando). Se filtran
  // saltos de GPS (>3 km entre fixes o >200 km/h) y se guardan como traza privada de
  // navegación si son al menos 500 m y 2 minutos.
  // Se guardan CADA 3 MINUTOS, al irse la app al fondo y al cerrar — no solo al
  // cerrar, como antes. Si el sistema mata la app en segundo plano o el rider la
  // cierra desde el gestor de apps, el desmontado nunca ocurre y se perdía todo
  // (Carlos: 20 km que no sumaron, 11-sep). Una sola traza por sesión del mapa, que
  // se va actualizando; así el total de la comunidad y el ranking la ven crecer en vivo.
  const kmNavegacionDe = (pts) => {
    if (!Array.isArray(pts) || pts.length < 4) return null;
    let km = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i];
      const d = haversineKm(a, b);
      const dt = Math.max(1, ((b.ts || 0) - (a.ts || 0)) / 1000);
      if (d > 3 || (d / dt) * 3600 > 200) continue;
      km += d;
    }
    const ini = pts[0].ts || Date.now(), fin = pts[pts.length - 1].ts || Date.now();
    if (km < 0.5 || fin - ini < 120000) return null;
    return { km: Math.round(km * 100) / 100, ini, fin };
  };
  const navTrazaIdRef = useRef(null);      // la traza de navegación de ESTA sesión
  const navFlushEnVueloRef = useRef(false);
  const navUltimoKmRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const flushKmNavegacion = async () => {
    try {
      const uid = userIdRef.current;
      if (!uid || navFlushEnVueloRef.current) return;
      if (estadoGrabRef.current === "grabando" || estadoGrabRef.current === "pausada") { pasivoRef.current = []; return; } // el recorrido grabado ya cuenta esos km
      const pts = pasivoRef.current;
      const r = kmNavegacionDe(pts);
      if (!r) return;
      if (Math.abs(r.km - navUltimoKmRef.current) < 0.05 && navTrazaIdRef.current) return; // nada nuevo
      navFlushEnVueloRef.current = true;
      const destino = navInfoRef.current && navInfoRef.current.destino ? String(navInfoRef.current.destino) : "";
      const campos = conVehiculo({
        titulo: destino ? `Navegación a ${destino}`.slice(0, 80) : "Navegación sin grabar",
        descripcion: "Kilómetros sumados usando el mapa sin grabar el recorrido.",
        puntos: downsampleRecorrido(pts, 200),
        distancia_km: r.km,
        duracion_seg: Math.round((r.fin - r.ini) / 1000),
        iniciada_en: new Date(r.ini).toISOString(),
        finalizada_en: new Date(r.fin).toISOString(),
        publica: false,
        estado: "finalizada",
        tipo_camino: "navegacion",
      });
      if (navTrazaIdRef.current) {
        await supabase.from("trazas").update({ ...campos, updated_at: new Date().toISOString() }).eq("id", navTrazaIdRef.current);
      } else {
        const { data } = await supabase.from("trazas").insert({ user_id: uid, ...campos }).select("id").maybeSingle();
        if (data && data.id) navTrazaIdRef.current = data.id;
      }
      navUltimoKmRef.current = r.km;
      window.dispatchEvent(new CustomEvent("olbos-trazas-actualizadas"));
    } catch (e) { /* nunca romper el mapa por esto */ }
    finally { navFlushEnVueloRef.current = false; }
  };
  const flushKmNavegacionRef = useRef(flushKmNavegacion);
  flushKmNavegacionRef.current = flushKmNavegacion;
  useEffect(() => {
    const cada = setInterval(() => { flushKmNavegacionRef.current(); }, 180000);
    // Al volver (desbloquear): el backfill trae lo andado con la pantalla bloqueada y a los
    // pocos segundos se guarda, sin esperar los 3 minutos (por si cierra la app enseguida)
    const alEsconder = () => {
      if (document.visibilityState === "hidden") flushKmNavegacionRef.current();
      else setTimeout(() => flushKmNavegacionRef.current(), 8000);
    };
    document.addEventListener("visibilitychange", alEsconder);
    window.addEventListener("pagehide", alEsconder);
    return () => { clearInterval(cada); document.removeEventListener("visibilitychange", alEsconder); window.removeEventListener("pagehide", alEsconder); };
  }, []);
  // Compatibilidad con el cierre: el flush final es el mismo guardado
  const guardarKmNavegacion = () => { flushKmNavegacionRef.current(); };

  // Cleanup
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) watchIdRef.current.stop();
      if (watchPausaRef.current) { try { watchPausaRef.current.stop(); } catch (e) { /* */ } watchPausaRef.current = null; }
      if (timerRetomarRef.current) clearInterval(timerRetomarRef.current);
      if (watchLibreRef.current !== null) { watchLibreRef.current.stop(); watchLibreRef.current = null; }
      // Lo andado con el mapa abierto sin grabar aporta anónimo a la red (con
      // opt-out). El ref es un array persistente nuestro, no un nodo de React.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      aportarTramoARed(pasivoRef.current);
      // Y también suma a TU contador (pedido de Carlos 04-sep: "los km se sumen
      // aunque no haya grabado"): se guarda como recorrido de navegación privado,
      // tipo_camino "navegacion" — cuenta para km de la comunidad y el ranking,
      // pero no aparece en Mis recorridos ni en el perfil público.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      guardarKmNavegacion(pasivoRef.current);
      bgSesionActualizar({ pasivo: false });
      // Un compartido en vivo NO puede quedar activo sin nadie empujando posición:
      // el que mira quedaría congelado hasta que expire (pasó cerrando desde el
      // mapa sin grabar, que no pasa por finalizar/cancelar)
      if (vivoRef.current) {
        supabase.from("ubicaciones_vivo").update({ activo: false }).eq("id", vivoRef.current.id).then(() => {}, () => {});
        vivoRef.current = null;
      }
      liberarWakeLock();
    };
  }, []);

  // Calcular heading (bearing) entre 2 puntos en grados (0 = norte)
  const calcularHeading = (a, b) => {
    const lat1 = a.lat * Math.PI / 180;
    const lat2 = b.lat * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  };

  // La FLECHA que ES tu posición, rotada hacia donde vas — como la de Waze
  // (la moto dibujada se veía rara, reporte de Carlos). La rotación del símbolo
  // es respecto de la PANTALLA, así que se descuenta el heading del mapa (en
  // modo navegación el mapa gira con vos y la flecha queda apuntando para arriba).
  const iconoMoto = (rot) => ({
    path: "M 0 -10 L 7 8 L 0 4.2 L -7 8 Z",
    scale: 1.6, fillColor: "#111111", fillOpacity: 1,
    strokeColor: "#fff", strokeWeight: 1.8,
    rotation: rot, anchor: new window.google.maps.Point(0, 0),
  });
  // Dibujo de la posición SIN pasar por React: se llama desde el callback del
  // GPS, así la flecha y la línea se mueven aunque el estado no se propague, el
  // efecto no corra o el componente no re-renderice. Esa cadena larga
  // (GPS → estado → efecto → marker) fue la causa de que la velocidad avanzara
  // con la flecha clavada al pausar y reanudar (reporte de Carlos, 23-ago).
  const dibujarPosGPS = (punto) => {
    if (!punto || !window.google) return;
    const pos = { lat: punto.lat, lng: punto.lng };
    // Rumbo con el punto anterior real, para que la flecha apunte a donde va.
    // Sin grabar (modo lista) no hay puntosRef: se usa el tramo pasivo — así el
    // mapa también sigue y rota cuando navegás sin grabar (reporte de Carlos).
    const pts = puntosRef.current || [];
    const pas = pasivoRef.current || [];
    const prev = pts.length >= 2 ? pts[pts.length - 2] : (pas.length >= 2 ? pas[pas.length - 2] : null);
    if (prev) {
      // El rumbo se recalcula solo con movimiento REAL (≥ 8 m). Antes bastaba 1 m,
      // que es menos que el ruido del GPS: parado en un semáforo el mapa giraba solo.
      if (haversineKm(prev, pos) * 1000 >= 8) {
        headingRef.current = calcularHeading(prev, pos);
      }
    }
    if (polylineRef.current && pts.length > 1) {
      // Con un hueco del GPS (pantalla bloqueada en la web, túnel) no se dibuja una recta
      // falsa entre las dos puntas: cada tramo real va en su propia línea
      const tramos = partirEnTramos(pts);
      const ultimo = tramos[tramos.length - 1] || [];
      polylineRef.current.setPath(ultimo.map(p => ({ lat: p.lat, lng: p.lng })));
      const previos = tramos.slice(0, -1);
      while (tramosGoogleRef.current.length < previos.length) {
        const i = tramosGoogleRef.current.length;
        try {
          tramosGoogleRef.current.push(new window.google.maps.Polyline({
            path: previos[i].map(p => ({ lat: p.lat, lng: p.lng })), map: polylineRef.current.getMap(),
            strokeColor: polylineRef.current.get("strokeColor"), strokeOpacity: polylineRef.current.get("strokeOpacity"),
            strokeWeight: polylineRef.current.get("strokeWeight"), zIndex: polylineRef.current.get("zIndex"),
          }));
        } catch (e) { break; }
      }
    }
    // Sin grabar: se dibuja el tramo pasivo (punteado). Al empezar a grabar, se borra.
    if (pasivaPolyRef.current) {
      if (pts.length > 1) pasivaPolyRef.current.setPath([]);
      else if (pas.length > 1) pasivaPolyRef.current.setPath(pas.slice(-600).map(p => ({ lat: p.lat, lng: p.lng })));
    }
    // Velocidad actual para el zoom dinámico (del GPS, o derivada de los últimos 2 puntos).
    // La rotación del mapa ya NO se aplica acá de un saque: se interpola dentro de
    // moverSuave, junto con el centro — el giro seco era parte de los "saltitos".
    if (punto.v != null && punto.v >= 0) velKmhRef.current = punto.v * 3.6;
    else if (pts.length >= 2) {
      const ant = pts[pts.length - 2];
      const dt = ((punto.ts || 0) - (ant.ts || 0)) / 1000;
      if (dt > 0 && dt < 30) velKmhRef.current = haversineKm(ant, pos) / (dt / 3600);
    }
    actualizarFlecha(pos);
  };
  const actualizarFlecha = (pos) => {
    if (!markerRef.current || !window.google) return;
    const mapa = mapInstanceRef.current;
    const rot = (headingRef.current || 0) - ((mapa && mapa.getHeading && mapa.getHeading()) || 0);
    markerRef.current.setIcon(iconoMoto(rot));
    moverSuave(markerRef.current, pos);
  };

  // Movimiento CONTINUO de la posición (interpolación entre lecturas de GPS):
  // el salto a tramitos se veía mal — reporte de Carlos
  const animPosRef = useRef(null);
  const ultimaPosMapaRef = useRef(null); // último destino real, animado o no
  // Token de la animación vigente. SIN esto, dos llamadas casi simultáneas
  // (el modo lista y el efecto de puntos, mientras grabás) dejaban una cadena
  // de requestAnimationFrame HUÉRFANA: el ref guardaba solo la última y la otra
  // seguía viva para siempre, re-agendándose sola y llamando setCenter 60 veces
  // por segundo. Con varias acumuladas el mapa se saturaba, dejaba de responder
  // a los gestos y la moto no se movía (Carlos: "todo en cero, como la pantalla
  // congelada, y no me dejaba mover el mapa a mano tampoco").
  const animTokenRef = useRef(0);
  // ── Cámara estilo Waze (pedidos de Carlos, 24-ago tras filmar Waze en el auto) ──
  const velKmhRef = useRef(0);         // velocidad actual, para el zoom dinámico
  const navGiroMRef = useRef(null);    // metros al próximo giro (lo empuja NavegadorOverlay)
  const ultimoDibujoTsRef = useRef(0); // ritmo real del GPS, para adaptar la animación
  const altoMapaRef = useRef(0);
  // Punto desplazado 'metros' hacia 'rumbo' (para correr el centro del mapa)
  const puntoAdelante = (p, rumbo, metros) => {
    const R = 6371000, br = rumbo * Math.PI / 180;
    return {
      lat: p.lat + (metros * Math.cos(br)) / R * 180 / Math.PI,
      lng: p.lng + (metros * Math.sin(br)) / (R * Math.cos(p.lat * Math.PI / 180)) * 180 / Math.PI,
    };
  };
  // En navegación el centro del mapa va ADELANTE de la posición real: la flecha
  // queda abajo (cerca del globo de velocidad) y se ve más camino por delante
  const centroNav = (p) => {
    const mapa = mapInstanceRef.current;
    if (!mapa || !modoNavegacionRef.current || headingRef.current == null) return p;
    // Si el mapa NO está rotado cerca del rumbo real (en algunos teléfonos el
    // vectorial cae a raster y setHeading se ignora en silencio), el corrimiento
    // "adelante" deja la flecha clavada contra un borde y parece que el mapa no
    // te sigue (video de Matías, 28-ago). Sin rotación efectiva: centrado clásico.
    const hMapa = (mapa.getHeading && mapa.getHeading()) || 0;
    const desvio = Math.abs(((headingRef.current - hMapa + 540) % 360) - 180);
    if (desvio > 60) return p;
    if (!altoMapaRef.current) {
      try { altoMapaRef.current = mapa.getDiv().clientHeight || 600; } catch (e) { altoMapaRef.current = 600; }
    }
    const z = mapa.getZoom() || 17;
    const mPorPx = 156543.03392 * Math.cos(p.lat * Math.PI / 180) / Math.pow(2, z);
    return puntoAdelante(p, headingRef.current, mPorPx * altoMapaRef.current * 0.22);
  };
  // Zoom objetivo según velocidad (despacio o giro cerca = más cerca; rápido y
  // recta larga = más lejos, como Waze). Se persigue de a poco, nunca de un salto.
  const zoomObjetivoNav = () => {
    const kmh = velKmhRef.current || 0;
    let z = kmh < 15 ? 18 : kmh < 35 ? 17.6 : kmh < 60 ? 17.1 : kmh < 90 ? 16.6 : 16.1;
    const dG = navGiroMRef.current;
    if (dG != null && dG < 250) z = Math.max(z, 17.7);
    return z;
  };
  // Poner la posición YA, sin animación (background, saltos de GPS, volver a la app)
  const ponerPosDirecto = (marker, p) => {
    if (!marker || !p) return;
    animTokenRef.current++; // corta cualquier animación en curso
    if (animPosRef.current) { cancelAnimationFrame(animPosRef.current); animPosRef.current = null; }
    marker.setPosition(p);
    // Tapado por el v2, Google no se mueve: cada setCenter pide tiles y anima al pedo en
    // un Android chico. Si el v2 cae, el próximo fix lo trae acá de un salto.
    if (usarNavV2Ref.current) return;
    if (seguirRef.current && mapInstanceRef.current && !gestoGoogleRef.current) {
      const mapa = mapInstanceRef.current;
      if (modoNavegacionRef.current && headingRef.current != null) {
        mapa.setHeading(headingRef.current);
        marker.setIcon(iconoMoto(0)); // mapa ya rotado al rumbo → flecha para arriba
      }
      mapa.setCenter(centroNav(p));
    }
  };
  const moverSuave = (marker, hasta, dur = 1100) => {
    if (!marker || !hasta) return;
    ultimaPosMapaRef.current = hasta;
    // Con el v2 arriba, Google no anima ni sigue: la flecha se deja en su lugar y listo
    if (usarNavV2Ref.current) { ponerPosDirecto(marker, hasta); return; }
    const desdeLL = marker.getPosition();
    if (!desdeLL) { ponerPosDirecto(marker, hasta); return; }
    const desde = { lat: desdeLL.lat(), lng: desdeLL.lng() };
    if (Math.abs(desde.lat - hasta.lat) + Math.abs(desde.lng - hasta.lng) < 1e-7) return;
    // requestAnimationFrame NO corre con la pantalla apagada ni con la app en
    // segundo plano: si animábamos igual, setPosition nunca se llamaba y la
    // moto quedaba clavada en el punto viejo mientras el rider seguía andando
    // (el track se grababa bien, pero el mapa mostraba otra cosa — reporte de
    // Carlos tras salir a la calle). Lo mismo si el GPS pega un salto grande.
    const saltoMetros = haversineKm(desde, hasta) * 1000;
    if (document.hidden || saltoMetros > 150 || saltoMetros < 0.5) { ponerPosDirecto(marker, hasta); return; }
    if (animPosRef.current) cancelAnimationFrame(animPosRef.current);
    // Duración adaptada al ritmo REAL del GPS: los fixes llegan cada ~2s y la
    // animación duraba 1,1s → el mapa quedaba quieto casi un segundo entre punto
    // y punto y se veía "a saltitos" (reporte de Carlos con el video de Waze).
    const ahoraTs = performance.now();
    if (ultimoDibujoTsRef.current) {
      const gap = ahoraTs - ultimoDibujoTsRef.current;
      // A paso de caravana (5 km/h) el filtro de 8 m del GPS nativo entrega un
      // punto cada ~6 s: con el tope anterior de 2,6 s la flecha corría 8 m y se
      // quedaba clavada 3 s — "el mapa a saltitos" del BikerFest. Ahora la
      // animación dura lo que tarda el punto siguiente en llegar.
      if (gap > 300 && gap < 12000) dur = Math.min(9000, Math.max(800, gap * 1.05));
    }
    ultimoDibujoTsRef.current = ahoraTs;
    // La rotación también se interpola (el setHeading seco era otro de los saltos)
    const mapa = mapInstanceRef.current;
    const navSigue = !!(seguirRef.current && modoNavegacionRef.current && mapa);
    const hDesde = navSigue && mapa.getHeading ? (mapa.getHeading() || 0) : 0;
    const dH = navSigue && headingRef.current != null
      ? ((headingRef.current - hDesde + 540) % 360) - 180 // por el arco corto
      : 0;
    const miToken = ++animTokenRef.current;
    const t0 = performance.now();
    const paso = (t) => {
      if (miToken !== animTokenRef.current) return; // otra animación tomó el control: esta se apaga
      const k = Math.min(1, (t - t0) / dur);
      const nueva = { lat: desde.lat + (hasta.lat - desde.lat) * k, lng: desde.lng + (hasta.lng - desde.lng) * k };
      marker.setPosition(nueva);
      // El mapa persigue a la flecha frame a frame con setCenter: panTo acá
      // re-arrancaba su propia animación en cada cuadro y el mapa avanzaba a
      // tramos aunque la flecha fuera fluida (reporte de Carlos)
      if (navSigue && !gestoGoogleRef.current) {
        if (Math.abs(dH) > 0.5) {
          const h = (hDesde + dH * k + 360) % 360;
          mapa.setHeading(h);
          marker.setIcon(iconoMoto((headingRef.current || 0) - h));
        }
        // El zoom por velocidad solo si el rider no eligió uno con los dedos (pinch/doble toque)
        if (!zoomManualRef.current) {
          const zt = zoomObjetivoNav();
          const z = mapa.getZoom() || 17;
          if (Math.abs(zt - z) > 0.03) mapa.setZoom(z + (zt - z) * 0.05);
        }
        mapa.setCenter(centroNav(nueva));
      } else if (seguirRef.current && mapInstanceRef.current && !gestoGoogleRef.current) {
        mapInstanceRef.current.setCenter(nueva);
      }
      if (k < 1) animPosRef.current = requestAnimationFrame(paso);
      else { animPosRef.current = null; marker.setPosition(hasta); } // cierre exacto
    };
    animPosRef.current = requestAnimationFrame(paso);
  };
  // Red de seguridad: cada 4 s se compara dónde está dibujada la flecha con la
  // última posición real. Si quedó atrás más de 30 m, se corrige sola. Así,
  // cualquier camino que falle en el futuro se nota como un salto y no como una
  // flecha clavada mientras el rider sigue andando.
  useEffect(() => {
    const id = setInterval(() => {
      const p = posActualRef.current;
      const mk = markerRef.current;
      if (!p || !mk || !mk.getPosition) return;
      const act = mk.getPosition();
      if (!act) return;
      if (haversineKm({ lat: act.lat(), lng: act.lng() }, p) * 1000 > 30) ponerPosDirecto(mk, p);
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Volver a la app después de tenerla en segundo plano: la moto salta al toque
  // a donde estás de verdad, sin esperar la próxima lectura del GPS
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState !== "visible") return;
      const p = posActualRef.current || ultimaPosMapaRef.current;
      if (p && markerRef.current) ponerPosDirecto(markerRef.current, p);
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || estado === "preparando") return;
    loadGoogleMaps().then(() => {
      if (!window.google || !mapRef.current) return;
      if (!mapInstanceRef.current && posActual) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: posActual,
          zoom: 16,
          mapId: MAP_ID_NAV, // mapa vectorial → permite rotación (course-up)
          disableDefaultUI: true,
          // El +/− de Google vivía abajo a la derecha, justo donde ahora está el
          // micrófono de los comandos de voz (captura de Matías, 18-sep). En el centro
          // del borde derecho chocaba con el pin de recentrar (Carlos): va al borde
          // izquierdo, a media altura, donde no hay nada.
          zoomControl: true,
          zoomControlOptions: { position: window.google.maps.ControlPosition.LEFT_CENTER },
          gestureHandling: "greedy",
        });
        polylineRef.current = new window.google.maps.Polyline({
          path: [],
          geodesic: true,
          strokeColor: "#111111",
          strokeOpacity: 0.9,
          strokeWeight: 4,
          map: mapInstanceRef.current,
        });
        // Tramo andado SIN grabar: se dibuja punteado y más tenue, para que se
        // distinga del recorrido grabado. No crea ninguna traza: es solo lo que
        // llevás recorrido en esta sesión del mapa (pedido de Carlos).
        pasivaPolyRef.current = new window.google.maps.Polyline({
          path: [],
          geodesic: true,
          strokeOpacity: 0,
          icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 0.55, strokeColor: "#111111", strokeWeight: 3, scale: 2 }, offset: "0", repeat: "12px" }],
          map: mapInstanceRef.current,
        });
        markerRef.current = new window.google.maps.Marker({
          position: posActual,
          map: mapInstanceRef.current,
          clickable: false,
          zIndex: 2000,
          icon: iconoMoto(0),
        });
        // Arrastrar el mapa a mano = dejar de perseguir; el botón Centrar vuelve.
        // Se corta también la animación en curso: si no, el recentrado le peleaba
        // el gesto al dedo y el mapa parecía trabado.
        mapInstanceRef.current.addListener("dragstart", () => {
          animTokenRef.current++;
          if (animPosRef.current) { cancelAnimationFrame(animPosRef.current); animPosRef.current = null; }
          gestoMapaRef.current = true; cancelarVolver();
          setSeguirMapa(false);
        });
        mapInstanceRef.current.addListener("dragend", () => { gestoMapaRef.current = false; programarVolver(); });
      }
      // Modo lista (todavía sin grabar): la moto sigue la posición en vivo
      if (estado === "lista" && markerRef.current && posActual && puntosRef.current.length === 0) {
        // El rumbo ya lo calcula dibujarPosGPS con movimiento real (≥ 8 m); acá
        // se recalculaba con 3 m y el mapa giraba nervioso parado en un semáforo.
        posPrevListaRef.current = posActual;
        actualizarFlecha(posActual); // mueve la moto (suave), rota y recentra si corresponde
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, posActual]);

  // Actualizar polyline y marker cuando cambian los puntos
  useEffect(() => {
    if (!polylineRef.current || !markerRef.current || puntos.length === 0) return;
    const path = puntos.map(p => ({ lat: p.lat, lng: p.lng }));
    polylineRef.current.setPath(path);
    const ultimo = path[path.length - 1];

    // Calcular heading si tenemos al menos 2 puntos
    if (path.length >= 2) {
      const penultimo = path[path.length - 2];
      // Solo recalcular heading si nos movimos al menos un poquito (evitar girar el mapa parado)
      const dlat = Math.abs(ultimo.lat - penultimo.lat);
      const dlng = Math.abs(ultimo.lng - penultimo.lng);
      if (dlat > 0.00001 || dlng > 0.00001) {
        headingRef.current = calcularHeading(penultimo, ultimo);
      }
    }

    // El course-up solo si el mapa te está siguiendo (si lo moviste a mano, quieto; tapado por el v2, tampoco)
    if (mapInstanceRef.current && seguirRef.current && modoNavegacion && headingRef.current !== null && !usarNavV2Ref.current) {
      mapInstanceRef.current.setHeading(headingRef.current);
    }
    // La moto se mueve suave, rota y arrastra el mapa (dentro de moverSuave)
    actualizarFlecha(ultimo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puntos, modoNavegacion]);

  // Cuando se desactiva el modo navegación, resetear heading a 0 (norte arriba)
  useEffect(() => {
    if (!modoNavegacion && mapInstanceRef.current) {
      mapInstanceRef.current.setHeading(0);
    }
  }, [modoNavegacion]);

  // Perspectiva tipo Waze: con un destino puesto, el mapa se inclina y se acerca;
  // al cancelar el viaje vuelve a plano. La transición va de a poco (12 pasos en
  // ~600 ms) porque el salto de golpe marea — pedido de Carlos.
  const tiltRef = useRef(null);
  useEffect(() => {
    const mapa = mapInstanceRef.current;
    if (!mapa || !mapa.setTilt) return;
    const destino = navegando && modoNavegacion && !usarNavV2 ? 55 : 0; // tapado por el v2, plano: GPU y memoria
    if (tiltRef.current) { clearInterval(tiltRef.current); tiltRef.current = null; }
    let actual = 0;
    try { actual = mapa.getTilt() || 0; } catch (e) { actual = 0; }
    if (Math.abs(actual - destino) < 1) return;
    const pasos = 12, delta = (destino - actual) / pasos;
    let i = 0;
    tiltRef.current = setInterval(() => {
      i++;
      const v = i >= pasos ? destino : actual + delta * i;
      try { mapa.setTilt(v); } catch (e) { /* mapa sin WebGL: se queda plano y listo */ }
      if (i >= pasos) { clearInterval(tiltRef.current); tiltRef.current = null; }
    }, 50);
    return () => { if (tiltRef.current) { clearInterval(tiltRef.current); tiltRef.current = null; } };
  }, [navegando, modoNavegacion, usarNavV2]);
  // Navegando, el +/− de Google sobra (y en pantallas chicas pisa el banner de giros):
  // pinch y doble toque alcanzan. Vuelve al cancelar el destino. Tapado por el v2, Google
  // tampoco escucha gestos (gestureHandling "none"): los dedos van al lienzo de arriba y
  // el mapa de abajo no procesa nada.
  const zoomCtrlRef = useRef(true);
  useEffect(() => {
    const mapa = mapInstanceRef.current;
    const querer = !navegando;
    if (!mapa || zoomCtrlRef.current === querer) return;
    zoomCtrlRef.current = querer;
    try { mapa.setOptions({ zoomControl: querer }); } catch (e) { /* */ }
  }, [navegando, posActual]);
  const gestosGoogleRef = useRef("greedy");
  useEffect(() => {
    const mapa = mapInstanceRef.current;
    const querer = usarNavV2 ? "none" : "greedy";
    if (!mapa || gestosGoogleRef.current === querer) return;
    gestosGoogleRef.current = querer;
    try { mapa.setOptions({ gestureHandling: querer }); } catch (e) { /* */ }
  }, [usarNavV2, posActual]);

  // Rodada espontánea: mientras comparto en vivo, veo en MI mapa a los que abrieron
  // el link y se sumaron (publican su posición en vivo_participantes cada ~10s).
  // Con el v2 arriba van como riders del lienzo (vivoPartsV2) y Google no los dibuja.
  const partMarkersRef = useRef({});
  const avatarVivoRef = useRef({}); // user_id -> avatar_url (vivo_participantes no trae la foto)
  useEffect(() => {
    if (!vivoRegistro) return;
    let activo = true;
    const traer = async () => {
      const { data } = await supabase.from("vivo_participantes").select("*")
        .eq("share_token", vivoRegistro.share_token)
        .gt("updated_at", new Date(Date.now() - 5 * 60000).toISOString());
      if (!activo || !Array.isArray(data) || !mapInstanceRef.current || !window.google) return;
      const porId = {};
      data.filter(p => p.lat && p.lng && p.user_id !== userId).forEach(p => { porId[p.user_id] = p; });
      const sinFoto = Object.keys(porId).filter(id => !(id in avatarVivoRef.current));
      if (sinFoto.length > 0) {
        try {
          const { data: ps } = await supabase.from("perfiles").select("id, avatar_url").in("id", sinFoto);
          (ps || []).forEach(p => { avatarVivoRef.current[p.id] = p.avatar_url || null; });
        } catch (e) { /* sin foto, inicial */ }
        sinFoto.forEach(id => { if (!(id in avatarVivoRef.current)) avatarVivoRef.current[id] = null; });
        if (!activo) return;
      }
      if (usarNavV2Ref.current) {
        setVivoPartsV2(Object.values(porId).map(p => ({ user_id: p.user_id, username: p.username || "rider", avatar_url: avatarVivoRef.current[p.user_id] || null, lat: Number(p.lat), lng: Number(p.lng), color: null })));
        Object.values(partMarkersRef.current).forEach(mk => mk.setMap(null));
        partMarkersRef.current = {};
        return;
      }
      Object.keys(partMarkersRef.current).forEach(id => {
        if (!porId[id]) { partMarkersRef.current[id].setMap(null); delete partMarkersRef.current[id]; }
      });
      Object.values(porId).forEach(p => {
        const pos = { lat: Number(p.lat), lng: Number(p.lng) };
        if (partMarkersRef.current[p.user_id]) {
          partMarkersRef.current[p.user_id].setPosition(pos);
        } else {
          const mk = new window.google.maps.Marker({
            position: pos, map: mapInstanceRef.current,
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5, labelOrigin: new window.google.maps.Point(0, -2.4) },
            label: { text: p.username || "rider", color: C.text, fontSize: "10px", fontWeight: "800" },
          });
          partMarkersRef.current[p.user_id] = mk;
          iconoAvatarMapa(avatarVivoRef.current[p.user_id], (icon) => { if (icon && partMarkersRef.current[p.user_id] === mk) { mk.setIcon(icon); mk.setLabel(null); } });
        }
      });
    };
    traer();
    const iv = setInterval(traer, 15000);
    return () => {
      activo = false; clearInterval(iv);
      Object.values(partMarkersRef.current).forEach(mk => mk.setMap(null));
      partMarkersRef.current = {};
      setVivoPartsV2([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vivoRegistro]);

  // (el push a ubicaciones_vivo se hace DESDE el callback del GPS, no acá: un useEffect
  // no corre con la app en background — era el motivo de que el link en vivo quedara viejo)

  // La ✕ del mapa (en el header, o flotando sobre el mapa mientras se navega)
  const cerrarDesdeHeader = () => {
    if (estado === "grabando" || estado === "pausada") {
      setShowSalir(true); // menú: seguir más tarde / finalizar / descartar
    } else if (estado === "lista" && vivoRegistro) {
      // Sin grabar pero compartiendo en vivo: avisar antes de cortar el compartido
      if (window.confirm("Estás compartiendo tu posición en vivo. Si salís, se corta el compartido. ¿Salir igual?")) onCerrar();
    } else if (estado === "lista" && navegando && onMinimizar) {
      // Navegando sin grabar: la ✕ del mapa minimiza y la navegación sigue de fondo
      // (voz incluida). Para apagarla está "Cancelar navegación" en el menú ⋯.
      onMinimizar();
    } else { onCerrar(); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: estado === "preparando" ? C.card : "#000", zIndex: 350, display: minimizado ? "none" : "flex", flexDirection: "column", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>
      {/* header — navegando NO hay barra: la ✕, el estado y SOS flotan sobre el mapa
          (el mapa ocupa toda la pantalla, como Waze) */}
      {!modoLimpio && (
      <div style={{
        padding: "var(--header-pt) 18px 14px",
        background: estado === "preparando" ? C.card : "rgba(0,0,0,.85)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        color: estado === "preparando" ? "#111111" : "#fff",
        borderBottom: estado === "preparando" ? `1px solid ${C.border}` : "none",
      }}>
        <button onClick={cerrarDesdeHeader} style={{
          width: 32, height: 32, borderRadius: "50%",
          background: estado === "preparando" ? C.surface : "rgba(255,255,255,.15)",
          border: "none", color: estado === "preparando" ? "#111111" : "#fff",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <X size={16} strokeWidth={2.5} />
        </button>
        <div style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 7, letterSpacing: "-0.01em" }}>
          {estado === "grabando" && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF3B30", animation: "pulse 1.5s infinite" }} />}
          {estado === "preparando" && "Nuevo recorrido"}
          {estado === "lista" && "En el mapa"}
          {estado === "grabando" && "Grabando"}
          {estado === "pausada" && "Pausado"}
        </div>
        {/* SOS: WhatsApp al contacto de emergencia con la posición exacta y el link en vivo */}
        {(estado === "grabando" || estado === "pausada" || estado === "lista") ? (
          <button onClick={enviarSOS} style={{
            padding: "6px 12px", borderRadius: 100,
            background: "#B3372C", color: "#fff", border: "none",
            fontSize: 12, fontWeight: 800, letterSpacing: "0.02em", cursor: "pointer",
          }}>SOS</button>
        ) : <div style={{ width: 32 }} />}
      </div>
      )}

      {/* Menú al salir mientras se graba/pausa: seguir más tarde / finalizar / descartar */}
      {showSalir && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowSalir(false)}>
          <div style={{ background: "#1a1a1a", color: "#f9f5f2", borderRadius: "20px 20px 0 0", padding: "20px 18px calc(28px + var(--sab))", width: "100%", maxWidth: 460 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>¿Qué hacés con el recorrido?</div>
            <div style={{ fontSize: 12.5, color: "#bbb", marginBottom: 16, lineHeight: 1.5 }}>Podés seguir navegando Olbos sin cortar la grabación, o finalizarla ahora.</div>
            {/* Antes esta opción PAUSABA y cerraba; ahora la grabación sigue de
                fondo y una barra roja arriba te trae de vuelta cuando quieras. */}
            <button onClick={() => { setShowSalir(false); if (onMinimizar) onMinimizar(); else { if (estado === "grabando") pausar(); onCerrar(); } }}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#f9f5f2", color: C.text, border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
              🛰️ Seguir navegando la app (sigue grabando)
            </button>
            <button onClick={() => { setShowSalir(false); intentarFinalizar(); }}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
              ✅ Finalizar y guardar
            </button>
            <button onClick={() => { setShowSalir(false); cancelarTraza(); }}
              style={{ width: "100%", padding: "11px", borderRadius: 12, background: "transparent", color: "#FF6B6B", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 4 }}>
              🗑️ Descartar recorrido
            </button>
            <button onClick={() => setShowSalir(false)}
              style={{ width: "100%", padding: "10px", background: "none", border: "none", color: "#888", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* contenido / mapa */}
      {estado !== "preparando" ? (
        <div ref={mapaContRef} style={{ flex: 1, position: "relative" }}>
          <div ref={mapRef} className={"olbos-mapa-google" + (mapaOscuro ? " olbos-mapa-oscuro" : "")} style={{ position: "absolute", inset: 0, background: "#1a1a1a" }} />
          {/* Navegador v2 (MapLibre), el default: tapa al mapa de Google siempre que el mapa
              está abierto (con destino o en rodada libre). zIndex 1: arriba de Google (sin z)
              y debajo de todos los botones y overlays del mapa (zIndex 5+). Si no arranca,
              onError lo saca del DOM y vuelve Google (y se registra el fallo). */}
          {usarNavV2 && (
            <React.Suspense fallback={null}>
              <MapaNav ruta={rutaV2} posicion={fixV2} siguiendo={seguirMapa} oscuro={mapaOscuro} marcadores={marcadoresV2}
                traza={puntos} pasivo={pasivoV2} trazadoCircuito={trazadoCircuitoV2} zoomAuto={!zoomManual}
                plan={planRodadaV2} lineaGrupo={lineaGrupoV2} colorRuta={rutaV2Riders ? "#8A6D3B" : undefined}
                calleActual={navInfoUi ? navInfoUi.calle_actual : null}
                style={{ zIndex: 1 }}
                onArrastre={() => { gestoMapaRef.current = true; cancelarVolver(); setSeguirMapa(false); }}
                onGestoFin={() => { gestoMapaRef.current = false; programarVolver(); }}
                onZoomManual={marcarZoomManual}
                onError={alFallarNavV2}
                onToqueMarcador={(m) => {
                  if (!m) return;
                  if (m.tipo === "aviso" && m.aviso) setAvisoSel(m.aviso);
                  else if (m.tipo === "alerta" && m.alerta && alertasSelRef.current) alertasSelRef.current(m.alerta);
                  else if (m.tipo === "foto" && m.foto) setFotoVisor(m.foto);
                }} />
            </React.Suspense>
          )}
          {/* Rodada libre sobre el v2: la pastilla "¿A dónde vamos?" donde navegando va el banner
              de giros (zona segura, hasta la columna del ⋯). Abre el buscador del navegador. */}
          {modoLibreV2 && (
            <button onClick={() => { if (navBuscarRef.current) navBuscarRef.current(); }}
              style={{ position: "absolute", top: "calc(var(--sat, 0px) + 60px)", left: 12, right: 66, zIndex: 9, height: 44, borderRadius: 14, background: C.card, color: C.text, border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", textAlign: "left", boxShadow: "0 6px 24px rgba(0,0,0,.25)", WebkitTapHighlightColor: "transparent" }}>
              <Search size={18} strokeWidth={2.4} color={C.muted} />
              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>¿A dónde vamos?</span>
            </button>
          )}
          {/* Navegando: sin barra superior. La ✕, el estado de la grabación y SOS flotan
              sobre el mapa en la zona segura, en la misma fila que el ⋯ (arriba a la derecha). */}
          {modoLimpio && (
            <>
              <button onClick={cerrarDesdeHeader} title="Salir del mapa"
                style={{ position: "absolute", top: "calc(var(--sat, 0px) + 14px)", left: 12, zIndex: 7, width: 36, height: 36, borderRadius: "50%", background: "rgba(13,13,18,.82)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(5px)", boxShadow: "0 2px 8px rgba(0,0,0,.25)", WebkitTapHighlightColor: "transparent" }}>
                <X size={17} strokeWidth={2.5} />
              </button>
              {(estado === "grabando" || estado === "pausada") && (
                <div style={{ position: "absolute", top: "calc(var(--sat, 0px) + 19px)", left: 54, zIndex: 7, height: 26, padding: "0 9px", borderRadius: 100, background: "rgba(13,13,18,.82)", color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 5, backdropFilter: "blur(5px)", pointerEvents: "none" }}>
                  {estado === "grabando" ? <><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#FF3B30", animation: "pulse 1.5s infinite" }} />REC</> : "⏸"}
                </div>
              )}
              <button onClick={enviarSOS}
                style={{ position: "absolute", top: "calc(var(--sat, 0px) + 18px)", right: 64, zIndex: 7, height: 28, padding: "0 11px", borderRadius: 100, background: "#B3372C", color: "#fff", border: "none", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.04em", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,.25)", WebkitTapHighlightColor: "transparent" }}>SOS</button>
            </>
          )}
          {/* Modo grupo: los avatares, roles, línea de la rodada, largada e
              intercom, montados SOBRE este mapa — un solo grabador para todo */}
          {/* Velocímetro SIEMPRE visible (como Waze) — abajo a la izquierda, como
              Waze: al centro quedaba pisando los chips de la rodada y el buscador
              del navegador (foto de Carlos, BikerFest); tocándolo alterna km/h ↔ millas */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <div onClick={alternarUnidadVel}
              style={{ position: "absolute", bottom: 12, left: 12, zIndex: 6, width: modoLimpio ? 64 : 62, height: modoLimpio ? 64 : 62, borderRadius: "50%", background: C.card, border: "3px solid #111111", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 10px rgba(0,0,0,.35)", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
              <div style={{ fontSize: modoLimpio ? 26 : 22, fontWeight: 900, color: C.text, lineHeight: 1, letterSpacing: modoLimpio ? "-0.02em" : undefined, fontVariantNumeric: "tabular-nums" }}>{velKmh != null ? (unidadVel === "mph" ? Math.round(velKmh * 0.621371) : velKmh) : "–"}</div>
              <div style={{ fontSize: 8.5, fontWeight: 700, color: C.muted, letterSpacing: "0.04em" }}>{unidadVel === "mph" ? "MPH" : "KM/H"}</div>
            </div>
          )}
          {/* Inclinación de la moto (como CHIGEE), arriba de la columna izquierda: velocímetro,
              🌙 (sin navegar, bottom 84) y ⚠️ avisar (84 navegando, 132 sin navegar). Antes
              quedaba debajo del ⚠️ (reporte de Carlos, 27-oct). */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && <InclinacionMoto velKmh={velKmh} abajo={(grupoEnVivoId || viajeEnVivoId) && !grupoVivo ? 190 /* arriba de la pastilla del grupo en vivo (148, también con el navegador nuevo) */ : modoLimpio ? 132 : 180} />}
          {/* Navegando: altura y temperatura en UNA pastilla al lado del velocímetro
              (adentro del círculo, a 8 px, no se leía) */}
          {modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (altM != null || tempC != null) && (
            <div style={{ position: "absolute", bottom: 31, left: 84, zIndex: 6, height: 26, padding: "0 10px", borderRadius: 100, background: "rgba(13,13,18,.85)", color: "#fff", fontSize: 12, fontWeight: 800, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 4, backdropFilter: "blur(4px)", whiteSpace: "nowrap", pointerEvents: "none" }}>
              {altM != null && <><IconoMontana /><span>{altM.toLocaleString("es-AR")} m</span></>}
              {altM != null && tempC != null && <span style={{ opacity: 0.6 }}>·</span>}
              {tempC != null && <span>{tempC}°</span>}
            </div>
          )}
          {/* Altura y temperatura, al lado del velocímetro (sin navegar: dos pastillas) */}
          {!modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (altM != null || tempC != null) && (
            <div style={{ position: "absolute", bottom: 14, left: 82, zIndex: 6, display: "flex", gap: 6, pointerEvents: "none" }}>
              {altM != null && <div style={{ background: "rgba(13,13,18,.85)", color: "#fff", borderRadius: 100, padding: "6px 10px", fontSize: 12, fontWeight: 800, letterSpacing: "-0.01em", backdropFilter: "blur(4px)", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}><IconoMontana /><span>{altM.toLocaleString("es-AR")} m</span></div>}
              {tempC != null && <div style={{ background: "rgba(13,13,18,.85)", color: "#fff", borderRadius: 100, padding: "6px 10px", fontSize: 12, fontWeight: 800, letterSpacing: "-0.01em", backdropFilter: "blur(4px)", whiteSpace: "nowrap" }}>🌡 {tempC}°</div>}
            </div>
          )}
          {/* Avisar: peligro, nafta, control, reagrupe (aviso efímero de 24 h en tu posición) */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={() => setShowAvisar(true)} title="Marcar un aviso en el mapa"
              style={{ position: "absolute", bottom: modoLimpio ? 84 : 132, left: 12, zIndex: 6, width: 40, height: 40, borderRadius: "50%", background: "#F9F5F2", border: "2px solid #B3372C", fontSize: 17, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent" }}>
              ⚠️
            </button>
          )}
          {showAvisar && (
            <div onClick={() => setShowAvisar(false)} style={{ position: "absolute", inset: 0, zIndex: 14, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "flex-end" }}>
              <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: "#111111", color: "#F9F5F2", borderRadius: "18px 18px 0 0", padding: "16px 16px calc(16px + var(--sab, 0px))" }}>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>¿Qué hay acá?</div>
                <div style={{ fontSize: 12, color: "#bdb6ab", marginBottom: 12 }}>Se marca en tu posición y lo ven los riders de la zona durante 24 horas.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  {Object.entries(TIPOS_AVISO).map(([k, [emoji, nombre, color]]) => (
                    <button key={k} onClick={() => crearAviso(k)} disabled={avisoEnviando} style={{ padding: "13px 10px", borderRadius: 12, background: "rgba(249,245,242,.08)", border: `1.5px solid ${color}`, color: "#F9F5F2", fontSize: 13.5, fontWeight: 800, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8, opacity: avisoEnviando ? 0.6 : 1 }}>
                      <span style={{ fontSize: 20 }}>{emoji}</span>{nombre}
                    </button>
                  ))}
                </div>
                <input value={avisoNota} onChange={e => setAvisoNota(e.target.value.slice(0, 80))} placeholder="Nota opcional: pozo grande, aceite, sin nafta hasta…"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(249,245,242,.25)", background: "rgba(249,245,242,.08)", color: "#F9F5F2", fontSize: 13, boxSizing: "border-box" }} />
              </div>
            </div>
          )}
          {avisoSel && (() => {
            const [emoji, nombre, color] = TIPOS_AVISO[avisoSel.tipo] || TIPOS_AVISO.peligro;
            const hace = Math.max(0, Math.round((Date.now() - Date.parse(avisoSel.created_at)) / 60000));
            const dist = posActual ? haversineKm(posActual, { lat: Number(avisoSel.lat), lng: Number(avisoSel.lng) }) : null;
            return (
              <div style={{ position: "absolute", left: 12, right: 12, bottom: 190, zIndex: 13, background: "#111111", color: "#F9F5F2", borderRadius: 14, padding: "12px 14px", boxShadow: "0 8px 30px rgba(0,0,0,.5)", borderLeft: `4px solid ${color}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 22 }}>{emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{nombre}{dist != null ? ` · a ${dist < 1 ? Math.round(dist * 1000) + " m" : dist.toFixed(1) + " km"}` : ""}</div>
                    <div style={{ fontSize: 11.5, color: "#bdb6ab" }}>@{avisoSel.username || "rider"} · hace {hace >= 60 ? `${Math.floor(hace / 60)} h ${hace % 60} min` : `${hace} min`}{avisoSel.confirmaciones ? ` · ${avisoSel.confirmaciones} confirm.` : ""}</div>
                  </div>
                  <button onClick={() => setAvisoSel(null)} style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", color: "#F9F5F2", cursor: "pointer", fontSize: 12 }}>✕</button>
                </div>
                {avisoSel.nota && <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>{avisoSel.nota}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={() => votarAviso(avisoSel, true)} style={{ flex: 1, padding: "9px", borderRadius: 10, background: "rgba(249,245,242,.12)", border: "none", color: "#F9F5F2", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>👍 Sigue ahí</button>
                  <button onClick={() => votarAviso(avisoSel, false)} style={{ flex: 1, padding: "9px", borderRadius: 10, background: "rgba(249,245,242,.12)", border: "none", color: "#F9F5F2", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Ya no está</button>
                </div>
              </div>
            );
          })()}
          {/* Mapa de noche / de día (navegando va al menú ⋯) */}
          {!modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={alternarMapaOscuro} title={mapaOscuro ? "Mapa de día" : "Mapa de noche"}
              style={{ position: "absolute", bottom: 84, left: 12, zIndex: 6, width: 40, height: 40, borderRadius: "50%", background: mapaOscuro ? "#F9F5F2" : "#111111", border: "2px solid #fff", fontSize: 17, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent" }}>
              {mapaOscuro ? "☀️" : "🌙"}
            </button>
          )}
          {/* En pausa y en movimiento: ¿retomamos? */}
          {avisoRetomar && estado === "pausada" && (
            <div style={{ position: "absolute", left: 12, right: 12, top: "38%", zIndex: 12, background: "#111111", color: "#F9F5F2", borderRadius: 16, padding: "16px 16px 14px", boxShadow: "0 8px 30px rgba(0,0,0,.5)" }}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4, letterSpacing: "-0.01em" }}>¿Seguimos grabando?</div>
              <div style={{ fontSize: 13, color: "#bdb6ab", lineHeight: 1.5, marginBottom: 12 }}>Te detectamos en movimiento con la grabación en pausa. Si no respondés, la retomo sola en <strong style={{ color: "#F9F5F2" }}>{avisoRetomar.seg} s</strong>; lo andado desde que arrancaste no se pierde.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => reanudar(false)} style={{ flex: 1.4, padding: 12, borderRadius: 12, background: "#27AE60", color: "#fff", border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>▶ Retomar</button>
                <button onClick={seguirEnPausa} style={{ flex: 1, padding: 12, borderRadius: 12, background: "rgba(249,245,242,.12)", color: "#F9F5F2", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Seguir en pausa</button>
              </div>
            </div>
          )}
          {avisoRetomado && (
            <div style={{ position: "absolute", left: 12, right: 66, top: topAvisos, zIndex: 9, background: "rgba(39,174,96,.95)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>
              ▶ Grabación retomada sola: te detectamos en movimiento.
            </div>
          )}
          {avisoPermisoFondo && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <div style={{ position: "absolute", left: 12, right: 66, top: topAvisos, zIndex: 9, background: "rgba(179,55,44,.95)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontSize: 12.5, fontWeight: 700, lineHeight: 1.45, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1 }}>
                {permisoFondoAyuda
                  ? (_plataformaNativa() === "ios"
                    ? "En Ajustes → Olbos → Ubicación elegí «Siempre». Cuando vuelvas, este aviso se va solo."
                    : "Andá a Ajustes del teléfono → Aplicaciones → Olbos → Permisos → Ubicación y elegí «Permitir todo el tiempo». Cuando vuelvas, este aviso se va solo.")
                  : "📵 Con el teléfono bloqueado no te van a ver en el mapa: la ubicación tiene que estar en «Permitir todo el tiempo»."}
              </div>
              {permisoFondoAyuda && (
                <button onClick={() => { setAvisoPermisoFondo(false); setPermisoFondoAyuda(false); }} style={{ flexShrink: 0, padding: "8px 11px", borderRadius: 10, background: "#fff", color: "#B3372C", border: "none", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>Listo</button>
              )}
              {!permisoFondoAyuda && <button onClick={pedirPermisoFondo} style={{ flexShrink: 0, padding: "8px 11px", borderRadius: 10, background: "#fff", color: "#B3372C", border: "none", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>Activar</button>}
            </div>
          )}
          {avisoWebBloqueo && (
            <div onClick={() => setAvisoWebBloqueo(false)} style={{ position: "absolute", left: 12, right: 66, top: topAvisos, zIndex: 9, background: "rgba(17,17,17,.94)", color: "#F9F5F2", borderRadius: 12, padding: "10px 12px", fontSize: 12.5, fontWeight: 700, lineHeight: 1.45, cursor: "pointer" }}>
              📵 En el navegador, con la pantalla bloqueada se corta el GPS y el recorrido queda con huecos. Dejá la pantalla prendida o usá la app de la tienda, que graba con el teléfono guardado.
            </div>
          )}
          {/* Moviste el mapa a mano → botón para volver a vos */}
          {!seguirMapa && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={() => { setSeguirMapa(true); volverZoomAuto(); if (!usarNavV2 && mapInstanceRef.current && posActualRef.current) mapInstanceRef.current.panTo(posActualRef.current); }}
              title="Volver a tu posición"
              style={{ position: "absolute", top: modoLimpio ? "calc(var(--sat, 0px) + 62px)" : 216, right: 12, zIndex: 6, width: 44, height: 44, borderRadius: "50%", background: C.accent, color: "#fff", border: "2px solid #fff", fontSize: 18, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              📍
            </button>
          )}
          {/* El rider eligió el zoom con los dedos: "auto" vuelve al zoom por velocidad
              (también vuelve solo a los 30 s sin tocar el mapa, o con 📍) */}
          {zoomManual && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={volverZoomAuto} title="Volver al zoom automático"
              style={{ position: "absolute", top: modoLimpio ? "calc(var(--sat, 0px) + 112px)" : 316, right: 12, zIndex: 6, width: 44, height: 32, borderRadius: 100, background: C.card, color: C.text, border: "2px solid #111111", fontSize: 11.5, fontWeight: 800, letterSpacing: "-0.01em", cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent" }}>
              auto
            </button>
          )}
          {/* ── Modo circuito ── */}
          {circuito && (estado === "grabando" || estado === "pausada") && (
            <PanelCircuitoVivo circuito={circuito} vueltas={vueltasCirc} vueltaIniTs={vueltaIniTs} mejorSeg={mejorSeg} ultimaDelta={ultimaDelta} onSalir={salirCircuito} saltos={saltosCirc} />
          )}
          {trazandoNuevo && !proponerCirc && estado === "grabando" && (
            <div style={{ position: "absolute", top: topAvisos, left: 12, right: 66, zIndex: 8, background: "rgba(13,13,18,.93)", borderRadius: 14, padding: "10px 14px", color: "#fff", fontSize: 13, backdropFilter: "blur(6px)" }}>
              🏁 <strong>Circuito nuevo:</strong> dá una vuelta completa. Cuando vuelvas a pasar por donde empezaste, te lo marco.
              <button onClick={() => { nuevoCircRef.current = null; setTrazandoNuevo(false); }} style={{ marginLeft: 8, background: "none", border: "none", color: "#bdb7b0", cursor: "pointer", fontSize: 12 }}>cancelar</button>
            </div>
          )}
          {/* Un circuito conocido a menos de 5 km: se ofrece una vez */}
          {!circuito && !circuitoDescartado && circuitosCerca && circuitosCerca.length > 0 && (estado === "lista" || estado === "grabando") && (
            <div style={{ position: "absolute", bottom: 122, left: 12, right: 12, zIndex: 9, background: "rgba(13,13,18,.94)", color: "#fff", borderRadius: 14, padding: "12px 14px", backdropFilter: "blur(6px)" }}>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>🏁 ¿Estás en {circuitosCerca[0].nombre}?</div>
              <div style={{ fontSize: 12, color: "#bdb7b0", marginBottom: 10 }}>Cuento las vueltas, los tiempos y las curvas. Cruzá la largada para arrancar.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => activarCircuito(circuitosCerca[0])} style={{ flex: 1.4, padding: "11px", borderRadius: 11, background: "#F9F5F2", color: "#111", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>Sí, contar vueltas</button>
                {circuitosCerca.length > 1 && <button onClick={() => setElegirCircuito(true)} style={{ flex: 1, padding: "11px", borderRadius: 11, background: "rgba(255,255,255,.12)", color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Es otro</button>}
                <button onClick={() => setCircuitoDescartado(true)} style={{ flex: 0.8, padding: "11px", borderRadius: 11, background: "transparent", color: "#bdb7b0", border: "1px solid rgba(255,255,255,.25)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>No</button>
              </div>
            </div>
          )}
          {/* Botón circuito en la columna derecha: elegir uno cerca o trazar uno nuevo (navegando va al menú ⋯) */}
          {!modoLimpio && !circuito && (estado === "lista" || estado === "grabando") && (
            <button onClick={() => setElegirCircuito(true)} title="Modo circuito"
              style={{ position: "absolute", top: 266, right: 12, zIndex: 6, width: 44, height: 44, borderRadius: "50%", background: trazandoNuevo ? "#111111" : "rgba(255,255,255,.95)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.25)", fontSize: 18 }}>
              🏁
            </button>
          )}
          {elegirCircuito && (
            <div onClick={e => e.target === e.currentTarget && setElegirCircuito(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 18px calc(28px + var(--sab))" }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 4 }}>🏁 Modo circuito</div>
                <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>Pista o off-road: cuento las vueltas por cruce de la largada, con tiempo, velocidad y, en pista, el análisis de cada curva.</div>
                {(circuitosCerca || []).map(c => (
                  <button key={c.id} onClick={() => activarCircuito(c)} style={{ width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 8, cursor: "pointer" }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{c.nombre}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{c.tipo === "offroad" ? "Off-road" : "Pista"}{c.largo_m ? ` · ${(c.largo_m / 1000).toFixed(2)} km` : ""}{c.dist_km != null ? ` · a ${Number(c.dist_km).toFixed(1)} km` : ""}</div>
                  </button>
                ))}
                {(!circuitosCerca || circuitosCerca.length === 0) && <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>No hay circuitos cargados cerca tuyo.</div>}
                <button onClick={empezarCircuitoNuevo} style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>＋ Nuevo circuito: dar una vuelta y marcarlo</button>
              </div>
            </div>
          )}
          {proponerCirc && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 18px calc(28px + var(--sab))" }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 4 }}>🏁 Cerraste una vuelta de {proponerCirc.km} km</div>
                <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>Donde arrancaste queda como línea de largada. Ponele nombre y el circuito queda guardado para vos y para todos los que vengan.</div>
                <input value={nombreCirc} onChange={e => setNombreCirc(e.target.value)} placeholder="Ej: Circuito de Roque Pérez" style={{ width: "100%", padding: "12px 13px", borderRadius: 11, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, background: C.surface, boxSizing: "border-box", marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {[["pista", "🏍️ Pista (asfalto)"], ["offroad", "🏔️ Off-road / motocross"]].map(([v, l]) => (
                    <button key={v} onClick={() => setTipoCirc(v)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: `1.5px solid ${tipoCirc === v ? C.text : C.border}`, background: tipoCirc === v ? C.text : C.card, color: tipoCirc === v ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: vigenciaCirc === "temporal" ? 8 : 12 }}>
                  {[["permanente", "📍 Permanente"], ["temporal", "⏳ Temporal (solo este evento)"]].map(([v, l]) => (
                    <button key={v} onClick={() => { setVigenciaCirc(v); if (v === "temporal" && !hastaCirc) setHastaCirc(hastaSugerida()); }} style={{ flex: 1, padding: "10px", borderRadius: 10, border: `1.5px solid ${vigenciaCirc === v ? C.text : C.border}`, background: vigenciaCirc === v ? C.text : C.card, color: vigenciaCirc === v ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
                  ))}
                </div>
                {vigenciaCirc === "temporal" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: C.muted, flex: 1, lineHeight: 1.4 }}>Trazado armado para una fecha (enduro, rally, prueba). Después de este día deja de aparecer; tus vueltas quedan guardadas igual.</div>
                    <input type="date" value={hastaCirc} onChange={e => setHastaCirc(e.target.value)} style={{ padding: "8px 10px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.surface }} />
                  </div>
                )}
                <button onClick={guardarCircuitoNuevo} style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}>Guardar y seguir contando vueltas</button>
                <button onClick={() => { setProponerCirc(null); nuevoCircRef.current = null; setTrazandoNuevo(false); }} style={{ width: "100%", padding: "10px", borderRadius: 12, background: "transparent", color: C.muted, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>No era una vuelta, seguir grabando normal</button>
              </div>
            </div>
          )}
          {/* Comandos de voz: el micrófono del mapa. Las acciones son las del grabador. */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <ComandosVoz visible compacto={modoLimpio} acciones={{
              foto: (f) => tomarFoto(f),
              parada: (nombre) => {
                if (!posActualRef.current) return "Todavía no tengo tu posición.";
                marcarParada(nombre || `Parada ${paradasRef.current.length + 1}`);
                return `Parada marcada${nombre ? `: ${nombre}` : ""}.`;
              },
              pausar: () => {
                if (estadoRecRef.current === "grabando") { pausar(); return "Grabación en pausa."; }
                return estadoRecRef.current === "pausada" ? "Ya estaba en pausa." : "No estás grabando. Decí 'grabar' para empezar.";
              },
              reanudar: () => {
                if (estadoRecRef.current === "pausada") { reanudar(); return "Seguimos grabando."; }
                if (estadoRecRef.current === "lista") { iniciar(); return "Empezamos a grabar."; }
                return "Ya estás grabando.";
              },
              finalizar: () => {
                if (estadoRecRef.current === "lista") return "No hay un recorrido grabándose.";
                intentarFinalizar();
                return "Te abro el cierre del recorrido: confirmalo en la pantalla.";
              },
              cuantoFalta: () => {
                const i = navInfoRef.current;
                if (!i || i.rest_km == null) return "No tenés un destino puesto. Decime 'llevame a' y el lugar.";
                return `Faltan ${kmAHablado(i.rest_km)}, ${minutosAHablado(i.min)}. Llegás a las ${i.eta}.`;
              },
              velocidad: () => velKmh == null ? "Todavía no tengo tu velocidad." : `Vas a ${velKmh} kilómetros por hora.`,
              kmLlevo: () => {
                if (puntosRef.current.length < 2) return "Todavía no empezaste a grabar.";
                const st = calcEstadisticas(puntosRef.current);
                return `Llevás ${kmAHablado(st.dist)} en ${minutosAHablado(st.dur / 60)}.`;
              },
              dondeEsta: async (nombre) => {
                if (!grupoVivo) return "No estás en una rodada con grupo.";
                const { data: raw } = await supabase.rpc("get_rodada_ubicaciones", { p_evento_id: grupoVivo.id });
                const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
                if (!Array.isArray(arr)) return "No pude ver al grupo.";
                const q = normalizarVoz(nombre);
                const cand = arr.filter(u => u.user_id !== userId && u.lat && u.lng)
                  .map(u => ({ u, n: normalizarVoz(u.username || "") }))
                  .filter(x => x.n && (x.n.includes(q) || q.includes(x.n) || x.n.split(/[_\s.]/).some(w => w.length >= 3 && q.includes(w)) || editDist(x.n, q) <= 2));
                if (!cand.length) return `No encuentro a ${nombre} en el mapa de la rodada.`;
                const u = cand[0].u;
                const yo = posActualRef.current;
                if (!yo) return `${u.username} está en el mapa, pero todavía no tengo tu posición.`;
                const otro = { lat: Number(u.lat), lng: Number(u.lng) };
                const km = haversineKm(yo, otro);
                let rel = "";
                if (headingRef.current != null) {
                  const d = ((calcularHeading(yo, otro) - headingRef.current + 540) % 360) - 180;
                  rel = Math.abs(d) < 60 ? "adelante" : Math.abs(d) > 120 ? "atrás" : d > 0 ? "a tu derecha" : "a tu izquierda";
                }
                const hace = u.updated_at ? Math.round((Date.now() - Date.parse(u.updated_at)) / 60000) : 0;
                return `${u.username} está a ${kmAHablado(km)}${rel ? ` ${rel}` : ""}${hace > 3 ? `, último dato hace ${hace} minutos` : ""}.`;
              },
              avisarGrupo: async (msg) => {
                const gid = (grupoVivo && grupoVivo.grupo_id) || grupoEnVivoId || null;
                if (!gid) return "Esta rodada no tiene chat de grupo.";
                const { error } = await supabase.rpc("enviar_mensaje_grupo", { p_grupo_id: gid, p_texto: `🎤 ${msg}`, p_media_url: null, p_media_type: null });
                return error ? "No pude mandar el mensaje al grupo." : `Listo, le avisé al grupo: ${msg}.`;
              },
              irA: (texto) => new Promise(resolve => {
                let listo = false;
                const h = (e) => {
                  if (listo) return; listo = true;
                  window.removeEventListener("olbos-voz-destino-listo", h);
                  const d = e.detail || {};
                  resolve(d.ok ? `Te llevo a ${d.nombre}: ${kmAHablado(d.km)}, ${minutosAHablado(d.min)}.` : `No encontré ${texto}. Probá con la ciudad o la dirección.`);
                };
                window.addEventListener("olbos-voz-destino-listo", h);
                window.dispatchEvent(new CustomEvent("olbos-voz-destino", { detail: { texto } }));
                setTimeout(() => { if (listo) return; listo = true; window.removeEventListener("olbos-voz-destino-listo", h); resolve("No pude calcular la ruta. Probá de nuevo."); }, 15000);
              }),
              reportar: async (tipo) => {
                const pos = posActualRef.current;
                if (!pos) return "Todavía no tengo tu posición.";
                const { data: raw, error } = await supabase.rpc("reportar_alerta", { p_tipo: tipo, p_lat: pos.lat, p_lng: pos.lng, p_descripcion: "Reportado por voz" });
                const r = typeof raw === "string" ? JSON.parse(raw) : raw;
                if (error || (r && r.error)) return r && r.error === "muy_seguido" ? "Esperá un par de minutos entre reportes." : "No se pudo reportar.";
                const t = ALERTA_TIPOS.find(x => x.id === tipo);
                return `Reportado: ${t ? t.n : tipo}. La comunidad ya lo ve.`;
              },
              nafta: () => { setShowCargaNafta(true); return "Te abro la carga de nafta."; },
              sos: () => { enviarSOS(); return "Mandando el aviso de emergencia."; },
              centrar: () => {
                setSeguirMapa(true);
                if (!usarNavV2 && mapInstanceRef.current && posActualRef.current) mapInstanceRef.current.panTo(posActualRef.current);
                return "Mapa centrado en vos.";
              },
            }} />
          )}
          {/* Capa de alertas comunitarias: pins + reportar + votar, en todos los modos.
              Con el v2 arriba publica los pins (alertasV2) y no los dibuja en Google. */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <AlertasMapaOverlay mapInstanceRef={mapInstanceRef} posRef={posActualRef} userId={userId} ocultarBoton={modoLimpio} abrirRef={alertasAbrirRef}
              alActualizar={setAlertasV2} seleccionarRef={alertasSelRef} soloDatos={usarNavV2} />
          )}
          {!modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <BotonCapasMapa mapInstanceRef={mapInstanceRef} style={{ top: 116, right: 12 }}
              onCambio={(t) => {
                // Sobre satélite/relieve la línea negra del recorrido se pierde en lo
                // verde oscuro: pasa a blanca y un punto más gruesa (pedido de Carlos)
                if (polylineRef.current) polylineRef.current.setOptions(t === "roadmap"
                  ? { strokeColor: "#111111", strokeWeight: 4 }
                  : { strokeColor: "#FFFFFF", strokeWeight: 5 });
              }} />
          )}
          {/* Carga de nafta desde el mapa (pedido de Carlos): parado en la estación,
              sin ir al garage. Toma la posición GPS como la estación donde cargaste. */}
          {!modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={() => setShowCargaNafta(true)} title="Registrar carga de nafta"
              style={{ position: "absolute", top: 166, right: 12, zIndex: 6, width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,.95)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.25)", fontSize: 18 }}>
              ⛽
            </button>
          )}
          {showCargaNafta && (
            <CargaNaftaModal userId={userId} gpsPos={posActualRef.current}
              onClose={() => setShowCargaNafta(false)} onGuardada={() => {}} />
          )}
          {/* El navegador turn-by-turn: destino, ruta, giros por voz y recálculo.
              navegarA (botón "🏍️ Olbos" de una ficha) entra como destinoInicial:
              apenas hay GPS calcula la ruta y arranca solo, sin depender de la
              restauración por localStorage ni del evento (reporte de Matías x2) */}
          {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <NavegadorOverlay mapInstanceRef={mapInstanceRef} posActual={posActual} velMovil={velMovilReciente}
              destinoInicial={navRodada || navegarA || undefined}
              abrirBuscadorRef={navBuscarRef} ocultarBuscador={modoLibreV2}
              alLlegar={(info) => setLlegada(info)}
              alActualizarParadasNav={setParadasNavV2}
              alPreview={(path, esRiders) => {
                // Mientras se elige el camino, el lienzo v2 muestra la alternativa marcada;
                // al cancelar vuelve la ruta que ya había (o nada)
                if (path && path.length > 1) { setRutaV2(path); setRutaV2Riders(!!esRiders); }
                else { const prev = rutaPlaneadaRef.current; setRutaV2(prev && prev.length > 1 ? prev : null); }
              }}
              alCambiarRuta={(path, restaurada, esRiders) => {
                rutaPlaneadaRef.current = path;
                // Navegador v2: la línea del lienzo MapLibre (marrón si es un camino de riders)
                // y, con ruta nueva (no restaurada), la descarga del corredor para seguir sin señal
                setRutaV2(path && path.length > 1 ? path : null);
                setRutaV2Riders(!!esRiders);
                if (restaurada) cancelarCorredor(); else descargarCorredorV2(path);
                // Puso un destino AHORA: eso es intención de rodar → se empieza a grabar.
                // Si la ruta se restauró sola de un viaje anterior (el navegador la guarda
                // 12 horas), NO cuenta: el rider abrió el mapa para otra cosa y se le
                // ponía a grabar con un destino que no había puesto (reporte de Matías).
                if (path && path.length > 1 && !restaurada) arrancarSiHayIntencion("destino");
                // Si estás compartiendo en vivo, el plan viaja al que te mira
                if (vivoRef.current) {
                  supabase.from("ubicaciones_vivo").update({ ruta_planeada: path ? codificarPolyline(path) : null }).eq("id", vivoRef.current.id).then(() => {}, () => {});
                }
              }}
              menuRef={navMenuRef}
              alActualizarNav={(info) => { navInfoRef.current = info; navGiroMRef.current = info && info.giro_m != null ? info.giro_m : null; setNavInfoUi(info); setNavegando(!!info); }} />
          )}
          {/* Llegaste: ficha de cierre de dos líneas. "Listo" la cierra (la navegación ya se
              apagó sola al llegar); si se estaba grabando, ofrece Finalizar. */}
          {llegada && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <div style={{ position: "absolute", left: 12, right: 12, bottom: 84, zIndex: 13, background: "#111111", color: "#F9F5F2", borderRadius: 16, padding: "14px 16px", boxShadow: "0 10px 34px rgba(0,0,0,.5)" }}>
              <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🏁 Llegaste a {llegada.nombre}</div>
              <div style={{ fontSize: 13, color: "#bdb6ab", marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
                {llegada.km != null ? `${llegada.km < 10 ? Number(llegada.km).toFixed(1).replace(".", ",") : Math.round(llegada.km)} km` : ""}
                {llegada.km != null && llegada.min != null ? " · " : ""}
                {llegada.min != null ? (llegada.min >= 60 ? `${Math.floor(llegada.min / 60)} h ${String(llegada.min % 60).padStart(2, "0")} min` : `${llegada.min} min`) : ""}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => setLlegada(null)} style={{ flex: 1, padding: "12px", borderRadius: 12, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>Listo</button>
                {(estado === "grabando" || estado === "pausada") && (
                  <button onClick={() => { setLlegada(null); intentarFinalizar(); }} style={{ flex: 1, padding: "12px", borderRadius: 12, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>Finalizar</button>
                )}
              </div>
            </div>
          )}
          {/* Empezó a grabar solo por haber puesto destino o entrado a una rodada */}
          {avisoAutoGrabo && estado === "grabando" && (
            <div style={{ position: "absolute", bottom: 122, left: 12, right: 12, zIndex: 9, background: "rgba(13,13,18,.93)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.45, backdropFilter: "blur(6px)" }}>
              🔴 Empezamos a grabar {avisoAutoGrabo === "rodada" ? "la rodada" : "el recorrido"}. Al terminar elegís si lo guardás o lo descartás.
            </div>
          )}
          {/* El organizador marcó la rodada como terminada mientras grababas: cierre en UN
              toque, con el nombre de la rodada y asfalto por defecto. Antes era un banner
              de 7 s y el rider seguía grabando hasta la casa; las trazas sin finalizar
              quedaban en "grabando". De noche y con guantes: dos botones grandes. */}
          {rodadaTermino && grupoVivo && (estado === "grabando" || estado === "pausada") && !showFinalizar && (
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 40, background: "#111111", color: "#F9F5F2", borderRadius: "18px 18px 0 0", padding: "18px 18px calc(22px + var(--sab, 0px))", boxShadow: "0 -8px 30px rgba(0,0,0,.45)" }} className="fade-up">
              <div style={{ fontSize: 11, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 6 }}>🏁 La rodada terminó</div>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2, marginBottom: 4 }}>{String(grupoVivo.titulo || grupoVivo.title || "la rodada").trim()}</div>
              <div style={{ fontSize: 13, color: "#bdb6ab", lineHeight: 1.5, marginBottom: 14 }}>
                {puntos.length ? `${calcEstadisticas(puntos).dist.toFixed(1).replace(".", ",")} km grabados` : "Tu recorrido"} · suma a tu pasaporte y al recorrido oficial de la rodada.
              </div>
              <button onClick={() => {
                setRodadaTermino(false);
                intentarFinalizar();
                // Después de intentarFinalizar, que pisa el título con "Recorrido del …"
                setTituloFinal(String(grupoVivo.titulo || grupoVivo.title || "").trim() || `Recorrido del ${new Date().toLocaleDateString("es-AR", { day: "numeric", month: "long" })}`);
                if (!tipoCamino) setTipoCamino("asfalto");
              }}
                style={{ width: "100%", padding: "15px", borderRadius: 14, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: "inherit", marginBottom: 8 }}>
                ✅ Guardar mi recorrido
              </button>
              <button onClick={() => setRodadaTermino(false)}
                style={{ width: "100%", padding: "12px", borderRadius: 14, background: "transparent", color: "#bdb6ab", border: "1px solid rgba(249,245,242,.25)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Seguir grabando
              </button>
            </div>
          )}
          {/* Avisos sobre el mapa: clima + recolección anónima (primera vez) */}
          {(avisoClima || avisoRed || avisoMapaAnterior) && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            /* Navegando, el banner de giros (más alto desde la v1.10.9) ocupa la parte
               de arriba: los avisos bajan para no quedar pisados (reporte de Carlos) */
            <div style={{ position: "absolute", top: grupoVivo && modoLimpio ? `calc(${topAvisos} + 40px)` : topAvisos, left: 12, right: 66, zIndex: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {/* El navegador nuevo falló dos veces en 24 h en este teléfono: Google queda por defecto acá */}
              {avisoMapaAnterior && (
                <div style={{ alignSelf: "flex-start", background: "rgba(13,13,18,.92)", color: "#fff", borderRadius: 12, padding: "7px 11px", fontSize: 12, fontWeight: 700, letterSpacing: "-0.01em", backdropFilter: "blur(5px)", pointerEvents: "none" }}>
                  Usamos el mapa anterior en este teléfono
                </div>
              )}
              {/* El mapa del corredor (fase 2) se baja en silencio: el aviso de "Mapa guardado X%"
                  aparecía con cada recálculo de la ruta y molestaba (Carlos, 25-oct-2026) */}
              {/* Clima + aporte anónimo en UNA línea plegada: las dos tarjetas apiladas tapaban
                  un tercio del mapa al entrar a la rodada (auditoría 09-oct). Tocar abre. */}
              {(avisoClima || avisoRed) && !avisosAbiertos && (
                <div onClick={() => setAvisosAbiertos(true)}
                  style={{ background: "rgba(13,13,18,.92)", color: "#fff", borderRadius: 12, padding: "7px 11px", fontSize: 12, fontWeight: 700, display: "flex", gap: 8, alignItems: "center", backdropFilter: "blur(5px)", cursor: "pointer" }}>
                  <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                    {[avisoClima ? String(avisoClima).split(/ — |\. /)[0] : null, avisoRed ? "🗺️ Tu camino aporta al mapa" : null].filter(Boolean).join(" · ")}
                  </span>
                  <span style={{ fontSize: 10, opacity: .8, flexShrink: 0 }}>▾</span>
                  <button onClick={(e) => { e.stopPropagation(); setAvisoClima(null); setAvisoRed(false); }} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>✕</button>
                </div>
              )}
              {avisosAbiertos && avisoClima && (
                <div onClick={() => setAvisosAbiertos(false)} style={{ background: "rgba(13,13,18,.92)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, display: "flex", gap: 8, alignItems: "flex-start", backdropFilter: "blur(5px)", cursor: "pointer" }}>
                  <span style={{ flex: 1 }}>{avisoClima}</span>
                  <button onClick={(e) => { e.stopPropagation(); setAvisoClima(null); }} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>✕</button>
                </div>
              )}
              {avisosAbiertos && avisoRed && (
                <div onClick={() => setAvisosAbiertos(false)} style={{ background: "rgba(13,13,18,.92)", color: "#fff", borderRadius: 12, padding: "10px 12px", fontSize: 12, lineHeight: 1.5, display: "flex", gap: 8, alignItems: "flex-start", backdropFilter: "blur(5px)", cursor: "pointer" }}>
                  <span style={{ flex: 1 }}>🗺️ Tu camino aporta, de forma <strong>anónima</strong>, al mapa motero de la comunidad (se recortan los tramos cerca de tu punto de partida). Podés apagarlo en tu perfil.</span>
                  <button onClick={(e) => { e.stopPropagation(); setAvisoRed(false); }} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>✕</button>
                </div>
              )}
            </div>
          )}
          {grupoVivo && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <GrupoRodadaOverlay evento={grupoVivo} mapInstanceRef={mapInstanceRef} puntosRef={puntosRef} posRef={posActualRef} userId={userId} estado={estado}
              alActualizarOtros={setRidersV2} alActualizarResumen={setRodadaResumen} uiRef={rodadaUiRef}
              alActualizarPlan={setPlanRodadaV2} alActualizarParadasPlan={setParadasPlanV2} alActualizarLinea={setLineaGrupoV2} soloDatos={usarNavV2}
              compacto={modoLimpio} topUi={topAvisos}
              alCambiarEstado={(e) => { rodadaEnCursoRef.current = e === "en_curso"; if (e === "finalizada" && (estadoRecRef.current === "grabando" || estadoRecRef.current === "pausada")) setRodadaTermino(true); }}
              alArrancar={() => arrancarSiHayIntencion("rodada")} />
          )}
          {/* Invitado por link /vivo: el mapa compartido de la rodada espontánea,
              sobre el mismo grabador */}
          {/* Mapa en vivo del GRUPO (desde la pestaña En vivo del grupo o del viaje):
              comparte tu posición y dibuja a los demás sobre este mismo mapa */}
          {(grupoEnVivoId || viajeEnVivoId) && !grupoVivo && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <GrupoVivoMapaOverlay grupoId={grupoEnVivoId} viajeId={grupoEnVivoId ? null : viajeEnVivoId} mapInstanceRef={mapInstanceRef} posRef={posActualRef} userId={userId} alActualizarOtros={setRidersV2} soloDatos={usarNavV2} />
          )}
          {vivoToken && !grupoVivo && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <VivoEspontaneoOverlay token={vivoToken} mapInstanceRef={mapInstanceRef} puntosRef={puntosRef} posRef={posActualRef} userId={userId} topUi={modoLimpio ? topAvisos : 12}
              alActualizarOtros={setEspontaneoV2} alActualizarPlan={setPlanRodadaV2} alActualizarLinea={setLineaGrupoV2} soloDatos={usarNavV2} />
          )}
          {vivoRegistro && vivoRegistro.share_token && !grupoVivo && !vivoToken && (
            <div style={{ position: "absolute", top: modoLimpio ? topAvisos : 12, left: 12, zIndex: 5, display: "flex", gap: 6 }}>
              <IntercomVivo token={vivoRegistro.share_token} creatorId={userId} texto="🎙️ Hablar con los que te siguen" />
            </div>
          )}
          {/* Modo navegación limpio: un solo botón ⋯ arriba a la derecha con todo lo que
              no hace falta ver manejando (rumbo arriba, día/noche, nafta, alerta a la
              comunidad, circuito, tipo de mapa). Hoja oscura, como la de avisar. */}
          {modoLimpio && (
            <button onClick={() => setMenuMapa(v => !v)} title="Más opciones del mapa"
              style={{ position: "absolute", top: "calc(var(--sat, 0px) + 10px)", right: 12, zIndex: 6, width: 44, height: 44, borderRadius: "50%", background: menuMapa ? "#111111" : "rgba(255,255,255,.95)", color: menuMapa ? "#F9F5F2" : "#111111", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.25)", fontSize: 24, fontWeight: 900, lineHeight: 1, paddingBottom: 8, WebkitTapHighlightColor: "transparent" }}>
              ⋯
            </button>
          )}
          {modoLimpio && menuMapa && (
            <div onClick={() => setMenuMapa(false)} style={{ position: "absolute", inset: 0, zIndex: 14, background: "rgba(0,0,0,.35)", display: "flex", alignItems: "flex-end" }}>
              <div onClick={e => e.stopPropagation()} style={{ width: "100%", background: "#111111", color: "#F9F5F2", borderRadius: "18px 18px 0 0", padding: "14px 14px calc(14px + var(--sab, 0px))" }}>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", marginBottom: 10 }}>Mapa</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                  {[
                    ["🧭", "Rumbo arriba", () => setModoNavegacion(v => !v), modoNavegacion],
                    [mapaOscuro ? "☀️" : "🌙", mapaOscuro ? "Mapa de día" : "Mapa de noche", alternarMapaOscuro, false],
                    ["⛽", "Cargué nafta", () => setShowCargaNafta(true), false],
                    ["⚠️", "Alerta a la comunidad", () => { if (alertasAbrirRef.current) alertasAbrirRef.current(); }, false],
                    ...(!circuito && (estado === "lista" || estado === "grabando") ? [["🏁", "Modo circuito", () => setElegirCircuito(true), trazandoNuevo]] : []),
                    // Lo que salió del banner de giros: guardar el destino y la voz
                    ...(navMenuRef.current ? [
                      [navMenuRef.current.destinoGuardado ? "★" : "☆", navMenuRef.current.destinoGuardado ? "Destino guardado" : "Guardar destino", navMenuRef.current.guardar, navMenuRef.current.destinoGuardado],
                      [navMenuRef.current.voz ? "🔊" : "🔇", navMenuRef.current.voz ? "Voz: silenciar" : "Voz: activar", navMenuRef.current.alternarVoz, false],
                      ["📍", "Agregar parada", navMenuRef.current.agregarParada, false],
                      ...((navMenuRef.current.paradasPropias || []).length ? [["↩️", `Quitar parada: ${navMenuRef.current.paradasPropias[navMenuRef.current.paradasPropias.length - 1].nombre}`.slice(0, 28), navMenuRef.current.quitarParada, false]] : []),
                    ] : []),
                  ].map(([emoji, nombre, accion, activo]) => (
                    <button key={nombre} onClick={() => { setMenuMapa(false); accion(); }}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 4px 10px", borderRadius: 12, background: activo ? "#F9F5F2" : "rgba(249,245,242,.08)", color: activo ? "#111111" : "#F9F5F2", border: "none", cursor: "pointer", fontSize: 10.5, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2, textAlign: "center", WebkitTapHighlightColor: "transparent" }}>
                      <span style={{ fontSize: 22, lineHeight: 1 }}>{emoji}</span>{nombre}
                    </button>
                  ))}
                  {/* Tipo de mapa (satélite/relieve) es de Google: con el lienzo v2 no aplica */}
                  {!usarNavV2 && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 4px 10px", borderRadius: 12, background: "rgba(249,245,242,.08)", color: "#F9F5F2", fontSize: 10.5, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2, textAlign: "center" }}>
                      <BotonCapasMapa mapInstanceRef={mapInstanceRef} style={{ position: "relative", width: 30, height: 30, boxShadow: "none", background: "transparent", fontSize: 22 }}
                        onCambio={(t) => { if (polylineRef.current) polylineRef.current.setOptions(t === "roadmap" ? { strokeColor: "#111111", strokeWeight: 4 } : { strokeColor: "#FFFFFF", strokeWeight: 5 }); }} />
                      Tipo de mapa
                    </div>
                  )}
                </div>
                {navMenuRef.current && (
                  <button onClick={() => { setMenuMapa(false); navMenuRef.current.cancelar(); }}
                    style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 12, background: "rgba(179,55,44,.2)", border: "none", color: "#FF8A80", fontSize: 13.5, fontWeight: 800, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                    ✕ Cancelar navegación
                  </button>
                )}
                {/* La atribución del mapa vectorial (el ⓘ de MapLibre se sacó del lienzo) */}
                {usarNavV2 && <div style={{ marginTop: 10, fontSize: 10.5, color: "#8A857F", textAlign: "center", letterSpacing: "-0.01em" }}>Mapa © OpenStreetMap · OpenFreeMap</div>}
              </div>
            </div>
          )}
          {/* Botón toggle modo navegación (course-up + recentrado); navegando va al menú ⋯ */}
          {!modoLimpio && (estado === "grabando" || estado === "pausada" || estado === "lista") && (
            <button onClick={() => setModoNavegacion(v => !v)}
              style={{
                position: "absolute", top: 12, right: 12, zIndex: 5,
                width: 44, height: 44, borderRadius: "50%",
                background: modoNavegacion ? "#111111" : "rgba(255,255,255,.95)",
                color: modoNavegacion ? "#fff" : "#111111",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,.25)",
                fontSize: 18,
              }}
              title={modoNavegacion ? "Modo navegación activo (course-up)" : "Activar modo navegación"}>
              🧭
            </button>
          )}
        </div>
      ) : (!trazaPrevia && !error && userId && (esNativo() || enRodada || entradaExplicita)) ? (
        /* Flujo normal en la app nativa (o entrando a una rodada): nada que decidir,
           se está por entrar al mapa solo. Un splash de un segundo en vez de la
           pantalla de intro con botón. */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background: C.card }}>
          <div style={{ width: 26, height: 26, border: `3px solid ${C.border}`, borderTopColor: "#111111", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontSize: 13.5, color: C.muted, fontWeight: 600 }}>🛰️ Buscando GPS… abriendo el mapa</div>
        </div>
      ) : (!trazaPrevia && !error && userId) ? (
        /* En la web no se entra solo (pedido de Carlos, 07-sep): sentado en una
           computadora, que te salte el pedido de ubicación y el navegador a pantalla
           completa sin pedirlo era molesto. Pero el splash de arriba asumía el arranque
           automático y en la web giraba para siempre —el mapa "no cargaba" (11-sep).
           Acá va lo que faltaba: el botón que hace lo que la nativa hace sola. */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", background: C.card, textAlign: "center" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px" }}>
            <Navigation size={28} strokeWidth={1.5} color="#111111" />
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>El mapa de Olbos</div>
          <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, maxWidth: 320, marginBottom: 24 }}>
            Tu posición en vivo, navegar a un destino, grabar el recorrido y ver a tu grupo. Al entrar te va a pedir la ubicación.
          </div>
          <button onClick={entrarAlMapa} disabled={buscandoGps}
            style={{ width: "100%", maxWidth: 320, padding: "15px", borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 15.5, fontWeight: 800, cursor: buscandoGps ? "default" : "pointer", letterSpacing: "-0.01em", opacity: buscandoGps ? 0.7 : 1 }}>
            {buscandoGps ? "🛰️ Buscando tu ubicación…" : "Entrar al mapa"}
          </button>
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 14, maxWidth: 300, lineHeight: 1.5 }}>
            En la app instalada el mapa se abre solo y el recorrido se graba con la pantalla apagada.
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "24px 28px calc(24px + env(safe-area-inset-bottom, 0px))", background: C.card, overflowY: "auto", WebkitOverflowScrolling: "touch", minHeight: 0 }}>
          <div style={{ flex: "1 0 0" }} />
          {!error ? (
            <>
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: C.surface,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 22px",
              }}>
                <Navigation size={28} strokeWidth={1.5} color="#111111" />
              </div>

              <h2 style={{
                fontSize: 26, fontWeight: 700, color: C.text,
                letterSpacing: "-0.03em", marginBottom: 8, textAlign: "center",
              }}>Grabar nuevo recorrido</h2>

              <p style={{
                fontSize: 14.5, color: C.muted, textAlign: "center", lineHeight: 1.5,
                marginBottom: 24, maxWidth: 320, letterSpacing: "-0.005em",
              }}>
                Vamos a registrar tu ruta GPS punto a punto, con fotos y stats.
              </p>

              {/* Lista de "qué hace y qué necesita" estilo Apple */}
              <div style={{
                width: "100%", maxWidth: 340,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                overflow: "hidden",
                marginBottom: 28,
              }}>
                {[
                  { Ico: MapPin, t: "GPS preciso", d: "Captura cada 5 segundos, incluso off-road" },
                  { Ico: Camera, t: "Fotos en el camino", d: "Cada foto queda anclada al mapa" },
                  { Ico: Zap, t: "Pantalla siempre encendida", d: "Mantenemos la pantalla activa durante el viaje" },
                  { Ico: Bookmark, t: "Auto-guardado", d: "Se sincroniza cada 30 segundos automáticamente" },
                ].map((f, i, arr) => (
                  <div key={i} style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    padding: "12px 14px",
                    borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none",
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: C.card, border: `1px solid ${C.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <f.Ico size={14} strokeWidth={1.8} color="#111111" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: "-0.01em", marginBottom: 1 }}>{f.t}</div>
                      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, letterSpacing: "-0.005em" }}>{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recorrido sin terminar (de una sesión/día anterior): retomar / finalizar / descartar */}
              {trazaPrevia && (() => {
                // Con más de 12 horas desde el último punto NO se retoma: seguir
                // grabando desde otro lugar une los dos con una recta. Se guarda
                // como quedó o se descarta, y el de hoy arranca de cero.
                const pts = trazaPrevia.puntos || [];
                const ultimoTs = Math.max((pts[pts.length - 1] || {}).ts || 0, Date.parse(trazaPrevia.updated_at || 0) || 0);
                const vieja = ultimoTs > 0 && Date.now() - ultimoTs > 12 * 3600000;
                return (
                <div style={{ width: "100%", maxWidth: 340, background: C.accent, color: "#f9f5f2", borderRadius: 14, padding: "16px 18px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(249,245,242,.6)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{vieja ? "Recorrido de otro día" : "Recorrido en curso"}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{vieja ? "Quedó un recorrido sin cerrar" : "Tenés un recorrido sin terminar"}</div>
                  <div style={{ fontSize: 12.5, color: "#bbb", marginBottom: 14, lineHeight: 1.5 }}>
                    {calcEstadisticas(pts).dist.toFixed(1)} km · {pts.length} puntos{trazaPrevia.iniciada_en ? ` · ${new Date(trazaPrevia.iniciada_en).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}` : ""}
                    {vieja ? " · Guardalo como quedó o descartalo; el de hoy arranca de cero." : ""}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {!vieja && <button onClick={retomarTrazaPrevia} style={{ flex: 1, padding: "10px", borderRadius: 10, background: "#f9f5f2", color: C.text, border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>▶ Retomar</button>}
                    <button onClick={finalizarTrazaPrevia} style={{ flex: 1, padding: "10px", borderRadius: 10, background: vieja ? "#f9f5f2" : "rgba(255,255,255,.12)", color: vieja ? C.text : "#f9f5f2", border: "none", fontSize: 13, fontWeight: vieja ? 800 : 700, cursor: "pointer" }}>{vieja ? "Guardar como quedó" : "Finalizar"}</button>
                  </div>
                  <button onClick={descartarTrazaPrevia} style={{ width: "100%", marginTop: 8, padding: "8px", borderRadius: 10, background: "transparent", color: "#FF6B6B", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Descartar</button>
                </div>
                );
              })()}

              {/* Aviso importante */}
              <div style={{
                width: "100%", maxWidth: 340,
                background: `${C.gold}14`,
                border: "1px solid #FFE4C4",
                borderRadius: 12,
                padding: "11px 13px",
                marginBottom: 24,
                fontSize: 11.5, color: "#8B5A00", lineHeight: 1.5,
                letterSpacing: "-0.005em",
              }}>
                <strong style={{ color: "#5C3A00", fontWeight: 600 }}>Tip:</strong> en la app instalada el recorrido se sigue grabando con la pantalla apagada. Si parás a comer o dormir, tocá <strong>Pausa</strong>: podés retomarlo después, incluso al otro día.
              </div>

              {/* Sin sesión: pedir la cuenta ANTES del GPS. Antes se pedía el permiso igual
                  y el insert sin user_id terminaba en "No se pudo iniciar el GPS · Permiso
                  denegado" (auditoría 09-oct). */}
              <button onClick={userId ? iniciar : () => window.dispatchEvent(new CustomEvent("olbos-abrir-registro"))}
                style={{
                  padding: "14px 36px", borderRadius: 12,
                  background: C.accent, color: "#fff",
                  border: "none", fontSize: 15, fontWeight: 500,
                  cursor: "pointer", letterSpacing: "-0.01em",
                  width: "100%", maxWidth: 340,
                  transition: "background .15s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#000"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#111111"; }}>
                {userId ? "Empezar a grabar" : "Crear mi cuenta para grabar"}
              </button>
              {!userId && (
                <div style={{ fontSize: 12, color: C.muted, marginTop: 10, textAlign: "center", maxWidth: 320 }}>Gratis, en un minuto. Tus recorridos quedan guardados en tu cuenta.</div>
              )}
              <VideoAyuda seccion="recorder" style={{ marginTop: 14, justifyContent: "center" }} />
            </>
          ) : (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "#FFF1F0",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
              }}>
                <AlertTriangle size={24} strokeWidth={1.5} color="#FF3B30" />
              </div>
              <p style={{ fontSize: 14, color: C.text, textAlign: "center", marginBottom: 6, fontWeight: 500, letterSpacing: "-0.01em", maxWidth: 320 }}>No se pudo iniciar el GPS</p>
              <p style={{ fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 22, lineHeight: 1.5, letterSpacing: "-0.005em", maxWidth: 320 }}>{error}</p>
              <button onClick={() => { setError(null); iniciar(); }}
                style={{
                  padding: "11px 24px", borderRadius: 100,
                  background: C.accent, color: "#fff",
                  border: "none", fontSize: 13.5, fontWeight: 500,
                  cursor: "pointer", letterSpacing: "-0.005em",
                }}>
                Reintentar
              </button>
            </>
          )}
          <div style={{ flex: "1 0 0" }} />
        </div>
      )}

      {/* Navegando: barra inferior tipo Waze. Hora de llegada grande, tiempo y km
          debajo (los calcula el navegador: navInfoUi), y la grabación como pastilla a
          la derecha: "⏺ Grabar" arranca a grabar; grabando/pausado abre el panel.
          Tocar la barra abre el panel completo, que se vuelve a plegar solo a los 8 s. */}
      {(estado === "grabando" || estado === "pausada" || estado === "lista") && panelPlegado && (() => {
        const info = navInfoUi;
        const minNum = info && info.min != null ? (info.min >= 60 ? { v: `${Math.floor(info.min / 60)}:${String(info.min % 60).padStart(2, "0")}`, u: "h" } : { v: String(Math.max(0, info.min)), u: "min" }) : null;
        const kmNum = info && info.rest_km != null ? { v: info.rest_km < 10 ? info.rest_km.toFixed(1).replace(".", ",") : String(Math.round(info.rest_km)), u: "km" } : null;
        const grabTxt = `${stats.dist.toFixed(1).replace(".", ",")} km · ${formatDur(tiempoTranscurrido)}`;
        const ahora = new Date();
        const horaTxt = `${ahora.getHours()}:${String(ahora.getMinutes()).padStart(2, "0")}`;
        // En rodada: cuántos hay en el mapa y, si alguno perdió la señal, cuántos (tocable: abre la lista)
        const enRodadaUi = !!(grupoVivo || grupoEnVivoId || viajeEnVivoId || vivoToken);
        const cuantos = rodadaResumen && rodadaResumen.count != null ? rodadaResumen.count : (ridersV2.length ? ridersV2.length + 1 : null);
        const sinSenalN = rodadaResumen ? rodadaResumen.sinSenal || 0 : 0;
        const pastilla = { flexShrink: 0, height: 38, borderRadius: 100, border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, letterSpacing: "-0.01em", cursor: "pointer", WebkitTapHighlightColor: "transparent", fontWeight: 800 };
        return (
          <div role="button" tabIndex={0} onClick={() => setPanelNavAbierto(true)}
            onTouchStart={swipeInicio} onTouchEnd={(e) => swipeFin(e, () => setPanelNavAbierto(true), null)}
            style={{ height: "calc(60px + var(--sab, 0px))", flexShrink: 0, width: "100%", background: C.card, borderTopLeftRadius: 18, borderTopRightRadius: 18, boxShadow: "0 -8px 30px rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "0 14px var(--sab, 0px) 18px", boxSizing: "border-box", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
            {navegando ? (
              /* Como Waze (pedido de Carlos, 18-oct): llegada, tiempo y km lado a lado, los tres
                 en negro y grandes, con la unidad más chica. Antes solo se leía la hora de llegada
                 y lo que falta iba en gris de 12 px, imposible de leer con el casco puesto. */
              info && info.eta && minNum && kmNum ? (
                <div style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 0 }}>
                  {[
                    { v: info.eta, u: "", l: "llegada" },
                    { v: minNum.v, u: minNum.u, l: "faltan" },
                    { v: kmNum.v, u: kmNum.u, l: "distancia" },
                  ].map((d, i) => (
                    <div key={d.l} style={{ minWidth: 0, flex: i === 0 ? "0 0 auto" : 1, padding: i === 0 ? "0 12px 0 0" : "0 10px", borderLeft: i ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ fontSize: 23, fontWeight: 800, color: C.text, lineHeight: 1, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {d.v}{d.u && <span style={{ fontSize: 14, fontWeight: 700, marginLeft: 2, letterSpacing: "-0.01em" }}>{d.u}</span>}
                      </div>
                      <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginTop: 4, whiteSpace: "nowrap", letterSpacing: "0.01em" }}>{d.l}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ minWidth: 0, flex: 1, fontSize: 15, fontWeight: 700, color: C.text, whiteSpace: "nowrap", letterSpacing: "-0.01em" }}>Calculando el camino…</div>
              )
            ) : (
              /* Rodada libre: lo grabado grande (o "Sin grabar" y la hora), y abajo el estado de
                 la grabación y, en rodada, cuántos hay en el mapa / sin señal */
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: estado === "lista" ? 20 : 22, fontWeight: 800, color: C.text, lineHeight: 1, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{estado === "lista" ? "Sin grabar" : grabTxt}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginTop: 4, whiteSpace: "nowrap", letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 5, overflow: "hidden" }}>
                  {estado === "grabando" && <><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#B3372C", animation: "pulse 1.5s infinite", flexShrink: 0 }} /><span>grabando</span></>}
                  {estado === "pausada" && <span>⏸ en pausa</span>}
                  {estado === "lista" && (salidasCerca.length > 0 && !enRodadaUi
                    ? <button onClick={(e) => { e.stopPropagation(); registrarUso("salida_abierta_ver_mapa", { n: salidasCerca.length }); setVerSalidas(true); }}
                        style={{ background: "none", border: "none", padding: 0, color: C.text, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", WebkitTapHighlightColor: "transparent", display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, animation: "pulse 1.5s infinite", flexShrink: 0 }} />
                        🏍️ {salidasCerca.length} salida{salidasCerca.length === 1 ? "" : "s"} cerca
                      </button>
                    : <span style={{ fontVariantNumeric: "tabular-nums" }}>{horaTxt}</span>)}
                  {enRodadaUi && cuantos != null && <><span style={{ opacity: 0.5 }}>·</span><span>{cuantos} en el mapa</span></>}
                  {enRodadaUi && sinSenalN > 0 && (
                    <><span style={{ opacity: 0.5 }}>·</span>
                    <button onClick={(e) => { e.stopPropagation(); if (rodadaUiRef.current && rodadaUiRef.current.toggleSinSenal) rodadaUiRef.current.toggleSinSenal(); }}
                      style={{ background: "none", border: "none", padding: 0, color: C.gold, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", WebkitTapHighlightColor: "transparent" }}>📵 {sinSenalN} sin señal</button></>
                  )}
                </div>
              </div>
            )}
            {/* Moto o auto, también en la barra compacta del navegador (Carlos navegó en auto y no
                lo encontró: el panel de abajo se pliega al navegar). Antes de grabar se cambia. */}
            {estado === "lista" && (
              <button onClick={(e) => { e.stopPropagation(); const v = vehiculo === "auto" ? "moto" : "auto"; elegirVehiculo(v); registrarUso("vehiculo_elegido", { v, desde: "barra" }); }}
                title={vehiculo === "auto" ? "Vas en auto: tocá para cambiar a moto" : "Vas en moto: tocá para cambiar a auto"}
                style={{ ...pastilla, padding: "0 10px", background: vehiculo === "auto" ? C.text : C.surface, color: vehiculo === "auto" ? "#F9F5F2" : C.text, fontSize: 12.5, whiteSpace: "nowrap" }}>
                {vehiculo === "auto" ? "🚗 Auto" : "🏍️ Moto"}
              </button>
            )}
            {navegando && estado !== "lista"
              ? <span style={{ ...pastilla, height: 36, padding: "0 11px", background: C.surface, color: C.text, fontSize: 13, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {estado === "pausada" ? "⏸" : <><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#B3372C", animation: "pulse 1.5s infinite", flexShrink: 0 }} />{stats.dist.toFixed(1).replace(".", ",")}</>}
                </span>
              : estado === "lista"
                ? <button onClick={(e) => { e.stopPropagation(); iniciar(); }} title="Empezar a grabar"
                    style={{ ...pastilla, padding: "0 15px", background: "#27AE60", color: "#fff", fontSize: 13.5 }}>⏺ Grabar</button>
                : estado === "grabando"
                  ? <button onClick={(e) => { e.stopPropagation(); pausar(); }} title="Pausar la grabación"
                      style={{ ...pastilla, width: 56, background: C.surface, color: C.text, fontSize: 17 }}>⏸</button>
                  : <button onClick={(e) => { e.stopPropagation(); reanudar(false); }} title="Reanudar la grabación"
                      style={{ ...pastilla, width: 56, background: "#27AE60", color: "#fff", fontSize: 17 }}>▶</button>}
          </div>
        );
      })()}
      {/* Inputs de cámara SIEMPRE montados, fuera del panel: el panel se pliega solo a los 8 s
          (modo limpio) y si el input vivía adentro se desmontaba mientras estaba la cámara abierta:
          la foto sacada nunca llegaba (pedido de Matías, 1-nov-2026) */}
      {(estado === "grabando" || estado === "pausada" || estado === "lista") && (
        <>
          <input ref={inputFotoRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; tomarFoto(f); }} />
          <input ref={inputVideoRef} type="file" accept="video/*" capture="environment" style={{ display: "none" }}
            onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; tomarFoto(f); }} />
        </>
      )}
      {/* panel inferior con stats y controles */}
      {(estado === "grabando" || estado === "pausada" || estado === "lista") && !panelPlegado && (
        <div onPointerDown={modoLimpio ? tocarPanelNav : undefined}
          onTouchStart={swipeInicio}
          onTouchEnd={(e) => swipeFin(e,
            () => setPanelChico(false),
            () => { if (modoLimpio) setPanelNavAbierto(false); else setPanelChico(true); })}
          style={{
          background: C.card,
          padding: panelChico ? "8px 18px calc(10px + var(--sab))" : "10px 18px calc(22px + var(--sab))",
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          boxShadow: "0 -8px 30px rgba(0,0,0,.3)",
        }}>
          {/* manija: plegar/desplegar el panel (clave en horizontal, el mapa manda) */}
          <button onClick={() => setPanelChico(v => !v)}
            style={{ display: "block", width: "100%", padding: "2px 0 8px", background: "none", border: "none", cursor: "pointer" }}>
            <span style={{ display: "block", width: 44, height: 5, borderRadius: 100, background: C.border, margin: "0 auto" }} />
            <span style={{ display: "block", fontSize: 9, color: C.muted, marginTop: 2 }}>{panelChico ? "▲ más opciones" : "▼ achicar"}</span>
          </button>
          {/* stats — solo cuando hay algo que contar (grabando/pausado, no en lista) */}
          {!panelChico && estado !== "lista" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: C.text, letterSpacing: "-0.02em" }}>{stats.dist.toFixed(1)}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 1 }}>km</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: C.text, letterSpacing: "-0.02em" }}>{formatDur(tiempoTranscurrido)}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 1 }}>tiempo</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: C.text, letterSpacing: "-0.02em" }}>{Math.round(stats.velProm)}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 1 }}>km/h prom</div>
            </div>
          </div>
          )}

          {/* Compartir en vivo: las DOS intenciones a la vista — que te sigan
              (mirar por dónde vas, sin cuenta) o sumarse a rodar con vos */}
          {!panelChico && (
          <div style={{ display: "flex", gap: 8, marginBottom: vivoRegistro ? 8 : 12 }}>
            <button onClick={() => compartirVivo("seguir")} disabled={iniciandoVivo}
              style={{ flex: 1, padding: "11px 8px", borderRadius: 12, background: C.surface, border: "1px solid transparent", color: C.text, fontWeight: 700, fontSize: 12.5, cursor: iniciandoVivo ? "default" : "pointer", letterSpacing: "-0.005em" }}>
              👀 {iniciandoVivo ? "Activando…" : "Que sigan tu camino"}
            </button>
            <button onClick={() => compartirVivo("sumarse")} disabled={iniciandoVivo}
              style={{ flex: 1, padding: "11px 8px", borderRadius: 12, background: C.surface, border: "1px solid transparent", color: C.text, fontWeight: 700, fontSize: 12.5, cursor: iniciandoVivo ? "default" : "pointer", letterSpacing: "-0.005em" }}>
              🏍️ {iniciandoVivo ? "Activando…" : "Invitar a la rodada"}
            </button>
          </div>
          )}
          {/* Salida abierta: que te vean los riders de la zona y se sumen (sin mandar links) */}
          {!panelChico && !(vivoRegistro && vivoRegistro.abierta) && !grupoVivo && !vivoToken && !grupoEnVivoId && !viajeEnVivoId && (
            <button onClick={() => { registrarUso("salida_abierta_abrir", { desde: "mapa" }); setSalgoSheet(true); }}
              style={{ width: "100%", padding: "11px 8px", borderRadius: 12, marginBottom: 12, background: C.accent, border: "none", color: "#F9F5F2", fontWeight: 800, fontSize: 13, cursor: "pointer", letterSpacing: "-0.005em" }}>
              📣 Salgo a rodar: que se sumen los de cerca
            </button>
          )}
          {!panelChico && vivoRegistro && (
            <button onClick={() => setMostrarVivo(true)}
              style={{ width: "100%", padding: "9px", borderRadius: 10, marginBottom: 12, background: "#27AE600F", border: "1px solid #27AE6055", color: "#1F8347", fontWeight: 600, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#27AE60", animation: "pulse 1.5s ease-in-out infinite" }} />
              {vivoRegistro.abierta ? "Salida abierta: te ven los riders de la zona · ver link o cortar" : "En vivo activo · ver link o cortar"}
            </button>
          )}

          {(fotos.length > 0 || subiendoMedia > 0) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4, alignItems: "center" }}>
              {fotos.map((f, i) => (
                f.type === "video"
                  ? <video key={i} src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" muted onClick={() => setFotoVisor(f)} style={{ width: 50, height: 50, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "#000", cursor: "pointer" }} />
                  : <img loading="lazy" decoding="async" key={i} src={cldOpt(f.url, 200)} alt="" onClick={() => setFotoVisor(f)} style={{ width: 50, height: 50, borderRadius: 10, objectFit: "cover", flexShrink: 0, cursor: "pointer" }} />
              ))}
              {subiendoMedia > 0 && (
                <div style={{ width: 50, height: 50, borderRadius: 10, flexShrink: 0, background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, border: "1px dashed #d0d0d5" }}>
                  <div style={{ width: 14, height: 14, border: "2px solid #d0d0d5", borderTopColor: "#111111", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ fontSize: 8, color: C.muted, fontWeight: 600 }}>subiendo</span>
                </div>
              )}
              {pendientesCola > 0 && (
                <div title="Se suben solas cuando vuelva la señal"
                  style={{ width: 50, height: 50, borderRadius: 10, flexShrink: 0, background: `${C.gold}14`, border: `1px dashed ${C.gold}55`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <span style={{ fontSize: 15 }}>📴</span>
                  <span style={{ fontSize: 8, color: C.gold, fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>{pendientesCola} sin<br />señal</span>
                </div>
              )}
            </div>
          )}

          {/* Moto o auto: se elige antes de grabar y se recuerda */}
          {estado === "lista" && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 700 }}>Voy en</span>
              {[["moto", "🏍️ Moto"], ["auto", "🚗 Auto"]].map(([v, l]) => (
                <button key={v} onClick={() => { elegirVehiculo(v); registrarUso("vehiculo_elegido", { v }); }}
                  style={{ padding: "5px 12px", borderRadius: 100, border: `1px solid ${vehiculo === v ? C.text : C.border}`, background: vehiculo === v ? C.text : C.card, color: vehiculo === v ? "#F9F5F2" : C.text, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>{l}</button>
              ))}
              {vehiculo === "auto" && <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.3 }}>Tus km se guardan, pero no suman al ranking de motos.</span>}
            </div>
          )}
          {/* Dos filas: arriba Grabar/Pausar/Reanudar y Finalizar; abajo Foto, Video y Parada.
              En una sola fila, a 375 px "Finalizar" quedaba recortado. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {estado === "lista" ? (
              <button onClick={iniciar}
                style={{
                  flex: 1.4, padding: "12px", borderRadius: 12,
                  background: "#27AE60", color: "#fff",
                  border: "none", fontWeight: 700, fontSize: 13.5,
                  cursor: "pointer", letterSpacing: "-0.005em",
                }}>
                ⏺ Grabar
              </button>
            ) : estado === "grabando" ? (
              <button onClick={pausar}
                style={{
                  flex: 1, padding: "12px", borderRadius: 12,
                  background: C.surface, color: C.text,
                  border: "none", fontWeight: 500, fontSize: 13.5,
                  cursor: "pointer", letterSpacing: "-0.005em",
                }}>
                Pausar
              </button>
            ) : (
              <button onClick={() => reanudar(false)}
                style={{
                  flex: 1, padding: "12px", borderRadius: 12,
                  background: "#27AE60", color: "#fff",
                  border: "none", fontWeight: 500, fontSize: 13.5,
                  cursor: "pointer", letterSpacing: "-0.005em",
                }}>
                Reanudar
              </button>
            )}
            {estado !== "lista" && (
              <button onClick={intentarFinalizar}
                style={{
                  flex: 1, padding: "12px", borderRadius: 12,
                  background: C.accent, color: "#fff",
                  border: "none", fontWeight: 500, fontSize: 13.5,
                  cursor: "pointer", letterSpacing: "-0.005em",
                }}>
                Finalizar
              </button>
            )}
          </div>
          {!panelChico && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => inputFotoRef.current && inputFotoRef.current.click()} style={{
              flex: 1, padding: "12px", borderRadius: 12,
              background: C.surface, color: C.text, border: "none",
              fontWeight: 500, fontSize: 13.5, cursor: "pointer",
              textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              letterSpacing: "-0.005em",
            }}>
              <Camera size={14} strokeWidth={2} /> Foto
            </button>
            <button onClick={() => inputVideoRef.current && inputVideoRef.current.click()} style={{
              flex: 1, padding: "12px", borderRadius: 12,
              background: C.surface, color: C.text, border: "none",
              fontWeight: 500, fontSize: 13.5, cursor: "pointer",
              textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              letterSpacing: "-0.005em",
            }}>
              🎥 Video
            </button>
            <button onClick={marcarParada}
              style={{
                flex: 1, padding: "12px", borderRadius: 12,
                background: C.surface, color: C.text,
                border: "none", fontWeight: 500, fontSize: 13.5,
                cursor: "pointer", letterSpacing: "-0.005em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
              📍 Parada{paradas.length > 0 ? ` (${paradas.length})` : ""}
            </button>
          </div>
          )}
          </div>
        </div>
      )}

      {/* sheet: link de compartir en vivo */}
      {/* Salidas abiertas: publicar la mía y ver las de cerca (desde la barra o el panel) */}
      {salgoSheet && <SalgoARodarSheet session={session} onClose={() => setSalgoSheet(false)} />}
      {verSalidas && (
        <div onClick={e => e.target === e.currentTarget && setVerSalidas(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(17,17,17,.6)", zIndex: 2500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: 460, background: C.bg, borderRadius: "20px 20px 0 0", padding: "16px 14px calc(22px + var(--sab, 0px))", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              <button onClick={() => setVerSalidas(false)} aria-label="Cerrar" style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: C.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={15} strokeWidth={2.5} /></button>
            </div>
            <SalidasAbiertas session={session} posFija={posActual} onSalidas={(l) => { if (Array.isArray(l)) setSalidasCerca(l.filter(s => !s.es_mia)); }} />
          </div>
        </div>
      )}
      {mostrarVivo && vivoRegistro && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 380, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setMostrarVivo(false)}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(32px + var(--sab))" }} className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#27AE60", animation: "pulse 1.5s ease-in-out infinite" }} />
                Rodada en vivo
              </h2>
              <button onClick={() => setMostrarVivo(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
              Cualquiera con este link ve tu posición y tu recorrido en tiempo real, sin instalar nada. Y los que tienen cuenta pueden <strong style={{ color: C.text }}>sumarse al mapa</strong>: se ven todos entre todos, rodada espontánea instantánea.
            </p>
            {(() => {
              const linkVivo = `${SITE_URL}/vivo/${vivoRegistro.share_token}`;
              const linkRodar = `${linkVivo}?rodar=1`; // el de invitación: al que tiene cuenta lo mete directo al mapa
              return (
                <>
                  <div style={{ padding: "10px 12px", background: C.surface, borderRadius: 8, fontSize: 11, color: C.text, fontFamily: "monospace", wordBreak: "break-all", border: `1px solid ${C.border}`, marginBottom: 12 }}>{linkVivo}</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <button onClick={() => compartirNativo({ texto: "🏍️ ¡Rodada en vivo! Mirá por dónde voy y sumate al mapa:", url: linkRodar, titulo: "Rodada en vivo" })}
                      style={{ flex: 1, padding: "11px 14px", borderRadius: 10, background: "#25D366", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      WhatsApp
                    </button>
                    <button onClick={async () => { try { await navigator.clipboard.writeText(linkVivo); alert("Link copiado"); } catch { alert("El link es: " + linkVivo); } }}
                      style={{ flex: 1, padding: "11px 14px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      Copiar link
                    </button>
                  </div>
                  {/* Los que ya están en Olbos se invitan por mensaje interno,
                      sin salir a WhatsApp — antes no había forma */}
                  <div style={{ marginBottom: 12 }}>
                    <InvitarRiders mensaje={`🏍️ ¡Te invito a mi rodada en vivo! Abrí este link y sumate al mapa: ${linkRodar}`} />
                  </div>
                </>
              );
            })()}
            <button onClick={detenerVivo}
              style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.card, color: C.red, border: `1px solid ${C.red}33`, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              ✕ Dejar de compartir en vivo
            </button>
          </div>
        </div>
      )}

      {/* Foto o video del recorrido a pantalla completa (tocado en el mapa del v2 o en la tira) */}
      {fotoVisor && (
        <div onClick={() => setFotoVisor(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.94)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button onClick={() => setFotoVisor(null)} style={{ position: "absolute", top: "max(52px, calc(var(--sat, 0px) + 12px))", right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
            <X size={20} strokeWidth={2} color="#fff" />
          </button>
          {fotoVisor.type === "video" ? (
            <video src={cldOpt(fotoVisor.url, 1080)} poster={cldPoster(fotoVisor.url, 480)} controls autoPlay playsInline onClick={e => e.stopPropagation()}
              style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, background: "#000" }} />
          ) : (
            <img decoding="async" src={cldOpt(fotoVisor.url, 1440)} alt="" onClick={e => e.stopPropagation()}
              style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, objectFit: "contain" }} />
          )}
        </div>
      )}

      {/* Caída detectada: pantalla completa, roja, con un solo botón enorme */}
      {caida && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9800, background: caida.fase === "avisado" ? "#111111" : "#B3372C", color: "#F9F5F2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 26, textAlign: "center" }}>
          {caida.fase === "preguntando" ? (
            <>
              <div style={{ fontSize: 54, marginBottom: 10 }}>⚠️</div>
              <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>¿Estás bien?</div>
              <div style={{ fontSize: 15, opacity: 0.85, lineHeight: 1.5, maxWidth: 320, marginBottom: 22 }}>
                Detectamos un golpe fuerte. Si no contestás, en <strong>{caida.queda} segundos</strong> les avisamos a los de tu rodada dónde estás.
              </div>
              <button onClick={estoyBien}
                style={{ width: "100%", maxWidth: 340, padding: "26px", borderRadius: 18, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", cursor: "pointer" }}>
                Estoy bien
              </button>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 16, maxWidth: 300, lineHeight: 1.45 }}>
                Olbos no es un servicio de emergencias. Ante una emergencia real, llamá al 911.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 44, marginBottom: 10 }}>🆘</div>
              <div style={{ fontSize: 21, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>Avisamos a tu rodada</div>
              <div style={{ fontSize: 14, opacity: 0.8, lineHeight: 1.55, maxWidth: 320, marginBottom: 20 }}>
                Les llegó tu posición. Si estás bien, cerrá este aviso.
              </div>
              {/* Ficha para el que se acerque a ayudar: se lee sin desbloquear nada */}
              <div style={{ width: "100%", maxWidth: 340, background: "rgba(249,245,242,.1)", borderRadius: 14, padding: "14px 16px", textAlign: "left", marginBottom: 16 }}>
                <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.55, marginBottom: 6 }}>Para quien esté ayudando</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                  Este rider salió con Olbos y puede necesitar ayuda. Llamá al <strong>911</strong>.
                  {posActual && <> Posición: {posActual.lat.toFixed(5)}, {posActual.lng.toFixed(5)}.</>}
                </div>
              </div>
              <button onClick={enviarSOS}
                style={{ width: "100%", maxWidth: 340, padding: "15px", borderRadius: 13, background: "#F9F5F2", color: "#111111", border: "none", fontSize: 15.5, fontWeight: 800, cursor: "pointer", marginBottom: 9 }}>
                Avisar también al contacto de emergencia
              </button>
              <button onClick={() => { caidaRef.current = null; setCaida(null); }}
                style={{ width: "100%", maxWidth: 340, padding: "13px", borderRadius: 13, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.35)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                Cerrar
              </button>
            </>
          )}
        </div>
      )}

      {/* modal de finalización */}
      {showFinalizar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 360, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: C.card, borderRadius: 16, padding: "22px 20px 20px", width: "100%", maxWidth: 380, maxHeight: "90vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 14 }}>Finalizar recorrido</h2>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              <div style={{ background: C.surface, borderRadius: 10, padding: 10, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{stats.dist.toFixed(1)} km</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Distancia</div>
              </div>
              <div style={{ background: C.surface, borderRadius: 10, padding: 10, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{formatDur(tiempoTranscurrido)}</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Duración</div>
              </div>
              <div style={{ background: C.surface, borderRadius: 10, padding: 10, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{Math.round(stats.velProm)} km/h</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Vel. prom.</div>
              </div>
              <div style={{ background: C.surface, borderRadius: 10, padding: 10, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{Math.round(stats.velMax)} km/h</div>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Vel. máx.</div>
              </div>
            </div>

            <Input label="Título" value={tituloFinal} onChange={e => setTituloFinal(e.target.value)} />
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción (opcional)</div>
              <textarea value={descripcionFinal} onChange={e => setDescripcionFinal(e.target.value)} rows={3}
                placeholder="Cómo estuvo, qué viste, recomendaciones del camino (tranqueras, dónde cargar nafta, vados)..."
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }} />
            </div>

            {/* El camino, contado por el que lo acaba de hacer — alimenta la red de caminos */}
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>¿Qué camino era? (opcional)</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[["asfalto", "🛣️ Asfalto"], ["ripio", "🪨 Ripio"], ["tierra", "🟤 Tierra"], ["mixto", "🔀 Mixto"]].map(([k, l]) => (
                  <button key={k} onClick={() => setTipoCamino(t => t === k ? "" : k)}
                    style={{ flex: 1, padding: "8px 4px", borderRadius: 10, fontSize: 11.5, fontWeight: tipoCamino === k ? 800 : 500, background: tipoCamino === k ? C.text : C.surface, color: tipoCamino === k ? "#fff" : C.muted, border: `1px solid ${tipoCamino === k ? C.text : C.border}`, cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>¿En qué estado está?</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[["bueno", "✅ Bueno"], ["regular", "⚠️ Regular"], ["malo", "🕳️ Malo"]].map(([k, l]) => (
                  <button key={k} onClick={() => setEstadoCamino(t => t === k ? "" : k)}
                    style={{ flex: 1, padding: "8px 4px", borderRadius: 10, fontSize: 11.5, fontWeight: estadoCamino === k ? 800 : 500, background: estadoCamino === k ? C.text : C.surface, color: estadoCamino === k ? "#fff" : C.muted, border: `1px solid ${estadoCamino === k ? C.text : C.border}`, cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Selector de grupo si tengo grupos */}
            {misGruposFinal.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>¿Para qué grupo?</div>
                <select value={grupoFinal} onChange={e => setGrupoFinal(e.target.value)}
                  style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box", fontFamily: "inherit" }}>
                  <option value="">Ninguno (ruta pública o privada)</option>
                  {misGruposFinal.map(g => (
                    <option key={g.id} value={g.id}>{g.nombre}</option>
                  ))}
                </select>
                {grupoFinal && (
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>
                    La ruta queda visible solo para los miembros del grupo.
                  </p>
                )}
              </div>
            )}

            {/* Publicar no hace falta para sumar (Carlos, 26-oct-2026: publicaban todo pensando que si no, no contaba) */}
            <div style={{ fontSize: 12, color: C.text, background: C.surface, borderRadius: 10, padding: "8px 11px", marginBottom: 8, lineHeight: 1.45 }}>
              ✅ Tus {stats.dist.toFixed(1).replace(".", ",")} km ya suman al ranking semanal y al Fondo Olbos. <b>No hace falta publicar el recorrido</b> para participar.
            </div>
            {stats.dist < KM_MIN_PUBLICAR && (
              <div style={{ fontSize: 12, color: C.muted, background: C.surface, borderRadius: 10, padding: "10px 12px", marginBottom: 10, lineHeight: 1.4 }}>
                🔒 Con {stats.dist.toFixed(1).replace(".", ",")} km no se puede publicar en el feed ni en Rutas: hace falta un mínimo de {KM_MIN_PUBLICAR} km. Igual queda guardado en Mis recorridos.
              </div>
            )}
            {/* Toggles públicos solo si NO está asociada a un grupo */}
            {!grupoFinal && (
              <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, cursor: "pointer", marginBottom: 8, background: publicaFinal ? "#27AE600C" : C.card }}>
                <input type="checkbox" checked={publicaFinal && stats.dist >= KM_MIN_PUBLICAR} disabled={stats.dist < KM_MIN_PUBLICAR} onChange={e => setPublicaFinal(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: "#27AE60" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Hacer pública</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Otros riders podrán verla en la sección Rutas</div>
                </div>
              </label>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, cursor: "pointer", marginBottom: 14, background: publicarEnFeed ? "#1111110C" : C.card }}>
              <input type="checkbox" checked={publicarEnFeed && stats.dist >= KM_MIN_PUBLICAR} disabled={stats.dist < KM_MIN_PUBLICAR}
                onChange={e => {
                  setPublicarEnFeed(e.target.checked);
                  // Si lo apaga, no se lo volvemos a proponer prendido
                  try { localStorage.setItem("olbos_no_publicar_feed", e.target.checked ? "0" : "1"); } catch (er) { /* */ }
                }}
                style={{ width: 18, height: 18, accentColor: "#111111" }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {grupoFinal ? "Compartir en el Feed del grupo" : "Compartir en el Feed"}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {grupoFinal
                    ? "Un post en el feed del grupo con el mapa, tus fotos y los km"
                    : "Un post en el Feed con el mapa, tus fotos y los km. El mapa se recorta 500 m en cada punta, así no se ve de dónde saliste ni dónde terminaste."}
                </div>
              </div>
            </label>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowFinalizar(false)}
                style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Volver
              </button>
              <button onClick={guardarFinal} disabled={guardando}
                style={{ flex: 2, padding: "12px", borderRadius: 10, border: "none", background: C.accent, color: "#fff", fontWeight: 800, fontSize: 14, cursor: guardando ? "default" : "pointer", opacity: guardando ? 0.6 : 1 }}>
                {guardando ? "Guardando…" : "Guardar recorrido"}
              </button>
            </div>
            {/* Descartar desde acá mismo (pedido de Carlos 04-sep): antes había que volver
                atrás y tocar la ✕ para encontrar la opción */}
            <button onClick={() => { if (window.confirm("¿Descartar este recorrido? Se borra y sus kilómetros NO suman al ranking ni al Fondo.\n\nSi querés que sumen, tocá Cancelar y después Guardar recorrido: no hace falta hacerlo público ni publicarlo en el feed.")) { setShowFinalizar(false); cancelarTraza(); } }} disabled={guardando}
              style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 10, border: "none", background: "transparent", color: C.red, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              🗑️ Descartar recorrido (no guardar)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SEGUIR TRACK (navegación dentro de Olbos) ─────────────────────────────
const SeguirTrack = ({ traza, onCerrar }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylineRef = useRef(null);
  const meMarkerRef = useRef(null);
  const trackPoints = useMemo(
    () => Array.isArray(traza.puntos) ? traza.puntos.map(p => ({ lat: p.lat, lng: p.lng })) : [],
    [traza.puntos]
  );

  const [posActual, setPosActual] = useState(null);
  const [enRuta, setEnRuta] = useState(true);
  const [distanciaAlTrack, setDistanciaAlTrack] = useState(0);
  const [kmRecorridos, setKmRecorridos] = useState(0);
  const [porcentajeCompletado, setPorcentajeCompletado] = useState(0);
  const [registroId, setRegistroId] = useState(null);
  const [modoNavegacion, setModoNavegacion] = useState(true);
  const [mostrarAlerta, setMostrarAlerta] = useState(null); // 'fuera' | 'volvio' | null
  const [showIrAlTrack, setShowIrAlTrack] = useState(false); // navegar al inicio/fin/punto más cercano
  // Navegación con Olbos HASTA el track, sin salir de la app: turn-by-turn con voz
  // sobre este mismo mapa; al volver a estar en ruta se retira solo y seguís el
  // track (pedido de un rider que probó Wikiloc: "si se combinara todo junto...")
  const [navegandoAlTrack, setNavegandoAlTrack] = useState(null); // {lat,lng,nombre} | null
  useEffect(() => { if (enRuta && navegandoAlTrack) setNavegandoAlTrack(null); }, [enRuta, navegandoAlTrack]);
  // "214438m" no le dice nada a nadie: en metros hasta 1 km, después en km
  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(m >= 10000 ? 0 : 1)} km` : `${Math.round(m)} m`;
  // El punto del track más cercano a donde estoy parado ahora
  const puntoMasCercano = () => {
    if (!posActual || trackPoints.length === 0) return null;
    let mejor = trackPoints[0], mejorD = Infinity;
    for (const pt of trackPoints) {
      const d = haversineKm(posActual, pt);
      if (d < mejorD) { mejorD = d; mejor = pt; }
    }
    return { ...mejor, distKm: mejorD };
  };

  const headingRef = useRef(null);
  const ultimaPosRef = useRef(null);
  const watchIdRef = useRef(null);
  const enRutaRef = useRef(true);

  // Calcular distancia entre 2 puntos en metros
  const distMetros = (a, b) => {
    const R = 6371000;
    const lat1 = a.lat * Math.PI / 180;
    const lat2 = b.lat * Math.PI / 180;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };

  // Calcular distancia del punto al segmento más cercano del track
  const distAlTrack = (pos) => {
    if (trackPoints.length < 2) return Infinity;
    let minDist = Infinity;
    let indiceCercano = 0;
    for (let i = 0; i < trackPoints.length; i++) {
      const d = distMetros(pos, trackPoints[i]);
      if (d < minDist) {
        minDist = d;
        indiceCercano = i;
      }
    }
    return { dist: minDist, indice: indiceCercano };
  };

  // Calcular heading (bearing) entre 2 puntos
  const calcularHeading = (a, b) => {
    const lat1 = a.lat * Math.PI / 180;
    const lat2 = b.lat * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  };

  // Iniciar registro de ruta + watch GPS
  useEffect(() => {
    (async () => {
      // Registrar en BD que se inició. Las rutas publicadas se recorren sin traza
      // propia (traza.id null): ahí no hay registro que crear, solo se sigue.
      if (traza.id) {
        const { data } = await supabase.rpc("iniciar_ruta", { p_traza_id: traza.id });
        if (data) setRegistroId(data);
      }
    })();

    if (!navigator.geolocation) {
      alert("Tu dispositivo no permite ubicación");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const nueva = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosActual(nueva);

        // Sumar km recorridos si hay posición anterior
        if (ultimaPosRef.current) {
          const d = distMetros(ultimaPosRef.current, nueva);
          if (d > 1 && d < 200) { // filtrar ruido GPS
            setKmRecorridos(prev => prev + d / 1000);
          }
          // Heading para course-up
          if (d > 3) {
            headingRef.current = calcularHeading(ultimaPosRef.current, nueva);
          }
        }
        ultimaPosRef.current = nueva;

        // Distancia al track + % completado
        const info = distAlTrack(nueva);
        if (info !== Infinity) {
          setDistanciaAlTrack(info.dist);
          setPorcentajeCompletado(Math.round((info.indice / (trackPoints.length - 1)) * 100));
          const ahoraEnRuta = info.dist <= 100;
          if (ahoraEnRuta !== enRutaRef.current) {
            enRutaRef.current = ahoraEnRuta;
            setEnRuta(ahoraEnRuta);
            setMostrarAlerta(ahoraEnRuta ? "volvio" : "fuera");
            // Vibración
            if (navigator.vibrate) {
              navigator.vibrate(ahoraEnRuta ? [200, 100, 200] : 400);
            }
            // Ocultar alerta después de 4s
            setTimeout(() => setMostrarAlerta(null), 4000);
          }
        }
      },
      (err) => {
        console.warn("Error GPS:", err);
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  // eslint-disable-next-line
  }, [traza.id]);

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || trackPoints.length < 2) return;
    loadGoogleMaps().then(() => {
      if (!window.google || mapInstanceRef.current) return;
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: trackPoints[0],
        zoom: 16,
        mapId: MAP_ID_NAV, // mapa vectorial → permite rotación (course-up)
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: "greedy",
      });
      polylineRef.current = new window.google.maps.Polyline({
        path: trackPoints,
        strokeColor: "#111111",
        strokeOpacity: 0.85,
        strokeWeight: 6,
        map: mapInstanceRef.current,
        // Flechitas cada tramo: de un vistazo se ve para qué lado va el recorrido
        icons: FLECHAS_SENTIDO,
      });
      // Marker inicio (verde)
      new window.google.maps.Marker({
        position: trackPoints[0],
        map: mapInstanceRef.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#1F8347", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
      });
      // Marker fin (rojo)
      new window.google.maps.Marker({
        position: trackPoints[trackPoints.length - 1],
        map: mapInstanceRef.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
      });
      // Ajustar bounds
      const bounds = new window.google.maps.LatLngBounds();
      trackPoints.forEach(p => bounds.extend(p));
      mapInstanceRef.current.fitBounds(bounds, { top: 100, bottom: 200, left: 40, right: 40 });
    });
  }, [trackPoints]);

  // Actualizar marker propio + course-up
  useEffect(() => {
    if (!posActual || !mapInstanceRef.current || !window.google) return;
    if (!meMarkerRef.current) {
      meMarkerRef.current = new window.google.maps.Marker({
        position: posActual,
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 7,
          fillColor: "#111111",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
          rotation: 0,
        },
        zIndex: 999,
      });
    } else {
      meMarkerRef.current.setPosition(posActual);
      if (headingRef.current !== null) {
        const icon = meMarkerRef.current.getIcon();
        meMarkerRef.current.setIcon({ ...icon, rotation: headingRef.current });
      }
    }
    if (modoNavegacion) {
      mapInstanceRef.current.panTo(posActual);
      if (headingRef.current !== null) {
        mapInstanceRef.current.setHeading(headingRef.current);
      }
    } else {
      mapInstanceRef.current.setHeading(0);
    }
  }, [posActual, modoNavegacion]);

  // Finalizar
  const finalizar = async (completada) => {
    if (registroId) {
      await supabase.rpc("finalizar_ruta", { p_id: registroId, p_km: kmRecorridos, p_completada: completada });
    }
    onCerrar();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#1a1a1a",
      zIndex: 9500, display: "flex", flexDirection: "column",
    }}>
      {/* Mapa */}
      <div ref={mapRef} style={{ flex: 1, position: "relative" }} />

      {/* Turn-by-turn de Olbos hasta el punto elegido del track: mismo mapa, con
          voz; al volver a estar EN RUTA se desmonta solo y seguís el track */}
      {navegandoAlTrack && (
        <NavegadorOverlay mapInstanceRef={mapInstanceRef} posActual={posActual} velMovil={null}
          destinoInicial={navegandoAlTrack} efimero ocultarBuscador topBanner="calc(var(--header-pt) + 30px)"
          alTerminar={() => setNavegandoAlTrack(null)} />
      )}

      {/* Header: título + cerrar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        padding: "var(--header-pt) 14px 12px",
        background: "linear-gradient(to bottom, rgba(0,0,0,.7), transparent)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        zIndex: 10, pointerEvents: "none",
      }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: "#fff",
          letterSpacing: "-0.005em", flex: 1,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          textShadow: "0 1px 4px rgba(0,0,0,.5)",
          paddingRight: 12,
        }}>
          🗺️ Siguiendo: {traza.titulo || "Recorrido"}
        </div>
        <button onClick={() => {
          if (window.confirm("¿Salir de la navegación? Vas a perder el progreso.")) {
            finalizar(false);
          }
        }}
          style={{
            width: 38, height: 38, borderRadius: "50%",
            background: "rgba(255,255,255,.95)",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,.3)",
            pointerEvents: "auto",
          }}>
          <X size={18} strokeWidth={2.5} color="#111111" />
        </button>
      </div>

      {/* Botón course-up flotante */}
      <button onClick={() => setModoNavegacion(v => !v)}
        style={{
          position: "absolute", top: 100, right: 14, zIndex: 10,
          width: 44, height: 44, borderRadius: "50%",
          background: modoNavegacion ? "#111111" : "rgba(255,255,255,.95)",
          color: modoNavegacion ? "#fff" : "#111111",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,.25)",
          fontSize: 18,
        }}>
        🧭
      </button>

      {/* Tipo de mapa: normal / satélite / relieve */}
      <BotonCapasMapa mapInstanceRef={mapInstanceRef} style={{ top: 152, right: 14 }}
        onCambio={(t) => {
          // Mismo criterio que en el recorder: track blanco sobre satélite/relieve
          if (polylineRef.current) polylineRef.current.setOptions(t === "roadmap"
            ? { strokeColor: "#111111", strokeWeight: 6 }
            : { strokeColor: "#FFFFFF", strokeWeight: 7 });
        }} />

      {/* Alerta fuera/volvió a ruta */}
      {mostrarAlerta && (
        <div style={{
          position: "absolute", top: 96, left: 14, right: 70,
          padding: "10px 14px", borderRadius: 12,
          background: mostrarAlerta === "fuera" ? "#B82F26" : "#1F8347",
          color: "#fff",
          fontSize: 13, fontWeight: 600,
          letterSpacing: "-0.005em",
          zIndex: 11,
          boxShadow: "0 4px 16px rgba(0,0,0,.3)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {mostrarAlerta === "fuera"
            ? `⚠️ Te alejaste de la ruta (${fmtDist(distanciaAlTrack)})`
            : "✓ Volviste a la ruta"}
        </div>
      )}

      {/* Panel inferior con stats */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "rgba(255,255,255,.97)",
        backdropFilter: "blur(20px)",
        padding: "14px 18px calc(14px + env(safe-area-inset-bottom))",
        boxShadow: "0 -4px 20px rgba(0,0,0,.15)",
        zIndex: 10,
      }}>
        {/* Indicador en ruta */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 10px", borderRadius: 100,
          background: enRuta ? "#1F834720" : "#B82F2620",
          color: enRuta ? "#1F8347" : "#B82F26",
          fontSize: 11.5, fontWeight: 600,
          marginBottom: 12, width: "fit-content",
          letterSpacing: "-0.005em",
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: enRuta ? "#1F8347" : "#B82F26",
          }} />
          {enRuta ? "En ruta" : `Fuera de ruta (${fmtDist(distanciaAlTrack)})`}
        </div>
        {!enRuta && !navegandoAlTrack && (
          <button onClick={() => setShowIrAlTrack(true)}
            style={{ width: "100%", padding: "11px", borderRadius: 10, marginBottom: 12, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
            🧭 Llevame a la ruta
          </button>
        )}

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.015em", lineHeight: 1 }}>
              {kmRecorridos.toFixed(1)}
            </div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3 }}>Km</div>
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.015em", lineHeight: 1 }}>
              {porcentajeCompletado}%
            </div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3 }}>Completado</div>
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.015em", lineHeight: 1 }}>
              {traza.distancia_km ? Math.max(0, traza.distancia_km - kmRecorridos).toFixed(1) : "—"}
            </div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3 }}>Km restantes</div>
          </div>
        </div>

        {/* Ir a la ruta: navegar con Waze/Maps al inicio, al fin o al punto más cercano */}
        {showIrAlTrack && (() => {
          const ini = trackPoints[0], fin = trackPoints[trackPoints.length - 1];
          const cerca = puntoMasCercano();
          const opciones = [
            ini && { label: "Punto de salida del track", p: ini, d: posActual ? haversineKm(posActual, ini) : null },
            cerca && { label: "El punto más cercano a mí", p: cerca, d: cerca.distKm },
            fin && { label: "Punto final del track", p: fin, d: posActual ? haversineKm(posActual, fin) : null },
          ].filter(Boolean);
          return (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
              onClick={e => e.target === e.currentTarget && setShowIrAlTrack(false)}>
              <div style={{ background: C.card, borderRadius: "18px 18px 0 0", width: "100%", maxWidth: 430, padding: "18px 18px calc(24px + env(safe-area-inset-bottom))" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text }}>¿A qué punto de la ruta vamos?</div>
                  <button onClick={() => setShowIrAlTrack(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer" }}>✕</button>
                </div>
                {opciones.map((o, i) => (
                  <div key={i} style={{ padding: "10px 12px", borderRadius: 12, background: "#f9f5f2", border: `1px solid ${C.border}`, marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{o.label}</span>
                      {o.d != null && <span style={{ fontSize: 11.5, color: C.muted }}>a {o.d < 1 ? `${Math.round(o.d * 1000)} m` : `${o.d.toFixed(o.d >= 10 ? 0 : 1)} km`}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setNavegandoAlTrack({ lat: o.p.lat, lng: o.p.lng, nombre: o.label }); setShowIrAlTrack(false); }}
                        style={{ flex: 1.4, padding: "9px 0", borderRadius: 9, background: C.accent, border: "none", color: "#fff", fontWeight: 800, fontSize: 12.5, textAlign: "center", cursor: "pointer" }}>
                        🏍️ Con Olbos
                      </button>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${o.p.lat},${o.p.lng}&travelmode=driving`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "9px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 12.5, textAlign: "center", textDecoration: "none" }}>
                        Maps
                      </a>
                      <a href={`${WAZE_BASE}ll=${o.p.lat},${o.p.lng}&navigate=yes`} target="_blank" rel="noreferrer"
                        style={{ flex: 1, padding: "9px 0", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, color: "#0099BB", fontWeight: 700, fontSize: 12.5, textAlign: "center", textDecoration: "none" }}>
                        Waze
                      </a>
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                  Se abre la navegación afuera y Olbos sigue midiendo tu avance sobre el track cuando vuelvas a estar en ruta.
                </div>
              </div>
            </div>
          );
        })()}

        {/* Botón finalizar */}
        <button onClick={() => finalizar(porcentajeCompletado >= 85)}
          style={{
            width: "100%", padding: "12px", borderRadius: 12,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
          }}>
          Finalizar
        </button>
      </div>
    </div>
  );
};

// ─── MAPA FULLSCREEN (overlay) ─────────────────────────────────────────────
const MapaFullscreen = ({ puntos, fotos = [], titulo, onCerrar }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !puntos || puntos.length === 0) return;
    loadGoogleMaps().then(() => {
      if (!window.google || !mapRef.current) return;
      if (!mapInstanceRef.current) {
        const path = puntos.map(p => ({ lat: p.lat, lng: p.lng }));
        const bounds = new window.google.maps.LatLngBounds();
        path.forEach(p => bounds.extend(p));
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: path[0],
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
          fullscreenControl: false,
          gestureHandling: "greedy",
        });
        new window.google.maps.Polyline({
          path, strokeColor: "#111111", strokeOpacity: 0.9, strokeWeight: 5,
          map: mapInstanceRef.current,
        });
        new window.google.maps.Marker({
          position: path[0], map: mapInstanceRef.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#1F8347", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
          title: "Inicio",
        });
        new window.google.maps.Marker({
          position: path[path.length - 1], map: mapInstanceRef.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
          title: "Fin",
        });
        fotos.forEach((f, i) => {
          if (!f.lat || !f.lng || !f.url) return;
          const isVideo = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url);
          const thumbUrl = isVideo
            ? f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/").replace(/\.(mp4|mov|webm)$/i, ".jpg")
            : f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/");
          new window.google.maps.Marker({
            position: { lat: f.lat, lng: f.lng },
            map: mapInstanceRef.current,
            icon: { url: thumbUrl, scaledSize: new window.google.maps.Size(40, 40), anchor: new window.google.maps.Point(20, 20) },
            title: isVideo ? `Video ${i+1}` : `Foto ${i+1}`,
          });
        });
        mapInstanceRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 30, right: 30 });
      }
    });
  }, [puntos, fotos]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 9000 }}>
      <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
      {/* Header con título y cerrar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        padding: "var(--header-pt) 16px 14px",
        background: "linear-gradient(to bottom, rgba(0,0,0,.6), transparent)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        zIndex: 1,
        pointerEvents: "none",
      }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: "#fff",
          letterSpacing: "-0.005em", flex: 1,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          textShadow: "0 1px 4px rgba(0,0,0,.5)",
          paddingRight: 12,
        }}>
          {titulo}
        </div>
        <button onClick={onCerrar} style={{
          width: 38, height: 38, borderRadius: "50%",
          background: "rgba(255,255,255,.95)",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,.3)",
          pointerEvents: "auto",
        }}>
          <X size={18} strokeWidth={2.5} color="#111111" />
        </button>
      </div>
    </div>
  );
};

// ─── 🎬 VIDEO DEL RECORRIDO (estilo Relive, 100% en el teléfono) ──────────────
// Canvas 720x1280 (formato historia de IG/WhatsApp) grabado con MediaRecorder:
// la línea del recorrido se dibuja sola con contador de km/tiempo, las fotos de
// la rodada aparecen como flashes en su momento del camino, y cierra con una
// placa Olbos con las stats. Fondo: tiles oscuros de Esri (gratis, apagados con
// un velo negro para que la línea crema mande); si no cargan, negro cálido puro.
// Si la traza es de una rodada (evento_id), los compañeros que también grabaron
// aparecen como puntos con su @, sincronizados por hora real (ts de los puntos).
// Cero servidor: el video se genera y se comparte desde el teléfono.
const VideoRecorridoModal = ({ traza, onClose }) => {
  const canvasRef = useRef(null);
  const [fase, setFase] = useState("prep"); // prep | grabando | listo | error
  const [errorMsg, setErrorMsg] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoExt, setVideoExt] = useState("webm");
  const [reintento, setReintento] = useState(0);
  const rafRef = useRef(null);
  const recRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const W = 720, H = 1280, DUR = 13000; // 13 segundos
      const puntos = (Array.isArray(traza.puntos) ? traza.puntos : []).filter(p => p && p.lat && p.lng);
      if (puntos.length < 2) { setErrorMsg("El recorrido no tiene suficientes puntos GPS para animarlo."); setFase("error"); return; }
      if (typeof MediaRecorder === "undefined" || !canvasRef.current || !canvasRef.current.captureStream) {
        setErrorMsg("Este dispositivo no puede generar videos. Probá desde Chrome o actualizá el sistema."); setFase("error"); return;
      }

      // ── Datos que viajan en el video ──
      let username = null;
      try { const { data } = await supabase.from("perfiles").select("username").eq("id", traza.user_id).maybeSingle(); username = data?.username || null; } catch (e) { /* */ }

      // Compañeros de la misma rodada: trazas hermanas del mismo evento
      let comps = [];
      if (traza.evento_id) {
        try {
          const { data } = await supabase.from("trazas").select("id, user_id, puntos").eq("evento_id", traza.evento_id).neq("id", traza.id).limit(8);
          const filas = (data || []).filter(t => Array.isArray(t.puntos) && t.puntos.length > 1 && t.user_id !== traza.user_id);
          if (filas.length) {
            const ids = filas.map(t => t.user_id).filter((v, i, a) => a.indexOf(v) === i);
            const { data: perfs } = await supabase.from("perfiles").select("id, username").in("id", ids);
            const nombres = {};
            (perfs || []).forEach(p => { nombres[p.id] = p.username; });
            comps = filas.map(t => ({ puntos: t.puntos.filter(p => p && p.lat && p.lng), nombre: nombres[t.user_id] ? "@" + nombres[t.user_id] : "rider" })).filter(c => c.puntos.length > 1);
          }
        } catch (e) { /* sin compañeros, sigue solo */ }
      }

      // ── Proyección Mercator en "píxeles de mundo" (alinea ruta y tiles) ──
      const todos = [...puntos, ...comps.flatMap(c => c.puntos)];
      const latMin = Math.min(...todos.map(p => p.lat)), latMax = Math.max(...todos.map(p => p.lat));
      const lngMin = Math.min(...todos.map(p => p.lng)), lngMax = Math.max(...todos.map(p => p.lng));
      const wpx = (lat, lng, z) => {
        const n = 256 * Math.pow(2, z);
        const x = (lng + 180) / 360 * n;
        const rad = lat * Math.PI / 180;
        const y = (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * n;
        return { x, y };
      };
      // Zoom: el más chico cuyo bounding box de la ruta llene el área útil (580x640)
      let z = 4;
      for (let zi = 16; zi >= 4; zi--) {
        const a = wpx(latMax, lngMin, zi), b = wpx(latMin, lngMax, zi);
        if ((b.x - a.x) <= 580 && (b.y - a.y) <= 640) { z = zi; break; }
      }
      const pA = wpx(latMax, lngMin, z), pB = wpx(latMin, lngMax, z);
      const midX = (pA.x + pB.x) / 2, midY = (pA.y + pB.y) / 2;
      const span = Math.max(pB.x - pA.x, 1), spanY = Math.max(pB.y - pA.y, 1);
      const esc = Math.min(580 / span, 640 / spanY, 1.6); // no agrandar tiles más de 1.6x
      const CX = 360, CY = 600; // centro del área de mapa en el canvas
      const aXY = (p) => { const w = wpx(p.lat, p.lng, z); return { x: CX + (w.x - midX) * esc, y: CY + (w.y - midY) * esc }; };

      // ── Fondo: tiles oscuros de Esri para TODO el canvas (máx 35, con timeout) ──
      let fondo = null, conTiles = false;
      try {
        const wxL = midX + (0 - CX) / esc, wxR = midX + (W - CX) / esc;
        const wyT = midY + (0 - CY) / esc, wyB = midY + (H - CY) / esc;
        const txMin = Math.floor(wxL / 256), txMax = Math.floor(wxR / 256);
        const tyMin = Math.floor(wyT / 256), tyMax = Math.floor(wyB / 256);
        const pedidos = [];
        for (let tx = txMin; tx <= txMax; tx++) for (let ty = tyMin; ty <= tyMax; ty++) pedidos.push({ tx, ty });
        if (pedidos.length <= 35) {
          const imgs = await Promise.race([
            Promise.all(pedidos.map(t => new Promise(res => {
              const im = new window.Image();
              im.crossOrigin = "anonymous";
              im.onload = () => res({ ...t, im });
              im.onerror = () => res(null);
              im.src = `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/${z}/${t.ty}/${t.tx}`;
            }))),
            new Promise(res => setTimeout(() => res(null), 4000)),
          ]);
          if (imgs && imgs.some(Boolean)) {
            fondo = document.createElement("canvas");
            fondo.width = W; fondo.height = H;
            const fctx = fondo.getContext("2d");
            fctx.fillStyle = "#111111"; fctx.fillRect(0, 0, W, H);
            imgs.filter(Boolean).forEach(({ tx, ty, im }) => {
              fctx.drawImage(im, CX + (tx * 256 - midX) * esc, CY + (ty * 256 - midY) * esc, 256 * esc + 1, 256 * esc + 1);
            });
            // velo negro: el mapa es contexto, la línea crema es protagonista
            fctx.fillStyle = "rgba(17,17,17,0.55)"; fctx.fillRect(0, 0, W, H);
            conTiles = true;
          }
        }
      } catch (e) { fondo = null; }

      // ── Fotos de la rodada (máx 5, precargadas con CORS para no ensuciar el canvas) ──
      const fotosTraza = (Array.isArray(traza.fotos) ? traza.fotos : []).filter(f => f && f.url && f.type !== "video").slice(0, 5);
      const fotosCargadas = (await Promise.all(fotosTraza.map(f => new Promise(res => {
        const im = new window.Image();
        im.crossOrigin = "anonymous";
        im.onload = () => res({ f, im });
        im.onerror = () => res(null);
        im.src = cldThumb(f.url, 800) || f.url;
      })))).filter(Boolean);

      if (!vivo) return;

      // ── Geometría del trazo: distancias acumuladas para dibujar "por km" ──
      const xy = puntos.map(aXY);
      const acum = [0];
      for (let i = 1; i < puntos.length; i++) acum.push(acum[i - 1] + haversineKm(puntos[i - 1], puntos[i]));
      const kmTotal = kmTraza(traza) || acum[acum.length - 1];
      const t0 = puntos[0].ts || null, t1 = puntos[puntos.length - 1].ts || null;
      const durSeg = traza.duracion_seg || (t0 && t1 ? Math.round((t1 - t0) / 1000) : null);
      // Momento (0..1) de cada foto sobre el recorrido: por hora real o pareja
      const fotosConMomento = fotosCargadas.map(({ f, im }, i) => {
        let frac = (i + 1) / (fotosCargadas.length + 1);
        if (f.ts && t0 && t1 && t1 > t0) frac = Math.min(0.97, Math.max(0.03, (f.ts - t0) / (t1 - t0)));
        return { im, frac };
      }).sort((a, b) => a.frac - b.frac);
      // separadas al menos 9% para que los flashes no se pisen
      for (let i = 1; i < fotosConMomento.length; i++) if (fotosConMomento[i].frac - fotosConMomento[i - 1].frac < 0.09) fotosConMomento[i].frac = fotosConMomento[i - 1].frac + 0.09;

      // posición interpolada del trazo principal a una fracción de distancia
      const posEnFrac = (frac) => {
        const d = frac * acum[acum.length - 1];
        let i = 1;
        while (i < acum.length - 1 && acum[i] < d) i++;
        const dPrev = acum[i - 1], dSeg = acum[i] - dPrev || 1e-9;
        const tt = Math.min(1, Math.max(0, (d - dPrev) / dSeg));
        return { x: xy[i - 1].x + (xy[i].x - xy[i - 1].x) * tt, y: xy[i - 1].y + (xy[i].y - xy[i - 1].y) * tt, idx: i };
      };
      // posición de un compañero a una hora real dada (sincronizado por ts)
      const compPos = comps.map(c => {
        const cxy = c.puntos.map(aXY);
        const cts = c.puntos.map(p => p.ts || null);
        return { nombre: c.nombre, xy: cxy, ts: cts, tIni: cts[0], tFin: cts[cts.length - 1] };
      });
      const posComp = (c, tReal) => {
        if (!c.tIni || !c.tFin || !tReal || tReal < c.tIni) return null;
        if (tReal >= c.tFin) return c.xy[c.xy.length - 1];
        let i = 1;
        while (i < c.ts.length - 1 && (c.ts[i] || 0) < tReal) i++;
        const a = c.ts[i - 1] || c.tIni, b = c.ts[i] || c.tFin;
        const tt = b > a ? (tReal - a) / (b - a) : 0;
        return { x: c.xy[i - 1].x + (c.xy[i].x - c.xy[i - 1].x) * tt, y: c.xy[i - 1].y + (c.xy[i].y - c.xy[i - 1].y) * tt };
      };

      // ── Dibujo de un frame en el tiempo t (ms desde el arranque) ──
      const canvas = canvasRef.current;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      const F = "'Archivo', Arial, sans-serif";
      const CREMA = "#F9F5F2", MUTED = "#bdb6ab";
      const rrect = (x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); };
      const ease = (u) => u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
      const fmtDurV = (s) => { if (!s) return ""; const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h > 0 ? `${h} h ${m} min` : `${m} min`; };
      const titulo = traza.titulo || "Mi recorrido";
      const fecha = traza.created_at ? new Date(traza.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : "";
      const T_INTRO = 1600, T_TRAZO = 8600, T_PLACA_IN = DUR - 2800;

      const frame = (t) => {
        // fondo
        if (fondo) ctx.drawImage(fondo, 0, 0); else { ctx.fillStyle = "#111111"; ctx.fillRect(0, 0, W, H); }
        const gTop = ctx.createLinearGradient(0, 0, 0, 260); gTop.addColorStop(0, "rgba(17,17,17,0.92)"); gTop.addColorStop(1, "rgba(17,17,17,0)");
        ctx.fillStyle = gTop; ctx.fillRect(0, 0, W, 260);
        const gBot = ctx.createLinearGradient(0, H - 360, 0, H); gBot.addColorStop(0, "rgba(17,17,17,0)"); gBot.addColorStop(1, "rgba(17,17,17,0.95)");
        ctx.fillStyle = gBot; ctx.fillRect(0, H - 360, W, 360);

        // header de marca
        ctx.textAlign = "left"; ctx.fillStyle = CREMA; ctx.font = `800 34px ${F}`;
        ctx.fillText("OLBOS®", 44, 74);
        ctx.textAlign = "right"; ctx.fillStyle = MUTED; ctx.font = `600 22px ${F}`;
        ctx.fillText(fecha, W - 44, 72);
        // título
        ctx.textAlign = "left"; ctx.fillStyle = CREMA; ctx.font = `700 40px ${F}`;
        let tit = titulo; while (ctx.measureText(tit).width > W - 88 && tit.length > 4) tit = tit.slice(0, -2);
        ctx.fillText(tit + (tit !== titulo ? "…" : ""), 44, 136);

        const prog = t < T_INTRO ? 0 : Math.min(1, ease((t - T_INTRO) / T_TRAZO));

        // línea completa tenue de referencia
        ctx.strokeStyle = "rgba(249,245,242,0.18)"; ctx.lineWidth = 5; ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctx.beginPath(); xy.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)); ctx.stroke();

        // compañeros de rodada (sincronizados por hora real)
        if (compPos.length && t0 && t1 && prog > 0) {
          const tReal = t0 + prog * (t1 - t0);
          ctx.font = `700 21px ${F}`;
          compPos.forEach((c, i) => {
            const p = posComp(c, tReal);
            if (!p) return;
            ctx.fillStyle = "#8A6D3B";
            ctx.beginPath(); ctx.arc(p.x, p.y, 11, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = "#111111"; ctx.lineWidth = 3; ctx.stroke();
            ctx.fillStyle = MUTED; ctx.textAlign = "left";
            ctx.fillText(c.nombre, p.x + 18, p.y + 7);
          });
        }

        // el trazo que se dibuja
        if (prog > 0) {
          const cab = posEnFrac(prog);
          ctx.strokeStyle = CREMA; ctx.lineWidth = 8; ctx.lineCap = "round"; ctx.lineJoin = "round";
          ctx.beginPath(); ctx.moveTo(xy[0].x, xy[0].y);
          for (let i = 1; i < cab.idx; i++) ctx.lineTo(xy[i].x, xy[i].y);
          ctx.lineTo(cab.x, cab.y); ctx.stroke();
          // punto de salida
          ctx.fillStyle = CREMA; ctx.beginPath(); ctx.arc(xy[0].x, xy[0].y, 9, 0, Math.PI * 2); ctx.fill();
          // cabeza con glow
          ctx.shadowColor = CREMA; ctx.shadowBlur = 24;
          ctx.beginPath(); ctx.arc(cab.x, cab.y, 14, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
          ctx.fillStyle = "#111111"; ctx.beginPath(); ctx.arc(cab.x, cab.y, 6, 0, Math.PI * 2); ctx.fill();
        }

        // HUD: km y tiempo corriendo
        ctx.textAlign = "left"; ctx.fillStyle = CREMA; ctx.font = `800 92px ${F}`;
        ctx.fillText(`${(prog * kmTotal).toFixed(1)}`, 44, H - 170);
        const wKm = ctx.measureText(`${(prog * kmTotal).toFixed(1)}`).width;
        ctx.font = `700 40px ${F}`; ctx.fillStyle = MUTED; ctx.fillText("km", 44 + wKm + 14, H - 170);
        if (durSeg) { ctx.font = `600 32px ${F}`; ctx.fillStyle = MUTED; ctx.fillText(fmtDurV(Math.round(prog * durSeg)), 46, H - 112); }
        if (conTiles) { ctx.font = `500 16px ${F}`; ctx.fillStyle = "rgba(189,182,171,0.6)"; ctx.textAlign = "right"; ctx.fillText("© Esri", W - 20, H - 20); }

        // flash de fotos
        fotosConMomento.forEach(({ im, frac }) => {
          const tFoto = T_INTRO + frac * T_TRAZO;
          const dt = t - tFoto;
          if (dt < 0 || dt > 1200) return;
          const a = dt < 200 ? dt / 200 : dt > 1000 ? (1200 - dt) / 200 : 1;
          ctx.globalAlpha = Math.max(0, Math.min(1, a));
          ctx.fillStyle = "rgba(17,17,17,0.72)"; ctx.fillRect(0, 0, W, H);
          const fw = 560, fh = Math.min(700, fw * (im.height / (im.width || 1)));
          const fx = (W - fw) / 2, fy = (H - fh) / 2 - 60;
          ctx.save(); rrect(fx, fy, fw, fh, 22); ctx.clip();
          ctx.drawImage(im, fx, fy, fw, fh); ctx.restore();
          rrect(fx, fy, fw, fh, 22); ctx.strokeStyle = CREMA; ctx.lineWidth = 4; ctx.stroke();
          ctx.globalAlpha = 1;
        });

        // placa final
        if (t > T_PLACA_IN) {
          const a = Math.min(1, (t - T_PLACA_IN) / 500);
          ctx.globalAlpha = a;
          ctx.fillStyle = "#111111"; ctx.fillRect(0, 0, W, H);
          ctx.textAlign = "center";
          ctx.fillStyle = CREMA; ctx.font = `800 76px ${F}`; ctx.fillText("OLBOS®", W / 2, 340);
          ctx.fillStyle = MUTED; ctx.font = `600 30px ${F}`; ctx.fillText(fecha, W / 2, 400);
          ctx.fillStyle = CREMA; ctx.font = `700 44px ${F}`;
          let t2 = titulo; while (ctx.measureText(t2).width > W - 120 && t2.length > 4) t2 = t2.slice(0, -2);
          ctx.fillText(t2 + (t2 !== titulo ? "…" : ""), W / 2, 500);
          const stats = [[`${kmTotal ? kmTotal.toFixed(1) : "—"}`, "KM"]];
          if (durSeg) stats.push([fmtDurV(durSeg), "TIEMPO"]);
          if (traza.velocidad_max) stats.push([`${Math.round(traza.velocidad_max)}`, "KM/H MÁX"]);
          // columnas de ancho fijo y fuente que se achica hasta entrar: antes los
          // valores anchos ("1 h 49 min") se pisaban entre sí (reporte de Carlos)
          const colW = (W - 80) / stats.length;
          stats.forEach(([v, l], i) => {
            const cxCol = 40 + colW * (i + 0.5);
            let fs = 54;
            ctx.font = `800 ${fs}px ${F}`;
            while (ctx.measureText(v).width > colW - 24 && fs > 24) { fs -= 2; ctx.font = `800 ${fs}px ${F}`; }
            ctx.fillStyle = CREMA; ctx.fillText(v, cxCol, 660);
            ctx.fillStyle = MUTED; ctx.font = `700 21px ${F}`; ctx.fillText(l, cxCol, 700);
          });
          if (username) { ctx.fillStyle = CREMA; ctx.font = `700 34px ${F}`; ctx.fillText(`@${username}`, W / 2, 810); }
          ctx.fillStyle = MUTED; ctx.font = `600 26px ${F}`; ctx.fillText("olbos.ai", W / 2, 900);
          ctx.font = `italic 500 24px ${F}`; ctx.fillText("El camino es el destino", W / 2, 940);
          ctx.globalAlpha = 1;
        }
      };

      // ── Grabar: el canvas se anima en vivo y MediaRecorder lo captura ──
      const mimes = ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9", "video/webm"];
      const mime = mimes.find(m => { try { return MediaRecorder.isTypeSupported(m); } catch (e) { return false; } });
      if (!mime) { setErrorMsg("Este navegador no soporta ningún formato de video."); setFase("error"); return; }
      setVideoExt(mime.startsWith("video/mp4") ? "mp4" : "webm");
      const stream = canvas.captureStream(30);
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 5000000 });
      recRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = () => {
        if (!vivo) return;
        const blob = new Blob(chunks, { type: mime.split(";")[0] });
        setVideoBlob(blob);
        setVideoUrl(URL.createObjectURL(blob));
        setFase("listo");
      };
      setFase("grabando");
      frame(0);
      rec.start(250);
      const inicio = performance.now();
      const loop = () => {
        if (!vivo) return;
        const t = performance.now() - inicio;
        if (t >= DUR) { frame(DUR - 1); try { rec.stop(); } catch (e) { /* */ } return; }
        frame(t);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    })();
    return () => {
      vivo = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) { /* */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reintento]);

  useEffect(() => () => { if (videoUrl) URL.revokeObjectURL(videoUrl); }, [videoUrl]);

  // En la app nativa el WebView de Android no comparte archivos ni descarga blobs: el botón
  // no hacía nada (documento de Matías, N2). Ahí se sube a Cloudinary y se abre en el
  // navegador del teléfono como adjunto mp4, igual que descargarImagen.
  const [preparandoDescarga, setPreparandoDescarga] = useState(false);
  const compartirVideo = async () => {
    if (!videoBlob || preparandoDescarga) return;
    const file = new File([videoBlob], `olbos-recorrido.${videoExt}`, { type: videoBlob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: traza.titulo || "Mi recorrido en Olbos" }); return; } catch (e) { if (e && e.name === "AbortError") return; }
    }
    if (esNativo()) {
      setPreparandoDescarga(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("upload_preset", "olbos_uploads");
        const up = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload", { method: "POST", body: fd }).then(r => r.json());
        if (!up || !up.secure_url) throw new Error("sin url");
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: up.secure_url.replace("/video/upload/", "/video/upload/fl_attachment,f_mp4/").replace(/\.webm$/i, ".mp4") });
      } catch (e) {
        alert("No se pudo preparar el video para descargar. Probá con Publicar en el feed y bajalo desde ahí.");
      }
      setPreparandoDescarga(false);
      return;
    }
    const a = document.createElement("a");
    a.href = videoUrl; a.download = `olbos-recorrido.${videoExt}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Publicar el video como post en el feed: sube a Cloudinary y crea el post con
  // los mismos campos que compartirTrazaEnFeed. Se pide mp4 en la entrega para
  // que un video generado en Android (webm) también se vea en los iPhone.
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const publicarEnFeed = async () => {
    if (!videoBlob || publicando || publicado) return;
    setPublicando(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([videoBlob], `olbos-video.${videoExt}`, { type: videoBlob.type }));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data.error?.message || "no se pudo subir el video");
      const urlVideo = data.secure_url.replace("/video/upload/", "/video/upload/f_mp4/").replace(/\.webm$/i, ".mp4");
      const { data: perfilAutor } = await supabase.from("perfiles").select("username").eq("id", traza.user_id).maybeSingle();
      const { error } = await supabase.from("posts").insert({
        user_id: traza.user_id,
        username: perfilAutor?.username || null,
        texto: construirTextoTraza(traza),
        media_url: urlVideo,
        media_type: "video",
        media_urls: JSON.stringify([{ url: urlVideo, type: "video" }]),
        tipo: "recorrido",
        traza_id: traza.id,
        grupo_id: traza.grupo_id || null,
        evento_id: traza.evento_id || null,
        compartido_feed_general: !traza.grupo_id,
      });
      if (error) throw error;
      setPublicado(true);
    } catch (e) { alert("No se pudo publicar: " + (e.message || e)); }
    setPublicando(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9700, background: C.accent, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "calc(var(--sat, 0px) + 16px) 16px calc(var(--sab, 0px) + 16px)" }}>
      <button onClick={onClose} style={{ position: "absolute", top: "max(46px, calc(var(--sat) + 14px))", right: 16, width: 38, height: 38, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}>
        <X size={19} strokeWidth={2} color="#F9F5F2" />
      </button>
      {fase === "error" ? (
        <div style={{ maxWidth: 320, textAlign: "center", color: "#F9F5F2" }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>🎬</div>
          <div style={{ fontSize: 14.5, lineHeight: 1.6, color: "#bdb6ab" }}>{errorMsg}</div>
        </div>
      ) : fase === "listo" && videoUrl ? (
        <>
          <video src={videoUrl} autoPlay loop playsInline controls={false}
            style={{ maxHeight: "72vh", maxWidth: "100%", borderRadius: 18, background: "#000" }} />
          <div style={{ display: "flex", gap: 10, marginTop: 18, width: "100%", maxWidth: 380 }}>
            <button onClick={compartirVideo} disabled={preparandoDescarga}
              style={{ flex: 2, padding: "14px", borderRadius: 12, background: "#F9F5F2", color: C.text, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: preparandoDescarga ? 0.6 : 1 }}>
              {preparandoDescarga ? "Preparando el video…" : "📤 Compartir video"}
            </button>
            <button onClick={() => { setFase("prep"); setVideoUrl(null); setVideoBlob(null); setPublicado(false); setReintento(r => r + 1); }}
              style={{ flex: 1, padding: "14px", borderRadius: 12, background: "rgba(249,245,242,.1)", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
              Rehacer
            </button>
          </div>
          <button onClick={publicarEnFeed} disabled={publicando || publicado}
            style={{ marginTop: 10, width: "100%", maxWidth: 380, padding: "13px", borderRadius: 12, background: publicado ? "rgba(47,125,79,.25)" : "rgba(249,245,242,.1)", color: publicado ? "#7FBF9A" : "#F9F5F2", border: "1px solid rgba(249,245,242,.25)", fontSize: 14, fontWeight: 700, cursor: (publicando || publicado) ? "default" : "pointer", opacity: publicando ? 0.6 : 1 }}>
            {publicado ? "✓ Publicado en el feed" : publicando ? "Subiendo al feed…" : "📣 Publicar en el feed"}
          </button>
        </>
      ) : (
        <>
          <canvas ref={canvasRef} style={{ maxHeight: "72vh", maxWidth: "100%", borderRadius: 18, background: C.accent }} />
          <div style={{ marginTop: 16, fontSize: 13.5, color: "#bdb6ab", fontWeight: 600 }}>
            {fase === "prep" ? "Preparando tu video…" : "🎬 Generando — tu rodada se está reviviendo…"}
          </div>
        </>
      )}
    </div>
  );
};

// ─── 🛣️ PUBLICAR UN RECORRIDO COMO RUTA DEL CATÁLOGO ─────────────────────────
// El puente que faltaba (pedido de Carlos, 31-ago): un recorrido grabado pasa al
// catálogo de Rutas con su trazado REAL precargado — solo se completan nombre,
// dificultad, superficie y descripción. El catálogo crece del uso real.
// (usa el codificarPolyline que ya existe en el codebase)
const PublicarRecorridoModal = ({ traza, onClose }) => {
  const puntosT = (Array.isArray(traza.puntos) ? traza.puntos : []).filter(p => p && p.lat && p.lng);
  const [nombre, setNombre] = useState(traza.titulo || "");
  const [desdeNom, setDesdeNom] = useState("");
  const [hastaNom, setHastaNom] = useState("");
  const [diff, setDiff] = useState("Moderada");
  const [camino, setCamino] = useState("Asfalto");
  const [desc, setDesc] = useState("");
  const [publicando, setPublicando] = useState(false);
  // Nombres de inicio/fin sugeridos por geocoder (editables — mejor sugerir que pedir)
  useEffect(() => {
    if (puntosT.length < 2) return;
    let vivo = true;
    loadGoogleMaps().then(() => {
      if (!window.google || !vivo) return;
      const geo = new window.google.maps.Geocoder();
      const nombrar = (p, set) => geo.geocode({ location: { lat: p.lat, lng: p.lng }, language: "es" }, (res, st) => {
        if (!vivo || st !== "OK" || !res || !res[0]) return;
        const parts = res[0].address_components;
        const loc = parts.find(x => x.types.includes("locality"))?.long_name || parts.find(x => x.types.includes("administrative_area_level_2"))?.long_name || "";
        if (loc) set(prev => prev || loc);
      });
      nombrar(puntosT[0], setDesdeNom);
      nombrar(puntosT[puntosT.length - 1], setHastaNom);
    }).catch(() => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const publicar = async () => {
    if (publicando || !nombre.trim()) return;
    setPublicando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Iniciá sesión");
      const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", user.id).maybeSingle();
      if (!perfil?.username) throw new Error("Configurá tu @usuario en el perfil antes de publicar");
      const simplificados = downsampleRecorrido(puntosT, 300);
      const p0 = puntosT[0], pF = puntosT[puntosT.length - 1];
      const { error } = await supabase.from("rutas").insert({
        user_id: user.id,
        username: perfil.username,
        nombre: nombre.trim(),
        desde: { nombre: desdeNom.trim() || "Inicio del recorrido", lat: p0.lat, lng: p0.lng },
        hasta: { nombre: hastaNom.trim() || "Destino", lat: pF.lat, lng: pF.lng },
        paradas: (Array.isArray(traza.paradas) ? traza.paradas : []).filter(p => p && p.lat && p.lng).map(p => ({ nombre: p.nombre || "Parada", lat: p.lat, lng: p.lng })),
        km: Math.round(kmTraza(traza)), // la columna km es entera (falló con 190.2)
        diff,
        camino,
        pct_offroad: camino === "Asfalto" ? 0 : camino === "Mixto" ? 50 : 80,
        desc_texto: desc.trim() || `Publicada desde un recorrido real grabado con Olbos (${Math.round(kmTraza(traza))} km).`,
        alertas: "",
        foto_url: null,
        // el trazado REAL grabado, no el camino teórico de Google
        polyline: codificarPolyline(simplificados),
      });
      if (error) throw error;
      alert("🛣️ ¡Publicada! Tu recorrido ya está en el catálogo de Rutas, con el trazado real que grabaste.");
      window.dispatchEvent(new CustomEvent("olbos-rutas-actualizadas"));
      onClose(true);
    } catch (e) { alert("No se pudo publicar: " + (e.message || e)); }
    setPublicando(false);
  };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" };
  const lbl = { fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5 };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose(false)}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(26px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>🛣️ Publicar como ruta</h2>
          <button onClick={() => onClose(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer" }}><X size={15} strokeWidth={2} color={C.muted} /></button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
          Tu recorrido pasa al catálogo público de Rutas con el trazado <strong>real</strong> que grabaste ({Math.round(kmTraza(traza))} km{(traza.paradas || []).length ? ` y ${(traza.paradas || []).length} paradas` : ""}). Solo falta presentarlo.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><div style={lbl}>Nombre de la ruta *</div>
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Vuelta al dique por el ripio viejo" style={inp} /></div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}><div style={lbl}>Sale de</div>
              <input value={desdeNom} onChange={e => setDesdeNom(e.target.value)} placeholder="Ciudad / punto" style={inp} /></div>
            <div style={{ flex: 1 }}><div style={lbl}>Llega a</div>
              <input value={hastaNom} onChange={e => setHastaNom(e.target.value)} placeholder="Ciudad / punto" style={inp} /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}><div style={lbl}>Dificultad</div>
              <select value={diff} onChange={e => setDiff(e.target.value)} style={{ ...inp, background: C.card }}>
                {["Fácil", "Moderada", "Difícil", "Muy difícil"].map(d => <option key={d}>{d}</option>)}
              </select></div>
            <div style={{ flex: 1 }}><div style={lbl}>Camino</div>
              <select value={camino} onChange={e => setCamino(e.target.value)} style={{ ...inp, background: C.card }}>
                {["Asfalto", "Ripio", "Tierra", "Mixto"].map(d => <option key={d}>{d}</option>)}
              </select></div>
          </div>
          <div><div style={lbl}>Por qué vale la pena (opcional)</div>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} placeholder="Curvas, paisajes, dónde parar a comer…" style={{ ...inp, resize: "none" }} /></div>
        </div>
        <button onClick={publicar} disabled={publicando || !nombre.trim() || puntosT.length < 2}
          style={{ width: "100%", marginTop: 16, padding: "14px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: (publicando || !nombre.trim()) ? 0.6 : 1 }}>
          {publicando ? "Publicando…" : "Publicar en el catálogo"}
        </button>
      </div>
    </div>
  );
};

// ─── DETALLE DE TRAZA ─────────────────────────────────────────────────────────
// ─── Fotos y videos a un recorrido YA terminado (10-oct-2026) ─────────────────
// "Muchas veces solo yo grababa el recorrido, pero otros sacan fotos en las paradas y
// me las pasan al llegar" (Carlos). Cada archivo va en un punto del recorrido (se toca
// el mapa y se pega al punto más cercano del trazo) o "general" (sin lat/lng: solo en
// la tira de fotos y en el post del recorrido).

// Fecha de la toma (DateTimeOriginal) y GPS de un JPEG, sin dependencias: recorre el
// bloque Exif a mano. WhatsApp borra el EXIF, así que las fotos que llegan por ahí no
// traen nada (se elige el punto a mano). Se lee del archivo ORIGINAL, antes de achicarlo.
const leerExifFoto = async (file) => {
  try {
    if (!file || !/jpe?g/i.test((file.type || "") + " " + (file.name || ""))) return null;
    const buf = await file.slice(0, 256 * 1024).arrayBuffer();
    const v = new DataView(buf);
    if (v.getUint16(0) !== 0xFFD8) return null;
    let off = 2;
    while (off + 10 < v.byteLength) {
      const marker = v.getUint16(off);
      const len = v.getUint16(off + 2);
      if (marker === 0xFFE1 && v.getUint32(off + 4) === 0x45786966) {
        const t = off + 10; // cabecera TIFF
        const le = v.getUint16(t) === 0x4949;
        const u16 = (o) => v.getUint16(t + o, le), u32 = (o) => v.getUint32(t + o, le);
        const leerIFD = (o) => { const n = u16(o); const tags = {}; for (let i = 0; i < n; i++) { const e = o + 2 + i * 12; tags[u16(e)] = { cant: u32(e + 4), valOff: e + 8 }; } return tags; };
        const ascii = (tag) => { const ptr = tag.cant > 4 ? u32(tag.valOff) : tag.valOff; let s = ""; for (let i = 0; i < tag.cant - 1; i++) s += String.fromCharCode(v.getUint8(t + ptr + i)); return s; };
        const grados = (tag) => { const ptr = u32(tag.valOff); const r = (k) => u32(ptr + k * 8) / (u32(ptr + k * 8 + 4) || 1); return r(0) + r(1) / 60 + r(2) / 3600; };
        const ifd0 = leerIFD(u32(4));
        let ts = null, lat = null, lng = null;
        if (ifd0[0x8769]) {
          const ex = leerIFD(u32(ifd0[0x8769].valOff));
          const tg = ex[0x9003] || ex[0x9004];
          const m = tg ? ascii(tg).match(/(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/) : null;
          if (m) { const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]); if (!isNaN(d.getTime())) ts = d.getTime(); }
        }
        if (ifd0[0x8825]) {
          const g = leerIFD(u32(ifd0[0x8825].valOff));
          if (g[2] && g[4]) {
            lat = grados(g[2]); lng = grados(g[4]);
            if (g[1] && ascii(g[1]) === "S") lat = -lat;
            if (g[3] && ascii(g[3]) === "W") lng = -lng;
            if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) { lat = null; lng = null; }
          }
        }
        return (ts || lat != null) ? { ts, lat, lng } : null;
      }
      if ((marker & 0xFF00) !== 0xFF00) break;
      off += 2 + len;
    }
  } catch (e) { /* EXIF raro: se elige a mano */ }
  return null;
};

// Índice del punto del trazo más cercano a una posición / a una hora
const idxPuntoCercano = (puntos, pos) => {
  let best = -1, bd = Infinity;
  const k = Math.cos((pos.lat * Math.PI) / 180);
  for (let i = 0; i < puntos.length; i++) {
    const p = puntos[i];
    if (!p || p.lat == null || p.lng == null) continue;
    const d = (p.lat - pos.lat) ** 2 + ((p.lng - pos.lng) * k) ** 2;
    if (d < bd) { bd = d; best = i; }
  }
  return best;
};
const idxPuntoPorHora = (puntos, ts) => {
  let best = -1, bd = Infinity;
  for (let i = 0; i < puntos.length; i++) {
    const t = puntos[i] && puntos[i].ts;
    if (!t) continue;
    const d = Math.abs(t - ts);
    if (d < bd) { bd = d; best = i; }
  }
  return best;
};

// Mapa a pantalla completa para elegir el punto del recorrido: se toca y se pega al trazo
const ElegirPuntoTraza = ({ puntos, inicial, onElegir, onCerrar }) => {
  const divRef = useRef(null);
  const mapaRef = useRef(null);
  const markerRef = useRef(null);
  const [idx, setIdx] = useState(inicial != null && inicial >= 0 ? inicial : null);
  useEffect(() => {
    let vivo = true;
    loadGoogleMaps().then(() => {
      if (!vivo || !window.google || !divRef.current) return;
      const g = window.google.maps;
      const mapa = new g.Map(divRef.current, { disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy", clickableIcons: false });
      mapaRef.current = mapa;
      const bounds = new g.LatLngBounds();
      puntos.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapa.fitBounds(bounds, 40);
      const linea = new g.Polyline({ path: puntos.map(p => ({ lat: p.lat, lng: p.lng })), strokeColor: "#111111", strokeOpacity: 0.9, strokeWeight: 5, map: mapa });
      const marcar = (i) => {
        const p = puntos[i];
        if (!p) return;
        if (!markerRef.current) {
          markerRef.current = new g.Marker({ map: mapa, zIndex: 999, icon: { path: g.SymbolPath.CIRCLE, scale: 10, fillColor: "#111111", fillOpacity: 1, strokeColor: "#F9F5F2", strokeWeight: 3 } });
        }
        markerRef.current.setPosition({ lat: p.lat, lng: p.lng });
      };
      const alTocar = (ev) => {
        if (!ev || !ev.latLng) return;
        const i = idxPuntoCercano(puntos, { lat: ev.latLng.lat(), lng: ev.latLng.lng() });
        if (i >= 0) { setIdx(i); marcar(i); }
      };
      mapa.addListener("click", alTocar);
      linea.addListener("click", alTocar);
      if (inicial != null && inicial >= 0) marcar(inicial);
    });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 620, background: C.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "calc(var(--sat) + 12px) 16px 12px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, background: C.card }}>
        <button onClick={onCerrar} style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <ChevronLeft size={18} strokeWidth={2} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>¿Dónde la sacaron?</div>
          <div style={{ fontSize: 12, color: C.muted }}>Tocá el mapa: se pega al punto más cercano del recorrido</div>
        </div>
      </div>
      <div ref={divRef} style={{ flex: 1, background: "#f6f1eb" }} />
      <div style={{ padding: "12px 16px calc(14px + var(--sab))", background: C.card, borderTop: `1px solid ${C.border}` }}>
        <button disabled={idx == null} onClick={() => onElegir(idx)}
          style={{ width: "100%", padding: "14px", borderRadius: 12, background: idx == null ? C.surface : "#111111", color: idx == null ? C.muted : "#F9F5F2", border: "none", fontSize: 15, fontWeight: 700, cursor: idx == null ? "default" : "pointer" }}>
          {idx == null ? "Tocá un punto del recorrido" : "Listo, va acá"}
        </button>
      </div>
    </div>
  );
};

const AgregarFotosTraza = ({ traza, archivos, miId, onListo, onCerrar }) => {
  const puntos = useMemo(() => (Array.isArray(traza.puntos) ? traza.puntos : []).filter(p => p && p.lat != null && p.lng != null), [traza.puntos]);
  const hayMapa = puntos.length > 1;
  const kmHasta = useMemo(() => {
    const acum = [0];
    for (let i = 1; i < puntos.length; i++) acum.push(acum[i - 1] + haversineKm(puntos[i - 1], puntos[i]));
    return acum;
  }, [puntos]);
  const [items, setItems] = useState(() => (archivos || []).map((f, i) => ({
    id: `${Date.now()}_${i}`, file: f, prev: URL.createObjectURL(f),
    isVideo: (f.type || "").startsWith("video"), destino: "general", idx: null, auto: null,
  })));
  const [eligiendo, setEligiendo] = useState(null); // id del item que elige punto en el mapa
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState("");

  // Propuesta automática: GPS de la foto (si cae cerca del trazo) o la hora de la toma
  // dentro del recorrido. El rider la ve marcada y la puede cambiar.
  useEffect(() => {
    if (!hayMapa) return;
    let vivo = true;
    const t0 = puntos[0].ts, t1 = puntos[puntos.length - 1].ts;
    (async () => {
      for (const it of items) {
        if (it.isVideo) continue;
        const ex = await leerExifFoto(it.file);
        if (!vivo || !ex) continue;
        let idx = -1, auto = null;
        if (ex.lat != null) {
          const i = idxPuntoCercano(puntos, ex);
          if (i >= 0 && haversineKm(puntos[i], ex) < 3) { idx = i; auto = "gps"; }
        }
        if (idx < 0 && ex.ts && t0 && t1 && ex.ts >= t0 - 20 * 60000 && ex.ts <= t1 + 20 * 60000) {
          idx = idxPuntoPorHora(puntos, ex.ts); if (idx >= 0) auto = "hora";
        }
        if (idx >= 0) setItems(arr => arr.map(x => x.id === it.id ? { ...x, destino: "punto", idx, auto, ts: ex.ts || null } : x));
        else if (ex.ts) setItems(arr => arr.map(x => x.id === it.id ? { ...x, ts: ex.ts } : x));
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => { items.forEach(it => { try { URL.revokeObjectURL(it.prev); } catch (e) { /* */ } }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiar = (id, patch) => setItems(arr => arr.map(x => x.id === id ? { ...x, ...patch } : x));
  const faltaPunto = items.some(it => it.destino === "punto" && (it.idx == null || it.idx < 0));
  const itemEligiendo = items.find(x => x.id === eligiendo);

  const subir = async () => {
    if (subiendo || !items.length || faltaPunto) return;
    setSubiendo(true);
    const nuevas = [];
    let fallidas = 0;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      setProgreso(`${i + 1}/${items.length}`);
      try {
        let vid = it.isVideo ? it.file : null;
        if (it.isVideo && it.file.size > TOPE_VIDEO_MB * 1024 * 1024) {
          vid = await videoParaSubir(it.file, (pct) => setProgreso(`${i + 1}/${items.length} · achicando video ${pct}%`));
          if (!vid) { fallidas++; continue; }
          setProgreso(`${i + 1}/${items.length}`);
        }
        const fd = new FormData();
        fd.append("file", it.isVideo ? vid : await achicarImagen(it.file));
        fd.append("upload_preset", "olbos_uploads");
        fd.append("cloud_name", "dhwhk48wb");
        const endpoint = it.isVideo
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const res = await fetch(endpoint, { method: "POST", body: fd });
        const data = await res.json();
        if (!data.secure_url) { fallidas++; continue; }
        const p = it.destino === "punto" && it.idx != null ? puntos[it.idx] : null;
        nuevas.push({
          url: it.isVideo ? urlVideoMp4(data.secure_url) : data.secure_url, type: it.isVideo ? "video" : "image",
          lat: p ? p.lat : null, lng: p ? p.lng : null,
          ts: (p && p.ts) || it.ts || null,
          agregada: true,
        });
      } catch (e) { fallidas++; }
    }
    if (!nuevas.length) {
      setSubiendo(false); setProgreso("");
      alert("No se pudo subir ninguna. Revisá la señal y probá de nuevo (los videos, con WiFi mejor).");
      return;
    }
    // Se relee lo guardado justo antes de escribir, para no pisar fotos que hayan
    // entrado mientras tanto (la cola offline del grabador, por ejemplo)
    let base = Array.isArray(traza.fotos) ? traza.fotos : [];
    try {
      const { data: fresca } = await supabase.from("trazas").select("fotos").eq("id", traza.id).maybeSingle();
      if (fresca && Array.isArray(fresca.fotos)) base = fresca.fotos;
    } catch (e) { /* queda la versión en memoria */ }
    const fotosFinal = [...base, ...nuevas];
    const { data: upd, error } = await supabase.from("trazas").update({ fotos: fotosFinal }).eq("id", traza.id).eq("user_id", miId).select("id");
    if (error || !upd || !upd.length) {
      setSubiendo(false); setProgreso("");
      alert("No se pudieron guardar en el recorrido" + (error && error.message ? ": " + error.message : ". Solo el dueño del recorrido puede agregarle fotos."));
      return;
    }
    // Si el recorrido ya está publicado en el feed, el post se actualiza con las nuevas
    try {
      const { data: postsRec } = await supabase.from("posts").select("id").eq("traza_id", traza.id).eq("tipo", "recorrido").eq("user_id", miId);
      if (postsRec && postsRec.length) {
        const medias = construirMediasTraza(recortarPuntas(Array.isArray(traza.puntos) ? traza.puntos : []), fotosFinal);
        await supabase.from("posts").update({
          media_url: medias[0]?.url || null, media_type: medias[0]?.type || null,
          media_urls: medias.length ? JSON.stringify(medias) : null,
        }).eq("traza_id", traza.id).eq("tipo", "recorrido").eq("user_id", miId);
      }
    } catch (e) { /* el post queda como estaba; las fotos ya están en el recorrido */ }
    registrarUso("recorrido_fotos_agregadas", {
      cantidad: nuevas.length,
      en_punto: nuevas.filter(f => f.lat != null).length,
      generales: nuevas.filter(f => f.lat == null).length,
      auto: items.filter(it => it.auto && it.destino === "punto").length,
      fallidas,
    });
    setSubiendo(false); setProgreso("");
    if (fallidas) alert(`Se agregaron ${nuevas.length}. ${fallidas === 1 ? "Una no se pudo subir" : `${fallidas} no se pudieron subir`} (revisá la señal o el peso del video).`);
    onListo(fotosFinal);
  };

  const chip = (activo) => ({ padding: "7px 11px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${activo ? C.text : C.border}`, background: activo ? C.text : C.card, color: activo ? "#F9F5F2" : C.text, whiteSpace: "nowrap" });

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 610, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
        onClick={e => { if (e.target === e.currentTarget && !subiendo) onCerrar(); }}>
        <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "20px 20px 0 0", padding: "18px 16px calc(18px + var(--sab))", maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.015em" }}>Agregar al recorrido</div>
            <button disabled={subiendo} onClick={onCerrar} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={16} color={C.muted} />
            </button>
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.45, marginBottom: 14 }}>
            {hayMapa ? "Elegí dónde va cada una: en el punto del recorrido donde la sacaron, o general (queda en las fotos del recorrido)." : "Van a las fotos del recorrido."}
          </div>
          {items.map(it => {
            const km = it.idx != null && it.idx >= 0 ? kmHasta[it.idx] : null;
            return (
              <div key={it.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                <div style={{ width: 64, height: 64, borderRadius: 10, overflow: "hidden", background: C.surface, flexShrink: 0, position: "relative" }}>
                  {it.isVideo
                    ? <video src={it.prev} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <img src={it.prev} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  {it.isVideo && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Play size={18} color="#fff" fill="#fff" /></span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {hayMapa && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                      <button onClick={() => { cambiar(it.id, { destino: "punto" }); if (it.idx == null) setEligiendo(it.id); }} style={chip(it.destino === "punto")}>📍 En un punto</button>
                      <button onClick={() => cambiar(it.id, { destino: "general" })} style={chip(it.destino === "general")}>General</button>
                    </div>
                  )}
                  {it.destino === "punto" ? (
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>
                      {km != null
                        ? <>En el <b style={{ color: C.text }}>km {km.toLocaleString("es-AR", { maximumFractionDigits: 1 })}</b> del recorrido{it.auto === "gps" ? " · por el GPS de la foto" : it.auto === "hora" ? " · por la hora de la foto" : ""}</>
                        : "Falta elegir el punto"}
                      {" · "}
                      <button onClick={() => setEligiendo(it.id)} style={{ background: "none", border: "none", padding: 0, color: C.text, fontWeight: 700, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>
                        {km != null ? "Cambiar" : "Elegir en el mapa"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: C.muted }}>Sin punto: va a las fotos del recorrido</div>
                  )}
                </div>
                {!subiendo && (
                  <button onClick={() => setItems(arr => arr.filter(x => x.id !== it.id))} style={{ width: 26, height: 26, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <X size={13} color={C.muted} />
                  </button>
                )}
              </div>
            );
          })}
          <button disabled={subiendo || !items.length || faltaPunto} onClick={subir}
            style={{ width: "100%", marginTop: 12, padding: "14px", borderRadius: 12, border: "none", fontSize: 15, fontWeight: 700, cursor: (subiendo || !items.length || faltaPunto) ? "default" : "pointer", background: (subiendo || !items.length || faltaPunto) ? C.surface : "#111111", color: (subiendo || !items.length || faltaPunto) ? C.muted : "#F9F5F2" }}>
            {subiendo ? `Subiendo ${progreso}…` : faltaPunto ? "Elegí el punto de las que van en el mapa" : `Agregar ${items.length === 1 ? "1 archivo" : `${items.length} archivos`}`}
          </button>
        </div>
      </div>
      {itemEligiendo && (
        <ElegirPuntoTraza puntos={puntos} inicial={itemEligiendo.idx}
          onCerrar={() => { if (itemEligiendo.idx == null) cambiar(itemEligiendo.id, { destino: "general" }); setEligiendo(null); }}
          onElegir={(i) => { cambiar(itemEligiendo.id, { destino: "punto", idx: i, auto: null }); setEligiendo(null); }} />
      )}
    </>
  );
};

// ── Recorrido pegado a las calles (pedido de Carlos, 23-oct-2026) ─────────────────
// El GPS del teléfono en ciudad se corre 20-30 m y la línea quedaba sobre las manzanas.
// Se manda el recorrido a la Roads API de Google (snapToRoads con interpolate): devuelve
// el camino real por las calles del mismo mapa en el que se ve. Solo para DIBUJAR: los km
// y las velocidades siguen saliendo del GPS. Se respeta cada hueco del GPS (tramos
// separados) y se calcula una sola vez por recorrido (queda en trazas.puntos_calle).
const AJUSTE_CALLES_V = 2;
const FUERA_DE_CALLE_M = 35; // más lejos que esto de la calle que asigna Google = camino que no está en el mapa
const ajustarTrazaACalles = async (puntos) => {
  const tramos = partirEnTramos((puntos || []).filter(p => p && isFinite(p.lat) && isFinite(p.lng)));
  const salida = [];
  for (const tramo of tramos) {
    // Un punto cada ~25 m alcanza (con interpolate Google completa la calle) y la API pide
    // que dos puntos seguidos estén a menos de 300 m: si no, el tramo se parte ahí.
    const muestras = [];
    let previo = null;
    tramo.forEach((p, i) => {
      if (!previo || i === tramo.length - 1 || haversineKm(previo, p) >= 0.025) { muestras.push({ p, ti: i }); previo = p; }
    });
    const partes = [[]];
    muestras.forEach((m, i) => {
      if (i > 0 && haversineKm(muestras[i - 1].p, m.p) > 0.28) partes.push([]);
      partes[partes.length - 1].push(m);
    });
    for (const parte of partes) {
      if (parte.length < 2) continue;
      const camino = [];
      const agregar = (pts) => pts.forEach(q => {
        const ult = camino[camino.length - 1];
        if (!ult || ult[0] !== q[0] || ult[1] !== q[1]) camino.push(q);
      });
      const redondo = (lat, lng) => [Math.round(lat * 1e6) / 1e6, Math.round(lng * 1e6) / 1e6];
      for (let i = 0; i < parte.length - 1; i += 99) {
        const lote = parte.slice(i, i + 100);
        if (lote.length < 2) break;
        const path = lote.map(m => `${m.p.lat.toFixed(6)},${m.p.lng.toFixed(6)}`).join("|");
        const r = await fetch(`https://roads.googleapis.com/v1/snapToRoads?interpolate=true&path=${encodeURIComponent(path)}&key=${GMAPS_KEY}`);
        const d = await r.json();
        if (!r.ok) throw new Error((d && d.error && d.error.message) || "roads " + r.status);
        // Sin snappedPoints = ningún punto cerca de una calle (campo abierto): todo va con el GPS real
        const sp = (Array.isArray(d.snappedPoints) ? d.snappedPoints : []).map(s => ({ q: redondo(s.location.latitude, s.location.longitude), oi: s.originalIndex }));
        // Dónde quedó cada punto original y si Google lo dejó sobre una calle de verdad
        const pos = new Array(lote.length).fill(-1);
        sp.forEach((s, k) => { if (s.oi != null && s.oi < lote.length) pos[s.oi] = k; });
        const enCalle = lote.map((m, k) => pos[k] >= 0 && haversineKm(m.p, { lat: sp[pos[k]].q[0], lng: sp[pos[k]].q[1] }) * 1000 <= FUERA_DE_CALLE_M);
        for (let k = 0; k < lote.length - 1; k++) {
          if (enCalle[k] && enCalle[k + 1] && pos[k + 1] > pos[k]) {
            agregar(sp.slice(pos[k], pos[k + 1] + 1).map(s => s.q));
          } else {
            // Campo, ripio sin mapear, un predio: va el GPS real entre esas dos muestras
            agregar(tramo.slice(lote[k].ti, lote[k + 1].ti + 1).map(p => redondo(p.lat, p.lng)));
          }
        }
      }
      if (camino.length > 1) salida.push(camino);
    }
  }
  return salida; // [[ [lat,lng], … ], …] un array por tramo
};

const DetalleTrazaModal = ({ traza, onCerrar, onEliminar, onTogglePublica, onRenombrar, onFotosActualizadas }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  // ➕ Agregar fotos/videos después de grabar (solo el dueño)
  const [miIdTraza, setMiIdTraza] = useState(null);
  const [fotosLocal, setFotosLocal] = useState(null);
  const [archivosNuevos, setArchivosNuevos] = useState(null);
  const inputAgregarRef = useRef(null);
  const dibujadasRef = useRef(null);
  if (!dibujadasRef.current) dibujadasRef.current = new window.Set();
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMiIdTraza(data?.user?.id || null), () => {});
  }, []);
  const esMiTraza = !!miIdTraza && miIdTraza === traza.user_id;
  const elegirArchivos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const ok = [];
    for (const f of files) {
      if ((f.type || "").startsWith("video")) { if (await videoDentroDelLimite(f)) ok.push(f); }
      else ok.push(f);
    }
    if (ok.length) setArchivosNuevos(ok.slice(0, 20));
  };
  const [fotoFullscreen, setFotoFullscreen] = useState(null);
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState("");
  const [mapaFullscreen, setMapaFullscreen] = useState(false);
  const [siguiendoTrack, setSiguiendoTrack] = useState(false);
  const [paradaSeleccionada, setParadaSeleccionada] = useState(null);
  const [visitas, setVisitas] = useState(null); // métricas: solo el autor/admin las recibe
  const [yaEnFeed, setYaEnFeed] = useState(null); // null=cargando, true/false=si ya hay un post de este recorrido
  const [compartiendoFeed, setCompartiendoFeed] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // 🎬 video del recorrido
  const [showPublicarRuta, setShowPublicarRuta] = useState(false); // 🛣️ al catálogo de Rutas

  // Traer métricas de visitas (el RPC valida que sea el autor o admin)
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: raw } = await supabase.rpc("get_visitas_traza", { p_traza_id: traza.id });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (vivo && data && !data.error) setVisitas(data);
    })();
    return () => { vivo = false; };
  }, [traza.id]);

  // ¿Este recorrido ya está publicado en el feed?
  useEffect(() => {
    let vivo = true;
    (async () => {
      const { count } = await supabase.from("posts").select("id", { count: "exact", head: true })
        .eq("traza_id", traza.id).eq("tipo", "recorrido");
      if (vivo) setYaEnFeed((count || 0) > 0);
    })();
    return () => { vivo = false; };
  }, [traza.id]);

  const compartirEnFeed = async () => {
    if (compartiendoFeed) return;
    if (kmTraza(traza) < KM_MIN_PUBLICAR) { alert(msgMinKm(kmTraza(traza))); return; }
    setCompartiendoFeed(true);
    const { error } = await compartirTrazaEnFeed(traza, traza.user_id);
    setCompartiendoFeed(false);
    if (error) { alert("No se pudo compartir: " + (error.message || error)); return; }
    setYaEnFeed(true);
    alert("¡Listo! Tu recorrido se publicó en el feed 🏍️");
  };
  const puntos = Array.isArray(traza.puntos) ? traza.puntos : [];
  const [calles, setCalles] = useState(() => {
    const pc = traza.puntos_calle;
    return pc && !Array.isArray(pc) && pc.v === AJUSTE_CALLES_V && Array.isArray(pc.tramos) && pc.tramos.length ? pc.tramos : null;
  });
  const lineasRef = useRef([]);
  useEffect(() => {
    if (calles || puntos.length < 2) return;
    let vivo = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || user.id !== traza.user_id) return; // se calcula una vez, cuando lo abre el dueño
        const c = await ajustarTrazaACalles(puntos);
        if (!vivo || !c.length) return;
        setCalles(c);
        registrarUso("traza_ajustada_calles", { tramos: c.length, puntos: c.reduce((s, x) => s + x.length, 0) });
        supabase.from("trazas").update({ puntos_calle: { v: AJUSTE_CALLES_V, tramos: c } }).eq("id", traza.id).then(() => {}, () => {});
      } catch (e) { reportarError("ajustar traza a calles: " + (e && e.message), null, "DetalleTrazaModal"); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traza.id]);
  // Dibujo de la línea: por calles si ya está, si no el GPS crudo (cortado en los huecos)
  const dibujarLineaTraza = () => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;
    lineasRef.current.forEach(l => l.setMap(null));
    const tramos = calles ? calles.map(tr => tr.map(([lat, lng]) => ({ lat, lng }))) : partirEnTramos(puntos).map(tr => tr.map(p => ({ lat: p.lat, lng: p.lng })));
    lineasRef.current = tramos.map(path => new window.google.maps.Polyline({ path, strokeColor: "#111111", strokeOpacity: 0.9, strokeWeight: 4, map }));
  };
  useEffect(() => { dibujarLineaTraza(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [calles]);
  const fotos = fotosLocal || (Array.isArray(traza.fotos) ? traza.fotos : []);
  const paradasTraza = Array.isArray(traza.paradas) ? traza.paradas : [];
  // Las fotos agregadas después aparecen en el mapa ya armado (el mapa se arma una vez)
  useEffect(() => {
    if (!fotosLocal || !mapInstanceRef.current || !window.google) return;
    fotosLocal.forEach((f, i) => {
      if (!f || !f.url || !f.lat || !f.lng || dibujadasRef.current.has(f.url)) return;
      dibujadasRef.current.add(f.url);
      const isVideo = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url);
      const thumbUrl = isVideo
        ? f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/").replace(/\.(mp4|mov|webm)$/i, ".jpg")
        : f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/");
      const marker = new window.google.maps.Marker({
        position: { lat: f.lat, lng: f.lng }, map: mapInstanceRef.current, zIndex: 1000 + i,
        icon: { url: thumbUrl, scaledSize: new window.google.maps.Size(38, 38), anchor: new window.google.maps.Point(2, 42) },
        title: isVideo ? `Video ${i + 1}` : `Foto ${i + 1}`,
      });
      marker.addListener("click", () => setFotoFullscreen(f));
    });
  }, [fotosLocal]);

  useEffect(() => {
    if (!mapRef.current || puntos.length === 0) return;
    loadGoogleMaps().then(() => {
      if (!window.google || !mapRef.current) return;
      if (!mapInstanceRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        puntos.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy", // mover con UN dedo
        });
        mapInstanceRef.current.fitBounds(bounds, 40);
        dibujarLineaTraza();
        // Inicio (verde) y fin (rojo)
        new window.google.maps.Marker({
          position: { lat: puntos[0].lat, lng: puntos[0].lng },
          map: mapInstanceRef.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#27AE60", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
          title: "Inicio",
        });
        const ult = puntos[puntos.length - 1];
        new window.google.maps.Marker({
          position: { lat: ult.lat, lng: ult.lng },
          map: mapInstanceRef.current,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: C.red, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
          title: "Fin",
        });
        // Marcadores de paradas PRIMERO y con zIndex bajo (pin azul con label).
        // Van debajo de las fotos para que, si hay una foto en la misma parada,
        // el tap caiga sobre la foto y no sobre el waypoint.
        paradasTraza.forEach((p, i) => {
          if (p.lat && p.lng) {
            const marker = new window.google.maps.Marker({
              position: { lat: p.lat, lng: p.lng },
              map: mapInstanceRef.current,
              zIndex: 100 + i,
              icon: {
                path: "M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z",
                fillColor: "#111111",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 2,
                scale: 1,
              },
              label: { text: "P", color: "#fff", fontSize: "11px", fontWeight: "700" },
              title: p.nombre || `Parada ${i + 1}`,
            });
            marker.addListener("click", () => setParadaSeleccionada(p));
          }
        });
        // Marcadores de fotos/videos con thumbnail circular, ENCIMA de las paradas.
        // Se anclan flotando arriba-derecha del punto exacto y se abanican cuando
        // hay varias cerca, así no se tapan entre sí ni pisan el pin de parada.
        const ocupados = []; // posiciones ya usadas, para detectar fotos en el mismo lugar
        fotos.forEach((f, i) => {
          if (f.lat && f.lng && f.url) {
            const isVideo = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url);
            // Para videos, Cloudinary genera thumbnail con .jpg
            let thumbUrl;
            if (isVideo) {
              thumbUrl = f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/").replace(/\.(mp4|mov|webm)$/i, ".jpg");
            } else {
              thumbUrl = f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/");
            }
            // Abanicar fotos que caen casi en el mismo punto
            const cercanos = ocupados.filter(o => Math.abs(o.lat - f.lat) < 0.0004 && Math.abs(o.lng - f.lng) < 0.0004).length;
            ocupados.push({ lat: f.lat, lng: f.lng });
            dibujadasRef.current.add(f.url);
            const marker = new window.google.maps.Marker({
              position: { lat: f.lat, lng: f.lng },
              map: mapInstanceRef.current,
              zIndex: 1000 + i,
              icon: {
                url: thumbUrl,
                scaledSize: new window.google.maps.Size(38, 38),
                // ancla abajo-izquierda → la miniatura flota arriba-derecha del punto;
                // cada foto cercana se corre un poco a la derecha
                anchor: new window.google.maps.Point(2 - cercanos * 22, 42),
              },
              title: isVideo ? `Video ${i + 1}` : `Foto ${i + 1}`,
            });
            marker.addListener("click", () => setFotoFullscreen(f));
          }
        });
      }
    });
  }, [puntos, fotos, paradasTraza]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 300, display: "flex", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div style={{ width: "100%", maxWidth: 430, background: C.card, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(var(--sat) + 14px) 16px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, zIndex: 2 }}>
          <button onClick={onCerrar} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={18} strokeWidth={2} />
          </button>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text, textAlign: "center", flex: 1, padding: "0 8px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{traza.titulo || "Recorrido"}</div>
          <button onClick={onEliminar} style={{ width: 32, height: 32, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={14} color={C.red} strokeWidth={2} />
          </button>
        </div>

        {/* mapa */}
        <div style={{ position: "relative", width: "100%" }}>
          <div ref={mapRef} style={{ width: "100%", height: 420, background: "#f6f1eb" }} />
          {/* Botón expandir a pantalla completa */}
          <button onClick={() => setMapaFullscreen(true)}
            style={{
              position: "absolute", top: 12, right: 12,
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(255,255,255,.95)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,.2)",
              fontSize: 16,
            }}
            title="Ver en pantalla completa">
            ⛶
          </button>
        </div>

        {/* Modal mapa fullscreen */}
        {mapaFullscreen && (
          <MapaFullscreen
            puntos={puntos}
            fotos={fotos}
            titulo={traza.titulo || "Recorrido"}
            onCerrar={() => setMapaFullscreen(false)}
          />
        )}

        {siguiendoTrack && (
          <SeguirTrack
            traza={traza}
            onCerrar={() => setSiguiendoTrack(false)}
          />
        )}

        {/* info */}
        <div style={{ padding: 18 }}>
          {/* validación de la comunidad sobre este camino */}
          <div style={{ marginBottom: 10 }}>
            <RidersEnCamino puntos={puntos} />
          </div>
          {editandoTitulo ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <input autoFocus value={nuevoTitulo} onChange={e => setNuevoTitulo(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && nuevoTitulo.trim()) { onRenombrar(nuevoTitulo.trim()); setEditandoTitulo(false); } }}
                placeholder="Nombre del recorrido"
                style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, color: C.text, padding: "8px 10px", borderRadius: 10, border: `1.5px solid ${C.border}`, outline: "none" }} />
              <button onClick={() => { if (nuevoTitulo.trim()) { onRenombrar(nuevoTitulo.trim()); } setEditandoTitulo(false); }}
                style={{ padding: "9px 14px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                OK
              </button>
            </div>
          ) : (
            <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.2, marginBottom: 4 }}>
              {traza.titulo || "Sin título"}
              {onRenombrar && (
                <button onClick={() => { setNuevoTitulo(traza.titulo || ""); setEditandoTitulo(true); }}
                  style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14, marginLeft: 8, padding: 2, verticalAlign: "middle" }}
                  title="Cambiar el nombre">
                  ✏️
                </button>
              )}
            </h2>
          )}
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
            {traza.iniciada_en && new Date(traza.iniciada_en).toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>

          {traza.descripcion && (
            <div style={{ padding: 12, background: C.surface, borderRadius: 10, fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: 14 }}>{traza.descripcion}</div>
          )}

          {/* stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
            <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{kmTraza(traza) ? kmTraza(traza).toFixed(1) : "—"}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Km recorridos</div>
            </div>
            <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{traza.duracion_seg ? formatDur(traza.duracion_seg) : "—"}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Duración</div>
            </div>
            <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{traza.velocidad_prom ? Math.round(traza.velocidad_prom) : "—"} <span style={{ fontSize: 11, color: C.muted }}>km/h</span></div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Vel. promedio</div>
            </div>
            <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text }}>{traza.velocidad_max ? Math.round(traza.velocidad_max) : "—"} <span style={{ fontSize: 11, color: C.muted }}>km/h</span></div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Vel. máxima</div>
            </div>
          </div>

          {/* 🎬 Video del recorrido: la rodada se revive sola, lista para stories */}
          {(traza.puntos || []).length > 1 && (
            <button onClick={() => setShowVideo(true)}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, marginBottom: 8,
                background: C.accent, color: "#F9F5F2", border: "1px solid #8A6D3B55",
                cursor: "pointer", fontSize: 14, fontWeight: 800, letterSpacing: "-0.005em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
              🎬 Video del recorrido
            </button>
          )}
          {showVideo && <VideoRecorridoModal traza={traza} onClose={() => setShowVideo(false)} />}

          {/* Llevártelo a donde quieras: Wikiloc, Calimoto, el Garmin. Que se pueda
              sacar es parte de que se pueda confiar. */}
          {(traza.puntos || []).length > 1 && (
            <button onClick={() => bajarGPX(traza.titulo || "Recorrido", traza.puntos)}
              style={{
                width: "100%", padding: "11px", borderRadius: 12, marginBottom: 16,
                background: C.surface, color: C.text, border: `1px solid ${C.border}`,
                cursor: "pointer", fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
              <Download size={14} strokeWidth={2.2} /> Descargar en GPX
            </button>
          )}

          {/* Botones de acción */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {/* Seguir track dentro de Olbos */}
            <button onClick={() => {
              const puntos = traza.puntos || [];
              if (puntos.length < 2) { alert("Esta ruta no tiene suficientes puntos"); return; }
              setSiguiendoTrack(true);
            }}
            style={{
              flex: 1, padding: "12px", borderRadius: 12,
              background: C.accent, color: "#fff",
              border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              🗺️ Seguir track
            </button>
            {/* Compartir por WhatsApp con link público */}
            {traza.publica && (
              <button onClick={() => {
                const distancia = kmTraza(traza) ? `${kmTraza(traza).toFixed(1)} km` : "";
                const titulo = traza.titulo || "Recorrido";
                // Usar el origin actual (dominio primario, sin redirect apex→www) para que
                // WhatsApp lea bien los OG tags. El redirect 307 del apex rompe el preview.
                const url = `${SITE_URL}/ruta/${traza.id}`;
                compartirNativo({ texto: `🏍️ Mirá este recorrido: ${titulo}${distancia ? ` (${distancia})` : ""} en Olbos`, url, titulo });
              }}
              style={{
                flex: 1, padding: "12px", borderRadius: 12,
                background: "#25D366", color: "#fff",
                border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                📤 Compartir
              </button>
            )}
          </div>

          {/* Compartir en el feed (publicar como post). Si ya está, lo muestra. */}
          {yaEnFeed === true ? (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "12px", borderRadius: 12, marginBottom: 12,
              background: C.surface, color: C.muted,
              fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
            }}>
              ✓ Ya está publicado en el feed
            </div>
          ) : (
            <button onClick={compartirEnFeed} disabled={compartiendoFeed || yaEnFeed === null}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, marginBottom: 12,
                background: C.accent, color: "#fff", border: "none",
                cursor: (compartiendoFeed || yaEnFeed === null) ? "default" : "pointer",
                opacity: (compartiendoFeed || yaEnFeed === null) ? 0.6 : 1,
                fontSize: 14, fontWeight: 600, letterSpacing: "-0.005em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
              {compartiendoFeed ? "Publicando…" : "📣 Compartir en el feed"}
            </button>
          )}

          {/* 🛣️ Al catálogo de Rutas: el recorrido grabado como ruta publicada, con
              el trazado real precargado — el catálogo crece del uso (31-ago) */}
          {(traza.puntos || []).length > 1 && (
            <button onClick={() => setShowPublicarRuta(true)}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, marginBottom: 12,
                background: C.card, color: C.text, border: `1px solid ${C.border}`,
                cursor: "pointer", fontSize: 14, fontWeight: 700, letterSpacing: "-0.005em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              }}>
              🛣️ Publicar como ruta del catálogo
            </button>
          )}
          {showPublicarRuta && <PublicarRecorridoModal traza={traza} onClose={() => setShowPublicarRuta(false)} />}

          {/* Transferir propiedad del recorrido (autor o admin) */}
          <BotonTransferir tipo="traza" objetoId={traza.id} autorId={traza.user_id} onListo={onCerrar} />

          {/* Métricas de visitas (solo el autor/admin las recibe del RPC) */}
          {visitas && (
            <div style={{ marginBottom: 16, padding: 14, background: "#f6f1eb", border: "1px solid #D6E8FB", borderRadius: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
                📈 Visitas al link
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: (Array.isArray(visitas.por_dia) && visitas.por_dia.length > 0) ? 12 : 0 }}>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>{visitas.total || 0}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Visitas</div>
                </div>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>{visitas.unicas || 0}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Únicas aprox.</div>
                </div>
              </div>
              {Array.isArray(visitas.por_dia) && visitas.por_dia.length > 0 ? (() => {
                const dias = [...visitas.por_dia].sort((a, b) => (a.dia < b.dia ? -1 : 1));
                const max = Math.max(...dias.map(d => d.visitas), 1);
                return (
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 60 }}>
                    {dias.map((d, i) => (
                      <div key={i} title={`${d.dia}: ${d.visitas} visita${d.visitas !== 1 ? "s" : ""}`}
                        style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", minWidth: 0 }}>
                        <div style={{ fontSize: 8, color: C.text, fontWeight: 700, marginBottom: 2 }}>{d.visitas}</div>
                        <div style={{ width: "100%", maxWidth: 18, height: `${Math.max(6, (d.visitas / max) * 100)}%`, background: "#0A6CE0", borderRadius: "3px 3px 0 0" }} />
                        <div style={{ fontSize: 7.5, color: C.muted, marginTop: 3, whiteSpace: "nowrap" }}>{d.dia.slice(8, 10)}/{d.dia.slice(5, 7)}</div>
                      </div>
                    ))}
                  </div>
                );
              })() : (
                <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: "4px 0" }}>
                  Todavía nadie abrió el link. Compartilo para empezar a medir.
                </div>
              )}
              {visitas.bots > 0 && (
                <div style={{ fontSize: 10, color: C.muted, marginTop: 10, textAlign: "center" }}>
                  +{visitas.bots} vista{visitas.bots !== 1 ? "s" : ""} de previews de apps (no contadas)
                </div>
              )}
            </div>
          )}

          {/* fotos (las de un punto también están en el mapa; las generales, solo acá) */}
          {(fotos.length > 0 || esMiTraza) && (
            <div style={{ marginBottom: 16 }}>
              {fotos.length > 0 && <>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Fotos del recorrido ({fotos.length})</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {fotos.map((f, i) => {
                    const esVid = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url || "");
                    return (
                      <div key={i} onClick={() => setFotoFullscreen(f)} style={{ position: "relative", cursor: "pointer" }}>
                        <img loading="lazy" decoding="async" src={esVid ? (cldPoster(f.url, 300) || f.url) : cldThumb(f.url, 300)} alt=""
                          style={{ width: "100%", aspectRatio: "1", borderRadius: 8, objectFit: "cover", display: "block", background: C.surface }} />
                        {esVid && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Play size={18} color="#fff" fill="#fff" /></span>}
                      </div>
                    );
                  })}
                </div>
              </>}
              {esMiTraza && (
                <>
                  <input ref={inputAgregarRef} type="file" accept="image/*,video/*" multiple style={{ display: "none" }} onChange={elegirArchivos} />
                  <button onClick={() => inputAgregarRef.current && inputAgregarRef.current.click()}
                    style={{ width: "100%", marginTop: fotos.length ? 10 : 0, padding: "12px", borderRadius: 12, background: C.card, color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 13.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                    <Camera size={15} strokeWidth={2} /> Agregar fotos o videos
                  </button>
                  {fotos.length === 0 && <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", marginTop: 6 }}>Las que te pasaron los demás también: las ubicás en el recorrido.</div>}
                </>
              )}
            </div>
          )}
          {archivosNuevos && (
            <AgregarFotosTraza traza={{ ...traza, fotos }} archivos={archivosNuevos} miId={miIdTraza}
              onCerrar={() => setArchivosNuevos(null)}
              onListo={(nuevas) => { setArchivosNuevos(null); setFotosLocal(nuevas); if (onFotosActualizadas) onFotosActualizadas(nuevas); }} />
          )}

          {/* toggle público */}
          <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`, cursor: "pointer", marginBottom: 10, background: traza.publica ? "#27AE600C" : C.card }}>
            <input type="checkbox" checked={!!traza.publica} onChange={onTogglePublica}
              style={{ width: 20, height: 20, accentColor: "#27AE60" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{traza.publica ? "Pública" : "Privada"}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{traza.publica ? "Otros riders pueden ver este recorrido" : "Solo vos podés ver este recorrido"}</div>
            </div>
          </label>
        </div>

        {fotoFullscreen && (
          <div onClick={() => setFotoFullscreen(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.94)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <button onClick={() => setFotoFullscreen(null)} style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={20} color="#fff" />
            </button>
            {(fotoFullscreen.type === "video" || /\.(mp4|mov|webm)$/i.test(fotoFullscreen.url)) ? (
              <video src={cldOpt(fotoFullscreen.url, 1080)} poster={cldPoster(fotoFullscreen.url, 480)} controls autoPlay style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16 }} onClick={e => e.stopPropagation()} />
            ) : (
              <ImgZoom src={fotoFullscreen.url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16 }} />
            )}
          </div>
        )}

        {/* Popup de parada: navegar con Waze o Google Maps */}
        {paradaSeleccionada && (
          <div onClick={() => setParadaSeleccionada(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div onClick={e => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 430,
                background: C.card, borderRadius: "20px 20px 0 0",
                padding: "20px 18px calc(20px + env(safe-area-inset-bottom))",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", background: "#11111115",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                }}>📍</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
                    {paradaSeleccionada.nombre || "Parada"}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    {paradaSeleccionada.lat.toFixed(5)}, {paradaSeleccionada.lng.toFixed(5)}
                  </div>
                </div>
                <button onClick={() => setParadaSeleccionada(null)}
                  style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={15} color="#111111" />
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={() => { navegarConOlbos(paradaSeleccionada.lat, paradaSeleccionada.lng, paradaSeleccionada.nombre || "la parada"); setParadaSeleccionada(null); }}
                  style={{
                    flex: 1, padding: "13px", borderRadius: 12,
                    background: C.accent, color: "#fff",
                    border: "none", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 700, letterSpacing: "-0.005em",
                  }}>
                  🏍️ Con Olbos
                </button>
                <button onClick={() => window.open(`${WAZE_BASE}ll=${paradaSeleccionada.lat},${paradaSeleccionada.lng}&navigate=yes`, "_blank")}
                  style={{
                    flex: 1, padding: "13px", borderRadius: 12,
                    background: "#33CCFF", color: "#fff",
                    border: "none", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.005em",
                  }}>
                  Waze
                </button>
                <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${paradaSeleccionada.lat},${paradaSeleccionada.lng}`, "_blank")}
                  style={{
                    flex: 1, padding: "13px", borderRadius: 12,
                    background: "#1F8347", color: "#fff",
                    border: "none", cursor: "pointer",
                    fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.005em",
                  }}>
                  Google Maps
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── POLYLINE → PUNTOS ─────────────────────────────────────────────────────
// Las rutas publicadas guardan el trazado real como polyline codificada de
// Google. Para poder recorrerlas con el mismo motor que los tracks grabados
// hay que volver a la lista de puntos. Se decodifica a mano a propósito: el SDK
// se carga solo con `places`, y sumar la librería `geometry` afectaría la carga
// de toda la app por una función de quince líneas.
// ¿Cuántos riders distintos pasaron por este camino? Chip que consulta la red
// anónima de tracks (red_tracks + PostGIS) con una muestra de puntos del camino.
// No es lo mismo un camino que hizo una sola persona que uno validado por cientos.
const RidersEnCamino = ({ puntos }) => {
  const [n, setN] = useState(null); // null = sin consultar, {riders, pasadas} = resultado
  const [cargando, setCargando] = useState(false);
  const coords = (puntos || []).filter(p => p && p.lat && p.lng);
  if (coords.length < 2) return null;
  const consultar = async (e) => {
    e.stopPropagation();
    if (cargando || n !== null) return;
    setCargando(true);
    // Muestra de hasta 30 puntos repartidos a lo largo del camino
    const paso = Math.max(1, Math.floor(coords.length / 30));
    const muestra = coords.filter((_, i) => i % paso === 0).slice(0, 30).map(p => [p.lng, p.lat]);
    const { data: raw } = await supabase.rpc("riders_en_camino", { p_coords: muestra });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setCargando(false);
    setN(r || { riders: 0, pasadas: 0 });
  };
  return (
    <button onClick={consultar}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 100, background: n && n.riders > 0 ? "#111111" : C.surface, color: n && n.riders > 0 ? "#F9F5F2" : C.text, border: `1px solid ${n && n.riders > 0 ? "#111111" : C.border}`, fontSize: 12, fontWeight: 700, cursor: n === null ? "pointer" : "default" }}>
      🏍️ {cargando ? "Contando…"
        : n === null ? "¿Cuántos pasaron por acá?"
        : n.riders === 0 ? "Sos de los primeros en registrarlo"
        : `${n.riders} rider${n.riders !== 1 ? "s" : ""} ya lo recorrieron${n.pasadas > n.riders ? ` (${n.pasadas} pasadas)` : ""}`}
    </button>
  );
};

// ── Recolección pasiva para la red de caminos ────────────────────────────────
// Cualquier tramo andado con el mapa abierto (grabando o no) aporta su camino
// ANÓNIMO a la red — salvo que el usuario lo apague en su perfil (aporta_red).
// Mismas reglas que el aporte de grabaciones: 500 m recortados de cada punta,
// mínimo 2 km, sin identidad (solo el hash irreversible del RPC).
let _aportaRedCache = null;
// Switch del perfil: aportar (o no) mis caminos anónimos a la red de la comunidad
const AjusteAporteRed = () => {
  const [valor, setValor] = useState(null); // null = cargando
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from("perfiles").select("aporta_red").eq("id", user.id).maybeSingle();
        if (vivo) setValor(data ? data.aporta_red !== false : true);
      } catch (e) { if (vivo) setValor(true); }
    })();
    return () => { vivo = false; };
  }, []);
  const cambiar = async () => {
    const nuevo = !valor;
    setValor(nuevo);
    _aportaRedCache = nuevo;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) supabase.from("perfiles").update({ aporta_red: nuevo }).eq("id", user.id).then(() => {}, () => {});
    } catch (e) { /* */ }
  };
  if (valor === null) return null;
  return (
    <div style={{ marginTop: 14, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>Aportar al mapa motero</div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Tus caminos suman, de forma <strong>anónima</strong>, a la red de rutas y caminos de la comunidad. Nunca incluye quién sos ni los tramos cerca de tu punto de partida.
        </div>
      </div>
      <button onClick={cambiar}
        style={{ width: 48, height: 28, borderRadius: 100, border: "none", cursor: "pointer", flexShrink: 0, background: valor ? "#2F7D4F" : C.border, position: "relative", transition: "background .15s" }}>
        <span style={{ position: "absolute", top: 3, left: valor ? 23 : 3, width: 22, height: 22, borderRadius: "50%", background: C.card, transition: "left .15s", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }} />
      </button>
    </div>
  );
};

// Interruptor de la detección de caídas. Apagado por default a propósito: prenderle
// a alguien un detector que le puede sonar solo en medio de la ruta sin que lo haya
// pedido está mal, y encima nos cubre.
let _detectarCaidasCache = null;
const AjusteDetectarCaidas = () => {
  const [valor, setValor] = useState(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase.from("perfiles").select("detectar_caidas").eq("id", user.id).maybeSingle();
        if (vivo) { const v = !!(data && data.detectar_caidas); setValor(v); _detectarCaidasCache = v; }
      } catch (e) { if (vivo) setValor(false); }
    })();
    return () => { vivo = false; };
  }, []);
  const cambiar = async () => {
    const nuevo = !valor;
    setValor(nuevo);
    _detectarCaidasCache = nuevo;
    try { localStorage.setItem("olbos_detectar_caidas", nuevo ? "1" : "0"); } catch (e) { /* */ }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) supabase.from("perfiles").update({ detectar_caidas: nuevo }).eq("id", user.id).then(() => {}, () => {});
    } catch (e) { /* */ }
  };
  if (valor === null) return null;
  return (
    <div style={{ marginTop: 14, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>Detectar caídas</div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Mientras grabás, si hay un golpe fuerte y después quedás quieto, la app te pregunta si estás bien. Si no contestás en 30 segundos, <strong>avisa a los de tu rodada</strong> con tu posición. Olbos no es un servicio de emergencias: ante una emergencia real, llamá al 911.
        </div>
      </div>
      <button onClick={cambiar}
        style={{ width: 48, height: 28, borderRadius: 100, border: "none", cursor: "pointer", flexShrink: 0, background: valor ? "#2F7D4F" : C.border, position: "relative", transition: "background .15s" }}>
        <span style={{ position: "absolute", top: 3, left: valor ? 23 : 3, width: 22, height: 22, borderRadius: "50%", background: C.card, transition: "left .15s", boxShadow: "0 1px 3px rgba(0,0,0,.25)" }} />
      </button>
    </div>
  );
};

const puedeAportarRed = async () => {
  if (_aportaRedCache !== null) return _aportaRedCache;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase.from("perfiles").select("aporta_red").eq("id", user.id).maybeSingle();
    _aportaRedCache = data ? data.aporta_red !== false : true;
  } catch (e) { _aportaRedCache = true; }
  return _aportaRedCache;
};
const aportarTramoARed = async (pts) => {
  try {
    if (!Array.isArray(pts) || pts.length < 20) return;
    if (!(await puedeAportarRed())) return;
    const recorte = (arr) => { let acc = 0, i = 0; for (; i < arr.length - 1 && acc < 0.5; i++) acc += haversineKm(arr[i], arr[i + 1]); return i; };
    const ini = recorte(pts);
    const fin = pts.length - recorte([...pts].reverse());
    const nucleo = pts.slice(ini, fin);
    if (nucleo.length < 11) return;
    const st = calcEstadisticas(nucleo);
    if ((st.dist || 0) < 2) return;
    const reducido = downsampleRecorrido(nucleo);
    supabase.rpc("aportar_a_red", {
      p_polyline: codificarPolyline(reducido),
      p_coords: reducido.map(p => [p.lng, p.lat]),
      p_puntos: nucleo.length,
      p_km: st.dist,
      p_vel_prom: st.velProm,
      p_vel_max: st.velMax,
      p_region: `${Math.round(nucleo[0].lat)},${Math.round(nucleo[0].lng)}`,
    }).then(() => {}, () => {});
  } catch (e) { /* el aporte jamás rompe nada */ }
};

// Codificador de polyline (formato Google) — el inverso de decodificarPolyline.
// Se usa para el aporte anónimo a la red de caminos.
// Camino DETALLADO de una rodada para el mapa en vivo (documento de Matías, imágenes 2 y 3).
// El polyline guardado en el evento es el "overview" de Google: una línea resumida para ver de
// lejos que, a nivel calle, corta las curvas y se sale del camino. Acá se pide la misma ruta
// (con las paradas del organizador, o si no hay, puntos tomados del propio overview) y se arma
// la línea con la geometría de cada paso. Se cachea en el teléfono por evento y por ruta.
const caminoDetalladoRodada = async (evento, pathBase) => {
  try {
    const firma = String(evento.polyline || "") + JSON.stringify(Array.isArray(evento.puntos) ? evento.puntos.map(p => [p && p.lat, p && p.lng]) : []);
    let h = 0; for (let i = 0; i < firma.length; i++) h = (h * 31 + firma.charCodeAt(i)) | 0;
    const clave = "olbos_plan_det_" + evento.id + "_" + (h >>> 0).toString(36);
    try { const c = localStorage.getItem(clave); if (c) { const p = decodificarPolyline(c); if (p.length > 1) return p; } } catch (e) { /* */ }
    let pts = Array.isArray(evento.puntos) ? evento.puntos.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })) : [];
    if (pts.length < 2 && Array.isArray(pathBase) && pathBase.length > 1) {
      const n = Math.min(25, pathBase.length);
      pts = Array.from({ length: n }, (_, i) => pathBase[Math.round(i * (pathBase.length - 1) / (n - 1))]);
    }
    if (pts.length < 2) return null;
    if (pts.length > 25) { const n = 25; pts = Array.from({ length: n }, (_, i) => pts[Math.round(i * (pts.length - 1) / (n - 1))]); }
    const o = pts[0], d = pts[pts.length - 1];
    const wps = pts.slice(1, -1).map(p => `${p.lat},${p.lng}`).join("|");
    const qs = `origin=${encodeURIComponent(o.lat + "," + o.lng)}&destination=${encodeURIComponent(d.lat + "," + d.lng)}${wps ? "&waypoints=" + encodeURIComponent(wps) : ""}`;
    const res = await fetch(`${API_BASE}/api/directions?${qs}`);
    const data = await res.json();
    const ruta = data && data.routes && data.routes[0];
    if (!ruta || !Array.isArray(ruta.legs)) return null;
    const out = [];
    for (const leg of ruta.legs) for (const st of (leg.steps || [])) {
      const enc = st.polyline && st.polyline.points;
      if (!enc) continue;
      const seg = decodificarPolyline(enc);
      for (const p of seg) { const u = out[out.length - 1]; if (!u || u.lat !== p.lat || u.lng !== p.lng) out.push(p); }
    }
    if (out.length < 2) return null;
    try { localStorage.setItem(clave, codificarPolyline(out)); } catch (e) { /* sin lugar: se pide de nuevo la próxima */ }
    return out;
  } catch (e) { return null; }
};

const codificarPolyline = (puntos) => {
  let lastLat = 0, lastLng = 0, out = "";
  const enc = (v) => {
    v = v < 0 ? ~(v << 1) : v << 1;
    let s = "";
    while (v >= 0x20) { s += String.fromCharCode((0x20 | (v & 0x1f)) + 63); v >>= 5; }
    return s + String.fromCharCode(v + 63);
  };
  (puntos || []).forEach(p => {
    const lat = Math.round(p.lat * 1e5), lng = Math.round(p.lng * 1e5);
    out += enc(lat - lastLat) + enc(lng - lastLng);
    lastLat = lat; lastLng = lng;
  });
  return out;
};

const decodificarPolyline = (str) => {
  if (!str || typeof str !== "string") return [];
  const puntos = [];
  let indice = 0, lat = 0, lng = 0;
  while (indice < str.length) {
    let resultado = 0, shift = 0, byte;
    do { byte = str.charCodeAt(indice++) - 63; resultado |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lat += (resultado & 1) ? ~(resultado >> 1) : (resultado >> 1);
    resultado = 0; shift = 0;
    do { byte = str.charCodeAt(indice++) - 63; resultado |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lng += (resultado & 1) ? ~(resultado >> 1) : (resultado >> 1);
    puntos.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return puntos;
};

// ── REPORTAR UNA RUTA ─────────────────────────────────────────────────────
// Desde que las rutas se publican sin revisión previa, la comunidad es el primer
// filtro. A diferencia de las fichas de Servicios, un reporte NO baja la ruta
// sola: una ruta es el trabajo de alguien y "es peligrosa" muchas veces es
// opinión. Le llega al admin y decide.
const MOTIVOS_REPORTE_RUTA = [
  { id: "no_existe", label: "El camino no existe o está cortado" },
  { id: "peligrosa", label: "Es peligrosa y no lo aclara" },
  { id: "mal_trazada", label: "El trazado está mal" },
  { id: "datos_erroneos", label: "Los datos no coinciden (km, dificultad, superficie)" },
  { id: "copiada", label: "Es copia de otra ruta" },
  { id: "otro", label: "Otra cosa" },
];

const ReportarRuta = ({ ruta }) => {
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [detalle, setDetalle] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const cerrar = () => { setAbierto(false); setMotivo(""); setDetalle(""); setEnviado(false); };

  const enviar = async () => {
    if (!motivo || enviando) return;
    setEnviando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { alert("Para reportar una ruta necesitás iniciar sesión."); setEnviando(false); return; }
      const { error } = await supabase.from("ruta_reportes").insert({
        ruta_id: ruta._supabaseId, user_id: user.id, motivo, detalle: detalle.trim() || null,
      });
      if (error) throw error;
      setEnviado(true);
    } catch (e) {
      alert("No pudimos enviar el reporte. Probá de nuevo en un rato.");
    }
    setEnviando(false);
  };

  if (!ruta?._supabaseId) return null;

  return (
    <>
      <button onClick={() => setAbierto(true)}
        style={{ width: "100%", marginTop: 14, padding: "11px 0", borderRadius: 10, background: "transparent", color: C.muted, fontSize: 12.5, fontWeight: 600, border: `1px solid ${C.border}`, cursor: "pointer" }}>
        ¿Hay algo mal en esta ruta? Reportala
      </button>

      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && cerrar()}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "20px 20px 0 0", padding: "22px 20px calc(28px + var(--sab))", maxHeight: "88vh", overflowY: "auto" }}>
            {enviado ? (
              <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>🙏</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 8 }}>Gracias por avisar</div>
                <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                  Lo revisamos. La ruta sigue publicada mientras tanto: no la bajamos por un reporte sin mirarla.
                </p>
                <button onClick={cerrar}
                  style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: C.accent, color: "#fff", fontSize: 14.5, fontWeight: 700, border: "none", cursor: "pointer" }}>
                  Listo
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 16.5, fontWeight: 800, color: C.text, letterSpacing: "-0.015em" }}>Reportar la ruta</div>
                    <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{ruta.name}</div>
                  </div>
                  <button onClick={cerrar} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                    <X size={14} strokeWidth={2.5} color={C.muted} />
                  </button>
                </div>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: "10px 0 16px" }}>
                  Las rutas las sube la comunidad. Si encontraste algo que no cierra, contanos qué y lo miramos.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 16 }}>
                  {MOTIVOS_REPORTE_RUTA.map(m => (
                    <button key={m.id} onClick={() => setMotivo(m.id)}
                      style={{ width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 11, cursor: "pointer",
                        background: motivo === m.id ? C.text : C.surface,
                        color: motivo === m.id ? "#fff" : C.text,
                        border: `1px solid ${motivo === m.id ? C.text : C.border}`,
                        fontSize: 13.5, fontWeight: motivo === m.id ? 700 : 500 }}>
                      {m.label}
                    </button>
                  ))}
                </div>

                <textarea value={detalle} onChange={e => setDetalle(e.target.value)}
                  placeholder="Contanos qué viste (opcional, pero ayuda un montón)"
                  style={{ width: "100%", minHeight: 74, padding: "12px 14px", borderRadius: 11, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13.5, color: C.text, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 16 }} />

                <button onClick={enviar} disabled={!motivo || enviando}
                  style={{ width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                    background: motivo ? C.accent : C.border, color: motivo ? "#fff" : C.muted,
                    fontSize: 14.5, fontWeight: 700, cursor: motivo && !enviando ? "pointer" : "default" }}>
                  {enviando ? "Enviando…" : "Enviar reporte"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── VIAJES (v1) ─────────────────────────────────────────────────────────────
// El viaje multi-día con amigos: etapas + logística + gastos compartidos.
// Reemplaza al PowerPoint de tramos + la planilla de gastos + el caos del grupo
// de WhatsApp (diseñado sobre el caso real "NOA 2026" de Carlos, ago-2026).
// Dos vistas: la grupal (el plan y la plata de todos) y "Mi viaje" (mi agenda
// y mi bolsillo). Gastos con alcance propio/grupo y división configurable.

const MONEDAS_VIAJE = ["ARS", "USD", "EUR", "CLP", "UYU", "BRL", "BOB", "PEN"];
const CAT_GASTO = [["pasajes", "✈️ Pasajes"], ["alquiler", "🏍️ Alquiler"], ["hospedaje", "🛏️ Hospedaje"], ["comida", "🍖 Comida"], ["nafta", "⛽ Nafta"], ["peajes", "🛣️ Peajes"], ["otro", "💸 Otro"]];
const TIPO_ITEM_VIAJE = [["vuelo", "✈️ Vuelo"], ["alquiler", "🏍️ Alquiler de moto"], ["hospedaje", "🛏️ Hospedaje"], ["traslado", "🚕 Traslado"], ["otro", "📌 Otro"]];
const emojiCatGasto = (c) => (CAT_GASTO.find(([k]) => k === c) || ["", "💸"])[1].split(" ")[0];
const emojiTipoItem = (t) => (TIPO_ITEM_VIAJE.find(([k]) => k === t) || ["", "📌"])[1].split(" ")[0];
const fmtMonto = (m, mon) => `$${Number(m).toLocaleString("es-AR", { maximumFractionDigits: 0 })}${mon && mon !== "ARS" ? ` ${mon}` : ""}`;
const fmtFechaViaje = (f) => {
  if (!f) return "";
  const d = new Date(f + "T12:00:00");
  return d.toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" });
};
const estadoViaje = (v) => {
  const hoy = hoyLocalISO();
  if (!v.fecha_inicio) return ["Planificación", C.muted];
  if (hoy < v.fecha_inicio) return ["Próximo", "#8A6D3B"];
  if (v.fecha_fin && hoy > v.fecha_fin) return ["Finalizado", C.muted];
  return ["En curso", "#2F7D4F"];
};
// Balance por moneda: para cada gasto de grupo, el pagador acredita el total y
// cada integrante de la división debita su parte. Devuelve {moneda: {userId: neto}}
const calcularBalancesViaje = (gastos, miembroIds, pagos = []) => {
  const porMoneda = {};
  for (const g of gastos) {
    if (g.alcance !== "grupo") continue;
    const div = (g.division && g.division.length ? g.division : miembroIds).filter(u => miembroIds.includes(u));
    if (!div.length) continue;
    const neto = porMoneda[g.moneda] = porMoneda[g.moneda] || {};
    const parte = Number(g.monto) / div.length;
    neto[g.pagado_por] = (neto[g.pagado_por] || 0) + Number(g.monto);
    for (const u of div) neto[u] = (neto[u] || 0) - parte;
  }
  // Pagos entre riders ("ya pagué"): quien pagó sube su saldo, quien cobró lo baja
  for (const p of pagos) {
    const neto = porMoneda[p.moneda] = porMoneda[p.moneda] || {};
    neto[p.de_user] = (neto[p.de_user] || 0) + Number(p.monto);
    neto[p.a_user] = (neto[p.a_user] || 0) - Number(p.monto);
  }
  return porMoneda;
};
// Quién le paga a quién para saldar (de a pares, el que más debe con el que más le deben)
const sugerirPagosViaje = (netos) => {
  const deudores = [], acreedores = [];
  for (const [u, v] of Object.entries(netos || {})) { const n = Math.round(v); if (n < 0) deudores.push([u, -n]); else if (n > 0) acreedores.push([u, n]); }
  deudores.sort((a, b) => b[1] - a[1]); acreedores.sort((a, b) => b[1] - a[1]);
  const out = []; let i = 0, j = 0;
  while (i < deudores.length && j < acreedores.length) {
    const m = Math.min(deudores[i][1], acreedores[j][1]);
    out.push({ de: deudores[i][0], a: acreedores[j][0], monto: m });
    deudores[i][1] -= m; acreedores[j][1] -= m;
    if (!deudores[i][1]) i++; if (!acreedores[j][1]) j++;
  }
  return out;
};

const inputViajeStyle = { width: "100%", padding: "11px 13px", borderRadius: 11, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", color: C.text, background: C.card };
const labelViajeStyle = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, display: "block" };
const ChipsViaje = ({ opciones, valor, onSet }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {opciones.map(([k, l]) => (
      <button key={k} onClick={() => onSet(k)} type="button"
        style={{ padding: "8px 12px", borderRadius: 100, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: valor === k ? "#111111" : C.surface, color: valor === k ? "#fff" : C.text, border: `1px solid ${valor === k ? "#111111" : C.border}` }}>
        {l}
      </button>
    ))}
  </div>
);

// Modal crear/editar viaje
const ViajeFormModal = ({ userId, viaje = null, onClose, onListo }) => {
  const [form, setForm] = useState({
    nombre: viaje?.nombre || "",
    descripcion: viaje?.descripcion || "",
    fecha_inicio: viaje?.fecha_inicio || "",
    fecha_fin: viaje?.fecha_fin || "",
    moneda_base: viaje?.moneda_base || "ARS",
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const guardar = async () => {
    if (!form.nombre.trim()) { alert("Ponele un nombre al viaje"); return; }
    setGuardando(true);
    if (viaje) {
      const { error } = await supabase.from("viajes").update({ ...form, updated_at: new Date().toISOString() }).eq("id", viaje.id);
      setGuardando(false);
      if (error) { alert("No se pudo guardar: " + error.message); return; }
    } else {
      const { data, error } = await supabase.from("viajes").insert({ ...form, fecha_inicio: form.fecha_inicio || null, fecha_fin: form.fecha_fin || null, creator_id: userId }).select().single();
      if (error) { setGuardando(false); alert("No se pudo crear: " + error.message); return; }
      // El creador entra como organizador; si falla no rompe el alta
      await supabase.from("viaje_miembros").insert({ viaje_id: data.id, user_id: userId, rol: "organizador" }).then(() => {}, () => {});
      setGuardando(false);
    }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", background: C.bg, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom, 0px))" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 16 }}>{viaje ? "Editar viaje" : "🧳 Nuevo viaje"}</div>
        <label style={labelViajeStyle}>Nombre</label>
        <input value={form.nombre} onChange={e => set("nombre", e.target.value)} placeholder="NOA 2026, Escapada a Tandil…" style={{ ...inputViajeStyle, marginBottom: 14 }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Desde</label>
            <input type="date" value={form.fecha_inicio} onChange={e => set("fecha_inicio", e.target.value)} style={inputViajeStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Hasta</label>
            <input type="date" value={form.fecha_fin} onChange={e => set("fecha_fin", e.target.value)} style={inputViajeStyle} />
          </div>
        </div>
        <label style={labelViajeStyle}>Moneda principal</label>
        <select value={form.moneda_base} onChange={e => set("moneda_base", e.target.value)} style={{ ...inputViajeStyle, marginBottom: 14 }}>
          {MONEDAS_VIAJE.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <label style={labelViajeStyle}>Descripción (opcional)</label>
        <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={2} placeholder="Salta y Jujuy en moto con los pibes" style={{ ...inputViajeStyle, resize: "none", marginBottom: 18 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "13px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>{guardando ? "Guardando…" : viaje ? "Guardar" : "Crear viaje"}</button>
        </div>
      </div>
    </div>
  );
};

// Modal etapa (tramo del viaje)
const EtapaViajeModal = ({ viajeId, userId, etapa = null, onClose, onListo }) => {
  const [form, setForm] = useState({
    titulo: etapa?.titulo || "",
    fecha: etapa?.fecha || "",
    hora_salida: etapa?.hora_salida || "",
    origen: etapa?.origen?.nombre || "",
    destino: etapa?.destino?.nombre || "",
    km: etapa?.km != null ? String(etapa.km) : "",
    superficie: etapa?.superficie || "",
    notas: etapa?.notas || "",
    es_alternativa: !!etapa?.es_alternativa,
  });
  const [guardando, setGuardando] = useState(false);
  // Tramo en el mapa (pedido de Carlos 02-sep): la etapa puede llevar un trazado
  // real asociado — un recorrido grabado propio o una ruta del catálogo
  const [tramo, setTramo] = useState(etapa?.tramo_polyline
    ? { tipo: etapa.tramo_tipo, id: etapa.tramo_id, nombre: etapa.tramo_nombre, polyline: etapa.tramo_polyline, km: etapa.tramo_km }
    : null);
  const teniaTramo = !!etapa?.tramo_polyline;
  const [pickTramo, setPickTramo] = useState(null); // null | "recorridos" | "rutas"
  const [listaPick, setListaPick] = useState(null); // null = cargando
  const [buscaRuta, setBuscaRuta] = useState("");
  const [cargandoTramo, setCargandoTramo] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Los km siguen al tramo armado/elegido mientras el rider no los haya tocado a mano
  const kmTocado = useRef(!!etapa?.km && !etapa?.tramo_polyline);
  const kmDelTramo = (km) => { if (km && !kmTocado.current) set("km", String(km)); };
  const abrirPickRecorridos = async () => {
    setPickTramo("recorridos"); setListaPick(null);
    const { data } = await supabase.from("trazas")
      .select("id, titulo, distancia_km, km_auto, vehiculo, created_at, estado")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(30);
    setListaPick((data || []).filter(t => t.estado !== "grabando" && t.estado !== "pausada"));
  };
  const buscarRutasCat = async (txt) => {
    setBuscaRuta(txt);
    if (txt.trim().length < 2) { setListaPick([]); return; }
    const { data } = await supabase.from("rutas")
      .select("id, nombre, km, polyline, desde, hasta")
      .not("polyline", "is", null)
      .ilike("nombre", `%${txt.trim().replace(/[%,]/g, "")}%`)
      .limit(10);
    setListaPick(data || []);
  };
  const elegirRecorrido = async (t) => {
    if (cargandoTramo) return;
    setCargandoTramo(true);
    const { data } = await supabase.from("trazas").select("puntos, distancia_km, km_auto").eq("id", t.id).single();
    setCargandoTramo(false);
    const pts = (Array.isArray(data?.puntos) ? data.puntos : []).filter(p => p && p.lat && p.lng);
    if (pts.length < 2) { alert("Ese recorrido no tiene puntos GPS."); return; }
    const km = Math.round(kmTraza(data));
    setTramo({ tipo: "recorrido", id: t.id, nombre: t.titulo || "Recorrido", polyline: codificarPolyline(downsampleRecorrido(pts, 300)), km });
    kmDelTramo(km);
    setPickTramo(null); setListaPick(null);
  };
  const elegirRuta = (r) => {
    const km = Math.round(Number(r.km) || 0);
    setTramo({ tipo: "ruta", id: r.id, nombre: r.nombre, polyline: r.polyline, km });
    kmDelTramo(km);
    if (!form.origen.trim() && r.desde?.nombre) set("origen", r.desde.nombre);
    if (!form.destino.trim() && r.hasta?.nombre) set("destino", r.hasta.nombre);
    setPickTramo(null); setListaPick(null); setBuscaRuta("");
  };
  // Tercera fuente (pedido de Carlos): armar el tramo AHÍ MISMO con el creador de
  // rutas de siempre — al confirmar se pide el camino real a Directions, como
  // hacen los creadores de eventos y de rutas
  const [puntosArmar, setPuntosArmar] = useState([]);
  const usarTramoArmado = async () => {
    const conCoords = puntosArmar.filter(p => p.lat && p.lng);
    if (conCoords.length < 2) { alert("Marcá al menos el inicio y el final del tramo."); return; }
    setCargandoTramo(true);
    let poly = null, km = 0;
    try {
      const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
      const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
      const waypoints = conCoords.slice(1, -1).slice(0, 23).map(p => `${p.lat},${p.lng}`).join("|");
      const res = await fetch(`${API_BASE}/api/directions?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`);
      const data = await res.json();
      poly = data?.routes?.[0]?.overview_polyline?.points || data?.polyline || null;
      const legs = data?.routes?.[0]?.legs;
      if (Array.isArray(legs)) km = Math.round(legs.reduce((s, l) => s + (l.distance?.value || 0), 0) / 1000);
    } catch (e) { /* sin Directions: rectas entre los puntos, abajo */ }
    setCargandoTramo(false);
    if (!poly) poly = codificarPolyline(conCoords);
    if (!km) km = distanciaTotal(conCoords);
    const nomIni = conCoords[0].nombre, nomFin = conCoords[conCoords.length - 1].nombre;
    setTramo({ tipo: "armado", id: null, nombre: nomIni && nomFin ? `${nomIni} → ${nomFin}` : "Tramo armado a mano", polyline: poly, km });
    kmDelTramo(km);
    if (!form.origen.trim() && nomIni) set("origen", nomIni);
    if (!form.destino.trim() && nomFin) set("destino", nomFin);
    setPickTramo(null); setPuntosArmar([]);
  };
  const guardar = async () => {
    if (!form.titulo.trim() && !form.destino.trim()) { alert("Poné al menos un título o el destino"); return; }
    setGuardando(true);
    const fila = {
      titulo: form.titulo.trim() || `${form.origen} → ${form.destino}`.trim(),
      fecha: form.fecha || null,
      hora_salida: form.hora_salida || null,
      origen: form.origen.trim() ? { nombre: form.origen.trim() } : null,
      destino: form.destino.trim() ? { nombre: form.destino.trim() } : null,
      km: form.km ? Number(form.km) : null,
      superficie: form.superficie || null,
      notas: form.notas.trim() || null,
      es_alternativa: form.es_alternativa,
    };
    // Las columnas de tramo viajan SOLO si hay (o había) uno: así las etapas sin
    // tramo siguen guardando aunque la migración SQL todavía no haya corrido
    if (tramo || teniaTramo) {
      fila.tramo_tipo = tramo?.tipo || null;
      fila.tramo_id = tramo?.id || null;
      fila.tramo_nombre = tramo?.nombre || null;
      fila.tramo_polyline = tramo?.polyline || null;
      fila.tramo_km = tramo?.km ?? null;
    }
    const { error } = etapa
      ? await supabase.from("viaje_etapas").update(fila).eq("id", etapa.id)
      : await supabase.from("viaje_etapas").insert({ ...fila, viaje_id: viajeId, creado_por: userId });
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", background: C.bg, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom, 0px))" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 16 }}>{etapa ? "Editar etapa" : "🛣️ Nueva etapa"}</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Desde</label>
            <input value={form.origen} onChange={e => set("origen", e.target.value)} placeholder="Salta" style={inputViajeStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Hasta</label>
            <input value={form.destino} onChange={e => set("destino", e.target.value)} placeholder="Cachi" style={inputViajeStyle} />
          </div>
        </div>
        <label style={labelViajeStyle}>Título (opcional — si no, usa Desde → Hasta)</label>
        <input value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Día 1: la cuesta del Obispo" style={{ ...inputViajeStyle, marginBottom: 14 }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1.2 }}>
            <label style={labelViajeStyle}>Fecha</label>
            <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)} style={inputViajeStyle} />
          </div>
          <div style={{ flex: 0.9 }}>
            <label style={labelViajeStyle}>Salida</label>
            <input type="time" value={form.hora_salida} onChange={e => set("hora_salida", e.target.value)} style={inputViajeStyle} />
          </div>
          <div style={{ flex: 0.7 }}>
            <label style={labelViajeStyle}>Km</label>
            <input type="number" inputMode="numeric" value={form.km} onChange={e => { kmTocado.current = true; set("km", e.target.value); }} placeholder="km" style={inputViajeStyle} />
          </div>
        </div>
        <label style={labelViajeStyle}>Camino</label>
        <div style={{ marginBottom: 14 }}>
          <ChipsViaje opciones={[["asfalto", "Asfalto"], ["ripio", "Ripio"], ["mixto", "Mixto"]]} valor={form.superficie} onSet={v => set("superficie", form.superficie === v ? "" : v)} />
        </div>
        <label style={labelViajeStyle}>Tramo en el mapa (opcional)</label>
        {tramo ? (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
            {(() => { try { const p = decodificarPolyline(tramo.polyline); return p.length > 1 ? <MapaPreview puntos={[p[0], p[p.length - 1]]} polyline={tramo.polyline} alto={110} /> : null; } catch (e) { return null; } })()}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: C.card }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {tramo.tipo === "ruta" ? "🗺️" : "🛰️"} {tramo.nombre}{tramo.km ? ` · ${tramo.km} km` : ""}
              </span>
              <button onClick={() => setTramo(null)} style={{ background: "none", border: "none", color: "#B3372C", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, padding: "2px 4px" }}>Quitar</button>
            </div>
          </div>
        ) : pickTramo ? (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.card, padding: "10px 12px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text, flex: 1 }}>{pickTramo === "recorridos" ? "Elegí un recorrido tuyo" : pickTramo === "armar" ? "Armá el tramo en el mapa" : "Buscá una ruta del catálogo"}</span>
              <button onClick={() => { setPickTramo(null); setListaPick(null); setBuscaRuta(""); setPuntosArmar([]); }} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "2px 4px" }}>Cerrar</button>
            </div>
            {pickTramo === "armar" ? (
              <>
                <MapaCreadorRuta puntos={puntosArmar} setPuntos={setPuntosArmar} onKmReal={kmDelTramo} />
                <button onClick={usarTramoArmado} disabled={cargandoTramo || puntosArmar.filter(p => p.lat && p.lng).length < 2}
                  style={{ width: "100%", padding: "11px 0", borderRadius: 10, background: puntosArmar.filter(p => p.lat && p.lng).length < 2 ? C.surface : C.accent, border: `1px solid ${C.border}`, color: puntosArmar.filter(p => p.lat && p.lng).length < 2 ? C.muted : "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", marginTop: 8 }}>
                  {cargandoTramo ? "Trazando el camino…" : "✓ Usar este tramo"}
                </button>
              </>
            ) : (
            <>
            {pickTramo === "rutas" && (
              <input value={buscaRuta} onChange={e => buscarRutasCat(e.target.value)} placeholder="Junín – Los Toldos" style={{ ...inputViajeStyle, marginBottom: 8 }} autoFocus />
            )}
            {listaPick === null ? (
              <div style={{ fontSize: 12.5, color: C.muted, padding: "6px 0" }}>Cargando…</div>
            ) : listaPick.length === 0 ? (
              <div style={{ fontSize: 12.5, color: C.muted, padding: "6px 0" }}>{pickTramo === "recorridos" ? "No tenés recorridos grabados todavía." : buscaRuta.trim().length < 2 ? "Escribí parte del nombre de la ruta." : "No encontramos rutas con ese nombre."}</div>
            ) : (
              <div style={{ maxHeight: 190, overflowY: "auto" }}>
                {listaPick.map(x => (
                  <button key={x.id} onClick={() => pickTramo === "recorridos" ? elegirRecorrido(x) : elegirRuta(x)} disabled={cargandoTramo}
                    style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, padding: "9px 2px", cursor: "pointer" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{pickTramo === "recorridos" ? (x.titulo || "Recorrido sin título") : x.nombre}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {pickTramo === "recorridos"
                        ? [kmTraza(x) ? `${Math.round(kmTraza(x))} km${enAuto(x) ? " en auto" : ""}` : null, x.created_at ? String(x.created_at).slice(0, 10) : null].filter(Boolean).join(" · ")
                        : [x.km ? `${Math.round(Number(x.km))} km` : null, x.desde?.nombre && x.hasta?.nombre ? `${x.desde.nombre} → ${x.hasta.nombre}` : null].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {cargandoTramo && <div style={{ fontSize: 12, color: C.muted, paddingTop: 6 }}>Trayendo el trazado…</div>}
            </>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            <button onClick={abrirPickRecorridos} style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>🛰️ Mis recorridos</button>
            <button onClick={() => { setPickTramo("rutas"); setListaPick([]); }} style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>🗺️ Del catálogo</button>
            <button onClick={() => { setPickTramo("armar"); setListaPick(null); }} style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>✏️ Armarlo acá</button>
          </div>
        )}
        <label style={labelViajeStyle}>Notas</label>
        <textarea value={form.notas} onChange={e => set("notas", e.target.value)} rows={2} placeholder="Cargar nafta en Payogasta, no hay más hasta Cachi" style={{ ...inputViajeStyle, resize: "none", marginBottom: 12 }} />
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, cursor: "pointer" }}>
          <input type="checkbox" checked={form.es_alternativa} onChange={e => set("es_alternativa", e.target.checked)} style={{ width: 17, height: 17, accentColor: "#111111" }} />
          <span style={{ fontSize: 13, color: C.text }}>Es una alternativa (plan B de otra etapa)</span>
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "13px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>{guardando ? "Guardando…" : "Guardar etapa"}</button>
        </div>
      </div>
    </div>
  );
};

// Modal ítem de logística (vuelo, alquiler, hospedaje…)
const ItemViajeModal = ({ viajeId, userId, item = null, onClose, onListo }) => {
  const [form, setForm] = useState({
    tipo: item?.tipo || "vuelo",
    titulo: item?.titulo || "",
    fecha: item?.fecha || "",
    hora: item?.hora || "",
    estado: item?.estado || "pendiente",
    personal: item ? !!item.user_id : true,
    notas: item?.notas || "",
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const guardar = async () => {
    if (!form.titulo.trim()) { alert("Ponele un título (ej: Vuelo AEP → SLA)"); return; }
    setGuardando(true);
    const fila = {
      tipo: form.tipo, titulo: form.titulo.trim(),
      fecha: form.fecha || null, hora: form.hora || null,
      estado: form.estado, notas: form.notas.trim() || null,
      user_id: form.personal ? userId : null,
    };
    const { error } = item
      ? await supabase.from("viaje_items").update(fila).eq("id", item.id)
      : await supabase.from("viaje_items").insert({ ...fila, viaje_id: viajeId, creado_por: userId });
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", background: C.bg, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom, 0px))" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 16 }}>{item ? "Editar ítem" : "📋 Logística"}</div>
        <div style={{ marginBottom: 14 }}>
          <ChipsViaje opciones={TIPO_ITEM_VIAJE} valor={form.tipo} onSet={v => set("tipo", v)} />
        </div>
        <label style={labelViajeStyle}>Título</label>
        <input value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Vuelo AEP → Salta 8:05 · AR1470" style={{ ...inputViajeStyle, marginBottom: 14 }} />
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1.2 }}>
            <label style={labelViajeStyle}>Fecha</label>
            <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)} style={inputViajeStyle} />
          </div>
          <div style={{ flex: 0.9 }}>
            <label style={labelViajeStyle}>Hora</label>
            <input type="time" value={form.hora} onChange={e => set("hora", e.target.value)} style={inputViajeStyle} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Estado</label>
            <select value={form.estado} onChange={e => set("estado", e.target.value)} style={inputViajeStyle}>
              <option value="pendiente">Pendiente</option>
              <option value="reservado">Reservado</option>
              <option value="pagado">Pagado</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>¿De quién es?</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[[true, "🧍 Mío"], [false, "👥 Del grupo"]].map(([v, l]) => (
                <button key={String(v)} onClick={() => set("personal", v)} type="button"
                  style={{ flex: 1, padding: "10px 4px", borderRadius: 11, fontSize: 12, fontWeight: 700, cursor: "pointer", background: form.personal === v ? "#111111" : C.surface, color: form.personal === v ? "#fff" : C.text, border: `1px solid ${form.personal === v ? "#111111" : C.border}` }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <label style={labelViajeStyle}>Notas</label>
        <textarea value={form.notas} onChange={e => set("notas", e.target.value)} rows={2} placeholder="Código de reserva, dirección…" style={{ ...inputViajeStyle, resize: "none", marginBottom: 18 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "13px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>{guardando ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
};

// Modal gasto: monto → moneda → categoría → propio/grupo → listo
const GastoViajeModal = ({ viaje, miembros, userId, gasto = null, onClose, onListo }) => {
  const [form, setForm] = useState({
    monto: gasto?.monto != null ? String(gasto.monto) : "",
    moneda: gasto?.moneda || viaje.moneda_base || "ARS",
    categoria: gasto?.categoria || "comida",
    alcance: gasto?.alcance || "grupo",
    pagado_por: gasto?.pagado_por || userId,
    fecha: gasto?.fecha || hoyLocalISO(),
    descripcion: gasto?.descripcion || "",
    division: gasto?.division || null, // null = todos
  });
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const idsMiembros = miembros.map(m => m.user_id);
  const divActiva = form.division && form.division.length ? form.division : idsMiembros;
  const toggleDiv = (uid) => {
    const nueva = divActiva.includes(uid) ? divActiva.filter(u => u !== uid) : [...divActiva, uid];
    if (!nueva.length) return; // alguien tiene que quedar
    set("division", nueva.length === idsMiembros.length ? null : nueva);
  };
  const guardar = async () => {
    const monto = Number(form.monto);
    if (!monto || monto <= 0) { alert("Poné el monto"); return; }
    setGuardando(true);
    const fila = {
      monto, moneda: form.moneda, categoria: form.categoria, alcance: form.alcance,
      pagado_por: form.pagado_por, fecha: form.fecha,
      descripcion: form.descripcion.trim() || null,
      division: form.alcance === "grupo" ? form.division : null,
    };
    const { error } = gasto
      ? await supabase.from("viaje_gastos").update(fila).eq("id", gasto.id)
      : await supabase.from("viaje_gastos").insert({ ...fila, viaje_id: viaje.id, creado_por: userId });
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", background: C.bg, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(20px + env(safe-area-inset-bottom, 0px))" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 16 }}>{gasto ? "Editar gasto" : "💸 Nuevo gasto"}</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1.4 }}>
            <label style={labelViajeStyle}>Monto</label>
            <input type="number" inputMode="decimal" value={form.monto} onChange={e => set("monto", e.target.value)} placeholder="45000" autoFocus={!gasto} style={{ ...inputViajeStyle, fontSize: 18, fontWeight: 800 }} />
          </div>
          <div style={{ flex: 0.8 }}>
            <label style={labelViajeStyle}>Moneda</label>
            <select value={form.moneda} onChange={e => set("moneda", e.target.value)} style={{ ...inputViajeStyle, padding: "13px 8px" }}>
              {MONEDAS_VIAJE.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <ChipsViaje opciones={CAT_GASTO} valor={form.categoria} onSet={v => set("categoria", v)} />
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["propio", "🧍 Propio — no se divide"], ["grupo", "👥 Del grupo — se divide"]].map(([v, l]) => (
            <button key={v} onClick={() => set("alcance", v)} type="button"
              style={{ flex: 1, padding: "11px 6px", borderRadius: 11, fontSize: 12.5, fontWeight: 700, cursor: "pointer", background: form.alcance === v ? "#111111" : C.surface, color: form.alcance === v ? "#fff" : C.text, border: `1px solid ${form.alcance === v ? "#111111" : C.border}` }}>
              {l}
            </button>
          ))}
        </div>
        {form.alcance === "grupo" && miembros.length > 1 && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelViajeStyle}>Se divide entre ({divActiva.length})</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {miembros.map(m => (
                <button key={m.user_id} onClick={() => toggleDiv(m.user_id)} type="button"
                  style={{ padding: "7px 11px", borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: "pointer", background: divActiva.includes(m.user_id) ? "#2F7D4F" : C.surface, color: divActiva.includes(m.user_id) ? "#fff" : C.muted, border: `1px solid ${divActiva.includes(m.user_id) ? "#2F7D4F" : C.border}` }}>
                  {divActiva.includes(m.user_id) ? "✓ " : ""}{m.username || "rider"}
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Lo pagó</label>
            <select value={form.pagado_por} onChange={e => set("pagado_por", e.target.value)} style={inputViajeStyle}>
              {miembros.map(m => <option key={m.user_id} value={m.user_id}>{m.user_id === userId ? "Yo" : "@" + (m.username || "rider")}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelViajeStyle}>Fecha</label>
            <input type="date" value={form.fecha} onChange={e => set("fecha", e.target.value)} style={inputViajeStyle} />
          </div>
        </div>
        <label style={labelViajeStyle}>Detalle (opcional)</label>
        <input value={form.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Cena en Purmamarca" style={{ ...inputViajeStyle, marginBottom: 18 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} style={{ flex: 2, padding: "13px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>{guardando ? "Guardando…" : "Guardar gasto"}</button>
        </div>
      </div>
    </div>
  );
};

// Buscador simple de riders para sumar miembros al viaje
const AgregarMiembroViajeModal = ({ viajeId, yaMiembros, onClose, onListo }) => {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from("perfiles").select("id, username, nombre, avatar_url").ilike("username", `%${q.trim()}%`).limit(8);
      setResultados((data || []).filter(p => !yaMiembros.includes(p.id)));
    }, 300);
    return () => clearTimeout(t);
  }, [q, yaMiembros]);
  const agregar = async (p) => {
    const { error } = await supabase.from("viaje_miembros").insert({ viaje_id: viajeId, user_id: p.id });
    if (error) { alert("No se pudo agregar: " + error.message); return; }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 60 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "92%", maxWidth: 400, background: C.bg, borderRadius: 18, padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 12 }}>Sumar rider al viaje</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por usuario…" autoFocus style={{ ...inputViajeStyle, marginBottom: 10 }} />
        {resultados.map(p => (
          <button key={p.id} onClick={() => agregar(p)}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 8px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
            {p.avatar_url ? <img loading="lazy" decoding="async" src={p.avatar_url} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface }} />}
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>@{p.username}</div>
              {p.nombre && <div style={{ fontSize: 11.5, color: C.muted }}>{p.nombre}</div>}
            </div>
            <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 800, color: "#2F7D4F" }}>+ Sumar</div>
          </button>
        ))}
        {q.trim().length >= 2 && !resultados.length && <div style={{ fontSize: 12.5, color: C.muted, padding: "8px 4px" }}>No aparece nadie con ese usuario.</div>}
        <button onClick={onClose} style={{ width: "100%", marginTop: 12, padding: "11px", borderRadius: 11, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
      </div>
    </div>
  );
};

// Vincular el viaje a un grupo de Olbos: habilita el mapa "En vivo" del grupo
// (todos los del viaje se ven en el mapa, con canal de voz) y el chat
const VincularGrupoViajeModal = ({ viajeId, userId, onClose, onListo }) => {
  const [grupos, setGrupos] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("grupo_miembros").select("grupo_id, grupo:grupos(id, nombre)").eq("user_id", userId);
      setGrupos((data || []).map(g => g.grupo).filter(Boolean));
    })();
  }, [userId]);
  const vincular = async (g) => {
    const { error } = await supabase.from("viajes").update({ grupo_id: g.id }).eq("id", viajeId);
    if (error) { alert("No se pudo vincular: " + error.message); return; }
    onListo();
    onClose();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 60 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "92%", maxWidth: 400, background: C.bg, borderRadius: 18, padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>🔗 Vincular a un grupo</div>
        <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
          Con el viaje vinculado a un grupo, tienen el chat y el mapa <strong>En vivo</strong>: durante el viaje se ven todos sobre el mapa, con canal de voz.
        </p>
        {grupos === null && <div style={{ fontSize: 13, color: C.muted, padding: 8 }}>Cargando tus grupos…</div>}
        {grupos && !grupos.length && <div style={{ fontSize: 13, color: C.muted, padding: 8, lineHeight: 1.5 }}>No estás en ningún grupo todavía. Creá uno en la sección Grupos (Más → Grupos) y volvé.</div>}
        {(grupos || []).map(g => (
          <button key={g.id} onClick={() => vincular(g)}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 10px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
            <span style={{ fontSize: 16 }}>👥</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1 }}>{g.nombre}</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#2F7D4F" }}>Vincular</span>
          </button>
        ))}
        <button onClick={onClose} style={{ width: "100%", marginTop: 12, padding: "11px", borderRadius: 11, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
      </div>
    </div>
  );
};

// Detalle del viaje: Plan (etapas + logística) · Gastos · Mi viaje
const ViajeDetalle = ({ viajeInicial, userId, onClose, onCambio }) => {
  const [viaje, setViaje] = useState(viajeInicial);
  const [vista, setVista] = useState("plan"); // plan | gastos | mio
  const [miembros, setMiembros] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [items, setItems] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [pagos, setPagos] = useState([]); // pagos entre riders (tabla viaje_pagos)
  const [modal, setModal] = useState(null); // {tipo, dato}
  const soyCreator = viaje.creator_id === userId;

  const cargar = async () => {
    const [rv, rm, re, ri, rg, rp] = await Promise.all([
      supabase.from("viajes").select("*").eq("id", viaje.id).maybeSingle(),
      supabase.from("viaje_miembros").select("user_id, rol, perfil:perfiles(username, avatar_url)").eq("viaje_id", viaje.id),
      supabase.from("viaje_etapas").select("*").eq("viaje_id", viaje.id).order("fecha", { ascending: true, nullsFirst: false }).order("orden"),
      supabase.from("viaje_items").select("*").eq("viaje_id", viaje.id).order("fecha", { ascending: true, nullsFirst: false }),
      supabase.from("viaje_gastos").select("*").eq("viaje_id", viaje.id).order("fecha", { ascending: false }),
      supabase.from("viaje_pagos").select("*").eq("viaje_id", viaje.id).order("created_at", { ascending: false }),
    ]);
    if (rv.data) setViaje(rv.data);
    setMiembros((rm.data || []).map(m => ({ user_id: m.user_id, rol: m.rol, username: m.perfil?.username, avatar_url: m.perfil?.avatar_url })));
    setEtapas(re.data || []);
    setItems(ri.data || []);
    setGastos(rg.data || []);
    setPagos(rp && !rp.error ? (rp.data || []) : []); // sin la tabla todavía: todo sigue como antes
  };
  useEffect(() => { cargar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nombreDe = (uid) => uid === userId ? "vos" : "@" + (miembros.find(m => m.user_id === uid)?.username || "rider");
  const idsMiembros = miembros.map(m => m.user_id);
  const [estadoTxt, estadoColor] = estadoViaje(viaje);

  // Totales por moneda: del grupo, y "por cabeza" (grupo/n)
  const totalesGrupo = {};
  for (const g of gastos) if (g.alcance === "grupo") totalesGrupo[g.moneda] = (totalesGrupo[g.moneda] || 0) + Number(g.monto);
  // Mi bolsillo: mis propios + mi parte de los grupales en los que estoy
  const misTotales = {};
  for (const g of gastos) {
    if (g.alcance === "propio" && g.pagado_por === userId) misTotales[g.moneda] = (misTotales[g.moneda] || 0) + Number(g.monto);
    if (g.alcance === "grupo") {
      const div = (g.division && g.division.length ? g.division : idsMiembros);
      if (div.includes(userId)) misTotales[g.moneda] = (misTotales[g.moneda] || 0) + Number(g.monto) / div.length;
    }
  }
  const balances = calcularBalancesViaje(gastos, idsMiembros, pagos);
  const marcarPagado = async (de, a, monto, mon) => {
    if (!window.confirm(`¿Confirmás que ${nombreDe(de) === "vos" ? "vos" : nombreDe(de)} le pagó ${fmtMonto(monto, mon)} a ${nombreDe(a) === "vos" ? "vos" : nombreDe(a)}?`)) return;
    const { error } = await supabase.from("viaje_pagos").insert({ viaje_id: viaje.id, de_user: de, a_user: a, monto, moneda: mon, creado_por: userId });
    if (error) { alert("No se pudo registrar el pago: " + error.message); return; }
    cargar();
  };
  const deshacerPago = async (id) => {
    if (!window.confirm("¿Deshacer este pago? La deuda vuelve a aparecer.")) return;
    const { error } = await supabase.from("viaje_pagos").delete().eq("id", id);
    if (error) { alert("No se pudo deshacer: " + error.message); return; }
    cargar();
  };

  // Agenda de "Mi viaje": etapas + ítems grupales + mis ítems, agrupado por fecha
  const agenda = [
    ...etapas.filter(e => !e.es_alternativa).map(e => ({ fecha: e.fecha, hora: e.hora_salida, tipo: "etapa", d: e })),
    ...items.filter(i => !i.user_id || i.user_id === userId).map(i => ({ fecha: i.fecha, hora: i.hora, tipo: "item", d: i })),
  ].sort((a, b) => String(a.fecha || "9999").localeCompare(String(b.fecha || "9999")) || String(a.hora || "99").localeCompare(String(b.hora || "99")));
  const agendaPorDia = [];
  for (const en of agenda) {
    const ult = agendaPorDia[agendaPorDia.length - 1];
    if (ult && ult.fecha === (en.fecha || "sin")) ult.items.push(en);
    else agendaPorDia.push({ fecha: en.fecha || "sin", items: [en] });
  }

  const borrarRegistro = async (tabla, id) => {
    if (!window.confirm("¿Borrar?")) return;
    const { error } = await supabase.from(tabla).delete().eq("id", id);
    if (error) { alert("No se pudo borrar: " + error.message); return; }
    cargar();
  };

  const cardStyle = { background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: "12px 14px", marginBottom: 8 };

  const renderEtapa = (e, i) => (
    <div key={e.id} style={{ ...cardStyle, opacity: e.es_alternativa ? 0.85 : 1, borderStyle: e.es_alternativa ? "dashed" : "solid" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text, flex: 1, minWidth: 0 }}>
          {e.es_alternativa ? "↔️ " : `${i + 1}. `}{e.titulo}
        </div>
        {e.km != null && <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>{Number(e.km)} km</div>}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
        {[e.fecha ? fmtFechaViaje(e.fecha) : null, e.hora_salida ? `salida ${e.hora_salida}` : null, e.superficie || null].filter(Boolean).join(" · ")}
      </div>
      {e.notas && <div style={{ fontSize: 12.5, color: C.text, marginTop: 6, lineHeight: 1.45 }}>{e.notas}</div>}
      {/* Tramo asociado: el trazado real de la etapa (recorrido propio o ruta del catálogo) */}
      {e.tramo_polyline && (() => {
        try {
          const p = decodificarPolyline(e.tramo_polyline);
          if (p.length < 2) return null;
          return (
            <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}`, marginTop: 8 }}>
              <MapaPreview puntos={[p[0], p[p.length - 1]]} polyline={e.tramo_polyline} alto={110} />
              {e.tramo_nombre && <div style={{ fontSize: 11, color: C.muted, padding: "5px 8px", background: C.card }}>{e.tramo_tipo === "ruta" ? "🗺️" : "🛰️"} {e.tramo_nombre}</div>}
            </div>
          );
        } catch (er) { return null; }
      })()}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {(e.tramo_polyline || e.destino?.nombre) && (
          <button onClick={() => {
            // El detalle del viaje (zIndex 9500) tapaba el mapa (350): se cierra primero, como "En vivo"
            onClose();
            // Con tramo asociado se navega al final del trazado (coordenadas reales);
            // sin tramo queda la búsqueda por el nombre del destino, como antes
            if (e.tramo_polyline) {
              try {
                const p = decodificarPolyline(e.tramo_polyline);
                // Sigue el tramo armado (documento de Matías, C2: viaje con ruta y paradas sin rodada)
                if (p.length > 1) { navegarConOlbos(p[p.length - 1].lat, p[p.length - 1].lng, e.destino?.nombre || e.titulo || "el destino de la etapa", paradasDeCamino(p), viaje.grupo_id || null, viaje.id); return; }
              } catch (er) { /* */ }
            }
            if (e.destino?.nombre) navegarConOlbosBusqueda(e.destino.nombre, viaje.grupo_id || null, viaje.id);
          }}
            style={{ padding: "7px 12px", borderRadius: 100, background: C.accent, border: "none", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>🧭 Navegar</button>
        )}
        {(e.creado_por === userId || soyCreator) && (
          <>
            <button onClick={() => setModal({ tipo: "etapa", dato: e })} style={{ padding: "7px 12px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Editar</button>
            <button onClick={() => borrarRegistro("viaje_etapas", e.id)} style={{ padding: "7px 12px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, color: "#B3372C", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Borrar</button>
          </>
        )}
      </div>
    </div>
  );

  const renderItem = (it) => (
    <div key={it.id} style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 17 }}>{emojiTipoItem(it.tipo)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{it.titulo}</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
            {[it.fecha ? fmtFechaViaje(it.fecha) : null, it.hora || null, it.user_id ? (it.user_id === userId ? "mío" : nombreDe(it.user_id)) : "del grupo"].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 100, flexShrink: 0, background: it.estado === "pagado" ? "#2F7D4F18" : it.estado === "reservado" ? "#8A6D3B18" : C.surface, color: it.estado === "pagado" ? "#2F7D4F" : it.estado === "reservado" ? "#8A6D3B" : C.muted }}>
          {it.estado}
        </span>
        {(it.creado_por === userId || it.user_id === userId || soyCreator) && (
          <button onClick={() => setModal({ tipo: "item", dato: it })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.muted, padding: 4 }}>✏️</button>
        )}
      </div>
      {it.notas && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{it.notas}</div>}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: C.bg, overflowY: "auto" }}>
      {/* header oscuro del viaje */}
      <div style={{ background: C.accent, color: "#F9F5F2", padding: "calc(var(--sat) + 14px) 18px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(249,245,242,.12)", border: "none", color: "#F9F5F2", fontSize: 15, cursor: "pointer", flexShrink: 0 }}>←</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🧳 {viaje.nombre}</div>
            <div style={{ fontSize: 12, color: "#bdb6ab", marginTop: 2 }}>
              {[viaje.fecha_inicio ? `${fmtFechaViaje(viaje.fecha_inicio)} → ${fmtFechaViaje(viaje.fecha_fin) || "?"}` : null, `${miembros.length} rider${miembros.length !== 1 ? "s" : ""}`].filter(Boolean).join(" · ")}
              {" · "}<span style={{ color: estadoColor === C.muted ? "#bdb6ab" : estadoColor }}>{estadoTxt}</span>
            </div>
          </div>
          <button onClick={() => { onClose(); window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: viaje.grupo_id ? { grupoId: viaje.grupo_id, grupoEnVivo: true } : { viajeId: viaje.id } })); }}
            style={{ background: "#B3372C", border: "none", borderRadius: 100, color: "#fff", fontSize: 11.5, fontWeight: 800, padding: "8px 12px", cursor: "pointer", flexShrink: 0 }}>🔴 En vivo</button>
          {!viaje.grupo_id && soyCreator && (
            <button onClick={() => setModal({ tipo: "grupo" })}
              style={{ background: "rgba(249,245,242,.12)", border: "none", borderRadius: 100, color: "#F9F5F2", fontSize: 11.5, fontWeight: 700, padding: "8px 12px", cursor: "pointer", flexShrink: 0 }}>🔗 Grupo</button>
          )}
          {soyCreator && (
            <button onClick={() => setModal({ tipo: "viaje", dato: viaje })} style={{ background: "rgba(249,245,242,.12)", border: "none", borderRadius: 100, color: "#F9F5F2", fontSize: 11.5, fontWeight: 700, padding: "8px 12px", cursor: "pointer", flexShrink: 0 }}>Editar</button>
          )}
        </div>
        {/* miembros */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          {miembros.map(m => (
            <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(249,245,242,.1)", borderRadius: 100, padding: "4px 10px 4px 4px" }}>
              {m.avatar_url ? <img loading="lazy" decoding="async" src={m.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(249,245,242,.25)" }} />}
              <span style={{ fontSize: 11.5, fontWeight: 700 }}>{m.user_id === userId ? "Vos" : m.username || "rider"}</span>
            </div>
          ))}
          {soyCreator && (
            <button onClick={() => setModal({ tipo: "miembro" })} style={{ background: "none", border: "1px dashed rgba(249,245,242,.4)", borderRadius: 100, color: "#F9F5F2", fontSize: 11.5, fontWeight: 700, padding: "6px 12px", cursor: "pointer" }}>+ Sumar</button>
          )}
        </div>
      </div>

      {/* tabs */}
      <div style={{ display: "flex", gap: 4, padding: 3, margin: "14px 18px", background: C.surface, borderRadius: 10 }}>
        {[["plan", "🛣️ Plan"], ["gastos", "💸 Gastos"], ["mio", "🧍 Mi viaje"]].map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            style={{ flex: 1, padding: "8px 0", background: vista === k ? C.card : "transparent", border: "none", borderRadius: 8, color: vista === k ? "#111111" : C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: vista === k ? "0 1px 3px rgba(0,0,0,.06)" : "none" }}>
            {l}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 18px 90px" }}>
        {vista === "plan" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Etapas</div>
              <button onClick={() => setModal({ tipo: "etapa" })} style={{ padding: "7px 13px", borderRadius: 100, background: C.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>+ Etapa</button>
            </div>
            {!etapas.length && <div style={{ fontSize: 13, color: C.muted, padding: "14px 4px", lineHeight: 1.5 }}>Todavía no hay etapas. Cargá el recorrido de cada día: de dónde a dónde, los km y las notas del camino.</div>}
            {etapas.filter(e => !e.es_alternativa).map((e, i) => renderEtapa(e, i))}
            {etapas.some(e => e.es_alternativa) && (
              <>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, margin: "14px 0 8px" }}>Alternativas</div>
                {etapas.filter(e => e.es_alternativa).map((e, i) => renderEtapa(e, i))}
              </>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "18px 0 8px" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Logística</div>
              <button onClick={() => setModal({ tipo: "item" })} style={{ padding: "7px 13px", borderRadius: 100, background: C.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>+ Ítem</button>
            </div>
            {!items.length && <div style={{ fontSize: 13, color: C.muted, padding: "6px 4px", lineHeight: 1.5 }}>Vuelos, alquiler de motos, hospedajes: cada cosa con su fecha y estado.</div>}
            {items.map(renderItem)}
          </>
        )}

        {vista === "gastos" && (
          <>
            {/* resumen */}
            <div style={{ background: C.accent, borderRadius: 14, padding: "14px 16px", marginBottom: 12, color: "#F9F5F2" }}>
              {Object.keys(totalesGrupo).length === 0 && <div style={{ fontSize: 13, color: "#bdb6ab" }}>Todavía no hay gastos del grupo. Cargá el primero 👇</div>}
              {Object.entries(totalesGrupo).map(([mon, tot]) => (
                <div key={mon} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                  <div style={{ fontSize: 13, color: "#bdb6ab" }}>Grupo ({mon})</div>
                  <div style={{ fontSize: 17, fontWeight: 800 }}>{fmtMonto(tot, mon)} <span style={{ fontSize: 11.5, color: "#bdb6ab", fontWeight: 600 }}>· {fmtMonto(tot / Math.max(1, miembros.length), mon)} por cabeza</span></div>
                </div>
              ))}
            </div>
            {/* balances */}
            {Object.entries(balances).map(([mon, netos]) => (
              <div key={mon} style={{ ...cardStyle, marginBottom: 12 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Cómo venimos ({mon})</div>
                {miembros.map(m => {
                  const n = Math.round(netos[m.user_id] || 0);
                  if (!n) return <div key={m.user_id} style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{m.user_id === userId ? "Vos" : "@" + (m.username || "rider")} · al día ✓</div>;
                  return (
                    <div key={m.user_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, marginBottom: 4 }}>
                      <span style={{ color: C.text, fontWeight: 600 }}>{m.user_id === userId ? "Vos" : "@" + (m.username || "rider")}</span>
                      <span style={{ fontWeight: 800, color: n > 0 ? "#2F7D4F" : "#B3372C" }}>{n > 0 ? `le deben ${fmtMonto(n, mon)}` : `debe ${fmtMonto(-n, mon)}`}</span>
                    </div>
                  );
                })}
                {/* Saldar: quién le paga a quién; el deudor, el que cobra o el organizador lo marca */}
                {sugerirPagosViaje(netos).map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.text, lineHeight: 1.35 }}>
                      {nombreDe(s.de) === "vos" ? "Vos" : nombreDe(s.de)} → {nombreDe(s.a)} <strong>{fmtMonto(s.monto, mon)}</strong>
                    </div>
                    {(s.de === userId || s.a === userId || soyCreator) && (
                      <button onClick={() => marcarPagado(s.de, s.a, s.monto, mon)}
                        style={{ padding: "7px 11px", borderRadius: 100, background: "#2F7D4F", border: "none", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
                        ✓ {s.de === userId ? "Ya pagué" : "Ya pagó"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {pagos.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: 12 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Pagos entre riders</div>
                {pagos.map(p => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 12.5, color: C.text }}>
                    <span style={{ flex: 1, minWidth: 0 }}>✅ {nombreDe(p.de_user) === "vos" ? "Vos" : nombreDe(p.de_user)} le pagó {fmtMonto(p.monto, p.moneda)} a {nombreDe(p.a_user)} <span style={{ color: C.muted }}>· {fmtFechaViaje(p.fecha)}</span></span>
                    {(p.creado_por === userId || soyCreator) && (
                      <button onClick={() => deshacerPago(p.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11.5, color: C.muted, padding: 4, textDecoration: "underline", flexShrink: 0 }}>Deshacer</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>Todos los gastos</div>
              <button onClick={() => setModal({ tipo: "gasto" })} style={{ padding: "7px 13px", borderRadius: 100, background: C.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>+ Gasto</button>
            </div>
            {gastos.map(g => (
              <div key={g.id} style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ fontSize: 17 }}>{emojiCatGasto(g.categoria)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{g.descripcion || g.categoria}</div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {fmtFechaViaje(g.fecha)} · pagó {nombreDe(g.pagado_por)} · {g.alcance === "propio" ? "propio" : `del grupo${g.division && g.division.length ? ` (÷${g.division.length})` : ""}`}
                    </div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.text, flexShrink: 0 }}>{fmtMonto(g.monto, g.moneda)}</div>
                  {(g.creado_por === userId || g.pagado_por === userId || soyCreator) && (
                    <button onClick={() => setModal({ tipo: "gasto", dato: g })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.muted, padding: 4 }}>✏️</button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {vista === "mio" && (
          <>
            <div style={{ background: C.accent, borderRadius: 14, padding: "14px 16px", marginBottom: 14, color: "#F9F5F2" }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 6 }}>Tu costo del viaje</div>
              {Object.keys(misTotales).length === 0 && <div style={{ fontSize: 13, color: "#bdb6ab" }}>Todavía no tenés gastos: ni propios ni parte de los del grupo.</div>}
              {Object.entries(misTotales).map(([mon, tot]) => (
                <div key={mon} style={{ fontSize: 18, fontWeight: 800, marginBottom: 2 }}>{fmtMonto(tot, mon)}</div>
              ))}
              {Object.entries(balances).map(([mon, netos]) => {
                const n = Math.round(netos[userId] || 0);
                if (!n) return null;
                return <div key={mon} style={{ fontSize: 12.5, color: n > 0 ? "#7fc79b" : "#e0958b", marginTop: 4, fontWeight: 700 }}>{n > 0 ? `Te deben ${fmtMonto(n, mon)}` : `Debés ${fmtMonto(-n, mon)}`} ({mon})</div>;
              })}
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Tu agenda</div>
            {!agendaPorDia.length && <div style={{ fontSize: 13, color: C.muted, padding: "6px 4px" }}>Cuando haya etapas y logística con fecha, acá se arma tu día por día.</div>}
            {agendaPorDia.map(dia => (
              <div key={dia.fecha} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text, marginBottom: 6, textTransform: "capitalize" }}>{dia.fecha === "sin" ? "Sin fecha" : fmtFechaViaje(dia.fecha)}</div>
                {dia.items.map((en, i) => (
                  <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "7px 4px", borderLeft: `2px solid ${C.border}`, marginLeft: 4, paddingLeft: 12 }}>
                    <span style={{ fontSize: 14 }}>{en.tipo === "etapa" ? "🛣️" : emojiTipoItem(en.d.tipo)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{en.tipo === "etapa" ? en.d.titulo : en.d.titulo}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{[en.hora || null, en.tipo === "etapa" && en.d.km != null ? `${Number(en.d.km)} km` : null, en.tipo === "item" && en.d.user_id ? "tuyo" : en.tipo === "item" ? "del grupo" : null].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {modal?.tipo === "viaje" && <ViajeFormModal userId={userId} viaje={modal.dato} onClose={() => setModal(null)} onListo={() => { cargar(); onCambio(); }} />}
      {modal?.tipo === "etapa" && <EtapaViajeModal viajeId={viaje.id} userId={userId} etapa={modal.dato || null} onClose={() => setModal(null)} onListo={cargar} />}
      {modal?.tipo === "item" && <ItemViajeModal viajeId={viaje.id} userId={userId} item={modal.dato || null} onClose={() => setModal(null)} onListo={cargar} />}
      {modal?.tipo === "gasto" && <GastoViajeModal viaje={viaje} miembros={miembros} userId={userId} gasto={modal.dato || null} onClose={() => setModal(null)} onListo={cargar} />}
      {modal?.tipo === "miembro" && <AgregarMiembroViajeModal viajeId={viaje.id} yaMiembros={idsMiembros} onClose={() => setModal(null)} onListo={cargar} />}
      {modal?.tipo === "grupo" && <VincularGrupoViajeModal viajeId={viaje.id} userId={userId} onClose={() => setModal(null)} onListo={cargar} />}
    </div>
  );
};

// "Navegar" desde una etapa: como el destino es texto (v1 sin picker de mapa),
// se abre el navegador del mapa con la búsqueda pre-cargada vía localStorage
const navegarConOlbosBusqueda = (nombre, grupoId = null, viajeId = null) => {
  try { localStorage.setItem("olbos_nav_buscar", nombre); } catch (e) { /* */ }
  window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: grupoId ? { grupoId, grupoEnVivo: true } : viajeId ? { viajeId } : {} }));
};

// Lista de viajes (vista "Viajes" dentro de la pestaña Mapa)
const SeccionViajes = ({ userId }) => {
  const [viajes, setViajes] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [crear, setCrear] = useState(false);
  const cargar = async () => {
    const { data } = await supabase.from("viajes").select("*").order("fecha_inicio", { ascending: true, nullsFirst: false });
    setViajes(data || []);
  };
  useEffect(() => { if (userId) cargar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  if (!userId) return <div style={{ fontSize: 13.5, color: C.muted, padding: "20px 4px", textAlign: "center" }}>Iniciá sesión para planificar viajes con tu grupo.</div>;
  return (
    <>
      <div style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, padding: "16px 16px 6px", marginBottom: 12 }}>
        <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 4 }}>🧳 Tus viajes</div>
        <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 12 }}>
          El plan por etapas, la logística y los gastos del grupo, en un solo lugar. Chau planilla, chau PowerPoint.
        </p>
      </div>
      <button onClick={() => setCrear(true)}
        style={{ width: "100%", padding: "14px", borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", marginBottom: 14 }}>
        + Nuevo viaje
      </button>
      {viajes === null && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 10 }}>Cargando…</div>}
      {viajes && !viajes.length && (
        <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "10px 20px", lineHeight: 1.6 }}>
          Todavía no tenés viajes. Creá el primero y sumá a tu grupo: etapas, vuelos, hospedajes y la caja común.
        </div>
      )}
      {(viajes || []).map(v => {
        const [txt, color] = estadoViaje(v);
        return (
          <button key={v.id} onClick={() => setDetalle(v)}
            style={{ display: "block", width: "100%", textAlign: "left", background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: "14px 15px", marginBottom: 8, cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🧳 {v.nombre}</div>
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 100, background: `${color}18`, color, flexShrink: 0 }}>{txt}</span>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              {v.fecha_inicio ? `${fmtFechaViaje(v.fecha_inicio)} → ${fmtFechaViaje(v.fecha_fin) || "?"}` : "Sin fecha todavía"}
              {v.descripcion ? ` · ${v.descripcion}` : ""}
            </div>
          </button>
        );
      })}
      {crear && <ViajeFormModal userId={userId} onClose={() => setCrear(false)} onListo={cargar} />}
      {detalle && <ViajeDetalle viajeInicial={detalle} userId={userId} onClose={() => { setDetalle(null); cargar(); }} onCambio={cargar} />}
    </>
  );
};

// Los caminos que más pisan los riders alrededor tuyo. No son rutas que alguien se
// sentó a publicar: son los caminos que la gente efectivamente anda, contados por
// cuántos riders distintos pasaron por ahí. Es el único contenido de Olbos que no
// depende de que nadie cargue nada.
const CaminosComunidad = ({ session }) => {
  const [caminos, setCaminos] = useState(undefined); // undefined=cargando, null=sin base
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      // Dónde está el rider, sin molestarlo: el GPS solo si YA dio permiso; si no, la
      // ciudad de su perfil. Si no tenemos ninguna de las dos, no se muestra la sección.
      let centro = null;
      try {
        if (navigator.permissions && navigator.geolocation) {
          const st = await navigator.permissions.query({ name: "geolocation" });
          if (st.state === "granted") {
            centro = await new Promise(res => navigator.geolocation.getCurrentPosition(
              p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { maximumAge: 900000, timeout: 7000 }));
          }
        }
      } catch (e) { /* sin API de permisos (webview): sigue por la ciudad */ }
      if (!centro && session?.user?.id) {
        const { data } = await supabase.from("perfiles").select("ciudad").eq("id", session.user.id).maybeSingle();
        const ciudad = (data && data.ciudad || "").trim();
        if (ciudad) {
          try {
            await loadGoogleMaps();
            centro = await new Promise(res => {
              new window.google.maps.Geocoder().geocode({ address: ciudad, language: "es" }, (r, st) => {
                if (st === "OK" && r && r[0]) { const l = r[0].geometry.location; res({ lat: l.lat(), lng: l.lng() }); }
                else res(null);
              });
            });
          } catch (e) { /* sin geocoder: no se muestra */ }
        }
      }
      if (!vivo) return;
      if (!centro) { setCaminos(null); return; }
      const { data: raw, error } = await supabase.rpc("caminos_de_la_comunidad", { p_lat: centro.lat, p_lng: centro.lng });
      if (!vivo) return;
      if (error) { setCaminos(null); return; }
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setCaminos(Array.isArray(d) ? d : null);
    })();
    return () => { vivo = false; };
  }, [session]);

  if (!caminos || caminos.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>
        Los caminos de la comunidad
      </div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Lo que más se anda cerca tuyo, contado por los recorridos grabados. Nadie los publicó: se armaron solos.
      </div>

      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
        {caminos.map((c, i) => {
          const pts = decodificarPolyline(c.polyline || "");
          if (pts.length < 2) return null;
          const activo = abierto === i;
          return (
            <div key={i} style={{ flexShrink: 0, width: activo ? 300 : 210, background: C.card, border: `1px solid ${activo ? C.text : C.border}`, borderRadius: 14, overflow: "hidden", transition: "width .18s" }}>
              <MapaPreview puntos={pts.map(p => ({ lat: p.lat, lng: p.lng }))} polyline={c.polyline} alto={activo ? 150 : 110} />
              <div style={{ padding: "10px 12px 12px" }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>
                  {Number(c.distancia_km).toLocaleString("es-AR")} km
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                  {c.riders} {Number(c.riders) === 1 ? "rider lo anduvo" : "riders lo anduvieron"}
                  {c.vel_prom ? ` · ${c.vel_prom} km/h prom` : ""}
                </div>
                {(c.tipo_camino || c.estado_camino) && (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 5, display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {c.tipo_camino && <span style={{ padding: "2px 7px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, fontWeight: 700 }}>{c.tipo_camino}</span>}
                    {c.estado_camino && <span style={{ padding: "2px 7px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}` }}>{c.estado_camino}</span>}
                  </div>
                )}
                {activo && c.recomendaciones && (
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 7, lineHeight: 1.45 }}>“{c.recomendaciones}”</div>
                )}
                <button onClick={() => setAbierto(activo ? null : i)}
                  style={{ marginTop: 9, width: "100%", padding: "8px", borderRadius: 9, background: activo ? C.card : C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer" }}>
                  {activo ? "Achicar" : "Ver mejor"}
                </button>
                {activo && (
                  <button onClick={() => {
                    const fin = pts[pts.length - 1];
                    window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { navegarA: { lat: fin.lat, lng: fin.lng, nombre: "Camino de la comunidad" } } }));
                  }}
                    style={{ marginTop: 6, width: "100%", padding: "9px", borderRadius: 9, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                    Ir hasta ahí
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: C.faint, marginTop: 7, lineHeight: 1.45 }}>
        Se arman con los recorridos grabados, sin identidad y recortando 500 m en cada punta, así no se ve de dónde salió nadie.
      </div>
    </div>
  );
};

const Rutas = ({ isAdmin }) => {
  // Abre en las rutas de la comunidad: "Mis recorridos" está vacío para el que
  // recién llega (y sin sesión ni siquiera hay nada que mostrar), así que entrar
  // a Rutas y ver una pantalla en blanco era la peor puerta de entrada posible.
  const [vistaActiva, setVistaActiva] = useState(() => {
    // Un banner del carrusel puede pedir abrir directo una vista (ej. Viajes)
    const v = typeof window !== "undefined" ? window.olbosPendingVistaMapa : null;
    if (v) { window.olbosPendingVistaMapa = null; return v; }
    return "mapa"; // 'mapa' (el navegador) | 'publicas' | 'trazas' | 'viajes'
  });
  const [filtroOrigen, setFiltroOrigen] = useState("publicas"); // dentro de Rutas: 'publicas' | 'grupos'
  // La pestaña Mapa ES el navegador: al entrar (o volver a tocarla) se abre el
  // mapa con el grabador. Si ya hay uno andando, el handler global lo retoma.
  // Antes entrar a la pestaña abría el mapa solo, también en la app: para ver
  // Mis recorridos o Rutas había que salir del mapa primero (Carlos, 18-sep).
  // Ahora la pestaña muestra el selector y el mapa se abre con su botón.
  const [misTrazas, setMisTrazas] = useState([]);
  const [cargandoTrazas, setCargandoTrazas] = useState(false);
  // Sesiones en circuito de mis recorridos (vueltas, mejor tiempo) para el chip 🏁
  const [sesionesCirc, setSesionesCirc] = useState({});
  const [filtroTrazas, setFiltroTrazas] = useState("todos"); // "todos" | "circuitos"
  const nCircuitos = misTrazas.filter(t => sesionesCirc[t.id]).length;
  const trazasVisibles = filtroTrazas === "circuitos" ? misTrazas.filter(t => sesionesCirc[t.id]) : misTrazas;
  useEffect(() => {
    if (!misTrazas.length) return;
    const ids = misTrazas.map(t => t.id);
    supabase.from("sesiones_circuito").select("traza_id, circuito_id, n_vueltas, mejor_seg, vueltas, circuitos(id, nombre, tipo, meta_lat, meta_lng, largo_m, curvas)").in("traza_id", ids)
      .then(({ data }) => { const m = {}; (data || []).forEach(s => { m[s.traza_id] = s; }); setSesionesCirc(m); }, () => {});
  }, [misTrazas]);
  const verSesionCirc = (t, s) => {
    const c = s.circuitos ? { id: s.circuitos.id, nombre: s.circuitos.nombre, tipo: s.circuitos.tipo, meta: { lat: Number(s.circuitos.meta_lat), lng: Number(s.circuitos.meta_lng) }, largo_m: s.circuitos.largo_m, curvas: s.circuitos.curvas || null } : { id: s.circuito_id, nombre: "Circuito", tipo: "pista", meta: null };
    const mejor = (s.vueltas || []).reduce((m, v) => (m == null || v.seg < m.seg) ? v : m, null);
    window.dispatchEvent(new CustomEvent("olbos-ver-sesion-circuito", { detail: { traza: t, sesion: { ...s, mejor_n: mejor ? mejor.n : 1 }, circuito: c } }));
  };
  // El TrackRecorder vive en la RAÍZ de la app (sobrevive a la navegación);
  // desde acá solo se dispara el evento global que lo abre.
  const [grupoIdParaPublicar, setGrupoIdParaPublicar] = useState(null);
  const [grupoNombreParaPublicar, setGrupoNombreParaPublicar] = useState("");
  const [detalleTraza, setDetalleTraza] = useState(null);
  // Cuando el grabador (montado en la raíz) termina, refrescar la lista. Va con
  // ref porque cargarMisTrazas se define más abajo, después de returns
  // condicionales — referenciarla directo acá sería el TDZ de siempre.
  const cargarTrazasRef = useRef(null);
  useEffect(() => {
    const h = () => { if (cargarTrazasRef.current) cargarTrazasRef.current(); };
    window.addEventListener("olbos-trazas-actualizadas", h);
    return () => window.removeEventListener("olbos-trazas-actualizadas", h);
  }, []);

  // Si vienen desde un grupo con un grupoId preseleccionado, abrir TrackRecorder
  useEffect(() => {
    if (window.olbosPendingGrupoPublicarRuta) {
      const gId = window.olbosPendingGrupoPublicarRuta;
      const gNombre = window.olbosPendingGrupoPublicarRutaNombre || "";
      window.olbosPendingGrupoPublicarRuta = null;
      window.olbosPendingGrupoPublicarRutaNombre = null;
      setGrupoIdParaPublicar(gId);
      setGrupoNombreParaPublicar(gNombre);
      setShowPublicar(true);
    }
  }, []);
  const [sel, setSel] = useState(null);  const [routes, setRoutes] = useState(ROUTES);
  const [reviews, setReviews] = useState(ROUTES.map(r => r.reviews));
  const [collabStops, setCollabStops] = useState(ROUTES.map(() => []));
  // Atrás cierra el detalle de la ruta, no la app
  useBackGuard(sel !== null, () => setSel(null));
  const [draft, setDraft] = useState("");
  const [draftRating, setDraftRating] = useState(4);
  const [filterDiff, setFilterDiff] = useState("todas");
  const [ordenRutas, setOrdenRutas] = useState("nuevas"); // nuevas | rating | km
  const [busquedaRutas, setBusquedaRutas] = useState(""); // texto libre: nombre, provincia, origen/destino (pedido de Mati, 27-oct-2026)
  const [recorriendoRuta, setRecorriendoRuta] = useState(null); // ruta publicada convertida a "traza" para SeguirTrack

  // El trazado real viene en la polyline; si no está, se arma con los puntos
  // sueltos (salida, paradas, llegada), que igual sirve para orientarse.
  const puntosParaRecorrer = (r) => {
    const delPolyline = decodificarPolyline(r.polyline);
    if (delPolyline.length >= 2) return delPolyline;
    const sueltos = [];
    if (r.lat_from && r.lng_from) sueltos.push({ lat: r.lat_from, lng: r.lng_from });
    (r.stops || []).forEach(s => { if (s.lat && s.lng) sueltos.push({ lat: s.lat, lng: s.lng }); });
    if (r.lat_to && r.lng_to) sueltos.push({ lat: r.lat_to, lng: r.lng_to });
    return sueltos;
  };
  const [showPublicar, setShowPublicar] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newStop, setNewStop] = useState("");
  const [expandedStop, setExpandedStop] = useState(null);
  const [stopFeed, setStopFeed] = useState({
    "0-1": [
      { user: "NomadaMoto", avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80", txt: "El café de la estación YPF es sorprendentemente bueno. Y tienen medialunas enormes.", t: "Mar 2026", type: "comentario", img: null, likes: 12, comments: 3 },
      { user: "FedeMoto_GS", avatarUrl: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=80&q=80", txt: "Atención: la nafta premium a veces no hay. Verificar antes de salir de BsAs.", t: "Feb 2026", type: "comentario", img: null, likes: 24, comments: 1 },
    ],
    "0-2": [
      { user: "CamiRutera", avatarUrl: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80", txt: "San Luis es excelente para almorzar. Recomiendo el centro, hay varios bodegones.", t: "Ene 2026", type: "foto", img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80", likes: 31, comments: 7 },
    ],
    "1-0": [
      { user: "AndeanRider", avatarUrl: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=80&q=80", txt: "El lago en Angostura al amanecer es de otro mundo. Lleguen temprano, vale la pena.", t: "Nov 2025", type: "foto", img: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80", likes: 56, comments: 12 },
    ],
  });
  const [stopDraft, setStopDraft] = useState({});
  const [stopMedia, setStopMedia] = useState({});

  // ── ESTADO ADMIN ──
  const [editingRoute, setEditingRoute] = useState(null);
  const [editingStop, setEditingStop] = useState(null);
  const [pendingRoutes, setPendingRoutes] = useState([]);
  const [session, setSession] = useState(null);
  const [username, setUsername] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState("");

  // Equipamiento sugerido (wiki por sumatoria)
  const [equipamiento, setEquipamiento] = useState({});  // { rutaSupabaseId: [...sugerencias] }
  const [showSugerirEquip, setShowSugerirEquip] = useState(null); // null o { categoria }
  const [equipDraft, setEquipDraft] = useState("");

  const cargarEquipamientoRuta = async (rutaId) => {
    if (!rutaId) return;
    const { data } = await supabase
      .from("ruta_equipamiento")
      .select("*")
      .eq("ruta_id", rutaId)
      .order("created_at", { ascending: true });
    setEquipamiento(prev => ({ ...prev, [rutaId]: data || [] }));
  };

  const enviarSugerenciaEquip = async (rutaId, categoria) => {
    if (!equipDraft.trim()) return;
    if (!session) { alert("Tenés que estar logueado."); return; }
    if (!username) { alert("Configurá tu username en Mi perfil primero."); return; }
    const { error } = await supabase.from("ruta_equipamiento").insert({
      ruta_id: rutaId,
      categoria,
      texto: equipDraft.trim(),
      user_id: session.user.id,
      username,
    });
    if (error) { alert("Error: " + error.message); return; }
    setEquipDraft("");
    setShowSugerirEquip(null);
    cargarEquipamientoRuta(rutaId);
  };

  const eliminarSugerenciaEquip = async (sugerencia, rutaId) => {
    if (!window.confirm("¿Eliminar esta sugerencia?")) return;
    await supabase.from("ruta_equipamiento").delete().eq("id", sugerencia.id);
    cargarEquipamientoRuta(rutaId);
  };

  // Cargar equipamiento cuando se abre el detalle de una ruta de Supabase
  useEffect(() => {
    if (sel !== null && routes[sel]?._supabaseId) {
      cargarEquipamientoRuta(routes[sel]._supabaseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  // Cargar sesión, username, rutas pendientes y rutas aprobadas desde Supabase
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        supabase.from("perfiles").select("username, avatar_url").eq("id", session.user.id).single()
          .then(({ data }) => {
            if (data) {
              setUsername(data.username);
              setUserAvatar(data.avatar_url || "");
            }
          });
      }
    });
    cargarRutasPendientes();
    cargarRutasAprobadas().then(() => {
      // Si hay una selección pendiente desde un chip de mención en el feed
      if (window.olbosPendingSelect?.tipo === "ruta") {
        const targetId = window.olbosPendingSelect.id;
        window.olbosPendingSelect = null;
        // esperar un tick para que routes esté actualizado
        setTimeout(() => {
          setRoutes(currentRoutes => {
            const idx = currentRoutes.findIndex(r => r._supabaseId === targetId);
            if (idx !== -1) setSel(idx);
            return currentRoutes;
          });
        }, 100);
      }
    });
  }, []);

  const userId = session?.user?.id;

  // Cargar trazas cuando cambia la vista o el usuario
  useEffect(() => {
    if (vistaActiva === "trazas" && userId) {
      (async () => {
        setCargandoTrazas(true);
        const { data } = await supabase.from("trazas")
          .select("*")
          .eq("user_id", userId)
          .or("tipo_camino.is.null,tipo_camino.neq.navegacion") // los km de navegación sin grabar suman pero no se listan
          .order("created_at", { ascending: false });
        setMisTrazas(data || []);
        setCargandoTrazas(false);
      })();
    }
  }, [vistaActiva, userId]);

  const cargarRutasPendientes = async () => {
    const { data } = await supabase
      .from("rutas_pendientes")
      .select("*")
      .eq("estado", "pendiente")
      .order("created_at", { ascending: false });
    if (data) setPendingRoutes(data);
  };

  // Convertir una fila de Supabase al formato interno de routes
  const supabaseToRoute = (r, perfilCreador, reviewsRuta) => ({
    name: r.nombre,
    km: r.km,
    dur: `${Math.ceil(r.km / 80)}hs`,
    diff: r.diff,
    desde: r.desde.nombre,
    hasta: r.hasta.nombre,
    img: r.foto_url || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
    desc: r.desc_texto || "",
    alertas: r.alertas || "",
    camino: r.camino,
    pctOffroad: r.pct_offroad || 0,
    lat_from: r.desde.lat,
    lng_from: r.desde.lng,
    lat_to: r.hasta.lat,
    lng_to: r.hasta.lng,
    creator: {
      user: perfilCreador?.username || (r.username.includes("@") ? r.username.split("@")[0] : r.username),
      avatarUrl: perfilCreador?.avatar_url || avatarInicial(perfilCreador?.username || r.username),
      moto: perfilCreador?.motos?.[0]?.modelo || "—",
      fecha: new Date(r.created_at).toLocaleDateString("es-AR", { month: "short", year: "numeric" }),
    },
    stops: (r.paradas || []).map(p => ({ nombre: p.nombre, desc: "", tipo: p.tipo, lat: p.lat, lng: p.lng, fotos: [] })),
    reviews: (reviewsRuta || []).map(rev => ({
      u: rev._perfil?.username || rev.username,
      m: rev._perfil?.motos?.[0]?.modelo || "—",
      avatarUrl: rev._perfil?.avatar_url || avatarInicial(rev._perfil?.username || rev.username),
      t: rev.texto,
      r: rev.rating,
      d: new Date(rev.created_at).toLocaleDateString("es-AR", { month: "short", year: "numeric" }),
      _supabaseId: rev.id,
    })),
    alerts: r.alertas ? [r.alertas] : [],
    polyline: r.polyline || null,
    _supabaseId: r.id,
    user_id: r.user_id,
  });

  const cargarRutasAprobadas = async () => {
    const { data: rutasData } = await supabase
      .from("rutas")
      .select("*")
      .is("grupo_id", null) // rutas públicas
      .order("created_at", { ascending: false });

    // Las rutas de MIS grupos (para el filtro "Mis grupos" de la pestaña Rutas)
    let rutasGrupo = [];
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (u) {
        const { data: mems } = await supabase.from("grupo_miembros").select("grupo_id").eq("user_id", u.id);
        const gIds = [...new Set((mems || []).map(m => m.grupo_id).filter(Boolean))];
        if (gIds.length) {
          const [{ data: rg }, { data: gs }] = await Promise.all([
            supabase.from("rutas").select("*").in("grupo_id", gIds).order("created_at", { ascending: false }),
            supabase.from("grupos").select("id, nombre").in("id", gIds),
          ]);
          const gNom = {};
          (gs || []).forEach(g => { gNom[g.id] = g.nombre; });
          rutasGrupo = (rg || []).map(r => ({ ...r, _grupoNombre: gNom[r.grupo_id] || "Grupo" }));
        }
      }
    } catch (e) { /* sin sesión o sin grupos: solo públicas */ }

    const todasRutas = [...(rutasData || []), ...rutasGrupo];
    if (todasRutas.length === 0) return;

    // Cargar todos los perfiles de los creadores en un solo query
    const userIds = [...new Set(todasRutas.map(r => r.user_id).filter(Boolean))];
    const { data: perfilesData } = await supabase
      .from("perfiles")
      .select("id, username, avatar_url, motos")
      .in("id", userIds);
    const perfilesMap = {};
    (perfilesData || []).forEach(p => { perfilesMap[p.id] = p; });

    // Cargar todas las reviews de estas rutas
    const rutaIds = todasRutas.map(r => r.id);
    const { data: reviewsData } = await supabase
      .from("ruta_reviews")
      .select("*")
      .in("ruta_id", rutaIds)
      .order("created_at", { ascending: false });

    // Paradas que la comunidad sugirió sobre estas rutas (solo las vigentes)
    const { data: sugData } = await supabase
      .from("ruta_paradas_sugeridas")
      .select("*")
      .in("ruta_id", rutaIds)
      .eq("estado", "sugerida")
      .order("created_at", { ascending: true });

    // Cargar también los perfiles de los autores de reviews y de las sugerencias
    // (pueden ser distintos a los creadores de las rutas)
    const reviewerIds = [...new Set((reviewsData || []).map(r => r.user_id).filter(Boolean))];
    const sugerenteIds = [...new Set((sugData || []).map(s => s.user_id).filter(Boolean))];
    const todosIds = [...new Set([...userIds, ...reviewerIds, ...sugerenteIds])];
    if (todosIds.length > userIds.length) {
      const { data: perfilesExtra } = await supabase
        .from("perfiles")
        .select("id, username, avatar_url, motos")
        .in("id", todosIds);
      (perfilesExtra || []).forEach(p => { perfilesMap[p.id] = p; });
    }

    const reviewsMap = {};
    (reviewsData || []).forEach(rev => {
      if (!reviewsMap[rev.ruta_id]) reviewsMap[rev.ruta_id] = [];
      reviewsMap[rev.ruta_id].push({ ...rev, _perfil: perfilesMap[rev.user_id] });
    });

    const sugMap = {};
    (sugData || []).forEach(s => {
      if (!sugMap[s.ruta_id]) sugMap[s.ruta_id] = [];
      sugMap[s.ruta_id].push({
        id: s.id,
        nombre: s.nombre,
        user_id: s.user_id,
        user: perfilesMap[s.user_id]?.username || "rider",
        avatarUrl: perfilesMap[s.user_id]?.avatar_url || avatarInicial(perfilesMap[s.user_id]?.username),
      });
    });

    const aprobadas = todasRutas.map(r => ({
      ...supabaseToRoute(r, perfilesMap[r.user_id], reviewsMap[r.id]),
      _grupoId: r.grupo_id || null,
      _grupoNombre: r._grupoNombre || null,
    }));
    setRoutes([...ROUTES, ...aprobadas]);
    setReviews([...ROUTES.map(r => r.reviews), ...aprobadas.map(r => r.reviews)]);
    setCollabStops([...ROUTES.map(() => []), ...aprobadas.map(r => sugMap[r._supabaseId] || [])]);
  };

  const [newRuta, setNewRuta] = useState({
    nombre: "", puntos: [], km: "", diff: "", camino: "Asfalto",
    pctOffroad: 0, desc: "", alertas: "",
  });

  // Botón atrás mientras se publica una ruta → cerrar con confirmación, no salir de la app
  useBackGuard(showPublicar, () => {
    if (!newRuta?.puntos?.length || window.confirm("¿Salir sin guardar? Vas a perder los datos cargados.")) setShowPublicar(false);
  });

  const setR = (k, v) => setNewRuta(n => ({ ...n, [k]: v }));

  // Auto-calcular distancia cuando cambian los puntos (en línea recta, estimación)
  useEffect(() => {
    if (newRuta.puntos.length >= 2) {
      const km = distanciaTotal(newRuta.puntos);
      setNewRuta(n => ({ ...n, km: String(km) }));
    } else if (newRuta.puntos.length < 2 && newRuta.km) {
      setNewRuta(n => ({ ...n, km: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRuta.puntos]);

  // Subir ruta a Supabase
  const enviarRuta = async () => {
    if (!session) {
      setErrorSubmit("Tenés que estar logueado para publicar una ruta.");
      return;
    }
    if (!username) {
      setErrorSubmit("Necesitás configurar tu username en Mi perfil antes de publicar una ruta.");
      return;
    }
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorSubmit("");
    try {
      const desde = newRuta.puntos[0];
      const hasta = newRuta.puntos[newRuta.puntos.length - 1];
      const paradas = newRuta.puntos.slice(1, -1);

      // Polyline real de Directions API (camino por calles)
      let polylineRuta = null;
      const conCoords = newRuta.puntos.filter(p => p.lat && p.lng);
      if (conCoords.length >= 2) {
        try {
          const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
          const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
          const waypoints = conCoords.slice(1, -1).map(p => `${p.lat},${p.lng}`).join("|");
          const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`;
          const res = await fetch(`${API_BASE}/api/directions?${qs}`);
          const data = await res.json();
          polylineRuta = data?.routes?.[0]?.overview_polyline?.points || data?.polyline || null;
        } catch (e) { /* fallback a rectas */ }
      }

      // Si es ruta de un grupo, va directo a la tabla "rutas" (saltea el sistema de aprobación)
      if (grupoIdParaPublicar) {
        const { error } = await supabase.from("rutas").insert({
          user_id: session.user.id,
          username: username,
          nombre: newRuta.nombre,
          desde,
          hasta,
          paradas,
          km: Number(newRuta.km),
          diff: newRuta.diff,
          camino: newRuta.camino,
          pct_offroad: newRuta.pctOffroad,
          desc_texto: newRuta.desc,
          alertas: newRuta.alertas,
          foto_url: null,
          polyline: polylineRuta,
          grupo_id: grupoIdParaPublicar,
        });
        if (error) throw error;
        setShowPublicar(false);
        setGrupoIdParaPublicar(null);
        setGrupoNombreParaPublicar("");
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 4000);
        setNewRuta({ nombre: "", puntos: [], km: "", diff: "", camino: "Asfalto", pctOffroad: 0, desc: "", alertas: "" });
      } else {
        // Ruta pública: se publica DIRECTO, sin revisión previa. La moderación es
        // posterior — cualquiera puede reportarla y el admin puede bajarla. El gate
        // previo tenía la app con 2 rutas: frenaba más de lo que cuidaba (15-ago-2026).
        const { error } = await supabase.from("rutas").insert({
          user_id: session.user.id,
          username: username,
          nombre: newRuta.nombre,
          desde,
          hasta,
          paradas,
          km: Number(newRuta.km),
          diff: newRuta.diff,
          camino: newRuta.camino,
          pct_offroad: newRuta.pctOffroad,
          desc_texto: newRuta.desc,
          alertas: newRuta.alertas,
          foto_url: null,
          polyline: polylineRuta,
        });
        if (error) throw error;
        setShowPublicar(false);
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 4000);
        setNewRuta({ nombre: "", puntos: [], km: "", diff: "", camino: "Asfalto", pctOffroad: 0, desc: "", alertas: "" });
        cargarRutasAprobadas();
      }
    } catch (e) {
      setErrorSubmit("Error al enviar: " + e.message);
    }
    setSubmitting(false);
  };

  // Aprobar ruta pendiente → guardarla en tabla rutas y marcar pendiente como aprobada
  const aprobarRuta = async (ruta) => {
    try {
      // Insertar en tabla rutas (permanente)
      const { data: nuevaFila, error: errInsert } = await supabase.from("rutas").insert({
        user_id: ruta.user_id,
        username: ruta.username,
        nombre: ruta.nombre,
        desde: ruta.desde,
        hasta: ruta.hasta,
        paradas: ruta.paradas,
        km: ruta.km,
        diff: ruta.diff,
        camino: ruta.camino,
        pct_offroad: ruta.pct_offroad,
        desc_texto: ruta.desc_texto,
        alertas: ruta.alertas,
        foto_url: ruta.foto_url,
        polyline: ruta.polyline || null,
        aprobada_por: session?.user?.id,
      }).select().single();

      if (errInsert) throw errInsert;

      // Buscar perfil del creador
      const { data: perfilCreador } = await supabase
        .from("perfiles")
        .select("id, username, avatar_url, motos")
        .eq("id", ruta.user_id)
        .single();

      // Agregar al state para que aparezca inmediatamente
      const nuevaRuta = supabaseToRoute(nuevaFila, perfilCreador, []);
      setRoutes(prev => [...prev, nuevaRuta]);
      setReviews(prev => [...prev, []]);
      setCollabStops(prev => [...prev, []]);

      // Marcar la pendiente como aprobada
      await supabase.from("rutas_pendientes").update({ estado: "aprobada" }).eq("id", ruta.id);
      cargarRutasPendientes();
    } catch (e) {
      alert("Error al aprobar la ruta: " + e.message);
    }
  };

  const rechazarRuta = async (ruta) => {
    if (!window.confirm(`¿Rechazar la ruta "${ruta.nombre}"?`)) return;
    await supabase.from("rutas_pendientes").update({ estado: "rechazada" }).eq("id", ruta.id);
    cargarRutasPendientes();
  };

  const canSubmit = newRuta.nombre && newRuta.km && newRuta.diff &&
    newRuta.puntos.length >= 2;

  // ── EDITAR UNA RUTA YA PUBLICADA (autor o admin) ─────────────────────────
  const [showEditar, setShowEditar] = useState(false);
  const [editRuta, setEditRuta] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [errorEdit, setErrorEdit] = useState("");

  // Botón atrás mientras se edita → cerrar con confirmación, no salir de la app
  useBackGuard(showEditar, () => {
    if (window.confirm("¿Salir sin guardar? Vas a perder los cambios.")) setShowEditar(false);
  });

  const setE = (k, v) => setEditRuta(n => ({ ...n, [k]: v }));

  // Puede editar el que la publicó, o un admin
  const puedeEditarRuta = (ruta) =>
    !!ruta?._supabaseId && (isAdmin || (!!session?.user?.id && session.user.id === ruta.user_id));

  const abrirEditorRuta = (ruta, idx) => {
    // Reconstruir los puntos del mapa: inicio + paradas intermedias + fin
    const puntos = [];
    if (ruta.lat_from && ruta.lng_from) puntos.push({ lat: ruta.lat_from, lng: ruta.lng_from, nombre: ruta.desde, tipo: "inicio" });
    (ruta.stops || []).forEach(s => {
      if (s.lat && s.lng) puntos.push({ lat: s.lat, lng: s.lng, nombre: s.nombre, tipo: s.tipo || "parada" });
    });
    if (ruta.lat_to && ruta.lng_to) puntos.push({ lat: ruta.lat_to, lng: ruta.lng_to, nombre: ruta.hasta, tipo: "fin" });
    setEditRuta({
      _supabaseId: ruta._supabaseId,
      _idx: idx,
      nombre: ruta.name || "",
      puntos,
      km: String(ruta.km ?? ""),
      diff: ruta.diff || "",
      pctOffroad: ruta.pctOffroad || 0,
      desc: ruta.desc || "",
      alertas: ruta.alertas || "",
      polyline: ruta.polyline || null,
    });
    setErrorEdit("");
    setShowEditar(true);
  };

  const canSaveEdit = !!editRuta?.nombre && !!editRuta?.km && !!editRuta?.diff &&
    (editRuta?.puntos?.length || 0) >= 2;

  const guardarEdicionRuta = async () => {
    if (!editRuta || !canSaveEdit || savingEdit) return;
    setSavingEdit(true);
    setErrorEdit("");
    try {
      const desde = editRuta.puntos[0];
      const hasta = editRuta.puntos[editRuta.puntos.length - 1];
      const paradas = editRuta.puntos.slice(1, -1);

      // Recalcular el camino real: si se agregó o movió una parada, el trazado cambia.
      // Si Directions falla, se conserva el polyline anterior en vez de dejarlo en null.
      let polylineRuta = editRuta.polyline || null;
      const conCoords = editRuta.puntos.filter(p => p.lat && p.lng);
      if (conCoords.length >= 2) {
        try {
          const origin = `${conCoords[0].lat},${conCoords[0].lng}`;
          const destination = `${conCoords[conCoords.length - 1].lat},${conCoords[conCoords.length - 1].lng}`;
          const waypoints = conCoords.slice(1, -1).map(p => `${p.lat},${p.lng}`).join("|");
          const qs = `origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`;
          const res = await fetch(`${API_BASE}/api/directions?${qs}`);
          const data = await res.json();
          polylineRuta = data?.routes?.[0]?.overview_polyline?.points || data?.polyline || polylineRuta;
        } catch (e) { /* se conserva el polyline anterior */ }
      }

      const { data: filas, error } = await supabase.from("rutas").update({
        nombre: editRuta.nombre,
        desde,
        hasta,
        paradas,
        km: Number(editRuta.km),
        diff: editRuta.diff,
        pct_offroad: editRuta.pctOffroad,
        desc_texto: editRuta.desc,
        alertas: editRuta.alertas,
        polyline: polylineRuta,
      }).eq("id", editRuta._supabaseId).select();

      if (error) throw error;
      // Con RLS, una fila que no te pertenece no da error: devuelve 0 filas actualizadas.
      if (!filas || filas.length === 0) {
        throw new Error("No se guardó ningún cambio. Puede que ya no tengas permiso para editar esta ruta.");
      }

      const kmNum = Number(editRuta.km);
      const idx = editRuta._idx;
      setRoutes(prev => prev.map((ro, i) => i !== idx ? ro : {
        ...ro,
        name: editRuta.nombre,
        km: kmNum,
        dur: `${Math.ceil(kmNum / 80)}hs`,
        diff: editRuta.diff,
        desde: desde.nombre,
        hasta: hasta.nombre,
        lat_from: desde.lat, lng_from: desde.lng,
        lat_to: hasta.lat, lng_to: hasta.lng,
        pctOffroad: editRuta.pctOffroad,
        desc: editRuta.desc,
        alertas: editRuta.alertas,
        alerts: editRuta.alertas ? [editRuta.alertas] : [],
        stops: paradas.map(p => ({ nombre: p.nombre, desc: "", tipo: p.tipo, lat: p.lat, lng: p.lng, fotos: [] })),
        polyline: polylineRuta,
      }));
      setShowEditar(false);
      setEditRuta(null);
    } catch (e) {
      setErrorEdit(e.message || "No se pudo guardar.");
    }
    setSavingEdit(false);
  };

  // ── MODAL PUBLICAR RUTA (JSX directo, no componente — evita perder focus al tipear) ──
  const publicarModalJSX = showPublicar ? (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && (newRuta.puntos.length === 0 ? setShowPublicar(false) : window.confirm("¿Salir sin guardar? Vas a perder los datos cargados.") && setShowPublicar(false))}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "92vh", overflow: "auto", padding: "20px 20px calc(36px + var(--sab))" }} className="fade-up">

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700 }}>
            {grupoIdParaPublicar ? `Ruta para ${grupoNombreParaPublicar}` : "Publicar una ruta"}
          </h3>
          <button onClick={() => { setShowPublicar(false); setGrupoIdParaPublicar(null); setGrupoNombreParaPublicar(""); }} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        {/* aviso validación / privacidad */}
        {grupoIdParaPublicar ? (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 18, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Users size={14} strokeWidth={2} color="#111111" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, letterSpacing: "-0.005em" }}>
              Esta ruta va a ser visible <strong style={{ color: C.text }}>solo para miembros de {grupoNombreParaPublicar}</strong>. No pasa por aprobación, queda disponible inmediatamente.
            </p>
          </div>
        ) : (
          <div style={{ background: "#f6f1eb", border: "1px solid #8a857f22", borderRadius: 10, padding: "10px 12px", marginBottom: 18, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Shield size={14} strokeWidth={2} color={C.blue} style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              Tu ruta se publica al instante y queda con tu nombre. La comunidad la puede calificar y sugerirte paradas. <strong style={{ color: C.text }}>Subí solo caminos que hiciste</strong> — si algo está mal, cualquiera puede reportarla.
            </p>
          </div>
        )}

        {/* campos obligatorios */}
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
          Información obligatoria
        </div>

        <Input label="Nombre de la ruta" value={newRuta.nombre} onChange={e => setR("nombre", e.target.value)} placeholder="Ej: Mendoza → San Juan por la 40" />

        {/* Traer una ruta que ya tenés en otra app, en vez de marcarla punto por punto */}
        <ImportarGPX
          onListo={({ nombre, puntos, km }) => setNewRuta(n => ({
            ...n,
            nombre: n.nombre || nombre || "",
            puntos,
            km: String(Math.round(km)),
          }))}
        />

        {/* MAPA ÚNICO con taps secuenciales */}
        <MapaCreadorRuta
          puntos={newRuta.puntos}
          setPuntos={updater => setNewRuta(n => ({ ...n, puntos: typeof updater === "function" ? updater(n.puntos) : updater }))}
          onKmReal={km => setR("km", String(km))}
        />

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input label="Distancia (km)" value={newRuta.km} onChange={e => setR("km", e.target.value)} placeholder="Ej: 340" />
            {newRuta.puntos.length >= 2 && (
              <div style={{ fontSize: 10, color: C.muted, marginTop: -10, marginBottom: 14 }}>
                Estimado en línea recta · ajustable
              </div>
            )}
          </div>
          <div style={{ flex: 1, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Dificultad</div>
            <select value={newRuta.diff} onChange={e => setR("diff", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card }}>
              <option value="">Elegir</option>
              {["Fácil", "Moderada", "Difícil", "Muy difícil"].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* superficie */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Superficie del camino</div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Asfalto</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Off-road</span>
          </div>
          <input type="range" min={0} max={100} step={10} value={newRuta.pctOffroad}
            onChange={e => setR("pctOffroad", Number(e.target.value))}
            style={{ width: "100%", accentColor: C.text, cursor: "pointer" }} />
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 8, marginTop: 6, border: `1px solid ${C.border}` }}>
            <div style={{ width: `${100 - newRuta.pctOffroad}%`, background: C.accent, transition: "width .2s" }} />
            <div style={{ width: `${newRuta.pctOffroad}%`, background: "#8B6914", transition: "width .2s" }} />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
            {newRuta.pctOffroad === 0 ? "100% asfalto" : newRuta.pctOffroad === 100 ? "100% off-road" : `${100 - newRuta.pctOffroad}% asfalto · ${newRuta.pctOffroad}% off-road`}
          </div>
        </div>

        {/* campos opcionales */}
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, marginTop: 8 }}>
          Información adicional (opcional)
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Descripción del recorrido</div>
          <textarea value={newRuta.desc} onChange={e => setR("desc", e.target.value)}
            placeholder="Contá de qué va la ruta, qué la hace especial, qué tipo de rider es ideal..."
            style={{ width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Alertas conocidas</div>
          <textarea value={newRuta.alertas} onChange={e => setR("alertas", e.target.value)}
            placeholder="Ej: Viento cruzado en el km 200, obras en temporada de verano..."
            style={{ width: "100%", minHeight: 55, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none" }} />
        </div>

        {/* qué falta */}
        {!canSubmit && (
          <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#B07D1A", marginBottom: 6 }}>Faltan datos obligatorios:</div>
            {[
              !newRuta.nombre && "Nombre de la ruta",
              newRuta.puntos.length < 2 && "Al menos 2 puntos en el mapa (origen y destino)",
              !newRuta.km && "Distancia en km",
              !newRuta.diff && "Dificultad",
            ].filter(Boolean).map((m, i) => (
              <div key={i} style={{ fontSize: 12, color: "#B07D1A", display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#B07D1A", flexShrink: 0 }} />
                {m}
              </div>
            ))}
          </div>
        )}

        {!session && (
          <div style={{ background: "#FFF7E6", border: "1px solid #FFD485", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: "#8B6914" }}>
            ⚠️ Tenés que estar logueado para publicar una ruta. <br />Tu ruta se guardará permanentemente al enviar.
          </div>
        )}

        {errorSubmit && (
          <div style={{ background: "#FEE", border: "1px solid #FCC", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: "#C00" }}>
            {errorSubmit}
          </div>
        )}

        <Btn full onClick={enviarRuta}
          style={{ opacity: (canSubmit && session && !submitting) ? 1 : 0.5 }}>
          {submitting ? "Publicando..." : "Publicar ruta"}
        </Btn>
        <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 8, lineHeight: 1.6 }}>
          Se publica ya, con tu nombre. Después la podés editar cuando quieras.
        </p>
      </div>
    </div>
  ) : null;

  // ── MODAL EDITAR RUTA (JSX directo, igual que el de publicar) ──
  const editarModalJSX = showEditar && editRuta ? (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && window.confirm("¿Salir sin guardar? Vas a perder los cambios.") && setShowEditar(false)}>
      <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "92vh", overflow: "auto", padding: "20px 20px calc(36px + var(--sab))" }} className="fade-up">

        {/* header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700 }}>Editar ruta</h3>
          <button onClick={() => setShowEditar(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        <div style={{ background: "#f6f1eb", border: "1px solid #8a857f22", borderRadius: 10, padding: "10px 12px", marginBottom: 18, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Info size={14} strokeWidth={2} color="#111111" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, letterSpacing: "-0.005em" }}>
            Los cambios se ven al instante en la ruta publicada. Si movés o agregás una parada, el trazado del mapa se recalcula solo.
          </p>
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
          Información obligatoria
        </div>

        <Input label="Nombre de la ruta" value={editRuta.nombre} onChange={e => setE("nombre", e.target.value)} placeholder="Ej: Mendoza → San Juan por la 40" />

        <MapaCreadorRuta
          puntos={editRuta.puntos}
          setPuntos={updater => setEditRuta(n => ({ ...n, puntos: typeof updater === "function" ? updater(n.puntos) : updater }))}
        />

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Input label="Distancia (km)" value={editRuta.km} onChange={e => setE("km", e.target.value)} placeholder="Ej: 340" />
            {editRuta.puntos.length >= 2 && (() => {
              const est = distanciaTotal(editRuta.puntos);
              return String(est) === String(editRuta.km) ? null : (
                <button onClick={() => setE("km", String(est))}
                  style={{ fontSize: 10, color: C.text, fontWeight: 700, background: "transparent", border: "none", padding: 0, marginTop: -10, marginBottom: 14, cursor: "pointer", textAlign: "left", textDecoration: "underline" }}>
                  Recalcular: {est} km en línea recta
                </button>
              );
            })()}
          </div>
          <div style={{ flex: 1, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Dificultad</div>
            <select value={editRuta.diff} onChange={e => setE("diff", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card }}>
              <option value="">Elegir</option>
              {["Fácil", "Moderada", "Difícil", "Muy difícil"].map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* superficie */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Superficie del camino</div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Asfalto</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Off-road</span>
          </div>
          <input type="range" min={0} max={100} step={10} value={editRuta.pctOffroad}
            onChange={e => setE("pctOffroad", Number(e.target.value))}
            style={{ width: "100%", accentColor: C.text, cursor: "pointer" }} />
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 8, marginTop: 6, border: `1px solid ${C.border}` }}>
            <div style={{ width: `${100 - editRuta.pctOffroad}%`, background: C.accent, transition: "width .2s" }} />
            <div style={{ width: `${editRuta.pctOffroad}%`, background: "#8B6914", transition: "width .2s" }} />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
            {editRuta.pctOffroad === 0 ? "100% asfalto" : editRuta.pctOffroad === 100 ? "100% off-road" : `${100 - editRuta.pctOffroad}% asfalto · ${editRuta.pctOffroad}% off-road`}
          </div>
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, marginTop: 8 }}>
          Información adicional (opcional)
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Descripción del recorrido</div>
          <textarea value={editRuta.desc} onChange={e => setE("desc", e.target.value)}
            placeholder="Contá de qué va la ruta, qué la hace especial, qué tipo de rider es ideal..."
            style={{ width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Alertas conocidas</div>
          <textarea value={editRuta.alertas} onChange={e => setE("alertas", e.target.value)}
            placeholder="Ej: Viento cruzado en el km 200, obras en temporada de verano..."
            style={{ width: "100%", minHeight: 55, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none" }} />
        </div>

        {!canSaveEdit && (
          <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#B07D1A", marginBottom: 6 }}>Faltan datos obligatorios:</div>
            {[
              !editRuta.nombre && "Nombre de la ruta",
              editRuta.puntos.length < 2 && "Al menos 2 puntos en el mapa (origen y destino)",
              !editRuta.km && "Distancia en km",
              !editRuta.diff && "Dificultad",
            ].filter(Boolean).map((m, i) => (
              <div key={i} style={{ fontSize: 12, color: "#B07D1A", display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#B07D1A", flexShrink: 0 }} />
                {m}
              </div>
            ))}
          </div>
        )}

        {errorEdit && (
          <div style={{ background: "#FEE", border: "1px solid #FCC", borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: "#C00" }}>
            {errorEdit}
          </div>
        )}

        <Btn full onClick={guardarEdicionRuta}
          style={{ opacity: (canSaveEdit && !savingEdit) ? 1 : 0.5 }}>
          {savingEdit ? "Guardando..." : "Guardar cambios"}
        </Btn>
      </div>
    </div>
  ) : null;

  // ── DETALLE DE RUTA ──────────────────────────────────────────────────────
  if (sel !== null) {
    const r = routes[sel];
    const n = r.stops.length;
    const W = 340, H = 120, pad = 24;
    const pts = r.stops.map((_, i) => ({
      x: pad + (i / (n - 1)) * (W - pad * 2),
      y: H / 2 + (i % 2 === 0 ? -16 : 16),
    }));
    const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
    const allStops = [...r.stops, ...collabStops[sel].map(s => ({ nombre: s.nombre, desc: `Sugerida por @${s.user}`, lat: null, lng: null }))];

    // Construir puntos para el mapa preview del hero
    const heroPuntos = [];
    if (r.lat_from && r.lng_from) heroPuntos.push({ nombre: r.desde, lat: r.lat_from, lng: r.lng_from });
    if (r.stops) r.stops.forEach(s => { if (s.lat && s.lng) heroPuntos.push({ nombre: s.nombre, lat: s.lat, lng: s.lng }); });
    if (r.lat_to && r.lng_to) heroPuntos.push({ nombre: r.hasta, lat: r.lat_to, lng: r.lng_to });
    const tieneCoordenadas = heroPuntos.length >= 2;

    // Backfill: rutas guardadas cuando /api/directions no devolvía el camino quedaron
    // sin polyline (se veían como línea recta). Al abrir el detalle se pide una vez,
    // se guarda en la base y no se vuelve a pedir.
    if (!r.polyline && !r._polylinePedido && tieneCoordenadas && r._supabaseId) {
      r._polylinePedido = true; // marca en memoria para no repetir el fetch en cada render
      (async () => {
        try {
          const origin = `${heroPuntos[0].lat},${heroPuntos[0].lng}`;
          const destination = `${heroPuntos[heroPuntos.length - 1].lat},${heroPuntos[heroPuntos.length - 1].lng}`;
          const waypoints = heroPuntos.slice(1, -1).slice(0, 23).map(p => `${p.lat},${p.lng}`).join("|");
          const res = await fetch(`${API_BASE}/api/directions?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}`);
          const data = await res.json();
          const enc = data?.polyline || data?.routes?.[0]?.overview_polyline?.points || null;
          if (enc) {
            supabase.from("rutas").update({ polyline: enc }).eq("id", r._supabaseId).then(() => {}, () => {});
            setRoutes(prev => prev.map(ro => ro._supabaseId === r._supabaseId ? { ...ro, polyline: enc } : ro));
          }
        } catch (e) { /* queda sin línea, solo marcadores */ }
      })();
    }

    return (
      <PageWrap>
        {/* hero — mapa con pinpoints */}
        <div style={{ height: 280, position: "relative", background: "#f6f1eb", overflow: "hidden" }}>
          {tieneCoordenadas ? (
            <MapaPreview puntos={heroPuntos} alto={280} sinInteraccion polyline={r.polyline} />
          ) : (
            <img loading="lazy" decoding="async" src={r.img} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.65 }} />
          )}
          <button onClick={() => setSel(null)} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.65)", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", zIndex: 5 }}>
            <ChevronLeft size={20} strokeWidth={2} color="#fff" />
          </button>
          {/* botón compartir — corrido a la izquierda del avatar flotante de Mi perfil,
              que es fijo arriba a la derecha y lo pisaba (reporte de Matías) */}
          <div style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", right: 62, zIndex: 5 }}>
            <BtnCompartir
              texto={`🛣️ ${r.name} · ${r.km}km · ${r.diff}`}
              tipo="ruta" id={r._supabaseId} dark
            />
          </div>
          <div style={{ position: "absolute", bottom: 14, left: 14, right: 14, zIndex: 5, pointerEvents: "none" }}>
            <div style={{ background: "rgba(0,0,0,.78)", borderRadius: 10, padding: "10px 14px", display: "inline-block", maxWidth: "100%" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.55)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Olbos · Rutas</div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: -0.3, marginBottom: 6 }}>{r.name}</h1>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[r.diff, r.camino, r.dur].map(t => (
                  <span key={t} style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 4, background: "rgba(255,255,255,.15)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 80px" }}>

          {/* editar ruta — el que la publicó, o un admin */}
          {puedeEditarRuta(r) && (
            <button onClick={() => abrirEditorRuta(r, sel)} style={{
              width: "100%", marginBottom: 14, padding: "11px 14px", borderRadius: 12,
              background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <Edit2 size={14} strokeWidth={2} color={C.text} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: C.text, fontWeight: 700, letterSpacing: "-0.005em" }}>Editar ruta</span>
              <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>
                {isAdmin && session?.user?.id !== r.user_id ? "como admin" : "es tuya"}
              </span>
            </button>
          )}

          {/* eliminar ruta propia (pedido de Matías: publicada no se podía borrar;
              antes solo el admin tenía el botón) */}
          {session?.user?.id === r.user_id && (
            <button onClick={async () => {
              if (!window.confirm("¿Eliminar tu ruta? Se borra para todos y no se puede deshacer.")) return;
              const rutaIdBD = r._supabaseId || r.id;
              const { error } = await supabase.from("rutas").delete().eq("id", rutaIdBD);
              if (error) { alert("No se pudo eliminar: " + error.message); return; }
              setRoutes(prev => prev.filter((_, i) => i !== sel));
              setSel(null);
            }} style={{
              width: "100%", marginBottom: 14, padding: "10px 14px", borderRadius: 12,
              background: "transparent", border: `1px solid ${C.red}33`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Trash2 size={13} strokeWidth={2} color={C.red} />
              <span style={{ fontSize: 12.5, color: C.red, fontWeight: 700 }}>Eliminar ruta</span>
            </button>
          )}

          {/* banner admin */}
          {isAdmin && (
            <div style={{ background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 12, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 10, alignItems: "center" }}>
              <Shield size={14} strokeWidth={2} color="#B07D1A" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#8B6914", fontWeight: 600, flex: 1 }}>Modo administrador activo</span>
              <button onClick={async () => {
                if (!window.confirm("¿Eliminar esta ruta? No se puede deshacer.")) return;
                // Borrar de BD
                const rutaIdBD = r._supabaseId || r.id;
                const { error } = await supabase.from("rutas").delete().eq("id", rutaIdBD);
                if (error) {
                  alert("Error al eliminar: " + error.message);
                  return;
                }
                setRoutes(prev => prev.filter((_, i) => i !== sel));
                setSel(null);
              }} style={{ fontSize: 11, color: C.red, fontWeight: 700, background: "transparent", cursor: "pointer", border: "none" }}>
                Eliminar ruta
              </button>
            </div>
          )}

          {/* Recorrer la ruta con el mismo motor que los tracks grabados:
              muestra dónde estás sobre el trazado, cuánto llevás y cuánto falta,
              y avisa si te saliste del camino. */}
          <button onClick={() => {
            const pts = puntosParaRecorrer(r);
            if (pts.length < 2) { alert("Esta ruta todavía no tiene el trazado cargado para poder seguirla."); return; }
            setRecorriendoRuta({ id: null, titulo: r.name, puntos: pts, distancia_km: r.km });
          }}
            style={{
              width: "100%", padding: "13px", borderRadius: 12, marginBottom: 14,
              background: C.text, color: "#fff", border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 700, letterSpacing: "-0.005em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            }}>
            🗺️ Recorrer esta ruta
          </button>

          {/* La validación de la comunidad: cuántos riders ya pasaron por este camino */}
          <div style={{ marginBottom: 14 }}>
            <RidersEnCamino puntos={puntosParaRecorrer(r)} />
          </div>

          {/* Transferir propiedad de la ruta (autor o admin) */}
          <BotonTransferir tipo="ruta" objetoId={r._supabaseId || r.id} autorId={r.user_id} onListo={() => setSel(null)} />

          {/* creador */}
          {r.creator && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <img loading="lazy" decoding="async" src={r.creator.avatarUrl} alt={r.creator.user} style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>Ruta creada por</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>@{r.creator.user}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{r.creator.moto} · {r.creator.fecha}</div>
              </div>
              <div style={{ background: "#f6f1eb", border: "1px solid #8a857f22", borderRadius: 8, padding: "4px 10px", fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: 0.5 }}>
                CREADOR
              </div>
            </div>
          )}

          {/* stats */}
          <div style={{ display: "flex", gap: 12, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
            {[["Distancia", `${r.km} km`], ["Duración", r.dur], ["Camino", r.camino]].map(([k, v]) => (
              <div key={k} style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{v}</div>
              </div>
            ))}
          </div>

          {/* descripción */}
          {isAdmin && editingRoute?.field === "desc" ? (
            <div style={{ marginBottom: 20 }}>
              <textarea value={editingRoute.value} onChange={e => setEditingRoute(r => ({ ...r, value: e.target.value }))}
                style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 10, border: `2px solid #F0C060`, fontSize: 14, color: C.text, resize: "none" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => { setRoutes(prev => prev.map((ro, i) => i === sel ? { ...ro, desc: editingRoute.value } : ro)); setEditingRoute(null); }}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: C.text, color: "#fff", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer" }}>Guardar</button>
                <button onClick={() => setEditingRoute(null)}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: C.surface, color: C.muted, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, cursor: "pointer" }}>Cancelar</button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 20, position: "relative" }}>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{r.desc}</p>
              {isAdmin && (
                <button onClick={() => setEditingRoute({ field: "desc", value: r.desc })}
                  style={{ position: "absolute", top: 0, right: 0, fontSize: 11, color: "#B07D1A", fontWeight: 600, background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}>
                  Editar
                </button>
              )}
            </div>
          )}

          {/* Mapa del recorrido: Google estático con el camino real (polyline).
              Antes era un iframe de Leaflet armado con las PARADAS: una ruta sin paradas
              daba Math.min() de un array vacío => mapa gris (bug visto post-Arribeños). */}
          {tieneCoordenadas && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Mapa del recorrido</div>
              <div style={{ borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}` }}>
                <MapaPreview puntos={heroPuntos} alto={240} polyline={r.polyline} />
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                <span style={{ fontSize: 11, color: C.muted }}>🟢 Origen</span>
                <span style={{ fontSize: 11, color: C.muted }}>🟠 Paradas</span>
                <span style={{ fontSize: 11, color: C.muted }}>🔴 Destino</span>
              </div>
            </div>
          )}

          {/* paradas — con mini-feed por parada */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                Paradas ({r.stops.filter(s => s.tipo !== "paso").length + collabStops[sel].length})
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>tocá para ver más</div>
            </div>

            {r.stops.map((s, i) => {
              const isFirst = i === 0;
              const isLast = i === r.stops.length - 1;
              const esPaso = s.tipo === "paso";
              const waze = `https://www.waze.com/ul?ll=${s.lat},${s.lng}&navigate=yes&zoom=17`;
              const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}&travelmode=driving`;
              const stopKey = `${sel}-${i}`;
              const feed = stopFeed[stopKey] || [];
              const expanded = expandedStop === stopKey;

              return (
                <div key={`orig-${i}`} style={{ marginBottom: 4 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    {/* línea vertical */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div style={{ width: esPaso ? 18 : 26, height: esPaso ? 18 : 26, borderRadius: "50%", background: esPaso ? C.card : (isFirst || isLast ? C.text : C.surface), border: `2px solid ${esPaso ? "#111111" : (isFirst || isLast ? C.text : C.border)}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {esPaso
                          ? <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.accent }} />
                          : isFirst || isLast
                            ? <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.card }} />
                            : <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted }} />
                        }
                      </div>
                      {i < r.stops.length - 1 && <div style={{ width: 1.5, height: expanded ? 12 : 36, background: C.border, marginTop: 2 }} />}
                    </div>

                    {/* contenido parada */}
                    <div style={{ flex: 1, paddingBottom: 6 }}>
                      <button onClick={() => setExpandedStop(expanded ? null : stopKey)}
                        style={{ width: "100%", textAlign: "left", background: "transparent", cursor: "pointer", marginBottom: 4 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: 13, color: C.text, fontWeight: isFirst || isLast ? 700 : 600 }}>{s.nombre}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {feed.length > 0 && (
                              <span style={{ fontSize: 10, fontWeight: 600, color: C.muted, background: C.surface, border: `1px solid ${C.border}`, padding: "1px 7px", borderRadius: 10 }}>
                                {feed.length}
                              </span>
                            )}
                            <ChevronDown size={14} strokeWidth={2} color={C.muted} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: esPaso ? "#111111" : C.muted, marginTop: 1 }}>{esPaso ? "🔵 Punto de paso" : s.desc}</div>
                      </button>

                      {/* botones navegar */}
                      <div style={{ display: "flex", gap: 6, marginBottom: expanded ? 12 : 0 }}>
                        {s.lat && s.lng && (
                          <button onClick={() => navegarConOlbos(s.lat, s.lng, s.nombre || "la parada")}
                            style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: C.accent, border: "none", fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
                            🏍️ Olbos
                          </button>
                        )}
                        <a href={waze} target="_blank" rel="noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: "#f6f1eb", border: "1px solid #05C8F733", fontSize: 11, fontWeight: 600, color: "#0099BB", textDecoration: "none" }}>
                          <Navigation size={11} strokeWidth={2} /> Waze
                        </a>
                        <a href={gmaps} target="_blank" rel="noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: "#f6f1eb", border: "1px solid #11111133", fontSize: 11, fontWeight: 600, color: C.text, textDecoration: "none" }}>
                          <Map size={11} strokeWidth={2} /> Maps
                        </a>
                        {isAdmin && (
                          <>
                            <button onClick={() => setEditingStop({ routeIdx: sel, stopIdx: i, nombre: s.nombre, desc: s.desc })}
                              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: `${C.gold}14`, border: `1px solid ${C.gold}55`, fontSize: 11, fontWeight: 600, color: "#B07D1A", cursor: "pointer" }}>
                              Editar
                            </button>
                            <button onClick={() => {
                              setRoutes(prev => prev.map((ro, ri) => ri !== sel ? ro : { ...ro, stops: ro.stops.filter((_, si) => si !== i) }));
                            }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, background: `${C.red}14`, border: `1px solid ${C.red}33`, fontSize: 11, fontWeight: 600, color: C.red, cursor: "pointer" }}>
                              Eliminar
                            </button>
                          </>
                        )}
                      </div>

                      {/* mini-feed expandido */}
                      {expanded && (
                        <div style={{ marginTop: 4, background: C.surface, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` }}>

                          {/* posts existentes */}
                          {feed.length === 0 && (
                            <div style={{ textAlign: "center", padding: "12px 0", color: C.muted, fontSize: 12 }}>
                              Sé el primero en compartir algo de esta parada
                            </div>
                          )}
                          {feed.map((post, pi) => (
                            <div key={pi} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: pi < feed.length - 1 ? `1px solid ${C.border}` : "none" }}>
                              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E8E8E8", overflow: "hidden", flexShrink: 0 }}>
                                  {post.avatarUrl
                                    ? <img loading="lazy" decoding="async" src={post.avatarUrl} alt={post.user} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><User size={14} strokeWidth={1.5} color={C.muted} /></div>
                                  }
                                </div>
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>@{post.user}</span>
                                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 6 }}>{post.t}</span>
                                </div>
                                {post.type === "foto" && <Image size={13} strokeWidth={1.8} color={C.muted} />}
                                {post.type === "video" && <Play size={13} strokeWidth={1.8} color={C.muted} />}
                              </div>
                              {post.img && (
                                <div style={{ borderRadius: 8, overflow: "hidden", marginBottom: 8, height: 160 }}>
                                  <img loading="lazy" decoding="async" src={post.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                </div>
                              )}
                              {post.txt && <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{post.txt}</p>}
                              <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                                <button style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted, background: "transparent", cursor: "pointer" }}>
                                  <Heart size={12} strokeWidth={2} /> {post.likes || 0}
                                </button>
                                <button style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted, background: "transparent", cursor: "pointer" }}>
                                  <MessageCircle size={12} strokeWidth={2} /> {post.comments || 0}
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* aviso solo riders que estuvieron */}
                          <div style={{ background: "#f6f1eb", border: "1px solid #8a857f22", borderRadius: 8, padding: "8px 10px", marginBottom: 10, display: "flex", gap: 6, alignItems: "flex-start" }}>
                            <CheckCircle size={12} strokeWidth={2} color={C.blue} style={{ flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 11, color: C.blue, lineHeight: 1.5 }}>Solo pueden publicar aquí riders que marcaron haber hecho esta ruta.</span>
                          </div>

                          {/* campo publicar */}
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E8E8E8", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <User size={14} strokeWidth={1.5} color={C.muted} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <textarea
                                value={stopDraft[stopKey] || ""}
                                onChange={e => setStopDraft(d => ({ ...d, [stopKey]: e.target.value }))}
                                placeholder="¿Pasaste por acá? Compartí una foto, video o tip..."
                                style={{ width: "100%", minHeight: 60, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, resize: "none", background: C.card }}
                              />
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted, cursor: "pointer" }}>
                                    <Camera size={14} strokeWidth={1.8} color={C.muted} />
                                    <span>Foto/Video</span>
                                    <input type="file" accept="image/*,video/*" style={{ display: "none" }}
                                      onChange={e => {
                                        const f = e.target.files[0];
                                        if (!f) return;
                                        const url = URL.createObjectURL(f);
                                        setStopMedia(m => ({ ...m, [stopKey]: { url, type: f.type.startsWith("video") ? "video" : "foto" } }));
                                      }} />
                                  </label>
                                  {stopMedia[stopKey] && (
                                    <span style={{ fontSize: 11, color: C.green, display: "flex", alignItems: "center", gap: 3 }}>
                                      <CheckCircle size={11} strokeWidth={2} color={C.green} />
                                      {stopMedia[stopKey].type === "video" ? "Video listo" : "Foto lista"}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    const txt = stopDraft[stopKey]?.trim();
                                    const media = stopMedia[stopKey];
                                    if (!txt && !media) return;
                                    const newPost = {
                                      user: "Vos", avatarUrl: null,
                                      txt, t: "Ahora",
                                      img: media?.type === "foto" ? media.url : null,
                                      type: media?.type || "comentario",
                                      likes: 0, comments: 0,
                                    };
                                    setStopFeed(f => ({ ...f, [stopKey]: [...(f[stopKey] || []), newPost] }));
                                    setStopDraft(d => ({ ...d, [stopKey]: "" }));
                                    setStopMedia(m => { const n = { ...m }; delete n[stopKey]; return n; });
                                  }}
                                  style={{ padding: "5px 14px", borderRadius: 8, background: C.text, color: "#fff", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer" }}>
                                  Publicar
                                </button>
                              </div>
                              {/* preview media */}
                              {stopMedia[stopKey] && (
                                <div style={{ position: "relative", marginTop: 8, borderRadius: 8, overflow: "hidden", height: 100 }}>
                                  <img loading="lazy" decoding="async" src={stopMedia[stopKey].url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <button onClick={() => setStopMedia(m => { const n = { ...m }; delete n[stopKey]; return n; })}
                                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
                                    <X size={11} strokeWidth={2.5} color="#fff" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {!expanded && i < r.stops.length - 1 && <div style={{ width: 1.5, height: 8, background: C.border, marginLeft: 12 }} />}
                </div>
              );
            })}

            {/* paradas colaborativas */}
            {collabStops[sel].map((s, i) => {
              // La puede sacar quien la sugirió, el dueño de la ruta, o un admin
              const puedeSacarla = session && (session.user.id === s.user_id || session.user.id === r.user_id || isAdmin);
              return (
                <div key={s.id || `collab-${i}`} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#f6f1eb", border: `2px solid #8a857f44`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue }} />
                    </div>
                  </div>
                  <div style={{ paddingTop: 2, flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: C.text }}>{s.nombre}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                      {s.avatarUrl && <img loading="lazy" decoding="async" src={s.avatarUrl} alt="" style={{ width: 14, height: 14, borderRadius: "50%", objectFit: "cover" }} />}
                      <span style={{ fontSize: 11, color: C.blue }}>sugerida por @{s.user}</span>
                    </div>
                  </div>
                  {puedeSacarla && s.id && (
                    <button onClick={async () => {
                      if (!window.confirm("¿Sacar esta sugerencia?")) return;
                      const { error } = await supabase.from("ruta_paradas_sugeridas")
                        .update({ estado: "descartada" }).eq("id", s.id);
                      if (error) { alert("No se pudo sacar. Probá de nuevo."); return; }
                      await cargarRutasAprobadas();
                    }} title="Sacar sugerencia"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, color: C.muted }}>
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              );
            })}
            {/* Si es tu ruta y alguien sugirió algo, avisamos cómo incorporarlo */}
            {collabStops[sel].length > 0 && session && session.user.id === r.user_id && (
              <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.55, margin: "2px 0 10px 38px" }}>
                Son sugerencias de la comunidad, todavía no forman parte del trazado. Si alguna te sirve, agregala desde “Editar ruta” y el camino se recalcula solo.
              </div>
            )}

            {/* agregar parada colaborativa */}
            <div style={{ background: "#f6f1eb", border: "1px solid #8a857f22", borderRadius: 12, padding: "12px 14px", marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Sugerir una parada
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                {r.km > 0 && `Esta ruta tiene ${r.km}km — se recomienda una parada cada 150-200km (${Math.ceil(r.km / 175)} paradas sugeridas).`}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={newStop} onChange={e => setNewStop(e.target.value)}
                  placeholder="Ej: Villa Mercedes — km 640, nafta y descanso"
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 10, border: `1px solid #8a857f44`, fontSize: 13, color: C.text, background: C.card }} />
                <button onClick={async () => {
                  if (!newStop.trim()) return;
                  if (!session) { alert("Iniciá sesión para sugerir una parada."); return; }
                  if (!r._supabaseId) { alert("Esta ruta todavía no acepta sugerencias."); return; }
                  const texto = newStop.trim();
                  setNewStop("");
                  const { error } = await supabase.from("ruta_paradas_sugeridas").insert({
                    ruta_id: r._supabaseId, user_id: session.user.id, nombre: texto,
                  });
                  if (error) { alert("No pudimos guardar la sugerencia. Probá de nuevo."); setNewStop(texto); return; }
                  await cargarRutasAprobadas();
                }} style={{ padding: "8px 14px", borderRadius: 10, background: C.blue, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", flexShrink: 0 }}>
                  Agregar
                </button>
              </div>
            </div>
          </div>

          {/* alertas */}
          {r.alerts.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Alertas de la comunidad</div>
              {r.alerts.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
                  <AlertTriangle size={14} strokeWidth={2} color="#B07D1A" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{a}</span>
                </div>
              ))}
            </div>
          )}

          {/* equipamiento sugerido — wiki */}
          {r._supabaseId && (() => {
            const sugerencias = equipamiento[r._supabaseId] || [];
            const categorias = [
              { id: "proteccion", label: "Protección", icono: "🛡️", placeholder: "Casco, guantes, botas, traje..." },
              { id: "clima", label: "Clima", icono: "🌦️", placeholder: "Impermeable, térmica, gafas..." },
              { id: "herramientas", label: "Herramientas", icono: "🧰", placeholder: "Kit de parches, cargador..." },
              { id: "otro", label: "Otros tips", icono: "💡", placeholder: "Hidratación, snacks, contactos..." },
            ];
            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
                  Equipamiento sugerido por la comunidad
                </div>
                {categorias.map(cat => {
                  const items = sugerencias.filter(s => s.categoria === cat.id);
                  return (
                    <div key={cat.id} style={{ background: C.card, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: items.length > 0 ? 10 : 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 18 }}>{cat.icono}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{cat.label}</span>
                          {items.length > 0 && (
                            <span style={{ fontSize: 11, color: C.muted, background: C.surface, padding: "2px 7px", borderRadius: 10 }}>{items.length}</span>
                          )}
                        </div>
                        <button onClick={() => { setShowSugerirEquip({ categoria: cat.id, label: cat.label, placeholder: cat.placeholder }); setEquipDraft(""); }}
                          style={{ fontSize: 11, color: C.blue, background: "transparent", border: "none", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                          <Plus size={12} strokeWidth={2.5} /> Sugerir
                        </button>
                      </div>
                      {items.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
                          Aún no hay sugerencias. Sé el primero.
                        </div>
                      ) : (
                        items.map(s => (
                          <div key={s.id} style={{ background: C.surface, borderRadius: 8, padding: "8px 10px", marginBottom: 6, display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: 4 }}>{s.texto}</div>
                              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>— @{s.username}</div>
                            </div>
                            {session?.user?.id === s.user_id && (
                              <button onClick={() => eliminarSugerenciaEquip(s, r._supabaseId)}
                                style={{ width: 22, height: 22, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}33`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <X size={10} strokeWidth={2.5} color={C.red} />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* reviews */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
              Experiencias ({reviews[sel].length})
            </div>
            {reviews[sel].map((rv, i) => (
              <div key={i} style={{ background: C.card, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  {rv.avatarUrl
                    ? <img loading="lazy" decoding="async" src={rv.avatarUrl} alt={rv.u} style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
                    : <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}><User size={18} strokeWidth={1.5} color={C.muted} /></div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>@{rv.u}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{rv.m} · {rv.d}</div>
                  </div>
                  <Stars n={rv.r} size={12} />
                </div>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{rv.t}</p>
              </div>
            ))}

            {/* agregar review */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Tu experiencia</div>
              <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => setDraftRating(n)}>
                    <Star size={22} strokeWidth={1.5} fill={n <= draftRating ? "#B07D1A" : "none"} color={n <= draftRating ? "#B07D1A" : C.faint} />
                  </button>
                ))}
              </div>
              <textarea value={draft} onChange={e => setDraft(e.target.value)}
                placeholder="¿Hiciste esta ruta? Contá condiciones, paradas recomendadas, tips..."
                style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, resize: "none" }} />
              <Btn full style={{ marginTop: 10 }} onClick={async () => {
                if (!draft.trim()) return;
                if (!session) { alert("Tenés que estar logueado para publicar una experiencia."); return; }
                if (!username) { alert("Configurá tu username en Mi perfil antes de publicar."); return; }

                const ruta = routes[sel];
                const supabaseId = ruta._supabaseId;

                // Si la ruta tiene _supabaseId, guardar en Supabase
                if (supabaseId) {
                  const { data, error } = await supabase.from("ruta_reviews").insert({
                    ruta_id: supabaseId,
                    user_id: session.user.id,
                    username: username,
                    texto: draft,
                    rating: draftRating,
                  }).select().single();

                  if (error) { alert("Error al guardar: " + error.message); return; }

                  const nueva = {
                    u: username,
                    m: "—",
                    avatarUrl: userAvatar || avatarInicial(username),
                    t: draft,
                    r: draftRating,
                    d: "Ahora",
                    _supabaseId: data.id,
                  };
                  const updated = [...reviews];
                  updated[sel] = [...updated[sel], nueva];
                  setReviews(updated);
                } else {
                  // Ruta hardcoded (ROUTES) — solo en memoria
                  const updated = [...reviews];
                  updated[sel] = [...updated[sel], { u: username, m: "Tu moto", avatarUrl: userAvatar || null, t: draft, r: draftRating, d: "Ahora" }];
                  setReviews(updated);
                }
                setDraft("");
              }}>Publicar experiencia</Btn>
            </div>

            {/* Sin revisión previa, el primer filtro es la comunidad */}
            <ReportarRuta ruta={r} />
          </div>
        </div>

        {/* Modal sugerir equipamiento */}
        {showSugerirEquip && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 250, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            onClick={e => e.target === e.currentTarget && setShowSugerirEquip(null)}>
            <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(30px + var(--sab))" }} className="fade-up">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontSize: 17, fontWeight: 700 }}>Sugerir — {showSugerirEquip.label}</h3>
                <button onClick={() => setShowSugerirEquip(null)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                Compartí tu recomendación con la comunidad. Va a aparecer firmada con tu @usuario.
              </p>
              <textarea
                autoFocus
                value={equipDraft}
                onChange={e => setEquipDraft(e.target.value)}
                placeholder={showSugerirEquip.placeholder}
                style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box" }} />
              <Btn full style={{ marginTop: 12 }} onClick={() => enviarSugerenciaEquip(r._supabaseId, showSugerirEquip.categoria)}>
                Publicar sugerencia
              </Btn>
            </div>
          </div>
        )}

        {/* modal de edición — hermano del contenido, nunca anidado en un .fade-up */}
        {editarModalJSX}

        {/* Recorrer la ruta: mismo componente que sigue los tracks grabados */}
        {recorriendoRuta && (
          <SeguirTrack traza={recorriendoRuta} onCerrar={() => setRecorriendoRuta(null)} />
        )}
      </PageWrap>
    );
  }

  // ── TRAZAS GPS ────────────────────────────────────────────────────────────
  const cargarMisTrazas = async () => {
    if (!userId) { setMisTrazas([]); return; }
    setCargandoTrazas(true);
    const { data } = await supabase.from("trazas")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    setMisTrazas(data || []);
    setCargandoTrazas(false);
  };
  cargarTrazasRef.current = cargarMisTrazas;

  const eliminarTraza = async (id) => {
    if (!window.confirm("¿Eliminar este recorrido? No se puede deshacer.")) return;
    await supabase.from("trazas").delete().eq("id", id);
    setMisTrazas(t => t.filter(x => x.id !== id));
    if (detalleTraza?.id === id) setDetalleTraza(null);
  };

  const togglePublicaTraza = async (traza) => {
    const nueva = !traza.publica;
    if (nueva && kmTraza(traza) < KM_MIN_PUBLICAR) { alert(msgMinKm(kmTraza(traza))); return; }
    await supabase.from("trazas").update({ publica: nueva, updated_at: new Date().toISOString() }).eq("id", traza.id);
    setMisTrazas(t => t.map(x => x.id === traza.id ? { ...x, publica: nueva } : x));
    if (detalleTraza?.id === traza.id) setDetalleTraza(d => ({ ...d, publica: nueva }));
  };

  const renombrarTraza = async (traza, titulo) => {
    await supabase.from("trazas").update({ titulo, updated_at: new Date().toISOString() }).eq("id", traza.id);
    setMisTrazas(t => t.map(x => x.id === traza.id ? { ...x, titulo } : x));
    if (detalleTraza?.id === traza.id) setDetalleTraza(d => ({ ...d, titulo }));
  };

  // ── LISTADO ──────────────────────────────────────────────────────────────
  const diffs = ["todas", "Fácil", "Moderada", "Difícil"];
  // Ranking: promedio de las calificaciones de la comunidad. Con menos de 2 opiniones
  // no se rankea — una sola estrella de un amigo no puede poner una ruta arriba de todo.
  const ratingDeRuta = (routeIdx) => {
    const revs = reviews[routeIdx] || [];
    const conNota = revs.filter(rev => typeof rev.r === "number" && rev.r > 0);
    if (conNota.length === 0) return { prom: 0, cant: 0 };
    return { prom: conNota.reduce((a, rev) => a + rev.r, 0) / conNota.length, cant: conNota.length };
  };

  const filtered = routes
    .map((r, idx) => ({ r, idx }))
    .filter(({ r }) => (filtroOrigen === "grupos" ? !!r._grupoId : !r._grupoId))
    .filter(({ r }) => filterDiff === "todas" || r.diff === filterDiff)
    .filter(({ r }) => {
      const norm = t => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const palabras = norm(busquedaRutas).split(/\s+/).filter(Boolean);
      if (!palabras.length || vistaActiva !== "publicas") return true;
      const pajar = norm([r.name, r.desde, r.hasta, r.desc, r.camino, r.creator?.user].join(" "));
      return palabras.every(p => pajar.includes(p));
    })
    .sort((a, b) => {
      if (ordenRutas === "km") return (b.r.km || 0) - (a.r.km || 0);
      if (ordenRutas === "rating") {
        const ra = ratingDeRuta(a.idx), rb = ratingDeRuta(b.idx);
        // Las que tienen 2+ opiniones van primero; entre ellas, por promedio
        const pesoA = ra.cant >= 2 ? ra.prom : -1, pesoB = rb.cant >= 2 ? rb.prom : -1;
        if (pesoB !== pesoA) return pesoB - pesoA;
        return rb.cant - ra.cant;
      }
      return 0; // "nuevas": ya vienen ordenadas por fecha desde Supabase
    })
    .map(({ r }) => r);

  return (
    <PageWrap>
      {/* modal editar parada */}
      {editingStop && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={e => e.target === e.currentTarget && setEditingStop(null)}>
          <div style={{ background: C.card, borderRadius: 20, padding: 24, width: "100%", maxWidth: 380 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>Editar parada</h3>
              <button onClick={() => setEditingStop(null)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Nombre</div>
              <input value={editingStop.nombre} onChange={e => setEditingStop(s => ({ ...s, nombre: e.target.value }))}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Descripción</div>
              <textarea value={editingStop.desc} onChange={e => setEditingStop(s => ({ ...s, desc: e.target.value }))}
                style={{ width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none" }} />
            </div>
            <Btn full onClick={() => {
              setRoutes(prev => prev.map((ro, ri) => ri !== editingStop.routeIdx ? ro : {
                ...ro, stops: ro.stops.map((s, si) => si !== editingStop.stopIdx ? s : { ...s, nombre: editingStop.nombre, desc: editingStop.desc })
              }));
              setEditingStop(null);
            }}>Guardar cambios</Btn>
          </div>
        </div>
      )}

      <div style={{ padding: "var(--header-pt) 24px 0", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Rutas</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: C.text,
            letterSpacing: "-0.03em", lineHeight: 1.1,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}>Mapa</h1>
          <span style={{ fontSize: 13, color: C.muted, letterSpacing: "-0.005em" }}>
            {vistaActiva === "publicas" ? `${filtered.length} rutas` : vistaActiva === "trazas" ? `${misTrazas.length} recorridos` : ""}
          </span>
        </div>
        <VideoAyuda seccion="rutas" style={{ marginBottom: 14 }} />
        {/* Rodada en vivo de un evento al que estoy anotado: arriba de las solapas */}
        {session && <TarjetaRodadaEnVivo session={session} style={{ marginBottom: 12 }} />}
        {/* tabs estilo iOS — segmented control */}
        <div style={{
          display: "flex", gap: 4, padding: 3, marginBottom: 16,
          background: C.surface, borderRadius: 10,
        }}>
          {[["mapa", "🗺️ Mapa"], ["publicas", "Rutas"], ["trazas", "Recorridos"], ["circuitos", "🏁 Circuitos"], ["viajes", "🧳 Viajes"]].map(([k, l]) => (
            <button key={k} onClick={() => setVistaActiva(k)}
              style={{
                flex: 1, padding: "7px 0", whiteSpace: "nowrap",
                background: vistaActiva === k ? C.card : "transparent",
                border: "none", borderRadius: 8,
                color: vistaActiva === k ? "#111111" : C.muted,
                fontSize: 12, fontWeight: 500, cursor: "pointer",
                letterSpacing: "-0.01em",
                boxShadow: vistaActiva === k ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                transition: "all .15s ease",
              }}>
              {l}
            </button>
          ))}
        </div>
        {/* pestaña Mapa: el navegador se abre solo; este panel queda detrás para
            cuando lo minimizás con la barra roja */}
        {vistaActiva === "mapa" && (
          <div style={{ padding: "28px 20px", textAlign: "center", background: C.card, borderRadius: 16, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🗺️</div>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: C.text, marginBottom: 6, letterSpacing: "-0.01em" }}>El mapa de Olbos</div>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
              Tu posición en vivo, el grabador de recorridos, compartir tu camino con la familia e invitar a rodar. Si está grabando de fondo, volvés con la barra roja de arriba.
            </p>
            <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }))}
              style={{ width: "100%", maxWidth: 300, padding: "16px", borderRadius: 13, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 16, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.01em" }}>
              🧭 Entrar al mapa
            </button>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>Tus recorridos, las rutas de la comunidad y los viajes están en las solapas de arriba.</div>
          </div>
        )}
        {vistaActiva === "viajes" && (
          <SeccionViajes userId={session?.user?.id || null} />
        )}
        {vistaActiva === "circuitos" && (
          <SeccionCircuitos userId={session?.user?.id || null} />
        )}
        {vistaActiva === "publicas" && <CaminosComunidad session={session} />}

        {/* filtro origen (solo en Rutas): públicas o de mis grupos */}
        {vistaActiva === "publicas" && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[["publicas", "🌎 Públicas"], ["grupos", "👥 Mis grupos"]].map(([k, l]) => (
              <button key={k} onClick={() => setFiltroOrigen(k)}
                style={{ padding: "6px 13px", borderRadius: 100, fontSize: 12, fontWeight: filtroOrigen === k ? 700 : 500, background: filtroOrigen === k ? C.text : "transparent", color: filtroOrigen === k ? "#fff" : C.muted, border: `1px solid ${filtroOrigen === k ? C.text : C.border}`, cursor: "pointer" }}>
                {l}
              </button>
            ))}
          </div>
        )}
        {/* buscador por provincia, ciudad o nombre (solo en públicas) */}
        {vistaActiva === "publicas" && (
          <div style={{ position: "relative", paddingBottom: 12 }}>
            <input value={busquedaRutas} onChange={e => setBusquedaRutas(e.target.value)}
              placeholder="Buscar por provincia, ciudad o nombre…"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 34px 10px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14, outline: "none", fontFamily: "inherit" }} />
            {busquedaRutas && (
              <button onClick={() => setBusquedaRutas("")} aria-label="Borrar búsqueda"
                style={{ position: "absolute", right: 8, top: 5, width: 28, height: 28, border: "none", background: "transparent", color: C.muted, fontSize: 18, cursor: "pointer" }}>×</button>
            )}
          </div>
        )}
        {/* filtros dificultad (solo en públicas) */}
        {vistaActiva === "publicas" && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 14 }}>
            {diffs.map(d => (
              <button key={d} onClick={() => setFilterDiff(d)} style={{
                padding: "6px 12px", borderRadius: 100, fontSize: 12,
                fontWeight: 500, whiteSpace: "nowrap", cursor: "pointer",
                background: filterDiff === d ? "#111111" : C.surface,
                border: "none",
                color: filterDiff === d ? "#fff" : "#111111",
                letterSpacing: "-0.005em",
                transition: "all .15s ease",
              }}>{d === "todas" ? "Todas" : d}</button>
            ))}
          </div>
        )}
        {/* orden: lo que la comunidad votó mejor, lo último, o las más largas */}
        {vistaActiva === "publicas" && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 14 }}>
            {[["nuevas", "Últimas"], ["rating", "★ Mejor calificadas"], ["km", "Más largas"]].map(([id, label]) => (
              <button key={id} onClick={() => setOrdenRutas(id)} style={{
                padding: "5px 11px", borderRadius: 100, fontSize: 11.5,
                fontWeight: ordenRutas === id ? 700 : 500, whiteSpace: "nowrap", cursor: "pointer",
                background: "transparent",
                border: `1px solid ${ordenRutas === id ? C.text : C.border}`,
                color: ordenRutas === id ? C.text : C.muted,
                letterSpacing: "-0.005em",
              }}>{label}</button>
            ))}
          </div>
        )}
        {vistaActiva === "trazas" && <div style={{ height: 14 }} />}
      </div>

      {/* CONTENIDO ─ Mis recorridos */}
      {vistaActiva === "trazas" && (
        <div style={{ padding: "20px 18px 80px" }}>
          {!userId ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                Iniciá sesión para grabar y guardar tus recorridos.
              </p>
            </div>
          ) : (
            <>
              {/* CTA grabar — Apple-style */}
              <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }))}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: 12,
                  background: C.accent, color: "#fff",
                  border: "none", fontWeight: 500, fontSize: 15,
                  cursor: "pointer", marginBottom: 16,
                  letterSpacing: "-0.01em",
                  transition: "background .15s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#000"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#111111"; }}>
                Grabar nuevo recorrido
              </button>

              <div style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 12, padding: "12px 14px",
                marginBottom: 22,
                fontSize: 12.5, color: C.muted, lineHeight: 1.5,
                letterSpacing: "-0.005em",
              }}>
                <strong style={{ color: C.text, fontWeight: 600 }}>¿Para qué sirve?</strong> Grabá tu recorrido en tiempo real, sumá fotos en el camino y revivilo después. Ideal para off-road donde Google Maps no llega.
              </div>

              {nCircuitos > 0 && (
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  {[["todos", `Todos · ${misTrazas.length}`], ["circuitos", `🏁 Circuitos · ${nCircuitos}`]].map(([k, l]) => (
                    <button key={k} onClick={() => setFiltroTrazas(k)} style={{ padding: "8px 14px", borderRadius: 999, border: `1px solid ${filtroTrazas === k ? C.accent : C.border}`, background: filtroTrazas === k ? C.accent : C.card, color: filtroTrazas === k ? "#F9F5F2" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
                  ))}
                </div>
              )}
              {cargandoTrazas ? (
                <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
              ) : misTrazas.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center" }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: C.surface,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 18px",
                  }}>
                    <Map size={24} strokeWidth={1.5} color={C.muted} />
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>Todavía no tenés recorridos</h3>
                  <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>Tu primer recorrido queda guardado acá apenas lo termines.</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 16px" }}>
                  {trazasVisibles.map((t) => {
                    const puntos = Array.isArray(t.puntos) ? t.puntos : [];
                    const tienePuntos = puntos.length >= 2;
                    // Usar primer + último + algunos del medio para preview
                    // Se simplifica por forma (no 20 puntos parejos): en un circuito con
                    // vueltas, 20 puntos parejos unían vueltas distintas con rectas
                    // cruzadas y la miniatura no se parecía en nada al recorrido (Carlos, 19-sep)
                    const puntosPreview = tienePuntos ? (puntos.length <= 120 ? puntos : downsampleRecorrido(puntos, 120)) : [];
                    // Un recorrido sin terminar (grabando/pausado) NO se abre como recorrido
                    // cerrado: abre el grabador, que ofrece retomarlo. Antes se abría el
                    // detalle y no había forma visible de reanudar (feedback 08-ago-2026).
                    const sinTerminar = t.estado === "grabando" || t.estado === "pausada";
                    return (
                      <button key={t.id} onClick={() => sinTerminar ? window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: { retomarId: t.id } })) : setDetalleTraza(t)} style={{
                        width: "100%",
                        background: C.card,
                        border: sinTerminar ? "1.5px solid #27AE60" : `1px solid ${C.border}`,
                        borderRadius: 14,
                        overflow: "hidden",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        boxShadow: "0 1px 4px rgba(0,0,0,.04)",
                      }}>
                        {/* Mini mapa */}
                        {tienePuntos ? (
                          <div style={{ height: 160, background: "#f6f1eb", position: "relative", pointerEvents: "none" }}>
                            <MapaPreview puntos={puntosPreview} alto={160} sinInteraccion track />
                            {sinTerminar && (
                              <span style={{
                                position: "absolute", top: 10, left: 10,
                                fontSize: 11, padding: "5px 10px", borderRadius: 100,
                                background: "#27AE60", color: "#fff",
                                fontWeight: 700, letterSpacing: "0.01em",
                                display: "inline-flex", alignItems: "center", gap: 5,
                              }}>⏸ En pausa · tocá para seguir</span>
                            )}
                            {sesionesCirc[t.id] && (
                              <span onClick={(e) => { e.stopPropagation(); verSesionCirc(t, sesionesCirc[t.id]); }} style={{
                                position: "absolute", bottom: 10, left: 10, pointerEvents: "auto",
                                fontSize: 11, padding: "5px 10px", borderRadius: 100,
                                background: "#111111", color: "#F9F5F2", fontWeight: 800, cursor: "pointer",
                              }}>🏁 {sesionesCirc[t.id].n_vueltas} vueltas · mejor {fmtSegVuelta(Number(sesionesCirc[t.id].mejor_seg))} ›</span>
                            )}
                            {t.publica && (
                              <span style={{
                                position: "absolute", top: 10, right: 10,
                                fontSize: 10, padding: "3px 8px", borderRadius: 100,
                                background: "rgba(29,29,31,.85)", color: "#fff",
                                fontWeight: 500, letterSpacing: "0.02em",
                                backdropFilter: "blur(4px)",
                              }}>Pública</span>
                            )}
                          </div>
                        ) : (
                          <div style={{ height: 60, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 11 }}>
                            Sin puntos GPS
                          </div>
                        )}
                        {/* Info */}
                        <div style={{ padding: "12px 14px" }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, lineHeight: 1.3, letterSpacing: "-0.01em", marginBottom: 2 }}>
                            {t.titulo || "Sin título"}
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, letterSpacing: "-0.005em", marginBottom: 8 }}>
                            {t.iniciada_en && new Date(t.iniciada_en).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" })}
                          </div>
                          <div style={{ display: "flex", gap: 14, fontSize: 12, color: C.muted, letterSpacing: "-0.005em" }}>
                            <span><strong style={{ color: C.text, fontWeight: 600 }}>{kmTraza(t) ? kmTraza(t).toFixed(1) : "—"}</strong> km{enAuto(t) ? " · 🚗 en auto" : ""}</span>
                            <span><strong style={{ color: C.text, fontWeight: 600 }}>{t.duracion_seg ? formatDur(t.duracion_seg) : "—"}</strong></span>
                            {t.velocidad_prom && <span><strong style={{ color: C.text, fontWeight: 600 }}>{Math.round(t.velocidad_prom)}</strong> km/h</span>}
                            {Array.isArray(t.fotos) && t.fotos.length > 0 && <span>{t.fotos.length} 📷</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CONTENIDO ─ Rutas públicas (existente) */}
      {vistaActiva === "publicas" && <div style={{ padding: "16px 18px 80px" }}>
        {/* banner colaborativo */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 18, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Users size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>Alertas, paradas y condiciones actualizadas por la comunidad en tiempo real.</span>
        </div>

        {busquedaRutas.trim() && filtered.length === 0 && (
          <div style={{ textAlign: "center", color: C.muted, fontSize: 13.5, padding: "28px 10px" }}>No encontramos rutas para "{busquedaRutas.trim()}". Probá con otra provincia o ciudad.</div>
        )}
        {filtered.map((r, i) => {
          const routeIdx = routes.indexOf(r);
          // Construir puntos para el mapa preview
          const puntos = [];
          if (r.lat_from && r.lng_from) puntos.push({ nombre: r.desde, lat: r.lat_from, lng: r.lng_from });
          if (r.stops) r.stops.forEach(s => { if (s.lat && s.lng) puntos.push({ nombre: s.nombre, lat: s.lat, lng: s.lng }); });
          if (r.lat_to && r.lng_to) puntos.push({ nombre: r.hasta, lat: r.lat_to, lng: r.lng_to });
          // Si no hay coordenadas explícitas pero las stops tienen, usar las stops
          const tienePuntos = puntos.length >= 2;

          return (
          <div key={r.id || r._supabaseId || i} onClick={() => setSel(routeIdx)} style={{
            background: C.card, borderRadius: 14, border: `1px solid ${C.border}`,
            overflow: "hidden", marginBottom: 14, cursor: "pointer",
            boxShadow: "0 1px 4px rgba(0,0,0,.06)",
          }}>
            {/* mapa con pinpoints */}
            <div style={{ height: 180, position: "relative", overflow: "hidden", background: "#f6f1eb" }}>
              {tienePuntos ? (
                <MapaPreview puntos={puntos} alto={180} sinInteraccion polyline={r.polyline} />
              ) : (
                <img loading="lazy" decoding="async" src={r.img} alt={r.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
              <div style={{ position: "absolute", top: 10, right: 12, zIndex: 5, pointerEvents: "none", display: "flex", gap: 5 }}>
                {r._grupoNombre && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: "rgba(17,17,17,.75)", color: "#F9F5F2", backdropFilter: "blur(4px)" }}>👥 {r._grupoNombre}</span>
                )}
                <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 4, background: "rgba(0,0,0,.6)", color: "#fff", backdropFilter: "blur(4px)" }}>{r.diff}</span>
              </div>
              <div style={{ position: "absolute", bottom: 10, left: 12, right: 12, zIndex: 5, pointerEvents: "none" }}>
                <div style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, background: "rgba(0,0,0,.7)", color: "#fff" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.2 }}>{r.name}</h3>
                </div>
              </div>
            </div>
            {/* info */}
            <div style={{ padding: "12px 14px 14px" }}>
              {r.desc && <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>{r.desc}</p>}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 12 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{r.km} km</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{r.dur}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{r.camino}</span>
                </div>
                <span style={{ fontSize: 12, color: C.muted }}>{(reviews[routeIdx] || []).length} experiencias</span>
              </div>

              {/* quién la creó + qué le puso la comunidad */}
              {(() => {
                const { prom, cant } = ratingDeRuta(routeIdx);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                    {r.creator?.avatarUrl && (
                      <img loading="lazy" decoding="async" src={r.creator.avatarUrl} alt="" style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      por <strong style={{ color: C.text, fontWeight: 600 }}>@{r.creator?.user || "rider"}</strong>
                    </span>
                    <div style={{ flex: 1 }} />
                    {cant > 0 ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                        <Stars n={prom} size={11} />
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>{prom.toFixed(1)}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>({cant})</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>Sin calificar</span>
                    )}
                  </div>
                );
              })()}
              {r.alerts && r.alerts.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={12} strokeWidth={2} color="#B07D1A" />
                  <span style={{ fontSize: 11, color: "#B07D1A" }}>{r.alerts.length} alerta{r.alerts.length > 1 ? "s" : ""} activa{r.alerts.length > 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
          </div>
          );
        })}

        {/* panel admin — rutas pendientes */}
        {isAdmin && pendingRoutes.length > 0 && (
          <div style={{ marginTop: 20, background: `${C.gold}14`, border: `1px solid ${C.gold}55`, borderRadius: 14, padding: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
              <Shield size={16} strokeWidth={2} color="#B07D1A" />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#8B6914" }}>Rutas pendientes de aprobación ({pendingRoutes.length})</span>
            </div>
            {pendingRoutes.map(p => (
              <div key={p.id} style={{ background: C.card, borderRadius: 12, overflow: "hidden", marginBottom: 12, border: `1px solid ${C.gold}55` }}>
                {p.foto_url && (
                  <div style={{ height: 120, position: "relative", overflow: "hidden" }}>
                    <img loading="lazy" decoding="async" src={p.foto_url} alt={p.nombre} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.8 }} />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.6) 0%, transparent 60%)" }} />
                    <div style={{ position: "absolute", bottom: 10, left: 12, right: 12 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{p.nombre}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,.85)" }}>
                        {p.km}km · {p.diff} · {p.desde?.nombre} → {p.hasta?.nombre}
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ padding: "12px 14px" }}>
                  {!p.foto_url && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{p.nombre}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {p.km}km · {p.diff} · {p.desde?.nombre} → {p.hasta?.nombre}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                    @{p.username} · {new Date(p.created_at).toLocaleDateString("es-AR")}
                  </div>
                  {p.desc_texto && <p style={{ fontSize: 12, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>{p.desc_texto}</p>}
                  {p.alertas && (
                    <div style={{ fontSize: 11, color: "#B07D1A", background: "#FFF7E6", padding: "6px 8px", borderRadius: 6, marginBottom: 8 }}>
                      ⚠️ {p.alertas}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
                    📍 {p.paradas.filter(s => s.tipo !== "paso").length} paradas: {p.paradas.filter(s => s.tipo !== "paso").map(s => s.nombre).join(" → ")}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => aprobarRuta(p)}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: "#276749", color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer" }}>
                      ✓ Aprobar
                    </button>
                    <button onClick={() => rechazarRuta(p)}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: `${C.red}14`, color: C.red, fontWeight: 700, fontSize: 13, border: `1px solid #FED7D7`, cursor: "pointer" }}>
                      ✗ Rechazar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* publicar ruta */}
        <button style={{
          width: "100%", padding: "14px", borderRadius: 12,
          border: `1px dashed ${C.border}`, background: "transparent",
          color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          marginTop: 14,
        }}>
          <Plus size={16} strokeWidth={2} /> Publicar una ruta
        </button>
      </div>}

      {/* TRACK RECORDER + DETALLE DE TRAZA */}
      {/* El TrackRecorder se monta en la raíz de la app: cuando termina una
          grabación avisa por "olbos-trazas-actualizadas" y acá se recarga. */}

      {detalleTraza && (
        <DetalleTrazaModal
          traza={detalleTraza}
          onCerrar={() => setDetalleTraza(null)}
          onEliminar={() => eliminarTraza(detalleTraza.id)}
          onTogglePublica={() => togglePublicaTraza(detalleTraza)}
          onRenombrar={(titulo) => renombrarTraza(detalleTraza, titulo)}
          onFotosActualizadas={(fotos) => setMisTrazas(t => t.map(x => x.id === detalleTraza.id ? { ...x, fotos } : x))}
        />
      )}

      {/* botón flotante siempre visible */}
      <button onClick={() => {
        if (vistaActiva === "circuitos") {
          // Crear circuito: abre el mapa directo en modo "circuito nuevo" (das una vuelta y queda guardado)
          if (!session?.user?.id) { alert("Iniciá sesión para crear un circuito."); return; }
          window.olbosPendingCircuitoNuevo = true;
          window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} }));
          return;
        }
        setShowPublicar(true);
      }} style={{
        position: "fixed", bottom: 78, right: 18,
        padding: "7px 14px", borderRadius: 20,
        background: C.card, color: C.muted,
        display: "flex", alignItems: "center", gap: 5,
        boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
        border: `1px solid ${C.border}`, cursor: "pointer", zIndex: 50,
        fontSize: 13, fontWeight: 600,
      }}>
        <Plus size={14} strokeWidth={2} color="#555" /> {vistaActiva === "circuitos" ? "Crear circuito" : "Publicar ruta"}
      </button>

      {/* toast confirmación */}
      {submitted && (
        <div style={{ position: "fixed", bottom: 120, left: "50%", transform: "translateX(-50%)", background: C.text, color: "#fff", padding: "12px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600, zIndex: 300, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 20px rgba(0,0,0,.2)", whiteSpace: "nowrap" }} className="fade-up">
          <CheckCircle size={16} strokeWidth={2} color={C.green} />
          ¡Ruta publicada! Ya la puede ver la comunidad
        </div>
      )}

      {publicarModalJSX}
    </PageWrap>
  );
};

// ─── DM DATA ─────────────────────────────────────────────────────────────────
const DM_CONVOS = [
  {
    id: 1, user: "PatagoniaRider", avatar: "🧔", avatarUrl: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=80&q=80", moto: "KTM 790 Adventure",
    seguidores: 2100, online: true,
    msgs: [
      { from: "them", txt: "Che, ¿vas a ir al Cruce Cordillera en julio?", t: "Ayer" },
      { from: "me", txt: "Sí! Me anoté ayer. Vos?", t: "Ayer" },
      { from: "them", txt: "Todavía no, esperando que me aprueben. ¿Con qué moto vas?", t: "Ayer" },
      { from: "me", txt: "Con la GS. Vos con la 790 estás perfecto para ese camino.", t: "10:30" },
      { from: "them", txt: "Jaja sí, espero que no haya tanto ripio como el año pasado 😅", t: "10:31" },
    ]
  },
  {
    id: 2, user: "CamiRutera", avatar: "👩", avatarUrl: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80", moto: "Royal Enfield Meteor",
    seguidores: 8700, online: false,
    msgs: [
      { from: "them", txt: "Hermosas las fotos del NOA! ¿Cuántos días estuviste?", t: "Lun" },
      { from: "me", txt: "5 días! La ruta de Salta a Cafayate es increíble", t: "Lun" },
      { from: "them", txt: "Lo tengo pendiente hace tiempo. ¿Sirve la Meteor para esa ruta?", t: "Lun" },
    ]
  },
  {
    id: 3, user: "NomadaMoto", avatar: "👨", avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80", moto: "Honda Africa Twin",
    seguidores: 4200, online: true,
    msgs: [
      { from: "them", txt: "¿Tenés datos del paso Cardenal Samoré en invierno?", t: "Mar" },
      { from: "me", txt: "Sí, estuve en agosto. Hay tramos helados al amanecer, mejor salir tarde.", t: "Mar" },
    ]
  },
];

// ─── MODAL: CONVERSACIÓN DM ───────────────────────────────────────────────────
const DMConvoModal = ({ convo, onClose }) => {
  const [msgs, setMsgs] = useState(convo.msgs);
  const [input, setInput] = useState("");
  const bottomRef = useRef(null);

  const send = () => {
    if (!input.trim()) return;
    setMsgs(m => [...m, { from: "me", txt: input, t: "ahora" }]);
    setInput("");
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: C.card, zIndex: 300,
      display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto",
    }}>
      {/* header */}
      <div style={{
        padding: "48px 16px 12px", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", gap: 12, background: C.card,
        flexShrink: 0,
      }}>
        <button onClick={onClose} style={{ color: C.text, display: "flex", alignItems: "center" }}><ChevronLeft size={26} strokeWidth={2} /></button>
        <div style={{ position: "relative" }}>
          {convo.avatarUrl
            ? <img loading="lazy" decoding="async" src={convo.avatarUrl} alt={convo.user} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
            : <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{convo.avatar}</div>
          }
          {convo.online && <div style={{ position: "absolute", bottom: 1, right: 1, width: 10, height: 10, borderRadius: "50%", background: C.green, border: "2px solid #fff" }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>@{convo.user}</div>
          <div style={{ fontSize: 11, color: convo.online ? C.green : C.muted }}>{convo.online ? "En línea" : "Desconectado"} · {convo.moto}</div>
        </div>
        <button style={{ fontSize: 20, color: C.muted, padding: 4 }}>⋯</button>
      </div>

      {/* mensajes */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 8px" }}>
        {msgs.map((m, i) => {
          const isMe = m.from === "me";
          return (
            <div key={i} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 8 }}>
              {!isMe && (
                convo.avatarUrl
                  ? <img loading="lazy" decoding="async" src={convo.avatarUrl} alt={convo.user} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", marginRight: 8, flexShrink: 0, alignSelf: "flex-end" }} />
                  : <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, marginRight: 8, flexShrink: 0, alignSelf: "flex-end" }}>{convo.avatar}</div>
              )}
              <div style={{ maxWidth: "72%" }}>
                <div style={{
                  padding: "10px 14px", borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                  background: isMe ? "#4A7BC4" : C.surface,
                  color: isMe ? "#fff" : C.text,
                  fontSize: 14, lineHeight: 1.5,
                }}>
                  {m.txt}
                </div>
                <div style={{ fontSize: 10, color: C.faint, marginTop: 3, textAlign: isMe ? "right" : "left" }}>{m.t}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* input */}
      <div style={{
        padding: "10px 16px 28px", borderTop: `1px solid ${C.border}`,
        display: "flex", gap: 10, alignItems: "center", background: C.card, flexShrink: 0,
      }}>
        <input
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Mensaje..."
          style={{
            flex: 1, padding: "10px 16px", borderRadius: 24,
            background: C.surface, border: `1px solid ${C.border}`,
            fontSize: 14, color: C.text,
          }}
        />
        <button onClick={send} style={{
          width: 40, height: 40, borderRadius: "50%",
          background: input.trim() ? "#4A7BC4" : C.faint,
          color: "#fff", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background .2s",
        }}>↑</button>
      </div>
    </div>
  );
};

// ─── MODAL: LISTA DMs ─────────────────────────────────────────────────────────
const DMListModal = ({ onClose, onOpen }) => (
  <div style={{
    position: "fixed", inset: 0, background: C.card, zIndex: 300,
    display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto",
  }}>
    <div style={{ padding: "max(48px, calc(var(--sat) + 12px)) 18px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
      <button onClick={onClose} style={{ color: C.text, display: "flex" }}><ChevronLeft size={26} strokeWidth={2} /></button>
      <h2 style={{ fontSize: 18, fontWeight: 700, flex: 1 }}>Mensajes</h2>
      <button style={{ color: C.accent, display: "flex" }}><Plus size={24} strokeWidth={2} /></button>
    </div>
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
      {DM_CONVOS.map(c => (
        <button key={c.id} onClick={() => onOpen(c)} style={{
          width: "100%", padding: "12px 18px", display: "flex", gap: 12, alignItems: "center",
          borderBottom: `1px solid ${C.border}`, background: "transparent", textAlign: "left",
          transition: "background .15s",
        }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            {c.avatarUrl
              ? <img loading="lazy" decoding="async" src={c.avatarUrl} alt={c.user} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }} />
              : <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>{c.avatar}</div>
            }
            {c.online && <div style={{ position: "absolute", bottom: 2, right: 2, width: 12, height: 12, borderRadius: "50%", background: C.green, border: "2px solid #fff" }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>@{c.user}</span>
              <span style={{ fontSize: 11, color: C.muted }}>{c.msgs[c.msgs.length - 1].t}</span>
            </div>
            <div style={{ fontSize: 13, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {c.msgs[c.msgs.length - 1].from === "me" ? "Vos: " : ""}{c.msgs[c.msgs.length - 1].txt}
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ─── MODAL: COMENTARIOS POST ──────────────────────────────────────────────────
const CommentsModal = ({ post, onClose }) => {
  const [comments, setComments] = useState([
    { user: "FedeMoto_GS", avatar: "🧔", txt: "Increíble foto! ¿Cuánto tardaste en llegar?", t: "1h" },
    { user: "AndeanRider", avatar: "🧑", txt: "La Ruta 40 en moto es otro mundo. 🙌", t: "2h" },
    { user: "GS_Cris", avatar: "🧔", txt: "¿Con qué nafta cargaste? Vi que hay tramos sin estación.", t: "3h" },
  ]);
  const [input, setInput] = useState("");

  return (
    <Modal title="Comentarios" onClose={onClose}>
      {/* post resumido */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{post.avatar || "👤"}</div>
        <div>
          <span style={{ fontWeight: 700, fontSize: 13 }}>@{post.u}</span>
          <p style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{post.txt.slice(0, 80)}...</p>
        </div>
      </div>

      {/* comentarios */}
      <div style={{ maxHeight: 280, overflowY: "auto", marginBottom: 14 }}>
        {comments.map((c, i) => (
          <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{c.avatar}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{c.user}</span>
                <span style={{ fontSize: 10, color: C.muted }}>{c.t}</span>
              </div>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{c.txt}</p>
              <button style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Me gusta · Responder</button>
            </div>
          </div>
        ))}
      </div>

      {/* agregar comentario */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.accentLo, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>⭐</div>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && input.trim()) { setComments(c => [...c, { user: "Vos", avatar: "⭐", txt: input, t: "ahora" }]); setInput(""); }}}
          placeholder="Agregá un comentario..."
          style={{ flex: 1, padding: "9px 14px", borderRadius: 24, background: C.surface, border: `1px solid ${C.border}`, fontSize: 13, color: C.text }} />
        <button onClick={() => { if (input.trim()) { setComments(c => [...c, { user: "Vos", avatar: "⭐", txt: input, t: "ahora" }]); setInput(""); }}}
          style={{ color: C.accent, fontWeight: 700, fontSize: 13 }}>Publicar</button>
      </div>
    </Modal>
  );
};

// ─── FEED ITEMS DATA ─────────────────────────────────────────────────────────
// ─── DATA ─────────────────────────────────────────────────────────────────────
// Tipos de item en el feed: "post" | "reel" | "ad"
const UNSPLASH = {
  ruta40:    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
  andes:     "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
  nafta:     "https://images.unsplash.com/photo-1545127398-14699f92334b?w=800&q=80",
  sunset:    "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=800&q=80",
  paso:      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80",
  chile:     "https://images.unsplash.com/photo-1524397057410-1e775ed476f3?w=800&q=80",
  lago:      "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80",
  flores:    "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?w=800&q=80",
  nieve:     "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80",
  bosque:    "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80",
  moto1:     "https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=800&q=80",
  moto2:     "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800&q=80",
  mendoza:   "https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=800&q=80",
  noa:       "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80",
  cafayate:  "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=800&q=80",
  urban:     "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&q=80",
  av1:       "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=80&q=80",
  av2:       "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80",
  av3:       "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80",
  av4:       "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=80&q=80",
};

// Pexels free-license video clips (motorcycle / road)
const PEXELS_VIDEOS = {
  road:     "https://videos.pexels.com/video-files/854100/854100-hd_1920_1080_25fps.mp4",
  mountain: "https://videos.pexels.com/video-files/1448735/1448735-hd_1920_1080_25fps.mp4",
  desert:   "https://videos.pexels.com/video-files/2169880/2169880-hd_1920_1080_25fps.mp4",
};

const ESPACIOS = [
  { id: "e1", tipo: "evento", label: "Vuelta Atlántica · Jun 2026", short: "Vuelta Atlántica",        icon: "📅", color: "#4A7FB5" },
  { id: "e2", tipo: "evento", label: "Cruce Cordillera · Jul 2026", short: "Cruce Cordillera",        icon: "📅", color: "#4A7FB5" },
  { id: "r1", tipo: "ruta",   label: "Ruta 40",                     short: "Ruta 40",                 icon: "🗺️", color: "#5A8A6A" },
  { id: "r2", tipo: "ruta",   label: "7 Lagos",                     short: "7 Lagos",                 icon: "🗺️", color: "#5A8A6A" },
  { id: "l1", tipo: "lugar",  label: "Enduro Park Argentina",        short: "Enduro Park AR",          icon: "📍", color: "#8A5A3A" },
  { id: "l2", tipo: "lugar",  label: "Paso Cardenal Samoré",         short: "Paso Samoré",            icon: "📍", color: "#8A5A3A" },
  { id: "l3", tipo: "lugar",  label: "Cachi · Salta",               short: "Cachi",                   icon: "📍", color: "#8A5A3A" },
  // Motos
  { id: "m_bmw1",  tipo: "moto", label: "BMW R 1300 GS",             short: "BMW R1300GS",              icon: "🏍️", color: "#1C69D4" },
  { id: "m_hon1",  tipo: "moto", label: "Honda Africa Twin 1100",    short: "Africa Twin",              icon: "🏍️", color: "#CC0000" },
  { id: "m_yam1",  tipo: "moto", label: "Yamaha MT-09",              short: "MT-09",                    icon: "🏍️", color: "#003087" },
  { id: "m_ktm1",  tipo: "moto", label: "KTM 790 Adventure",         short: "KTM 790 Adv",              icon: "🏍️", color: "#FF6600" },
  { id: "m_re1",   tipo: "moto", label: "Royal Enfield Himalayan 450", short: "Himalayan 450",           icon: "🏍️", color: "#8B4513" },
  { id: "m_tri1",  tipo: "moto", label: "Triumph Tiger 900 Rally",   short: "Tiger 900",                icon: "🏍️", color: "#003087" },
  { id: "m_duc1",  tipo: "moto", label: "Ducati Multistrada V4 S",   short: "Multistrada V4",           icon: "🏍️", color: "#CC0000" },
  { id: "m_yam2",  tipo: "moto", label: "Yamaha Ténéré 700",         short: "Ténéré 700",               icon: "🏍️", color: "#003087" },
  { id: "m_ktm2",  tipo: "moto", label: "KTM Duke 390",              short: "Duke 390",                 icon: "🏍️", color: "#FF6600" },
  { id: "m_kaw1",  tipo: "moto", label: "Kawasaki Z 900",            short: "Z900",                     icon: "🏍️", color: "#009900" },
  { id: "m_kym1",  tipo: "moto", label: "Kymco AK 550",               short: "Kymco AK 550",             icon: "🏍️", color: "#E60012" },
];

const POSTS_TAGGED = [
  {
    id: 1, user: "FedeMoto_GS", avatarUrl: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=80&q=80",
    moto: "BMW R1250GS", t: "2h", tipo: "post",
    espacioId: "e1",
    imgs: [
      "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
    ],
    txt: "Día 1 de la Vuelta Atlántica. Salimos 34 motos desde el Obelisco. Lo que se viene 🏍️", likes: 203, comments: 28,
  },
  {
    id: 2, user: "CamiRutera", avatarUrl: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80",
    moto: "Royal Enfield Meteor", t: "5h", tipo: "reel",
    espacioId: "r1", motoEspacioId: "m_yam1",
    thumbUrl: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=800&q=80",
    videoUrl: "https://videos.pexels.com/video-files/854100/854100-hd_1920_1080_25fps.mp4",
    duration: "0:42", audio: "original · CamiRutera",
    txt: "Ruta 40 al atardecer. No hay palabras.", likes: 1820, comments: 94,
  },
  {
    id: 3, user: "NomadaMoto", avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80",
    moto: "Honda Africa Twin", t: "1d", tipo: "post",
    espacioId: "l1",
    imgs: [
      "https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=800&q=80",
      "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800&q=80",
      "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&q=80",
    ],
    txt: "Enduro Park Argentina. Circuito nuevo habilitado, ripio técnico pero muy disfrutable. Los espero.", likes: 412, comments: 37,
  },
  {
    id: 4, user: "PatagoniaRider", avatarUrl: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=80&q=80",
    moto: "KTM 790 Adventure", t: "2d", tipo: "post",
    espacioId: "e2",
    imgs: [
      "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80",
    ],
    txt: "Cruce Cordillera confirmado. Partimos el 5 de julio. Preparando la moto.", likes: 89, comments: 14,
  },
  {
    id: 5, user: "AndeanRider", avatarUrl: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=80&q=80",
    moto: "Triumph Tiger 900", t: "3d", tipo: "reel",
    espacioId: "l2", motoEspacioId: "m_tri1",
    thumbUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&q=80",
    videoUrl: "https://videos.pexels.com/video-files/1448735/1448735-hd_1920_1080_25fps.mp4",
    duration: "1:05", audio: "original",
    txt: "Paso Samoré con niebla. Una de las experiencias más épicas en moto.", likes: 3210, comments: 187,
  },
  {
    id: 6, user: "GS_Cris", avatarUrl: "https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=80&q=80",
    moto: "BMW R1250GS", t: "4d", tipo: "post",
    espacioId: "r1",
    imgs: [
      "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80",
      "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=800&q=80",
    ],
    txt: "Km 1200 de la 40. El NOA desde la moto es incomparable.", likes: 654, comments: 41,
  },
];

const FEED_ITEMS = [];

const getEspacio = (id) => ESPACIOS.find(e => e.id === id);


// ─── BADGE DE ESPACIO (aparece en feed) ──────────────────────────────────────
const EspacioBadge = ({ espacioId, onClick }) => {
  const e = getEspacio(espacioId);
  if (!e) return null;
  return (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 6,
      background: e.color + "12", border: `1px solid ${e.color}30`,
      cursor: "pointer", marginBottom: 8,
    }}>
      <span style={{ fontSize: 11 }}>{e.icon}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: e.color }}>{e.short}</span>
    </button>
  );
};


// ─── CARRUSEL DE IMÁGENES ─────────────────────────────────────────────────────
const Carousel = ({ images, onDoubleTap }) => {
  const [cur, setCur] = useState(0);
  const startX = useRef(null);
  const trackRef = useRef(null);

  const goTo = (i) => setCur(Math.max(0, Math.min(images.length - 1, i)));

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) dx < 0 ? goTo(cur + 1) : goTo(cur - 1);
    startX.current = null;
  };
  // Mouse drag support for desktop preview
  const mouseStart = useRef(null);
  const onMouseDown = (e) => { mouseStart.current = e.clientX; };
  const onMouseUp = (e) => {
    if (mouseStart.current === null) return;
    const dx = e.clientX - mouseStart.current;
    if (Math.abs(dx) > 40) dx < 0 ? goTo(cur + 1) : goTo(cur - 1);
    mouseStart.current = null;
  };

  return (
    <div style={{ position: "relative", overflow: "hidden", height: 300, userSelect: "none" }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown} onMouseUp={onMouseUp}
      onDoubleClick={onDoubleTap}>

      {/* track deslizable */}
      <div ref={trackRef} style={{ display: "flex", height: "100%", transition: "transform .28s cubic-bezier(.4,0,.2,1)", transform: `translateX(-${cur * 100}%)` }}>
        {images.map((img, i) => (
          <div key={i} style={{ minWidth: "100%", height: "100%", position: "relative", flexShrink: 0, background: `${C.border}` }}>
            {img.url ? (
              <img loading="lazy" decoding="async" src={img.url} alt={img.caption || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", background: img.bg || "linear-gradient(135deg, #e8e8e8 0%, #d0d0d0 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>{img.emoji}</div>
            )}
            {img.caption && (
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(0,0,0,.6) 0%, transparent 100%)", padding: "30px 14px 10px", fontSize: 12, color: "rgba(255,255,255,.9)", fontWeight: 500 }}>
                {img.caption}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* flechas laterales si hay más de una */}
      {images.length > 1 && cur > 0 && (
        <button onClick={() => goTo(cur - 1)} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.85)", backdropFilter: "blur(4px)", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.2)" }}>‹</button>
      )}
      {images.length > 1 && cur < images.length - 1 && (
        <button onClick={() => goTo(cur + 1)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.85)", backdropFilter: "blur(4px)", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.2)" }}>›</button>
      )}

      {/* dots indicadores */}
      {images.length > 1 && (
        <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
          {images.map((_, i) => (
            <div key={i} onClick={() => goTo(i)} style={{ width: i === cur ? 18 : 6, height: 6, borderRadius: 3, background: i === cur ? C.card : "rgba(255,255,255,.5)", transition: "all .25s", cursor: "pointer" }} />
          ))}
        </div>
      )}

      {/* badge contador arriba derecha */}
      {images.length > 1 && (
        <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20, backdropFilter: "blur(4px)" }}>
          {cur + 1}/{images.length}
        </div>
      )}
    </div>
  );
};

// ─── ITEM: POST ───────────────────────────────────────────────────────────────
const PostItem = ({ item, onComment, onDM, onEspacioClick }) => {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(item.likes);
  const [popping, setPopping] = useState(false);

  const toggleLike = () => {
    setLiked(l => !l);
    setLikes(l => liked ? l - 1 : l + 1);
    if (!liked) { setPopping(true); setTimeout(() => setPopping(false), 300); }
  };

  // si el post tiene imágenes múltiples las usa, sino crea una sola del emoji
  const images = item.images || [{ emoji: item.emoji, caption: `📍 ${item.loc}` }];

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      {/* header */}
      <div style={{ padding: "12px 16px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ position: "relative" }}>
          {item.avatarUrl
            ? <img loading="lazy" decoding="async" src={item.avatarUrl} alt={item.user} style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", border: item.verified ? `2px solid ${C.accent}` : `1px solid ${C.border}` }} />
            : <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, border: item.verified ? `2px solid ${C.accent}` : "none" }}>{item.avatar}</div>
          }
          {item.verified && <div style={{ position: "absolute", bottom: -1, right: -1, width: 14, height: 14, borderRadius: "50%", background: C.accent, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, color: "#fff", fontWeight: 700 }}>✓</div>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>@{item.user}</span>
            {item.seguidores > 10000 && <OTag>Top</OTag>}
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>{item.moto} · {item.t}</div>
        </div>
        <button onClick={() => onDM(item)} style={{ fontSize: 18, color: C.muted, padding: 4 }}>✉️</button>
      </div>

      {/* espacio badge */}
      {item.espacioId && onEspacioClick && (
        <div style={{ paddingLeft: 14, paddingBottom: 2 }}>
          <EspacioBadge espacioId={item.espacioId} onClick={() => onEspacioClick(item.espacioId)} />
        </div>
      )}
      {item.motoEspacioId && onEspacioClick && (
        <div style={{ paddingLeft: 14, paddingBottom: 4 }}>
          <EspacioBadge espacioId={item.motoEspacioId} onClick={() => onEspacioClick(item.motoEspacioId)} />
        </div>
      )}

      {/* carrusel */}
      <div style={{ position: "relative" }}>
        <Carousel images={images} onDoubleTap={toggleLike} />
        {popping && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80, animation: "heartPop .4s ease", pointerEvents: "none", zIndex: 10 }}>❤️</div>
        )}
      </div>

      {/* acciones */}
      <div style={{ padding: "10px 16px 4px", display: "flex", gap: 18, alignItems: "center" }}>
        <button onClick={toggleLike} style={{ color: liked ? C.red : C.text, transition: "all .15s", display: "flex", alignItems: "center" }}>
          <Heart size={26} strokeWidth={1.8} fill={liked ? C.red : "none"} color={liked ? C.red : C.text} />
        </button>
        <button onClick={() => onComment(item)} style={{ color: C.text, display: "flex", alignItems: "center" }}>
          <MessageCircle size={25} strokeWidth={1.8} />
        </button>
        <button style={{ color: C.text, display: "flex", alignItems: "center" }}>
          <Share2 size={23} strokeWidth={1.8} />
        </button>
        <button onClick={() => onDM(item)} style={{ color: C.text, marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <Bookmark size={23} strokeWidth={1.8} />
        </button>
      </div>
      <div style={{ padding: "2px 16px 12px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{likes.toLocaleString()} Me gusta</div>
        <p style={{ fontSize: 13, lineHeight: 1.6 }}><span style={{ fontWeight: 700 }}>@{item.user} </span><span style={{ color: C.muted }}>{item.txt}</span></p>
        <button onClick={() => onComment(item)} style={{ fontSize: 12, color: C.muted, marginTop: 4, display: "block" }}>Ver los {item.comments} comentarios</button>
      </div>
    </div>
  );
};


// ─── ITEM: REEL ───────────────────────────────────────────────────────────────
const ReelItem = ({ item, onComment, onDM, onEspacioClick }) => {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(item.likes);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef(null);

  // autoplay on mount
  useEffect(() => {
    const el = videoRef.current;
    if (el) { el.muted = true; el.play().catch(() => {}); }
  }, []);

  const togglePause = () => {
    const el = videoRef.current;
    if (!el) return;
    if (paused) { el.play().catch(() => {}); setPaused(false); }
    else { el.pause(); setPaused(true); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(m => !m);
  };

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div style={{ position: "relative", background: "#000", overflow: "hidden" }}>
        <div style={{ height: 480, position: "relative" }} onClick={togglePause}>
          {item.videoUrl ? (
            <video
              ref={videoRef}
              src={item.videoUrl}
              poster={item.thumbUrl}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              loop playsInline muted
            />
          ) : (
            <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${item.thumbUrl})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.7) 0%, transparent 50%)" }} />

          {/* acciones lado derecho */}
          <div style={{ position: "absolute", right: 14, bottom: 100, display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <button onClick={e => { e.stopPropagation(); setLiked(l => !l); setLikes(l => liked ? l-1 : l+1); }}
                style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
                <Heart size={24} strokeWidth={1.8} fill={liked ? C.red : "none"} color={liked ? C.red : "#fff"} />
              </button>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.85)", fontWeight: 600 }}>{likes.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <button onClick={e => { e.stopPropagation(); onComment(item); }}
                style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
                <MessageCircle size={22} strokeWidth={1.8} color="#fff" />
              </button>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.85)", fontWeight: 600 }}>{item.comments?.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <button onClick={e => e.stopPropagation()}
                style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
                <Share2 size={22} strokeWidth={1.8} color="#fff" />
              </button>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,.85)", fontWeight: 600 }}>{item.shares || ""}</span>
            </div>
            <button onClick={e => { e.stopPropagation(); onDM(item); }}
              style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
              <Send size={20} strokeWidth={1.8} color="#fff" />
            </button>
          </div>

          {/* info usuario abajo izquierda */}
          <div style={{ position: "absolute", bottom: 56, left: 14, right: 70 }}>
            {item.espacioId && onEspacioClick && (
              <div style={{ marginBottom: 8 }}>
                <EspacioBadge espacioId={item.espacioId} onClick={e => { e.stopPropagation(); onEspacioClick(item.espacioId); }} />
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              {item.avatarUrl
                ? <img loading="lazy" decoding="async" src={item.avatarUrl} alt={item.user} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,.7)" }} />
                : <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, border: "2px solid rgba(255,255,255,.5)" }}>{item.avatar}</div>
              }
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
                  @{item.user}
                  {item.verified && <span style={{ background: C.accent, color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 10 }}>✓</span>}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)" }}>{item.seguidores?.toLocaleString()} seguidores</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.9)", lineHeight: 1.5, marginBottom: 6 }}>{item.txt}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,.65)" }}>
              <span style={{ animation: !paused ? "spin 3s linear infinite" : "none", display: "inline-block" }}>🎵</span>
              <span>{item.audio}</span>
            </div>
          </div>

          {/* paused indicator */}
          {paused && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", opacity: 0.75, pointerEvents: "none" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
                <Play size={28} strokeWidth={1.5} color="#fff" fill="#fff" />
              </div>
            </div>
          )}

          {/* mute toggle — arriba derecha */}
          <button onClick={toggleMute} style={{
            position: "absolute", top: 12, right: 14,
            width: 36, height: 36, borderRadius: "50%",
            background: "rgba(0,0,0,.45)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {muted
              ? <VolumeX size={16} strokeWidth={2} color="#fff" />
              : <Volume2 size={16} strokeWidth={2} color="#fff" />
            }
          </button>

          {/* duración — arriba izquierda */}
          <div style={{ position: "absolute", top: 14, left: 14, background: "rgba(0,0,0,.45)", color: "#fff", fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 20, backdropFilter: "blur(4px)" }}>
            {item.duration}
          </div>
        </div>

        {/* barra de progreso — sin estado, el video maneja su propio tiempo */}
        <div style={{ height: 2, background: "rgba(0,0,0,.08)" }} />
      </div>
    </div>
  );
};



// ─── ITEM: AD (anuncio nativo) ────────────────────────────────────────────────
const AdItem = ({ item }) => {
  const [saved, setSaved] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  if (dismissed) return null;

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      {/* header del anuncio */}
      <div style={{ padding: "10px 16px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{item.avatar}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{item.brand}</span>
            <OTag>Patrocinado</OTag>
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>{item.tag} · {item.url}</div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => setShowInfo(true)} style={{ fontSize: 16, color: C.muted, padding: 4 }}>ⓘ</button>
          <button onClick={() => setDismissed(true)} style={{ fontSize: 16, color: C.muted, padding: 4 }}>✕</button>
        </div>
      </div>

      {/* imagen del anuncio */}
      <div style={{ height: 240, position: "relative", overflow: "hidden", background: C.accent }}>
        {item.imgUrl && <img loading="lazy" decoding="async" src={item.imgUrl} alt={item.brand} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.75) 0%, rgba(0,0,0,.3) 100%)" }} />
        <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", zIndex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: 10, letterSpacing: -0.3 }}>{item.headline}</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.75)", lineHeight: 1.6, marginBottom: 16 }}>{item.body}</div>
          <button style={{ background: C.card, color: C.text, fontWeight: 700, fontSize: 14, padding: "10px 28px", borderRadius: 24, border: "none", cursor: "pointer" }}>
            {item.cta} →
          </button>
        </div>
      </div>

      {/* acciones del ad */}
      <div style={{ padding: "10px 16px 12px", display: "flex", gap: 14, alignItems: "center" }}>
        <button onClick={() => setSaved(s => !s)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: saved ? C.accent : C.muted, fontWeight: saved ? 600 : 400 }}>
          <span style={{ fontSize: 18 }}>{saved ? "🔖" : "🏷️"}</span> {saved ? "Guardado" : "Guardar"}
        </button>
        <button style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.muted }}>
          <span style={{ fontSize: 18 }}>↗️</span> Compartir
        </button>
        <button onClick={() => setDismissed(true)} style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>No me interesa</button>
      </div>

      {/* modal info del anuncio */}
      {showInfo && (
        <Modal title="¿Por qué veo este anuncio?" onClose={() => setShowInfo(false)}>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 14 }}>
            Este anuncio de <strong style={{ color: C.text }}>{item.brand}</strong> aparece porque Olbos muestra publicidad de marcas relevantes para la comunidad motera.
          </p>
          <div style={{ background: C.surface, borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Política de anuncios Olbos</div>
            {["Los anuncios son claramente identificados como 'Patrocinado'",
              "Nunca compartimos tus datos personales con anunciantes",
              "Podés ocultar o reportar cualquier anuncio",
              "Los anunciantes pagan por visibilidad, no por tus datos",
              "Usuarios Premium ven menos anuncios (próximamente)"].map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "flex", gap: 8 }}>
                <span style={{ color: C.green }}>✓</span> {t}
              </div>
            ))}
          </div>
          <button onClick={() => setShowInfo(false)} style={{ width: "100%", padding: 12, borderRadius: 10, background: C.accent, color: C.text, fontWeight: 700, fontSize: 14 }}>Entendido</button>
        </Modal>
      )}
    </div>
  );
};


// ─── SECCIÓN: FEED ──────────────────────────────────────────────────────────
// ─── VISOR DE FOTO FULL SCREEN ───────────────────────────────────────────────
const FotoViewer = ({ url, onClose }) => (
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <button onClick={onClose} style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <X size={20} strokeWidth={2} color="#fff" />
    </button>
    <ImgZoom src={cldOpt(url, 1440)} alt="Foto de perfil" onClick={e => e.stopPropagation()}
      onError={e => { if (e.target.src !== url) e.target.src = url; }}
      style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,.6)" }} />
  </div>
);

// ─── VIDEO CON AUTOPAUSA ─────────────────────────────────────────────────────
// Aspect ratio del media en el feed: se respeta el de la foto/video real, acotado
// entre 9:16 (vertical máximo — flyers de eventos e historias, pedido de Matías:
// el tope 4:5 estilo Instagram los recortaba) y 1.91:1 (horizontal máximo).
const RATIO_DEFAULT = 1;
const clampRatio = (r) => (!r || !isFinite(r)) ? RATIO_DEFAULT : Math.min(1.91, Math.max(0.5625, r));

// Envuelve la media de un post: dos toques = like, con el corazón grande de Instagram.
// Pinch-zoom estilo Instagram: pellizcás y la foto/video se agranda siguiendo los
// dedos; al soltar vuelve sola con animación. Se aplica sobre un elemento via ref.
// Listeners manuales con passive:false (React no garantiza poder frenar el scroll).
const usePinchZoom = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let ini = null;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const medio = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    const onStart = (e) => {
      if (e.touches.length !== 2) return;
      const r = el.getBoundingClientRect();
      const m = medio(e.touches);
      ini = { d: dist(e.touches), x: m.x, y: m.y, ox: m.x - r.left, oy: m.y - r.top };
      el.style.transition = "none";
      el.style.zIndex = "999";
    };
    const onMove = (e) => {
      if (!ini || e.touches.length !== 2) return;
      e.preventDefault(); // que el pellizco no scrollee la página
      const esc = Math.min(Math.max(dist(e.touches) / ini.d, 1), 4);
      const m = medio(e.touches);
      el.style.transformOrigin = `${ini.ox}px ${ini.oy}px`;
      el.style.transform = `translate(${m.x - ini.x}px, ${m.y - ini.y}px) scale(${esc})`;
    };
    const onEnd = (e) => {
      if (!ini || e.touches.length >= 2) return;
      ini = null;
      el.style.transition = "transform .25s ease";
      el.style.transform = "";
      setTimeout(() => { el.style.zIndex = ""; el.style.transition = ""; }, 260);
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [ref]);
};

// Imagen suelta con pinch-zoom (para los visores fullscreen)
const ImgZoom = (props) => {
  const ref = useRef(null);
  usePinchZoom(ref);
  return <img loading="lazy" decoding="async" ref={ref} draggable={false} {...props} />;
};

const MediaDobleTap = ({ onLike, children }) => {
  const ultimoTap = useRef(0);
  const [burst, setBurst] = useState(0);
  const zoomRef = useRef(null);
  usePinchZoom(zoomRef); // pinch-zoom sobre todo el media del post, como Instagram
  const tap = () => {
    const ahora = Date.now();
    if (ahora - ultimoTap.current < 320) {
      ultimoTap.current = 0;
      setBurst(b => b + 1);
      try { navigator.vibrate?.(25); } catch (e) {}
      onLike?.();
    } else {
      ultimoTap.current = ahora;
    }
  };
  return (
    <div ref={zoomRef} onClick={tap} style={{ position: "relative", WebkitTapHighlightColor: "transparent" }}>
      {children}
      {burst > 0 && (
        <div key={burst} className="like-burst" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 6 }}>
          <Heart size={96} fill="#fff" color="#fff" strokeWidth={1} style={{ filter: "drop-shadow(0 3px 14px rgba(0,0,0,.45))" }} />
        </div>
      )}
    </div>
  );
};

const VideoPost = ({ src }) => {
  const [muted, setMuted] = useState(true);
  const [ratio, setRatio] = useState(null); // ancho/alto real, para que ocupe como en Instagram
  const [listo, setListo] = useState(false); // el video ya tiene frames: se funde encima del poster
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // Altura en píxeles (no aspect-ratio directo): cuando llega el metadata la caja
  // se ESTIRA con transición suave en vez de saltar de golpe al tamaño real.
  const [alto, setAlto] = useState(null);
  useEffect(() => {
    const medir = () => {
      const w = containerRef.current?.clientWidth;
      if (w) setAlto(Math.round(w / (ratio || RATIO_DEFAULT)));
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, [ratio]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        if (entry.isIntersecting) videoRef.current.play().catch(() => {});
        else { videoRef.current.pause(); videoRef.current.currentTime = 0; }
      },
      { threshold: 0.5 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const poster = cldPoster(src);
  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", height: alto || undefined, aspectRatio: alto ? undefined : String(RATIO_DEFAULT), transition: "height .35s ease", background: poster ? `#111 url(${poster}) center/cover no-repeat` : "#111", overflow: "hidden", lineHeight: 0 }}>
      <video ref={videoRef} src={cldOpt(src, 720)} poster={poster} preload="none"
        onLoadedMetadata={e => { const w = e.target.videoWidth, h = e.target.videoHeight; if (w && h) setRatio(clampRatio(w / h)); }}
        onLoadedData={() => setListo(true)}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", objectFit: "cover", opacity: listo ? 1 : 0, transition: "opacity .3s ease" }}
        autoPlay muted={muted} playsInline loop />
      <button onClick={(e) => { e.stopPropagation(); setMuted(m => !m); if (videoRef.current) videoRef.current.muted = !muted; }}
        style={{ position: "absolute", bottom: 10, left: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {muted ? <VolumeX size={16} strokeWidth={2} color="#fff" /> : <Volume2 size={16} strokeWidth={2} color="#fff" />}
      </button>
    </div>
  );
};

// ─── CARRUSEL CON INDICADOR ACTIVO ───────────────────────────────────────────
// Imagen con etiquetas de personas (estilo Instagram): si hay tags, muestra un ícono 👤;
// al tocar la imagen se muestran/ocultan los carteles @usuario posicionados; cada uno abre el perfil.
const FotoConTags = ({ url, tags = [], onPerfil, fill = false, onImgLoad }) => {
  const [show, setShow] = useState(false);
  const [cargada, setCargada] = useState(false);
  const [ratio, setRatio] = useState(null); // ancho/alto real de la foto
  const hay = tags.length > 0;
  const blur = cldBlur(url);
  // Si la versión optimizada fallara (raro), caemos al original una sola vez.
  const onImgError = (e) => { if (e.target.src !== url) e.target.src = url; };
  const alCargar = (e) => {
    const w = e.target.naturalWidth, h = e.target.naturalHeight;
    if (w && h) setRatio(clampRatio(w / h));
    setCargada(true);
    onImgLoad && onImgLoad(e);
  };
  // La caja toma el aspect ratio de la foto (como Instagram): ocupa todo el ancho y
  // el alto que le corresponde, en vez de quedar recortada a una miniatura fija.
  const imgStyle = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: hay ? "pointer" : "default" };
  return (
    <div style={{ position: "relative", lineHeight: 0, background: C.accent, overflow: "hidden", width: "100%", ...(fill ? { height: "100%" } : { aspectRatio: String(ratio || RATIO_DEFAULT) }) }}>
      {/* placeholder borroso (blur-up estilo Instagram): se ve al instante mientras
          carga la foto real, que se funde encima cuando termina */}
      {blur && (
        <img loading="lazy" decoding="async" src={blur} alt="" aria-hidden="true"
          style={{ ...imgStyle, filter: "blur(16px)", transform: "scale(1.12)", cursor: "default" }} />
      )}
      <img loading="lazy" decoding="async" src={cldOpt(url)} alt="" onClick={() => hay && setShow(s => !s)}
        onLoad={alCargar} onError={onImgError}
        loading="lazy" decoding="async"
        style={{ ...imgStyle, opacity: blur ? (cargada ? 1 : 0) : 1, transition: "opacity .35s ease" }} />
      {hay && (
        <button onClick={(e) => { e.stopPropagation(); setShow(s => !s); }}
          style={{ position: "absolute", bottom: 10, left: 10, width: 30, height: 30, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4 }}>
          <User size={15} color="#fff" strokeWidth={2.2} />
        </button>
      )}
      {show && tags.map((t, i) => (
        <button key={t.id || i} onClick={(e) => { e.stopPropagation(); onPerfil?.(t.user_id); }}
          style={{ position: "absolute", left: `${Math.min(98, Math.max(2, (t.x ?? 0.5) * 100))}%`, top: `${Math.min(94, Math.max(2, (t.y ?? 0.5) * 100))}%`, transform: "translate(-50%, 0)", background: "rgba(17,17,17,.85)", color: "#fff", border: "none", padding: "5px 9px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,.3)" }}>
          @{t.username}
        </button>
      ))}
    </div>
  );
};

// Modal para etiquetar personas SOBRE las fotos (estilo Instagram). Tocás la foto donde
// querés etiquetar y buscás el rider. Solo imágenes (los videos no se etiquetan).
const EtiquetarPersonasModal = ({ medias, tagsIniciales = [], onGuardar, onClose }) => {
  const imagenes = medias.filter(m => m.type !== "video" && m.url && !m.url.startsWith("blob:"));
  const [idx, setIdx] = useState(0);
  const [tags, setTags] = useState(tagsIniciales);
  const [pending, setPending] = useState(null); // {url,x,y}
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const imgRef = useRef(null);

  if (imagenes.length === 0) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: C.card, borderRadius: 14, padding: 24, textAlign: "center", maxWidth: 320 }}>
          <div style={{ fontSize: 14, color: C.text, marginBottom: 16 }}>Subí al menos una foto para poder etiquetar personas.</div>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontWeight: 700, cursor: "pointer" }}>Entendido</button>
        </div>
      </div>
    );
  }

  const actual = imagenes[idx];
  const tagsActual = tags.filter(t => t.url === actual.url);

  const onTapFoto = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setPending({ url: actual.url, x, y });
    setQ(""); setResultados([]);
  };

  const buscar = async (texto) => {
    setQ(texto);
    if (texto.trim().length < 1) { setResultados([]); return; }
    const { data } = await supabase.from("perfiles").select("id, username, avatar_url").ilike("username", `${String(texto).trim().replace(/^@/, "")}%`).limit(6);
    setResultados(data || []);
  };

  const elegir = (u) => {
    if (!pending) return;
    setTags(prev => [...prev, { url: pending.url, x: pending.x, y: pending.y, user_id: u.id, username: u.username }]);
    setPending(null); setQ(""); setResultados([]);
  };

  const quitar = (t) => setTags(prev => prev.filter(x => x !== t));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#0d0d12", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", paddingTop: "var(--sat)", paddingBottom: "var(--sab)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 14, cursor: "pointer" }}>Cancelar</button>
        <span style={{ color: "#fff", fontSize: 14, fontWeight: 800 }}>Etiquetar personas</span>
        <button onClick={() => onGuardar(tags)} style={{ background: "none", border: "none", color: "#4ade80", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Listo</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 12 }}>
        <div style={{ position: "relative", width: "100%" }}>
          <img loading="lazy" decoding="async" ref={imgRef} src={actual.url} alt="" onClick={onTapFoto}
            style={{ width: "100%", borderRadius: 10, display: "block", cursor: "crosshair" }} />
          {tagsActual.map((t, i) => (
            <div key={i} onClick={() => quitar(t)}
              style={{ position: "absolute", left: `${t.x * 100}%`, top: `${t.y * 100}%`, transform: "translate(-50%,0)", background: "rgba(17,17,17,.9)", color: "#fff", padding: "4px 8px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
              @{t.username} ✕
            </div>
          ))}
          {pending && pending.url === actual.url && (
            <div style={{ position: "absolute", left: `${pending.x * 100}%`, top: `${pending.y * 100}%`, transform: "translate(-50%,-50%)", width: 16, height: 16, borderRadius: "50%", background: C.card, border: "3px solid #111" }} />
          )}
        </div>
        <div style={{ color: "rgba(255,255,255,.55)", fontSize: 12, marginTop: 10, textAlign: "center" }}>
          {pending ? "Buscá el rider para esta etiqueta" : "Tocá la foto donde querés etiquetar a alguien"}
        </div>
      </div>

      {pending && (
        <div style={{ background: "#16161c", borderTop: "1px solid rgba(255,255,255,.1)", padding: 12 }}>
          <input autoFocus value={q} onChange={e => buscar(e.target.value)} placeholder="Buscar un rider por su @usuario…"
            style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "#0d0d12", color: "#fff", fontSize: 14, boxSizing: "border-box" }} />
          <div style={{ maxHeight: 170, overflowY: "auto", marginTop: 8 }}>
            {resultados.map(u => (
              <button key={u.id} onClick={() => elegir(u)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 6px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accent, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {u.avatar_url ? <img loading="lazy" decoding="async" src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>{u.username?.[0]?.toUpperCase()}</span>}
                </div>
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>@{u.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {imagenes.length > 1 && (
        <div style={{ display: "flex", gap: 6, justifyContent: "center", padding: 10, background: "#0d0d12", borderTop: "1px solid rgba(255,255,255,.08)" }}>
          {imagenes.map((im, i) => (
            <button key={i} onClick={() => { setIdx(i); setPending(null); }}
              style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", border: i === idx ? "2px solid #fff" : "2px solid transparent", padding: 0, cursor: "pointer", position: "relative" }}>
              <img loading="lazy" decoding="async" src={im.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {tags.some(t => t.url === im.url) && <span style={{ position: "absolute", top: 1, right: 1, width: 8, height: 8, borderRadius: "50%", background: "#4ade80" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CarruselMedia = ({ medias, tagsPorIndice = {}, onPerfil }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const videoRefs = useRef({});
  // Encuadre único estilo Instagram: la PRIMERA foto/video define la proporción del
  // carrusel (limitada entre 4:5 vertical y 1.91:1 apaisada) y TODAS las demás llenan
  // ese mismo marco con recorte centrado → altura uniforme, sin huecos blancos.
  const [ratio, setRatio] = useState(1);
  const ratioListo = useRef(false);
  const fijarRatio = (w, h) => {
    if (ratioListo.current || !w || !h) return;
    ratioListo.current = true;
    setRatio(Math.min(1.91, Math.max(0.8, w / h)));
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    const idx = Math.round(scrollLeft / clientWidth);
    const newIdx = Math.min(idx, medias.length - 1);
    setActiveIdx(newIdx);
    // pausar videos que no están activos
    Object.entries(videoRefs.current).forEach(([i, v]) => {
      if (!v) return;
      if (parseInt(i) === newIdx) v.play().catch(() => {});
      else v.pause();
    });
  };

  // pausar cuando sale del viewport
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        Object.values(videoRefs.current).forEach(v => {
          if (!v) return;
          if (entry.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div ref={scrollRef} onScroll={handleScroll}
        style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", aspectRatio: String(ratio), background: C.accent }}>
        {medias.map((m, i) => (
          // scrollSnapStop "always": el swipe frena SIEMPRE en la siguiente foto (antes un
          // flick rápido pasaba 2-3 juntas)
          <div key={i} style={{ flexShrink: 0, width: "100%", height: "100%", scrollSnapAlign: "start", scrollSnapStop: "always", position: "relative" }}>
            {m.type === "video"
              ? <>
                  <video ref={el => videoRefs.current[i] = el} src={cldOpt(m.url)} poster={cldPoster(m.url)} preload="metadata"
                    onLoadedMetadata={i === 0 ? (e) => fijarRatio(e.target.videoWidth, e.target.videoHeight) : undefined}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    autoPlay muted={muted} playsInline loop />
                  <button onClick={(e) => {
                    e.stopPropagation();
                    const newMuted = !muted;
                    setMuted(newMuted);
                    Object.values(videoRefs.current).forEach(v => { if (v) v.muted = newMuted; });
                  }} style={{ position: "absolute", bottom: 10, left: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
                    {muted ? <VolumeX size={16} strokeWidth={2} color="#fff" /> : <Volume2 size={16} strokeWidth={2} color="#fff" />}
                  </button>
                </>
              : <FotoConTags url={m.url} tags={tagsPorIndice[i] || []} onPerfil={onPerfil} fill
                  onImgLoad={i === 0 ? (e) => fijarRatio(e.target.naturalWidth, e.target.naturalHeight) : undefined} />}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 5, padding: "8px 0" }}>
        {medias.map((_, i) => (
          <div key={i} style={{ width: i === activeIdx ? 16 : 6, height: 6, borderRadius: 3, background: i === activeIdx ? C.text : C.faint, transition: "all .2s ease" }} />
        ))}
      </div>
    </div>
  );
};

// ─── REELS: los videos de la comunidad a pantalla completa ─────────────────────
// Visor vertical estilo Instagram (Carlos, 30-sep) armado SOBRE los posts con video
// que ya existen: los de la comunidad y los videos del recorrido. Sin tablas nuevas:
// likes, comentarios y vistas son los del post. Un reel por post (su primer video).
// Cada slide carga sus datos y su video recién cuando está cerca del activo, así
// abrir el visor no dispara 60 requests ni 60 descargas.
const videoDePost = (p) => {
  try {
    const medias = p.media_urls ? JSON.parse(p.media_urls) : (p.media_url ? [{ url: p.media_url, type: p.media_type }] : []);
    const v = (medias || []).find(m => m && (m.type === "video" || /\.(mp4|mov|webm)$/i.test(m.url || "") || /\/video\/upload\//.test(m.url || "")));
    return v ? v.url : null;
  } catch (e) { return null; }
};

const ReelComentarios = ({ post, onClose, requireAuth, onPerfil, onCambio }) => {
  const [lista, setLista] = useState(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const cargar = async () => {
    const { data } = await supabase.from("comentarios").select("*").eq("post_id", post.id).order("created_at", { ascending: true });
    const filas = data || [];
    const ids = filas.map(c => c.user_id).filter((v, i, a) => v && a.indexOf(v) === i);
    let mapa = {};
    if (ids.length) {
      const { data: perfs } = await supabase.from("perfiles").select("id, username, avatar_url").in("id", ids);
      (perfs || []).forEach(p => { mapa[p.id] = p; });
    }
    setLista(filas.map(c => ({ ...c, username: mapa[c.user_id]?.username || c.username, _avatar: mapa[c.user_id]?.avatar_url || null })));
  };
  useEffect(() => { cargar(); }, []);
  const enviar = () => {
    const t = texto.trim();
    if (!t || enviando) return;
    const hacer = async () => {
      setEnviando(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setEnviando(false); return; }
      const { data: miPerfil } = await supabase.from("perfiles").select("username").eq("id", user.id).maybeSingle();
      const { error } = await supabase.from("comentarios").insert({ post_id: post.id, user_id: user.id, username: miPerfil?.username || user.email?.split("@")[0], texto: t });
      setEnviando(false);
      if (error) { alert("No se pudo comentar: " + error.message); return; }
      setTexto("");
      await cargar();
      onCambio && onCambio();
    };
    requireAuth ? requireAuth(hacer) : hacer();
  };
  return (
    <div onClick={onClose} style={{ position: "absolute", inset: 0, zIndex: 20, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxHeight: "70%", background: C.card, borderRadius: "18px 18px 0 0", display: "flex", flexDirection: "column", paddingBottom: "var(--sab, 0px)" }}>
        <div style={{ display: "flex", alignItems: "center", padding: "12px 16px 8px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: C.text }}>Comentarios{lista ? ` · ${lista.length}` : ""}</div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} color={C.text} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px" }}>
          {lista === null ? <div style={{ fontSize: 13, color: C.muted, padding: 16, textAlign: "center" }}>Cargando…</div>
            : lista.length === 0 ? <div style={{ fontSize: 13, color: C.muted, padding: 20, textAlign: "center" }}>Todavía no hay comentarios. Dejá el primero.</div>
            : lista.map(c => (
              <div key={c.id} style={{ display: "flex", gap: 10, padding: "8px 0" }}>
                <div onClick={() => c.user_id && onPerfil(c.user_id)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.accent, overflow: "hidden", flexShrink: 0, cursor: "pointer" }}>
                  {c._avatar ? <img src={cldThumb(c._avatar, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 800 }}>{c.username?.[0]?.toUpperCase() || "?"}</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.45 }}><strong onClick={() => c.user_id && onPerfil(c.user_id)} style={{ cursor: "pointer" }}>@{c.username}</strong> {c.texto}</div>
                  <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{new Date(c.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
                </div>
              </div>
            ))}
        </div>
        <div style={{ display: "flex", gap: 8, padding: "10px 16px", borderTop: `1px solid ${C.border}` }}>
          <input value={texto} onChange={e => setTexto(e.target.value)} onKeyDown={e => { if (e.key === "Enter") enviar(); }} placeholder="Escribí un comentario…"
            style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, background: C.bg }} />
          <button onClick={enviar} disabled={!texto.trim() || enviando} style={{ padding: "0 14px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (!texto.trim() || enviando) ? 0.5 : 1 }}>Enviar</button>
        </div>
      </div>
    </div>
  );
};

const ReelSlide = ({ post, idx, activo, cerca, muted, onToggleMute, requireAuth, onPerfil, onComentarios }) => {
  const videoRef = useRef(null);
  const [perfil, setPerfil] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [nComentarios, setNComentarios] = useState(0);
  const [pausado, setPausado] = useState(false);
  const [textoAbierto, setTextoAbierto] = useState(false);
  const [corazon, setCorazon] = useState(false);
  const [cargado, setCargado] = useState(false);
  const src = videoDePost(post);

  useEffect(() => {
    if (!cerca || cargado) return;
    setCargado(true);
    supabase.from("perfiles").select("username, avatar_url").eq("id", post.user_id).maybeSingle().then(({ data }) => { if (data) setPerfil(data); }, () => {});
    supabase.from("likes").select("id", { count: "exact", head: true }).eq("post_id", post.id).then(({ count }) => { if (count !== null && count !== undefined) setLikes(count); }, () => {});
    supabase.from("comentarios").select("id", { count: "exact", head: true }).eq("post_id", post.id).then(({ count }) => { if (count !== null && count !== undefined) setNComentarios(count); }, () => {});
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from("likes").select("id").eq("post_id", post.id).eq("user_id", user.id).maybeSingle().then(({ data }) => { if (data) setLiked(true); }, () => {});
    }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cerca]);

  // Reproducir solo el activo; los demás pausados y al inicio
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (activo) { v.muted = muted; v.play().catch(() => {}); setPausado(false); registrarVistaPost(post.id); }
    else { v.pause(); try { v.currentTime = 0; } catch (e) { /* */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, cerca]);
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);

  const toggleLike = () => {
    const dar = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      if (liked) { setLiked(false); setLikes(c => Math.max(0, c - 1)); await supabase.from("likes").delete().eq("post_id", post.id).eq("user_id", user.id); }
      else { setLiked(true); setLikes(c => c + 1); await supabase.from("likes").insert({ post_id: post.id, user_id: user.id }); registrarUso("feed_interaccion", { tipo: "like", origen: "reels" }); }
    };
    requireAuth ? requireAuth(dar) : dar();
  };
  const lastTap = useRef(0);
  const onTap = () => {
    const ahora = Date.now();
    if (ahora - lastTap.current < 300) { // doble toque = like
      lastTap.current = 0;
      if (!liked) toggleLike();
      setCorazon(true); setTimeout(() => setCorazon(false), 800);
      return;
    }
    lastTap.current = ahora;
    setTimeout(() => {
      if (lastTap.current !== ahora) return;
      const v = videoRef.current; if (!v) return;
      if (v.paused) { v.play().catch(() => {}); setPausado(false); } else { v.pause(); setPausado(true); }
    }, 300);
  };
  const username = perfil?.username || post.username;
  const texto = (post.texto || "").trim();
  const largo = texto.length > 100 || texto.split("\n").length > 2;
  const esRecorrido = post.tipo === "recorrido";
  const btn = { background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, color: "#fff", padding: 0 };
  const num = { fontSize: 11.5, fontWeight: 700, textShadow: "0 1px 3px rgba(0,0,0,.6)" };

  return (
    <div data-idx={idx} style={{ width: "100%", height: "100%", flexShrink: 0, scrollSnapAlign: "start", scrollSnapStop: "always", position: "relative", background: "#000", overflow: "hidden" }}>
      {cerca && src ? (
        <video ref={videoRef} src={cldOpt(src, 720)} poster={cldPoster(src, 720)} preload={activo ? "auto" : "metadata"} loop playsInline muted={muted}
          onClick={onTap}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
      ) : (
        <div style={{ position: "absolute", inset: 0, background: src && cldPoster(src, 480) ? `#000 url(${cldPoster(src, 480)}) center/contain no-repeat` : "#000" }} />
      )}
      {/* pausa y corazón del doble toque */}
      {pausado && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center" }}><Play size={30} color="#fff" fill="#fff" /></div>
        </div>
      )}
      {corazon && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <Heart size={96} color="#fff" fill="#fff" style={{ animation: "likeBurst .8s ease forwards", filter: "drop-shadow(0 2px 12px rgba(0,0,0,.5))" }} />
        </div>
      )}
      {/* degradé inferior para leer el texto */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "45%", background: "linear-gradient(to top, rgba(0,0,0,.75), rgba(0,0,0,0))", pointerEvents: "none" }} />
      {/* columna de acciones */}
      <div style={{ position: "absolute", right: 10, bottom: "calc(var(--sab, 0px) + 84px)", display: "flex", flexDirection: "column", gap: 18, alignItems: "center", zIndex: 5 }}>
        <button onClick={toggleLike} style={btn}>
          <Heart size={28} color={liked ? "#E0245E" : "#fff"} fill={liked ? "#E0245E" : "none"} strokeWidth={2} style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))" }} />
          <span style={num}>{likes}</span>
        </button>
        <button onClick={() => onComentarios(post, (n) => setNComentarios(n))} style={btn}>
          <MessageCircle size={28} color="#fff" strokeWidth={2} style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))" }} />
          <span style={num}>{nComentarios}</span>
        </button>
        <button onClick={() => compartirWhatsApp({ texto: `🎬 ${esRecorrido ? "El recorrido" : "Video"} de @${username} en Olbos${texto ? "\n" + texto.slice(0, 120) : ""}`, tipo: "reel", id: post.id })} style={btn}>
          <Share2 size={26} color="#fff" strokeWidth={2} style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))" }} />
          <span style={num}>Compartir</span>
        </button>
        <button onClick={onToggleMute} style={btn}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {muted ? <VolumeX size={18} color="#fff" strokeWidth={2} /> : <Volume2 size={18} color="#fff" strokeWidth={2} />}
          </div>
        </button>
      </div>
      {/* autor y texto */}
      <div style={{ position: "absolute", left: 14, right: 70, bottom: "calc(var(--sab, 0px) + 22px)", color: "#fff", zIndex: 5 }}>
        <div onClick={() => post.user_id && onPerfil(post.user_id)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 8 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", background: "#333", border: "1.5px solid #fff", flexShrink: 0 }}>
            {perfil?.avatar_url ? <img src={cldThumb(perfil.avatar_url, 80)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800 }}>{username?.[0]?.toUpperCase() || "?"}</div>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>@{username}</div>
          {esRecorrido && <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 100, background: "rgba(249,245,242,.18)", border: "1px solid rgba(249,245,242,.35)" }}>🗺️ Recorrido</span>}
        </div>
        {texto && (
          <div onClick={() => setTextoAbierto(a => !a)} style={{ fontSize: 13, lineHeight: 1.45, textShadow: "0 1px 3px rgba(0,0,0,.6)", whiteSpace: "pre-line", cursor: largo ? "pointer" : "default", ...(textoAbierto ? { maxHeight: "40vh", overflowY: "auto" } : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }) }}>
            {texto}
          </div>
        )}
        {largo && !textoAbierto && <div onClick={() => setTextoAbierto(true)} style={{ fontSize: 12, color: "#d6d0c8", marginTop: 3, cursor: "pointer", fontWeight: 700 }}>más</div>}
        <div style={{ fontSize: 11, color: "#d6d0c8", marginTop: 6 }}>{new Date(post.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
      </div>
    </div>
  );
};

const ReelsViewer = ({ posts, inicio = 0, onClose, requireAuth }) => {
  const scrollRef = useRef(null);
  const [activo, setActivo] = useState(inicio);
  const [muted, setMuted] = useState(true);
  const [perfilVer, setPerfilVer] = useState(null);
  const [comentariosDe, setComentariosDe] = useState(null); // { post, onCambio }
  useBackGuard(true, onClose);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && inicio > 0) el.scrollTop = inicio * el.clientHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) setActivo(Number(en.target.dataset.idx)); });
    }, { root: el, threshold: 0.6 });
    Array.from(el.children).forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, [posts.length]);
  // Teclado en escritorio: flechas para pasar, Escape para cerrar
  useEffect(() => {
    const h = (e) => {
      if (perfilVer) return;
      if (comentariosDe) { if (e.key === "Escape") setComentariosDe(null); return; }
      const el = scrollRef.current; if (!el) return;
      if (e.key === "ArrowDown") { e.preventDefault(); el.scrollBy({ top: el.clientHeight, behavior: "smooth" }); }
      else if (e.key === "ArrowUp") { e.preventDefault(); el.scrollBy({ top: -el.clientHeight, behavior: "smooth" }); }
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [comentariosDe, perfilVer, onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9750, background: "#000" }}>
      <div ref={scrollRef} style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, margin: "0 auto", width: "100%", maxWidth: 480, overflowY: "auto", scrollSnapType: "y mandatory", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", overscrollBehavior: "contain" }}>
        {posts.map((p, i) => (
          <ReelSlide key={p.id} post={p} idx={i} activo={i === activo} cerca={Math.abs(i - activo) <= 1}
            muted={muted} onToggleMute={() => setMuted(m => !m)} requireAuth={requireAuth}
            onPerfil={(uid) => setPerfilVer(uid)} onComentarios={(post, onCambio) => setComentariosDe({ post, onCambio })} />
        ))}
      </div>
      {/* cabecera */}
      <div style={{ position: "absolute", top: "calc(var(--sat, 0px) + 10px)", left: 0, right: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", zIndex: 6, pointerEvents: "none" }}>
        <button onClick={onClose} style={{ pointerEvents: "auto", width: 38, height: 38, borderRadius: "50%", background: "rgba(0,0,0,.45)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={22} color="#fff" strokeWidth={2.2} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em", textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>Reels</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#d6d0c8", minWidth: 38, textAlign: "right", textShadow: "0 1px 3px rgba(0,0,0,.6)" }}>{activo + 1}/{posts.length}</div>
      </div>
      {comentariosDe && (
        <div style={{ position: "absolute", inset: 0, margin: "0 auto", maxWidth: 480, zIndex: 20 }}>
          <ReelComentarios post={comentariosDe.post} requireAuth={requireAuth} onPerfil={(uid) => setPerfilVer(uid)}
            onCambio={() => { supabase.from("comentarios").select("id", { count: "exact", head: true }).eq("post_id", comentariosDe.post.id).then(({ count }) => { if (count !== null && count !== undefined) comentariosDe.onCambio(count); }, () => {}); }}
            onClose={() => setComentariosDe(null)} />
        </div>
      )}
      {perfilVer && <PerfilPublico userId={perfilVer} onClose={() => setPerfilVer(null)} />}
    </div>
  );
};

// ─── PERFIL PÚBLICO ───────────────────────────────────────────────────────────
// ── CONCESIONARIOS OFICIALES (30-oct-2026, caso Motos Benito) ──────────────────
// La concesionaria maneja una cuenta por local ("Zanella Junín") y esa cuenta lleva
// "Concesionario oficial Zanella ✓" solo si lo validó la marca (o Olbos). La lista de
// aprobados es chica: se pide una vez por sesión y la comparten todos los distintivos.
// SQL: docs/sql/concesionarios_oficiales.sql (sin correrlo, no se muestra nada).
let _concesCache = null, _concesPromesa = null;
const cargarConcesionarios = (forzar = false) => {
  if (forzar) { _concesCache = null; _concesPromesa = null; }
  if (_concesCache) return Promise.resolve(_concesCache);
  if (!_concesPromesa) {
    _concesPromesa = supabase.rpc("concesionarios_aprobados").then(({ data: raw, error }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      const m = {};
      (!error && Array.isArray(d) ? d : []).forEach(x => { (m[x.cuenta_id] = m[x.cuenta_id] || []).push(x); });
      _concesCache = m;
      return m;
    }, () => { _concesCache = {}; return {}; });
  }
  return _concesPromesa;
};
const SelloConcesionario = ({ userId, grande = false }) => {
  const [marcas, setMarcas] = useState(() => (_concesCache && userId ? _concesCache[userId] || null : null));
  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    cargarConcesionarios().then(m => { if (vivo) setMarcas(m[userId] || null); });
    return () => { vivo = false; };
  }, [userId]);
  if (!marcas || !marcas.length) return null;
  return (
    <>
      {marcas.map(x => (
        <span key={x.marca} title={`Concesionario oficial de ${x.marca}, validado`}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: grande ? "3px 9px" : "1px 7px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, fontSize: grande ? 11.5 : 10, fontWeight: 700, color: C.text, whiteSpace: "nowrap", letterSpacing: "-0.005em" }}>
          {x.logo_url && <img loading="lazy" decoding="async" src={cldOpt(x.logo_url, 60)} alt="" style={{ width: grande ? 14 : 12, height: grande ? 14 : 12, borderRadius: 3, objectFit: "contain" }} />}
          Concesionario oficial {x.marca}
          <span style={{ color: C.green, fontWeight: 900 }}>✓</span>
        </span>
      ))}
    </>
  );
};

const PerfilPublico = ({ userId, username, onClose }) => {
  const [perfil, setPerfil] = useState(null);
  const [posts, setPosts] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [fotoViewer, setFotoViewer] = useState(null);
  // Todo lo público del usuario, estilo Instagram (pedido de Carlos 11-ago-2026):
  // eventos que organiza, rutas y recorridos publicados, lugares que recomendó
  const [misEventos, setMisEventos] = useState([]);
  const [misRutas, setMisRutas] = useState([]);
  const [misTrazasPub, setMisTrazasPub] = useState([]);
  const [misLugares, setMisLugares] = useState([]);
  const [miId, setMiId] = useState(null);
  const [seguidoresPub, setSeguidoresPub] = useState(0);
  const [procesandoSeg, setProcesandoSeg] = useState(false);
  const seguidosPub = useSeguidos(miId);
  const toggleSeguirPub = async () => {
    if (!perfil || !seguidosPub || procesandoSeg) return;
    const lo = seguidosPub.has(perfil.id);
    setProcesandoSeg(true);
    const { error } = await supabase.rpc("toggle_seguir", { p_following_id: perfil.id });
    setProcesandoSeg(false);
    if (error) { alert("No se pudo " + (lo ? "dejar de seguir" : "seguir") + ": " + (error.message || "probá de nuevo")); return; }
    if (lo) _seg.ids.delete(perfil.id); else _seg.ids.add(perfil.id);
    _seg.subs.forEach(fn => { try { fn(perfil.id); } catch (e) { /* */ } });
    setSeguidoresPub(n => Math.max(0, n + (lo ? -1 : 1)));
    if (!lo) registrarUso("feed_interaccion", { tipo: "seguir", origen: "perfil" });
  };
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setMiId(data?.session?.user?.id || null), () => {});
  }, []);

  useEffect(() => {
    cargarPerfil();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, username]);

  const cargarPerfil = async () => {
    setCargando(true);
    // Se puede abrir por id o por username (los listados suelen tener solo el username)
    let q = supabase.from("perfiles").select("*");
    q = userId ? q.eq("id", userId) : q.ilike("username", String(username || "").replace(/^@/, ""));
    const { data: p } = await q.maybeSingle();
    if (!p) { setPerfil(null); setCargando(false); return; }
    setPerfil(p);
    const uid = p.id;
    supabase.rpc("get_perfil_social", { p_user_id: uid }).then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setSeguidoresPub(Number(d?.seguidores) || 0);
    }, () => {});
    const [postData, evData, ruData, trData, luData] = await Promise.all([
      supabase.from("posts").select("*").eq("user_id", uid).order("created_at", { ascending: false }),
      supabase.from("eventos").select("id, titulo, fecha, tipo, foto_url").eq("creator_id", uid).is("grupo_id", null).order("fecha", { ascending: false }).limit(20),
      supabase.from("rutas").select("id, nombre, desde, hasta, km").eq("user_id", uid).is("grupo_id", null).order("created_at", { ascending: false }).limit(10),
      supabase.from("trazas").select("id, titulo, distancia_km, km_auto, iniciada_en").eq("user_id", uid).eq("publica", true).order("created_at", { ascending: false }).limit(10),
      supabase.from("lugares_comunidad").select("id, nombre, tipo, ciudad, categoria").eq("creador_id", uid).order("created_at", { ascending: false }).limit(10),
    ]);
    setPosts(postData.data || []);
    setMisEventos(evData.data || []);
    setMisRutas(ruData.data || []);
    setMisTrazasPub(trData.data || []);
    setMisLugares(luData.data || []);
    setCargando(false);
  };

  const fmtF = (f) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "2-digit" }); };
  const filaPublica = (key, titulo, sub, onIr) => (
    <button key={key} onClick={onIr} disabled={!onIr}
      style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 6, cursor: onIr ? "pointer" : "default", textAlign: "left" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{titulo}</div>
        {sub && <div style={{ fontSize: 11.5, color: C.muted }}>{sub}</div>}
      </div>
      {onIr && <ChevronRight size={15} color={C.muted} style={{ flexShrink: 0 }} />}
    </button>
  );
  const seccionPublica = (titulo, hijos) => hijos.length === 0 ? null : (
    <div style={{ padding: "14px 18px 4px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>{titulo} ({hijos.length})</div>
      {hijos}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 200, overflowY: "auto" }}>
      {/* header */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: C.card, borderBottom: `1px solid ${C.border}`, padding: "max(48px, calc(var(--sat) + 12px)) 18px 12px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronLeft size={20} strokeWidth={2} color={C.text} />
        </button>
        <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
          {perfil ? `@${perfil.username}` : "Perfil"}
        </span>
      </div>

      {cargando ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
          <div style={{ fontSize: 14, color: C.muted }}>Cargando...</div>
        </div>
      ) : perfil ? (
        <>
          {/* info perfil */}
          <div style={{ padding: "20px 18px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 14 }}>
              {/* avatar */}
              <div onClick={() => perfil.avatar_url && setFotoViewer(perfil.avatar_url)}
                style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", border: `2px solid ${C.border}`, flexShrink: 0, background: C.accent, cursor: perfil.avatar_url ? "pointer" : "default" }}>
                {perfil.avatar_url
                  ? <img loading="lazy" decoding="async" src={perfil.avatar_url} alt={perfil.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#fff" }}>
                      {perfil.username?.[0]?.toUpperCase() || "?"}
                    </div>
                }
              </div>
              {/* datos */}
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>@{perfil.username}</div>
                  <BadgeComunidad tipo={perfil.tipo_perfil} />
                  {perfil.embajador_fundador && <BadgeComunidad tipo="embajador" />}
                  <BadgeTag badge={getBadge(null, perfil.username)} />
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}><SelloConcesionario userId={perfil.id} grande /></div>
                {perfil.ciudad && <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>📍 {perfil.ciudad}</div>}
                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{posts.length}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>posts</div>
                  </div>
                  {/* Seguidores sí, "seguidos" no (Carlos, 30-oct). Y las vistas de todo lo que publicó:
                      ver que te miran es lo que más empuja a publicar */}
                  {seguidoresPub > 0 && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{seguidoresPub.toLocaleString("es-AR")}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>seguidor{seguidoresPub === 1 ? "" : "es"}</div>
                    </div>
                  )}
                  {(() => { const v = posts.reduce((a, p) => a + Number(p.vistas || 0), 0); return v > 0 ? (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{v.toLocaleString("es-AR")}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>vistas</div>
                    </div>
                  ) : null; })()}
                  {(perfil.motos || []).filter(m => m && (!m.estado || m.estado === "activa")).length > 0 && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{(perfil.motos || []).filter(m => m && (!m.estado || m.estado === "activa")).length}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>motos</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* mensaje privado (no en el propio perfil) */}
            {miId && miId !== perfil.id && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {seguidosPub && (
                  <button onClick={toggleSeguirPub} disabled={procesandoSeg} style={{ flex: 1, padding: "11px 10px", background: seguidosPub.has(perfil.id) ? C.surface : C.accent, color: seguidosPub.has(perfil.id) ? C.text : "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: procesandoSeg ? "default" : "pointer", opacity: procesandoSeg ? 0.6 : 1 }}>
                    {seguidosPub.has(perfil.id) ? "Siguiendo" : "Seguir"}
                  </button>
                )}
                <button onClick={() => {
                  window.__olbosDMPendiente = { id: perfil.id, username: perfil.username, avatar_url: perfil.avatar_url };
                  window.dispatchEvent(new CustomEvent("olbos-abrir-dm"));
                }} style={{ flex: 1, padding: "11px 10px", background: seguidosPub ? C.surface : C.accent, color: seguidosPub ? C.text : "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <MessageCircle size={16} strokeWidth={2} /> Enviar mensaje
                </button>
              </div>
            )}

            {/* bio */}
            {perfil.bio && (
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>{perfil.bio}</p>
            )}

            {/* redes y links */}
            <LinksPerfil links={perfil.links} style={{ marginBottom: 14 }} />

            {/* motos (los perfiles comunidad no muestran garage) */}
            {perfil.tipo_perfil !== "comunidad" && (perfil.motos || []).filter(m => m && (!m.estado || m.estado === "activa")).length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Garage</div>
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                  {/* solo activas: las eliminadas/vendidas no son "su garage" (borrado suave) */}
                  {(perfil.motos || []).filter(m => m && (!m.estado || m.estado === "activa")).map((m, i) => (
                    <div key={i} style={{ flexShrink: 0, background: C.surface, borderRadius: 10, padding: "8px 12px", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                      {m.fotoUrl
                        ? <img src={cldThumb(m.fotoUrl, 100)} alt={m.modelo} loading="lazy" decoding="async" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }} />
                        : <Bike size={18} strokeWidth={1.8} color={C.muted} />
                      }
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{m.marca} {m.modelo}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{m.año}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* todo lo público del usuario, estilo IG: organiza / rutas / recorridos / lugares */}
          {seccionPublica("Organiza", misEventos.map(e => filaPublica(e.id, e.titulo, fmtF(e.fecha), () => { window.location.href = `/evento/${e.id}`; })))}
          {seccionPublica("Rutas publicadas", misRutas.map(r => filaPublica(r.id, r.nombre, [r.desde, r.hasta].filter(Boolean).join(" → ") + (r.km ? ` · ${r.km} km` : ""), null)))}
          {seccionPublica("Recorridos públicos", misTrazasPub.map(t => filaPublica(t.id, t.titulo || "Recorrido", `${kmTraza(t) ? kmTraza(t).toFixed(0) + " km · " : ""}${fmtF(t.iniciada_en)}`, () => { window.location.href = `/ruta/${t.id}`; })))}
          {seccionPublica("Lugares que recomendó", misLugares.map(l => filaPublica(l.id, l.nombre, `${l.tipo || l.categoria} · ${l.ciudad || ""}`, null)))}

          {/* posts */}
          <div>
            {posts.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
                Todavía no hay publicaciones
              </div>
            ) : (
              posts.map(post => (
                <PostReal key={post.id} post={post} onReload={() => {}} requireAuth={null} />
              ))
            )}
          </div>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Perfil no encontrado</div>
      )}
      {fotoViewer && <FotoViewer url={fotoViewer} onClose={() => setFotoViewer(null)} />}
    </div>
  );
};

// ─── POST REAL (desde Supabase) con likes y comentarios ──────────────────────
// ── INVITADOS DE UN EVENTO PRIVADO ────────────────────────────────────────
// Solo el creador maneja la lista (la RLS lo garantiza del lado del servidor).
const InvitadosEvento = ({ evento }) => {
  const [lista, setLista] = useState([]);
  const [busca, setBusca] = useState("");
  const [result, setResult] = useState([]);

  const cargar = async () => {
    const { data: invs } = await supabase.from("evento_invitados").select("user_id").eq("evento_id", evento.id);
    const ids = (invs || []).map(i => i.user_id);
    if (!ids.length) { setLista([]); return; }
    const { data: perfs } = await supabase.from("perfiles").select("id, username, avatar_url").in("id", ids);
    setLista(perfs || []);
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [evento.id]);

  const buscar = async (q) => {
    setBusca(q);
    if (q.trim().length < 2) { setResult([]); return; }
    const { data } = await supabase.from("perfiles").select("id, username, avatar_url").ilike("username", `%${q.trim()}%`).limit(8);
    const ya = new Set(lista.map(l => l.id));
    setResult((data || []).filter(p => !ya.has(p.id) && p.id !== evento.creator_id));
  };

  const invitar = async (p) => {
    setBusca(""); setResult([]);
    const { error } = await supabase.from("evento_invitados").insert({ evento_id: evento.id, user_id: p.id, invitado_por: evento.creator_id });
    if (error) { alert("No se pudo invitar. Probá de nuevo."); return; }
    setLista(prev => [...prev, p]);
  };
  const sacar = async (p) => {
    if (!window.confirm(`¿Sacar a @${p.username} de la lista? Deja de ver el evento.`)) return;
    const { error } = await supabase.from("evento_invitados").delete().eq("evento_id", evento.id).eq("user_id", p.id);
    if (error) { alert("No se pudo sacar. Probá de nuevo."); return; }
    setLista(prev => prev.filter(x => x.id !== p.id));
  };

  return (
    <div style={{ background: "#F5EFE5", border: "1px solid #8A6D3B33", borderRadius: 12, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#8A6D3B", marginBottom: 4 }}>🔒 Evento solo con invitados</div>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
        No sale en la agenda ni en el feed. Lo ven únicamente las personas de esta lista, y solo vos la manejás.
      </div>
      {lista.map(p => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0" }}>
          <img loading="lazy" decoding="async" src={p.avatar_url || avatarInicial(p.username)} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: C.text }}>@{p.username}</span>
          <button onClick={() => sacar(p)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 4 }}>
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      ))}
      <input value={busca} onChange={e => buscar(e.target.value)}
        placeholder="Invitar por @usuario…"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box", background: C.card, marginTop: 6 }} />
      {result.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 6, overflow: "hidden", background: C.card }}>
          {result.map(p => (
            <button key={p.id} onClick={() => invitar(p)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "8px 12px", background: "none", border: "none", borderBottom: `1px solid ${C.surface}`, cursor: "pointer", textAlign: "left" }}>
              <img loading="lazy" decoding="async" src={p.avatar_url || avatarInicial(p.username)} alt="" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>@{p.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── VISUALIZACIONES DE POSTS ──────────────────────────────────────────────
// Se cuenta como visto lo mismo que cuenta Instagram: la mitad del post en
// pantalla durante un segundo. Los ids se juntan y se mandan en UN request al
// dejar de scrollear, no uno por post. El Set local evita repetir en la misma
// sesión, y el ON CONFLICT de la base evita repetir entre sesiones.
const _vistasYaMandadas = new Set();
const _vistasPendientes = new Set();
let _vistasTimer = null;

const _flushVistas = () => {
  if (!_vistasPendientes.size) return;
  const ids = [..._vistasPendientes];
  _vistasPendientes.clear();
  supabase.rpc("registrar_vistas", { p_posts: ids }).then(() => {}, () => {});
};

const registrarVistaPost = (postId) => {
  if (!postId || _vistasYaMandadas.has(postId)) return;
  _vistasYaMandadas.add(postId);
  _vistasPendientes.add(postId);
  clearTimeout(_vistasTimer);
  _vistasTimer = setTimeout(_flushVistas, 3000);
};

// Si cierra la app o cambia de pestaña, mandar lo que quedó pendiente
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (document.hidden) _flushVistas(); });
}

// Cuando 17 riders graban la misma salida, el feed se llena con 17 veces el mismo
// mapa y el resto de la comunidad deja de ver otra cosa. Se juntan en una sola
// tarjeta: quiénes fueron, cuántos km entre todos, y adentro el recorrido de cada uno.
// Es solo cómo se muestra: cada post sigue siendo de su autor, con sus likes y sus
// comentarios, y se puede borrar o editar como siempre.
// eventosDeFeed: evento_id -> { padre_id, titulo, padre_titulo }. Con eso, los
// recorridos de las caravanas de un mismo evento central (el BikerFest tuvo
// siete) se agrupan en UNA tarjeta, en vez de cinco posts iguales seguidos
// (reporte de Carlos: "para el que no fue es un embole").
const agruparPorRodada = (posts, eventosDeFeed = {}) => {
  const salida = [];
  const yaPuestos = new Set();
  const claveDe = (p) => {
    const ev = eventosDeFeed[p.evento_id];
    return (ev && ev.padre_id) || p.evento_id;
  };
  (posts || []).forEach(p => {
    if (yaPuestos.has(p.id)) return;
    if (p.tipo === "recorrido" && p.evento_id) {
      const clave = claveDe(p);
      const hermanos = posts.filter(q => q.tipo === "recorrido" && q.evento_id && claveDe(q) === clave);
      if (hermanos.length > 1) {
        hermanos.forEach(q => yaPuestos.add(q.id));
        const ev = eventosDeFeed[p.evento_id];
        const esPadre = !!(ev && ev.padre_id);
        salida.push({ clave: "rodada-" + clave, posts: hermanos, titulo: esPadre ? ev.padre_titulo : null, esPadre });
        return;
      }
    }
    yaPuestos.add(p.id);
    salida.push({ clave: p.id, post: p });
  });
  return salida;
};

const PostsDeRodada = ({ posts, onReload, requireAuth, titulo: tituloPadre = null, esPadre = false }) => {
  const [abierto, setAbierto] = useState(false);
  // El título y los km salen del texto del propio post ("🏍️ Titulo" / "📍 12.3 km"),
  // así no hace falta ir a buscar el evento a la base para dibujar la tarjeta.
  // Si son caravanas de un evento central, el título es el del evento.
  const titulo = tituloPadre || String(posts[0]?.texto || "").split("\n")[0].replace(/^🏍️\s*/, "").trim() || "Rodada";
  const kmTotal = posts.reduce((acc, p) => {
    const m = String(p.texto || "").match(/📍\s*([\d.,]+)\s*km/);
    return acc + (m ? (parseFloat(m[1].replace(",", ".")) || 0) : 0);
  }, 0);
  const riders = posts.filter((p, i, a) => a.findIndex(q => q.user_id === p.user_id) === i);
  const nombres = riders.map(r => "@" + (r.username || "rider"));
  const quienes = nombres.length <= 2 ? nombres.join(" y ") : `${nombres.slice(0, 2).join(", ")} y ${nombres.length - 2} más`;
  const mapas = posts.map(p => p.media_url).filter(Boolean).slice(0, 6);

  return (
    <div style={{ margin: "0 16px 14px" }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 15px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>
          {esPadre ? "Llegaron en caravana" : "Rodada en grupo"}
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: -0.2, marginBottom: 3 }}>{titulo}</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          {riders.length} riders {esPadre ? "grabaron su camino hasta acá" : "la grabaron"}{kmTotal > 0 ? ` · ${Math.round(kmTotal).toLocaleString("es-AR")} km entre todos` : ""}
        </div>
        {mapas.length > 0 && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
            {mapas.map((u, i) => (
              <img key={i} loading="lazy" decoding="async" src={cldThumb(u, 400)} alt=""
                style={{ width: 78, height: 78, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: `1px solid ${C.border}` }} />
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{quienes}</div>
        <button onClick={() => setAbierto(a => !a)}
          style={{ width: "100%", padding: "9px 12px", borderRadius: 9, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>
          {abierto ? "Ocultar los recorridos" : `Ver los ${posts.length} recorridos`}
        </button>
      </div>
      {abierto && (
        <div style={{ marginTop: 10, marginLeft: -16, marginRight: -16 }}>
          {posts.map(p => <PostReal key={p.id} post={p} onReload={onReload} requireAuth={requireAuth} />)}
        </div>
      )}
    </div>
  );
};

// ── Seguidos: cache compartido por todos los posts del feed y las sugerencias ──
// Se carga UNA vez por sesión (no una consulta por post) y, al seguir a alguien desde
// cualquier lado, se avisa a todos los botones para que se acomoden sin recargar.
const _seg = { uid: null, ids: null, prom: null, subs: [] };
const cargarSeguidos = (uid) => {
  if (!uid) return Promise.resolve(null);
  if (_seg.uid === uid && _seg.ids) return Promise.resolve(_seg.ids);
  if (_seg.uid === uid && _seg.prom) return _seg.prom;
  _seg.uid = uid; _seg.ids = null;
  _seg.prom = (async () => {
    try {
      // OJO: la tabla viva es `seguidores` (ahí inserta toggle_seguir); `follows` es un resto
      // vacío del schema viejo. Leer `follows` hacía que todos aparecieran como "Seguir"
      // aunque ya los siguieras (Carlos, 10-oct).
      const { data, error } = await supabase.from("seguidores").select("following_id").eq("follower_id", uid).limit(5000);
      if (error) { _seg.prom = null; return null; } // sin el dato, el botón no se muestra (no adivinamos)
      _seg.ids = new window.Set((data || []).map(f => f.following_id).filter(Boolean));
      return _seg.ids;
    } catch (e) { _seg.prom = null; return null; }
  })();
  return _seg.prom;
};
const useSeguidos = (uid) => {
  const [ids, setIds] = useState(() => (uid && _seg.uid === uid ? _seg.ids : null));
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!uid) { setIds(null); return; }
    let vivo = true;
    cargarSeguidos(uid).then(s => { if (vivo) setIds(s); }, () => {});
    const fn = () => { if (vivo) setTick(n => n + 1); };
    _seg.subs.push(fn);
    return () => { vivo = false; _seg.subs = _seg.subs.filter(x => x !== fn); };
  }, [uid]);
  return ids;
};
// Seguir (nunca "dejar de seguir": el botón solo aparece si todavía no lo seguís)
const seguirRider = async (id, origen) => {
  if (_seg.ids && _seg.ids.has(id)) return {};
  const { error } = await supabase.rpc("toggle_seguir", { p_following_id: id });
  if (error) return { error };
  if (_seg.ids) _seg.ids.add(id);
  _seg.subs.forEach(fn => { try { fn(id); } catch (e) { /* */ } });
  registrarUso("feed_interaccion", { tipo: "seguir", origen });
  return {};
};

// 👋 "Riders cerca tuyo": sugerencias para seguir, intercaladas en el feed (estilo
// Instagram). Primero los de tu ciudad que no seguís; si no hay ciudad (o hay pocos),
// los que más publicaron últimamente. Máximo 10. Sin sesión no aparece.
const SugerenciasRiders = ({ session }) => {
  const uid = session?.user?.id || null;
  const seguidos = useSeguidos(uid);
  const [lista, setLista] = useState(null);
  const [cerca, setCerca] = useState(false);
  const [seguidosAca, setSeguidosAca] = useState({});
  const [verPerfil, setVerPerfil] = useState(null);
  useEffect(() => {
    if (!uid || !seguidos) return;
    let vivo = true;
    (async () => {
      try {
        let ocultar = [];
        try { ocultar = await idsOcultos(); } catch (e) { /* */ }
        const sirve = (p) => p && p.id && p.id !== uid && p.id !== BOT_OLBOS_ID && p.username && !seguidos.has(p.id) && !ocultar.includes(p.id) && (!p.tipo_perfil || p.tipo_perfil === "persona");
        const { data: yo } = await supabase.from("perfiles").select("ciudad").eq("id", uid).maybeSingle();
        const ciudad = String(yo?.ciudad || "").split(",")[0].trim();
        let out = [];
        if (ciudad.length >= 3) {
          const { data } = await supabase.from("perfiles").select("id, username, avatar_url, ciudad, tipo_perfil")
            .ilike("ciudad", `${ciudad}%`).neq("id", uid).order("created_at", { ascending: false }).limit(40);
          out = (data || []).filter(sirve);
        }
        const deCiudad = out.length;
        if (out.length < 4) {
          // Los más activos: autores de lo último que se publicó
          const { data: ult } = await supabase.from("posts").select("user_id").order("created_at", { ascending: false }).limit(120);
          const cuenta = {};
          (ult || []).forEach(p => { if (p.user_id) cuenta[p.user_id] = (cuenta[p.user_id] || 0) + 1; });
          const ids = Object.keys(cuenta).filter(id => !out.some(x => x.id === id)).sort((a, b) => cuenta[b] - cuenta[a]).slice(0, 30);
          if (ids.length) {
            const { data: perfs } = await supabase.from("perfiles").select("id, username, avatar_url, ciudad, tipo_perfil").in("id", ids);
            const activos = (perfs || []).filter(sirve).sort((a, b) => cuenta[b.id] - cuenta[a.id]);
            out = [...out, ...activos];
          }
        }
        if (!vivo) return;
        setCerca(deCiudad > 0);
        setLista(out.filter((p, i, a) => a.findIndex(x => x.id === p.id) === i).slice(0, 10));
      } catch (e) { if (vivo) setLista([]); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, !!seguidos]);
  if (!uid || !lista || lista.length === 0) return null;
  const seguir = async (p) => {
    setSeguidosAca(s => ({ ...s, [p.id]: "yendo" }));
    const { error } = await seguirRider(p.id, "sugerencias");
    setSeguidosAca(s => ({ ...s, [p.id]: error ? undefined : "ok" }));
    if (error) alert("No se pudo seguir: " + (error.message || "probá de nuevo"));
  };
  return (
    <div style={{ padding: "14px 0 16px", background: C.card, borderBottom: `1px solid ${C.border}` }}>
      <div style={{ padding: "0 16px 10px", fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
        {cerca ? "Riders cerca tuyo" : "Riders para seguir"}
      </div>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", padding: "0 16px 2px", scrollbarWidth: "none" }} className="sin-scrollbar">
        {lista.map(p => {
          const st = seguidosAca[p.id];
          return (
            <div key={p.id} style={{ flex: "0 0 auto", width: 128, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 10px 10px", textAlign: "center", boxSizing: "border-box" }}>
              <div onClick={() => setVerPerfil(p.id)} style={{ cursor: "pointer" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", margin: "0 auto 8px", overflow: "hidden", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {p.avatar_url
                    ? <img loading="lazy" decoding="async" src={cldThumb(p.avatar_url, 120)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: 20, fontWeight: 700, color: C.muted }}>{(p.username || "?")[0].toUpperCase()}</span>}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>@{p.username}</div>
                <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minHeight: 15 }}>{p.ciudad ? String(p.ciudad).split(",")[0] : " "}</div>
              </div>
              <button disabled={!!st} onClick={() => seguir(p)}
                style={{ width: "100%", marginTop: 8, padding: "7px 0", borderRadius: 9, border: "none", fontSize: 12.5, fontWeight: 700, cursor: st ? "default" : "pointer", background: st === "ok" ? C.surface : "#111111", color: st === "ok" ? C.text : "#F9F5F2" }}>
                {st === "ok" ? "Siguiendo" : st === "yendo" ? "…" : "Seguir"}
              </button>
            </div>
          );
        })}
      </div>
      {verPerfil && <PerfilPublico userId={verPerfil} onClose={() => setVerPerfil(null)} />}
    </div>
  );
};

// ── LINKS PROMOCIONADOS (idea de Carlos, 23-oct-2026) ─────────────────────────────
// Un post puede llevar un botón con link (tienda, publicación de Mercado Libre…). Cada click
// de un rider descuenta el precio por click del saldo prepago del que publicó (uno por rider
// por día, nunca el propio). El saldo se carga desde la web con Mercado Pago: en la app de
// las tiendas no se compra (Apple 3.1.1 / Google). SQL: docs/sql/promo_links.sql.
// Apagado para el público con "promos" en version.json; el equipo lo ve siempre.
const BOTONES_PROMO = ["Ver en mi tienda", "Comprar", "Ver en Mercado Libre", "Más info", "Escribime"];
const BotonPromoPost = ({ promo }) => {
  if (!promo || !promo.activo) return null;
  const abrir = async () => {
    // Se abre una pestaña YA (si se espera a la base, el navegador la bloquea) y se le pone el link
    const w = esNativo() ? null : window.open("", "_blank"); // con "noopener" devuelve null
    if (w) { try { w.opener = null; } catch (e) { /* */ } }
    let url = promo.url;
    try {
      const { data: raw } = await supabase.rpc("promo_click", { p_link: promo.id });
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (d && d.url) url = d.url;
    } catch (e) { /* el click igual lleva al link */ }
    registrarUso("promo_click", { link: promo.id });
    if (w) { try { w.location.href = url; } catch (e) { window.open(url, "_blank", "noopener"); } }
    else window.open(url, "_blank", "noopener");
  };
  return (
    <div style={{ padding: "10px 16px 0" }}>
      <button onClick={abrir} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "11px 14px", borderRadius: 12, background: C.text, color: "#F9F5F2", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em" }}>
        <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{promo.boton_texto || "Ver más"}</span>
        <ChevronRight size={17} strokeWidth={2.2} style={{ flexShrink: 0 }} />
      </button>
      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5, letterSpacing: "0.02em" }}>Promocionado</div>
    </div>
  );
};
const MisLinksPromo = ({ onBack }) => {
  useBackGuard(true, onBack);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [monto, setMonto] = useState("");
  const [pagando, setPagando] = useState(false);
  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("mis_promos");
    const x = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (x && x.error)) { setErr(error ? "Todavía no está habilitado (falta un paso en el servidor)." : "Entrá a tu cuenta para ver tus promociones."); return; }
    setD(x);
  };
  useEffect(() => { cargar(); }, []);
  const plata = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-AR");
  const cargarSaldo = async (valor) => {
    const m = Math.round(Number(String(valor).replace(/[^0-9]/g, "")) || 0);
    if (!d || m < Number(d.carga_minima)) { alert(`La carga mínima es de ${plata(d ? d.carga_minima : 0)}.`); return; }
    setPagando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`${API_BASE}/api/anuncio-pago`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` }, body: JSON.stringify({ promo_carga: m }) });
      const j = await r.json().catch(() => ({}));
      if (j.url) { registrarUso("promo_carga_iniciada", { monto: m }); window.location.href = j.url; return; }
      alert("No se pudo iniciar el pago: " + (j.error || r.status));
    } catch (e) { alert("No se pudo conectar. Probá de nuevo."); }
    setPagando(false);
  };
  const cambiar = async (l, activo) => {
    const { data: raw, error } = await supabase.rpc("promo_estado_link", { p_link: l.id, p_activo: activo });
    const x = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || (x && x.error)) { alert(x && x.error === "sin_saldo" ? "No tenés saldo suficiente para reactivarlo. Cargá saldo primero." : "No se pudo cambiar."); return; }
    cargar();
  };
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Mis links promocionados</div>
      </div>
      <div style={{ padding: "4px 16px 110px" }}>
        {err && <div style={{ background: C.surface, borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.muted }}>{err}</div>}
        {!err && !d && <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: 20 }}>Cargando…</div>}
        {d && <>
          <div style={{ background: C.text, color: "#F9F5F2", borderRadius: 16, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#D9B368" }}>Tu saldo</div>
            <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: "-0.02em", marginTop: 4 }}>{plata(d.saldo)}</div>
            <div style={{ fontSize: 12.5, color: "#c9c3ba", marginTop: 4, lineHeight: 1.5 }}>Cada click de un rider en tu botón cuesta {plata(d.precio_click)}. Cuenta uno por rider por día, y tus propios clicks no se cobran. Sin saldo, tus links se pausan solos.</div>
          </div>
          {esNativo() ? (
            <div style={{ background: C.surface, borderRadius: 12, padding: "12px 14px", fontSize: 13, color: C.text, lineHeight: 1.55, marginBottom: 16 }}>
              El saldo se carga desde <b>olbos.ai</b>, en el navegador de la compu o del teléfono, con Mercado Pago. Los clicks de la app cuentan igual.
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 10 }}>Cargar saldo con Mercado Pago</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Al cargar saldo aceptás <a href="/terminos#links-promocionados" target="_blank" rel="noreferrer" style={{ color: C.text, fontWeight: 700 }}>las condiciones de los links promocionados</a>. Podés arrepentirte dentro de los 10 días si no usaste el saldo.</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {[1, 2, 4].map(k => Number(d.carga_minima) * k).map(v => (
                  <button key={v} disabled={pagando} onClick={() => cargarSaldo(v)} style={{ padding: "9px 14px", borderRadius: 100, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>{plata(v)}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={monto} onChange={e => setMonto(e.target.value)} placeholder={`Otro monto (mínimo ${plata(d.carga_minima)})`} inputMode="numeric"
                  style={{ flex: 1, minWidth: 0, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.surface, color: C.text, fontFamily: "inherit" }} />
                <button disabled={pagando || !monto} onClick={() => cargarSaldo(monto)} style={{ padding: "0 16px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontWeight: 800, cursor: "pointer" }}>{pagando ? "…" : "Cargar"}</button>
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Tus links</div>
          {(d.links || []).length === 0 && <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>Todavía no tenés links. Al publicar en el feed, tocá "Agregar un botón con link" y elegí el texto del botón.</div>}
          {(d.links || []).map(l => (
            <div key={l.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, marginBottom: 10, display: "flex", gap: 10 }}>
              {l.media_url && <img src={cldThumb(l.media_url, 120)} alt="" style={{ width: 54, height: 54, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{l.boton_texto} · {String(l.url).replace(/^https?:\/\//, "")}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{l.texto || "Sin texto"}</div>
                <div style={{ fontSize: 12.5, color: C.text, marginTop: 6 }}><b>{l.clicks}</b> click{l.clicks === 1 ? "" : "s"} · {plata(l.gastado)} gastados · <span style={{ color: l.activo ? C.green : C.muted, fontWeight: 700 }}>{l.activo ? "Activo" : l.motivo === "sin_saldo" ? "Pausado sin saldo" : "Pausado"}</span></div>
              </div>
              <button onClick={() => cambiar(l, !l.activo)} style={{ alignSelf: "center", padding: "8px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>{l.activo ? "Pausar" : "Activar"}</button>
            </div>
          ))}
        </>}
      </div>
    </PageWrap>
  );
};

const PostReal = ({ post, onReload, requireAuth, promo = null }) => {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const likeEnCurso = useRef(false);
  const [showComentarios, setShowComentarios] = useState(false);
  // ¿El evento del post ya pasó? Cambia el CTA: "anotarse" no tiene sentido en
  // un evento realizado (reporte de Carlos con el video de la Travesía)
  const [eventoPasado, setEventoPasado] = useState(null); // null = no se sabe aún
  const [showRecorrido, setShowRecorrido] = useState(false); // visor del track de un post de recorrido
  // Sin esto, el atrás de Android no hacía nada con el visor abierto: quedabas
  // encerrado adentro del recorrido (reporte de Carlos).
  useBackGuard(showRecorrido, () => setShowRecorrido(false));
  useEffect(() => {
    if (!post.evento_id) return;
    let vivo = true;
    supabase.from("eventos").select("fecha, fecha_fin").eq("id", post.evento_id).maybeSingle()
      .then(({ data }) => {
        if (!vivo || !data || !data.fecha) return;
        // mediodía LOCAL (gotcha de fechas): medianoche UTC corre un día en AR
        const fin = new Date((data.fecha_fin || data.fecha) + "T12:00:00");
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        setEventoPasado(fin < hoy);
      }, () => {});
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.evento_id]);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [, setNombreVivo] = useState(null); // solo fuerza el re-render cuando el @ del perfil difiere del guardado en el post
  const [showPerfilPublico, setShowPerfilPublico] = useState(false);
  const [perfilComentarista, setPerfilComentarista] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showDenunciar, setShowDenunciar] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editTexto, setEditTexto] = useState(post.texto || "");
  // Copy plegado como en Instagram: se muestran los primeros 125 caracteres y el
  // resto queda detrás de "más", así una descripción larga no empuja el feed.
  const [copyExpandido, setCopyExpandido] = useState(false);
  const LIMITE_COPY = 125;
  const copyLargo = (post.texto || "").length > LIMITE_COPY;
  const textoCopy = (!copyLargo || copyExpandido)
    ? (post.texto || "")
    : (post.texto || "").slice(0, LIMITE_COPY).trimEnd() + "…";
  const [miUserId, setMiUserId] = useState(null);
  // "Seguir" en la cabecera del post (revisión UX, nivel "esta semana")
  const seguidosPost = useSeguidos(miUserId);
  const [seguirEstado, setSeguirEstado] = useState(null); // null | "yendo" | "ok"
  const mostrarSeguir = !!miUserId && !!post.user_id && post.user_id !== miUserId && post.user_id !== BOT_OLBOS_ID
    && !!seguidosPost && (!seguidosPost.has(post.user_id) || seguirEstado === "ok");
  const seguirAutor = async (e) => {
    e.stopPropagation();
    if (seguirEstado) return;
    setSeguirEstado("yendo");
    const { error } = await seguirRider(post.user_id, "post");
    setSeguirEstado(error ? null : "ok");
    if (error) alert("No se pudo seguir: " + (error.message || "probá de nuevo"));
  };
  // Cuenta la vista cuando la mitad del post estuvo un segundo en pantalla
  const postRef = useRef(null);
  useEffect(() => {
    const el = postRef.current;
    if (!el || !post?.id || typeof IntersectionObserver === "undefined") return;
    let timer = null;
    const obs = new IntersectionObserver((entradas) => {
      entradas.forEach(en => {
        if (en.isIntersecting) {
          timer = setTimeout(() => registrarVistaPost(post.id), 1000);
        } else {
          clearTimeout(timer);
        }
      });
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => { clearTimeout(timer); obs.disconnect(); };
  }, [post?.id]);
  const [showEtiquetarPost, setShowEtiquetarPost] = useState(false);

  useEffect(() => {
    verificarLike();
    cargarAvatar();
    cargarComentarios();
    supabase.auth.getUser().then(({ data: { user } }) => { if (user) setMiUserId(user.id); });
  }, []);

  const eliminarPost = async () => {
    if (!window.confirm("¿Eliminár esta publicación?")) return;
    await supabase.from("posts").delete().eq("id", post.id);
    onReload?.();
  };

  const guardarEdicion = async () => {
    await supabase.from("posts").update({ texto: editTexto }).eq("id", post.id);
    setEditando(false);
    onReload?.();
  };

  // Guardar etiquetas de personas en un post YA publicado (reconcilia: borra y reinserta).
  const guardarTagsPost = async (nuevos) => {
    setShowEtiquetarPost(false);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const filas = nuevos.map(t => {
      const idx = medias.findIndex(m => m.url === t.url);
      return idx >= 0 ? { post_id: post.id, media_index: idx, user_id: t.user_id, username: t.username, x: t.x, y: t.y } : null;
    }).filter(Boolean);
    await supabase.from("post_tags").delete().eq("post_id", post.id).then(() => {}, () => {});
    if (filas.length) await supabase.from("post_tags").insert(filas).then(() => {}, () => {});
    // notificar solo a los etiquetados NUEVOS
    const antes = new Set(tags.map(t => t.user_id));
    const nuevosUsuarios = filas.filter((t, i, a) => a.findIndex(x => x.user_id === t.user_id) === i).filter(t => !antes.has(t.user_id) && t.user_id !== user.id);
    for (const t of nuevosUsuarios) {
      supabase.from("notificaciones").insert({
        user_id: t.user_id, tipo: "etiqueta",
        from_username: await usernameReal(user),
        texto: "te etiquetó en una foto",
      }).then(() => {}, () => {});
    }
    cargarTags();
  };

  // Denunciar una publicación (la revisa el equipo desde el AdminPanel → reportes)
  const denunciarPost = async (motivo) => {
    if (!miUserId) { alert("Iniciá sesión para denunciar."); return; }
    await supabase.from("reportes").insert({
      reporter_id: miUserId,
      reported_id: post.user_id,
      tipo: "post",
      referencia_id: post.id,
      motivo,
    });
    setShowDenunciar(false);
    setShowMenu(false);
    alert("Gracias. Vamos a revisar esta publicación.");
  };

  const [tags, setTags] = useState([]);
  const cargarTags = async () => {
    const { data } = await supabase.from("post_tags").select("*").eq("post_id", post.id);
    if (data) setTags(data);
  };

  useEffect(() => {
    verificarLike();
    cargarAvatar();
    cargarComentarios(); // cargar comentarios al montar
    cargarTags(); // etiquetas de personas sobre las fotos
  }, []);

  const cargarAvatar = async () => {
    if (!post.user_id) return;
    const { data } = await supabase.from("perfiles").select("avatar_url, username").eq("id", post.user_id).single();
    if (data?.avatar_url) setAvatarUrl(data.avatar_url);
    // El @ vivo del perfil manda sobre la copia guardada en el post: si el rider
    // cambió su @ (o el post se guardó con el nombre de Auth), se muestra el actual
    if (data?.username && data.username !== post.username) { post.username = data.username; setNombreVivo(data.username); }
  };

  const verificarLike = async () => {
    // Siempre cargar el conteo real de likes
    const { count } = await supabase.from("likes").select("id", { count: "exact", head: true }).eq("post_id", post.id);
    if (count !== null) setLikesCount(count);
    // Solo verificar si el usuario actual le dio like si está logueado
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("likes").select("id").eq("post_id", post.id).eq("user_id", user.id).single();
    if (data) setLiked(true);
  };

  const toggleLike = async () => {
    if (likeEnCurso.current) return;
    const { data: { user } } = await supabase.auth.getSession().then(r => ({ data: { user: r.data?.session?.user || null } }));
    if (!user) return;
    likeEnCurso.current = true;
    // Actualización instantánea: el contador y el corazón cambian al tocar, y si falla se revierte.
    const eraLike = liked;
    setLiked(!eraLike);
    setLikesCount(c => eraLike ? Math.max(0, c - 1) : c + 1);
    try {
      const { error } = eraLike
        ? await supabase.from("likes").delete().eq("post_id", post.id).eq("user_id", user.id)
        : await supabase.from("likes").insert({ post_id: post.id, user_id: user.id });
      if (error && error.code !== "23505") throw error;
      if (!eraLike) registrarUso("feed_interaccion", { tipo: "like" });
    } catch (e) {
      setLiked(eraLike);
      setLikesCount(c => eraLike ? c + 1 : Math.max(0, c - 1));
    }
    likeEnCurso.current = false;
  };

  const cargarComentarios = async () => {
    const { data } = await supabase.from("comentarios").select("*").eq("post_id", post.id).order("created_at", { ascending: true });
    if (!data) return;
    const ocultar = await idsOcultos();
    const visibles = ocultar.length ? data.filter(c => !ocultar.includes(c.user_id)) : data;
    // El usuario queda guardado en el comentario tal como estaba ese día, así que
    // si después lo cambiás, los comentarios viejos seguían mostrando el anterior.
    // Se resuelve siempre contra el perfil actual.
    const ids = [...new Set(visibles.map(c => c.user_id).filter(Boolean))];
    if (!ids.length) { setComentarios(visibles); return; }
    const { data: perfs } = await supabase.from("perfiles").select("id, username, avatar_url").in("id", ids);
    const mapa = {};
    (perfs || []).forEach(p => { mapa[p.id] = p; });
    setComentarios(visibles.map(c => ({
      ...c,
      username: mapa[c.user_id]?.username || c.username,
      _avatar: mapa[c.user_id]?.avatar_url || null,
    })));
  };

  const enviarComentario = async () => {
    if (!nuevoComentario.trim()) return;
    setEnviando(true);
    const { data: { user } } = await supabase.auth.getUser();
    // Sin sesión: pedir la cuenta en vez de reventar con user.id (el botón quedaba en "…")
    if (!user) { setEnviando(false); if (requireAuth) requireAuth(() => {}); else window.dispatchEvent(new CustomEvent("olbos-abrir-registro")); return; }
    // Guardar el username REAL del perfil, no el de user_metadata (que puede ser
    // el nombre de Google o el mail cortado y no coincide con el @ que se ve)
    const { data: miPerfil } = await supabase.from("perfiles").select("username").eq("id", user.id).maybeSingle();
    await supabase.from("comentarios").insert({
      post_id: post.id, user_id: user.id,
      username: miPerfil?.username || user.user_metadata?.nombre || user.email.split("@")[0],
      texto: nuevoComentario.trim(),
    });
    registrarUso("feed_interaccion", { tipo: "comentario" });
    setNuevoComentario("");
    cargarComentarios();
    setEnviando(false);
  };

  const medias = post.media_urls ? JSON.parse(post.media_urls) : post.media_url ? [{ url: post.media_url, type: post.media_type }] : [];
  const tagsPorIndice = {};
  tags.forEach(t => { (tagsPorIndice[t.media_index] = tagsPorIndice[t.media_index] || []).push(t); });

  return (
    <div ref={postRef} style={{ background: C.card, borderBottom: `1px solid ${C.border}`, position: "relative" }}>
      {/* header */}
      <div onClick={() => setShowPerfilPublico(true)} style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 16px 10px", cursor: "pointer" }}>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.accent, overflow: "hidden", flexShrink: 0, border: `1px solid ${C.border}` }}>
          {avatarUrl
            ? <img loading="lazy" decoding="async" src={avatarUrl} alt={post.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#fff" }}>
                {post.username?.[0]?.toUpperCase() || "?"}
              </div>
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>@{post.username}</div>
            <BadgeTag badge={getBadge(null, post.username)} />
            <SelloConcesionario userId={post.user_id} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 11, color: C.muted }}>{new Date(post.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</div>
            {post.categoria && post.categoria !== "general" && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.muted }}>
                {post.categoria === "ruta" ? "📍 Ruta" : post.categoria === "mecanica" ? "🔧 Mecánica" : post.categoria === "moto" ? "🏍️ Mi moto" : post.categoria === "evento" ? "🎉 Evento" : post.categoria === "foto" ? "📸 Foto" : "💬 General"}
              </span>
            )}
          </div>
        </div>
        {mostrarSeguir && (
          <button onClick={seguirAutor} disabled={seguirEstado === "yendo"}
            style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 9, border: "none", fontSize: 12, fontWeight: 700, letterSpacing: "-0.005em", cursor: seguirEstado ? "default" : "pointer", background: seguirEstado === "ok" ? C.surface : "#111111", color: seguirEstado === "ok" ? C.text : "#F9F5F2" }}>
            {seguirEstado === "ok" ? "Siguiendo" : seguirEstado === "yendo" ? "…" : "Seguir"}
          </button>
        )}
        {/* tres puntos: autor → editar/eliminar; resto → denunciar */}
        {miUserId && (
          <button onClick={e => { e.stopPropagation(); setShowMenu(s => !s); }}
            style={{ width: 32, height: 32, borderRadius: "50%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <MoreHorizontal size={18} strokeWidth={2} color={C.muted} />
          </button>
        )}
      </div>

      {/* menú editar/eliminar (autor) o denunciar (otros) */}
      {showMenu && (
        <div style={{ position: "absolute", top: 56, right: 16, background: C.card, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,.15)", border: `1px solid ${C.border}`, zIndex: 50, overflow: "hidden", minWidth: 160 }}>
          {miUserId === post.user_id ? (
            <>
              <button onClick={() => { setEditando(true); setShowMenu(false); }}
                style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                ✏️ Editar
              </button>
              {medias.some(m => m.type !== "video") && (
                <button onClick={() => { setShowEtiquetarPost(true); setShowMenu(false); }}
                  style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                  👤 Etiquetar personas
                </button>
              )}
              <button onClick={() => { eliminarPost(); setShowMenu(false); }}
                style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.red, display: "flex", alignItems: "center", gap: 8 }}>
                🗑️ Eliminar
              </button>
            </>
          ) : (
            <>
              <button onClick={async () => {
                setShowMenu(false);
                if (!miUserId) { alert("Iniciá sesión."); return; }
                if (!window.confirm(`¿Dejar de ver publicaciones de @${post.username}? Él no se entera, y podés revertirlo desde su perfil.`)) return;
                const { error } = await supabase.rpc("toggle_silenciar", { p_id: post.user_id });
                if (error) { alert("Error: " + error.message); return; }
                invalidarOcultos();
                onReload?.();
              }}
                style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                🔇 No ver más a @{post.username}
              </button>
              {/* Bloquear: corta el vínculo en los dos sentidos y le saca el contenido del
                  feed al toque (el filtro de ocultos se invalida y recarga). */}
              <button onClick={async () => {
                setShowMenu(false);
                if (!miUserId) { alert("Iniciá sesión."); return; }
                if (!window.confirm(`¿Bloquear a @${post.username}? No van a poder verse ni escribirse, y sus publicaciones desaparecen de tu feed.`)) return;
                const { error } = await supabase.rpc("toggle_bloquear", { p_blocked_id: post.user_id });
                if (error) { alert("Error: " + error.message); return; }
                // El bloqueo también le avisa al equipo (lo exige Apple en la guideline 1.2):
                // queda en la bandeja de reportes del AdminPanel para revisar en el día.
                supabase.from("reportes").insert({
                  reporter_id: miUserId, reported_id: post.user_id,
                  tipo: "bloqueo", referencia_id: post.id,
                  motivo: "Usuario bloqueado desde una publicación",
                }).then(() => {}, () => {});
                invalidarOcultos();
                onReload?.();
                alert(`Bloqueaste a @${post.username}. Podés revertirlo desde su perfil.`);
              }}
                style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                <Lock size={15} strokeWidth={2} /> Bloquear a @{post.username}
              </button>
              <button onClick={() => { setShowMenu(false); setShowDenunciar(true); }}
                style={{ width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.red, display: "flex", alignItems: "center", gap: 8 }}>
                <Flag size={15} strokeWidth={2} /> Denunciar publicación
              </button>
            </>
          )}
        </div>
      )}

      {showEtiquetarPost && (
        <EtiquetarPersonasModal
          medias={medias}
          tagsIniciales={tags.map(t => ({ url: medias[t.media_index]?.url, x: t.x, y: t.y, user_id: t.user_id, username: t.username })).filter(t => t.url)}
          onClose={() => setShowEtiquetarPost(false)}
          onGuardar={guardarTagsPost}
        />
      )}

      {/* modal denunciar publicación */}
      {showDenunciar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowDenunciar(false)}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(28px + var(--sab))", width: "100%", maxWidth: 460 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4, letterSpacing: "-0.01em" }}>Denunciar publicación</h3>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>¿Cuál es el motivo? Lo revisa el equipo de Olbos.</p>
            {["No tiene que ver con motos", "Contenido inapropiado", "Spam o promoción", "Otro"].map(m => (
              <button key={m} onClick={() => denunciarPost(m)}
                style={{ width: "100%", padding: "13px 14px", textAlign: "left", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                {m}
              </button>
            ))}
            <button onClick={() => setShowDenunciar(false)}
              style={{ width: "100%", padding: "12px", background: "none", border: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: C.muted, marginTop: 4 }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {showPerfilPublico && post.user_id && (
        <PerfilPublico userId={post.user_id} onClose={() => setShowPerfilPublico(false)} />
      )}
      {perfilComentarista && (
        <PerfilPublico userId={perfilComentarista} onClose={() => setPerfilComentarista(null)} />
      )}

      {/* texto con mentions clicables o modo edición */}
      {editando ? (
        <div style={{ padding: "0 16px 10px" }}>
          <textarea value={editTexto} onChange={e => setEditTexto(e.target.value)} rows={3}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, resize: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditando(false)} style={{ flex: 1, padding: "8px 0", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
            <button onClick={guardarEdicion} style={{ flex: 2, padding: "8px 0", borderRadius: 10, background: C.text, color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer" }}>Guardar</button>
          </div>
        </div>
      ) : (post.texto && medias.length === 0) ? (
        // Sin foto, el texto es el post: va acá arriba. Con foto va DEBAJO (estilo Instagram).
        <p style={{ fontSize: 14, color: C.text, lineHeight: 1.6, padding: "0 16px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {renderTextoConMenciones(textoCopy, async (username) => {
            const { data } = await supabase.from("perfiles").select("id").eq("username", username).maybeSingle();
            if (data?.id) setPerfilComentarista(data.id);
          })}
          {copyLargo && !copyExpandido && (
            <span onClick={() => setCopyExpandido(true)}
              style={{ color: C.muted, fontWeight: 600, cursor: "pointer", marginLeft: 4 }}> más</span>
          )}
        </p>
      ) : null}

      {/* media — borde a borde, con etiquetas y doble toque para dar like */}
      {medias.length > 0 && (
        <MediaDobleTap onLike={() => { const dar = () => { if (!liked) toggleLike(); }; requireAuth ? requireAuth(dar) : dar(); }}>
          {medias.length === 1
            ? (medias[0].type === "video"
                ? <VideoPost src={medias[0].url} />
                : <FotoConTags url={medias[0].url} tags={tagsPorIndice[0] || []} onPerfil={setPerfilComentarista} />)
            : <CarruselMedia medias={medias} tagsPorIndice={tagsPorIndice} onPerfil={setPerfilComentarista} />}
        </MediaDobleTap>
      )}
      {/* línea "con @X, @Y" debajo de la media */}
      {tags.length > 0 && (
        <div style={{ padding: "8px 16px 0", fontSize: 12.5, color: C.muted, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <User size={13} strokeWidth={2} color={C.muted} /> Con
          {tags.filter((t, i, a) => a.findIndex(x => x.user_id === t.user_id) === i).map((t, i, arr) => (
            <span key={t.user_id} onClick={() => setPerfilComentarista(t.user_id)} style={{ color: C.text, fontWeight: 700, cursor: "pointer" }}>
              @{t.username}{i < arr.length - 1 ? "," : ""}
            </span>
          ))}
        </div>
      )}

      <BotonPromoPost promo={promo} />

      {post.ig_permalink && (
        <a href={post.ig_permalink} target="_blank" rel="noopener noreferrer"
          style={{ display: "inline-block", margin: "8px 16px 0", fontSize: 12.5, fontWeight: 700, color: C.muted, textDecoration: "none" }}>
          Ver el reel en Instagram ↗
        </a>
      )}

      {/* acciones */}
      <div style={{ display: "flex", gap: 16, padding: "10px 16px" }}>
        <button onClick={() => requireAuth ? requireAuth(toggleLike) : toggleLike()} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: liked ? C.red : C.muted, fontSize: 13, fontWeight: liked ? 700 : 400 }}>
          <Heart size={18} strokeWidth={liked ? 0 : 1.8} fill={liked ? C.red : "none"} color={liked ? C.red : C.muted} /> {likesCount}
        </button>
        <button onClick={() => setShowComentarios(s => !s)}
          style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: showComentarios ? C.text : C.muted, fontSize: 13, fontWeight: showComentarios ? 700 : 400 }}>
          <MessageCircle size={18} strokeWidth={showComentarios ? 2.2 : 1.8} /> {comentarios.length > 0 ? comentarios.length : "Comentar"}
        </button>
        {/* Vistas en fotos y videos (Carlos, 30-oct: "que se vean como en Instagram"). El
            que publicó las ve siempre; los demás, desde 10 (un "3" a la vista de todos
            desalienta más de lo que motiva). Se cuenta una por rider, como Instagram. */}
        {(() => {
          const vistas = Number(post.vistas || 0);
          const esMio = miUserId && post.user_id === miUserId;
          if (vistas < 1 || (!esMio && vistas < 10)) return null;
          return (
            <span title={`${vistas.toLocaleString("es-AR")} rider${vistas === 1 ? "" : "s"} lo vieron`} style={{ display: "flex", alignItems: "center", gap: 5, color: C.muted, fontSize: 13 }}>
              <Eye size={17} strokeWidth={1.8} />
              {vistas.toLocaleString("es-AR")}
            </span>
          );
        })()}
        <BtnCompartir
          texto={`${post.username ? "@" + post.username + ": " : ""}${(post.texto || "").slice(0, 100)}`}
          tipo="post" id={post.id} small
          style={{ marginLeft: "auto" }}
        />
      </div>

      {/* El post que anuncia un evento lleva directo a anotarse — antes decía
          "buscalo en la sección Eventos" y había que ir a revolver el listado */}
      {post.evento_id && (
        <div style={{ padding: "0 16px 10px" }}>
          <button onClick={() => { if (eventoPasado === false) registrarUso("feed_interaccion", { tipo: "anotarse" }); window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: post.evento_id } })); }}
            style={{ width: "100%", padding: "11px", borderRadius: 11, background: C.text, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.005em" }}>
            {eventoPasado === false ? "🎉 Ver el evento y anotarse" : "Ver el evento"}
          </button>
        </div>
      )}

      {/* Post de un recorrido suelto (sin evento): CTA al track — de ahí cualquiera
          puede tocar "Recorrer esta ruta". El loop: video en el feed → recorrido → salir a hacerlo */}
      {!post.evento_id && post.traza_id && (
        <div style={{ padding: "0 16px 10px" }}>
          <button onClick={() => setShowRecorrido(true)}
            style={{ width: "100%", padding: "11px", borderRadius: 11, background: C.text, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", letterSpacing: "-0.005em" }}>
            Ver el recorrido
          </button>
        </div>
      )}
      {showRecorrido && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9400, background: C.card, overflowY: "auto" }}>
          <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
            <RutaPublica trazaId={post.traza_id} onVolver={() => setShowRecorrido(false)} />
          </div>
        </div>
      )}

      {/* caption debajo de la foto, como Instagram: @usuario + texto */}
      {post.texto && medias.length > 0 && !editando && (
        <p style={{ fontSize: 14, color: C.text, lineHeight: 1.5, padding: "0 16px 10px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          <span onClick={() => post.user_id && setShowPerfilPublico(true)}
            style={{ fontWeight: 800, marginRight: 5, cursor: "pointer" }}>@{post.username}</span>
          {renderTextoConMenciones(textoCopy, async (username) => {
            const { data } = await supabase.from("perfiles").select("id").eq("username", username).maybeSingle();
            if (data?.id) setPerfilComentarista(data.id);
          })}
          {copyLargo && !copyExpandido && (
            <span onClick={() => setCopyExpandido(true)}
              style={{ color: C.muted, fontWeight: 600, cursor: "pointer", marginLeft: 4 }}> más</span>
          )}
        </p>
      )}

      {/* comentarios */}
      {showComentarios && (
        <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          {comentarios.map(c => (
            <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {/* foto real del que comenta (pedido de Matías); la inicial queda de fallback */}
              {c._avatar ? (
                <img loading="lazy" decoding="async" src={cldThumb(c._avatar, 56)} alt="" onClick={() => c.user_id && setPerfilComentarista(c.user_id)}
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}`, flexShrink: 0, cursor: c.user_id ? "pointer" : "default" }} />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                  {c.username?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div style={{ flex: 1, background: C.surface, borderRadius: 10, padding: "7px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <div onClick={() => c.user_id && setPerfilComentarista(c.user_id)}
                    style={{ fontSize: 11, fontWeight: 700, color: C.text, cursor: c.user_id ? "pointer" : "default" }}>
                    @{c.username}
                  </div>
                  <BadgeTag badge={getBadge(null, c.username)} />
                  {/* borrar el comentario propio (pedido de Matías) */}
                  {miUserId && c.user_id === miUserId && (
                    <button onClick={async () => {
                      if (!window.confirm("¿Borrar tu comentario?")) return;
                      await supabase.from("comentarios").delete().eq("id", c.id).then(() => {}, () => {});
                      cargarComentarios();
                    }} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 11, padding: "0 2px" }}>✕</button>
                  )}
                </div>
                <div style={{ fontSize: 13, color: C.text }}>{c.texto}</div>
              </div>
            </div>
          ))}
          {/* nuevo comentario */}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input value={nuevoComentario} onChange={e => setNuevoComentario(e.target.value)}
              placeholder="Escribí un comentario..." onKeyDown={e => e.key === "Enter" && enviarComentario()}
              style={{ flex: 1, padding: "9px 12px", borderRadius: 20, border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text }} />
            <button onClick={enviarComentario} disabled={enviando || !nuevoComentario.trim()}
              style={{ padding: "9px 14px", borderRadius: 20, background: nuevoComentario.trim() ? C.text : C.border, color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              {enviando ? "..." : "Enviar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Tarjeta de onboarding "Primeros pasos" — guía la primera vez en el feed.
// Los tildes salen de datos reales (avatar, motos, grupos, trazas); el de "salida"
// se marca al tocarlo (no hay señal de completado). Se oculta cuando termina o si cierra.
const OnboardingChecklist = ({ session, setTab, compacto = false }) => {
  const [estado, setEstado] = useState(null);
  const [cerrado, setCerrado] = useState(false);

  useEffect(() => {
    if (!session) { setEstado(null); return; }
    let activo = true;
    (async () => {
      const uid = session.user.id;
      const [perfilRes, gruposRes, trazasRes] = await Promise.all([
        supabase.from("perfiles").select("avatar_url, motos, onboarding").eq("id", uid).single(),
        supabase.rpc("get_mis_grupos"),
        supabase.from("trazas").select("id", { count: "exact", head: true }).eq("user_id", uid),
      ]);
      if (!activo) return;
      const perfil = perfilRes.data || {};
      const ob = perfil.onboarding || {}; // persistido en la base, por usuario
      const gruposArr = typeof gruposRes.data === "string" ? JSON.parse(gruposRes.data) : gruposRes.data;
      setCerrado(ob.oculto === true);
      setEstado({
        avatar: !!perfil.avatar_url,
        moto: Array.isArray(perfil.motos) && perfil.motos.length > 0,
        grupo: Array.isArray(gruposArr) && gruposArr.length > 0,
        evento: ob.evento === true,
        track: (trazasRes.count || 0) > 0,
      });
    })();
    return () => { activo = false; };
  }, [session]);

  if (!session || cerrado || !estado) return null;

  const pasos = [
    { key: "avatar", emoji: "📸", label: "Subí tu foto de perfil",     corto: "Tu foto",        done: estado.avatar, tab: "perfil" },
    { key: "moto",   emoji: "🏍️", label: "Agregá tu moto al garage",   corto: "Tu moto",        done: estado.moto,   tab: "perfil" },
    { key: "grupo",  emoji: "👥", label: "Unite o creá un grupo con tus amigos", corto: "Tu grupo", done: estado.grupo,  tab: "grupos" },
    { key: "evento", emoji: "📍", label: "Mirá una salida cerca tuyo", corto: "Una salida",     done: estado.evento, tab: "eventos" },
    { key: "track",  emoji: "🛰️", label: "Grabá tu primera salida",     corto: "Grabá una salida", done: estado.track,  tab: "rutas" },
  ];
  const completos = pasos.filter(p => p.done).length;
  const total = pasos.length;

  const guardarOb = (patch) => {
    supabase.rpc("set_onboarding", { p_patch: patch }).then(() => {}, () => {});
  };
  const cerrar = () => {
    setCerrado(true);
    guardarOb({ oculto: true });
  };
  const ir = (p) => {
    if (p.key === "evento") {
      setEstado(e => e ? { ...e, evento: true } : e); // tilde inmediato
      guardarOb({ evento: true });
    }
    if (setTab) setTab(p.tab);
  };

  // Compacto (intercalado en el feed, revisión UX 10-oct-2026): una fila de chips con lo que falta
  if (compacto && completos < total) {
    const pendientes = pasos.filter(p => !p.done).slice(0, 3);
    return (
      <div style={{ margin: "12px 16px", background: C.card, borderRadius: 16, padding: "12px 14px", border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>Primeros pasos <span style={{ color: C.muted, fontWeight: 600 }}>· {completos} de {total}</span></div>
          <button onClick={cerrar} style={{ background: "none", border: "none", color: "#c9c3ba", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "2px 4px" }}>Ocultar</button>
        </div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 2 }}>
          {pendientes.map(p => (
            <button key={p.key} onClick={() => ir(p)} style={{ flex: "0 0 auto", padding: "9px 13px", borderRadius: 100, background: C.text, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{p.emoji} {p.corto}</button>
          ))}
        </div>
      </div>
    );
  }

  // Todo completo → tarjeta de festejo
  if (completos === total) {
    return (
      <div style={{ margin: "12px 16px", background: "linear-gradient(135deg,#FFF4ED 0%,#fff 100%)", borderRadius: 16, padding: "20px 18px", border: "1px solid #11111122", textAlign: "center" }}>
        <div style={{ fontSize: 30, marginBottom: 6 }}>🎉</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>¡Completaste tus primeros pasos!</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 4, marginBottom: 14, lineHeight: 1.5 }}>Ya sos parte de la comunidad. Disfrutá el camino 🏍️</div>
        <button onClick={cerrar} style={{ padding: "9px 24px", borderRadius: 10, background: C.accent, color: "#fff", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer" }}>Listo</button>
      </div>
    );
  }

  return (
    <div style={{ margin: "12px 16px", background: C.card, borderRadius: 16, padding: "16px 16px 8px", border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.03)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>Primeros pasos</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>Arrancá tu camino en Olbos</div>
        </div>
        <button onClick={cerrar} style={{ background: "none", border: "none", color: "#c9c3ba", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "2px 4px" }}>Ocultar</button>
      </div>
      {/* barra de progreso */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 14px" }}>
        <div style={{ flex: 1, height: 6, borderRadius: 100, background: `${C.border}`, overflow: "hidden" }}>
          <div style={{ width: `${(completos / total) * 100}%`, height: "100%", borderRadius: 100, background: C.accent, transition: "width .3s" }} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{completos} de {total}</div>
      </div>
      {/* pasos */}
      {pasos.map((p, i) => (
        <button key={p.key} onClick={() => { if (!p.done) ir(p); }} disabled={p.done}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 2px", background: "none", borderTop: i === 0 ? "none" : "1px solid #f1ece6", borderLeft: "none", borderRight: "none", borderBottom: "none", cursor: p.done ? "default" : "pointer", textAlign: "left" }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, background: p.done ? "#1F8347" : C.surface, color: "#fff" }}>
            {p.done ? "✓" : p.emoji}
          </div>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: p.done ? "#b0b0b5" : "#111111", textDecoration: p.done ? "line-through" : "none", letterSpacing: "-0.01em" }}>{p.label}</div>
          {!p.done && <span style={{ fontSize: 18, color: "#c9c3ba", fontWeight: 400 }}>›</span>}
        </button>
      ))}
    </div>
  );
};

// ─── ¿Quién te invitó? ───────────────────────────────────────────────────────
// La atribución de referidos se pierde cuando la cuenta se crea en la app
// instalada: el código del link queda en el navegador web y no viaja a la app.
// Solución sin fricción (pedido de Carlos): UNA tarjeta descartable en la
// primera sesión — opcional, se cierra con un toque y no vuelve a aparecer.
const PreguntaReferido = ({ session }) => {
  const [visible, setVisible] = useState(false);
  const [quien, setQuien] = useState("");
  const [estado, setEstado] = useState(null); // null | guardando | ok | error
  useEffect(() => {
    if (!session?.user?.id) return;
    try { if (localStorage.getItem("olbos_ref_preguntado")) return; } catch (e) { /* */ }
    let vivo = true;
    supabase.from("perfiles").select("referido_por, created_at").eq("id", session.user.id).maybeSingle()
      .then(({ data }) => {
        if (!vivo || !data) return;
        const dias = (Date.now() - new Date(data.created_at).getTime()) / 86400000;
        if (!data.referido_por && dias < 14) setVisible(true);
      }, () => {});
    return () => { vivo = false; };
  }, [session?.user?.id]);
  const cerrar = () => { setVisible(false); try { localStorage.setItem("olbos_ref_preguntado", "1"); } catch (e) { /* */ } };
  const guardar = async () => {
    const cod = quien.trim().replace(/^@/, "");
    if (!cod) return;
    setEstado("guardando");
    let { data: cv } = await supabase.from("codigos_referidos").select("codigo").eq("codigo", cod).eq("activo", true).maybeSingle();
    if (!cv) { const r2 = await supabase.from("codigos_referidos").select("codigo").ilike("codigo", cod).eq("activo", true).limit(1); cv = r2.data && r2.data[0]; }
    if (!cv) { setEstado("error"); return; }
    await supabase.from("perfiles").update({ referido_por: cv.codigo }).eq("id", session.user.id);
    setEstado("ok");
    setTimeout(cerrar, 1800);
  };
  if (!visible) return null;
  return (
    <div className="fade-up" style={{ margin: "12px 16px 0", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 15px" }}>
      {estado === "ok" ? (
        <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>✓ ¡Listo! Le sumaste el referido.</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text }}>¿Un rider te invitó a Olbos?</div>
            <button onClick={cerrar} style={{ width: 24, height: 24, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.muted, fontSize: 11, flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 8px" }}>Poné su @usuario y le sumás el referido. Si llegaste por tu cuenta, cerrá esto y listo.</div>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={quien} onChange={e => { setQuien(e.target.value); if (estado === "error") setEstado(null); }} placeholder="@usuario"
              style={{ flex: 1, padding: "9px 11px", borderRadius: 9, border: `1px solid ${estado === "error" ? C.red : C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box" }} />
            <button onClick={guardar} disabled={estado === "guardando" || !quien.trim()}
              style={{ padding: "9px 16px", borderRadius: 9, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (estado === "guardando" || !quien.trim()) ? 0.6 : 1 }}>
              {estado === "guardando" ? "…" : "Listo"}
            </button>
          </div>
          {estado === "error" && <div style={{ fontSize: 11, color: C.red, marginTop: 5 }}>No encontramos ese usuario — fijate cómo se escribe.</div>}
        </>
      )}
    </div>
  );
};

// ─── Completar el perfil sin tipear: ciudad (1 tap) y moto (2 toques) ─────────
// Lo que de verdad le importa a una marca o a un organizador es CUÁNTOS riders
// hay en su zona y de qué marca — y el 83% de los perfiles tenía la ciudad vacía
// (02-sep-2026). La regla: nunca pedir que escriban, solo que confirmen.
// Ciudad → "Ciudad, Provincia" (nunca coordenadas). Moto → del catálogo MOTOS_DB
// para que la marca quede normalizada y contable.
const ciudadDesdeCoords = (lat, lng) => new Promise((resolve) => {
  loadGoogleMaps().then(() => {
    if (!window.google) { resolve(null); return; }
    new window.google.maps.Geocoder().geocode({ location: { lat, lng }, language: "es" }, (res, st) => {
      if (st !== "OK" || !res || !res[0]) { resolve(null); return; }
      const parts = res[0].address_components || [];
      const get = (t) => parts.find(x => x.types.includes(t))?.long_name || "";
      const ciudad = get("locality") || get("administrative_area_level_2") || "";
      const prov = get("administrative_area_level_1") || "";
      if (!ciudad && !prov) { resolve(null); return; }
      resolve(ciudad && prov && ciudad !== prov ? `${ciudad}, ${prov}` : (ciudad || prov));
    });
  }).catch(() => resolve(null));
});
// Marcas que se venden acá y todavía no están en el catálogo: van como chip igual,
// con el modelo a mano, para que la marca quede limpia aunque el modelo sea libre
const MARCAS_EXTRA = ["Zanella", "Gilera", "Motomel", "Guerrero", "Keeway", "Mondial", "Beta", "Hero", "Brava", "Keller", "RVM", "QJ Motor", "Aprilia", "Husqvarna", "Indian", "MV Agusta", "Moto Guzzi", "Jawa", "Gas Gas"];
const MARCAS_POPULARES = ["Honda", "Yamaha", "Bajaj", "Zanella", "Motomel", "Gilera", "Kawasaki", "Suzuki", "KTM", "BMW", "Royal Enfield", "Voge"];
const marcasParaElegir = () => {
  const cat = MOTOS_DB.map(m => m.marca).filter((x, i, a) => a.indexOf(x) === i);
  const todas = [...cat, ...MARCAS_EXTRA].filter((x, i, a) => a.indexOf(x) === i);
  const pop = MARCAS_POPULARES.filter(m => todas.includes(m));
  return [...pop, ...todas.filter(m => !pop.includes(m)).sort((a, b) => a.localeCompare(b))];
};

// Vacía la cola de fotos sin señal: al abrir la app, al volver la conexión y cuando
// la app vuelve al frente. Va montado en la raíz para que funcione aunque el
// grabador esté cerrado (la foto puede haberse sacado horas antes).
const ColaFotosWorker = () => {
  useEffect(() => {
    const correr = () => { subirColaFotos(); };
    correr();
    window.addEventListener("online", correr);
    const alVolver = () => { if (document.visibilityState === "visible") correr(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => { window.removeEventListener("online", correr); document.removeEventListener("visibilitychange", alVolver); };
  }, []);
  return null;
};

// ─── PUERTA DE EDAD (16+) ─────────────────────────────────────────────────────
// Los Términos dicen 16+ desde junio pero no se verificaba nada (mitigación legal
// 04-sep-2026). Toda cuenta sin fecha_nacimiento en perfiles la tiene que cargar
// para seguir: registro por email la trae en los metadatos de Auth (se guarda sola),
// Google/Apple y los usuarios viejos la cargan acá. Menor de 16 → no se guarda nada
// y se cierra la sesión. Si la columna todavía no existe en la BD, no bloquea.
const GateEdad = ({ session, esperar = false }) => {
  const userId = session?.user?.id;
  const [estado, setEstado] = useState("cargando"); // cargando | ok | pedir | menor
  const [fecha, setFecha] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!userId) { setEstado("ok"); return; }
    let vivo = true;
    (async () => {
      const { data, error } = await supabase.from("perfiles").select("fecha_nacimiento").eq("id", userId).maybeSingle();
      if (!vivo) return;
      if (error || data === null) { setEstado("ok"); return; } // sin columna aún / perfil en creación
      if (data.fecha_nacimiento) { setEstado("ok"); return; }
      const meta = session?.user?.user_metadata?.fecha_nacimiento;
      if (meta && (edadDesde(meta) ?? 0) >= EDAD_MINIMA) {
        await supabase.from("perfiles").update({ fecha_nacimiento: String(meta).slice(0, 10) }).eq("id", userId).then(() => {}, () => {});
        setEstado("ok"); return;
      }
      setEstado("pedir");
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const confirmar = async () => {
    const e = edadDesde(fecha);
    if (e === null || e < 0 || e > 110) { alert("Revisá la fecha."); return; }
    if (e < EDAD_MINIMA) { setEstado("menor"); return; }
    setGuardando(true);
    const { error } = await supabase.from("perfiles").update({ fecha_nacimiento: fecha }).eq("id", userId);
    setGuardando(false);
    if (error) { alert("No se pudo guardar. Probá de nuevo."); return; }
    setEstado("ok");
  };
  const salir = async () => { try { await supabase.auth.signOut(); } catch (e) { /* */ } window.location.href = "/"; };

  if (estado !== "pedir" && estado !== "menor") return null;
  if (esperar) return null; // la bienvenida va primero; la puerta aparece al cerrarla
  const hoy = new Date().toISOString().slice(0, 10);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(0,0,0,.55)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 400, background: C.card, borderRadius: 18, padding: "22px 20px", border: `1px solid ${C.border}` }}>
        {estado === "pedir" ? (<>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>Un dato antes de seguir</div>
          <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, marginBottom: 10 }}>Te pedimos tu fecha de nacimiento <strong>una sola vez</strong>. Olbos tiene funciones que se usan arriba de la moto (salidas grupales, ubicación en vivo, navegación) y por eso la comunidad es para riders de {EDAD_MINIMA} años o más: es un requisito de nuestros Términos y de las tiendas de aplicaciones.</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>Tu fecha es privada: no aparece en tu perfil, no la ve ningún otro rider y no se usa para publicidad. Solo sirve para confirmar la edad.</div>
          <input type="date" value={fecha} max={hoy} onChange={e => setFecha(e.target.value)}
            style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 16, background: C.surface, color: C.text, boxSizing: "border-box", fontFamily: "inherit" }} />
          <button onClick={confirmar} disabled={!fecha || guardando}
            style={{ width: "100%", marginTop: 12, padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", opacity: !fecha || guardando ? 0.6 : 1 }}>
            {guardando ? "Guardando…" : "Continuar"}
          </button>
          <button onClick={salir} style={{ width: "100%", marginTop: 8, padding: "10px 0", background: "none", border: "none", color: C.muted, fontSize: 12.5, cursor: "pointer" }}>Cerrar sesión</button>
        </>) : (<>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>Gracias por ser sincero</div>
          <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, marginBottom: 10 }}>Por ahora Olbos es para riders de {EDAD_MINIMA} años o más. No es nada personal: las salidas en ruta, la ubicación en vivo y el resto de las funciones están pensadas para gente que ya puede manejar, y la ley nos pide cuidar eso.</div>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>No guardamos tu fecha ni ningún dato tuyo. Cuando cumplas {EDAD_MINIMA}, la cuenta te espera: el camino es el destino.</div>
          <button onClick={salir} style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>Cerrar sesión</button>
          <a href="/eliminar-cuenta" style={{ display: "block", textAlign: "center", marginTop: 10, fontSize: 12.5, color: C.muted }}>Eliminar mi cuenta y mis datos</a>
        </>)}
      </div>
    </div>
  );
};

// El ✕ posterga una semana en vez de callar para siempre. Antes, un toque distraído
// dejaba a ese rider sin que se le volviera a preguntar nunca, y así llegamos a que 8 de
// cada 10 no tengan ciudad. A la tercera vez que lo cierra ya se entiende que no quiere
// y no se le pregunta más. Los que la cerraron con la versión vieja vuelven a verla una vez.
const CLAVE_SNOOZE = { ciudad: "olbos_zona_preguntada", moto: "olbos_moto_preguntada" };
const DIAS_SNOOZE = 7;
const MAX_CIERRES = 3;
const leerSnooze = (cual) => {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_SNOOZE[cual]) || "null");
    if (!v || typeof v !== "object") return false; // formato viejo ("1"): se vuelve a preguntar
    if ((v.veces || 0) >= MAX_CIERRES) return true;
    return Date.now() < (v.hasta || 0);
  } catch (e) { return false; }
};

const PreguntaPerfil = ({ session, soloCiudad }) => {
  const userId = session?.user?.id;
  const [perfil, setPerfil] = useState(null); // {ciudad, motos}
  const [sugCiudad, setSugCiudad] = useState(undefined); // undefined=buscando, null=sin dato, string
  const [modoCiudad, setModoCiudad] = useState("confirmar"); // confirmar | escribir
  const [qCiudad, setQCiudad] = useState("");
  const [predsCiudad, setPredsCiudad] = useState([]);
  const [marca, setMarca] = useState(null);
  const [verTodas, setVerTodas] = useState(false);
  const [modeloLibre, setModeloLibre] = useState("");
  const [estado, setEstado] = useState(null); // null | guardando | okCiudad | okMoto
  const [cerradas, setCerradas] = useState(() => ({ ciudad: leerSnooze("ciudad"), moto: leerSnooze("moto") }));
  const debRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    supabase.from("perfiles").select("ciudad, motos, tipo_perfil").eq("id", userId).maybeSingle()
      .then(({ data }) => { if (vivo && data) setPerfil(data); }, () => {});
    return () => { vivo = false; };
  }, [userId]);

  const faltaCiudad = perfil && !String(perfil.ciudad || "").trim() && !cerradas.ciudad;
  const motosActivas = (Array.isArray(perfil?.motos) ? perfil.motos : []).filter(m => m && m.estado !== "eliminada");
  const faltaMoto = perfil && motosActivas.length === 0 && !cerradas.moto;
  const esPersona = !perfil?.tipo_perfil || perfil.tipo_perfil === "persona";

  // Inferir la ciudad SIN pedir permisos: GPS solo si ya estaba concedido; si no,
  // el primer punto del primer recorrido grabado; si no hay nada, se pregunta.
  useEffect(() => {
    if (!faltaCiudad || sugCiudad !== undefined) return;
    let vivo = true;
    (async () => {
      let coords = null;
      try {
        if (navigator.permissions && navigator.geolocation) {
          const st = await navigator.permissions.query({ name: "geolocation" });
          if (st.state === "granted") {
            coords = await new Promise(res => navigator.geolocation.getCurrentPosition(
              p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { maximumAge: 600000, timeout: 8000 }));
          }
        }
      } catch (e) { /* sin permissions API (webview): sigue al recorrido */ }
      if (!coords) {
        try {
          const { data } = await supabase.from("trazas").select("inicio:puntos->0").eq("user_id", userId).order("created_at", { ascending: true }).limit(1);
          const p = data && data[0] && data[0].inicio;
          if (p && p.lat && p.lng) coords = { lat: Number(p.lat), lng: Number(p.lng) };
        } catch (e) { /* */ }
      }
      const nombre = coords ? await ciudadDesdeCoords(coords.lat, coords.lng) : null;
      if (!vivo) return;
      setSugCiudad(nombre || null);
      if (!nombre) setModoCiudad("escribir");
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faltaCiudad]);

  const buscarCiudad = (txt) => {
    setQCiudad(txt);
    clearTimeout(debRef.current);
    if (txt.trim().length < 3) { setPredsCiudad([]); return; }
    debRef.current = setTimeout(() => {
      loadGoogleMaps().then(() => {
        if (!window.google) return;
        new window.google.maps.places.AutocompleteService().getPlacePredictions({ input: txt, types: ["(cities)"], language: "es" }, (preds) => {
          setPredsCiudad((preds || []).slice(0, 5).map(p => p.description.replace(/, (Argentina|Chile|Uruguay|Paraguay|Bolivia|Perú|Colombia|México|España)$/, "")));
        });
      }).catch(() => {});
    }, 300);
  };
  const guardarCiudad = async (nombre) => {
    const c = String(nombre || "").trim();
    if (!c || estado === "guardando") return;
    setEstado("guardando");
    const { error } = await supabase.from("perfiles").update({ ciudad: c }).eq("id", userId);
    if (error) { setEstado(null); alert("No se pudo guardar: " + error.message); return; }
    setEstado("okCiudad");
    setTimeout(() => { setPerfil(p => ({ ...p, ciudad: c })); setEstado(null); }, 1400);
  };
  const cerrar = (cual) => {
    setCerradas(c => ({ ...c, [cual]: true }));
    try {
      const prev = JSON.parse(localStorage.getItem(CLAVE_SNOOZE[cual]) || "null");
      const veces = (prev && typeof prev === "object" ? (prev.veces || 0) : 0) + 1;
      localStorage.setItem(CLAVE_SNOOZE[cual], JSON.stringify({ veces, hasta: Date.now() + DIAS_SNOOZE * 86400000 }));
    } catch (e) { /* */ }
  };
  const guardarMoto = async (modelo, cc) => {
    const mod = String(modelo || "").trim();
    if (!marca || !mod || estado === "guardando") return;
    setEstado("guardando");
    // Misma forma que agregarMoto del garage (JSONB en perfiles.motos, borrado suave)
    const motoNueva = { marca, modelo: mod, año: "", color: "", cc: cc || "", fotoUrl: null, nombre: "", media: [], id: Date.now(), estado: "activa" };
    const { data: fresco } = await supabase.from("perfiles").select("motos").eq("id", userId).maybeSingle();
    const lista = [...(Array.isArray(fresco?.motos) ? fresco.motos : []), motoNueva];
    const { error } = await supabase.from("perfiles").update({ motos: lista }).eq("id", userId);
    if (error) { setEstado(null); alert("No se pudo guardar: " + error.message); return; }
    setEstado("okMoto");
    setTimeout(() => { setPerfil(p => ({ ...p, motos: lista })); setEstado(null); }, 1600);
  };

  if (!userId || !perfil || !esPersona) return null;
  const card = { margin: "12px 16px 0", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 15px" };
  const btnPri = { padding: "9px 14px", borderRadius: 9, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" };
  const btnSec = { padding: "9px 12px", borderRadius: 9, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" };
  const chip = (activo) => ({ padding: "7px 11px", borderRadius: 100, border: `1px solid ${activo ? C.accent : C.border}`, background: activo ? C.accent : C.surface, color: activo ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" });
  const Cerrar = ({ cual }) => <button onClick={() => cerrar(cual)} style={{ width: 24, height: 24, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", color: C.muted, fontSize: 11, flexShrink: 0 }}>✕</button>;

  // ── 1) Ciudad ──
  if (faltaCiudad) {
    if (estado === "okCiudad") return <div className="fade-up" style={card}><div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>✓ Listo — ya sos parte de los riders de tu zona.</div></div>;
    if (sugCiudad === undefined) return null; // todavía infiriendo: no parpadear
    return (
      <div className="fade-up" style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text }}>
            {modoCiudad === "confirmar" && sugCiudad ? `¿Sos de ${sugCiudad}?` : "¿De dónde sos?"}
          </div>
          <Cerrar cual="ciudad" />
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 9px" }}>Para mostrarte las rodadas y los eventos que salen cerca tuyo. Solo la ciudad, nunca tu ubicación en vivo.</div>
        {modoCiudad === "confirmar" && sugCiudad ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => guardarCiudad(sugCiudad)} disabled={estado === "guardando"} style={{ ...btnPri, flex: 1 }}>{estado === "guardando" ? "…" : "Sí, de ahí"}</button>
            <button onClick={() => setModoCiudad("escribir")} style={btnSec}>Soy de otro lado</button>
          </div>
        ) : (
          <div>
            <input value={qCiudad} onChange={e => buscarCiudad(e.target.value)} placeholder="Tu ciudad" autoFocus
              style={{ width: "100%", padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box" }} />
            {predsCiudad.length > 0 && (
              <div style={{ marginTop: 6, border: `1px solid ${C.border}`, borderRadius: 9, overflow: "hidden" }}>
                {predsCiudad.map(p => (
                  <button key={p} onClick={() => guardarCiudad(p)} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 11px", background: C.card, border: "none", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.text, cursor: "pointer" }}>📍 {p}</button>
                ))}
              </div>
            )}
            {predsCiudad.length === 0 && qCiudad.trim().length >= 3 && (
              <button onClick={() => guardarCiudad(qCiudad)} disabled={estado === "guardando"} style={{ ...btnPri, marginTop: 8, width: "100%" }}>Guardar "{qCiudad.trim()}"</button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── 2) Moto ──
  if (faltaMoto && !soloCiudad) {
    if (estado === "okMoto") return <div className="fade-up" style={card}><div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>✓ Quedó en tu garage. Fotos, año y km los completás cuando quieras.</div></div>;
    const marcas = marcasParaElegir();
    const visibles = verTodas ? marcas : marcas.slice(0, 12);
    const modelosCat = marca ? MOTOS_DB.filter(m => m.marca === marca).map(m => ({ modelo: m.modelo, cc: m.cc })).filter((x, i, a) => a.findIndex(y => y.modelo === x.modelo) === i) : [];
    return (
      <div className="fade-up" style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text }}>{marca ? `¿Qué ${marca} tenés?` : "¿Qué moto tenés?"}</div>
          <Cerrar cual="moto" />
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 9px" }}>{marca ? "Elegí el modelo y listo — queda en tu garage." : "Dos toques y queda en tu garage. Los detalles los completás después."}</div>
        {!marca ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visibles.map(m => <button key={m} onClick={() => { setMarca(m); setModeloLibre(""); }} style={chip(false)}>{m}</button>)}
            {!verTodas && marcas.length > 12 && <button onClick={() => setVerTodas(true)} style={{ ...chip(false), color: C.muted }}>+ más marcas</button>}
          </div>
        ) : (
          <div>
            {modelosCat.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {modelosCat.map(x => <button key={x.modelo} onClick={() => guardarMoto(x.modelo, x.cc)} disabled={estado === "guardando"} style={chip(false)}>{x.modelo}</button>)}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <input value={modeloLibre} onChange={e => setModeloLibre(e.target.value)} placeholder={modelosCat.length ? "Otro modelo" : "Modelo (ej: RX 150)"}
                style={{ flex: 1, padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box" }} />
              <button onClick={() => guardarMoto(modeloLibre, "")} disabled={estado === "guardando" || !modeloLibre.trim()} style={{ ...btnPri, opacity: modeloLibre.trim() ? 1 : 0.6 }}>{estado === "guardando" ? "…" : "Listo"}</button>
            </div>
            <button onClick={() => setMarca(null)} style={{ background: "none", border: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "8px 0 0" }}>← Otra marca</button>
          </div>
        )}
      </div>
    );
  }
  return null;
};

// ─── 🏆 Ranking de km de la comunidad ─────────────────────────────────────────
// Se abre tocando el contador de km del feed. Top 20 por km grabados (RPC
// ranking_km, security definer que suma las trazas) + tu propio total al pie.
const PERIODOS_RANKING = [
  ["hoy", "Hoy"],
  ["semana", "Semana"],
  ["mes", "Mes"],
  ["total", "Histórico"],
];

// Semanas ya cerradas con su podio congelado. El RPC, además de devolverlas,
// congela las que faltan, así el podio queda guardado aunque nadie abra la app el lunes.
const SemanasCerradas = () => {
  const [semanas, setSemanas] = useState(undefined);
  const [premios, setPremios] = useState({}); // semana (lunes) -> fila de premios_semanales
  useEffect(() => {
    supabase.rpc("ranking_semanas_cerradas", { p_limit: 4 }).then(({ data: raw, error }) => {
      if (error) { setSemanas(null); return; }
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      const arr = Array.isArray(d) ? d : null;
      setSemanas(arr);
      // Premio de cada puesto: vive en premios_semanales (lectura pública), clave = lunes de la semana
      const desdes = (arr || []).map(s => s.desde).filter(Boolean);
      if (desdes.length) {
        supabase.from("premios_semanales").select("semana, premio1, premio2, premio3").in("semana", desdes)
          .then(({ data: rows }) => {
            const m = {};
            (rows || []).forEach(r => { m[r.semana] = r; });
            setPremios(m);
          }, () => {});
      }
    }, () => setSemanas(null));
  }, []);
  if (!semanas || semanas.length === 0) return null;
  const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" });
  const medallas = ["🥇", "🥈", "🥉"];
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Semanas cerradas</div>
      {semanas.map((sem, i) => {
        const podio = (Array.isArray(sem.podio) ? sem.podio : []).slice(0, 3);
        const pr = premios[sem.desde];
        return (
          <div key={sem.desde || i} style={{ padding: "9px 11px", borderRadius: 10, background: C.surface, marginBottom: 6 }}>
            <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600, marginBottom: podio.length ? 5 : 0 }}>{fmt(sem.desde)} al {fmt(sem.hasta)}</div>
            {podio.length === 0 && (
              <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Sin recorridos esa semana</div>
            )}
            {podio.map((g, j) => {
              const premio = pr && pr["premio" + (j + 1)];
              return (
                <div key={g.username || j} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                  <div style={{ fontSize: 15, flexShrink: 0 }}>{medallas[j]}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{g.username}</div>
                    {premio && <div style={{ fontSize: 11.5, color: C.gold, fontWeight: 700 }}>Premio: {premio}</div>}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.text, flexShrink: 0 }}>{Number(g.km).toLocaleString("es-AR")} km</div>
                </div>
              );
            })}
            {sem.sponsor && sem.sponsor.nombre && (
              <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, marginTop: 4 }}>Premio de {sem.sponsor.nombre}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const RankingKmModal = ({ onClose }) => {
  // Un ranking por período. Se cachean los ya traídos para que moverse entre
  // solapas sea instantáneo y no se golpee la base cada vez.
  // Abre en el histórico: hoy es el número que más impresiona con la comunidad chica.
  // Cuando haya volumen conviene arrancar en Semana, que es el período premiable.
  // Desde el 26-oct-2026 abre en Semana: es el período que tiene premio
  const [periodo, setPeriodo] = useState("semana");
  const [cache, setCache] = useState({});
  const [premio, setPremio] = useState(null); // premio de la semana en curso (premios_semanales)
  const [extraDesafio, setExtraDesafio] = useState(0); // en plata: bonus del desafío × valor de hoy del punto de km
  const [sinMoto, setSinMoto] = useState(false); // sin moto cargada en el perfil no se cobra ningún premio
  useEffect(() => {
    supabase.rpc("fondo_mis_puntos").then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (d && d.semana) setExtraDesafio((Number(d.semana.bonus) || 0) * (Number(d.valor_punto_km) || 0));
      if (d && d.tiene_moto === false && d.participa !== false) setSinMoto(true);
    }, () => {});
  }, []);
  useEffect(() => {
    supabase.rpc("premio_semana_actual").then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setPremio(d && d.premio1 ? d : null);
    }, () => {});
  }, []);
  const datos = cache[periodo];
  const traer = (p) => {
    setCache(c => (p in c ? c : { ...c, [p]: undefined }));
    supabase.rpc("ranking_km_periodo", { p_periodo: p }).then(({ data: raw, error }) => {
      if (error) {
        // Todavía sin el RPC nuevo: el histórico cae al de siempre, el resto avisa
        if (p === "total") {
          supabase.rpc("ranking_km").then(({ data: r2 }) => {
            const d2 = typeof r2 === "string" ? JSON.parse(r2) : r2;
            setCache(c => ({ ...c, total: d2 && Array.isArray(d2.top) ? d2 : null }));
          }, () => setCache(c => ({ ...c, total: null })));
        } else setCache(c => ({ ...c, [p]: null }));
        return;
      }
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setCache(c => ({ ...c, [p]: d && Array.isArray(d.top) ? d : null }));
    }, () => setCache(c => ({ ...c, [p]: null })));
  };
  useEffect(() => { if (!(periodo in cache)) traer(periodo); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodo]);

  // Deslizar al costado para cambiar de período, como las historias
  const tocoRef = useRef(null);
  const idxPeriodo = PERIODOS_RANKING.findIndex(([k]) => k === periodo);
  const irA = (delta) => {
    const i = idxPeriodo + delta;
    if (i < 0 || i >= PERIODOS_RANKING.length) return;
    setPeriodo(PERIODOS_RANKING[i][0]);
  };
  const alTocar = (e) => { tocoRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const alSoltar = (e) => {
    if (!tocoRef.current) return;
    const dx = e.changedTouches[0].clientX - tocoRef.current.x;
    const dy = e.changedTouches[0].clientY - tocoRef.current.y;
    tocoRef.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) irA(dx < 0 ? 1 : -1);
  };
  // Podio sobrio (manual de marca): círculos numerados — oro heritage para el 1°,
  // negros para 2° y 3°, número pelado para el resto. Nada de medallas emoji.
  const Puesto = ({ i }) => i < 3 ? (
    <span style={{ width: 26, height: 26, borderRadius: "50%", background: i === 0 ? "#8A6D3B" : "#111111", color: "#F9F5F2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0, opacity: i === 2 ? 0.75 : 1 }}>{i + 1}</span>
  ) : (
    <span style={{ width: 26, textAlign: "center", fontSize: 12.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>{i + 1}</span>
  );
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" onTouchStart={alTocar} onTouchEnd={alSoltar} style={{ width: "100%", maxWidth: 430, maxHeight: "82vh", overflowY: "auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(24px + var(--sab))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
            <Award size={17} strokeWidth={2.2} color="#8A6D3B" /> Ranking de kilómetros
          </h2>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={15} strokeWidth={2} color={C.muted} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Los que más kilómetros sumaron grabando con Olbos.</div>
        {/* Solapas de período: también se cambian deslizando al costado */}
        <div style={{ display: "flex", gap: 4, padding: 3, background: C.surface, borderRadius: 10, marginBottom: 12 }}>
          {PERIODOS_RANKING.map(([k, label]) => (
            <button key={k} onClick={() => setPeriodo(k)}
              style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: periodo === k ? 800 : 600, background: periodo === k ? C.card : "transparent", color: periodo === k ? C.text : C.muted, boxShadow: periodo === k ? "0 1px 3px rgba(0,0,0,.06)" : "none" }}>
              {label}
            </button>
          ))}
        </div>
        {/* Premio semanal (bases en /premios): un momento oscuro de la marca arriba del ranking */}
        {periodo === "semana" && premio && (
          <div style={{ background: "#111111", color: "#F9F5F2", borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#bdb6ab" }}>Premio de esta semana</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {[premio.premio1, premio.premio2, premio.premio3].map((p, i) => p ? (
                <div key={i} style={{ flex: 1, minWidth: 0, background: "rgba(249,245,242,.08)", border: "1px solid rgba(249,245,242,.14)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10.5, color: "#bdb6ab", fontWeight: 700 }}>{i + 1}° puesto</div>
                  <div style={{ fontSize: 15.5, fontWeight: 800, letterSpacing: "-0.01em", overflowWrap: "anywhere" }}>{p}</div>
                </div>
              ) : null)}
            </div>
            {premio.nota && <div style={{ fontSize: 12, color: "#E7E1DA", marginTop: 8 }}>{premio.nota}</div>}
            <div style={{ fontSize: 12, color: "#bdb6ab", marginTop: 6, lineHeight: 1.5 }}>
              Salí a rodar: tus km en moto de lunes a domingo suman solos. <a href={`${API_BASE}/premios`} target="_blank" rel="noopener noreferrer" style={{ color: "#F9F5F2", fontWeight: 700 }}>Bases</a>
            </div>
          </div>
        )}
        {/* Tu semana (Carlos, 25-oct-2026): que nadie se desanime al ver un podio de 1.000 km.
            Cada uno mira su propia carrera: la meta de 150 km y cuánto le falta para el de arriba. */}
        {periodo === "semana" && datos && (() => {
          const META = 150;
          const mis = Number(datos.mio && datos.mio.km) || 0;
          const top = Array.isArray(datos.top) ? datos.top : [];
          const arriba = top.filter(r => Number(r.km) > mis).slice(-1)[0];
          const enPodio = mis > 0 && top.slice(0, 3).some(r => datos.mio && r.username === datos.mio.username);
          const pct = Math.min(100, Math.round((mis / META) * 100));
          const fmt = (n) => Math.round(n).toLocaleString("es-AR");
          return (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Desafío de la semana</div>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>{fmt(mis)} km</div>
              </div>
              <div style={{ height: 8, borderRadius: 8, background: C.border, marginTop: 8, overflow: "hidden" }}>
                <div style={{ width: pct + "%", height: "100%", background: mis >= META ? C.green : C.text, borderRadius: 8 }} />
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                {mis >= META ? `✅ ¡Cumplido! Sumaste un extra${extraDesafio ? ` de ~${fmt(extraDesafio)}` : ""} para fin de mes.` : `Hacé 150 km en moto esta semana y sumás un extra${extraDesafio ? ` de ~${fmt(extraDesafio)}` : ""} para fin de mes. ${mis > 0 ? `Llevás ${fmt(mis)} · te faltan ${fmt(META - mis)}.` : "Con una salida de finde lo cumplís."}`}
                {enPodio ? " Estás en el podio: ¡que no te pasen!" : arriba && mis > 0 ? ` Para pasar a @${arriba.username} te faltan ${fmt(Number(arriba.km) - mis + 1)} km.` : ""}
              </div>
              {sinMoto && (
                <div style={{ fontSize: 12.5, color: C.text, fontWeight: 700, marginTop: 8, lineHeight: 1.5 }}>
                  Completá tu perfil (foto, ciudad y tu moto en el garage): sin eso no se cobra ningún premio.
                </div>
              )}
            </div>
          );
        })()}
        {datos === undefined && <div style={{ textAlign: "center", color: C.muted, padding: 30, fontSize: 13 }}>Cargando…</div>}
        {datos === null && <div style={{ textAlign: "center", color: C.muted, padding: 30, fontSize: 13, lineHeight: 1.6 }}>El ranking se está preparando. Volvé en un rato.</div>}
        {datos && datos.top.length === 0 && (
          <div style={{ textAlign: "center", color: C.muted, padding: 30, fontSize: 13, lineHeight: 1.6 }}>
            {periodo === "hoy" ? "Todavía nadie grabó hoy. Podés ser el primero." : periodo === "semana" ? "Ningún recorrido grabado esta semana todavía." : periodo === "mes" ? "Ningún recorrido grabado este mes todavía." : "Todavía no hay recorridos grabados. ¡El primero puede ser tuyo!"}
          </div>
        )}
        {datos && datos.top.map((r, i) => (
          <div key={r.username || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, background: i < 3 ? C.surface : "transparent", marginBottom: 4 }}>
            <Puesto i={i} />
            {r.avatar_url
              ? <img loading="lazy" decoding="async" src={cldThumb(r.avatar_url, 64)} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
              : <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{r.username?.[0]?.toUpperCase() || "?"}</div>}
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{r.username}</span>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text, flexShrink: 0 }}>{Number(r.km).toLocaleString("es-AR")} km</span>
          </div>
        ))}
        {periodo === "semana" && <SemanasCerradas />}
        {datos && datos.mio && Number(datos.mio.km) > 0 && (
          <div style={{ marginTop: 12, padding: "11px 14px", borderRadius: 12, background: C.accent, color: "#F9F5F2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Vos</span>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>{Number(datos.mio.km).toLocaleString("es-AR")} km en {datos.mio.recorridos} recorrido{datos.mio.recorridos !== 1 ? "s" : ""}</span>
          </div>
        )}
        <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
          Cada recorrido que grabás en el mapa suma acá y al contador de la comunidad.
        </div>
      </div>
    </div>
  );
};

const Feed = ({ session, requireAuth, onAI, setTab }) => {
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  // Push tocada: abre la campanita (si el feed recién se monta, por la marca global)
  useEffect(() => {
    if (window._olbosAbrirCampanita) { window._olbosAbrirCampanita = false; setShowNotifs(true); }
    const h = () => { window._olbosAbrirCampanita = false; setShowNotifs(true); };
    window.addEventListener("olbos-abrir-campanita", h);
    return () => window.removeEventListener("olbos-abrir-campanita", h);
  }, []);
  const [notifs, setNotifs] = useState([]);
  const [notifNuevas, setNotifNuevas] = useState([]); // ids sin leer al momento de abrir
  const [view, setView] = useState("feed");
  const [activeDM, setActiveDM] = useState(null);
  const [commentsItem, setCommentsItem] = useState(null);
  const [galeriaId, setGaleriaId] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [newPostTag, setNewPostTag] = useState(null);
  const [showPublicar, setShowPublicar] = useState(false);
  const [showBuscarPerfiles, setShowBuscarPerfiles] = useState(false);
  const [mensajesNoLeidos, setMensajesNoLeidos] = useState(0);

  // Cargar contador de mensajes no leídos (DMs + grupos) + suscribirse a cambios
  useEffect(() => {
    if (!session) { setMensajesNoLeidos(0); return; }
    const cargarNoLeidos = async () => {
      const { data } = await supabase.rpc("get_total_no_leidos");
      setMensajesNoLeidos(data || 0);
    };
    cargarNoLeidos();
    window.addEventListener("olbos-msgs-leidos", cargarNoLeidos);

    // Suscribirse a cambios en DMs Y en mensajes de grupo
    const ch = supabase
      .channel("feed_msg_badge")
      .on("postgres_changes", { event: "*", schema: "public", table: "mensajes" }, () => {
        cargarNoLeidos();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "grupo_mensajes" }, () => {
        cargarNoLeidos();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "grupo_lectura" }, () => {
        cargarNoLeidos();
      })
      .subscribe();
    return () => { window.removeEventListener("olbos-msgs-leidos", cargarNoLeidos); supabase.removeChannel(ch); };
  }, [session]);
  const [publicando, setPublicando] = useState(false);
  const [newPost, setNewPost] = useState({ texto: "", medias: [], categoria: null, grupo_id: "", compartido_feed_general: true });
  const [misGruposPost, setMisGruposPost] = useState([]);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Cargar mis grupos para el selector al publicar
  useEffect(() => {
    if (!session) return;
    (async () => {
      const { data: raw } = await supabase.rpc("get_mis_grupos");
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      setMisGruposPost(data || []);
    })();
  }, [session]);

  // Si vienen desde un grupo, abrir publicar con el grupo preseleccionado
  useEffect(() => {
    if (window.olbosPendingGrupoPost) {
      const gId = window.olbosPendingGrupoPost;
      window.olbosPendingGrupoPost = null;
      window.olbosPendingGrupoPostNombre = null;
      setNewPost(p => ({ ...p, grupo_id: gId, compartido_feed_general: false }));
      setShowPublicar(true);
    }
  }, []);

  const handleMediaUpload = async (files, tipo) => {
    if (!files || files.length === 0) return;
    let fileArray = Array.from(files);
    // Videos de más de 90 s: se avisa y no se suben (mismo criterio que Instagram)
    if (tipo === "video") {
      const ok = [];
      for (const f of fileArray) { if (await videoDentroDelLimite(f)) ok.push(f); }
      fileArray = ok;
      if (fileArray.length === 0) return;
    }
    // preview local inmediato con id único
    const localPreviews = fileArray.map((f, i) => ({ 
      url: URL.createObjectURL(f), 
      type: tipo, 
      uploading: true,
      tempId: `${Date.now()}_${i}`
    }));
    setNewPost(p => ({ ...p, medias: [...p.medias, ...localPreviews] }));
    // subir cada archivo a Cloudinary
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const tempId = localPreviews[i].tempId;
      // Si algo falla, el placeholder se SACA y se avisa con claridad. Antes un
      // video rechazado (ej: más pesado que el tope de Cloudinary) quedaba en
      // "Subiendo..." para siempre y bloqueaba el Publicar sin decir nada
      // (video de 85 segundos de Mario Llanes, 30-ago).
      const sacarYAvisar = (msg) => {
        setNewPost(p => ({ ...p, medias: p.medias.filter(m => m.tempId !== tempId) }));
        alert(msg);
      };
      try {
        let aSubir = file;
        if (tipo === "video" && file.size > TOPE_VIDEO_MB * 1024 * 1024) {
          const nota = (txt) => setNewPost(p => ({ ...p, medias: p.medias.map(m => m.tempId === tempId ? { ...m, nota: txt } : m) }));
          nota("Achicando 0%");
          aSubir = await videoParaSubir(file, (pct) => nota(`Achicando ${pct}%`));
          if (!aSubir) {
            sacarYAvisar(`Ese video pesa ${Math.round(file.size / 1024 / 1024)} MB y este teléfono no lo pudo achicar. Grabalo en 1080p (no 4K) o recortalo un poco, y va a entrar.`);
            continue;
          }
          nota("Subiendo...");
        }
        const formData = new FormData();
        formData.append("file", aSubir);
        formData.append("upload_preset", "olbos_uploads");
        formData.append("cloud_name", "dhwhk48wb");
        const endpoint = tipo === "video"
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const res = await fetch(endpoint, { method: "POST", body: formData });
        const data = await res.json();
        if (data.secure_url) {
          setNewPost(p => {
            const newMedias = [...p.medias];
            const idx = newMedias.findIndex(m => m.tempId === tempId);
            if (idx !== -1) newMedias[idx] = { url: tipo === "video" ? urlVideoMp4(data.secure_url) : data.secure_url, type: tipo, uploading: false };
            return { ...p, medias: newMedias };
          });
        } else {
          sacarYAvisar("No se pudo subir: " + (data.error?.message || "probá de nuevo, y si es un video pesado, con WiFi mejor."));
        }
      } catch (err) {
        sacarYAvisar(tipo === "video"
          ? "No se pudo subir el video. Si es pesado, probá con WiFi o grabalo en 1080p."
          : "No se pudo subir la foto. Revisá la señal y probá de nuevo.");
      }
    }
  };

  const handlePublicar = async () => {
    if (!newPost.texto && newPost.medias.length === 0) return;
    const aunSubiendo = newPost.medias.some(m => m.uploading);
    if (aunSubiendo) { alert("Esperá que terminen de subir todas las fotos"); return; }
    setPublicando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { alert("Sesión expirada. Volvé a iniciar sesión."); setPublicando(false); return; }
      const mediasLimpias = newPost.medias.filter(m => m.url && !m.url.startsWith("blob:"));
      const post = {
        user_id: user.id,
        username: await usernameReal(user),
        texto: newPost.texto || "",
        categoria: newPost.categoria || null,
        media_url: mediasLimpias[0]?.url || null,
        media_type: mediasLimpias[0]?.type || null,
        media_urls: mediasLimpias.length > 0 ? JSON.stringify(mediasLimpias.map(m => ({ url: m.url, type: m.type }))) : null,
        likes: 0,
        grupo_id: newPost.grupo_id || null,
        // Si es post de grupo, compartido_feed_general según toggle. Si no es de grupo, siempre true (es público).
        compartido_feed_general: newPost.grupo_id ? newPost.compartido_feed_general : true,
      };
      // Si etiquetó un evento con #, la publicación va también al muro de ese evento
      const evTag = !newPost.grupo_id ? (newPost.texto || "").match(/#\[evento:[^\]]+\]\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/i) : null;
      if (evTag) post.evento_id = evTag[1];
      let postCreado = null, error = null;
      if (publicarComo) {
        const { user_id, username, ...resto } = post;
        const r = await supabase.rpc("publicar_como", { p_cuenta: publicarComo, p_post: resto });
        error = r.error;
        const d = typeof r.data === "string" ? JSON.parse(r.data) : r.data;
        if (!error && d && d.error) error = { message: d.error };
        postCreado = d && !d.error ? d : null;
      } else {
        ({ data: postCreado, error } = await supabase.from("posts").insert(post).select().single());
        if (error && post.evento_id) {
          // Si el muro del evento no la acepta (permisos), se publica igual en el feed
          delete post.evento_id;
          ({ data: postCreado, error } = await supabase.from("posts").insert(post).select().single());
        }
      }
      if (error) {
        console.error("Error guardando post:", error);
        alert("Error al publicar: " + error.message);
      } else {
        registrarUso("feed_interaccion", { tipo: "publicar", medias: mediasLimpias.length, con_evento: !!post.evento_id, grupo: !!post.grupo_id });
        if (promoOk && promoForm.on && promoForm.url.trim() && postCreado && postCreado.id) {
          let link = promoForm.url.trim();
          if (!/^https?:\/\//i.test(link)) link = "https://" + link;
          const { data: rawP, error: eP } = await supabase.rpc("promo_guardar_link", { p_post: postCreado.id, p_url: link, p_boton: promoForm.boton });
          const rp = typeof rawP === "string" ? JSON.parse(rawP) : rawP;
          if (eP || (rp && rp.error)) alert("El post se publicó, pero el botón con link no: " + (rp && rp.error === "link_invalido" ? "revisá el link." : (eP ? eP.message : rp.error)));
          else registrarUso("promo_link_creado", {});
          setPromoForm({ on: false, url: "", boton: BOTONES_PROMO[0] });
        }
        // Guardar menciones de entidades (#) en la tabla post_menciones
        // Extraer del texto: #[tipo:nombre](id)
        const regexEntidades = /#\[(ruta|moto|evento|negocio):([^\]]+)\]\(([^)]+)\)/g;
        const entidadesEnTexto = [];
        let match;
        while ((match = regexEntidades.exec(newPost.texto || "")) !== null) {
          entidadesEnTexto.push({
            post_id: postCreado.id,
            tipo: match[1],
            referencia_id: match[3],
            referencia_nombre: match[2],
          });
        }
        if (entidadesEnTexto.length > 0) {
          await supabase.from("post_menciones").insert(entidadesEnTexto);
        }

        // notificaciones de menciones de usuarios
        const menciones = (newPost.texto?.match(/@(\w+)/g) || []).map(m => m.slice(1));
        for (const username of menciones) {
          const { data: pm } = await supabase.from("perfiles").select("id").eq("username", username).single();
          if (pm && pm.id !== user.id) {
            await supabase.from("notificaciones").insert({
              user_id: pm.id, tipo: "mencion",
              from_username: await usernameReal(user),
              texto: "te mencionó en una publicación",
            });
          }
        }

        // etiquetas de personas sobre las fotos → post_tags + notificación.
        // Resolvemos el índice por URL (robusto a reordenar/borrar fotos antes de publicar).
        const tagsLimpias = (newPost.tags || []).map(t => {
          const idx = mediasLimpias.findIndex(m => m.url === t.url);
          return idx >= 0 ? { post_id: postCreado.id, media_index: idx, user_id: t.user_id, username: t.username, x: t.x, y: t.y } : null;
        }).filter(Boolean);
        if (tagsLimpias.length > 0) {
          await supabase.from("post_tags").insert(tagsLimpias);
          const uniqTag = tagsLimpias.filter((t, i, a) => a.findIndex(x => x.user_id === t.user_id) === i);
          for (const t of uniqTag) {
            if (t.user_id !== user.id) {
              await supabase.from("notificaciones").insert({
                user_id: t.user_id, tipo: "etiqueta",
                from_username: await usernameReal(user),
                texto: "te etiquetó en una foto",
              });
            }
          }
        }

        setNewPost({ texto: "", medias: [], categoria: null, grupo_id: "", compartido_feed_general: true, tags: [] });
        setMenciones([]);
        setShowPublicar(false);
        cargarPosts();
      }
    } catch (err) {
      console.error("Error en handlePublicar:", err);
      alert("Error inesperado: " + err.message);
    }
    setPublicando(false);
  };

  const [postReales, setPostReales] = useState([]);
  // Posts que llegaron en tiempo real mientras el usuario estaba scrolleado: esperan en la
  // pastilla "N fotos nuevas" hasta que la toca (como Instagram, para no moverle el feed).
  const [nuevosPend, setNuevosPend] = useState([]);
  // Dos solapas (decisión de Carlos y el equipo tras el BikerFest): las fotos y
  // videos por un lado, los recorridos por otro. Mezclados, un día de rodada
  // grande llenaba el feed de mapas iguales. Cada solapa se carga aparte para
  // que una no se coma el cupo de la otra.
  // Tres solapas (Carlos, 29-sep: el feed quedaba sucio con los posts de eventos): fotos y
  // videos de la comunidad | eventos (los posts automáticos de cada evento) | recorridos
  const [feedTab, setFeedTab] = useState(() => { try { const t = localStorage.getItem("olbos_feed_tab"); return ["comunidad", "eventos", "recorridos"].includes(t) ? t : "comunidad"; } catch (e) { return "comunidad"; } });
  // Cuentas en cuyo nombre este usuario puede publicar (ej. Matías o la agencia como @olbos).
  // Las da la tabla delegaciones; se publica por RPC, nunca con la contraseña de la cuenta.
  const [cuentasDelegadas, setCuentasDelegadas] = useState([]);
  const [publicarComo, setPublicarComo] = useState(null); // null = mi cuenta | uuid de la cuenta delegada
  // Links promocionados: formulario al publicar + botones de los posts que se ven
  const [promoOk, setPromoOk] = useState(false);
  const [promoForm, setPromoForm] = useState({ on: false, url: "", boton: BOTONES_PROMO[0] });
  const [promoPorPost, setPromoPorPost] = useState({});
  const promoPedidosRef = useRef(new window.Set());
  useEffect(() => { funcionVisible("promos").then(v => setPromoOk(!!v), () => {}); }, []);
  useEffect(() => {
    const ids = postReales.map(p => p.id).filter(id => id && !promoPedidosRef.current.has(id));
    if (!ids.length) return;
    ids.forEach(id => promoPedidosRef.current.add(id));
    (async () => {
      for (let i = 0; i < ids.length; i += 80) {
        try {
          const { data } = await supabase.from("promo_links").select("id, post_id, url, boton_texto, activo").in("post_id", ids.slice(i, i + 80)).eq("activo", true);
          if (data && data.length) setPromoPorPost(m => { const n = { ...m }; data.forEach(l => { n[l.post_id] = l; }); return n; });
        } catch (e) { /* sin tabla todavía: no hay botones */ }
      }
    })();
  }, [postReales.length]);
  useEffect(() => {
    supabase.rpc("mis_cuentas_delegadas").then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setCuentasDelegadas(Array.isArray(d) ? d.filter(c => c.puede_posts) : []);
    }, () => setCuentasDelegadas([]));
  }, []);
  const esPostDeEvento = (p) => p.tipo !== "recorrido" && (p.categoria === "evento" || !!p.evento_id);
  const cambiarFeedTab = (t) => { setFeedTab(t); try { localStorage.setItem("olbos_feed_tab", t); } catch (e) { /* */ } };
  // ── Revisión UX 10-oct-2026 (contenido primero) ──
  // El momento: los días del evento grande (Originarios, 10 y 11-oct-2026) la primera tarjeta
  // es "subí tu foto". Pasado el evento desaparece sola.
  const esHoyOriginarios = (() => { const d = new Date(); const k = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); return k === "2026-10-10" || k === "2026-10-11"; })();
  const camaraRef = useRef(null);
  const abrirCamara = () => {
    const abrir = () => {
      setNewPost(p => ({ ...p, categoria: "foto" }));
      setShowPublicar(true);
      setTimeout(() => { try { if (camaraRef.current) camaraRef.current.click(); } catch (e) { /* queda el publicador abierto */ } }, 350);
    };
    if (requireAuth) requireAuth(abrir); else abrir();
  };
  // La fila de Originarios se le muestra SOLO al que está cerca del predio (Villa Carlos Paz,
  // menos de 40 km). Sin pedir permisos nuevos: GPS solo si ya estaba concedido (en la
  // nativa se intenta igual, como PreguntaPerfil); sin posición, no aparece.
  const [cercaOriginarios, setCercaOriginarios] = useState(false);
  useEffect(() => {
    if (!esHoyOriginarios) return;
    let vivo = true;
    (async () => {
      let coords = null;
      try {
        if (navigator.geolocation) {
          let concedido = esNativo();
          if (!concedido && navigator.permissions) {
            const st = await navigator.permissions.query({ name: "geolocation" });
            concedido = st.state === "granted";
          }
          if (concedido) {
            coords = await new Promise(res => navigator.geolocation.getCurrentPosition(
              p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { maximumAge: 600000, timeout: 8000 }));
          }
        }
      } catch (e) { /* sin permissions API: no se muestra */ }
      if (vivo && coords && distanciaKm(coords, { lat: -31.424, lng: -64.498 }) < 40) setCercaOriginarios(true);
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // El "+" flotante dice "Publicar" las primeras tres veces que se abre el feed; después, solo el ícono
  const [fabConTexto] = useState(() => { try { const n = parseInt(localStorage.getItem("olbos_feed_sesiones") || "0", 10) + 1; localStorage.setItem("olbos_feed_sesiones", String(n)); return n <= 3; } catch (e) { return false; } });
  // Coachmark de primera vez en "Nueva publicación": se ve la primera vez que se abre
  const [coachPub, setCoachPub] = useState(() => { try { return !localStorage.getItem("olbos_coach_publicar"); } catch (e) { return false; } });
  const coachVistoRef = useRef(false);
  useEffect(() => {
    if (showPublicar && coachPub && !coachVistoRef.current) {
      coachVistoRef.current = true;
      try { localStorage.setItem("olbos_coach_publicar", "1"); } catch (e) { /* */ }
    }
    if (!showPublicar && coachVistoRef.current && coachPub) setCoachPub(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPublicar]);
  // 🎬 Reels: los posts públicos con video, del más nuevo al más viejo. Se cargan
  // aparte del feed (con su propio cupo) y se abren a pantalla completa. Un link
  // compartido llega como ?reel=<id> y abre el visor en ese video.
  const [reels, setReels] = useState([]);
  const [reelsAbierto, setReelsAbierto] = useState(null); // índice del reel con el que abre
  const cargarReels = async () => {
    const { data } = await supabase.from("posts").select("*")
      .or("and(grupo_id.is.null,evento_id.is.null),compartido_feed_general.eq.true")
      .or("media_type.eq.video,media_urls.ilike.*video*")
      .order("created_at", { ascending: false }).limit(60);
    if (!data) return [];
    const ocultar = await idsOcultos();
    const lista = data.filter(p => videoDePost(p) && !ocultar.includes(p.user_id));
    setReels(lista);
    return lista;
  };
  useEffect(() => {
    (async () => {
      let lista = await cargarReels();
      let id = null;
      try { id = new URLSearchParams(window.location.search).get("reel"); } catch (e) { /* */ }
      if (!id) return;
      let i = lista.findIndex(p => p.id === id);
      if (i < 0) {
        const { data: p } = await supabase.from("posts").select("*").eq("id", id).maybeSingle();
        if (p && videoDePost(p)) { lista = [p, ...lista]; setReels(lista); i = 0; }
      }
      if (i >= 0) {
        setReelsAbierto(i);
        try { window.history.replaceState({}, "", window.location.pathname); } catch (e) { /* */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [eventosDeFeed, setEventosDeFeed] = useState({}); // evento_id -> { padre_id, titulo, padre_titulo }
  const [totalRiders, setTotalRiders] = useState(0);
  const [ultimosRiders, setUltimosRiders] = useState([]);
  const [kmCom, setKmCom] = useState(null); // km comunitarios + meta activa (idea de Carlos 27-ago)
  const [showRankingKm, setShowRankingKm] = useState(false); // 🏆 ranking de usuarios por km
  // olbos.ai/?ranking=semana (el botón del mail del premio semanal) abre el ranking
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (!q.get("ranking")) return;
      setShowRankingKm(true);
      q.delete("ranking");
      window.history.replaceState({}, "", window.location.pathname + (q.toString() ? "?" + q.toString() : ""));
    } catch (e) { /* */ }
  }, []);
  // "Olbos te paga por andar en moto" (25-oct-2026): el premio de la semana arriba del feed y,
  // una sola vez por teléfono, el aviso a pantalla completa para que lo vea todo el que entra
  const [premioFeed, setPremioFeed] = useState(null);
  const [avisoTePaga, setAvisoTePaga] = useState(false);
  useEffect(() => {
    supabase.rpc("premio_semana_actual").then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!d || !d.premio1) return;
      setPremioFeed(d);
      try { if (!localStorage.getItem("olbos_aviso_te_paga_1")) setTimeout(() => setAvisoTePaga(true), 1500); } catch (e) { /* */ }
    }, () => {});
  }, []);
  const cerrarAvisoTePaga = (abrirRanking) => {
    try { localStorage.setItem("olbos_aviso_te_paga_1", "1"); } catch (e) { /* */ }
    setAvisoTePaga(false);
    registrarUso("aviso_te_paga", { ranking: !!abrirRanking });
    if (abrirRanking) setShowRankingKm(true);
  };
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [showEtiquetar, setShowEtiquetar] = useState(false);

  // Sistema de menciones de entidades con #
  const [hashtagMode, setHashtagMode] = useState(null); // null | "menu" | "ruta" | "moto" | "evento" | "negocio"
  const [hashtagQuery, setHashtagQuery] = useState("");
  const [hashtagResults, setHashtagResults] = useState([]);
  const [menciones, setMenciones] = useState([]); // [{tipo, ref_id, nombre}]
  const textareaRef = useRef(null);

  const handleTextoChange = async (e) => {
    const val = e.target.value;
    setNewPost(p => ({ ...p, texto: val }));

    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);

    // detectar @ activo (menciones de usuario)
    const matchAt = textBefore.match(/@(\w*)$/);
    if (matchAt) {
      const q = matchAt[1];
      setMentionQuery(q);
      setShowMentions(true);
      setHashtagMode(null);
      if (q.length >= 1) {
        const { data } = await supabase.from("perfiles").select("username, avatar_url").ilike("username", `${String(q).trim().replace(/^@/, "")}%`).limit(5);
        setMentionResults(data || []);
      } else {
        setMentionResults([]);
      }
      return;
    }

    // detectar # activo (menciones de entidades)
    const matchHash = textBefore.match(/#([\wáéíóúñ ]*)$/i);
    if (matchHash) {
      const q = matchHash[1];
      setShowMentions(false);
      setHashtagQuery(q);
      // si está vacío, mostrar el menú de categorías
      if (q.length === 0 && hashtagMode === null) {
        setHashtagMode("menu");
        setHashtagResults([]);
      } else if (hashtagMode && hashtagMode !== "menu") {
        // Ya eligió categoría, buscar en esa entidad
        await buscarEntidades(hashtagMode, q);
      } else {
        // primera letra escrita sin haber elegido categoría: abrir menú
        setHashtagMode("menu");
      }
      return;
    }

    setShowMentions(false);
    setHashtagMode(null);
  };

  // Buscar entidades según el tipo
  const buscarEntidades = async (tipo, q) => {
    const qLower = q.toLowerCase();
    if (tipo === "ruta") {
      const { data } = await supabase.from("rutas").select("id, nombre, desde, hasta").ilike("nombre", `%${q}%`).limit(8);
      setHashtagResults((data || []).map(r => ({
        id: r.id, nombre: r.nombre, subtitulo: `${r.desde?.nombre} → ${r.hasta?.nombre}`, icono: "🛣️",
      })));
    } else if (tipo === "moto") {
      // MOTOS_DB es en memoria; filtrar
      const filtered = MOTOS_DB.filter(m =>
        m.modelo.toLowerCase().includes(qLower) || m.marca.toLowerCase().includes(qLower)
      ).slice(0, 8);
      setHashtagResults(filtered.map(m => ({
        id: m.id, nombre: `${m.marca} ${m.modelo}`, subtitulo: fichaCorta(m), icono: "🏍️",
      })));
    } else if (tipo === "evento") {
      // Eventos reales de la base (antes era una lista fija de ejemplo): al etiquetar
      // uno, la publicación va también al muro (galería) de ese evento.
      try {
        const desde = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
        let qe = supabase.from("eventos").select("id, titulo, fecha").gte("fecha", desde).order("fecha", { ascending: true }).limit(8);
        if (q.trim()) qe = qe.ilike("titulo", `%${q.trim().replace(/[%,()]/g, " ")}%`);
        const { data } = await qe;
        setHashtagResults((data || []).filter(e => e.titulo).map(e => ({
          id: String(e.id), nombre: String(e.titulo).replace(/[[\]()]/g, " ").trim(),
          subtitulo: e.fecha ? new Date(String(e.fecha).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" }) : "",
          icono: "📅",
        })));
      } catch (e) { setHashtagResults([]); }
    } else if (tipo === "negocio") {
      const { data } = await supabase.from("negocios").select("id, nombre, categoria, ciudad").eq("estado", "verificado").ilike("nombre", `%${q}%`).limit(8);
      setHashtagResults((data || []).map(n => ({
        id: n.id, nombre: n.nombre, subtitulo: `${n.categoria} · ${n.ciudad}`, icono: "📍",
      })));
    }
  };

  const elegirCategoriaHashtag = (tipo) => {
    setHashtagMode(tipo);
    setHashtagResults([]);
    buscarEntidades(tipo, "");
  };

  const insertHashtag = (item) => {
    const cursor = textareaRef.current?.selectionStart || newPost.texto.length;
    const textBefore = newPost.texto.slice(0, cursor);
    const textAfter = newPost.texto.slice(cursor);
    // Reemplazar el #q por #[tipo:nombre](id)
    const tag = `#[${hashtagMode}:${item.nombre}](${item.id}) `;
    const newText = textBefore.replace(/#[\wáéíóúñ ]*$/i, tag) + textAfter;
    setNewPost(p => ({ ...p, texto: newText }));
    setMenciones(prev => [...prev, { tipo: hashtagMode, ref_id: item.id, nombre: item.nombre }]);
    setHashtagMode(null);
    setHashtagResults([]);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const insertMention = (username) => {
    const cursor = textareaRef.current?.selectionStart || newPost.texto.length;
    const textBefore = newPost.texto.slice(0, cursor);
    const textAfter = newPost.texto.slice(cursor);
    const newText = textBefore.replace(/@\w*$/, `@${username} `) + textAfter;
    setNewPost(p => ({ ...p, texto: newText }));
    setShowMentions(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  useEffect(() => {
    cargarPosts();
    cargarRiders();
    if (session) cargarNotifs();
  }, []);

  const cargarRiders = async () => {
    const { data: count } = await supabase.rpc("get_total_riders");
    if (count !== null && count !== undefined) setTotalRiders(count);
    const { data } = await supabase.from("perfiles").select("username, avatar_url").order("created_at", { ascending: false }).limit(6);
    if (data) setUltimosRiders(data);
    // Km de la comunidad + meta activa: si el RPC todavía no existe, el banner
    // muestra el contador de riders de siempre (fallback silencioso)
    supabase.rpc("km_comunidad").then(({ data: raw }) => {
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (d && d.km_total != null) setKmCom(d);
    }, () => {});
  };

  // Se traen las últimas, leídas y sin leer. Antes venían solo las nuevas y, una vez
  // abierta la campanita, desaparecían para siempre (reporte de Mario Llanes: quiso
  // volver a mirar quién había ganado un sorteo y ya no estaba en ningún lado).
  // El contador sigue siendo solo las sin leer, así la campanita no insiste con lo visto.
  const cargarNotifs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("notificaciones").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(40);
    if (data) { setNotifCount(data.filter(n => !n.leida).length); setNotifs(data); }
  };

  const marcarLeidas = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("notificaciones").update({ leida: true }).eq("user_id", user.id).eq("leida", false);
    setNotifCount(0);
    setNotifs(ns => ns.map(n => (n.leida ? n : { ...n, leida: true })));
  };

  const [feedCargado, setFeedCargado] = useState(false); // primera respuesta recibida
  const cargarPosts = async () => {
    // Feed público: solo posts sin grupo, o posts de grupo marcados como compartidos al feed general
    const base = () => supabase
      .from("posts")
      .select("*")
      .or("and(grupo_id.is.null,evento_id.is.null),compartido_feed_general.eq.true")
      .order("created_at", { ascending: false })
      .limit(FEED_PAGINA);
    const [{ data: comunidad, error: e1 }, { data: recorridos, error: e2 }] = await Promise.all([
      base().neq("tipo", "recorrido"),
      base().eq("tipo", "recorrido"),
    ]);
    if (!e1 || !e2) {
      const data = [...(comunidad || []), ...(recorridos || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      // No mostrar contenido de usuarios bloqueados o silenciados
      const ocultar = await idsOcultos();
      const frescos = ocultar.length ? data.filter(p => !ocultar.includes(p.user_id)) : data;
      // Las páginas viejas que el rider ya bajó con "anteriores" no se pierden al refrescar
      // (pedido de Matías N11: las fotos viejas desaparecían del feed)
      const corteCom = comunidad && comunidad.length >= FEED_PAGINA ? comunidad[comunidad.length - 1].created_at : null;
      const corteRec = recorridos && recorridos.length >= FEED_PAGINA ? recorridos[recorridos.length - 1].created_at : null;
      setPostReales(prev => {
        const viejos = prev.filter(p => !frescos.some(x => x.id === p.id) && (p.tipo === "recorrido"
          ? (corteRec && String(p.created_at) < String(corteRec))
          : (corteCom && String(p.created_at) < String(corteCom))));
        return [...frescos, ...viejos].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      });
      setHayMasFeed(h => ({ com: corteCom ? h.com !== false : false, rec: corteRec ? h.rec !== false : false }));
      setNuevosPend(prev => prev.filter(p => !data.some(x => x.id === p.id)));
      // Para agrupar caravanas: de qué evento central cuelga cada rodada con recorridos
      try {
        const ids = [...new Set((recorridos || []).map(p => p.evento_id).filter(Boolean))];
        if (ids.length) {
          const { data: evs } = await supabase.from("eventos").select("id, titulo, evento_padre_id").in("id", ids);
          const padresIds = [...new Set((evs || []).map(e => e.evento_padre_id).filter(Boolean))];
          let padres = [];
          if (padresIds.length) {
            const { data: pd } = await supabase.from("eventos").select("id, titulo").in("id", padresIds);
            padres = pd || [];
          }
          const mapa = {};
          (evs || []).forEach(e => {
            const padre = e.evento_padre_id ? padres.find(x => x.id === e.evento_padre_id) : null;
            mapa[e.id] = { titulo: e.titulo, padre_id: e.evento_padre_id || null, padre_titulo: padre ? padre.titulo : null };
          });
          setEventosDeFeed(mapa);
        }
      } catch (e) { /* sin agrupado por evento central, el feed sigue */ }
    }
    setFeedCargado(true);
  };

  // ── Publicaciones anteriores (pedido de Matías N11, 20-oct-2026) ──
  // El feed trae las 50 más nuevas de cada tipo; al llegar al final se piden las 30
  // anteriores a la más vieja cargada de esa solapa, solas, y así sucesivamente.
  // com = fotos, videos y eventos (no recorridos); rec = recorridos.
  const [hayMasFeed, setHayMasFeed] = useState({ com: true, rec: true });
  const [cargandoMas, setCargandoMas] = useState(false);
  const cargandoMasRef = useRef(false);
  const finFeedRef = useRef(null);
  const finVisibleRef = useRef(false);
  const cargarAnteriores = async (clase) => {
    if (cargandoMasRef.current) return;
    const propios = postsRef.current.filter(p => clase === "rec" ? p.tipo === "recorrido" : p.tipo !== "recorrido");
    const corte = propios.reduce((m, p) => (!m || String(p.created_at) < m ? String(p.created_at) : m), null);
    if (!corte) return;
    cargandoMasRef.current = true; setCargandoMas(true);
    try {
      let q = supabase.from("posts").select("*")
        .or("and(grupo_id.is.null,evento_id.is.null),compartido_feed_general.eq.true")
        .lt("created_at", corte).order("created_at", { ascending: false }).limit(30);
      q = clase === "rec" ? q.eq("tipo", "recorrido") : q.neq("tipo", "recorrido");
      const { data, error } = await q;
      if (error) throw error;
      const lista = data || [];
      let ocultar = [];
      try { ocultar = await idsOcultos(); } catch (e) { /* */ }
      const nuevos = lista.filter(p => !ocultar.includes(p.user_id));
      setPostReales(prev => [...prev, ...nuevos.filter(p => !prev.some(x => x.id === p.id))].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
      setHayMasFeed(h => ({ ...h, [clase]: lista.length >= 30 }));
      if (clase === "rec" && nuevos.length) {
        try {
          const ids = nuevos.map(p => p.evento_id).filter((x, i, a) => x && a.indexOf(x) === i);
          if (ids.length) {
            const { data: evs } = await supabase.from("eventos").select("id, titulo, evento_padre_id").in("id", ids);
            const padresIds = (evs || []).map(e => e.evento_padre_id).filter((x, i, a) => x && a.indexOf(x) === i);
            let padres = [];
            if (padresIds.length) { const { data: pd } = await supabase.from("eventos").select("id, titulo").in("id", padresIds); padres = pd || []; }
            const mapa = {};
            (evs || []).forEach(e => { const pa = e.evento_padre_id ? padres.find(x => x.id === e.evento_padre_id) : null; mapa[e.id] = { titulo: e.titulo, padre_id: e.evento_padre_id || null, padre_titulo: pa ? pa.titulo : null }; });
            setEventosDeFeed(m => ({ ...m, ...mapa }));
          }
        } catch (e) { /* sin agrupado: igual se ven */ }
      }
    } catch (e) {
      /* sin señal: el botón queda para reintentar */
    } finally {
      cargandoMasRef.current = false; setCargandoMas(false);
    }
  };
  const claseFeed = feedTab === "recorridos" ? "rec" : "com";
  const cargarAnterioresRef = useRef(cargarAnteriores);
  cargarAnterioresRef.current = cargarAnteriores;
  useEffect(() => {
    const el = finFeedRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver((entradas) => {
      finVisibleRef.current = !!(entradas[0] && entradas[0].isIntersecting);
      if (finVisibleRef.current && hayMasFeed[claseFeed]) cargarAnterioresRef.current(claseFeed);
    }, { rootMargin: "600px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedCargado, claseFeed, hayMasFeed.com, hayMasFeed.rec, postReales.length > 0]);
  // Si después de cargar el final sigue a la vista (solapa con pocos posts de su tipo), seguir
  useEffect(() => {
    if (!cargandoMas && finVisibleRef.current && hayMasFeed[claseFeed]) {
      const tm = setTimeout(() => { if (finVisibleRef.current && !cargandoMasRef.current) cargarAnterioresRef.current(claseFeed); }, 300);
      return () => clearTimeout(tm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargandoMas, postReales.length]);

  // ── Fotos nuevas en tiempo real (10-oct-2026, Originarios) ──
  // UN solo canal para todo el feed (INSERT + DELETE en posts), que se cierra al salir de
  // la pantalla. Si Realtime no responde (o la tabla no está publicada), poll liviano de
  // 60 s que solo trae lo más nuevo que el último post conocido; y siempre se refresca al
  // volver la app al frente.
  const postsRef = useRef([]);
  postsRef.current = postReales;
  const pendRef = useRef([]);
  pendRef.current = nuevosPend;
  const rtOkRef = useRef(false);
  const ultimoRefrescoRef = useRef(Date.now());
  const miIdFeed = session?.user?.id || null;
  const porFecha = (a, b) => String(b.created_at).localeCompare(String(a.created_at));
  const entraEnFeed = (p) => !!p && !!p.id && ((!p.grupo_id && !p.evento_id) || p.compartido_feed_general === true);
  const arribaDeTodo = () => (window.scrollY || document.documentElement.scrollTop || 0) < 160;
  const solapaDe = (p) => p.tipo === "recorrido" ? "recorridos" : esPostDeEvento(p) ? "eventos" : "comunidad";
  const recibirNuevos = async (lista) => {
    const ya = new window.Set([...postsRef.current.map(p => p.id), ...pendRef.current.map(p => p.id)]);
    let nuevos = (lista || []).filter(p => entraEnFeed(p) && !ya.has(p.id));
    if (!nuevos.length) return;
    let ocultar = [];
    try { ocultar = await idsOcultos(); } catch (e) { /* sin filtro de bloqueados, igual se muestra */ }
    nuevos = nuevos.filter((p, i, a) => !ocultar.includes(p.user_id) && a.findIndex(x => x.id === p.id) === i);
    if (!nuevos.length) return;
    // Lo mío entra directo (acabo de publicarlo); lo de los demás, directo si estoy arriba
    // de todo y a la pastilla si estoy scrolleado.
    const directos = nuevos.filter(p => (miIdFeed && p.user_id === miIdFeed) || arribaDeTodo());
    const aPastilla = nuevos.filter(p => !directos.includes(p));
    if (directos.length) setPostReales(prev => [...directos.filter(p => !prev.some(x => x.id === p.id)), ...prev].sort(porFecha));
    if (aPastilla.length) setNuevosPend(prev => [...aPastilla.filter(p => !prev.some(x => x.id === p.id)), ...prev].sort(porFecha));
  };
  const recibirRef = useRef(recibirNuevos);
  recibirRef.current = recibirNuevos;
  const refrescarNuevos = async () => {
    const desde = postsRef.current.reduce((m, p) => (String(p.created_at || "") > m ? String(p.created_at) : m), "");
    if (!desde) return;
    try {
      const { data } = await supabase.from("posts").select("*")
        .or("and(grupo_id.is.null,evento_id.is.null),compartido_feed_general.eq.true")
        .gt("created_at", desde).order("created_at", { ascending: false }).limit(30);
      if (data && data.length) recibirRef.current(data);
    } catch (e) { /* sin señal: el próximo intento */ }
  };
  const refrescarRef = useRef(refrescarNuevos);
  refrescarRef.current = refrescarNuevos;
  const cargarPostsRef = useRef(cargarPosts);
  cargarPostsRef.current = cargarPosts;
  const verNuevos = () => {
    const pend = pendRef.current;
    setNuevosPend([]);
    if (!pend.length) return;
    setPostReales(prev => [...pend.filter(p => !prev.some(x => x.id === p.id)), ...prev].sort(porFecha));
    // Si ninguno es de la solapa que estoy mirando, voy a la del más nuevo
    if (!pend.some(p => solapaDe(p) === feedTab)) cambiarFeedTab(solapaDe(pend[0]));
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { window.scrollTo(0, 0); }
  };
  useEffect(() => {
    const ch = supabase.channel("feed_posts_" + Math.random().toString(36).slice(2, 10))
      .on("system", {}, (p) => { if (p && p.extension === "postgres_changes") rtOkRef.current = p.status === "ok"; })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts" }, (payload) => {
        if (payload && payload.new) recibirRef.current([payload.new]);
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "posts" }, (payload) => {
        const id = payload && payload.old && payload.old.id;
        if (!id) return;
        setPostReales(prev => prev.some(p => p.id === id) ? prev.filter(p => p.id !== id) : prev);
        setNuevosPend(prev => prev.some(p => p.id === id) ? prev.filter(p => p.id !== id) : prev);
      })
      .subscribe((status) => { if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") rtOkRef.current = false; });
    const alVolver = () => {
      if (document.visibilityState !== "visible" || Date.now() - ultimoRefrescoRef.current < 15000) return;
      ultimoRefrescoRef.current = Date.now();
      if (arribaDeTodo()) cargarPostsRef.current(); else refrescarRef.current();
    };
    document.addEventListener("visibilitychange", alVolver);
    const iv = setInterval(() => {
      if (document.visibilityState !== "visible" || rtOkRef.current) return;
      refrescarRef.current();
    }, 60000);
    return () => { supabase.removeChannel(ch); document.removeEventListener("visibilitychange", alVolver); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Tirar para refrescar (desde arriba de todo). El indicador se mueve por ref, sin
  // re-renderizar el feed entero en cada touchmove.
  const ptrRef = useRef({ y0: null, dy: 0 });
  const ptrIndRef = useRef(null);
  const [refrescando, setRefrescando] = useState(false);
  const refrescarTodo = async () => {
    if (refrescando) return;
    setRefrescando(true);
    ultimoRefrescoRef.current = Date.now();
    try { await cargarPosts(); } catch (e) { /* queda lo que había */ }
    setRefrescando(false);
  };
  const pintarPtr = (dy) => {
    const el = ptrIndRef.current;
    if (!el) return;
    const h = Math.min(56, dy * 0.4);
    el.style.height = h + "px";
    el.style.opacity = String(Math.min(1, h / 36));
    if (el.firstChild) el.firstChild.textContent = dy > 130 ? "Soltá para actualizar" : "Tirá para actualizar";
  };
  const ptrStart = (e) => {
    if ((window.scrollY || 0) > 2 || showPublicar || !e.touches || e.touches.length !== 1) { ptrRef.current.y0 = null; return; }
    ptrRef.current.y0 = e.touches[0].clientY; ptrRef.current.dy = 0;
  };
  const ptrMove = (e) => {
    if (ptrRef.current.y0 == null || !e.touches) return;
    const dy = e.touches[0].clientY - ptrRef.current.y0;
    if (dy <= 0 || (window.scrollY || 0) > 2) { if (ptrRef.current.dy) pintarPtr(0); ptrRef.current.dy = 0; return; }
    ptrRef.current.dy = dy; pintarPtr(dy);
  };
  const ptrEnd = () => {
    const dy = ptrRef.current.dy;
    ptrRef.current.y0 = null; ptrRef.current.dy = 0;
    if (dy) pintarPtr(0);
    if (dy > 130) refrescarTodo();
  };
  const esTactil = typeof window !== "undefined" && ("ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0);

  const handleDM = (item) => {
    const convo = DM_CONVOS.find(c => c.user === item.user) || DM_CONVOS[0];
    setActiveDM(convo); setView("dm");
  };

  if (view === "dmlist") return <DMListModal onClose={() => setView("feed")} onOpen={c => { setActiveDM(c); setView("dm"); }} />;
  if (view === "dm" && activeDM) return <DMConvoModal convo={activeDM} onClose={() => setView("dmlist")} />;
  if (galeriaId) return <EspacioGaleria espacioId={galeriaId} onClose={() => setGaleriaId(null)} />;

  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: C.text, letterSpacing: -0.5 }}>Comunidad</h1>
          <VideoAyuda seccion="feed" style={{ marginTop: 8 }} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Actualizar: en la compu, donde no hay "tirar para refrescar" */}
          {!esTactil && (
            <button onClick={refrescarTodo} title="Actualizar" style={{
              width: 36, height: 36, borderRadius: "50%",
              background: C.surface, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
              <RefreshCw size={15} strokeWidth={2} color={C.text} style={refrescando ? { animation: "spin 0.8s linear infinite" } : undefined} />
            </button>
          )}
          {/* Buscar perfiles (estilo Instagram) */}
          <button onClick={() => setShowBuscarPerfiles(true)} style={{
            width: 36, height: 36, borderRadius: "50%",
            background: C.surface, border: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }} title="Buscar riders">
            <Search size={16} strokeWidth={2} color={C.text} />
          </button>
          {/* Mensajes con badge */}
          {session && (
            <button onClick={() => setTab && setTab("mensajes")} style={{
              position: "relative",
              width: 36, height: 36, borderRadius: "50%",
              background: C.surface, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }} title="Mensajes">
              <MessageCircle size={16} strokeWidth={2} color={mensajesNoLeidos > 0 ? "#111111" : C.text} />
              {mensajesNoLeidos > 0 && (
                <div style={{
                  position: "absolute", top: -3, right: -3,
                  minWidth: 16, height: 16, padding: "0 4px", borderRadius: 100,
                  background: C.accent, color: "#fff",
                  fontSize: 9, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "2px solid #fff",
                }}>
                  {mensajesNoLeidos > 99 ? "99+" : mensajesNoLeidos}
                </div>
              )}
            </button>
          )}
          {/* IA */}
          {onAI && <button onClick={onAI} style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} title="Olbos AI"><Zap size={16} strokeWidth={2} color={C.text} /></button>}
          {/* campana notificaciones */}
          {session && (
            <button onClick={() => { setShowNotifs(s => !s); if (!showNotifs) { setNotifNuevas(notifs.filter(n => !n.leida).map(n => n.id)); marcarLeidas(); } }}
              style={{ position: "relative", width: 36, height: 36, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Bell size={16} strokeWidth={2} color={notifCount > 0 ? C.accent : C.text} />
              {notifCount > 0 && (
                <div style={{ position: "absolute", top: -2, right: -2, width: 16, height: 16, borderRadius: "50%", background: C.accent, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {notifCount}
                </div>
              )}
            </button>
          )}
          {/* acceso a Mi perfil */}
          {session && <AvatarNav onClick={() => setTab && setTab("perfil")} size={36} />}
        </div>
      </div>

      {/* Premio de la semana: la franja pasa a ser un "momento oscuro" mientras hay premio */}
      {premioFeed ? (
        <div onClick={() => setShowRankingKm(true)} style={{ background: "#111111", color: "#F9F5F2", padding: "10px 18px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12.5 }}>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            🏆 <b>Olbos te paga por andar:</b> esta semana {premioFeed.premio1} · {premioFeed.premio2} · {premioFeed.premio3}
          </span>
          <span style={{ flexShrink: 0, whiteSpace: "nowrap", color: "#bdb6ab" }}>Ver ranking ›</span>
        </div>
      ) : null}
      {avisoTePaga && premioFeed && (
        <div onClick={e => e.target === e.currentTarget && cerrarAvisoTePaga(false)} style={{ position: "fixed", inset: 0, zIndex: 9400, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ width: "100%", maxWidth: 400, background: "#111111", color: "#F9F5F2", borderRadius: 18, padding: "26px 22px", boxSizing: "border-box" }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#bdb6ab" }}>Nuevo en Olbos</div>
            <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15, marginTop: 8 }}>Olbos te paga por andar en moto 🏍️</div>
            <div style={{ fontSize: 14.5, color: "#E7E1DA", lineHeight: 1.55, marginTop: 10 }}>
              Lo que más queremos es que salgas a rodar. Por eso, cada semana, los 3 que más kilómetros hagan en su moto ganan:
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {[premioFeed.premio1, premioFeed.premio2, premioFeed.premio3].map((p, i) => p ? (
                <div key={i} style={{ flex: 1, minWidth: 0, background: "rgba(249,245,242,.08)", border: "1px solid rgba(249,245,242,.16)", borderRadius: 12, padding: "10px 10px" }}>
                  <div style={{ fontSize: 11, color: "#bdb6ab", fontWeight: 700 }}>{i + 1}° puesto</div>
                  <div style={{ fontSize: 17, fontWeight: 800, overflowWrap: "anywhere" }}>{p}</div>
                </div>
              ) : null)}
            </div>
            <ComoFuncionaTePaga oscuro style={{ marginTop: 16 }} />
            <div style={{ fontSize: 12.5, color: "#bdb6ab", lineHeight: 1.5, marginTop: 10 }}>
              No hace falta anotarse: grabá tus salidas o navegá con Olbos y tus km suman solos.
            </div>
            <button onClick={() => cerrarAvisoTePaga(true)} style={{ width: "100%", marginTop: 18, padding: 14, borderRadius: 12, border: "none", background: "#F9F5F2", color: "#111111", fontWeight: 800, fontSize: 15.5, cursor: "pointer" }}>Ver el ranking de la semana</button>
            <button onClick={() => cerrarAvisoTePaga(false)} style={{ width: "100%", marginTop: 8, padding: 10, border: "none", background: "none", color: "#bdb6ab", fontSize: 13, cursor: "pointer" }}>Más tarde</button>
            <div style={{ fontSize: 11.5, color: "#8A857F", textAlign: "center", marginTop: 4 }}><a href={`${API_BASE}/olbos-te-paga`} target="_blank" rel="noopener noreferrer" style={{ color: "#bdb6ab" }}>Bases y condiciones</a></div>
          </div>
        </div>
      )}
      {/* Los km de todos: franja de ancho completo debajo del header (en el header la
          cortaban los íconos, pedido de Carlos desde el predio 10-oct) */}
      {kmCom ? (
        <div onClick={() => setShowRankingKm(true)} style={{ background: C.surface, padding: "10px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: C.muted, letterSpacing: "-0.01em" }}>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            🏁 <b style={{ color: C.text }}>{Math.round(kmCom.km_total).toLocaleString("es-AR")} km</b> recorridos por <b style={{ color: C.text }}>{totalRiders.toLocaleString("es-AR")} riders</b>
          </span>
          <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>· Ver ranking ›</span>
        </div>
      ) : (
        <div style={{ background: C.surface, padding: "10px 18px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          🏁 {totalRiders >= 100 ? <><b style={{ color: C.text }}>{totalRiders.toLocaleString("es-AR")} riders</b> viviendo Olbos</> : "Los primeros riders ya están viviendo Olbos"}
        </div>
      )}

      {/* Ganador del premio semanal con los datos de pago pendientes: se lo pedimos acá arriba */}
      {session && <PremioGanadorCard session={session} style={{ margin: "10px 14px 0" }} />}

      {/* panel notificaciones */}
      {showNotifs && (
        <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: "12px 18px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 10 }}>NOTIFICACIONES</div>
          {notifs.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "12px 0" }}>Todavía no tenés notificaciones.</div>
          ) : <div style={{ maxHeight: "60vh", overflowY: "auto", margin: "0 -6px", padding: "0 6px" }}>{notifs.map(n => (
            <div key={n.id}
              // Un mensaje directo se toca y abre Mensajes (Matías, 27-oct-2026: nadie veía los mensajes)
              onClick={n.tipo === "mensaje" ? () => { setShowNotifs(false); window.dispatchEvent(new CustomEvent("olbos-abrir-dm")); } : undefined}
              style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 6px", borderBottom: `1px solid ${C.border}`, background: notifNuevas.includes(n.id) ? C.surface : "transparent", borderRadius: 8, cursor: n.tipo === "mensaje" ? "pointer" : "default" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.accentLo, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {({ mencion: "📣", like: "❤️", inscripcion: "🏍️", pago: "💳", turno: "📅",
                    transferencia: "🎁", co_organizador: "🤝", recordatorio: "⏰", sello: "🏅", fondo: "💸", mensaje: "✉️", vistas: "👀", seguidor: "👤", concesionario: "✅" })[n.tipo] || "💬"}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>@{n.from_username} </span>
                <span style={{ fontSize: 13, color: C.muted }}>{n.texto}</span>
                <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>
                  {new Date(n.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {notifNuevas.includes(n.id) && <span style={{ color: C.accent, fontWeight: 700 }}> · Nueva</span>}
                </div>
              </div>
            </div>
          ))}</div>}
        </div>
      )}
      <div style={{ paddingBottom: 100 }} onTouchStart={ptrStart} onTouchMove={ptrMove} onTouchEnd={ptrEnd} onTouchCancel={ptrEnd}>
        {/* Tirar para refrescar */}
        <div ref={ptrIndRef} style={{ height: 0, opacity: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.muted }}><span>Tirá para actualizar</span></div>
        {refrescando && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 0 0", fontSize: 12, fontWeight: 700, color: C.muted }}>
            <span style={{ width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: "#111", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
            Actualizando…
          </div>
        )}
        {/* Pastilla "N fotos nuevas": llegaron mientras estabas scrolleado */}
        {nuevosPend.length > 0 && (
          <button onClick={verNuevos} style={{
            position: "fixed", top: "calc(var(--sat) + 64px)", left: "50%", transform: "translateX(-50%)", zIndex: 95,
            padding: "8px 16px 8px 10px", borderRadius: 100, background: "#111111", color: "#F9F5F2", border: "none",
            fontSize: 13, fontWeight: 700, letterSpacing: "-0.01em", boxShadow: "0 6px 18px rgba(0,0,0,.25)", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", maxWidth: "calc(100vw - 32px)",
          }}>
            <span style={{ display: "flex", flexShrink: 0 }}>
              {nuevosPend.filter((p, i, a) => a.findIndex(x => x.user_id === p.user_id) === i).slice(0, 3).map((p, i) => (
                <span key={p.id} style={{ width: 22, height: 22, borderRadius: "50%", marginLeft: i ? -7 : 0, border: "1.5px solid #111111", background: "#3a3a3a", overflow: "hidden", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#F9F5F2" }}>
                  {(p.username || "?")[0].toUpperCase()}
                </span>
              ))}
            </span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              ↑ {nuevosPend.length} {nuevosPend.every(p => p.media_url || p.media_urls)
                ? (nuevosPend.length === 1 ? "foto nueva" : "fotos nuevas")
                : (nuevosPend.length === 1 ? "publicación nueva" : "publicaciones nuevas")} · tocá para verlas
            </span>
          </button>
        )}

        {/* 📍 El momento (solo los días del evento grande): el que entra desde el QR ve primero
            qué hacer, y es lo único que quiere hacer en el predio */}
        {esHoyOriginarios && cercaOriginarios && (
          <div style={{ margin: "12px 16px 0", background: "#111111", color: "#F9F5F2", borderRadius: 12, height: 46, padding: "0 6px 0 14px", display: "flex", alignItems: "center", gap: 10, boxSizing: "border-box" }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📍 Estás en Originarios · Subí tu foto</div>
            <button onClick={abrirCamara} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 9, background: "#F9F5F2", color: "#111111", border: "none", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}>Publicar</button>
          </div>
        )}

        {/* Solapas del feed: fotos y videos | eventos | recorridos */}
        {postReales.length > 0 && (
          <div style={{ display: "flex", margin: "12px 16px 12px", background: C.surface, borderRadius: 12, padding: 3, border: `1px solid ${C.border}` }}>
            {[["comunidad", "📸 Fotos y videos"], ["eventos", "📅 Eventos"], ["recorridos", "🗺️ Recorridos"]].map(([t, l]) => (
              <button key={t} onClick={() => cambiarFeedTab(t)}
                style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 800, letterSpacing: "-0.01em", whiteSpace: "nowrap", background: feedTab === t ? C.card : "transparent", color: feedTab === t ? C.text : C.muted, boxShadow: feedTab === t ? "0 1px 4px rgba(0,0,0,.08)" : "none" }}>
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Posts, con lo demás intercalado (revisión UX 10-oct-2026): como en Instagram, el
            contenido va primero. Después del 2º post, primeros pasos + ciudad + referido; después
            del 4º, el partner y los reels. Antes eran seis tarjetas antes de la primera foto. */}
        {(() => {
          const visibles = postReales.filter(p =>
            feedTab === "recorridos" ? p.tipo === "recorrido"
            : feedTab === "eventos" ? esPostDeEvento(p)
            : (p.tipo !== "recorrido" && !esPostDeEvento(p)));
          if (feedCargado && postReales.length > 0 && visibles.length === 0) {
            return (
              <div style={{ padding: "40px 32px", textAlign: "center", fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                {feedTab === "recorridos" ? "Todavía no hay recorridos compartidos. Grabá el tuyo desde el mapa y aparece acá."
                  : feedTab === "eventos" ? "Todavía no hay eventos publicados en el feed. Cuando un organizador publica el suyo, aparece acá."
                  : "Todavía no hay fotos ni videos. Subí la primera."}
              </div>
            );
          }
          const lista = agruparPorRodada(visibles, eventosDeFeed).map(item => (
            item.posts
              ? <PostsDeRodada key={item.clave} posts={item.posts} titulo={item.titulo} esPadre={item.esPadre} onReload={cargarPosts} requireAuth={requireAuth} />
              : <PostReal key={item.clave} post={item.post} onReload={cargarPosts} requireAuth={requireAuth} promo={promoPorPost[item.post.id] || null} />
          ));
          const bloque1 = (
            <div key="_ux_bloque1">
              <OnboardingChecklist session={session} setTab={setTab} compacto />
              <PreguntaPerfil session={session} />
              <PreguntaReferido session={session} />
            </div>
          );
          const bloque2 = (
            <div key="_ux_bloque2" style={{ paddingTop: 12 }}>
              {/* 📣 Sponsor del feed: espacio self-service (vacío se ofrece, ocupado mide) */}
              <EspacioAnuncio slot="feed" estilo={{ margin: "0 16px 12px" }} />
              {/* 🎬 Reels: lanzador (momento oscuro) — solo si hay videos */}
              {reels.length > 0 && (
                <div onClick={() => setReelsAbierto(0)} style={{ margin: "0 16px 12px", borderRadius: 14, background: "#111111", color: "#F9F5F2", padding: "11px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <div style={{ display: "flex", flexShrink: 0 }}>
                    {reels.slice(0, 3).map((p, i) => (
                      <div key={p.id} style={{ width: 32, height: 44, borderRadius: 8, overflow: "hidden", background: "#2a2a2a", marginLeft: i ? -10 : 0, border: "2px solid #111111", backgroundImage: cldPoster(videoDePost(p), 200) ? `url(${cldPoster(videoDePost(p), 200)})` : "none", backgroundSize: "cover", backgroundPosition: "center", zIndex: 3 - i, position: "relative" }} />
                    ))}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 6 }}><Play size={13} fill="#F9F5F2" color="#F9F5F2" /> Reels</div>
                    <div style={{ fontSize: 12, color: "#bdb6ab", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{reels.length} video{reels.length !== 1 ? "s" : ""} de la comunidad, a pantalla completa</div>
                  </div>
                  <ChevronRight size={18} color="#bdb6ab" style={{ flexShrink: 0 }} />
                </div>
              )}
            </div>
          );
          // 🏍️ Arriba de todo: salidas abiertas cerca + "Salgo a rodar" (pedido de Carlos: bien visible)
          const out = [
            // 💰 El pozo del mes (bandera "pozo": por ahora solo lo ve el equipo)
            <PozoOlbos key="_pozo_olbos" estilo={{ margin: "4px 16px 12px" }} />,
            <SalidasAbiertas key="_salidas_abiertas" session={session} requireAuth={requireAuth} estilo={{ margin: "4px 16px 12px" }} />,
          ];
          lista.forEach((el, i) => { out.push(el); if (i === 1) out.push(bloque1); if (i === 3) out.push(bloque2); if (i === 5 && session) out.push(<SugerenciasRiders key="_ux_sugerencias" session={session} />); });
          if (lista.length < 2) out.push(bloque1);
          if (lista.length < 4) out.push(bloque2);
          return out;
        })()}

        {/* Final del feed: al acercarse se cargan solas las anteriores; el botón queda por si
            el teléfono no avisa el scroll o se cortó la señal */}
        {feedCargado && postReales.length > 0 && (
          <div ref={finFeedRef} style={{ padding: "18px 16px 8px", textAlign: "center" }}>
            {cargandoMas ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 700, color: C.muted }}>
                <span style={{ width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: "#111", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                Cargando anteriores…
              </div>
            ) : hayMasFeed[claseFeed] ? (
              <button onClick={() => cargarAnteriores(claseFeed)} style={{ padding: "10px 18px", borderRadius: 100, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Ver publicaciones anteriores</button>
            ) : (
              <div style={{ fontSize: 12, color: C.muted }}>Ya viste todo lo publicado</div>
            )}
          </div>
        )}

        {/* 🏆 ranking de usuarios por km grabados */}
        {showRankingKm && <RankingKmModal onClose={() => setShowRankingKm(false)} />}
        {reelsAbierto !== null && reels.length > 0 && (
          <ReelsViewer posts={reels} inicio={Math.min(reelsAbierto, reels.length - 1)} onClose={() => setReelsAbierto(null)} requireAuth={requireAuth} />
        )}

        {/* con señal mala: primero "cargando", no el vacío como si no existiera nada */}
        {!feedCargado && postReales.length === 0 && (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ width: 26, height: 26, margin: "0 auto 14px", border: `3px solid ${C.border}`, borderTopColor: "#111", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <p style={{ fontSize: 13, color: C.muted }}>Cargando el feed…</p>
          </div>
        )}
        {/* estado vacío cuando no hay posts reales */}
        {feedCargado && postReales.length === 0 && (
          <div style={{ padding: "60px 32px", textAlign: "center" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <Camera size={36} strokeWidth={1.5} color="#111111" />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 10, letterSpacing: -0.3 }}>
              Sé el primero en publicar
            </h3>
            <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
              El feed está esperando tus rutas, fotos y experiencias. Compartí la primera.
            </p>
            <button onClick={() => requireAuth ? requireAuth(() => setShowPublicar(true)) : setShowPublicar(true)}
              style={{ padding: "13px 28px", borderRadius: 14, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer" }}>
              Publicar algo 🏍️
            </button>
            <p style={{ fontSize: 12, color: C.faint, marginTop: 20, fontStyle: "italic" }}>
              — El camino es el destino
            </p>
          </div>
        )}
        <div style={{ padding: "24px 18px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🏁</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>Ya viste todo por hoy</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>Salí a andar y volvé con más historias 🏍️</div>
          <button onClick={() => compartirWhatsApp({ texto: totalRiders >= 100 ? `🏍️ Ya somos ${totalRiders} riders en Olbos. Sumate, es gratis!` : `🏍️ Sumate a los primeros riders de Olbos. Es gratis!`, tipo: "instalar", id: "1" })}
            style={{ padding: "11px 18px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 18, height: 18, borderRadius: 5, objectFit: "cover" }} />
            Invitá a tus amigos moteros
          </button>
        </div>
      </div>
      {commentsItem && <CommentsModal post={commentsItem} onClose={() => setCommentsItem(null)} />}
      {showTagModal && <TagEspacioModal onClose={() => setShowTagModal(false)} onSelect={id => setNewPostTag(id)} />}

      {/* botón flotante publicar */}
      <button onClick={() => requireAuth ? requireAuth(() => setShowPublicar(true)) : setShowPublicar(true)} title="Publicar" style={{
        position: "fixed", bottom: 80, right: "calc(50% - 215px + 16px)",
        width: fabConTexto ? "auto" : 52, height: 52, borderRadius: fabConTexto ? 100 : "50%", background: C.text, padding: fabConTexto ? "0 18px 0 14px" : 0,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        boxShadow: "0 4px 16px rgba(0,0,0,.25)", zIndex: 80, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em"
      }}>
        <Plus size={24} strokeWidth={2.5} />{fabConTexto ? "Publicar" : null}
      </button>

      {/* Modal publicar — scrolleable y con tope de alto: con el teclado abierto el
          modal se recorta solo y el texto sigue visible (antes lo tapaba) */}
      {showPublicar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}>
          <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 18px calc(40px + var(--sab))", maxHeight: "88vh", overflowY: "auto" }}>

            {/* header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, position: "sticky", top: 0, background: C.card, zIndex: 2, paddingBottom: 6 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>
                {newPost.grupo_id ? `Publicar en ${(misGruposPost.find(g => g.id === newPost.grupo_id)?.nombre) || "grupo"}` : "Nueva publicación"}
              </h2>
              <button onClick={() => { setShowPublicar(false); setNewPost({ texto: "", medias: [], categoria: null, grupo_id: "", compartido_feed_general: true }); }}
                style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
                <X size={16} strokeWidth={2} color={C.muted} />
              </button>
            </div>

            {/* Primera vez: qué se puede publicar, en una línea */}
            {coachPub && (
              <div style={{ marginBottom: 14, padding: "11px 12px", background: "#111111", color: "#F9F5F2", borderRadius: 12, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.45, letterSpacing: "-0.005em" }}>
                  📸 La foto de tu moto, una ruta, un video de la rodada. Etiquetá el evento con <b>#</b> y va también a su muro.
                </div>
                <button onClick={() => setCoachPub(false)} style={{ background: "none", border: "none", color: "#bdb6ab", cursor: "pointer", padding: 0, flexShrink: 0 }}><X size={15} /></button>
              </div>
            )}

            {/* Selector de grupo + toggle (solo si hay grupos) */}
            {misGruposPost.length > 0 && (
              <div style={{ marginBottom: 14, padding: "12px", background: C.surface, borderRadius: 12, border: `1px solid ${C.border}` }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 8 }}>
                  Publicar en
                </label>
                <select value={newPost.grupo_id || ""} onChange={e => {
                  const gId = e.target.value;
                  // Al cambiar de grupo a otro, conservamos el toggle.
                  // Al pasar de público a grupo por primera vez, OFF por default (privado del grupo).
                  // Al pasar de grupo a público, ignoramos el toggle (es público).
                  setNewPost(p => ({
                    ...p,
                    grupo_id: gId,
                    compartido_feed_general: gId ? p.compartido_feed_general : true,
                  }));
                }}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, letterSpacing: "-0.005em", fontFamily: "inherit", appearance: "none", WebkitAppearance: "none" }}>
                  <option value="">🌎 Feed público (todos los riders)</option>
                  {misGruposPost.map(g => (
                    <option key={g.id} value={g.id}>👥 {g.nombre}</option>
                  ))}
                </select>

                {/* Toggle compartir en feed general */}
                {newPost.grupo_id && (
                  <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, padding: "8px 0", cursor: "pointer" }}>
                    <input type="checkbox" checked={newPost.compartido_feed_general}
                      onChange={e => setNewPost(p => ({ ...p, compartido_feed_general: e.target.checked }))}
                      style={{ accentColor: "#111111", width: 16, height: 16 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.text, letterSpacing: "-0.005em" }}>Compartir también en feed general</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4, letterSpacing: "-0.005em" }}>
                        {newPost.compartido_feed_general
                          ? "Lo van a ver tanto los miembros del grupo como el resto de los riders"
                          : "Solo lo van a ver los miembros del grupo"}
                      </div>
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* texto con @mentions */}
            <div style={{ position: "relative", marginBottom: 12 }}>
              <textarea
                ref={textareaRef}
                value={newPost.texto}
                onChange={handleTextoChange}
                onFocus={e => { const el = e.target; setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 320); }}
                placeholder="¿Qué querés compartir? Usá @ para mencionar riders"
                maxLength={2200}
                style={{ width: "100%", minHeight: 100, padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 16, color: C.text, background: C.surface, resize: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              {/* Aviso solo cuando se está cerca del tope (2200, igual que Instagram) */}
              {newPost.texto.length > 1900 && (
                <div style={{ fontSize: 11, color: newPost.texto.length >= 2200 ? C.red : C.muted, textAlign: "right", marginTop: 4 }}>
                  {newPost.texto.length} / 2200
                </div>
              )}
              {/* dropdown de mentions */}
              {showMentions && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden" }}>
                  {mentionResults.length === 0 ? (
                    <div style={{ padding: "12px 14px", fontSize: 13, color: C.muted }}>
                      {mentionQuery.length === 0 ? "Escribí un nombre..." : "Sin resultados"}
                    </div>
                  ) : mentionResults.map(u => (
                    <button key={u.username} onClick={() => insertMention(u.username)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                      onMouseOver={e => e.currentTarget.style.background = C.surface}
                      onMouseOut={e => e.currentTarget.style.background = "none"}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accent, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {u.avatar_url
                          ? <img loading="lazy" decoding="async" src={u.avatar_url} alt={u.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{u.username?.[0]?.toUpperCase()}</span>
                        }
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>@{u.username}</div>
                        <BadgeTag badge={getBadge(null, u.username)} />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* dropdown de hashtags — selector de categoría o resultados */}
              {hashtagMode === "menu" && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px 6px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>¿Qué querés mencionar?</div>
                  {[
                    { tipo: "ruta", label: "Ruta", icono: "🛣️", desc: "Una ruta de la app" },
                    { tipo: "moto", label: "Moto", icono: "🏍️", desc: "Un modelo de la base" },
                    { tipo: "evento", label: "Evento", icono: "📅", desc: "Una salida o evento" },
                    { tipo: "negocio", label: "Negocio", icono: "📍", desc: "Taller, marca, concesionario" },
                  ].map(c => (
                    <button key={c.tipo} onClick={() => elegirCategoriaHashtag(c.tipo)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                      onMouseOver={e => e.currentTarget.style.background = C.surface}
                      onMouseOut={e => e.currentTarget.style.background = "none"}>
                      <div style={{ fontSize: 22 }}>{c.icono}</div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{c.label}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{c.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {hashtagMode && hashtagMode !== "menu" && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: "0 4px 20px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
                  <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Buscando {hashtagMode}s</span>
                    <button onClick={() => setHashtagMode("menu")} style={{ fontSize: 10, color: C.blue, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>← Cambiar</button>
                  </div>
                  {hashtagResults.length === 0 ? (
                    <div style={{ padding: "12px 14px", fontSize: 13, color: C.muted }}>
                      {hashtagQuery.length === 0 ? "Escribí para buscar..." : "Sin resultados"}
                    </div>
                  ) : hashtagResults.map(item => (
                    <button key={item.id} onClick={() => insertHashtag(item)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                      onMouseOver={e => e.currentTarget.style.background = C.surface}
                      onMouseOut={e => e.currentTarget.style.background = "none"}>
                      <div style={{ fontSize: 20, flexShrink: 0 }}>{item.icono}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.nombre}</div>
                        <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.subtitulo}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* categoría */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Categoría</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { id: "ruta", label: "📍 Ruta / Viaje" },
                  { id: "mecanica", label: "🔧 Mecánica" },
                  { id: "moto", label: "🏍️ Mi moto" },
                  { id: "evento", label: "🎉 Evento" },
                  { id: "foto", label: "📸 Foto del día" },
                  { id: "general", label: "💬 General" },
                ].map(c => (
                  <button key={c.id} onClick={() => setNewPost(p => ({ ...p, categoria: p.categoria === c.id ? null : c.id }))}
                    style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${newPost.categoria === c.id ? C.text : C.border}`, background: newPost.categoria === c.id ? C.text : C.card, color: newPost.categoria === c.id ? "#fff" : C.muted, cursor: "pointer" }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* preview múltiples medias con reorden por flechas */}
            {newPost.medias.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                  {newPost.medias.map((m, i) => (
                    <div key={m.tempId || i} style={{ flexShrink: 0, position: "relative", width: newPost.medias.length === 1 ? "100%" : 110 }}>
                      {m.type === "video"
                        ? <video src={cldOpt(m.url, 400)} poster={cldPoster(m.url, 400)} preload="none" style={{ width: "100%", height: newPost.medias.length === 1 ? 220 : 110, objectFit: "cover", borderRadius: 10 }} />
                        : <img loading="lazy" decoding="async" src={m.url} alt="" style={{ width: "100%", height: newPost.medias.length === 1 ? 220 : 110, objectFit: "cover", borderRadius: 10 }} />
                      }
                      {m.uploading && (
                        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.4)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ color: "#fff", fontSize: 11, fontWeight: 600, textAlign: "center", padding: "0 6px" }}>{m.nota || "Subiendo..."}</span>
                        </div>
                      )}
                      {/* número */}
                      {newPost.medias.length > 1 && (
                        <div style={{ position: "absolute", top: 4, left: 4, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,.7)", color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {i + 1}
                        </div>
                      )}
                      {/* flechas reorden */}
                      {newPost.medias.length > 1 && (
                        <div style={{ position: "absolute", bottom: 4, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 4 }}>
                          {i > 0 && (
                            <button onClick={() => setNewPost(p => {
                              const arr = [...p.medias];
                              [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
                              return { ...p, medias: arr };
                            })} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,.7)", border: "none", cursor: "pointer", color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              ←
                            </button>
                          )}
                          {i < newPost.medias.length - 1 && (
                            <button onClick={() => setNewPost(p => {
                              const arr = [...p.medias];
                              [arr[i], arr[i+1]] = [arr[i+1], arr[i]];
                              return { ...p, medias: arr };
                            })} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,.7)", border: "none", cursor: "pointer", color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              →
                            </button>
                          )}
                        </div>
                      )}
                      {/* borrar */}
                      <button onClick={() => setNewPost(p => ({ ...p, medias: p.medias.filter((_, j) => j !== i) }))}
                        style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <X size={12} strokeWidth={2} color="#fff" />
                      </button>
                    </div>
                  ))}
                  {newPost.medias.length < 10 && (
                    <label style={{ flexShrink: 0, width: 80, height: 110, borderRadius: 10, border: `2px dashed ${C.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 4 }}>
                      <Plus size={20} strokeWidth={2} color={C.muted} />
                      <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>Agregar</span>
                      <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
                        onChange={e => {
                          const files = Array.from(e.target.files);
                          files.forEach(f => handleMediaUpload([f], f.type.startsWith("video") ? "video" : "foto"));
                        }} />
                    </label>
                  )}
                </div>
                {/* Etiquetar personas sobre las fotos (estilo Instagram) */}
                {newPost.medias.some(m => m.type !== "video" && m.url && !m.uploading) && (
                  <button onClick={() => setShowEtiquetar(true)}
                    style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <User size={16} strokeWidth={2} /> Etiquetar personas{(newPost.tags?.length || 0) > 0 ? ` (${newPost.tags.length})` : ""}
                  </button>
                )}
              </div>
            )}

            {showEtiquetar && (
              <EtiquetarPersonasModal
                medias={newPost.medias}
                tagsIniciales={newPost.tags || []}
                onClose={() => setShowEtiquetar(false)}
                onGuardar={(tags) => { setNewPost(p => ({ ...p, tags })); setShowEtiquetar(false); }}
              />
            )}

            {/* botones media — solo si no hay fotos */}
            {newPost.medias.length === 0 && (
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                {/* foto de galería - múltiple */}
                <label style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <Image size={22} strokeWidth={1.8} color={C.muted} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>Fotos</span>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                    onChange={e => handleMediaUpload(Array.from(e.target.files), "foto")} />
                </label>

                {/* video */}
                <label style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <Play size={22} strokeWidth={1.8} color={C.muted} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>Video</span>
                  <input ref={videoInputRef} type="file" accept="video/*" style={{ display: "none" }}
                    onChange={e => handleMediaUpload(Array.from(e.target.files), "video")} />
                </label>

                {/* cámara */}
                <label style={{ flex: 1, padding: "12px 0", borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <Camera size={22} strokeWidth={1.8} color={C.muted} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>Cámara</span>
                  <input ref={camaraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                    onChange={e => handleMediaUpload(Array.from(e.target.files), "foto")} />
                </label>
              </div>
            )}

            {/* Selector de grupo (si tengo grupos) */}
            {misGruposPost.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Publicar en</div>
                <select value={newPost.grupo_id} onChange={e => setNewPost(p => ({ ...p, grupo_id: e.target.value }))}
                  style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box", fontFamily: "inherit" }}>
                  <option value="">Feed general (público)</option>
                  {misGruposPost.map(g => (
                    <option key={g.id} value={g.id}>Grupo: {g.nombre}</option>
                  ))}
                </select>
                {/* Toggle "compartir también en feed general" si elegí un grupo */}
                {newPost.grupo_id && (
                  <label style={{
                    display: "flex", alignItems: "center", gap: 10, marginTop: 8,
                    padding: "10px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, cursor: "pointer",
                    background: newPost.compartido_feed_general ? "#1111110C" : C.card,
                  }}>
                    <input type="checkbox" checked={newPost.compartido_feed_general}
                      onChange={e => setNewPost(p => ({ ...p, compartido_feed_general: e.target.checked }))}
                      style={{ width: 18, height: 18, accentColor: "#111111" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Compartir también en el Feed general</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Lo van a ver todos los riders, no solo el grupo</div>
                    </div>
                  </label>
                )}
              </div>
            )}

            {cuentasDelegadas.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Publicar como</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[{ id: null, username: "mi cuenta" }, ...cuentasDelegadas].map(c => (
                    <button key={c.id || "yo"} type="button" onClick={() => setPublicarComo(c.id)}
                      style={{ padding: "7px 12px", borderRadius: 100, border: `1.5px solid ${publicarComo === c.id ? C.text : C.border}`, background: publicarComo === c.id ? C.text : C.card, color: publicarComo === c.id ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                      {c.avatar_url ? <img src={cldThumb(c.avatar_url, 40)} alt="" style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} /> : null}
                      {c.id ? "@" + c.username : c.username}
                    </button>
                  ))}
                </div>
                {publicarComo && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Sale con el nombre y la foto de esa cuenta. Queda registrado que lo publicaste vos.</div>}
              </div>
            )}

            {/* Botón con link promocionado (cobro por click del saldo prepago) */}
            {promoOk && !newPost.grupo_id && (
              <div style={{ marginBottom: 12, background: C.surface, borderRadius: 12, padding: "10px 12px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13.5, fontWeight: 700, color: C.text }}>
                  <input type="checkbox" checked={promoForm.on} onChange={e => setPromoForm(p => ({ ...p, on: e.target.checked }))} style={{ width: 17, height: 17, accentColor: "#111111" }} />
                  Agregar un botón con link (promocionado)
                </label>
                {promoForm.on && (
                  <div style={{ marginTop: 10 }}>
                    <input value={promoForm.url} onChange={e => setPromoForm(p => ({ ...p, url: e.target.value }))} placeholder="Link: tu tienda o tu publicación de Mercado Libre" inputMode="url"
                      style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, background: C.card, color: C.text, fontFamily: "inherit" }} />
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      {BOTONES_PROMO.map(b => (
                        <button key={b} type="button" onClick={() => setPromoForm(p => ({ ...p, boton: b }))} style={{ padding: "6px 11px", borderRadius: 100, fontSize: 12, fontWeight: 700, border: `1px solid ${promoForm.boton === b ? C.text : C.border}`, background: promoForm.boton === b ? C.text : C.card, color: promoForm.boton === b ? "#fff" : C.text, cursor: "pointer" }}>{b}</button>
                      ))}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>Cada click de un rider te descuenta del saldo de links promocionados (uno por rider por día). Sin saldo, el botón no aparece. Tu saldo y tus números están en Más → Mis links promocionados.</div>
                  </div>
                )}
              </div>
            )}

            {/* publicar */}
            <button onClick={handlePublicar} disabled={publicando || (!newPost.texto && newPost.medias.length === 0) || newPost.medias.some(m => m.uploading)}
              style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: (!newPost.texto && newPost.medias.length === 0) ? C.border : newPost.medias.some(m => m.uploading) ? C.faint : C.text, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", opacity: publicando ? 0.7 : 1 }}>
              {publicando ? "Publicando..." : newPost.medias.some(m => m.uploading && m.nota && m.nota.startsWith("Achicando")) ? "Achicando el video..." : newPost.medias.some(m => m.uploading) ? "Subiendo fotos..." : "Publicar"}
            </button>
          </div>
        </div>
      )}

      {/* Modal buscar perfiles */}
      {showBuscarPerfiles && (
        <BuscarPerfilesModal
          session={session}
          setTab={setTab}
          onClose={() => setShowBuscarPerfiles(false)}
        />
      )}
    </PageWrap>
  );
};

// ─── DATA: MARCAS & CONCESIONARIOS ───────────────────────────────────────────
const MARCAS = [];

const CONCESIONARIOS = [];

// ─── COMPRAVENTA DATA ─────────────────────────────────────────────────────────
const COMPRAVENTA = [];

// ─── ALQUILER DATA ────────────────────────────────────────────────────────────
const ALQUILER = [];

// ─── SECCIÓN: MERCADO ─────────────────────────────────────────────────────────
// ─── RESEÑAS DE NEGOCIOS (Pit Stop) ──────────────────────────────────────────
// Estrellas + comentario, una por rider y editable. Sirve para cualquier negocio.
const EstrellasFijas = ({ valor, size = 13 }) => (
  <span style={{ display: "inline-flex", gap: 1, verticalAlign: "middle" }}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={size} strokeWidth={2}
        fill={i <= Math.round(valor) ? "#8A6D3B" : "none"}
        color={i <= Math.round(valor) ? "#8A6D3B" : C.border} />
    ))}
  </span>
);

const ResenasNegocio = ({ negocioId, nombre, resumen, onCambio }) => {
  const [abierto, setAbierto] = useState(false);
  const [lista, setLista] = useState([]);
  const [rating, setRating] = useState(0);
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [tengoUna, setTengoUna] = useState(false);
  const prom = resumen?.prom || 0;
  const cant = resumen?.n || 0;

  const cargar = async () => {
    const { data: raw, error } = await supabase.rpc("get_resenas_negocio", { p_negocio_id: negocioId });
    if (error) return; // SQL todavía sin correr → la sección simplemente no muestra reseñas
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(d)) {
      setLista(d);
      const mia = d.find(r => r.mia);
      if (mia) { setTengoUna(true); setRating(mia.rating); setTexto(mia.texto || ""); }
    }
  };
  useEffect(() => { if (abierto) cargar(); /* eslint-disable-next-line */ }, [abierto]);

  const guardar = async () => {
    if (!rating) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { alert("Iniciá sesión para dejar tu recomendación."); return; }
    setGuardando(true);
    await supabase.rpc("asegurar_perfil").then(() => {}, () => {});
    const { error } = await supabase.from("negocio_resenas")
      .upsert({ negocio_id: negocioId, user_id: user.id, rating, texto: texto.trim() || null }, { onConflict: "negocio_id,user_id" });
    setGuardando(false);
    if (error) { alert("No se pudo guardar tu reseña: " + error.message); return; }
    setTengoUna(true);
    await cargar();
    onCambio && onCambio();
  };

  const borrar = async () => {
    if (!window.confirm("¿Borrar tu reseña?")) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("negocio_resenas").delete().eq("negocio_id", negocioId).eq("user_id", user.id);
    setTengoUna(false); setRating(0); setTexto("");
    await cargar();
    onCambio && onCambio();
  };

  const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" }); };

  return (
    <>
      <button onClick={() => setAbierto(true)}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", padding: 0, cursor: "pointer", marginTop: 8 }}>
        {cant > 0 ? (
          <>
            <EstrellasFijas valor={prom} />
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{prom}</span>
            <span style={{ fontSize: 12, color: C.muted }}>({cant})</span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 4 }}>
            <Star size={13} strokeWidth={2} color={C.muted} /> Sé el primero en recomendarlo
          </span>
        )}
      </button>

      {abierto && (
        <div onClick={e => e.target === e.currentTarget && setAbierto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div className="fade-up" style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", padding: "20px 20px calc(28px + var(--sab))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h2 style={{ fontSize: 16.5, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>{nombre}</h2>
                {cant > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                    <EstrellasFijas valor={prom} size={12} />
                    <span style={{ fontSize: 12, color: C.muted }}>{prom} · {cant} reseña{cant !== 1 ? "s" : ""}</span>
                  </div>
                )}
              </div>
              <button onClick={() => setAbierto(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} strokeWidth={2} color={C.muted} />
              </button>
            </div>

            {/* mi reseña */}
            <div style={{ background: C.surface, borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                {tengoUna ? "Tu reseña" : "¿Lo recomendás?"}
              </div>
              <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <button key={i} onClick={() => setRating(i)} style={{ background: "none", border: "none", padding: 2, cursor: "pointer" }}>
                    <Star size={26} strokeWidth={2} fill={i <= rating ? "#8A6D3B" : "none"} color={i <= rating ? "#8A6D3B" : C.border} />
                  </button>
                ))}
              </div>
              <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={3}
                placeholder="Contá tu experiencia: ¿resolvió rápido? ¿fue claro con los costos? (opcional)"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, resize: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={guardar} disabled={!rating || guardando}
                  style={{ flex: 1, padding: "11px", borderRadius: 10, background: rating ? C.text : C.border, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: rating ? "pointer" : "default" }}>
                  {guardando ? "Guardando…" : tengoUna ? "Actualizar" : "Publicar reseña"}
                </button>
                {tengoUna && (
                  <button onClick={borrar} style={{ padding: "11px 14px", borderRadius: 10, background: `${C.red}14`, border: `1px solid ${C.red}22`, color: C.red, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    Borrar
                  </button>
                )}
              </div>
            </div>

            {/* reseñas de otros */}
            {lista.filter(r => !r.mia).length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted, textAlign: "center", padding: "16px 0" }}>
                Todavía no hay otras reseñas.
              </div>
            ) : lista.filter(r => !r.mia).map(r => (
              <div key={r.id} style={{ padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: r.avatar_url ? `url(${cldThumb(r.avatar_url, 80)}) center/cover` : C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: C.muted, flexShrink: 0 }}>
                    {!r.avatar_url && (r.username?.[0]?.toUpperCase() || "?")}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>@{r.username || "rider"}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <EstrellasFijas valor={r.rating} size={11} />
                      <span style={{ fontSize: 11, color: C.muted }}>{fmt(r.created_at)}</span>
                    </div>
                  </div>
                </div>
                {r.texto && <p style={{ fontSize: 13.5, color: C.text, lineHeight: 1.5, paddingLeft: 36 }}>{r.texto}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ─── LUGARES RECOMENDADOS POR LA COMUNIDAD (hospedajes y dónde comer) ────────
// Hoteles, motoposadas, Airbnbs, parrillas y paradores donde un rider estuvo de
// verdad y los recomienda. El dato que ningún booking o Google Maps tiene: si la
// moto queda segura. Cada lugar lleva la cara del que lo recomienda, y los demás
// suman su "yo también estuve". Tabla: lugares_comunidad + lugar_visitas.
const LUGARES_CFG = {
  hospedaje: {
    titulo: "hospedaje",
    tipos: ["Hotel", "Motoposada", "Cabaña", "Airbnb / Alquiler", "Camping", "Hostel"],
    flag: "🏍 La moto duerme bajo techo / lugar seguro",
    flagCorto: "🏍 Moto bajo techo",
    estuve: "🏍 Yo también me quedé acá",
    estuveOk: "✓ Confirmaste que te quedaste acá",
    confirman: "más se quedaron y lo confirman",
    intro: <>Lugares donde durmieron otros riders y los recomiendan. El dato que importa: <strong style={{ color: C.text }}>si la moto duerme bajo techo</strong>.</>,
    placeholderDesc: "¿Por qué lo recomendás? Precio aproximado, cómo te trataron, qué tiene para la moto…",
    vacio: "¿Te quedaste en algún lugar que valga la pena? Sé el primero en recomendarlo.",
  },
  comida: {
    titulo: "lugar para comer",
    tipos: ["Parrilla", "Parador de ruta", "Restaurante", "Café / Panadería", "Comedor", "Food truck"],
    flag: "🏍 Estacionamiento seguro / la moto a la vista",
    flagCorto: "🏍 Moto a la vista",
    estuve: "🍴 Yo también comí acá",
    estuveOk: "✓ Confirmaste que comiste acá",
    confirman: "más comieron acá y lo confirman",
    intro: <>Paradores, parrillas y lugares donde pararon otros riders. Con el dato clave: <strong style={{ color: C.text }}>dónde dejás la moto mientras comés</strong>.</>,
    placeholderDesc: "¿Por qué lo recomendás? Qué pedir, precios, si atienden bien a los moteros…",
    vacio: "¿Conocés una parrilla o parador que valga la pena? Sé el primero en recomendarlo.",
  },
};
const LugaresSeccion = ({ categoria }) => {
  const cfg = LUGARES_CFG[categoria];
  const [session, setSession] = useState(null);
  useEffect(() => { supabase.auth.getSession().then(({ data: { session: se } }) => setSession(se)); }, []);
  const [lugares, setLugares] = useState([]);
  const [cargado, setCargado] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("Todos");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [misVisitas, setMisVisitas] = useState({});
  const [verPerfil, setVerPerfil] = useState(null); // username a mostrar
  const [conteos, setConteos] = useState({});
  const [visitas, setVisitas] = useState({}); // { lugar_id: [visitas con estrellas y comentario] }
  const [calificando, setCalificando] = useState(null); // lugar en proceso de "yo estuve"
  const [califEstrellas, setCalifEstrellas] = useState(5);
  const [califComentario, setCalifComentario] = useState("");
  // Promedio de estrellas de un lugar (solo visitas que calificaron)
  const rating = (lugarId) => {
    const conEst = (visitas[lugarId] || []).filter(v => v.estrellas);
    if (conEst.length === 0) return null;
    return { prom: conEst.reduce((a, v) => a + v.estrellas, 0) / conEst.length, n: conEst.length };
  };

  const cargar = async () => {
    const { data } = await supabase.from("lugares_comunidad").select("*").eq("categoria", categoria).order("created_at", { ascending: false });
    setLugares(data || []);
    setCargado(true);
    const ids = (data || []).map(l => l.id);
    if (ids.length === 0) { setConteos({}); setVisitas({}); return; }
    const { data: vs } = await supabase.from("lugar_visitas").select("*").in("lugar_id", ids).order("created_at", { ascending: false });
    const c = {}, mias = {}, porLugar = {};
    const uid = (await supabase.auth.getSession()).data?.session?.user?.id;
    (vs || []).forEach(v => {
      c[v.lugar_id] = (c[v.lugar_id] || 0) + 1;
      (porLugar[v.lugar_id] = porLugar[v.lugar_id] || []).push(v);
      if (uid && v.user_id === uid) mias[v.lugar_id] = true;
    });
    setConteos(c); setMisVisitas(mias); setVisitas(porLugar);
  };
  useEffect(() => { cargar(); }, [categoria]); // eslint-disable-line react-hooks/exhaustive-deps

  const tambienEstuve = (l) => {
    if (!session?.user) { alert("Iniciá sesión para confirmar que estuviste."); return; }
    if (misVisitas[l.id]) return;
    setCalifEstrellas(5); setCalifComentario("");
    setCalificando(l); // abre el mini-form: estrellas + comentario opcional
  };
  const confirmarVisita = async () => {
    const l = calificando;
    if (!l) return;
    const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", session.user.id).maybeSingle();
    const { error } = await supabase.from("lugar_visitas").insert({
      lugar_id: l.id, user_id: session.user.id,
      username: perfil?.username || null,
      estrellas: califEstrellas || null,
      comentario: califComentario.trim() || null,
    });
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    setCalificando(null);
    cargar();
  };

  const [form, setForm] = useState({ nombre: "", tipo: cfg.tipos[0], ciudad: "", descripcion: "", link: "", telefono: "", moto_segura: true, foto: "" });
  const [guardando, setGuardando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [verMapa, setVerMapa] = useState(false); // los lugares, en el mapa (pin por lugar)
  // El creador puede agregar/cambiar la foto de SU lugar desde el detalle
  const cambiarFotoLugar = async (file) => {
    if (!file || !sel) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const r = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await r.json();
      if (j.secure_url) {
        const { error } = await supabase.from("lugares_comunidad").update({ foto_url: j.secure_url }).eq("id", sel.id);
        if (error) { alert("No se pudo guardar la foto: " + error.message); }
        else { setSel(s => s ? { ...s, foto_url: j.secure_url } : s); cargar(); }
      }
    } catch (e) { alert("No se pudo subir la foto."); }
    setSubiendoFoto(false);
  };
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const subirFoto = async (file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const r = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await r.json();
      if (j.secure_url) setF("foto", j.secure_url);
    } catch (e) { alert("No se pudo subir la foto."); }
    setSubiendoFoto(false);
  };
  const guardar = async () => {
    if (!session?.user) { alert(`Iniciá sesión para recomendar un ${cfg.titulo}.`); return; }
    if (!form.nombre.trim() || !form.ciudad.trim() || !form.descripcion.trim()) { alert("Completá el nombre, la ciudad y por qué lo recomendás."); return; }
    setGuardando(true);
    let lat = null, lng = null;
    try {
      const r = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(form.nombre + ", " + form.ciudad)}`);
      const d = await r.json();
      if (d?.lat) { lat = d.lat; lng = d.lng; }
      else {
        const r2 = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(form.ciudad)}`);
        const d2 = await r2.json();
        if (d2?.lat) { lat = d2.lat; lng = d2.lng; }
      }
    } catch (e) { /* sin coords igual sirve */ }
    const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", session.user.id).maybeSingle();
    const { error } = await supabase.from("lugares_comunidad").insert({
      creador_id: session.user.id,
      creador_username: perfil?.username || null,
      categoria,
      nombre: form.nombre.trim(), tipo: form.tipo, ciudad: form.ciudad.trim(),
      descripcion: form.descripcion.trim(), link: form.link.trim() || null,
      telefono: form.telefono.trim() || null, moto_segura: !!form.moto_segura,
      foto_url: form.foto || null, lat, lng,
    });
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    setShowForm(false);
    setForm({ nombre: "", tipo: cfg.tipos[0], ciudad: "", descripcion: "", link: "", telefono: "", moto_segura: true, foto: "" });
    cargar();
    alert("✓ ¡Gracias! Tu recomendación ya está visible para toda la comunidad.");
  };

  const nq = (t) => String(t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filtrados = lugares
    .filter(l => filtroTipo === "Todos" || l.tipo === filtroTipo)
    .filter(l => !q.trim() || nq(`${l.nombre} ${l.ciudad} ${l.descripcion}`).includes(nq(q)));

  const inputS = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" };

  return (
    <div style={{ padding: "14px 18px 80px" }}>
      <div style={{ padding: "12px 14px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
        {cfg.intro}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input value={q} onChange={e => setQ(e.target.value)} type="search" enterKeyHint="search"
          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
          placeholder="Buscar por ciudad o nombre…"
          style={{ ...inputS, flex: 1 }} />
        <button onClick={() => session?.user ? setShowForm(true) : alert(`Iniciá sesión para recomendar un ${cfg.titulo}.`)}
          style={{ padding: "0 16px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          ＋ Recomendar
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, marginBottom: 10 }}>
        <button onClick={() => setVerMapa(v => !v)}
          style={{ flexShrink: 0, padding: "6px 13px", borderRadius: 100, fontSize: 12, fontWeight: 700, background: verMapa ? C.text : "transparent", color: verMapa ? "#fff" : C.muted, border: `1px solid ${verMapa ? C.text : C.border}`, cursor: "pointer" }}>
          🗺️ Mapa
        </button>
        {["Todos", ...cfg.tipos].map(t => (
          <button key={t} onClick={() => setFiltroTipo(t)}
            style={{ flexShrink: 0, padding: "6px 13px", borderRadius: 100, fontSize: 12, fontWeight: filtroTipo === t ? 700 : 500, background: filtroTipo === t ? C.text : "transparent", color: filtroTipo === t ? "#fff" : C.muted, border: `1px solid ${filtroTipo === t ? C.text : C.border}`, cursor: "pointer" }}>
            {t}
          </button>
        ))}
      </div>

      {/* el mapa: los mismos filtrados de la lista, un pin por lugar */}
      {verMapa && cargado && (
        <div style={{ marginBottom: 12 }}>
          <MapaServicios items={filtrados} onAbrir={setSel} />
          {filtrados.filter(l => !l.lat || !l.lng).length > 0 && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, textAlign: "center" }}>
              {filtrados.filter(l => !l.lat || !l.lng).length} lugar{filtrados.filter(l => !l.lat || !l.lng).length !== 1 ? "es" : ""} sin ubicación exacta no aparece{filtrados.filter(l => !l.lat || !l.lng).length !== 1 ? "n" : ""} en el mapa (sí en la lista)
            </div>
          )}
        </div>
      )}

      {!cargado && (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <div style={{ width: 24, height: 24, margin: "0 auto", border: `3px solid ${C.border}`, borderTopColor: C.text, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      )}
      {cargado && filtrados.length === 0 && (
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            {categoria === "comida" ? <Utensils size={26} strokeWidth={1.6} color={C.text} /> : <Home size={28} strokeWidth={1.6} color={C.text} />}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>Todavía no hay recomendaciones {filtroTipo !== "Todos" || q ? "con ese filtro" : ""}</div>
          <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>{cfg.vacio}</p>
        </div>
      )}

      {filtrados.map(l => (
        <button key={l.id} onClick={() => setSel(l)}
          style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 10, cursor: "pointer", textAlign: "left", padding: 0 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ width: 96, height: 96, flexShrink: 0, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {l.foto_url ? <img loading="lazy" decoding="async" src={cldThumb(l.foto_url, 400)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : categoria === "comida" ? <Utensils size={24} strokeWidth={1.5} color={C.muted} /> : <Home size={26} strokeWidth={1.5} color={C.muted} />}
            </div>
            <div style={{ flex: 1, minWidth: 0, padding: "10px 12px 10px 0" }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: 0.5 }}>{l.tipo}</span>
                {l.moto_segura && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 100, background: `${C.green}14`, border: `1px solid ${C.green}33`, color: "#1F8347" }}>{cfg.flagCorto}</span>}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: C.text, lineHeight: 1.25, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.nombre}</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{l.ciudad}</div>
              {rating(l.id) && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                  <EstrellasFijas valor={rating(l.id).prom} size={11} />
                  <span style={{ fontSize: 11, color: C.muted }}>{rating(l.id).prom.toFixed(1)} ({rating(l.id).n})</span>
                </div>
              )}
              <div style={{ fontSize: 11.5, color: C.muted }}>
                Recomendado por <strong onClick={e => { e.stopPropagation(); if (l.creador_username) setVerPerfil(l.creador_username); }} style={{ color: C.text, textDecoration: "underline", textDecorationColor: C.border }}>@{l.creador_username || "un rider"}</strong>
                {(conteos[l.id] || 0) > 0 && ` · +${conteos[l.id]} rider${conteos[l.id] !== 1 ? "s" : ""}`}
              </div>
            </div>
          </div>
        </button>
      ))}

      {/* detalle */}
      {sel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setSel(null)}>
          <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto", paddingBottom: "calc(24px + var(--sab))" }}>
            {sel.foto_url && <img loading="lazy" decoding="async" src={cldOpt(sel.foto_url, 1080)} alt="" style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }} />}
            {/* El que lo recomendó puede agregar o cambiar la foto — antes no había forma */}
            {session?.user?.id && sel.creador_id === session.user.id && (
              <label style={{ display: "block", margin: sel.foto_url ? "8px 20px 0" : "16px 20px 0", padding: "9px 12px", borderRadius: 10, background: C.surface, border: `1px dashed ${C.border}`, fontSize: 12.5, fontWeight: 700, color: C.text, textAlign: "center", cursor: "pointer" }}>
                {subiendoFoto ? "Subiendo foto…" : sel.foto_url ? "📷 Cambiar la foto" : "📷 Agregar una foto"}
                <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendoFoto}
                  onChange={e => { cambiarFotoLugar(e.target.files[0]); e.target.value = ""; }} />
              </label>
            )}
            <div style={{ padding: "16px 20px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 4 }}>
                <div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: 0.5 }}>{sel.tipo}</span>
                    {sel.moto_segura && <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: `${C.green}14`, border: `1px solid ${C.green}33`, color: "#1F8347" }}>{cfg.flagCorto}</span>}
                  </div>
                  <h3 style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{sel.nombre}</h3>
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{sel.ciudad}</div>
                </div>
                <button onClick={() => setSel(null)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>
              <div style={{ padding: "12px 14px", borderRadius: 12, background: C.surface, fontSize: 13.5, color: C.text, lineHeight: 1.6, margin: "12px 0", whiteSpace: "pre-wrap" }}>
                {sel.descripcion}
                <div onClick={() => sel.creador_username && setVerPerfil(sel.creador_username)} style={{ fontSize: 11.5, color: C.muted, marginTop: 8, cursor: "pointer", textDecoration: "underline", textDecorationColor: C.border }}>— @{sel.creador_username || "un rider"}, que estuvo acá</div>
              </div>
              {rating(sel.id) && (
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                  <EstrellasFijas valor={rating(sel.id).prom} size={15} />
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{rating(sel.id).prom.toFixed(1)}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>({rating(sel.id).n} calificacion{rating(sel.id).n !== 1 ? "es" : ""})</span>
                </div>
              )}
              {(conteos[sel.id] || 0) > 0 && (
                <div style={{ fontSize: 12.5, color: "#1F8347", fontWeight: 600, marginBottom: 10 }}>✓ {conteos[sel.id]} rider{conteos[sel.id] !== 1 ? "s" : ""} {cfg.confirman}</div>
              )}
              {(visitas[sel.id] || []).filter(v => v.estrellas || v.comentario).slice(0, 12).map((v, i) => (
                <div key={i} style={{ padding: "9px 12px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span onClick={() => v.username && setVerPerfil(v.username)} style={{ fontSize: 12.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>@{v.username || "rider"}</span>
                    {v.estrellas && <EstrellasFijas valor={v.estrellas} size={11} />}
                  </div>
                  {v.comentario && <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5, marginTop: 3 }}>{v.comentario}</div>}
                </div>
              ))}
              <button onClick={() => tambienEstuve(sel)} disabled={misVisitas[sel.id]}
                style={{ width: "100%", padding: "12px", borderRadius: 12, background: misVisitas[sel.id] ? `${C.green}14` : C.text, color: misVisitas[sel.id] ? "#1F8347" : "#fff", border: misVisitas[sel.id] ? `1px solid ${C.green}33` : "none", fontSize: 13.5, fontWeight: 700, cursor: misVisitas[sel.id] ? "default" : "pointer", marginBottom: 10 }}>
                {misVisitas[sel.id] ? cfg.estuveOk : cfg.estuve}
              </button>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {sel.lat && sel.lng && (
                  <button onClick={() => navegarConOlbos(sel.lat, sel.lng, sel.nombre || "el lugar")}
                    style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.accent, border: "none", color: "#fff", fontWeight: 800, fontSize: 12.5, textAlign: "center", cursor: "pointer" }}>
                    🏍️ Navegar
                  </button>
                )}
                {sel.lat && sel.lng && (
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 12.5, textAlign: "center", textDecoration: "none" }}>
                    Cómo llegar
                  </a>
                )}
                {sel.telefono && (
                  <a href={`https://wa.me/${String(sel.telefono).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 12.5, textAlign: "center", textDecoration: "none" }}>
                    WhatsApp
                  </a>
                )}
                {sel.link && (
                  <a href={sel.link.startsWith("http") ? sel.link : `https://${sel.link}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontWeight: 700, fontSize: 12.5, textAlign: "center", textDecoration: "none" }}>
                    Ver más
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {verPerfil && <PerfilPublico username={verPerfil} onClose={() => setVerPerfil(null)} />}

      {/* mini-form: calificar al confirmar "yo también estuve" */}
      {calificando && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 450, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={e => e.target === e.currentTarget && setCalificando(null)}>
          <div style={{ background: C.card, borderRadius: 18, width: "100%", maxWidth: 360, padding: "20px 20px 18px" }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text, marginBottom: 2 }}>{calificando.nombre}</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 14 }}>¿Qué tal estuvo? Tu opinión queda con tu nombre.</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 12 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <button key={i} onClick={() => setCalifEstrellas(i)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                  <Star size={30} strokeWidth={1.8} fill={i <= califEstrellas ? "#8A6D3B" : "none"} color={i <= califEstrellas ? "#8A6D3B" : C.border} />
                </button>
              ))}
            </div>
            <textarea value={califComentario} onChange={e => setCalifComentario(e.target.value)} rows={2}
              placeholder="Contá algo (opcional): qué tal la atención, la cochera, los precios…"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, background: C.surface, resize: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setCalificando(null)} style={{ flex: 1, padding: "12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
              <button onClick={confirmarVisita} style={{ flex: 2, padding: "12px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>Confirmar y calificar</button>
            </div>
          </div>
        </div>
      )}

      {/* form recomendar */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background: C.card, borderRadius: "0 0 20px 20px", width: "100%", maxWidth: 430, maxHeight: "85vh", overflowY: "auto", padding: "calc(var(--sat) + 16px) 20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Recomendar un {cfg.titulo}</h3>
              <button onClick={() => setShowForm(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} strokeWidth={2.5} />
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>Solo lugares donde estuviste de verdad — tu nombre va junto a la recomendación.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input value={form.nombre} onChange={e => setF("nombre", e.target.value)} placeholder="Nombre del lugar" style={inputS} />
              <div style={{ display: "flex", gap: 8 }}>
                <select value={form.tipo} onChange={e => setF("tipo", e.target.value)} style={{ ...inputS, flex: 1, background: C.card }}>
                  {cfg.tipos.map(t => <option key={t}>{t}</option>)}
                </select>
                <input value={form.ciudad} onChange={e => setF("ciudad", e.target.value)} placeholder="Ciudad / zona" style={{ ...inputS, flex: 1 }} />
              </div>
              <textarea value={form.descripcion} onChange={e => setF("descripcion", e.target.value)} rows={3}
                placeholder={cfg.placeholderDesc}
                style={{ ...inputS, resize: "none", fontFamily: "inherit" }} />
              <button onClick={() => setF("moto_segura", !form.moto_segura)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 10, background: form.moto_segura ? `${C.green}14` : C.surface, border: `1px solid ${form.moto_segura ? `${C.green}33` : C.border}`, cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, background: form.moto_segura ? "#2F7D4F" : C.card, border: `1.5px solid ${form.moto_segura ? "#2F7D4F" : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 800 }}>{form.moto_segura ? "✓" : ""}</div>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{cfg.flag}</span>
              </button>
              <input value={form.link} onChange={e => setF("link", e.target.value)} placeholder="Link (web, Booking, Instagram) — opcional" style={inputS} />
              <input value={form.telefono} onChange={e => setF("telefono", e.target.value)} placeholder="WhatsApp del lugar (con código de país) — opcional" style={inputS} />
              <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: form.foto ? 0 : "14px", borderRadius: 10, border: `1.5px dashed ${C.border}`, background: C.surface, cursor: "pointer", overflow: "hidden" }}>
                {form.foto ? <img loading="lazy" decoding="async" src={form.foto} alt="" style={{ width: "100%", height: 120, objectFit: "cover" }} />
                  : <><Camera size={16} strokeWidth={2} color={C.muted} /><span style={{ fontSize: 13, color: C.muted }}>{subiendoFoto ? "Subiendo…" : "Foto del lugar (opcional)"}</span></>}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { subirFoto(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <button onClick={guardar} disabled={guardando}
              style={{ width: "100%", marginTop: 14, padding: "14px", borderRadius: 12, background: C.text, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", opacity: guardando ? 0.7 : 1 }}>
              {guardando ? "Guardando…" : "Publicar recomendación"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Mapa de servicios: todos los negocios con coordenadas, en pins como los eventos
const MapaServicios = ({ items, onAbrir }) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markersRef = useRef([]);
  useEffect(() => {
    let vivo = true;
    loadGoogleMaps().then(() => {
      if (!vivo || !mapRef.current || !window.google) return;
      if (!mapInst.current) {
        mapInst.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: -34.6037, lng: -58.3816 }, zoom: 5, disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
        });
      }
      markersRef.current.forEach(mk => mk.setMap(null));
      markersRef.current = [];
      const bounds = new window.google.maps.LatLngBounds();
      // Etiquetas con nombre SOLO con zoom cerca — a nivel país se pisaban todas
      const labelDe = (n) => ({ text: n.nombre.length > 16 ? n.nombre.slice(0, 15) + "…" : n.nombre, color: C.text, fontSize: "10.5px", fontWeight: "700" });
      const aplicarLabels = () => {
        const cerca = (mapInst.current.getZoom() || 5) >= 9;
        markersRef.current.forEach(mk => mk.setLabel(cerca ? labelDe(mk._negocio) : null));
      };
      items.filter(n => n.lat && n.lng).forEach(n => {
        const pos = { lat: Number(n.lat), lng: Number(n.lng) };
        bounds.extend(pos);
        const mk = new window.google.maps.Marker({
          position: pos, map: mapInst.current, title: n.nombre,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5, labelOrigin: new window.google.maps.Point(0, -2.4) },
        });
        mk._negocio = n;
        mk.addListener("click", () => onAbrir(n));
        markersRef.current.push(mk);
      });
      if (!bounds.isEmpty()) mapInst.current.fitBounds(bounds, 46);
      aplicarLabels();
      if (!mapInst.current._zoomListener) {
        mapInst.current._zoomListener = mapInst.current.addListener("zoom_changed", aplicarLabels);
      } else {
        window.google.maps.event.removeListener(mapInst.current._zoomListener);
        mapInst.current._zoomListener = mapInst.current.addListener("zoom_changed", aplicarLabels);
      }
    });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  return <div ref={mapRef} style={{ width: "100%", height: 440, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}`, background: C.surface }} />;
};

// ── ALQUILER DE MOTOS ENTRE RIDERS: APAGADO A PROPÓSITO ───────────────────
// Está construido y funciona (publicar desde el garage eligiendo "La alquilo",
// solapa propia en Mercado, precio por día/finde/semana, zona y requisitos).
// Queda apagado hasta que Carlos hable con un productor de seguros y un abogado:
// si el que alquila no devuelve la moto, para el seguro NO es robo — hubo entrega
// voluntaria — y las pólizas particulares suelen excluir la cesión onerosa a
// terceros. O sea, el dueño hoy no estaría cubierto por nada.
// Para reactivarlo alcanza con poner esto en true. Lo que falta antes:
//   1) póliza o cobertura contratable (hablar con un broker: Planner ya está en Servicios)
//   2) comprobante de entrega firmado por ambos con nombre y DNI (se reusa la
//      infraestructura del deslinde de eventos, que ya captura eso)
//   3) las calificaciones cruzadas — el SQL está escrito, sin correr (15-ago-2026)
const ALQUILER_P2P_ACTIVO = false;

// ── CATEGORÍA DE NEGOCIO: del id del formulario al valor que guarda la base ──
// El formulario de alta manda el id en minúscula ("taller"), pero la app filtra
// las pestañas con el string exacto ("Taller"). Un negocio dado de alta desde la
// app quedaba fuera de su pestaña Y su dueño no veía la agenda de turnos, que es
// justo lo que le ofrecemos. Todo lo que se guarde pasa por acá.
const CATEGORIA_NEGOCIO = {
  taller: "Taller", concesionario: "Concesionario", gromeria: "Gomeria",
  alquiler: "Alquiler", accesorios: "Accesorios", grua: "Grua",
  seguro: "Seguro", gestoria: "Gestoria", marca: "Marca",
  motoescuela: "Motoescuela", pista: "Pista", instructor: "Motoescuela",
  // Restaurantes, paradores y hospedajes también pueden tener ficha de negocio (dueño,
  // Mercado Pago, cobrar un evento): hasta el 24-sep-2026 solo existían como lugares
  // recomendados por la comunidad y un restaurante no podía registrarse (caso O'Brien).
  restaurante: "Restaurante", comida: "Restaurante", parador: "Restaurante", hospedaje: "Hospedaje", hotel: "Hospedaje",
};
const normalizarCategoria = (c) => CATEGORIA_NEGOCIO[String(c || "").trim().toLowerCase()] || c;

// ── REPORTAR UNA FICHA DESACTUALIZADA ─────────────────────────────────────
// El directorio lo mantiene la comunidad: cualquiera puede avisar que un dato
// ya no corre. "Cerró" y "está duplicado" bajan la ficha a standby de una; el
// resto necesita dos avisos de personas distintas (el trigger de la base lo
// resuelve). Bajar un taller que existe porque alguien tipeó mal un número
// sería peor que mostrar un dato viejo un día más.
const MOTIVOS_REPORTE = [
  { id: "cerrado", label: "Cerró o ya no existe" },
  { id: "telefono", label: "El teléfono no funciona" },
  { id: "direccion", label: "La dirección está mal" },
  { id: "horarios", label: "Cambiaron los horarios" },
  { id: "precios", label: "Los precios están viejos" },
  { id: "duplicado", label: "Está cargado dos veces" },
  { id: "otro", label: "Otra cosa" },
];

const ReportarFicha = ({ negocio }) => {
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [detalle, setDetalle] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const cerrar = () => { setAbierto(false); setMotivo(""); setDetalle(""); setEnviado(false); };

  const enviar = async () => {
    if (!motivo || enviando) return;
    setEnviando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { alert("Para reportar una ficha necesitás iniciar sesión."); setEnviando(false); return; }
      const { error } = await supabase.from("negocio_reportes").insert({
        negocio_id: negocio.id,
        user_id: user.id,
        motivo,
        detalle: detalle.trim() || null,
      });
      if (error) throw error;
      setEnviado(true);
    } catch (e) {
      alert("No pudimos enviar el reporte. Probá de nuevo en un rato.");
    }
    setEnviando(false);
  };

  return (
    <>
      <button onClick={() => setAbierto(true)}
        style={{ width: "100%", marginTop: 18, padding: "11px 0", borderRadius: 10, background: "transparent", color: C.muted, fontSize: 12.5, fontWeight: 600, border: `1px solid ${C.border}`, cursor: "pointer" }}>
        ¿Algún dato está desactualizado? Avisanos
      </button>

      {abierto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && cerrar()}>
          <div style={{ width: "100%", maxWidth: 430, background: C.card, borderRadius: "20px 20px 0 0", padding: "22px 20px calc(28px + var(--sab))", maxHeight: "88vh", overflowY: "auto" }}>
            {enviado ? (
              <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>🙏</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 8 }}>Gracias por avisar</div>
                <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                  Lo vamos a verificar. Si hace falta, la ficha queda en revisión hasta que confirmemos los datos.
                </p>
                <button onClick={cerrar}
                  style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: C.accent, color: "#fff", fontSize: 14.5, fontWeight: 700, border: "none", cursor: "pointer" }}>
                  Listo
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 16.5, fontWeight: 800, color: C.text, letterSpacing: "-0.015em" }}>Reportar esta ficha</div>
                    <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{negocio.nombre}</div>
                  </div>
                  <button onClick={cerrar} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                    <X size={14} strokeWidth={2.5} color={C.muted} />
                  </button>
                </div>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: "10px 0 16px" }}>
                  Este mapa lo mantenemos entre todos. Contanos qué está mal y lo revisamos.
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 16 }}>
                  {MOTIVOS_REPORTE.map(m => (
                    <button key={m.id} onClick={() => setMotivo(m.id)}
                      style={{ width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 11, cursor: "pointer",
                        background: motivo === m.id ? C.text : C.surface,
                        color: motivo === m.id ? "#fff" : C.text,
                        border: `1px solid ${motivo === m.id ? C.text : C.border}`,
                        fontSize: 13.5, fontWeight: motivo === m.id ? 700 : 500 }}>
                      {m.label}
                    </button>
                  ))}
                </div>

                <textarea value={detalle} onChange={e => setDetalle(e.target.value)}
                  placeholder="Si sabés el dato correcto, escribilo acá (opcional)"
                  style={{ width: "100%", minHeight: 74, padding: "12px 14px", borderRadius: 11, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13.5, color: C.text, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 16 }} />

                <button onClick={enviar} disabled={!motivo || enviando}
                  style={{ width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                    background: motivo ? C.accent : C.border, color: motivo ? "#fff" : C.muted,
                    fontSize: 14.5, fontWeight: 700, cursor: motivo && !enviando ? "pointer" : "default" }}>
                  {enviando ? "Enviando…" : "Enviar reporte"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ── AVISO AL DUEÑO CUANDO SU FICHA ESTÁ EN STANDBY ────────────────────────
// La ficha deja de mostrarse al resto, pero el dueño la sigue viendo y editando:
// acá le explicamos por qué está abajo y qué le reportaron, para que lo corrija.
const AvisoFichaEnRevision = ({ negocio }) => {
  const [motivos, setMotivos] = useState([]);

  useEffect(() => {
    if (!negocio || negocio.estado !== "standby") return;
    supabase.from("negocio_reportes").select("motivo, detalle")
      .eq("negocio_id", negocio.id).eq("estado", "pendiente")
      .then(({ data }) => setMotivos(data || []), () => {});
  }, [negocio]);

  if (!negocio || negocio.estado !== "standby") return null;

  return (
    <div style={{ background: "#FDECEA", border: `1px solid ${C.red}33`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: C.red, marginBottom: 6 }}>Tu ficha está pausada</div>
      <p style={{ fontSize: 12.5, color: C.text, lineHeight: 1.6, margin: 0 }}>
        Riders de la comunidad nos avisaron que algún dato no estaría al día, así que la sacamos del mapa hasta verificarla.
        Revisá y corregí lo que haga falta, y escribinos para que la volvamos a publicar.
      </p>
      {motivos.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.red}22` }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5 }}>Qué nos reportaron</div>
          {motivos.map((m, i) => (
            <div key={i} style={{ fontSize: 12.5, color: C.text, lineHeight: 1.55 }}>
              · {(MOTIVOS_REPORTE.find(x => x.id === m.motivo) || {}).label || m.motivo}
              {m.detalle ? ` — "${m.detalle}"` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── RUTAS REPORTADAS (panel del admin) ────────────────────────────────────
// A propósito no baja ninguna ruta sola: acá se lee el reporte, se abre la ruta
// si hace falta, y se decide. Eliminarla se sigue haciendo desde su detalle.
const ReportesRutasAdmin = () => {
  const [reportes, setReportes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(null);

  const cargar = async () => {
    const { data } = await supabase
      .from("ruta_reportes")
      .select("*, rutas(nombre, username, km, diff)")
      .eq("estado", "pendiente")
      .order("created_at", { ascending: false });
    setReportes(data || []);
    setCargando(false);
  };
  useEffect(() => { cargar(); }, []);

  const resolver = async (id) => {
    setTrabajando(id);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("ruta_reportes")
      .update({ estado: "resuelto", resuelto_at: new Date().toISOString(), resuelto_por: user?.id || null })
      .eq("id", id);
    if (error) alert("No se pudo marcar como visto.");
    else await cargar();
    setTrabajando(null);
  };

  if (cargando || reportes.length === 0) return null;

  const etiqueta = (id) => (MOTIVOS_REPORTE_RUTA.find(m => m.id === id) || {}).label || id;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.green}33`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.green, marginBottom: 10 }}>
        Rutas reportadas ({reportes.length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {reportes.map(rep => {
          const ru = rep.rutas || {};
          return (
            <div key={rep.id} style={{ background: C.surface, borderRadius: 10, padding: 11 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{ru.nombre || "(ruta borrada)"}</div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                por @{ru.username || "—"}{ru.km ? ` · ${ru.km} km` : ""}{ru.diff ? ` · ${ru.diff}` : ""}
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
                · <strong style={{ color: C.text, fontWeight: 600 }}>{etiqueta(rep.motivo)}</strong>
                {rep.detalle ? ` — "${rep.detalle}"` : ""}
              </div>
              <button onClick={() => resolver(rep.id)} disabled={trabajando === rep.id}
                style={{ marginTop: 9, width: "100%", padding: "8px 0", borderRadius: 9, background: "#276749", color: "#fff", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
                Lo miré, marcar como visto
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 9, lineHeight: 1.5 }}>
        Ninguna ruta se baja sola por un reporte. Si hay que eliminarla, se hace desde su detalle en Rutas.
      </div>
    </div>
  );
};

// ── REPORTES PENDIENTES (panel del admin) ─────────────────────────────────
// Agrupa por ficha: lo que importa es "este negocio tiene avisos", no la lista
// suelta de reportes. Desde acá se republica o se confirma que sigue abajo.
const ReportesPendientesAdmin = () => {
  const [reportes, setReportes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(null);

  const cargar = async () => {
    const { data } = await supabase
      .from("negocio_reportes")
      .select("*, negocios(nombre, ciudad, categoria, estado)")
      .eq("estado", "pendiente")
      .order("created_at", { ascending: false });
    setReportes(data || []);
    setCargando(false);
  };
  useEffect(() => { cargar(); }, []);

  const resolver = async (negocioId, republicar) => {
    setTrabajando(negocioId);
    try {
      const { data, error } = await supabase.rpc("negocio_resolver_reportes", {
        p_negocio_id: negocioId, p_republicar: republicar,
      });
      if (error || (data && data.error)) throw error || new Error(data.error);
      await cargar();
    } catch (e) {
      alert("No se pudo resolver. Revisá que tu cuenta siga con permisos de admin.");
    }
    setTrabajando(null);
  };

  if (cargando || reportes.length === 0) return null;

  // Un bloque por ficha reportada
  const porNegocio = reportes.reduce((acc, r) => {
    (acc[r.negocio_id] = acc[r.negocio_id] || []).push(r);
    return acc;
  }, {});
  const etiqueta = (id) => (MOTIVOS_REPORTE.find(m => m.id === id) || {}).label || id;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.green}33`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.green, marginBottom: 10 }}>
        Fichas reportadas ({Object.keys(porNegocio).length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {Object.entries(porNegocio).map(([negocioId, lista]) => {
          const n = lista[0].negocios || {};
          const enStandby = n.estado === "standby";
          return (
            <div key={negocioId} style={{ background: C.surface, borderRadius: 10, padding: 11 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.nombre || "(ficha borrada)"}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{n.categoria} · {n.ciudad}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 100, flexShrink: 0,
                  background: enStandby ? "#FDECEA" : `${C.green}14`, color: enStandby ? C.red : "#276749" }}>
                  {enStandby ? "No se muestra" : "Visible"}
                </span>
              </div>

              {lista.map(r => (
                <div key={r.id} style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, paddingLeft: 2 }}>
                  · <strong style={{ color: C.text, fontWeight: 600 }}>{etiqueta(r.motivo)}</strong>
                  {r.detalle ? ` — "${r.detalle}"` : ""}
                </div>
              ))}

              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                <button onClick={() => resolver(negocioId, true)} disabled={trabajando === negocioId}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 9, background: "#276749", color: "#fff", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
                  Está bien, republicar
                </button>
                <button onClick={() => resolver(negocioId, false)} disabled={trabajando === negocioId}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 9, background: C.card, color: C.text, fontSize: 12, fontWeight: 700, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                  Confirmar y dejar abajo
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PitStop = ({ onAI, userPosGlobal, setUserPosGlobal, onRegistro }) => {
  // Si vino por link (?pitstop=tramites), abrir esa sección directo
  const [subTab, setSubTab] = useState(() => {
    const t = typeof window !== "undefined" ? window.olbosPitStopTab : null;
    if (t) { window.olbosPitStopTab = null; return t; }
    return "todos"; // la vista unificada es la puerta de entrada (feedback Cata 13-ago)
  });
  useEffect(() => { registrarSeccion("pitstop:" + subTab); }, [subTab]); // tablero de estadísticas
  const [resenasResumen, setResenasResumen] = useState({}); // { negocio_id: {prom, n} }
  const [filtroCatTodos, setFiltroCatTodos] = useState("Todos"); // chip de rubro/etiqueta en la vista unificada
  const [vistaTodos, setVistaTodos] = useState("lista"); // 'lista' | 'mapa' en la vista unificada
  const [showFiltrosTodos, setShowFiltrosTodos] = useState(false); // filtros colapsados, como en Eventos
  const cargarResenasResumen = async () => {
    const { data: raw, error } = await supabase.rpc("get_resenas_resumen");
    if (error) return; // SQL sin correr todavía
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (d && typeof d === "object") setResenasResumen(d);
  };
  useEffect(() => { cargarResenasResumen(); }, []);
  const [selTaller, setSelTaller] = useState(null);
  const [selMarca, setSelMarca] = useState(null);
  const [selConce, setSelConce] = useState(null);
  const [selPubli, setSelPubli] = useState(null);
  const [selAlquiler, setSelAlquiler] = useState(null);
  const [filterTaller, setFilterTaller] = useState("Todos");
  const [filterMarca, setFilterMarca] = useState("Todas");
  const [filterConce, setFilterConce] = useState("Todos");
  const [filterPubli, setFilterPubli] = useState("todo");
  const [filterAlquiler, setFilterAlquiler] = useState("Todas");
  const [showPublicarVenta, setShowPublicarVenta] = useState(false);
  const [filtroCerca, setFiltroCerca] = useState(false);
  const [showMapaTalleres, setShowMapaTalleres] = useState(false);
  const mapTalleresRef = useRef(null);
  const mapTalleresInstanceRef = useRef(null);

  // Usar la ubicación global (persiste entre tabs)
  const userPos = userPosGlobal || null;
  const setUserPos = setUserPosGlobal || (() => {});
  // Si la app todavía no tiene la posición (la pide al arrancar, en silencio), se vuelve
  // a intentar acá: con ella la lista sale ordenada por cercanía y con "a 3 km"
  useEffect(() => {
    if (userPos) return;
    obtenerPosicion({ timeout: 8000, enableHighAccuracy: false, maximumAge: 300000 }).then(
      p => { if (p && p.lat) setUserPos({ lat: p.lat, lng: p.lng }); },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función de distancia Haversine — debe estar antes de usarse
  const distancia = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Motos reales en venta de Supabase
  const [motosEnVenta, setMotosEnVenta] = useState([]);
  const [ventaSel, setVentaSel] = useState(null); // ficha completa de una moto en venta (historia clínica)
  const [yoVentaId, setYoVentaId] = useState(null);
  const [editandoAviso, setEditandoAviso] = useState(null);
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setYoVentaId(data?.user?.id || null), () => {}); }, []);
  // Avisos de los locales que manejo (Mis locales): también los edito y los retiro
  const [misCuentasVentaIds, setMisCuentasVentaIds] = useState([]);
  useEffect(() => {
    supabase.rpc("mis_cuentas_delegadas").then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setMisCuentasVentaIds(Array.isArray(d) ? d.map(c => c.id) : []);
    }, () => {});
  }, []);
  const eliminarAviso = async (v) => {
    if (!window.confirm(`¿Eliminar el aviso "${tituloVenta(v)}"? Deja de verse en Compra-Venta.`)) return;
    const { error } = await supabase.from("motos_venta").update({ estado: "retirada" }).eq("id", v.id);
    if (error) { alert("No se pudo eliminar: " + error.message); return; }
    setMotosEnVenta(prev => prev.filter(x => x.id !== v.id));
  };
  // Publicaciones destacadas (slot "moto" pago): van primeras con etiqueta
  const [ventasDestacadas, setVentasDestacadas] = useState({});
  useEffect(() => {
    cargarAnunciosSlot("moto").then(l => { const m = {}; (l || []).forEach(a => { if (a.venta_id) m[a.venta_id] = true; }); setVentasDestacadas(m); });
  }, []);
  const [negocioSel, setNegocioSel] = useState(null);
  // Reclamo automático de una ficha de scouting: el dueño pide un código y lo manda por
  // WhatsApp DESDE el número del negocio; el robot (o el admin en un toque) compara el
  // número que escribió con el de la ficha y la pasa a su nombre. Sin ida y vuelta.
  const [reclamo, setReclamo] = useState(null); // { negocioId, codigo }
  const reclamarNegocio = async (n) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { alert("Para reclamar tu negocio primero creá tu cuenta en Olbos (tarda un minuto). Después volvé a esta ficha y tocá «Es mi negocio»."); return; }
    const { data: raw, error } = await supabase.rpc("crear_reclamo_negocio", { p_negocio: n.id });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || !r?.codigo) { alert("No se pudo iniciar el reclamo." + (error?.message ? "\n" + error.message : "")); return; }
    setReclamo({ negocioId: n.id, codigo: r.codigo });
    window.open(`https://wa.me/${WA_OLBOS_AUTOMATICO}?text=${encodeURIComponent(`OLBOS-VERIFICA ${r.codigo}\nSoy de "${n.nombre}" y quiero administrar mi ficha en Olbos.`)}`, "_blank", "noopener,noreferrer");
  };
  // Atrás cierra la ficha del negocio, no la app
  useBackGuard(!!negocioSel, () => setNegocioSel(null));
  // Abrir una ficha cuenta como visita: es el número que le sirve al comercio
  useEffect(() => {
    if (!negocioSel?.id) return;
    supabase.rpc("registrar_vista_negocio", { p_negocio: negocioSel.id }).then(() => {}, () => {});
  }, [negocioSel?.id]);
  // Cuál es MI ficha, para mostrarme solo a mí las visitas que tuvo
  const [miNegocioId, setMiNegocioId] = useState(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from("negocios").select("id").eq("user_id", user.id).maybeSingle()
        .then(({ data }) => setMiNegocioId(data?.id || null), () => {});
    }, () => {});
  }, []);
  const [showCrearMarca, setShowCrearMarca] = useState(null); // userId del que solicita su espacio de marca
  // Negocios reales de Supabase
  const [negociosDB, setNegociosDB] = useState([]);
  useEffect(() => {
    supabase.from("motos_venta").select("*").eq("estado", "activa").order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setMotosEnVenta(data); });
  }, []);
  // Tarjeta del carrusel de la portada → abre la ficha de esa moto en venta
  useEffect(() => {
    const abrir = async () => {
      const id = window.olbosAbrirVentaId;
      if (!id) return;
      window.olbosAbrirVentaId = null;
      const { data } = await supabase.from("motos_venta").select("*").eq("id", id).maybeSingle();
      if (data) setVentaSel(data);
    };
    abrir();
    window.addEventListener("olbos-abrir-venta", abrir);
    return () => window.removeEventListener("olbos-abrir-venta", abrir);
  }, []);
  useEffect(() => {
    supabase.from("negocios").select("*").eq("estado", "verificado").order("created_at", { ascending: false })
      .then(({ data }) => {
        if (!data) return;
        setNegociosDB(data);
        const id = window.olbosAbrirNegocioId;
        if (id) { window.olbosAbrirNegocioId = null; const n = data.find(x => x.id === id); if (n) setNegocioSel(n); }
      });
  }, []);

  // Helpers para categorizar negocios de Supabase
  const talleresDB = negociosDB.filter(n => n.categoria === "Taller");
  // Restaurantes y hospedajes con ficha propia (dueño, MP, cobros): van arriba de los
  // lugares recomendados por la comunidad en sus pestañas
  const negociosRubro = (rubro) => {
    const lista = negociosDB.filter(n => {
      const c = String(n.categoria || "").toLowerCase();
      return rubro === "comida" ? (c.startsWith("restaurante") || c.startsWith("comida") || c.startsWith("parador")) : (c.startsWith("hospedaje") || c.startsWith("hotel"));
    });
    if (!lista.length) return null;
    return (
      <div style={{ padding: "14px 18px 0" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Con ficha en Olbos · reservan y cobran por la app</div>
        {lista.map(n => {
          const res = resenasResumen?.[n.id];
          return (
            <button key={n.id} onClick={() => setNegocioSel(n)}
              style={{ width: "100%", display: "flex", gap: 12, alignItems: "center", padding: "11px 13px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, marginBottom: 8, cursor: "pointer", textAlign: "left", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, fontSize: 20 }}>
                {n.logo_url ? <img loading="lazy" decoding="async" src={n.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (rubro === "comida" ? "🍽️" : "🛏️")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{n.nombre}</span>
                  <CheckCircle size={13} strokeWidth={2.5} color={C.blue} style={{ flexShrink: 0 }} />
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {n.ciudad || n.direccion || ""}{res ? ` · ★ ${Number(res.prom).toFixed(1)} (${res.n})` : ""}{n.beneficio_activo ? " · 🎁 beneficio" : ""}
                </div>
              </div>
              <ChevronRight size={15} color={C.muted} style={{ flexShrink: 0 }} />
            </button>
          );
        })}
      </div>
    );
  };
  const concesionariosDB = negociosDB.filter(n => n.categoria === "Concesionario");
  // Seguros y gestorías tienen pestaña propia. El match es tolerante porque conviven
  // categorías capitalizadas ("Taller") con los ids del formulario ("seguro").
  // Seguros y gestores (categoría "Gestor" del registro): la lista exacta dejaba afuera a los gestores (Pablo Altamirano, 04-sep)
  const esTramite = (n) => { const c = String(n.categoria || "").toLowerCase(); return c.startsWith("seguro") || c.startsWith("gestor"); };
  // Promedio y cantidad de reseñas de todos los negocios, en una sola consulta
  const tramitesDB = negociosDB.filter(esTramite);
  const negociosGeneral = negociosDB.filter(n => !["Taller","Concesionario"].includes(n.categoria) && !esTramite(n));

  // Ordenar por distancia si tenemos ubicación del usuario
  const ordenarPorCercania = (lista) => {
    if (!userPos || !userPos.lat) return lista;
    return [...lista].sort((a, b) => {
      if (!a.lat || !a.lng) return 1;
      if (!b.lat || !b.lng) return -1;
      const dA = distancia(userPos.lat, userPos.lng, a.lat, a.lng);
      const dB = distancia(userPos.lat, userPos.lng, b.lat, b.lng);
      return dA - dB;
    });
  };

  // Filtros por chip (Gomería, Traslado, marca…) sobre los negocios REALES de la base: antes solo
  // filtraban los arrays demo y la lista real quedaba igual (pedido de @MatiAguilar_26, 22-oct).
  // Gomería y Traslado también traen a los negocios cargados con ESA categoría (no solo talleres).
  const normFiltro = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const textoNegocio = (n) => normFiltro([n.nombre, n.descripcion, n.especialidades, ...(Array.isArray(n.tags) ? n.tags : [])].filter(Boolean).join(" "));
  const catNegocio = (n) => normFiltro(n.categoria);
  const coincideFiltro = (n, f, base) => {
    if (f === "Todos") return true;
    if (f === "Destacados") return !!n.destacado;
    const txt = textoNegocio(n), cat = catNegocio(n);
    if (f.includes("Gomer")) return cat.startsWith("gomer") || cat.startsWith("gromer") || /gomer|neumatic|cubierta/.test(txt);
    if (f.includes("Traslado")) return cat.startsWith("grua") || /traslado|grua|acarreo|flete/.test(txt);
    if (f.includes("24hs")) return /24 ?hs|24 ?horas|urgencia/.test(txt + " " + normFiltro(n.horarios));
    return txt.includes(normFiltro(f));
  };
  const filtrarNegocios = (lista, f) => lista.filter(n => coincideFiltro(n, f));
  const talleresBase = (filterTaller.includes("Gomer") || filterTaller.includes("Traslado"))
    ? negociosDB.filter(n => n.categoria === "Taller" || coincideFiltro(n, filterTaller))
    : talleresDB;
  const talleresFiltrados = filterTaller === "Todos" ? talleresBase : filtrarNegocios(talleresBase, filterTaller);
  const concesionariosFiltrados = filterConce === "Todos" ? concesionariosDB : (filterConce === "Multi-marca"
    ? concesionariosDB.filter(n => /multi ?marca|todas las marcas|varias marcas/.test(textoNegocio(n)))
    : filtrarNegocios(concesionariosDB, filterConce));
  const talleresOrdenados = ordenarPorCercania(talleresFiltrados);
  const concesionariosOrdenados = ordenarPorCercania(concesionariosFiltrados);

  // Con filtroCerca: mostrar los dentro de 50km, o si no hay ninguno, los 5 más cercanos
  const talleresVisibles = (() => {
    if (!filtroCerca || !userPos) return talleresOrdenados;
    const cercanos = talleresOrdenados.filter(n => n.lat && n.lng && distancia(userPos.lat, userPos.lng, n.lat, n.lng) <= 50);
    return cercanos.length > 0 ? cercanos : talleresOrdenados.slice(0, 5);
  })();

  const concesionariosVisibles = (() => {
    if (!filtroCerca || !userPos) return concesionariosOrdenados;
    const cercanos = concesionariosOrdenados.filter(n => n.lat && n.lng && distancia(userPos.lat, userPos.lng, n.lat, n.lng) <= 50);
    return cercanos.length > 0 ? cercanos : concesionariosOrdenados.slice(0, 5);
  })();

  const esperandoUbicacion = filtroCerca && !userPos;

  const activarCerca = () => {
    if (filtroCerca) { setFiltroCerca(false); return; }
    if (userPos) { setFiltroCerca(true); return; }
    if (!navigator.geolocation) {
      alert("Tu browser no soporta geolocalización.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setFiltroCerca(true);
      },
      (err) => {
        if (err.code === 1) {
          alert("Permiso de ubicación denegado. En Chrome, tocá el candado 🔒 en la barra de dirección → Ubicación → Permitir → Recargá la página.");
        } else {
          alert("No se pudo obtener tu ubicación. Intentá de nuevo.");
        }
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };
  const [searchQ, setSearchQ] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [followed, setFollowed] = useState(false);

  // búsqueda global cross-tab
  // Buscador Pit Stop: primero los negocios REALES de la base (antes solo miraba los
  // arrays demo), sin acentos y exigiendo todas las palabras en nombre/ciudad/rubro/etc.
  const normPS = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const tokensPS = normPS(searchQ).trim().split(/\s+/).filter(Boolean);
  const matchNegocio = (n) => {
    const heno = normPS([n.nombre, n.categoria, n.ciudad, n.direccion, n.descripcion, n.especialidades].filter(Boolean).join(" "));
    return tokensPS.every(t => heno.includes(t));
  };
  const subTabDeNegocio = (n) => {
    const c = String(n.categoria || "").toLowerCase();
    if (c.startsWith("taller")) return "talleres";
    if (c.startsWith("concesionario")) return "concesionarios";
    if (c.startsWith("seguro") || c.startsWith("gestor")) return "tramites";
    if (c.startsWith("alquiler")) return "alquiler";
    if (c.startsWith("marca")) return "marcas";
    if (c.startsWith("restaurante") || c.startsWith("comida") || c.startsWith("parador")) return "comida";
    if (c.startsWith("hospedaje") || c.startsWith("hotel")) return "hospedajes";
    return "talleres";
  };
  const searchResults = searchQ.trim().length > 1 ? [
    ...negociosDB.filter(matchNegocio).map(n => ({ tipo: "negocio", item: n })),
    ...TALLERES.filter(t => [t.nombre, t.zona, ...t.especialidad].some(v => v.toLowerCase().includes(searchQ.toLowerCase()))).map(t => ({ tipo: "taller", item: t, idx: TALLERES.indexOf(t) })),
    ...MARCAS.filter(m => [m.nombre, m.pais, m.tipo, ...m.modelos_destacados].some(v => v.toLowerCase().includes(searchQ.toLowerCase()))).map(m => ({ tipo: "marca", item: m, idx: MARCAS.indexOf(m) })),
    ...CONCESIONARIOS.filter(c => [c.nombre, c.zona, ...c.marcas, ...c.servicios].some(v => v.toLowerCase().includes(searchQ.toLowerCase()))).map(c => ({ tipo: "concesionario", item: c, idx: CONCESIONARIOS.indexOf(c) })),
    ...COMPRAVENTA.filter(p => [p.titulo, p.ubicacion, p.tipo, ...p.tags].some(v => v.toLowerCase().includes(searchQ.toLowerCase()))).map(p => ({ tipo: "aviso", item: p, idx: COMPRAVENTA.indexOf(p) })),
    ...ALQUILER.filter(a => [a.agencia, a.zona, ...a.motos.map(m => m.modelo)].some(v => v.toLowerCase().includes(searchQ.toLowerCase()))).map(a => ({ tipo: "alquiler", item: a, idx: ALQUILER.indexOf(a) })),
  ] : [];

  // ── FICHA MOTO EN VENTA (historia clínica completa) ─────────────────────
  if (ventaSel) return <FichaMotoVenta venta={ventaSel} onClose={() => setVentaSel(null)} />;
  if (editandoAviso) return <EditarAvisoModal aviso={editandoAviso} onClose={() => setEditandoAviso(null)} onGuardado={(n) => { setMotosEnVenta(prev => prev.map(x => x.id === n.id ? { ...x, ...n } : x)); setEditandoAviso(null); }} />;

  // ── DETALLE TALLER ──────────────────────────────────────────────────────
  // ── DETALLE NEGOCIO SUPABASE ──────────────────────────────────────────────
  if (negocioSel) {
    const n = negocioSel;
    return (
      <PageWrap>
        <div style={{ padding: "48px 18px 18px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <button onClick={() => setNegocioSel(null)} style={{ color: C.text, display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer" }}>
              <ChevronLeft size={26} strokeWidth={2} />
            </button>
            <div style={{ marginRight: 44 }}><BtnCompartir texto={`${n.categoria === "Taller" ? "🔧" : "🏪"} ${n.nombre}\n${n.ciudad}`} tipo="negocio" id={n.id} small /></div>
          </div>

          {/* logo + nombre */}
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: String(n.categoria || "").toLowerCase().startsWith("marca") ? C.card : C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
              {n.logo_url
                ? <img loading="lazy" decoding="async" src={n.logo_url} alt={n.nombre} style={{ width: "100%", height: "100%", objectFit: String(n.categoria || "").toLowerCase().startsWith("marca") ? "contain" : "cover", padding: String(n.categoria || "").toLowerCase().startsWith("marca") ? 6 : 0, boxSizing: "border-box" }} />
                : (n.categoria === "Taller" ? <Wrench size={28} strokeWidth={1.5} color={C.muted} /> : <ShoppingBag size={28} strokeWidth={1.5} color={C.muted} />)}
            </div>
            <div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                <h1 style={{ fontSize: 20, fontWeight: 900, color: C.text, letterSpacing: -0.3 }}>{n.nombre}</h1>
                {negocioVerificado(n) && <CheckCircle size={16} strokeWidth={2} color={C.blue} />}
              </div>
              <div style={{ fontSize: 13, color: C.muted }}>{n.categoria} · {n.ciudad}</div>
              {/* Concesionario oficial validado por la marca (la ficha es de la cuenta del local) */}
              {n.user_id && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}><SelloConcesionario userId={n.user_id} grande /></div>}
            </div>
          </div>

          {/* Qué significa el tilde, y el reclamo para las fichas cargadas por scouting.
              Legal: "verificado" es una afirmación nuestra; si no lo confirmó el dueño,
              se dice claro y se le da al negocio una salida en 48 h (reclamar o borrar). */}
          {negocioVerificado(n) ? (
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={12} strokeWidth={2.5} color={C.blue} /> Verificado: el negocio confirmó sus datos con Olbos.
            </div>
          ) : (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 12px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
                Ficha cargada por Olbos a partir de datos públicos. <strong style={{ color: C.text }}>Todavía no fue verificada por el negocio.</strong> ¿Es tuyo? Reclamalo gratis: se verifica solo con un WhatsApp desde el número del negocio. O pedí que lo saquemos.
              </div>
              {reclamo?.negocioId === n.id && (
                <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 10, padding: "9px 11px", marginBottom: 8, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                  <strong>Tu código: {reclamo.codigo}</strong><br />
                  Mandá el mensaje que se abrió <strong>desde el WhatsApp del negocio</strong>{n.whatsapp || n.telefono ? ` (el que termina en …${String(n.whatsapp || n.telefono).replace(/[^0-9]/g, "").slice(-4)})` : ""}. Se verifica solo y te contestamos por ahí mismo: la ficha queda a tu nombre y la administrás desde Más → Mi negocio.<br />
                  <span style={{ color: C.muted }}>Ese número es automático y solo entiende el código. Para hablar con una persona de Olbos, usá el botón de al lado.</span>
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => reclamarNegocio(n)}
                  style={{ flex: 1, padding: "9px 10px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  {reclamo?.negocioId === n.id ? "Reabrir WhatsApp" : "Es mi negocio"}
                </button>
                <button onClick={() => window.open(`https://wa.me/${WA_OLBOS_HUMANO}?text=${encodeURIComponent(`Hola! Soy de "${n.nombre}" (${n.ciudad || ""}). Pido que saquen la ficha de Servicios de Olbos o que corrijan estos datos: ... Ref: ${n.id}`)}`, "_blank", "noopener,noreferrer")}
                  style={{ flex: 1, padding: "9px 10px", borderRadius: 10, background: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  Hablar con Olbos
                </button>
              </div>
            </div>
          )}

          {/* etiquetas de servicio (Taller oficial, Test rides, circuitos, etc.) */}
          {Array.isArray(n.tags) && n.tags.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {n.tags.map(t => (
                <span key={t} style={{ fontSize: 11, fontWeight: 700, color: C.text, background: C.surface, border: `1px solid ${C.border}`, padding: "4px 10px", borderRadius: 100 }}>🏷 {t}</span>
              ))}
            </div>
          )}

          {/* descripción */}
          {n.descripcion && <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 14 }}>{n.descripcion}</p>}

          {/* espacio de marca: sus modelos del catálogo + sus eventos oficiales */}
          {String(n.categoria || "").toLowerCase().startsWith("marca") && (() => {
            const modelos = MOTOS_DB.filter(mo =>
              mo.marca.toLowerCase() === n.nombre.toLowerCase() ||
              n.nombre.toLowerCase().includes(mo.marca.toLowerCase())
            );
            return (<>
              {modelos.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Modelos</div>
                  <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
                    {modelos.map(mo => (
                      <div key={mo.id} style={{ flexShrink: 0, width: 148, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}`, background: C.card }}>
                        <div style={{ width: "100%", height: 86, background: `#111 url(${imgMoto(mo.img)}) center/cover` }} />
                        <div style={{ padding: "8px 10px 10px" }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.text, lineHeight: 1.2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{mo.modelo}</div>
                          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{fichaCorta(mo)}</div>
                          {mo.precio_ar && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#8A6D3B", marginTop: 2 }}>{mo.precio_ar}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <EventosDeMarca userId={n.user_id} nombreMarca={n.nombre} />
            </>);
          })()}

          {/* horarios */}
          {n.horarios && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, background: C.surface, padding: "10px 14px", borderRadius: 10 }}>
              <Clock size={15} strokeWidth={2} color={C.muted} />
              <span style={{ fontSize: 13, color: C.muted }}>{n.horarios}</span>
            </div>
          )}

          {/* turnos: talleres, motoescuelas, pistas y concesionarios (test rides) */}
          {/* Circuitos y locales: días, horarios, tarifas por vehículo y eventos que se hacen ahí */}
          {String(n.categoria || "").toLowerCase().startsWith("pista") && <CircuitoDelLugar negocio={n} />}
          <BeneficioFicha negocio={n} />
          <HorariosTarifasFicha negocio={n} />
          <TiendaFicha negocio={n} onVerVenta={(v) => setVentaSel(v)} />
          <EventosDelNegocio negocio={n} />
          {["taller", "motoescuela", "pista", "conces", "restaurante", "hospedaje"].some(p => String(n.categoria || "").toLowerCase().startsWith(p)) && <TurnosTallerFicha negocio={n} />}

          {/* botones de contacto (cada toque se cuenta para el tablero: contactos por negocio) */}
          <div onClick={(e) => registrarContactoNegocio(e, n.id)} style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {n.whatsapp && (
              <a href={`https://wa.me/${n.whatsapp}?text=Hola! Te contacto desde Olbos 🏍️`} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "12px 0", borderRadius: 12, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 14, textAlign: "center", textDecoration: "none" }}>
                💬 WhatsApp
              </a>
            )}
            {n.telefono && (
              <a href={`tel:${n.telefono}`}
                style={{ flex: 1, padding: "12px 0", borderRadius: 12, background: C.surface, color: C.text, fontWeight: 700, fontSize: 14, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                📞 Llamar
              </a>
            )}
          </div>

          {/* links */}
          <div onClick={(e) => registrarContactoNegocio(e, n.id)} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
            {n.web && (
              <a href={n.web.startsWith("http") ? n.web : `https://${n.web}`} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, color: C.blue, fontWeight: 600, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                🌐 Sitio web
              </a>
            )}
            {n.instagram && (
              <a href={`https://instagram.com/${n.instagram.replace("@", "")}`} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, color: "#E1306C", fontWeight: 600, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                📷 Instagram
              </a>
            )}
          </div>
        </div>

        {/* mapa */}
        <div style={{ padding: "18px 18px 100px" }}>
          {/* Un instructor no tiene local: mostrarle una dirección fija (o peor, un
              mapa con "cómo llegar") sería mentirle al rider. Se cuenta la zona donde
              trabaja, y las clases concretas salen en "Próximas clases", cada una con
              el lugar donde se da. */}
          {n.itinerante ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Dónde da clases</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: C.surface, borderRadius: 10, padding: "10px 13px" }}>
                <MapPin size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                  {String(n.zona_cobertura || "").trim() || "En distintos circuitos y lugares"}
                  {n.ciudad ? ` · Base en ${n.ciudad}` : ""}
                </span>
              </div>
            </div>
          ) : n.direccion && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Ubicación</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
                <MapPin size={14} strokeWidth={2} color={C.muted} />
                <span style={{ fontSize: 13, color: C.muted }}>{n.direccion}, {n.ciudad}</span>
              </div>
            </div>
          )}
          {/* Cómo llegar: Waze/Maps directo desde la ficha */}
          {!n.itinerante && ((n.lat && n.lng) || n.direccion) ? (() => {
            const q = n.lat && n.lng ? `${n.lat},${n.lng}` : encodeURIComponent(`${n.direccion || ""}, ${n.ciudad || ""}`);
            const urlMaps = `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`;
            const urlWaze = n.lat && n.lng ? `${WAZE_BASE}ll=${n.lat},${n.lng}&navigate=yes` : `${WAZE_BASE}q=${q}&navigate=yes`;
            return (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={() => (n.lat && n.lng) ? navegarConOlbos(n.lat, n.lng, n.nombre) : navegarConOlbosBusqueda(`${n.direccion || ""}, ${n.ciudad || ""}`)}
                  style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 13, textAlign: "center", border: "none", cursor: "pointer" }}>
                  🏍️ Olbos
                </button>
                <a href={urlMaps} target="_blank" rel="noreferrer"
                  style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 700, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                  🧭 Google Maps
                </a>
                <a href={urlWaze} target="_blank" rel="noreferrer"
                  style={{ flex: 1, padding: "11px 0", borderRadius: 10, background: C.surface, color: "#0099BB", fontWeight: 700, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                  Waze
                </a>
              </div>
            );
          })() : null}
          <MapaEmbed lat={n.lat} lng={n.lng} nombre={n.nombre} direccion={`${n.nombre}, ${n.direccion || ""}, ${n.ciudad}, Argentina`} />
          {/* Cuánta gente miró la ficha — solo para el dueño. Es el dato que le
              muestra que estar en Olbos le sirve. */}
          {miNegocioId === n.id && Number(n.vistas || 0) > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "11px 14px", marginTop: 16 }}>
              <TrendingUp size={16} strokeWidth={2} color={C.muted} />
              <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                {Number(n.vistas).toLocaleString("es-AR")} {Number(n.vistas) === 1 ? "rider vio" : "riders vieron"} tu ficha
              </span>
              <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>solo lo ves vos</span>
            </div>
          )}
          <ReportarFicha negocio={n} />
        </div>
      </PageWrap>
    );
  }

  if (selTaller !== null) {
    const t = TALLERES[selTaller];
    return (
      <PageWrap>
        <div style={{ padding: "var(--header-pt) 18px 18px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <button onClick={() => setSelTaller(null)} style={{ color: C.text, display: "flex", alignItems: "center" }}>
              <ChevronLeft size={26} strokeWidth={2} />
            </button>
            <div style={{ marginRight: 44 }}><BtnCompartir texto={`🔧 ${t.nombre}\n${t.zona} · ${t.especialidad?.join(", ")}`} tipo="negocio" id={String(t.id)} small /></div>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 56, height: 56, background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Wrench size={26} strokeWidth={1.5} color={C.muted} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>{t.nombre}</h1>
                {t.destacado && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.border}`, color: C.muted }}>Destacado</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <MapPin size={12} strokeWidth={2} color={C.muted} />
                <span style={{ fontSize: 12, color: C.muted }}>{t.zona}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Stars n={t.rating} size={13} />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t.rating}</span>
                <span style={{ fontSize: 12, color: C.muted }}>({t.reviews} reseñas)</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 80px" }}>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 20 }}>{t.desc}</p>

          {/* servicios disponibles */}
          {t.servicios && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Servicios</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { key: "gromeria", icon: "🛞", label: "Gomería" },
                  { key: "traslado", icon: "🚛", label: "Traslado / Grúa" },
                  { key: "domicilio", icon: "🏠", label: "A domicilio" },
                  { key: "urgencias24", icon: "🕐", label: "Urgencias 24hs" },
                ].map(s => (
                  <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, background: t.servicios[s.key] ? `${C.green}14` : C.surface, border: `1px solid ${t.servicios[s.key] ? C.green + "44" : C.border}`, opacity: t.servicios[s.key] ? 1 : 0.4 }}>
                    <span style={{ fontSize: 14 }}>{s.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.servicios[s.key] ? C.green : C.muted }}>{s.label}</span>
                    {t.servicios[s.key] && <CheckCircle size={12} strokeWidth={2.5} color={C.green} />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* horarios */}
          <div style={{ background: C.surface, borderRadius: 12, padding: "12px 16px", border: `1px solid ${C.border}`, marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <Clock size={18} strokeWidth={1.8} color={C.muted} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>Horarios de atención</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t.hrs}</div>
            </div>
          </div>

          {/* especialidad */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Especialidad</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {t.especialidad.map(e => <OTag key={e}>{e}</OTag>)}
            </div>
          </div>

          {/* botones de contacto */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <a href={`tel:${t.tel}`} style={{ flex: 1, padding: "13px 0", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" }}>
              <Phone size={16} strokeWidth={2} color={C.text} />
              <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Llamar</span>
            </a>
            {t.whatsapp && (
              <a href={`https://wa.me/${t.whatsapp}?text=Hola! Te contacto desde Olbos 🏍️`} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "13px 0", borderRadius: 12, background: "#25D36615", border: "1px solid #25D36633", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" }}>
                <span style={{ fontSize: 18 }}>💬</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#128C7E" }}>WhatsApp</span>
              </a>
            )}
          </div>

          {t.dir && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Cómo llegar</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <MapPin size={13} strokeWidth={2} color={C.muted} /> {t.dir}
              </div>
              <BotonesNavegacion direccion={t.dir} ciudad={t.zona} />
            </div>
          )}
        </div>
      </PageWrap>
    );
  }

  // ── DETALLE MARCA ────────────────────────────────────────────────────────
  if (selMarca !== null) {
    const m = MARCAS[selMarca];
    return (
      <PageWrap>
        <div style={{ padding: "var(--header-pt) 18px 18px", borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => setSelMarca(null)} style={{ color: C.text, display: "flex", alignItems: "center", marginBottom: 14 }}>
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ width: 60, height: 60, background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: C.text, letterSpacing: 1, flexShrink: 0 }}>{m.logo}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>{m.nombre}</h1>
                {negocioVerificado(m) && <CheckCircle size={18} strokeWidth={2} color={C.blue} />}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{m.pais} · {m.tipo}</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: C.muted }}>{m.seguidores.toLocaleString()} seguidores</span>
                <button onClick={() => setFollowed(f => !f)} style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: followed ? "transparent" : C.text, border: `1px solid ${C.text}`, color: followed ? C.text : "#fff", transition: "all .2s", cursor: "pointer" }}>
                  {followed ? "Siguiendo" : "Seguir"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 80px" }}>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 20 }}>{m.descripcion}</p>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Modelos destacados</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {m.modelos_destacados.map(mod => (
                <div key={mod} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
                  <Bike size={14} strokeWidth={1.8} color={C.muted} /> {mod}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Novedades oficiales</div>
            {m.novedades.map((n, i) => (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <OTag>{n.tipo}</OTag>
                  <span style={{ fontSize: 11, color: C.muted }}>{n.fecha}</span>
                </div>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>{n.titulo}</h4>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{n.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Contacto oficial</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[[Phone, m.contacto], [Globe, m.web], [Globe, m.instagram]].filter(([,v]) => v).map(([Icon, val]) => (
                <div key={val} style={{ background: C.surface, borderRadius: 10, padding: "10px 12px", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon size={13} strokeWidth={1.8} color={C.muted} />
                  <span style={{ fontSize: 12, color: C.text }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageWrap>
    );
  }

  // ── DETALLE CONCESIONARIO ─────────────────────────────────────────────────
  if (selConce !== null) {
    const c = CONCESIONARIOS[selConce];
    return (
      <PageWrap>
        <div style={{ padding: "var(--header-pt) 18px 18px", borderBottom: `1px solid ${C.border}` }}>
          <button onClick={() => setSelConce(null)} style={{ color: C.text, display: "flex", alignItems: "center", marginBottom: 14 }}>
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ width: 56, height: 56, background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ShoppingBag size={26} strokeWidth={1.5} color={C.muted} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>{c.nombre}</h1>
                {negocioVerificado(c) && <CheckCircle size={16} strokeWidth={2} color={C.blue} />}
                {c.destacado && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.border}`, color: C.muted }}>Destacado</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <MapPin size={12} strokeWidth={2} color={C.muted} />
                <span style={{ fontSize: 12, color: C.muted }}>{c.direccion}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Stars n={c.rating} size={12} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>{c.rating}</span>
                <span style={{ fontSize: 12, color: C.muted }}>({c.reviews} reseñas)</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 80px" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Marcas que representa</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {c.marcas.map(marca => <OTag key={marca}>{marca}</OTag>)}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[[Phone, "Teléfono", c.tel], [Clock, "Horarios", c.hrs]].map(([Icon, k, v]) => (
              <div key={k} style={{ background: C.surface, borderRadius: 12, padding: "12px 14px", border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <Icon size={13} strokeWidth={1.8} color={C.muted} />
                  <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Servicios</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {c.servicios.map(s => <OTag key={s}>{s}</OTag>)}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Ofertas y stock</div>
            {c.ofertas.map((o, i) => (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1, marginRight: 8 }}>{o.titulo}</h4>
                  <OTag>{o.badge}</OTag>
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 10 }}>{o.precio}</div>
                <Btn full small onClick={() => {}}>Consultar disponibilidad</Btn>
              </div>
            ))}
          </div>

          {c.novedades && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <Bell size={13} strokeWidth={2} color={C.muted} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Novedad</span>
              </div>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{c.novedades}</p>
            </div>
          )}

          <Btn full onClick={() => {}}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Phone size={16} strokeWidth={2} /> Llamar al concesionario
            </span>
          </Btn>
        </div>
      </PageWrap>
    );
  }

  // ── DETALLE PUBLICACIÓN ──────────────────────────────────────────────────
  if (selPubli !== null) {
    const p = COMPRAVENTA[selPubli];
    const tipoLabel = { moto: "Moto", accesorio: "Accesorio", indumentaria: "Indumentaria", repuesto: "Repuesto" };
    return (
      <PageWrap>
        {/* hero */}
        <div style={{ height: 260, position: "relative", background: C.accent, overflow: "hidden" }}>
          <img loading="lazy" decoding="async" src={p.img} alt={p.titulo} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.75 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.75) 0%, transparent 55%)" }} />
          <button onClick={() => setSelPubli(null)} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={20} strokeWidth={2} color="#fff" />
          </button>
          <div style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 16 }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: "rgba(0,0,0,.55)", color: "#fff", backdropFilter: "blur(4px)" }}>{tipoLabel[p.tipo]}</span>
          </div>
          <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: -0.3, marginBottom: 6 }}>{p.titulo}</h1>
            <div style={{ fontSize: 26, fontWeight: 900, color: "#fff" }}>
              {p.moneda} {p.precio.toLocaleString()}
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 100px" }}>

          {/* stats rápidos */}
          {p.tipo === "moto" && (
            <div style={{ display: "flex", gap: 12, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              {[["Año", p.año], ["Kilómetros", `${p.km?.toLocaleString()} km`], ["Ubicación", p.ubicacion]].map(([k, v]) => (
                <div key={k} style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {p.tipo !== "moto" && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <MapPin size={13} strokeWidth={2} color={C.muted} />
              <span style={{ fontSize: 13, color: C.muted }}>{p.ubicacion}</span>
            </div>
          )}

          {/* descripción */}
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 18 }}>{p.desc}</p>

          {/* tags */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {p.tags.map(t => <OTag key={t}>{t}</OTag>)}
          </div>

          {/* vendedor */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Vendedor</div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <img loading="lazy" decoding="async" src={p.vendedor.avatarUrl} alt={p.vendedor.user} style={{ width: 46, height: 46, borderRadius: "50%", objectFit: "cover" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>@{p.vendedor.user}</div>
                <div style={{ fontSize: 12, color: C.muted }}>Rider desde {p.vendedor.rider_desde}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{p.vendedor.ventas} venta{p.vendedor.ventas !== 1 ? "s" : ""} en Olbos</div>
              </div>
            </div>
          </div>

          {/* aviso */}
          <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <AlertTriangle size={14} strokeWidth={2} color="#B07D1A" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 12, color: "#8B6914", lineHeight: 1.6 }}>Olbos no interviene en la transacción. Coordiná el pago y la entrega directamente con el vendedor. Verificá el estado del vehículo antes de pagar.</p>
          </div>

          {/* CTA */}
          <Btn full onClick={() => {}}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <MessageCircle size={16} strokeWidth={2} /> Contactar por DM
            </span>
          </Btn>
          <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 8 }}>Publicado {p.fecha}</p>
        </div>
      </PageWrap>
    );
  }

  // ── LISTADO PRINCIPAL ────────────────────────────────────────────────────
  return (
    <PageWrap>
      {/* Banner destacado arriba de todo (como el carrusel de Eventos): negocios con
          plan destacado rotando; si no hay, el espacio se ofrece — es el slot vendible */}
      {(() => {
        const destacados = negociosDB.filter(n => n.plan && n.plan !== "gratis");
        const b = destacados.length > 0 ? destacados[Math.floor(Date.now() / 8000) % destacados.length] : null;
        return (
          <div style={{ padding: "calc(var(--sat) + 58px) 18px 0" }}>
            {/* Anuncio pago primero; si no hay, el destacado por plan; si no, se ofrece el espacio */}
            <EspacioAnuncio slot="pitstop" alternativa={b ? (
              <div onClick={() => setNegocioSel(b)}
                style={{ background: C.accent, borderRadius: 16, padding: 16, cursor: "pointer", color: "#F9F5F2", display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ width: 54, height: 54, borderRadius: 14, background: C.card, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                  {b.logo_url ? <img loading="lazy" decoding="async" src={b.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4, boxSizing: "border-box" }} /> : <Wrench size={24} color="#111111" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 3 }}>Destacado</div>
                  <div style={{ fontSize: 16, fontWeight: 800, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{b.nombre}</div>
                  {b.descripcion && <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{b.descripcion}</div>}
                </div>
                <ChevronRight size={16} color={C.muted} style={{ flexShrink: 0 }} />
              </div>
            ) : null /* "¿Tenés un negocio?" va al final de la lista: el 99% no tiene negocio */} />
          </div>
        );
      })()}
      <div style={{ padding: "12px 18px 14px", borderBottom: `1px solid ${C.border}` }}>
        {/* buscador */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", background: C.surface, border: `1px solid ${searchActive ? C.text : C.border}`, borderRadius: 12, padding: "10px 14px", transition: "border .15s" }}>
          <Search size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onFocus={() => setSearchActive(true)}
            onBlur={() => setTimeout(() => setSearchActive(false), 150)}
            placeholder="Buscá por nombre, ciudad o rubro…"
            style={{ flex: 1, background: "transparent", border: "none", fontSize: 14, color: C.text, outline: "none" }}
          />
          {searchQ && (
            <button onClick={() => setSearchQ("")} style={{ flexShrink: 0 }}>
              <X size={15} strokeWidth={2.5} color={C.muted} />
            </button>
          )}
        </div>

        {/* resultados de búsqueda */}
        {searchQ.trim().length > 1 && (
          <div style={{ marginTop: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,.08)" }}>
            {searchResults.length === 0 ? (
              <div style={{ padding: "20px 14px", textAlign: "center", color: C.muted, fontSize: 13 }}>Sin resultados para "{searchQ}"</div>
            ) : searchResults.slice(0, 8).map((r, i) => {
              const tipoLabel = { negocio: r.item.categoria || "Negocio", taller: "Taller", marca: "Marca", concesionario: "Concesionario", aviso: "Compra/Venta", alquiler: "Alquiler" };
              const tipoIcon = { negocio: Wrench, taller: Wrench, marca: Award, concesionario: ShoppingBag, aviso: Tag, alquiler: Bike };
              const Icon = tipoIcon[r.tipo];
              const nombre = r.tipo === "aviso" ? r.item.titulo : r.tipo === "alquiler" ? r.item.agencia : r.item.nombre;
              const res = r.tipo === "negocio" ? resenasResumen[r.item.id] : null;
              const sub = r.tipo === "negocio" ? [r.item.ciudad, r.item.direccion].filter(Boolean).join(" · ")
                : r.tipo === "aviso" ? `${r.item.moneda} ${r.item.precio.toLocaleString()} · ${r.item.ubicacion}` : r.tipo === "alquiler" ? r.item.zona : r.tipo === "taller" ? r.item.zona : r.tipo === "marca" ? `${r.item.pais} · ${r.item.tipo}` : r.item.zona;
              return (
                <button key={i} onClick={() => {
                  setSearchQ("");
                  if (r.tipo === "negocio") { setSubTab(subTabDeNegocio(r.item)); setNegocioSel(r.item); }
                  else if (r.tipo === "taller") { setSubTab("talleres"); setSelTaller(r.idx); }
                  else if (r.tipo === "marca") { setSubTab("marcas"); setSelMarca(r.idx); }
                  else if (r.tipo === "concesionario") { setSubTab("concesionarios"); setSelConce(r.idx); }
                  else if (r.tipo === "aviso") { setSubTab("compraventa"); setSelPubli(r.idx); }
                  else if (r.tipo === "alquiler") { setSubTab("alquiler"); setSelAlquiler(r.idx); }
                }} style={{ width: "100%", display: "flex", gap: 12, alignItems: "center", padding: "11px 14px", borderBottom: i < searchResults.slice(0,6).length - 1 ? `1px solid ${C.border}` : "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 34, height: 34, background: C.surface, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={16} strokeWidth={1.8} color={C.muted} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nombre}</div>
                    <div style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 5 }}>
                      {res?.n > 0 && <><Star size={10} strokeWidth={2} fill="#8A6D3B" color="#8A6D3B" /><span style={{ fontWeight: 700, color: C.text }}>{res.prom}</span></>}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, border: `1px solid ${C.border}`, color: C.muted, flexShrink: 0 }}>{tipoLabel[r.tipo]}</span>
                </button>
              );
            })}
            {searchResults.length > 8 && (
              <div style={{ padding: "10px 14px", fontSize: 12, color: C.muted, borderTop: `1px solid ${C.border}` }}>
                +{searchResults.length - 8} resultados — refiná la búsqueda
              </div>
            )}
          </div>
        )}
      </div>

      {/* sub-tabs STICKY */}
      <div style={{ position: "sticky", top: 0, zIndex: 90, background: C.card, borderBottom: `1px solid ${C.border}`, display: "flex", overflowX: "auto" }}>
        {[["todos", MapPin, "Todos"], ["tienda", ShoppingBag, "Tienda"], ["talleres", Wrench, "Talleres"], ["marcas", Award, "Marcas"], ["concesionarios", ShoppingBag, "Concesionarios"], ["compraventa", Tag, "Compra/Venta"], ["alquiler", Bike, "Alquiler"], ["hospedajes", Home, "Hospedajes"], ["comida", Utensils, "Dónde comer"], ["tramites", Shield, "Seguros / Gestoría"]].map(([id, Icon, label]) => (
          <button key={id} onClick={() => setSubTab(id)} style={{ flex: "0 0 auto", padding: "11px 14px", fontSize: 11, fontWeight: subTab === id ? 700 : 500, textAlign: "center", color: subTab === id ? C.text : C.muted, borderBottom: subTab === id ? `2px solid ${C.text}` : "2px solid transparent", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", whiteSpace: "nowrap" }}>
            <Icon size={18} strokeWidth={subTab === id ? 2.2 : 1.6} data-subtab={id} />
            {label}
          </button>
        ))}
      </div>

      {/* compartir la sección actual: para responder "¿alguien conoce un gestor?" con un link */}
      <div style={{ padding: "10px 18px 0", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => {
          const nombres = { talleres: "talleres", marcas: "marcas", concesionarios: "concesionarios", compraventa: "avisos de compra y venta", alquiler: "alquiler de motos", tramites: "seguros y gestorías" };
          const link = `https://olbos.ai/?pitstop=${subTab}`;
          compartirNativo({ texto: `🏍️ Acá tenés ${nombres[subTab] || "los servicios"} recomendados por la comunidad de Olbos, con reseñas de otros motociclistas:`, url: link, titulo: "Servicios en Olbos" });
        }}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 100, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          <Share2 size={13} strokeWidth={2} /> Compartir esta sección
        </button>
      </div>

      {/* TALLERES */}
      {subTab === "talleres" && (
        <div style={{ padding: "14px 18px 80px" }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 4 }}>
            {["Todos", "Destacados", "🛞 Gomería", "🚛 Traslado", "🕐 24hs", "BMW", "Honda", "Custom"].map(f => (
              <button key={f} onClick={() => setFilterTaller(f)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: filterTaller === f ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${filterTaller === f ? C.text : C.border}`, color: filterTaller === f ? C.text : C.muted, cursor: "pointer" }}>{f}</button>
            ))}
          </div>
          {/* filtro cerca + mapa */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button onClick={activarCerca} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1px solid ${filtroCerca ? C.green : C.border}`, background: filtroCerca ? `${C.green}14` : C.card, color: filtroCerca ? C.green : C.muted, cursor: "pointer" }}>
              <Navigation size={13} strokeWidth={2} /> {filtroCerca ? "Cerca tuyo ✓" : "Cerca tuyo"}
            </button>
            <button onClick={() => setShowMapaTalleres(s => !s)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1px solid ${showMapaTalleres ? C.text : C.border}`, background: showMapaTalleres ? C.text : C.card, color: showMapaTalleres ? "#fff" : C.muted, cursor: "pointer" }}>
              <Map size={13} strokeWidth={2} /> Ver mapa
            </button>
          </div>

          {/* mapa de talleres — el MISMO mapa que la vista Todos (pins + labels con zoom) */}
          {showMapaTalleres && (
            <MapaServicios items={talleresVisibles} onAbrir={setNegocioSel} />
          )}

          {/* esperando ubicación */}
          {esperandoUbicacion && (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📡</div>
              <p style={{ fontSize: 14, color: C.muted }}>Obteniendo tu ubicación...</p>
            </div>
          )}

          {/* aviso cuando no hay talleres en 50km pero mostramos los más cercanos */}
          {filtroCerca && userPos && talleresVisibles.length > 0 &&
            talleresDB.filter(n => n.lat && distancia(userPos.lat, userPos.lng, n.lat, n.lng) <= 50).length === 0 && (
            <div style={{ background: "#FFF9EE", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#8B6914" }}>
              📍 No hay talleres verificados a menos de 50 km. Mostrando los 5 más cercanos.
            </div>
          )}

          {/* talleres reales de Supabase */}
          {filterTaller !== "Todos" && talleresVisibles.length === 0 && talleresDB.length > 0 && (
            <div style={{ padding: "24px 12px", textAlign: "center", fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              Todavía no hay negocios con «{filterTaller.replace(/^\S+\s/, "")}». <button onClick={() => setFilterTaller("Todos")} style={{ background: "none", border: "none", padding: 0, fontWeight: 700, color: C.text, textDecoration: "underline", cursor: "pointer", fontSize: 13 }}>Ver todos</button>
            </div>
          )}
          {talleresVisibles.map(n => (
            <div key={n.id} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 48, height: 48, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                  {n.logo_url ? <img loading="lazy" decoding="async" src={n.logo_url} alt={n.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Wrench size={22} strokeWidth={1.5} color={C.muted} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                    <h3 style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{n.nombre}</h3>
                    <CheckCircle size={14} strokeWidth={2} color={C.blue} />
                    {userPos && n.lat && n.lng && (
                      <span style={{ fontSize: 11, color: C.text, fontWeight: 700, background: C.surface, padding: "1px 7px", borderRadius: 8 }}>
                        {distancia(userPos.lat, userPos.lng, n.lat, n.lng) < 1
                          ? `${Math.round(distancia(userPos.lat, userPos.lng, n.lat, n.lng) * 1000)} m`
                          : `${distancia(userPos.lat, userPos.lng, n.lat, n.lng).toFixed(0)} km`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>{n.ciudad}</div>
                  {n.direccion && <div style={{ fontSize: 11, color: C.muted }}>{n.direccion}</div>}
                </div>
              </div>
              {n.descripcion && <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginTop: 10 }}>{n.descripcion}</p>}
              {n.horarios && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>🕐 {n.horarios}</div>}
              <div onClick={(e) => registrarContactoNegocio(e, n.id)} style={{ display: "flex", gap: 8, marginTop: 12 }}>
                {n.whatsapp && (
                  <a href={`https://wa.me/${n.whatsapp}?text=Hola! Te contacto desde Olbos 🏍️`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "9px 0", borderRadius: 10, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 13, textAlign: "center", textDecoration: "none" }}>
                    💬 WhatsApp
                  </a>
                )}
                {n.telefono && (
                  <a href={`tel:${n.telefono}`}
                    style={{ flex: 1, padding: "9px 0", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 700, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                    📞 Llamar
                  </a>
                )}
                <button onClick={() => setNegocioSel(n)}
                  style={{ padding: "9px 14px", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                  Ver
                </button>
                <BtnCompartir texto={`🔧 ${n.nombre}\n${n.ciudad} · ${n.direccion || ""}`} tipo="negocio" id={n.id} small />
              </div>
              <ResenasNegocio negocioId={n.id} nombre={n.nombre} resumen={resenasResumen[n.id]} onCambio={cargarResenasResumen} />
            </div>
          ))}

          {TALLERES.length === 0 && talleresDB.length === 0 && (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FFF3EE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                <Wrench size={32} strokeWidth={1.5} color="#111111" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>Estamos armando el directorio</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
                ¿Conocés un taller o mecánico de confianza? Sugerilo y ayudá a construir el Pit Stop de la comunidad.
              </p>
              <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-registro-negocio"))}
                style={{ padding: "12px 24px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
                Sugerir negocio 🔧
              </button>
            </div>
          )}
          {TALLERES
            .filter(t => {
              if (filterTaller === "Todos") return true;
              if (filterTaller === "Destacados") return t.destacado;
              if (filterTaller === "🛞 Gomería") return t.servicios?.gromeria;
              if (filterTaller === "🚛 Traslado") return t.servicios?.traslado;
              if (filterTaller === "🕐 24hs") return t.servicios?.urgencias24;
              return t.especialidad.some(e => e.includes(filterTaller));
            })
            .filter(t => !filtroCerca || !userPos || distancia(userPos.lat, userPos.lng, t.lat, t.lng) <= 10)
            .map((t, i) => (
            <div key={t.id} onClick={() => setSelTaller(TALLERES.indexOf(t))} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 48, height: 48, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}` }}>
                  <Wrench size={22} strokeWidth={1.5} color={C.muted} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{t.nombre}</h3>
                    {t.destacado && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, border: `1px solid ${C.border}`, color: C.muted }}>DESTACADO</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                    <MapPin size={11} strokeWidth={2} color={C.muted} />
                    <span style={{ fontSize: 12, color: C.muted }}>{t.dir || t.zona}</span>
                  </div>
                  {userPos && t.lat && (
                    <div style={{ fontSize: 11, color: C.green, fontWeight: 600, marginBottom: 4 }}>
                      📍 {distancia(userPos.lat, userPos.lng, t.lat, t.lng).toFixed(1)} km de vos
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Stars n={t.rating} size={11} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{t.rating}</span>
                    <span style={{ fontSize: 12, color: C.muted }}>({t.reviews}) · {t.precio}</span>
                  </div>
                </div>
                <ChevronRight size={18} strokeWidth={1.8} color={C.faint} />
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {t.especialidad.map(e => <OTag key={e}>{e}</OTag>)}
                {t.servicios?.gromeria && <OTag style={{ background: `${C.green}14`, borderColor: C.green + "44", color: C.green }}>🛞 Gomería</OTag>}
                {t.servicios?.traslado && <OTag style={{ background: `${C.gold}14`, borderColor: C.accent + "44", color: C.accent }}>🚛 Traslado</OTag>}
                {t.servicios?.urgencias24 && <OTag style={{ background: "#FFF0F0", borderColor: C.red + "44", color: C.red }}>🕐 24hs</OTag>}
              </div>
            </div>
          ))}
          <button style={{ width: "100%", padding: 14, borderRadius: 12, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Plus size={16} strokeWidth={2} /> Recomendar un taller
          </button>
        </div>
      )}

      {/* MARCAS */}
      {/* TODOS: la vista unificada — todos los servicios en una hoja, filtrables por
          rubro y también en mapa (feedback Cata 13-ago) */}
      {subTab === "todos" && (() => {
        const catDe = (n) => {
          const c = String(n.categoria || "").toLowerCase();
          if (c.startsWith("taller")) return "Talleres";
          if (c.startsWith("conces")) return "Concesionarios";
          if (c.startsWith("marca")) return "Marcas";
          if (c.includes("escuela") || c.includes("curso")) return "Motoescuelas";
          if (c.startsWith("pista") || c.includes("circuito")) return "Pistas";
          if (c.startsWith("seguro") || c.startsWith("gestor")) return "Seguros/Gestoría";
          if (c.startsWith("gromeria") || c.startsWith("gomeria") || c.startsWith("gomería")) return "Gomerías";
          if (c.startsWith("alquiler")) return "Alquiler";
          if (c.startsWith("accesorio")) return "Accesorios";
          if (c.startsWith("grua") || c.startsWith("grúa")) return "Grúas";
          if (c.startsWith("restaurante") || c.startsWith("comida") || c.startsWith("parador")) return "Dónde comer";
          if (c.startsWith("hospedaje") || c.startsWith("hotel")) return "Hospedajes";
          return "Otros";
        };
        const conteo = {};
        negociosDB.forEach(n => { const cc = catDe(n); conteo[cc] = (conteo[cc] || 0) + 1; });
        const cats = Object.entries(conteo).sort((a, b) => b[1] - a[1]);
        // Etiquetas de servicio (Taller oficial, Circuito enduro, etc.): cruzan categorías
        const conteoTags = {};
        negociosDB.forEach(n => (Array.isArray(n.tags) ? n.tags : []).forEach(t => { conteoTags[t] = (conteoTags[t] || 0) + 1; }));
        // Circuitos enduro y motocross siempre visibles aunque estén vacíos: marcan
        // la cancha de lo que va a haber e invitan a los dueños a registrarse
        const tagsSiempre = ["Circuito enduro", "Circuito motocross"];
        const tagsConNegocios = TAGS_NEGOCIO.filter(t => conteoTags[t] || tagsSiempre.includes(t));
        let visibles = ordenarPorCercania(negociosDB.filter(n =>
          filtroCatTodos === "Todos" ? true
            : filtroCatTodos.startsWith("#") ? (Array.isArray(n.tags) && n.tags.includes(filtroCatTodos.slice(1)))
            : catDe(n) === filtroCatTodos));
        // Cerca tuyo: dentro de 50 km — si no hay ninguno, los 8 más cercanos
        if (filtroCerca && userPos) {
          const cercanos = visibles.filter(n => n.lat && n.lng && distancia(userPos.lat, userPos.lng, n.lat, n.lng) <= 50);
          visibles = cercanos.length > 0 ? cercanos : visibles.slice(0, 8);
        }
        return (
          <div style={{ padding: "14px 18px 80px" }}>
            {/* Un solo sistema de filtros, colapsado como en Eventos: rubros y etiquetas
                juntos, y el filtro elegido se mantiene al pasar de lista a mapa */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Pill active={vistaTodos === "lista"} onClick={() => setVistaTodos("lista")}>📋 Lista</Pill>
              <Pill active={vistaTodos === "mapa"} onClick={() => setVistaTodos("mapa")}>🗺️ Mapa</Pill>
              <Pill active={filtroCerca} onClick={activarCerca}>📍 {esperandoUbicacion ? "Ubicando…" : "Cerca tuyo"}</Pill>
              <button onClick={() => setShowFiltrosTodos(v => !v)}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 100, background: (showFiltrosTodos || filtroCatTodos !== "Todos") ? "#111111" : C.surface, color: (showFiltrosTodos || filtroCatTodos !== "Todos") ? "#fff" : C.text, border: `1px solid ${(showFiltrosTodos || filtroCatTodos !== "Todos") ? "#111111" : C.border}`, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                🎛️ Filtrar{filtroCatTodos !== "Todos" ? `: ${filtroCatTodos.replace(/^#/, "")}` : ""}
                <ChevronDown size={12} strokeWidth={2.5} style={{ transform: showFiltrosTodos ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              </button>
              {filtroCatTodos !== "Todos" && (
                <button onClick={() => setFiltroCatTodos("Todos")} style={{ fontSize: 12, color: C.muted, background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }}>Limpiar</button>
              )}
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.muted }}>{visibles.length} de {negociosDB.length}</span>
            </div>
            {showFiltrosTodos && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {cats.map(([cc, cn]) => (
                  <Pill key={cc} active={filtroCatTodos === cc} onClick={() => setFiltroCatTodos(filtroCatTodos === cc ? "Todos" : cc)}>{cc} · {cn}</Pill>
                ))}
                {tagsConNegocios.map(t => (
                  <Pill key={"#" + t} active={filtroCatTodos === "#" + t} onClick={() => setFiltroCatTodos(filtroCatTodos === "#" + t ? "Todos" : "#" + t)}>🏷 {t}{conteoTags[t] ? ` · ${conteoTags[t]}` : ""}</Pill>
                ))}
              </div>
            )}
            {vistaTodos === "mapa" ? (
              <MapaServicios items={visibles} onAbrir={setNegocioSel} />
            ) : visibles.length === 0 ? (
              <div style={{ padding: "36px 20px", textAlign: "center", fontSize: 13, color: C.muted }}>
                Sin servicios en este rubro todavía. ¿Conocés uno? Invitalo a registrarse — es gratis.
              </div>
            ) : (
              visibles.map(n => {
                const res = resenasResumen[n.id];
                return (
                  <button key={n.id} onClick={() => setNegocioSel(n)}
                    style={{ width: "100%", display: "flex", gap: 12, alignItems: "center", padding: "11px 13px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, marginBottom: 8, cursor: "pointer", textAlign: "left", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                      {n.logo_url
                        ? <img loading="lazy" decoding="async" src={n.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: catDe(n) === "Marcas" ? "contain" : "cover", padding: catDe(n) === "Marcas" ? 4 : 0, boxSizing: "border-box" }} />
                        : <Wrench size={20} strokeWidth={1.6} color={C.muted} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{n.nombre}</span>
                        <CheckCircle size={13} strokeWidth={2.5} color={C.blue} style={{ flexShrink: 0 }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                        {catDe(n)}{n.ciudad ? ` · ${n.ciudad}` : ""}{(userPos && n.lat && n.lng) ? (() => { const d = distancia(userPos.lat, userPos.lng, n.lat, n.lng); return ` · a ${d < 1 ? `${Math.round(d * 1000)} m` : `${Math.round(d)} km`}`; })() : ""}{res ? ` · ★ ${Number(res.prom).toFixed(1)} (${res.n})` : ""}
                      </div>
                    </div>
                    <ChevronRight size={15} color={C.muted} style={{ flexShrink: 0 }} />
                  </button>
                );
              })
            )}
            {vistaTodos === "lista" && (
              <div onClick={onRegistro} style={{ marginTop: 14, background: C.accent, borderRadius: 16, padding: "16px 18px", cursor: "pointer", color: "#F9F5F2", display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 3 }}>Para negocios</div>
                  <div style={{ fontSize: 15.5, fontWeight: 800, letterSpacing: "-0.01em" }}>¿Tenés un negocio motero?</div>
                  <div style={{ fontSize: 11.5, color: "#c9c3ba", marginTop: 2 }}>Registralo gratis y aparecé donde te buscan los riders</div>
                </div>
                <ChevronRight size={16} color={C.muted} style={{ flexShrink: 0 }} />
              </div>
            )}
          </div>
        );
      })()}

      {subTab === "marcas" && (
        <div style={{ padding: "14px 18px 80px" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <CheckCircle size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>Espacios oficiales verificados: modelos, eventos, sorteos y contacto directo de cada marca.</span>
          </div>

          {/* espacios oficiales reales (negocios con categoría Marca, verificados) */}
          {(() => {
            const marcasDB = negociosDB.filter(n => String(n.categoria || "").toLowerCase().startsWith("marca"));
            return (<>
              {marcasDB.map(n => (
                <div key={n.id} onClick={() => setNegocioSel(n)}
                  style={{ background: C.accent, borderRadius: 16, padding: 16, marginBottom: 12, cursor: "pointer", color: "#F9F5F2", display: "flex", gap: 14, alignItems: "center" }}>
                  <div style={{ width: 58, height: 58, borderRadius: 14, background: C.card, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                    {n.logo_url ? <img loading="lazy" decoding="async" src={n.logo_url} alt={n.nombre} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 5, boxSizing: "border-box" }} /> : <Award size={26} color="#111111" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 3 }}>Espacio oficial</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{n.nombre}</span>
                      <CheckCircle size={15} strokeWidth={2.5} color="#8FD6AC" style={{ flexShrink: 0 }} />
                    </div>
                    {n.descripcion && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{n.descripcion}</div>}
                  </div>
                  <ChevronRight size={16} color={C.muted} style={{ flexShrink: 0 }} />
                </div>
              ))}
              {marcasDB.length === 0 && (
                <div style={{ padding: "30px 24px 22px", textAlign: "center" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#f6f1eb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <Award size={30} strokeWidth={1.5} color="#111111" />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>Los espacios oficiales están llegando</h3>
                  <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.7, maxWidth: 300, margin: "0 auto" }}>
                    Acá van a vivir los perfiles verificados de las marcas: sus modelos, lanzamientos, eventos oficiales y contacto directo.
                  </p>
                </div>
              )}

              {/* pitch para la marca que entra a mirar: el espacio se pide desde acá */}
              <div style={{ background: C.accent, borderRadius: 16, padding: "20px 18px", color: "#F9F5F2", marginTop: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 8 }}>Para marcas</div>
                <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.3, marginBottom: 8 }}>¿Representás una marca?</div>
                <div style={{ fontSize: 12.5, color: "#c9c3ba", lineHeight: 1.6, marginBottom: 14 }}>
                  Tu espacio oficial en Olbos: tus modelos en el catálogo, eventos y test rides con inscripción, sorteos para la comunidad y contacto directo con riders activos.
                </div>
                <button onClick={async () => {
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) { alert("Creá tu cuenta primero para solicitar el espacio de tu marca."); return; }
                  setShowCrearMarca(user.id);
                }} style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#F9F5F2", color: C.text, border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
                  Solicitar mi espacio oficial
                </button>
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8, textAlign: "center" }}>Gratis. Lo verificamos y queda publicado.</div>
              </div>
            </>);
          })()}
          {showCrearMarca && (
            <CrearNegocioModal userId={showCrearMarca} onClose={() => setShowCrearMarca(null)}
              onSuccess={() => { setShowCrearMarca(null); alert("¡Solicitud enviada! La verificamos y tu espacio queda publicado. Te avisamos por acá."); }} />
          )}
          {MARCAS.map((m, i) => (
            <div key={m.id} onClick={() => setSelMarca(i)} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 52, height: 52, background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: C.text, letterSpacing: 1, flexShrink: 0 }}>{m.logo}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                    <h3 style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{m.nombre}</h3>
                    {negocioVerificado(m) && <CheckCircle size={15} strokeWidth={2} color={C.blue} />}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{m.pais} · {m.tipo}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{m.seguidores.toLocaleString()} seguidores</div>
                </div>
                <ChevronRight size={18} strokeWidth={1.8} color={C.faint} />
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {m.modelos_destacados.slice(0, 3).map(mod => <OTag key={mod}>{mod}</OTag>)}
                {m.novedades.length > 0 && <OTag>{m.novedades.length} novedades</OTag>}
              </div>
            </div>
          ))}
          <button style={{ width: "100%", padding: 14, borderRadius: 12, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Plus size={16} strokeWidth={2} /> ¿Tu marca no está? Contactanos
          </button>
        </div>
      )}

      {/* SEGUROS / GESTORÍA — trámites de la moto */}
      {subTab === "tramites" && (
        <div style={{ padding: "14px 18px 80px" }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Shield size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              Seguros, gestorías y trámites: patentamiento, transferencias, cédula azul, verificación policial y pólizas. Todo lo aburrido de tener una moto, resuelto por gente de la comunidad.
            </span>
          </div>
          {tramitesDB.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ width: 68, height: 68, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <Shield size={30} strokeWidth={1.5} color={C.muted} />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>Todavía no hay nadie acá</h3>
              <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
                ¿Sos productor de seguros o gestor? Registrá tu servicio y aparecé cuando un rider necesite resolver un trámite.
              </p>
            </div>
          ) : tramitesDB.map(n => (
            <div key={n.id} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 48, height: 48, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                  {n.logo_url ? <img loading="lazy" decoding="async" src={cldThumb(n.logo_url, 160)} alt={n.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Shield size={22} strokeWidth={1.5} color={C.muted} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>{n.nombre}</h3>
                    {negocioVerificado(n) && <CheckCircle size={13} strokeWidth={2.5} color={C.green} />}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {String(n.categoria || "").toLowerCase().startsWith("seguro") ? "📋 Seguros" : "📄 Gestoría / Trámites"}
                    {n.ciudad ? ` · ${n.ciudad}` : ""}
                  </div>
                </div>
              </div>
              {n.descripcion && (
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginTop: 10 }}>{n.descripcion}</p>
              )}
              <ResenasNegocio negocioId={n.id} nombre={n.nombre} resumen={resenasResumen[n.id]} onCambio={cargarResenasResumen} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {n.whatsapp && (
                  <a href={`https://wa.me/${String(n.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, minWidth: 120, padding: "9px 12px", borderRadius: 10, background: "#2F7D4F", color: "#fff", textDecoration: "none", fontSize: 13, fontWeight: 700, textAlign: "center" }}>
                    WhatsApp
                  </a>
                )}
                {n.telefono && !n.whatsapp && (
                  <a href={`tel:${n.telefono}`}
                    style={{ flex: 1, minWidth: 120, padding: "9px 12px", borderRadius: 10, background: C.text, color: "#fff", textDecoration: "none", fontSize: 13, fontWeight: 700, textAlign: "center" }}>
                    Llamar
                  </a>
                )}
                {n.instagram && (
                  <a href={`https://instagram.com/${String(n.instagram).replace("@", "")}`} target="_blank" rel="noreferrer"
                    style={{ padding: "9px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, textDecoration: "none", fontSize: 13, fontWeight: 700 }}>
                    Instagram
                  </a>
                )}
                {n.web && (
                  <a href={n.web.startsWith("http") ? n.web : `https://${n.web}`} target="_blank" rel="noreferrer"
                    style={{ padding: "9px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, textDecoration: "none", fontSize: 13, fontWeight: 700 }}>
                    Web
                  </a>
                )}
              </div>
            </div>
          ))}
          {onRegistro && (
            <button onClick={onRegistro}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.text, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", marginTop: 6 }}>
              Registrar mi servicio
            </button>
          )}
          {/* el taller pide acceso al historial de la moto de un cliente */}
          <AccesoHistoriaTaller />
        </div>
      )}

      {/* HOSPEDAJES y DÓNDE COMER recomendados por la comunidad */}
      {subTab === "tienda" && <TiendaGeneral />}
      {subTab === "hospedajes" && <>{negociosRubro("hospedaje")}<LugaresSeccion categoria="hospedaje" /></>}
      {subTab === "comida" && <>{negociosRubro("comida")}<LugaresSeccion categoria="comida" /></>}

      {/* CONCESIONARIOS */}
      {subTab === "concesionarios" && (
        <div style={{ padding: "14px 18px 80px" }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 4 }}>
            {["Todos", "Destacados", "BMW", "Honda", "Royal Enfield", "Multi-marca"].map(f => (
              <button key={f} onClick={() => setFilterConce(f)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: filterConce === f ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${filterConce === f ? C.text : C.border}`, color: filterConce === f ? C.text : C.muted, cursor: "pointer" }}>{f}</button>
            ))}
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <CheckCircle size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>Los concesionarios verificados tienen perfil oficial. Los <strong style={{ color: C.text }}>Destacados</strong> aparecen primero con sus ofertas activas.</span>
          </div>
          {/* concesionarios reales de Supabase */}
          {filterConce !== "Todos" && concesionariosVisibles.length === 0 && concesionariosDB.length > 0 && (
            <div style={{ padding: "24px 12px", textAlign: "center", fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              Todavía no hay concesionarios con «{filterConce}». <button onClick={() => setFilterConce("Todos")} style={{ background: "none", border: "none", padding: 0, fontWeight: 700, color: C.text, textDecoration: "underline", cursor: "pointer", fontSize: 13 }}>Ver todos</button>
            </div>
          )}
          {concesionariosVisibles.map(n => (
            <div key={n.id} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 48, height: 48, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                  {n.logo_url ? <img loading="lazy" decoding="async" src={n.logo_url} alt={n.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ShoppingBag size={22} strokeWidth={1.5} color={C.muted} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                    <h3 style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{n.nombre}</h3>
                    <CheckCircle size={14} strokeWidth={2} color={C.blue} />
                    {userPos && n.lat && n.lng && (
                      <span style={{ fontSize: 11, color: C.text, fontWeight: 700, background: C.surface, padding: "1px 7px", borderRadius: 8 }}>
                        {distancia(userPos.lat, userPos.lng, n.lat, n.lng) < 1
                          ? `${Math.round(distancia(userPos.lat, userPos.lng, n.lat, n.lng) * 1000)} m`
                          : `${distancia(userPos.lat, userPos.lng, n.lat, n.lng).toFixed(0)} km`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>{n.ciudad}</div>
                  {n.direccion && <div style={{ fontSize: 11, color: C.muted }}>{n.direccion}</div>}
                </div>
              </div>
              {n.descripcion && <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginTop: 10 }}>{n.descripcion}</p>}
              {n.horarios && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>🕐 {n.horarios}</div>}
              <div onClick={(e) => registrarContactoNegocio(e, n.id)} style={{ display: "flex", gap: 8, marginTop: 12 }}>
                {n.whatsapp && (
                  <a href={`https://wa.me/${n.whatsapp}?text=Hola! Te contacto desde Olbos 🏍️`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "9px 0", borderRadius: 10, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 13, textAlign: "center", textDecoration: "none" }}>
                    💬 WhatsApp
                  </a>
                )}
                {n.telefono && (
                  <a href={`tel:${n.telefono}`}
                    style={{ flex: 1, padding: "9px 0", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 700, fontSize: 13, textAlign: "center", border: `1px solid ${C.border}`, textDecoration: "none" }}>
                    📞 Llamar
                  </a>
                )}
                <button onClick={() => setNegocioSel(n)}
                  style={{ padding: "9px 14px", borderRadius: 10, background: C.surface, color: C.text, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                  Ver
                </button>
                <BtnCompartir texto={`📍 ${n.nombre}\n${n.ciudad} · ${n.direccion || ""}`} tipo="negocio" id={n.id} small />
              </div>
              <ResenasNegocio negocioId={n.id} nombre={n.nombre} resumen={resenasResumen[n.id]} onCambio={cargarResenasResumen} />
            </div>
          ))}

          {CONCESIONARIOS.length === 0 && concesionariosDB.length === 0 && (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#f6f1eb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                <ShoppingBag size={32} strokeWidth={1.5} color="#111111" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>Concesionarios próximamente</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
                ¿Trabajás en un concesionario o lo conocés? Ayudá a construir el directorio oficial.
              </p>
              <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-registro-negocio"))}
                style={{ padding: "12px 24px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
                Sugerir concesionario
              </button>
            </div>
          )}
          {CONCESIONARIOS.filter(c => filterConce === "Todos" ? true : filterConce === "Destacados" ? c.destacado : filterConce === "Multi-marca" ? c.marcas.length > 1 : c.marcas.some(m => m.includes(filterConce))).map((c, i) => (
            <div key={c.id} onClick={() => setSelConce(i)} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.05)" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 48, height: 48, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${C.border}` }}>
                  <ShoppingBag size={22} strokeWidth={1.5} color={C.muted} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{c.nombre}</h3>
                    {negocioVerificado(c) && <CheckCircle size={14} strokeWidth={2} color={C.blue} />}
                    {c.destacado && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, border: `1px solid ${C.border}`, color: C.muted }}>DESTACADO</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
                    <MapPin size={11} strokeWidth={2} color={C.muted} />
                    <span style={{ fontSize: 12, color: C.muted }}>{c.zona}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Stars n={c.rating} size={11} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{c.rating}</span>
                    <span style={{ fontSize: 12, color: C.muted }}>({c.reviews})</span>
                  </div>
                </div>
                <ChevronRight size={18} strokeWidth={1.8} color={C.faint} />
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {c.marcas.map(marca => <OTag key={marca}>{marca}</OTag>)}
              </div>
              {c.ofertas.length > 0 && (
                <div style={{ marginTop: 10, background: C.surface, borderRadius: 8, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{c.ofertas[0].titulo}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{c.ofertas[0].precio}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* COMPRA/VENTA */}
      {subTab === "compraventa" && (
        <div style={{ padding: "14px 18px 100px" }}>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 4 }}>
            {[["todo", "Todo"], ["moto", "Motos"], ...(ALQUILER_P2P_ACTIVO ? [["alquiler", "En alquiler"]] : []), ["accesorio", "Accesorios"], ["indumentaria", "Indumentaria"], ["repuesto", "Repuestos"]].map(([id, label]) => (
              <button key={id} onClick={() => setFilterPubli(id)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: filterPubli === id ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${filterPubli === id ? C.text : C.border}`, color: filterPubli === id ? C.text : C.muted, cursor: "pointer" }}>{label}</button>
            ))}
          </div>

          {/* 📣 Destacado del Mercado: espacio self-service arriba del listado */}
          <EspacioAnuncio slot="mercado" estilo={{ marginBottom: 12 }} />

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Users size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              {filterPubli === "alquiler"
                ? "Riders que alquilan su propia moto. El acuerdo, el pago y la cobertura son entre las partes — Olbos no cobra ni garantiza. Chequeá el seguro antes de salir a la ruta."
                : "Compra/venta entre riders de la comunidad. El contacto y la transacción son entre las partes — Olbos no interviene."}
            </span>
          </div>

          {/* motos reales de Supabase: las de alquiler tienen su propia solapa para no
              mezclarlas con quien está buscando comprar */}
          {motosEnVenta.filter(v => filterPubli === "alquiler" ? v.tipo === "alquiler" : (v.tipo || "venta") === "venta" && (filterPubli === "todo" || categoriaVenta(v) === filterPubli))
            .sort((a, b) => (ventasDestacadas[b.id] ? 1 : 0) - (ventasDestacadas[a.id] ? 1 : 0)).map(v => (
            <div key={v.id} onClick={() => setVentaSel(v)} style={{ background: C.card, borderRadius: 14, border: `1px solid ${ventasDestacadas[v.id] ? C.gold : C.border}`, overflow: "hidden", marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,.05)", cursor: "pointer", position: "relative" }}>
              {ventasDestacadas[v.id] && <div style={{ position: "absolute", top: 10, left: 10, zIndex: 1, fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 9px", borderRadius: 6, background: "#111111", color: "#D9B368" }}>⭐ Destacada</div>}
              {v.foto_url && (
                <div style={{ height: 160, overflow: "hidden" }}>
                  <img loading="lazy" decoding="async" src={v.foto_url} alt={tituloVenta(v)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
              <div style={{ padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{tituloVenta(v)}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{detalleVenta(v)}</div>
                    {v.ciudad && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>📍 {v.ciudad}</div>}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: 18, color: C.green }}>{v.moneda} {Number(v.precio).toLocaleString("es-AR")}</div>
                    {v.tipo === "alquiler" && (
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
                        {v.periodo === "semana" ? "por semana" : v.periodo === "fin_de_semana" ? "por finde" : "por día"}
                      </div>
                    )}
                  </div>
                </div>
                {v.tipo === "alquiler" && v.zona && (
                  <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 8 }}>
                    <MapPin size={13} strokeWidth={2} color={C.muted} />
                    <span style={{ fontSize: 12.5, color: C.muted }}>Se entrega en {v.zona}</span>
                  </div>
                )}
                {v.descripcion && <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>{v.descripcion}</p>}
                {v.tipo === "alquiler" && v.requisitos && (
                  <div style={{ background: C.surface, borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Qué pide el dueño</div>
                    <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>{v.requisitos}</div>
                  </div>
                )}
                {v.historial_mantenimiento?.length > 0 && (
                  <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 3 }}>✅ Historia clínica cargada por el vendedor en Olbos</div>
                    <div style={{ fontSize: 11, color: C.green }}>
                      {v.historial_mantenimiento.length} registros · {v.historial_mantenimiento.map(r => r.tipo).filter((t, i, a) => a.indexOf(t) === i).slice(0, 4).join(", ")} · <strong>tocá para ver todo</strong>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                  <div style={{ fontSize: 11, color: C.muted }}>@{v.username} · {new Date(v.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
                  <div style={{ flex: 1 }} />
                  {yoVentaId && (v.user_id === yoVentaId || misCuentasVentaIds.includes(v.user_id)) && (
                    <>
                      <button onClick={() => setEditandoAviso(v)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: C.surface, color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Editar</button>
                      <button onClick={() => eliminarAviso(v)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: C.surface, color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Eliminar</button>
                    </>
                  )}
                  <ConsultarVendedor venta={v} chico />
                  <BtnCompartir texto={`🏍️ ${tituloVenta(v)} ${v.año || ""} ${v.tipo === "alquiler" ? "en alquiler" : "en venta"}\n${v.moneda} ${Number(v.precio).toLocaleString()}\nVen en Olbos 👇`} tipo="venta" id={v.id} small arriba />
                </div>
              </div>
            </div>
          ))}

          {filterPubli === "alquiler" && motosEnVenta.filter(v => v.tipo === "alquiler").length === 0 && (
            <div style={{ padding: "44px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                <Bike size={32} strokeWidth={1.5} color={C.muted} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10, letterSpacing: "-0.015em" }}>Todavía no hay motos en alquiler</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>
                ¿Tenés una moto parada y querés que alguien la disfrute? Publicala desde tu garage: elegís "La alquilo", ponés el precio por día y con qué condiciones.
                El historial de service que ya tenés cargado se muestra solo — es lo que le da confianza al que la alquila.
              </p>
            </div>
          )}

          {filterPubli !== "alquiler" && COMPRAVENTA.length === 0 && motosEnVenta.filter(v => (v.tipo || "venta") === "venta" && (filterPubli === "todo" || categoriaVenta(v) === filterPubli)).length === 0 && (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${C.green}14`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                <Tag size={32} strokeWidth={1.5} color="#27AE60" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>{filterPubli === "todo" ? "Sé el primer vendedor" : `Todavía no hay ${({ moto: "motos", accesorio: "accesorios", indumentaria: "indumentaria", repuesto: "repuestos" })[filterPubli] || "avisos"} en venta`}</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
                ¿Tenés una moto, accesorios, indumentaria o repuestos para vender? Publicalo y llegá a toda la comunidad.
              </p>
              <button onClick={() => setShowPublicarVenta(true)} style={{ padding: "12px 24px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
                Publicar aviso 🏷️
              </button>
            </div>
          )}
          {COMPRAVENTA.filter(p => filterPubli === "todo" || p.tipo === filterPubli).map((p) => (
            <div key={p.id} onClick={() => setSelPubli(COMPRAVENTA.indexOf(p))} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 14, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              <div style={{ height: 160, position: "relative", overflow: "hidden", background: `${C.border}` }}>
                <img loading="lazy" decoding="async" src={p.img} alt={p.titulo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 55%)" }} />
                {p.destacado && (
                  <div style={{ position: "absolute", top: 10, left: 12 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: "rgba(0,0,0,.55)", color: "#fff", letterSpacing: 0.5 }}>DESTACADO</span>
                  </div>
                )}
                <div style={{ position: "absolute", top: 10, right: 12 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: "rgba(255,255,255,.9)", color: C.text, letterSpacing: 0.5 }}>
                    {{ moto: "MOTO", accesorio: "ACCESORIO", indumentaria: "INDUMENTARIA", repuesto: "REPUESTO" }[p.tipo]}
                  </span>
                </div>
                <div style={{ position: "absolute", bottom: 10, left: 14 }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#fff" }}>{p.moneda} {p.precio.toLocaleString()}</div>
                </div>
              </div>
              <div style={{ padding: "12px 14px 14px" }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6, letterSpacing: -0.2 }}>{p.titulo}</h3>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    {p.km && <span style={{ fontSize: 12, color: C.muted }}>{p.km.toLocaleString()} km</span>}
                    {p.año && <span style={{ fontSize: 12, color: C.muted }}>{p.año}</span>}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <MapPin size={11} strokeWidth={2} color={C.muted} />
                      <span style={{ fontSize: 12, color: C.muted }}>{p.ubicacion}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: C.muted }}>{p.fecha}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img loading="lazy" decoding="async" src={p.vendedor.avatarUrl} alt={p.vendedor.user} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} />
                  <span style={{ fontSize: 12, color: C.muted }}>@{p.vendedor.user}</span>
                  {p.vendedor.ventas > 0 && <span style={{ fontSize: 11, color: C.muted }}>· {p.vendedor.ventas} ventas</span>}
                </div>
              </div>
            </div>
          ))}

          <button onClick={() => setShowPublicarVenta(true)} style={{ width: "100%", padding: 14, borderRadius: 12, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Plus size={16} strokeWidth={2} /> Publicar un aviso
          </button>
        </div>
      )}

      {/* ALQUILER */}
      {subTab === "alquiler" && (
        <div style={{ padding: "14px 18px 80px" }}>

          {/* filtros zona */}
          <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 4 }}>
            {["Todas", "CABA", "Mendoza", "Bariloche", "Córdoba", "Salta"].map(f => (
              <button key={f} onClick={() => setFilterAlquiler(f)} style={{ padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: filterAlquiler === f ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${filterAlquiler === f ? C.text : C.border}`, color: filterAlquiler === f ? C.text : C.muted, cursor: "pointer" }}>{f}</button>
            ))}
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Bike size={14} strokeWidth={2} color={C.muted} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>Agencias verificadas por Olbos. Precios en USD por día. Consultá disponibilidad directamente con la agencia.</span>
          </div>

          {ALQUILER.length === 0 && (
            <div style={{ padding: "50px 24px", textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#f6f1eb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
                <Bike size={32} strokeWidth={1.5} color="#111111" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>Alquiler próximamente</h3>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
                ¿Tenés una agencia de alquiler de motos? Sumala a Olbos y llegá a riders de todo el país.
              </p>
              <button onClick={() => window.dispatchEvent(new CustomEvent("olbos-registro-negocio"))}
                style={{ padding: "12px 24px", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
                Registrar agencia
              </button>
            </div>
          )}
          {ALQUILER.filter(a => filterAlquiler === "Todas" || a.zona.includes(filterAlquiler)).map((a, i) => (
            <div key={a.id} onClick={() => setSelAlquiler(i)} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 14, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              {/* foto hero */}
              <div style={{ height: 150, position: "relative", overflow: "hidden", background: `${C.border}` }}>
                <img loading="lazy" decoding="async" src={a.img} alt={a.agencia} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 60%)" }} />
                {a.destacado && (
                  <div style={{ position: "absolute", top: 10, left: 12 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: "rgba(0,0,0,.55)", color: "#fff", letterSpacing: 0.5 }}>DESTACADO</span>
                  </div>
                )}
                <div style={{ position: "absolute", bottom: 10, left: 14, right: 14 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: "#fff", letterSpacing: -0.3 }}>{a.agencia}</h3>
                </div>
              </div>

              {/* info */}
              <div style={{ padding: "12px 14px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8 }}>
                  <MapPin size={11} strokeWidth={2} color={C.muted} />
                  <span style={{ fontSize: 12, color: C.muted }}>{a.zona}</span>
                  {negocioVerificado(a) && <CheckCircle size={13} strokeWidth={2} color={C.blue} style={{ marginLeft: 4 }} />}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <Stars n={a.rating} size={12} />
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{a.rating}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>({a.reviews} reseñas)</span>
                </div>

                {/* motos disponibles preview */}
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                  {a.motos.map((m, mi) => (
                    <div key={mi} style={{ flexShrink: 0, background: m.disponible ? C.surface : "#F5F5F5", borderRadius: 10, padding: "8px 12px", border: `1px solid ${m.disponible ? C.border : C.faint}`, opacity: m.disponible ? 1 : 0.6, minWidth: 120 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>{m.modelo}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{m.cc}cc · {m.tipo}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: m.disponible ? C.text : C.muted }}>USD {m.precio_dia}/día</div>
                      {!m.disponible && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>No disponible</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <button style={{ width: "100%", padding: 14, borderRadius: 12, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Plus size={16} strokeWidth={2} /> ¿Tenés una agencia? Sumala
          </button>
        </div>
      )}

      {/* DETALLE ALQUILER */}
      {selAlquiler !== null && (() => {
        const a = ALQUILER[selAlquiler];
        return (
          <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 150, overflowY: "auto" }}>
            {/* hero */}
            <div style={{ height: 220, position: "relative", background: C.accent, overflow: "hidden" }}>
              <img loading="lazy" decoding="async" src={a.img} alt={a.agencia} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.75) 0%, transparent 55%)" }} />
              <button onClick={() => setSelAlquiler(null)} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronLeft size={20} strokeWidth={2} color="#fff" />
              </button>
              <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,.6)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>Olbos · Alquiler</div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: -0.3, marginBottom: 4 }}>{a.agencia}</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Stars n={a.rating} size={12} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{a.rating}</span>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>({a.reviews})</span>
                  {negocioVerificado(a) && <CheckCircle size={14} strokeWidth={2} color={C.muted} />}
                </div>
              </div>
            </div>

            <div style={{ padding: "18px 18px 100px" }}>

              {/* info básica */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                <MapPin size={13} strokeWidth={2} color={C.muted} />
                <span style={{ fontSize: 13, color: C.muted }}>{a.direccion}</span>
              </div>

              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 20 }}>{a.desc}</p>

              {/* motos disponibles */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Motos disponibles</div>
                {a.motos.map((m, i) => (
                  <div key={i} style={{ background: m.disponible ? C.card : C.surface, border: `1px solid ${m.disponible ? C.border : C.faint}`, borderRadius: 12, padding: 14, marginBottom: 10, opacity: m.disponible ? 1 : 0.7 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <div style={{ width: 80, height: 60, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: `${C.border}` }}>
                        <img loading="lazy" decoding="async" src={m.img} alt={m.modelo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{m.modelo}</div>
                            <div style={{ fontSize: 12, color: C.muted }}>{m.año} · {m.cc}cc · {m.tipo}</div>
                          </div>
                          {!m.disponible && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: C.surface, color: C.muted, border: `1px solid ${C.border}` }}>No disponible</span>}
                        </div>
                        <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                          <div>
                            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>Por día</div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: m.disponible ? C.text : C.muted }}>USD {m.precio_dia}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>Por semana</div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: m.disponible ? C.text : C.muted }}>USD {m.precio_semana}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* qué incluye */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Incluye</div>
                {a.incluye.map((inc, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <CheckCircle size={14} strokeWidth={2} color={C.green} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: C.text }}>{inc}</span>
                  </div>
                ))}
              </div>

              {/* requisitos */}
              <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 12, padding: 14, marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#B07D1A", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Requisitos</div>
                {a.requisitos.map((req, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                    <AlertTriangle size={13} strokeWidth={2} color="#B07D1A" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 13, color: "#8B6914" }}>{req}</span>
                  </div>
                ))}
              </div>

              {/* contacto */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                {[[Phone, "Teléfono", a.tel], [Clock, "Horarios", a.hrs]].map(([Icon, k, v]) => (
                  <div key={k} style={{ background: C.surface, borderRadius: 12, padding: "12px 14px", border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <Icon size={13} strokeWidth={1.8} color={C.muted} />
                      <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{v}</div>
                  </div>
                ))}
              </div>

              <Btn full onClick={() => {}}>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <Phone size={16} strokeWidth={2} /> Consultar disponibilidad
                </span>
              </Btn>

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Cómo llegar</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <MapPin size={13} strokeWidth={2} color={C.muted} /> {ALQUILER[selAlquiler]?.direccion}
                </div>
                <BotonesNavegacion direccion={ALQUILER[selAlquiler]?.direccion} ciudad={ALQUILER[selAlquiler]?.zona} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* botón flotante compra/venta */}
      {subTab === "compraventa" && (
        <button onClick={() => setShowPublicarVenta(true)} style={{ position: "fixed", bottom: 78, right: 18, padding: "7px 14px", borderRadius: 20, background: C.card, color: C.muted, display: "flex", alignItems: "center", gap: 5, boxShadow: "0 2px 10px rgba(0,0,0,.10)", border: `1px solid ${C.border}`, cursor: "pointer", zIndex: 50, fontSize: 13, fontWeight: 600 }}>
          <Plus size={14} strokeWidth={2} color="#555" /> Publicar aviso
        </button>
      )}

      {/* modal publicar aviso: formulario real con categoría (pedido de Matías N8) */}
      {showPublicarVenta && (
        <PublicarAvisoModal onClose={() => setShowPublicarVenta(false)}
          onPublicado={(fila) => { setMotosEnVenta(prev => [fila, ...prev.filter(x => x.id !== fila.id)]); setFilterPubli(categoriaVenta(fila)); }} />
      )}

      {/* detalle publicación */}
      {selPubli !== null && (() => {
        const p = COMPRAVENTA[selPubli];
        return (
          <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 150, overflowY: "auto" }}>
            <div style={{ height: 260, position: "relative", background: C.accent, overflow: "hidden" }}>
              <img loading="lazy" decoding="async" src={p.img} alt={p.titulo} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.75 }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.75) 0%, transparent 55%)" }} />
              <button onClick={() => setSelPubli(null)} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronLeft size={20} strokeWidth={2} color="#fff" />
              </button>
              <div style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 16 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: "rgba(0,0,0,.55)", color: "#fff" }}>{{ moto: "MOTO", accesorio: "ACCESORIO", indumentaria: "INDUMENTARIA", repuesto: "REPUESTO" }[p.tipo]}</span>
              </div>
              <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: -0.3, marginBottom: 6 }}>{p.titulo}</h1>
                <div style={{ fontSize: 28, fontWeight: 900, color: "#fff" }}>{p.moneda} {p.precio.toLocaleString()}</div>
              </div>
            </div>

            <div style={{ padding: "18px 18px 100px" }}>
              {p.tipo === "moto" && (
                <div style={{ display: "flex", gap: 12, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                  {[["Año", p.año], ["Kilómetros", `${p.km?.toLocaleString()} km`], ["Ubicación", p.ubicacion]].map(([k, v]) => (
                    <div key={k} style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {p.tipo !== "moto" && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
                  <MapPin size={13} strokeWidth={2} color={C.muted} />
                  <span style={{ fontSize: 13, color: C.muted }}>{p.ubicacion}</span>
                </div>
              )}

              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 18 }}>{p.desc}</p>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {p.tags.map(t => <OTag key={t}>{t}</OTag>)}
              </div>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Vendedor</div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <img loading="lazy" decoding="async" src={p.vendedor.avatarUrl} alt={p.vendedor.user} style={{ width: 46, height: 46, borderRadius: "50%", objectFit: "cover" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>@{p.vendedor.user}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>Rider desde {p.vendedor.rider_desde}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.vendedor.ventas} venta{p.vendedor.ventas !== 1 ? "s" : ""} en Olbos</div>
                  </div>
                </div>
              </div>

              <div style={{ background: "#FFF9F0", border: `1px solid ${C.gold}55`, borderRadius: 10, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <AlertTriangle size={14} strokeWidth={2} color="#B07D1A" style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 12, color: "#8B6914", lineHeight: 1.6 }}>Olbos no interviene en la transacción. Coordiná el pago y la entrega directamente con el vendedor. Verificá el estado del vehículo antes de pagar.</p>
              </div>

              <Btn full onClick={() => setSelPubli(null)}>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <MessageCircle size={16} strokeWidth={2} /> Contactar por DM
                </span>
              </Btn>
              <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 8 }}>Publicado {p.fecha}</p>
            </div>
          </div>
        );
      })()}
    </PageWrap>
  );
};

// ─── SECCIÓN: ROBADAS ─────────────────────────────────────────────────────────
const Robadas = ({ onAI, session, userMotos, isAdmin }) => {
  const [q, setQ] = useState("");
  const [alertas, setAlertas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [showDenunciar, setShowDenunciar] = useState(false);
  const [detalleAlerta, setDetalleAlerta] = useState(null);
  const [reportando, setReportando] = useState(null);
  const userId = session?.user?.id;

  // cargar alertas activas
  const cargarAlertas = async () => {
    setCargando(true);
    const { data } = await supabase.from("alertas_seguridad")
      .select("*")
      .eq("estado", "activa")
      .order("created_at", { ascending: false });
    setAlertas(data || []);
    setCargando(false);
  };

  useEffect(() => { cargarAlertas(); }, []);

  const onDenunciar = () => {
    if (!session) { alert("Tenés que iniciar sesión para crear una denuncia"); return; }
    setShowDenunciar(true);
  };

  const filtered = alertas.filter(m =>
    !q || [m.marca, m.modelo, m.patente, m.zona, m.ciudad].some(v => (v || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <PageWrap>
      <PageHeader label="Seguridad" title="Alertas" sub="Base colaborativa de la comunidad" onAI={onAI} />
      <div style={{ padding: "14px 18px" }}>
        <div style={{ background: "#C0392B10", border: `1px solid #C0392B40`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, display: "flex", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.red, marginBottom: 2 }}>Si ves una de estas motos</div>
            <div style={{ fontSize: 12, color: C.muted }}>No te confrontes. Anotá el lugar, llamá al 911 y avisá al dueño desde la app.</div>
          </div>
        </div>

        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por marca, patente, zona..."
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />

        {cargando ? (
          <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "50px 24px", textAlign: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FEE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
              <AlertTriangle size={32} strokeWidth={1.5} color="#E74C3C" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 10 }}>{q ? "Sin resultados" : "No hay alertas activas"}</h3>
            {!q && (
              <>
                <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 22 }}>
                  Si te robaron la moto o ves algo sospechoso, avisá a la comunidad. Entre todos nos cuidamos.
                </p>
                <button onClick={onDenunciar} style={{ padding: "12px 24px", borderRadius: 12, background: "#E74C3C", color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
                  Denunciar moto robada o alerta de seguridad 🚨
                </button>
                <p style={{ fontSize: 12, color: C.faint, marginTop: 18, fontStyle: "italic" }}>— Entre todos nos cuidamos</p>
              </>
            )}
          </div>
        ) : (
          filtered.map((a) => (
            <Card key={a.id} style={{ marginBottom: 12 }}>
              <div style={{ padding: 14 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  {a.foto_url ? (
                    <img loading="lazy" decoding="async" src={a.foto_url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 64, height: 64, background: "#C0392B15", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>🏍️</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "flex-start" }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{a.marca} {a.modelo}</h3>
                      {a.recompensa && <OTag>💰 {a.recompensa_moneda || "ARS"} {Number(a.recompensa).toLocaleString()}</OTag>}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{[a.año, a.color].filter(Boolean).join(" · ")}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      {a.patente && <OTag>{a.patente}</OTag>}
                      {a.zona && <span style={{ fontSize: 11, color: C.muted }}>📍 {a.zona}{a.ciudad ? `, ${a.ciudad}` : ""}</span>}
                      {a.fecha_evento && <span style={{ fontSize: 11, color: C.muted }}>📅 {new Date(a.fecha_evento + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
                  <Btn full variant="danger" small onClick={() => setReportando(a)}>🚨 La vi</Btn>
                  <Btn full variant="outline" small onClick={() => setDetalleAlerta(a)}>Más info</Btn>
                  <BtnCompartir texto={`🚨 MOTO ROBADA\n${a.marca} ${a.modelo} ${a.año || ""} · ${a.color || ""}\n${a.patente ? `Patente: ${a.patente}\n` : ""}${a.zona ? `Zona: ${a.zona}${a.ciudad ? ", " + a.ciudad : ""}\n` : ""}${a.fecha_evento ? `Fecha: ${a.fecha_evento}\n` : ""}\nSi la ves, avisá al dueño y llamá al 911.\n\nMás info en olbos.ai`} tipo="robada" id={a.id} small />
                </div>
                {(userId === a.user_id || isAdmin) && (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
                    <button onClick={async () => {
                      if (!window.confirm("¿Marcar como recuperada? Se ocultará de las alertas activas.")) return;
                      await supabase.from("alertas_seguridad").update({ estado: "recuperada", updated_at: new Date().toISOString() }).eq("id", a.id);
                      cargarAlertas();
                    }} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, background: `${C.green}14`, border: `1px solid ${C.green}33`, color: C.green, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ✓ Recuperada
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm("¿Eliminar esta alerta?")) return;
                      await supabase.from("alertas_seguridad").delete().eq("id", a.id);
                      cargarAlertas();
                    }} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, background: `${C.red}14`, border: `1px solid ${C.red}22`, color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}

        {filtered.length > 0 && (
          <button onClick={onDenunciar} style={{ width: "100%", padding: 14, borderRadius: 12, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 15, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
            ＋ Denunciar moto robada
          </button>
        )}
      </div>

      {showDenunciar && (
        <DenunciarRoboModal
          userId={userId}
          userMotos={userMotos}
          onClose={() => setShowDenunciar(false)}
          onCreada={() => { setShowDenunciar(false); cargarAlertas(); }}
        />
      )}

      {detalleAlerta && (
        <Modal title={`${detalleAlerta.marca} ${detalleAlerta.modelo}`} onClose={() => setDetalleAlerta(null)}>
          {detalleAlerta.foto_url && (
            <img loading="lazy" decoding="async" src={detalleAlerta.foto_url} alt="" style={{ width: "100%", borderRadius: 12, marginBottom: 12, maxHeight: 240, objectFit: "cover" }} />
          )}
          <div style={{ display: "grid", gap: 8 }}>
            {detalleAlerta.descripcion && (
              <div style={{ padding: 10, background: C.surface, borderRadius: 8, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{detalleAlerta.descripcion}</div>
            )}
            {[
              ["Año", detalleAlerta.año],
              ["Color", detalleAlerta.color],
              ["Patente", detalleAlerta.patente],
              ["Chasis", detalleAlerta.chasis],
              ["Motor", detalleAlerta.motor],
              ["Zona", detalleAlerta.zona],
              ["Ciudad", detalleAlerta.ciudad],
              ["Fecha", detalleAlerta.fecha_evento ? new Date(detalleAlerta.fecha_evento + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" }) : null],
              ["Recompensa", detalleAlerta.recompensa ? `${detalleAlerta.recompensa_moneda || "ARS"} ${Number(detalleAlerta.recompensa).toLocaleString()}` : null],
            ].filter(([_, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: C.muted }}>{k}</span>
                <span style={{ color: C.text, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            {detalleAlerta.contacto && (
              <a href={`https://wa.me/${detalleAlerta.contacto.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola, te escribo desde Olbos por tu ${detalleAlerta.marca} ${detalleAlerta.modelo} reportada como robada.`)}`}
                target="_blank" rel="noreferrer"
                style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 16px", borderRadius: 10, background: "#25D366", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
                Contactar al dueño por WhatsApp
              </a>
            )}
          </div>
        </Modal>
      )}

      {reportando && (
        <Modal title="¿Dónde la viste?" onClose={() => setReportando(null)}>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
            ⚠️ <strong style={{ color: C.text }}>No te confrontes</strong>. Anotá el lugar, llamá al 911 y enviá un WhatsApp al dueño con la ubicación.
          </p>
          {reportando.contacto ? (
            <a href={`https://wa.me/${reportando.contacto.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hola, vi tu ${reportando.marca} ${reportando.modelo}${reportando.patente ? " (patente " + reportando.patente + ")" : ""} reportada como robada en Olbos. Ubicación: `)}`}
              target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 16px", borderRadius: 10, background: "#25D366", color: "#fff", textDecoration: "none", fontWeight: 700, fontSize: 14 }}
              onClick={() => setReportando(null)}>
              Abrir WhatsApp del dueño
            </a>
          ) : (
            <div style={{ padding: 14, background: C.surface, borderRadius: 10, fontSize: 13, color: C.muted, textAlign: "center" }}>
              El dueño no dejó WhatsApp de contacto. Llamá al 911.
            </div>
          )}
        </Modal>
      )}
    </PageWrap>
  );
};

// ─── MODAL: CREAR DENUNCIA DE ROBO ─────────────────────────────────────────────
const DenunciarRoboModal = ({ userId, userMotos, onClose, onCreada }) => {
  const motosDisponibles = (userMotos || []).filter(m => m && m.marca && m.modelo);
  const [motoSel, setMotoSel] = useState(motosDisponibles.length === 1 ? motosDisponibles[0] : null);
  const [paso, setPaso] = useState(motosDisponibles.length > 0 ? 0 : 1); // 0: elegir moto, 1: completar
  const [form, setForm] = useState({
    marca: "", modelo: "", año: "", color: "", patente: "", chasis: "", motor: "",
    zona: "", ciudad: "", fecha_evento: hoyLocalISO(),
    descripcion: "", contacto: "", recompensa: "", recompensa_moneda: "ARS", foto_url: ""
  });
  const [guardando, setGuardando] = useState(false);

  // si elegís moto del Garage, pre-llenar
  useEffect(() => {
    if (motoSel) {
      setForm(f => ({
        ...f,
        marca: motoSel.marca || "",
        modelo: motoSel.modelo || "",
        año: motoSel.año || "",
        color: motoSel.color || "",
        foto_url: motoSel.fotoUrl || ((motoSel.media || []).find(x => x.type === "image")?.url) || "",
      }));
    }
  }, [motoSel]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.marca || !form.modelo) { alert("Marca y modelo son obligatorios"); return; }
    setGuardando(true);
    const { error } = await supabase.from("alertas_seguridad").insert({
      user_id: userId,
      moto_id: motoSel?.id || null,
      tipo: "moto_robada",
      marca: form.marca, modelo: form.modelo, año: form.año || null, color: form.color || null,
      patente: form.patente || null, chasis: form.chasis || null, motor: form.motor || null,
      zona: form.zona || null, ciudad: form.ciudad || null,
      fecha_evento: form.fecha_evento || null,
      descripcion: form.descripcion || null,
      foto_url: form.foto_url || null,
      contacto: form.contacto || null,
      recompensa: form.recompensa ? Number(form.recompensa) : null,
      recompensa_moneda: form.recompensa_moneda,
      estado: "activa",
    });
    setGuardando(false);
    if (error) { alert("Error: " + error.message); return; }
    onCreada();
  };

  return (
    <Modal title="🚨 Denunciar moto robada" onClose={onClose}>
      {paso === 0 && motosDisponibles.length > 0 && (
        <div>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>¿Es una de tus motos del Garage?</p>
          {motosDisponibles.map((m, i) => (
            <button key={m.id || i} onClick={() => { setMotoSel(m); setPaso(1); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, cursor: "pointer", marginBottom: 8, textAlign: "left" }}>
              {m.fotoUrl ? (
                <img src={cldThumb(m.fotoUrl, 120)} alt="" loading="lazy" decoding="async" style={{ width: 50, height: 40, borderRadius: 6, objectFit: "cover" }} />
              ) : (
                <div style={{ width: 50, height: 40, borderRadius: 6, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Bike size={20} color={C.muted} />
                </div>
              )}
              <div style={{ flex: 1 }}>
                {m.nombre && <div style={{ fontSize: 11, color: C.text, fontWeight: 700, fontStyle: "italic" }}>"{m.nombre}"</div>}
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{m.marca} {m.modelo}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{[m.año, m.color].filter(Boolean).join(" · ")}</div>
              </div>
              <ChevronRight size={18} color={C.muted} />
            </button>
          ))}
          <button onClick={() => setPaso(1)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: `1px dashed ${C.border}`, background: "transparent", color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 6 }}>
            Es otra moto / cargar manual
          </button>
        </div>
      )}

      {paso === 1 && (
        <div style={{ display: "grid", gap: 10 }}>
          {motoSel && (
            <div style={{ padding: 10, background: "#FFF5F0", borderRadius: 8, fontSize: 12, color: "#C0392B" }}>
              📦 Datos pre-cargados de tu moto del Garage. Completá los demás.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Input label="Marca *" value={form.marca} onChange={e => set("marca", e.target.value)} />
            <Input label="Modelo *" value={form.modelo} onChange={e => set("modelo", e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Input label="Año" value={form.año} onChange={e => set("año", e.target.value)} />
            <Input label="Color" value={form.color} onChange={e => set("color", e.target.value)} />
          </div>
          <Input label="Patente" value={form.patente} onChange={e => set("patente", e.target.value)} placeholder="Ej: AB123CD" />
          <div style={{ display: "flex", gap: 8 }}>
            <Input label="Nº chasis" value={form.chasis} onChange={e => set("chasis", e.target.value)} />
            <Input label="Nº motor" value={form.motor} onChange={e => set("motor", e.target.value)} />
          </div>
          <Input label="Zona del robo *" value={form.zona} onChange={e => set("zona", e.target.value)} placeholder="Ej: Av. Cabildo y Juramento" />
          <Input label="Ciudad" value={form.ciudad} onChange={e => set("ciudad", e.target.value)} placeholder="Ej: Belgrano, CABA" />
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha del robo</div>
            <input type="date" value={form.fecha_evento} onChange={e => set("fecha_evento", e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción / circunstancias</div>
            <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3}
              placeholder="Detalles que ayuden a identificarla, marcas, accesorios, sticker, daños..."
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }} />
          </div>
          <Input label="WhatsApp de contacto *" value={form.contacto} onChange={e => set("contacto", e.target.value)} placeholder="Ej: +54 9 11 1234 5678" />
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Recompensa (opcional)</div>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.recompensa_moneda} onChange={e => set("recompensa_moneda", e.target.value)}
                style={{ padding: "10px 10px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, background: C.card }}>
                <option value="ARS">$ ARS</option>
                <option value="USD">USD</option>
              </select>
              <input type="number" value={form.recompensa} onChange={e => set("recompensa", e.target.value)}
                placeholder="Monto"
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>
          </div>
          {!motoSel && (
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>URL de foto (opcional)</div>
              <input value={form.foto_url} onChange={e => set("foto_url", e.target.value)}
                placeholder="Pegá una URL de foto de la moto"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button onClick={onClose}
              style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Cancelar
            </button>
            <button onClick={guardar} disabled={guardando || !form.marca || !form.modelo}
              style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#E74C3C", color: "#fff", fontWeight: 700, fontSize: 14, cursor: guardando ? "default" : "pointer", opacity: (guardando || !form.marca || !form.modelo) ? 0.55 : 1 }}>
              {guardando ? "Publicando…" : "Publicar denuncia"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─── SECCIÓN: PERFIL ──────────────────────────────────────────────────────────
// Etiquetas de servicios: lo que un negocio OFRECE, cruza categorías (un concesionario
// puede tener test rides; una pista, cursos). Alimentan los chips del listado unificado.
const TAGS_NEGOCIO = ["Taller oficial", "Test rides", "Cursos de manejo", "Circuito enduro", "Circuito motocross", "Pista asfalto", "Alquiler de equipo"];

// ─── CREAR NEGOCIO MODAL ──────────────────────────────────────────────────────
const CrearNegocioModal = ({ userId, onClose, onSuccess }) => {
  const [paso, setPaso] = useState(1);
  const [form, setForm] = useState({ nombre: "", categoria: "", descripcion: "", ciudad: "", direccion: "", telefono: "", whatsapp: "", web: "", instagram: "", tags: [] });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const categorias = [
    { id: "taller", label: "🔧 Taller mecánico" },
    { id: "concesionario", label: "🏪 Concesionario" },
    { id: "gromeria", label: "🛞 Gomería" },
    { id: "alquiler", label: "🗺️ Alquiler de motos" },
    { id: "accesorios", label: "🛡️ Accesorios / Indumentaria" },
    { id: "grua", label: "🚛 Grúa / Traslado" },
    { id: "seguro", label: "📋 Seguros" },
    { id: "gestoria", label: "📄 Gestoría / Trámites" },
    { id: "marca", label: "🏍️ Marca / Importadora" },
    { id: "motoescuela", label: "🎓 Instructor / Escuela de manejo" },
    { id: "pista", label: "🏁 Pista / Circuito" },
    { id: "restaurante", label: "🍽️ Restaurante / Parador" },
    { id: "hospedaje", label: "🛏️ Hospedaje / Motoposada" },
  ];

  const handleLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleGuardar = async () => {
    // Ciudad obligatoria: seguros/gestorías sin dirección física necesitan al menos
    // desde dónde operan para aparecer bien en el listado y el orden por cercanía
    if (!form.nombre || !form.categoria || !(form.ciudad || "").trim()) { setError(!(form.ciudad || "").trim() && form.nombre && form.categoria ? "Cargá al menos la ciudad desde donde operan" : "Completá nombre, categoría y ciudad"); return; }
    setGuardando(true); setError("");
    try {
      let logoUrl = null;
      if (logo) {
        const fd = new FormData();
        fd.append("file", logo);
        fd.append("upload_preset", "olbos_uploads");
        const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
        const data = await res.json();
        logoUrl = data.secure_url;
      }
      const r = await registrarNegocioServidor(null, {
        nombre: form.nombre, categoria: normalizarCategoria(form.categoria),
        descripcion: form.descripcion, ciudad: form.ciudad, direccion: form.direccion,
        telefono: form.telefono, whatsapp: form.whatsapp, web: form.web,
        instagram: form.instagram, logo_url: logoUrl,
        tags: Array.isArray(form.tags) ? form.tags : [],
      });
      if (r.error) throw new Error(r.error);
      const { data } = await supabase.from("negocios").select("*").eq("id", r.id).maybeSingle();
      onSuccess(data || { id: r.id, nombre: form.nombre, estado: "pendiente" });
    } catch (e) { setError("No se pudo guardar: " + (e.message || e) + ". Intentá de nuevo o escribinos por WhatsApp."); }
    setGuardando(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
      <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", maxHeight: "92vh", overflowY: "auto" }}>
        {/* header */}
        <div style={{ padding: "calc(var(--sat) + 20px) 20px 16px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, zIndex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Registrá tu negocio</h2>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={16} strokeWidth={2} color={C.muted} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3].map(p => (
              <div key={p} style={{ flex: 1, height: 3, borderRadius: 2, background: p <= paso ? C.text : C.faint }} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            Paso {paso} de 3 — {paso === 1 ? "Datos básicos" : paso === 2 ? "Ubicación y contacto" : "Confirmar"}
          </div>
        </div>

        <div style={{ padding: "16px 20px 32px" }}>
          {/* PASO 1 */}
          {paso === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* logo */}
              <label style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
                <div style={{ width: 64, height: 64, borderRadius: 14, border: `2px dashed ${C.border}`, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                  {logoPreview ? <img loading="lazy" decoding="async" src={logoPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Camera size={22} strokeWidth={1.5} color={C.muted} />}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Logo del negocio</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Tocá para subir una imagen</div>
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogo} />
              </label>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Nombre del negocio *</div>
                <input value={form.nombre} onChange={e => set("nombre", e.target.value)} placeholder="Ej: Gomería Los Andes"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text }} />
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Categoría *</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {categorias.map(c => (
                    <button key={c.id} onClick={() => set("categoria", c.id)}
                      style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${form.categoria === c.id ? C.text : C.border}`, background: form.categoria === c.id ? C.text : C.card, color: form.categoria === c.id ? "#fff" : C.text, fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left" }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>¿Qué ofrecen? (opcional, mejora cómo te encuentran)</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {TAGS_NEGOCIO.map(t => {
                    const sel = (form.tags || []).includes(t);
                    return (
                      <button key={t} onClick={() => set("tags", sel ? form.tags.filter(x => x !== t) : [...(form.tags || []), t])}
                        style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${sel ? C.text : C.border}`, background: sel ? C.text : C.card, color: sel ? "#fff" : C.muted, cursor: "pointer" }}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Descripción</div>
                <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3}
                  placeholder="Contá qué ofrecen y qué los diferencia..."
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", resize: "none", fontFamily: "inherit", color: C.text }} />
              </div>
            </div>
          )}

          {/* PASO 2 */}
          {paso === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                ["ciudad", "Ciudad *", "Buenos Aires, Mendoza..."],
                ["direccion", "Dirección", "Av. Corrientes 1234"],
                ["telefono", "Teléfono", "011-4567-8901"],
                ["whatsapp", "WhatsApp (sin + ni espacios)", "5491145678901"],
                ["instagram", "Instagram", "@tunegocio"],
                ["web", "Sitio web", "www.tunegocio.com"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
                  <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text }} />
                </div>
              ))}
            </div>
          )}

          {/* PASO 3 — confirmación */}
          {paso === 3 && (
            <div>
              <div style={{ background: C.surface, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
                  {logoPreview && <img loading="lazy" decoding="async" src={logoPreview} alt="" style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />}
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{form.nombre}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{categorias.find(c => c.id === form.categoria)?.label}</div>
                  </div>
                </div>
                {[["📍", form.ciudad], ["📞", form.telefono], ["💬", form.whatsapp ? `wa.me/${form.whatsapp}` : null], ["📸", form.instagram]].filter(([,v]) => v).map(([icon, val]) => (
                  <div key={val} style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{icon} {val}</div>
                ))}
              </div>
              {/* Sin "planes": registrar el negocio es gratis y punto (decisión 26-ago / 02-sep) */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}><strong style={{ color: C.text }}>Registrar tu negocio es gratis.</strong> Tu ficha queda en revisión y en 24-48 hs te confirmamos por email: desde ahí aparece en el Pit Stop, en el mapa y en el navegador.</div>
              </div>
            </div>
          )}

          {error && <div style={{ marginTop: 12, padding: "10px 14px", background: `${C.red}14`, borderRadius: 10, fontSize: 13, color: C.red }}>{error}</div>}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            {paso > 1 && (
              <button onClick={() => setPaso(p => p - 1)}
                style={{ flex: 1, padding: "14px 0", borderRadius: 14, background: C.surface, color: C.text, fontSize: 15, fontWeight: 600, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                Atrás
              </button>
            )}
            <button onClick={() => paso < 3 ? setPaso(p => p + 1) : handleGuardar()} disabled={guardando}
              style={{ flex: 2, padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", opacity: guardando ? 0.7 : 1 }}>
              {guardando ? "Guardando..." : paso < 3 ? "Continuar →" : "Registrar negocio"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── PASAPORTE OLBOS (Capa 1: pasaporte personal básico) ──────────────────
// Registro de aventuras del rider: km totales y rodadas completadas, calculados
// desde sus trazas finalizadas (verificable por GPS, no auto-declarado).
// Avatar neutro para cuentas sin foto: silueta sin género en colores de marca
// (antes había una foto genérica de un varón — feedback Cata 13-ago)
const AVATAR_FALLBACK = "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#F1ECE6"/><circle cx="50" cy="38" r="16" fill="#C9C3BA"/><path d="M50 60c-17 0-28 9-30 22 8 11 60 11 60 0-2-13-13-22-30-22z" fill="#C9C3BA"/></svg>');
// Avatar para cuentas sin foto: círculo crema con la inicial del usuario (pedido de Mati, 27-oct-2026)
const avatarInicial = (nombre) => {
  const ini = (String(nombre || "").replace(/^@/, "").trim()[0] || "").toUpperCase();
  if (!ini) return AVATAR_FALLBACK;
  return "data:image/svg+xml," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#E7E1DA"/><text x="50" y="50" text-anchor="middle" dominant-baseline="central" font-family="Archivo, Arial, sans-serif" font-weight="700" font-size="46" fill="#111111">' + ini.replace(/[<>&"']/g, "") + '</text></svg>');
};

// Sellos globales de Olbos. Sobrios a propósito (ver "no sobre-gamificar" en el
// documento de diseño): son hitos de ruta, no logros de videojuego. Todos se validan
// contra recorridos grabados, así que un sello cuesta salir a andar.
const SELLOS_OLBOS = [
  { id: "primera",    emoji: "🏁", nombre: "Primera salida",  pista: "Grabá tu primer recorrido",             cumple: s => s.rodadas >= 1 },
  { id: "km100",      emoji: "🛣️", nombre: "100 km",          pista: "Sumá 100 km grabados",                  cumple: s => s.km >= 100 },
  { id: "km500",      emoji: "🛣️", nombre: "500 km",          pista: "Sumá 500 km grabados",                  cumple: s => s.km >= 500 },
  { id: "km1000",     emoji: "🏆", nombre: "1.000 km",        pista: "Sumá 1.000 km grabados",                cumple: s => s.km >= 1000 },
  { id: "km5000",     emoji: "🏆", nombre: "5.000 km",        pista: "Sumá 5.000 km grabados",                cumple: s => s.km >= 5000 },
  { id: "km10000",    emoji: "👑", nombre: "10.000 km",       pista: "Sumá 10.000 km grabados",               cumple: s => s.km >= 10000 },
  { id: "salidas10",  emoji: "🔁", nombre: "10 salidas",      pista: "Grabá 10 recorridos",                   cumple: s => s.rodadas >= 10 },
  { id: "salidas50",  emoji: "🔁", nombre: "50 salidas",      pista: "Grabá 50 recorridos",                   cumple: s => s.rodadas >= 50 },
  { id: "larga200",   emoji: "🌄", nombre: "200 de un tirón", pista: "Hacé 200 km en una sola salida",        cumple: s => s.masLargo >= 200 },
  { id: "larga500",   emoji: "🌄", nombre: "500 de un tirón", pista: "Hacé 500 km en una sola salida",        cumple: s => s.masLargo >= 500 },
  { id: "madrugador", emoji: "🌅", nombre: "Madrugador",      pista: "Arrancá una salida antes de las 7",     cumple: s => s.madrugadas >= 1 },
  { id: "nocturno",   emoji: "🌙", nombre: "Nocturno",        pista: "Terminá una salida después de las 22",  cumple: s => s.nocturnas >= 1 },
  { id: "grupo1",     emoji: "🤝", nombre: "En grupo",        pista: "Grabá una rodada con otros riders",     cumple: s => s.enGrupo >= 1 },
  { id: "grupo10",    emoji: "🤝", nombre: "10 en grupo",     pista: "Grabá 10 rodadas con otros riders",     cumple: s => s.enGrupo >= 10 },
];
const sellosDe = (stats) => (stats ? SELLOS_OLBOS.filter(x => { try { return x.cumple(stats); } catch (e) { return false; } }) : []);

const PasaporteModal = ({ perfil, stats, onCerrar }) => {
  const fmt = (n) => Math.round(n || 0).toLocaleString("es-AR");
  const cover = "linear-gradient(135deg,#111111 0%,#3a2a1a 100%)";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, background: C.surface, overflowY: "auto" }}>
      {/* Tapa del pasaporte */}
      <div style={{ background: cover, color: "#fff", padding: "52px 22px 28px", position: "relative" }}>
        <button onClick={onCerrar} style={{ position: "absolute", top: "max(44px, calc(var(--sat) + 12px))", right: 18, width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", color: "#fff", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        <div style={{ fontSize: 11, color: "rgba(249,245,242,.6)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 16 }}>🛂 Pasaporte Olbos</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img loading="lazy" decoding="async" src={perfil?.avatar_url || avatarInicial(perfil?.username)} alt="" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,.3)", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em" }}>@{perfil?.username || "rider"}</div>
            {stats?.desde && (
              <div style={{ fontSize: 12, color: "#bbb", marginTop: 3 }}>
                Rodando en Olbos desde {new Date(stats.desde).toLocaleDateString("es-AR", { month: "long", year: "numeric" })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ padding: "20px 18px 48px", maxWidth: 430, margin: "0 auto" }}>
        {/* Stats principales */}
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          {[
            { emoji: "🛣️", label: "Km totales", val: stats ? fmt(stats.km) : "—" },
            { emoji: "🏁", label: "Rodadas completadas", val: stats ? stats.rodadas : "—" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, background: C.card, borderRadius: 16, padding: "18px 14px", border: `1px solid ${C.border}`, textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.emoji}</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginTop: 6, letterSpacing: "-0.005em" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Stats secundarios (sobrios, sin gamificar) */}
        <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
          {[
            { label: "Tiempo en ruta", val: stats ? formatDur(stats.tiempo) : "—" },
            { label: "Recorrido más largo", val: stats ? `${stats.masLargo.toFixed(1)} km` : "—" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, background: C.card, borderRadius: 14, padding: "12px 14px", border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{s.val}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Nota: verificable por GPS */}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 14 }}>
          <span style={{ fontSize: 16 }}>📡</span>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
            Tu pasaporte se llena solo: cada km y cada rodada salen del GPS de tus recorridos grabados. No es lo que decís que hiciste, es lo que recorriste de verdad.
          </div>
        </div>

        {/* Sellos: los conseguidos en color, los que faltan en gris con la consigna.
            Mostrar los que faltan es la mitad del valor — es lo que da la próxima excusa
            para salir a andar. */}
        {(() => {
          const logrados = sellosDe(stats);
          const ids = logrados.map(x => x.id);
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
                Sellos · {logrados.length} de {SELLOS_OLBOS.length}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
                {SELLOS_OLBOS.map(sl => {
                  const tiene = ids.includes(sl.id);
                  return (
                    <div key={sl.id} title={sl.pista}
                      style={{ background: tiene ? C.card : C.surface, border: `1px ${tiene ? "solid" : "dashed"} ${C.border}`, borderRadius: 12, padding: "12px 8px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, marginBottom: 5, filter: tiene ? "none" : "grayscale(1)", opacity: tiene ? 1 : 0.35 }}>{sl.emoji}</div>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: tiene ? C.text : C.muted, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{sl.nombre}</div>
                      {!tiene && <div style={{ fontSize: 10, color: C.faint, marginTop: 4, lineHeight: 1.3 }}>{sl.pista}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                Cada sello sale de tus recorridos grabados. No se piden ni se compran: se andan.
              </div>
            </div>
          );
        })()}

        <SellosOrgPasaporte userId={perfil?.id} />

        {stats && stats.rodadas === 0 && (
          <div style={{ textAlign: "center", color: C.muted, fontSize: 13, lineHeight: 1.6, marginTop: 20 }}>
            Todavía no tenés recorridos grabados.<br/>Grabá tu primera salida y empezá a llenar tu pasaporte 🏍️
          </div>
        )}
      </div>
    </div>
  );
};

// Avisar por la campanita cuando aparece un sello nuevo. La primera vez NO avisa:
// solo anota lo que el rider ya tenía, para no dispararle ocho notificaciones juntas
// a alguien que viene rodando hace meses. La notificación queda en el historial de la
// campanita, así que se puede volver a mirar (y es una razón concreta para volver).
const avisarSellosNuevos = (userId, stats) => {
  try {
    const clave = "olbos_sellos_" + userId;
    const ids = sellosDe(stats).map(x => x.id);
    const previos = JSON.parse(localStorage.getItem(clave) || "null");
    localStorage.setItem(clave, JSON.stringify(ids));
    if (!Array.isArray(previos)) return; // primera vez: se toma estado, no se avisa
    const nuevos = SELLOS_OLBOS.filter(x => ids.includes(x.id) && !previos.includes(x.id));
    nuevos.forEach(sl => {
      supabase.from("notificaciones").insert({
        user_id: userId,
        tipo: "sello",
        from_username: "olbos",
        texto: `${sl.emoji} Conseguiste el sello «${sl.nombre}». Está en tu pasaporte.`,
      }).then(() => {}, () => {});
    });
  } catch (e) { /* los sellos nunca rompen el perfil */ }
};

// Tarjeta del hub del Perfil: ícono, título y una línea de qué hay adentro. Las que
// abren un bloque debajo (acordeón) marcan `activa` y giran la flechita.
const TarjetaHubPerfil = ({ Icono, titulo, linea, onClick, activa, acordeon }) => (
  <button onClick={onClick}
    style={{ textAlign: "left", cursor: "pointer", padding: "13px 14px", borderRadius: 14, background: C.card, border: `1px solid ${activa ? C.text : C.border}`, display: "flex", flexDirection: "column", gap: 9, minWidth: 0 }}>
    <div style={{ width: 34, height: 34, borderRadius: 10, background: activa ? C.accent : C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icono size={17} strokeWidth={2} color={activa ? "#F9F5F2" : C.text} />
    </div>
    <div style={{ minWidth: 0, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>{titulo}</span>
        {acordeon
          ? <ChevronDown size={14} strokeWidth={2.2} color={C.muted} style={{ flexShrink: 0, transform: activa ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
          : <ChevronRight size={14} strokeWidth={2.2} color={C.muted} style={{ flexShrink: 0 }} />}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 2 }}>{linea}</div>
    </div>
  </button>
);

// Tarjeta teaser en el perfil: calcula los stats desde las trazas y abre el pasaporte.
// Con variante="hub" es una tarjeta chica para la grilla del Perfil (mismo modal).
const PasaporteCard = ({ userId, perfil, variante }) => {
  const [abierto, setAbierto] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    (async () => {
      const { data } = await supabase.from("trazas")
        .select("distancia_km, duracion_seg, iniciada_en, finalizada_en, evento_id, grupo_id")
        .eq("user_id", userId).eq("estado", "finalizada");
      if (!vivo) return;
      const ts = data || [];
      let km = 0, tiempo = 0, masLargo = 0, desde = null, madrugadas = 0, nocturnas = 0, enGrupo = 0;
      ts.forEach(t => {
        const d = Number(t.distancia_km) || 0;
        km += d;
        tiempo += Number(t.duracion_seg) || 0;
        if (d > masLargo) masLargo = d;
        const f = t.iniciada_en || t.finalizada_en;
        if (f && (!desde || f < desde)) desde = f;
        // Hora local del teléfono: es la que vivió el rider
        if (t.iniciada_en && new Date(t.iniciada_en).getHours() < 7) madrugadas++;
        if (t.finalizada_en && new Date(t.finalizada_en).getHours() >= 22) nocturnas++;
        if (t.evento_id || t.grupo_id) enGrupo++;
      });
      const nuevos = { km, rodadas: ts.length, tiempo, masLargo, desde, madrugadas, nocturnas, enGrupo };
      setStats(nuevos);
      avisarSellosNuevos(userId, nuevos);
    })();
    return () => { vivo = false; };
  }, [userId]);

  // "Pasaporte" era un nombre interno que el usuario nuevo no entendía (feedback de
  // Franco, 08-oct): en el Perfil se llama por lo que es, tus km y rodadas.
  if (variante === "hub") {
    const nSellos = stats ? sellosDe(stats).length : 0;
    const linea = stats
      ? `${Math.round(stats.km).toLocaleString("es-AR")} km · ${stats.rodadas} rodada${stats.rodadas === 1 ? "" : "s"} · ${nSellos} sello${nSellos === 1 ? "" : "s"}`
      : "Lo que recorriste de verdad, por GPS";
    return (
      <>
        <TarjetaHubPerfil Icono={TrendingUp} titulo="Tus km y rodadas" linea={linea} onClick={() => setAbierto(true)} />
        {abierto && <PasaporteModal perfil={perfil} stats={stats} onCerrar={() => setAbierto(false)} />}
      </>
    );
  }

  return (
    <>
      <div style={{ padding: "16px 18px 0" }}>
        <button onClick={() => setAbierto(true)} style={{
          width: "100%", textAlign: "left", cursor: "pointer", border: "none", padding: 0,
          borderRadius: 16, overflow: "hidden",
          background: "linear-gradient(135deg,#111111 0%,#3a2a1a 100%)",
          boxShadow: "0 2px 12px rgba(0,0,0,.12)",
        }}>
          <div style={{ padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 30, flexShrink: 0 }}>🛂</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "rgba(249,245,242,.6)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Tus km y rodadas</div>
              <div style={{ display: "flex", gap: 20, marginTop: 8 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>{stats ? Math.round(stats.km).toLocaleString("es-AR") : "—"}</div>
                  <div style={{ fontSize: 9.5, color: "#aaa", fontWeight: 600, marginTop: 3, letterSpacing: 0.5 }}>KM TOTALES</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>{stats ? stats.rodadas : "—"}</div>
                  <div style={{ fontSize: 9.5, color: "#aaa", fontWeight: 600, marginTop: 3, letterSpacing: 0.5 }}>RODADAS</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>{stats ? sellosDe(stats).length : "—"}</div>
                  <div style={{ fontSize: 9.5, color: "#aaa", fontWeight: 600, marginTop: 3, letterSpacing: 0.5 }}>SELLOS</div>
                </div>
              </div>
            </div>
            <span style={{ fontSize: 22, color: "#888", flexShrink: 0 }}>›</span>
          </div>
        </button>
      </div>
      {abierto && <PasaporteModal perfil={perfil} stats={stats} onCerrar={() => setAbierto(false)} />}
    </>
  );
};

// Caché en memoria del perfil propio y sus mantenimientos: la pestaña Perfil se
// desmonta al cambiar de tab y volvía a arrancar VACÍA varios segundos mientras
// re-consultaba todo (feedback 13-ago). Con el caché, al volver renderiza al
// instante lo último conocido y refresca por atrás.
let _cachePerfilPropio = null;
let _cacheMantenimientos = null;
// Precarga del perfil propio apenas hay sesión: cuando el usuario toca Garage por
// primera vez, los datos ya están en memoria en vez de arrancar la consulta recién ahí.
const prefetchPerfilPropio = (userId) => {
  if (_cachePerfilPropio || !userId) return;
  supabase.from("perfiles").select("*").eq("id", userId).maybeSingle()
    .then(({ data }) => { if (data) _cachePerfilPropio = { ...data, motos: data.motos || [] }; }, () => {});
};

const Perfil = ({ isAdmin, setIsAdmin, onAdmin }) => {
  const [perfil, setPerfil] = useState(_cachePerfilPropio || { username: "", bio: "", avatar_url: "", ciudad: "", motos: [] });
  const [editando, setEditando] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [ficha, setFicha] = useState(null);       // ficha de rider cargada (rider_fichas)
  const [fichaForm, setFichaForm] = useState({});  // edición de la ficha
  const [showQREmergencia, setShowQREmergencia] = useState(false); // QR médico para el casco
  const [guardando, setGuardando] = useState(false);
  const [showAddMoto, setShowAddMoto] = useState(false);
  const [newMoto, setNewMoto] = useState({ marca: "", modelo: "", año: "", color: "", cc: "", fotoUrl: null, nombre: "", media: [] });
  const [mantenimientos, setMantenimientos] = useState(_cacheMantenimientos || {});  // { motoId: [...registros] }
  const [planMantenimiento, setPlanMantenimiento] = useState({}); // { motoId: { items: [...] } }
  const [generandoPlan, setGenerandoPlan] = useState({}); // { motoId: true } durante la generación
  const [motoExpandida, setMotoExpandida] = useState(null);  // motoId con ficha abierta
  const [showAddMant, setShowAddMant] = useState(null);      // motoId para agregar registro
  const [showVenderMoto, setShowVenderMoto] = useState(null); // moto a vender
  const [showDenunciarRobo, setShowDenunciarRobo] = useState(null); // moto a denunciar como robada
  // tipo: "venta" (la de siempre) o "alquiler" (prestás tu moto y arreglás con el rider)
  const [ventaForm, setVentaForm] = useState({ precio: "", moneda: "ARS", descripcion: "", km: "", tipo: "venta", periodo: "dia", zona: "", requisitos: "", ciudad: "" });
  const [publicandoVenta, setPublicandoVenta] = useState(false);
  const [editandoMant, setEditandoMant] = useState(null);    // { motoId, registro } para editar
  const [newMant, setNewMant] = useState({ tipo: "", descripcion: "", fecha: hoyLocalISO(), km: "", costo: "", lugar: "", moneda: "ARS", fotos: [] });
  const [subiendo, setSubiendo] = useState(false);
  const [showCambiarPass, setShowCambiarPass] = useState(false);
  const [exportando, setExportando] = useState(false);
  // Hub del perfil: qué bloque está abierto debajo de las tarjetas ("seguridad" | "cuenta" | null)
  const [seccionAbierta, setSeccionAbierta] = useState(null);
  const [verMiActividad, setVerMiActividad] = useState(false);
  // ¿Es admin de verdad? (es_admin en su perfil). El toggle "Modo Administrador"
  // solo se le muestra a estas cuentas — antes lo veía cualquier usuario.
  const esAdminReal = !!perfil?.es_admin;
  // Mantener el caché al día con cada cambio (alta de moto, service nuevo, edición…)
  useEffect(() => { if (perfil && perfil.username) _cachePerfilPropio = perfil; }, [perfil]);
  useEffect(() => { if (Object.keys(mantenimientos).length > 0) _cacheMantenimientos = mantenimientos; }, [mantenimientos]);

  // Incluye los ítems del plan de mantenimiento para que "✓ Hecho" pueda preseleccionarlos
  const TIPOS_MANT = ["Aceite de motor", "Filtro de aire", "Bujías", "Kit de transmisión",
    "Cubierta delantera", "Cubierta trasera", "Pastillas freno delantero", "Pastillas freno trasero",
    "Líquido de frenos", "Refrigerante", "Ajuste de válvulas",
    "Aceite y filtros", "Cubiertas", "Cadena", "Frenos", "Service", "Revisión general", "Modificación", "Reparación", "Otro"];

  // Diccionario de nombres legibles de tipos de mantenimiento (debe matchear con tabla mant_tipos)
  const MANT_NOMBRES = {
    aceite_motor: "Aceite de motor",
    filtro_aire: "Filtro de aire",
    bujia: "Bujías",
    kit_transmision: "Kit de transmisión",
    cubierta_delantera: "Cubierta delantera",
    cubierta_trasera: "Cubierta trasera",
    pastillas_delanteras: "Pastillas freno delantero",
    pastillas_traseras: "Pastillas freno trasero",
    liquido_frenos: "Líquido de frenos",
    refrigerante: "Refrigerante",
    valvulas: "Ajuste de válvulas",
  };

  const [negociosDB, setNegociosDB] = useState([]);

  const cargarMantenimientos = async (motoId, motoNombre) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from("moto_mantenimientos")
      .select("*")
      .eq("user_id", user.id)
      .eq("moto_id", String(motoId))
      // OJO: fecha es un DATE. Si se ordena solo por fecha, dos registros del MISMO día
      // quedan en orden arbitrario y el km recién cargado puede aparecer segundo — por
      // eso el contador "no se actualizaba" al corregirlo el mismo día (reporte de Carlos).
      .order("fecha", { ascending: false })
      .order("created_at", { ascending: false });
    setMantenimientos(prev => ({ ...prev, [motoId]: data || [] }));
  };

  // Cargar plan de mantenimiento de una moto (si existe)
  const cargarPlanMoto = async (moto) => {
    const { data: raw } = await supabase.rpc("get_plan_moto", { p_moto_id: String(moto.id) });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && Array.isArray(data.items) && data.items.length > 0) {
      setPlanMantenimiento(prev => ({ ...prev, [moto.id]: data }));
      return;
    }
    // Si no tiene plan personalizado, intentamos buscar el plan modelo
    const { data: rawModelo } = await supabase.rpc("buscar_plan_modelo", {
      p_marca: moto.marca,
      p_modelo: moto.modelo,
      p_anio: moto.año ? Number(moto.año) : null,
    });
    const modelo = typeof rawModelo === "string" ? JSON.parse(rawModelo) : rawModelo;
    if (modelo?.encontrado && modelo.items?.length > 0) {
      setPlanMantenimiento(prev => ({ ...prev, [moto.id]: { items: modelo.items, observaciones: modelo.observaciones, basado_en: modelo.plan_id, _solo_modelo: true } }));
    }
  };

  // Generar plan con IA
  const generarPlanConIA = async (moto) => {
    setGenerandoPlan(prev => ({ ...prev, [moto.id]: true }));
    const prompt = `Sos un experto en mantenimiento de motos. Para una ${moto.marca} ${moto.modelo}${moto.año ? ` ${moto.año}` : ""}${moto.cc ? ` de ${moto.cc}cc` : ""}, devolveme el plan de mantenimiento recomendado.

Devolveme SOLO un JSON con este formato exacto, sin texto adicional, sin backticks, sin explicaciones:
{
  "items": [
    {"tipo_id": "aceite_motor", "cada_km": 5000, "cada_meses": 6, "notas": "Aceite mineral 10W-40 o semi-sintético"},
    {"tipo_id": "filtro_aire", "cada_km": 12000, "cada_meses": 12, "notas": ""},
    ...
  ],
  "observaciones": "Comentario general sobre este modelo (mantenimientos específicos, tips, peculiaridades)"
}

Los tipo_id válidos son: aceite_motor, filtro_aire, bujia, kit_transmision, cubierta_delantera, cubierta_trasera, pastillas_delanteras, pastillas_traseras, liquido_frenos, refrigerante, valvulas

Si el modelo no tiene refrigerante líquido, no incluyas "refrigerante". Si la moto no es de cilindrada/diseño que use kit, no lo incluyas. Sé realista con las frecuencias (en km y meses). Si no conocés exactamente este modelo, devolvé valores genéricos pero razonables para una moto de esa cilindrada/categoría, y mencionalo en "observaciones".`;

    try {
      const res = await fetch(API_BASE + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1500,
          system: "Sos un asistente experto en motos. Respondés ÚNICAMENTE con JSON válido, sin texto adicional.",
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "";
      // Intentar parsear el JSON
      let plan;
      try {
        const limpio = reply.trim().replace(/^```json?\s*/i, "").replace(/```\s*$/, "");
        plan = JSON.parse(limpio);
      } catch (e) {
        alert("La IA devolvió un formato inválido. Probá de nuevo.");
        setGenerandoPlan(prev => ({ ...prev, [moto.id]: false }));
        return;
      }
      if (!plan?.items || plan.items.length === 0) {
        alert("La IA no devolvió items válidos. Probá de nuevo.");
        setGenerandoPlan(prev => ({ ...prev, [moto.id]: false }));
        return;
      }

      // Guardar como plan modelo (compartido)
      const { data: planModeloId } = await supabase.rpc("guardar_plan_modelo", {
        p_marca: moto.marca,
        p_modelo: moto.modelo,
        p_anio: moto.año ? Number(moto.año) : null,
        p_items: plan.items,
        p_observaciones: plan.observaciones || null,
      });

      // Guardar plan personalizado del usuario también
      await supabase.rpc("guardar_plan_moto", {
        p_moto_id: String(moto.id),
        p_items: plan.items,
        p_basado_en: planModeloId,
      });

      setPlanMantenimiento(prev => ({
        ...prev,
        [moto.id]: { items: plan.items, observaciones: plan.observaciones, basado_en: planModeloId },
      }));
    } catch (err) {
      alert("Error al generar plan: " + err.message);
    }
    setGenerandoPlan(prev => ({ ...prev, [moto.id]: false }));
  };

  // Calcular pendientes basándose en el plan + último registro del tipo + km actual
  const calcularPendientes = (moto) => {
    const plan = planMantenimiento[moto.id];
    if (!plan?.items || plan.items.length === 0) return [];
    const registros = mantenimientos[moto.id] || [];
    // Km actual REAL: el mayor entre el de la ficha y el de cualquier registro del libro
    // de servicio. Sin esto el plan quedaba congelado en el kilometraje inicial y mostraba
    // siempre el intervalo completo ("Falta 5.000 km") en vez de lo que falta de verdad.
    const kmActual = Math.max(
      moto.km ? Number(moto.km) : 0,
      ...registros.map(r => Number(r.km) || 0),
      0
    );

    // Por tipo, encontrar el último registro (los registros vienen del más nuevo al más viejo)
    const ultimoPorTipo = {};
    const normTipo = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const tipoMap = {
      "Aceite y filtros": "aceite_motor",
      "Cubiertas": "cubierta_delantera",
      "Cadena": "kit_transmision",
      "Frenos": "pastillas_delanteras",
    };
    [...registros]
      .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))
      .forEach(r => {
        // 1) el tipo_id explícito (lo guardan los registros hechos desde el plan)
        let tipoId = r.tipo_id || tipoMap[r.tipo] || null;
        // 2) si no, buscar el nombre del ítem dentro del tipo o la descripción
        if (!tipoId) {
          const txt = normTipo(`${r.tipo} ${r.descripcion || ""}`);
          for (const [id, nombre] of Object.entries(MANT_NOMBRES)) {
            if (txt.includes(normTipo(nombre))) { tipoId = id; break; }
          }
        }
        if (tipoId && !ultimoPorTipo[tipoId]) {
          ultimoPorTipo[tipoId] = { km: r.km, fecha: r.fecha };
        }
      });

    return plan.items.map(item => {
      const ultimo = ultimoPorTipo[item.tipo_id];
      const kmDesdeUltimo = ultimo?.km != null ? kmActual - Number(ultimo.km) : null;
      const fechaUltimo = ultimo?.fecha ? new Date(ultimo.fecha) : null;
      const mesesDesdeUltimo = fechaUltimo ? Math.floor((Date.now() - fechaUltimo.getTime()) / (1000 * 60 * 60 * 24 * 30)) : null;

      let kmRestante = null;
      let mesesRestante = null;
      let urgencia = "ok"; // 'ok' | 'pronto' | 'vencido'

      if (item.cada_km && kmDesdeUltimo != null) {
        kmRestante = item.cada_km - kmDesdeUltimo;
      } else if (item.cada_km && ultimo == null) {
        // Nunca lo hicieron: si llevan más de cada_km en el odómetro, está vencido
        kmRestante = item.cada_km - kmActual;
      }
      if (item.cada_meses && mesesDesdeUltimo != null) {
        mesesRestante = item.cada_meses - mesesDesdeUltimo;
      }

      // "Pronto" proporcional al intervalo: avisar a 500 km de un cambio de aceite cada
      // 5.000 es tarde; el 10% del intervalo (mínimo 500 km) da tiempo de coordinar el taller.
      const umbralKm = Math.max(500, Math.round((item.cada_km || 0) * 0.1));
      if ((kmRestante != null && kmRestante <= 0) || (mesesRestante != null && mesesRestante <= 0)) urgencia = "vencido";
      else if ((kmRestante != null && kmRestante <= umbralKm) || (mesesRestante != null && mesesRestante <= 1)) urgencia = "pronto";

      return { ...item, kmRestante, mesesRestante, urgencia, ultimo, kmActual };
    });
  };

  // Helper: arma el texto "restante" para un pendiente
  const textoRestante = (p) => {
    if (p.urgencia === "vencido") {
      if (p.kmRestante != null && p.kmRestante <= 0) return `vencido hace ${Math.abs(p.kmRestante).toLocaleString()} km`;
      if (p.mesesRestante != null && p.mesesRestante <= 0) return `vencido hace ${Math.abs(p.mesesRestante)} meses`;
      return "vencido";
    }
    if (p.kmRestante != null && p.kmRestante > 0) return `faltan ${p.kmRestante.toLocaleString()} km`;
    if (p.mesesRestante != null && p.mesesRestante > 0) return `falta${p.mesesRestante === 1 ? "" : "n"} ${p.mesesRestante} ${p.mesesRestante === 1 ? "mes" : "meses"}`;
    return "";
  };

  // Chequear pendientes de todas las motos y mandar DM si toca (anti-spam server-side: 7 días)
  const chequearNotifMant = async () => {
    // Solo correr una vez por día por sesión
    const lastCheck = localStorage.getItem("olbos_last_notif_mant_check");
    const hoy = hoyLocalISO();
    if (lastCheck === hoy) return;
    localStorage.setItem("olbos_last_notif_mant_check", hoy);

    if (!perfil?.motos?.length) return;

    for (const moto of perfil.motos) {
      // Saltar motos que ya no están activas (vendidas, robadas o retiradas)
      if (moto.estado && moto.estado !== "activa") continue;
      if (!planMantenimiento[moto.id]) continue;

      const pendientes = calcularPendientes(moto);
      const vencidos = pendientes
        .filter(p => p.urgencia === "vencido")
        .map(p => ({ tipo_id: p.tipo_id, nombre: MANT_NOMBRES[p.tipo_id] || p.tipo_id }));
      const proximos = pendientes
        .filter(p => p.urgencia === "pronto")
        .map(p => ({
          tipo_id: p.tipo_id,
          nombre: MANT_NOMBRES[p.tipo_id] || p.tipo_id,
          restante: textoRestante(p),
        }));

      if (vencidos.length === 0 && proximos.length === 0) continue;

      try {
        await supabase.rpc("enviar_notif_mant", {
          p_moto_id: String(moto.id),
          p_moto_nombre: `${moto.marca} ${moto.modelo}`,
          p_vencidos: vencidos,
          p_proximos: proximos,
        });
      } catch (e) {
        console.warn("Error chequeando notif mant:", e);
      }
    }
  };

  const guardarMantenimiento = async (moto) => {
    // Se pueden marcar VARIOS tipos de una: en un service casi siempre se hace
    // aceite + filtro + bujías juntos, y antes había que cargarlos de a uno
    // (reporte de Carlos). Cada tipo entra como su propio registro, con la misma
    // fecha y km, para que el plan de mantenimiento los reconozca uno por uno.
    const tipos = Array.isArray(newMant.tipos) && newMant.tipos.length > 0
      ? newMant.tipos
      : (newMant.tipo ? [newMant.tipo] : []);
    if (tipos.length === 0 || !newMant.fecha) return;
    setSubiendo(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSubiendo(false);
      alert("Tu sesión expiró. Cerrá y volvé a abrir la app (o recargá la página) y probá de nuevo.");
      return;
    }
    const fotos = (newMant.fotos || []).filter(f => f.url && !f.uploading).map(f => ({ url: f.url, type: f.type }));
    // El costo se carga una vez (es el total del service): va en el primero para
    // no multiplicar el gasto por cada ítem
    const filas = tipos.map((t, i) => ({
      user_id: user.id,
      moto_id: String(moto.id),
      moto_nombre: `${moto.marca} ${moto.modelo}`,
      tipo: t,
      tipo_id: tipos.length === 1 ? (newMant.tipo_id || null) : null, // vincula el registro con el ítem del plan
      descripcion: newMant.descripcion,
      fecha: newMant.fecha,
      km: newMant.km ? Number(newMant.km) : null,
      costo: i === 0 && newMant.costo ? Number(newMant.costo) : null,
      lugar: newMant.lugar,
      fotos: i === 0 ? fotos : [],
      moneda: newMant.moneda || "ARS",
    }));
    const { error } = await supabase.from("moto_mantenimientos").insert(filas);
    if (!error) {
      setShowAddMant(null);
      setNewMant({ tipo: "", tipos: [], descripcion: "", fecha: hoyLocalISO(), km: "", costo: "", lugar: "", moneda: "ARS", fotos: [] });
      cargarMantenimientos(moto.id, `${moto.marca} ${moto.modelo}`);
      // Trackear acción tomada (puede haber venido por un DM del bot)
      supabase.rpc("marcar_dm_accion", { p_tipo: "carga_mant" }).then(() => {}, () => {});
    } else {
      // Fallar en silencio dejaba el botón como si nada y el usuario sin saber
      // qué pasó (reporte de Carlos: "guardando un instante y no hace nada")
      alert("No se pudo guardar: " + (error.message || "probá de nuevo en un momento."));
    }
    setSubiendo(false);
  };


  const publicarVenta = async (moto) => {
    if (!ventaForm.precio) return;
    setPublicandoVenta(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setPublicandoVenta(false); return; }

    // Traer historial de mantenimientos de esta moto
    const historial = mantenimientos[moto.id] || [];

    // Fotos del garage (galería de la ficha) y el id de la moto (historia clínica
    // en vivo vía RPC). Si esas columnas todavía no existen, se reintenta sin ellas.
    const extras = {
      moto_id: String(moto.id),
      fotos: [moto.fotoUrl, ...((moto.media || []).filter(x => x.url && x.type !== "video").map(x => x.url))].filter((u, i, a) => u && a.indexOf(u) === i),
      ciudad: (ventaForm.ciudad || "").trim() || null, // dónde está la moto (pedido de Carlos 03-sep)
    };
    const filaVenta = {
      user_id: user.id,
      username: perfil.username,
      marca: moto.marca,
      modelo: moto.modelo,
      año: moto.año ? Number(moto.año) : null,
      cc: moto.cc ? Number(moto.cc) : null,
      color: moto.color,
      km: ventaForm.km ? Number(ventaForm.km) : null,
      precio: Number(ventaForm.precio),
      moneda: ventaForm.moneda,
      descripcion: ventaForm.descripcion,
      tipo: ventaForm.tipo,
      periodo: ventaForm.tipo === "alquiler" ? ventaForm.periodo : null,
      zona: ventaForm.tipo === "alquiler" ? (ventaForm.zona || null) : null,
      requisitos: ventaForm.tipo === "alquiler" ? (ventaForm.requisitos || null) : null,
      foto_url: moto.fotoUrl || null,
      historial_mantenimiento: historial.map(r => ({
        tipo: r.tipo,
        fecha: r.fecha,
        km: r.km,
        descripcion: r.descripcion,
        lugar: r.lugar,
        costo: r.costo,
        moneda: r.moneda || "ARS",
      })),
      estado: "activa",
    };
    let { error } = await supabase.from("motos_venta").insert({ ...extras, ...filaVenta });
    if (error && /moto_id|fotos|ciudad|schema cache/i.test(error.message || "")) ({ error } = await supabase.from("motos_venta").insert(filaVenta));

    if (!error) {
      // OJO: publicar NO es vender — la moto sigue activa en el garage hasta que
      // el dueño marque "La vendí" (feedback de Carlos 10-ago-2026). El vínculo
      // con la publicación se maneja con cargarMisVentas().
      await cargarMisVentas();
      const eraAlquiler = ventaForm.tipo === "alquiler";
      setShowVenderMoto(null);
      setVentaForm({ precio: "", moneda: "ARS", descripcion: "", km: "", tipo: "venta", periodo: "dia", zona: "", requisitos: "", ciudad: "" });
      alert(eraAlquiler
        ? `✅ ¡${moto.marca} ${moto.modelo} publicada en alquiler! Sigue en tu garage y la ven en Mercado → En alquiler. Cuando no la quieras alquilar más, tocá "En venta → Retirar".`
        : `✅ ¡${moto.marca} ${moto.modelo} publicada en Compra-Venta! Sigue activa en tu garage: cuando la vendas, tocá "En venta → La vendí".`);
    } else {
      alert("Error al publicar: " + error.message);
    }
    setPublicandoVenta(false);
  };


  const eliminarMantenimiento = async (id, motoId) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    await supabase.from("moto_mantenimientos").delete().eq("id", id);
    cargarMantenimientos(motoId);
  };

  const actualizarMantenimiento = async (motoId) => {
    if (!newMant.tipo || !newMant.fecha) return;
    setSubiendo(true);
    const { error } = await supabase.from("moto_mantenimientos").update({
      tipo: newMant.tipo,
      descripcion: newMant.descripcion,
      fecha: newMant.fecha,
      km: newMant.km ? Number(newMant.km) : null,
      costo: newMant.costo ? Number(newMant.costo) : null,
      lugar: newMant.lugar,
      fotos: (newMant.fotos || []).filter(f => f.url && !f.uploading).map(f => ({ url: f.url, type: f.type })),
      moneda: newMant.moneda || "ARS",
    }).eq("id", editandoMant.registro.id);
    if (!error) {
      setEditandoMant(null);
      setNewMant({ tipo: "", descripcion: "", fecha: hoyLocalISO(), km: "", costo: "", lugar: "", moneda: "ARS", fotos: [] });
      cargarMantenimientos(motoId);
    }
    setSubiendo(false);
  };

  const subirMediaMant = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    const fotosActuales = newMant.fotos || [];
    const espacio = 5 - fotosActuales.length;
    if (espacio <= 0) { alert("Máximo 5 archivos por registro"); return; }
    const aSubir = fileArray.slice(0, espacio);
    if (fileArray.length > espacio) alert(`Se subirán ${espacio} de ${fileArray.length} (límite: 5)`);
    const previews = aSubir.map((f, i) => ({
      url: URL.createObjectURL(f),
      type: f.type.startsWith("video") ? "video" : "image",
      uploading: true,
      tempId: `mant_${Date.now()}_${i}`,
    }));
    setNewMant(n => ({ ...n, fotos: [...(n.fotos || []), ...previews] }));
    for (let i = 0; i < aSubir.length; i++) {
      const file = aSubir[i];
      const tempId = previews[i].tempId;
      const tipo = file.type.startsWith("video") ? "video" : "image";
      try {
        const fd = new FormData();
        fd.append("file", await achicarImagen(file));
        fd.append("upload_preset", "olbos_uploads");
        fd.append("cloud_name", "dhwhk48wb");
        const endpoint = tipo === "video"
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const res = await fetch(endpoint, { method: "POST", body: fd });
        const data = await res.json();
        if (data.secure_url) {
          setNewMant(n => ({
            ...n,
            fotos: (n.fotos || []).map(f => f.tempId === tempId ? { url: data.secure_url, type: tipo, uploading: false } : f),
          }));
        } else {
          setNewMant(n => ({ ...n, fotos: (n.fotos || []).filter(f => f.tempId !== tempId) }));
        }
      } catch (err) {
        console.error("Error subiendo media mantenimiento:", err);
        setNewMant(n => ({ ...n, fotos: (n.fotos || []).filter(f => f.tempId !== tempId) }));
      }
    }
  };

  const quitarMediaMant = (idx) => {
    setNewMant(n => ({ ...n, fotos: (n.fotos || []).filter((_, i) => i !== idx) }));
  };

  const [mantMediaView, setMantMediaView] = useState(null); // { url, type }
  const [showAdminPin, setShowAdminPin] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  // Mapa del grabador: el navegador nuevo (MapLibre) es el default para todos; este switch
  // fuerza el mapa de Google en este teléfono (override "0"). Apagado = lo que diga el remoto.
  const [mapaAnterior, setMapaAnterior] = useState(() => navV2Override() === false);
  const [misPostsReales, setMisPostsReales] = useState([]);
  const [userId, setUserId] = useState(null);

  // ── Historia clínica compartida: token por moto, en motos_privado (RLS dueño) ──
  const [showHistorialMoto, setShowHistorialMoto] = useState(null); // { moto, token }
  const [motosPriv, setMotosPriv] = useState({}); // { moto_id: { patente, historia_token, ... } }
  const cargarMotosPriv = async () => {
    if (!userId) return;
    const { data } = await supabase.from("motos_privado").select("*").eq("user_id", userId);
    const map = {};
    (data || []).forEach(r => { map[r.moto_id] = r; });
    setMotosPriv(map);
  };
  useEffect(() => { cargarMotosPriv(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const compartirHistorialMoto = async (m) => {
    let token = motosPriv[String(m.id)]?.historia_token;
    if (!token) {
      token = `hm_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      const { error } = await supabase.from("motos_privado").upsert({ user_id: userId, moto_id: String(m.id), historia_token: token });
      if (error) { alert("No se pudo generar el link: " + error.message); return; }
      await cargarMotosPriv();
    }
    setShowHistorialMoto({ moto: m, token });
  };
  const revocarHistorialMoto = async (m) => {
    if (!window.confirm("¿Desactivar el link? Los talleres que lo tengan van a dejar de ver el historial (podés generar uno nuevo cuando quieras).")) return;
    await supabase.from("motos_privado").update({ historia_token: null }).eq("user_id", userId).eq("moto_id", String(m.id));
    await cargarMotosPriv();
    setShowHistorialMoto(null);
  };


  // ── Publicaciones de venta activas mías: vinculan cada moto del garage con su
  //    aviso en Compra-Venta (por marca+modelo, sin necesitar columnas nuevas) ──
  const [misVentas, setMisVentas] = useState([]);
  const [gestionVenta, setGestionVenta] = useState(null); // { moto, venta }
  const [destacarVenta, setDestacarVenta] = useState(null); // venta a destacar (compra del slot "moto")
  const cargarMisVentas = async () => {
    if (!userId) return;
    const { data } = await supabase.from("motos_venta").select("*").eq("user_id", userId).eq("estado", "activa");
    setMisVentas(data || []);
  };
  useEffect(() => { cargarMisVentas(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const ventaDe = (m) => misVentas.find(v => (v.marca || "").toLowerCase() === (m.marca || "").toLowerCase() && (v.modelo || "").toLowerCase() === (m.modelo || "").toLowerCase()) || null;

  const retirarDeVenta = async (venta) => {
    await supabase.from("motos_venta").update({ estado: "retirada" }).eq("id", venta.id);
    await cargarMisVentas();
    setGestionVenta(null);
    alert("Publicación retirada de Compra-Venta. La moto sigue en tu garage.");
  };
  const marcarVendida = async (moto, venta) => {
    if (!window.confirm(`¿Confirmás que vendiste la ${moto.marca} ${moto.modelo}? Sale de Compra-Venta y pasa a "Por estas pasé" con toda su historia.`)) return;
    await supabase.from("motos_venta").update({ estado: "vendida" }).eq("id", venta.id);
    await cambiarEstadoMoto(moto.id, "vendida");
    await cargarMisVentas();
    setGestionVenta(null);
  };

  const [userEmail, setUserEmail] = useState("");
  const [cropSrc, setCropSrc] = useState(null);
  const [cropTipo, setCropTipo] = useState(null); // "avatar" | "moto"
  const [miNegocio, setMiNegocio] = useState(null);
  const [showCrearNegocio, setShowCrearNegocio] = useState(false);
  const [modoPerfil, setModoPerfil] = useState("rider");
  const [fotoViewer, setFotoViewer] = useState(null);
  const ADMIN_PIN = "9786";
  const avatarInputRef = useRef(null);
  const motoPhotoRef = useRef(null);

  useEffect(() => {
    cargarPerfil();
    supabase.from("negocios").select("id, nombre, categoria").eq("estado", "verificado")
      .then(({ data }) => { if (data) setNegociosDB(data); });
  }, []);

  // Cargar mantenimientos de todas las motos cuando se carga el perfil
  useEffect(() => {
    if (perfil?.motos?.length > 0) {
      perfil.motos.forEach(m => {
        cargarMantenimientos(m.id, `${m.marca} ${m.modelo}`);
        cargarPlanMoto(m);
      });
    }
  }, [perfil?.motos?.length]);

  // Chequear notificaciones de mantenimiento (una vez por día) cuando hay motos + planes + mantenimientos
  useEffect(() => {
    if (!perfil?.motos?.length) return;
    const motosConPlan = perfil.motos.filter(m => planMantenimiento[m.id] && mantenimientos[m.id] !== undefined);
    if (motosConPlan.length === 0) return;
    // Debounce: chequear después de 2s para no disparar mientras cargan más motos
    const t = setTimeout(() => {
      chequearNotifMant();
    }, 2000);
    return () => clearTimeout(t);
  }, [perfil?.motos?.length, Object.keys(planMantenimiento).length, Object.keys(mantenimientos).length]);

  const cargarPerfil = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setUserId(user.id);
    setUserEmail(user.email);

    // El perfil se crea automáticamente vía trigger en auth.users → handle_new_user
    // Cargamos el perfil; si por timing aún no aparece, hacemos 1 reintento corto
    let { data: p } = await supabase.from("perfiles").select("*").eq("id", user.id).maybeSingle();
    if (!p) {
      await new Promise(r => setTimeout(r, 800));
      const retry = await supabase.from("perfiles").select("*").eq("id", user.id).maybeSingle();
      p = retry.data;
    }
    if (p) {
      setPerfil({ ...p, motos: p.motos || [] });
      // Ficha de rider (tabla privada aparte)
      supabase.from("rider_fichas").select("*").eq("user_id", user.id).maybeSingle()
        .then(({ data }) => setFicha(data || null), () => {});

      // Códigos de referido: en segundo plano — bloqueaban la carga del garage
      (async () => {
        const ref = localStorage.getItem("olbos_ref");
        if (ref && !p.referido_por) {
          const { data: codigoValido } = await supabase
            .from("codigos_referidos").select("codigo")
            .eq("codigo", ref).eq("activo", true).maybeSingle();
          if (codigoValido) {
            await supabase.from("perfiles").update({ referido_por: ref }).eq("id", user.id);
            setPerfil(prev => ({ ...prev, referido_por: ref }));
          }
          localStorage.removeItem("olbos_ref");
        }
        const { data: codigoPropio } = await supabase
          .from("codigos_referidos").select("id, user_id")
          .eq("username", p.username).is("user_id", null).maybeSingle();
        if (codigoPropio) {
          await supabase.from("codigos_referidos")
            .update({ user_id: user.id, updated_at: new Date().toISOString() })
            .eq("id", codigoPropio.id);
        }
      })().then(() => {}, () => {});
    } else {
      console.warn("Perfil no encontrado después del trigger. UserId:", user.id);
    }

    // cargar mis posts
    const { data: posts } = await supabase.from("posts").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
    if (posts) setMisPostsReales(posts);

    // cargar mi negocio si tiene
    const { data: neg } = await supabase.from("negocios").select("*").eq("user_id", user.id).maybeSingle();
    if (neg) setMiNegocio(neg);
  };

  const guardarPerfil = async () => {
    // El @ tiene que ser válido y único (ignorando mayúsculas); si lo cambió, se chequea antes de guardar
    if (editForm.username !== undefined && normalizarUsername(editForm.username) !== (perfil.username || "")) {
      const problema = await validarUsernameDisponible(editForm.username, userId);
      if (problema) { alert(problema); return; }
    }
    setGuardando(true);
    const linksLimpios = Array.isArray(editForm.links)
      ? editForm.links.filter(l => l && l.tipo && l.url && l.url.trim()).map(l => ({ tipo: l.tipo, url: l.url.trim() }))
      : (perfil.links || []);
    const updates = { ...perfil, ...editForm, links: linksLimpios, updated_at: new Date().toISOString() };
    if (updates.username !== undefined) updates.username = normalizarUsername(updates.username);
    const { error: errPerfil } = await supabase.from("perfiles").update(updates).eq("id", userId);
    if (errPerfil) {
      setGuardando(false);
      alert(/USERNAME_EN_USO|duplicate|unique/i.test(errPerfil.message || "") ? `El @${updates.username} ya está en uso — elegí otro.` : "No se pudo guardar: " + errPerfil.message);
      return;
    }
    setPerfil(updates);

    // Ficha de rider (tabla privada aparte): upsert solo si cargó algo
    const f = fichaForm || {};
    const tieneAlgo = ["nombre_completo", "dni", "telefono", "casco", "emergencia_nombre", "emergencia_relacion", "emergencia_telefono", "condicion_medica", "medicacion", "grupo_sanguineo"]
      .some(k => f[k] && String(f[k]).trim()) || typeof f.rodadas_antes === "boolean";
    if (tieneAlgo) {
      const fichaPayload = {
        user_id: userId,
        nombre_completo: f.nombre_completo?.trim() || null,
        dni: f.dni?.trim() || null,
        telefono: f.telefono?.trim() || null,
        casco: f.casco?.trim() || null,
        rodadas_antes: typeof f.rodadas_antes === "boolean" ? f.rodadas_antes : null,
        emergencia_nombre: f.emergencia_nombre?.trim() || null,
        emergencia_relacion: f.emergencia_relacion?.trim() || null,
        emergencia_telefono: f.emergencia_telefono?.trim() || null,
        condicion_medica: f.condicion_medica?.trim() || null,
        medicacion: f.medicacion?.trim() || null,
        grupo_sanguineo: f.grupo_sanguineo?.trim() || null,
        updated_at: new Date().toISOString(),
      };
      const { error: errFicha } = await supabase.from("rider_fichas").upsert(fichaPayload);
      if (!errFicha) setFicha(fichaPayload);
    }

    setEditando(false);
    setGuardando(false);
  };

  const handleLogout = async () => {
    // Un invitado que cierra sesión no tiene cómo volver a entrar: se avisa antes
    try {
      const { data } = await supabase.auth.getSession();
      if (esInvitado(data && data.session) && !window.confirm("Entraste como invitado: si cerrás sesión, perdés esta cuenta y lo que hiciste con ella.\n\nPara no perderla, cancelá y tocá «Guardar mi cuenta» arriba de tu perfil.\n\n¿Cerrar sesión igual?")) return;
    } catch (e) { /* */ }
    _cachePerfilPropio = null; _cacheMantenimientos = null; // que otro usuario no vea datos ajenos
    await supabase.auth.signOut();
  };

  // Eliminar cuenta (requisito de las stores): borra TODOS mis datos vía RPC y después
  // el usuario de Auth vía /api/eliminar-cuenta (service_role). Doble confirmación.
  const eliminarCuenta = async () => {
    if (!window.confirm("¿Eliminar tu cuenta de forma permanente?\n\nSe borran tu perfil, publicaciones, fotos, recorridos, motos y mensajes. Esta acción NO se puede deshacer.")) return;
    if (!window.confirm("Última confirmación: esto es definitivo.\n\n¿Borrar tu cuenta y todos tus datos?")) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert("Tenés que estar logueado para eliminar la cuenta."); return; }
      // 1) Borrar todos mis datos
      const { error } = await supabase.rpc("eliminar_mi_cuenta");
      if (error) { alert("No se pudo eliminar la cuenta: " + error.message); return; }
      // 2) Borrar el usuario de Auth (los datos ya se borraron)
      try {
        await fetch(API_BASE + "/api/eliminar-cuenta", { method: "POST", headers: { Authorization: `Bearer ${session.access_token}` } });
      } catch (e) { /* el dato ya se borró; seguimos al signOut igual */ }
      // 3) Cerrar sesión y recargar
      await supabase.auth.signOut();
      alert("Tu cuenta fue eliminada. ¡Gracias por haber sido parte de Olbos!");
      window.location.reload();
    } catch (e) {
      alert("Error al eliminar la cuenta: " + (e.message || e));
    }
  };

  const subirImagen = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "olbos_uploads");
    formData.append("cloud_name", "dhwhk48wb");
    const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: formData });
    const data = await res.json();
    return data.secure_url;
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setCropSrc(localUrl);
    setCropTipo("avatar");
  };

  const handleMotoPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setCropSrc(localUrl);
    setCropTipo("moto");
  };

  const handleCropDone = async (blob) => {
    setCropSrc(null);
    // subir blob a Cloudinary
    const formData = new FormData();
    formData.append("file", blob, "foto.jpg");
    formData.append("upload_preset", "olbos_uploads");
    formData.append("cloud_name", "dhwhk48wb");
    const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: formData });
    const data = await res.json();
    if (!data.secure_url) return;

    if (cropTipo === "avatar") {
      await supabase.from("perfiles").update({ avatar_url: data.secure_url }).eq("id", userId);
      setPerfil(p => ({ ...p, avatar_url: data.secure_url }));
    } else if (cropTipo === "moto") {
      setNewMoto(n => ({ ...n, fotoUrl: data.secure_url }));
    }
  };

  const agregarMoto = async () => {
    if (!newMoto.marca || !newMoto.modelo) return;
    // Patente / chasis / motor NO van al JSON público del perfil (cualquiera puede
    // leerlo): viven en motos_privado, con RLS de dueño. Los RPCs de emergencia,
    // taller e historia los leen de ahí con SECURITY DEFINER.
    const { patente, chasis, motor, ...motoPublica } = newMoto;
    const motoNueva = {
      ...motoPublica,
      media: (newMoto.media || []).filter(x => x.url && !x.uploading).map(x => ({ url: x.url, type: x.type })),
      id: Date.now(),
      estado: "activa",
    };
    const motosActualizadas = [...(perfil.motos || []), motoNueva];
    await supabase.from("perfiles").update({ motos: motosActualizadas }).eq("id", userId);
    if (patente || chasis || motor) {
      await supabase.from("motos_privado").upsert({
        user_id: userId, moto_id: String(motoNueva.id),
        patente: patente || null, chasis: chasis || null, motor: motor || null,
      }).then(() => {}, () => {});
    }
    setPerfil(p => ({ ...p, motos: motosActualizadas }));
    setNewMoto({ marca: "", modelo: "", año: "", color: "", cc: "", fotoUrl: null, nombre: "", media: [] });
    setShowAddMoto(false);
  };

  const eliminarMoto = async (motoId) => {
    const m = (perfil.motos || []).find(x => x.id === motoId);
    if (!window.confirm(`¿Eliminar ${m ? `"${m.marca} ${m.modelo}"` : "esta moto"} de tu garage?\n\nSe oculta de todos lados pero su historial de service no se pierde.`)) return;
    // Borrado SUAVE: la moto queda en el JSON con estado "eliminada" (oculta en toda la
    // app). Nunca se saca del array — así un borrado accidental no pierde el historial
    // ni el vínculo con moto_mantenimientos/motos_privado (falsa alarma del 13-ago-2026).
    const motosActualizadas = perfil.motos.map(x =>
      x.id === motoId ? { ...x, estado: "eliminada", fecha_estado: new Date().toISOString() } : x
    );
    await supabase.from("perfiles").update({ motos: motosActualizadas }).eq("id", userId);
    setPerfil(p => ({ ...p, motos: motosActualizadas }));
  };

  // La moto principal es simplemente la PRIMERA del garage: es la que se muestra
  // al lado de tu nombre en rutas, reseñas y experiencias. Hasta ahora quedaba
  // fija la que habías cargado primero y no había forma de cambiarla.
  const hacerMotoPrincipal = async (motoId) => {
    const lista = perfil.motos || [];
    const elegida = lista.find(x => x.id === motoId);
    if (!elegida) return;
    const motosActualizadas = [elegida, ...lista.filter(x => x.id !== motoId)];
    await supabase.from("perfiles").update({ motos: motosActualizadas }).eq("id", userId);
    setPerfil(p => ({ ...p, motos: motosActualizadas }));
  };

  // Cambiar estado de moto (activa, vendida, robada)
  const cambiarEstadoMoto = async (motoId, nuevoEstado, datosExtra = {}) => {
    const motosActualizadas = perfil.motos.map(m =>
      m.id === motoId ? { ...m, estado: nuevoEstado, ...datosExtra, fecha_estado: new Date().toISOString() } : m
    );
    await supabase.from("perfiles").update({ motos: motosActualizadas }).eq("id", userId);
    setPerfil(p => ({ ...p, motos: motosActualizadas }));
  };

  // Exportar mis datos (JSON): transparencia total — junta lo que la app guarda del
  // usuario en sus tablas y lo baja como archivo. De los recorridos va el resumen,
  // no los miles de puntos GPS (el archivo sería gigante y no le sirve a nadie).
  const exportarMisDatos = async () => {
    if (exportando) return;
    setExportando(true);
    try {
      const [pf, fr, mant, cargas, insc, posts, trz] = await Promise.all([
        supabase.from("perfiles").select("*").eq("id", userId).maybeSingle(),
        supabase.from("rider_fichas").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("moto_mantenimientos").select("*").eq("user_id", userId),
        supabase.from("moto_cargas").select("*").eq("user_id", userId),
        supabase.from("inscripciones").select("*").eq("user_id", userId),
        supabase.from("posts").select("*").eq("user_id", userId),
        supabase.from("trazas").select("*").eq("user_id", userId),
      ]);
      const datos = {
        exportado: new Date().toISOString(),
        app: "Olbos " + APP_VERSION,
        perfil: pf.data || null,
        ficha_rider: fr.data || null,
        mantenimientos: mant.data || [],
        cargas_combustible: cargas.data || [],
        inscripciones: insc.data || [],
        publicaciones: posts.data || [],
        recorridos: (trz.data || []).map(t => {
          const { puntos, ...resto } = t;
          return { ...resto, puntos_gps: Array.isArray(puntos) ? puntos.length : 0 };
        }),
      };
      const blob = new Blob([JSON.stringify(datos, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `olbos-mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      alert("No se pudo armar el archivo. Probá de nuevo.");
    }
    setExportando(false);
  };

  // Galería de la moto: subir fotos/videos (máx 10), upload paralelo a Cloudinary
  const subirMediaMoto = async (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    const actuales = newMoto.media || [];
    const espacio = 10 - actuales.length;
    if (espacio <= 0) { alert("Máximo 10 archivos por moto"); return; }
    const aSubir = fileArray.slice(0, espacio);
    if (fileArray.length > espacio) alert(`Se subirán ${espacio} de ${fileArray.length} (límite: 10)`);
    const previews = aSubir.map((f, i) => ({
      url: URL.createObjectURL(f),
      type: f.type.startsWith("video") ? "video" : "image",
      uploading: true,
      tempId: `moto_${Date.now()}_${i}`,
    }));
    setNewMoto(n => ({ ...n, media: [...(n.media || []), ...previews] }));
    for (let i = 0; i < aSubir.length; i++) {
      const file = aSubir[i];
      const tempId = previews[i].tempId;
      const tipo = file.type.startsWith("video") ? "video" : "image";
      try {
        const fd = new FormData();
        fd.append("file", await achicarImagen(file));
        fd.append("upload_preset", "olbos_uploads");
        fd.append("cloud_name", "dhwhk48wb");
        const endpoint = tipo === "video"
          ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
          : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
        const res = await fetch(endpoint, { method: "POST", body: fd });
        const data = await res.json();
        if (data.secure_url) {
          setNewMoto(n => ({
            ...n,
            media: (n.media || []).map(m => m.tempId === tempId ? { url: data.secure_url, type: tipo, uploading: false } : m),
          }));
        } else {
          setNewMoto(n => ({ ...n, media: (n.media || []).filter(m => m.tempId !== tempId) }));
        }
      } catch (err) {
        console.error("Error subiendo media moto:", err);
        setNewMoto(n => ({ ...n, media: (n.media || []).filter(m => m.tempId !== tempId) }));
      }
    }
  };

  const quitarMediaMoto = (idx) => {
    setNewMoto(n => ({ ...n, media: (n.media || []).filter((_, i) => i !== idx) }));
  };

  // Detalle de moto y viewer
  const [detalleMoto, setDetalleMoto] = useState(null);
  const [editandoMoto, setEditandoMoto] = useState(null);
  const [galeriaIdx, setGaleriaIdx] = useState(0);
  const [mediaFullscreen, setMediaFullscreen] = useState(null);

  // Combustible: modal de carga (motoId preseleccionado) + refresco del bloque
  const [cargaNafta, setCargaNafta] = useState(null); // { motoId } | null
  const [refrescoCargas, setRefrescoCargas] = useState(0);

  // Actualizar km (registro mínimo en moto_mantenimientos)
  const [showActualizarKm, setShowActualizarKm] = useState(null); // motoId
  const [verHistorialKm, setVerHistorialKm] = useState(false);
  const [kmUpdate, setKmUpdate] = useState({ km: "", fecha: hoyLocalISO() });

  const guardarActualizacionKm = async () => {
    if (!showActualizarKm || !kmUpdate.km) return;
    const moto = (perfil.motos || []).find(m => m.id === showActualizarKm);
    const { error } = await supabase.from("moto_mantenimientos").insert({
      user_id: userId,
      moto_id: showActualizarKm,
      moto_nombre: moto ? `${moto.marca || ""} ${moto.modelo || ""}`.trim() || "Moto" : "Moto",
      tipo: "Actualización km",
      fecha: kmUpdate.fecha,
      km: Number(kmUpdate.km),
      moneda: "ARS",
    });
    if (error) { alert("Error: " + error.message); return; }
    await cargarMantenimientos(showActualizarKm, "");
    setShowActualizarKm(null);
    setKmUpdate({ km: "", fecha: hoyLocalISO() });
    // Trackear acción tomada
    supabase.rpc("marcar_dm_accion", { p_tipo: "actualizo_km" }).then(() => {}, () => {});
  };

  // Abrir detalle de moto: si no hay mantenimientos cargados, traerlos
  const abrirDetalleMoto = (moto) => {
    setDetalleMoto(moto);
    setGaleriaIdx(0);
    if (!mantenimientos[moto.id]) cargarMantenimientos(moto.id, `${moto.marca} ${moto.modelo}`);
    if (!planMantenimiento[moto.id]) cargarPlanMoto(moto);
  };

  // Guardar cambios al editar una moto existente
  const guardarEdicionMoto = async () => {
    if (!editandoMoto || !newMoto.marca || !newMoto.modelo) return;
    const motosActualizadas = (perfil.motos || []).map(m =>
      m.id === editandoMoto.id
        ? { ...m, marca: newMoto.marca, modelo: newMoto.modelo, año: newMoto.año, color: newMoto.color, cc: newMoto.cc, fotoUrl: newMoto.fotoUrl, nombre: newMoto.nombre, media: (newMoto.media || []).filter(x => x.url && !x.uploading).map(x => ({ url: x.url, type: x.type })) }
        : m
    );
    // Patente: confidencial — va a motos_privado (RLS dueño), nunca al JSON público
    supabase.from("motos_privado").upsert({
      user_id: userId, moto_id: String(editandoMoto.id),
      patente: (newMoto.patente || "").trim().toUpperCase() || null,
    }).then(() => cargarMotosPriv(), () => {});
    await supabase.from("perfiles").update({ motos: motosActualizadas }).eq("id", userId);
    setPerfil(p => ({ ...p, motos: motosActualizadas }));
    setNewMoto({ marca: "", modelo: "", año: "", color: "", cc: "", fotoUrl: null, nombre: "", media: [] });
    const motoActualizada = motosActualizadas.find(m => m.id === editandoMoto.id);
    setEditandoMoto(null);
    if (motoActualizada) setDetalleMoto(motoActualizada);
  };

  // Abrir "Editar perfil" con el formulario precargado (se usa desde el header, la
  // ficha de emergencia y los primeros pasos). Si viene `foco`, enfoca ese campo.
  const abrirEditarPerfil = (foco) => {
    setEditForm({ username: perfil.username, bio: perfil.bio, ciudad: perfil.ciudad, links: Array.isArray(perfil.links) ? perfil.links : [], tipo_perfil: perfil.tipo_perfil || "persona" });
    setFichaForm(ficha || {});
    setEditando(true);
    if (foco) setTimeout(() => { const el = document.getElementById(foco); if (el) el.focus(); }, 150);
  };

  // Primeros pasos del perfil (foto, moto, ciudad). Mismo criterio que la lista del
  // feed (OnboardingChecklist) pero con los datos que esta pantalla ya tiene; se
  // oculta sola cuando están los tres.
  const pasosPerfil = [
    { key: "avatar", label: "Subí tu foto de perfil", done: !!perfil.avatar_url, ir: () => avatarInputRef.current?.click() },
    { key: "moto", label: "Agregá tu moto al garage", done: (perfil.motos || []).some(m => m && m.estado !== "eliminada"), ir: () => setShowAddMoto(true) },
    { key: "ciudad", label: "Contá de qué ciudad sos", done: !!(perfil.ciudad || "").trim(), ir: () => abrirEditarPerfil("perfil-input-ciudad") },
  ];
  const faltanPasos = pasosPerfil.some(p => !p.done);

  return (
    <PageWrap>
      {/* ── HEADER CON FOTO DE PERFIL — momento oscuro (Fase 4 del rebrand: el perfil
           propio es un momento íntimo de la marca, negro cálido + crema). Compacto a
           propósito: bio, redes y negocio bajaron para que lo primero sin scroll sea
           el garage (feedback de Franco, usuario nuevo, 08-oct) ── */}
      <div style={{ padding: "var(--header-pt) 18px 18px", background: C.accent, color: "#F9F5F2" }}>
        {/* logo + seccion */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover" }} />
          <div style={{ fontSize: 11, color: "#bdb6ab", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Olbos · Perfil</div>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          {/* avatar con botón de edición */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <img loading="lazy" decoding="async"               src={cldThumb(perfil.avatar_url, 240) || avatarInicial(perfil.username)}
              alt="Tu perfil"
              onClick={() => perfil.avatar_url && setFotoViewer(perfil.avatar_url)}
              style={{ width: 68, height: 68, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(249,245,242,.4)", cursor: perfil.avatar_url ? "pointer" : "default" }}
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              style={{
                position: "absolute", bottom: 0, right: 0,
                width: 26, height: 26, borderRadius: "50%",
                background: "#F9F5F2", color: C.text,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #111111", cursor: "pointer",
              }}
            >
              <Camera size={12} strokeWidth={2.5} />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleAvatarChange}
            />
          </div>

          {/* info usuario */}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#F9F5F2", letterSpacing: -0.3 }}>@{perfil.username || "..."}</div>
              {/* Tilde solo para perfiles verificados (salía para todos y devaluaba a los negocios verificados) */}
              {(perfil.verificado === true || !!perfil.verificado_en) && <CheckCircle size={16} strokeWidth={2} color="#F9F5F2" />}
              <BadgeComunidad tipo={perfil.tipo_perfil} />
              <BadgeTag badge={getBadge(userEmail, perfil.username, perfil.numero_registro)} />
              {perfil.numero_registro && perfil.numero_registro <= 300 && (
                <span style={{ fontSize: 10, color: "#bdb6ab", fontWeight: 600 }}>#{perfil.numero_registro}</span>
              )}
            </div>
            {/* Subtítulo: la ciudad, o la invitación a cargarla. Nunca el email (dato sensible y feo) */}
            <div style={{ fontSize: 12, color: "#bdb6ab", marginBottom: 6 }}>
              {perfil.ciudad || (
                <button onClick={() => abrirEditarPerfil("perfil-input-ciudad")}
                  style={{ background: "none", border: "none", padding: 0, color: "#bdb6ab", fontSize: 12, cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(189,182,171,.5)", fontFamily: "inherit" }}>
                  Contá de dónde sos ›
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {/* Contraseña y Salir se mudaron a la sección CUENTA (el perfil como hub
                  por secciones — aprendido de la prueba de Stribo, 27-ago) */}
              <button onClick={() => abrirEditarPerfil()}
                style={{ fontSize: 12, fontWeight: 600, color: "#F9F5F2", padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(249,245,242,.3)", background: "rgba(249,245,242,.08)", cursor: "pointer" }}>
                ✏️ Editar perfil
              </button>
              <button onClick={() => avatarInputRef.current?.click()}
                style={{ fontSize: 12, fontWeight: 600, color: "#F9F5F2", padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(249,245,242,.3)", background: "rgba(249,245,242,.08)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <Camera size={12} strokeWidth={2} /> Foto
              </button>
            </div>
          </div>
        </div>
      </div>

      <GuardarCuentaInvitado />
      <OlbosTePaga />
      <ConectarInstagram />

      {showQREmergencia && (
        <QREmergenciaModal ficha={ficha} userId={userId}
          onToken={(t) => setFicha(f => (f ? { ...f, qr_token: t } : f))}
          onClose={() => setShowQREmergencia(false)} />
      )}

      {/* CREAR NEGOCIO MODAL */}
      {fotoViewer && <FotoViewer url={fotoViewer} onClose={() => setFotoViewer(null)} />}

      {mantMediaView && (
        <div onClick={() => setMantMediaView(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.94)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button onClick={() => setMantMediaView(null)} style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
            <X size={20} strokeWidth={2} color="#fff" />
          </button>
          {mantMediaView.type === "video" ? (
            <video src={cldOpt(mantMediaView.url, 1080)} poster={cldPoster(mantMediaView.url, 480)} controls autoPlay onClick={e => e.stopPropagation()}
              style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, background: "#000" }} />
          ) : (
            <img loading="lazy" decoding="async" src={mantMediaView.url} alt="" onClick={e => e.stopPropagation()}
              style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,.6)" }} />
          )}
        </div>
      )}

      {showCrearNegocio && (
        <CrearNegocioModal
          userId={userId}
          onClose={() => setShowCrearNegocio(false)}
          onSuccess={(neg) => { setMiNegocio(neg); setShowCrearNegocio(false); }}
        />
      )}
      {editando && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }}>
          <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "24px 20px calc(44px + var(--sab))", maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, position: "sticky", top: 0, background: C.card, paddingBottom: 8, zIndex: 1 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800 }}>Editar perfil</h2>
              <button onClick={() => setEditando(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Usuario</div>
                <input value={editForm.username || ""} onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="Tu nombre de usuario" style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Ciudad</div>
                <input id="perfil-input-ciudad" value={editForm.ciudad || ""} onChange={e => setEditForm(f => ({ ...f, ciudad: e.target.value }))}
                  placeholder="Buenos Aires, Mendoza..." style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Bio</div>
                <textarea value={editForm.bio || ""} onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="Contá algo sobre vos como rider..." rows={3}
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, resize: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>


              {/* Tipo de perfil */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Tipo de perfil</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["persona", "🏍️ Rider"], ["comunidad", "👥 Comunidad / Tienda"]].map(([val, label]) => (
                    <button key={val} onClick={() => setEditForm(f => ({ ...f, tipo_perfil: val }))}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: (editForm.tipo_perfil || "persona") === val ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: (editForm.tipo_perfil || "persona") === val ? "#1111110C" : C.card, color: (editForm.tipo_perfil || "persona") === val ? C.accent : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Redes y links */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Redes y links</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                  Sumá tu Instagram, web, WhatsApp… Olbos complementa tus redes, no las reemplaza. Podés pegar el link completo o solo el @usuario.
                </div>
                {(editForm.links || []).map((l, i) => {
                  const cfg = LINK_TIPOS[l.tipo] || LINK_TIPOS.instagram;
                  return (
                    <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
                      <select value={l.tipo} onChange={e => setEditForm(f => ({ ...f, links: f.links.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x) }))}
                        style={{ padding: "10px 8px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, flexShrink: 0 }}>
                        {LINK_TIPOS_ORDEN.map(t => <option key={t} value={t}>{LINK_TIPOS[t].label}</option>)}
                      </select>
                      <input value={l.url} onChange={e => setEditForm(f => ({ ...f, links: f.links.map((x, j) => j === i ? { ...x, url: e.target.value } : x) }))}
                        placeholder={l.tipo === "whatsapp" ? "Nº con código país (ej: 549351…)" : l.tipo === "web" ? "tutienda.com" : "@usuario o link"}
                        style={{ flex: 1, minWidth: 0, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" }} />
                      <button onClick={() => setEditForm(f => ({ ...f, links: f.links.filter((_, j) => j !== i) }))}
                        style={{ width: 30, height: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <X size={13} strokeWidth={2.5} color={C.red} />
                      </button>
                    </div>
                  );
                })}
                <button onClick={() => setEditForm(f => ({ ...f, links: [...(f.links || []), { tipo: "instagram", url: "" }] }))}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
                  <Plus size={14} strokeWidth={2.5} /> Agregar link
                </button>
              </div>

              {/* Ficha de rider (privada) — función inline (no componente) para no perder foco */}
              {(() => {
                const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" };
                const setF = (k, v) => setFichaForm(f => ({ ...f, [k]: v }));
                const campo = (k, label, ph) => (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>{label}</div>
                    <input value={fichaForm[k] || ""} onChange={e => setF(k, e.target.value)} placeholder={ph} style={inputStyle} />
                  </div>
                );
                return (
                  <div style={{ marginTop: 6, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                      <Lock size={11} strokeWidth={2.5} /> Ficha de rider
                    </div>
                    <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                      Privada — solo la ves vos. Se comparte únicamente con la salida a la que te anotás, así no volvés a cargar tus datos cada vez.
                    </div>
                    {/* Consentimiento datos de salud (Ley 25.326): decirlo antes de pedirlo
                        genera confianza y nos deja bien parados legalmente */}
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 12, fontSize: 11.5, color: C.muted, lineHeight: 1.55 }}>
                      Grupo sanguíneo, condiciones y medicación son <strong>datos de salud</strong>: la ley los considera sensibles (Ley 25.326). Cargarlos es <strong>100% opcional</strong> — la app funciona igual sin ellos — y podés borrarlos cuando quieras.
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {campo("nombre_completo", "Nombre y apellido", "Como figura en tu DNI")}
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={{ flex: 1 }}>{campo("dni", "DNI", "Sin puntos")}</div>
                        <div style={{ flex: 1 }}>{campo("telefono", "Teléfono", "Cód. país + número")}</div>
                      </div>
                      {campo("casco", "Casco (marca y modelo)", "Ej: LS2 Storm")}
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 6 }}>¿Hiciste rodadas grupales antes?</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          {[["Sí", true], ["No", false]].map(([lbl, val]) => (
                            <button key={lbl} onClick={() => setF("rodadas_antes", val)}
                              style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: fichaForm.rodadas_antes === val ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: fichaForm.rodadas_antes === val ? "#1111110C" : C.card, color: fichaForm.rodadas_antes === val ? C.accent : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>Contacto de emergencia</div>
                      {campo("emergencia_nombre", "Nombre y apellido", "A quién avisamos")}
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={{ flex: 1 }}>{campo("emergencia_relacion", "Relación", "Pareja, hermano…")}</div>
                        <div style={{ flex: 1 }}>{campo("emergencia_telefono", "Teléfono", "Su número")}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Grupo sanguíneo</div>
                        <select value={fichaForm.grupo_sanguineo || ""} onChange={e => setF("grupo_sanguineo", e.target.value)}
                          style={{ ...inputStyle, color: fichaForm.grupo_sanguineo ? C.text : C.muted, background: C.card }}>
                          <option value="">No sé / prefiero no cargarlo</option>
                          {["0+", "0-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                      {campo("condicion_medica", "Condición médica (dejá vacío si ninguna)", "Alergias, etc.")}
                      {campo("medicacion", "Medicación (dejá vacío si ninguna)", "Medicamentos que tomás")}
                    </div>
                  </div>
                );
              })()}
            </div>
            <button onClick={guardarPerfil} disabled={guardando}
              style={{ width: "100%", marginTop: 20, padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer", opacity: guardando ? 0.7 : 1 }}>
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: "14px 18px" }}>
        {/* stats hardcodeados ocultos hasta tener datos reales — reintroducir cuando haya volumen */}
        {/*
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
          {[["Eventos", "12"], ["Km", "48k"], ["Reviews", "34"], ["Rank", "4.8★"]].map(([k, v]) => (
            <div key={k} style={{ background: C.card, borderRadius: 10, padding: "10px 8px", border: `1px solid ${C.border}`, textAlign: "center" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{v}</div>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase" }}>{k}</div>
            </div>
          ))}
        </div>

        <Card style={{ marginBottom: 20, border: `1px solid ${C.border}` }}>
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Ranking organizador</div>
              <OTag>Top 5%</OTag>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: C.text }}>4.8</div>
              <div>
                <Stars n={4.8} size={15} />
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>12 eventos organizados</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[["Puntualidad", 5], ["Organización", 4.8], ["Comunicación", 4.6]].map(([k, v]) => (
                <div key={k} style={{ flex: 1, background: C.surface, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{k}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
        */}

        {/* ── PRIMEROS PASOS: foto, moto y ciudad. Solo mientras falte algo: el usuario
             nuevo tiene que ver qué hacer sin leer la pantalla entera (feedback de
             Franco, 08-oct). Cada ítem lleva directo a la acción. ── */}
        {perfil.tipo_perfil !== "comunidad" && perfil.username && faltanPasos && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px 6px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>Primeros pasos</div>
              <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>{pasosPerfil.filter(p => p.done).length} de {pasosPerfil.length}</div>
            </div>
            {pasosPerfil.map(p => (
              <button key={p.key} onClick={p.done ? undefined : p.ir} disabled={p.done}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 0", background: "none", border: "none", cursor: p.done ? "default" : "pointer", textAlign: "left" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: p.done ? C.green : "transparent", border: p.done ? "none" : `1.5px solid ${C.border}` }}>
                  {p.done && <span style={{ color: "#fff", fontSize: 12, fontWeight: 800, lineHeight: 1 }}>✓</span>}
                </div>
                <div style={{ flex: 1, fontSize: 13.5, fontWeight: p.done ? 500 : 600, color: p.done ? C.muted : C.text, textDecoration: p.done ? "line-through" : "none" }}>{p.label}</div>
                {!p.done && <ChevronRight size={15} strokeWidth={2} color={C.muted} />}
              </button>
            ))}
          </div>
        )}

        {/* garage — solo motos activas (los perfiles comunidad no tienen garage personal) */}
        {/* solicitudes de talleres para ver/cargar el historial de mis motos */}
        {perfil.tipo_perfil !== "comunidad" && <SolicitudesHistoriaGarage userId={userId} />}
        {/* "Mis amores" era lindo pero no se entendía; ahora la pestaña se llama Perfil,
            así que la sección de motos lleva su nombre de siempre */}
        {/* Con el garage vacío, el único "agregá tu moto" es el del vacío de abajo (estaba por triplicado) */}
        {perfil.tipo_perfil !== "comunidad" && <SectionHead title="Garage" action={(perfil.motos || []).length > 0 ? "＋ Agregar moto" : undefined} onAction={(perfil.motos || []).length > 0 ? () => setShowAddMoto(true) : undefined} />}
        {/* Acceso rápido a la carga de nafta: se usa parado en la estación */}
        {perfil.tipo_perfil !== "comunidad" && (perfil.motos || []).some(m => m.estado !== "eliminada" && m.estado !== "retirada") && (
          <button onClick={() => setCargaNafta({ motoId: null })}
            style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
            ⛽ Cargué nafta
          </button>
        )}
        {cargaNafta && (
          <CargaNaftaModal userId={userId} motos={perfil.motos || []} motoPre={cargaNafta.motoId}
            onClose={() => setCargaNafta(null)}
            onGuardada={(motoId) => { setRefrescoCargas(n => n + 1); if (motoId) cargarMantenimientos(motoId, ""); }} />
        )}
        {/* Garage vacío: CTA grande y explícito — "Mis amores" solo no se entendía
            la primera vez (feedback post-Arribeños) */}
        {/* mientras el perfil carga: spinner, NO el cartel de vacio (aparecia 4-5 seg) */}
        {perfil.tipo_perfil !== "comunidad" && !perfil.username && (
          <div style={{ padding: "30px 0", textAlign: "center" }}>
            <div style={{ width: 22, height: 22, margin: "0 auto", border: `3px solid ${C.border}`, borderTopColor: C.text, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          </div>
        )}
        {perfil.tipo_perfil !== "comunidad" && perfil.username && (perfil.motos || []).length === 0 && (
          <div style={{ padding: "18px 18px 16px", textAlign: "center", background: C.card, borderRadius: 14, border: `1.5px dashed ${C.border}`, marginBottom: 10 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <Bike size={24} strokeWidth={1.6} color={C.text} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4, letterSpacing: "-0.01em" }}>Tu garage está vacío</div>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, maxWidth: 280, margin: "0 auto 12px" }}>
              Con foto, nombre y patente: plan de mantenimiento, historial de service y nafta, todo en un lugar.
            </p>
            <button onClick={() => setShowAddMoto(true)}
              style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: C.text, color: "#fff", fontWeight: 700, fontSize: 14.5, border: "none", cursor: "pointer" }}>
              ＋ Agregá tu moto
            </button>
          </div>
        )}
        {(perfil.motos || []).length > 0 && (perfil.motos || []).filter(m => !m.estado || m.estado === "activa").length === 0 && (
          <div style={{ padding: "16px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>
            No tenés motos activas — agregá una nueva o mirá las de tu historia más abajo
          </div>
        )}
        {(perfil.motos || []).filter(m => !m.estado || m.estado === "activa").map((m, idx) => (
          <Card key={m.id || idx} style={{ marginBottom: 12, overflow: "hidden" }}>
            {/* header: foto + identidad — todo el bloque abre la ficha */}
            <div style={{ display: "flex", gap: 12, padding: "13px 14px 0", alignItems: "center", cursor: "pointer" }} onClick={() => abrirDetalleMoto(m)}>
              {m.fotoUrl ? (
                <img src={cldThumb(m.fotoUrl, 200)} alt={`${m.marca} ${m.modelo}`} loading="lazy" decoding="async" style={{ width: 92, height: 72, borderRadius: 12, objectFit: "cover", border: `1px solid ${C.border}`, display: "block", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 92, height: 72, background: C.surface, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${C.border}`, flexShrink: 0 }}>
                  <Bike size={32} strokeWidth={1.5} color={C.muted} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {m.marca || "Mi moto"}
                </div>
                <div style={{ fontWeight: 800, fontSize: 16.5, color: C.text, lineHeight: 1.2, letterSpacing: "-0.015em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {m.modelo || m.marca}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {[m.nombre ? `"${m.nombre}"` : null, m.año, m.color].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                <button onClick={(e) => {
                  e.stopPropagation();
                  const estaExpandida = motoExpandida === m.id;
                  setMotoExpandida(estaExpandida ? null : m.id);
                  if (!estaExpandida) cargarMantenimientos(m.id, `${m.marca} ${m.modelo}`);
                }} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <Wrench size={13} strokeWidth={2} color={motoExpandida === m.id ? "#111111" : C.muted} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); eliminarMoto(m.id); }} style={{ width: 28, height: 28, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <X size={13} strokeWidth={2} color={C.red} />
                </button>
              </div>
            </div>

            {/* resumen: km actuales, servicios e invertido en chips prolijos */}
            {(() => {
              const regs = mantenimientos[m.id] || [];
              const kmActual = regs.find(r => r.km)?.km;
              const servicios = regs.filter(r => r.tipo !== "Actualización km").length;
              const totales = {};
              regs.forEach(r => { if (r.costo) { const mon = r.moneda || "ARS"; totales[mon] = (totales[mon] || 0) + Number(r.costo); } });
              const entries = Object.entries(totales);
              const chip = { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 100, background: C.surface, fontSize: 11, fontWeight: 700, color: C.text };
              return (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "10px 14px 0" }}>
                  {idx === 0 && <OTag>Principal</OTag>}
                  <span style={chip}>📍 {kmActual ? `${Number(kmActual).toLocaleString("es-AR")} km` : "km sin cargar"}</span>
                  {servicios > 0 && <span style={chip}>🔧 {servicios} service{servicios > 1 ? "s" : ""}</span>}
                  {entries.length > 0 && <span style={chip}>💰 {entries.map(([mon, total]) => `${mon} ${total >= 1000 ? `${(total / 1000).toFixed(0)}k` : total.toLocaleString("es-AR")}`).join(" + ")}</span>}
                </div>
              );
            })()}

            {/* acciones en grilla pareja, todas con el mismo cuerpo */}
            {(() => {
              const btn = { padding: "9px 6px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, cursor: "pointer", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" };
              const tk = motosPriv[String(m.id)]?.historia_token;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: "10px 14px 13px" }}>
                  <BotonCotizarSeguro moto={m} perfil={perfil} email={userEmail} estilo={{ ...btn, gridColumn: "1 / -1", background: C.text, border: `1px solid ${C.text}`, color: "#F9F5F2", padding: "10px 6px", fontSize: 12.5 }} />
                  {ventaDe(m) ? (
                    <button onClick={() => setGestionVenta({ moto: m, venta: ventaDe(m) })}
                      style={{ ...btn, background: "#276749", border: "1px solid #276749", color: "#fff" }}>
                      🏷 En venta
                    </button>
                  ) : (
                    <button onClick={() => {
                      setShowVenderMoto(m);
                      const ultimoKm = (mantenimientos[m.id] || []).find(r => r.km)?.km;
                      setVentaForm({ precio: "", moneda: "ARS", descripcion: "", km: ultimoKm ? String(ultimoKm) : "", tipo: "venta", periodo: "dia", zona: "", requisitos: "", ciudad: perfil?.ciudad || "" });
                    }} style={{ ...btn, color: C.green }}>
                      {ALQUILER_P2P_ACTIVO ? "🏷 Vender o alquilar" : "🏷 Vender"}
                    </button>
                  )}
                  <button onClick={() => compartirHistorialMoto(m)}
                    style={{ ...btn, color: tk ? "#1F8347" : C.text }}>
                    🔗 Historial a un taller{tk ? " ✓" : ""}
                  </button>
                  <button onClick={() => setShowDenunciarRobo(m)} style={{ ...btn, color: C.red }}>
                    🚨 Marcar como robada
                  </button>
                  <button onClick={() => {
                    if (!window.confirm(`¿Marcar "${m.marca} ${m.modelo}" como que ya no la tenés? Pasa a "Por estas pasé" con toda su historia (podés restaurarla cuando quieras).`)) return;
                    cambiarEstadoMoto(m.id, "retirada");
                  }} style={{ ...btn, color: C.muted }}>
                    👋 Ya no la tengo
                  </button>
                  {/* La principal es la que te representa en rutas, reseñas y experiencias */}
                  {(perfil.motos || []).filter(x => x.estado !== "eliminada")[0]?.id === m.id ? (
                    <div style={{ ...btn, gridColumn: "1 / -1", background: C.surface, color: C.muted, cursor: "default", textAlign: "center", border: `1px dashed ${C.border}` }}>
                      ⭐ Es tu moto principal
                    </div>
                  ) : (
                    <button onClick={() => hacerMotoPrincipal(m.id)}
                      style={{ ...btn, gridColumn: "1 / -1", color: C.text }}>
                      ⭐ Hacerla mi moto principal
                    </button>
                  )}
                </div>
              );
            })()}

            {motoExpandida !== m.id && (
              <ResumenCombustibleMini userId={userId} moto={m} refrescoKey={refrescoCargas}
                onAbrir={() => { setMotoExpandida(m.id); cargarMantenimientos(m.id, `${m.marca} ${m.modelo}`); }} />
            )}
            {/* ficha de servicio expandible */}
            {motoExpandida === m.id && (
              <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 14px" }}>

                {/* COMBUSTIBLE: va primero — cargar nafta pasa mucho más seguido que un service */}
                <BloqueCombustible userId={userId} moto={m} refrescoKey={refrescoCargas}
                  onRegistrar={() => setCargaNafta({ motoId: m.id })} />

                {/* PLAN DE MANTENIMIENTO */}
                {(() => {
                  const plan = planMantenimiento[m.id];
                  const generando = generandoPlan[m.id];
                  const pendientes = plan ? calcularPendientes(m) : [];
                  const vencidos = pendientes.filter(p => p.urgencia === "vencido");
                  const pronto = pendientes.filter(p => p.urgencia === "pronto");

                  return (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                          📋 Plan de mantenimiento
                        </div>
                        {!plan && !generando && (
                          <button onClick={() => generarPlanConIA(m)}
                            style={{ fontSize: 11.5, color: "#1F8347", fontWeight: 700, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                            ✨ Generar con IA
                          </button>
                        )}
                        {plan && !generando && (
                          <button onClick={() => generarPlanConIA(m)}
                            style={{ fontSize: 11, color: C.muted, fontWeight: 500, background: "transparent", border: "none", cursor: "pointer" }}>
                            Regenerar
                          </button>
                        )}
                      </div>

                      {generando ? (
                        <div style={{ padding: "20px 14px", background: "#F0F8F2", borderRadius: 10, border: "1px solid #B5DFC0", textAlign: "center" }}>
                          <div style={{ fontSize: 13, color: "#1F8347", fontWeight: 600, marginBottom: 4 }}>✨ Generando plan con IA…</div>
                          <div style={{ fontSize: 11, color: C.muted }}>Esto puede tomar 5-10 segundos</div>
                        </div>
                      ) : !plan ? (
                        <div style={{ padding: "14px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
                          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                            Generá un plan personalizado para tu {m.marca} {m.modelo}. Te muestra cuándo toca cada service según km y meses.
                          </div>
                          <button onClick={() => generarPlanConIA(m)}
                            style={{
                              width: "100%", padding: "10px", borderRadius: 10,
                              background: "#1F8347", color: "#fff",
                              border: "none", cursor: "pointer",
                              fontSize: 13, fontWeight: 500,
                            }}>
                            ✨ Generar plan con IA
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Alerta de vencidos */}
                          {vencidos.length > 0 && (
                            <div style={{ padding: "10px 12px", background: "#FFF1F0", border: "1px solid #FFD7D2", borderRadius: 10, marginBottom: 8 }}>
                              <div style={{ fontSize: 12, color: "#B82F26", fontWeight: 700, marginBottom: 4 }}>
                                ⚠️ {vencidos.length} mantenimiento{vencidos.length > 1 ? "s" : ""} vencido{vencidos.length > 1 ? "s" : ""}
                              </div>
                              <div style={{ fontSize: 11.5, color: "#B82F26", lineHeight: 1.4 }}>
                                {vencidos.map(v => MANT_NOMBRES[v.tipo_id] || v.tipo_id).join(", ")}
                              </div>
                            </div>
                          )}

                          {pronto.length > 0 && (
                            <div style={{ padding: "10px 12px", background: `${C.gold}14`, border: "1px solid #FFE89A", borderRadius: 10, marginBottom: 8 }}>
                              <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, marginBottom: 4 }}>
                                🟡 {pronto.length} servicio{pronto.length > 1 ? "s" : ""} próximo{pronto.length > 1 ? "s" : ""}
                              </div>
                              <div style={{ fontSize: 11.5, color: C.gold, lineHeight: 1.4 }}>
                                {pronto.map(v => MANT_NOMBRES[v.tipo_id] || v.tipo_id).join(", ")}
                              </div>
                            </div>
                          )}

                          {/* Lista de items del plan */}
                          <div style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                            {pendientes.map((p, idx) => {
                              const nombre = MANT_NOMBRES[p.tipo_id] || p.tipo_id;
                              const cada = [
                                p.cada_km ? `${p.cada_km.toLocaleString()} km` : null,
                                p.cada_meses ? `${p.cada_meses} ${p.cada_meses === 1 ? "mes" : "meses"}` : null,
                              ].filter(Boolean).join(" o ");
                              const colorUrgencia = p.urgencia === "vencido" ? "#B82F26" : p.urgencia === "pronto" ? "#8B6A00" : C.muted;
                              const restanteText = (() => {
                                if (p.urgencia === "vencido") {
                                  if (p.kmRestante != null && p.kmRestante <= 0) return `${Math.abs(p.kmRestante).toLocaleString()} km pasado`;
                                  if (p.mesesRestante != null && p.mesesRestante <= 0) return `${Math.abs(p.mesesRestante)} meses pasado`;
                                  return "Vencido";
                                }
                                if (p.kmRestante != null && p.kmRestante > 0) return `Falta ${p.kmRestante.toLocaleString()} km`;
                                if (p.mesesRestante != null && p.mesesRestante > 0) return `Falta ${p.mesesRestante} ${p.mesesRestante === 1 ? "mes" : "meses"}`;
                                return "Sin registros";
                              })();
                              return (
                                <div key={idx} style={{
                                  padding: "10px 12px",
                                  borderBottom: idx < pendientes.length - 1 ? `1px solid ${C.border}` : "none",
                                  display: "flex", alignItems: "center", gap: 10,
                                }}>
                                  <div style={{
                                    width: 8, height: 8, borderRadius: "50%",
                                    background: colorUrgencia, flexShrink: 0,
                                  }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>
                                      {nombre}
                                    </div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1, letterSpacing: "-0.005em" }}>
                                      Cada {cada}{p.notas ? ` · ${p.notas}` : ""}
                                    </div>
                                    {p.ultimo?.km != null && (
                                      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>
                                        Último: {Number(p.ultimo.km).toLocaleString()} km
                                        {p.ultimo.fecha ? ` · ${new Date(p.ultimo.fecha).toLocaleDateString("es-AR", { month: "short", year: "numeric" })}` : ""}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                                    <div style={{ fontSize: 10.5, fontWeight: 600, color: colorUrgencia, textAlign: "right", letterSpacing: "-0.005em" }}>
                                      {restanteText}
                                    </div>
                                    {/* marcar como hecho: precarga el registro con el km actual y va al libro de servicio */}
                                    <button onClick={() => {
                                      setNewMant({
                                        tipo: nombre, tipos: [nombre], tipo_id: p.tipo_id, descripcion: "",
                                        fecha: hoyLocalISO(),
                                        km: p.kmActual ? String(p.kmActual) : "",
                                        costo: "", lugar: "", moneda: "ARS", fotos: [],
                                      });
                                      setShowAddMant(m.id);
                                      // El formulario está ARRIBA y el plan ACÁ ABAJO: sin este scroll
                                      // el botón parecía no hacer nada (reporte de Carlos, "botón fantasma")
                                      setTimeout(() => {
                                        const el = document.getElementById(`form-mant-${m.id}`);
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                                      }, 80);
                                    }}
                                      style={{ padding: "3px 9px", borderRadius: 100, background: p.urgencia === "vencido" ? "#B82F26" : C.surface, color: p.urgencia === "vencido" ? "#fff" : "#111111", border: "none", fontSize: 10.5, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
                                      ✓ Hecho
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {plan.observaciones && (
                            <div style={{ marginTop: 8, padding: "8px 10px", background: "#F0F8F2", borderRadius: 8, fontSize: 11, color: "#1F8347", lineHeight: 1.5 }}>
                              <strong>Notas:</strong> {plan.observaciones}
                            </div>
                          )}

                          <div style={{ marginTop: 6, fontSize: 10, color: C.muted, textAlign: "center", lineHeight: 1.4, letterSpacing: "-0.005em" }}>
                            Generado por IA · Confirmá con el manual oficial de tu moto
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                    🔧 Libro de servicio
                  </div>
                  <button onClick={() => setShowAddMant(m.id)}
                    style={{ fontSize: 12, color: C.text, fontWeight: 700, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <Plus size={13} strokeWidth={2.5} /> Agregar
                  </button>
                </div>

                {(mantenimientos[m.id] || []).filter(r => r.tipo !== "Actualización km").length === 0 ? (
                  <div style={{ padding: "16px 0", textAlign: "center", fontSize: 13, color: C.muted }}>
                    Sin registros todavía — agregá el primero.
                  </div>
                ) : (
                  <div>
                    {(mantenimientos[m.id] || []).filter(r => r.tipo !== "Actualización km").map((r, ri) => (
                      <div key={r.id}>
                        {/* modo edición */}
                        {editandoMant?.registro?.id === r.id ? (
                          <div style={{ background: "#FFF9F0", borderRadius: 10, border: `1px solid #F0C060`, padding: "12px 14px", marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#B07D1A", marginBottom: 10 }}>Editando registro</div>

                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                              {TIPOS_MANT.map(t => (
                                <button key={t} onClick={() => setNewMant(n => ({ ...n, tipo: t }))}
                                  style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${newMant.tipo === t ? "#111111" : C.border}`, background: newMant.tipo === t ? "#FFF3EE" : C.card, color: newMant.tipo === t ? "#111111" : C.text, fontSize: 12, fontWeight: newMant.tipo === t ? 700 : 400, cursor: "pointer" }}>
                                  {t}
                                </button>
                              ))}
                            </div>

                            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Fecha</div>
                                <input type="date" value={newMant.fecha} onChange={e => setNewMant(n => ({ ...n, fecha: e.target.value }))}
                                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Kilometraje</div>
                                <input type="number" value={newMant.km} onChange={e => setNewMant(n => ({ ...n, km: e.target.value }))}
                                  placeholder="Ej: 15000"
                                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
                              </div>
                            </div>

                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Descripción</div>
                              <textarea value={newMant.descripcion} onChange={e => setNewMant(n => ({ ...n, descripcion: e.target.value }))}
                                placeholder="Qué se hizo, qué se cambió..."
                                style={{ width: "100%", minHeight: 55, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, resize: "none", boxSizing: "border-box" }} />
                            </div>

                            <div style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Dónde se hizo</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                                <button onClick={() => setNewMant(n => ({ ...n, lugar: "Lo hice yo" }))}
                                  style={{ padding: "4px 9px", borderRadius: 8, border: `1px solid ${newMant.lugar === "Lo hice yo" ? "#111111" : C.border}`, background: newMant.lugar === "Lo hice yo" ? "#FFF3EE" : C.card, color: newMant.lugar === "Lo hice yo" ? "#111111" : C.text, fontSize: 12, fontWeight: newMant.lugar === "Lo hice yo" ? 700 : 400, cursor: "pointer" }}>
                                  🔧 Lo hice yo
                                </button>
                                {negociosDB.filter(n => n.categoria === "Taller").map(t => (
                                  <button key={t.id} onClick={() => setNewMant(n => ({ ...n, lugar: t.nombre }))}
                                    style={{ padding: "4px 9px", borderRadius: 8, border: `1px solid ${newMant.lugar === t.nombre ? "#111111" : C.border}`, background: newMant.lugar === t.nombre ? "#FFF3EE" : C.card, color: newMant.lugar === t.nombre ? "#111111" : C.text, fontSize: 12, fontWeight: newMant.lugar === t.nombre ? 700 : 400, cursor: "pointer" }}>
                                    {t.nombre}
                                  </button>
                                ))}
                              </div>
                              <input value={newMant.lugar} onChange={e => setNewMant(n => ({ ...n, lugar: e.target.value }))}
                                placeholder="O escribí el nombre del taller..."
                                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
                            </div>

                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Costo</div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <select value={newMant.moneda || "ARS"} onChange={e => setNewMant(n => ({ ...n, moneda: e.target.value }))}
                                  style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, flexShrink: 0 }}>
                                  <option value="ARS">$ ARS</option>
                                  <option value="USD">USD</option>
                                  <option value="EUR">EUR</option>
                                </select>
                                <input type="number" value={newMant.costo} onChange={e => setNewMant(n => ({ ...n, costo: e.target.value }))}
                                  placeholder="Ej: 50000"
                                  style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: "border-box" }} />
                              </div>
                            </div>

                            {/* fotos / videos opcionales (hasta 5) */}
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fotos / videos (opcional, hasta 5)</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {(newMant.fotos || []).map((f, i) => (
                                  <div key={f.tempId || f.url || i} style={{ position: "relative", width: 60, height: 60, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, background: "#000" }}>
                                    {f.type === "video" ? (
                                      <video src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                                    ) : (
                                      <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    )}
                                    {f.uploading && (
                                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>···</div>
                                    )}
                                    <button type="button" onClick={() => quitarMediaMant(i)}
                                      style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,.75)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                      <X size={10} strokeWidth={3} color="#fff" />
                                    </button>
                                  </div>
                                ))}
                                {(newMant.fotos || []).length < 5 && (
                                  <label style={{ width: 60, height: 60, borderRadius: 8, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.surface, color: C.muted, fontSize: 22, fontWeight: 300 }}>
                                    +
                                    <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
                                      onChange={e => { subirMediaMant(e.target.files); e.target.value = ""; }} />
                                  </label>
                                )}
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => { setEditandoMant(null); setNewMant({ tipo: "", descripcion: "", fecha: hoyLocalISO(), km: "", costo: "", lugar: "", moneda: "ARS", fotos: [] }); }}
                                style={{ flex: 1, padding: "9px 0", borderRadius: 10, background: C.surface, color: C.muted, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                                Cancelar
                              </button>
                              <button onClick={() => actualizarMantenimiento(m.id)} disabled={!newMant.tipo || subiendo}
                                style={{ flex: 2, padding: "9px 0", borderRadius: 10, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer" }}>
                                {subiendo ? "Guardando..." : "Guardar cambios"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* vista normal del registro */
                          <div style={{ background: C.surface, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.tipo}</span>
                                  {r.km && <span style={{ fontSize: 11, color: C.muted, background: C.card, padding: "1px 6px", borderRadius: 6, border: `1px solid ${C.border}` }}>{r.km.toLocaleString()} km</span>}
                                </div>
                                {r.descripcion && <div style={{ fontSize: 12, color: C.muted, marginBottom: 3 }}>{r.descripcion}</div>}
                                <div style={{ display: "flex", gap: 10, fontSize: 11, color: C.faint, flexWrap: "wrap" }}>
                                  <span>📅 {new Date(r.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}</span>
                                  {r.lugar && <span>📍 {r.lugar}</span>}
                                  {r.costo && <span>💰 {r.moneda || "ARS"} {Number(r.costo).toLocaleString()}</span>}
                                </div>
                                {Array.isArray(r.fotos) && r.fotos.length > 0 && (
                                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                                    {r.fotos.map((f, fi) => (
                                      <div key={fi} onClick={() => setMantMediaView(f)}
                                        style={{ position: "relative", width: 52, height: 52, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, background: "#000", cursor: "pointer", flexShrink: 0 }}>
                                        {f.type === "video" ? (
                                          <>
                                            <video src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                                            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.25)" }}>
                                              <div style={{ width: 0, height: 0, borderLeft: "10px solid #fff", borderTop: "7px solid transparent", borderBottom: "7px solid transparent", marginLeft: 3, filter: "drop-shadow(0 1px 2px rgba(0,0,0,.5))" }} />
                                            </div>
                                          </>
                                        ) : (
                                          <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                                <button onClick={() => {
                                  setEditandoMant({ motoId: m.id, registro: r });
                                  setShowAddMant(null);
                                  setNewMant({
                                    tipo: r.tipo,
                                    descripcion: r.descripcion || "",
                                    fecha: r.fecha,
                                    km: r.km ? String(r.km) : "",
                                    costo: r.costo ? String(r.costo) : "",
                                    lugar: r.lugar || "",
                                    moneda: r.moneda || "ARS",
                                    fotos: Array.isArray(r.fotos) ? r.fotos : [],
                                  });
                                }} style={{ width: 24, height: 24, borderRadius: "50%", background: "#f6f1eb", border: `1px solid #8a857f22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                  <Wrench size={11} strokeWidth={2} color="#111111" />
                                </button>
                                <button onClick={() => eliminarMantenimiento(r.id, m.id)}
                                  style={{ width: 24, height: 24, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                  <X size={10} strokeWidth={2.5} color={C.red} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* historial de km (actualizaciones de odómetro, separado del libro de servicio) */}
                {(() => {
                  const kms = (mantenimientos[m.id] || []).filter(r => r.tipo === "Actualización km");
                  if (kms.length === 0) return null;
                  return (
                    <details style={{ marginTop: 12 }}>
                      <summary style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, cursor: "pointer", padding: "6px 0" }}>
                        📍 Historial de kilometraje ({kms.length})
                      </summary>
                      <div style={{ marginTop: 6 }}>
                        {kms.map(r => (
                          <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 2px", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                            <span style={{ color: C.muted }}>{new Date(r.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}</span>
                            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <strong style={{ color: C.text }}>{r.km ? Number(r.km).toLocaleString() : "—"} km</strong>
                              <button onClick={() => eliminarMantenimiento(r.id, m.id)}
                                style={{ width: 22, height: 22, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
                                <X size={10} strokeWidth={2.5} color={C.red} />
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                })()}

                {/* modal agregar mantenimiento */}
                {showAddMant === m.id && (
                  <div id={`form-mant-${m.id}`} style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 16, marginTop: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Nuevo registro</div>

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
                        Tipo <span style={{ textTransform: "none", fontWeight: 400 }}>— podés marcar varios</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {TIPOS_MANT.map(t => {
                          const sel = Array.isArray(newMant.tipos) ? newMant.tipos.includes(t) : newMant.tipo === t;
                          return (
                            <button key={t} onClick={() => setNewMant(n => {
                              const act = Array.isArray(n.tipos) ? n.tipos : (n.tipo ? [n.tipo] : []);
                              const nuevos = act.includes(t) ? act.filter(x => x !== t) : [...act, t];
                              return { ...n, tipos: nuevos, tipo: nuevos[0] || "" };
                            })}
                              style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${sel ? "#111111" : C.border}`, background: sel ? "#FFF3EE" : C.card, color: sel ? "#111111" : C.text, fontSize: 12, fontWeight: sel ? 700 : 400, cursor: "pointer" }}>
                              {sel ? "✓ " : ""}{t}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha</div>
                        <input type="date" value={newMant.fecha} onChange={e => setNewMant(n => ({ ...n, fecha: e.target.value }))}
                          style={{ width: "100%", padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Kilometraje</div>
                        <input type="number" value={newMant.km} onChange={e => setNewMant(n => ({ ...n, km: e.target.value }))}
                          placeholder="Ej: 15000"
                          style={{ width: "100%", padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción (opcional)</div>
                      <textarea value={newMant.descripcion} onChange={e => setNewMant(n => ({ ...n, descripcion: e.target.value }))}
                        placeholder="Qué se hizo, qué se cambió..."
                        style={{ width: "100%", minHeight: 60, padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, resize: "none", boxSizing: "border-box" }} />
                    </div>

                    {/* lugar con opciones rápidas + texto libre */}
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Dónde se hizo</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                        <button onClick={() => setNewMant(n => ({ ...n, lugar: "Lo hice yo" }))}
                          style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${newMant.lugar === "Lo hice yo" ? "#111111" : C.border}`, background: newMant.lugar === "Lo hice yo" ? "#FFF3EE" : C.card, color: newMant.lugar === "Lo hice yo" ? "#111111" : C.text, fontSize: 12, fontWeight: newMant.lugar === "Lo hice yo" ? 700 : 400, cursor: "pointer" }}>
                          🔧 Lo hice yo
                        </button>
                        {negociosDB.filter(n => n.categoria === "Taller").map(t => (
                          <button key={t.id} onClick={() => setNewMant(n => ({ ...n, lugar: t.nombre }))}
                            style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${newMant.lugar === t.nombre ? "#111111" : C.border}`, background: newMant.lugar === t.nombre ? "#FFF3EE" : C.card, color: newMant.lugar === t.nombre ? "#111111" : C.text, fontSize: 12, fontWeight: newMant.lugar === t.nombre ? 700 : 400, cursor: "pointer" }}>
                            {t.nombre}
                          </button>
                        ))}
                      </div>
                      <input value={newMant.lugar} onChange={e => setNewMant(n => ({ ...n, lugar: e.target.value }))}
                        placeholder="O escribí el nombre del taller..."
                        style={{ width: "100%", padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
                    </div>

                    {/* costo con selector de moneda */}
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Costo</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <select value={newMant.moneda || "ARS"} onChange={e => setNewMant(n => ({ ...n, moneda: e.target.value }))}
                          style={{ padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, flexShrink: 0 }}>
                          <option value="ARS">$ ARS</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </select>
                        <input type="number" value={newMant.costo} onChange={e => setNewMant(n => ({ ...n, costo: e.target.value }))}
                          placeholder="Ej: 50000"
                          style={{ flex: 1, padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
                      </div>
                    </div>

                    {/* fotos / videos opcionales (hasta 5) */}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fotos / videos (opcional, hasta 5)</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {(newMant.fotos || []).map((f, i) => (
                          <div key={f.tempId || f.url || i} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, background: "#000" }}>
                            {f.type === "video" ? (
                              <video src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                            ) : (
                              <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            )}
                            {f.uploading && (
                              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>···</div>
                            )}
                            <button type="button" onClick={() => quitarMediaMant(i)}
                              style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,.75)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                              <X size={10} strokeWidth={3} color="#fff" />
                            </button>
                          </div>
                        ))}
                        {(newMant.fotos || []).length < 5 && (
                          <label style={{ width: 64, height: 64, borderRadius: 8, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.surface, color: C.muted, fontSize: 24, fontWeight: 300 }}>
                            +
                            <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
                              onChange={e => { subirMediaMant(e.target.files); e.target.value = ""; }} />
                          </label>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button onClick={() => { setShowAddMant(null); setNewMant({ tipo: "", descripcion: "", fecha: hoyLocalISO(), km: "", costo: "", lugar: "", moneda: "ARS", fotos: [] }); }}
                        style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: C.surface, color: C.muted, fontWeight: 600, fontSize: 13, border: `1px solid ${C.border}`, cursor: "pointer" }}>
                        Cancelar
                      </button>
                      <button onClick={() => guardarMantenimiento(m)} disabled={!newMant.tipo || subiendo}
                        style={{ flex: 2, padding: "10px 0", borderRadius: 10, background: newMant.tipo ? "#111111" : C.faint, color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: newMant.tipo ? "pointer" : "default" }}>
                        {subiendo ? "Guardando..." : (Array.isArray(newMant.tipos) && newMant.tipos.length > 1 ? `Guardar ${newMant.tipos.length} registros` : "Guardar registro")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}

        {/* SECCIÓN: Por estas pasé (motos vendidas, robadas o que ya no están) */}
        {(perfil.motos || []).filter(m => m.estado === "vendida" || m.estado === "robada" || m.estado === "retirada").length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, height: 1, background: C.border }} />
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
                Por estas pasé
              </span>
              <div style={{ flex: 1, height: 1, background: C.border }} />
            </div>
            <p style={{ fontSize: 12, color: C.muted, textAlign: "center", marginBottom: 16, fontStyle: "italic" }}>
              Las motos que fueron parte de tu historia
            </p>
            {(perfil.motos || []).filter(m => m.estado === "vendida" || m.estado === "robada" || m.estado === "retirada").map((m, idx) => (
              <Card key={m.id || idx} style={{ marginBottom: 10, opacity: 0.7 }}>
                <div style={{ display: "flex", gap: 12, padding: "12px 14px", alignItems: "center", filter: "grayscale(100%)" }}>
                  <div style={{ flexShrink: 0 }}>
                    {m.fotoUrl ? (
                      <img src={cldThumb(m.fotoUrl, 160)} alt={`${m.marca} ${m.modelo}`} loading="lazy" decoding="async" style={{ width: 70, height: 56, borderRadius: 10, objectFit: "cover", border: `1px solid ${C.border}`, display: "block" }} />
                    ) : (
                      <div style={{ width: 70, height: 56, background: C.surface, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${C.border}` }}>
                        <Bike size={26} strokeWidth={1.5} color={C.muted} />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {m.nombre && (
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 1, fontStyle: "italic" }}>"{m.nombre}"</div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.3 }}>{m.marca} {m.modelo}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{[m.año, m.cc ? `${m.cc}cc` : null, m.color].filter(Boolean).join(" · ")}</div>
                    <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: "2px 8px", borderRadius: 100,
                        background: m.estado === "robada" ? "#FFF1F0" : C.surface,
                        color: m.estado === "robada" ? "#B82F26" : C.muted,
                        textTransform: "uppercase", letterSpacing: 0.5,
                      }}>
                        {m.estado === "robada" ? "Robada" : m.estado === "retirada" ? "Ya no está conmigo" : "Vendida"}
                      </span>
                      {m.fecha_estado && (
                        <span style={{ fontSize: 10, color: C.muted }}>
                          {new Date(m.fecha_estado).toLocaleDateString("es-AR", { month: "short", year: "numeric" })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button onClick={() => {
                      if (!window.confirm(`¿Volver "${m.marca} ${m.modelo}" al garage como activa?`)) return;
                      cambiarEstadoMoto(m.id, "activa");
                    }} style={{ padding: "5px 10px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: C.text, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                      Restaurar
                    </button>
                    <button onClick={() => {
                      if (!window.confirm(`¿Eliminar permanentemente "${m.marca} ${m.modelo}" del historial?`)) return;
                      eliminarMoto(m.id);
                    }} style={{ padding: "5px 10px", borderRadius: 8, background: "#FFF1F0", border: `1px solid ${C.red}22`, color: C.red, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                      Borrar
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── NEGOCIO: la herramienta de trabajo del que tiene taller, comercio o marca.
             Va después del garage y solo si tiene uno; el que no, lo registra desde
             una línea al final de Cuenta ── */}
        {miNegocio && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Mi negocio</div>
            <div style={{ padding: "12px 14px", background: C.card, borderRadius: 14, border: `1px solid ${modoPerfil === "negocio" ? C.text : C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {miNegocio.logo_url
                  ? <img loading="lazy" decoding="async" src={miNegocio.logo_url} alt={miNegocio.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <Wrench size={20} strokeWidth={1.8} color={C.muted} />
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{miNegocio.nombre}</div>
                <div style={{ fontSize: 11, color: miNegocio.estado === "verificado" ? C.green : C.muted, fontWeight: 600 }}>
                  {miNegocio.estado === "verificado" ? "✓ Verificado" : miNegocio.estado === "pendiente" ? "⏳ En revisión" : miNegocio.estado === "standby" ? "⏸ Pausada" : miNegocio.estado}
                </div>
              </div>
              <button onClick={() => setModoPerfil(modoPerfil === "rider" ? "negocio" : "rider")}
                style={{ padding: "7px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: modoPerfil === "negocio" ? C.surface : C.accent, color: modoPerfil === "negocio" ? C.text : "#F9F5F2", border: "none", cursor: "pointer", flexShrink: 0 }}>
                {modoPerfil === "negocio" ? "Cerrar panel" : "Gestionar →"}
              </button>
            </div>
            {/* PANEL NEGOCIO */}
            {modoPerfil === "negocio" && (
          <div style={{ marginTop: 10, padding: "14px 14px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Panel de negocio</div>
            <AvisoFichaEnRevision negocio={miNegocio} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              {[
                { icon: "🏪", label: "Categoría", val: miNegocio.categoria },
                { icon: "📍", label: "Ciudad", val: miNegocio.ciudad },
                { icon: "🧭", label: "En el mapa", val: miNegocio.lat && miNegocio.lng ? "Sí" : "Sin ubicación" },
                { icon: "✅", label: "Estado", val: miNegocio.estado === "verificado" ? "Verificado" : miNegocio.estado === "standby" ? "Pausada" : "En revisión" },
              ].map(item => (
                <div key={item.label} style={{ background: C.card, borderRadius: 10, padding: "10px 12px", border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{item.icon} {item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{item.val || "—"}</div>
                </div>
              ))}
            </div>
            {miNegocio.descripcion && (
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>{miNegocio.descripcion}</p>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              {miNegocio.whatsapp && (
                <a href={`https://wa.me/${miNegocio.whatsapp}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "#25D36615", border: "1px solid #25D36633", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
                  <span style={{ fontSize: 14 }}>💬</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#128C7E" }}>WhatsApp</span>
                </a>
              )}
              {miNegocio.instagram && (
                <a href={`https://instagram.com/${miNegocio.instagram.replace("@", "")}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "#E1306C15", border: "1px solid #E1306C33", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
                  <span style={{ fontSize: 14 }}>📸</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#E1306C" }}>Instagram</span>
                </a>
              )}
            </div>
            <MercadoPagoConnect />
            {/* 📣 Espacios contratados: vistas, contactos, renovar */}
            <MisAnuncios />
            {/* 🛍️ Mi tienda: productos, importación y pedidos */}
            <MisProductos negocio={miNegocio} />
            <EditorHorariosTarifas negocio={miNegocio} onGuardado={(campos) => setMiNegocio(n => ({ ...n, ...campos }))} />
            <EditorBeneficio negocio={miNegocio} onGuardado={(campos) => setMiNegocio(n => ({ ...n, ...campos }))} />
            <EditorSinLocal negocio={miNegocio} onGuardado={(campos) => setMiNegocio(n => ({ ...n, ...campos }))} />
            {miNegocio.categoria === "Taller" && miNegocio.estado === "verificado" && (
              <AgendaTurnosNegocio negocio={miNegocio} />
            )}
            {/* Sin planes ni upgrades: la ficha es gratis; sponsoreos y destacados se conversan */}
            <a href="https://wa.me/5491130718112?text=Hola%2C%20tengo%20un%20negocio%20en%20Olbos%20y%20quiero%20saber%20c%C3%B3mo%20destacarlo%20o%20sponsorear%20a%20la%20comunidad" target="_blank" rel="noreferrer"
              style={{ display: "block", marginTop: 12, padding: "10px 14px", background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, textDecoration: "none" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>¿Querés destacar tu negocio o sponsorear a la comunidad?</div>
              <div style={{ fontSize: 11, color: C.muted }}>Escribinos por WhatsApp y lo vemos juntos.</div>
            </a>
          </div>
            )}
          </div>
        )}

        {/* ── HUB: cuatro puertas con una línea cada una. Seguridad y Cuenta se abren
             debajo (acordeón) para que el primer scroll no sea la pantalla entera;
             km/rodadas abre su modal y Mis recorridos va a la pestaña Mapa ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
          <PasaporteCard userId={userId} perfil={perfil} variante="hub" />
          <TarjetaHubPerfil Icono={Navigation} titulo="Mis recorridos" linea="Tus tracks grabados, con mapa y video"
            onClick={() => window.dispatchEvent(new CustomEvent("olbos-navegar", { detail: { tab: "rutas", vistaMapa: "trazas" } }))} />
          {perfil.tipo_perfil !== "comunidad" && (
            <TarjetaHubPerfil Icono={Shield} titulo="Seguridad" linea="Ficha de emergencia y QR para el casco" acordeon activa={seccionAbierta === "seguridad"}
              onClick={() => setSeccionAbierta(v => v === "seguridad" ? null : "seguridad")} />
          )}
          <TarjetaHubPerfil Icono={User} titulo="Cuenta" linea="Contraseña, apariencia, datos y sesión" acordeon activa={seccionAbierta === "cuenta"}
            onClick={() => setSeccionAbierta(v => v === "cuenta" ? null : "cuenta")} />
        </div>
        {/* Tus publicaciones y eventos en un toque: abre tu perfil público, tal como lo ven los demás riders */}
        <div style={{ marginTop: 10 }}>
          <TarjetaHubPerfil Icono={Users} titulo="Mis publicaciones y eventos" linea="Todo lo que compartiste y organizaste, en un solo lugar"
            onClick={() => userId && setVerMiActividad(true)} />
        </div>
        {verMiActividad && userId && <PerfilPublico userId={userId} onClose={() => setVerMiActividad(false)} />}

        {/* ── SEGURIDAD (acordeón del hub): ficha de emergencia y QR del casco. Los
             nombres dicen para qué sirve cada cosa, no cómo se llama adentro ── */}
        {seccionAbierta === "seguridad" && perfil.tipo_perfil !== "comunidad" && (
          <div style={{ marginTop: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
            <button onClick={() => abrirEditarPerfil()}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Shield size={17} strokeWidth={2} color={C.text} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>Ficha de emergencia: a quién llamar y tus datos médicos</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 2 }}>
                  {ficha && (ficha.grupo_sanguineo || ficha.emergencia_telefono) ? "Grupo sanguíneo, medicación y a quién avisar" : "Completala: 2 minutos que pueden salvarte"}
                </div>
              </div>
              <ChevronRight size={16} strokeWidth={2} color={C.muted} />
            </button>
            <button onClick={() => setShowQREmergencia(true)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#B3372C12", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>🆘</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>QR para el casco: tu ficha si tenés un accidente</div>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, marginTop: 2 }}>
                  {ficha && (ficha.grupo_sanguineo || ficha.emergencia_telefono) ? "Descargá el sticker y pegalo en el casco" : "Se genera al completar tu ficha"}
                </div>
              </div>
              <ChevronRight size={16} strokeWidth={2} color={C.muted} />
            </button>
          </div>
        )}

        {/* ── CUENTA (acordeón del hub): contraseña, datos, apariencia, inicio, ajustes
             y salir; bien abajo lo legal y eliminar la cuenta. Modo administrador
             solo para cuentas admin. ── */}
        {seccionAbierta === "cuenta" && (
        <div style={{ margin: "10px 0 0" }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              <button onClick={() => setShowCambiarPass(true)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left" }}>
                <Lock size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: C.text }}>Cambiar contraseña</div>
                <ChevronRight size={16} strokeWidth={2} color={C.muted} />
              </button>
              <button onClick={exportarMisDatos} disabled={exportando}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left", opacity: exportando ? 0.6 : 1 }}>
                <Download size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{exportando ? "Armando el archivo…" : "Exportar mis datos"}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>Descargar mis datos guardados</div>
                </div>
                <ChevronRight size={16} strokeWidth={2} color={C.muted} />
              </button>
              {/* Apariencia (modo oscuro fase 1): abierto a TODOS como Beta el 31-ago
                  (a Carlos le encantó y Mario lo había pedido). Cambiar recarga la app. */}
              <div style={{ padding: "13px 14px" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                  Apariencia <span style={{ fontSize: 9, fontWeight: 800, color: C.gold, letterSpacing: 1 }}>BETA</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["claro", "Claro"], ["oscuro", "Oscuro"], ["sistema", "Sistema"]].map(([val, lbl]) => (
                    <button key={val} onClick={() => cambiarTema(val)}
                      style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: temaPreferido() === val ? `2px solid ${C.text}` : `1px solid ${C.border}`, background: temaPreferido() === val ? C.accentLo : C.card, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>El modo oscuro es nuevo: lo estamos puliendo pantalla por pantalla. Si ves algo raro, contanos desde Más → Contacto.</div>
              </div>
              {/* El navegador nuevo (MapLibre) es el mapa del grabador para todos (ver
                  docs/NAVEGADOR_V2.md). Este switch vuelve al mapa de Google en este teléfono
                  si el nuevo da problemas; apagado, manda el interruptor remoto de version.json. */}
              <div style={{ padding: "13px 14px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Mapa anterior (si el nuevo te da problemas)</div>
                  <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4 }}>Encendido: el mapa de siempre. Apagado: el navegador nuevo, más fluido y que sigue sin señal.</div>
                </div>
                <button aria-label="Mapa anterior"
                  onClick={() => { const n = !mapaAnterior; setMapaAnterior(n); try { if (n) localStorage.setItem("olbos_nav_v2", "0"); else localStorage.removeItem("olbos_nav_v2"); localStorage.removeItem("olbos_nav_v2_fallo"); localStorage.removeItem("olbos_nav_v2_fallo_hasta"); } catch (e) { /* elegido a mano: no es por fallos */ } }}
                  style={{ width: 44, height: 26, borderRadius: 13, background: mapaAnterior ? "#276749" : C.border, position: "relative", transition: "background .2s", cursor: "pointer", border: "none", flexShrink: 0 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.card, position: "absolute", top: 3, left: mapaAnterior ? 21 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
                </button>
              </div>
            </div>
          </div>
          {esAdminReal && (
          <div style={{ background: isAdmin ? `${C.green}14` : C.surface, border: `1px solid ${isAdmin ? `${C.green}33` : C.border}`, borderRadius: 14, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isAdmin ? 12 : 0 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: isAdmin ? "#276749" : C.border, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Shield size={18} strokeWidth={2} color={isAdmin ? "#fff" : C.muted} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Modo Administrador</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{isAdmin ? "Activo — acceso completo" : "Desactivado"}</div>
                </div>
              </div>
              <button
                onClick={() => {
                  if (isAdmin) { setIsAdmin(false); setAdminPin(""); }
                  else setShowAdminPin(true);
                }}
                style={{ width: 44, height: 26, borderRadius: 13, background: isAdmin ? "#276749" : C.border, position: "relative", transition: "background .2s", cursor: "pointer", border: "none", flexShrink: 0 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.card, position: "absolute", top: 3, left: isAdmin ? 21 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
              </button>
            </div>

            {/* cuando está activo — accesos rápidos */}
            {isAdmin && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[["Rutas pendientes", "3"], ["Paradas reportadas", "1"]].map(([label, count]) => (
                    <div key={label} style={{ background: C.card, border: `1px solid ${C.green}33`, borderRadius: 8, padding: "6px 12px", fontSize: 12 }}>
                      <span style={{ color: C.muted }}>{label}: </span>
                      <strong style={{ color: C.green }}>{count}</strong>
                    </div>
                  ))}
                </div>
                <ReportesPendientesAdmin />
                <ReportesRutasAdmin />
                <button onClick={onAdmin}
                  style={{ width: "100%", padding: "11px 0", borderRadius: 12, background: "#276749", color: "#fff", fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  🏪 Panel de Negocios
                </button>
              </div>
            )}
          </div>
          )}

          {/* Pantalla de inicio: cada uno abre la app donde la usa */}
          <div style={{ marginTop: 14, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px" }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>Pantalla de inicio</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
              Elegí qué se abre cuando entrás a Olbos.
            </div>
            <select
              defaultValue={localStorage.getItem("olbos_tab_inicio") || "ultima"}
              onChange={e => {
                try { localStorage.setItem("olbos_tab_inicio", e.target.value); } catch (err) {}
              }}
              style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13.5, fontWeight: 600, boxSizing: "border-box" }}>
              <option value="ultima">La última que usé (como siempre)</option>
              <option value="eventos">Eventos</option>
              <option value="feed">Feed social</option>
              <option value="perfil">Perfil</option>
              <option value="rutas">Rutas</option>
              <option value="grabador">Directo a grabar un recorrido</option>
              <option value="mercado">Servicios</option>
            </select>
          </div>

          {/* Aporte anónimo a la red de caminos: opt-out prometido en el aviso del mapa */}
          <AjusteAporteRed />
          <AjusteDetectarCaidas />

          <button onClick={handleLogout}
            style={{ width: "100%", marginTop: 14, display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, cursor: "pointer", textAlign: "left" }}>
            <X size={16} strokeWidth={2} color={C.red} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: C.red }}>Cerrar sesión</div>
          </button>

          {/* El alta de negocio dejó de ser un botón grande arriba de todo: el 99% no
              tiene uno. Queda como una línea para el que sí (feedback de Franco, 08-oct) */}
          {!miNegocio && (
            <button onClick={() => setShowCrearNegocio(true)}
              style={{ width: "100%", marginTop: 18, padding: "6px 0", background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: C.muted, lineHeight: 1.5, textAlign: "center" }}>
              ¿Tenés un taller, un comercio o una marca? <span style={{ color: C.text, fontWeight: 700, textDecoration: "underline" }}>Registrá tu negocio</span>
            </button>
          )}

          {/* Pie del perfil: legal + eliminar cuenta */}
          <div style={{ marginTop: 28, paddingTop: 18, borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 14 }}>
              <a href="https://olbos.ai/privacidad" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C.muted, fontWeight: 600, textDecoration: "none" }}>Privacidad</a>
              <a href="https://olbos.ai/terminos" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C.muted, fontWeight: 600, textDecoration: "none" }}>Términos</a>
            </div>
            <button onClick={eliminarCuenta}
              style={{ fontSize: 12.5, fontWeight: 600, color: C.red, background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              Eliminar mi cuenta
            </button>
          </div>
        </div>
        )}

        {/* ── BIO Y REDES: solo si hay algo que mostrar (se editan en "Editar perfil") ── */}
        {(perfil.bio || (Array.isArray(perfil.links) && perfil.links.length > 0)) && (
          <div style={{ marginTop: 16, padding: "12px 14px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
            {perfil.bio && <p style={{ fontSize: 13, color: C.text, lineHeight: 1.55, margin: 0 }}>{perfil.bio}</p>}
            <LinksPerfil links={perfil.links} style={{ marginTop: perfil.bio ? 10 : 0 }} />
          </div>
        )}

        {/* modal denunciar robo desde garage */}
        {showDenunciarRobo && (
          <DenunciarRoboModal
            userId={userId}
            userMotos={[showDenunciarRobo]}
            onClose={() => setShowDenunciarRobo(null)}
            onCreada={async () => {
              // Marcar moto como robada en el garage
              await cambiarEstadoMoto(showDenunciarRobo.id, "robada");
              setShowDenunciarRobo(null);
              alert("Denuncia publicada en Alertas. La moto pasa a la sección 'Por estas pasé'.");
            }}
          />
        )}

        {/* modal gestionar una moto publicada en Compra-Venta */}
        {gestionVenta && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            onClick={e => e.target === e.currentTarget && setGestionVenta(null)}>
            <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(32px + var(--sab))" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{gestionVenta.moto.marca} {gestionVenta.moto.modelo}</h3>
                <button onClick={() => setGestionVenta(null)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>
              <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                Publicada en Compra-Venta a <strong style={{ color: C.text }}>${Number(gestionVenta.venta.precio).toLocaleString()} {gestionVenta.venta.moneda}</strong>. Sigue activa en tu garage hasta que la vendas.
              </p>
              {/* Destacar la publicación (slot "moto", solo web por la política de pagos de las tiendas) */}
              {!esNativo() && ESPACIOS_VENTA_ACTIVA && (
                <button onClick={() => { const v = gestionVenta.venta; setGestionVenta(null); setDestacarVenta(v); }}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
                  ⭐ Destacarla — primera en Compra-Venta desde ${ESPACIOS_DEF.moto.precio_semana.toLocaleString("es-AR")}/semana
                </button>
              )}
              <button onClick={() => marcarVendida(gestionVenta.moto, gestionVenta.venta)}
                style={{ width: "100%", padding: "14px", borderRadius: 12, background: "#276749", color: "#fff", border: "none", fontSize: 14.5, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
                ✅ La vendí
              </button>
              <button onClick={() => retirarDeVenta(gestionVenta.venta)}
                style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                Retirar de la venta (me la quedo)
              </button>
            </div>
          </div>
        )}

        {destacarVenta && (
          <ComprarEspacioModal slotInicial="moto" ventaId={destacarVenta.id}
            base={{ titulo: `${destacarVenta.marca} ${destacarVenta.modelo}`, texto: destacarVenta.descripcion || "", imagen_url: destacarVenta.foto_url || "", link: "" }}
            onClose={() => setDestacarVenta(null)} />
        )}

        {/* modal compartir historial con un taller (QR + link) */}
        {showHistorialMoto && (() => {
          const linkH = `${SITE_URL}/historial/${showHistorialMoto.token}`;
          const m = showHistorialMoto.moto;
          return (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
              onClick={e => e.target === e.currentTarget && setShowHistorialMoto(null)}>
              <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(32px + var(--sab))", maxHeight: "90vh", overflowY: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Historial de {m.marca} {m.modelo}</h3>
                  <button onClick={() => setShowHistorialMoto(null)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={15} strokeWidth={2.5} />
                  </button>
                </div>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  Mostrale este QR al taller (o mandale el link). Ven todo el historial de service y pueden cargar lo que le hicieron, firmado con su cuenta — te aparece solo en el garage.
                </p>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                  <div style={{ padding: 14, background: C.card, borderRadius: 16, border: `1px solid ${C.border}` }}>
                    <QRCanvas texto={linkH} size={190} />
                  </div>
                </div>
                <div style={{ padding: "10px 12px", background: C.surface, borderRadius: 8, fontSize: 11, color: C.text, fontFamily: "monospace", wordBreak: "break-all", border: `1px solid ${C.border}`, marginBottom: 12 }}>{linkH}</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <button onClick={() => compartirNativo({ texto: `🔧 Historial de service de mi ${m.marca} ${m.modelo} en Olbos:`, url: linkH, titulo: "Historial de service" })}
                    style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#25D366", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    WhatsApp
                  </button>
                  <button onClick={async () => { try { await navigator.clipboard.writeText(linkH); alert("Link copiado"); } catch { /* */ } }}
                    style={{ flex: 1, padding: "12px", borderRadius: 10, background: C.text, color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    Copiar link
                  </button>
                </div>
                <button onClick={() => revocarHistorialMoto(m)}
                  style={{ width: "100%", padding: "10px", borderRadius: 10, background: "transparent", border: "none", color: C.red, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  Desactivar este link
                </button>
              </div>
            </div>
          );
        })()}

        {/* modal vender moto */}
        {showVenderMoto && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            onClick={e => e.target === e.currentTarget && setShowVenderMoto(null)}>
            <div style={{ background: C.card, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px calc(36px + var(--sab))", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{ventaForm.tipo === "alquiler" ? "Publicar en alquiler" : "Publicar en venta"}</h3>
                <button onClick={() => setShowVenderMoto(null)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>

              {/* datos de la moto */}
              <div style={{ background: C.surface, borderRadius: 12, padding: 14, marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
                {showVenderMoto.fotoUrl ? (
                  <img loading="lazy" decoding="async" src={showVenderMoto.fotoUrl} alt="" style={{ width: 60, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 60, height: 48, borderRadius: 8, background: "#e2dcd4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Bike size={22} strokeWidth={1.5} color={C.muted} />
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{showVenderMoto.marca} {showVenderMoto.modelo}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{showVenderMoto.año}{showVenderMoto.cc ? ` · ${showVenderMoto.cc}cc` : ""}{showVenderMoto.color ? ` · ${showVenderMoto.color}` : ""}</div>
                </div>
              </div>

              {/* historial */}
              {(mantenimientos[showVenderMoto.id] || []).length > 0 && (
                <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 10, padding: "10px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 4 }}>✅ Se incluye el historial de servicio cargado por vos en Olbos</div>
                  <div style={{ fontSize: 12, color: C.green }}>
                    {(mantenimientos[showVenderMoto.id] || []).length} registros · {(mantenimientos[showVenderMoto.id] || []).map(r => r.tipo).filter((v, i, a) => a.indexOf(v) === i).slice(0, 3).join(", ")}
                  </div>
                </div>
              )}

              {/* vendo o alquilo */}
              {ALQUILER_P2P_ACTIVO && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[["venta", "La vendo"], ["alquiler", "La alquilo"]].map(([id, label]) => (
                  <button key={id} onClick={() => setVentaForm(f => ({ ...f, tipo: id }))}
                    style={{ flex: 1, padding: "11px 0", borderRadius: 11, cursor: "pointer", fontSize: 14, fontWeight: ventaForm.tipo === id ? 700 : 500,
                      background: ventaForm.tipo === id ? C.text : C.surface, color: ventaForm.tipo === id ? "#fff" : C.text,
                      border: `1px solid ${ventaForm.tipo === id ? C.text : C.border}` }}>
                    {label}
                  </button>
                ))}
              </div>
              )}

              {/* precio */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>
                  {ventaForm.tipo === "alquiler" ? "Precio del alquiler *" : "Precio de venta *"}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <select value={ventaForm.moneda} onChange={e => setVentaForm(f => ({ ...f, moneda: e.target.value }))}
                    style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.card, flexShrink: 0 }}>
                    <option value="ARS">$ ARS</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <input type="number" value={ventaForm.precio} onChange={e => setVentaForm(f => ({ ...f, precio: e.target.value }))}
                    placeholder={ventaForm.tipo === "alquiler" ? "Ej: 60000" : "Ej: 8000000"}
                    style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
                  {ventaForm.tipo === "alquiler" && (
                    <select value={ventaForm.periodo} onChange={e => setVentaForm(f => ({ ...f, periodo: e.target.value }))}
                      style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, background: C.card, flexShrink: 0 }}>
                      <option value="dia">por día</option>
                      <option value="fin_de_semana">por finde</option>
                      <option value="semana">por semana</option>
                    </select>
                  )}
                </div>
              </div>

              {/* alquiler: dónde se entrega y qué pide el dueño */}
              {ventaForm.tipo === "alquiler" && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Dónde la entregás</div>
                    <input value={ventaForm.zona} onChange={e => setVentaForm(f => ({ ...f, zona: e.target.value }))}
                      placeholder="Ej: Bariloche centro"
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Qué pedís</div>
                    <textarea value={ventaForm.requisitos} onChange={e => setVentaForm(f => ({ ...f, requisitos: e.target.value }))}
                      placeholder="Ej: registro con más de 2 años, depósito en garantía, seguro propio…"
                      rows={2}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, resize: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                </>
              )}

              {/* km actuales */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Kilometraje actual</div>
                <input type="number" value={ventaForm.km} onChange={e => setVentaForm(f => ({ ...f, km: e.target.value }))}
                  placeholder="Ej: 15000"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
              </div>

              {/* dónde está la moto (pedido de Carlos 03-sep): el comprador necesita saber si le queda cerca */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Ciudad donde está la moto</div>
                <input value={ventaForm.ciudad || ""} onChange={e => setVentaForm(f => ({ ...f, ciudad: e.target.value }))}
                  placeholder="Ej: Junín, Buenos Aires"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
              </div>

              {/* descripción */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Descripción del estado</div>
                <textarea value={ventaForm.descripcion} onChange={e => setVentaForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Estado general, qué se hizo recientemente, por qué vendés..."
                  rows={3}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, resize: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>

              {/* Referencia de precio: abre la búsqueda en MercadoLibre para comparar.
                  (Traer el promedio automático requiere una app OAuth de ML — descartado
                  10-ago-2026: su API anónima da 403 y el HTML tiene anti-bots.) */}
              {ventaForm.tipo === "venta" && (
                <button onClick={() => window.open(`https://listado.mercadolibre.com.ar/${encodeURIComponent(`${showVenderMoto.marca} ${showVenderMoto.modelo}`.trim().toLowerCase().replace(/\s+/g, "-"))}${showVenderMoto.año ? `_Desde_${showVenderMoto.año}` : ""}`, "_blank")}
                  style={{ width: "100%", padding: "10px", borderRadius: 10, background: `${C.gold}14`, border: `1px solid ${C.gold}55`, color: C.gold, fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}>
                  💰 Ver a cuánto se venden motos como esta en MercadoLibre →
                </button>
              )}

              {/* Alquilar tu moto a un desconocido tiene una letra chica que no puede ser
                  letra chica: la póliza común cubre al titular, no al que la alquila. */}
              {ventaForm.tipo === "alquiler" && (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 4 }}>Antes de publicar, tenelo claro</div>
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: 0 }}>
                    El acuerdo es entre vos y el rider: Olbos no cobra, no retiene ni garantiza nada.
                    Fijate cómo queda tu cobertura — la mayoría de las pólizas cubren al titular y a los conductores declarados, no a un tercero.
                    Si tenés dudas, preguntale a un productor de seguros antes de entregar la moto.
                  </p>
                </div>
              )}

              {/* El botón avisa qué falta en vez de quedar muerto: deshabilitado en silencio
                  parecía "sin programar" (feedback post-Arribeños) */}
              <button onClick={() => {
                if (!ventaForm.precio) { alert(ventaForm.tipo === "alquiler" ? "Falta el precio del alquiler — completalo arriba y publicás." : "Falta el precio de venta — completalo arriba y publicás."); return; }
                publicarVenta(showVenderMoto);
              }} disabled={publicandoVenta}
                style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: ventaForm.precio ? "#276749" : C.faint, color: "#fff", fontWeight: 700, fontSize: 16, border: "none", cursor: "pointer" }}>
                {publicandoVenta ? "Publicando..." : ventaForm.tipo === "alquiler" ? "📢 Publicar en alquiler" : "📢 Publicar en Compra-Venta"}
              </button>
              <p style={{ fontSize: 12, color: C.muted, textAlign: "center", marginTop: 10, lineHeight: 1.6 }}>
                {ventaForm.tipo === "alquiler"
                  ? "Va a aparecer en Servicios → Mercado, en Alquiler, con el historial de servicio a la vista: es lo que le da confianza al que la alquila."
                  : "La publicación va a aparecer en Pit Stop → Compra-Venta con el historial de servicio incluido."}
              </p>
            </div>
          </div>
        )}

        {/* modal agregar moto */}
        {showAddMoto && (
          <Modal title="Agregar moto al garage" onClose={() => setShowAddMoto(false)}>
            {/* upload foto moto */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Foto de tu moto</div>
              {newMoto.fotoUrl ? (
                <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", marginBottom: 4 }}>
                  <img loading="lazy" decoding="async" src={newMoto.fotoUrl} alt="Tu moto" style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
                  <button
                    onClick={() => setNewMoto(n => ({ ...n, fotoUrl: null }))}
                    style={{
                      position: "absolute", top: 8, right: 8,
                      width: 28, height: 28, borderRadius: "50%",
                      background: "rgba(0,0,0,.6)", color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "none", cursor: "pointer",
                    }}
                  >
                    <X size={14} strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <label style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  height: 120, borderRadius: 12, border: `1.5px dashed ${C.border}`,
                  background: C.surface, cursor: "pointer", gap: 8,
                }}>
                  <Camera size={28} strokeWidth={1.5} color={C.muted} />
                  <span style={{ fontSize: 13, color: C.muted }}>Subir foto de la moto</span>
                  <span style={{ fontSize: 11, color: C.faint }}>JPG, PNG · máx 10MB</span>
                  <input ref={motoPhotoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleMotoPhoto} />
                </label>
              )}
            </div>

            <Input label="Marca" value={newMoto.marca} onChange={e => setNewMoto(n => ({ ...n, marca: e.target.value }))} placeholder="BMW, Honda, Yamaha..." />
            <Input label="Modelo" value={newMoto.modelo} onChange={e => setNewMoto(n => ({ ...n, modelo: e.target.value }))} placeholder="R1250GS, Africa Twin..." />
            <Input label='Nombre de tu moto (opcional)' value={newMoto.nombre} onChange={e => setNewMoto(n => ({ ...n, nombre: e.target.value }))} placeholder='Ej: La Negra, Pipo, Rocío...' />
            <div style={{ display: "flex", gap: 10 }}>
              <Input label="Año" value={newMoto.año} onChange={e => setNewMoto(n => ({ ...n, año: e.target.value }))} placeholder="2023" />
              <Input label="CC" value={newMoto.cc} onChange={e => setNewMoto(n => ({ ...n, cc: e.target.value }))} placeholder="1254" />
            </div>
            <Input label="Color" value={newMoto.color} onChange={e => setNewMoto(n => ({ ...n, color: e.target.value }))} placeholder="Blanca, Negra..." />
            <Input label="Patente (opcional)" value={newMoto.patente || ""} onChange={e => setNewMoto(n => ({ ...n, patente: e.target.value.toUpperCase() }))} placeholder="AB123CD" />
            <p style={{ fontSize: 11, color: C.muted, marginTop: -10, marginBottom: 14, lineHeight: 1.5 }}>
              En una rodada, si te pasa algo, el organizador puede identificarte por la patente y avisarle a tu contacto de emergencia. No se muestra en tu perfil público.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <Input label="Nº de chasis (opcional)" value={newMoto.chasis || ""} onChange={e => setNewMoto(n => ({ ...n, chasis: e.target.value.toUpperCase() }))} placeholder="8CH..." />
              <Input label="Nº de motor (opcional)" value={newMoto.motor || ""} onChange={e => setNewMoto(n => ({ ...n, motor: e.target.value.toUpperCase() }))} placeholder="Del grabado" />
            </div>
            <p style={{ fontSize: 11, color: C.muted, marginTop: -10, marginBottom: 14, lineHeight: 1.5 }}>
              Identifican a tu moto de forma unívoca: sirven para el historial de service y ante un robo. Privados, no se muestran a nadie.
            </p>

            {/* galería: fotos y videos (máx 10) */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Galería de la moto (opcional, hasta 10)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(newMoto.media || []).map((f, i) => (
                  <div key={f.tempId || f.url || i} style={{ position: "relative", width: 68, height: 68, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}`, background: "#000" }}>
                    {f.type === "video" ? (
                      <video src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                    ) : (
                      <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                    {f.uploading && (
                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>···</div>
                    )}
                    <button type="button" onClick={() => quitarMediaMoto(i)}
                      style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.75)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                      <X size={11} strokeWidth={3} color="#fff" />
                    </button>
                  </div>
                ))}
                {(newMoto.media || []).length < 10 && (
                  <label style={{ width: 68, height: 68, borderRadius: 10, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.surface, color: C.muted, fontSize: 26, fontWeight: 300 }}>
                    +
                    <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
                      onChange={e => { subirMediaMoto(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>

            <Btn full onClick={agregarMoto}>Agregar al garage</Btn>
          </Modal>
        )}

        {/* ─── DETALLE DE LA MOTO ─── */}
        {detalleMoto && (() => {
          const m = detalleMoto;
          const galeria = (m.media && m.media.length > 0) ? m.media : (m.fotoUrl ? [{ url: m.fotoUrl, type: "image" }] : []);
          const regs = mantenimientos[m.id] || [];
          const totales = {};
          regs.forEach(r => { if (r.costo) { const mon = r.moneda || "ARS"; totales[mon] = (totales[mon] || 0) + Number(r.costo); } });
          const ultimoKm = regs.find(r => r.km)?.km;
          return (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 300, display: "flex", justifyContent: "center" }}
              onClick={e => e.target === e.currentTarget && setDetalleMoto(null)}>
              <div style={{ width: "100%", maxWidth: 430, background: C.card, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                {/* header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(var(--sat) + 14px) 16px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, zIndex: 2 }}>
                  <button onClick={() => setDetalleMoto(null)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ChevronLeft size={18} strokeWidth={2} />
                  </button>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, textAlign: "center", flex: 1, padding: "0 8px" }}>
                    {m.nombre ? `"${m.nombre}"` : `${m.marca} ${m.modelo}`}
                  </div>
                  <button onClick={() => {
                    setEditandoMoto(m);
                    setDetalleMoto(null);
                    setNewMoto({
                      marca: m.marca || "", modelo: m.modelo || "", año: m.año || "",
                      color: m.color || "", cc: m.cc || "", fotoUrl: m.fotoUrl || null,
                      nombre: m.nombre || "", media: Array.isArray(m.media) ? [...m.media] : [],
                      patente: motosPriv[String(m.id)]?.patente || "",
                    });
                  }} style={{ padding: "6px 12px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <Edit2 size={12} strokeWidth={2.5} /> Editar
                  </button>
                </div>

                {/* galería */}
                {galeria.length > 0 ? (
                  <div style={{ position: "relative", width: "100%", aspectRatio: "4/3", background: "#000" }}>
                    {galeria[galeriaIdx].type === "video" ? (
                      <video src={galeria[galeriaIdx].url} controls style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
                    ) : (
                      <img loading="lazy" decoding="async" src={galeria[galeriaIdx].url} alt="" onClick={() => setMediaFullscreen(galeria[galeriaIdx])}
                        style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
                    )}
                    {galeria.length > 1 && (
                      <>
                        <button onClick={() => setGaleriaIdx(i => (i - 1 + galeria.length) % galeria.length)}
                          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <ChevronLeft size={20} strokeWidth={2.5} />
                        </button>
                        <button onClick={() => setGaleriaIdx(i => (i + 1) % galeria.length)}
                          style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <ChevronRight size={20} strokeWidth={2.5} />
                        </button>
                        <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
                          {galeria.map((_, i) => (
                            <div key={i} style={{ width: i === galeriaIdx ? 18 : 6, height: 6, borderRadius: 3, background: i === galeriaIdx ? C.card : "rgba(255,255,255,.5)", transition: "all .2s" }} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ width: "100%", aspectRatio: "4/3", background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.muted, gap: 8 }}>
                    <Bike size={48} strokeWidth={1.5} />
                    <span style={{ fontSize: 12 }}>Sin fotos todavía — tocá "Editar" para agregar</span>
                  </div>
                )}

                {/* ficha técnica — presentación de concesionaria, momento oscuro de marca */}
                <div style={{ margin: "16px 18px", background: C.accent, borderRadius: 16, padding: "20px 20px 18px", color: "#F9F5F2" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.22em", marginBottom: 5 }}>
                        {m.marca || "Ficha técnica"}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                        {m.modelo || "—"}
                      </div>
                      {m.nombre && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginTop: 4 }}>"{m.nombre}"</div>}
                    </div>
                    {m.año && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#F9F5F2", padding: "4px 12px", borderRadius: 100, border: "1px solid rgba(249,245,242,.3)", flexShrink: 0 }}>
                        {m.año}
                      </div>
                    )}
                  </div>

                  <div style={{ height: 1, background: "rgba(249,245,242,.14)", margin: "16px 0 14px" }} />

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px 10px" }}>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Cilindrada</div>
                      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>{m.cc ? `${m.cc} cc` : "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Color</div>
                      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{m.color || "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Kilometraje</div>
                      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>{ultimoKm ? Number(ultimoKm).toLocaleString("es-AR") : "—"}</div>
                      <button onClick={() => { setShowActualizarKm(m.id); setKmUpdate({ km: ultimoKm ? String(ultimoKm) : "", fecha: hoyLocalISO() }); }}
                        style={{ marginTop: 4, padding: "2px 0", background: "transparent", border: "none", borderBottom: "1px solid rgba(249,245,242,.35)", fontSize: 10, fontWeight: 700, color: "#c9c3ba", cursor: "pointer" }}>
                        Actualizar
                      </button>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Servicios</div>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{regs.filter(r => r.tipo !== "Actualización km").length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Último service</div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, lineHeight: "20px" }}>
                        {(() => { const u = regs.find(r => r.tipo !== "Actualización km"); return u ? new Date(u.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "2-digit" }) : "—"; })()}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>Invertido</div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, lineHeight: "20px" }}>
                        {Object.keys(totales).length === 0 ? "—" : Object.entries(totales).map(([mon, t]) => `${mon} ${(t/1000).toFixed(1)}k`).join(" + ")}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 4 }}>🔒 Patente</div>
                      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.02em" }}>{motosPriv[String(m.id)]?.patente || "—"}</div>
                    </div>
                  </div>

                  <div style={{ height: 1, background: "rgba(249,245,242,.14)", margin: "14px 0 10px" }} />
                  <div style={{ fontSize: 9, color: C.muted, textAlign: "center", letterSpacing: "0.06em", lineHeight: 1.6 }}>
                    <span style={{ textTransform: "uppercase", letterSpacing: "0.18em" }}>Olbos · Garage</span><br />
                    La patente y los datos privados solo se comparten en las rodadas que vos elijas
                  </div>
                </div>

                {/* historial de km (toggle) */}
                {regs.filter(r => r.km).length > 1 && (
                  <div style={{ padding: "0 18px 12px" }}>
                    <button onClick={() => setVerHistorialKm(v => !v)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <TrendingUp size={13} strokeWidth={2.2} color={C.muted} />
                        Historial de kilometraje ({regs.filter(r => r.km).length})
                      </span>
                      <ChevronDown size={14} strokeWidth={2.5} color={C.muted} style={{ transform: verHistorialKm ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                    </button>
                    {verHistorialKm && (
                      <div style={{ marginTop: 8, padding: 14, background: "#FAFAFA", borderRadius: 10, border: `1px solid ${C.border}` }}>
                        {regs.filter(r => r.km).map((r, i, arr) => {
                          const prev = arr[i + 1];
                          const delta = prev ? Number(r.km) - Number(prev.km) : null;
                          return (
                            <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none" }}>
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: i === 0 ? "#111111" : C.faint, flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                  {Number(r.km).toLocaleString("es-AR")} km
                                  {delta !== null && delta > 0 && (
                                    <span style={{ fontSize: 11, color: C.green, fontWeight: 600, marginLeft: 6 }}>+{delta.toLocaleString("es-AR")}</span>
                                  )}
                                </div>
                                <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                                  📅 {new Date(r.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}
                                  {r.tipo !== "Actualización km" && ` · ${r.tipo}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* últimos mantenimientos */}
                <div style={{ padding: "0 18px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Últimos servicios</div>
                    <button onClick={() => { setShowAddMant(m.id); }} style={{ fontSize: 12, color: C.text, fontWeight: 700, background: "transparent", border: "none", cursor: "pointer" }}>+ Agregar</button>
                  </div>
                  {(() => {
                    const servicios = regs.filter(r => r.tipo !== "Actualización km");
                    if (servicios.length === 0) {
                      return <div style={{ padding: "16px 0", textAlign: "center", fontSize: 12, color: C.muted, background: C.surface, borderRadius: 10 }}>Sin registros todavía</div>;
                    }
                    return servicios.slice(0, 5).map((r, i) => (
                      <div key={r.id || i} style={{ padding: "10px 12px", borderRadius: 10, background: C.surface, marginBottom: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{r.tipo}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                          📅 {new Date(r.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}
                          {r.costo && ` · 💰 ${r.moneda || "ARS"} ${Number(r.costo).toLocaleString()}`}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── EDITAR MOTO (reutiliza el form de agregar) ─── */}
        {editandoMoto && (
          <Modal title="Editar moto" onClose={() => { setEditandoMoto(null); setNewMoto({ marca: "", modelo: "", año: "", color: "", cc: "", fotoUrl: null, nombre: "", media: [] }); }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Foto principal</div>
              {newMoto.fotoUrl ? (
                <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", marginBottom: 4 }}>
                  <img loading="lazy" decoding="async" src={newMoto.fotoUrl} alt="" style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
                  <button onClick={() => setNewMoto(n => ({ ...n, fotoUrl: null }))}
                    style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer" }}>
                    <X size={14} strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 120, borderRadius: 12, border: `1.5px dashed ${C.border}`, background: C.surface, cursor: "pointer", gap: 8 }}>
                  <Camera size={28} strokeWidth={1.5} color={C.muted} />
                  <span style={{ fontSize: 13, color: C.muted }}>Subir foto principal</span>
                  <input ref={motoPhotoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleMotoPhoto} />
                </label>
              )}
            </div>

            <Input label="Marca" value={newMoto.marca} onChange={e => setNewMoto(n => ({ ...n, marca: e.target.value }))} />
            <Input label="Modelo" value={newMoto.modelo} onChange={e => setNewMoto(n => ({ ...n, modelo: e.target.value }))} />
            <Input label='Nombre de tu moto (opcional)' value={newMoto.nombre} onChange={e => setNewMoto(n => ({ ...n, nombre: e.target.value }))} placeholder='Ej: La Negra, Pipo...' />
            <div style={{ display: "flex", gap: 10 }}>
              <Input label="Año" value={newMoto.año} onChange={e => setNewMoto(n => ({ ...n, año: e.target.value }))} />
              <Input label="CC" value={newMoto.cc} onChange={e => setNewMoto(n => ({ ...n, cc: e.target.value }))} />
            </div>
            <Input label="Color" value={newMoto.color} onChange={e => setNewMoto(n => ({ ...n, color: e.target.value }))} />
            <Input label="Patente" value={newMoto.patente || ""} onChange={e => setNewMoto(n => ({ ...n, patente: e.target.value.toUpperCase() }))} placeholder="AB123CD" />
            <p style={{ fontSize: 11, color: C.muted, marginTop: -10, marginBottom: 14, lineHeight: 1.5 }}>
              🔒 <strong>Confidencial.</strong> La patente no se muestra en tu perfil ni en ningún lado público: solo se usa en las rodadas donde vos elijas compartirla (identificación y emergencias) y para las alertas de robo.
            </p>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Galería (hasta 10)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(newMoto.media || []).map((f, i) => (
                  <div key={f.tempId || f.url || i} style={{ position: "relative", width: 68, height: 68, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}`, background: "#000" }}>
                    {f.type === "video" ? (
                      <video src={cldOpt(f.url, 400)} poster={cldPoster(f.url, 400)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                    ) : (
                      <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                    {f.uploading && (
                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 700 }}>···</div>
                    )}
                    <button type="button" onClick={() => quitarMediaMoto(i)}
                      style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.75)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                      <X size={11} strokeWidth={3} color="#fff" />
                    </button>
                  </div>
                ))}
                {(newMoto.media || []).length < 10 && (
                  <label style={{ width: 68, height: 68, borderRadius: 10, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.surface, color: C.muted, fontSize: 26, fontWeight: 300 }}>
                    +
                    <input type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
                      onChange={e => { subirMediaMoto(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>

            <Btn full onClick={guardarEdicionMoto}>Guardar cambios</Btn>
          </Modal>
        )}

        {/* ─── ACTUALIZAR KM ─── */}
        {showActualizarKm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
            onClick={e => e.target === e.currentTarget && setShowActualizarKm(null)}>
            <div style={{ background: C.card, borderRadius: 16, padding: "22px 20px 20px", width: "100%", maxWidth: 360 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 6 }}>
                  <TrendingUp size={16} strokeWidth={2.2} color="#111111" /> Actualizar kilometraje
                </h2>
                <button onClick={() => setShowActualizarKm(null)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={14} strokeWidth={2} color={C.muted} />
                </button>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Kilometraje actual</div>
                <input type="number" value={kmUpdate.km} onChange={e => setKmUpdate(k => ({ ...k, km: e.target.value }))}
                  placeholder="Ej: 12500" autoFocus
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 16, color: C.text, boxSizing: "border-box", fontWeight: 700 }} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Fecha</div>
                <input type="date" value={kmUpdate.fecha} onChange={e => setKmUpdate(k => ({ ...k, fecha: e.target.value }))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowActualizarKm(null)}
                  style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Cancelar
                </button>
                <button onClick={guardarActualizacionKm} disabled={!kmUpdate.km}
                  style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: kmUpdate.km ? "pointer" : "default", opacity: kmUpdate.km ? 1 : 0.55 }}>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── VIEWER FULLSCREEN PARA GALERÍA ─── */}
        {mediaFullscreen && (
          <div onClick={() => setMediaFullscreen(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.94)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <button onClick={() => setMediaFullscreen(null)} style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: 18, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,.15)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
              <X size={20} strokeWidth={2} color="#fff" />
            </button>
            {mediaFullscreen.type === "video" ? (
              <video src={cldOpt(mediaFullscreen.url, 1080)} poster={cldPoster(mediaFullscreen.url, 480)} controls autoPlay onClick={e => e.stopPropagation()}
                style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, background: "#000" }} />
            ) : (
              <img loading="lazy" decoding="async" src={mediaFullscreen.url} alt="" onClick={e => e.stopPropagation()}
                style={{ maxWidth: "94%", maxHeight: "88vh", borderRadius: 16, objectFit: "contain" }} />
            )}
          </div>
        )}

        {/* CROPPER */}
        {cropSrc && (
          <ImageCropper
            imageSrc={cropSrc}
            aspect={1}
            titulo={cropTipo === "avatar" ? "Ajustá tu foto de perfil" : "Ajustá la foto de tu moto"}
            onCrop={handleCropDone}
            onCancel={() => setCropSrc(null)}
          />
        )}

        {/* modal PIN */}
        {showAdminPin && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={e => e.target === e.currentTarget && setShowAdminPin(false)}>
            <div style={{ background: C.card, borderRadius: 20, padding: 24, width: "100%", maxWidth: 320 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Acceso de administrador</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Ingresá tu PIN</div>
                </div>
                <button onClick={() => { setShowAdminPin(false); setAdminPin(""); }} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>

              {/* display PIN */}
              <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ width: 48, height: 56, borderRadius: 12, border: `2px solid ${adminPin.length === i ? C.text : adminPin.length > i ? "#276749" : C.border}`, background: adminPin.length > i ? `${C.green}14` : C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: C.green, transition: "border .15s" }}>
                    {adminPin.length > i ? "•" : ""}
                  </div>
                ))}
              </div>

              {/* teclado numérico */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k, i) => (
                  <button key={i} onClick={() => {
                    if (k === "⌫") { setAdminPin(p => p.slice(0, -1)); return; }
                    if (k === "") return;
                    const next = adminPin + k;
                    setAdminPin(next);
                    if (next.length === 4) {
                      if (next === ADMIN_PIN) {
                        setIsAdmin(true);
                        setShowAdminPin(false);
                        setAdminPin("");
                      } else {
                        setTimeout(() => setAdminPin(""), 400);
                      }
                    }
                  }} style={{ height: 52, borderRadius: 12, background: k === "" ? "transparent" : k === "⌫" ? C.surface : C.surface, border: k === "" ? "none" : `1px solid ${C.border}`, fontSize: k === "⌫" ? 18 : 20, fontWeight: 600, color: C.text, cursor: k === "" ? "default" : "pointer" }}>
                    {k}
                  </button>
                ))}
              </div>

              {adminPin.length === 4 && adminPin !== ADMIN_PIN && (
                <div style={{ textAlign: "center", fontSize: 12, color: C.red, marginTop: 12 }}>PIN incorrecto. Intentá de nuevo.</div>
              )}
            </div>
          </div>
        )}
      {/* Modal cambiar contraseña */}
      {showCambiarPass && <CambiarPassModal onClose={() => setShowCambiarPass(false)} />}
      </div>
    </PageWrap>
  );
};

// ─── MODAL CAMBIAR CONTRASEÑA ────────────────────────────────────────────────
const CambiarPassModal = ({ onClose }) => {
  const [passActual, setPassActual] = useState("");
  const [passNueva, setPassNueva] = useState("");
  const [passConfirm, setPassConfirm] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);

  const validar = () => {
    if (!passNueva || passNueva.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return false;
    }
    if (!/[A-Z]/.test(passNueva) || !/[a-z]/.test(passNueva) || !/\d/.test(passNueva)) {
      setError("La contraseña debe tener mayúsculas, minúsculas y números.");
      return false;
    }
    if (passNueva !== passConfirm) {
      setError("Las contraseñas no coinciden.");
      return false;
    }
    return true;
  };

  const cambiar = async () => {
    setError(null);
    if (!validar()) return;
    setCargando(true);

    // Verificar contraseña actual reautenticando
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      setError("No se pudo identificar tu cuenta.");
      setCargando(false);
      return;
    }
    const { error: errSignIn } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: passActual,
    });
    if (errSignIn) {
      setError("La contraseña actual es incorrecta.");
      setCargando(false);
      return;
    }

    // Actualizar a la nueva
    const { error: errUpdate } = await supabase.auth.updateUser({ password: passNueva });
    if (errUpdate) {
      setError(errUpdate.message);
      setCargando(false);
      return;
    }
    setExito(true);
    setCargando(false);
    setTimeout(() => onClose(), 1800);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 430,
        background: C.card, borderRadius: "20px 20px 0 0",
        maxHeight: "92vh", overflowY: "auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      }}>
        {/* header */}
        <div style={{
          padding: "calc(var(--sat) + 16px) 20px 14px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: C.card, zIndex: 2,
        }}>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: C.text,
            fontSize: 14, fontWeight: 400, cursor: "pointer",
          }}>Cancelar</button>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>Cambiar contraseña</div>
          <div style={{ width: 60 }} />
        </div>

        <div style={{ padding: "22px 20px 28px" }}>
          {exito ? (
            <div style={{ padding: "32px 0", textAlign: "center" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: `${C.green}14`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px",
              }}>
                <CheckCircle size={28} strokeWidth={1.8} color="#27AE60" />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>Contraseña actualizada</h3>
              <p style={{ fontSize: 13.5, color: C.muted, letterSpacing: "-0.005em" }}>Ya podés usar la nueva contraseña la próxima vez.</p>
            </div>
          ) : (
            <>
              {/* Pass actual */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Contraseña actual</label>
                <CampoPass value={passActual} onChange={e => setPassActual(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: "100%", padding: "11px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, fontSize: 14.5,
                    color: C.text, boxSizing: "border-box",
                    fontFamily: "inherit", letterSpacing: "-0.005em",
                  }} />
              </div>

              {/* Pass nueva */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Nueva contraseña</label>
                <CampoPass value={passNueva} onChange={e => setPassNueva(e.target.value)}
                  placeholder="Al menos 8 caracteres"
                  style={{
                    width: "100%", padding: "11px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, fontSize: 14.5,
                    color: C.text, boxSizing: "border-box",
                    fontFamily: "inherit", letterSpacing: "-0.005em",
                  }} />
                <p style={{ fontSize: 11, color: C.muted, marginTop: 6, letterSpacing: "-0.005em", lineHeight: 1.4 }}>
                  Debe incluir mayúsculas, minúsculas y números.
                </p>
              </div>

              {/* Confirmar */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Confirmar nueva contraseña</label>
                <CampoPass value={passConfirm} onChange={e => setPassConfirm(e.target.value)}
                  placeholder="Repetí la nueva contraseña"
                  style={{
                    width: "100%", padding: "11px 12px", borderRadius: 10,
                    border: `1px solid ${C.border}`, fontSize: 14.5,
                    color: C.text, boxSizing: "border-box",
                    fontFamily: "inherit", letterSpacing: "-0.005em",
                  }} />
              </div>

              {error && (
                <div style={{
                  padding: "10px 12px", borderRadius: 10,
                  background: "#FFF1F0", border: "1px solid #FFD7D2",
                  marginBottom: 14,
                  fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em",
                }}>{error}</div>
              )}

              <button onClick={cambiar} disabled={cargando || !passActual || !passNueva || !passConfirm}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 12,
                  background: C.accent, color: "#fff",
                  border: "none", fontSize: 15, fontWeight: 500,
                  cursor: cargando ? "default" : "pointer",
                  opacity: (cargando || !passActual || !passNueva || !passConfirm) ? 0.5 : 1,
                  letterSpacing: "-0.01em",
                }}>
                {cargando ? "Guardando…" : "Cambiar contraseña"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
// ── DATA: RIDERS EN LA ZONA ───────────────────────────────────────────────────
// Privacy levels: "preciso" = exact (only for SOS), "zona" = ~500m radius blur, "ciudad" = city only
// ─── AVATAR DE RIDER (busca foto real en Supabase) ───────────────────────────
const RiderAvatar = ({ username, size = 40, status }) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const color = status === "sos" ? C.red : status === "ride_now" ? C.accent : C.green;

  useEffect(() => {
    if (!username) return;
    supabase.from("perfiles").select("avatar_url").eq("username", username).single()
      .then(({ data }) => { if (data?.avatar_url) setAvatarUrl(data.avatar_url); });
  }, [username]);

  return (
    <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", border: `2.5px solid ${color}`, flexShrink: 0, background: avatarUrl ? `${C.border}` : color + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {avatarUrl
        ? <img loading="lazy" decoding="async" src={avatarUrl} alt={username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span style={{ fontSize: size * 0.38, fontWeight: 800, color }}>{username?.[0]?.toUpperCase() || "?"}</span>
      }
    </div>
  );
};

// ── MAPA ESQUEMÁTICO ─────────────────────────────────────────────────────────

const MapView = ({ riders, onSelectRider, activeFilter }) => {
  // Posiciones ficticias relativas en el mapa
  const positions = [
    { id: 1, x: 52, y: 38 },
    { id: 2, x: 35, y: 55 },
    { id: 3, x: 68, y: 62 },
    { id: 4, x: 78, y: 28 }, // SOS
    { id: 5, x: 22, y: 72 },
    { id: 6, x: 60, y: 80 },
  ];

  const statusColor = (s) => s === "sos" ? C.red : s === "ride_now" ? C.accent : C.green;
  const statusEmoji = (s) => s === "sos" ? "🆘" : s === "ride_now" ? "🏍️" : "●";

  return (
    <div style={{ position: "relative", height: 260, background: "linear-gradient(135deg, #e8f0e8 0%, #d4e8d4 50%, #c8dcc8 100%)", borderRadius: 16, overflow: "hidden", border: `1px solid ${C.border}` }}>
      {/* cuadrícula de calles simulada */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.3 }}>
        {[20, 40, 60, 80].map(x => <line key={`v${x}`} x1={`${x}%`} y1="0" x2={`${x}%`} y2="100%" stroke="#999" strokeWidth="1" />)}
        {[25, 50, 75].map(y => <line key={`h${y}`} x1="0" y1={`${y}%`} x2="100%" y2={`${y}%`} stroke="#999" strokeWidth="1" />)}
        {/* ruta principal diagonal */}
        <line x1="0" y1="30%" x2="100%" y2="70%" stroke="#bbb" strokeWidth="3" />
        <line x1="15%" y1="0" x2="85%" y2="100%" stroke="#ccc" strokeWidth="2" />
      </svg>

      {/* label ciudad */}
      <div style={{ position: "absolute", top: 10, left: 14, fontSize: 11, fontWeight: 700, color: C.muted, background: "rgba(255,255,255,.7)", padding: "2px 8px", borderRadius: 10 }}>
        📍 Mendoza, Argentina
      </div>

      {/* tu ubicación */}
      <div style={{ position: "absolute", left: "48%", top: "48%", transform: "translate(-50%,-50%)" }}>
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.blue, border: "3px solid #fff", boxShadow: "0 0 0 4px rgba(0,102,204,.25)", position: "relative", zIndex: 10 }} />
        <div style={{ position: "absolute", top: -4, left: -4, width: 26, height: 26, borderRadius: "50%", border: `2px solid ${C.blue}`, animation: "ping 1.5s ease-out infinite", opacity: 0 }} />
      </div>

      {/* riders en el mapa */}
      {positions
        .filter(p => {
          const r = riders.find(r => r.id === p.id);
          if (!r) return false;
          if (activeFilter === "ride_now") return r.status === "ride_now";
          if (activeFilter === "sos") return r.status === "sos";
          return true;
        })
        .map(p => {
          const r = riders.find(r => r.id === p.id);
          if (!r) return null;
          const color = statusColor(r.status);
          const isSos = r.status === "sos";
          return (
            <button key={p.id} onClick={() => onSelectRider(r)}
              style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%,-50%)", zIndex: isSos ? 20 : 10 }}>
              <div style={{ position: "relative" }}>
                {isSos && <div style={{ position: "absolute", inset: -4, borderRadius: "50%", background: C.red, animation: "ripple 1.2s ease-out infinite" }} />}
                <div style={{
                  width: isSos ? 40 : 32, height: isSos ? 40 : 32,
                  borderRadius: "50%", background: color,
                  border: "3px solid #fff", boxShadow: `0 2px 8px ${color}66`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: isSos ? 18 : 14,
                  animation: isSos ? "sosPulse 1.6s ease-in-out infinite" : "none",
                }}>
                  {r.avatar}
                </div>
                {r.status === "ride_now" && (
                  <div style={{ position: "absolute", bottom: -14, left: "50%", transform: "translateX(-50%)", background: C.accent, color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 8, whiteSpace: "nowrap" }}>SALIDA</div>
                )}
              </div>
            </button>
          );
        })}

      {/* leyenda */}
      <div style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(255,255,255,.9)", borderRadius: 10, padding: "6px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
        {[["●", C.green, "Activo"], ["🏍️", C.accent, "Salida ahora"], ["🆘", C.red, "SOS"]].map(([icon, color, label]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10 }}>
            <span style={{ color, fontSize: 12 }}>{icon}</span>
            <span style={{ color: C.muted }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ── CARD: RIDER CERCANO ───────────────────────────────────────────────────────
const RiderCard = ({ rider, onDM, onAyudar }) => {
  const statusBg  = rider.status === "sos" ? C.redLo  : rider.status === "ride_now" ? C.accentLo : C.greenLo;
  const statusClr = rider.status === "sos" ? C.red    : rider.status === "ride_now" ? C.accent   : C.green;
  const statusTxt = rider.status === "sos" ? "🆘 SOS — Necesita ayuda" : rider.status === "ride_now" ? "🏍️ Quiere salir ahora" : "● En zona";

  return (
    <div style={{ background: C.card, borderRadius: 14, border: `1.5px solid ${rider.status === "sos" ? C.red : rider.status === "ride_now" ? C.accent : C.border}`, padding: 14, marginBottom: 10, boxShadow: rider.status === "sos" ? `0 0 0 3px ${C.red}22` : "0 1px 4px rgba(0,0,0,.06)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{ width: 46, height: 46, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, border: `2px solid ${statusClr}` }}>{rider.avatar}</div>
          {rider.online && <div style={{ position: "absolute", bottom: 1, right: 1, width: 12, height: 12, borderRadius: "50%", background: C.green, border: "2px solid #fff" }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                @{rider.user}
                {rider.verified && <span style={{ background: C.accent, color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 8 }}>✓</span>}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>🏍️ {rider.moto}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: statusClr }}>{rider.dist} km</div>
              <div style={{ fontSize: 10, color: C.muted }}>de vos</div>
            </div>
          </div>
          <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: statusBg, padding: "4px 10px", borderRadius: 20 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: statusClr }}>{statusTxt}</span>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {rider.status === "sos" ? (
          <Btn full variant="red" small onClick={() => onAyudar(rider)}>🆘 Ir a ayudar</Btn>
        ) : rider.status === "ride_now" ? (
          <Btn full small onClick={() => onDM(rider)}>Sumarme</Btn>
        ) : (
          <Btn full variant="outline" small onClick={() => onDM(rider)}>✉️ Escribir</Btn>
        )}
      </div>
    </div>
  );
};



// ─── COMPARTIR UBICACIÓN EN VIVO ──────────────────────────────────────────────
//   1) CompartirVivoModal — el rider comparte su ubicación (hoy sin punto de entrada:
//      la pantalla "En Vivo" que lo abría se eliminó el 26-ago-2026 y el camino real
//      pasa por el track recorder → "Seguime en vivo". Se deja armado por si vuelve a
//      colgarse de un botón.)
//   2) VerUbicacionVivo   — vista pública (sin login) para quien recibe el link

// Sesión de compartir-en-vivo que SOBREVIVE al modal: el rider puede cerrar el modal y
// seguir usando la app sin cortar la transmisión (el watch de background sigue andando).
// Al reabrir el modal, retoma esta sesión. Solo "Detener" la termina de verdad.
let _vivoSesion = null; // { registro, watch, coords, ts }

const CompartirVivoModal = ({ session, onClose }) => {
  const [paso, setPaso] = useState(_vivoSesion ? "activo" : "config"); // config | activo
  const [titulo, setTitulo] = useState("");
  const [destinoNombre, setDestinoNombre] = useState("");
  const [duracionHoras, setDuracionHoras] = useState(3);
  const [iniciando, setIniciando] = useState(false);
  const [registro, setRegistro] = useState(_vivoSesion ? _vivoSesion.registro : null); // {id, share_token, expira_en}
  const [coords, setCoords] = useState(_vivoSesion ? _vivoSesion.coords || null : null);
  const [ultimoUpdate, setUltimoUpdate] = useState(_vivoSesion && _vivoSesion.ts ? new Date(_vivoSesion.ts) : null);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(_vivoSesion ? _vivoSesion.watch : null);
  const ultimaSubidaRef = useRef(0);
  const userId = session?.user?.id;

  // Mientras está activo, refrescar la posición mostrada desde la sesión (el callback
  // del watch escribe ahí aunque este modal se haya cerrado y reabierto).
  useEffect(() => {
    if (paso !== "activo") return;
    const t = setInterval(() => {
      if (_vivoSesion && _vivoSesion.coords) {
        setCoords(_vivoSesion.coords);
        if (_vivoSesion.ts) setUltimoUpdate(new Date(_vivoSesion.ts));
      }
    }, 3000);
    return () => clearInterval(t);
  }, [paso]);

  // Iniciar compartido
  const iniciar = async () => {
    if (!navigator.geolocation) { setError("Tu navegador no soporta geolocalización"); return; }
    setIniciando(true);
    setError(null);

    // Pedir ubicación inicial
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const expira = new Date(Date.now() + duracionHoras * 3600 * 1000).toISOString();
      const token = `vivo_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

      // Obtener username del perfil
      const { data: perfil } = await supabase.from("perfiles").select("username").eq("id", userId).single();

      const { data, error: errIns } = await supabase.from("ubicaciones_vivo").insert({
        user_id: userId,
        share_token: token,
        username: perfil?.username || "Rider",
        lat, lng,
        velocidad: pos.coords.speed || null,
        precision_metros: pos.coords.accuracy || null,
        titulo: titulo || null,
        destino_nombre: destinoNombre || null,
        activo: true,
        expira_en: expira,
      }).select().single();

      setIniciando(false);
      if (errIns) { setError("Error: " + errIns.message); return; }

      setRegistro(data);
      bgSesionActualizar({ share_token: data.share_token });
      setCoords({ lat, lng });
      setUltimoUpdate(new Date());
      setPaso("activo");

      // Watch continuo — startGeoWatch sigue transmitiendo con la pantalla apagada (nativo)
      watchIdRef.current = await startGeoWatch(
        async (gp) => {
          setCoords({ lat: gp.lat, lng: gp.lng });
          // La sesión module-level guarda la última posición (el modal puede estar cerrado)
          if (_vivoSesion) { _vivoSesion.coords = { lat: gp.lat, lng: gp.lng }; _vivoSesion.ts = Date.now(); }
          // Throttle: máximo 1 update cada 8 segundos
          const ahora = Date.now();
          if (ahora - ultimaSubidaRef.current < 8000) return;
          ultimaSubidaRef.current = ahora;
          await supabase.from("ubicaciones_vivo").update({
            lat: gp.lat,
            lng: gp.lng,
            velocidad: gp.v || null,
            precision_metros: gp.acc || null,
            ultimo_update: new Date().toISOString(),
          }).eq("id", data.id);
          setUltimoUpdate(new Date());
        },
        (err) => { console.error("GPS watch error:", err); }
      );
      _vivoSesion = { registro: data, watch: watchIdRef.current, coords: { lat, lng }, ts: Date.now() };
    }, (err) => {
      setIniciando(false);
      setError(err.code === 1 ? "Permiso de ubicación denegado" : "No se pudo obtener tu ubicación");
    }, { enableHighAccuracy: true, timeout: 12000 });
  };

  const detener = async () => {
    const w = watchIdRef.current || (_vivoSesion && _vivoSesion.watch);
    if (w) { try { await w.stop(); } catch (e) {} }
    watchIdRef.current = null;
    const reg = registro || (_vivoSesion && _vivoSesion.registro);
    _vivoSesion = null;
    if (reg) {
      await supabase.from("ubicaciones_vivo").update({ activo: false }).eq("id", reg.id);
    }
    onClose();
  };

  // Al desmontar: si hay sesión viva NO frenar nada — el usuario cerró el modal pero
  // sigue compartiendo (podrá volver desde el botón "Compartiendo" o detener después).
  useEffect(() => {
    return () => {
      if (!_vivoSesion && watchIdRef.current) watchIdRef.current.stop();
    };
  }, []);

  const linkVivo = registro ? `${SITE_URL}/vivo/${registro.share_token}` : "";

  const compartirWhatsApp = () => {
    compartirNativo({ texto: "📍 Estoy compartiendo mi ubicación en vivo en Olbos. Seguime acá:", url: linkVivo, titulo: "Mi ubicación en vivo" });
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(linkVivo);
      alert("Link copiado");
    } catch { alert("No se pudo copiar. El link es: " + linkVivo); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => e.target === e.currentTarget && paso === "config" && onClose()}>
      <div style={{ background: C.card, borderRadius: 16, padding: "22px 20px 20px", width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: paso === "activo" ? "#27AE60" : C.muted, display: "inline-block", animation: paso === "activo" ? "pulse 1.5s ease-in-out infinite" : "none" }} />
            {paso === "activo" ? "Compartiendo en vivo" : "Compartir ubicación en vivo"}
          </h2>
          <button onClick={onClose} title={paso === "activo" ? "Cerrar (seguís compartiendo)" : "Cerrar"}
            style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={14} strokeWidth={2} color={C.muted} />
          </button>
        </div>

        {paso === "config" && (
          <>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
              Otros riders (o tu familia) van a poder ver dónde estás en tiempo real con un link, sin necesidad de tener cuenta en Olbos.
            </p>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Título (opcional)</div>
              <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej: Saliendo a Mar del Plata"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Destino (opcional)</div>
              <input value={destinoNombre} onChange={e => setDestinoNombre(e.target.value)} placeholder="Ej: Mar del Plata"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box" }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Compartir durante</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[1, 3, 6, 12].map(h => (
                  <button key={h} onClick={() => setDuracionHoras(h)}
                    style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: duracionHoras === h ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: duracionHoras === h ? "#1111110C" : C.card, color: duracionHoras === h ? C.accent : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    {h}h
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: 10, background: "#F6FBF7", border: `1px solid ${C.green}33`, borderRadius: 8, fontSize: 11, color: "#1F8347", marginBottom: 14, lineHeight: 1.5 }}>
              ✓ Podés bloquear el celular o seguir usando Olbos tranquilo: tu ubicación se sigue compartiendo igual hasta que la detengas.
            </div>

            {error && (
              <div style={{ padding: 10, background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 8, fontSize: 12, color: C.red, marginBottom: 12 }}>{error}</div>
            )}

            <button onClick={iniciar} disabled={iniciando}
              style={{ width: "100%", padding: "13px 16px", borderRadius: 10, background: "#27AE60", color: "#fff", border: "none", fontWeight: 800, fontSize: 14, cursor: iniciando ? "default" : "pointer", opacity: iniciando ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              📍 {iniciando ? "Activando GPS…" : "Empezar a compartir"}
            </button>
          </>
        )}

        {paso === "activo" && (
          <>
            <div style={{ padding: 12, background: "#27AE600C", border: "1px solid #27AE6033", borderRadius: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#27AE60", fontWeight: 700, marginBottom: 4 }}>● EN VIVO</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                Ubicación actualizada {ultimoUpdate ? `hace ${Math.floor((Date.now() - ultimoUpdate.getTime()) / 1000)}s` : "—"}
              </div>
              {coords && (
                <div style={{ fontSize: 11, color: C.faint, marginTop: 4, fontFamily: "monospace" }}>
                  {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Link para compartir</div>
              <div style={{ padding: "10px 12px", background: C.surface, borderRadius: 8, fontSize: 11, color: C.text, fontFamily: "monospace", wordBreak: "break-all", border: `1px solid ${C.border}` }}>{linkVivo}</div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button onClick={compartirWhatsApp}
                style={{ flex: 1, padding: "11px 14px", borderRadius: 10, background: "#25D366", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                WhatsApp
              </button>
              <button onClick={copiarLink}
                style={{ flex: 1, padding: "11px 14px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Copiar link
              </button>
            </div>

            <button onClick={onClose}
              style={{ width: "100%", padding: "12px 16px", borderRadius: 10, marginBottom: 8, background: C.accent, color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Cerrar y seguir compartiendo
            </button>
            <button onClick={detener}
              style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.card, color: C.red, border: `1px solid ${C.red}33`, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              ✕ Detener y dejar de compartir
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Vista pública del link compartido (no requiere login)
const VerUbicacionVivo = ({ token, onVolver, rodar = false }) => {
  const [ubicacion, setUbicacion] = useState(null);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [expirado, setExpirado] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const recorridoRef = useRef(null);
  const planRef = useRef(null); // el camino planeado del navegador (punteado)
  const animVisorRef = useRef(null);
  const ultimoDragRef = useRef(0); // si el que mira movió el mapa, no recentrar por un rato
  // El punto se desliza hasta la posición nueva (estilo Uber): antes saltaba en
  // seco con cada actualización y parecía estático — reporte de Carlos
  const deslizarMarker = (marker, hasta, dur = 5000) => {
    if (!marker) return;
    const desdeLL = marker.getPosition();
    if (!desdeLL) { marker.setPosition(hasta); return; }
    const desde = { lat: desdeLL.lat(), lng: desdeLL.lng() };
    if (Math.abs(desde.lat - hasta.lat) + Math.abs(desde.lng - hasta.lng) < 1e-7) return;
    if (animVisorRef.current) cancelAnimationFrame(animVisorRef.current);
    const t0 = performance.now();
    const paso = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      const nueva = { lat: desde.lat + (hasta.lat - desde.lat) * k, lng: desde.lng + (hasta.lng - desde.lng) * k };
      marker.setPosition(nueva);
      if (mapInstanceRef.current && Date.now() - ultimoDragRef.current > 20000) mapInstanceRef.current.setCenter(nueva);
      if (k < 1) animVisorRef.current = requestAnimationFrame(paso);
    };
    animVisorRef.current = requestAnimationFrame(paso);
  };
  // Rodada espontánea: los que abren el link con cuenta pueden sumarse al mapa.
  // Solo el creador graba el recorrido; los sumados publican posición cada ~10s.
  const [participantes, setParticipantes] = useState([]);
  const [yo, setYo] = useState(null); // null = cargando, false = sin sesión, {id, username} = logueado
  const [publicando, setPublicando] = useState(false);
  const watchIdRef = useRef(null);
  const ultimoPushRef = useRef(0);
  const partMarkersRef = useRef({});
  const avatarUrlsRef = useRef({}); // user_id → avatar_url para los pins con foto
  // El que se suma también se lleva SU recorrido: el GPS ya está andando para el mapa,
  // acumular los puntos es gratis — al salir se guarda como traza y suma a su pasaporte
  const misPuntosRef = useRef([]);
  const trazaGuardadaRef = useRef(false);
  const creadorRef = useRef("amigos"); // en ref: el cleanup de desmontaje ve el valor real
  const guardarMiTraza = (userId) => {
    if (trazaGuardadaRef.current || !userId) return;
    const pts = misPuntosRef.current;
    if (pts.length < 2) return;
    const stats = calcEstadisticas(pts);
    if (stats.dist < 0.3) return; // menos de 300 m: fue una probadita, no un recorrido
    trazaGuardadaRef.current = true;
    supabase.from("trazas").insert(conVehiculo({
      user_id: userId,
      titulo: `Rodada con @${creadorRef.current}`,
      puntos: pts.map(p => ({ lat: p.lat, lng: p.lng })),
      distancia_km: stats.dist, duracion_seg: stats.dur,
      velocidad_max: stats.velMax, velocidad_prom: stats.velProm,
      estado: "finalizada", publica: false,
      iniciada_en: new Date(pts[0].ts).toISOString(),
      finalizada_en: new Date().toISOString(),
    })).then(() => {}, () => {});
    // Este camino también alimenta la red anónima (antes se escapaba por este atajo)
    try { aportarTramoARed(pts); } catch (e) { /* */ }
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { setYo(false); return; }
      supabase.from("perfiles").select("id, username").eq("id", user.id).single()
        .then(({ data }) => setYo(data || { id: user.id, username: "rider" }), () => setYo({ id: user.id, username: "rider" }));
    }, () => setYo(false));
  }, []);

  // Poll cada 10 segundos
  useEffect(() => {
    let activo = true;
    const fetch = async () => {
      const { data, error } = await supabase.from("ubicaciones_vivo")
        .select("*")
        .eq("share_token", token)
        .maybeSingle();
      if (!activo) return;
      // Un error (sin red, volver de otra app con la sesión a medio refrescar) NO es
      // "link no encontrado": se reintenta en el próximo tick. Antes cualquier fallo
      // dejaba la pantalla negra de "Link no encontrado" hasta reabrir el link (Carlos,
      // siguiendo a Matías camino a Carlos Paz, 09-oct).
      if (error) return;
      if (!data) { setNoEncontrado(true); return; }
      setNoEncontrado(false);
      // Participantes sumados (frescos: últimos 5 min). Si la tabla no existe aún, queda vacío.
      supabase.from("vivo_participantes").select("*")
        .eq("share_token", token)
        .gt("updated_at", new Date(Date.now() - 5 * 60000).toISOString())
        .then(({ data: parts }) => {
          if (!activo || !Array.isArray(parts)) return;
          setParticipantes(parts);
          // Fotos de perfil para los pins (solo las que falten)
          const faltan = parts.map(p => p.user_id).filter(id => !(id in avatarUrlsRef.current));
          if (faltan.length > 0) {
            supabase.from("perfiles").select("id, avatar_url").in("id", faltan)
              .then(({ data: ps }) => {
                (ps || []).forEach(p => { avatarUrlsRef.current[p.id] = p.avatar_url || null; });
                // Los pins que ya estaban dibujados con la inicial reciben la foto ahora
                (ps || []).forEach(p => {
                  const mk = partMarkersRef.current[p.id];
                  if (!mk || !p.avatar_url) return;
                  iconoAvatarMapa(p.avatar_url, (icon) => { if (icon && partMarkersRef.current[p.id] === mk) { mk.setIcon(icon); mk.setLabel(null); } });
                });
              }, () => {});
          }
        }, () => {});
      if (!data.activo) { setExpirado(true); setUbicacion(data); return; }
      if (data.expira_en && new Date(data.expira_en) < new Date()) { setExpirado(true); setUbicacion(data); return; }
      setUbicacion(data);
      setExpirado(false);
    };
    fetch();
    const intervalo = setInterval(fetch, 5000);
    // Los intervalos se pausan con la pestaña de fondo (pantalla bloqueada,
    // volver de WhatsApp): al volver a verse, refrescar al toque — si no, el
    // que mira ve al rider congelado en la posición vieja hasta el próximo tick
    const alVolver = () => { if (document.visibilityState === "visible") fetch(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => { activo = false; clearInterval(intervalo); document.removeEventListener("visibilitychange", alVolver); };
  }, [token]);

  // Publicar mi posición en la rodada (throttle 10s)
  const publicarPos = (lat, lng) => {
    if (!yo || !yo.id) return;
    supabase.from("vivo_participantes").upsert({
      share_token: token, user_id: yo.id, username: yo.username || "rider",
      lat, lng, updated_at: new Date().toISOString(),
    }, { onConflict: "share_token,user_id" }).then(() => {}, () => {});
  };
  const sumarme = () => {
    if (!navigator.geolocation) { alert("Tu navegador no soporta ubicación."); return; }
    navigator.geolocation.getCurrentPosition(pos => {
      publicarPos(pos.coords.latitude, pos.coords.longitude);
      ultimoPushRef.current = Date.now();
      misPuntosRef.current.push({ lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() });
      setPublicando(true);
      watchIdRef.current = navigator.geolocation.watchPosition(p => {
        misPuntosRef.current.push({ lat: p.coords.latitude, lng: p.coords.longitude, ts: Date.now() });
        if (Date.now() - ultimoPushRef.current < 10000) return;
        ultimoPushRef.current = Date.now();
        publicarPos(p.coords.latitude, p.coords.longitude);
      }, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    }, () => alert("No pudimos leer tu ubicación. Revisá el permiso de ubicación del navegador."), { enableHighAccuracy: true, timeout: 12000 });
  };
  const salirme = () => {
    if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    setPublicando(false);
    if (yo && yo.id) {
      guardarMiTraza(yo.id); // su recorrido queda en Mis recorridos y suma al pasaporte
      supabase.from("vivo_participantes").delete().eq("share_token", token).eq("user_id", yo.id).then(() => {}, () => {});
    }
  };
  const yoRefTraza = useRef(null);
  useEffect(() => { yoRefTraza.current = yo && yo.id ? yo.id : null; }, [yo]);
  useEffect(() => { if (ubicacion && ubicacion.username) creadorRef.current = ubicacion.username; }, [ubicacion]);
  useEffect(() => () => {
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    guardarMiTraza(yoRefTraza.current); // cerró la pestaña sin tocar "salir": se guarda igual
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar Google Maps y mostrar el marcador
  useEffect(() => {
    if (!ubicacion || !mapRef.current) return;
    loadGoogleMaps().then(() => {
      const google = window.google;
      if (!google || !mapRef.current) return;
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: Number(ubicacion.lat), lng: Number(ubicacion.lng) },
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
        });
        // Si el que mira explora el mapa a mano, dejamos de recentrarlo por un rato
        mapInstanceRef.current.addListener("dragstart", () => { ultimoDragRef.current = Date.now(); });
      }
      const pos = { lat: Number(ubicacion.lat), lng: Number(ubicacion.lng) };
      if (!markerRef.current) {
        markerRef.current = new google.maps.Marker({
          position: pos,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: expirado ? "#999" : "#27AE60",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          },
        });
      } else {
        deslizarMarker(markerRef.current, pos);
        markerRef.current.setIcon({
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: expirado ? "#999" : "#27AE60",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 3,
        });
      }
      // Recorrido trazado hasta ahora (si comparte desde el grabador de tracks).
      // Sin los primeros 500 m: el link es público y el arranque suele ser la casa
      // (mismo criterio que el post automático del recorrido). La punta de adelante
      // es la posición en vivo: esa se ve entera.
      const sinArranque = (arr, km = 0.5) => {
        if (arr.length < 10) return arr;
        let acc = 0, i = 0;
        for (; i < arr.length - 1 && acc < km; i++) acc += haversineKm(arr[i], arr[i + 1]);
        return arr.slice(i);
      };
      const rec = sinArranque(Array.isArray(ubicacion.recorrido)
        ? ubicacion.recorrido.filter(p => p && p.lat && p.lng).map(p => ({ lat: Number(p.lat), lng: Number(p.lng) }))
        : []);
      if (rec.length > 1) {
        // Un hueco (pantalla bloqueada, sin señal) no se dibuja como recta: un tramo por línea
        const tramos = partirEnTramos(rec);
        const lineas = Array.isArray(recorridoRef.current) ? recorridoRef.current : (recorridoRef.current ? [recorridoRef.current] : []);
        while (lineas.length < tramos.length) lineas.push(new google.maps.Polyline({ strokeOpacity: 0.9, strokeWeight: 4, zIndex: 2, map: mapInstanceRef.current }));
        lineas.forEach((pl, i) => {
          if (i < tramos.length) { pl.setPath(tramos[i]); pl.setOptions({ strokeColor: expirado ? "#999" : "#111111" }); pl.setMap(mapInstanceRef.current); }
          else pl.setMap(null);
        });
        recorridoRef.current = lineas;
      }
      // El CAMINO PLANEADO del navegador (punteado azul): no solo por dónde vino,
      // también a dónde va — pedido de Carlos
      if (ubicacion.ruta_planeada) {
        const plan = sinArranque(decodificarPolyline(ubicacion.ruta_planeada));
        if (plan.length > 1) {
          if (!planRef.current) {
            planRef.current = new google.maps.Polyline({
              path: plan, map: mapInstanceRef.current, strokeOpacity: 0, zIndex: 1,
              icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 0.95, strokeColor: "#1A73E8", scale: 4 }, offset: "0", repeat: "13px" }],
            });
          } else { planRef.current.setPath(plan); planRef.current.setMap(mapInstanceRef.current); }
        }
      } else if (planRef.current) { planRef.current.setMap(null); }
      // Los que se sumaron a la rodada: un punto negro con su usuario arriba
      const vivos = participantes.filter(p => p.lat && p.lng);
      const porId = {};
      vivos.forEach(p => { porId[p.user_id] = p; });
      Object.keys(partMarkersRef.current).forEach(id => {
        if (!porId[id]) { partMarkersRef.current[id].setMap(null); delete partMarkersRef.current[id]; }
      });
      vivos.forEach(p => {
        const ppos = { lat: Number(p.lat), lng: Number(p.lng) };
        if (partMarkersRef.current[p.user_id]) {
          partMarkersRef.current[p.user_id].setPosition(ppos);
        } else {
          const mk = new google.maps.Marker({
            position: ppos, map: mapInstanceRef.current,
            icon: { path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5, labelOrigin: new google.maps.Point(0, -2.4) },
            label: { text: p.username || "rider", color: C.text, fontSize: "10px", fontWeight: "800" },
          });
          partMarkersRef.current[p.user_id] = mk;
          iconoAvatarMapa(avatarUrlsRef.current[p.user_id], (icon) => {
            if (icon && partMarkersRef.current[p.user_id] === mk) { mk.setIcon(icon); mk.setLabel(null); }
          });
        }
      });
      if (Date.now() - ultimoDragRef.current > 20000) mapInstanceRef.current.panTo(pos);
    });
  }, [ubicacion, expirado, participantes]);

  if (noEncontrado) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>Link no encontrado</h2>
        <p style={{ fontSize: 14, color: C.muted, marginBottom: 18 }}>Este link puede haber sido eliminado o nunca existió.</p>
        <button onClick={onVolver} style={{ padding: "10px 18px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontWeight: 700, cursor: "pointer" }}>Ir a Olbos</button>
      </div>
    );
  }

  if (!ubicacion) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Cargando…</div>;
  }

  const segundosDesdeUpdate = Math.floor((Date.now() - new Date(ubicacion.ultimo_update).getTime()) / 1000);
  // Km recorridos: se calculan de la línea del recorrido que ya viaja en la fila
  let kmRecorridos = 0;
  if (Array.isArray(ubicacion.recorrido) && ubicacion.recorrido.length > 1) {
    const rec = ubicacion.recorrido.filter(p => p && p.lat && p.lng);
    for (let i = 1; i < rec.length; i++) kmRecorridos += haversineKm({ lat: Number(rec[i - 1].lat), lng: Number(rec[i - 1].lng) }, { lat: Number(rec[i].lat), lng: Number(rec[i].lng) });
  }
  // Tiempo en ruta: desde que arrancó el compartido hasta la última posición (o ahora)
  const tIni = ubicacion.iniciado_en ? new Date(ubicacion.iniciado_en).getTime() : null;
  const tFin = expirado && ubicacion.ultimo_update ? new Date(ubicacion.ultimo_update).getTime() : Date.now();
  const minEnRuta = tIni && tFin > tIni ? Math.round((tFin - tIni) / 60000) : null;
  const fmtKm = (k) => (k < 10 ? k.toFixed(1).replace(".", ",") : String(Math.round(k))) + " km";
  const fmtHm = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}` : `${m} min`);
  const navV = ubicacion.nav || null;
  const destinoTxt = ubicacion.destino_nombre || (navV && navV.destino) || null;
  const datos = [];
  if (kmRecorridos > 0.1) datos.push({ k: "Recorrió", v: fmtKm(kmRecorridos) });
  if (minEnRuta != null && minEnRuta >= 1) datos.push({ k: expirado ? "Duró" : "En ruta", v: fmtHm(minEnRuta) });
  if (!expirado && navV && navV.rest_km != null) datos.push({ k: "Faltan", v: fmtKm(navV.rest_km) });
  if (!expirado && navV && navV.eta) datos.push({ k: navV.min != null ? `Llega · ${fmtHm(navV.min)}` : "Llega", v: `~${navV.eta}` });

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "#FAFAFA" }}>
      {/* header */}
      <div style={{ padding: "14px 18px", background: C.card, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Olbos · En vivo</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginTop: 2 }}>
            {ubicacion.titulo || `${ubicacion.username || "Rider"} está en camino`}
          </div>
          {destinoTxt && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>→ {destinoTxt}</div>
          )}
        </div>
        <button onClick={onVolver} style={{ padding: "8px 12px", borderRadius: 8, background: C.accent, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Abrir Olbos
        </button>
      </div>

      {/* status banner */}
      <div style={{ padding: "10px 18px", background: expirado ? "#9993" : "#27AE600C", borderBottom: `1px solid ${expirado ? "#999" : "#27AE6033"}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: expirado ? "#666" : "#27AE60", fontWeight: 700 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: expirado ? "#999" : "#27AE60", animation: expirado ? "none" : "pulse 1.5s ease-in-out infinite" }} />
        {expirado ? "Compartido finalizado" : "Compartiendo en vivo"}
        <span style={{ marginLeft: "auto", fontWeight: 500, color: C.muted }}>
          {expirado ? "" : `Actualizado hace ${segundosDesdeUpdate < 90 ? `${segundosDesdeUpdate}s`
            : segundosDesdeUpdate < 5400 ? `${Math.round(segundosDesdeUpdate / 60)} min`
            : `${Math.round(segundosDesdeUpdate / 3600)} h`}`}
        </span>
      </div>

      {/* datos del viaje, en una grilla que se lee de un vistazo: cuánto lleva recorrido
          y hace cuánto salió, cuánto le falta y a qué hora llega. Antes iba todo en una
          línea sin corte y con el nombre del destino adelante: en el teléfono solo se veía
          el destino y el resto quedaba fuera de pantalla (Matías camino a Carlos Paz). */}
      {datos.length > 0 && (
        <div style={{ padding: "10px 18px", background: C.card, borderBottom: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: `repeat(${Math.min(4, datos.length)}, minmax(0, 1fr))`, gap: 8 }}>
          {datos.map(d => (
            <div key={d.k} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>{d.k}</div>
              <div style={{ fontSize: 15, color: C.text, fontWeight: 800, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontVariantNumeric: "tabular-nums" }}>{d.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* sumarse a la rodada: SOLO con el link de invitación (?rodar=1). El link
          de seguimiento es para mirar — ofrecer "sumarse" ahí confundía (reporte
          de Carlos: la familia tocaba el botón y no le servía de nada) */}
      {!expirado && (
        <div style={{ padding: "8px 18px", background: C.card, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, fontSize: 12, color: C.muted, minWidth: 0 }}>Hablá en vivo con {creadorRef.current && creadorRef.current !== "amigos" ? `@${creadorRef.current}` : "quien comparte"}</div>
          <IntercomVivo token={token} creatorId={ubicacion && ubicacion.user_id} texto="🎙️ Hablar"
            sinSesion={() => { try { localStorage.setItem("olbos_pending_vivo", token); } catch (e) { /* */ } onVolver(); }} />
        </div>
      )}
      {!expirado && rodar && (
        <div style={{ padding: "10px 18px", background: C.card, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, fontSize: 12, color: C.muted, minWidth: 0 }}>
            {publicando
              ? <span style={{ color: "#2F7D4F", fontWeight: 700 }}>✓ Estás en el mapa</span>
              : participantes.length > 0
                ? `🏍️ ${participantes.length} rodando en el mapa`
                : "Sumate para que te vean en el mapa"}
          </div>
          {yo === false ? (
            /* El token queda pendiente: al entrar (login O registro) la app abre el mapa
               de la rodada directo y lo suma con todos. Un solo botón: "Ya tengo cuenta"
               y "Crear cuenta" hacían exactamente lo mismo. */
            <button onClick={() => {
              try { localStorage.setItem("olbos_pending_vivo", token); } catch (e) { /* */ }
              onVolver();
            }}
              style={{ padding: "9px 14px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              🏍️ Entrar y sumarme
            </button>
          ) : publicando ? (
            <button onClick={salirme}
              style={{ padding: "9px 14px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
              Salir del mapa
            </button>
          ) : yo ? (
            <button onClick={sumarme}
              style={{ padding: "9px 14px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              🏍️ Sumarme al mapa
            </button>
          ) : null}
        </div>
      )}

      {/* mapa: ocupa lo que queda, sea cual sea la altura de las barras de arriba */}
      <div ref={mapRef} style={{ width: "100%", flex: 1, minHeight: 240 }} />
    </div>
  );
};


// ─── GALERÍA DE ESPACIO ───────────────────────────────────────────────────────
const EspacioGaleria = ({ espacioId, onClose }) => {
  const espacio = getEspacio(espacioId);
  const posts = POSTS_TAGGED.filter(p => p.espacioId === espacioId);
  const [view, setView] = useState("grid");
  const [filterTipo, setFilterTipo] = useState("todo");

  if (!espacio) return null;

  const filtered = posts.filter(p => filterTipo === "todo" || p.tipo === filterTipo);

  return (
    <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 300, display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto" }}>
      <div style={{ padding: "var(--header-pt) 18px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <button onClick={onClose} style={{ display: "flex", color: C.text, background: "none", border: "none", cursor: "pointer" }}>
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 2 }}>
              Espacio · {espacio.tipo}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>{espacio.label}</h2>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["todo", "post", "reel"].map(f => (
            <button key={f} onClick={() => setFilterTipo(f)}
              style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: filterTipo === f ? C.text : C.surface, color: filterTipo === f ? "#fff" : C.muted, border: `1px solid ${filterTipo === f ? "transparent" : C.border}`, cursor: "pointer" }}>
              {f === "todo" ? "Todos" : f === "post" ? "Fotos" : "Reels"}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 80px" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.muted }}>No hay publicaciones en este espacio</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3 }}>
            {filtered.map((p, i) => (
              <div key={i} style={{ aspectRatio: "1", overflow: "hidden", borderRadius: 4, background: `${C.border}` }}>
                <img loading="lazy" decoding="async" src={p.imgs?.[0]?.url || p.thumbUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MODAL: TAGUEAR ESPACIO AL PUBLICAR ──────────────────────────────────────
const TagEspacioModal = ({ onClose, onSelect }) => {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const filtered = ESPACIOS.filter(e => !q || e.label.toLowerCase().includes(q.toLowerCase()));

  const groups = [
    { tipo: "evento", label: "Eventos", icon: "📅" },
    { tipo: "ruta",   label: "Rutas",   icon: "🗺️" },
    { tipo: "lugar",  label: "Lugares", icon: "📍" },
    { tipo: "moto",   label: "Motos",   icon: "🏍️" },
  ];

  return (
    <Modal title="Etiquetá un espacio" onClose={onClose}>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
        Tu publicación aparecerá en el feed general y también dentro de ese evento, ruta o lugar.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", marginBottom: 16 }}>
        <Search size={16} strokeWidth={1.8} color={C.muted} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar evento, ruta o lugar..."
          style={{ flex: 1, fontSize: 14, color: C.text, background: "transparent" }} />
      </div>

      {groups.map(g => {
        const items = filtered.filter(e => e.tipo === g.tipo);
        if (!items.length) return null;
        return (
          <div key={g.tipo} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
              {g.icon} {g.label}
            </div>
            {items.map(e => (
              <button key={e.id} onClick={() => setSelected(e.id === selected ? null : e.id)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                borderRadius: 10, marginBottom: 6, background: selected === e.id ? e.color + "10" : C.surface,
                border: `1px solid ${selected === e.id ? e.color + "44" : C.border}`, cursor: "pointer", textAlign: "left",
                transition: "all .15s",
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: e.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{e.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{e.label}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                    {POSTS_TAGGED.filter(p => p.espacioId === e.id).length} publicaciones
                  </div>
                </div>
                <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${selected === e.id ? e.color : C.faint}`, background: selected === e.id ? e.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {selected === e.id && <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.card }} />}
                </div>
              </button>
            ))}
          </div>
        );
      })}

      <button disabled={!selected} onClick={() => { onSelect(selected); onClose(); }} style={{
        width: "100%", padding: 12, borderRadius: 12,
        background: selected ? C.blue : C.faint, color: "#fff",
        fontWeight: 600, fontSize: 14, border: "none", cursor: selected ? "pointer" : "default",
        transition: "background .2s",
      }}>
        {selected ? `Etiquetar: ${getEspacio(selected)?.short}` : "Seleccioná un espacio"}
      </button>
    </Modal>
  );
};


// ─── SECCIÓN: MOTOS DB ───────────────────────────────────────────────────────
// Los filtros salen del propio catálogo: toda marca o tipo que se sume a MOTOS_DB
// aparece solo. Estaban escritos a mano y habían quedado viejos — faltaban CF Moto,
// Zontes, Voge, Kove, Bajaj, Motomel, TVS y Benelli (ya estaban en el catálogo pero
// no se podían filtrar), sobraba Kymco (no existe) y los tipos Trail y Sport Touring
// no figuraban, así que esas motos no aparecían en ninguna categoría.
// Sin new Set(): el ícono Map de lucide sombrea el global (ver CLAUDE.md).
const unicos = (arr) => arr.filter((v, i, a) => v && a.indexOf(v) === i);
const CATS = ["Todas", ...unicos(MOTOS_DB.map(m => m.tipo)).sort((a, b) => a.localeCompare(b, "es"))];
const MARCAS_FILTER = ["Todas", ...unicos(MOTOS_DB.map(m => m.marca)).sort((a, b) => a.localeCompare(b, "es"))];

// ─── MINIATURA REEL CON AUTOPLAY ─────────────────────────────────────────────
const ReelThumb = ({ item }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {});
    // pausa automáticamente a los 3 segundos
    const timer = setTimeout(() => {
      if (el) el.pause();
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ position: "relative", height: 108 }}>
      <video
        ref={videoRef}
        src={item.videoUrl}
        poster={item.thumbUrl}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        loop={false}
        playsInline
        muted
      />
      <div style={{ position: "absolute", top: 5, right: 5, background: "rgba(0,0,0,.6)", borderRadius: 4, padding: "2px 5px", display: "flex", alignItems: "center", gap: 3 }}>
        <Play size={9} strokeWidth={2} color="#fff" fill="#fff" />
        <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }}>{item.duration}</span>
      </div>
    </div>
  );
};

const MotosDB = ({ isAdmin }) => {
  const [sel, setSel] = useState(null);
  // Consumo REAL del modelo, agregado (anónimo) de las cargas de nafta de la
  // comunidad — dato que la ficha de fábrica no tiene. RPC consumo_real_modelo.
  const [consumoReal, setConsumoReal] = useState(null);
  useEffect(() => {
    setConsumoReal(null);
    if (!sel || !sel.marca || !sel.modelo) return;
    let vivo = true;
    supabase.rpc("consumo_real_modelo", { p_marca: sel.marca, p_modelo: sel.modelo }).then(({ data }) => {
      if (!vivo) return;
      const d = typeof data === "string" ? JSON.parse(data) : data;
      if (d && Number(d.riders) > 0 && Number(d.kml) > 0) setConsumoReal(d);
    }, () => {});
    return () => { vivo = false; };
  }, [sel]);
  const [cat, setCat] = useState("Todas");
  const [marca, setMarca] = useState("Todas");
  const [q, setQ] = useState("");
  const [reviews, setReviews] = useState([]);
  const [reviewDraft, setReviewDraft] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewMedia, setReviewMedia] = useState(null);
  const [reviewMediaPreview, setReviewMediaPreview] = useState(null);
  const [reviewYouTube, setReviewYouTube] = useState("");
  const [publicarEnFeed, setPublicarEnFeed] = useState(false);
  const [enviandoReview, setEnviandoReview] = useState(false);
  const [showSugerir, setShowSugerir] = useState(false);
  const [sugerencia, setSugerencia] = useState({ marca: "", modelo: "", año: "", info: "" });
  const [enviandoSug, setEnviandoSug] = useState(false);
  const [sugEnviada, setSugEnviada] = useState(false);
  const [session, setSession] = useState(null);
  const [username, setUsername] = useState("");
  const [fotosCustom, setFotosCustom] = useState({});
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        supabase.from("perfiles").select("username").eq("id", session.user.id).single()
          .then(({ data }) => { if (data) setUsername(data.username); });
      }
    });
    supabase.from("moto_fotos").select("moto_id, img_url").then(({ data }) => {
      if (data) {
        const map = {};
        data.forEach(f => { map[f.moto_id] = f.img_url; });
        setFotosCustom(map);
      }
    });

    // Si llegó por chip de mención del feed
    if (window.olbosPendingSelect?.tipo === "moto") {
      const targetId = window.olbosPendingSelect.id;
      window.olbosPendingSelect = null;
      const moto = MOTOS_DB.find(m => m.id === targetId);
      if (moto) setSel(moto);
    }
  }, []);

  const getFoto = (moto) => imgMoto(fotosCustom[moto.id] || moto.img);

  const subirFotoMoto = async (motoId, file) => {
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const data = await res.json();
      const url = data.secure_url;
      const { error } = await supabase.from("moto_fotos").upsert({
        moto_id: motoId, img_url: url,
        updated_by: session?.user?.id,
        updated_at: new Date().toISOString(),
      }, { onConflict: "moto_id" });
      if (!error) {
        setFotosCustom(prev => ({ ...prev, [motoId]: url }));
      } else alert("Error: " + error.message);
    } catch (e) { alert("Error: " + e.message); }
    setSubiendoFoto(false);
  };

  useEffect(() => {
    if (sel) cargarReviews(sel.id);
  }, [sel]);

  const cargarReviews = async (motoId) => {
    const { data } = await supabase.from("moto_reviews").select("*").eq("moto_id", motoId).order("created_at", { ascending: false });
    if (data) setReviews(data);
  };

  // extraer ID de YouTube de cualquier URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const enviarReview = async () => {
    if (!reviewDraft.trim() && !reviewYouTube) return;
    if (!session) { alert("Iniciá sesión para dejar una reseña"); return; }
    setEnviandoReview(true);
    try {
      let mediaUrl = null;
      if (reviewMedia) {
        const fd = new FormData();
        fd.append("file", reviewMedia);
        fd.append("upload_preset", "olbos_uploads");
        const res = await fetch(`https://api.cloudinary.com/v1_1/dhwhk48wb/${reviewMedia.type.startsWith("video") ? "video" : "image"}/upload`, { method: "POST", body: fd });
        const data = await res.json();
        mediaUrl = data.secure_url;
      }
      const youtubeId = getYouTubeId(reviewYouTube);
      const { error } = await supabase.from("moto_reviews").insert({
        user_id: session.user.id,
        username: username || session.user.email.split("@")[0],
        moto_id: sel.id,
        rating: reviewRating,
        texto: reviewDraft,
        media_url: mediaUrl,
        media_type: reviewMedia?.type.startsWith("video") ? "video" : reviewMedia ? "foto" : null,
        youtube_id: youtubeId,
      });
      if (!error) {
        // publicar en feed si el usuario lo eligió
        if (publicarEnFeed && (reviewDraft.trim() || mediaUrl)) {
          await supabase.from("posts").insert({
            user_id: session.user.id,
            username: username || session.user.email.split("@")[0],
            texto: `⭐ ${reviewRating}/5 ${sel.marca} ${sel.modelo} — ${reviewDraft}`,
            media_url: mediaUrl,
            media_type: reviewMedia?.type.startsWith("video") ? "video" : reviewMedia ? "foto" : null,
            categoria: "moto",
            likes: 0,
          });
        }
        setReviewDraft(""); setReviewRating(5); setReviewMedia(null);
        setReviewMediaPreview(null); setReviewYouTube(""); setPublicarEnFeed(false);
        cargarReviews(sel.id);
      }
    } catch (e) { console.error(e); }
    setEnviandoReview(false);
  };

  const enviarSugerencia = async () => {
    if (!sugerencia.marca || !sugerencia.modelo) return;
    setEnviandoSug(true);
    await supabase.from("moto_sugerencias").insert({
      user_id: session?.user?.id || null,
      username: username || "anónimo",
      marca: sugerencia.marca, modelo: sugerencia.modelo,
      año: sugerencia.año ? parseInt(sugerencia.año) : null,
      info_adicional: sugerencia.info,
    });
    setSugEnviada(true); setEnviandoSug(false);
    setTimeout(() => { setShowSugerir(false); setSugEnviada(false); setSugerencia({ marca: "", modelo: "", año: "", info: "" }); }, 2000);
  };

  const filtered = MOTOS_DB.filter(m => {
    const matchCat = cat === "Todas" || m.tipo === cat;
    const matchMarca = marca === "Todas" || m.marca === marca;
    const matchQ = !q.trim() || [m.marca, m.modelo, m.tipo].some(v => v.toLowerCase().includes(q.toLowerCase()));
    return matchCat && matchMarca && matchQ;
  });

  // ── DETALLE ──────────────────────────────────────────────────────────────
  if (sel) {
    const m = sel;
    // Las video-reviews del bot van sin puntaje (no es su opinión): el promedio
    // se calcula solo con las que puntuó gente de verdad
    const conPuntaje = reviews.filter(r => r.rating != null);
    const avgRating = conPuntaje.length
      ? (conPuntaje.reduce((a, r) => a + r.rating, 0) / conPuntaje.length).toFixed(1)
      : m.rating;

    return (
      <PageWrap>
        {/* hero */}
        <div style={{ height: 240, position: "relative", background: C.accent, overflow: "hidden" }}>
          <img loading="lazy" decoding="async" src={getFoto(m)} alt={m.modelo} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }}
            onError={e => { if (e.target.src !== MOTO_FALLBACK) e.target.src = MOTO_FALLBACK; }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.8) 0%, transparent 55%)" }} />
          <button onClick={() => setSel(null)} style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", left: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,.5)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <ChevronLeft size={20} strokeWidth={2} color="#fff" />
          </button>
          {/* compartir */}
          <div style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", right: isAdmin ? 150 : 16 }}>
            <BtnCompartir texto={`🏍️ ${m.marca} ${m.modelo} · ${fichaCorta(m)}`} tipo="moto" id={m.id} dark />
          </div>
          {/* botón admin: cambiar foto */}
          {isAdmin && (
            <label style={{ position: "absolute", top: "max(48px, calc(var(--sat) + 12px))", right: 58, padding: "8px 14px", borderRadius: 20, background: "rgba(0,0,0,.65)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: subiendoFoto ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, border: "1px solid rgba(255,255,255,.3)" }}>
              <Camera size={14} strokeWidth={2} color="#fff" />
              {subiendoFoto ? "Subiendo..." : "Cambiar foto"}
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendoFoto}
                onChange={e => { const f = e.target.files[0]; if (f) subirFotoMoto(m.id, f); }} />
            </label>
          )}
          <div style={{ position: "absolute", top: "max(52px, calc(var(--sat) + 12px))", right: isAdmin ? 140 : 16 }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: "rgba(0,0,0,.55)", color: "#fff" }}>{m.tipo}</span>
          </div>
          {/* Crédito de la foto: las imágenes del catálogo salen de Wikimedia Commons y casi
              todas son CC BY-SA, que obliga a nombrar al autor. Va discreto, abajo a la derecha.
              Si el admin subió una foto propia (fotosCustom), no corresponde el crédito. */}
          {m.credito && !fotosCustom[m.id] && (
            <div style={{ position: "absolute", bottom: 4, right: 10, fontSize: 8, color: "rgba(255,255,255,.55)", letterSpacing: 0.2, maxWidth: "62%", textAlign: "right", lineHeight: 1.2 }}>
              {m.credito}
            </div>
          )}
          <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.6)", marginBottom: 4 }}>{m.marca}</div>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: "#fff", letterSpacing: -0.5, marginBottom: 6 }}>{m.modelo}</h1>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Stars n={Number(avgRating)} size={13} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{avgRating}</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>({reviews.length} reseñas)</span>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 18px 80px" }}>
          {/* ficha técnica */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
            {/* Solo los datos que la marca publica: las nacionales y chinas no dan
                torque ni consumo, y un "undefinedNm" queda peor que no mostrarlo. */}
            {[["CC", m.cc && `${m.cc}cc`], ["Motor", m.motor_w && `${m.motor_w.toLocaleString("es-AR")}W`], ["Potencia", m.pot && `${m.pot}cv`], ["Par", m.par && `${m.par}Nm`], ["Peso", m.peso && `${m.peso}kg`], ["Consumo", m.consumo && `${m.consumo}L/100`], ["Autonomía", m.autonomia && `${m.autonomia} km`], ["Vel. máx", m.vmax && `${m.vmax} km/h`], ["Batería", m.bateria], ["Carga", m.carga], ["Año", m.año]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ background: C.surface, borderRadius: 10, padding: "10px 12px", border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{v}</div>
              </div>
            ))}
          </div>

          {/* consumo real de la comunidad: sale de las cargas de nafta (anónimas, opt-in)
              de los riders que tienen este modelo en el garage */}
          {consumoReal && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.accent, borderRadius: 12, padding: "12px 15px", marginBottom: 20 }}>
              <span style={{ fontSize: 20 }}>⛽</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 2 }}>Consumo real de la comunidad</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#F9F5F2" }}>
                  {Number(consumoReal.kml).toFixed(1)} km/L
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: "#bdb6ab", marginLeft: 8 }}>
                    {(100 / Number(consumoReal.kml)).toFixed(1)} L/100 · {consumoReal.riders} rider{Number(consumoReal.riders) !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* precio: NO se muestra — los valores en USD son referencia local y no aplican
              a todo el mercado hispano (decisión de marca: sin datos atados a un país) */}

          {/* descripción */}
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 20 }}>{m.desc}</p>

          {/* pros y contras */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
            <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.green, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>✓ Pros</div>
              {(m.pros || []).map((p, i) => <div key={i} style={{ fontSize: 12, color: C.green, marginBottom: 6, display: "flex", gap: 6 }}><span>·</span>{p}</div>)}
            </div>
            <div style={{ background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9B2C2C", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>✗ Contras</div>
              {(m.contras || []).map((c, i) => <div key={i} style={{ fontSize: 12, color: "#9B2C2C", marginBottom: 6, display: "flex", gap: 6 }}><span>·</span>{c}</div>)}
            </div>
          </div>

          {/* reviews */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 14 }}>
              Reseñas de la comunidad {reviews.length > 0 && `(${reviews.length})`}
            </div>

            {reviews.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px 0", color: C.muted, fontSize: 13 }}>
                Todavía no hay reseñas. ¡Sé el primero!
              </div>
            )}

            {reviews.map((rv, i) => (
              <div key={i} style={{ background: C.surface, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                    {rv.username?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>@{rv.username}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{new Date(rv.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}</div>
                  </div>
                  {rv.rating != null && <Stars n={rv.rating} size={12} />}
                </div>
                {rv.texto && <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: rv.media_url || rv.youtube_id ? 10 : 0 }}>{rv.texto}</p>}
                {/* foto/video de la review */}
                {rv.media_url && rv.media_type === "video" && (
                  <VideoPost src={rv.media_url} />
                )}
                {rv.media_url && rv.media_type === "foto" && (
                  <img src={cldOpt(rv.media_url, 720)} alt="" loading="lazy" decoding="async" onError={e => { if (e.target.src !== rv.media_url) e.target.src = rv.media_url; }} style={{ width: "100%", borderRadius: 10, objectFit: "cover", maxHeight: 300 }} />
                )}
                {/* video YouTube embebido */}
                {rv.youtube_id && (
                  <div style={{ marginTop: 10, borderRadius: 12, overflow: "hidden", position: "relative", paddingBottom: "56.25%", height: 0 }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${rv.youtube_id}`}
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* formulario de review */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                {session ? "Tu reseña" : "Iniciá sesión para dejar una reseña"}
              </div>

              {session && (
                <>
                  {/* estrellas */}
                  <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setReviewRating(n)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                        <Star size={26} strokeWidth={1.5} fill={n <= reviewRating ? "#B07D1A" : "none"} color={n <= reviewRating ? "#B07D1A" : C.faint} />
                      </button>
                    ))}
                    <span style={{ fontSize: 13, color: C.muted, marginLeft: 6, alignSelf: "center" }}>{reviewRating}/5</span>
                  </div>

                  {/* texto */}
                  <textarea value={reviewDraft} onChange={e => setReviewDraft(e.target.value)} rows={3}
                    placeholder="¿Tenés o tuviste esta moto? Contá tu experiencia real..."
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, resize: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10 }} />

                  {/* foto/video */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 0" }}>
                      <Camera size={16} strokeWidth={2} color={C.muted} />
                      <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>Agregar foto o video</span>
                      <input type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setReviewMedia(file);
                        setReviewMediaPreview(URL.createObjectURL(file));
                      }} />
                    </label>
                    {reviewMediaPreview && (
                      <div style={{ position: "relative", marginTop: 6 }}>
                        {reviewMedia?.type.startsWith("video")
                          ? <video src={reviewMediaPreview} style={{ width: "100%", borderRadius: 10, maxHeight: 200, objectFit: "cover" }} controls />
                          : <img loading="lazy" decoding="async" src={reviewMediaPreview} alt="" style={{ width: "100%", borderRadius: 10, maxHeight: 200, objectFit: "cover" }} />
                        }
                        <button onClick={() => { setReviewMedia(null); setReviewMediaPreview(null); }}
                          style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: "50%", background: "rgba(0,0,0,.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <X size={13} strokeWidth={2} color="#fff" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* YouTube */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.card }}>
                      <span style={{ fontSize: 16 }}>▶️</span>
                      <input value={reviewYouTube} onChange={e => setReviewYouTube(e.target.value)}
                        placeholder="Link de YouTube (opcional)"
                        style={{ flex: 1, border: "none", fontSize: 13, color: C.text, background: "transparent", outline: "none" }} />
                    </div>
                    {getYouTubeId(reviewYouTube) && (
                      <div style={{ marginTop: 8, borderRadius: 10, overflow: "hidden", position: "relative", paddingBottom: "56.25%", height: 0 }}>
                        <iframe src={`https://www.youtube.com/embed/${getYouTubeId(reviewYouTube)}`}
                          title="Preview" frameBorder="0" allowFullScreen
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
                      </div>
                    )}
                  </div>

                  {/* toggle publicar en feed */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}`, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Publicar también en el Feed</div>
                      <div style={{ fontSize: 11, color: C.muted }}>Tu reseña aparecerá en la comunidad</div>
                    </div>
                    <button onClick={() => setPublicarEnFeed(p => !p)} style={{ width: 44, height: 24, borderRadius: 12, background: publicarEnFeed ? C.text : C.faint, border: "none", cursor: "pointer", position: "relative", transition: "background .2s", flexShrink: 0 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.card, position: "absolute", top: 3, left: publicarEnFeed ? 23 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                    </button>
                  </div>

                  <button onClick={enviarReview} disabled={enviandoReview || (!reviewDraft.trim() && !reviewYouTube && !reviewMedia)}
                    style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: reviewDraft.trim() || reviewYouTube || reviewMedia ? C.text : C.faint, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
                    {enviandoReview ? "Publicando..." : "Publicar reseña"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* modal sugerir */}
        {showSugerir && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
            <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 20px calc(40px + var(--sab))" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 800 }}>Sugerir un modelo</h3>
                <button onClick={() => setShowSugerir(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={16} strokeWidth={2} color={C.muted} />
                </button>
              </div>
              {sugEnviada ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>¡Gracias por tu sugerencia!</div>
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>La revisamos y la agregamos pronto.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[["marca", "Marca *", "Honda, KTM, Yamaha..."], ["modelo", "Modelo *", "CB 500 F, Duke 390..."], ["año", "Año", "2024"], ["info", "Información adicional", "Link, ficha técnica..."]].map(([k, label, ph]) => (
                    <div key={k}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
                      <input value={sugerencia[k]} onChange={e => setSugerencia(s => ({ ...s, [k]: e.target.value }))} placeholder={ph}
                        style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", color: C.text }} />
                    </div>
                  ))}
                  <button onClick={enviarSugerencia} disabled={enviandoSug || !sugerencia.marca || !sugerencia.modelo}
                    style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: sugerencia.marca && sugerencia.modelo ? C.text : C.faint, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
                    {enviandoSug ? "Enviando..." : "Enviar sugerencia"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </PageWrap>
    );
  }

  // ── LISTADO ───────────────────────────────────────────────────────────────
  return (
    <PageWrap>
      <div style={{ padding: "var(--header-pt) 18px 14px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginBottom: 4 }}><img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: "cover", marginRight: 6, verticalAlign: "middle" }} />Olbos · Motos</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.5 }}>Motos</h1>
          <span style={{ fontSize: 13, color: C.muted }}>{filtered.length} modelos</span>
        </div>

        {/* disclaimer sugerir */}
        <div style={{ background: C.surface, borderRadius: 12, padding: "10px 14px", border: `1px solid ${C.border}`, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🏍️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>¿No encontrás tu moto? La base crece con la comunidad.</div>
          </div>
          <button onClick={() => setShowSugerir(true)}
            style={{ padding: "6px 12px", borderRadius: 20, background: C.text, color: "#fff", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer", flexShrink: 0 }}>
            Sugerir
          </button>
        </div>

        {/* buscador */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", background: C.surface, border: `1px solid ${q ? C.text : C.border}`, borderRadius: 12, padding: "10px 14px", transition: "border .15s", marginBottom: 12 }}>
          <Search size={16} strokeWidth={2} color={C.muted} style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Marca, modelo o tipo..."
            style={{ flex: 1, background: "transparent", border: "none", fontSize: 14, color: C.text, outline: "none" }} />
          {q && <button onClick={() => setQ("")}><X size={15} strokeWidth={2.5} color={C.muted} /></button>}
        </div>
      </div>

      <div style={{ padding: "12px 18px 80px" }}>
        {/* filtro marca */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 10, paddingBottom: 4 }}>
          {MARCAS_FILTER.map(f => (
            <button key={f} onClick={() => setMarca(f)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: marca === f ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${marca === f ? C.text : C.border}`, color: marca === f ? C.text : C.muted, cursor: "pointer" }}>{f}</button>
          ))}
        </div>

        {/* filtro categoría */}
        <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 16, paddingBottom: 4 }}>
          {CATS.map(f => (
            <button key={f} onClick={() => setCat(f)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: cat === f ? 700 : 500, whiteSpace: "nowrap", background: "transparent", border: `1px solid ${cat === f ? C.text : C.border}`, color: cat === f ? C.text : C.muted, cursor: "pointer" }}>{f}</button>
          ))}
        </div>

        {/* sin resultados */}
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
            <Bike size={40} strokeWidth={1} color={C.faint} style={{ margin: "0 auto 12px" }} />
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Sin resultados</div>
            <div style={{ fontSize: 13 }}>Probá con otra marca o categoría</div>
          </div>
        )}

        {/* grid de motos */}
        {filtered.map(m => (
          <div key={m.id} onClick={() => setSel(m)} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 14, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
            <div style={{ height: 170, position: "relative", overflow: "hidden", background: `${C.border}` }}>
              <img loading="lazy" decoding="async" src={getFoto(m)} alt={m.modelo} style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={e => { if (e.target.src !== MOTO_FALLBACK) e.target.src = MOTO_FALLBACK; }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,.5) 0%, transparent 55%)" }} />
              <div style={{ position: "absolute", top: 10, left: 12 }}>
                <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: "rgba(0,0,0,.55)", color: "#fff", letterSpacing: 0.5 }}>{m.tipo.toUpperCase()}</span>
              </div>
              <div style={{ position: "absolute", bottom: 10, left: 14, right: 14 }}>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)", marginBottom: 2 }}>{m.marca}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: -0.3 }}>{m.modelo}</div>
              </div>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Stars n={m.rating} size={12} />
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{m.rating}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>({(m.reviews || []).length})</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {m.cc ? <span style={{ fontSize: 12, color: C.muted }}>{m.cc}cc</span> : null}
                {m.autonomia ? <span style={{ fontSize: 12, color: C.muted }}>{m.autonomia} km</span> : null}
                {m.motor_w ? <span style={{ fontSize: 12, color: C.muted }}>{m.motor_w.toLocaleString("es-AR")}W</span> : null}
                {m.pot ? <span style={{ fontSize: 12, color: C.muted }}>{m.pot}cv</span> : null}
                {m.peso ? <span style={{ fontSize: 12, color: C.muted }}>{m.peso}kg</span> : null}
                <span style={{ fontSize: 12, color: C.muted }}>{m.año}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* modal sugerir modelo */}
      {showSugerir && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
          <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", padding: "20px 20px calc(40px + var(--sab))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800 }}>Sugerir un modelo</h3>
              <button onClick={() => setShowSugerir(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            {sugEnviada ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>¡Gracias por tu sugerencia!</div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>La revisamos y la agregamos pronto.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[["marca","Marca *","Honda, KTM..."],["modelo","Modelo *","CB 500, Duke 390..."],["año","Año","2024"],["info","Más info","Link, ficha técnica..."]].map(([k,label,ph]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
                    <input value={sugerencia[k]} onChange={e => setSugerencia(s => ({ ...s, [k]: e.target.value }))} placeholder={ph}
                      style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", color: C.text }} />
                  </div>
                ))}
                <button onClick={enviarSugerencia} disabled={enviandoSug || !sugerencia.marca || !sugerencia.modelo}
                  style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: sugerencia.marca && sugerencia.modelo ? C.text : C.faint, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
                  {enviandoSug ? "Enviando..." : "Enviar sugerencia"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </PageWrap>
  );
};

// ─── PANTALLA DE LOGIN ────────────────────────────────────────────────────────
// ─── OLBOS AI ASSISTANT ───────────────────────────────────────────────────────
// Extrae el JSON de un flyer de la respuesta de la IA, aunque venga envuelto en
// ```json ... ``` o con texto alrededor. Devuelve el objeto o null si no parsea.
const extraerFlyer = (texto) => {
  if (!texto) return null;
  let s = String(texto).trim();
  // Limpiar el fence ```json ... ``` (o ``` ... ```)
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  // Si todavía hay texto alrededor, tomar el primer bloque {...}
  if (!s.startsWith("{")) {
    const bloque = s.match(/\{[\s\S]*\}/);
    if (bloque) s = bloque[0];
  }
  try { return JSON.parse(s); } catch (e) { return null; }
};

const OlbosAI = ({ onClose, onCrearEvento }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "¡Hola! Soy el asistente de Olbos 🏍️ Podés preguntarme sobre talleres, motos, precios, rutas, o **subir la foto de un flyer** para crear un evento automáticamente. ¿En qué te ayudo?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [imagenPendiente, setImagenPendiente] = useState(null); // { base64, preview }
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Contexto de Olbos para Claude: mapa de la app + MANUAL DE USO paso a paso.
  // Este asistente es el canal de soporte — cualquier usuario (o el equipo) pregunta
  // "¿cómo hago X?" y recibe los pasos exactos, sin depender de Carlos.
  const OLBOS_CONTEXT = `Sos el asistente de Olbos, la comunidad para los que viven la moto. Tagline: "El camino es el destino". NUNCA digas que Olbos es de un país específico: es para riders, sin fronteras.

SECCIONES DE LA APP (barra de abajo): EVENTOS (agenda de salidas y encuentros, con carrusel de destacados arriba) · SOCIAL (el feed) · GARAGE (perfil propio, motos, mantenimiento) · RUTAS (rutas públicas y Mis recorridos grabados con GPS) · SERVICIOS (talleres, marcas, concesionarios, hospedajes, dónde comer, seguros/gestorías, compra-venta) · MÁS (mensajes, alertas de robo, grupos, en vivo, noticias, invitá y sumá).

MANUAL DE USO — respondé con estos pasos exactos cuando pregunten cómo hacer algo:

• CREAR UN EVENTO O SALIDA: Eventos → botón "+ Crear" → elegí evento fijo o salida grupal. En salidas: tocá el mapa o buscá lugares para armar el recorrido (primer toque = salida, después paradas, último = destino). En cada parada podés tildar "🙋 Acá se suma gente" con horario. IMPORTANTE: si lo creás desde un grupo, elegí "🌍 Pública" para que cualquiera pueda anotarse (si elegís "Solo el grupo", nadie de afuera lo ve). El formulario se guarda solo como borrador: si se cierra la app, al volver ofrece retomar.
• COBRAR INSCRIPCIONES ONLINE: al crear el evento con precio, en "Cobro de la inscripción" tocá "Conectar Mercado Pago" y autorizá con tu cuenta de MP (una sola vez, vale para todos tus eventos). Los pagos van directo a tu cuenta y las inscripciones se confirman solas. Alternativa: dejá tu alias/CBU y los inscriptos suben el comprobante.
• TRANSFERIR UN EVENTO A SU ORGANIZADOR: abrí el evento → "Gestión de la salida" → Transferir → buscá el @usuario → confirmá. Le llega aviso por campanita, email y podés mandarle el WhatsApp prearmado. (Disponible para el dueño del evento, admins y staff en eventos de la agenda.)
• VER INSCRIPTOS: abrí tu evento → "Gestión de la salida" → "Ver inscriptos": lista completa con datos, pagos, comprobantes, exportá CSV o copiá. El escáner de entradas QR está en la misma sección.
• ANOTARSE A UN EVENTO: abrilo → "Anotarme" → completá los datos que pida (vienen precargados de tu ficha) → si tiene paradas, elegí desde qué punto te sumás → si es pago, pagá con Mercado Pago o adjuntá el comprobante.
• GRABAR UN RECORRIDO: Rutas → Mis recorridos → grabar. Funciona con pantalla apagada. Al terminar le ponés nombre (editable después desde el detalle, lapicito) y podés compartirlo al feed.
• RODADA EN VIVO (espontánea): mientras grabás, tocá "📡 Rodada en vivo · invitá gente con un link" y mandá el link por WhatsApp. Los que lo abren te ven en el mapa; con cuenta pueden tocar "Sumarme al mapa" y se ven todos entre todos. Solo el creador graba el track completo; los sumados igual se llevan su recorrido al salir.
• AGREGAR MI MOTO: Perfil → Garage → "＋ Agregar moto" → marca, modelo, foto, patente (confidencial: solo para emergencias y robos, nunca pública). El historial de service se carga con la llavecita 🔧 de cada moto.
• COMPARTIR HISTORIAL CON UN TALLER: Perfil → Garage → en tu moto → "🔗 Historial a un taller": genera un QR/link para que el taller vea el historial y cargue lo que le hizo, firmado.
• PEDIR TURNO EN UN TALLER: Servicios → abrí el taller → "📅 Pedir turno" → fecha, hora, tu moto y qué necesitás. El taller confirma y te avisa por campanita.
• REGISTRAR MI NEGOCIO: Servicios → banner de arriba o desde Perfil → Cuenta → "Registrá tu negocio" → 3 pasos, gratis. Queda en revisión y al aprobarse aparece en el listado y el mapa.
• CREAR UN GRUPO: Más → Grupos → crear. Tiene chat, ubicación compartida, eventos internos y galería.
• INVITAR GENTE / REFERIDOS: Más → "Invitá y sumá" → tu link personal olbos.ai/r/tu-usuario. Los que se crean cuenta con tu link quedan en tu red.
• SOS / EMERGENCIAS: En Vivo tiene botón SOS. Los organizadores pueden identificar a un rider caído por la patente (botón en el evento) y ver su contacto de emergencia y datos médicos de la ficha.
• EQUIPO OLBOS (staff): además pueden transferir eventos de la agenda y crear perfiles de organizador desde Más → "Crear perfil de organizador" (genera link de reclamo para mandar por WhatsApp).

Si te preguntan por un taller, negocio o evento específico, NO inventes: decí que lo busquen en la sección correspondiente (Servicios o Eventos), que ahí está lo real cargado por la comunidad. Si te suben un flyer de evento, extraé los datos. Si no sabés algo de la app, decí que lo pregunten a hola@olbos.ai.

Respondé siempre en español, conciso, con voseo y buena onda. Emojis de moto con moderación 🏍️`;

  const sendMessage = async () => {
    if ((!input.trim() && !imagenPendiente) || loading) return;
    const userMsg = input.trim();
    setInput("");
    setLoading(true);

    // Construir el mensaje del usuario (puede tener texto + imagen)
    const userContent = imagenPendiente
      ? [
          { type: "image", source: { type: "base64", media_type: imagenPendiente.mediaType, data: imagenPendiente.base64 } },
          { type: "text", text: userMsg || "Analizá este flyer y extraé la información del evento." },
        ]
      : userMsg;

    setMessages(m => [...m, {
      role: "user",
      content: userMsg || "📷 Imagen enviada",
      preview: imagenPendiente?.preview,
    }]);
    setImagenPendiente(null);

    // System prompt especial si hay imagen
    const systemExtra = imagenPendiente
      ? `\n\nSi el usuario sube una imagen de un flyer de evento, extraé TODA la información disponible y respondé EXACTAMENTE con este formato JSON (solo el JSON, sin texto adicional ni backticks):\n{"esFlyer":true,"nombre":"...","fecha":"...","hora":"...","lugar":"...","ciudad":"...","descripcion":"...","precio":"...","organizador":"...","instagram":"...","whatsapp":"..."}\nSi algún campo no está en el flyer, dejalo como string vacío "". Si no es un flyer de evento, respondé normalmente.`
      : "";

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(API_BASE + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: OLBOS_CONTEXT + systemExtra,
          messages: [...history, { role: "user", content: userContent }],
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "No pude procesar tu consulta. Intentá de nuevo.";

      // ¿Es el JSON de un flyer? Puede venir envuelto en ```json ... ``` o con texto
      // alrededor → extraerFlyer limpia el fence y parsea con try/catch.
      const flyer = extraerFlyer(reply);
      if (flyer && flyer.esFlyer) {
        setLoading(false);
        if (onCrearEvento) {
          // No mostrar el JSON: abrir el formulario de crear evento ya pre-cargado
          // para que el usuario solo revise y confirme.
          setMessages(m => [...m, {
            role: "assistant",
            content: `✅ Extraje los datos del flyer:\n\n**${flyer.nombre || "Evento"}**${flyer.fecha ? `\n📅 ${flyer.fecha}${flyer.hora ? ` · ${flyer.hora}` : ""}` : ""}${(flyer.lugar || flyer.ciudad) ? `\n📍 ${[flyer.lugar, flyer.ciudad].filter(Boolean).join(", ")}` : ""}\n\nTe abro el formulario para que revises y confirmes. 📝`,
          }]);
          onCrearEvento(flyer);
          onClose && onClose();
        } else {
          // Fallback (sin handler): resumen + botón para crear el evento
          setMessages(m => [...m, {
            role: "assistant",
            content: `✅ Encontré la info del evento:\n\n**${flyer.nombre}**\n📅 ${flyer.fecha} ${flyer.hora}\n📍 ${flyer.lugar}, ${flyer.ciudad}`,
            flyerData: flyer,
          }]);
        }
        return;
      }

      setMessages(m => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Hubo un error de conexión. Intentá de nuevo." }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const subirImagen = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const base64 = dataUrl.split(",")[1];
      const mediaType = file.type || "image/jpeg";
      const preview = dataUrl;
      setImagenPendiente({ base64, mediaType, preview });
    };
    reader.readAsDataURL(file);
  };

  const sugerencias = [
    "📷 Subí un flyer para crear un evento",
    "¿Qué taller tiene gomería cerca?",
    "Compará Honda CB750 vs KTM Duke 890",
    "¿Qué motos son buenas para ruta larga?",
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 400, display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto" }}>
      {/* header */}
      <div style={{ padding: "max(48px, calc(var(--sat) + 12px)) 18px 14px", borderBottom: `1px solid ${C.border}`, background: C.card, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: C.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 28, height: 28, borderRadius: 8, objectFit: "cover" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Asistente Olbos</div>
          <div style={{ fontSize: 12, color: C.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.green }} /> Powered by Claude AI
          </div>
        </div>
        <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={18} strokeWidth={2} color={C.muted} />
        </button>
      </div>

      {/* mensajes */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ marginBottom: 14, display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            {msg.role === "assistant" && (
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: 8, marginTop: 2 }}>
                <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 20, height: 20, borderRadius: 5, objectFit: "cover" }} />
              </div>
            )}
            <div style={{ maxWidth: "78%" }}>
              {/* preview de imagen subida */}
              {msg.preview && (
                <img loading="lazy" decoding="async" src={msg.preview} alt="flyer" style={{ width: "100%", borderRadius: 12, marginBottom: 6, display: "block" }} />
              )}
              <div style={{
                padding: "10px 14px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                background: msg.role === "user" ? C.text : C.surface,
                color: msg.role === "user" ? "#fff" : C.text,
                fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
              }}>
                {msg.content}
              </div>
              {/* botón crear evento si es flyer detectado */}
              {msg.flyerData && onCrearEvento && (
                <button onClick={() => { onCrearEvento(msg.flyerData); onClose(); }}
                  style={{ marginTop: 8, width: "100%", padding: "10px 0", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer" }}>
                  📅 Crear este evento en Olbos
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <img loading="lazy" decoding="async" src={LOGO_URL} alt="" style={{ width: 20, height: 20, borderRadius: 5, objectFit: "cover" }} />
            </div>
            <div style={{ background: C.surface, borderRadius: "18px 18px 18px 4px", padding: "12px 16px", display: "flex", gap: 4, alignItems: "center" }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted, animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}

        {/* sugerencias si es el primer mensaje */}
        {messages.length === 1 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Sugerencias</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sugerencias.map(s => (
                <button key={s} onClick={() => { setInput(s); setTimeout(() => inputRef.current?.focus(), 50); }}
                  style={{ padding: "10px 14px", borderRadius: 12, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13, fontWeight: 500, textAlign: "left", cursor: "pointer" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* input */}
      <div style={{ padding: "8px 18px 32px", borderTop: `1px solid ${C.border}`, background: C.card }}>
        {/* preview imagen pendiente */}
        {imagenPendiente && (
          <div style={{ position: "relative", marginBottom: 8, display: "inline-block" }}>
            <img loading="lazy" decoding="async" src={imagenPendiente.preview} alt="flyer" style={{ height: 80, borderRadius: 10, objectFit: "cover" }} />
            <button onClick={() => setImagenPendiente(null)}
              style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#333", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={11} strokeWidth={2.5} color="#fff" />
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          {/* botón cámara */}
          <label style={{ width: 44, height: 44, borderRadius: "50%", background: C.surface, border: `1.5px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Camera size={18} strokeWidth={1.8} color={C.muted} />
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={e => { const f = e.target.files[0]; if (f) subirImagen(f); e.target.value = ""; }} />
          </label>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={imagenPendiente ? "Agregá un comentario o enviá la imagen..." : "Preguntá sobre motos, talleres, rutas..."} rows={1}
            style={{ flex: 1, padding: "12px 14px", borderRadius: 22, border: `1.5px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, resize: "none", fontFamily: "inherit", maxHeight: 120, overflowY: "auto", lineHeight: 1.5 }} />
          <button onClick={sendMessage} disabled={loading || (!input.trim() && !imagenPendiente)}
            style={{ width: 44, height: 44, borderRadius: "50%", background: (input.trim() || imagenPendiente) ? C.text : C.faint, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background .2s" }}>
            <Send size={18} strokeWidth={2} color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── REGISTRO NEGOCIO ─────────────────────────────────────────────────────────
// ─── MAPA EMBED GOOGLE MAPS ───────────────────────────────────────────────────
const MapaEmbed = ({ lat, lng, nombre, direccion }) => {
  if (!lat || !lng) return null;
  const query = encodeURIComponent(`${nombre}, ${direccion || ""}, Argentina`);
  // Usamos el embed sin key — funciona para uso general
  const embedUrl = `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed&hl=es`;
  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2dcd4", marginTop: 12 }}>
      <iframe
        title={nombre}
        src={embedUrl}
        width="100%"
        height="220"
        style={{ border: "none", display: "block" }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
      />
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", fontSize: 13, color: C.text, fontWeight: 600, textDecoration: "none", background: "#F8F9FF", borderTop: "1px solid #e2dcd4" }}
      >
        <Navigation size={14} strokeWidth={2} color="#111111" />
        Abrir en Google Maps
      </a>
    </div>
  );
};

// ─── SELECTOR DE PUNTOS DE RUTA (reusar LugarPicker existente) ───────────────
const SelectorRuta = ({ puntos, onChange }) => {
  const TIPOS = [
    { id: "salida",   label: "🟢 Salida",   placeholder: "Ciudad o lugar de salida" },
    { id: "parada",   label: "📍 Parada",   placeholder: "Parada del camino" },
    { id: "almuerzo", label: "🍽️ Almuerzo", placeholder: "Dónde se almuerza" },
    { id: "noche",    label: "🌙 Noche",    placeholder: "Dónde se hace noche" },
    { id: "destino",  label: "🔴 Destino",  placeholder: "Destino final" },
  ];

  const agregarPunto = (tipo) => {
    onChange([...puntos, { tipo, nombre: "", lat: null, lng: null }]);
  };

  const actualizarPunto = (i, valor) => {
    const nuevos = [...puntos];
    nuevos[i] = { ...nuevos[i], ...valor };
    onChange(nuevos);
  };

  const eliminarPunto = (i) => {
    onChange(puntos.filter((_, j) => j !== i));
  };

  return (
    <div>
      {puntos.map((p, i) => {
        const tipo = TIPOS.find(t => t.id === p.tipo) || TIPOS[0];
        return (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{tipo.label}</span>
              <button onClick={() => eliminarPunto(i)}
                style={{ width: 22, height: 22, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <X size={10} strokeWidth={2.5} color={C.red} />
              </button>
            </div>
            <LugarPicker
              value={p.lat ? { nombre: p.nombre, lat: p.lat, lng: p.lng } : null}
              onChange={val => actualizarPunto(i, { nombre: val.nombre, lat: val.lat, lng: val.lng })}
              placeholder={tipo.placeholder}
              label=""
            />
          </div>
        );
      })}

      {/* botones para agregar puntos */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
        {TIPOS.map(t => (
          <button key={t.id} onClick={() => agregarPunto(t.id)}
            style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${C.border}`, background: C.card, color: C.muted, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            <Plus size={11} strokeWidth={2.5} /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const AutocompleteDir = ({ value, onChange, onSelect, placeholder, style }) => {
  const [sugerencias, setSugerencias] = useState([]);
  const [show, setShow] = useState(false);
  const timer = useRef(null);

  const buscar = async (q) => {
    onChange(q);
    if (q.length < 3) { setSugerencias([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/places?input=${encodeURIComponent(q + ", Argentina")}`);
        const data = await res.json();
        if (data.predictions) setSugerencias(data.predictions);
      } catch (e) { /* silenciar */ }
    }, 300);
  };

  const elegir = async (pred) => {
    onChange(pred.description.replace(", Argentina", ""));
    setSugerencias([]);
    setShow(false);
    // Geocodificar para obtener lat/lng
    try {
      const res = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(pred.description)}`);
      const data = await res.json();
      if (data.lat && onSelect) onSelect({ direccion: pred.description.replace(", Argentina", ""), lat: data.lat, lng: data.lng, ciudad: data.ciudad || null, provincia: data.provincia || null });
    } catch (e) { /* silenciar */ }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        onChange={e => buscar(e.target.value)}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder={placeholder || "Dirección..."}
        style={style}
        autoComplete="off"
      />
      {show && sugerencias.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: "1px solid #e2dcd4", borderRadius: 10, zIndex: 100, boxShadow: "0 4px 16px rgba(0,0,0,.12)", overflow: "hidden" }}>
          {sugerencias.map((s, i) => (
            <button key={i} onClick={() => elegir(s)}
              style={{ width: "100%", padding: "10px 14px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.text, borderBottom: i < sugerencias.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <MapPin size={12} strokeWidth={2} color="#999" style={{ marginRight: 6 }} />
              {s.description.replace(", Argentina", "")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ciudad con autocompletar de ciudades (Google Places). Antes era texto libre y una
// ficha con "Tigre" o "tigre " o "Tigre, Bs As" no se podía agrupar ni ubicar (Carlos, 08-oct).
const AutocompleteCiudad = ({ value, onChange, placeholder, style }) => {
  const [preds, setPreds] = useState([]);
  const [show, setShow] = useState(false);
  const timer = useRef(null);
  const buscar = (q) => {
    onChange(q);
    clearTimeout(timer.current);
    if (q.trim().length < 3) { setPreds([]); return; }
    timer.current = setTimeout(() => {
      loadGoogleMaps().then(() => {
        if (!window.google?.maps?.places) return;
        new window.google.maps.places.AutocompleteService().getPlacePredictions({ input: q, types: ["(cities)"], language: "es" }, (ps) => {
          setPreds((ps || []).slice(0, 6).map(p => p.description.replace(/, (Argentina|Chile|Uruguay|Paraguay|Bolivia|Perú|Colombia|México|España)$/, "")));
        });
      }).catch(() => {});
    }, 300);
  };
  return (
    <div style={{ position: "relative" }}>
      <input value={value} onChange={e => buscar(e.target.value)} onFocus={() => setShow(true)} onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder={placeholder || "Ciudad"} style={style} autoComplete="off" />
      {show && preds.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, zIndex: 100, boxShadow: "0 4px 16px rgba(0,0,0,.12)", overflow: "hidden" }}>
          {preds.map((p, i) => (
            <button key={i} onClick={() => { onChange(p); setPreds([]); setShow(false); }}
              style={{ width: "100%", padding: "10px 14px", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.text, borderBottom: i < preds.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <MapPin size={12} strokeWidth={2} color="#999" style={{ marginRight: 6 }} />{p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const registrarNegocioServidor = async (email, ficha) => {
  let { data, error } = await supabase.rpc("registrar_negocio", { p_email: email || null, p_ficha: ficha });
  // Si el RPC nuevo todavía no existe en la base, cae al anterior (solo sirve sin sesión)
  if (error && /function|schema cache/i.test(error.message || "")) {
    ({ data, error } = await supabase.rpc("registrar_negocio_sin_sesion", { p_email: email || null, p_ficha: ficha }));
  }
  if (error) return { error: error.message || "No se pudo registrar" };
  const r = typeof data === "string" ? JSON.parse(data) : data;
  return r || { error: "Sin respuesta del servidor" };
};

const RegistroNegocio = ({ onBack, onSuccess }) => {
  const [paso, setPaso] = useState(1);
  const [form, setForm] = useState({
    email: "", password: "", nombre: "", categoria: "", descripcion: "",
    direccion: "", ciudad: "", telefono: "", whatsapp: "", web: "", instagram: "",
    lat: null, lng: null,
  });
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const categorias = [
    { id: "taller", label: "🔧 Taller mecánico" },
    { id: "concesionario", label: "🏪 Concesionario" },
    { id: "marca", label: "🏍️ Marca / Importadora" },
    { id: "alquiler", label: "🗺️ Agencia de alquiler" },
    { id: "accesorios", label: "🛡️ Accesorios / Indumentaria" },
    { id: "seguro", label: "📋 Seguros" },
    { id: "gestoria", label: "📄 Gestoría / Trámites" },
    { id: "motoescuela", label: "🎓 Instructor / Escuela" },
    { id: "pista", label: "🏁 Pista / Circuito" },
    { id: "restaurante", label: "🍽️ Restaurante / Parador" },
    { id: "hospedaje", label: "🛏️ Hospedaje / Motoposada" },
  ];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const subirLogo = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "olbos_uploads");
    formData.append("cloud_name", "dhwhk48wb");
    const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: formData });
    const data = await res.json();
    return data.secure_url;
  };

  const handleSubmit = async () => {
    if (!form.nombre || !form.categoria || !form.ciudad) {
      setError("Completá nombre, categoría y ciudad"); return;
    }
    setLoading(true); setError("");
    const MSG_RED = "No pudimos conectar. Revisá tu señal e intentá de nuevo tocando \"Registrar negocio\" — lo que cargaste no se pierde.";
    // Sin coordenadas la ficha no sale en el mapa ni en el navegador: si no eligió la
    // dirección de la lista, se intenta ubicar lo que escribió (dirección + ciudad, o la
    // ciudad sola) y si tampoco se puede, se frena acá en vez de guardar una ficha ciega.
    if (!form.lat || !form.lng) {
      let ubic = null;
      try {
        const intentos = [form.direccion && `${form.direccion}, ${form.ciudad}`, form.ciudad].filter(Boolean);
        for (const q of intentos) {
          const r = await fetch(`${API_BASE}/api/geocode?address=${encodeURIComponent(q)}`);
          const d = await r.json();
          if (d && d.lat) { ubic = d; break; }
        }
      } catch (e) { /* se resuelve abajo */ }
      if (!ubic) {
        setError(form.direccion ? "No pudimos ubicar esa dirección en el mapa. Escribila de nuevo y elegila de la lista de sugerencias." : "No pudimos ubicar esa ciudad. Elegila de la lista de sugerencias.");
        setLoading(false); return;
      }
      form.lat = ubic.lat; form.lng = ubic.lng;
      if (!form.ciudad && ubic.ciudad) form.ciudad = ubic.ciudad;
      setForm(f => ({ ...f, lat: ubic.lat, lng: ubic.lng, ciudad: f.ciudad || ubic.ciudad || "" }));
    }
    try {
      // crear cuenta (o recuperar la sesión si quedó creada en un intento anterior que falló a mitad de camino)
      let userId = null;
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email, password: form.password,
        options: { data: { nombre: form.nombre, tipo: "negocio" } }
      });
      if (authError) {
        const msg = (authError.message || "").toLowerCase();
        if (msg.includes("already registered") || msg.includes("already exists")) {
          const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
          if (loginError || !loginData?.user) { setError("Ese email ya tiene una cuenta. Volvé al paso 1 y revisá email y contraseña."); setLoading(false); return; }
          userId = loginData.user.id;
        } else if (msg.includes("fetch") || msg.includes("network")) {
          setError(MSG_RED); setLoading(false); return;
        } else {
          setError(authError.message); setLoading(false); return;
        }
      } else {
        userId = authData.user.id;
      }
      // La ficha se registra SIEMPRE por el servidor (RPC security definer): con sesión usa
      // auth.uid(); sin sesión (cuenta recién creada, email sin confirmar) la ata por email.
      // El insert directo dependía de RLS y falló con dos usuarios reales (24 y 29-sep-2026).

      // subir logo si hay (si Cloudinary falla, seguimos sin logo — la ficha es lo importante)
      let logoUrl = null;
      if (logo) { try { logoUrl = await subirLogo(logo); } catch (e) { logoUrl = null; } }

      const ficha = {
        nombre: form.nombre,
        categoria: normalizarCategoria(form.categoria),
        descripcion: form.descripcion,
        direccion: form.direccion,
        ciudad: form.ciudad,
        telefono: form.telefono,
        whatsapp: form.whatsapp,
        web: form.web,
        instagram: form.instagram,
        logo_url: logoUrl,
        lat: form.lat || null, lng: form.lng || null,
      };

      const r = await registrarNegocioServidor(form.email, ficha);
      if (r.error) {
        const em = String(r.error).toLowerCase();
        setError(em.includes("fetch") || em.includes("network") ? MSG_RED : "Tu cuenta quedó creada, pero la ficha no se pudo registrar: " + r.error + " Tocá \"Registrar negocio\" de nuevo; si sigue fallando, escribinos por WhatsApp y la cargamos nosotros.");
        setLoading(false); return;
      }
      onSuccess();
    } catch (e) {
      const em = String(e?.message || e).toLowerCase();
      setError(em.includes("fetch") || em.includes("network") ? MSG_RED : "Error al registrar. Intentá de nuevo.");
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, overflowY: "auto" }}>
      {/* header */}
      <div style={{ padding: "48px 20px 20px", borderBottom: `1px solid ${C.border}` }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14, marginBottom: 16 }}>
          <ChevronLeft size={18} strokeWidth={2} /> Volver
        </button>
        <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Olbos para negocios</div>
        <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5, color: C.text, marginBottom: 4 }}>Registrá tu negocio</h1>
        <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>Gratis para empezar. Llegá a miles de riders en Argentina.</p>

        {/* pasos */}
        <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
          {[1, 2, 3].map(p => (
            <div key={p} style={{ flex: 1, height: 4, borderRadius: 2, background: p <= paso ? C.text : C.faint, transition: "background .3s" }} />
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
          Paso {paso} de 3 — {paso === 1 ? "Tu cuenta" : paso === 2 ? "Tu negocio" : "Contacto"}
        </div>
      </div>

      <div style={{ padding: "20px 20px 100px" }}>

        {/* PASO 1 — cuenta */}
        {paso === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Email</div>
              <input value={form.email} onChange={e => set("email", e.target.value)} type="email" placeholder="hola@tunegocio.com"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Contraseña</div>
              <CampoPass value={form.password} onChange={e => set("password", e.target.value)} placeholder="Mínimo 6 caracteres"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }} />
            </div>
          </div>
        )}

        {/* PASO 2 — negocio */}
        {paso === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* logo */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Logo del negocio</div>
              <label style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}>
                <div style={{ width: 70, height: 70, borderRadius: 16, border: `2px dashed ${C.border}`, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                  {logo
                    ? <img loading="lazy" decoding="async" src={URL.createObjectURL(logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <Camera size={24} strokeWidth={1.5} color={C.muted} />
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>Subir logo</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Recomendado: cuadrado, 400x400px</div>
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => setLogo(e.target.files[0])} />
              </label>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Nombre del negocio *</div>
              <input value={form.nombre} onChange={e => set("nombre", e.target.value)} placeholder="Ej: Taller Mototech"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }} />
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Categoría *</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {categorias.map(c => (
                  <button key={c.id} onClick={() => set("categoria", c.id)}
                    style={{ padding: "10px 12px", borderRadius: 12, border: `1px solid ${form.categoria === c.id ? C.text : C.border}`, background: form.categoria === c.id ? C.text : C.card, color: form.categoria === c.id ? "#fff" : C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left" }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Descripción</div>
              <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3}
                placeholder="Contá qué hacen, qué los diferencia..."
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", resize: "none", fontFamily: "inherit", color: C.text, background: C.surface }} />
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Ciudad *</div>
              <AutocompleteCiudad value={form.ciudad} onChange={v => set("ciudad", v)} placeholder="Buenos Aires, Mendoza..."
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }} />
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Dirección</div>
              <AutocompleteDir
                value={form.direccion}
                onChange={v => setForm(f => ({ ...f, direccion: v, lat: null, lng: null }))}
                onSelect={({ direccion, lat, lng, ciudad }) => setForm(f => ({ ...f, direccion, lat, lng, ciudad: ciudad || f.ciudad }))}
                placeholder="Av. Corrientes 1234"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${form.lat ? C.green : C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }}
              />
              <div style={{ fontSize: 11.5, color: form.lat ? C.green : C.muted, marginTop: 5 }}>{form.lat ? "✓ Ubicada en el mapa" : "Elegí la dirección de la lista para que la ficha aparezca en el mapa y en el navegador."}</div>
            </div>
          </div>
        )}

        {/* PASO 3 — contacto */}
        {paso === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[["telefono", "Teléfono", "011-4567-8901", "tel"], ["whatsapp", "WhatsApp", "5491145678901", "tel"], ["web", "Sitio web", "www.tunegocio.com.ar", "url"], ["instagram", "Instagram", "@tunegocio", "text"]].map(([k, label, ph, type]) => (
              <div key={k}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
                <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph} type={type}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, boxSizing: "border-box", color: C.text, background: C.surface }} />
              </div>
            ))}

            {/* resumen */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Resumen de tu registro</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.8 }}>
                <div>📍 <strong>{form.nombre}</strong></div>
                <div>🏷️ {categorias.find(c => c.id === form.categoria)?.label}</div>
                <div>📌 {form.ciudad}</div>
              </div>
            </div>

            {/* Sin "planes": registrar el negocio es gratis y punto (decisión 26-ago / 02-sep) */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}><strong style={{ color: C.text }}>Registrar tu negocio es gratis.</strong> La ficha queda en revisión y en 24-48 hs te confirmamos por email: desde ahí aparece en el Pit Stop, en el mapa y en el navegador de los riders.</div>
            </div>
          </div>
        )}

        {error && <div style={{ marginTop: 14, padding: "10px 14px", background: `${C.red}14`, border: `1px solid ${C.red}22`, borderRadius: 10, fontSize: 13, color: C.red }}>{error}</div>}

        {/* botones navegación */}
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          {paso > 1 && (
            <button onClick={() => setPaso(p => p - 1)}
              style={{ flex: 1, padding: "14px 0", borderRadius: 14, background: C.surface, color: C.text, fontSize: 15, fontWeight: 600, border: `1px solid ${C.border}`, cursor: "pointer" }}>
              Atrás
            </button>
          )}
          <button onClick={() => paso < 3 ? setPaso(p => p + 1) : handleSubmit()} disabled={loading}
            style={{ flex: 2, padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Registrando..." : paso < 3 ? "Continuar →" : "Registrar negocio"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PANEL ADMIN ──────────────────────────────────────────────────────────────
// Pre-llenar el perfil de un partner (tienda/comunidad) y generar un link para que lo reclame
// ─── Campañas medibles (socios, influencers, QR del stand) ───────────────────
// Cada campaña tiene su link olbos.ai/c/<codigo> y mide todo lo que entra por ahí:
// registrados, cuántos cargaron su moto, cuántos se anotaron a eventos, activos.
// Nació del acuerdo con Expo Moto (sponsor oro), que pide un link de seguimiento.
// ─── BUSINESS MANAGER DE OLBOS (panel de agencia, etapa 2) ───────────────────
// Dos herramientas para el equipo de growth: ACCIONES (cada evento, influencer,
// pauta o stand tiene su link con QR, su costo y sus resultados, así se sabe el
// costo por cuenta nueva de cada cosa) y ANUNCIOS (programar los espacios
// publicitarios de la app para una marca, con fechas, sin pasar por Mercado
// Pago: canje, transferencia o sponsor, y ver impresiones y clicks).
const slugCodigo = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
const TIPOS_ACCION = [["evento", "Evento / stand"], ["influencer", "Influencer"], ["pauta", "Pauta paga"], ["socio", "Socio / marca"], ["contenido", "Contenido propio"], ["otro", "Otro"]];
const fmtARS = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-AR");
const fmtDia = (f) => f ? new Date(String(f).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" }) : "";

const AccionesTab = () => {
  const [datos, setDatos] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [form, setForm] = useState({ nombre: "", codigo: "", socio: "", tipo: "evento", costo: "", desde: "", hasta: "", notas: "" });
  const [guardando, setGuardando] = useState(false);
  const [qr, setQr] = useState(null); // { codigo, dataUrl }
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v, ...(k === "nombre" && !f.codigoManual ? { codigo: slugCodigo(v) } : {}) }));
  const cargar = async () => {
    const { data: raw } = await supabase.rpc("stats_campanias");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setDatos(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const crear = async () => {
    if (!form.nombre.trim() || !form.codigo.trim()) { alert("Poné un nombre y un código para el link."); return; }
    setGuardando(true);
    const { data: raw, error } = await supabase.rpc("agencia_crear_campania", {
      p_codigo: form.codigo.trim(), p_nombre: form.nombre.trim(), p_socio: form.socio.trim() || null, p_tipo: form.tipo,
      p_costo: form.costo ? Number(String(form.costo).replace(/[^\d]/g, "")) : null, p_desde: form.desde || null, p_hasta: form.hasta || null, p_notas: form.notas.trim() || null,
    });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setGuardando(false);
    if (error || r?.error) { alert("No se pudo crear: " + (error?.message || r?.error)); return; }
    setNueva(false); setForm({ nombre: "", codigo: "", socio: "", tipo: "evento", costo: "", desde: "", hasta: "", notas: "" });
    cargar();
  };
  const toggle = async (c) => {
    await supabase.rpc("agencia_editar_campania", { p_codigo: c.codigo, p_activa: !c.activa });
    cargar();
  };
  const copiar = async (url) => { try { await navigator.clipboard.writeText(url); alert("Link copiado ✓"); } catch (e) { alert(url); } };
  const verQr = async (c) => {
    try {
      const url = `${SITE_URL}/c/${c.codigo}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 900, margin: 2, color: { dark: "#111111", light: "#F9F5F2" } });
      setQr({ codigo: c.codigo, nombre: c.nombre, url, dataUrl });
    } catch (e) { alert("No se pudo generar el QR."); }
  };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" };
  const lab = { fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5, display: "block" };
  if (!datos) return <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando acciones…</div>;
  const totCosto = datos.reduce((a, c) => a + (Number(c.costo) || 0), 0);
  const totReg = datos.reduce((a, c) => a + (Number(c.registrados) || 0), 0);
  return (
    <div style={{ padding: "4px 0 20px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          Cada acción (un evento, un influencer, una pauta, un stand) tiene su link y su QR. Se cuenta quién entra por ahí, quién crea la cuenta y qué hace después. Con el costo cargado sale el <strong style={{ color: C.text }}>costo por cuenta nueva</strong> de cada acción.
        </div>
        <button onClick={() => setNueva(v => !v)} style={{ padding: "10px 16px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>{nueva ? "Cerrar" : "+ Nueva acción"}</button>
      </div>
      {datos.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {[["Acciones", datos.length], ["Cuentas nuevas atribuidas", totReg], ["Inversión cargada", fmtARS(totCosto)], ["Costo por cuenta", totReg > 0 && totCosto > 0 ? fmtARS(totCosto / totReg) : "—"]].map(([l, v]) => (
            <div key={l} style={{ flex: 1, minWidth: 130, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 12px" }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text, letterSpacing: "-0.02em" }}>{v}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{l}</div>
            </div>
          ))}
        </div>
      )}
      {nueva && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
            <div><label style={lab}>Nombre de la acción</label><input value={form.nombre} onChange={e => setF("nombre", e.target.value)} placeholder="Ej: Reel con @minita.en.moto" style={inp} /></div>
            <div><label style={lab}>Código del link</label><input value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: slugCodigo(e.target.value), codigoManual: true }))} placeholder="reel-minita" style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div><label style={lab}>Tipo</label><select value={form.tipo} onChange={e => setF("tipo", e.target.value)} style={inp}>{TIPOS_ACCION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label style={lab}>Socio / cuenta</label><input value={form.socio} onChange={e => setF("socio", e.target.value)} placeholder="@cuenta o marca" style={inp} /></div>
            <div><label style={lab}>Costo (ARS)</label><input value={form.costo} onChange={e => setF("costo", e.target.value)} placeholder="0 si es canje" inputMode="numeric" style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10, marginBottom: 12 }}>
            <div><label style={lab}>Desde</label><input type="date" value={form.desde} onChange={e => setF("desde", e.target.value)} style={inp} /></div>
            <div><label style={lab}>Hasta</label><input type="date" value={form.hasta} onChange={e => setF("hasta", e.target.value)} style={inp} /></div>
            <div><label style={lab}>Notas</label><input value={form.notas} onChange={e => setF("notas", e.target.value)} placeholder="Qué se acordó, dónde se publica" style={inp} /></div>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>El link va a ser <code style={{ color: C.text }}>{SITE_URL}/c/{form.codigo || "…"}</code></div>
          <button onClick={crear} disabled={guardando} style={{ padding: "11px 18px", borderRadius: 11, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: guardando ? 0.6 : 1 }}>{guardando ? "Creando…" : "Crear el link"}</button>
        </div>
      )}
      {datos.length === 0 && !nueva && <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Todavía no hay acciones. Creá la primera con el botón de arriba.</div>}
      {datos.map(c => {
        const url = `${SITE_URL}/c/${c.codigo}`;
        const costo = Number(c.costo) || 0;
        const cac = costo > 0 && c.registrados > 0 ? fmtARS(costo / c.registrados) : "—";
        const tipoL = (TIPOS_ACCION.find(t => t[0] === c.tipo) || [])[1];
        return (
          <div key={c.codigo} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 10, opacity: c.activa ? 1 : 0.55 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{c.nombre}</span>
              {tipoL && <span style={{ fontSize: 10.5, fontWeight: 700, color: C.text, background: C.surface, borderRadius: 6, padding: "2px 7px" }}>{tipoL}</span>}
              {c.socio && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.1em" }}>{c.socio}</span>}
              {(c.desde || c.hasta) && <span style={{ fontSize: 11, color: C.muted }}>{fmtDia(c.desde)}{c.hasta ? ` → ${fmtDia(c.hasta)}` : ""}</span>}
              <span style={{ marginLeft: "auto", fontSize: 11, color: c.activa ? "#2F7D4F" : C.muted, fontWeight: 700 }}>{c.activa ? "Activa" : "Pausada"}</span>
            </div>
            {c.notas && <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{c.notas}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <code style={{ flex: 1, fontSize: 11, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{url}</code>
              <button onClick={() => copiar(url)} style={{ padding: "5px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.text, cursor: "pointer" }}>Copiar</button>
              <button onClick={() => verQr(c)} style={{ padding: "5px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.text, cursor: "pointer" }}>QR</button>
              <button onClick={() => toggle(c)} style={{ padding: "5px 10px", borderRadius: 8, background: "transparent", border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.muted, cursor: "pointer" }}>{c.activa ? "Pausar" : "Activar"}</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
              {[["Visitas", c.visitas], ["Cuentas nuevas", c.registrados], ["Conversión", c.visitas > 0 ? Math.round((c.registrados || 0) * 100 / c.visitas) + "%" : "—"],
                ["Activos (7d)", c.activos_7d], ["Costo por cuenta", cac],
                ["Últimos 7 días", c.ultimos_7d], ["Con moto", c.con_moto], ["Se anotaron", c.inscriptos], ["Crearon eventos", c.organizadores], ["Inversión", costo ? fmtARS(costo) : "—"]].map(([label, valor]) => (
                <div key={label} style={{ background: C.surface, borderRadius: 10, padding: "8px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{valor ?? 0}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {qr && (
        <div onClick={() => setQr(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#F9F5F2", borderRadius: 18, padding: 22, textAlign: "center", maxWidth: 420, width: "100%" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#111", marginBottom: 4 }}>{qr.nombre}</div>
            <div style={{ fontSize: 12, color: "#8A857F", marginBottom: 12 }}>{qr.url}</div>
            <img src={qr.dataUrl} alt="QR" style={{ width: "100%", maxWidth: 320, borderRadius: 12 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => descargarImagen(qr.dataUrl, `qr-olbos-${qr.codigo}.png`)} style={{ flex: 1, padding: "11px", borderRadius: 11, background: "#111", color: "#fff", fontSize: 13, fontWeight: 800, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Descargar PNG</button>
              <button onClick={() => setQr(null)} style={{ flex: 1, padding: "11px", borderRadius: 11, background: "#F1ECE6", color: "#111", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── INGRESOS (panel de admin): el número que decide en marzo 2027 ────────────
// Ingresos cobrados de verdad, por mes: espacios pagados por Mercado Pago,
// acuerdos por transferencia, y lo que Carlos registra a mano (sponsors, leads,
// afiliados). Aparte, el volumen que mueve la app (inscripciones y turnos
// pagados) que NO es ingreso de Olbos pero muestra la tracción.
const INGRESOS_CATS = [["espacios", "Espacios / anuncios"], ["sorteos", "Sorteos patrocinados"], ["sponsors", "Sponsors"], ["leads", "Leads"], ["comisiones", "Comisiones"], ["afiliados", "Afiliados"], ["otros", "Otros"]];
const IngresosTab = ({ soloLectura = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mep, setMep] = useState(() => { try { return Number(localStorage.getItem("olbos_mep")) || 1540; } catch (e) { return 1540; } });
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), concepto: "", categoria: "sponsors", monto_ars: "", notas: "" });
  const [guardando, setGuardando] = useState(false);
  const cargar = async () => {
    const { data: raw, error: err } = await supabase.rpc("admin_ingresos");
    if (err) { setError(err.message || "No se pudo cargar"); setData({ meses: [], manuales: [] }); return; }
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setData(d || { meses: [], manuales: [] });
  };
  useEffect(() => { cargar(); }, []);
  useEffect(() => { try { localStorage.setItem("olbos_mep", String(mep)); } catch (e) { /* */ } }, [mep]);
  const guardar = async () => {
    if (!form.concepto.trim() || !(Number(form.monto_ars) > 0)) { alert("Poné concepto y monto."); return; }
    setGuardando(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error: err } = await supabase.from("ingresos").insert({ fecha: form.fecha, concepto: form.concepto.trim(), categoria: form.categoria, monto_ars: Number(form.monto_ars), monto_usd: Math.round(Number(form.monto_ars) / mep), notas: form.notas.trim() || null, creado_por: user?.id || null });
    setGuardando(false);
    if (err) { alert("No se pudo guardar: " + err.message); return; }
    setForm(f => ({ ...f, concepto: "", monto_ars: "", notas: "" }));
    cargar();
  };
  const borrar = async (id) => {
    if (!window.confirm("¿Borrar este ingreso?")) return;
    await supabase.from("ingresos").delete().eq("id", id);
    cargar();
  };
  const usd = (ars) => Math.round(Number(ars || 0) / (mep || 1));
  const fmtUSD = (n) => "USD " + Math.round(n).toLocaleString("es-AR");
  const meses = (data?.meses || []).slice().sort((a, b) => a.mes < b.mes ? 1 : -1);
  const mesKey = (d) => d.toISOString().slice(0, 7);
  const hoy = new Date();
  const esteMes = meses.find(m => m.mes === mesKey(hoy)) || { total: 0 };
  const ant = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 15);
  const mesAnt = meses.find(m => m.mes === mesKey(ant)) || { total: 0 };
  const acum = meses.reduce((a, m) => a + Number(m.total || 0), 0);
  const ultimos6 = meses.slice(0, 6).reverse();
  const maxBar = Math.max(1, ...ultimos6.map(m => Math.max(Number(m.total || 0), Number(m.gmv || 0))));
  const PISO = 200, OBJETIVO = 930; // USD/mes: infra+herramientas / con el equipo desde octubre 2026, Matías + Emilia (INVERSION.md)
  const esteUsd = usd(esteMes.total);
  const cell = { padding: "8px 10px", borderBottom: `1px solid ${C.border}`, fontSize: 12.5, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
  const th = { ...cell, fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 };
  const tile = (l, v, s, color) => (
    <div key={l} style={{ flex: 1, minWidth: 150, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 12px" }}>
      <div style={{ fontSize: 20, fontWeight: 900, color: color || C.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>{v}</div>
      <div style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>{l}</div>
      {s && <div style={{ fontSize: 10.5, color: C.muted }}>{s}</div>}
    </div>
  );
  if (data === null) return <div style={{ padding: 30, textAlign: "center", color: C.muted }}>Cargando ingresos…</div>;
  return (
    <div>
      {error && <div style={{ background: "#FEE", border: "1px solid #FCC", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#C00" }}>{error} (¿falta correr el SQL de admin_ingresos?)</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: C.muted }}>Ingresos <strong style={{ color: C.text }}>cobrados</strong>, no comprometidos. Dólar MEP para convertir:</div>
        <input type="number" value={mep} onChange={e => setMep(Number(e.target.value) || 0)} style={{ width: 90, padding: "6px 8px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontVariantNumeric: "tabular-nums" }} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {tile("Este mes", fmtARS(esteMes.total || 0), fmtUSD(esteUsd), esteUsd >= PISO ? "#2F7D4F" : C.text)}
        {tile("Mes anterior", fmtARS(mesAnt.total || 0), fmtUSD(usd(mesAnt.total)))}
        {tile("Acumulado", fmtARS(acum), fmtUSD(usd(acum)) + " · desde el primer ingreso")}
        {tile("Movido por la app este mes", fmtARS(esteMes.gmv || 0), "inscripciones y turnos pagados (no es ingreso de Olbos)")}
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Criterio de marzo 2027: la app tiene que pagarse sola</div>
        {[["Piso: infra y herramientas", PISO], ["Objetivo: con el equipo (desde octubre)", OBJETIVO]].map(([l, meta]) => (
          <div key={l} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: C.muted, marginBottom: 3 }}><span>{l}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtUSD(esteUsd)} / {fmtUSD(meta)} por mes · {Math.min(100, Math.round(esteUsd / meta * 100))} %</span></div>
            <div style={{ height: 8, background: C.surface, borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${Math.min(100, esteUsd / meta * 100)}%`, height: "100%", background: esteUsd >= meta ? "#2F7D4F" : C.accent }} /></div>
          </div>
        ))}
        <div style={{ fontSize: 10.5, color: C.muted }}>Los dos umbrales salen de docs/INVERSION.md (costos recurrentes sin la agencia). Se mide con el mes en curso al dólar MEP de arriba.</div>
      </div>
      {ultimos6.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Últimos meses · negro = ingresos de Olbos · gris = volumen movido por la app</div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", height: 120 }}>
            {ultimos6.map(m => (
              <div key={m.mes} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                <div style={{ display: "flex", gap: 3, alignItems: "flex-end", width: "100%", justifyContent: "center", flex: 1 }}>
                  <div title={fmtARS(m.total)} style={{ width: "40%", height: `${Math.max(2, Number(m.total || 0) / maxBar * 100)}%`, background: C.accent, borderRadius: "4px 4px 0 0" }} />
                  <div title={fmtARS(m.gmv)} style={{ width: "40%", height: `${Math.max(2, Number(m.gmv || 0) / maxBar * 100)}%`, background: C.border, borderRadius: "4px 4px 0 0" }} />
                </div>
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>{m.mes.slice(5)}/{m.mes.slice(2, 4)}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(usd(m.total))}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 14 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Mes</th><th style={th}>Espacios (MP)</th><th style={th}>Acuerdos (transf.)</th><th style={th}>Registrados a mano</th><th style={th}>Total ARS</th><th style={th}>Total USD</th><th style={th}>Canjes</th><th style={th}>Movido: inscripciones</th><th style={th}>Movido: turnos</th></tr></thead>
          <tbody>
            {meses.length === 0 ? <tr><td style={cell} colSpan={9}><span style={{ color: C.muted }}>Todavía no hay ingresos cobrados. Los espacios pagados por Mercado Pago aparecen solos; lo demás se registra abajo.</span></td></tr>
              : meses.map(m => (
                <tr key={m.mes}>
                  <td style={{ ...cell, fontWeight: 800 }}>{m.mes}</td>
                  <td style={cell}>{fmtARS(m.anuncios_mp || 0)}</td>
                  <td style={cell}>{fmtARS(m.anuncios_acordado || 0)}</td>
                  <td style={cell}>{fmtARS(m.manual || 0)}</td>
                  <td style={{ ...cell, fontWeight: 800 }}>{fmtARS(m.total || 0)}</td>
                  <td style={{ ...cell, fontWeight: 800 }}>{fmtUSD(usd(m.total))}</td>
                  <td style={{ ...cell, color: C.muted }}>{m.canjes || 0}</td>
                  <td style={{ ...cell, color: C.muted }}>{fmtARS(m.gmv_inscripciones || 0)}</td>
                  <td style={{ ...cell, color: C.muted }}>{fmtARS(m.gmv_turnos || 0)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!soloLectura && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.text, marginBottom: 8 }}>Registrar un ingreso cobrado (sponsor, lead, afiliados, lo que entre por fuera de Mercado Pago)</div>
          <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 170px 130px", gap: 8, marginBottom: 8 }}>
            <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} />
            <input value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Concepto (ej. Sponsor meta de km · Marca X, septiembre)" style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} />
            <select value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }}>
              {INGRESOS_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input type="number" value={form.monto_ars} onChange={e => setForm(f => ({ ...f, monto_ars: e.target.value }))} placeholder="Monto ARS" style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Notas (comprobante, quién pagó, a qué corresponde)" style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} />
            <button onClick={guardar} disabled={guardando} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: C.accent, color: "#F9F5F2", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: guardando ? .6 : 1 }}>{guardando ? "Guardando…" : "Guardar"}</button>
          </div>
        </div>
      )}
      {(data?.manuales || []).length > 0 && (
        <div style={{ overflowX: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Fecha</th><th style={th}>Concepto</th><th style={th}>Categoría</th><th style={th}>ARS</th><th style={th}>USD</th><th style={th}>Notas</th>{!soloLectura && <th style={th}></th>}</tr></thead>
            <tbody>
              {data.manuales.map(r => (
                <tr key={r.id}>
                  <td style={cell}>{r.fecha}</td>
                  <td style={{ ...cell, whiteSpace: "normal", minWidth: 200 }}>{r.concepto}</td>
                  <td style={cell}>{(INGRESOS_CATS.find(([k]) => k === r.categoria) || [null, r.categoria])[1]}</td>
                  <td style={{ ...cell, fontWeight: 700 }}>{fmtARS(r.monto_ars)}</td>
                  <td style={cell}>{r.monto_usd != null ? fmtUSD(r.monto_usd) : ""}</td>
                  <td style={{ ...cell, whiteSpace: "normal", color: C.muted, minWidth: 160 }}>{r.notas || ""}</td>
                  {!soloLectura && <td style={cell}><button onClick={() => borrar(r.id)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 12 }}>Borrar</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AnunciosTab = () => {
  const [lista, setLista] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ slot: "feed", anunciante: "", titulo: "", texto: "", imagen: "", link: "", premio: "", desde: new Date(Date.now() + 86400000).toISOString().slice(0, 10), semanas: 1, pagado_por: "canje", monto: "", notas: "" });
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cargar = async () => {
    const { data: raw } = await supabase.rpc("agencia_anuncios");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setLista(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  const subirImagen = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append("file", await achicarImagen(file)); fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const j = await res.json();
      if (j.secure_url) setF("imagen", j.secure_url);
    } catch (e) { alert("No se pudo subir la imagen."); }
    setSubiendo(false);
  };
  const hasta = form.desde ? new Date(new Date(form.desde + "T12:00:00").getTime() + (form.semanas * 7 - 1) * 86400000).toISOString().slice(0, 10) : "";
  const programar = async () => {
    if (!form.titulo.trim() || !form.anunciante.trim()) { alert("Poné el anunciante y el título del anuncio."); return; }
    if (form.slot === "sorteo" && !form.premio.trim()) { alert("Un sorteo necesita el premio."); return; }
    setGuardando(true);
    const { data: raw, error } = await supabase.rpc("agencia_programar_anuncio", {
      p_slot: form.slot, p_anunciante: form.anunciante.trim(), p_titulo: form.titulo.trim(), p_texto: form.texto.trim() || null, p_imagen: form.imagen || null,
      p_link: normalizarLinkAnuncio(form.link), p_premio: form.slot === "sorteo" ? form.premio.trim() : null,
      p_desde: form.desde, p_semanas: Number(form.semanas) || 1, p_pagado_por: form.pagado_por, p_monto: form.monto ? Number(String(form.monto).replace(/[^\d]/g, "")) : null, p_notas: form.notas.trim() || null,
    });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    setGuardando(false);
    if (error || r?.error) { alert(r?.error === "ocupado" ? "Ese espacio ya está tomado en esas fechas. Probá otra fecha." : "No se pudo programar: " + (error?.message || r?.error)); return; }
    setNuevo(false); setForm(f => ({ ...f, anunciante: "", titulo: "", texto: "", imagen: "", link: "", premio: "", monto: "", notas: "" }));
    Object.keys(anunciosCache).forEach(k => delete anunciosCache[k]);
    cargar();
  };
  const cambiarEstado = async (a, estado) => {
    await supabase.rpc("agencia_estado_anuncio", { p_id: a.id, p_estado: estado });
    Object.keys(anunciosCache).forEach(k => delete anunciosCache[k]);
    cargar();
  };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" };
  const lab = { fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5, display: "block" };
  const hoyISO = new Date().toISOString().slice(0, 10);
  const estadoDe = (a) => a.estado === "pausado" ? ["Pausado", C.muted] : a.estado !== "activo" ? [a.estado, C.muted] : a.hasta < hoyISO ? ["Terminado", C.muted] : a.desde > hoyISO ? ["Programado", "#8A6D3B"] : ["En el aire", "#2F7D4F"];
  if (!lista) return <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando anuncios…</div>;
  const def = ESPACIOS_DEF[form.slot] || {};
  return (
    <div style={{ padding: "4px 0 20px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          Los espacios publicitarios de la app, programados para una marca con fechas. Acá se cargan los acuerdos que no pasan por Mercado Pago (canje, transferencia, sponsor). Cada anuncio muestra impresiones y clicks.
        </div>
        <button onClick={() => setNuevo(v => !v)} style={{ padding: "10px 16px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>{nuevo ? "Cerrar" : "+ Programar anuncio"}</button>
      </div>
      {nuevo && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lab}>Espacio</label>
              <select value={form.slot} onChange={e => setF("slot", e.target.value)} style={inp}>
                {Object.entries(ESPACIOS_DEF).filter(([s]) => s !== "moto").map(([s, d]) => <option key={s} value={s}>{d.nombre} · lista {fmtARS(d.precio_semana)}/sem · hasta {d.capacidad} a la vez</option>)}
              </select>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{def.desc}</div>
            </div>
            <div><label style={lab}>Anunciante (marca o negocio)</label><input value={form.anunciante} onChange={e => setF("anunciante", e.target.value)} placeholder="Ej: Benito Motos" style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div><label style={lab}>Título del anuncio</label><input value={form.titulo} onChange={e => setF("titulo", e.target.value)} maxLength={60} placeholder="Lo que ve el rider" style={inp} /></div>
            <div><label style={lab}>Link o WhatsApp</label><input value={form.link} onChange={e => setF("link", e.target.value)} placeholder="https://… o 11 1234 5678" style={inp} /></div>
          </div>
          <div style={{ marginBottom: 10 }}><label style={lab}>Texto (opcional)</label><input value={form.texto} onChange={e => setF("texto", e.target.value)} maxLength={120} placeholder="Una línea más" style={inp} /></div>
          {form.slot === "sorteo" && <div style={{ marginBottom: 10 }}><label style={lab}>Premio del sorteo</label><input value={form.premio} onChange={e => setF("premio", e.target.value)} placeholder="Ej: Casco LS2" style={inp} /></div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div><label style={lab}>Desde</label><input type="date" value={form.desde} onChange={e => setF("desde", e.target.value)} style={inp} /></div>
            <div><label style={lab}>Semanas</label><select value={form.semanas} onChange={e => setF("semanas", Number(e.target.value))} style={inp}>{[1, 2, 3, 4, 6, 8, 12].map(n => <option key={n} value={n}>{n} semana{n > 1 ? "s" : ""} · hasta {fmtDia(new Date(new Date(form.desde + "T12:00:00").getTime() + (n * 7 - 1) * 86400000).toISOString().slice(0, 10))}</option>)}</select></div>
            <div><label style={lab}>Cómo se paga</label><select value={form.pagado_por} onChange={e => setF("pagado_por", e.target.value)} style={inp}><option value="canje">Canje</option><option value="transferencia">Transferencia</option><option value="sponsor">Sponsor / acuerdo</option><option value="bonificado">Bonificado</option></select></div>
            <div><label style={lab}>Monto acordado (ARS)</label><input value={form.monto} onChange={e => setF("monto", e.target.value)} placeholder={fmtARS(precioEspacio(def.precio_semana || 0, form.semanas))} inputMode="numeric" style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 12, alignItems: "end" }}>
            <div>
              <label style={lab}>Imagen / logo</label>
              <label style={{ ...inp, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                {form.imagen ? <img src={cldThumb(form.imagen, 80)} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} /> : <span>📷</span>}
                <span style={{ fontSize: 13, color: C.muted }}>{subiendo ? "Subiendo…" : form.imagen ? "Cambiar" : "Elegir imagen"}</span>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => subirImagen(e.target.files[0])} />
              </label>
            </div>
            <div><label style={lab}>Notas internas</label><input value={form.notas} onChange={e => setF("notas", e.target.value)} placeholder="Qué se acordó, con quién" style={inp} /></div>
          </div>
          {form.titulo && <div style={{ marginBottom: 12 }}><div style={lab}>Así se ve</div><TarjetaAnuncio a={{ titulo: form.titulo, texto: form.texto, imagen_url: form.imagen, link: form.link }} onClick={() => {}} /></div>}
          <button onClick={programar} disabled={guardando} style={{ padding: "11px 18px", borderRadius: 11, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: guardando ? 0.6 : 1 }}>{guardando ? "Programando…" : `Programar del ${fmtDia(form.desde)} al ${fmtDia(hasta)}`}</button>
        </div>
      )}
      {lista.length === 0 && !nuevo && <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>No hay anuncios todavía.</div>}
      {lista.map(a => {
        const [est, col] = estadoDe(a);
        const ctr = a.impresiones > 0 ? (a.clicks * 100 / a.impresiones).toFixed(1) + "%" : "—";
        return (
          <div key={a.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: C.text, background: C.surface, borderRadius: 6, padding: "2px 7px" }}>{(ESPACIOS_DEF[a.slot] || {}).nombre || a.slot}</span>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{a.anunciante || a.titulo}</span>
              <span style={{ fontSize: 11, color: C.muted }}>{fmtDia(a.desde)} → {fmtDia(a.hasta)}</span>
              <span style={{ fontSize: 11, color: C.muted }}>· {a.pagado_por || (a.mp_payment_id ? "Mercado Pago" : "")}{a.monto_acordado ? ` · ${fmtARS(a.monto_acordado)}` : a.precio ? ` · ${fmtARS(a.precio)}` : ""}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: col, fontWeight: 800 }}>{est}</span>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}><TarjetaAnuncio a={a} onClick={() => a.link && window.open(a.link, "_blank", "noopener")} /></div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                {[["Impresiones", a.impresiones], ["Clicks", a.clicks], ["CTR", ctr]].map(([l, v]) => (
                  <div key={l} style={{ background: C.surface, borderRadius: 10, padding: "8px 10px", textAlign: "center", minWidth: 70 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{v ?? 0}</div>
                    <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 600 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
            {a.notas && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{a.notas}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {a.estado === "activo" && a.hasta >= hoyISO && <button onClick={() => cambiarEstado(a, "pausado")} style={{ padding: "6px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>Pausar</button>}
              {a.estado === "pausado" && <button onClick={() => cambiarEstado(a, "activo")} style={{ padding: "6px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11.5, fontWeight: 700, color: C.text, cursor: "pointer" }}>Reanudar</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CampaniasTab = () => {
  const [datos, setDatos] = useState(null);
  const cargar = async () => {
    const { data: raw } = await supabase.rpc("stats_campanias");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    setDatos(Array.isArray(d) ? d : []);
  };
  useEffect(() => { cargar(); }, []);
  if (!datos) return <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando campañas…</div>;
  if (datos.length === 0) return <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Sin campañas cargadas.</div>;
  const copiar = async (url) => { try { await navigator.clipboard.writeText(url); alert("Link copiado ✓"); } catch (e) { alert(url); } };
  return (
    <div style={{ padding: "4px 0 20px" }}>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
        Cada campaña tiene su link propio. Se cuenta todo el que ENTRA (una vez por día por teléfono) y aparte el que termina creando su cuenta, así se distingue un link que no trae gente de uno que trae gente y no convierte. Sirve para socios (Expo Moto), reels con influencers y el QR del stand.
      </div>
      {datos.map(c => {
        const url = `${SITE_URL}/c/${c.codigo}`;
        return (
          <div key={c.codigo} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
              <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text, flex: 1 }}>{c.nombre}</span>
              {c.socio && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#8A6D3B", textTransform: "uppercase", letterSpacing: "0.1em" }}>{c.socio}</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <code style={{ flex: 1, fontSize: 11, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{url}</code>
              <button onClick={() => copiar(url)} style={{ padding: "5px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 700, color: C.text, cursor: "pointer", flexShrink: 0 }}>Copiar</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[["Visitas al link", c.visitas], ["Cuentas nuevas", c.registrados], ["Conversión", c.visitas > 0 ? Math.round((c.registrados || 0) * 100 / c.visitas) + "%" : "—"],
                ["Últimos 7 días", c.ultimos_7d], ["Activos (7d)", c.activos_7d], ["Con moto cargada", c.con_moto],
                ["Se anotaron a algo", c.inscriptos], ["Crearon eventos", c.organizadores]].map(([label, valor]) => (
                <div key={label} style={{ background: C.surface, borderRadius: 10, padding: "9px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>{valor ?? 0}</div>
                  <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InvitarPerfilTab = () => {
  const [datos, setDatos] = useState({ username: "", bio: "", ciudad: "", avatar_url: "", links: [], tipo_perfil: "persona" });
  const [subiendo, setSubiendo] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [token, setToken] = useState(null);
  const [error, setError] = useState("");
  const [invitaciones, setInvitaciones] = useState([]);
  const setD = (k, v) => setDatos(d => ({ ...d, [k]: v }));

  const cargarInvitaciones = async () => {
    const { data: raw } = await supabase.rpc("mis_invitaciones_perfil");
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) setInvitaciones(data);
  };
  useEffect(() => { cargarInvitaciones(); }, []);

  const subirLogo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) setD("avatar_url", data.secure_url);
    } catch (e) { /* */ }
    setSubiendo(false);
  };

  const generar = async () => {
    if (!datos.username.trim()) { setError("Poné un username"); return; }
    setGenerando(true); setError("");
    const links = (datos.links || []).filter(l => l.tipo && l.url && l.url.trim()).map(l => ({ tipo: l.tipo, url: l.url.trim() }));
    const { data: raw, error: err } = await supabase.rpc("crear_invitacion_perfil", { p_datos: { ...datos, username: datos.username.trim().replace(/^@/, ""), links } });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    setGenerando(false);
    if (err || data?.error) { setError(data?.error === "no_autorizado" ? "Solo admin puede crear invitaciones" : (err?.message || "Error")); return; }
    setToken(data.token);
    cargarInvitaciones();
  };

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.surface, boxSizing: "border-box" };

  if (token) {
    const link = `${SITE_URL}/reclamar/${token}`;
    return (
      <div style={{ padding: "8px 2px" }}>
        <div style={{ fontSize: 40, textAlign: "center", marginBottom: 8 }}>🎟️</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, textAlign: "center", marginBottom: 6 }}>Link de invitación listo</div>
        <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, textAlign: "center", marginBottom: 14 }}>
          Mandáselo por WhatsApp. Solo tienen que poner su email y validar — el perfil ya queda armado con todo lo que cargaste.
        </p>
        <div style={{ padding: "10px 12px", background: C.surface, borderRadius: 8, fontSize: 11, color: C.text, fontFamily: "monospace", wordBreak: "break-all", border: `1px solid ${C.border}`, marginBottom: 12 }}>{link}</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`¡Te armé el perfil de @${datos.username} en Olbos! Reclamalo acá (solo ponés tu email):\n${link}`)}`, "_blank")}
            style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#25D366", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>WhatsApp</button>
          <button onClick={async () => { try { await navigator.clipboard.writeText(link); alert("Link copiado"); } catch { alert(link); } }}
            style={{ flex: 1, padding: "11px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Copiar</button>
        </div>
        <button onClick={() => { setToken(null); setDatos({ username: "", bio: "", ciudad: "", avatar_url: "", links: [], tipo_perfil: "persona" }); }}
          style={{ width: "100%", padding: "10px", borderRadius: 10, background: "none", border: "none", color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Crear otra invitación</button>
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 2px" }}>
      <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
        Pre-llená el perfil de una tienda o comunidad (ej. RODPRO). Generás un link, se lo mandás, y ellos solo crean su cuenta — el perfil queda armado.
      </p>

      {/* logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, overflow: "hidden", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {datos.avatar_url ? <img loading="lazy" decoding="async" src={datos.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={22} color={C.muted} />}
        </div>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.text, cursor: subiendo ? "wait" : "pointer" }}>
          {subiendo ? "Subiendo…" : datos.avatar_url ? "Cambiar logo" : "Subir logo"}
          <input type="file" accept="image/*" style={{ display: "none" }} disabled={subiendo} onChange={e => { subirLogo(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div><div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Username</div>
          <input value={datos.username} onChange={e => setD("username", e.target.value)} placeholder="rodpro" style={inputStyle} /></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Bio</div>
          <textarea value={datos.bio} onChange={e => setD("bio", e.target.value)} placeholder="Tienda de motos y comunidad de rodadas." rows={2} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} /></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Ciudad</div>
          <input value={datos.ciudad} onChange={e => setD("ciudad", e.target.value)} placeholder="Córdoba" style={inputStyle} /></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Tipo de perfil</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["persona", "🏍️ Rider"], ["comunidad", "👥 Comunidad / Tienda"]].map(([val, label]) => (
              <button key={val} onClick={() => setD("tipo_perfil", val)}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: (datos.tipo_perfil || "persona") === val ? `2px solid ${C.accent}` : `1px solid ${C.border}`, background: (datos.tipo_perfil || "persona") === val ? "#1111110C" : C.card, color: (datos.tipo_perfil || "persona") === val ? C.accent : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* links */}
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Redes y links</div>
          {(datos.links || []).map((l, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
              <select value={l.tipo} onChange={e => setDatos(d => ({ ...d, links: d.links.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x) }))}
                style={{ padding: "10px 8px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, flexShrink: 0 }}>
                {LINK_TIPOS_ORDEN.map(t => <option key={t} value={t}>{LINK_TIPOS[t].label}</option>)}
              </select>
              <input value={l.url} onChange={e => setDatos(d => ({ ...d, links: d.links.map((x, j) => j === i ? { ...x, url: e.target.value } : x) }))}
                placeholder="@usuario o link" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
              <button onClick={() => setDatos(d => ({ ...d, links: d.links.filter((_, j) => j !== i) }))}
                style={{ width: 30, height: 30, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={13} strokeWidth={2.5} color={C.red} />
              </button>
            </div>
          ))}
          <button onClick={() => setDatos(d => ({ ...d, links: [...(d.links || []), { tipo: "instagram", url: "" }] }))}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text }}>
            <Plus size={14} strokeWidth={2.5} /> Agregar link
          </button>
        </div>
      </div>

      {error && <div style={{ padding: 10, background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 8, fontSize: 12, color: C.red, margin: "12px 0" }}>{error}</div>}

      <button onClick={generar} disabled={generando}
        style={{ width: "100%", marginTop: 16, padding: "13px", borderRadius: 12, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", opacity: generando ? 0.7 : 1 }}>
        {generando ? "Generando…" : "Generar link de invitación"}
      </button>

      {/* Invitaciones ya creadas */}
      {invitaciones.length > 0 && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Invitaciones creadas</div>
          {invitaciones.map((inv) => {
            const link = `${SITE_URL}/reclamar/${inv.token}`;
            return (
              <div key={inv.token} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, overflow: "hidden", background: C.surface, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {inv.avatar_url ? <img loading="lazy" decoding="async" src={inv.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={16} color={C.muted} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>@{inv.username || "—"}</div>
                  <div style={{ fontSize: 11, color: inv.reclamada ? "#1F8347" : "#B07D1A", fontWeight: 600 }}>
                    {inv.reclamada ? "✓ Reclamada" : "Pendiente"}
                  </div>
                </div>
                {!inv.reclamada && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`¡Te armé el perfil de @${inv.username} en Olbos! Reclamalo acá (solo ponés tu email):\n${link}`)}`, "_blank")}
                      title="WhatsApp"
                      style={{ width: 32, height: 32, borderRadius: 8, background: "#25D366", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <MessageCircle size={15} color="#fff" strokeWidth={2.2} />
                    </button>
                    <button onClick={async () => { try { await navigator.clipboard.writeText(link); alert("Link copiado"); } catch { alert(link); } }}
                      title="Copiar link"
                      style={{ width: 32, height: 32, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Share2 size={14} color={C.text} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── MÉTRICAS PARA SPONSORS (AdminPanel → 📈 Métricas) ───────────────────────
// Dashboard visual para mostrar números a marcas: DAU/WAU/MAU, retención por
// cohortes, crecimiento diario/semanal (con pico visible el día de un evento),
// ciudades y engagement por rodada. Datos del RPC admin_metricas (solo admins).
const MetricasSponsors = ({ data, cargando, onRecargar }) => {
  if (cargando || (!data && cargando !== false)) return <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Cargando métricas…</div>;
  if (!data) return <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Sin datos todavía.</div>;
  if (data.error) return <div style={{ textAlign: "center", padding: 40, color: C.red, fontSize: 13 }}>No se pudieron cargar las métricas ({data.error}). ¿Está corrido el SQL de admin_metricas?</div>;

  const pct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);
  const fmtDia = (iso) => { const d = new Date(iso + "T12:00:00"); return d.getDate() + "/" + (d.getMonth() + 1); };
  const isoLocal = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");

  // Serie diaria completa (30 días, rellenando los días sin registros con 0)
  const porDia = {}; (data.registros_por_dia || []).forEach(d => { porDia[d.dia] = d.n; });
  const serieDias = [];
  for (let i = 29; i >= 0; i--) { const dt = new Date(Date.now() - i * 86400000); const iso = isoLocal(dt); serieDias.push({ dia: iso, n: porDia[iso] || 0 }); }
  const maxDia = Math.max(1, ...serieDias.map(d => d.n));
  const semanas = data.registros_por_semana || [];
  const maxSem = Math.max(1, ...semanas.map(s => s.n));
  const ciudades = data.ciudades || [];
  const maxCiu = Math.max(1, ...ciudades.map(c => c.n));
  const Titulo = ({ children }) => <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>{children}</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 900, color: C.text, letterSpacing: -0.3 }}>Métricas Olbos</div>
          <div style={{ fontSize: 11, color: C.muted }}>al {data.hoy ? fmtDia(data.hoy) : "hoy"} · uso interno / marcas</div>
        </div>
        <button onClick={onRecargar} style={{ padding: "7px 12px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, color: C.text, cursor: "pointer" }}>↻ Actualizar</button>
      </div>

      {/* Usuarios activos */}
      <Titulo>Usuarios activos</Titulo>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Riders totales", val: data.total_usuarios, sub: "cuentas registradas", color: C.text },
          { label: "Hoy (DAU)", val: data.dau, sub: "usuarios únicos activos", color: C.green },
          { label: "7 días (WAU)", val: data.wau, sub: "usuarios únicos activos", color: C.text },
          { label: "30 días (MAU)", val: data.mau, sub: "usuarios únicos activos", color: C.text },
        ].map(s => (
          <div key={s.label} style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 30, fontWeight: 900, color: s.color, letterSpacing: -1, marginBottom: 2 }}>{s.val ?? "—"}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontSize: 10.5, color: C.muted }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Retención — LA métrica de las marcas */}
      <Titulo>Retención semanal (vuelven a la semana siguiente)</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 14 }}>
        {(data.retencion_semanal || []).length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Todavía no hay cohortes completas.</div>}
        {(data.retencion_semanal || []).map(c => {
          const p = pct(c.vuelven, c.registrados);
          return (
            <div key={c.semana} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ width: 78, fontSize: 11, color: C.muted, flexShrink: 0 }}>sem. {fmtDia(c.semana)}</div>
              <div style={{ flex: 1, height: 16, background: C.surface, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ width: `${p}%`, height: "100%", background: C.green, borderRadius: 8 }} />
              </div>
              <div style={{ width: 96, fontSize: 11.5, color: C.text, textAlign: "right", flexShrink: 0 }}><strong>{p}%</strong> · {c.vuelven}/{c.registrados}</div>
            </div>
          );
        })}
      </div>
      <Titulo>Retención mensual (vuelven al mes siguiente)</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 20 }}>
        {(data.retencion_mensual || []).map(c => {
          const p = pct(c.vuelven, c.registrados);
          const mesTxt = new Date(c.mes + "T12:00:00").toLocaleDateString("es-AR", { month: "short" });
          return (
            <div key={c.mes} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ width: 78, fontSize: 11, color: C.muted, flexShrink: 0, textTransform: "capitalize" }}>{mesTxt}</div>
              <div style={{ flex: 1, height: 16, background: C.surface, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ width: `${p}%`, height: "100%", background: "#8A6D3B", borderRadius: 8 }} />
              </div>
              <div style={{ width: 96, fontSize: 11.5, color: C.text, textAlign: "right", flexShrink: 0 }}><strong>{p}%</strong> · {c.vuelven}/{c.registrados}</div>
            </div>
          );
        })}
      </div>

      {/* Crecimiento diario — acá se ve el pico del día de un evento */}
      <Titulo>Nuevos registros por día (últimos 30)</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 90 }}>
          {serieDias.map(d => (
            <div key={d.dia} title={`${fmtDia(d.dia)}: ${d.n}`}
              style={{ flex: 1, background: d.n === maxDia && d.n > 0 ? C.text : "#CFC8BE", borderRadius: 3, height: `${Math.max(3, (d.n / maxDia) * 100)}%` }} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted, marginTop: 6 }}>
          <span>{fmtDia(serieDias[0].dia)}</span>
          <span>pico: {maxDia}/día</span>
          <span>{fmtDia(serieDias[serieDias.length - 1].dia)}</span>
        </div>
      </div>
      <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 14 }}>El pico de un día de evento se ve acá — captura ideal para mostrar conversión.</div>
      <Titulo>Nuevos registros por semana (últimas 12)</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 20 }}>
        {semanas.map(s => (
          <div key={s.semana} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 78, fontSize: 11, color: C.muted, flexShrink: 0 }}>sem. {fmtDia(s.semana)}</div>
            <div style={{ flex: 1, height: 14, background: C.surface, borderRadius: 7, overflow: "hidden" }}>
              <div style={{ width: `${(s.n / maxSem) * 100}%`, height: "100%", background: C.text, borderRadius: 7 }} />
            </div>
            <div style={{ width: 40, fontSize: 11.5, fontWeight: 700, color: C.text, textAlign: "right", flexShrink: 0 }}>{s.n}</div>
          </div>
        ))}
      </div>

      {/* Geografía */}
      <Titulo>Riders por ciudad</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 20 }}>
        {ciudades.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Pocos perfiles tienen ciudad cargada todavía.</div>}
        {ciudades.map(c => (
          <div key={c.ciudad} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 110, fontSize: 11.5, color: C.text, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.ciudad}</div>
            <div style={{ flex: 1, height: 14, background: C.surface, borderRadius: 7, overflow: "hidden" }}>
              <div style={{ width: `${(c.n / maxCiu) * 100}%`, height: "100%", background: "#8A6D3B", borderRadius: 7 }} />
            </div>
            <div style={{ width: 34, fontSize: 11.5, fontWeight: 700, color: C.text, textAlign: "right", flexShrink: 0 }}>{c.n}</div>
          </div>
        ))}
      </div>

      {/* Engagement por evento */}
      <Titulo>Engagement por salida / evento (últimos 10)</Titulo>
      <div style={{ background: C.card, borderRadius: 14, padding: "6px 14px", border: `1px solid ${C.border}`, marginBottom: 14 }}>
        <div style={{ display: "flex", padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>
          <div style={{ flex: 1 }}>Evento</div>
          <div style={{ width: 58, textAlign: "right" }}>Inscr.</div>
          <div style={{ width: 58, textAlign: "right" }}>Check-in</div>
          <div style={{ width: 58, textAlign: "right" }}>Tracks</div>
        </div>
        {(data.eventos || []).map((e, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: i < (data.eventos || []).length - 1 ? `1px solid ${C.surface}` : "none", fontSize: 12 }}>
            <div style={{ flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>
              {e.titulo}
              {e.fecha && <span style={{ color: C.muted, fontSize: 10.5 }}> · {fmtDia(e.fecha)}</span>}
            </div>
            <div style={{ width: 58, textAlign: "right", fontWeight: 700, color: C.text }}>{e.inscriptos}</div>
            <div style={{ width: 58, textAlign: "right", fontWeight: 700, color: C.green }}>{e.checkins}</div>
            <div style={{ width: 58, textAlign: "right", fontWeight: 700, color: "#8A6D3B" }}>{e.recorridos}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5, paddingBottom: 10 }}>
        Metodología: activo = abrió la app o realizó una acción (post, like, comentario, recorrido, inscripción, check-in) ese día.
        Retención = % de la cohorte de registro con actividad en el período siguiente. La actividad previa al lanzamiento de esta medición se aproxima por acciones de contenido.
      </div>
    </div>
  );
};

const AdminPanel = ({ onClose, modo = "admin" }) => {
  // modo "agencia": solo lectura de Stats, Métricas, Campañas y Mails, desde la compu
  const esAgencia = modo === "agencia";
  const enPc = typeof window !== "undefined" && window.innerWidth >= 1024;
  const [negocios, setNegocios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [tabAdmin, setTabAdmin] = useState("stats");
  const [showCrear, setShowCrear] = useState(false);
  const [form, setForm] = useState({ nombre: "", categoria: "", descripcion: "", ciudad: "", direccion: "", telefono: "", whatsapp: "", web: "", instagram: "", horarios: "" });
  const [guardando, setGuardando] = useState(false);
  const [stats, setStats] = useState(null);
  const [vistas, setVistas] = useState(null); // visualizaciones de posts, eventos y fichas
  const [cargandoStats, setCargandoStats] = useState(true);
  const [usuariosRecientes, setUsuariosRecientes] = useState([]);
  const [postsRecientes, setPostsRecientes] = useState([]);
  // Referidos
  const [codigos, setCodigos] = useState([]);
  const [statsReferidos, setStatsReferidos] = useState({}); // {codigo: count}
  const [cargandoRefs, setCargandoRefs] = useState(false);
  const [showCrearCodigo, setShowCrearCodigo] = useState(false);
  const [editandoCodigo, setEditandoCodigo] = useState(null);
  const [nuevoCodigo, setNuevoCodigo] = useState({ codigo: "", username: "", nombre_completo: "", beneficios: "" });
  // Reportes
  const [reportes, setReportes] = useState([]);
  const [cargandoReportes, setCargandoReportes] = useState(false);
  const [filtroEstadoRep, setFiltroEstadoRep] = useState("pendiente");
  const [perfilesReportados, setPerfilesReportados] = useState({}); // id → perfil
  // Métricas DMs
  const [metricasDms, setMetricasDms] = useState([]);
  const [cargandoMetricasDms, setCargandoMetricasDms] = useState(false);
  // Métricas para sponsors
  const [metricas, setMetricas] = useState(null);
  const [cargandoMetricas, setCargandoMetricas] = useState(false);
  // Reclamos de fichas de negocio (verificación por WhatsApp desde el número de la ficha)
  const [reclamos, setReclamos] = useState([]);
  const [cargandoReclamos, setCargandoReclamos] = useState(false);
  const [telReclamo, setTelReclamo] = useState({}); // id → teléfono desde el que escribió
  const cargarReclamos = async () => {
    setCargandoReclamos(true);
    const { data } = await supabase.from("reclamos_negocio")
      .select("id, codigo, estado, telefono_visto, created_at, negocio:negocios(id, nombre, ciudad, whatsapp, telefono), perfil:perfiles(username)")
      .order("created_at", { ascending: false }).limit(100);
    setReclamos(data || []);
    setCargandoReclamos(false);
  };
  const resolverReclamo = async (r, forzar) => {
    const tel = (telReclamo[r.id] || "").trim();
    if (!forzar && !tel) { alert("Poné el número desde el que escribió (como aparece en WhatsApp)."); return; }
    const { data: raw, error } = await supabase.rpc("resolver_reclamo_negocio", { p_codigo: r.codigo, p_telefono: tel || null, p_forzar: !!forzar });
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || d?.error) { alert("Error: " + (error?.message || d?.error)); return; }
    if (d?.ok) alert(`✓ ${r.negocio?.nombre} quedó verificado y a nombre de @${r.perfil?.username}.`);
    else alert(`El número no coincide con el de la ficha (${r.negocio?.whatsapp || r.negocio?.telefono || "sin teléfono"}). Quedó anotado; si igual es el dueño, usá «Aprobar igual».`);
    cargarReclamos();
  };
  const rechazarReclamo = async (r) => {
    if (!window.confirm(`¿Rechazar el reclamo de @${r.perfil?.username} sobre ${r.negocio?.nombre}?`)) return;
    await supabase.rpc("rechazar_reclamo_negocio", { p_id: r.id }).then(() => {}, () => {});
    cargarReclamos();
  };

  const cargarMetricas = async () => {
    setCargandoMetricas(true);
    const { data: raw, error } = await supabase.rpc("admin_metricas");
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    setMetricas(error ? { error: error.message } : data);
    setCargandoMetricas(false);
  };

  const cargarMetricasDms = async () => {
    setCargandoMetricasDms(true);
    const { data: raw } = await supabase.rpc("admin_metricas_dms");
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data && !data.error) setMetricasDms(data.metricas || []);
    setCargandoMetricasDms(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const categorias = [
    { id: "taller", label: "🔧 Taller" },
    { id: "concesionario", label: "🏪 Concesionario" },
    { id: "gromeria", label: "🛞 Gomería" },
    { id: "alquiler", label: "🗺️ Alquiler" },
    { id: "accesorios", label: "🛡️ Accesorios" },
    { id: "grua", label: "🚛 Grúa" },
    { id: "seguro", label: "📋 Seguros" },
    { id: "gestoria", label: "📄 Gestoría / Trámites" },
    { id: "marca", label: "🏍️ Marca" },
    { id: "motoescuela", label: "🎓 Motoescuela" },
    { id: "pista", label: "🏁 Pista" },
    { id: "restaurante", label: "🍽️ Restaurante" },
    { id: "hospedaje", label: "🛏️ Hospedaje" },
  ];

  useEffect(() => {
    cargarStats();
  }, []);

  useEffect(() => {
    if (tabAdmin !== "stats" && tabAdmin !== "referidos" && tabAdmin !== "reportes" && tabAdmin !== "reclamos") cargarNegocios();
    if (tabAdmin === "referidos") cargarCodigos();
    if (tabAdmin === "reportes") cargarReportes();
    if (tabAdmin === "reclamos") cargarReclamos();
    if (tabAdmin === "dms") cargarMetricasDms();
    if (tabAdmin === "metricas" && !metricas) cargarMetricas();
  }, [tabAdmin, filtroEstadoRep]);

  const cargarReportes = async () => {
    setCargandoReportes(true);
    const { data: raw, error } = await supabase.rpc("admin_get_reportes", { p_estado: filtroEstadoRep });
    if (error) {
      console.error("Error cargando reportes:", error);
      setCargandoReportes(false);
      return;
    }
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (data?.error) {
      console.error(data.error);
      setCargandoReportes(false);
      return;
    }
    const lista = data?.reportes || [];
    setReportes(lista);

    // Cargar perfiles reportados
    if (lista.length > 0) {
      const userIds = [...new Set(lista.flatMap(r => [r.reporter_id, r.reported_id]).filter(Boolean))];
      const { data: perfiles } = await supabase.from("perfiles").select("id, username, avatar_url").in("id", userIds);
      const map = {};
      perfiles?.forEach(p => { map[p.id] = p; });
      setPerfilesReportados(map);
    }
    setCargandoReportes(false);
  };

  const cambiarEstadoReporte = async (reporteId, nuevoEstado, notas = null) => {
    const { error } = await supabase.rpc("admin_actualizar_reporte", {
      p_reporte_id: reporteId,
      p_estado: nuevoEstado,
      p_notas: notas,
    });
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    cargarReportes();
  };

  const cargarStats = async () => {
    setCargandoStats(true);
    const hoy = new Date();
    const hace7dias = new Date(hoy - 7 * 24 * 60 * 60 * 1000).toISOString();
    const hace30dias = new Date(hoy - 30 * 24 * 60 * 60 * 1000).toISOString();

    const [
      { count: totalPerfiles },
      { count: perfilesHoy },
      { count: perfiles7d },
      { count: totalPosts },
      { count: posts7d },
      { count: totalNegocios },
      { count: negociosPendientes },
      { count: totalLikes },
      { count: totalComentarios },
      { data: ultimosPerfiles },
      { data: ultimosPosts },
    ] = await Promise.all([
      supabase.from("perfiles").select("*", { count: "exact", head: true }),
      // "Hoy" = desde la medianoche LOCAL del que mira el panel (created_at es timestamp)
      supabase.from("perfiles").select("*", { count: "exact", head: true }).gte("created_at", (() => { const m = new Date(); m.setHours(0, 0, 0, 0); return m.toISOString(); })()),
      supabase.from("perfiles").select("*", { count: "exact", head: true }).gte("created_at", hace7dias),
      supabase.from("posts").select("*", { count: "exact", head: true }),
      supabase.from("posts").select("*", { count: "exact", head: true }).gte("created_at", hace7dias),
      supabase.from("negocios").select("*", { count: "exact", head: true }),
      supabase.from("negocios").select("*", { count: "exact", head: true }).eq("estado", "pendiente"),
      supabase.from("likes").select("*", { count: "exact", head: true }),
      supabase.from("comentarios").select("*", { count: "exact", head: true }),
      supabase.from("perfiles").select("username, ciudad, created_at").order("created_at", { ascending: false }).limit(10),
      supabase.from("posts").select("username, texto, categoria, created_at").order("created_at", { ascending: false }).limit(10),
    ]);

    // Visualizaciones: se leen de los contadores de cada tabla (las tablas *_vistas
    // solo las ve el dueño de cada cosa, el contador en cambio es público).
    const [{ data: vPosts }, { data: vEventos }, { data: vNegocios }] = await Promise.all([
      supabase.from("posts").select("vistas"),
      supabase.from("eventos").select("id, titulo, fecha, vistas").order("vistas", { ascending: false }).limit(200),
      supabase.from("negocios").select("id, nombre, ciudad, categoria, vistas").order("vistas", { ascending: false }).limit(200),
    ]);
    const sumar = (arr) => (arr || []).reduce((a, x) => a + Number(x.vistas || 0), 0);
    // Serie temporal por RPC: las tablas de detalle tienen RLS cerrada
    const { data: serieRaw } = await supabase.rpc("metricas_vistas", { p_dias: 14 });
    const serie = typeof serieRaw === "string" ? JSON.parse(serieRaw) : serieRaw;
    setVistas({
      posts: sumar(vPosts),
      eventos: sumar(vEventos),
      negocios: sumar(vNegocios),
      topEventos: (vEventos || []).filter(e => Number(e.vistas) > 0).slice(0, 5),
      topNegocios: (vNegocios || []).filter(n => Number(n.vistas) > 0).slice(0, 5),
      serie: serie && !serie.error ? serie : null,
    });

    setStats({ totalPerfiles, perfilesHoy, perfiles7d, totalPosts, posts7d, totalNegocios, negociosPendientes, totalLikes, totalComentarios });
    setUsuariosRecientes(ultimosPerfiles || []);
    setPostsRecientes(ultimosPosts || []);
    setCargandoStats(false);
  };

  const cargarNegocios = async () => {
    setCargando(true);
    let query = supabase.from("negocios").select("*").order("created_at", { ascending: false });
    if (tabAdmin === "pendientes") query = query.eq("estado", "pendiente");
    if (tabAdmin === "verificados") query = query.eq("estado", "verificado");
    const { data } = await query;
    if (data) setNegocios(data);
    setCargando(false);
  };

  // REFERIDOS
  const cargarCodigos = async () => {
    setCargandoRefs(true);
    const { data: cods } = await supabase.from("codigos_referidos")
      .select("*")
      .order("created_at", { ascending: false });
    setCodigos(cods || []);

    // Contar cuántos perfiles tiene cada código
    if (cods && cods.length > 0) {
      const counts = {};
      for (const c of cods) {
        const { count } = await supabase.from("perfiles")
          .select("id", { count: "exact", head: true })
          .eq("referido_por", c.codigo);
        counts[c.codigo] = count || 0;
      }
      setStatsReferidos(counts);
    }
    setCargandoRefs(false);
  };

  const generarCodigoDesdeUsername = (u) => {
    if (!u) return "";
    // Limpiar: solo letras, números, mayúsculas, sin caracteres especiales
    return u.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 20);
  };

  const guardarCodigo = async () => {
    const codigoLimpio = generarCodigoDesdeUsername(nuevoCodigo.codigo || nuevoCodigo.username);
    if (!codigoLimpio || !nuevoCodigo.username) {
      alert("Necesitás un username o código válido");
      return;
    }
    // Buscar user_id si existe el username en perfiles
    const { data: perfil } = await supabase.from("perfiles")
      .select("id")
      .eq("username", nuevoCodigo.username)
      .single();

    const payload = {
      codigo: codigoLimpio,
      user_id: perfil?.id || null,
      username: nuevoCodigo.username,
      nombre_completo: nuevoCodigo.nombre_completo || null,
      beneficios: nuevoCodigo.beneficios || null,
      activo: true,
      updated_at: new Date().toISOString(),
    };

    if (editandoCodigo) {
      const { error } = await supabase.from("codigos_referidos").update(payload).eq("id", editandoCodigo.id);
      if (error) { alert("Error: " + error.message); return; }
    } else {
      const { error } = await supabase.from("codigos_referidos").insert(payload);
      if (error) {
        if (error.code === "23505") alert("Ya existe ese código. Probá con otro.");
        else alert("Error: " + error.message);
        return;
      }
    }
    setShowCrearCodigo(false);
    setEditandoCodigo(null);
    setNuevoCodigo({ codigo: "", username: "", nombre_completo: "", beneficios: "" });
    cargarCodigos();
  };

  const toggleActivoCodigo = async (c) => {
    await supabase.from("codigos_referidos")
      .update({ activo: !c.activo, updated_at: new Date().toISOString() })
      .eq("id", c.id);
    cargarCodigos();
  };

  const eliminarCodigo = async (c) => {
    if (!window.confirm(`¿Eliminar el código ${c.codigo}? Esta acción no se puede deshacer.`)) return;
    await supabase.from("codigos_referidos").delete().eq("id", c.id);
    cargarCodigos();
  };

  const copiarLink = (codigo) => {
    const url = `https://olbos.ai/?ref=${codigo}`;
    navigator.clipboard.writeText(url);
    alert(`Link copiado: ${url}`);
  };

  const cambiarEstado = async (id, estado) => {
    // Vía RPC de admin: el update directo del cliente lo bloquea RLS en silencio
    const { data: raw, error } = await supabase.rpc("admin_set_negocio_estado", { p_id: id, p_estado: estado });
    const r = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (error || r?.error) { alert("No se pudo cambiar el estado: " + (error?.message || r?.error)); return; }
    cargarNegocios();
  };

  const crearNegocio = async () => {
    if (!form.nombre || !form.categoria) return;
    setGuardando(true);
    await supabase.from("negocios").insert({
      ...form, categoria: normalizarCategoria(form.categoria), estado: "verificado", plan: "gratis", admin_created: true
    });
    setGuardando(false);
    setShowCrear(false);
    setForm({ nombre: "", categoria: "", descripcion: "", ciudad: "", direccion: "", telefono: "", whatsapp: "", web: "", instagram: "", horarios: "" });
    cargarNegocios();
  };

  const tiempoAtras = (fecha) => {
    const diff = (Date.now() - new Date(fecha)) / 1000 / 60;
    if (diff < 60) return `hace ${Math.floor(diff)}m`;
    if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
    return `hace ${Math.floor(diff / 1440)}d`;
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 400, display: "flex", flexDirection: "column", maxWidth: esAgencia ? 1100 : 430, margin: "0 auto" }}>
      {/* header */}
      <div style={{ padding: "max(48px, calc(var(--sat) + 12px)) 18px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ChevronLeft size={20} strokeWidth={2} color={C.text} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{esAgencia ? "Agencia · Olbos" : "Admin · Olbos"}</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{esAgencia ? "Métricas y campañas" : "Panel de Control"}</h2>
        </div>
        {!esAgencia && tabAdmin !== "stats" && (
          <button onClick={() => setShowCrear(true)} style={{ padding: "8px 14px", borderRadius: 20, background: C.text, color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer" }}>
            + Agregar
          </button>
        )}
      </div>

      {/* tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, overflowX: "auto" }}>
        {[["stats", "📊 Stats"], ["metricas", "📈 Métricas"], ["ingresos", "💰 Ingresos"], ["acciones", "🎯 Acciones"], ["anuncios", "📺 Anuncios"], ["mails", "📣 Mails"], ["invitar", "🎟️ Invitar"], ["pendientes", "⏳ Pendientes"], ["verificados", "✓ Negocios"], ["reclamos", "🏷️ Reclamos"], ["todos", "Todos"], ["referidos", "🔗 Referidos"], ["reportes", "🚩 Reportes"], ["dms", "💬 DMs"]]
          .filter(([id]) => !esAgencia || ["stats", "metricas", "ingresos", "acciones", "anuncios", "mails"].includes(id)).map(([id, label]) => (
          <button key={id} onClick={() => setTabAdmin(id)}
            style={{ flexShrink: 0, padding: "11px 14px", fontSize: 12, fontWeight: tabAdmin === id ? 700 : 500, color: tabAdmin === id ? C.text : C.muted, background: "none", border: "none", borderBottom: tabAdmin === id ? `2px solid ${C.text}` : "2px solid transparent", cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      {/* contenido */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 80px" }}>

        {/* INVITAR PERFIL (pre-llenado) */}
        {tabAdmin === "acciones" && <AccionesTab />}
        {tabAdmin === "anuncios" && <AnunciosTab />}
        {/* El numero que decide en marzo 2027: ingresos cobrados por mes (la agencia lo ve en lectura) */}
        {tabAdmin === "ingresos" && <IngresosTab soloLectura={esAgencia} />}
        {/* La agencia es el equipo de marketing: escribe y programa los mails a la base */}
        {tabAdmin === "mails" && <MailsAdminTab />}
        {/* El panel de agencia se usa desde la compu: en el teléfono solo se avisa */}
        {esAgencia && !enPc && (
          <div style={{ position: "absolute", inset: 0, top: 120, background: C.card, padding: 28, textAlign: "center", zIndex: 5 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>💻</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>Este panel se usa desde la compu</div>
            <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.55 }}>Entrá a olbos.ai desde una laptop o un monitor: las tablas y los gráficos necesitan ancho.</div>
          </div>
        )}
        {tabAdmin === "invitar" && <InvitarPerfilTab />}

        {/* MÉTRICAS PARA SPONSORS */}
        {tabAdmin === "metricas" && <MetricasSponsors data={metricas} cargando={cargandoMetricas} onRecargar={cargarMetricas} />}

        {/* STATS */}
        {tabAdmin === "stats" && (
          cargandoStats ? (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Cargando estadísticas...</div>
          ) : (
            <div>
              {/* métricas principales */}
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Comunidad</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Riders totales", val: stats.totalPerfiles, sub: `${stats.perfilesHoy || 0} hoy`, color: C.text },
                  { label: "Nuevos esta semana", val: stats.perfiles7d, sub: "últimos 7 días", color: C.green },
                  { label: "Posts totales", val: stats.totalPosts, sub: `${stats.posts7d || 0} esta semana`, color: C.accent },
                  { label: "Interacciones", val: (stats.totalLikes || 0) + (stats.totalComentarios || 0), sub: `${stats.totalLikes || 0} likes · ${stats.totalComentarios || 0} comentarios`, color: "#E1306C" },
                ].map(s => (
                  <div key={s.label} style={{ background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: s.color, letterSpacing: -1, marginBottom: 2 }}>{s.val ?? "—"}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* VISUALIZACIONES — lo que la gente mira, que no es lo mismo que lo
                  que interactúa. Los contadores arrancaron el 15-ago-2026. */}
              {vistas && (vistas.posts + vistas.eventos + vistas.negocios) > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Qué se está mirando</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                    {[
                      { label: "Posts", val: vistas.posts },
                      { label: "Eventos", val: vistas.eventos },
                      { label: "Servicios", val: vistas.negocios },
                    ].map(s => (
                      <div key={s.label} style={{ background: C.surface, borderRadius: 14, padding: "12px 10px", border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 24, fontWeight: 900, color: C.text, letterSpacing: -0.8, marginBottom: 2 }}>{s.val.toLocaleString("es-AR")}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{s.label}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>visitas</div>
                      </div>
                    ))}
                  </div>

                  {/* Últimos 7 días contra los 7 anteriores: la pregunta no es
                      cuántas visitas hay, es si vienen subiendo o bajando */}
                  {vistas.serie && (() => {
                    const s = vistas.serie;
                    const act = (s.ultimos7?.posts || 0) + (s.ultimos7?.eventos || 0) + (s.ultimos7?.negocios || 0);
                    const prev = (s.previos7?.posts || 0) + (s.previos7?.eventos || 0) + (s.previos7?.negocios || 0);
                    const dias = Array.isArray(s.serie) ? s.serie : [];
                    const totalDia = (d) => (d.posts || 0) + (d.eventos || 0) + (d.negocios || 0);
                    const tope = Math.max(1, ...dias.map(totalDia));
                    const variacion = prev > 0 ? Math.round(((act - prev) / prev) * 100) : null;
                    return (
                      <div style={{ background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                          <span style={{ fontSize: 22, fontWeight: 900, color: C.text, letterSpacing: -0.6 }}>{act.toLocaleString("es-AR")}</span>
                          <span style={{ fontSize: 11.5, color: C.muted, flex: 1 }}>visitas en los últimos 7 días</span>
                          {variacion !== null && (
                            <span style={{ fontSize: 12, fontWeight: 800, color: variacion >= 0 ? C.green : C.red }}>
                              {variacion >= 0 ? "↑" : "↓"} {Math.abs(variacion)}%
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 54 }}>
                          {dias.map(d => {
                            const t = totalDia(d);
                            return (
                              <div key={d.dia} title={`${d.dia}: ${t}`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                                <div style={{ height: `${Math.max(3, (t / tope) * 100)}%`, background: t > 0 ? C.text : C.border, borderRadius: 3 }} />
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted, marginTop: 5 }}>
                          <span>hace {dias.length} días</span><span>hoy</span>
                        </div>
                      </div>
                    );
                  })()}

                  {vistas.topEventos.length > 0 && (
                    <div style={{ background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.text, marginBottom: 8 }}>Eventos más vistos</div>
                      {vistas.topEventos.map(e => (
                        <div key={e.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
                          <span style={{ fontSize: 12.5, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.titulo}</span>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text, flexShrink: 0 }}>{Number(e.vistas).toLocaleString("es-AR")}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {vistas.topNegocios.length > 0 && (
                    <div style={{ background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${C.border}`, marginBottom: 20 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.text, marginBottom: 4 }}>Fichas más vistas</div>
                      <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>Sirve para el outreach: al que tiene visitas, mostrarle el número.</div>
                      {vistas.topNegocios.map(n => (
                        <div key={n.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}>
                          <span style={{ fontSize: 12.5, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.nombre} <span style={{ color: C.muted }}>· {n.ciudad || n.categoria}</span>
                          </span>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text, flexShrink: 0 }}>{Number(n.vistas).toLocaleString("es-AR")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Negocios</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Negocios totales", val: stats.totalNegocios, color: C.text },
                  { label: "Pendientes de revisión", val: stats.negociosPendientes, color: C.accent },
                ].map(s => (
                  <div key={s.label} style={{ background: C.surface, borderRadius: 14, padding: 14, border: `1px solid ${s.val > 0 && s.color === C.accent ? C.accent + "33" : C.border}` }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: s.color, letterSpacing: -1, marginBottom: 2 }}>{s.val ?? "—"}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* últimos usuarios */}
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Últimos riders</div>
              {usuariosRecientes.map((u, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: C.surface, borderRadius: 10, marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>@{u.username || "sin username"}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{u.ciudad || "sin ciudad"}</div>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>{tiempoAtras(u.created_at)}</div>
                </div>
              ))}

              {/* últimos posts */}
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, marginTop: 20 }}>Últimas publicaciones</div>
              {postsRecientes.map((p, i) => (
                <div key={i} style={{ padding: "10px 14px", background: C.surface, borderRadius: 10, marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>@{p.username}</span>
                    <span style={{ fontSize: 11, color: C.muted }}>{tiempoAtras(p.created_at)}</span>
                  </div>
                  {p.categoria && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6, background: C.accentLo, color: C.accent, marginBottom: 4, display: "inline-block" }}>{p.categoria}</span>}
                  {p.texto && <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.texto}</div>}
                </div>
              ))}

              <button onClick={cargarStats} style={{ width: "100%", marginTop: 16, padding: "12px 0", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                🔄 Actualizar estadísticas
              </button>
            </div>
          )
        )}

        {/* NEGOCIOS */}
        {tabAdmin !== "stats" && tabAdmin !== "referidos" && (
          cargando ? (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Cargando...</div>
          ) : negocios.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>No hay negocios aquí</div>
          ) : negocios.map(n => (
            <div key={n.id} style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2 }}>{n.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{n.categoria} · {n.ciudad}</div>
                  {n.direccion && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>📍 {n.direccion}</div>}
                  {n.telefono && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>📞 {n.telefono}</div>}
                  {n.whatsapp && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>💬 {n.whatsapp}</div>}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: n.estado === "verificado" ? `${C.green}14` : `${C.gold}14`, color: n.estado === "verificado" ? C.green : C.accent, flexShrink: 0 }}>
                  {n.estado}
                </span>
              </div>
              {n.descripcion && <p style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>{n.descripcion}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                {n.estado !== "verificado" && (
                  <button onClick={() => cambiarEstado(n.id, "verificado")}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: `${C.green}14`, border: `1px solid ${C.green}44`, color: C.green, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    ✓ Verificar
                  </button>
                )}
                {n.estado === "verificado" && (
                  <button onClick={() => cambiarEstado(n.id, "pendiente")}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    Suspender
                  </button>
                )}
                {n.whatsapp && (
                  <a href={`https://wa.me/${n.whatsapp}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: "#25D36615", border: "1px solid #25D36633", color: "#128C7E", fontSize: 12, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          ))
        )}

        {/* REFERIDOS */}
        {tabAdmin === "referidos" && (
          <div>
            {/* Botón crear */}
            <button onClick={() => {
              setEditandoCodigo(null);
              setNuevoCodigo({ codigo: "", username: "", nombre_completo: "", beneficios: "" });
              setShowCrearCodigo(true);
            }} style={{
              width: "100%", padding: "12px", borderRadius: 12,
              background: C.accent, color: "#fff",
              border: "none", cursor: "pointer", marginBottom: 16,
              fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <Plus size={16} strokeWidth={2} /> Crear nuevo código
            </button>

            {/* Resumen */}
            {codigos.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                <div style={{ background: C.surface, borderRadius: 12, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{codigos.length}</div>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>códigos</div>
                </div>
                <div style={{ background: C.surface, borderRadius: 12, padding: 12, border: `1px solid ${C.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>
                    {Object.values(statsReferidos).reduce((a, b) => a + b, 0)}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>riders traídos</div>
                </div>
              </div>
            )}

            {/* Lista de códigos */}
            {cargandoRefs ? (
              <div style={{ textAlign: "center", padding: 30, color: C.muted, fontSize: 13 }}>Cargando códigos…</div>
            ) : codigos.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 24px" }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                  <Share2 size={22} strokeWidth={1.5} color={C.muted} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.015em" }}>Sin códigos creados</h3>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>Creá el primer código para un influencer o referente.</p>
              </div>
            ) : (
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                {codigos.map((c, i) => (
                  <div key={c.id} style={{
                    padding: "12px 14px",
                    borderBottom: i < codigos.length - 1 ? `1px solid ${C.border}` : "none",
                    opacity: c.activo ? 1 : 0.55,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <span style={{
                            fontFamily: "monospace", fontSize: 13, fontWeight: 700,
                            color: C.text, letterSpacing: "0.02em",
                            padding: "2px 8px", borderRadius: 6,
                            background: `${C.gold}14`, border: "1px solid #FFE89A",
                          }}>{c.codigo}</span>
                          {!c.activo && (
                            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 100, background: C.surface, color: C.muted, textTransform: "uppercase" }}>Inactivo</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: C.text, fontWeight: 500, marginTop: 4 }}>
                          {c.nombre_completo || `@${c.username}`}
                        </div>
                        {c.nombre_completo && (
                          <div style={{ fontSize: 11, color: C.muted }}>@{c.username}</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", lineHeight: 1 }}>
                          {statsReferidos[c.codigo] || 0}
                        </div>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 }}>riders</div>
                      </div>
                    </div>

                    {c.beneficios && (
                      <div style={{ padding: "6px 10px", background: C.surface, borderRadius: 8, fontSize: 11.5, color: C.text, lineHeight: 1.4, marginBottom: 8, letterSpacing: "-0.005em" }}>
                        <strong style={{ fontWeight: 600 }}>Beneficios:</strong> {c.beneficios}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => copiarLink(c.codigo)} style={{
                        flex: 1, padding: "6px 0", borderRadius: 8,
                        background: C.accent, color: "#fff",
                        border: "none", cursor: "pointer",
                        fontSize: 11, fontWeight: 500, letterSpacing: "-0.005em",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                      }}>
                        Copiar link
                      </button>
                      <button onClick={() => {
                        setEditandoCodigo(c);
                        setNuevoCodigo({ codigo: c.codigo, username: c.username, nombre_completo: c.nombre_completo || "", beneficios: c.beneficios || "" });
                        setShowCrearCodigo(true);
                      }} style={{
                        padding: "6px 10px", borderRadius: 8,
                        background: C.surface, color: C.text,
                        border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500,
                      }}>
                        Editar
                      </button>
                      <button onClick={() => toggleActivoCodigo(c)} style={{
                        padding: "6px 10px", borderRadius: 8,
                        background: c.activo ? C.surface : "#27AE60",
                        color: c.activo ? "#111111" : "#fff",
                        border: "none", cursor: "pointer", fontSize: 11, fontWeight: 500,
                      }}>
                        {c.activo ? "Pausar" : "Activar"}
                      </button>
                      <button onClick={() => eliminarCodigo(c)} style={{
                        padding: "6px 10px", borderRadius: 8,
                        background: "#FFF1F0", color: "#FF3B30",
                        border: "none", cursor: "pointer",
                      }}>
                        <Trash2 size={11} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* RECLAMOS DE FICHAS: el dueño mandó "OLBOS-VERIFICA <código>" por WhatsApp desde
            el número del negocio. Se pega el número desde el que escribió y «Verificar»
            compara contra la ficha (últimos 8 dígitos). Lo hace el robot cada 30 min o
            el admin en un toque. */}
        {tabAdmin === "reclamos" && (
          <div>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
              Cada reclamo trae un código. El dueño lo manda por WhatsApp <strong>desde el número del negocio</strong>. Pegá acá el número que escribió y tocá Verificar: si coincide con la ficha, queda a su nombre.
            </p>
            {cargandoReclamos ? (
              <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>Cargando…</div>
            ) : reclamos.length === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center", color: C.muted, fontSize: 13 }}>Sin reclamos todavía.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {reclamos.map(r => (
                  <div key={r.id} style={{ padding: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, opacity: r.estado === "pendiente" ? 1 : 0.6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{r.negocio?.nombre || "(negocio)"} <span style={{ fontWeight: 400, color: C.muted }}>· {r.negocio?.ciudad || ""}</span></div>
                      <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: r.estado === "verificado" ? `${C.green}14` : r.estado === "rechazado" ? `${C.red}14` : `${C.gold}14`, color: r.estado === "verificado" ? C.green : r.estado === "rechazado" ? C.red : C.gold }}>{r.estado}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                      Código <strong style={{ color: C.text }}>{r.codigo}</strong> · pide @{r.perfil?.username || "?"} · {new Date(r.created_at).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}<br />
                      Teléfono de la ficha: <strong style={{ color: C.text }}>{r.negocio?.whatsapp || r.negocio?.telefono || "no tiene"}</strong>{r.telefono_visto ? ` · escribió desde ${r.telefono_visto}` : ""}
                    </div>
                    {r.estado === "pendiente" && (
                      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                        <input value={telReclamo[r.id] || ""} onChange={e => setTelReclamo(t => ({ ...t, [r.id]: e.target.value }))} placeholder="Número desde el que escribió"
                          style={{ flex: "1 1 160px", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12.5, background: C.surface, color: C.text }} />
                        <button onClick={() => resolverReclamo(r, false)} style={{ padding: "8px 12px", borderRadius: 8, background: C.text, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Verificar</button>
                        <button onClick={() => resolverReclamo(r, true)} style={{ padding: "8px 12px", borderRadius: 8, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Aprobar igual</button>
                        <button onClick={() => rechazarReclamo(r)} style={{ padding: "8px 12px", borderRadius: 8, background: "none", color: C.red, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Rechazar</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REPORTES */}
        {tabAdmin === "reportes" && (
          <div>
            {/* Filtro por estado */}
            <div style={{ display: "flex", gap: 4, padding: 3, background: C.surface, borderRadius: 10, marginBottom: 16 }}>
              {[
                ["pendiente", "Pendientes"],
                ["revisado", "Revisados"],
                ["accion_tomada", "Con acción"],
                ["descartado", "Descartados"],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setFiltroEstadoRep(id)} style={{
                  flex: 1, padding: "7px 0",
                  background: filtroEstadoRep === id ? C.card : "transparent",
                  border: "none", borderRadius: 8,
                  color: filtroEstadoRep === id ? "#111111" : C.muted,
                  fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                  letterSpacing: "-0.005em",
                  boxShadow: filtroEstadoRep === id ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                }}>{label}</button>
              ))}
            </div>

            {cargandoReportes ? (
              <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>Cargando reportes…</div>
            ) : reportes.length === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                  <span style={{ fontSize: 24 }}>🚩</span>
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Sin reportes</h3>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}>
                  No hay reportes en estado "{filtroEstadoRep}".
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {reportes.map(r => {
                  const reporter = perfilesReportados[r.reporter_id];
                  const reportado = perfilesReportados[r.reported_id];
                  const fecha = new Date(r.created_at).toLocaleString("es-AR", {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  });
                  return (
                    <div key={r.id} style={{
                      padding: "14px",
                      background: C.card,
                      border: `1px solid ${C.border}`,
                      borderRadius: 12,
                    }}>
                      {/* Cabecera */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: 100,
                            background: r.tipo === "usuario" ? "#FFE9E5" : r.tipo === "mensaje" ? "#FFF4D6" : "#E6F4FF",
                            color: r.tipo === "usuario" ? "#B82F26" : r.tipo === "mensaje" ? "#8B6A00" : "#111111",
                            fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                          }}>{r.tipo}</span>
                          <span style={{ fontSize: 11, color: C.muted, letterSpacing: "-0.005em" }}>{fecha}</span>
                        </div>
                      </div>

                      {/* Reporte */}
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: C.text, marginBottom: 2, letterSpacing: "-0.005em" }}>
                          Motivo: {r.motivo}
                        </div>
                        {r.descripcion && (
                          <div style={{ fontSize: 12.5, color: C.text, marginTop: 6, padding: "8px 10px", background: C.surface, borderRadius: 8, letterSpacing: "-0.005em", lineHeight: 1.4 }}>
                            "{r.descripcion}"
                          </div>
                        )}
                      </div>

                      {/* Usuarios involucrados */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted }}>
                          <span style={{ fontWeight: 500 }}>Reportador:</span>
                          <span style={{ color: C.text, fontWeight: 600 }}>@{reporter?.username || r.reporter_id?.slice(0, 8) || "?"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted }}>
                          <span style={{ fontWeight: 500 }}>Reportado:</span>
                          <span style={{ color: "#B82F26", fontWeight: 600 }}>@{reportado?.username || r.reported_id?.slice(0, 8) || "?"}</span>
                        </div>
                      </div>

                      {/* Notas admin previas */}
                      {r.notas_admin && (
                        <div style={{ padding: "8px 10px", background: `${C.gold}14`, border: "1px solid #FFE89A", borderRadius: 8, marginBottom: 10, fontSize: 11.5, color: C.gold, lineHeight: 1.4 }}>
                          <strong>Notas admin:</strong> {r.notas_admin}
                        </div>
                      )}

                      {/* Acciones (solo si está pendiente) */}
                      {r.estado === "pendiente" && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button onClick={() => cambiarEstadoReporte(r.id, "revisado")} style={{
                            flex: 1, minWidth: 90, padding: "8px 0", borderRadius: 8,
                            background: C.surface, color: C.text, border: "none",
                            cursor: "pointer", fontSize: 12, fontWeight: 500,
                          }}>👁️ Revisado</button>
                          <button onClick={() => {
                            const notas = window.prompt("¿Qué acción tomaste? (opcional)");
                            cambiarEstadoReporte(r.id, "accion_tomada", notas);
                          }} style={{
                            flex: 1, minWidth: 90, padding: "8px 0", borderRadius: 8,
                            background: "#FFE9E5", color: "#B82F26", border: "none",
                            cursor: "pointer", fontSize: 12, fontWeight: 600,
                          }}>⚠️ Acción</button>
                          <button onClick={() => cambiarEstadoReporte(r.id, "descartado")} style={{
                            flex: 1, minWidth: 90, padding: "8px 0", borderRadius: 8,
                            background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
                            cursor: "pointer", fontSize: 12, fontWeight: 500,
                          }}>Descartar</button>
                        </div>
                      )}

                      {/* Estado actual si no es pendiente */}
                      {r.estado !== "pendiente" && (
                        <div style={{ fontSize: 11.5, color: C.muted, padding: "6px 0", letterSpacing: "-0.005em" }}>
                          Estado: <strong style={{ color: C.text }}>{r.estado}</strong>
                          {r.resuelto_at && ` · ${new Date(r.resuelto_at).toLocaleDateString("es-AR")}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB: DMs Métricas */}
        {tabAdmin === "dms" && (
          <div>
            <div style={{ marginBottom: 16, padding: "12px 14px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <h3 style={{ fontSize: 13.5, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.005em" }}>
                Tracking de DMs del bot @olbos
              </h3>
              <p style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
                Si <strong>tasa apertura &gt; 50%</strong> y <strong>tasa acción &gt; 20%</strong> los DMs internos están funcionando bien.
                Si están por debajo, vale la pena evaluar WhatsApp Business API.
              </p>
            </div>

            {cargandoMetricasDms ? (
              <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>Cargando…</div>
            ) : metricasDms.length === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}>
                  Todavía no hay datos. Los riders tienen que recibir y abrir DMs del bot para empezar a medir.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {metricasDms.map((m, idx) => {
                  const semana = new Date(m.semana);
                  const semanaLabel = semana.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" });
                  const tasaApColor = m.tasa_apertura >= 50 ? "#1F8347" : m.tasa_apertura >= 25 ? "#8B6A00" : "#B82F26";
                  const tasaAcColor = m.tasa_accion >= 20 ? "#1F8347" : m.tasa_accion >= 10 ? "#8B6A00" : "#B82F26";
                  return (
                    <div key={idx} style={{ padding: "12px 14px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>
                          Semana del {semanaLabel}
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Enviados</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: C.text }}>{m.enviados}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Abiertos</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: C.text }}>{m.abiertos}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: tasaApColor, marginTop: 1 }}>{m.tasa_apertura || 0}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Con acción</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: C.text }}>{m.con_accion}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: tasaAcColor, marginTop: 1 }}>{m.tasa_accion || 0}%</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal crear/editar código */}
      {showCrearCodigo && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 600, display: "flex", alignItems: "flex-end" }}
          onClick={e => e.target === e.currentTarget && setShowCrearCodigo(false)}>
          <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", maxHeight: "90vh", overflowY: "auto", padding: "20px 20px calc(28px + var(--sab))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, letterSpacing: "-0.01em" }}>
                {editandoCodigo ? "Editar código" : "Nuevo código"}
              </h3>
              <button onClick={() => setShowCrearCodigo(false)} style={{
                width: 28, height: 28, borderRadius: "50%",
                background: C.surface, border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                Username del influencer (sin @)
              </label>
              <input type="text" value={nuevoCodigo.username}
                onChange={e => setNuevoCodigo(n => ({
                  ...n,
                  username: e.target.value,
                  codigo: generarCodigoDesdeUsername(e.target.value),
                }))}
                placeholder="maccmix_motovlog"
                style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit" }}
                disabled={!!editandoCodigo} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                Código (generado, podés editarlo)
              </label>
              <input type="text" value={nuevoCodigo.codigo}
                onChange={e => setNuevoCodigo(n => ({ ...n, codigo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))}
                placeholder="MACCMIXMOTOVLOG"
                style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "monospace", letterSpacing: "0.05em" }}
                disabled={!!editandoCodigo} />
              {nuevoCodigo.codigo && (
                <p style={{ fontSize: 11, color: C.muted, marginTop: 6, letterSpacing: "-0.005em" }}>
                  Link: <span style={{ color: C.text }}>https://olbos.ai/?ref={nuevoCodigo.codigo}</span>
                </p>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                Nombre completo (opcional)
              </label>
              <input type="text" value={nuevoCodigo.nombre_completo}
                onChange={e => setNuevoCodigo(n => ({ ...n, nombre_completo: e.target.value }))}
                placeholder="Maciel Diaz (Macmix Motovlog)"
                style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>
                Beneficios negociados (para tu uso interno)
              </label>
              <textarea value={nuevoCodigo.beneficios}
                onChange={e => setNuevoCodigo(n => ({ ...n, beneficios: e.target.value }))}
                placeholder="Ej: 10% comisión sobre productos ML vendidos, sponsoreo video Q3, equipamiento de marcas..."
                rows={3}
                style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5, resize: "vertical" }} />
            </div>

            <button onClick={guardarCodigo}
              style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14.5, fontWeight: 500, cursor: "pointer", letterSpacing: "-0.01em" }}>
              {editandoCodigo ? "Guardar cambios" : "Crear código"}
            </button>
          </div>
        </div>
      )}

      {/* modal crear negocio */}
      {showCrear && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 500, display: "flex", alignItems: "flex-end" }}>
          <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "20px 20px 0 0", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ padding: "20px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800 }}>Nuevo negocio</h3>
              <button onClick={() => setShowCrear(false)} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16} strokeWidth={2} color={C.muted} />
              </button>
            </div>
            <div style={{ padding: "0 20px 32px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Categoría *</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {categorias.map(c => (
                    <button key={c.id} onClick={() => set("categoria", c.id)}
                      style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: `1px solid ${form.categoria === c.id ? C.text : C.border}`, background: form.categoria === c.id ? C.text : C.card, color: form.categoria === c.id ? "#fff" : C.muted, cursor: "pointer" }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {[["nombre","Nombre *","Motocanal"],["ciudad","Ciudad *","Buenos Aires"],["direccion","Dirección","Av. Corrientes 1234"],["telefono","Teléfono","011-5594-5424"],["whatsapp","WhatsApp (sin + ni espacios)","5491155945424"],["horarios","Horarios","Lun-Vie 9-18hs"],["instagram","Instagram","@motocanal"],["web","Web","motocanal.com"]].map(([k,label,ph]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
                  <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
                    style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", color: C.text, background: C.surface }} />
                </div>
              ))}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Descripción</div>
                <textarea value={form.descripcion} onChange={e => set("descripcion", e.target.value)} rows={3}
                  placeholder="Especialidad, servicios..."
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: "border-box", resize: "none", fontFamily: "inherit", color: C.text, background: C.surface }} />
              </div>
              <button onClick={crearNegocio} disabled={guardando || !form.nombre || !form.categoria}
                style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: form.nombre && form.categoria ? C.text : C.faint, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer", marginTop: 8 }}>
                {guardando ? "Guardando..." : "Crear negocio verificado"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PANTALLA RESET DE CONTRASEÑA (desde email) ──────────────────────────────
// Campo de contraseña con el ojito para verla (documento de Matías, B3): mismo look que el
// input que reemplaza; el estilo se aplica al input y el botón queda adentro, a la derecha.
const CampoPass = ({ style, ...props }) => {
  const [ver, setVer] = useState(false);
  const { margin, marginTop, marginBottom, ...estiloInput } = style || {};
  return (
    <div style={{ position: "relative", width: "100%", margin, marginTop, marginBottom }}>
      <input {...props} type={ver ? "text" : "password"} autoCapitalize="none" autoCorrect="off" spellCheck={false}
        style={{ ...estiloInput, width: "100%", boxSizing: "border-box", paddingRight: 46 }} />
      <button type="button" onClick={() => setVer(v => !v)} aria-label={ver ? "Ocultar contraseña" : "Mostrar contraseña"}
        style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", width: 38, height: 38, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
        {ver ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
      </button>
    </div>
  );
};

const ResetPasswordScreen = ({ onDone }) => {
  const [passNueva, setPassNueva] = useState("");
  const [passConfirm, setPassConfirm] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);
  // Outlook/Hotmail (y otros) abren los links del mail para revisarlos apenas llega, y eso
  // GASTABA el link de un solo uso: el rider lo tocaba un minuto después y veía "Auth session
  // missing!" (le pasó a Carlos el 28-oct). Ahora el mail trae token_hash y el link se canjea
  // recién al tocar "Cambiar contraseña" (los robots no tocan botones). Si igual llega vencido
  // (#error_code=otp_expired, o un mail viejo), se dice claro y se pide otro ahí mismo.
  const qpReset = new URLSearchParams(window.location.search);
  const hpReset = new URLSearchParams(window.location.hash.slice(1));
  const tokenHash = qpReset.get("token_hash");
  const [vencido, setVencido] = useState(!tokenHash && !!(hpReset.get("error_code") || hpReset.get("error")));
  const [emailOtro, setEmailOtro] = useState("");
  const [otroEnviado, setOtroEnviado] = useState(false);

  const pedirOtro = async () => {
    setError(null);
    const em = emailOtro.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setError("Escribí el email de tu cuenta."); return; }
    setCargando(true);
    const { error: e } = await supabase.auth.resetPasswordForEmail(em, { redirectTo: `${SITE_URL}/?recovery=1` });
    setCargando(false);
    if (e) { setError(e.message); return; }
    setOtroEnviado(true);
  };

  const validar = () => {
    if (!passNueva || passNueva.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return false;
    }
    if (!/[A-Z]/.test(passNueva) || !/[a-z]/.test(passNueva) || !/\d/.test(passNueva)) {
      setError("Debe incluir mayúsculas, minúsculas y números.");
      return false;
    }
    if (passNueva !== passConfirm) {
      setError("Las contraseñas no coinciden.");
      return false;
    }
    return true;
  };

  const cambiar = async () => {
    setError(null);
    if (!validar()) return;
    setCargando(true);
    // Sin sesión todavía: se canjea el link ahora (si un intento anterior ya lo canjeó, hay sesión)
    const { data: ses } = await supabase.auth.getSession();
    if (!ses || !ses.session) {
      const { error: errVer } = tokenHash
        ? await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" })
        : { error: { message: "sin sesión" } };
      if (errVer) { setVencido(true); setCargando(false); return; }
    }
    const { error: errUpdate } = await supabase.auth.updateUser({ password: passNueva });
    if (errUpdate) {
      if (/session/i.test(errUpdate.message || "")) setVencido(true);
      else setError(errUpdate.message);
      setCargando(false);
      return;
    }
    setExito(true);
    setCargando(false);
    setTimeout(onDone, 1800);
  };

  return (
    <div style={{
      maxWidth: 430, margin: "0 auto", minHeight: "100vh",
      background: C.card, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: "0 28px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
    }}>
      <div style={{ marginBottom: 28, textAlign: "center" }}>
        <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 64, height: 64, marginBottom: 14, borderRadius: 16, objectFit: "cover" }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-0.03em", marginBottom: 6 }}>
          Nueva contraseña
        </h1>
        <p style={{ fontSize: 14, color: C.muted, letterSpacing: "-0.005em" }}>
          Elegí una nueva contraseña para tu cuenta
        </p>
      </div>

      {exito ? (
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: `${C.green}14`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px",
          }}>
            <CheckCircle size={28} strokeWidth={1.8} color="#27AE60" />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-0.02em" }}>Contraseña actualizada</h3>
          <p style={{ fontSize: 13.5, color: C.muted, letterSpacing: "-0.005em" }}>Ya podés iniciar sesión.</p>
        </div>
      ) : vencido ? (
        <div style={{ width: "100%", textAlign: "center" }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 8, letterSpacing: "-0.02em" }}>Este link ya se usó o venció</h3>
          {otroEnviado ? (
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
              Listo, te mandamos un link nuevo. Abrilo desde el último mail que te llegue (revisá también spam).
            </p>
          ) : (
            <>
              <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5, letterSpacing: "-0.005em", marginBottom: 16 }}>
                Cada link sirve una sola vez. Pedí uno nuevo y abrí el último mail que te llegue.
              </p>
              <input type="email" value={emailOtro} onChange={e => setEmailOtro(e.target.value)} placeholder="Tu email"
                autoCapitalize="none" autoCorrect="off"
                style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, color: C.text, boxSizing: "border-box", fontFamily: "inherit", marginBottom: 12 }} />
              {error && (
                <div style={{ padding: "10px 12px", borderRadius: 10, background: "#FFF1F0", border: "1px solid #FFD7D2", marginBottom: 14, fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em", textAlign: "left" }}>{error}</div>
              )}
              <button onClick={pedirOtro} disabled={cargando}
                style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 500, cursor: cargando ? "default" : "pointer", opacity: cargando ? 0.5 : 1, letterSpacing: "-0.01em" }}>
                {cargando ? "Enviando…" : "Mandarme un link nuevo"}
              </button>
            </>
          )}
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <CampoPass value={passNueva} onChange={e => setPassNueva(e.target.value)}
            placeholder="Nueva contraseña"
            style={{
              width: "100%", padding: "13px 16px", borderRadius: 12,
              border: `1px solid ${C.border}`, fontSize: 15,
              color: C.text, boxSizing: "border-box",
              fontFamily: "inherit", marginBottom: 10,
            }} />
          <CampoPass value={passConfirm} onChange={e => setPassConfirm(e.target.value)}
            placeholder="Repetir contraseña"
            style={{
              width: "100%", padding: "13px 16px", borderRadius: 12,
              border: `1px solid ${C.border}`, fontSize: 15,
              color: C.text, boxSizing: "border-box",
              fontFamily: "inherit", marginBottom: 8,
            }} />
          <p style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.4, letterSpacing: "-0.005em", marginBottom: 16 }}>
            Mínimo 8 caracteres, con mayúsculas, minúsculas y números.
          </p>

          {error && (
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: "#FFF1F0", border: "1px solid #FFD7D2",
              marginBottom: 14,
              fontSize: 13, color: "#B82F26", letterSpacing: "-0.005em",
            }}>{error}</div>
          )}

          <button onClick={cambiar} disabled={cargando}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 12,
              background: C.accent, color: "#fff",
              border: "none", fontSize: 15, fontWeight: 500,
              cursor: cargando ? "default" : "pointer",
              opacity: cargando ? 0.5 : 1,
              letterSpacing: "-0.01em",
            }}>
            {cargando ? "Guardando…" : "Cambiar contraseña"}
          </button>
        </div>
      )}
    </div>
  );
};

const LoginScreen = ({ onBack, modoInicial = "register" }) => {
  const [mode, setMode] = useState(modoInicial === "login" ? "login" : "register"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [fechaNac, setFechaNac] = useState(""); // registro: verificación de edad (16+)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sinConfirmar, setSinConfirmar] = useState(false); // login rechazado por email sin confirmar → ofrecer reenvío
  // Aceptación de términos ANTES de entrar o registrarse. Es requisito de Apple para
  // apps con contenido de usuarios (guideline 1.2): tiene que ser un acto explícito,
  // no una línea de letra chica, y tiene que decir que no se tolera el contenido ofensivo.
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [verEULA, setVerEULA] = useState(false);
  const faltaAceptar = () => {
    if (aceptaTerminos) return false;
    setError("Para continuar tenés que aceptar los Términos y las Normas de la comunidad.");
    return true;
  };
  // En iOS nativo el consentimiento tiene que ser un acto explícito antes de entrar
  // (guideline 1.2). En la web, Google/Apple no exigen el check previo: debajo del
  // botón va "Al continuar aceptás…". El check queda para el formulario de email.
  const exigeCheckOAuth = Capacitor.getPlatform() === "ios";

  const handleLogin = async () => {
    if (faltaAceptar()) return;
    if (!email || !password) { setError("Completá email y contraseña"); return; }
    setLoading(true); setError(""); setSinConfirmar(false);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // Antes TODO era "Email o contraseña incorrectos", incluso cuando el problema
      // real era que el email no estaba confirmado (caso del amigo de Carlos, 04-sep)
      const m = (error.message || "").toLowerCase();
      if (m.includes("not confirmed")) { setSinConfirmar(true); setError("Tu email todavía no está confirmado. Buscá el mail de Olbos (mirá también en spam) y tocá el link — o pedilo de nuevo acá abajo."); }
      else if (m.includes("fetch") || m.includes("network")) setError("Sin conexión. Probá de nuevo en un momento.");
      else setError("Email o contraseña incorrectos. Si te registraste hace poco, probá con «Olvidé mi contraseña»: te llega un link para elegir una nueva.");
    }
    setLoading(false);
  };
  const reenviarConfirmacion = async () => {
    if (!email) return;
    setLoading(true);
    const { error } = await supabase.auth.resend({ type: "signup", email, options: { emailRedirectTo: `${SITE_URL}/` } });
    setLoading(false);
    if (error) setError("No se pudo reenviar: " + error.message);
    else { setSinConfirmar(false); setError(""); setSuccess("Te reenviamos el mail de confirmación. Tocá el link y después iniciá sesión."); }
  };

  const handleRegister = async () => {
    // Cada freno del formulario queda registrado (registro_error con "validacion: …"):
    // el 10-oct en Originarios hubo gente que "no podía crear la cuenta" y en la base
    // no quedaba ninguna huella porque el signUp nunca llegaba a dispararse.
    const frenar = (msg, motivo) => { registrarUso("registro_error", { via: "email", motivo: "validacion: " + motivo }); setError(msg); };
    if (faltaAceptar()) { registrarUso("registro_error", { via: "email", motivo: "validacion: terminos" }); return; }
    // El @usuario ya no frena el registro: si lo dejan vacío se arma desde el email (se
    // cambia después en el perfil). El 10-oct en Originarios la gente completaba mail,
    // contraseña, fecha y términos y veía "Completá todos los campos" sin saber por qué.
    if (!email || !password) { frenar(!email && !password ? "Falta el email y la contraseña." : !email ? "Falta el email." : "Falta la contraseña.", "campos " + [!email && "email", !password && "contraseña"].filter(Boolean).join("+")); return; }
    // Tampoco frena si viene con espacios, tildes, muy largo o ya usado: se limpia y, si está
    // tomado, se le suman dos números (se cambia después en el perfil). En 45 días hubo ~40
    // registros frenados acá ("solo letras…", "hasta 24", "ya está en uso"); para la Expo Moto,
    // con varios del equipo registrando gente en el stand, cada freno es un rider que se va.
    const escrito = normalizarUsername(nombre);
    let usuario = escrito.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9._-]/g, "").slice(0, 22);
    if (usuario.length < 3) {
      const base = (String(email).split("@")[0] || "rider").normalize("NFD").replace(/[^A-Za-z0-9._-]/g, "").slice(0, 18) || "rider";
      usuario = base.length < 3 ? base + "rider" : base;
    }
    const raiz = usuario.slice(0, 20);
    for (let i = 0; i < 4 && await validarUsernameDisponible(usuario); i++) usuario = raiz + Math.floor(10 + Math.random() * 90);
    if (usuario !== escrito) setNombre(usuario);
    if (password.length < 6) { frenar("La contraseña debe tener al menos 6 caracteres", "password corta"); return; }
    // Edad mínima (Términos, punto 2): la fecha viaja en los metadatos y GateEdad la
    // pasa a perfiles en el primer ingreso. Menor de 16 → no se crea la cuenta.
    const edad = edadDesde(fechaNac);
    if (!fechaNac || edad === null || edad < 0 || edad > 110) { frenar("Ingresá tu fecha de nacimiento.", "fecha " + (fechaNac ? "invalida" : "vacia")); return; }
    if (edad < EDAD_MINIMA) { frenar(`Olbos es para riders de ${EDAD_MINIMA} años o más. Te esperamos cuando los cumplas.`, "menor"); return; }
    // El @usuario ya quedó limpio y libre arriba; si dos se registran a la vez con el mismo,
    // el trigger de perfiles le suma un número (y nunca queda un clon de otro, caso "charly" 03-sep)
    setLoading(true); setError("");
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { nombre: usuario, fecha_nacimiento: fechaNac }, emailRedirectTo: `${SITE_URL}/` } });
    if (error) {
      const m = (error.message || "").toLowerCase();
      registrarUso("registro_error", { via: "email", motivo: String(error.message || "").slice(0, 140) });
      setError(m.includes("already") ? "Ese email ya tiene una cuenta en Olbos. Iniciá sesión, o usá «Olvidé mi contraseña» si no te la acordás."
        : m.includes("password") ? "La contraseña es muy débil: usá al menos 6 caracteres."
        : m.includes("rate") || m.includes("limit") ? "Hay mucha gente registrándose en este momento. Esperá un minuto y probá de nuevo."
        : m.includes("fetch") || m.includes("network") ? "Sin conexión. Probá de nuevo en un momento."
        : "No pudimos crear la cuenta. Probá de nuevo en un momento.");
    } else if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      // Supabase responde "ok" aunque el email ya exista (para no revelar cuentas):
      // la señal es identities vacío. Antes decíamos "¡Cuenta creada!", no llegaba
      // ningún mail y la contraseña nueva no se aplicaba → "contraseña incorrecta".
      registrarUso("registro_error", { via: "email", motivo: "email ya registrado" });
      setMode("login");
      setError("Ese email ya tiene una cuenta en Olbos. Iniciá sesión con tu contraseña de siempre, o tocá «Olvidé mi contraseña» para elegir una nueva.");
    } else if (data?.session) {
      // Sin confirmación de email (config de Supabase): la sesión ya está y la app cierra el login sola
      setSuccess("¡Cuenta creada! Entrando…");
    } else {
      setSuccess("¡Cuenta creada! Te mandamos un mail: tocá el link para confirmar y quedás adentro.");
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!email) { setError("Ingresá tu email arriba para recuperar la contraseña"); return; }
    setLoading(true); setError(""); setSuccess("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${SITE_URL}/?recovery=1`,
    });
    if (error) setError(error.message);
    else setSuccess("Te mandamos un email para resetear tu contraseña. Revisá tu casilla (y la carpeta de spam).");
    setLoading(false);
  };

  const handleGoogle = async () => {
    if (exigeCheckOAuth && faltaAceptar()) return;
    setLoading(true); setError("");
    const { error } = await loginGoogle();
    if (error) { registrarUso("registro_error", { via: "google", motivo: String(error.message || "").slice(0, 140) }); setError("No pudimos conectar con Google. Probá de nuevo, o creá la cuenta con tu email."); setLoading(false); }
  };

  const handleApple = async () => {
    if (exigeCheckOAuth && faltaAceptar()) return;
    setLoading(true); setError("");
    try {
      const { error } = await loginApple();
      if (error) { setError("Error al iniciar sesión con Apple"); setLoading(false); }
      // Si salió bien, onAuthStateChange cierra el login.
    } catch (e) {
      // El usuario puede cancelar el diálogo de Apple → no mostramos un error feo.
      const msg = `${(e && (e.message || e.code)) || ""}`;
      if (/1001|cancel/i.test(msg)) { setLoading(false); return; }
      setError("No se pudo conectar con Apple"); setLoading(false);
    }
  };

  // Check de términos: en iOS va arriba de todo (antes de Apple/Google); en la web va
  // con el formulario de email, que es el único camino que lo exige.
  const checkTerminos = (
    <button onClick={() => { setAceptaTerminos(!aceptaTerminos); setError(""); }}
      style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 10, marginBottom: exigeCheckOAuth ? 18 : 0, padding: "12px 14px", borderRadius: 12, background: aceptaTerminos ? `${C.green}14` : C.surface, border: `1px solid ${aceptaTerminos ? `${C.green}33` : C.border}`, cursor: "pointer", textAlign: "left" }}>
      <div style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1, background: aceptaTerminos ? "#2F7D4F" : C.card, border: `1.5px solid ${aceptaTerminos ? "#2F7D4F" : C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {aceptaTerminos && <CheckCircle size={13} strokeWidth={3} color="#fff" />}
      </div>
      <span style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
        Acepto los <span onClick={e => { e.stopPropagation(); setVerEULA(true); }} style={{ fontWeight: 700, textDecoration: "underline" }}>Términos de uso y Normas de la comunidad</span>, y entiendo que en Olbos hay <strong>tolerancia cero con el contenido ofensivo y los usuarios abusivos</strong>.
      </span>
    </button>
  );

  return (
    // OJO: esta pantalla se renderiza SIN el <style>{G}</style> de la raíz, así que --sat
    // puede no existir: var(--sat, 0px) con fallback, o el padding entero queda inválido.
    <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "calc(var(--sat, 0px) + 64px) 28px 32px", boxSizing: "border-box", position: "relative" }}>
      <AvisoNavegadorInterno />
      {/* back button: en su propia franja, arriba del logo (antes se pisaba con el separador) */}
      {onBack && (
        <button onClick={onBack} style={{ position: "absolute", top: "calc(var(--sat, 0px) + 16px)", left: 20, display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14, padding: "6px 4px" }}>
          <ChevronLeft size={18} strokeWidth={2} /> Volver
        </button>
      )}
      {/* logo */}
      <div style={{ marginBottom: 22, textAlign: "center" }}>
        <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 60, height: 60, marginBottom: 10, borderRadius: 16, objectFit: "cover" }} />
        <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: -1.5, color: C.text, lineHeight: 1 }}>olbos</div>
        <div style={{ fontSize: 13.5, color: C.muted, marginTop: 6 }}>{mode === "register" ? "Creá tu cuenta: gratis, en un minuto" : "La comunidad de riders"}</div>
      </div>

      {/* Aceptación de términos (iOS nativo): va ARRIBA de todos los botones de acceso,
          para que sea lo primero que se ve y ningún camino de registro se saltee el consentimiento. */}
      {exigeCheckOAuth && checkTerminos}

      {/* botón Apple — SOLO iOS nativo (Apple lo quiere arriba de Google) */}
      {Capacitor.getPlatform() === "ios" && (
        <button onClick={handleApple} disabled={loading}
          style={{ width: "100%", padding: "13px 0", borderRadius: 14, background: C.accent, border: "none", fontSize: 15, fontWeight: 600, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,.08)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.42-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.36 15.4 3.14 7.5 8.7 7.22c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.18 4.17zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Continuar con Apple
        </button>
      )}

      {/* botón Google */}
      <button onClick={handleGoogle} disabled={loading}
        style={{ width: "100%", padding: "13px 0", borderRadius: 14, background: C.card, border: "1.5px solid #e2dcd4", fontSize: 15, fontWeight: 600, color: C.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,.08)" }}>
        <svg width="20" height="20" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#111111" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Continuar con Google
      </button>
      {!exigeCheckOAuth && (
        <div style={{ width: "100%", fontSize: 11, color: C.muted, textAlign: "center", marginTop: -12, marginBottom: 18, lineHeight: 1.45, letterSpacing: "-0.005em" }}>
          Al continuar aceptás los <span onClick={() => setVerEULA(true)} style={{ fontWeight: 700, textDecoration: "underline", cursor: "pointer", color: C.text }}>Términos y Normas de la comunidad</span> y la <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: C.text, fontWeight: 700 }}>Privacidad</a>.
        </div>
      )}

      {/* separador */}
      <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        <span style={{ fontSize: 12, color: C.muted }}>o con email</span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
      </div>

      {/* tabs */}
      <div style={{ display: "flex", width: "100%", marginBottom: 20, background: C.surface, borderRadius: 12, padding: 4 }}>
        {[["login", "Iniciar sesión"], ["register", "Crear cuenta"]].map(([m, label]) => (
          <button key={m} onClick={() => { setMode(m); setError(""); setSuccess(""); }}
            style={{ flex: 1, padding: "10px 0", borderRadius: 9, fontSize: 13, fontWeight: mode === m ? 700 : 500, background: mode === m ? C.card : "transparent", color: mode === m ? C.text : C.muted, boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,.08)" : "none", border: "none", cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      {/* campos */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
        {mode === "register" && (
          <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu @usuario (opcional, ej: charly_rider)"
            style={{ padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, background: C.surface, color: C.text, width: "100%", boxSizing: "border-box" }} />
        )}
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email"
          style={{ padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, background: C.surface, color: C.text, width: "100%", boxSizing: "border-box" }} />
        <CampoPass value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña"
          style={{ padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, background: C.surface, color: C.text, width: "100%", boxSizing: "border-box" }} />
        {mode === "register" && (
          <div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 4, letterSpacing: "-0.005em" }}>Fecha de nacimiento · Olbos es para riders de {EDAD_MINIMA} años o más</div>
            <input value={fechaNac} onChange={e => setFechaNac(e.target.value)} type="date" max={new Date().toISOString().slice(0, 10)}
              style={{ padding: "13px 16px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 15, background: C.surface, color: C.text, width: "100%", boxSizing: "border-box", fontFamily: "inherit" }} />
          </div>
        )}
        {mode === "login" && (
          <button onClick={handleResetPassword} disabled={loading}
            style={{ alignSelf: "flex-end", background: "none", border: "none", padding: "2px 4px", marginTop: -4, fontSize: 12.5, color: C.text, cursor: "pointer", letterSpacing: "-0.005em" }}>
            ¿Olvidaste tu contraseña?
          </button>
        )}
        {mode === "register" && (
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45, letterSpacing: "-0.005em" }}>
            {LEYENDA_25326} Más info en <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: C.muted }}>olbos.ai/privacidad</a>.
          </div>
        )}
        {/* En la web el check de términos va acá, con el formulario de email */}
        {!exigeCheckOAuth && checkTerminos}
      </div>

      {error && <div style={{ width: "100%", marginTop: 12, padding: "10px 14px", background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 10, fontSize: 13, color: C.red }}>{error}</div>}
      {sinConfirmar && (
        <button onClick={reenviarConfirmacion} disabled={loading} style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          📩 Reenviarme el mail de confirmación
        </button>
      )}
      {success && <div style={{ width: "100%", marginTop: 12, padding: "10px 14px", background: `${C.green}14`, border: `1px solid ${C.green}33`, borderRadius: 10, fontSize: 13, color: C.green }}>{success}</div>}

      <button onClick={mode === "login" ? handleLogin : handleRegister} disabled={loading}
        style={{ width: "100%", marginTop: 20, padding: "15px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Cargando..." : mode === "login" ? "Entrar" : "Crear cuenta"}
      </button>

      <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>¿Tenés un taller, concesionario o un local?</div>
        <button onClick={() => { if (onBack) onBack(); setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-registro-negocio")), 60); }} style={{ fontSize: 14, fontWeight: 700, color: C.text, background: "none", border: "none", cursor: "pointer" }}>
          Registrá tu negocio en Olbos →
        </button>
      </div>

      {/* Términos completos (EULA). Se abre desde el check de arriba, antes de entrar. */}
      {verEULA && (
        <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,.45)", backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={e => e.target === e.currentTarget && setVerEULA(false)}>
          <div style={{ width: "100%", maxWidth: 460, background: C.card, borderRadius: "20px 20px 0 0", maxHeight: "88vh", overflowY: "auto", padding: "18px 20px calc(24px + var(--sab))" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>Términos de uso y Normas de la comunidad</h3>
              <button onClick={() => setVerEULA(false)} style={{ width: 28, height: 28, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65, letterSpacing: "-0.005em" }}>
              <div style={{ padding: "12px 14px", background: `${C.red}14`, border: `1px solid ${C.red}33`, borderRadius: 10, marginBottom: 14 }}>
                <strong style={{ color: C.red }}>Tolerancia cero.</strong> En Olbos no se tolera el contenido ofensivo ni los usuarios abusivos. Publicar contenido ofensivo o acosar a otro rider deriva en la eliminación del contenido y la baja de la cuenta, sin aviso previo.
              </div>

              <p style={{ marginBottom: 10 }}>Olbos es una comunidad de riders. Al crear tu cuenta aceptás estas condiciones:</p>

              <p style={{ marginBottom: 8 }}><strong>1. Qué no se publica.</strong> Nada de insultos, amenazas, discriminación ni acoso. Nada sexual, violento, ilegal ni que ponga en riesgo a otros. Nada de spam, estafas ni suplantación de identidad.</p>

              <p style={{ marginBottom: 8 }}><strong>2. Sos responsable de lo que publicás.</strong> Las fotos, videos, rutas y mensajes que subís son tuyos y respondés por ellos. No subas contenido de terceros sin permiso ni datos personales de otra persona.</p>

              <p style={{ marginBottom: 8 }}><strong>3. Reportar.</strong> Cualquier publicación, comentario o perfil se puede denunciar desde el menú de los tres puntos. El reporte llega al equipo de Olbos.</p>

              <p style={{ marginBottom: 8 }}><strong>4. Bloquear.</strong> Podés bloquear a cualquier usuario desde su perfil o desde el menú de una publicación. El bloqueo es inmediato: su contenido desaparece de tu feed y dejan de poder contactarte.</p>

              <p style={{ marginBottom: 8 }}><strong>5. Qué hacemos nosotros.</strong> Revisamos cada reporte <strong>dentro de las 24 horas</strong>. Si el contenido incumple estas normas, lo damos de baja y suspendemos o eliminamos la cuenta del responsable.</p>

              <p style={{ marginBottom: 8 }}><strong>6. Manejate con cuidado en la ruta.</strong> Olbos organiza y muestra salidas, pero no las conduce. Cada rider es responsable de su moto, su documentación, su seguro y su manejo. Respetá las normas de tránsito.</p>

              <p style={{ marginBottom: 8 }}><strong>7. Los eventos son de quien los organiza.</strong> Los pagos de inscripción van directo al organizador; Olbos no participa de la transacción ni garantiza la realización del evento.</p>

              <p style={{ marginBottom: 8 }}><strong>8. Tu cuenta.</strong> Podés borrarla cuando quieras desde tu perfil, y con ella se va tu contenido.</p>

              <p style={{ marginBottom: 12 }}><strong>9. Tu contenido en la difusión de Olbos.</strong> Al publicar contenido autorizás su exhibición dentro de la plataforma y en material promocional de Olbos (capturas en tiendas de aplicaciones, sitio web y redes oficiales), siempre en el contexto de mostrar cómo funciona el servicio. Podés pedir que retiremos tu contenido del material promocional escribiendo a hola@olbos.ai.</p>

              <p style={{ fontSize: 11.5, color: C.muted, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                Dudas o denuncias: <strong>hola@olbos.ai</strong><br />
                Olbos · {new Date().getFullYear()} · olbos.ai/terminos
              </p>
            </div>
            <button onClick={() => { setAceptaTerminos(true); setVerEULA(false); setError(""); }}
              style={{ width: "100%", marginTop: 16, padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: "pointer" }}>
              Acepto los términos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── BIENVENIDA POST-REGISTRO ────────────────────────────────────────────────
// Una sola vista, "momento oscuro" de marca: el @ del rider, su número de registro
// y UN botón que lo lleva a lo que vino a hacer (anotarse al evento del QR) o a ver
// qué hay cerca. Reemplaza al modal "Original Rider" (código muerto hasta 09-oct).
// Bienvenida post-registro + onboarding de 3 pantallas (revisión UX, nivel "esta semana"):
// 1) qué es Olbos, 2) ciudad y moto (PreguntaPerfil, la misma del feed), 3) ¿qué querés
// hacer hoy? Una sola vez (perfiles.onboarding.intro vía set_onboarding), se puede saltar,
// y al que llega por el link de un evento (?evento=) NO se le muestra: va directo a anotarse.
const BienvenidaRider = ({ session, eventoId, onAnotar, onCerca, onExplorar, onIr }) => {
  const uid = session?.user?.id;
  const [perfil, setPerfil] = useState(null);
  const [evento, setEvento] = useState(null);
  const conIntro = !eventoId;
  const [paso, setPaso] = useState(1);
  useEffect(() => {
    if (!uid) return;
    let vivo = true, intentos = 0;
    // El perfil lo crea un trigger / asegurar_perfil: recién registrado puede tardar un segundo
    const buscar = async () => {
      const { data } = await supabase.from("perfiles").select("username, numero_registro, onboarding").eq("id", uid).maybeSingle();
      if (!vivo) return;
      if (data && data.username) {
        setPerfil(data);
        // Ya lo vio (en otro teléfono, por ejemplo): no se repite
        if (conIntro && data.onboarding && data.onboarding.intro === true && onExplorar) onExplorar();
        return;
      }
      if (++intentos < 8) setTimeout(buscar, 800);
    };
    buscar();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);
  useEffect(() => {
    if (conIntro) registrarUso("onboarding_paso", { paso, accion: "ver" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso]);
  const terminarIntro = (accion, destino) => {
    registrarUso("onboarding_paso", { paso, accion });
    supabase.rpc("set_onboarding", { p_patch: { intro: true } }).then(() => {}, () => {});
    if (destino && onIr) onIr(destino); else if (onExplorar) onExplorar();
  };
  const siguiente = () => { registrarUso("onboarding_paso", { paso, accion: "seguir" }); setPaso(p => p + 1); };
  useEffect(() => {
    if (!eventoId) return;
    supabase.from("eventos").select("id, titulo, fecha, hora").eq("id", eventoId).maybeSingle()
      .then(({ data }) => setEvento(data || null), () => {});
  }, [eventoId]);
  const metaNombre = session?.user?.user_metadata?.nombre;
  const nombre = perfil?.username ? `@${perfil.username}` : (metaNombre ? `@${normalizarUsername(metaNombre)}` : "");
  const n = perfil ? Number(perfil.numero_registro) : 0;
  const tituloCorto = (t) => {
    const s = String(t || "").split(/\s[—–-]\s|:|\(/)[0].trim();
    return s.length > 36 ? s.slice(0, 34).trim() + "…" : s;
  };
  const btn = { width: "100%", padding: "16px", borderRadius: 14, border: "none", fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", fontFamily: "inherit" };
  const saltar = (
    <button onClick={() => terminarIntro("saltar")} style={{ display: "block", margin: "14px auto 0", background: "none", border: "none", color: "#8A857F", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 6 }}>
      Saltar
    </button>
  );
  const puntos = (
    <div style={{ display: "flex", gap: 6, marginBottom: 26 }}>
      {[1, 2, 3].map(n => <span key={n} style={{ width: n === paso ? 22 : 7, height: 7, borderRadius: 4, background: n === paso ? "#F9F5F2" : "rgba(249,245,242,.25)", transition: "width .2s" }} />)}
    </div>
  );
  const fondo = { position: "fixed", inset: 0, zIndex: 9700, background: "#111111", color: "#F9F5F2", display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 28px calc(28px + var(--sab))", overflowY: "auto" };
  if (conIntro && paso === 2) {
    return (
      <div style={fondo}>
        <div style={{ width: "100%", maxWidth: 380, margin: "0 auto" }}>
          {puntos}
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 8px", color: "#F9F5F2" }}>Contanos de vos</h1>
          <p style={{ fontSize: 15, color: "#bdb6ab", lineHeight: 1.5, margin: "0 0 10px", letterSpacing: "-0.01em" }}>Tu ciudad y tu moto, para mostrarte las salidas y los riders de tu zona.</p>
          <div style={{ margin: "0 -16px 22px" }}>
            <PreguntaPerfil session={session} />
          </div>
          <button onClick={siguiente} style={{ ...btn, background: "#F9F5F2", color: "#111111" }}>Seguir</button>
          {saltar}
        </div>
      </div>
    );
  }
  if (conIntro && paso === 3) {
    const accion = (emoji, texto, sub, destino, accionId) => (
      <button onClick={() => terminarIntro(accionId, destino)}
        style={{ ...btn, background: "#F9F5F2", color: "#111111", marginBottom: 10, textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "15px 16px" }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>{emoji}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 15.5, fontWeight: 700 }}>{texto}</span>
          <span style={{ display: "block", fontSize: 12.5, fontWeight: 500, color: "#8A857F", marginTop: 2 }}>{sub}</span>
        </span>
        <ChevronRight size={18} color="#8A857F" style={{ flexShrink: 0 }} />
      </button>
    );
    return (
      <div style={fondo}>
        <div style={{ width: "100%", maxWidth: 380, margin: "0 auto" }}>
          {puntos}
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 22px", color: "#F9F5F2" }}>¿Qué querés hacer hoy?</h1>
          {accion("📍", "Anotarme a una salida", "Las rodadas y eventos que vienen", "eventos", "eventos")}
          {accion("🛰️", "Grabar un recorrido", "Abrí el mapa y dale a grabar", "rutas", "mapa")}
          {accion("📸", "Ver qué publica la comunidad", "Fotos, videos y recorridos", "feed", "comunidad")}
          {saltar}
        </div>
      </div>
    );
  }
  return (
    <div style={fondo}>
      <div style={{ width: "100%", maxWidth: 380, margin: "0 auto" }}>
        {conIntro ? puntos : <img src={LOGO_URL} alt="Olbos" style={{ width: 56, height: 56, borderRadius: 14, objectFit: "cover", marginBottom: 26, display: "block" }} />}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#8A6D3B", marginBottom: 10 }}>Bienvenido a Olbos</div>
        <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.08, margin: "0 0 12px", color: "#F9F5F2", wordBreak: "break-word" }}>
          ¡Listo{nombre ? `, ${nombre}` : ""}!
        </h1>
        <p style={{ fontSize: 16.5, color: "#bdb6ab", lineHeight: 1.5, margin: "0 0 6px", letterSpacing: "-0.01em" }}>
          {n > 0
            ? <>Sos el rider <strong style={{ color: "#F9F5F2" }}>#{n.toLocaleString("es-AR")}</strong> de Olbos.</>
            : "Ya sos parte de la comunidad."}
          {n > 0 && n <= 300 ? <> 🏁 <strong style={{ color: "#F9F5F2" }}>Original Rider</strong>: de los primeros 300.</> : null}
        </p>
        {conIntro ? (
          <>
            <div style={{ margin: "22px 0 30px", display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                ["🏍️", "Salidas y rodadas cerca tuyo: te anotás y vas con el grupo."],
                ["🛰️", "Grabás tu recorrido y queda con tus fotos, para revivirlo y compartirlo."],
                ["📸", "Una comunidad de riders que publica lo que anda: fotos, rutas, videos."],
              ].map(([e, t]) => (
                <div key={e} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>{e}</span>
                  <span style={{ fontSize: 15, color: "#F9F5F2", lineHeight: 1.45, letterSpacing: "-0.01em" }}>{t}</span>
                </div>
              ))}
            </div>
            <button onClick={siguiente} style={{ ...btn, background: "#F9F5F2", color: "#111111" }}>Seguir</button>
            {saltar}
          </>
        ) : (<>
        <p style={{ fontSize: 13.5, color: "#8A857F", lineHeight: 1.5, margin: "0 0 34px", letterSpacing: "-0.005em" }}>
          {eventoId
            ? "Ya tenés tu cuenta. Falta un toque para quedar anotado."
            : "El camino es el destino. Esto es lo que podés hacer ahora."}
        </p>
        {eventoId ? (
          <button onClick={onAnotar} style={{ ...btn, background: "#F9F5F2", color: "#111111", marginBottom: 10 }}>
            Anotarme a {evento ? tituloCorto(evento.titulo) : "la salida"}
          </button>
        ) : (
          <button onClick={onCerca} style={{ ...btn, background: "#F9F5F2", color: "#111111", marginBottom: 10 }}>
            Ver qué hay cerca
          </button>
        )}
        <button onClick={onExplorar} style={{ ...btn, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontWeight: 600, padding: "14px" }}>
          Explorar Olbos
        </button>
        </>)}
      </div>
    </div>
  );
};

// ─── MODAL REGISTRO REQUERIDO ─────────────────────────────────────────────────
// Hoja corta: hero chico y los botones a la vista sin scrollear (a 375×812 el hero
// con badge y la grilla de 6 tarjetas dejaban "Continuar con Google" recortado
// abajo; auditoría 09-oct). Por encima del visor de Reels (9750): un like sin
// sesión lo abría DEBAJO del visor.
const RegistroModal = ({ onClose, onLogin }) => {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9800, display: "flex", alignItems: "flex-end", background: "rgba(0,0,0,.4)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", background: C.card, borderRadius: "28px 28px 0 0", overflow: "hidden", boxShadow: "0 -12px 50px rgba(0,0,0,.2)", maxHeight: "94vh", overflowY: "auto" }}>

        {/* HERO chico */}
        <div style={{ padding: "26px 28px 12px", textAlign: "center" }}>
          <div style={{ width: 54, height: 54, borderRadius: 14, overflow: "hidden", margin: "0 auto 14px", boxShadow: "0 8px 24px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04)" }}>
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: "-0.03em", lineHeight: 1.08, marginBottom: 6 }}>
            Creá tu cuenta en Olbos
          </h2>
          <p style={{ fontSize: 14.5, color: C.muted, letterSpacing: "-0.01em", lineHeight: 1.45, marginBottom: 4 }}>
            Gratis, en menos de un minuto.
          </p>
          <p style={{ fontSize: 12.5, color: C.muted, letterSpacing: "-0.005em" }}>
            Salidas · Rodada en vivo · Grabá tu recorrido
          </p>
        </div>

        {/* ACCIONES — botones Apple-style */}
        <div style={{ padding: "10px 24px calc(22px + var(--sab))" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {/* Apple — SOLO iOS nativo, arriba de Google (requisito App Store por ofrecer Google) */}
            {Capacitor.getPlatform() === "ios" && (
              <button onClick={() => { loginApple().then(() => {}, () => {}); }}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: 12,
                  background: C.accent, border: "none",
                  fontSize: 15, fontWeight: 500, color: "#fff",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  letterSpacing: "-0.01em",
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.42-1.09-.47-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.36 15.4 3.14 7.5 8.7 7.22c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.18 4.17zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continuar con Apple
              </button>
            )}
            <button onClick={() => { loginGoogle().then(r => { if (r && r.error) registrarUso("registro_error", { via: "google", motivo: String(r.error.message || "").slice(0, 140) }); }, () => {}); }}
              style={{
                width: "100%", padding: "14px 0", borderRadius: 12,
                background: C.card, border: `1px solid ${C.border}`,
                fontSize: 15, fontWeight: 500, color: C.text,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                letterSpacing: "-0.01em",
                transition: "all .15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = C.surface; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#111111" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continuar con Google
            </button>

            <button onClick={() => onLogin("register")}
              style={{
                width: "100%", padding: "15px 0", borderRadius: 12,
                background: C.accent, color: "#fff",
                fontSize: 15, fontWeight: 600, border: "none",
                cursor: "pointer", letterSpacing: "-0.01em",
                transition: "all .15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#000"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#111111"; }}>
              Crear cuenta con email
            </button>

            <button onClick={() => onLogin("login")}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 12,
                background: "transparent", color: C.text,
                fontSize: 14, fontWeight: 500, border: "none",
                cursor: "pointer", letterSpacing: "-0.005em",
              }}>
              Ya tengo cuenta · Iniciar sesión
            </button>

            <button onClick={onClose}
              style={{
                width: "100%", padding: "8px 0", borderRadius: 12,
                background: "transparent", color: C.muted,
                fontSize: 12, fontWeight: 400, border: "none",
                cursor: "pointer", letterSpacing: "-0.005em", marginTop: 4,
              }}>
              Seguir viendo sin cuenta
            </button>
          </div>
          <div style={{ fontSize: 10.5, color: C.muted, textAlign: "center", marginTop: 10, lineHeight: 1.45, letterSpacing: "-0.005em" }}>
            Al continuar aceptás los <a href="/terminos" target="_blank" rel="noreferrer" style={{ color: C.muted }}>Términos</a> y la <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: C.muted }}>Privacidad</a>.
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
// ─── PANTALLA DE INSTALACIÓN ─────────────────────────────────────────────────
const PantallaInstalar = ({ onCerrar }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [instalado, setInstalado] = useState(false);

  const esIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const esAndroid = /android/i.test(navigator.userAgent);
  const esPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const instalarAndroid = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalado(true);
    setDeferredPrompt(null);
  };

  const VERDE = "#111111";
  const VERDE_OSCURO = "#8A6D3B";
  const VERDE_CLARO = C.surface;

  if (esPWA || instalado) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: C.card, borderRadius: 24, padding: 32, maxWidth: 340, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🏍️</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, color: C.text }}>¡Listo para rodar!</h2>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 8 }}>
            Olbos ya está en tu pantalla de inicio.
          </p>
          <p style={{ fontSize: 13, color: "#999", fontStyle: "italic", marginBottom: 24 }}>— El camino es el destino</p>
          <button onClick={onCerrar} style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: VERDE, color: "#fff", fontWeight: 700, fontSize: 16, border: "none", cursor: "pointer" }}>
            Entrar a Olbos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.card, zIndex: 9999, overflowY: "auto" }}>

      {/* hero */}
      <div style={{ background: `linear-gradient(160deg, ${VERDE_CLARO} 0%, #fff 100%)`, padding: "60px 28px 36px", textAlign: "center", borderBottom: `1px solid ${VERDE}22` }}>
        <div style={{ width: 88, height: 88, borderRadius: 22, overflow: "hidden", margin: "0 auto 20px", boxShadow: `0 8px 30px ${VERDE}44` }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: C.text, letterSpacing: -0.5, lineHeight: 1.2, marginBottom: 14 }}>
          Andar en moto<br />es un estilo de vida
        </h1>
        <p style={{ fontSize: 13, color: VERDE_OSCURO, fontStyle: "italic", fontWeight: 600 }}>
          — El camino es el destino
        </p>
      </div>

      <div style={{ padding: "28px 24px" }}>

        {/* La app de la tienda va PRIMERO: hace lo que el navegador no puede —seguir
            grabando con la pantalla apagada— y es lo que un rider necesita de verdad.
            Agregar al inicio queda abajo, para el que no quiere bajar nada. */}
        {(esAndroid || esIOS) && (
          <div style={{ background: "#111111", color: "#F9F5F2", borderRadius: 16, padding: "20px 18px", marginBottom: 22, textAlign: "center" }}>
            <div style={{ fontSize: 16.5, fontWeight: 800, letterSpacing: "-0.015em", marginBottom: 7 }}>Bajate la app</div>
            <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.55, marginBottom: 16 }}>
              Graba tu recorrido con la pantalla apagada, te avisa de las salidas cerca tuyo y funciona sin señal. En el navegador eso no se puede.
            </div>
            <a href={esAndroid ? "https://play.google.com/store/apps/details?id=ai.olbos.app" : "https://apps.apple.com/app/id6794469275"}
              target="_blank" rel="noreferrer"
              style={{ display: "block", padding: "14px", borderRadius: 12, background: "#F9F5F2", color: "#111111", fontSize: 15, fontWeight: 800, textDecoration: "none", letterSpacing: "-0.01em" }}>
              {esAndroid ? "Descargar de Google Play" : "Descargar del App Store"}
            </a>
            <button onClick={onCerrar}
              style={{ marginTop: 10, width: "100%", padding: "12px", borderRadius: 12, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.3)", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
              Seguir en el navegador
            </button>
          </div>
        )}

        <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, textAlign: "center", marginBottom: 24 }}>
          {(esAndroid || esIOS)
            ? "O agregala al inicio de tu celular, sin bajar nada, y entrá con un toque."
            : "Agregá Olbos al inicio de tu celular y entrá con un toque, sin abrir el navegador."}
        </p>

        {/* Android con prompt nativo */}
        {esAndroid && deferredPrompt && (
          <div style={{ background: VERDE_CLARO, border: `1px solid ${VERDE}44`, borderRadius: 16, padding: 20, marginBottom: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: VERDE_OSCURO, marginBottom: 14, fontWeight: 600 }}>
              ¡Tu browser está listo para agregarla!
            </div>
            <button onClick={instalarAndroid}
              style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: VERDE, color: "#fff", fontWeight: 700, fontSize: 16, border: "none", cursor: "pointer" }}>
              📲 Agregar Olbos al inicio
            </button>
          </div>
        )}

        {/* iOS paso a paso */}
        {esIOS && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>
              En iPhone / iPad:
            </div>
            {[
              { n: 1, icono: "🌐", titulo: "Abrí en Safari", desc: "Tiene que estar abierto en Safari, no en Chrome ni otro browser." },
              { n: 2, icono: "⬆️", titulo: 'Tocá "Compartir"', desc: 'El ícono de cuadrado con flecha, en la barra de abajo.' },
              { n: 3, icono: "➕", titulo: '"Añadir a pantalla de inicio"', desc: 'Buscá esa opción en el menú. Puede que tengas que scrollear.' },
              { n: 4, icono: "✅", titulo: "Confirmar", desc: 'Tocá "Añadir" y el ícono de Olbos queda en tu pantalla.' },
            ].map(p => (
              <div key={p.n} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: VERDE, color: "#fff", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {p.n}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>{p.icono} {p.titulo}</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{p.desc}</div>
                </div>
              </div>
            ))}
            <div style={{ background: VERDE_CLARO, border: `1px solid ${VERDE}33`, borderRadius: 12, padding: 14, textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 6 }}>⬆️</div>
              <div style={{ fontSize: 12, color: VERDE_OSCURO, fontWeight: 600 }}>
                Botón compartir — abajo en Safari, ícono de cuadrado con flecha
              </div>
            </div>
          </div>
        )}

        {/* Android sin prompt */}
        {esAndroid && !deferredPrompt && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>
              En Android con Chrome:
            </div>
            {[
              { n: 1, icono: "⋮", titulo: "Tocá los tres puntos", desc: "Arriba a la derecha del browser." },
              { n: 2, icono: "📲", titulo: '"Agregar a pantalla de inicio"', desc: "Buscá esa opción en el menú." },
              { n: 3, icono: "✅", titulo: "Confirmar", desc: "El ícono de Olbos aparece en tu pantalla." },
            ].map(p => (
              <div key={p.n} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: VERDE, color: "#fff", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {p.n}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>{p.icono} {p.titulo}</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Desktop */}
        {!esIOS && !esAndroid && (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>💻</div>
            <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>
              En la computadora podés agregarla desde el ícono en la barra del browser. En el celu la experiencia es mejor.
            </p>
          </div>
        )}

        <button onClick={onCerrar}
          style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer", marginTop: 16 }}>
          Entrar a Olbos sin agregar al inicio
        </button>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: "#aaa", fontStyle: "italic" }}>
          — El camino es el destino
        </div>
      </div>
    </div>
  );
};

// ─── RECLAMAR PERFIL PRE-LLENADO (partner crea su cuenta y hereda los datos) ────
const ReclamarPerfil = ({ token }) => {
  const [inv, setInv] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [yo, setYo] = useState(undefined); // undefined=cargando, null=anon, string=logueado
  const [yoUsername, setYoUsername] = useState("");
  const [reclamando, setReclamando] = useState(false);

  useEffect(() => {
    // NO guardar el token al previsualizar: solo se guarda cuando el usuario elige crear cuenta.
    (async () => {
      const { data: raw } = await supabase.rpc("get_invitacion_perfil", { p_token: token });
      setInv((typeof raw === "string" ? JSON.parse(raw) : raw) || { error: "no_existe" });
      setCargando(false);
      const { data: { user } } = await supabase.auth.getUser();
      setYo(user?.id || null);
      if (user) {
        const { data: p } = await supabase.from("perfiles").select("username").eq("id", user.id).single();
        setYoUsername(p?.username || "");
      }
    })();
  }, [token]);

  const reclamarAhora = async () => {
    // Confirmación fuerte: reclamar reemplaza el perfil actual (es para cuentas nuevas)
    const objetivo = inv?.datos?.username ? `@${inv.datos.username}` : "este perfil";
    if (!window.confirm(`Esto va a REEMPLAZAR tu perfil actual${yoUsername ? ` (@${yoUsername})` : ""} por ${objetivo}.\n\nEsta opción es solo para cuentas nuevas. Si esta es tu cuenta personal, cancelá y abrí el link desde una cuenta nueva.\n\n¿Continuar igual?`)) return;
    setReclamando(true);
    const { data: raw } = await supabase.rpc("reclamar_invitacion_perfil", { p_token: token });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    setReclamando(false);
    if (data?.ok) { try { localStorage.removeItem("olbos_claim_token"); } catch {} window.location.href = "/"; }
    else alert("No se pudo reclamar: " + (data?.error || "error"));
  };
  const conGoogle = () => {
    try { localStorage.setItem("olbos_claim_token", token); } catch {}
    loginGoogle();
  };

  const wrap = (children) => (
    <div style={{ minHeight: "100vh", background: C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, textAlign: "center", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>{children}</div>
  );

  if (cargando) return wrap(<div style={{ color: C.muted, fontSize: 13 }}>Cargando…</div>);
  if (inv?.error || !inv?.datos) return wrap(<>
    <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
    <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>Invitación no válida</h2>
    <p style={{ fontSize: 14, color: C.muted, marginBottom: 18 }}>Este link no existe o ya fue usado.</p>
    <button onClick={() => window.location.href = "/"} style={{ padding: "11px 24px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", fontWeight: 600, cursor: "pointer" }}>Ir a Olbos</button>
  </>);
  if (inv?.reclamada) return wrap(<>
    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
    <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>Este perfil ya fue reclamado</h2>
    <button onClick={() => window.location.href = "/"} style={{ padding: "11px 24px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", fontWeight: 600, cursor: "pointer" }}>Ir a Olbos</button>
  </>);

  const d = inv.datos;
  const esComunidad = d.tipo_perfil === "comunidad";
  return (
    <div style={{ minHeight: "100vh", background: C.card, maxWidth: 430, margin: "0 auto", display: "flex", flexDirection: "column", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif" }}>
      {/* hero con degradé suave */}
      <div style={{ background: "linear-gradient(180deg, #FFF4EF 0%, #ffffff 100%)", padding: "52px 28px 28px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginBottom: 30 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 24, height: 24, borderRadius: 6 }} />
          <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>Olbos · Invitación</span>
        </div>

        <div style={{ width: 96, height: 96, borderRadius: 24, overflow: "hidden", margin: "0 auto 16px", background: C.card, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 30px rgba(0,0,0,.12), 0 2px 6px rgba(0,0,0,.05)" }}>
          {d.avatar_url ? <img loading="lazy" decoding="async" src={d.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={36} color="#c0c0c5" />}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>@{d.username}</h1>
          {esComunidad && <BadgeComunidad tipo="comunidad" />}
        </div>
        {d.ciudad && <div style={{ fontSize: 13.5, color: C.muted, letterSpacing: "-0.005em", marginBottom: d.bio ? 10 : 0 }}>📍 {d.ciudad}</div>}
        {d.bio && <p style={{ fontSize: 14.5, color: C.text, lineHeight: 1.5, letterSpacing: "-0.01em", maxWidth: 320, margin: "0 auto" }}>{d.bio}</p>}
        {Array.isArray(d.links) && d.links.length > 0 && <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}><LinksPerfil links={d.links} /></div>}
      </div>

      {/* cuerpo */}
      <div style={{ flex: 1, padding: "8px 28px 36px" }}>
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <h2 style={{ fontSize: 21, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>Te armamos tu perfil 🎉</h2>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.55, letterSpacing: "-0.01em", maxWidth: 330, margin: "0 auto" }}>
            Creá tu cuenta y este perfil queda tuyo, ya completo. Desde acá vas a poder organizar tus salidas y que la gente se anote con un toque.
          </p>
        </div>

        {yo ? (
          <button onClick={reclamarAhora} disabled={reclamando}
            style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", cursor: "pointer", opacity: reclamando ? 0.7 : 1, boxShadow: "0 6px 18px rgba(255,69,0,.25)" }}>
            {reclamando ? "Reclamando…" : "Reclamar con mi cuenta"}
          </button>
        ) : (
          <>
            <button onClick={conGoogle}
              style={{ width: "100%", padding: "14px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, fontSize: 15.5, fontWeight: 500, color: C.text, letterSpacing: "-0.01em", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 10 }}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#111111" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Crear cuenta con Google
            </button>
            <button onClick={() => { try { localStorage.setItem("olbos_claim_token", token); } catch {} window.location.href = "/"; }}
              style={{ width: "100%", padding: "14px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em", cursor: "pointer" }}>
              Prefiero usar mi email
            </button>
            <p style={{ fontSize: 11.5, color: "#aeaeb2", textAlign: "center", marginTop: 14, lineHeight: 1.5, letterSpacing: "-0.005em" }}>
              Apenas crees tu cuenta y valides tu email, el perfil queda aplicado solo.
            </p>
          </>
        )}

        <div style={{ textAlign: "center", marginTop: 28, fontSize: 12, color: "#c9c3ba", fontStyle: "italic", letterSpacing: "-0.005em" }}>
          — El camino es el destino
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PÚBLICA DE EVENTO / SALIDA (no requiere login) ─────────────────────
// Galería pública (SIN login) de las fotos/videos de una salida. La ve cualquiera que
// abra el link del evento. Lee del RPC público get_galeria_evento.
const GaleriaEventoPublica = ({ eventoId }) => {
  const [items, setItems] = useState([]);
  const [viewer, setViewer] = useState(null);
  useEffect(() => {
    (async () => {
      const { data: raw } = await supabase.rpc("get_galeria_evento", { p_evento_id: eventoId });
      const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
      setItems(Array.isArray(arr) ? arr : []);
    })();
  }, [eventoId]);
  const mediaDe = (p) => {
    try { const m = p.media_urls ? JSON.parse(p.media_urls) : null; if (m && m.length) return m; } catch (e) {}
    return p.media_url ? [{ url: p.media_url, type: p.media_type }] : [];
  };
  const todos = items.flatMap(p => mediaDe(p).map((m, i) => ({ ...m, post: p, key: `${p.id}_${i}` })));
  if (todos.length === 0) return null;
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "14px 18px" }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>📸 Galería de la salida ({todos.length})</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
        {todos.map(m => {
          const esVideo = m.type === "video" || /\.(mp4|mov|webm)$/i.test(m.url || "");
          const thumb = esVideo ? (m.url || "").replace(/\.(mp4|mov|webm)$/i, ".jpg").replace("/upload/", "/upload/so_0/") : m.url;
          return (
            <button key={m.key} onClick={() => setViewer(m)} style={{ position: "relative", aspectRatio: "1", border: "none", padding: 0, borderRadius: 8, overflow: "hidden", cursor: "pointer", background: C.surface }}>
              <img loading="lazy" decoding="async" src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {esVideo && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.18)" }}><Play size={20} color="#fff" fill="#fff" /></span>}
            </button>
          );
        })}
      </div>
      {viewer && (
        <div onClick={() => setViewer(null)} style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,.93)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          {(viewer.type === "video" || /\.(mp4|mov|webm)$/i.test(viewer.url || ""))
            ? <video src={cldOpt(viewer.url, 1080)} poster={cldPoster(viewer.url, 480)} controls autoPlay playsInline onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "85%", borderRadius: 10 }} />
            : <img loading="lazy" decoding="async" src={viewer.url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "100%", maxHeight: "85%", objectFit: "contain", borderRadius: 10 }} />}
        </div>
      )}
    </div>
  );
};

// Mapa público (read-only, SIN login) del grupo en vivo de una rodada.
// Lo lee cualquiera que abra el link del evento. Polling cada 6s al RPC público.
// Se auto-oculta si la rodada no está en vivo (nadie compartiendo y estado != en_curso).
const MapaRodadaPublico = ({ eventoId, evento }) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markers = useRef({});
  const ajustado = useRef(false);
  const ultimaArr = useRef([]);
  const [count, setCount] = useState(0);
  const [mostrar, setMostrar] = useState(evento?.estado === "en_curso");

  const aplicar = (arr) => {
    ultimaArr.current = arr;
    setCount(arr.length);
    if (arr.length > 0) setMostrar(true);
    if (!mapInst.current || !window.google) return;
    const vistos = new Set();
    const bounds = new window.google.maps.LatLngBounds();
    arr.forEach(u => {
      if (!u.lat || !u.lng) return;
      vistos.add(u.user_id);
      const pos = { lat: Number(u.lat), lng: Number(u.lng) };
      bounds.extend(pos);
      if (markers.current[u.user_id]) {
        markers.current[u.user_id].setPosition(pos);
      } else {
        markers.current[u.user_id] = new window.google.maps.Marker({
          position: pos, map: mapInst.current, title: "@" + (u.username || "rider"),
          label: { text: (u.username || "?").slice(0, 2).toUpperCase(), color: "#fff", fontSize: "10px", fontWeight: "bold" },
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
        });
      }
    });
    Object.keys(markers.current).forEach(uid => { if (!vistos.has(uid)) { markers.current[uid].setMap(null); delete markers.current[uid]; } });
    if (!bounds.isEmpty() && !ajustado.current) { mapInst.current.fitBounds(bounds, 50); ajustado.current = true; }
  };

  const refetch = async () => {
    const { data: raw } = await supabase.rpc("get_rodada_publica", { p_evento_id: eventoId });
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(arr)) aplicar(arr);
  };

  // Poll solo cuando puede haber rodada en vivo: en curso o el día del evento (desde 4 h
  // antes). Antes era cada 6 s para siempre, también a un día del evento y en tipo
  // "evento": con miles de aperturas del link, 10 req/min por pestaña contra Supabase.
  useEffect(() => {
    const puedeHaberVivo = !!evento && (evento.estado === "en_curso" || esDiaDelEvento(evento));
    if (!puedeHaberVivo) return;
    refetch();
    const poll = setInterval(refetch, 6000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, evento?.estado]);

  // Construir el mapa recién cuando hay que mostrarlo
  useEffect(() => {
    if (!mostrar) return;
    let vivo = true;
    (async () => {
      await loadGoogleMaps();
      if (!vivo || !mapRef.current || !window.google || mapInst.current) return;
      mapInst.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: Number(evento?.lat) || -34.6037, lng: Number(evento?.lng) || -58.3816 },
        zoom: 12, disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      aplicar(ultimaArr.current);
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrar]);

  if (!mostrar) return null;
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 18px 8px" }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#2F7D4F", animation: "pulse 1.5s ease-in-out infinite" }} />
        <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Rodada en vivo</span>
        <span style={{ fontSize: 12, color: C.muted }}>· {count} {count === 1 ? "rider" : "riders"} en el mapa</span>
      </div>
      <div ref={mapRef} style={{ width: "100%", height: 300, background: "#1a1a22" }} />
    </div>
  );
};

// sumate = llegó por el link del grupo de WhatsApp: el botón es "Sumarme a la rodada" y,
// creada la cuenta, la app anota, revisa el teléfono y entra al mapa sin más pasos
const EventoPublico = ({ eventoId, sumate = false }) => {
  const [evento, setEvento] = useState(null);
  const [creador, setCreador] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: ev } = await supabase.from("eventos").select("*").eq("id", eventoId).maybeSingle();
      if (!ev) { setError(true); setCargando(false); return; }
      setEvento(ev);
      if (ev.creator_id) {
        const { data: p } = await supabase.from("perfiles").select("username, avatar_url, links").eq("id", ev.creator_id).maybeSingle();
        setCreador(p || null);
      }
      setCargando(false);
    })();
  }, [eventoId]);

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>;
  if (error || !evento) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Evento no disponible</h2>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Este evento es privado o ya no existe.</p>
      <button onClick={() => window.location.href = "/"} style={{ padding: "10px 22px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Ir a Olbos</button>
    </div>
  );

  const esSalida = evento.tipo !== "evento";
  const rutaOcultaPub = rutaOcultaDelEvento(evento);
  const puntosTodos = Array.isArray(evento.puntos) && evento.puntos.length > 0
    ? evento.puntos.filter(p => p && p.lat && p.lng)
    : (evento.lat && evento.lng ? [{ lat: evento.lat, lng: evento.lng, nombre: evento.lugar || evento.titulo }] : []);
  const puntos = rutaOcultaPub ? puntosTodos.slice(0, 1) : puntosTodos;
  // "Sábado 10 y domingo 11 de octubre · desde las 12:00": con el segundo día y la hora,
  // sin capitalizar palabra por palabra ("Sábado, 10 De Octubre De 2026")
  const fmtDia = (f, conMes) => { const d = new Date(String(f).slice(0, 10) + "T12:00:00"); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-AR", conMes ? { weekday: "long", day: "numeric", month: "long" } : { weekday: "long", day: "numeric" }).replace(/,/g, ""); };
  const hayFin = evento.fecha_fin && evento.fecha_fin !== evento.fecha;
  const fechaTxt = evento.fecha ? (hayFin ? `${fmtDia(evento.fecha, false)} y ${fmtDia(evento.fecha_fin, true)}` : fmtDia(evento.fecha, true)) : "";
  const cuandoTxt = [fechaTxt ? fechaTxt.charAt(0).toUpperCase() + fechaTxt.slice(1) : "", evento.hora ? `desde las ${String(evento.hora).slice(0, 5)}` : ""].filter(Boolean).join(" · ");
  const precioPub = Number(evento.precio_entrada || evento.precio || 0);
  // Los eventos de la agenda los carga el bot @olbos: "Organiza @olbos" hacía creer que el
  // evento era nuestro (feedback de organizadores reales)
  const esDeLaAgenda = evento.creator_id === "571720dc-a089-4e8a-a01b-7d1f92d99dbe";
  const irAnotarse = () => {
    if (sumate) {
      registrarUso("sumate_landing_tap", { id: eventoId, en_app_ajena: esNavegadorInApp() });
      try { localStorage.setItem("olbos_pending_sumate", JSON.stringify({ id: eventoId, t: Date.now() })); } catch (e) { /* */ }
      window.location.href = "/";
      return;
    }
    window.location.href = `/?evento=${eventoId}`;
  };

  return (
    <div style={{ minHeight: "100vh", background: C.card, paddingBottom: "calc(92px + var(--sab, 0px))" }}>
      <div style={{ padding: "var(--header-pt) 18px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 28, height: 28, borderRadius: 7 }} />
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · {esSalida ? "Salida" : "Evento"}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{sumate ? "Te invitaron a la rodada en vivo" : `Te invitaron a ${esSalida ? "esta salida" : "este evento"}`}</div>
          </div>
        </div>
        {/* Abrir Olbos con ESTE evento: antes iba a "/" y se perdía */}
        <button onClick={irAnotarse} style={{ padding: "8px 14px", borderRadius: 100, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Abrir Olbos</button>
      </div>

      {/* Mapa del grupo EN VIVO (solo aparece si la rodada está en curso) */}
      <MapaRodadaPublico eventoId={eventoId} evento={evento} />

      {(evento.foto_url || puntos.length > 0) && (
        <div style={{ width: "100%" }}>
          {evento.foto_url
            /* Entero (contain) sobre su propio fondo borroso: con cover un flyer vertical se veía por la mitad */
            ? <img decoding="async" src={cldOpt(evento.foto_url, 1080)} alt={evento.titulo} onError={ev => { if (ev.target.src !== evento.foto_url) ev.target.src = evento.foto_url; }}
                style={{ width: "100%", maxHeight: 300, objectFit: "contain", display: "block", background: cldBlur(evento.foto_url) ? `#000 url(${cldBlur(evento.foto_url)}) center/cover` : "#000" }} />
            : <MapaPreview puntos={puntos} alto={220} polyline={rutaOcultaPub ? null : evento.polyline} />}
        </div>
      )}

      {/* Galería pública de la salida (fotos/videos de los participantes) */}
      <GaleriaEventoPublica eventoId={eventoId} />

      <div style={{ padding: 18 }}>
        <h1 style={{ fontSize: 23, fontWeight: 700, color: C.text, lineHeight: 1.2, marginBottom: 8, letterSpacing: "-0.01em" }}>{evento.titulo}</h1>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {cuandoTxt && <div style={{ fontSize: 13.5, color: C.text, display: "flex", alignItems: "center", gap: 8 }}><Calendar size={15} strokeWidth={2} color={C.muted} /> {cuandoTxt}</div>}
          {esSalida && (evento.desde || evento.hasta) && <div style={{ fontSize: 13.5, color: C.text, display: "flex", alignItems: "center", gap: 8 }}><Navigation size={15} strokeWidth={2} color={C.muted} /> {evento.desde} {evento.hasta && !rutaOcultaPub ? `→ ${evento.hasta}` : ""}{evento.km ? ` · ${evento.km} km` : ""}</div>}
          {!esSalida && evento.lugar && <div style={{ fontSize: 13.5, color: C.text, display: "flex", alignItems: "center", gap: 8 }}><MapPin size={15} strokeWidth={2} color={C.muted} /> {evento.lugar}</div>}
          {creador && (
            <div style={{ fontSize: 13, color: C.muted, display: "flex", alignItems: "center", gap: 8 }}>
              <User size={15} strokeWidth={2} color={C.muted} />
              {esDeLaAgenda
                ? <span>Agenda de la comunidad · lo organiza quien figura en la descripción</span>
                : <span>Organiza <strong style={{ color: C.text }}>@{creador.username}</strong></span>}
            </div>
          )}
        </div>

        {/* Plegada a unos renglones con "Leer más": 800 px de descripción empujaban el botón
            de anotarse a 1.370 px del tope */}
        <DescripcionEvento texto={evento.descripcion} />

        {creador && !esDeLaAgenda && <LinksPerfil links={creador.links} style={{ marginBottom: 16 }} />}

        <p style={{ fontSize: 11.5, color: C.muted, textAlign: "center", lineHeight: 1.5 }}>
          Te anotás con un toque. La primera vez creás tu cuenta; después ya queda todo cargado.
          {esSalida ? " El día de la salida se ve al grupo en vivo en el mapa, así no se pierde nadie." : " Vas a ver quién va y las rodadas que llegan al evento."}
        </p>
      </div>

      {/* Barra inferior FIJA: la acción principal siempre a mano, no al fondo de la descripción */}
      <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 50, background: C.card, borderTop: `1px solid ${C.border}`, padding: "10px 16px calc(12px + var(--sab, 0px))" }}>
        <div style={{ maxWidth: 430, margin: "0 auto" }}>
          <button onClick={irAnotarse} style={{ width: "100%", padding: "13px 16px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{sumate ? "🏍️ Sumarme a la rodada" : "Anotarme"}</div>
            <div style={{ fontSize: 12, fontWeight: 500, opacity: .72, marginTop: 2 }}>
              {sumate ? "Entrás al mapa del grupo solo con tu nombre, sin crear cuenta." :`${precioPub > 0 ? `$${precioPub.toLocaleString("es-AR")}` : "Gratis"} · en Olbos, con un toque`}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PÚBLICA DE UNA COMUNIDAD (no requiere login) ───────────────────
// La dirección que un organizador manda a un municipio, a un sponsor o a un
// periodista: su identidad, los eventos que vienen, los que ya hizo y sus rutas,
// todo junto en una sola página que se comparte por link.
//
// Solo se muestra si la comunidad tiene al menos un evento público o una ruta
// publicada. Es a propósito: así el perfil de un rider común nunca queda
// expuesto en una página pública por tener un username.
const ComunidadPublica = ({ username }) => {
  const [perfil, setPerfil] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [anotados, setAnotados] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    (async () => {
      const limpio = String(username || "").replace(/[%,_]/g, "").trim();
      if (!limpio) { setCargando(false); return; }
      const { data: p } = await supabase.from("perfiles")
        .select("id, username, nombre, avatar_url, ciudad, bio, links")
        .ilike("username", limpio).limit(1).maybeSingle();
      if (!p) { setCargando(false); return; }
      setPerfil(p);

      const [rEv, rRu] = await Promise.all([
        supabase.from("eventos")
          .select("id, titulo, tipo, fecha, fecha_fin, lugar, desde, hasta, km, foto_url")
          .eq("creator_id", p.id).is("grupo_id", null).neq("visibilidad_tipo", "invitados")
          .order("fecha", { ascending: false }).limit(60),
        supabase.from("rutas")
          .select("id, nombre, km, desde, hasta, foto_url, diff")
          .eq("user_id", p.id).order("created_at", { ascending: false }).limit(12),
      ]);
      const evs = rEv.data || [];
      setEventos(evs);
      setRutas(rRu.data || []);

      // Cuánta gente movió: inscriptos sumados de todos sus eventos públicos.
      if (evs.length > 0) {
        const { count } = await supabase.from("inscripciones")
          .select("id", { count: "exact", head: true })
          .in("evento_id", evs.map(e => e.id));
        setAnotados(count || 0);
      }
      setCargando(false);
    })();
  }, [username]);

  const compartir = async () => {
    const url = window.location.href;
    const titulo = perfil ? (perfil.nombre || `@${perfil.username}`) : "Olbos";
    try {
      if (navigator.share) { await navigator.share({ title: titulo, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopiado(true); setTimeout(() => setCopiado(false), 2200);
    } catch (e) { /* el usuario canceló el compartir, no es un error */ }
  };

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>;

  const hayContenido = eventos.length > 0 || rutas.length > 0;
  if (!perfil || !hayContenido) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Comunidad no disponible</h2>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Esta comunidad todavía no publicó nada, o la dirección no existe.</p>
      <button onClick={() => { window.location.href = "/"; }} style={{ padding: "10px 22px", borderRadius: 100, background: C.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Ir a Olbos</button>
    </div>
  );

  // Los de hoy siguen contando como "lo que viene": una salida de hoy todavía no pasó.
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const finDe = (e) => new Date((e.fecha_fin || e.fecha) + "T12:00:00");
  const conFecha = eventos.filter(e => e.fecha);
  const proximos = conFecha.filter(e => finDe(e) >= hoy).sort((a, b) => a.fecha.localeCompare(b.fecha));
  const pasados = conFecha.filter(e => finDe(e) < hoy);
  const fmtFecha = (f) => new Date(f + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" });

  const FilaEvento = ({ e, apagado }) => (
    <a href={`/evento/${e.id}`} style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 0", borderBottom: `1px solid ${C.border}`, textDecoration: "none", opacity: apagado ? 0.62 : 1 }}>
      {e.foto_url
        ? <img loading="lazy" decoding="async" src={e.foto_url} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: C.surface }} />
        : <div style={{ width: 52, height: 52, borderRadius: 10, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Calendar size={19} strokeWidth={2} color={C.muted} /></div>}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.titulo}</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {[e.fecha && fmtFecha(e.fecha), e.lugar || (e.desde ? `${e.desde}${e.hasta ? ` → ${e.hasta}` : ""}` : ""), e.km ? `${e.km} km` : ""].filter(Boolean).join(" · ")}
        </div>
      </div>
    </a>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.card }}>
      <div style={{ padding: "var(--header-pt) 18px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#111111" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 26, height: 26, borderRadius: 7 }} />
          <div style={{ fontSize: 11, color: "#f9f5f299", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos · Comunidad</div>
        </div>
        <button onClick={compartir} style={{ padding: "7px 13px", borderRadius: 100, background: "#f9f5f21a", color: "#f9f5f2", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <Share2 size={13} strokeWidth={2.2} /> {copiado ? "Link copiado" : "Compartir"}
        </button>
      </div>

      {/* Identidad, en oscuro: es lo primero que ve un sponsor o un municipio */}
      <div style={{ background: "#111111", color: "#f9f5f2", padding: "6px 18px 26px" }}>
        {perfil.avatar_url
          ? <img loading="lazy" decoding="async" src={perfil.avatar_url} alt={perfil.nombre || perfil.username} style={{ width: 84, height: 84, borderRadius: "50%", objectFit: "cover", border: "2px solid #f9f5f233", marginBottom: 13 }} />
          : <div style={{ width: 84, height: 84, borderRadius: "50%", background: "#f9f5f21a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 13 }}><Users size={32} strokeWidth={1.8} color="#f9f5f2" /></div>}
        <h1 style={{ fontSize: 27, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.025em", margin: 0 }}>{perfil.nombre || `@${perfil.username}`}</h1>
        <div style={{ fontSize: 13, color: "#f9f5f299", marginTop: 5, display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <span>@{perfil.username}</span>
          {perfil.ciudad && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><MapPin size={12} strokeWidth={2} /> {perfil.ciudad}</span>}
        </div>
        {perfil.bio && <p style={{ fontSize: 14, color: "#f9f5f2cc", lineHeight: 1.6, marginTop: 13, marginBottom: 0, whiteSpace: "pre-wrap" }}>{perfil.bio}</p>}
        <LinksPerfil links={perfil.links} style={{ marginTop: 16 }} />
      </div>

      {/* Lo que movió, que es lo que le interesa a quien la recibe */}
      {(pasados.length > 0 || anotados > 0) && (
        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, background: C.surface }}>
          {pasados.length > 0 && (
            <div style={{ flex: 1, padding: "15px 18px", textAlign: "center" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{pasados.length}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>{pasados.length === 1 ? "salida hecha" : "salidas hechas"}</div>
            </div>
          )}
          {anotados > 0 && (
            <div style={{ flex: 1, padding: "15px 18px", textAlign: "center", borderLeft: pasados.length > 0 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ fontSize: 21, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}>{anotados}</div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>{anotados === 1 ? "rider anotado" : "riders anotados"}</div>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: 18 }}>
        {proximos.length > 0 && (
          <section style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Lo que viene</h2>
            {proximos.map(e => <FilaEvento key={e.id} e={e} />)}
          </section>
        )}

        {rutas.length > 0 && (
          <section style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Sus rutas</h2>
            {rutas.map(r => (
              <a key={r.id} href={`/ruta/${r.id}`} style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 0", borderBottom: `1px solid ${C.border}`, textDecoration: "none" }}>
                {r.foto_url
                  ? <img loading="lazy" decoding="async" src={r.foto_url} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: C.surface }} />
                  : <div style={{ width: 52, height: 52, borderRadius: 10, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Navigation size={18} strokeWidth={2} color={C.muted} /></div>}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[r.desde ? `${r.desde}${r.hasta ? ` → ${r.hasta}` : ""}` : "", r.km ? `${r.km} km` : "", r.diff].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </a>
            ))}
          </section>
        )}

        {pasados.length > 0 && (
          <section style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Lo que ya rodaron</h2>
            {pasados.slice(0, 8).map(e => <FilaEvento key={e.id} e={e} apagado />)}
          </section>
        )}

        {/* El cierre lleva a una acción concreta, no a la home: si hay una salida
            próxima, entra derecho a esa ficha (mismo camino que el link de un
            evento compartido, con el alta de cuenta adentro). Si no hay ninguna
            publicada, no prometemos un "seguir" que todavía no existe acá. */}
        <div style={{ padding: 16, background: C.surface, borderRadius: 12, textAlign: "center" }}>
          <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginTop: 0, marginBottom: 12 }}>
            {proximos.length > 0
              ? <>Su próxima salida es <strong>{proximos[0].titulo}</strong>{proximos[0].fecha ? `, el ${fmtFecha(proximos[0].fecha)}` : ""}. Te anotás con un toque desde Olbos.</>
              : "Todavía no publicaron la próxima salida. Entrá a Olbos y te enterás apenas la publiquen."}
          </p>
          <button onClick={() => { window.location.href = proximos.length > 0 ? `/?evento=${proximos[0].id}` : "/"; }} style={{ width: "100%", padding: "13px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 700 }}>
            {proximos.length > 0 ? "Anotarme en la próxima" : "Abrir Olbos"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PÚBLICA DE RUTA (no requiere login) ────────────────────────────
const RutaPublica = ({ trazaId, onVolver }) => {
  const [traza, setTraza] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const visitaRegistradaRef = useRef(false);
  // #5 Track colaborativo: contribuciones multimedia de cualquiera
  const [contribuciones, setContribuciones] = useState([]);
  const [subiendoContrib, setSubiendoContrib] = useState(false);
  const [nombreContrib, setNombreContrib] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("olbos_contrib_nombre")) || "");

  const cargarContribuciones = async () => {
    const { data: raw } = await supabase.rpc("get_traza_contribuciones", { p_traza_id: trazaId });
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(data)) setContribuciones(data);
  };

  const subirContribucion = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video");
    setSubiendoContrib(true);
    // Geo-tag opcional (no bloquea si el usuario no da permiso)
    const obtenerGeo = () => new Promise(resolve => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { timeout: 6000, enableHighAccuracy: false }
      );
    });
    try {
      const geo = await obtenerGeo();
      const fd = new FormData();
      fd.append("file", await achicarImagen(file));
      fd.append("upload_preset", "olbos_uploads");
      const endpoint = isVideo
        ? "https://api.cloudinary.com/v1_1/dhwhk48wb/video/upload"
        : "https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload";
      const res = await fetch(endpoint, { method: "POST", body: fd });
      const data = await res.json();
      if (!data.secure_url) throw new Error(data.error?.message || "no se recibió la URL");
      const autor = (nombreContrib || "").trim() || "Rider";
      if (typeof localStorage !== "undefined") localStorage.setItem("olbos_contrib_nombre", autor);
      const { data: rpcRaw } = await supabase.rpc("agregar_traza_contribucion", {
        p_traza_id: trazaId,
        p_url: data.secure_url,
        p_type: isVideo ? "video" : "image",
        p_autor: autor,
        p_lat: geo?.lat ?? null,
        p_lng: geo?.lng ?? null,
      });
      const rpc = typeof rpcRaw === "string" ? JSON.parse(rpcRaw) : rpcRaw;
      if (rpc?.error) throw new Error(rpc.error);
      await cargarContribuciones();
    } catch (e) {
      alert(`No se pudo subir ${isVideo ? "el video" : "la foto"}.\n${e.message || ""}`);
    }
    setSubiendoContrib(false);
  };

  useEffect(() => {
    (async () => {
      const { data: raw, error: err } = await supabase.rpc("get_traza_publica", { p_id: trazaId });
      if (err) { setError(err.message); setCargando(false); return; }
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (data?.error) { setError(data.error); setCargando(false); return; }
      setTraza(data);
      setCargando(false);
      cargarContribuciones();
      // Registrar la visita (el RPC valida que sea pública; los bots se marcan por user-agent)
      if (!visitaRegistradaRef.current) {
        visitaRegistradaRef.current = true;
        supabase.rpc("registrar_visita_traza", {
          p_traza_id: trazaId,
          p_referrer: typeof document !== "undefined" ? (document.referrer || null) : null,
          p_user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || null) : null,
        }).then(() => {}, () => {});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trazaId]);

  useEffect(() => {
    if (!traza || !mapRef.current || !traza.puntos || traza.puntos.length === 0) return;
    loadGoogleMaps().then(() => {
      if (!window.google || !mapRef.current) return;
      const path = traza.puntos.map(p => ({ lat: p.lat, lng: p.lng }));
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach(p => bounds.extend(p));
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          center: path[0],
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });
      }
      new window.google.maps.Polyline({
        path, strokeColor: "#111111", strokeOpacity: 0.9, strokeWeight: 4,
        map: mapInstanceRef.current,
      });
      new window.google.maps.Marker({
        position: path[0], map: mapInstanceRef.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#1F8347", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
        title: "Inicio",
      });
      new window.google.maps.Marker({
        position: path[path.length - 1], map: mapInstanceRef.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#111111", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
        title: "Fin",
      });
      // Markers de fotos con thumbnail
      (traza.fotos || []).forEach((f, i) => {
        if (f.lat && f.lng && f.url) {
          const isVideo = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url);
          let thumbUrl;
          if (isVideo) {
            thumbUrl = f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/").replace(/\.(mp4|mov|webm)$/i, ".jpg");
          } else {
            thumbUrl = f.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/");
          }
          new window.google.maps.Marker({
            position: { lat: f.lat, lng: f.lng },
            map: mapInstanceRef.current,
            icon: { url: thumbUrl, scaledSize: new window.google.maps.Size(36, 36), anchor: new window.google.maps.Point(18, 18) },
            title: isVideo ? `Video ${i+1}` : `Foto ${i+1}`,
          });
        }
      });
      mapInstanceRef.current.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
    });
  }, [traza]);

  // Markers de contribuciones de la comunidad (con borde azul para distinguirlas)
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google || contribuciones.length === 0) return;
    const markers = contribuciones.filter(c => c.lat && c.lng && c.url).map((c, i) => {
      const isVideo = c.type === "video" || /\.(mp4|mov|webm)$/i.test(c.url);
      const thumbUrl = isVideo
        ? c.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/").replace(/\.(mp4|mov|webm)$/i, ".jpg")
        : c.url.replace("/upload/", "/upload/c_thumb,w_80,h_80,g_center,r_max/");
      return new window.google.maps.Marker({
        position: { lat: c.lat, lng: c.lng },
        map: mapInstanceRef.current,
        icon: { url: thumbUrl, scaledSize: new window.google.maps.Size(34, 34), anchor: new window.google.maps.Point(17, 17) },
        title: `${c.autor_nombre || "Rider"} · ${isVideo ? "video" : "foto"}`,
        zIndex: 500 + i,
      });
    });
    return () => markers.forEach(m => m.setMap(null));
  }, [contribuciones, traza]);

  if (cargando) return (
    <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando recorrido…</div>
  );
  if (error) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Recorrido no disponible</h2>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Este recorrido es privado o ya no existe.</p>
      <button onClick={() => window.location.href = "/"} style={{
        padding: "10px 22px", borderRadius: 100,
        background: C.accent, color: "#fff",
        border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500,
      }}>Ir a Olbos</button>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.card, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "var(--header-pt) 18px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {onVolver ? (
          // Ya está adentro de Olbos: lo único que necesita es volver a donde estaba
          <button onClick={onVolver} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "8px 12px 8px 8px", borderRadius: 100,
            background: C.surface, border: `1px solid ${C.border}`, color: C.text,
            fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <ChevronLeft size={16} strokeWidth={2.4} /> Volver
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 28, height: 28, borderRadius: 7 }} />
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>Olbos</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, letterSpacing: "-0.005em" }}>Recorrido compartido</div>
            </div>
          </div>
        )}
        {onVolver ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "-0.005em" }}>Recorrido</div>
        ) : (
          <button onClick={() => window.location.href = "/"} style={{
            padding: "8px 14px", borderRadius: 100,
            background: C.accent, color: "#fff",
            border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
          }}>Abrir Olbos</button>
        )}
      </div>

      {/* Mapa */}
      <div ref={mapRef} style={{ width: "100%", height: 320, background: "#f6f1eb" }} />

      {/* Info */}
      <div style={{ padding: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.2, marginBottom: 6, letterSpacing: "-0.01em" }}>
          {traza.titulo || "Recorrido"}
        </h1>
        {traza.autor_username && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, letterSpacing: "-0.005em" }}>
            por <strong style={{ color: C.text }}>@{traza.autor_username}</strong>
            {traza.created_at && ` · ${new Date(traza.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" })}`}
          </div>
        )}

        {traza.descripcion && (
          <div style={{ padding: 12, background: C.surface, borderRadius: 10, fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: 14 }}>
            {traza.descripcion}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
          <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>{kmTraza(traza) ? kmTraza(traza).toFixed(1) : "—"}</div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Km</div>
          </div>
          <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>
              {traza.duracion_seg ? `${Math.floor(traza.duracion_seg / 3600)}h ${Math.floor((traza.duracion_seg % 3600) / 60)}m` : "—"}
            </div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Duración</div>
          </div>
          <div style={{ background: C.surface, borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>{traza.velocidad_prom ? Math.round(traza.velocidad_prom) : "—"}</div>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>km/h prom</div>
          </div>
        </div>

        {/* CTA para el que llega de afuera. Al que ya está adentro no se le ofrece
            sumarse a algo en lo que ya está: ahí no se muestra nada. */}
        {!onVolver && (
          <div style={{ padding: "16px 18px", borderRadius: 14, background: "linear-gradient(135deg, #111111, #2d2d30)", color: "#fff", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, letterSpacing: "-0.005em" }}>¿Te copó este recorrido?</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.75)", lineHeight: 1.5, marginBottom: 12 }}>
              Sumate gratis a Olbos para guardar rutas, coordinar salidas y salir a rodar con otros riders.
            </div>
            <button onClick={() => window.location.href = "/"} style={{
              width: "100%", padding: "11px", borderRadius: 10,
              background: C.card, color: C.text,
              border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em",
            }}>
              Abrir Olbos
            </button>
          </div>
        )}

        {/* Fotos del recorrido */}
        {traza.fotos && traza.fotos.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>
              Fotos ({traza.fotos.length})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {traza.fotos.map((f, i) => {
                const isVideo = f.type === "video" || /\.(mp4|mov|webm)$/i.test(f.url);
                return (
                  <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", background: "#000" }}>
                    {isVideo ? (
                      <video src={cldOpt(f.url, 720)} poster={cldPoster(f.url, 480)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted controls />
                    ) : (
                      <img loading="lazy" decoding="async" src={f.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* #5 — Fotos y videos de la comunidad (cualquiera puede subir) */}
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>
            De la comunidad{contribuciones.length > 0 ? ` (${contribuciones.length})` : ""}
          </div>
          <div style={{ padding: 14, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4, letterSpacing: "-0.005em" }}>¿Estuviste en este recorrido?</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
              Sumá tus fotos y videos. No hace falta tener cuenta en Olbos.
            </div>
            <input value={nombreContrib} onChange={e => setNombreContrib(e.target.value)} placeholder="Tu nombre (ej: Charly)"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", marginBottom: 10 }} />
            <label style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: "11px", borderRadius: 10,
              background: subiendoContrib ? C.surface : "#111111", color: subiendoContrib ? C.muted : "#fff",
              fontSize: 13, fontWeight: 600, cursor: subiendoContrib ? "wait" : "pointer", letterSpacing: "-0.005em",
            }}>
              {subiendoContrib ? "Subiendo…" : "📷 Subir foto o video"}
              <input type="file" accept="image/*,video/*" style={{ display: "none" }} disabled={subiendoContrib}
                onChange={e => { subirContribucion(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
          {contribuciones.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {contribuciones.map((c) => {
                const isVideo = c.type === "video" || /\.(mp4|mov|webm)$/i.test(c.url);
                return (
                  <div key={c.id} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", background: "#000" }}>
                    {isVideo
                      ? <video src={cldOpt(c.url, 720)} poster={cldPoster(c.url, 480)} preload="none" style={{ width: "100%", height: "100%", objectFit: "cover" }} muted controls />
                      : <img loading="lazy" decoding="async" src={c.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 6px 4px", background: "linear-gradient(transparent, rgba(0,0,0,.65))", fontSize: 10, color: "#fff", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.autor_nombre || "Rider"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Colaboradores de un evento: negocios de Pit Stop que participan (el lugar donde se
// come, el hospedaje, etc.). Aparecen en el evento y, si están verificados con cuenta,
// ven la lista de inscriptos y comprobantes desde su usuario — como un organizador más.
const ColaboradoresEditor = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const timerRef = useRef(null);
  const buscar = (texto) => {
    setQ(texto);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!texto.trim() || texto.trim().length < 2) { setResultados([]); return; }
    timerRef.current = setTimeout(async () => {
      const { data } = await supabase.from("negocios").select("id, user_id, nombre, categoria, ciudad")
        .eq("estado", "verificado").ilike("nombre", `%${texto.trim()}%`).limit(6);
      setResultados((data || []).filter(n => !(value || []).some(v => v.negocio_id === n.id)));
    }, 280);
  };
  const agregar = (n) => {
    onChange([...(value || []), { negocio_id: n.id, user_id: n.user_id, nombre: n.nombre, categoria: n.categoria || "" }]);
    setQ(""); setResultados([]);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Colaboradores (opcional)</div>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
        El lugar donde se come, el hospedaje o el negocio que participa del evento. Aparecen en la página del evento y pueden ver la lista de inscriptos y los comprobantes de pago desde su propia cuenta — como un organizador más.
      </div>
      {(value || []).map((cb, i) => (
        <div key={cb.negocio_id || i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 11px", borderRadius: 9, background: C.surface, marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", minWidth: 0 }}>
            {cb.nombre} {cb.categoria ? <span style={{ fontWeight: 500, color: C.muted }}>· {cb.categoria}</span> : null}
          </span>
          <button onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{ width: 26, height: 26, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", color: C.red, fontWeight: 700, flexShrink: 0 }}>✕</button>
        </div>
      ))}
      <input value={q} onChange={e => buscar(e.target.value)} placeholder="Buscar negocio de Pit Stop (ej: parrilla, hostería)…"
        style={{ width: "100%", padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
      {resultados.map(n => (
        <button key={n.id} onClick={() => agregar(n)}
          style={{ width: "100%", textAlign: "left", padding: "8px 11px", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, marginTop: 6, cursor: "pointer", fontSize: 13, color: C.text }}>
          <strong>{n.nombre}</strong> <span style={{ color: C.muted, fontSize: 12 }}>· {n.categoria}{n.ciudad ? ` · ${n.ciudad}` : ""}</span>
        </button>
      ))}
      {q.trim().length >= 2 && resultados.length === 0 && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Sin resultados — el negocio tiene que estar registrado y verificado en Pit Stop (es gratis, se hace desde la app).</div>
      )}
    </div>
  );
};

// ─── Co-organizadores de un evento ───────────────────────────────────────────
// El dueño suma usuarios que pueden EDITAR el evento con él (ej: Lhoa en las
// rodadas confluentes del BikerFest). Viven en inscripcion_config.co_organizadores
// y la policy de eventos + los RPCs de inscriptos los reconocen.
const CoOrganizadoresEvento = ({ evento, coOrgs }) => {
  const [lista, setLista] = useState(coOrgs || []);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const timerRef = useRef(null);
  const [guardando, setGuardando] = useState(false);
  const guardar = async (nueva) => {
    setGuardando(true);
    const cfg = { ...(evento.inscripcion_config || {}), co_organizadores: nueva };
    const { data: filas, error } = await supabase.from("eventos")
      .update({ inscripcion_config: cfg }).eq("id", evento.id).select("id");
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return false; }
    if (!filas || filas.length === 0) {
      alert("No se pudo guardar: esta cuenta no puede editar este evento.\n\nLos co-organizadores los suma el organizador del evento, o alguien a quien él ya haya sumado.");
      return false;
    }
    evento.inscripcion_config = cfg; // reflejar sin recargar
    setLista(nueva);
    return true;
  };
  const buscar = (texto) => {
    setQ(texto);
    if (timerRef.current) clearTimeout(timerRef.current);
    const t = texto.trim().replace(/^@/, "");
    if (t.length < 2) { setResultados([]); return; }
    timerRef.current = setTimeout(async () => {
      const { data } = await supabase.from("perfiles").select("id, username, avatar_url")
        .ilike("username", `%${t}%`).limit(6);
      setResultados((data || []).filter(p => p.id !== evento.creator_id && !lista.some(c => c.user_id === p.id)));
    }, 280);
  };
  // Con el teclado abierto, los resultados quedaban tapados: el buscador está al final
  // de una pantalla larga y el teclado se come la mitad de abajo. Al aparecer resultados
  // se acerca el buscador al centro de lo que queda visible.
  const cajaRef = useRef(null);
  useEffect(() => {
    if (resultados.length === 0 || !cajaRef.current) return;
    const t = setTimeout(() => {
      try { cajaRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { /* */ }
    }, 60);
    return () => clearTimeout(t);
  }, [resultados.length]);

  const agregar = async (p) => {
    if (await guardar([...lista, { user_id: p.id, username: p.username }])) {
      setQ(""); setResultados([]);
      alert("Listo: @" + p.username + " ya es co-organizador y le avisamos.");
      supabase.from("notificaciones").insert({
        user_id: p.id, tipo: "co_organizador", from_username: "olbos",
        texto: `te sumaron como co-organizador de «${evento.titulo || evento.title}» — ya podés editarlo y ver los inscriptos 🤝`,
      }).then(() => {}, () => {});
    }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>🤝 Co-organizadores</div>
      {lista.map((c, i) => (
        <div key={c.user_id || i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 11px", borderRadius: 9, background: C.surface, marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.text }}>@{c.username}</span>
          <button onClick={() => guardar(lista.filter((_, j) => j !== i))}
            style={{ width: 24, height: 24, borderRadius: "50%", background: `${C.red}14`, border: `1px solid ${C.red}22`, cursor: "pointer", color: C.red, fontWeight: 700, fontSize: 11 }}>✕</button>
        </div>
      ))}
      <div ref={cajaRef}>
        <input value={q} onChange={e => buscar(e.target.value)} placeholder="Sumar por @usuario — puede editar y ver inscriptos"
          style={{ width: "100%", padding: "9px 11px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, boxSizing: "border-box" }} />
        {q.trim().length >= 2 && resultados.length === 0 && (
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
            Ningún @usuario con ese nombre. Se busca por el usuario de Olbos, no por el nombre y apellido.
          </div>
        )}
        {resultados.map(p => (
          <button key={p.id} onClick={() => agregar(p)} disabled={guardando}
            style={{ width: "100%", textAlign: "left", padding: "10px 11px", borderRadius: 9, background: C.card, border: `1px solid ${C.border}`, marginTop: 6, cursor: guardando ? "default" : "pointer", fontSize: 13, color: C.text, fontWeight: 700, opacity: guardando ? 0.6 : 1 }}>
            {guardando ? "Sumando…" : "＋ @" + p.username}
          </button>
        ))}
        {/* Al tocar el resultado se guarda en el momento: no hay "guardar" aparte, y
            por eso conviene decirlo — si no, uno cree que le falta confirmar algo. */}
        {resultados.length > 0 && (
          <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>Se suma en el momento, no hace falta guardar nada más.</div>
        )}
      </div>
    </div>
  );
};

// ─── Pins con foto de perfil en los mapas en vivo ────────────────────────────
// Canvas circular cacheado: pesa lo mismo que un punto, se dibuja una sola vez.
const _iconosAvatar = {};
// ring: color del anillo exterior — identifica el rol en la rodada (adelante,
// atrás, quién graba). Sin ring, el borde blanco de siempre.
const iconoAvatarMapa = (url, cb, size = 36, ring = null) => {
  if (!url || !window.google || typeof document === "undefined") { cb(null); return; }
  const clave = `${url}|${size}|${ring || ""}`;
  if (_iconosAvatar[clave]) { cb(_iconosAvatar[clave]); return; }
  const img = new window.Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const s = size * 2;
      const c = document.createElement("canvas");
      c.width = s; c.height = s;
      const ctx = c.getContext("2d");
      ctx.save();
      ctx.beginPath(); ctx.arc(s / 2, s / 2, s / 2 - 4, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(img, 4, 4, s - 8, s - 8);
      ctx.restore();
      // Borde blanco interior siempre; si hay rol, un anillo de color por fuera
      ctx.beginPath(); ctx.arc(s / 2, s / 2, s / 2 - (ring ? 7 : 3), 0, Math.PI * 2);
      ctx.lineWidth = 5; ctx.strokeStyle = "#fff"; ctx.stroke();
      if (ring) {
        ctx.beginPath(); ctx.arc(s / 2, s / 2, s / 2 - 3, 0, Math.PI * 2);
        ctx.lineWidth = 6; ctx.strokeStyle = ring; ctx.stroke();
      }
      const icon = { url: c.toDataURL("image/png"), scaledSize: new window.google.maps.Size(size, size), anchor: new window.google.maps.Point(size / 2, size / 2) };
      _iconosAvatar[clave] = icon;
      cb(icon);
    } catch (e) { cb(null); }
  };
  img.onerror = () => cb(null);
  img.src = cldThumb(url, 100);
};

// ─── Cobros con Mercado Pago (marketplace) ───────────────────────────────────
// El organizador/negocio conecta SU cuenta de MP: los pagos van directo a él,
// Olbos nunca toca fondos. Tokens en mp_cuentas (solo service role, jamás cliente).
const MercadoPagoConnect = ({ soloDe }) => {
  const [estado, setEstado] = useState(null); // null = cargando o RPC sin correr
  const [uid, setUid] = useState(null);
  const [abriendo, setAbriendo] = useState(false);
  const [pestanaAbierta, setPestanaAbierta] = useState(false); // la autorización corre en otra pestaña
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setEstado({ conectado: false, sin_sesion: true }); return; }
      setUid(user.id);
      const { data: raw, error } = await supabase.rpc("mp_estado");
      if (error) { setEstado(null); return; } // SQL todavía sin correr → no mostrar nada
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      setEstado(d || { conectado: false });
    })();
  }, []);
  if (!estado || estado.sin_sesion) return null;
  if (soloDe && uid !== soloDe) return null; // el botón solo lo ve el dueño
  const conectar = async () => {
    setAbriendo(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/mp-oauth`, { method: "POST", headers: { Authorization: `Bearer ${session.access_token}` } });
      const j = await res.json();
      if (j.url) {
        // En pestaña nueva: si está en medio de crear un evento, el formulario NO se pierde
        // En la app nativa (iPhone sobre todo) window.open no abre nada dentro del WebView:
        // se abre en el navegador del sistema, igual que el pago del evento.
        let abierto = false;
        if (esNativo()) {
          try { const { Browser } = await import("@capacitor/browser"); await Browser.open({ url: j.url }); abierto = true; }
          catch (e) { /* sin el plugin, cae al window.open */ }
        }
        if (!abierto) window.open(j.url, "_blank");
        setPestanaAbierta(true);
        setAbriendo(false);
        return;
      }
      alert(j.error === "mp_no_configurado"
        ? "Los cobros con Mercado Pago se están terminando de habilitar — probá de nuevo en unos días."
        : "No se pudo iniciar la conexión con Mercado Pago. Probá de nuevo.");
    } catch (e) { alert("No se pudo iniciar la conexión con Mercado Pago. Probá de nuevo."); }
    setAbriendo(false);
  };
  const desvincular = async () => {
    if (!window.confirm("¿Desvincular esta cuenta de Mercado Pago? Dejás de cobrar hasta que conectes otra.")) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/mp-oauth`, { method: "DELETE", headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!res.ok) throw new Error("fallo");
      setEstado({ conectado: false });
      setPestanaAbierta(false);
    } catch (e) { alert("No se pudo desvincular la cuenta. Probá de nuevo."); }
  };
  const refrescar = async () => {
    const { data: raw } = await supabase.rpc("mp_estado");
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (d) { setEstado(d); if (d.conectado) setPestanaAbierta(false); }
  };
  const btnChico = { padding: "8px 12px", borderRadius: 10, background: C.surface, color: C.text, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" };
  if (pestanaAbierta && !estado.conectado) {
    return (
      <div style={{ padding: "10px 12px", borderRadius: 10, background: "#FFF9F0", border: `1px solid ${C.gold}55`, marginTop: 8, fontSize: 12, color: "#8A6D3B", lineHeight: 1.5 }}>
        Terminá la autorización en la pestaña de Mercado Pago. Cuando termines, tocá "Ya la terminé". Si no pudiste completarla, podés volver a intentar.
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button onClick={refrescar} style={btnChico}>Ya la terminé</button>
          <button onClick={() => { setPestanaAbierta(false); conectar(); }} style={btnChico}>Volver a intentar</button>
          <button onClick={() => setPestanaAbierta(false)} style={btnChico}>Cancelar</button>
        </div>
      </div>
    );
  }
  return estado.conectado ? (
    <div style={{ padding: "10px 12px", borderRadius: 10, background: `${C.green}14`, border: `1px solid ${C.green}33`, marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1F8347", flex: 1 }}>💳 Cobrás con Mercado Pago</span>
        <span style={{ fontSize: 11, color: C.muted }}>cuenta {estado.mp_user_id}</span>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button onClick={conectar} disabled={abriendo} style={btnChico}>{abriendo ? "Abriendo…" : "Cambiar cuenta"}</button>
        <button onClick={desvincular} style={{ ...btnChico, color: C.red }}>Desvincular</button>
      </div>
    </div>
  ) : (
    <button onClick={conectar} disabled={abriendo}
      style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, background: "#009EE3", color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer", opacity: abriendo ? 0.6 : 1 }}>
      {abriendo ? "Abriendo…" : "💳 Conectar Mercado Pago para cobrar"}
    </button>
  );
};

// Espacio de marca: próximos eventos oficiales creados por la cuenta de la marca.
// Si el espacio todavía es del bot @olbos (muestra curada, sin reclamar), se filtra
// por el nombre de la marca en el título — si no, TODOS los eventos de la agenda
// aparecerían en TODAS las marcas del bot.
const EventosDeMarca = ({ userId, nombreMarca }) => {
  const [eventos, setEventos] = useState([]);
  useEffect(() => {
    if (!userId) return;
    const esBot = userId === "571720dc-a089-4e8a-a01b-7d1f92d99dbe";
    let q = supabase.from("eventos").select("id, titulo, fecha, lugar")
      .eq("creator_id", userId).is("grupo_id", null)
      .gte("fecha", hoyLocalISO())
      .order("fecha", { ascending: true }).limit(5);
    if (esBot) {
      if (!nombreMarca) { setEventos([]); return; }
      q = q.ilike("titulo", `%${nombreMarca}%`);
    }
    q.then(({ data }) => setEventos(data || []), () => {});
  }, [userId, nombreMarca]);
  if (eventos.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Eventos oficiales</div>
      {eventos.map(e => (
        // Dentro de la app se abre el detalle interno, no la landing pública
        <button key={e.id} onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: e.id } }))}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: C.surface, marginBottom: 6, border: "none", width: "100%", cursor: "pointer", textAlign: "left" }}>
          <Calendar size={14} color={C.muted} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{e.titulo}</div>
            <div style={{ fontSize: 11, color: C.muted }}>
              {new Date(e.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}{e.lugar ? ` · ${e.lugar}` : ""}
            </div>
          </div>
          <ChevronRight size={14} color={C.muted} style={{ flexShrink: 0 }} />
        </button>
      ))}
    </div>
  );
};

// ─── Turnos de taller ────────────────────────────────────────────────────────
// Agenda simple: el rider pide turno desde la ficha del taller (Pit Stop) y el
// taller los gestiona desde su Panel de negocio (Perfil). Estados y avisos por campanita.
const TURNO_ESTADOS = {
  pendiente: { label: "Pendiente", color: "#8A6D3B", bg: "#8A6D3B18" },
  confirmado: { label: "Confirmado", color: "#2F7D4F", bg: "#2F7D4F18" },
  rechazado: { label: "No disponible", color: "#B3372C", bg: "#B3372C14" },
  cancelado: { label: "Cancelado", color: C.muted, bg: "#8A857F18" },
  atendido: { label: "Atendido", color: C.text, bg: "#11111110" },
};
const fmtFechaTurno = (f) => new Date(f + "T12:00:00").toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" });
const ChipTurno = ({ estado }) => {
  const e = TURNO_ESTADOS[estado] || TURNO_ESTADOS.pendiente;
  return <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 100, color: e.color, background: e.bg, flexShrink: 0 }}>{e.label}</span>;
};

// Ficha del taller (lado rider): botón "Pedir turno" + mis turnos con ese taller
// ─── PERFIL DE CIRCUITOS Y LOCALES: HORARIOS, TARIFAS Y EVENTOS ──────────────
// Un circuito no es un evento: abre habitualmente, con días, horarios y tarifas
// distintas según el vehículo. Todo esto lo administra el dueño de la ficha desde
// Más → Mi negocio, y se ve en el perfil junto con los eventos que se hacen ahí.
const DIAS_SEMANA = [["lun", "Lunes"], ["mar", "Martes"], ["mie", "Miércoles"], ["jue", "Jueves"], ["vie", "Viernes"], ["sab", "Sábado"], ["dom", "Domingo"]];
const VEHICULOS = [["moto", "🏍️ Moto"], ["cuatri", "🛺 Cuatri"], ["camioneta", "🛻 Camioneta / 4x4"]];
const CLAVE_DIA_HOY = () => ["dom", "lun", "mar", "mie", "jue", "vie", "sab"][new Date().getDay()];
const precioAR = (n) => {
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return null;
  return "$" + v.toLocaleString("es-AR");
};

// Ficha pública: qué días abre, a qué hora y cuánto sale entrar con cada vehículo.
// ─── BENEFICIOS PARA RIDERS EN PIT STOP ──────────────────────────────────────
// El pase se desbloquea estando en el lugar: no hay clave que tipear ni QR que
// escanear (la fricción del escaneo es justo lo que hace que estas cosas no se usen),
// y al comercio le mostramos a una persona con nombre y foto, que convence más que
// un número. De paso nos deja el dato que ninguna otra pata nos da: visitas reales.
const METROS_BENEFICIO = 300;

const PaseBeneficio = ({ negocio, onCerrar }) => {
  const [estado, setEstado] = useState("buscando"); // buscando | ok | lejos | error
  const [datos, setDatos] = useState(null);
  const [metros, setMetros] = useState(null);
  const [yo, setYo] = useState(null);
  const [reloj, setReloj] = useState(new Date());

  useEffect(() => { const id = setInterval(() => setReloj(new Date()), 1000); return () => clearInterval(id); }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setEstado("error"); return; }
      const { data: p } = await supabase.from("perfiles").select("username, avatar_url").eq("id", user.id).maybeSingle();
      if (vivo && p) setYo(p);
      // La posición no bloquea: si el GPS no contesta, se usa el pase igual y queda
      // marcado como no confirmado. Un rider parado en el mostrador no puede quedar
      // afuera porque el teléfono no enganchó satélites adentro de un local.
      let coords = null;
      try { coords = await obtenerPosicion({ timeout: 8000 }); } catch (e) { /* sigue sin coords */ }
      const { data: raw, error } = await supabase.rpc("usar_beneficio", {
        p_negocio_id: negocio.id,
        p_lat: coords ? coords.lat : null,
        p_lng: coords ? coords.lng : null,
      });
      if (!vivo) return;
      if (error) { setEstado("error"); return; }
      const d = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!d || !d.ok) { setEstado("error"); return; }
      setDatos(d);
      setMetros(d.metros);
      setEstado(d.lejos ? "lejos" : "ok");
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cerrar = (
    <button onClick={onCerrar} style={{ position: "absolute", top: 16, right: 16, width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.12)", border: "none", color: "#f9f5f2", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <X size={17} strokeWidth={2.4} />
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "#111111", color: "#f9f5f2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 26, textAlign: "center" }}>
      {cerrar}
      {estado === "buscando" && (
        <>
          <div style={{ width: 30, height: 30, border: "3px solid rgba(249,245,242,.25)", borderTopColor: "#f9f5f2", borderRadius: "50%", animation: "spin .8s linear infinite", marginBottom: 16 }} />
          <div style={{ fontSize: 13, opacity: 0.8 }}>Preparando tu pase…</div>
        </>
      )}

      {estado === "lejos" && (
        <>
          <div style={{ fontSize: 34, marginBottom: 12 }}>📍</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.01em" }}>Este beneficio se usa en el lugar</div>
          <div style={{ fontSize: 13.5, opacity: 0.75, lineHeight: 1.6, maxWidth: 320 }}>
            Estás a {metros != null ? (metros >= 1000 ? `${(metros / 1000).toFixed(1)} km` : `${Math.round(metros)} m`) : "buena distancia"} de {negocio.nombre}. Llegá y volvé a tocar el botón: el pase se arma solo.
          </div>
        </>
      )}

      {estado === "error" && (
        <>
          <div style={{ fontSize: 34, marginBottom: 12 }}>😕</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No pudimos armar el pase</div>
          <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.6, maxWidth: 320 }}>Probá de nuevo en unos segundos. Si sigue igual, mostrale igual tu perfil de Olbos al comercio.</div>
        </>
      )}

      {estado === "ok" && datos && (
        <>
          <div style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>Beneficio Olbos</div>
          <div style={{ fontSize: 15, fontWeight: 700, opacity: 0.9, marginBottom: 22 }}>{negocio.nombre}</div>

          <div style={{ fontSize: 27, fontWeight: 900, lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: datos.detalle ? 10 : 24, maxWidth: 340 }}>
            {datos.beneficio}
          </div>
          {datos.detalle && (
            <div style={{ fontSize: 12.5, opacity: 0.7, lineHeight: 1.5, marginBottom: 24, maxWidth: 320 }}>{datos.detalle}</div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 16px", borderRadius: 100, background: "rgba(249,245,242,.1)", marginBottom: 16 }}>
            {yo?.avatar_url
              ? <img loading="lazy" decoding="async" src={cldThumb(yo.avatar_url, 96)} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover" }} />
              : <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(249,245,242,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{(yo?.username || "?").charAt(0).toUpperCase()}</div>}
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>@{yo?.username || "rider"}</div>
              <div style={{ fontSize: 11, opacity: 0.6 }}>{datos.ya_usado ? "Ya lo usaste hoy" : "Rider de Olbos"}</div>
            </div>
          </div>

          {/* El reloj corriendo es lo que hace que una captura de pantalla no sirva */}
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", opacity: 0.85 }}>
            {reloj.toLocaleDateString("es-AR", { day: "numeric", month: "short" })} · {reloj.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <div style={{ fontSize: 11.5, opacity: 0.5, marginTop: 14, maxWidth: 300, lineHeight: 1.5 }}>
            Mostrale esta pantalla en el mostrador.
            {!datos.confirmada && " No pudimos confirmar tu ubicación."}
          </div>
        </>
      )}
    </div>
  );
};

// ─── TIENDA DE LOS NEGOCIOS (catálogo + venta con comisión) ─────────────────────
// Pedido de Carlos (08-oct-2026): que marcas, negocios y concesionarias vendan adentro
// de Olbos (accesorios, indumentaria, lo que sea) con una comisión mucho más chica que
// los marketplaces, y SIN publicar dos veces: lo que ya tienen en otra tienda se importa
// (archivo ahora; MercadoLibre y Tienda Nube con un botón cuando esté la app aprobada).
// Tabla productos (una fila por producto, fotos en jsonb) + pedidos (compras). El cobro
// va por Mercado Pago con el token del negocio y marketplace_fee = negocios.comision_tienda.
const CATEGORIAS_TIENDA = [["accesorios", "Accesorios"], ["indumentaria", "Indumentaria"], ["repuestos", "Repuestos"], ["motos", "Motos"], ["otros", "Otros"]];
const normTiendaTxt = (t) => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const nombreCatTienda = (k) => (CATEGORIAS_TIENDA.find(c => c[0] === k) || [null, "Otros"])[1];
const subirFotoProducto = async (file) => {
  const f = await achicarImagen(file, 1400);
  const fd = new FormData();
  fd.append("file", f);
  fd.append("upload_preset", "olbos_uploads");
  const res = await fetch("https://api.cloudinary.com/v1_1/dhwhk48wb/image/upload", { method: "POST", body: fd });
  const data = await res.json();
  if (!data.secure_url) throw new Error(data.error?.message || "no se pudo subir la foto");
  return data.secure_url;
};
// CSV simple (coma o punto y coma, comillas dobles): devuelve filas como objetos por encabezado
const parsearCSV = (texto) => {
  const t = String(texto || "").replace(/^\uFEFF/, "");
  const primera = t.split(/\r?\n/)[0] || "";
  const sep = (primera.match(/;/g) || []).length > (primera.match(/,/g) || []).length ? ";" : ",";
  const filas = []; let fila = [], campo = "", enComillas = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (enComillas) {
      if (ch === '"') { if (t[i + 1] === '"') { campo += '"'; i++; } else enComillas = false; }
      else campo += ch;
    } else if (ch === '"') enComillas = true;
    else if (ch === sep) { fila.push(campo); campo = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && t[i + 1] === "\n") i++; fila.push(campo); filas.push(fila); fila = []; campo = ""; }
    else campo += ch;
  }
  if (campo.length || fila.length) { fila.push(campo); filas.push(fila); }
  const limpias = filas.filter(f => f.some(c => String(c).trim()));
  if (limpias.length < 2) return [];
  const enc = limpias[0].map(h => String(h || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
  return limpias.slice(1).map(f => { const o = {}; enc.forEach((h, i) => { o[h] = f[i] != null ? String(f[i]).trim() : ""; }); return o; });
};
const campoCSV = (o, nombres) => { for (const n of nombres) { if (o[n] != null && o[n] !== "") return o[n]; } return ""; };
const precioDesdeTexto = (v) => { const n = Number(String(v || "").replace(/[^0-9,.-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".")); return isFinite(n) && n > 0 ? n : null; };

const ProductoModal = ({ p, negocio, onClose }) => {
  const fotos = Array.isArray(p.fotos) ? p.fotos : [];
  const [idx, setIdx] = useState(0);
  const [comprando, setComprando] = useState(false);
  const [entrega, setEntrega] = useState(p.envio === "envio" ? "envio" : "retiro");
  const sinStock = p.stock != null && Number(p.stock) <= 0;
  const wa = negocio && (negocio.whatsapp || negocio.telefono) ? String(negocio.whatsapp || negocio.telefono).replace(/[^0-9]/g, "") : null;
  // Publicaciones que vienen de MercadoLibre: la venta se hace ALLÁ, con nuestro link de
  // afiliado (Carlos, 08-oct). El vendedor no duplica stock ni logística, ML cobra y envía,
  // y Olbos cobra la comisión de afiliado. El cobro directo con Mercado Pago queda para lo
  // publicado en Olbos.
  const esML = p.origen === "ml" || /mercadolibre.com/.test(p.origen_url || "");
  const linkML = esML && p.origen_url ? p.origen_url + (p.origen_url.includes("?") ? "&" : "?") + "matt_tool=40541998&matt_word=olbos" : null;
  const txtWa = `Hola! Vi "${p.titulo}"${p.precio ? ` (${precioAR(p.precio)})` : ""} en tu tienda de Olbos. ¿Está disponible?`;
  const consultar = () => { if (wa) window.open(`https://wa.me/${wa}?text=${encodeURIComponent(txtWa)}`, "_blank", "noopener,noreferrer"); else alert("Este negocio no cargó WhatsApp. Probá por teléfono desde su ficha."); };
  const comprar = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { alert("Iniciá sesión para comprar."); return; }
    if (session.user.id === p.user_id) { alert("Es tu propio producto."); return; }
    setComprando(true);
    try {
      const { data: pedido, error } = await supabase.from("pedidos").insert({
        producto_id: p.id, negocio_id: p.negocio_id, comprador_id: session.user.id,
        titulo: p.titulo, cantidad: 1, precio_unit: p.precio, monto: p.precio, entrega, estado: "pendiente",
      }).select().single();
      if (error) throw error;
      const res = await fetch(`${API_BASE}/api/mp-pago`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ pedido_id: pedido.id }),
      });
      const j = await res.json();
      if (j.url) { window.location.href = j.url; return; }
      if (j.error === "negocio_sin_mp") {
        supabase.from("pedidos").update({ estado: "cancelado" }).eq("id", pedido.id).then(() => {}, () => {});
        if (window.confirm("Este negocio todavía no conectó Mercado Pago. ¿Le consultás por WhatsApp?")) consultar();
      } else throw new Error(j.detalle || j.error || "no se pudo iniciar el pago");
    } catch (e) { alert("No se pudo iniciar la compra: " + (e.message || e)); }
    setComprando(false);
  };
  return (
    <Modal title={p.titulo} onClose={onClose}>
      {fotos.length > 0 && (
        <div style={{ position: "relative", marginBottom: 12 }}>
          <div style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", borderRadius: 14, background: C.surface, scrollbarWidth: "none" }}
            onScroll={e => { const el = e.currentTarget; setIdx(Math.round(el.scrollLeft / Math.max(1, el.clientWidth))); }}>
            {fotos.map((u, i) => (
              <img key={i} src={cldOpt(u, 900)} alt="" style={{ flex: "0 0 100%", width: "100%", aspectRatio: "1", objectFit: "contain", scrollSnapAlign: "start", background: C.surface }} />
            ))}
          </div>
          {fotos.length > 1 && <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 6 }}>{fotos.map((_, i) => <span key={i} style={{ width: i === idx ? 16 : 6, height: 6, borderRadius: 3, background: i === idx ? C.text : C.border }} />)}</div>}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <div style={{ fontSize: 24, fontWeight: 900, color: C.text, letterSpacing: "-0.02em" }}>{p.precio ? precioAR(p.precio) : "Consultar"}</div>
        {p.stock != null && <div style={{ fontSize: 12, fontWeight: 700, color: sinStock ? C.red : C.green }}>{sinStock ? "Sin stock" : `${p.stock} disponible${Number(p.stock) === 1 ? "" : "s"}`}</div>}
      </div>
      {negocio && <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>Vende <strong style={{ color: C.text }}>{negocio.nombre}</strong>{p.categoria ? ` · ${nombreCatTienda(p.categoria)}` : ""}</div>}
      {p.descripcion && <p style={{ fontSize: 13.5, color: C.text, lineHeight: 1.55, whiteSpace: "pre-line", marginBottom: 12 }}>{p.descripcion}</p>}
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        {p.envio === "retiro" ? "📍 Retiro en el local" : p.envio === "envio" ? "📦 Envío a coordinar con el vendedor" : "📦 Envío a coordinar o retiro en el local"}
      </div>
      {!esML && p.modo === "pago" && p.precio && !sinStock && (
        <>
          {p.envio === "ambos" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {[["retiro", "Retiro en el local"], ["envio", "Envío"]].map(([k, l]) => (
                <button key={k} onClick={() => setEntrega(k)} style={{ flex: 1, padding: "9px", borderRadius: 10, border: `1.5px solid ${entrega === k ? C.text : C.border}`, background: entrega === k ? C.text : C.card, color: entrega === k ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
              ))}
            </div>
          )}
          <button onClick={comprar} disabled={comprando} style={{ width: "100%", padding: "14px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 8, opacity: comprando ? 0.6 : 1 }}>
            {comprando ? "Abriendo Mercado Pago…" : "💳 Comprar con Mercado Pago"}
          </button>
        </>
      )}
      <button onClick={consultar} style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#25D366", color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", marginBottom: 8 }}>💬 Consultar por WhatsApp</button>
      {esML && linkML && (
        <>
          <button onClick={() => window.open(linkML, "_blank", "noopener,noreferrer")} disabled={sinStock} style={{ width: "100%", padding: "14px", borderRadius: 12, background: "#FFE600", color: "#2D3277", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", marginBottom: 8, opacity: sinStock ? 0.5 : 1 }}>
            🛒 Comprar en MercadoLibre
          </button>
          <div style={{ fontSize: 11.5, color: C.muted, textAlign: "center", marginBottom: 8, lineHeight: 1.45 }}>La compra se hace en MercadoLibre, con el envío, el pago y la garantía de ahí.</div>
        </>
      )}
      {!esML && p.origen_url && <a href={p.origen_url} target="_blank" rel="noreferrer" style={{ display: "block", textAlign: "center", fontSize: 12, color: C.muted, textDecoration: "underline", marginTop: 4 }}>Ver la publicación original</a>}
    </Modal>
  );
};

const TarjetaProducto = ({ p, negocio, onClick, chico = false }) => {
  const foto = Array.isArray(p.fotos) && p.fotos[0];
  const sinStock = p.stock != null && Number(p.stock) <= 0;
  return (
    <button onClick={onClick} style={{ textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", padding: 0, cursor: "pointer", opacity: p.estado === "pausado" ? 0.55 : 1 }}>
      <div style={{ aspectRatio: "1", background: C.surface, position: "relative" }}>
        {foto ? <img loading="lazy" decoding="async" src={cldThumb(foto, 500)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30 }}>🛍️</div>}
        {sinStock && <span style={{ position: "absolute", top: 8, left: 8, fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 100, background: "rgba(13,13,18,.8)", color: "#fff" }}>Sin stock</span>}
        {p.estado === "pausado" && <span style={{ position: "absolute", top: 8, left: 8, fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 100, background: "rgba(13,13,18,.8)", color: "#fff" }}>Pausado</span>}
      </div>
      <div style={{ padding: "8px 10px 10px" }}>
        <div style={{ fontSize: chico ? 12 : 13, fontWeight: 700, color: C.text, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: chico ? 31 : 34 }}>{p.titulo}</div>
        <div style={{ fontSize: chico ? 13.5 : 15, fontWeight: 900, color: C.text, marginTop: 4, letterSpacing: "-0.01em" }}>{p.precio ? precioAR(p.precio) : "Consultar"}</div>
        {negocio && <div style={{ fontSize: 11, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{negocio.nombre}</div>}
      </div>
    </button>
  );
};

// En la ficha del negocio: su tienda, y las motos que tiene publicadas en Compra-Venta (30-oct,
// Carlos: "las motos de un local o una concesionaria se ven en su tienda y también en compra-venta")
const precioVenta = (v) => `${v.moneda === "USD" ? "US$" : "$"} ${Number(v.precio || 0).toLocaleString("es-AR")}`;
const TiendaFicha = ({ negocio, onVerVenta = null }) => {
  const [productos, setProductos] = useState(null);
  const [motos, setMotos] = useState([]);
  const [ver, setVer] = useState(null);
  const [todos, setTodos] = useState(false);
  const [todasMotos, setTodasMotos] = useState(false);
  useEffect(() => {
    if (!negocio?.id) return;
    supabase.from("productos").select("*").eq("negocio_id", negocio.id).eq("estado", "activo").order("created_at", { ascending: false }).limit(120)
      .then(({ data }) => setProductos(data || []), () => setProductos([]));
    // Los avisos que publicó la cuenta dueña de la ficha (o alguien como ella, con "Publicar como")
    if (negocio.user_id && negocio.user_id !== BOT_OLBOS_ID) {
      supabase.from("motos_venta").select("*").eq("user_id", negocio.user_id).eq("estado", "activa").order("created_at", { ascending: false }).limit(60)
        .then(({ data }) => setMotos(data || []), () => setMotos([]));
    }
  }, [negocio?.id, negocio?.user_id]);
  // ¿La maneja quien está mirando? Con la tienda vacía, a él se le dice cómo llenarla (Fran Voda,
  // 03-nov: cargó sus 6 concesionarias y "no se ven como tiendas" — la ficha no mostraba nada)
  const [manejo, setManejo] = useState(false);
  useEffect(() => {
    if (!negocio?.user_id || negocio.user_id === BOT_OLBOS_ID) return;
    let vivo = true;
    supabase.rpc("puedo_publicar_como", { p_cuenta: negocio.user_id }).then(({ data }) => { if (vivo) setManejo(data === true); }, () => {});
    return () => { vivo = false; };
  }, [negocio?.user_id]);
  const listaMotos = todasMotos ? motos : motos.slice(0, 6);
  const bloqueMotos = motos.length > 0 && (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>🏍️ En venta · {motos.length} aviso{motos.length === 1 ? "" : "s"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {listaMotos.map(v => (
          <button key={v.id} onClick={() => onVerVenta && onVerVenta(v)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", padding: 0, textAlign: "left", cursor: onVerVenta ? "pointer" : "default" }}>
            <div style={{ aspectRatio: "4 / 3", background: C.surface }}>
              {(v.foto_url || (v.fotos || [])[0]) && <img loading="lazy" decoding="async" src={cldThumb(v.foto_url || v.fotos[0], 400)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
            </div>
            <div style={{ padding: "8px 10px 10px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tituloVenta(v)}</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginTop: 2 }}>{precioVenta(v)}</div>
              {(v.año || v.km != null) && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{[v.año, v.km != null ? `${Number(v.km).toLocaleString("es-AR")} km` : null].filter(Boolean).join(" · ")}</div>}
            </div>
          </button>
        ))}
      </div>
      {motos.length > 6 && !todasMotos && <button onClick={() => setTodasMotos(true)} style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Ver los {motos.length} avisos</button>}
    </div>
  );
  if (productos && productos.length === 0 && !bloqueMotos && manejo) {
    return (
      <div style={{ marginBottom: 14, background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 12, padding: "12px 14px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 4 }}>🛍️ La tienda de este local está vacía</div>
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
          Aparece acá apenas publicás el primer producto (Más → Mis locales → Tienda del local) o una moto en Compra-Venta eligiendo este local en «Publicar como». Solo vos ves este aviso.
        </div>
      </div>
    );
  }
  if (!productos || productos.length === 0) return bloqueMotos || null;
  const lista = todos ? productos : productos.slice(0, 6);
  return (
    <>
    {bloqueMotos}
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>🛍️ Tienda · {productos.length} producto{productos.length === 1 ? "" : "s"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {lista.map(p => <TarjetaProducto key={p.id} p={p} onClick={() => setVer(p)} />)}
      </div>
      {productos.length > 6 && !todos && <button onClick={() => setTodos(true)} style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Ver los {productos.length} productos</button>}
      {ver && <ProductoModal p={ver} negocio={negocio} onClose={() => setVer(null)} />}
    </div>
    </>
  );
};

// Sección Tienda de Pit Stop: todo lo que venden los negocios de Olbos
const TiendaGeneral = () => {
  const [productos, setProductos] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [ver, setVer] = useState(null);
  useEffect(() => {
    supabase.from("productos").select("*, negocios(id, nombre, whatsapp, telefono, ciudad)").eq("estado", "activo").order("created_at", { ascending: false }).limit(300)
      .then(({ data }) => setProductos(data || []), () => setProductos([]));
  }, []);
  const qn = normTiendaTxt(q);
  const visibles = (productos || []).filter(p => (!cat || p.categoria === cat) && (!qn || normTiendaTxt(`${p.titulo} ${p.descripcion || ""} ${p.negocios?.nombre || ""}`).includes(qn)));
  return (
    <div style={{ padding: "12px 16px 90px" }}>
      <div style={{ position: "relative", marginBottom: 10 }}>
        <Search size={15} strokeWidth={2} color={C.muted} style={{ position: "absolute", left: 12, top: 12 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar producto o negocio…" type="search"
          style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", background: C.card }} />
      </div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 12, scrollbarWidth: "none" }}>
        {[["", "Todo"], ...CATEGORIAS_TIENDA].map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)} style={{ flex: "0 0 auto", padding: "7px 13px", borderRadius: 100, border: `1.5px solid ${cat === k ? C.text : C.border}`, background: cat === k ? C.text : C.card, color: cat === k ? "#fff" : C.text, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      {productos === null ? <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>Cargando…</div>
        : visibles.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🛍️</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>{productos.length === 0 ? "La tienda está arrancando" : "Nada con ese filtro"}</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>{productos.length === 0 ? "Los negocios y marcas de Olbos van a publicar acá sus accesorios, indumentaria y repuestos. ¿Tenés un negocio? Registralo y cargá tu tienda desde Más → Mi negocio." : "Probá con otra palabra o sacá el filtro."}</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {visibles.map(p => <TarjetaProducto key={p.id} p={p} negocio={p.negocios} onClick={() => setVer(p)} />)}
          </div>
        )}
      {ver && <ProductoModal p={ver} negocio={ver.negocios} onClose={() => setVer(null)} />}
    </div>
  );
};

// Alta / edición de un producto (dueño del negocio)
const FormProducto = ({ negocio, inicial, onClose, onGuardado }) => {
  const [f, setF] = useState(() => ({ titulo: "", descripcion: "", precio: "", stock: "", categoria: "accesorios", modo: "pago", envio: "ambos", fotos: [], ...(inicial || {}), precio: inicial?.precio ?? "", stock: inicial?.stock ?? "" }));
  const [subiendo, setSubiendo] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const agregarFotos = async (files) => {
    const lista = Array.from(files || []).slice(0, 8 - (f.fotos || []).length);
    if (!lista.length) return;
    setSubiendo(n => n + lista.length);
    for (const file of lista) {
      try { const url = await subirFotoProducto(file); setF(x => ({ ...x, fotos: [...(x.fotos || []), url] })); }
      catch (e) { alert("No se pudo subir una foto: " + (e.message || e)); }
      setSubiendo(n => Math.max(0, n - 1));
    }
  };
  const guardar = async () => {
    if (!f.titulo.trim()) { alert("Poné el título del producto."); return; }
    const precio = f.precio === "" ? null : Number(String(f.precio).replace(",", "."));
    if (f.modo === "pago" && !(precio > 0)) { alert("Para vender con Mercado Pago el producto necesita precio. Si es a consultar, elegí 'Consulta por WhatsApp'."); return; }
    setGuardando(true);
    const fila = {
      negocio_id: negocio.id, user_id: negocio.user_id,
      titulo: f.titulo.trim(), descripcion: f.descripcion.trim() || null, precio, moneda: "ARS",
      stock: f.stock === "" || f.stock == null ? null : Math.max(0, parseInt(f.stock, 10) || 0),
      categoria: f.categoria, modo: f.modo, envio: f.envio, fotos: f.fotos || [], updated_at: new Date().toISOString(),
    };
    const q = inicial?.id ? supabase.from("productos").update(fila).eq("id", inicial.id).select().single()
      : supabase.from("productos").insert({ ...fila, estado: "activo", origen: "olbos" }).select().single();
    const { data, error } = await q;
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onGuardado(data);
    onClose();
  };
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: "border-box", background: C.card };
  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 5, marginTop: 10 };
  return (
    <Modal title={inicial?.id ? "Editar producto" : "Nuevo producto"} onClose={onClose}>
      <div style={lbl}>Fotos (hasta 8)</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(f.fotos || []).map((u, i) => (
          <div key={i} style={{ position: "relative", width: 72, height: 72, borderRadius: 10, overflow: "hidden", background: C.surface }}>
            <img src={cldThumb(u, 200)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button onClick={() => set("fotos", f.fotos.filter((_, j) => j !== i))} style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.65)", border: "none", color: "#fff", fontSize: 11, cursor: "pointer" }}>✕</button>
          </div>
        ))}
        <label style={{ width: 72, height: 72, borderRadius: 10, border: `1.5px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.muted, fontSize: 22 }}>
          {subiendo > 0 ? <span style={{ fontSize: 11, fontWeight: 700 }}>Subiendo…</span> : "+"}
          <input type="file" accept="image/*" multiple onChange={e => agregarFotos(e.target.files)} style={{ display: "none" }} />
        </label>
      </div>
      <div style={lbl}>Título</div>
      <input value={f.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej: Casco LS2 Rapid II talle L" style={inp} />
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}><div style={lbl}>Precio (ARS)</div><input value={f.precio} onChange={e => set("precio", e.target.value)} inputMode="decimal" placeholder="0" style={inp} /></div>
        <div style={{ flex: 1 }}><div style={lbl}>Stock (vacío = sin control)</div><input value={f.stock} onChange={e => set("stock", e.target.value)} inputMode="numeric" placeholder="—" style={inp} /></div>
      </div>
      <div style={lbl}>Categoría</div>
      <select value={f.categoria} onChange={e => set("categoria", e.target.value)} style={inp}>{CATEGORIAS_TIENDA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <div style={lbl}>Descripción</div>
      <textarea value={f.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Talle, color, estado, qué incluye…" style={{ ...inp, minHeight: 80, resize: "vertical" }} />
      <div style={lbl}>Cómo se vende</div>
      <div style={{ display: "flex", gap: 8 }}>
        {[["pago", "💳 Pago en la app"], ["consulta", "💬 Consulta por WhatsApp"]].map(([k, l]) => (
          <button key={k} onClick={() => set("modo", k)} style={{ flex: 1, padding: "10px 6px", borderRadius: 10, border: `1.5px solid ${f.modo === k ? C.text : C.border}`, background: f.modo === k ? C.text : C.card, color: f.modo === k ? "#fff" : C.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      <div style={lbl}>Entrega</div>
      <div style={{ display: "flex", gap: 8 }}>
        {[["retiro", "Retiro"], ["envio", "Envío"], ["ambos", "Ambos"]].map(([k, l]) => (
          <button key={k} onClick={() => set("envio", k)} style={{ flex: 1, padding: "10px 6px", borderRadius: 10, border: `1.5px solid ${f.envio === k ? C.text : C.border}`, background: f.envio === k ? C.text : C.card, color: f.envio === k ? "#fff" : C.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      <button onClick={guardar} disabled={guardando || subiendo > 0} style={{ width: "100%", marginTop: 16, padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: (guardando || subiendo > 0) ? 0.6 : 1 }}>
        {guardando ? "Guardando…" : subiendo > 0 ? "Esperá las fotos…" : inicial?.id ? "Guardar cambios" : "Publicar producto"}
      </button>
    </Modal>
  );
};

// Panel del dueño: mis productos, importar, pedidos
const MisProductos = ({ negocio }) => {
  const [productos, setProductos] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [form, setForm] = useState(null); // null | {} (nuevo) | producto (editar)
  const [verImportar, setVerImportar] = useState(false);
  const [importando, setImportando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [ml, setMl] = useState(null); // null = cargando | false = no conectado | fila de ml_cuentas
  const [mlOcupado, setMlOcupado] = useState(false); // conectando o sincronizando
  const [tn, setTn] = useState(null); // null = cargando | false = no conectado | fila de tn_cuentas
  const [tnOcupado, setTnOcupado] = useState(false);
  const cargar = () => {
    if (!negocio?.id) return;
    supabase.from("productos").select("*").eq("negocio_id", negocio.id).order("created_at", { ascending: false }).then(({ data }) => setProductos(data || []), () => setProductos([]));
    supabase.from("pedidos").select("*, perfiles:comprador_id(username)").eq("negocio_id", negocio.id).neq("estado", "cancelado").order("created_at", { ascending: false }).limit(50).then(({ data }) => setPedidos(data || []), () => setPedidos([]));
    supabase.from("ml_cuentas").select("nickname, ml_user_id, ultima_sync").eq("negocio_id", negocio.id).maybeSingle().then(({ data }) => setMl(data || false), () => setMl(false));
    supabase.from("tn_cuentas").select("nombre_tienda, url, ultima_sync").eq("negocio_id", negocio.id).maybeSingle().then(({ data }) => setTn(data || false), () => setTn(false));
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [negocio?.id]);
  // Sin productos, abierta de entrada: plegada decía solo "0 publicados" y no se veía cómo empezar
  useEffect(() => { if (productos && productos.length === 0) setAbierto(true); }, [productos]);
  if (!negocio?.id) return null;
  // MercadoLibre: conectar la cuenta del negocio (OAuth en otra pestaña, igual que Mercado Pago)
  const conectarML = async () => {
    setMlOcupado(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/ml-oauth?accion=inicio`, { method: "POST", headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" }, body: JSON.stringify({ negocio_id: negocio.id }) });
      const j = await res.json().catch(() => ({}));
      if (j.url) {
        const w = window.open(j.url, "_blank");
        if (!w) window.location.href = j.url; // popup bloqueado → en la misma pestaña
        setMlOcupado(false);
        return;
      }
      alert(j.error === "ml_no_configurado"
        ? "La conexión con MercadoLibre se está terminando de habilitar — probá de nuevo en unos días. Mientras, podés importar el CSV."
        : "No se pudo iniciar la conexión con MercadoLibre: " + (j.error || "probá de nuevo"));
    } catch (e) { alert("No se pudo iniciar la conexión con MercadoLibre. Probá de nuevo."); }
    setMlOcupado(false);
  };
  const sincronizarML = async () => {
    setMlOcupado(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/ml-oauth?accion=sync`, { method: "POST", headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" }, body: JSON.stringify({ negocio_id: negocio.id }) });
      const j = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(`Listo: ${j.importados || 0} publicación${j.importados === 1 ? "" : "es"} de MercadoLibre actualizada${j.importados === 1 ? "" : "s"} en tu tienda${j.pausados ? ` y ${j.pausados} pausada${j.pausados === 1 ? "" : "s"} porque ya no están activas en ML` : ""}.${j.completo === false ? " Quedaron algunas por traer: tocá Sincronizar de nuevo en un rato." : ""}`);
        cargar();
      } else alert("No se pudo sincronizar con MercadoLibre: " + (j.error || res.status));
    } catch (e) { alert("No se pudo sincronizar con MercadoLibre. Probá de nuevo."); }
    setMlOcupado(false);
  };
  // Tienda Nube: misma mecánica que MercadoLibre (api/tiendanube-oauth.js)
  const conectarTN = async () => {
    setTnOcupado(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/tiendanube-oauth?accion=inicio`, { method: "POST", headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" }, body: JSON.stringify({ negocio_id: negocio.id }) });
      const j = await res.json().catch(() => ({}));
      if (j.url) {
        const w = window.open(j.url, "_blank");
        if (!w) window.location.href = j.url;
        setTnOcupado(false);
        return;
      }
      alert(j.error === "tn_no_configurado"
        ? "La conexión con Tienda Nube se está terminando de habilitar — probá de nuevo en unos días. Mientras, podés importar el CSV."
        : "No se pudo iniciar la conexión con Tienda Nube: " + (j.error || "probá de nuevo"));
    } catch (e) { alert("No se pudo iniciar la conexión con Tienda Nube. Probá de nuevo."); }
    setTnOcupado(false);
  };
  const sincronizarTN = async () => {
    setTnOcupado(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/tiendanube-oauth?accion=sync`, { method: "POST", headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" }, body: JSON.stringify({ negocio_id: negocio.id }) });
      const j = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(`Listo: ${j.importados || 0} producto${j.importados === 1 ? "" : "s"} de Tienda Nube actualizado${j.importados === 1 ? "" : "s"} en tu tienda${j.pausados ? ` y ${j.pausados} pausado${j.pausados === 1 ? "" : "s"} porque ya no están publicados` : ""}.${j.completo === false ? " Quedaron algunos por traer: tocá Sincronizar de nuevo en un rato." : ""}`);
        cargar();
      } else alert("No se pudo sincronizar con Tienda Nube: " + (j.error || res.status));
    } catch (e) { alert("No se pudo sincronizar con Tienda Nube. Probá de nuevo."); }
    setTnOcupado(false);
  };
  const cambiarEstado = async (p, estado) => {
    const { error } = await supabase.from("productos").update({ estado, updated_at: new Date().toISOString() }).eq("id", p.id);
    if (error) { alert("No se pudo: " + error.message); return; }
    setProductos(l => l.map(x => x.id === p.id ? { ...x, estado } : x));
  };
  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.titulo}"?`)) return;
    const { error } = await supabase.from("productos").delete().eq("id", p.id);
    if (error) { alert("No se pudo eliminar: " + error.message); return; }
    setProductos(l => l.filter(x => x.id !== p.id));
  };
  const marcarEntregado = async (pd) => {
    const { error } = await supabase.from("pedidos").update({ estado: "entregado", updated_at: new Date().toISOString() }).eq("id", pd.id);
    if (error) { alert("No se pudo: " + error.message); return; }
    setPedidos(l => l.map(x => x.id === pd.id ? { ...x, estado: "entregado" } : x));
  };
  const importarCSV = async (file) => {
    if (!file) return;
    setImportando(true);
    try {
      const texto = await file.text();
      const filas = parsearCSV(texto);
      const nuevos = filas.map(o => {
        const titulo = campoCSV(o, ["titulo", "title", "nombre", "name", "producto", "product"]);
        if (!titulo) return null;
        const foto = campoCSV(o, ["foto", "fotos", "imagen", "image", "image_link", "url de la foto", "url foto", "picture", "thumbnail"]);
        const precio = precioDesdeTexto(campoCSV(o, ["precio", "price", "precio de venta", "sale_price"]));
        const stockTxt = campoCSV(o, ["stock", "cantidad", "quantity", "available_quantity"]);
        const catTxt = normTiendaTxt(campoCSV(o, ["categoria", "category", "rubro"]));
        const categoria = (CATEGORIAS_TIENDA.find(([k, l]) => catTxt.includes(k) || catTxt.includes(normTiendaTxt(l))) || ["otros"])[0];
        return {
          negocio_id: negocio.id, user_id: negocio.user_id, titulo: titulo.slice(0, 200),
          descripcion: campoCSV(o, ["descripcion", "description", "detalle"]).slice(0, 4000) || null,
          precio, moneda: "ARS", stock: stockTxt === "" ? null : (parseInt(stockTxt, 10) || 0),
          categoria, fotos: foto ? foto.split(/[|,]\s*/).filter(u => /^https?:\/\//.test(u)).slice(0, 8) : [],
          modo: precio ? "pago" : "consulta", envio: "ambos", estado: "activo", origen: "csv",
          origen_url: campoCSV(o, ["url", "link", "permalink", "enlace"]) || null,
          origen_id: campoCSV(o, ["id", "sku", "codigo", "código"]) || null,
        };
      }).filter(Boolean);
      // Hay un índice único (negocio, origen, origen_id): si el CSV repite el id (variantes de
      // Tienda Nube, una fila por talle) se numeran, y re-importar el mismo archivo actualiza en vez de duplicar.
      const vistos = {};
      nuevos.forEach(n => { if (!n.origen_id) return; const k = n.origen_id; vistos[k] = (vistos[k] || 0) + 1; if (vistos[k] > 1) n.origen_id = `${k}-${vistos[k]}`; });
      if (!nuevos.length) { alert("No encontré productos en el archivo. Tiene que tener una columna de título o nombre (y si puede, precio, stock, foto y url)."); setImportando(false); return; }
      if (!window.confirm(`Se van a cargar ${nuevos.length} productos a tu tienda. ¿Dale?`)) { setImportando(false); return; }
      let ok = 0;
      for (let i = 0; i < nuevos.length; i += 50) {
        const { error, data } = await supabase.from("productos").upsert(nuevos.slice(i, i + 50), { onConflict: "negocio_id,origen,origen_id" }).select("id");
        if (error) { alert("Se cargaron " + ok + " y falló el resto: " + error.message); break; }
        ok += (data || []).length;
      }
      alert(`Listo: ${ok} productos cargados. Revisalos y pausá los que no correspondan.`);
      setVerImportar(false);
      cargar();
    } catch (e) { alert("No se pudo leer el archivo: " + (e.message || e)); }
    setImportando(false);
  };
  const activos = (productos || []).filter(p => p.estado === "activo").length;
  const pendEntrega = pedidos.filter(p => p.estado === "pagado").length;
  return (
    <div style={{ marginTop: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setAbierto(a => !a)} style={{ width: "100%", padding: "14px", background: "none", border: "none", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: "left" }}>
        <span style={{ fontSize: 18 }}>🛍️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Mi tienda</div>
          <div style={{ fontSize: 11.5, color: C.muted }}>{productos === null ? "…" : `${activos} publicado${activos === 1 ? "" : "s"}`}{pendEntrega ? ` · ${pendEntrega} pedido${pendEntrega === 1 ? "" : "s"} para entregar` : ""}</div>
        </div>
        <span style={{ color: C.muted }}>{abierto ? "▲" : "▼"}</span>
      </button>
      {abierto && (
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>Vendé accesorios, indumentaria, repuestos o motos adentro de Olbos. Comisión del {Number(negocio.comision_tienda ?? 5)}% por venta con Mercado Pago, nada si la venta es a consultar. Lo que traés de MercadoLibre se vende allá, en tu publicación, sin comisión nuestra. Para cobrar en la app conectá Mercado Pago más arriba.</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setForm({})} style={{ flex: 1.3, padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>+ Publicar producto</button>
            <button onClick={() => setVerImportar(v => !v)} style={{ flex: 1, padding: "11px", borderRadius: 10, background: C.surface, color: C.text, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>📥 Importar</button>
          </div>
          {verImportar && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, marginBottom: 12, fontSize: 12.5, color: C.text, lineHeight: 1.55 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Traé lo que ya tenés publicado, sin cargarlo de nuevo</div>
              <div style={{ color: C.muted, marginBottom: 8 }}>Subí el archivo CSV que exporta tu tienda (Tienda Nube, MercadoLibre, WooCommerce, Shopify o una planilla propia). Alcanza con las columnas <strong>título</strong> y <strong>precio</strong>; si vienen <strong>stock</strong>, <strong>foto</strong> (URL), <strong>descripción</strong>, <strong>categoría</strong> y <strong>url</strong>, mejor.</div>
              <label style={{ display: "block", padding: "11px", borderRadius: 10, background: C.card, border: `1.5px dashed ${C.border}`, textAlign: "center", fontWeight: 700, cursor: "pointer" }}>
                {importando ? "Cargando…" : "Elegir archivo CSV"}
                <input type="file" accept=".csv,text/csv" onChange={e => importarCSV(e.target.files && e.target.files[0])} style={{ display: "none" }} disabled={importando} />
              </label>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>MercadoLibre, con un botón</div>
                {ml ? (
                  <>
                    <div style={{ color: C.muted, marginBottom: 8 }}>
                      Conectado{ml.nickname ? <> como <strong style={{ color: C.text }}>@{ml.nickname}</strong></> : ""} · {ml.ultima_sync ? `última sincronización ${new Date(ml.ultima_sync).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "todavía sin sincronizar"}. Precio, stock y fotos se actualizan solos todas las mañanas; lo que pausás o vendés en ML se pausa acá.
                    </div>
                    <button onClick={sincronizarML} disabled={mlOcupado} style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: mlOcupado ? 0.6 : 1 }}>
                      {mlOcupado ? "Sincronizando…" : "🔄 Sincronizar ahora"}
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ color: C.muted, marginBottom: 8 }}>Conectá tu cuenta y tus publicaciones activas aparecen en tu tienda de Olbos, con precio, stock y fotos sincronizados todos los días. Sin cargar nada dos veces.</div>
                    <button onClick={conectarML} disabled={mlOcupado || ml === null} style={{ width: "100%", padding: "11px", borderRadius: 10, background: "#FFE600", color: "#2D3277", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: (mlOcupado || ml === null) ? 0.6 : 1 }}>
                      {mlOcupado ? "Abriendo…" : "🟡 Conectar MercadoLibre"}
                    </button>
                  </>
                )}
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>Tienda Nube, con un botón</div>
                {tn ? (
                  <>
                    <div style={{ color: C.muted, marginBottom: 8 }}>
                      Conectado{tn.nombre_tienda ? <> con <strong style={{ color: C.text }}>{tn.nombre_tienda}</strong></> : ""} · {tn.ultima_sync ? `última sincronización ${new Date(tn.ultima_sync).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "todavía sin sincronizar"}. Precio, stock y fotos se actualizan solos todas las mañanas; lo que despublicás allá se pausa acá.
                    </div>
                    <button onClick={sincronizarTN} disabled={tnOcupado} style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: tnOcupado ? 0.6 : 1 }}>
                      {tnOcupado ? "Sincronizando…" : "🔄 Sincronizar ahora"}
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ color: C.muted, marginBottom: 8 }}>Conectá tu Tienda Nube y tus productos publicados aparecen en tu tienda de Olbos, con precio, stock y fotos sincronizados todos los días.</div>
                    <button onClick={conectarTN} disabled={tnOcupado || tn === null} style={{ width: "100%", padding: "11px", borderRadius: 10, background: "#2C3357", color: "#fff", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer", opacity: (tnOcupado || tn === null) ? 0.6 : 1 }}>
                      {tnOcupado ? "Abriendo…" : "🔵 Conectar Tienda Nube"}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {pedidos.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Pedidos</div>
              {pedidos.map(pd => (
                <div key={pd.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, background: C.surface, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pd.titulo}</div>
                    <div style={{ fontSize: 11.5, color: C.muted }}>@{pd.perfiles?.username || "rider"} · {precioAR(pd.monto)} · {pd.entrega === "envio" ? "envío" : "retiro"} · {new Date(pd.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short" })}</div>
                  </div>
                  {pd.estado === "pagado" ? <button onClick={() => marcarEntregado(pd)} style={{ padding: "7px 10px", borderRadius: 8, background: C.green, color: "#fff", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Entregado</button>
                    : <span style={{ fontSize: 11, fontWeight: 800, color: pd.estado === "entregado" ? C.green : C.muted }}>{pd.estado === "entregado" ? "✓ Entregado" : "Pago pendiente"}</span>}
                </div>
              ))}
            </div>
          )}
          {productos && productos.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {productos.map(p => (
                <div key={p.id} style={{ display: "flex", flexDirection: "column" }}>
                  <TarjetaProducto p={p} chico onClick={() => setForm(p)} />
                  <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                    <button onClick={() => cambiarEstado(p, p.estado === "activo" ? "pausado" : "activo")} style={{ flex: 1, padding: "6px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{p.estado === "activo" ? "Pausar" : "Activar"}</button>
                    <button onClick={() => eliminar(p)} style={{ padding: "6px 9px", borderRadius: 8, background: "transparent", border: `1px solid ${C.border}`, color: C.red, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {productos && productos.length === 0 && <div style={{ fontSize: 12.5, color: C.muted, textAlign: "center", padding: "10px 0" }}>Todavía no publicaste nada.</div>}
        </div>
      )}
      {form && <FormProducto negocio={negocio} inicial={form.id ? form : null} onClose={() => setForm(null)} onGuardado={(p) => setProductos(l => { const existe = (l || []).some(x => x.id === p.id); return existe ? l.map(x => x.id === p.id ? p : x) : [p, ...(l || [])]; })} />}
    </div>
  );
};

const BeneficioFicha = ({ negocio }) => {
  const [abierto, setAbierto] = useState(false);
  if (!negocio || !negocio.beneficio_activo || !String(negocio.beneficio || "").trim()) return null;
  return (
    <>
      <div style={{ marginBottom: 14, padding: "14px 16px", borderRadius: 14, background: "#111111", color: "#f9f5f2" }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.55, marginBottom: 5 }}>Beneficio para riders</div>
        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.01em", marginBottom: negocio.beneficio_detalle ? 5 : 12 }}>{negocio.beneficio}</div>
        {negocio.beneficio_detalle && <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5, marginBottom: 12 }}>{negocio.beneficio_detalle}</div>}
        <button onClick={() => setAbierto(true)}
          style={{ width: "100%", padding: "11px", borderRadius: 10, background: "#f9f5f2", color: "#111111", border: "none", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
          Usar el beneficio
        </button>
        <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 8, lineHeight: 1.4 }}>Se activa estando en el lugar. Sin códigos ni escaneos.</div>
      </div>
      {abierto && <PaseBeneficio negocio={negocio} onCerrar={() => setAbierto(false)} />}
    </>
  );
};

// Lo que ve el dueño en Más → Mi negocio
// Sin local fijo: para instructores, escuelas itinerantes y cualquiera que trabaje
// yendo a distintos lugares en vez de esperar en un mostrador.
const EditorSinLocal = ({ negocio, onGuardado }) => {
  const [itinerante, setItinerante] = useState(!!negocio?.itinerante);
  const [zona, setZona] = useState(negocio?.zona_cobertura || "");
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    const campos = { itinerante, zona_cobertura: zona.trim() || null };
    const { error } = await supabase.from("negocios").update(campos).eq("id", negocio.id);
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onGuardado && onGuardado(campos);
    alert("Listo.");
  };

  return (
    <div style={{ marginTop: 16, padding: "14px 15px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 3 }}>¿Trabajás sin local fijo?</div>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 11 }}>
        Para instructores y escuelas que dan clases en distintos circuitos. En tu ficha, en vez de una dirección, se muestra la zona donde trabajás, y tus clases aparecen con el lugar de cada una.
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11, cursor: "pointer" }}>
        <input type="checkbox" checked={itinerante} onChange={e => setItinerante(e.target.checked)} style={{ width: 17, height: 17, accentColor: "#111111" }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>No tengo local fijo</span>
      </label>
      {itinerante && (
        <input value={zona} onChange={e => setZona(e.target.value.slice(0, 120))} placeholder="Dónde das clases: ej. circuitos de zona norte, Escobar y Cañuelas"
          style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box", marginBottom: 11 }} />
      )}
      <button onClick={guardar} disabled={guardando}
        style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: guardando ? "default" : "pointer", opacity: guardando ? 0.6 : 1 }}>
        {guardando ? "Guardando…" : "Guardar"}
      </button>
    </div>
  );
};

const EditorBeneficio = ({ negocio, onGuardado }) => {
  const [texto, setTexto] = useState(negocio?.beneficio || "");
  const [detalle, setDetalle] = useState(negocio?.beneficio_detalle || "");
  const [activo, setActivo] = useState(!!negocio?.beneficio_activo);
  const [guardando, setGuardando] = useState(false);
  const [canjes, setCanjes] = useState(null);

  useEffect(() => {
    if (!negocio?.id) return;
    supabase.from("beneficio_canjes").select("id", { count: "exact", head: true }).eq("negocio_id", negocio.id)
      .then(({ count }) => setCanjes(count ?? 0), () => setCanjes(null));
  }, [negocio?.id]);

  const guardar = async () => {
    setGuardando(true);
    const campos = { beneficio: texto.trim() || null, beneficio_detalle: detalle.trim() || null, beneficio_activo: activo && !!texto.trim() };
    const { error } = await supabase.from("negocios").update(campos).eq("id", negocio.id);
    setGuardando(false);
    if (error) { alert("No se pudo guardar: " + error.message); return; }
    onGuardado && onGuardado(campos);
    alert("Listo. El beneficio ya se ve en tu ficha.");
  };

  return (
    <div style={{ marginTop: 16, padding: "14px 15px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 3 }}>Beneficio para riders</div>
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 11 }}>
        Sin cuota y sin comisión: lo único que ponés es el beneficio, y recién cuando el rider ya está en tu local. Nadie tiene que escanear ni tipear nada.
      </div>
      <input value={texto} onChange={e => setTexto(e.target.value.slice(0, 70))} placeholder="10% en indumentaria"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box", marginBottom: 8 }} />
      <input value={detalle} onChange={e => setDetalle(e.target.value.slice(0, 120))} placeholder="Condiciones (opcional): no acumulable, de lunes a viernes…"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box", marginBottom: 10 }} />
      <label style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12, cursor: "pointer" }}>
        <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)} style={{ width: 17, height: 17, accentColor: "#111111" }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Mostrarlo en mi ficha</span>
      </label>
      {canjes != null && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          {canjes === 0 ? "Todavía no lo usó nadie." : `${canjes} ${canjes === 1 ? "rider lo usó" : "riders lo usaron"} en tu local.`}
        </div>
      )}
      <button onClick={guardar} disabled={guardando}
        style={{ width: "100%", padding: "11px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 800, cursor: guardando ? "default" : "pointer", opacity: guardando ? 0.6 : 1 }}>
        {guardando ? "Guardando…" : "Guardar beneficio"}
      </button>
    </div>
  );
};

const HorariosTarifasFicha = ({ negocio }) => {
  const hs = negocio && typeof negocio.horarios_semana === "object" && negocio.horarios_semana ? negocio.horarios_semana : null;
  const tarifas = Array.isArray(negocio?.tarifas) ? negocio.tarifas.filter(t => t && precioAR(t.precio)) : [];
  if (!hs && tarifas.length === 0) return null;
  const hoy = CLAVE_DIA_HOY();
  const abreHoy = hs && hs[hoy] && !hs[hoy].cerrado && hs[hoy].abre && hs[hoy].cierra;
  return (
    <div style={{ marginBottom: 14 }}>
      {hs && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Días y horarios</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: tarifas.length ? 14 : 0 }}>
            {DIAS_SEMANA.map(([k, label], i) => {
              const d = hs[k] || {};
              const cerrado = d.cerrado || !d.abre || !d.cierra;
              const esHoy = k === hoy;
              return (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", borderTop: i === 0 ? "none" : `1px solid ${C.border}`, background: esHoy ? `${C.gold}14` : "transparent" }}>
                  <span style={{ fontSize: 13, fontWeight: esHoy ? 800 : 600, color: C.text }}>{label}{esHoy ? " · hoy" : ""}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: cerrado ? C.muted : C.green }}>{cerrado ? "Cerrado" : `${d.abre} a ${d.cierra}`}</span>
                </div>
              );
            })}
          </div>
          {abreHoy && (
            <div style={{ fontSize: 12, color: C.green, fontWeight: 700, marginTop: -6, marginBottom: 12 }}>🟢 Hoy abre de {hs[hoy].abre} a {hs[hoy].cierra}</div>
          )}
        </>
      )}
      {tarifas.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Tarifas</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
            {tarifas.map((t, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: i === 0 ? "none" : `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{(VEHICULOS.find(v => v[0] === t.vehiculo) || [null, t.vehiculo || "Entrada"])[1]}</div>
                  {t.concepto && <div style={{ fontSize: 11.5, color: C.muted }}>{t.concepto}</div>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.text, flexShrink: 0 }}>{precioAR(t.precio)}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Precios informados por el local. Confirmalos antes de ir.</div>
        </>
      )}
    </div>
  );
};

// Eventos que se hacen en este lugar: los vinculados a la ficha y los que creó su dueño.
// Para un local, los eventos son "acá". Para un instructor son sus clases y clínicas,
// cada una en un lugar distinto — y son lo más importante de su ficha.
const EventosDelNegocio = ({ negocio }) => {
  const [eventos, setEventos] = useState([]);
  useEffect(() => {
    if (!negocio?.id) return;
    let vivo = true;
    const hoy = new Date().toISOString().slice(0, 10);
    const cols = "id, titulo, fecha, hora, lugar, foto_url, tipo";
    (async () => {
      // Vinculados por negocio_id (si la columna existe) o creados por el dueño
      let filas = null;
      const or = [`negocio_id.eq.${negocio.id}`];
      if (negocio.user_id) or.push(`creator_id.eq.${negocio.user_id}`);
      const r1 = await supabase.from("eventos").select(cols).or(or.join(",")).gte("fecha", hoy).order("fecha", { ascending: true }).limit(6);
      if (!r1.error) filas = r1.data;
      else if (negocio.user_id) {
        const r2 = await supabase.from("eventos").select(cols).eq("creator_id", negocio.user_id).gte("fecha", hoy).order("fecha", { ascending: true }).limit(6);
        if (!r2.error) filas = r2.data;
      }
      if (vivo && Array.isArray(filas)) setEventos(filas);
    })();
    return () => { vivo = false; };
  }, [negocio?.id, negocio?.user_id]);

  if (eventos.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
        {negocio?.itinerante ? "Próximas clases y clínicas" : "Próximos eventos acá"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {eventos.map(ev => (
          <button key={ev.id} onClick={() => window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: ev.id } }))}
            style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left", padding: 10, borderRadius: 12, background: C.card, border: `1px solid ${C.border}`, cursor: "pointer" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: ev.foto_url ? `url(${cldThumb(ev.foto_url, 120)}) center/cover` : C.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {!ev.foto_url && <span style={{ fontSize: 18 }}>📅</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.titulo}</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>
                {new Date(ev.fecha + "T12:00:00").toLocaleDateString("es-AR", { day: "numeric", month: "short" })}{ev.hora ? ` · ${ev.hora}` : ""}
              </div>
            </div>
            <ChevronRight size={16} strokeWidth={2} color={C.muted} />
          </button>
        ))}
      </div>
    </div>
  );
};

// Editor para el dueño de la ficha: días, horarios y tarifas por vehículo.
const EditorHorariosTarifas = ({ negocio, onGuardado }) => {
  const [abierto, setAbierto] = useState(false);
  const [hs, setHs] = useState(() => {
    const base = {};
    DIAS_SEMANA.forEach(([k]) => { base[k] = { abre: "09:00", cierra: "18:00", cerrado: true }; });
    const guardado = negocio && typeof negocio.horarios_semana === "object" && negocio.horarios_semana ? negocio.horarios_semana : {};
    DIAS_SEMANA.forEach(([k]) => { if (guardado[k]) base[k] = { ...base[k], ...guardado[k] }; });
    return base;
  });
  const [tarifas, setTarifas] = useState(() => (Array.isArray(negocio?.tarifas) ? negocio.tarifas : []));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const setDia = (k, campo, valor) => setHs(p => ({ ...p, [k]: { ...p[k], [campo]: valor } }));
  const addTarifa = () => setTarifas(t => [...t, { vehiculo: "moto", concepto: "", precio: "" }]);
  const setTarifa = (i, campo, valor) => setTarifas(t => t.map((x, j) => j === i ? { ...x, [campo]: valor } : x));
  const delTarifa = (i) => setTarifas(t => t.filter((_, j) => j !== i));

  const guardar = async () => {
    setGuardando(true); setError("");
    const limpias = tarifas
      .filter(t => Number(t.precio) > 0)
      .map(t => ({ vehiculo: t.vehiculo, concepto: (t.concepto || "").trim(), precio: Number(t.precio) }));
    const { error: e } = await supabase.from("negocios")
      .update({ horarios_semana: hs, tarifas: limpias, updated_at: new Date().toISOString() })
      .eq("id", negocio.id);
    setGuardando(false);
    if (e) { setError(/column|schema cache/i.test(e.message || "") ? "Falta correr el SQL de horarios y tarifas." : e.message); return; }
    if (onGuardado) onGuardado({ horarios_semana: hs, tarifas: limpias });
    setAbierto(false);
  };

  const inp = { padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13, fontFamily: "inherit" };
  return (
    <div style={{ marginTop: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <button onClick={() => setAbierto(a => !a)}
        style={{ width: "100%", padding: "12px 14px", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>🕒 Días, horarios y tarifas</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Lo que ve el rider cuando abre tu ficha</div>
        </div>
        <span style={{ fontSize: 12, color: C.muted }}>{abierto ? "▲" : "▼"}</span>
      </button>
      {abierto && (
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "4px 0 8px" }}>Horarios</div>
          {DIAS_SEMANA.map(([k, label]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <button onClick={() => setDia(k, "cerrado", !hs[k].cerrado)}
                style={{ width: 92, flexShrink: 0, textAlign: "left", padding: "7px 8px", borderRadius: 8, border: `1px solid ${C.border}`, background: hs[k].cerrado ? C.surface : `${C.green}14`, color: hs[k].cerrado ? C.muted : C.green, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {label}
              </button>
              {hs[k].cerrado ? (
                <span style={{ fontSize: 12, color: C.muted }}>Cerrado · tocá el día para abrir</span>
              ) : (
                <>
                  <input type="time" value={hs[k].abre || ""} onChange={e => setDia(k, "abre", e.target.value)} style={{ ...inp, flex: 1 }} />
                  <span style={{ fontSize: 12, color: C.muted }}>a</span>
                  <input type="time" value={hs[k].cierra || ""} onChange={e => setDia(k, "cierra", e.target.value)} style={{ ...inp, flex: 1 }} />
                </>
              )}
            </div>
          ))}

          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "14px 0 8px" }}>Tarifas por vehículo</div>
          {tarifas.length === 0 && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Sin tarifas cargadas.</div>}
          {tarifas.map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <select value={t.vehiculo} onChange={e => setTarifa(i, "vehiculo", e.target.value)} style={{ ...inp, width: 108, flexShrink: 0 }}>
                {VEHICULOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={t.concepto || ""} onChange={e => setTarifa(i, "concepto", e.target.value)} placeholder="Día completo, 2 horas…" style={{ ...inp, flex: 1, minWidth: 0 }} />
              <input value={t.precio} onChange={e => setTarifa(i, "precio", e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="$" style={{ ...inp, width: 84, flexShrink: 0 }} />
              <button onClick={() => delTarifa(i)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 16, padding: "0 2px" }}>×</button>
            </div>
          ))}
          <button onClick={addTarifa} style={{ padding: "7px 12px", borderRadius: 8, border: `1px dashed ${C.border}`, background: "none", color: C.text, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Agregar tarifa</button>

          {error && <div style={{ fontSize: 12, color: C.red, marginTop: 10 }}>{error}</div>}
          <button onClick={guardar} disabled={guardando}
            style={{ width: "100%", marginTop: 12, padding: "12px 0", borderRadius: 12, background: C.text, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: guardando ? 0.6 : 1 }}>
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}
    </div>
  );
};

// Vincular un evento a la ficha de un lugar (circuito, taller, local). Sirve para que
// el evento salga también en el perfil de esa ficha, aunque lo haya creado otra persona.
// Las opciones de un campo del formulario de inscripción: "menú" con asado,
// vegetariano y sin TACC, "talle de remera", "llevo acompañante". Cada opción puede
// sumar plata: el organizador de un evento con comida necesita las dos cosas juntas
// —saber cuántos de cada uno para el que cocina, y cobrar distinto si corresponde—
// y por eso termina usando una ticketera afuera de Olbos.
const EditorOpcionesCampo = ({ opciones, onChange }) => {
  const lista = Array.isArray(opciones) ? opciones : [];
  const inp = { padding: "8px 10px", borderRadius: 9, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.card, boxSizing: "border-box" };
  return (
    <div style={{ margin: "6px 0 4px 10px", paddingLeft: 10, borderLeft: `2px solid ${C.border}` }}>
      {lista.map((op, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
          <input value={op.texto || ""} onChange={e => onChange(lista.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
            placeholder="Opción (ej: Asado)" style={{ ...inp, flex: 1, minWidth: 0 }} />
          <input type="number" value={op.extra ?? ""} onChange={e => onChange(lista.map((x, j) => j === i ? { ...x, extra: e.target.value } : x))}
            placeholder="+ $" style={{ ...inp, width: 92, flexShrink: 0 }} />
          <button onClick={() => onChange(lista.filter((_, j) => j !== i))}
            style={{ width: 26, height: 26, borderRadius: "50%", background: "transparent", border: "none", cursor: "pointer", color: C.red, fontSize: 15, flexShrink: 0 }}>×</button>
        </div>
      ))}
      <button onClick={() => onChange([...lista, { texto: "", extra: "" }])}
        style={{ padding: "6px 10px", borderRadius: 8, border: `1px dashed ${C.border}`, background: "transparent", fontSize: 12, fontWeight: 700, color: C.text, cursor: "pointer" }}>
        + Agregar opción
      </button>
      <div style={{ fontSize: 10.5, color: C.faint, marginTop: 5, lineHeight: 1.4 }}>
        El "+ $" es lo que suma esa opción al precio. Dejalo vacío si no cambia nada.
      </div>
    </div>
  );
};

const SelectorSedeNegocio = ({ valor, nombre, onChange }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const deb = useRef(null);
  const buscar = (texto) => {
    setQ(texto);
    clearTimeout(deb.current);
    if (texto.trim().length < 3) { setRes([]); return; }
    setBuscando(true);
    deb.current = setTimeout(async () => {
      const { data } = await supabase.from("negocios")
        .select("id, nombre, categoria, ciudad")
        .eq("estado", "verificado")
        .ilike("nombre", `%${texto.trim()}%`)
        .limit(6);
      setRes(data || []); setBuscando(false);
    }, 300);
  };
  if (valor) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, background: C.surface, border: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 14 }}>📍</span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nombre || "Lugar vinculado"}</span>
        <button type="button" onClick={() => onChange(null, "")} style={{ background: "none", border: "none", color: C.red, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Quitar</button>
      </div>
    );
  }
  return (
    <div>
      <input value={q} onChange={e => buscar(e.target.value)} placeholder="Buscá el circuito, taller o local…"
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card, boxSizing: "border-box" }} />
      {buscando && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Buscando…</div>}
      {res.map(n => (
        <button key={n.id} type="button" onClick={() => { onChange(n.id, n.nombre); setQ(""); setRes([]); }}
          style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", marginTop: 6, borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, cursor: "pointer" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{n.nombre}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{n.categoria}{n.ciudad ? ` · ${n.ciudad}` : ""}</div>
        </button>
      ))}
      {q.trim().length >= 3 && !buscando && res.length === 0 && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Sin resultados. El lugar tiene que estar registrado en Servicios.</div>
      )}
    </div>
  );
};

const TurnosTallerFicha = ({ negocio }) => {
  const [uid, setUid] = useState(null);
  const [misTurnos, setMisTurnos] = useState([]);
  const [showPedir, setShowPedir] = useState(false);
  const [form, setForm] = useState({ fecha: "", hora: "", moto: "", motivo: "" });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  const cargar = async (u) => {
    const quien = u || uid;
    if (!quien) return;
    const { data } = await supabase.from("turnos_taller").select("*")
      .eq("negocio_id", negocio.id).eq("user_id", quien)
      .order("fecha", { ascending: false }).limit(5);
    setMisTurnos(data || []);
  };
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUid(user.id);
      cargar(user.id);
      const { data } = await supabase.from("perfiles").select("motos").eq("id", user.id).single();
      const ms = Array.isArray(data?.motos) ? data.motos : [];
      if (ms.length >= 1) setForm(f => ({ ...f, moto: `${ms[0].marca || ""} ${ms[0].modelo || ""}`.trim() }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocio.id]);

  const horas = [];
  for (let h = 8; h <= 18; h++) { horas.push(`${String(h).padStart(2, "0")}:00`); if (h < 18) horas.push(`${String(h).padStart(2, "0")}:30`); }

  // Tarifas cargadas por el dueño de la ficha: si hay, el rider elige una y puede
  // pagar la reserva por Mercado Pago (la plata va directo a la cuenta del negocio).
  const tarifasNegocio = Array.isArray(negocio?.tarifas) ? negocio.tarifas.filter(t => t && Number(t.precio) > 0) : [];
  const [pagando, setPagando] = useState(null);
  const pagarTurno = async (t) => {
    setPagando(t.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(API_BASE + "/api/mp-pago", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ turno_id: t.id }),
      });
      const j = await r.json();
      if (j.url) { window.location.href = j.url; return; }
      alert(j.error === "negocio_sin_mp"
        ? "El lugar todavía no conectó su Mercado Pago. Arreglá el pago directo con ellos."
        : j.error === "ya_pagado" ? "Ese turno ya está pagado."
        : "No se pudo abrir el pago. Probá de nuevo.");
    } catch (e) {
      alert("No se pudo abrir el pago. Revisá la señal.");
    } finally { setPagando(null); }
  };

  const pedir = async () => {
    if (!form.fecha || !form.hora) { setError("Elegí fecha y hora."); return; }
    setEnviando(true); setError("");
    const elegida = form.tarifaIdx != null ? tarifasNegocio[form.tarifaIdx] : null;
    const fila = {
      negocio_id: negocio.id, user_id: uid,
      fecha: form.fecha, hora: form.hora,
      moto: form.moto.trim() || null, motivo: form.motivo.trim() || null,
    };
    if (elegida) {
      fila.tarifa = `${(VEHICULOS.find(v => v[0] === elegida.vehiculo) || [null, "Entrada"])[1]}${elegida.concepto ? " · " + elegida.concepto : ""}`;
      fila.monto = Number(elegida.precio);
    }
    let { error: err } = await supabase.from("turnos_taller").insert(fila);
    // Si todavía no se corrió el SQL de tarifas, el turno se pide igual sin monto
    if (err && /tarifa|monto|schema cache|column/i.test(err.message || "")) {
      delete fila.tarifa; delete fila.monto;
      ({ error: err } = await supabase.from("turnos_taller").insert(fila));
    }
    if (err) { setError("No se pudo pedir el turno: " + err.message); setEnviando(false); return; }
    if (negocio.user_id) {
      const { data: yo } = await supabase.from("perfiles").select("username").eq("id", uid).single();
      supabase.from("notificaciones").insert({
        user_id: negocio.user_id, tipo: "turno",
        from_username: yo?.username || "olbos",
        texto: `pidió un turno en ${negocio.nombre} para el ${fmtFechaTurno(form.fecha)} a las ${form.hora} hs`,
      }).then(() => {}, () => {});
    }
    setEnviando(false); setShowPedir(false);
    setForm(f => ({ ...f, fecha: "", hora: "", motivo: "", tarifaIdx: null }));
    cargar();
  };

  const cancelar = async (t) => {
    if (!window.confirm("¿Cancelar este turno?")) return;
    await supabase.from("turnos_taller").update({ estado: "cancelado", updated_at: new Date().toISOString() }).eq("id", t.id);
    if (negocio.user_id) {
      supabase.from("notificaciones").insert({
        user_id: negocio.user_id, tipo: "turno", from_username: "olbos",
        texto: `se canceló el turno del ${fmtFechaTurno(t.fecha)} a las ${t.hora} hs en ${negocio.nombre}`,
      }).then(() => {}, () => {});
    }
    cargar();
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <button onClick={() => { if (!uid) { alert("Creá tu cuenta para pedir un turno."); return; } setShowPedir(true); }}
        style={{ width: "100%", padding: "12px 0", borderRadius: 12, background: C.accent, color: "#fff", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer" }}>
        📅 Pedir turno
      </button>

      {misTurnos.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>Tus turnos acá</div>
          {misTurnos.map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, background: C.surface, marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmtFechaTurno(t.fecha)} · {t.hora} hs</div>
                {(t.moto || t.motivo) && <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{[t.moto, t.motivo].filter(Boolean).join(" · ")}</div>}
                {t.nota_taller && <div style={{ fontSize: 11, color: "#8A6D3B", marginTop: 2 }}>💬 {t.nota_taller}</div>}
                {t.tarifa && (
                  <div style={{ fontSize: 11, color: t.pago_confirmado ? C.green : C.muted, marginTop: 2, fontWeight: 700 }}>
                    {t.tarifa}{Number(t.monto) > 0 ? ` · ${precioAR(t.monto)}` : ""}{t.pago_confirmado ? " · pagado ✓" : ""}
                  </div>
                )}
                {!t.pago_confirmado && Number(t.monto) > 0 && t.estado !== "cancelado" && (
                  <button onClick={() => pagarTurno(t)} disabled={pagando === t.id}
                    style={{ marginTop: 6, padding: "7px 12px", borderRadius: 8, background: "#009EE3", color: "#fff", border: "none", fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: pagando === t.id ? 0.6 : 1 }}>
                    {pagando === t.id ? "Abriendo…" : `Pagar ${precioAR(t.monto)}`}
                  </button>
                )}
              </div>
              <ChipTurno estado={t.estado} />
              {(t.estado === "pendiente" || t.estado === "confirmado") && (
                <button onClick={() => cancelar(t)} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 2, flexShrink: 0 }}>✕</button>
              )}
            </div>
          ))}
        </div>
      )}

      {showPedir && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1200, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto" }}
          onClick={e => e.target === e.currentTarget && setShowPedir(false)}>
          <div style={{ background: C.card, borderRadius: "0 0 20px 20px", width: "100%", maxWidth: 430, padding: "calc(var(--sat) + 18px) 20px 22px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Turno en {negocio.nombre}</div>
              <button onClick={() => setShowPedir(false)} style={{ width: 30, height: 30, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Fecha</div>
                <input type="date" min={hoyLocalISO()} value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card }} />
              </div>
              <div style={{ width: 110 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Hora</div>
                <select value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))}
                  style={{ width: "100%", padding: "10px 8px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: C.card }}>
                  <option value="">—</option>
                  {horas.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
            {tarifasNegocio.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>¿Con qué entrás?</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {tarifasNegocio.map((t, i) => {
                    const sel = form.tarifaIdx === i;
                    return (
                      <button key={i} type="button" onClick={() => setForm(f => ({ ...f, tarifaIdx: sel ? null : i }))}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer", textAlign: "left", border: sel ? `2px solid ${C.text}` : `1px solid ${C.border}`, background: sel ? C.surface : C.card }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                          {(VEHICULOS.find(v => v[0] === t.vehiculo) || [null, "Entrada"])[1]}{t.concepto ? ` · ${t.concepto}` : ""}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>{precioAR(t.precio)}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Elegí una tarifa para poder pagar la reserva por la app. Si no elegís, el turno se pide igual y arreglás en el lugar.</div>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Tu moto</div>
              <input value={form.moto} onChange={e => setForm(f => ({ ...f, moto: e.target.value }))} placeholder="Ej: Honda Twister 250"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, color: C.text }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>¿Qué necesitás?</div>
              <textarea value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Service, cubiertas, frenos, revisión…"
                style={{ width: "100%", minHeight: 64, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13.5, color: C.text, resize: "none" }} />
            </div>
            {error && <div style={{ fontSize: 12.5, color: C.red, marginBottom: 10 }}>{error}</div>}
            <button onClick={pedir} disabled={enviando}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: enviando ? 0.6 : 1 }}>
              {enviando ? "Pidiendo…" : "Pedir turno"}
            </button>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8, textAlign: "center" }}>El taller confirma o te propone otro horario. Te avisamos por la campanita.</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Panel del negocio (lado taller): la agenda de turnos pedidos, con confirmar/rechazar/atendido
const AgendaTurnosNegocio = ({ negocio }) => {
  const [turnos, setTurnos] = useState([]);
  const [perfilesMap, setPerfilesMap] = useState({});
  const [verPasados, setVerPasados] = useState(false);

  const cargar = async () => {
    const { data } = await supabase.from("turnos_taller").select("*")
      .eq("negocio_id", negocio.id)
      .order("fecha", { ascending: true }).order("hora", { ascending: true }).limit(120);
    const ts = data || [];
    setTurnos(ts);
    const ids = ts.map(t => t.user_id).filter((id, i, a) => a.indexOf(id) === i && !(id in perfilesMap));
    if (ids.length > 0) {
      const { data: ps } = await supabase.from("perfiles").select("id, nombre, username").in("id", ids);
      if (ps) setPerfilesMap(m => { const n = { ...m }; ps.forEach(p => { n[p.id] = p; }); return n; });
    }
  };
  useEffect(() => { cargar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocio.id]);

  const avisar = (t, texto) => {
    supabase.from("notificaciones").insert({
      user_id: t.user_id, tipo: "turno", from_username: "olbos",
      texto: `${negocio.nombre}: ${texto}`,
    }).then(() => {}, () => {});
  };
  const setEstado = async (t, estado) => {
    let nota = null;
    if (estado === "rechazado") nota = window.prompt("Opcional: proponele otro día u horario (le llega al rider)", "") || null;
    await supabase.from("turnos_taller").update({ estado, nota_taller: nota, updated_at: new Date().toISOString() }).eq("id", t.id);
    const cuando = `${fmtFechaTurno(t.fecha)} a las ${t.hora} hs`;
    if (estado === "confirmado") avisar(t, `tu turno del ${cuando} está confirmado ✔`);
    if (estado === "rechazado") avisar(t, `no puede tomar tu turno del ${cuando}${nota ? ` — "${nota}"` : ". Probá con otro horario."}`);
    cargar();
  };

  const hoy = hoyLocalISO();
  const activos = turnos.filter(t => t.fecha >= hoy && ["pendiente", "confirmado"].includes(t.estado));
  const pasados = turnos.filter(t => !(t.fecha >= hoy && ["pendiente", "confirmado"].includes(t.estado)));

  const Fila = ({ t, conAcciones }) => {
    const p = perfilesMap[t.user_id];
    return (
      <div style={{ padding: "10px 12px", borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: conAcciones ? 8 : 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
              {fmtFechaTurno(t.fecha)} · {t.hora} hs
              <span style={{ fontWeight: 600, color: C.muted }}> — {p ? (p.nombre || "@" + p.username) : "rider"}</span>
            </div>
            {(t.moto || t.motivo) && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>{[t.moto, t.motivo].filter(Boolean).join(" · ")}</div>}
          </div>
          <ChipTurno estado={t.estado} />
        </div>
        {conAcciones && t.estado === "pendiente" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEstado(t, "confirmado")}
              style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: "#2F7D4F", color: "#fff", border: "none", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Confirmar</button>
            <button onClick={() => setEstado(t, "rechazado")}
              style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.card, color: C.red, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>No puedo</button>
          </div>
        )}
        {conAcciones && t.estado === "confirmado" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEstado(t, "atendido")}
              style={{ flex: 1, padding: "8px 0", borderRadius: 9, background: C.accent, color: "#fff", border: "none", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Marcar atendido</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
        📅 Agenda de turnos {activos.length > 0 ? `· ${activos.length}` : ""}
      </div>
      {activos.length === 0 && (
        <div style={{ fontSize: 12, color: C.muted, padding: "10px 12px", background: C.card, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          Sin turnos por venir. Los riders piden turno desde tu ficha en Pit Stop y te aparecen acá.
        </div>
      )}
      {activos.map(t => <Fila key={t.id} t={t} conAcciones />)}
      {pasados.length > 0 && (
        <>
          <button onClick={() => setVerPasados(v => !v)}
            style={{ background: "transparent", border: "none", color: C.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "6px 0" }}>
            {verPasados ? "Ocultar historial" : `Ver historial (${pasados.length})`}
          </button>
          {verPasados && pasados.slice().reverse().map(t => <Fila key={t.id} t={t} conAcciones={false} />)}
        </>
      )}
    </div>
  );
};

// Landing del link de invitación /r/<usuario> para gente sin cuenta: primero se crea la
// cuenta ACÁ (eso asegura la atribución del referido — la tienda de Apple no pasa ningún
// dato del link a la app instalada) y recién después, si es iPhone, se ofrece la tienda.
// ─── QR DE EMERGENCIA (ficha médica en el casco) ─────────────────────────────
// La ficha de rider ya existe (rider_fichas, privada). Esto la hace leíble por
// un tercero en una emergencia: QR impreso en el casco → olbos.ai/sos/<token>
// → ficha pública SOLO con lo médico (vía RPC ficha_emergencia). El token se
// puede regenerar, lo que invalida los QR viejos.
const QREmergenciaModal = ({ ficha, userId, onToken, onClose }) => {
  const [qrUrl, setQrUrl] = useState(null);
  const [token, setToken] = useState(ficha?.qr_token || null);
  const [generando, setGenerando] = useState(false);
  // Apagable al instante (aprendido de Stribo): el QR impreso queda, pero deja de
  // mostrar datos hasta que lo vuelvas a prender. Requiere la columna qr_activo.
  const [activo, setActivo] = useState(ficha?.qr_activo !== false);
  const fichaCompleta = ficha && (ficha.grupo_sanguineo || ficha.emergencia_telefono);

  const toggleActivo = async () => {
    const nuevo = !activo;
    setActivo(nuevo);
    const { error } = await supabase.from("rider_fichas").update({ qr_activo: nuevo }).eq("user_id", userId);
    if (error) { setActivo(!nuevo); alert("No se pudo cambiar: " + error.message); }
  };

  const asegurarToken = async (regenerar = false) => {
    if (!fichaCompleta) return;
    setGenerando(true);
    let t = token;
    if (!t || regenerar) {
      t = crypto.randomUUID();
      const { error } = await supabase.from("rider_fichas").update({ qr_token: t, updated_at: new Date().toISOString() }).eq("user_id", userId);
      if (error) { alert("No se pudo generar: " + error.message); setGenerando(false); return; }
      setToken(t);
      if (onToken) onToken(t);
    }
    try {
      let data = await QRCode.toDataURL(`${SITE_URL}/sos/${t}`, { width: 640, margin: 2, color: { dark: "#111111", light: "#ffffff" } });
      try {
        // Lienzo más alto: arriba "EMERGENCIA" y abajo la explicación, para que quien lo vea pegado en un casco sepa para qué es
        const base = await new Promise((ok, no) => { const im = new window.Image(); im.onload = () => ok(im); im.onerror = no; im.src = data; });
        const cv = document.createElement("canvas"); cv.width = 640; cv.height = 640 + 190;
        const cx = cv.getContext("2d");
        cx.fillStyle = "#ffffff"; cx.fillRect(0, 0, 640, 830);
        cx.fillStyle = "#B3372C"; cx.fillRect(0, 0, 640, 100);
        cx.fillStyle = "#ffffff"; cx.textAlign = "center"; cx.textBaseline = "middle";
        cx.font = "800 54px Archivo, Arial, sans-serif";
        cx.fillText("🆘 EMERGENCIA", 320, 52);
        cx.drawImage(base, 0, 100);
        cx.fillStyle = "#111111";
        cx.font = "700 34px Archivo, Arial, sans-serif";
        cx.fillText("Escaneá este QR si el rider", 320, 770 - 8);
        cx.fillText("necesita ayuda", 320, 812 - 8);
        data = cv.toDataURL("image/png");
      } catch (e) { /* QR liso */ }
      setQrUrl(data);
    } catch (e) { alert("No se pudo armar el QR"); }
    setGenerando(false);
  };
  useEffect(() => { asegurarToken(false); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 380, maxHeight: "88vh", overflowY: "auto", background: C.card, borderRadius: 20, padding: "22px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: "-0.01em", marginBottom: 6 }}>🆘 QR de emergencia</div>
        {!fichaCompleta ? (
          <>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, margin: "10px 0 16px" }}>
              Primero completá tu <strong>ficha de rider</strong> (al menos el grupo sanguíneo o el contacto de emergencia): Editar perfil → Ficha de rider. Con eso armamos el QR.
            </p>
            <button onClick={onClose} style={{ width: "100%", padding: "12px", borderRadius: 12, background: C.accent, border: "none", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Entendido</button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
              Imprimilo y pegalo en el casco o en el tanque. Quien lo escanee ve <strong>solo tu ficha médica</strong>: grupo sanguíneo, condiciones, medicación y a quién llamar.
            </p>
            {qrUrl
              ? <img loading="lazy" decoding="async" src={qrUrl} alt="QR de emergencia" style={{ width: 230, height: 230, borderRadius: 12, border: `1px solid ${C.border}` }} />
              : <div style={{ height: 230, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13 }}>{generando ? "Generando…" : "…"}</div>}
            {qrUrl && (
              <button onClick={() => descargarImagen(qrUrl, "olbos-qr-emergencia.png")}
                style={{ display: "block", width: "100%", marginTop: 14, padding: "13px", borderRadius: 12, background: C.accent, color: "#fff", fontSize: 14, fontWeight: 800, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                ⬇ Descargar para imprimir
              </button>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, padding: "11px 13px", background: C.surface, borderRadius: 12, textAlign: "left" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Ficha visible al escanear</div>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>Apagala y el QR deja de mostrar tus datos al instante. El sticker vuelve a servir cuando la prendas.</div>
              </div>
              <button onClick={toggleActivo}
                style={{ width: 44, height: 26, borderRadius: 13, background: activo ? C.green : C.border, position: "relative", border: "none", cursor: "pointer", flexShrink: 0, transition: "background .2s" }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.card, position: "absolute", top: 3, left: activo ? 21 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
              </button>
            </div>
            <button onClick={() => { if (window.confirm("¿Generar un código nuevo? Los QR que ya imprimiste dejan de funcionar.")) asegurarToken(true); }}
              style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
              Regenerar código (invalida los anteriores)
            </button>
            <button onClick={onClose} style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 12, background: "none", border: "none", color: C.muted, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cerrar</button>
          </>
        )}
      </div>
    </div>
  );
};

// La página que ve quien ESCANEA el QR: sin login, alto contraste, lo esencial
const FichaEmergenciaPublica = ({ token, onVolver }) => {
  const [ficha, setFicha] = useState(undefined); // undefined = cargando
  useEffect(() => {
    supabase.rpc("ficha_emergencia", { p_token: token }).then(({ data }) => {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      setFicha(d && (d.nombre || d.emergencia_telefono || d.grupo_sanguineo) ? d : null);
    }, () => setFicha(null));
  }, [token]);
  const fila = (label, valor) => valor ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1.35 }}>{valor}</div>
    </div>
  ) : null;
  return (
    <div style={{ minHeight: "100vh", background: "#F9F5F2", fontFamily: "'Archivo', -apple-system, sans-serif" }}>
      <div style={{ background: "#B3372C", color: "#fff", padding: "calc(var(--sat, 0px) + 18px) 20px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: "-0.01em" }}>🆘 FICHA DE EMERGENCIA</div>
        <div style={{ fontSize: 12.5, marginTop: 4, opacity: 0.95 }}>Ante una emergencia real, llamá primero al <strong>911</strong></div>
      </div>
      <div style={{ maxWidth: 430, margin: "0 auto", padding: "22px 20px 40px" }}>
        {ficha === undefined && <div style={{ textAlign: "center", color: C.muted, padding: 30 }}>Cargando…</div>}
        {ficha === null && <div style={{ textAlign: "center", color: C.muted, padding: 30, lineHeight: 1.6 }}>Este código no corresponde a ninguna ficha activa.</div>}
        {ficha && (
          <>
            {fila("Nombre", ficha.nombre)}
            {ficha.grupo_sanguineo && (
              <div style={{ background: C.accent, color: "#F9F5F2", borderRadius: 16, padding: "16px 18px", marginBottom: 16, textAlign: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#bdb6ab", textTransform: "uppercase", letterSpacing: "0.14em" }}>Grupo sanguíneo</div>
                <div style={{ fontSize: 44, fontWeight: 900, lineHeight: 1.15 }}>{ficha.grupo_sanguineo}</div>
              </div>
            )}
            {fila("DNI", ficha.dni)}
            {fila("Condiciones médicas", ficha.condicion_medica)}
            {fila("Medicación", ficha.medicacion)}
            {ficha.emergencia_telefono && (
              <a href={`tel:${String(ficha.emergencia_telefono).replace(/[^+\d]/g, "")}`}
                style={{ display: "block", marginTop: 20, padding: "17px", borderRadius: 14, background: "#B3372C", color: "#fff", fontSize: 16.5, fontWeight: 800, textDecoration: "none", textAlign: "center" }}>
                📞 Llamar a {ficha.emergencia_nombre || "su contacto"}{ficha.emergencia_relacion ? ` (${ficha.emergencia_relacion})` : ""}
              </a>
            )}
            <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 26, lineHeight: 1.5 }}>
              Ficha de emergencia de OLBOS® · la app de la comunidad motociclista<br />olbos.ai
            </div>
          </>
        )}
        {onVolver && (
          <button onClick={onVolver} style={{ display: "block", width: "100%", marginTop: 18, padding: "13px", borderRadius: 12, background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Volver a Olbos</button>
        )}
      </div>
    </div>
  );
};

const InvitacionLanding = ({ username, onEntrar }) => {
  const [invitador, setInvitador] = useState(null);
  useEffect(() => {
    supabase.from("perfiles").select("nombre, username, avatar_url").ilike("username", username).limit(1)
      .then(({ data }) => { if (data && data[0]) setInvitador(data[0]); }, () => {});
  }, [username]);
  const nombre = invitador?.nombre || `@${username.toLowerCase()}`;
  return (
    <div style={{ minHeight: "100vh", background: C.accent, color: "#F9F5F2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", textAlign: "center", fontFamily: "'Archivo', -apple-system, sans-serif" }}>
      {invitador?.avatar_url
        ? <img loading="lazy" decoding="async" src={invitador.avatar_url} alt="" style={{ width: 84, height: 84, borderRadius: "50%", objectFit: "cover", border: "2px solid #F9F5F2", marginBottom: 18 }} />
        : <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 84, height: 84, borderRadius: 22, marginBottom: 18, background: "#F9F5F2" }} />}
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.25, marginBottom: 10 }}>
        {nombre} te invita a Olbos
      </div>
      <div style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.55, maxWidth: 300, marginBottom: 6 }}>
        La comunidad para los que viven la moto: salidas en grupo, recorridos, eventos y rutas.
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 30, fontStyle: "italic" }}>El camino es el destino.</div>
      <button onClick={() => onEntrar("registro")}
        style={{ width: "100%", maxWidth: 320, padding: "16px", borderRadius: 14, background: "#F9F5F2", color: C.text, border: "none", fontSize: 16, fontWeight: 700, letterSpacing: "-0.01em", cursor: "pointer", marginBottom: 12 }}>
        Crear mi cuenta
      </button>
      <button onClick={() => onEntrar("login")}
        style={{ width: "100%", maxWidth: 320, padding: "13px", borderRadius: 14, background: "transparent", color: "#F9F5F2", border: "1px solid rgba(249,245,242,.35)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
        Ya tengo cuenta
      </button>
      {/* Acceso directo a las tiendas (pedido de Carlos). La cuenta creada desde ACÁ
          asegura el referido; el link de Play igual lleva el código en el referrer
          para cuando la app nativa lo sepa leer (Install Referrer, pendiente). */}
      <div style={{ marginTop: 30, fontSize: 11.5, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>La app, en tu tienda</div>
      <div style={{ display: "flex", gap: 10, marginTop: 10, width: "100%", maxWidth: 320 }}>
        <a href={`https://play.google.com/store/apps/details?id=ai.olbos.app&referrer=${encodeURIComponent(`utm_source=referido&utm_content=${username.toLowerCase()}`)}`}
          target="_blank" rel="noreferrer"
          style={{ flex: 1, padding: "11px 8px", borderRadius: 12, background: "rgba(249,245,242,.1)", border: "1px solid rgba(249,245,242,.25)", color: "#F9F5F2", fontSize: 12.5, fontWeight: 700, textDecoration: "none" }}>
          ▶ Google Play
        </a>
        <a href="https://apps.apple.com/app/id6794469275" target="_blank" rel="noreferrer"
          style={{ flex: 1, padding: "11px 8px", borderRadius: 12, background: "rgba(249,245,242,.1)", border: "1px solid rgba(249,245,242,.25)", color: "#F9F5F2", fontSize: 12.5, fontWeight: 700, textDecoration: "none" }}>
           App Store
        </a>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, maxWidth: 300, lineHeight: 1.5 }}>
        Tip: creá tu cuenta desde acá así {nombre} suma tu invitación — después bajás la app y entrás con la misma cuenta.
      </div>
    </div>
  );
};

// ─── Red de seguridad global: NUNCA más una pantalla blanca ──────────────────
// Cualquier error de render muestra una pantalla de recuperación con el error
// visible (para capturas) y lo guarda en errores_cliente para diagnosticarlo.
// (Nació del crash de una cuenta nueva entrando al garage — 13-ago-2026.)
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    try {
      reportarError(error && error.message ? error.message : error, error && error.stack, info && info.componentStack, { pantalla_blanca: true });
      supabase.from("errores_cliente").insert({
        mensaje: String(error && error.message ? error.message : error).slice(0, 500),
        stack: String(error && error.stack ? error.stack : "").slice(0, 4000),
        component_stack: String(info && info.componentStack ? info.componentStack : "").slice(0, 2000),
        url: window.location.href.slice(0, 300),
        user_agent: navigator.userAgent.slice(0, 300),
        app_version: APP_VERSION,
      }).then(() => {}, () => {});
    } catch (e) { /* el reporte nunca debe romper la pantalla de recuperación */ }
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", background: "#F9F5F2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", textAlign: "center", fontFamily: "'Archivo', -apple-system, sans-serif" }}>
        <img loading="lazy" decoding="async" src={LOGO_URL} alt="Olbos" style={{ width: 64, height: 64, borderRadius: 16, marginBottom: 18 }} />
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>Uy, algo se rompió</div>
        <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6, maxWidth: 300, marginBottom: 20 }}>
          El error ya nos llegó y lo vamos a arreglar. Tocá el botón y seguí andando.
        </p>
        <button onClick={() => { window.location.href = "/"; }}
          style={{ width: "100%", maxWidth: 300, padding: "15px", borderRadius: 14, background: C.accent, color: "#F9F5F2", border: "none", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 16 }}>
          Recargar Olbos
        </button>
        <details style={{ maxWidth: 320, textAlign: "left" }}>
          <summary style={{ fontSize: 11, color: C.muted, cursor: "pointer" }}>Detalle técnico</summary>
          <pre style={{ fontSize: 10, color: C.muted, whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 6 }}>
            {String(this.state.error && this.state.error.message ? this.state.error.message : this.state.error)}
          </pre>
        </details>
      </div>
    );
  }
}

export default function Olbos() {
  return <ErrorBoundary><OlbosApp /></ErrorBoundary>;
}

function OlbosApp() {
  const [, setRecoveryNativo] = useState(0);
  useEffect(() => {
    const h = () => setRecoveryNativo(n => n + 1);
    window.addEventListener("olbos-recovery", h);
    return () => window.removeEventListener("olbos-recovery", h);
  }, []);
  // Arranca en Eventos: es la sección con contenido de verdad y la que explica qué es Olbos
  // Pantalla de inicio: cada uno abre la app donde la usa. Sin preferencia
  // elegida, se abre en la última pestaña usada (comportamiento de siempre).
  const [tab, setTab] = useState(() => {
    if (window.__olbosAbrirMensajes) { window.__olbosAbrirMensajes = false; return "mensajes"; }
    const pref = localStorage.getItem("olbos_tab_inicio");
    if (pref && pref !== "ultima") return pref === "grabador" ? "rutas" : pref;
    return localStorage.getItem("olbos_tab") || "eventos";
  });
  useEffect(() => {
    // Botón "Mensaje" del perfil público: deja el destino en window y abre la pestaña Mensajes
    const h = () => setTab("mensajes");
    window.addEventListener("olbos-abrir-dm", h);
    return () => window.removeEventListener("olbos-abrir-dm", h);
  }, []);
  useEffect(() => {
    // "Directo a grabar": abre Rutas con el grabador ya lanzado
    if (localStorage.getItem("olbos_tab_inicio") === "grabador") {
      setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-abrir-track-recorder", { detail: {} })), 700);
    }
  }, []);

  // Deep-link para organizadores de grupos: olbos.ai/?crear_rodada=<eventoId>
  // abre el creador de salidas con ese evento YA cargado como destino. Es el
  // link que el organizador de un evento grande (BikerFest, Expo Moto) reparte
  // a los grupos para que cada uno arme su rodada confluente en un toque.
  // Se persiste en localStorage para sobrevivir al login si no tenía cuenta.
  useEffect(() => {
    const procesarPendiente = () => {
      try {
        const pendiente = localStorage.getItem("olbos_pending_rodada_hacia");
        if (!pendiente) return;
        (async () => {
          const { data: e } = await supabase.from("eventos").select("id, titulo, lugar, lat, lng, fecha").eq("id", pendiente).maybeSingle();
          localStorage.removeItem("olbos_pending_rodada_hacia");
          if (!e || !e.lat || !e.lng) return;
          setTab("eventos");
          window.olbosRodadaHacia = { id: e.id, titulo: e.titulo, lugar: e.lugar, lat: e.lat, lng: e.lng, fecha: String(e.fecha || "").slice(0, 10) };
          setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-rodada-hacia", { detail: window.olbosRodadaHacia })), 400);
        })();
      } catch (e) { /* sin storage: nada */ }
    };
    try {
      const params = new URLSearchParams(window.location.search);
      const idDest = params.get("crear_rodada");
      if (idDest) {
        localStorage.setItem("olbos_pending_rodada_hacia", idDest);
        const url = new URL(window.location.href);
        url.searchParams.delete("crear_rodada");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) { /* */ }
    procesarPendiente();
    // Un App Link con ?crear_rodada= llegado con la app YA abierta re-dispara el proceso
    window.addEventListener("olbos-procesar-rodada-hacia", procesarPendiente);
    return () => window.removeEventListener("olbos-procesar-rodada-hacia", procesarPendiente);
  }, []);
  // Ref espejo del tab activo, para leerlo desde handlers registrados una sola vez
  const tabRef = useRef(tab);
  // Grabación en curso, montada en la raíz para que sobreviva a la navegación:
  // {tipo:"track"|"rodada", ...}. recMin = minimizado, grabando de fondo.
  const [recGlobal, setRecGlobal] = useState(null);
  const [recMin, setRecMin] = useState(false);
  // Comunidad abierta desde un link olbos.ai/comunidad/<username>. En la app nativa
  // ese link entra por App Links y no hay pathname que rutear, así que se abre acá
  // el perfil de esa comunidad — que es su versión adentro de la app.
  const [comunidadAbierta, setComunidadAbierta] = useState(null);
  // Ficha de emergencia abierta por el QR (olbos.ai/sos/<token>) con la app instalada:
  // el link entra por App Links y no hay pathname que rutear (pedido de @MatiAguilar_26)
  const [sosAbierto, setSosAbierto] = useState(null);
  // Estado real del grabador (lo avisa el TrackRecorder): la barra roja no
  // puede decir "grabando" si el mapa está abierto sin grabar
  const [recEstado, setRecEstado] = useState("");
  const recEstadoRef = useRef("");
  recEstadoRef.current = recEstado;
  useEffect(() => {
    const h = (e) => setRecEstado((e.detail && e.detail.estado) || "");
    window.addEventListener("olbos-rec-estado", h);
    return () => window.removeEventListener("olbos-rec-estado", h);
  }, []);
  useEffect(() => { tabRef.current = tab; registrarSeccion("tab:" + tab); }, [tab]);
  const [userPos, setUserPos] = useState(null); // ubicación global persistente entre tabs

  // Deep-link desde la página pública de evento (?evento=ID): abrir esa salida directo.
  // El id queda en localStorage (olbos_ref_evento) y NO se borra acá: el registro por
  // Google recarga la página en "/" y el que llegaba por el QR del stand caía en la
  // lista sin la ficha (auditoría UX 09-oct). Se consume recién cuando hay sesión.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    let id = sp.get("evento");
    // ?c=<campaña> (QR del stand: c=nocturna): se guarda para atribuir la cuenta y medir
    const camp = sp.get("c");
    if (camp && /^[a-zA-Z0-9_-]{1,40}$/.test(camp)) { try { localStorage.setItem("olbos_campania", camp.toLowerCase()); } catch (e) { /* */ } }
    // ?transferir=<perfil>: el admin abre la transferencia de ese evento a ese perfil
    try { const tr = sp.get("transferir"); if (tr && /^[0-9a-f-]{36}$/i.test(tr)) window.olbosTransferirA = tr; } catch (e) { /* */ }
    // pendiente dejado por un link anterior (sobrevive al registro); vence a los 3 días
    if (!id) {
      try {
        const ts = Number(localStorage.getItem("olbos_ref_evento_ts") || 0);
        if (ts && Date.now() - ts > 3 * 86400000) { localStorage.removeItem("olbos_ref_evento"); localStorage.removeItem("olbos_pending_anotar"); localStorage.removeItem("olbos_ref_evento_ts"); }
        else id = localStorage.getItem("olbos_ref_evento");
      } catch (e) { /* */ }
    }
    if (id) {
      try { localStorage.setItem("olbos_ref_evento", id); if (!localStorage.getItem("olbos_ref_evento_ts")) localStorage.setItem("olbos_ref_evento_ts", String(Date.now())); } catch (e) { /* */ }
      window.olbosAbrirEventoId = id;
      setTab("eventos");
      const url = new URL(window.location.href);
      url.searchParams.delete("evento");
      url.searchParams.delete("transferir");
      url.searchParams.delete("c");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Ubicación al inicio SOLO si el permiso ya está dado (en nativo va por el plugin — el
  // navigator del WebView mostraba el cartel feo de "localhost quiere tu ubicación").
  // Antes se pedía apenas abría la app, sin contexto: mucha gente decía que no y después en
  // la rodada no aparecía en el mapa (Emilia, 03-nov). Ahora se pide donde se usa (el mapa,
  // el chequeo de la rodada); mientras tanto sirve la última posición conocida.
  useEffect(() => {
    let vivo = true;
    (async () => {
      let dado = false;
      try {
        if (esNativo()) {
          const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
          const st = await BG.getProviderState();
          dado = !!st && (st.status === BG.AUTHORIZATION_STATUS_ALWAYS || st.status === BG.AUTHORIZATION_STATUS_WHEN_IN_USE);
        } else if (navigator.permissions && navigator.permissions.query) {
          const p = await navigator.permissions.query({ name: "geolocation" });
          dado = p.state === "granted";
        }
      } catch (e) { dado = false; }
      if (!vivo) return;
      if (!dado) { const r = posReciente(72); if (r) setUserPos(r); return; }
      obtenerPosicion({ timeout: 8000, enableHighAccuracy: false }).then(
        p => { if (vivo) setUserPos({ lat: p.lat, lng: p.lng }); },
        () => {}
      );
    })();
    return () => { vivo = false; };
  }, []);

  // Pantalla "Agregar al inicio": NO se muestra en el primer ingreso (es invasiva, espanta
  // a quien entró a ver una ruta compartida). Solo si vienen con ?instalar explícito; sino
  // se ofrece más tarde, cuando la persona ya se enganchó (varios cambios de sección).
  const [showInstalar, setShowInstalar] = useState(() => {
    if (typeof window === "undefined" || esNativo()) return false;
    return new URLSearchParams(window.location.search).has("instalar");
  });
  const cambiosSeccionRef = useRef(0);

  // ── Swipe horizontal entre secciones (idea del hijo de Carlos, estilo Instagram) ──
  // Solo gestos claramente horizontales, lejos de los bordes (para no pelear con el
  // "atrás" del sistema) y sin robarle el gesto a carruseles ni mapas.
  const swipeRef = useRef(null);
  const onTouchStartTabs = (e) => {
    if (e.touches.length !== 1) { swipeRef.current = null; return; }
    const t = e.touches[0];
    if (t.clientX < 24 || t.clientX > window.innerWidth - 24) { swipeRef.current = null; return; }
    // Si el dedo arranca sobre un mapa o algo scrolleable en horizontal, no interceptamos
    // Mapas, carruseles y cualquier fila que scrollee en horizontal manejan su propio gesto
    const el = e.target;
    if (el && el.closest && el.closest(".gm-style, .leaflet-container, [data-noswipe], input, textarea, select")) { swipeRef.current = null; return; }
    let p = el;
    for (let i = 0; i < 6 && p && p.getBoundingClientRect; i++) {
      if (p.scrollWidth > p.clientWidth + 12) { swipeRef.current = null; return; }
      p = p.parentElement;
    }
    swipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEndTabs = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s || !e.changedTouches || e.changedTouches.length === 0) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y, dt = Date.now() - s.t;
    // Horizontal de verdad: >55px, más horizontal que vertical, y rápido
    if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(dy) * 1.3 || dt > 800) return;
    const orden = ordenValido(ordenNav);
    const idx = orden.indexOf(tab);
    if (idx < 0) return;
    const destino = orden[idx + (dx < 0 ? 1 : -1)];
    if (destino) handleTabChange(destino);
  };

  // Dirección de la transición entre secciones: la nueva entra desde el lado
  // correcto (izq/der según el orden de la barra), estilo Instagram
  const [slideDir, setSlideDir] = useState("");
  const handleTabChange = (newTab) => {
    if (newTab === "perfil" && !session) { setShowRegistroModal(true); return; }
    registrarUso("tab", { tab: newTab });
    const orden = ordenValido(ordenNav);
    const iViejo = orden.indexOf(tab), iNuevo = orden.indexOf(newTab);
    setSlideDir(iViejo >= 0 && iNuevo >= 0 ? (iNuevo > iViejo ? "tab-entra-der" : iNuevo < iViejo ? "tab-entra-izq" : "") : "");
    setTab(newTab);
    localStorage.setItem("olbos_tab", newTab);
    // Si entra a eventos o rutas, marcamos como visitado y limpiamos el badge
    if ((newTab === "eventos" || newTab === "rutas") && session) {
      supabase.rpc("marcar_tab_visitado", { p_tab: newTab }).then(() => {}, () => {});
      setNovedades(prev => ({ ...prev, [newTab]: false }));
    }
    // Ofrecer "Agregar al inicio" recién después de engancharse (3 cambios de sección),
    // una sola vez, solo en mobile y si no está ya como PWA.
    cambiosSeccionRef.current += 1;
    if (cambiosSeccionRef.current === 3 && !esNativo()) { // en la app nativa NUNCA mostrar "agregar al inicio"
      const esPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
      const esMobile = /iphone|ipad|ipod|android/i.test(navigator.userAgent);
      if (!esPWA && esMobile && !localStorage.getItem("olbos_instalar_visto")) {
        setShowInstalar(true);
      }
    }
  };
  // Orden de la barra elegido por el rider. Arranca del teléfono (instantáneo) y se
  // corrige con el del perfil, que es el que manda y viaja entre dispositivos.
  const [ordenNav, setOrdenNav] = useState(() => leerOrdenLocal());
  const [novedades, setNovedades] = useState({ eventos: false, rutas: false });  const [isAdmin, setIsAdmin] = useState(false);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginModo, setLoginModo] = useState("register"); // con qué solapa abre LoginScreen
  // Bienvenida post-registro (una vista, oscura): { eventoId, anotar }. Reemplaza al
  // modal "Original Rider", que nunca aparecía (comparaba "🏁 Original Rider" contra
  // "Original Rider"). Declarada ANTES de los efectos que la usan.
  const [bienvenida, setBienvenida] = useState(null);
  const pendientesProcesadosRef = useRef(false);
  // Pendientes que dejó el camino sin sesión (ficha abierta por ?evento=, "Anotarme"
  // tocado sin cuenta). Se consumen UNA vez, cuando llega la sesión.
  const abrirEventoPendiente = (evId, anotar) => {
    try { localStorage.removeItem("olbos_ref_evento"); localStorage.removeItem("olbos_pending_anotar"); localStorage.removeItem("olbos_ref_evento_ts"); } catch (e) { /* */ }
    if (!evId) return;
    if (anotar) window.olbosPendingAnotar = evId;
    // Si Eventos todavía lo tiene para abrir al cargar la lista, no hace falta pedirlo de nuevo
    if (window.olbosAbrirEventoId === evId) setTab("eventos");
    else window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: evId } }));
    // Si la ficha YA estaba abierta con ese id (registro por email, sin recarga), el efecto
    // de Eventos no se re-dispara solo: se le avisa aparte, con un respiro para que abra
    if (anotar) setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-anotar-pendiente")), 700);
  };
  const esCuentaNueva = (sess) => {
    try {
      const uid = sess.user.id;
      if (localStorage.getItem("olbos_bienvenida_" + uid)) return false;
      const creada = new Date(sess.user.created_at).getTime();
      return Number.isFinite(creada) && Date.now() - creada < 20 * 60 * 1000;
    } catch (e) { return false; }
  };
  const procesarPendientesSesion = (sess) => {
    if (!sess?.user || pendientesProcesadosRef.current) return;
    pendientesProcesadosRef.current = true;
    let evId = null, anotar = null, camp = null;
    try { evId = localStorage.getItem("olbos_ref_evento"); anotar = localStorage.getItem("olbos_pending_anotar"); camp = localStorage.getItem("olbos_campania"); } catch (e) { /* */ }
    if (esCuentaNueva(sess)) {
      try { localStorage.setItem("olbos_bienvenida_" + sess.user.id, "1"); } catch (e) { /* */ }
      registrarUso("registro_ok", { c: camp || null, proveedor: sess.user.app_metadata?.provider || null, evento: evId || null });
      // Llegó por el link de la rodada: nada de bienvenida en el medio, directo a la rodada
      // (lo hace el efecto del "sumate" apenas hay sesión)
      let sumatePend = null;
      try { sumatePend = localStorage.getItem("olbos_pending_sumate"); } catch (e) { /* */ }
      if (sumatePend) return;
      // La bienvenida consume los pendientes con su botón ("Anotarme a …")
      setBienvenida({ eventoId: evId || null, anotar: !!evId && anotar === evId });
      return;
    }
    if (evId) abrirEventoPendiente(evId, anotar === evId);
  };
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) return;
    supabase.from("perfiles").select("orden_secciones").eq("id", uid).maybeSingle()
      .then(({ data }) => {
        const o = data && data.orden_secciones;
        if (!Array.isArray(o) || o.length === 0) return;
        setOrdenNav(ordenValido(o));
        try { localStorage.setItem("olbos_orden_nav", JSON.stringify(o)); } catch (e) { /* */ }
      }, () => {});
  }, [session]);
  const [showRegistroNegocio, setShowRegistroNegocio] = useState(false);
  const [negocioRegistrado, setNegocioRegistrado] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAgenciaPanel, setShowAgenciaPanel] = useState(false);
  const [regaloCodigo, setRegaloCodigo] = useState(() => {
    try {
      const m = window.location.pathname.match(/^\/regalo\/([a-z0-9_.-]+)\/?$/i);
      if (m) {
        const c = m[1].toLowerCase();
        localStorage.setItem("olbos_regalo", c);
        window.history.replaceState({}, "", "/" + window.location.search);
        return c;
      }
      return localStorage.getItem("olbos_regalo") || null;
    } catch (e) { return null; }
  });
  // Se cierra para dejar ver el registro; con la sesión iniciada vuelve a aparecer
  useEffect(() => {
    if (!session) return;
    try { const c = localStorage.getItem("olbos_regalo"); if (c) setRegaloCodigo(c); } catch (e) { /* */ }
  }, [session]);
  const [sesionCircuitoVer, setSesionCircuitoVer] = useState(null); // { traza, sesion, circuito }
  const [anuncioVer, setAnuncioVer] = useState(null); // flyer de un anuncio a pantalla completa
  const [circuitoVer, setCircuitoVer] = useState(null); // ficha de un circuito abierta desde Servicios
  useEffect(() => {
    const h = (e) => { if (e.detail && e.detail.sesion) setSesionCircuitoVer(e.detail); };
    const ha = (e) => { if (e.detail && e.detail.imagen_url) setAnuncioVer(e.detail); };
    const hc = async (e) => {
      const id = e.detail && e.detail.id;
      if (!id) return;
      let pos = null;
      try { pos = await Promise.race([obtenerPosicion({ enableHighAccuracy: false, timeout: 4000, maximumAge: 300000 }), new Promise(r => setTimeout(() => r(null), 4500))]); } catch (err) { pos = null; }
      const { data } = await supabase.rpc("circuitos_listar", { p_lat: pos ? pos.lat : null, p_lng: pos ? pos.lng : null });
      const d = typeof data === "string" ? JSON.parse(data) : data;
      const c = (Array.isArray(d) ? d : []).find(x => x.id === id);
      if (c) setCircuitoVer(c);
    };
    window.addEventListener("olbos-ver-sesion-circuito", h);
    window.addEventListener("olbos-ver-anuncio", ha);
    window.addEventListener("olbos-abrir-circuito", hc);
    return () => { window.removeEventListener("olbos-ver-sesion-circuito", h); window.removeEventListener("olbos-ver-anuncio", ha); window.removeEventListener("olbos-abrir-circuito", hc); };
  }, []);
  const [esAgencia, setEsAgencia] = useState(false); // perfiles.rol = "agencia": panel de métricas en lectura
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const esEscritorio = useEsEscritorio();
  const [userMotos, setUserMotos] = useState([]);
  const [bienvenidaPartner, setBienvenidaPartner] = useState(null); // username del partner recién reclamado
  const [showIosStore, setShowIosStore] = useState(false); // post-registro vía link de invitación en iPhone: ofrecer la app de la tienda

  // Link de rodada espontánea (/vivo/<token>): si el que lo abre TIENE sesión,
  // nada de visor web — directo al grabador con el mapa compartido de la rodada.
  // El token sobrevive al login en localStorage (mismo criterio que crear_rodada).
  useEffect(() => {
    if (!session) return;
    let tk = null;
    const m = window.location.pathname.match(/^\/vivo\/(.+)$/);
    const rodar = new URLSearchParams(window.location.search).get("rodar");
    // Solo el link de INVITACIÓN (?rodar=1) mete al grabador. El de seguimiento
    // abre el visor aunque tengas cuenta — la familia mira, no rueda.
    if (m && rodar) { tk = m[1]; window.history.replaceState({}, "", "/"); }
    else if (!m) { try { tk = localStorage.getItem("olbos_pending_vivo"); } catch (e) { /* */ } }
    if (!tk) return;
    try { localStorage.removeItem("olbos_pending_vivo"); } catch (e) { /* */ }
    setRecGlobal(prev => prev || { tipo: "vivo", token: tk });
    setRecMin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Visor de un compartido en vivo ADENTRO de la app (links de seguimiento
  // tocados en la app nativa o en los mensajes internos)
  const [verVivoToken, setVerVivoToken] = useState(null);
  useEffect(() => {
    const handlerVerVivo = (e) => { if (e.detail && e.detail.token) setVerVivoToken(e.detail.token); };
    window.addEventListener("olbos-ver-vivo", handlerVerVivo);
    return () => window.removeEventListener("olbos-ver-vivo", handlerVerVivo);
  }, []);
  // El botón atrás de Android cierra el visor en vez de sacar de la app (Carlos no
  // tenía forma de volver: solo "Abrir Olbos")
  useBackGuard(!!verVivoToken, () => setVerVivoToken(null));

  // ── "Sumate a la rodada" (03-nov-2026, feedback de Emilia) ──
  // El organizador manda al grupo de WhatsApp olbos.ai/evento/<id>?sumate=1. Con la app
  // instalada se abre la app (App Links); si no, la web. Con cuenta: anota (si es gratis y
  // directo), revisa el teléfono y, si es el día, mete al mapa del grupo. Sin cuenta, la
  // intención queda guardada y se retoma apenas la crea.
  const [chequeoRodada, setChequeoRodada] = useState(null); // { evento, modo, recienAnotado, desdeMapa }
  const [sumateSinCuenta, setSumateSinCuenta] = useState(null); // id del evento: ofrecer entrar como invitado
  const [, setSumateTick] = useState(0);
  const sumateEnCursoRef = useRef(false);
  const leerSumatePendiente = () => {
    try {
      const p = JSON.parse(localStorage.getItem("olbos_pending_sumate") || "null");
      if (p && p.id && Date.now() - (p.t || 0) < 2 * 86400000) return p.id;
      if (p) localStorage.removeItem("olbos_pending_sumate");
    } catch (e) { /* */ }
    return null;
  };
  const guardarSumatePendiente = (id) => { try { localStorage.setItem("olbos_pending_sumate", JSON.stringify({ id, t: Date.now() })); } catch (e) { /* */ } };
  const procesarSumate = async (eventoId) => {
    if (!eventoId || sumateEnCursoRef.current) return;
    sumateEnCursoRef.current = true;
    try { localStorage.removeItem("olbos_pending_sumate"); } catch (e) { /* */ }
    try {
      registrarUso("sumate_link", { id: eventoId, nativo: esNativo() });
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { guardarSumatePendiente(eventoId); setSumateSinCuenta(eventoId); return; }
      const { data: ev } = await supabase.from("eventos").select("*").eq("id", eventoId).maybeSingle();
      if (!ev) { alert("No encontramos esa salida: puede ser privada o que el organizador la haya dado de baja."); return; }
      const evMapa = { ...ev, title: ev.titulo };
      let recien = false;
      if (!gestionaEvento(ev, user.id)) {
        const { data: ins } = await supabase.from("inscripciones").select("id").eq("evento_id", ev.id).eq("user_id", user.id).maybeSingle();
        if (!ins) {
          if (!inscripcionDirectaPosible(ev)) {
            // Paga, pide datos o aprobación: la ficha con su formulario, como siempre
            window.olbosPendingAnotar = ev.id;
            window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: ev.id } }));
            setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-anotar-pendiente")), 700);
            return;
          }
          const r = await inscribirDirecto(ev.id);
          if (!r.ok) {
            if (r.motivo !== "requiere_formulario") alert("No pudimos anotarte a la rodada. Probá de nuevo en un momento o anotate desde la ficha.");
            window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: ev.id } }));
            return;
          }
          recien = !r.yaEstaba;
        }
      }
      if (ev.estado === "finalizada" || rodadaTerminada(ev)) { window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: ev.id } })); return; }
      const inicio = inicioEventoTs(ev);
      const esDia = ev.estado === "en_curso" || inicio == null || Date.now() >= inicio - 4 * 3600 * 1000;
      if (esDia) window._olbosChequeoVisto = { ...(window._olbosChequeoVisto || {}), [ev.id]: true };
      setChequeoRodada({ evento: evMapa, modo: esDia ? "dia" : "antes", recienAnotado: recien });
    } catch (e) {
      reportarError("sumate: " + String((e && e.message) || e).slice(0, 200), e && e.stack, "procesarSumate", { evento: eventoId });
    } finally { sumateEnCursoRef.current = false; }
  };
  // Web: el link llega como /evento/<id>?sumate=1; con la app, por App Links (olbos-sumate)
  useEffect(() => {
    if (authLoading) return;
    const m = window.location.pathname.match(/^\/evento\/([^/?#]+)/);
    if (m && new URLSearchParams(window.location.search).get("sumate")) {
      guardarSumatePendiente(m[1]);
      if (!session) return; // la página pública muestra "Sumarme a la rodada"
      window.history.replaceState({}, "", "/");
      setSumateTick(n => n + 1);
    }
    const id = leerSumatePendiente();
    if (!id) return;
    if (!session) { setSumateSinCuenta(id); return; }
    const t = setTimeout(() => procesarSumate(id), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, authLoading]);
  useEffect(() => {
    const hSumate = (e) => { const id = e.detail && e.detail.id; if (id) procesarSumate(String(id)); };
    // Desde el mapa ("Revisar mi teléfono"): el chequeo arriba del mapa, que ya está abierto
    const hChequeo = (e) => { const ev = e.detail && e.detail.evento; if (ev && ev.id) setChequeoRodada({ evento: ev, modo: "dia", desdeMapa: true }); };
    window.addEventListener("olbos-sumate", hSumate);
    window.addEventListener("olbos-chequeo-rodada", hChequeo);
    return () => { window.removeEventListener("olbos-sumate", hSumate); window.removeEventListener("olbos-chequeo-rodada", hChequeo); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Invitación a rodar (?rodar=1) abierta sin cuenta: al llegar a la app con el token
  // pendiente, se abre el registro solo. Si no, el invitado caía en el feed sin saber
  // que tenía que entrar; con cuenta, el efecto de arriba lo mete al mapa directo.
  useEffect(() => {
    if (authLoading || session) return;
    let tk = null;
    try { tk = localStorage.getItem("olbos_pending_vivo"); } catch (e) { /* */ }
    if (tk) setShowRegistroModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, session]);

  // Entrar al mapa de una rodada espontánea desde adentro de la app (el botón
  // que se pinta sobre los links /vivo en los mensajes internos)
  useEffect(() => {
    const handlerVivo = (e) => {
      const tk = e.detail && e.detail.token;
      if (!tk) return;
      // "Me sumo" a una salida abierta con el mapa abierto SIN grabar: se cambia a la rodada.
      // Grabando o en pausa no se toca (pisarlo mataría la grabación en curso).
      setRecGlobal(prev => (!prev || (prev.tipo === "track" && recEstadoRef.current === "lista")) ? { tipo: "vivo", token: tk } : prev);
      setRecMin(false);
    };
    window.addEventListener("olbos-abrir-vivo", handlerVivo);
    return () => window.removeEventListener("olbos-abrir-vivo", handlerVivo);
  }, []);

  // Ping de actividad diaria (alimenta DAU/WAU/MAU y retención del dashboard de métricas).
  // Una vez por día por usuario; guard en localStorage para no repetir en cada apertura.
  useEffect(() => {
    if (!session) return;
    try {
      const hoy = new Date();
      const clave = "olbos_act_" + hoy.getFullYear() + "-" + (hoy.getMonth() + 1) + "-" + hoy.getDate();
      if (localStorage.getItem(clave)) return;
      localStorage.setItem(clave, "1");
      supabase.rpc("marcar_actividad").then(() => {}, () => { try { localStorage.removeItem(clave); } catch (e) {} });
    } catch (e) {}
  }, [session]);

  // Auto-reclamo de perfil pre-llenado: cuando el partner se loguea (tras invitación), aplicar los datos
  useEffect(() => {
    if (!session) return;
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/reclamar")) return; // ahí se reclama con su propio botón
    let t = null;
    try { t = localStorage.getItem("olbos_claim_token"); } catch {}
    if (!t) return;
    (async () => {
      // Confirmar antes de aplicar: nunca pisar un perfil establecido sin avisar
      const { data: prevRaw } = await supabase.rpc("get_invitacion_perfil", { p_token: t });
      const prev = typeof prevRaw === "string" ? JSON.parse(prevRaw) : prevRaw;
      const objetivo = prev?.datos?.username ? `@${prev.datos.username}` : "el perfil de la invitación";
      if (!window.confirm(`¿Aplicar ${objetivo} a tu cuenta? Esto reemplaza tu perfil actual con los datos de la invitación.`)) {
        try { localStorage.removeItem("olbos_claim_token"); } catch {}
        return;
      }
      const { data: raw } = await supabase.rpc("reclamar_invitacion_perfil", { p_token: t });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      try { localStorage.removeItem("olbos_claim_token"); } catch {}
      if (data?.ok) setBienvenidaPartner(prev?.datos?.username || "");
    })();
  }, [session]);

  // Capturar parámetros de URL (?ref y ?join) — DESPUÉS de declarar session/setShowRegistroModal para evitar TDZ
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      const refUpper = ref.toUpperCase().trim();
      if (!localStorage.getItem("olbos_ref")) {
        localStorage.setItem("olbos_ref", refUpper);
        localStorage.setItem("olbos_ref_fecha", new Date().toISOString());
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("ref");
      window.history.replaceState({}, "", url.toString());
    }

    const joinToken = params.get("join");
    if (joinToken) {
      localStorage.setItem("olbos_pending_join", joinToken.trim());
      const url = new URL(window.location.href);
      url.searchParams.delete("join");
      window.history.replaceState({}, "", url.toString());
    }

    // ?pitstop=<seccion> → abre Pit Stop directo en esa categoría.
    // Sirve para responder "¿alguien conoce un gestor?" con un link al listado.
    const ps = params.get("pitstop");
    if (ps) {
      window.olbosPitStopTab = ps.trim();
      setTab("mercado");
      const url = new URL(window.location.href);
      url.searchParams.delete("pitstop");
      window.history.replaceState({}, "", url.toString());
    }

    // ?negocio=<id> / ?venta=<id> → abre la ficha de ese servicio / aviso de compra-venta
    const negParam = params.get("negocio"), ventaParam = params.get("venta");
    if (negParam || ventaParam) {
      if (negParam) { window.olbosAbrirNegocioId = negParam.trim(); window.olbosPitStopTab = window.olbosPitStopTab || "todos"; }
      if (ventaParam) window.olbosAbrirVentaId = ventaParam.trim();
      setTab("mercado");
      const url = new URL(window.location.href);
      url.searchParams.delete("negocio"); url.searchParams.delete("venta");
      window.history.replaceState({}, "", url.toString());
    }

    // Vueltas de Mercado Pago: del checkout (?pago=) y de conectar la cuenta (?mp=)
    const pagoParam = params.get("pago");
    if (pagoParam) {
      if (pagoParam === "ok") alert("¡Pago acreditado! 💳 Tu lugar quedó confirmado y la entrada te espera en el evento.");
      else if (pagoParam === "pendiente") alert("Tu pago quedó en proceso. Apenas se acredite te confirmamos por la campanita.");
      else if (pagoParam === "error") alert("El pago no se completó. Podés reintentarlo desde el evento cuando quieras.");
      const url = new URL(window.location.href);
      url.searchParams.delete("pago");
      window.history.replaceState({}, "", url.toString());
    }
    const pedidoParam = params.get("pedido");
    if (pedidoParam) {
      if (pedidoParam === "ok") alert("¡Pago acreditado! 🛍️ El negocio ya recibió tu pedido y te contacta para la entrega.");
      else if (pedidoParam === "pendiente") alert("Tu pago quedó en proceso. Apenas se acredite te avisamos por la campanita.");
      else if (pedidoParam === "error") alert("El pago no se completó. Podés volver a intentarlo desde el producto.");
      const url = new URL(window.location.href);
      url.searchParams.delete("pedido");
      window.history.replaceState({}, "", url.toString());
    }
    const mpParam = params.get("mp");
    if (mpParam) {
      alert(mpParam === "conectado"
        ? "✅ ¡Mercado Pago conectado! Ya cobrás online: los pagos van directo a tu cuenta y las inscripciones se confirman solas."
        : "No se pudo conectar Mercado Pago. Probá de nuevo en unos minutos.");
      const url = new URL(window.location.href);
      url.searchParams.delete("mp");
      window.history.replaceState({}, "", url.toString());
    }
    // Vuelta de conectar MercadoLibre a la tienda del negocio (?ml=ok&n=<importados> | ?ml=error)
    const mlParam = params.get("ml");
    if (mlParam) {
      const n = parseInt(params.get("n") || "0", 10) || 0;
      alert(mlParam === "ok"
        ? `🟡 ¡MercadoLibre conectado! ${n ? `Ya tenés ${n} publicación${n === 1 ? "" : "es"} en tu tienda de Olbos.` : "Tus publicaciones se están trayendo; si no aparecen, tocá Sincronizar ahora en Mi tienda."} Precio y stock se actualizan solos todas las mañanas.`
        : "No se pudo conectar MercadoLibre. Probá de nuevo en unos minutos desde Mi tienda.");
      const url = new URL(window.location.href);
      url.searchParams.delete("ml");
      url.searchParams.delete("n");
      window.history.replaceState({}, "", url.toString());
    }
    // Vuelta de conectar Tienda Nube (?tn=ok&n=<importados> | ?tn=error)
    const tnParam = params.get("tn");
    if (tnParam) {
      const n = parseInt(params.get("n") || "0", 10) || 0;
      alert(tnParam === "ok"
        ? `🔵 ¡Tienda Nube conectada! ${n ? `Ya tenés ${n} producto${n === 1 ? "" : "s"} en tu tienda de Olbos.` : "Tus productos se están trayendo; si no aparecen, tocá Sincronizar ahora en Mi tienda."} Precio y stock se actualizan solos todas las mañanas.`
        : "No se pudo conectar Tienda Nube. Probá de nuevo en unos minutos desde Mi tienda.");
      const url = new URL(window.location.href);
      url.searchParams.delete("tn");
      url.searchParams.delete("n");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Venir del landing de invitación (/r/<usuario>): abrir directo el registro o el login
  useEffect(() => {
    if (authLoading) return;
    let modo = null;
    try { modo = localStorage.getItem("olbos_ref_abrir"); } catch (e) { /* */ }
    if (!modo) return;
    try { localStorage.removeItem("olbos_ref_abrir"); } catch (e) { /* */ }
    if (!session) { if (modo === "login") { setLoginModo("login"); setShowLogin(true); } else setShowRegistroModal(true); }
  }, [session, authLoading]);

  // iPhone que llegó por link de invitación y ya tiene la cuenta: ofrecer la app de la tienda
  useEffect(() => {
    if (!session) return;
    let f = null;
    try { f = localStorage.getItem("olbos_ref_ios_store"); } catch (e) { /* */ }
    if (!f) return;
    try { localStorage.removeItem("olbos_ref_ios_store"); } catch (e) { /* */ }
    setShowIosStore(true);
  }, [session]);

  // Procesar token de invitación a grupo pendiente
  useEffect(() => {
    if (!session) {
      if (localStorage.getItem("olbos_pending_join")) {
        setShowRegistroModal(true);
      }
      return;
    }
    const pendingJoin = localStorage.getItem("olbos_pending_join");
    if (!pendingJoin) return;

    (async () => {
      const { data: raw, error } = await supabase.rpc("aceptar_invitacion_link", { p_token: pendingJoin });
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      localStorage.removeItem("olbos_pending_join");
      if (error || data?.error) {
        alert("No se pudo procesar la invitación: " + (data?.error || error?.message || "error desconocido"));
        return;
      }
      const mensaje = data.ya_miembro
        ? `Ya sos miembro de "${data.grupo_nombre}"`
        : `¡Te uniste a "${data.grupo_nombre}"!`;
      alert(mensaje);
      setTab("grupos");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("olbos-abrir-grupo", { detail: { grupoId: data.grupo_id } }));
      }, 200);
    })();
  }, [session]);

  // Cargar motos reales del perfil cuando hay sesión
  useEffect(() => {
    if (!session) { setUserMotos([]); return; }
    supabase.from("perfiles").select("motos").eq("id", session.user.id).single()
      .then(({ data }) => {
        if (data?.motos) setUserMotos(data.motos);
      });
  }, [session]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
      // Referido pendiente: atribuir al invitador (el RPC valida que la cuenta sea
      // nueva, que no sea auto-referido y que no esté ya atribuida — idempotente)
      try {
        const refPend = localStorage.getItem("olbos_ref");
        if (session?.user && refPend) {
          supabase.rpc("registrar_referido", { p_ref_username: refPend }).then(({ error }) => {
            if (!error) localStorage.removeItem("olbos_ref");
          }, () => {});
        }
        // Campaña pendiente (Expo Moto y compañía): se atribuye una sola vez
        const campPend = localStorage.getItem("olbos_campania");
        if (session?.user && campPend) {
          supabase.rpc("registrar_campania", { p_codigo: campPend }).then(({ error }) => {
            if (!error) localStorage.removeItem("olbos_campania");
          }, () => {});
        }
      } catch (e) { /* */ }
      if (session) setTimeout(() => registrarPush(), 2500);
      if (session) { verificarAdmin(session.user.id); prefetchPerfilPropio(session.user.id); supabase.rpc("asegurar_perfil").then(() => supabase.rpc("enviar_bienvenida_olbos").then(() => {}, () => {}), () => {}); }
      if (session) setTimeout(() => procesarPendientesSesion(session), 300);
    });
    const irACampanita = () => setTab("feed");
    window.addEventListener("olbos-abrir-campanita", irACampanita);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        setShowLogin(false);
        setShowRegistroModal(false);
        verificarAdmin(session.user.id);
        supabase.rpc("asegurar_perfil").then(() => supabase.rpc("enviar_bienvenida_olbos").then(() => {}, () => {}), () => {}); // red de seguridad: crear perfil si falta + saludo del bot (idempotente)
        setTimeout(() => procesarPendientesSesion(session), 300);
        if (_event === "SIGNED_IN") setTimeout(() => registrarPush(), 2500);
      } else {
        setIsAdmin(false);
        if (_event === "SIGNED_OUT") olvidarPush();
      }
    });
    return () => { subscription.unsubscribe(); window.removeEventListener("olbos-abrir-campanita", irACampanita); };
  }, []);

  // App nativa: capturar el deep link de auth (Google vuelve a ai.olbos.app://auth-callback)
  // y completar la sesión adentro de la app. onAuthStateChange hace el resto (cierra el login).
  // Y App Links: un https://olbos.ai/... tocado en WhatsApp abre la app — acá se
  // rutea a la pantalla correcta, igual que lo hace la web.
  useEffect(() => {
    if (!esNativo()) {
      try {
        const qp = new URLSearchParams(window.location.search);
        const igEstado = qp.get("ig");
        if (igEstado) {
          const motivo = qp.get("motivo");
          qp.delete("ig"); qp.delete("motivo");
          window.history.replaceState(null, "", window.location.pathname + (qp.toString() ? "?" + qp.toString() : ""));
          setTimeout(() => alVolverDeInstagram(igEstado, motivo), 1200);
        }
      } catch (e) { /* */ }
      return;
    }
    let listener;
    const rutearLinkOlbos = (url) => {
      try {
        const u = new URL(url);
        if (!u.hostname || u.hostname.indexOf("olbos.ai") === -1) return false;
        // "Olvidé mi contraseña" con la app instalada: el link del mail abre la app (App Link)
        // con los tokens en el # y la app caía en el inicio sin dejar cambiar la clave
        // (documento de Matías, A1). Se arma la sesión y se muestra la pantalla de nueva clave.
        const hashR = new URLSearchParams((u.hash || "").replace(/^#/, ""));
        if (u.searchParams.get("recovery") === "1" || hashR.get("type") === "recovery") {
          (async () => {
            try {
              const at = hashR.get("access_token"), rt = hashR.get("refresh_token");
              if (at && rt) await supabase.auth.setSession({ access_token: at, refresh_token: rt });
              else if (u.searchParams.get("code")) await supabase.auth.exchangeCodeForSession(u.searchParams.get("code"));
            } catch (e) { /* link vencido: la pantalla avisa al intentar guardar */ }
            // token_hash (mail nuevo): se canjea en la pantalla al tocar "Cambiar contraseña"
            const th = u.searchParams.get("token_hash");
            try { window.history.replaceState({}, "", "/?recovery=1" + (th ? `&token_hash=${encodeURIComponent(th)}&type=recovery` : "")); } catch (e) { /* */ }
            window.dispatchEvent(new CustomEvent("olbos-recovery"));
          })();
          return true;
        }
        // Vuelta del pago de Mercado Pago (pestaña del sistema): se cierra y se abre el evento
        if (u.searchParams.get("pago")) { import("@capacitor/browser").then(({ Browser }) => Browser.close().catch(() => {}), () => {}); }
        const mVivo = u.pathname.match(/^\/vivo\/(.+)$/);
        if (mVivo) {
          // ?rodar=1 = invitación a rodar (grabador); sin eso = seguimiento (visor)
          const evVivo = u.searchParams.get("rodar") ? "olbos-abrir-vivo" : "olbos-ver-vivo";
          window.dispatchEvent(new CustomEvent(evVivo, { detail: { token: mVivo[1] } }));
          return true;
        }
        const mEvento = u.pathname.match(/^\/evento\/(.+)$/);
        const idEvento = (mEvento && mEvento[1]) || u.searchParams.get("evento");
        // El link del grupo de WhatsApp ("sumate"): anota, revisa el teléfono y entra al mapa
        if (idEvento && u.searchParams.get("sumate")) { window.dispatchEvent(new CustomEvent("olbos-sumate", { detail: { id: idEvento } })); return true; }
        if (idEvento) { window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: idEvento } })); return true; }
        // Página pública de una comunidad. Con la app instalada, el link de
        // olbos.ai entra por App Links y acá no hay pathname que rutear: sin este
        // caso la app abría en el feed y el link parecía roto (lo vio Carlos).
        const mComunidad = u.pathname.match(/^\/comunidad\/([a-zA-Z0-9_.-]+)$/);
        if (mComunidad) {
          window.dispatchEvent(new CustomEvent("olbos-abrir-comunidad", { detail: { username: decodeURIComponent(mComunidad[1]) } }));
          return true;
        }
        // QR de la ficha médica: se muestra la ficha pública, sin login
        const mSos = u.pathname.match(/^\/sos\/([0-9a-fA-F-]{36})\/?$/);
        if (mSos) { window.dispatchEvent(new CustomEvent("olbos-abrir-sos", { detail: { token: mSos[1] } })); return true; }
        // Servicios compartidos (Pit Stop): ?negocio=ID, ?venta=ID o ?pitstop=<seccion>
        const qNegocio = u.searchParams.get("negocio"), qVenta = u.searchParams.get("venta"), qPit = u.searchParams.get("pitstop");
        if (qNegocio || qVenta || qPit) {
          window.dispatchEvent(new CustomEvent("olbos-abrir-servicio", { detail: { negocio: qNegocio, venta: qVenta, pitstop: qPit } }));
          return true;
        }
        const idDest = u.searchParams.get("crear_rodada");
        if (idDest) {
          try { localStorage.setItem("olbos_pending_rodada_hacia", idDest); } catch (e) { /* */ }
          window.dispatchEvent(new CustomEvent("olbos-procesar-rodada-hacia"));
          return true;
        }
        return true; // cualquier otro link de olbos.ai: alcanza con que la app se abra
      } catch (e) { return false; }
    };
    (async () => {
      const { App: CapApp } = await import("@capacitor/app");
      listener = await CapApp.addListener("appUrlOpen", async ({ url }) => {
        if (!url) return;
        if (url.indexOf("auth-callback") === -1) { rutearLinkOlbos(url); return; }
        try {
          const { Browser } = await import("@capacitor/browser");
          Browser.close().catch(() => {});
          // Los tokens pueden volver en el fragmento (#access_token=... flujo implicit)
          // o como ?code=... (flujo PKCE). Soportamos los dos.
          const hashStr = url.includes("#") ? url.slice(url.indexOf("#") + 1) : "";
          const qStr = url.includes("?") ? url.slice(url.indexOf("?") + 1, url.includes("#") ? url.indexOf("#") : undefined) : "";
          const igEstado = new URLSearchParams(qStr).get("ig");
          if (igEstado) { alVolverDeInstagram(igEstado, new URLSearchParams(qStr).get("motivo")); return; }
          const params = new URLSearchParams(hashStr || qStr);
          const access_token = params.get("access_token");
          const refresh_token = params.get("refresh_token");
          if (access_token && refresh_token) {
            await supabase.auth.setSession({ access_token, refresh_token });
            return;
          }
          const code = new URLSearchParams(qStr).get("code");
          if (code) await supabase.auth.exchangeCodeForSession(code);
        } catch (e) { console.error("deep link auth:", e); }
      });
      // Arranque en frío por App Link: el listener no llega a dispararse — el
      // link de lanzamiento se lee aparte (con un respiro para que monte todo)
      try {
        const launch = await CapApp.getLaunchUrl();
        if (launch && launch.url && launch.url.indexOf("auth-callback") === -1) {
          setTimeout(() => rutearLinkOlbos(launch.url), 900);
        }
      } catch (e) { /* */ }
    })();
    return () => { listener && listener.remove && listener.remove(); };
  }, []);

  const verificarAdmin = async (userId) => {
    const { data } = await supabase
      .from("admins")
      .select("id")
      .eq("user_id", userId)
      .single();
    if (data) setIsAdmin(true);
    // La agencia de growth entra con su propia cuenta y el rol "agencia"
    supabase.from("perfiles").select("rol").eq("id", userId).maybeSingle()
      .then(({ data: p }) => setEsAgencia(p?.rol === "agencia"), () => {});
  };

  // Escuchar evento de navegación desde chips de menciones en posts
  useEffect(() => {
    const handler = (e) => {
      const { tipo, id, nombre } = e.detail || {};
      // Navegación directa a una pestaña (banners de novedades del carrusel):
      // detail.tab + opcionales pitStopTab (sub-sección de Servicios) y vistaMapa
      if (e.detail?.tab) {
        if (e.detail.pitStopTab) window.olbosPitStopTab = e.detail.pitStopTab;
        if (e.detail.vistaMapa) window.olbosPendingVistaMapa = e.detail.vistaMapa;
        if (e.detail.ventaId) { window.olbosAbrirVentaId = e.detail.ventaId; window.dispatchEvent(new CustomEvent("olbos-abrir-venta")); }
        setTab(e.detail.tab);
        return;
      }
      const tabPorTipo = { ruta: "rutas", moto: "motos", evento: "eventos", negocio: "pitstop" };
      const tabDestino = tabPorTipo[tipo];
      if (tabDestino) {
        // Guardar la selección pendiente para que la sección la abra al cargar
        window.olbosPendingSelect = { tipo, id, nombre };
        setTab(tabDestino);
      }
    };
    window.addEventListener("olbos-navegar", handler);
    return () => window.removeEventListener("olbos-navegar", handler);
  }, []);

  useEffect(() => {
    if (esNativo()) {
      (async () => {
        try {
          const { default: BG } = await import("@transistorsoft/capacitor-background-geolocation");
          const st = await BG.getState();
          if (st && st.enabled) await BG.stop();
        } catch (e) { /* */ }
      })();
    }
    let campAbierta = null;
    try { campAbierta = localStorage.getItem("olbos_campania"); } catch (e) { /* */ }
    registrarUso("app_abierta", { ancho: window.innerWidth, pwa: window.matchMedia && window.matchMedia("(display-mode: standalone)").matches, c: campAbierta || null });
    const handler = () => setShowRegistroNegocio(true);
    window.addEventListener("olbos-registro-negocio", handler);
    const hReg = () => setShowRegistroModal(true);
    window.addEventListener("olbos-abrir-registro", hReg);
    return () => { window.removeEventListener("olbos-registro-negocio", handler); window.removeEventListener("olbos-abrir-registro", hReg); };
  }, []);

  // Eventos de grupos: abrir el flow correcto con grupoId preseleccionado
  useEffect(() => {
    // El grabador y el mapa en vivo se montan ACÁ, en la raíz: así seguís
    // navegando toda la app (feed, chat, lo que sea) sin que se corte la
    // grabación. Antes vivían dentro de una pestaña y cambiar de sección los
    // desmontaba — la grabación moría en silencio.
    const handlerTrack = (e) => {
      // Si YA hay un mapa/grabación activo (track O rodada), volver a ese: pisarlo
      // desmontaría el grabador en curso y mataría la grabación en silencio.
      const explicito = !e.detail?.automatico;
      setRecGlobal(prev => prev || {
        tipo: "track",
        grupoId: e.detail?.grupoId || null,
        grupoEnVivo: !!e.detail?.grupoEnVivo,
        viajeId: e.detail?.viajeId || null,
        evento: e.detail?.eventoId ? { id: e.detail.eventoId, titulo: e.detail.eventoTitulo || "" } : null,
        // "🏍️ Olbos" desde una ficha: el destino entra como prop y el navegador
        // arranca solo apenas hay GPS (si el mapa ya estaba abierto, prev se
        // conserva y el destino llega igual por el evento olbos-navegar-a)
        navegarA: e.detail?.navegarA || null,
        // El rider TOCÓ algo para abrirlo (el botón del panel, "Olbos" en una ficha,
        // un recorrido sin terminar): en la web eso entra al mapa sin volver a
        // preguntar. Solo la apertura automática por pestaña muestra el botón.
        explicito,
        retomarId: e.detail?.retomarId || null,
      });
      setRecMin(false);
      // Un recorrido concreto para retomar (desde Mis recorridos): si el grabador ya
      // estaba montado, se le avisa por evento; recién montado, lo toma de la prop
      if (e.detail?.retomarId) setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-retomar-traza", { detail: { id: e.detail.retomarId } })), 150);
      // Si el grabador YA estaba montado esperando en su pantalla de entrada (web),
      // un toque explícito lo tiene que hacer entrar: prev se conserva y la prop no
      // cambia, así que se le avisa por evento. El grabador lo ignora si ya entró.
      if (explicito) setTimeout(() => window.dispatchEvent(new CustomEvent("olbos-entrar-al-mapa")), 60);
    };
    // Abrir un evento desde cualquier sección (post del feed, evento de grupo):
    // cambia a la pestaña Eventos y deja el id pendiente para que abra el detalle
    const handlerAbrirEvento = (e) => {
      if (!e.detail?.id) return;
      window.olbosPendingAbrirEvento = e.detail.id;
      setTab("eventos");
      window.dispatchEvent(new CustomEvent("olbos-eventos-abrir-pendiente"));
    };
    window.addEventListener("olbos-abrir-evento", handlerAbrirEvento);
    // Link de una comunidad tocado con la app instalada: abre su perfil adentro
    const handlerAbrirComunidad = (e) => {
      if (!e.detail?.username) return;
      setComunidadAbierta(String(e.detail.username));
    };
    window.addEventListener("olbos-abrir-comunidad", handlerAbrirComunidad);
    const handlerAbrirSos = (e) => { if (e.detail?.token) setSosAbierto(String(e.detail.token)); };
    window.addEventListener("olbos-abrir-sos", handlerAbrirSos);
    // Link de un servicio (o de una sección de Servicios) con la app instalada
    const handlerAbrirServicio = (e) => {
      const d = e.detail || {};
      if (d.pitstop) window.olbosPitStopTab = String(d.pitstop).trim();
      if (d.negocio) window.olbosAbrirNegocioId = String(d.negocio);
      if (d.venta) window.olbosAbrirVentaId = String(d.venta);
      if (tabRef.current === "mercado") {
        // Pit Stop ya está montado y lee estos datos solo al montarse: se lo remonta
        setTab("feed");
        setTimeout(() => setTab("mercado"), 80);
      } else setTab("mercado");
    };
    window.addEventListener("olbos-abrir-servicio", handlerAbrirServicio);
    // Ir a un grupo desde cualquier lado (ej: "En vivo" del viaje): cambia a la
    // pestaña Grupos y abre ese grupo en la tab pedida (ej: "vivo")
    const handlerIrAGrupo = (e) => {
      if (!e.detail?.grupoId) return;
      setTab("grupos");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("olbos-abrir-grupo", { detail: { grupoId: e.detail.grupoId, tab: e.detail.tab || null } }));
      }, 200);
    };
    window.addEventListener("olbos-ir-a-grupo", handlerIrAGrupo);
    const handlerMapaVivo = (e) => {
      // Primera entrada de la sesión a esta rodada: antes del mapa, una mirada rápida al
      // teléfono. Si falta algo (permiso, "todo el tiempo", batería), el chequeo dice qué
      // tocar; si está todo bien, se entra directo sin ver nada.
      const evMv = e.detail?.evento;
      if (evMv && evMv.id && !e.detail?.sinChequeo && !(window._olbosChequeoVisto || {})[evMv.id]) {
        window._olbosChequeoVisto = { ...(window._olbosChequeoVisto || {}), [evMv.id]: true };
        const entrar = () => window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento: evMv, sinChequeo: true } }));
        leerEstadoTelefono({ conFix: false }).then(est => {
          if (pendientesTelefono(est).length) setChequeoRodada({ evento: evMv, modo: "dia" });
          else entrar();
        }, entrar);
        return;
      }
      registrarUso("mapa_vivo_abierto", { id: e.detail?.evento?.id || null });
      // Si ya está abierto (minimizado), volver a él en vez de recrearlo
      setRecGlobal(prev => (prev?.tipo === "rodada" && prev.evento?.id === e.detail?.evento?.id)
        ? prev
        : (e.detail?.evento ? { tipo: "rodada", evento: e.detail.evento } : prev));
      setRecMin(false);
    };
    window.addEventListener("olbos-abrir-mapa-vivo", handlerMapaVivo);
    const handlerPublicarRuta = (e) => {
      window.olbosPendingGrupoPublicarRuta = e.detail.grupoId;
      window.olbosPendingGrupoPublicarRutaNombre = e.detail.grupoNombre;
      setTab("rutas");
    };
    const handlerEvento = (e) => {
      window.olbosPendingGrupoEvento = e.detail.grupoId;
      window.olbosPendingGrupoEventoNombre = e.detail.grupoNombre;
      setTab("eventos");
    };
    const handlerPublicar = (e) => {
      window.olbosPendingGrupoPost = e.detail.grupoId;
      window.olbosPendingGrupoPostNombre = e.detail.grupoNombre;
      setTab("feed");
    };
    window.addEventListener("olbos-abrir-track-recorder", handlerTrack);
    window.addEventListener("olbos-abrir-publicar-ruta", handlerPublicarRuta);
    window.addEventListener("olbos-abrir-crear-evento", handlerEvento);
    window.addEventListener("olbos-abrir-publicar", handlerPublicar);
    return () => {
      window.removeEventListener("olbos-abrir-track-recorder", handlerTrack);
      window.removeEventListener("olbos-abrir-publicar-ruta", handlerPublicarRuta);
      window.removeEventListener("olbos-abrir-crear-evento", handlerEvento);
      window.removeEventListener("olbos-abrir-publicar", handlerPublicar);
      window.removeEventListener("olbos-abrir-mapa-vivo", handlerMapaVivo);
      window.removeEventListener("olbos-abrir-evento", handlerAbrirEvento);
      window.removeEventListener("olbos-abrir-comunidad", handlerAbrirComunidad);
      window.removeEventListener("olbos-abrir-sos", handlerAbrirSos);
      window.removeEventListener("olbos-abrir-servicio", handlerAbrirServicio);
      window.removeEventListener("olbos-ir-a-grupo", handlerIrAGrupo);
    };
  }, []);

  // Consultar novedades en eventos y rutas (se refresca cada 60s)
  useEffect(() => {
    if (!session) { setNovedades({ eventos: false, rutas: false }); return; }
    const cargar = async () => {
      const [{ data: hayEv }, { data: hayRu }] = await Promise.all([
        supabase.rpc("hay_novedades_tab", { p_tab: "eventos" }),
        supabase.rpc("hay_novedades_tab", { p_tab: "rutas" }),
      ]);
      setNovedades({
        eventos: hayEv && tab !== "eventos",
        rutas: hayRu && tab !== "rutas",
      });
    };
    cargar();
    const interval = setInterval(cargar, 60000);
    return () => clearInterval(interval);
  }, [session, tab]);

  // función para requerir auth antes de interactuar
  const requireAuth = (callback) => {
    if (session) { callback(); }
    else { setShowRegistroModal(true); }
  };


  if (showInstalar) return <PantallaInstalar onCerrar={() => {
    setShowInstalar(false);
    localStorage.setItem("olbos_instalar_visto", "1"); // no volver a mostrarla
    // Limpiar el parámetro de la URL sin recargar
    const url = new URL(window.location.href);
    url.searchParams.delete("instalar");
    window.history.replaceState({}, "", url.toString());
  }} />;

  if (authLoading) return (
    <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: -2, color: C.text }}>olbos</div>
    </div>
  );

  // Vista de recovery de contraseña (cuando viene del link del email). En la app nativa el
  // link entra por rutearLinkOlbos, que deja ?recovery=1 y avisa con "olbos-recovery".
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  if (params.get("recovery") === "1" || hashParams.get("type") === "recovery") {
    return <ResetPasswordScreen onDone={() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("recovery");
      url.hash = "";
      window.history.replaceState({}, "", url.toString());
      window.location.reload();
    }} />;
  }

  // Historial de service compartido con un taller (el taller lo abre logueado para firmar)
  const historialMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/historial\/(.+)$/);
  if (historialMatch) {
    return <HistoriaMotoPublica token={historialMatch[1]} onVolver={() => { window.history.pushState({}, "", "/"); window.location.reload(); }} />;
  }

  // Vista pública de ubicación en vivo (no requiere login)
  // Link de invitación /r/<usuario>: se guarda quién invitó y la app sigue normal.
  // La atribución real pasa cuando la persona crea cuenta (RPC registrar_referido).
  // Campañas medibles: /c/<codigo> (Expo Moto, influencers, QR del stand).
  // Se guarda el código y al crear la cuenta queda registrado en el perfil.
  const campMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/c\/([a-zA-Z0-9_-]+)$/);
  if (campMatch) {
    const codigo = campMatch[1].toLowerCase();
    try { localStorage.setItem("olbos_campania", codigo); } catch (e) { /* */ }
    // La visita se cuenta acá, apenas aterriza, sin esperar a que cree la cuenta: si no,
    // el que entra a mirar y se va —o el que se va derecho a la tienda— no figura en
    // ningún lado. Una vez por día por teléfono, para que recargar no infle el número.
    try {
      const clave = "olbos_camp_visita_" + codigo;
      const hoy = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(clave) !== hoy) {
        localStorage.setItem(clave, hoy);
        supabase.rpc("registrar_visita_campania", { p_codigo: codigo }).then(() => {}, () => {});
      }
    } catch (e) { /* nunca frenar la entrada por la métrica */ }
    window.history.replaceState({}, "", "/");
  }
  // Vuelta del link de confirmación de email: supabase-js inicia la sesión solo y
  // la app no decía nada ("confirmé pero no sé si confirmó" — amigo de Carlos, 04-sep)
  if (!avisoConfirmacionMostrado && /type=signup/.test(HASH_LLEGADA) && /access_token=/.test(HASH_LLEGADA)) {
    avisoConfirmacionMostrado = true;
    setTimeout(() => alert("✅ Email confirmado. Ya estás adentro de Olbos con tu cuenta — bienvenido/a."), 900);
  }
  // Vuelta de Mercado Pago tras contratar un espacio (?anuncio=ok|error|pendiente)
  const anuncioVuelta = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("anuncio");
  if (anuncioVuelta) {
    window.history.replaceState({}, "", "/");
    setTimeout(() => alert(anuncioVuelta === "ok"
      ? "✅ Pago recibido. Tu espacio se activa apenas Mercado Pago lo confirma (segundos). Lo seguís desde Mi negocio → Mis anuncios."
      : anuncioVuelta === "pendiente" ? "⏳ Pago pendiente. Cuando se acredite, tu espacio se activa solo." : "El pago no se completó. Podés reintentarlo desde Mi negocio → Mis anuncios."), 600);
  }

  // QR de emergencia escaneado: página pública, sin login, antes que todo
  const sosMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/sos\/([0-9a-fA-F-]{36})$/);
  if (sosMatch) return <FichaEmergenciaPublica token={sosMatch[1]} />;
  if (sosAbierto) return <FichaEmergenciaPublica token={sosAbierto} onVolver={() => setSosAbierto(null)} />;

  const refMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/r\/([^/]+)$/);
  if (refMatch) {
    try {
      localStorage.setItem("olbos_ref", decodeURIComponent(refMatch[1]));
      // /r/<usuario>?evento=<id>: además del referido, dejar pendiente abrir esa rodada
      // (en localStorage: el registro por OAuth o email recarga la página)
      const evPend = new URLSearchParams(window.location.search).get("evento");
      if (evPend) localStorage.setItem("olbos_ref_evento", evPend);
    } catch (e) { /* */ }
    // Sin sesión: landing de invitación. La cuenta se crea en la web (asegura el
    // referido) y después, si es iPhone, se ofrece bajar la app de la tienda.
    if (authLoading) return <div style={{ minHeight: "100vh", background: C.accent }} />;
    if (!session) {
      return <InvitacionLanding username={decodeURIComponent(refMatch[1])} onEntrar={(modo) => {
        try {
          localStorage.setItem("olbos_ref_abrir", modo);
          // Tras crear la cuenta (referido ya asegurado), a todo móvil web se le
          // ofrece la app de SU tienda — antes era solo iPhone, ahora Play está publicada
          const esMovilWeb = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && !esNativo();
          if (modo === "registro" && esMovilWeb) localStorage.setItem("olbos_ref_ios_store", "1");
        } catch (e) { /* */ }
        window.history.replaceState({}, "", "/");
        window.location.reload();
      }} />;
    }
    window.history.replaceState({}, "", "/");
  }

  const vivoMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/vivo\/(.+)$/);
  if (vivoMatch) {
    const esRodar = new URLSearchParams(window.location.search).get("rodar");
    // Solo el link de invitación (?rodar=1) con sesión va al grabador; el de
    // seguimiento muestra SIEMPRE el visor, tengas cuenta o no.
    if (esRodar && (session || authLoading)) {
      return (
        <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
          <div style={{ width: 26, height: 26, border: `3px solid ${C.border}`, borderTopColor: "#111111", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontSize: 13.5, color: C.muted, fontWeight: 600 }}>🏍️ Entrando al mapa de la rodada…</div>
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
        {/* location.href directo: el pushState+reload no navegaba en algunos
            navegadores embebidos (el de WhatsApp) — "el botón no camina" */}
        <VerUbicacionVivo token={vivoMatch[1]} rodar={new URLSearchParams(window.location.search).has("rodar")} onVolver={() => { window.location.href = "/"; }} />
      </div>
    );
  }

  // Vista pública de una ruta compartida (no requiere login)
  const rutaMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/ruta\/(.+)$/);
  if (rutaMatch) {
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
        <RutaPublica trazaId={rutaMatch[1]} onVolver={() => { window.history.pushState({}, "", "/"); window.location.reload(); }} />
      </div>
    );
  }

  // Vista pública de una comunidad: la dirección propia del organizador, para
  // mandarle a un municipio, a un sponsor o a un periodista. No requiere login.
  // Ojo: /c/ ya es de los links de campaña, por eso la URL es /comunidad/.
  const comunidadMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/comunidad\/([a-zA-Z0-9_.-]+)$/);
  if (comunidadMatch) {
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
        <ComunidadPublica username={decodeURIComponent(comunidadMatch[1])} />
      </div>
    );
  }

  // Vista pública de un evento/salida compartido (no requiere login)
  const eventoMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/evento\/(.+)$/);
  if (eventoMatch) {
    const esSumate = new URLSearchParams(window.location.search).has("sumate");
    // Con cuenta, el link del grupo no muestra la página: entra a la rodada (efecto "sumate")
    if (esSumate && (session || authLoading)) {
      return (
        <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
          <div style={{ width: 26, height: 26, border: `3px solid ${C.border}`, borderTopColor: "#111111", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontSize: 13.5, color: C.muted, fontWeight: 600 }}>🏍️ Entrando a la rodada…</div>
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
        <EventoPublico eventoId={eventoMatch[1]} sumate={esSumate} />
      </div>
    );
  }

  // Reclamar un perfil pre-llenado (invitación a partner)
  const reclamarMatch = typeof window !== "undefined" && window.location.pathname.match(/^\/reclamar\/(.+)$/);
  if (reclamarMatch) {
    return (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
        <ReclamarPerfil token={reclamarMatch[1]} />
      </div>
    );
  }

  if (showLogin) return <LoginScreen onBack={() => setShowLogin(false)} modoInicial={loginModo} />;
  if (showRegistroNegocio) return negocioRegistrado
    ? (
      <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: C.text, marginBottom: 12 }}>¡Registro enviado!</h1>
        <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7, marginBottom: 28 }}>Tu negocio está en revisión. En 24-48hs te confirmamos por email y tu ficha queda activa en Olbos.</p>
        <button onClick={() => { setShowRegistroNegocio(false); setNegocioRegistrado(false); }} style={{ width: "100%", padding: "14px 0", borderRadius: 14, background: C.text, color: "#fff", fontSize: 16, fontWeight: 700, border: "none", cursor: "pointer" }}>
          Ir a Olbos
        </button>
      </div>
    )
    : <RegistroNegocio onBack={() => setShowRegistroNegocio(false)} onSuccess={() => setNegocioRegistrado(true)} />;

  return (
    <div className={esEscritorio ? "olbos-escritorio" : undefined}
      style={esEscritorio
        ? { minHeight: "100vh", background: C.bg, position: "relative", paddingLeft: 200 }
        : { maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.bg, position: "relative" }}
      onTouchStart={onTouchStartTabs} onTouchEnd={onTouchEndTabs}>
      <style>{G}</style>

      <AvisoNavegadorInterno />

      {/* Comunidad abierta por link (olbos.ai/comunidad/<username>) con la app instalada */}
      {comunidadAbierta && <PerfilPublico username={comunidadAbierta} onClose={() => setComunidadAbierta(null)} />}

      {/* Avatar / acceso a Mi perfil — flotante en todas las secciones (el feed lo lleva en su propio header) */}
      {!esEscritorio && session && tab !== "perfil" && tab !== "feed" && (
        <div style={{ position: "fixed", top: "calc(var(--sat) + 12px)", right: "max(12px, calc((100vw - 430px) / 2 + 12px))", zIndex: 200 }}>
          <AvatarNav onClick={() => setTab("perfil")} />
        </div>
      )}

      {/* Botón flotante de Login — solo si no hay sesión */}
      {!esEscritorio && !session && (
        <button onClick={() => setShowRegistroModal(true)}
          style={{
            position: "fixed",
            top: "calc(var(--sat) + 12px)",
            right: "max(12px, calc((100vw - 430px) / 2 + 12px))",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px 8px 10px",
            borderRadius: 100,
            background: C.accent,
            color: "#fff",
            border: "none",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,.18)",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "-0.005em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}>
          <div style={{
            width: 24, height: 24, borderRadius: "50%",
            background: "rgba(255,255,255,.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <User size={13} strokeWidth={2} color="#fff" />
          </div>
          Iniciar sesión
        </button>
      )}
      {/* La sección activa entra deslizándose desde el lado del gesto (estilo Instagram) */}
      <div key={tab} className={slideDir}
        style={esEscritorio
          ? (tab === "rutas" || tab === "mercado"
              ? { minWidth: 0, maxWidth: 1180, margin: "0 auto" }
              : { minWidth: 0, maxWidth: 600, marginLeft: "max(24px, calc((100vw - 600px) / 2 - 200px))", marginRight: 24 })
          : undefined}>
      {tab === "eventos" && <Eventos userMotos={userMotos} requireAuth={requireAuth} session={session} isAdmin={isAdmin} onAI={() => setShowAI(true)} userPos={userPos} />}
      {tab === "rutas" && <Rutas isAdmin={isAdmin} />}
      {tab === "feed" && <Feed session={session} requireAuth={requireAuth} onAI={() => setShowAI(true)} setTab={setTab} />}
      {tab === "mercado" && <PitStop onAI={() => setShowAI(true)} userPosGlobal={userPos} setUserPosGlobal={setUserPos} onRegistro={() => setShowRegistroNegocio(true)} />}
      {tab === "motos" && <MotosDB isAdmin={isAdmin} />}
      {tab === "robadas" && <Robadas onAI={() => setShowAI(true)} session={session} userMotos={userMotos} isAdmin={isAdmin} />}
      {tab === "noticias" && <Noticias onAI={() => setShowAI(true)} />}
      {tab === "productos" && <Productos session={session} isAdmin={isAdmin} />}
      {tab === "grupos" && <MisGrupos session={session} requireAuth={requireAuth} />}
      {tab === "mensajes" && <Mensajes session={session} requireAuth={requireAuth} setMainTab={setTab} />}
      {tab === "mas" && <Mas setTab={setTab} session={session} requireAuth={requireAuth} onRegistroNegocio={() => setShowRegistroNegocio(true)} onAI={() => setShowAI(true)} onAdmin={() => setShowAdminPanel(true)} onOrdenNav={(o) => setOrdenNav(ordenValido(o))} esAgencia={esAgencia} onAgencia={() => setShowAgenciaPanel(true)} />}
      {tab === "perfil" && session && <Perfil isAdmin={isAdmin} setIsAdmin={setIsAdmin} onAdmin={() => setShowAdminPanel(true)} />}
      </div>
      <AvisoActualizacion />
      {/* Puerta de edad: toda cuenta sin fecha de nacimiento la carga antes de seguir
          (después de la bienvenida, para que lo primero que vea el nuevo sea su @) */}
      <GateEdad session={session} esperar={!!bienvenida} />
      <ColaFotosWorker />
      {esEscritorio
        ? <NavEscritorio active={tab} setActive={handleTabChange} session={session} novedades={novedades} orden={ordenNav}
            onLogin={() => setShowRegistroModal(true)} onPerfil={() => setTab("perfil")} />
        : <Nav active={tab} setActive={handleTabChange} session={session} novedades={novedades} orden={ordenNav} />}

      {/* Grabación en curso — montada acá, en la raíz, para que navegar la app
          no la corte. Minimizada sigue grabando; la barra roja te trae de vuelta. */}
      {recGlobal?.tipo === "track" && (
        <TrackRecorder
          session={session}
          grupoIdPreseleccionado={recGlobal.grupoId}
          grupoEnVivoId={recGlobal.grupoEnVivo ? recGlobal.grupoId : null}
          viajeEnVivoId={recGlobal.viajeId || null}
          eventoPreseleccionado={recGlobal.evento}
          navegarA={recGlobal.navegarA || null}
          entradaExplicita={!!recGlobal.explicito}
          retomarId={recGlobal.retomarId || null}
          minimizado={recMin}
          onMinimizar={() => setRecMin(true)}
          onCerrar={() => {
            // Cierre real del mapa (descartar/finalizar/✕): se olvida el destino para que
            // al volver a entrar no reaparezca la ruta vieja. Tras un reinicio sí se restaura.
            try { localStorage.removeItem("olbos_nav_activa"); } catch (e) { /* */ }
            setRecGlobal(null); setRecMin(false);
            window.dispatchEvent(new CustomEvent("olbos-trazas-actualizadas"));
          }}
        />
      )}
      {/* La rodada grupal usa EL MISMO grabador, en modo grupo: un solo mapa,
          un solo motor. RodadaEnVivo quedó reemplazado por esto. */}
      {recGlobal?.tipo === "rodada" && (
        <TrackRecorder
          session={session}
          grupoVivo={recGlobal.evento}
          eventoPreseleccionado={{ id: recGlobal.evento.id, titulo: recGlobal.evento.titulo || recGlobal.evento.title || "" }}
          minimizado={recMin}
          onMinimizar={() => setRecMin(true)}
          onCerrar={() => {
            setRecGlobal(null); setRecMin(false);
            window.dispatchEvent(new CustomEvent("olbos-trazas-actualizadas"));
          }}
        />
      )}
      {/* Invitado por link a una rodada espontánea: el grabador con el mapa compartido */}
      {recGlobal?.tipo === "vivo" && (
        <TrackRecorder
          session={session}
          vivoToken={recGlobal.token}
          minimizado={recMin}
          onMinimizar={() => setRecMin(true)}
          onCerrar={() => {
            setRecGlobal(null); setRecMin(false);
            window.dispatchEvent(new CustomEvent("olbos-trazas-actualizadas"));
          }}
        />
      )}
      {/* Link del grupo abierto sin cuenta: entrar como invitado (o crear la cuenta) */}
      {sumateSinCuenta && !session && (
        <EntrarComoInvitado eventoId={sumateSinCuenta}
          onCuenta={() => { setSumateSinCuenta(null); setShowRegistroModal(true); }}
          onCerrar={(entro) => {
            setSumateSinCuenta(null);
            // Cerrado sin entrar: no se le vuelve a ofrecer cada vez que abre la app
            if (entro !== true) { try { localStorage.removeItem("olbos_pending_sumate"); } catch (e) { /* */ } }
          }} />
      )}
      {/* Chequeo del teléfono antes de la rodada (link "sumate" o "Mapa en vivo") */}
      {chequeoRodada && (
        <ChequeoRodada evento={chequeoRodada.evento} modo={chequeoRodada.modo} recienAnotado={!!chequeoRodada.recienAnotado}
          onCerrar={() => setChequeoRodada(null)}
          onListo={() => {
            const c = chequeoRodada;
            setChequeoRodada(null);
            if (c.desdeMapa) return; // el mapa ya está abierto atrás
            if (c.modo === "dia") window.dispatchEvent(new CustomEvent("olbos-abrir-mapa-vivo", { detail: { evento: c.evento, sinChequeo: true } }));
            else window.dispatchEvent(new CustomEvent("olbos-abrir-evento", { detail: { id: c.evento.id } }));
          }} />
      )}
      {/* Visor de un compartido en vivo, adentro de la app (links de seguimiento) */}
      {verVivoToken && (
        <div style={{ position: "fixed", inset: 0, zIndex: 940, background: C.card, overflowY: "auto" }}>
          <div style={{ maxWidth: 430, margin: "0 auto", minHeight: "100vh", background: C.card }}>
            <VerUbicacionVivo token={verVivoToken} onVolver={() => setVerVivoToken(null)} />
          </div>
        </div>
      )}
      {recGlobal && recMin && (
        <button onClick={() => setRecMin(false)}
          style={{ position: "fixed", top: "var(--sat)", left: 0, right: 0, maxWidth: 430, margin: "0 auto", zIndex: 900, padding: "9px 14px", background: "#B3372C", color: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12.5, fontWeight: 800, letterSpacing: "-0.005em" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.card, animation: "pulse 1.5s ease-in-out infinite" }} />
          {recGlobal.tipo === "rodada" || recGlobal.tipo === "vivo"
            ? (recEstado === "grabando" ? "Rodada en vivo · compartiendo y grabando" : "Rodada en vivo · seguís en el mapa")
            : recEstado === "lista" ? "Navegando de fondo · sin grabar"
            : recEstado === "pausada" ? "Grabación en pausa"
            : "Grabando tu recorrido"} — tocá para volver
        </button>
      )}

      {showAI && <OlbosAI onClose={() => setShowAI(false)} onCrearEvento={(data) => {
        setShowAI(false);
        setTab("eventos");
        // Guardamos los datos del flyer para que Eventos los use
        window.olbosFlyerEvento = data;
      }} />}
      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
      {showAgenciaPanel && <AdminPanel modo="agencia" onClose={() => setShowAgenciaPanel(false)} />}
      {regaloCodigo && (
        <RegaloClicks codigo={regaloCodigo} session={session} onLogin={() => { setRegaloCodigo(null); setShowRegistroModal(true); }}
          onCerrar={() => { try { localStorage.removeItem("olbos_regalo"); } catch (e) { /* */ } setRegaloCodigo(null); }}
          onPublicar={() => { try { localStorage.removeItem("olbos_regalo"); } catch (e) { /* */ } setRegaloCodigo(null); setTab("feed"); }} />
      )}
      {sesionCircuitoVer && <ResumenCircuito traza={sesionCircuitoVer.traza} sesion={sesionCircuitoVer.sesion} circuito={sesionCircuitoVer.circuito} onClose={() => setSesionCircuitoVer(null)} />}
      {anuncioVer && <VisorAnuncio a={anuncioVer} onClose={() => setAnuncioVer(null)} />}
      <CotizarSeguroGlobal />
      {circuitoVer && <FichaCircuito c={circuitoVer} userId={session?.user?.id || null} onClose={() => setCircuitoVer(null)} />}

      {/* Bienvenida post-reclamo (partner): marca el próximo paso para que no se pierdan */}
      {bienvenidaPartner !== null && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>
          <div style={{ background: C.card, borderRadius: 24, maxWidth: 360, width: "100%", padding: "32px 26px 26px", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,.32)" }} className="fade-up">
            <div style={{ fontSize: 52, marginBottom: 10 }}>🎉</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>
              ¡Listo{bienvenidaPartner ? `, @${bienvenidaPartner}` : ""}!
            </h2>
            <p style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.55, letterSpacing: "-0.01em", marginBottom: 24 }}>
              Tu perfil ya quedó armado. El próximo paso es <strong style={{ color: C.text }}>crear tu primera salida</strong> y compartirla para que se anoten.
            </p>
            <button onClick={() => { setBienvenidaPartner(null); window.olbosAbrirCrear = true; setTab("eventos"); }}
              style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", cursor: "pointer", marginBottom: 10, boxShadow: "0 6px 18px rgba(255,69,0,.25)" }}>
              Crear mi primera salida
            </button>
            <button onClick={() => { setBienvenidaPartner(null); setTab("feed"); }}
              style={{ width: "100%", padding: "13px", borderRadius: 14, background: C.surface, color: C.text, border: "none", fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em", cursor: "pointer" }}>
              Explorar Olbos primero
            </button>
          </div>
        </div>
      )}

      {showRegistroModal && (
        <RegistroModal
          onClose={() => setShowRegistroModal(false)}
          onLogin={(modo) => { setShowRegistroModal(false); setLoginModo(modo === "login" ? "login" : "register"); setShowLogin(true); }}
        />
      )}

      {/* iPhone que vino por link de invitación: cuenta lista → ofrecer la app de la tienda */}
      {showIosStore && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="fade-up" style={{ background: C.card, borderRadius: 24, maxWidth: 360, width: "100%", padding: "32px 26px 26px", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,.32)" }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>🏍️</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 8 }}>¡Tu cuenta ya está!</h2>
            <p style={{ fontSize: 14.5, color: C.muted, lineHeight: 1.55, letterSpacing: "-0.01em", marginBottom: 24 }}>
              Ahora bajate la app en tu teléfono: <strong style={{ color: C.text }}>GPS con pantalla apagada</strong>, avisos al toque y todo Olbos completo.
            </p>
            <button onClick={() => {
              setShowIosStore(false);
              const esAndroid = /Android/i.test(navigator.userAgent);
              window.open(esAndroid ? "https://play.google.com/store/apps/details?id=ai.olbos.app" : "https://apps.apple.com/app/id6794469275", "_blank");
            }}
              style={{ width: "100%", padding: "15px", borderRadius: 14, background: C.accent, color: "#fff", border: "none", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", cursor: "pointer", marginBottom: 10 }}>
              {/Android/i.test(navigator.userAgent) ? "Descargarla de Google Play" : "Descargarla del App Store"}
            </button>
            <button onClick={() => setShowIosStore(false)}
              style={{ width: "100%", padding: "13px", borderRadius: 14, background: C.surface, color: C.text, border: "none", fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em", cursor: "pointer" }}>
              Seguir en el navegador
            </button>
          </div>
        </div>
      )}

      {/* Bienvenida post-registro: una vista, oscura, con el @ y el número de rider.
          Si venía con un evento pendiente (QR del stand), el botón principal lo anota. */}
      {bienvenida && session && (
        <BienvenidaRider
          session={session}
          eventoId={bienvenida.eventoId}
          onAnotar={() => { const b = bienvenida; setBienvenida(null); abrirEventoPendiente(b.eventoId, true); }}
          onCerca={() => { setBienvenida(null); abrirEventoPendiente(null, false); setTab("eventos"); }}
          onExplorar={() => { const b = bienvenida; setBienvenida(null); if (b.eventoId) abrirEventoPendiente(b.eventoId, b.anotar); }}
          onIr={(t) => { setBienvenida(null); abrirEventoPendiente(null, false); setTab(t); }}
        />
      )}
    </div>
  );
}

// ─── Premio semanal: cierre, revisión del podio y pagos (25-oct-2026, pedido de Carlos) ───
// SQL en docs/sql/premio_cierre_semanal.sql. El lunes a las 9 el cron arma el podio de la semana
// que terminó con las señales de auto; Carlos lo revisa acá (Más → Premio semanal), confirma,
// y los ganadores cargan su alias o CVU en la tarjeta de abajo. Después Carlos marca pagado.
const premioParse = (raw) => { try { return typeof raw === "string" ? JSON.parse(raw) : raw; } catch (e) { return null; } };
const premioFmtSemana = (sem) => {
  if (!sem) return "";
  const d = new Date(String(sem).slice(0, 10) + "T12:00:00");
  const h = new Date(d.getTime() + 6 * 86400000);
  const f = (x) => x.toLocaleDateString("es-AR", { day: "numeric", month: "numeric" });
  return `del ${f(d)} al ${f(h)}`;
};
const premioFmtFecha = (ts) => {
  if (!ts) return "";
  try { return new Date(ts).toLocaleDateString("es-AR", { day: "numeric", month: "short" }); } catch (e) { return ""; }
};
const premioDePuesto = (s, puesto) => (s ? (puesto === 1 ? s.premio1 : puesto === 2 ? s.premio2 : s.premio3) : null);
const PREMIO_MEDALLA = { 1: "🥇", 2: "🥈", 3: "🥉" };

// Tarjeta del ganador: aparece sola cuando tiene un premio esperando sus datos de pago.
const PremioGanadorCard = ({ session, style }) => {
  const [premios, setPremios] = useState(null);
  const [txt, setTxt] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState("");
  const [editar, setEditar] = useState(false);
  const uid = session?.user?.id || null;
  const cargar = () => {
    if (!uid) { setPremios(null); return; }
    supabase.rpc("premio_mis_premios").then(({ data, error }) => {
      if (error) { setPremios(null); return; }
      const d = premioParse(data);
      setPremios(Array.isArray(d) ? d : null);
    }, () => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [uid]);
  const lista = premios || [];
  const p = lista.find(x => x.estado === "pendiente_datos" && !x.vencido) || lista.find(x => x.estado === "datos_cargados");
  if (!p) return null;
  const pedir = p.estado === "pendiente_datos" || editar;
  const enviar = async () => {
    const v = txt.trim();
    if (v.length < 4) { setErr("Escribí tu alias o CVU de Mercado Pago."); return; }
    setEnviando(true); setErr("");
    try {
      const { data, error } = await supabase.rpc("premio_cargar_datos", { p_semana: p.semana, p_datos: v });
      const d = premioParse(data);
      if (error || d?.error) {
        const m = {
          vencido: "Pasaron los 15 días para pasar los datos y el premio venció. Escribinos a hola@olbos.ai.",
          ya_pagado: "Este premio ya está pagado.",
          datos_invalidos: "Revisá el alias o CVU: tiene que tener entre 4 y 200 caracteres.",
        }[d?.error];
        setErr(m || `No se pudo enviar. ${error?.message || d?.error || ""}`);
      } else { setEditar(false); setTxt(""); cargar(); }
    } catch (e) { setErr("No se pudo enviar. Probá de nuevo."); }
    setEnviando(false);
  };
  const crema = "#F9F5F2";
  return (
    <div style={{ background: C.accent, color: crema, borderRadius: 14, padding: "16px 16px 14px", ...(style || {}) }}>
      {pedir ? (
        <>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.25 }}>🏆 Ganaste: pasanos tu alias o CVU de Mercado Pago</div>
          <div style={{ fontSize: 12.5, color: "#c9c3ba", marginTop: 5, lineHeight: 1.5 }}>
            {PREMIO_MEDALLA[p.puesto] || ""} {p.puesto}° puesto del premio semanal, semana {premioFmtSemana(p.semana)}{p.premio ? `: ${p.premio}` : ""}.
            {p.vence_en && p.estado === "pendiente_datos" ? ` Tenés tiempo hasta el ${premioFmtFecha(p.vence_en)}.` : ""}
          </div>
          <input value={txt} onChange={e => setTxt(e.target.value)} placeholder="Tu alias o CVU" autoCapitalize="none" autoCorrect="off"
            style={{ width: "100%", boxSizing: "border-box", marginTop: 12, padding: "12px 13px", borderRadius: 10, border: "none", background: crema, color: "#111111", fontSize: 15 }} />
          {err && <div style={{ fontSize: 12.5, color: "#f0b4ac", marginTop: 8, lineHeight: 1.45 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={enviar} disabled={enviando}
              style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: crema, color: "#111111", fontSize: 14.5, fontWeight: 800, cursor: "pointer", opacity: enviando ? 0.6 : 1 }}>
              {enviando ? "Enviando…" : "Enviar"}
            </button>
            {editar && <button onClick={() => { setEditar(false); setErr(""); }} style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid #ffffff33", background: "transparent", color: crema, fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>}
          </div>
          <div style={{ fontSize: 11.5, color: "#a39d94", marginTop: 8, lineHeight: 1.45 }}>La cuenta tiene que estar a tu nombre. Bases en olbos.ai/premios.</div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em" }}>🏆 Ya tenemos tus datos para el premio</div>
          <div style={{ fontSize: 12.5, color: "#c9c3ba", marginTop: 5, lineHeight: 1.5 }}>
            {p.puesto}° puesto{p.premio ? ` · ${p.premio}` : ""} · Cuenta: <b style={{ color: crema }}>{p.datos_pago}</b>. Te avisamos cuando esté pagado.
          </div>
          <button onClick={() => { setEditar(true); setTxt(p.datos_pago || ""); }} style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, border: "1px solid #ffffff33", background: "transparent", color: crema, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Corregir</button>
        </>
      )}
    </div>
  );
};

// Entrada en Más: la tarjeta del ganador (si tiene premio) y, para el admin, la revisión del podio.
const PremioSemanalMas = ({ session, esAdmin }) => {
  const [abierto, setAbierto] = useState(false);
  const [pend, setPend] = useState(0);
  useEffect(() => {
    if (!esAdmin || abierto) return;
    supabase.rpc("premio_admin_panel").then(({ data }) => {
      const d = premioParse(data);
      if (!d || d.error) return;
      setPend((d.semanas || []).filter(s => s.estado === "a_revisar").length + (d.pagos || []).filter(x => x.estado === "datos_cargados").length);
    }, () => {});
  }, [esAdmin, abierto]);
  return (
    <>
      <PremioGanadorCard session={session} style={{ marginBottom: 12 }} />
      {esAdmin && (
        <button onClick={() => setAbierto(true)}
          style={{ width: "100%", marginBottom: 12, padding: "14px 16px", borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22 }}>🏆</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: "-0.01em" }}>Premio semanal: revisar podio</span>
            <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>Señales de auto, confirmar ganadores y pagos</span>
          </span>
          {pend > 0 && <span style={{ background: C.accent, color: "#F9F5F2", borderRadius: 999, fontSize: 11.5, fontWeight: 800, padding: "3px 9px" }}>{pend}</span>}
          <ChevronRight size={16} strokeWidth={2} color="#c9c3ba" />
        </button>
      )}
      {abierto && createPortal(<PremioSemanalAdmin onBack={() => setAbierto(false)} />, document.body)}
    </>
  );
};

// Pantalla del admin: podio con señales y recorridos, descalificar, confirmar y pagos.
const PremioSemanalAdmin = ({ onBack }) => {
  const [panel, setPanel] = useState(null);
  const [err, setErr] = useState("");
  const [descal, setDescal] = useState([]);       // user_id descalificados (en esta pantalla, antes de confirmar)
  const [abiertoUser, setAbiertoUser] = useState(null);
  const [abiertaTraza, setAbiertaTraza] = useState(null);
  const [mapas, setMapas] = useState({});          // traza_id → url del mapa estático | "cargando" | "sin"
  const [ocupado, setOcupado] = useState("");
  const [copiado, setCopiado] = useState("");
  useBackGuard(true, onBack);

  const cargar = () => {
    supabase.rpc("premio_admin_panel").then(({ data, error }) => {
      const d = premioParse(data);
      if (error || !d || d.error) { setErr(error?.message || (d?.error === "solo_admin" ? "Solo para el admin." : "No se pudo cargar. ¿Ya corriste docs/sql/premio_cierre_semanal.sql?")); return; }
      setErr(""); setPanel(d);
    }, () => setErr("No se pudo cargar."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const semanas = panel?.semanas || [];
  const sRev = semanas.find(s => s.estado === "a_revisar") || null;
  const hoy = panel?.hoy || "";
  const sinPreparar = semanas.filter(s => s.estado === "vigente" && hoy && new Date(s.semana + "T12:00:00").getTime() + 7 * 86400000 <= new Date(hoy + "T12:00:00").getTime());
  const cands = (sRev?.ganadores || []).filter(g => g.puesto != null).sort((a, b) => a.puesto - b.puesto);
  const excluidos = (sRev?.ganadores || []).filter(g => g.excluido);
  const activos = cands.filter(g => !descal.includes(g.user_id));
  const podio = activos.slice(0, 3);
  const suplentes = activos.slice(3);
  const fuera = cands.filter(g => descal.includes(g.user_id));
  useEffect(() => { setDescal([]); }, [sRev?.semana]);

  const preparar = async (semana) => {
    setOcupado("preparar");
    try {
      const { data, error } = await supabase.rpc("premio_cierre_preparar", { p_semana: semana });
      const d = premioParse(data);
      if (error || d?.error) alert(`No se pudo preparar: ${error?.message || d?.error}`);
      else if (d && d.ok === false) alert({ sin_premio: "Esa semana no tiene premio cargado.", ya_confirmado: "Esa semana ya está confirmada.", semana_en_curso: "La semana todavía no terminó." }[d.motivo] || d.motivo);
    } catch (e) { alert("No se pudo preparar."); }
    setOcupado(""); cargar();
  };

  const confirmar = async () => {
    if (!sRev || podio.length === 0) return;
    const lineas = podio.map((g, i) => `${i + 1}° @${g.username} · ${g.km} km${premioDePuesto(sRev, i + 1) ? ` · ${premioDePuesto(sRev, i + 1)}` : ""}`).join("\n");
    if (!window.confirm(`¿Confirmar el podio de la semana ${premioFmtSemana(sRev.semana)}?\n\n${lineas}\n\nSe avisa a los ganadores y se publica el anuncio en el feed como @olbos.`)) return;
    setOcupado("confirmar");
    try {
      const { data, error } = await supabase.rpc("premio_cierre_confirmar", { p_semana: sRev.semana, p_podio: podio.map(g => g.user_id) });
      const d = premioParse(data);
      if (error || d?.error) alert(`No se pudo confirmar: ${error?.message || d?.error}`);
    } catch (e) { alert("No se pudo confirmar."); }
    setOcupado(""); cargar();
  };

  const marcarPagado = async (x) => {
    if (!window.confirm(`¿Marcar pagado el ${x.puesto}° puesto de @${x.username}${x.premio ? ` (${x.premio})` : ""}?\n\nLe llega un aviso de que ya se pagó.`)) return;
    setOcupado(`pago-${x.semana}-${x.puesto}`);
    try {
      const { data, error } = await supabase.rpc("premio_marcar_pagado", { p_semana: x.semana, p_puesto: x.puesto });
      const d = premioParse(data);
      if (error || d?.error) alert(`No se pudo marcar: ${error?.message || d?.error}`);
    } catch (e) { alert("No se pudo marcar."); }
    setOcupado(""); cargar();
  };

  const verTraza = (id) => {
    if (abiertaTraza === id) { setAbiertaTraza(null); return; }
    setAbiertaTraza(id);
    if (mapas[id]) return;
    setMapas(m => ({ ...m, [id]: "cargando" }));
    supabase.rpc("premio_recorrido_puntos", { p_traza_id: id }).then(({ data }) => {
      const d = premioParse(data);
      const pts = Array.isArray(d?.puntos) ? d.puntos.filter(p => p && typeof p.lat === "number" && typeof p.lng === "number") : [];
      const url = pts.length >= 2 ? generarImagenTrackEstatica(pts) : null;
      setMapas(m => ({ ...m, [id]: url || "sin" }));
    }, () => setMapas(m => ({ ...m, [id]: "sin" })));
  };

  const copiar = (txt, key) => {
    try { navigator.clipboard.writeText(txt).then(() => { setCopiado(key); setTimeout(() => setCopiado(""), 1500); }, () => {}); } catch (e) { /* */ }
  };

  const btn = (primario) => ({ padding: "10px 14px", borderRadius: 10, border: primario ? "none" : `1px solid ${C.border}`, background: primario ? C.accent : C.card, color: primario ? "#F9F5F2" : C.text, fontSize: 13.5, fontWeight: 800, cursor: "pointer" });
  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px", marginBottom: 10 };

  const filaCandidato = (g, etiqueta, enPodio, esFuera) => {
    const sen = Array.isArray(g.senales) ? g.senales : [];
    const recs = Array.isArray(g.recorridos) ? g.recorridos : [];
    const abierto = abiertoUser === g.user_id;
    return (
      <div key={g.user_id} style={{ ...card, opacity: esFuera ? 0.6 : 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.muted, minWidth: 54 }}>{etiqueta}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{g.username}</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 1 }}>
              <b style={{ color: C.text }}>{Number(g.km || 0).toLocaleString("es-AR")} km</b> · {g.n_recorridos || recs.length} recorrido{(g.n_recorridos || recs.length) === 1 ? "" : "s"} · puesto en el ranking: {g.puesto}°
            </div>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          {sen.length === 0
            ? <div style={{ fontSize: 12.5, color: C.green, fontWeight: 700 }}>✓ Sin señales de auto</div>
            : sen.map((s, i) => <div key={i} style={{ fontSize: 12.5, color: C.red, lineHeight: 1.45, marginTop: i ? 3 : 0 }}>⚠️ {s}</div>)}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button onClick={() => { setAbiertoUser(abierto ? null : g.user_id); setAbiertaTraza(null); }} style={{ ...btn(false), flex: "1 1 auto" }}>{abierto ? "Ocultar recorridos" : `Ver recorridos (${recs.length})`}</button>
          {esFuera
            ? <button onClick={() => setDescal(d => d.filter(x => x !== g.user_id))} style={btn(false)}>Volver a sumar</button>
            : <button onClick={() => setDescal(d => [...d, g.user_id])} style={{ ...btn(false), color: C.red }}>Descalificar</button>}
        </div>
        {abierto && (
          <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}` }}>
            {recs.length === 0 && <div style={{ fontSize: 12.5, color: C.muted, padding: "10px 0" }}>Sin recorridos.</div>}
            {recs.map(r => (
              <div key={r.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <button onClick={() => verTraza(r.id)} style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", padding: "10px 0", cursor: "pointer" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: r.alerta ? C.red : C.text }}>
                    {r.alerta ? "⚠️ " : ""}{Number(r.km || 0).toLocaleString("es-AR")} km · {premioFmtFecha(r.inicio)}{r.titulo ? ` · ${r.titulo}` : ""}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                    {[r.vmax != null ? `máx ${r.vmax} km/h` : null, r.vprom != null ? `prom ${r.vprom} km/h` : null, r.dur_seg ? `${Math.round(r.dur_seg / 60)} min` : null, r.tipo_camino || null, r.publica ? "público" : "privado"].filter(Boolean).join(" · ")}
                  </div>
                </button>
                {abiertaTraza === r.id && (
                  <div style={{ paddingBottom: 10 }}>
                    {mapas[r.id] === "cargando" && <div style={{ fontSize: 12.5, color: C.muted }}>Cargando el trazo…</div>}
                    {mapas[r.id] === "sin" && <div style={{ fontSize: 12.5, color: C.muted }}>Este recorrido no tiene puntos para dibujar.</div>}
                    {mapas[r.id] && mapas[r.id] !== "cargando" && mapas[r.id] !== "sin" && (
                      <img src={mapas[r.id]} alt="Trazo del recorrido" style={{ width: "100%", borderRadius: 10, display: "block", background: C.surface }} />
                    )}
                    <a href={`${SITE_URL}/ruta/${r.id}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 8, fontSize: 12.5, fontWeight: 700, color: C.text }}>
                      Abrir la ficha ›{r.publica ? "" : " (es privado: la ficha pública puede no abrir)"}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const pagos = panel?.pagos || [];
  const estadoPago = (x) => x.estado === "pagado" ? { t: `Pagado ${premioFmtFecha(x.pagado_en)}`, c: C.green }
    : x.estado === "datos_cargados" ? { t: "Datos cargados: listo para pagar", c: C.text }
    : x.vencido ? { t: "Venció sin datos", c: C.red }
    : { t: `Falta que pase sus datos (vence ${premioFmtFecha(x.vence_en)})`, c: C.muted };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: C.bg, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <div style={{ padding: "var(--header-pt) 16px 8px", display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "50%", background: C.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={18} strokeWidth={2} color={C.text} />
          </button>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Premio semanal</div>
        </div>
        <div style={{ padding: "4px 16px 110px" }}>
          {err && <div style={{ ...card, fontSize: 13, color: C.muted }}>{err}</div>}
          {!panel && !err && <div style={{ fontSize: 13, color: C.muted, padding: "10px 2px" }}>Cargando…</div>}

          {sinPreparar.map(s => (
            <div key={s.semana} style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Semana {premioFmtSemana(s.semana)}</div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>Ya terminó y el cierre todavía no se armó (lo arma solo el lunes a las 9).</div>
              <button onClick={() => preparar(s.semana)} disabled={!!ocupado} style={{ ...btn(true), marginTop: 10, width: "100%" }}>{ocupado === "preparar" ? "Armando…" : "Armar el cierre ahora"}</button>
            </div>
          ))}

          {panel && !sRev && sinPreparar.length === 0 && (
            <div style={{ ...card, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>No hay ningún podio para revisar. El lunes a las 9 se arma solo el de la semana que terminó y te llega un aviso.</div>
          )}

          {sRev && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 2px 8px" }}>Revisar · semana {premioFmtSemana(sRev.semana)}</div>
              {cands.length === 0 && <div style={{ ...card, fontSize: 13, color: C.muted }}>Nadie sumó km en moto esa semana.</div>}
              {podio.map((g, i) => filaCandidato(g, `${PREMIO_MEDALLA[i + 1]} ${premioDePuesto(sRev, i + 1) || `${i + 1}°`}`, true, false))}
              {suplentes.length > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, margin: "14px 2px 8px" }}>SUPLENTES</div>}
              {suplentes.map(g => filaCandidato(g, "Suplente", false, false))}
              {fuera.length > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, margin: "14px 2px 8px" }}>DESCALIFICADOS</div>}
              {fuera.map(g => filaCandidato(g, "Fuera", false, true))}
              {excluidos.map(g => (
                <div key={`ex-${g.user_id}`} style={{ fontSize: 12.5, color: C.muted, margin: "6px 2px", lineHeight: 1.5 }}>
                  @{g.username} ({Number(g.km || 0).toLocaleString("es-AR")} km) no entra: {g.excluido}.
                </div>
              ))}
              {podio.length > 0 && (
                <button onClick={confirmar} disabled={!!ocupado} style={{ ...btn(true), width: "100%", padding: "14px 0", fontSize: 15, marginTop: 8 }}>
                  {ocupado === "confirmar" ? "Confirmando…" : "Confirmar podio"}
                </button>
              )}
              <button onClick={() => preparar(sRev.semana)} disabled={!!ocupado} style={{ ...btn(false), width: "100%", marginTop: 8 }}>
                {ocupado === "preparar" ? "Recalculando…" : "Recalcular con los datos de ahora"}
              </button>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>Confirmar avisa a los ganadores, publica el anuncio en el feed como @olbos y abre los pagos. Si descalificás a alguien, sube el suplente.</div>
            </>
          )}

          <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", margin: "22px 2px 8px" }}>Pagos</div>
          {panel && pagos.length === 0 && <div style={{ fontSize: 13, color: C.muted, padding: "4px 2px" }}>Todavía no hay premios confirmados.</div>}
          {pagos.map(x => {
            const e = estadoPago(x);
            const key = `${x.semana}-${x.puesto}`;
            return (
              <div key={key} style={card}>
                <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{PREMIO_MEDALLA[x.puesto]} @{x.username}{x.premio ? ` · ${x.premio}` : ""}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Semana {premioFmtSemana(x.semana)}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: e.c, marginTop: 6 }}>{e.t}</div>
                {x.datos_pago && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, background: C.surface, borderRadius: 10, padding: "8px 10px" }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: C.text, userSelect: "text", wordBreak: "break-all" }}>{x.datos_pago}</div>
                    <button onClick={() => copiar(x.datos_pago, key)} style={{ ...btn(false), padding: "6px 10px", fontSize: 12 }}>{copiado === key ? "Copiado" : "Copiar"}</button>
                  </div>
                )}
                {x.estado !== "pagado" && (
                  <button onClick={() => marcarPagado(x)} disabled={!!ocupado}
                    style={{ ...btn(x.estado === "datos_cargados"), width: "100%", marginTop: 10 }}>
                    {ocupado === `pago-${key}` ? "Marcando…" : "Marcar pagado"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
